/**
 * ReactPerfMetrics - Live performance metrics for React components
 * 
 * Tracks render counts and durations for components using usePerf().
 * Data is displayed in the debug panel when enabled.
 * 
 * Usage:
 * - Enable: window.ReactPerfMetrics.enabled = true;
 * - View: window.ReactPerfMetrics.getStats();
 * - Reset: window.ReactPerfMetrics.reset();
 */

(function () {
    'use strict';

    const MAX_HISTORY = 100; // Keep last N renders per component
    const MAX_ACTION_HISTORY = 20; // Keep last N action summaries

    const metrics = {
        enabled: false,
        stats: {},      // { componentName: { count, totalMs, lastMs, avgMs, history: [] } }
        listeners: [],  // Callbacks to notify on stats change

        // Action tracking - captures renders during a named action
        currentAction: null,     // { name, startTime, renders: [] }
        actionHistory: [],       // Completed action summaries

        /**
         * Log a component render
         * @param {string} componentName 
         * @param {number} durationMs 
         */
        logRender(componentName, durationMs) {
            if (!this.enabled) return;

            if (!this.stats[componentName]) {
                this.stats[componentName] = {
                    count: 0,
                    totalMs: 0,
                    lastMs: 0,
                    avgMs: 0,
                    history: []
                };
            }

            const stat = this.stats[componentName];
            stat.count++;
            stat.totalMs += durationMs;
            stat.lastMs = durationMs;
            stat.avgMs = stat.totalMs / stat.count;
            stat.history.push({ time: Date.now(), duration: durationMs });

            // Trim history
            if (stat.history.length > MAX_HISTORY) {
                stat.history = stat.history.slice(-MAX_HISTORY);
            }

            // Track render during active action
            if (this.currentAction) {
                this.currentAction.renders.push({
                    component: componentName,
                    duration: durationMs,
                    time: Date.now()
                });
            }

            // Notify listeners
            this.notifyListeners();
        },

        /**
         * Get all stats
         * @returns {Object} Stats object
         */
        getStats() {
            return { ...this.stats };
        },

        /**
         * Get summary for display
         * @returns {Array} Sorted array of [name, stats] pairs
         */
        getSummary() {
            return Object.entries(this.stats)
                .map(([name, stat]) => ({
                    name,
                    count: stat.count,
                    avgMs: stat.avgMs.toFixed(2),
                    lastMs: stat.lastMs.toFixed(2),
                    totalMs: stat.totalMs.toFixed(1)
                }))
                .sort((a, b) => b.count - a.count); // Sort by render count descending
        },

        /**
         * Get recent renders as a flat list for live trace
         * @param {number} limit - Max number of entries (default 50)
         * @returns {Array} Array of { time, component, duration } sorted newest first
         */
        getRecentRenders(limit = 50) {
            const all = [];
            for (const [name, stat] of Object.entries(this.stats)) {
                for (const h of stat.history) {
                    all.push({ time: h.time, component: name, duration: h.duration });
                }
            }
            // Sort by time descending (newest first) and limit
            return all.sort((a, b) => b.time - a.time).slice(0, limit);
        },

        /**
         * Get render rate (renders per second) over last N seconds
         * @param {number} seconds - Time window (default 5)
         * @returns {Object} { total, perComponent: { name: rate } }
         */
        getRate(seconds = 5) {
            const cutoff = Date.now() - (seconds * 1000);
            const rates = {};
            let total = 0;

            for (const [name, stat] of Object.entries(this.stats)) {
                const recentCount = stat.history.filter(h => h.time > cutoff).length;
                rates[name] = (recentCount / seconds).toFixed(1);
                total += recentCount;
            }

            return {
                total: (total / seconds).toFixed(1),
                perComponent: rates
            };
        },

        // ============ ACTION TRACKING ============

        /**
         * Start tracking renders for a named action (e.g., "typeInRow3")
         * @param {string} name - Descriptive name for this action
         */
        startAction(name) {
            // End any existing action first
            if (this.currentAction) {
                this.endAction();
            }
            this.currentAction = {
                name,
                startTime: Date.now(),
                renders: []
            };
            console.log(`[ReactPerfMetrics] Action started: ${name}`);
        },

        /**
         * End the current action and return a summary
         * @returns {Object|null} Action summary or null if no action was active
         */
        endAction() {
            if (!this.currentAction) return null;

            const action = this.currentAction;
            const endTime = Date.now();
            const duration = endTime - action.startTime;

            // Summarize renders by component
            const rendersByComponent = {};
            for (const r of action.renders) {
                if (!rendersByComponent[r.component]) {
                    rendersByComponent[r.component] = { count: 0, totalMs: 0 };
                }
                rendersByComponent[r.component].count++;
                rendersByComponent[r.component].totalMs += r.duration;
            }

            const summary = {
                name: action.name,
                duration,
                totalRenders: action.renders.length,
                components: Object.entries(rendersByComponent)
                    .map(([name, data]) => ({
                        name,
                        count: data.count,
                        avgMs: (data.totalMs / data.count).toFixed(2)
                    }))
                    .sort((a, b) => b.count - a.count)
            };

            // Store in history
            this.actionHistory.push(summary);
            if (this.actionHistory.length > MAX_ACTION_HISTORY) {
                this.actionHistory = this.actionHistory.slice(-MAX_ACTION_HISTORY);
            }

            this.currentAction = null;
            console.log(`[ReactPerfMetrics] Action ended: ${action.name}`, summary);
            this.notifyListeners();
            return summary;
        },

        /**
         * Get action history
         * @returns {Array} Recent action summaries
         */
        getActionHistory() {
            return [...this.actionHistory];
        },

        /**
         * Check if an action is currently active
         * @returns {Object|null} Current action info or null
         */
        getCurrentAction() {
            return this.currentAction ? {
                name: this.currentAction.name,
                renderCount: this.currentAction.renders.length,
                duration: Date.now() - this.currentAction.startTime
            } : null;
        },

        /**
         * Reset all stats
         */
        reset() {
            this.stats = {};
            this.currentAction = null;
            this.actionHistory = [];
            this.notifyListeners();
        },

        /**
         * Subscribe to stats changes
         * @param {Function} callback 
         * @returns {Function} Unsubscribe function
         */
        subscribe(callback) {
            this.listeners.push(callback);
            return () => {
                this.listeners = this.listeners.filter(l => l !== callback);
            };
        },

        /**
         * Notify all listeners (debounced)
         */
        notifyListeners() {
            if (this._notifyTimeout) return;
            this._notifyTimeout = setTimeout(() => {
                this._notifyTimeout = null;
                this.listeners.forEach(cb => {
                    try { cb(this.getStats()); } catch (e) { /* ignore */ }
                });
            }, 100); // Debounce 100ms
        },

        /**
         * Enable metrics collection
         */
        enable() {
            this.enabled = true;
            console.log('[ReactPerfMetrics] Enabled. Use getStats() to view.');
        },

        /**
         * Disable metrics collection
         */
        disable() {
            this.enabled = false;
            console.log('[ReactPerfMetrics] Disabled.');
        }
    };

    // Expose globally
    window.ReactPerfMetrics = metrics;

    // Log availability
    if (typeof console !== 'undefined') {
        console.log('[ReactPerfMetrics] Available. Enable with: window.ReactPerfMetrics.enable()');
    }
})();
;
/**
 * AUTO-GENERATED - DO NOT EDIT
 *
 * Default currency rates inlined at build time.
 * Generated from: public/data/currencies-fallback.json
 * Timestamp: 2026-02-18T08:00:00.000Z
 *
 * To update: npm run build (runs scripts/inline-currencies.js)
 */

(function() {
  // Default currencies - available synchronously, no fetch needed!
  var defaultCurrencies = {
  "AED": 3.67,
  "AFN": 62.5,
  "ALL": 81.42,
  "AMD": 375.99,
  "ANG": 1.79,
  "AOA": 912.25,
  "ARS": 1393.5,
  "AUD": 1.41,
  "AWG": 1.8,
  "AZN": 1.7,
  "BAM": 1.65,
  "BBD": 2,
  "BDT": 122.04,
  "BGN": 1.65,
  "BHD": 0.377,
  "BIF": 2961.35,
  "BMD": 1,
  "BND": 1.26,
  "BOB": 6.92,
  "BRL": 5.22,
  "BSD": 1,
  "BTC": 0.00001468,
  "BTN": 90.54,
  "BWP": 13.18,
  "BYN": 2.85,
  "BZD": 2.01,
  "CAD": 1.37,
  "CDF": 2307.61,
  "CHF": 0.7715,
  "CLF": 0.022,
  "CLP": 866.75,
  "CNH": 6.88,
  "CNY": 6.91,
  "COP": 3663.89,
  "CRC": 480.27,
  "CUC": 1,
  "CUP": 25.75,
  "CVE": 93.08,
  "CZK": 20.51,
  "DJF": 177.86,
  "DKK": 6.31,
  "DOP": 61.64,
  "DZD": 129.8,
  "EGP": 47.04,
  "ERN": 15,
  "ETB": 155.36,
  "EUR": 0.845,
  "FJD": 2.2,
  "FKP": 0.7374,
  "GBP": 0.7374,
  "GEL": 2.67,
  "GGP": 0.7374,
  "GHS": 10.98,
  "GIP": 0.7374,
  "GMD": 73.5,
  "GNF": 8767.16,
  "GTQ": 7.66,
  "GYD": 208.95,
  "HKD": 7.82,
  "HNL": 26.44,
  "HRK": 6.37,
  "HTG": 130.92,
  "HUF": 319.22,
  "IDR": 16880.71,
  "ILS": 3.1,
  "IMP": 0.7374,
  "INR": 90.65,
  "IQD": 1308.3,
  "IRR": 42086,
  "ISK": 122.52,
  "JEP": 0.7374,
  "JMD": 155.86,
  "JOD": 0.709,
  "JPY": 153.68,
  "KES": 129,
  "KGS": 87.45,
  "KHR": 4014.28,
  "KMF": 417,
  "KPW": 900,
  "KRW": 1444.83,
  "KWD": 0.3066,
  "KYD": 0.8324,
  "KZT": 490.2,
  "LAK": 21396.73,
  "LBP": 89420.76,
  "LKR": 309.01,
  "LRD": 185.76,
  "LSL": 16.03,
  "LYD": 6.3,
  "MAD": 9.11,
  "MDL": 17,
  "MGA": 4372.15,
  "MKD": 52.08,
  "MMK": 2099.9,
  "MNT": 3569,
  "MOP": 8.04,
  "MRU": 39.87,
  "MUR": 46,
  "MVR": 15.4,
  "MWK": 1731.89,
  "MXN": 17.13,
  "MYR": 3.9,
  "MZN": 63.9,
  "NAD": 16.03,
  "NGN": 1345.45,
  "NIO": 36.76,
  "NOK": 9.54,
  "NPR": 144.87,
  "NZD": 1.67,
  "OMR": 0.3845,
  "PAB": 1,
  "PEN": 3.34,
  "PGK": 4.29,
  "PHP": 57.84,
  "PKR": 279.3,
  "PLN": 3.57,
  "PYG": 6528.18,
  "QAR": 3.64,
  "RON": 4.31,
  "RSD": 99.22,
  "RUB": 76.4,
  "RWF": 1458.7,
  "SAR": 3.75,
  "SBD": 8.05,
  "SCR": 14.19,
  "SDG": 601.5,
  "SEK": 8.99,
  "SGD": 1.26,
  "SHP": 0.7374,
  "SLE": 24.45,
  "SLL": 20969.5,
  "SOS": 569.83,
  "SRD": 37.7,
  "SSP": 130.26,
  "STD": 22281.8,
  "STN": 20.68,
  "SVC": 8.74,
  "SYP": 13002,
  "SZL": 16.02,
  "THB": 31.28,
  "TJS": 9.45,
  "TMT": 3.51,
  "TND": 2.88,
  "TOP": 2.41,
  "TRY": 43.76,
  "TTD": 6.77,
  "TWD": 31.43,
  "TZS": 2593.4,
  "UAH": 43.22,
  "UGX": 3530.36,
  "USD": 1,
  "UYU": 38.86,
  "UZS": 12175.71,
  "VES": 395.87,
  "VND": 25970,
  "VUV": 120,
  "WST": 2.73,
  "XAF": 554.27,
  "XAG": 0.0132,
  "XAU": 0.0002028,
  "XCD": 2.7,
  "XCG": 1.8,
  "XDR": 0.6887,
  "XOF": 554.27,
  "XPD": 0.0005895,
  "XPF": 100.83,
  "XPT": 0.0004944,
  "YER": 238.37,
  "ZAR": 15.99,
  "ZMW": 18.47,
  "ZWG": 25.77,
  "ZWL": 322
};

  var defaultTimestamp = '2026-02-18T08:00:00.000Z';

  // Load defaults immediately if InstaCalc is available
  if (typeof InstaCalc !== 'undefined' && InstaCalc.loadCurrencies) {
    // Add _rateTimestamp to data so loadCurrencies sets it properly
    const dataWithTimestamp = { ...defaultCurrencies, _rateTimestamp: defaultTimestamp };
    InstaCalc.loadCurrencies(dataWithTimestamp);
    const rateDate = defaultTimestamp.split('T')[0];
    console.log(`✅ [currency-defaults] Loaded 172 default currencies (rates from ${rateDate})`);
  }

  // Also expose for manual loading if needed
  if (typeof window !== 'undefined') {
    window.DEFAULT_CURRENCIES = defaultCurrencies;
    window.DEFAULT_CURRENCY_TIMESTAMP = defaultTimestamp;
  } else if (typeof self !== 'undefined') {
    self.DEFAULT_CURRENCIES = defaultCurrencies;
    self.DEFAULT_CURRENCY_TIMESTAMP = defaultTimestamp;
  }
})();
;
var moo = (function () {
  'use strict';

  var hasOwnProperty = Object.prototype.hasOwnProperty
  var toString = Object.prototype.toString
  var hasSticky = typeof new RegExp().sticky === 'boolean'

  /***************************************************************************/

  function isRegExp(o) { return o && toString.call(o) === '[object RegExp]' }
  function isObject(o) { return o && typeof o === 'object' && !isRegExp(o) && !Array.isArray(o) }

  function reEscape(s) {
    return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
  }
  function reGroups(s) {
    var re = new RegExp('|' + s)
    return re.exec('').length - 1
  }
  function reCapture(s) {
    return '(' + s + ')'
  }
  function reUnion(regexps) {
    if (!regexps.length) return '(?!)'
    var source = regexps.map(function (s) {
      return "(?:" + s + ")"
    }).join('|')
    return "(?:" + source + ")"
  }

  function regexpOrLiteral(obj) {
    if (typeof obj === 'string') {
      return '(?:' + reEscape(obj) + ')'

    } else if (isRegExp(obj)) {
      // TODO: consider /u support
      if (obj.ignoreCase) throw new Error('RegExp /i flag not allowed')
      if (obj.global) throw new Error('RegExp /g flag is implied')
      if (obj.sticky) throw new Error('RegExp /y flag is implied')
      if (obj.multiline) throw new Error('RegExp /m flag is implied')
      return obj.source

    } else {
      throw new Error('Not a pattern: ' + obj)
    }
  }

  function pad(s, length) {
    if (s.length > length) {
      return s
    }
    return Array(length - s.length + 1).join(" ") + s
  }

  function lastNLines(string, numLines) {
    var position = string.length
    var lineBreaks = 0;
    while (true) {
      var idx = string.lastIndexOf("\n", position - 1)
      if (idx === -1) {
        break;
      } else {
        lineBreaks++
      }
      position = idx
      if (lineBreaks === numLines) {
        break;
      }
      if (position === 0) {
        break;
      }
    }
    var startPosition =
      lineBreaks < numLines ?
        0 :
        position + 1
    return string.substring(startPosition).split("\n")
  }

  function objectToRules(object) {
    var keys = Object.getOwnPropertyNames(object)
    var result = []
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i]
      var thing = object[key]
      var rules = [].concat(thing)
      if (key === 'include') {
        for (var j = 0; j < rules.length; j++) {
          result.push({ include: rules[j] })
        }
        continue
      }
      var match = []
      rules.forEach(function (rule) {
        if (isObject(rule)) {
          if (match.length) result.push(ruleOptions(key, match))
          result.push(ruleOptions(key, rule))
          match = []
        } else {
          match.push(rule)
        }
      })
      if (match.length) result.push(ruleOptions(key, match))
    }
    return result
  }

  function arrayToRules(array) {
    var result = []
    for (var i = 0; i < array.length; i++) {
      var obj = array[i]
      if (obj.include) {
        var include = [].concat(obj.include)
        for (var j = 0; j < include.length; j++) {
          result.push({ include: include[j] })
        }
        continue
      }
      if (!obj.type) {
        throw new Error('Rule has no type: ' + JSON.stringify(obj))
      }
      result.push(ruleOptions(obj.type, obj))
    }
    return result
  }

  function ruleOptions(type, obj) {
    if (!isObject(obj)) {
      obj = { match: obj }
    }
    if (obj.include) {
      throw new Error('Matching rules cannot also include states')
    }

    // nb. error and fallback imply lineBreaks
    var options = {
      defaultType: type,
      lineBreaks: !!obj.error || !!obj.fallback,
      pop: false,
      next: null,
      push: null,
      error: false,
      fallback: false,
      value: null,
      type: null,
      shouldThrow: false,
    }

    // Avoid Object.assign(), so we support IE9+
    for (var key in obj) {
      if (hasOwnProperty.call(obj, key)) {
        options[key] = obj[key]
      }
    }

    // type transform cannot be a string
    if (typeof options.type === 'string' && type !== options.type) {
      throw new Error("Type transform cannot be a string (type '" + options.type + "' for token '" + type + "')")
    }

    // convert to array
    var match = options.match
    options.match = Array.isArray(match) ? match : match ? [match] : []
    options.match.sort(function (a, b) {
      return isRegExp(a) && isRegExp(b) ? 0
        : isRegExp(b) ? -1 : isRegExp(a) ? +1 : b.length - a.length
    })
    return options
  }

  function toRules(spec) {
    return Array.isArray(spec) ? arrayToRules(spec) : objectToRules(spec)
  }

  var defaultErrorRule = ruleOptions('error', { lineBreaks: true, shouldThrow: true })
  function compileRules(rules, hasStates) {
    var errorRule = null
    var fast = Object.create(null)
    var fastAllowed = true
    var unicodeFlag = null
    var groups = []
    var parts = []

    // If there is a fallback rule, then disable fast matching
    for (var i = 0; i < rules.length; i++) {
      if (rules[i].fallback) {
        fastAllowed = false
      }
    }

    for (var i = 0; i < rules.length; i++) {
      var options = rules[i]

      if (options.include) {
        // all valid inclusions are removed by states() preprocessor
        throw new Error('Inheritance is not allowed in stateless lexers')
      }

      if (options.error || options.fallback) {
        // errorRule can only be set once
        if (errorRule) {
          if (!options.fallback === !errorRule.fallback) {
            throw new Error("Multiple " + (options.fallback ? "fallback" : "error") + " rules not allowed (for token '" + options.defaultType + "')")
          } else {
            throw new Error("fallback and error are mutually exclusive (for token '" + options.defaultType + "')")
          }
        }
        errorRule = options
      }

      var match = options.match.slice()
      if (fastAllowed) {
        while (match.length && typeof match[0] === 'string' && match[0].length === 1) {
          var word = match.shift()
          fast[word.charCodeAt(0)] = options
        }
      }

      // Warn about inappropriate state-switching options
      if (options.pop || options.push || options.next) {
        if (!hasStates) {
          throw new Error("State-switching options are not allowed in stateless lexers (for token '" + options.defaultType + "')")
        }
        if (options.fallback) {
          throw new Error("State-switching options are not allowed on fallback tokens (for token '" + options.defaultType + "')")
        }
      }

      // Only rules with a .match are included in the RegExp
      if (match.length === 0) {
        continue
      }
      fastAllowed = false

      groups.push(options)

      // Check unicode flag is used everywhere or nowhere
      for (var j = 0; j < match.length; j++) {
        var obj = match[j]
        if (!isRegExp(obj)) {
          continue
        }

        if (unicodeFlag === null) {
          unicodeFlag = obj.unicode
        } else if (unicodeFlag !== obj.unicode && options.fallback === false) {
          throw new Error('If one rule is /u then all must be')
        }
      }

      // convert to RegExp
      var pat = reUnion(match.map(regexpOrLiteral))

      // validate
      var regexp = new RegExp(pat)
      if (regexp.test("")) {
        throw new Error("RegExp matches empty string: " + regexp)
      }
      var groupCount = reGroups(pat)
      if (groupCount > 0) {
        throw new Error("RegExp has capture groups: " + regexp + "\nUse (?: … ) instead")
      }

      // try and detect rules matching newlines
      if (!options.lineBreaks && regexp.test('\n')) {
        throw new Error('Rule should declare lineBreaks: ' + regexp)
      }

      // store regex
      parts.push(reCapture(pat))
    }


    // If there's no fallback rule, use the sticky flag so we only look for
    // matches at the current index.
    //
    // If we don't support the sticky flag, then fake it using an irrefutable
    // match (i.e. an empty pattern).
    var fallbackRule = errorRule && errorRule.fallback
    var flags = hasSticky && !fallbackRule ? 'ym' : 'gm'
    var suffix = hasSticky || fallbackRule ? '' : '|'

    if (unicodeFlag === true) flags += "u"
    var combined = new RegExp(reUnion(parts) + suffix, flags)
    return { regexp: combined, groups: groups, fast: fast, error: errorRule || defaultErrorRule }
  }

  function compile(rules) {
    var result = compileRules(toRules(rules))
    return new Lexer({ start: result }, 'start')
  }

  function checkStateGroup(g, name, map) {
    var state = g && (g.push || g.next)
    if (state && !map[state]) {
      throw new Error("Missing state '" + state + "' (in token '" + g.defaultType + "' of state '" + name + "')")
    }
    if (g && g.pop && +g.pop !== 1) {
      throw new Error("pop must be 1 (in token '" + g.defaultType + "' of state '" + name + "')")
    }
  }
  function compileStates(states, start) {
    var all = states.$all ? toRules(states.$all) : []
    delete states.$all

    var keys = Object.getOwnPropertyNames(states)
    if (!start) start = keys[0]

    var ruleMap = Object.create(null)
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i]
      ruleMap[key] = toRules(states[key]).concat(all)
    }
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i]
      var rules = ruleMap[key]
      var included = Object.create(null)
      for (var j = 0; j < rules.length; j++) {
        var rule = rules[j]
        if (!rule.include) continue
        var splice = [j, 1]
        if (rule.include !== key && !included[rule.include]) {
          included[rule.include] = true
          var newRules = ruleMap[rule.include]
          if (!newRules) {
            throw new Error("Cannot include nonexistent state '" + rule.include + "' (in state '" + key + "')")
          }
          for (var k = 0; k < newRules.length; k++) {
            var newRule = newRules[k]
            if (rules.indexOf(newRule) !== -1) continue
            splice.push(newRule)
          }
        }
        rules.splice.apply(rules, splice)
        j--
      }
    }

    var map = Object.create(null)
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i]
      map[key] = compileRules(ruleMap[key], true)
    }

    for (var i = 0; i < keys.length; i++) {
      var name = keys[i]
      var state = map[name]
      var groups = state.groups
      for (var j = 0; j < groups.length; j++) {
        checkStateGroup(groups[j], name, map)
      }
      var fastKeys = Object.getOwnPropertyNames(state.fast)
      for (var j = 0; j < fastKeys.length; j++) {
        checkStateGroup(state.fast[fastKeys[j]], name, map)
      }
    }

    return new Lexer(map, start)
  }

  function keywordTransform(map) {

    // Use a JavaScript Map to map keywords to their corresponding token type
    // unless Map is unsupported, then fall back to using an Object:
    var isMap = typeof Map !== 'undefined'
    var reverseMap = isMap ? new Map : Object.create(null)

    var types = Object.getOwnPropertyNames(map)
    for (var i = 0; i < types.length; i++) {
      var tokenType = types[i]
      var item = map[tokenType]
      var keywordList = Array.isArray(item) ? item : [item]
      keywordList.forEach(function (keyword) {
        if (typeof keyword !== 'string') {
          throw new Error("keyword must be string (in keyword '" + tokenType + "')")
        }
        if (isMap) {
          reverseMap.set(keyword, tokenType)
        } else {
          reverseMap[keyword] = tokenType
        }
      })
    }
    return function (k) {
      return isMap ? reverseMap.get(k) : reverseMap[k]
    }
  }

  /***************************************************************************/

  var Lexer = function (states, state) {
    this.startState = state
    this.states = states
    this.buffer = ''
    this.stack = []
    this.reset()
  }

  Lexer.prototype.reset = function (data, info) {
    this.buffer = data || ''
    this.index = 0
    this.line = info ? info.line : 1
    this.col = info ? info.col : 1
    this.queuedToken = info ? info.queuedToken : null
    this.queuedText = info ? info.queuedText : "";
    this.queuedThrow = info ? info.queuedThrow : null
    this.setState(info ? info.state : this.startState)
    this.stack = info && info.stack ? info.stack.slice() : []
    return this
  }

  Lexer.prototype.save = function () {
    return {
      line: this.line,
      col: this.col,
      state: this.state,
      stack: this.stack.slice(),
      queuedToken: this.queuedToken,
      queuedText: this.queuedText,
      queuedThrow: this.queuedThrow,
    }
  }

  Lexer.prototype.setState = function (state) {
    if (!state || this.state === state) return
    this.state = state
    var info = this.states[state]
    this.groups = info.groups
    this.error = info.error
    this.re = info.regexp
    this.fast = info.fast
  }

  Lexer.prototype.popState = function () {
    this.setState(this.stack.pop())
  }

  Lexer.prototype.pushState = function (state) {
    this.stack.push(this.state)
    this.setState(state)
  }

  var eat = hasSticky ? function (re, buffer) { // assume re is /y
    return re.exec(buffer)
  } : function (re, buffer) { // assume re is /g
    var match = re.exec(buffer)
    // will always match, since we used the |(?:) trick
    if (match[0].length === 0) {
      return null
    }
    return match
  }

  Lexer.prototype._getGroup = function (match) {
    var groupCount = this.groups.length
    for (var i = 0; i < groupCount; i++) {
      if (match[i + 1] !== undefined) {
        return this.groups[i]
      }
    }
    throw new Error('Cannot find token type for matched text')
  }

  function tokenToString() {
    return this.value
  }

  Lexer.prototype.next = function () {
    var index = this.index

    // If a fallback token matched, we don't need to re-run the RegExp
    if (this.queuedGroup) {
      var token = this._token(this.queuedGroup, this.queuedText, index)
      this.queuedGroup = null
      this.queuedText = ""
      return token
    }

    var buffer = this.buffer
    if (index === buffer.length) {
      return // EOF
    }

    // Fast matching for single characters
    var group = this.fast[buffer.charCodeAt(index)]
    if (group) {
      return this._token(group, buffer.charAt(index), index)
    }

    // Execute RegExp
    var re = this.re
    re.lastIndex = index
    var match = eat(re, buffer)

    // Error tokens match the remaining buffer
    var error = this.error
    if (match == null) {
      return this._token(error, buffer.slice(index, buffer.length), index)
    }

    var group = this._getGroup(match)
    var text = match[0]

    if (error.fallback && match.index !== index) {
      this.queuedGroup = group
      this.queuedText = text

      // Fallback tokens contain the unmatched portion of the buffer
      return this._token(error, buffer.slice(index, match.index), index)
    }

    return this._token(group, text, index)
  }

  Lexer.prototype._token = function (group, text, offset) {
    // count line breaks
    var lineBreaks = 0
    if (group.lineBreaks) {
      var matchNL = /\n/g
      var nl = 1
      if (text === '\n') {
        lineBreaks = 1
      } else {
        while (matchNL.exec(text)) { lineBreaks++; nl = matchNL.lastIndex }
      }
    }

    var token = {
      type: (typeof group.type === 'function' && group.type(text)) || group.defaultType,
      value: typeof group.value === 'function' ? group.value(text) : text,
      text: text,
      toString: tokenToString,
      offset: offset,
      lineBreaks: lineBreaks,
      line: this.line,
      col: this.col,
    }
    // nb. adding more props to token object will make V8 sad!

    var size = text.length
    this.index += size
    this.line += lineBreaks
    if (lineBreaks !== 0) {
      this.col = size - nl + 1
    } else {
      this.col += size
    }

    // throw, if no rule with {error: true}
    if (group.shouldThrow) {
      var err = new Error(this.formatError(token, "invalid syntax"))
      throw err;
    }

    if (group.pop) this.popState()
    else if (group.push) this.pushState(group.push)
    else if (group.next) this.setState(group.next)

    return token
  }

  if (typeof Symbol !== 'undefined' && Symbol.iterator) {
    var LexerIterator = function (lexer) {
      this.lexer = lexer
    }

    LexerIterator.prototype.next = function () {
      var token = this.lexer.next()
      return { value: token, done: !token }
    }

    LexerIterator.prototype[Symbol.iterator] = function () {
      return this
    }

    Lexer.prototype[Symbol.iterator] = function () {
      return new LexerIterator(this)
    }
  }

  Lexer.prototype.formatError = function (token, message) {
    if (token == null) {
      // An undefined token indicates EOF
      var text = this.buffer.slice(this.index)
      var token = {
        text: text,
        offset: this.index,
        lineBreaks: text.indexOf('\n') === -1 ? 0 : 1,
        line: this.line,
        col: this.col,
      }
    }

    var numLinesAround = 2
    var firstDisplayedLine = Math.max(token.line - numLinesAround, 1)
    var lastDisplayedLine = token.line + numLinesAround
    var lastLineDigits = String(lastDisplayedLine).length
    var displayedLines = lastNLines(
      this.buffer,
      (this.line - token.line) + numLinesAround + 1
    )
      .slice(0, 5)
    var errorLines = []
    errorLines.push(message + " at line " + token.line + " col " + token.col + ":")
    errorLines.push("")
    for (var i = 0; i < displayedLines.length; i++) {
      var line = displayedLines[i]
      var lineNo = firstDisplayedLine + i
      errorLines.push(pad(String(lineNo), lastLineDigits) + "  " + line);
      if (lineNo === token.line) {
        errorLines.push(pad("", lastLineDigits + token.col + 1) + "^")
      }
    }
    return errorLines.join("\n")
  }

  Lexer.prototype.clone = function () {
    return new Lexer(this.states, this.state)
  }

  Lexer.prototype.has = function (tokenType) {
    return true
  }


  return {
    compile: compile,
    states: compileStates,
    error: Object.freeze({ error: true }),
    fallback: Object.freeze({ fallback: true }),
    keywords: keywordTransform,
  }

}());

if (typeof window !== 'undefined') {
  window.moo = moo;
}

if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
  module.exports = moo;
}

if (typeof window !== 'undefined') {
  window.moo = moo;
}

if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
  module.exports = moo;
}

if (typeof window !== 'undefined') {
  window.moo = moo;
}

if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
  module.exports = moo;
}
;
/**
 * Minified by jsDelivr using Terser v5.3.5.
 * Original file: /npm/nearley@2.20.1/lib/nearley.js
 *
 * Do NOT use SRI with dynamically generated files! More information: https://www.jsdelivr.com/using-sri-with-dynamic-files
 */
!function (t, e) { "object" == typeof module && module.exports ? module.exports = e() : t.nearley = e() }(this || window, (function () { function t(e, r, i) { return this.id = ++t.highestId, this.name = e, this.symbols = r, this.postprocess = i, this } function e(t, e, r, i) { this.rule = t, this.dot = e, this.reference = r, this.data = [], this.wantedBy = i, this.isComplete = this.dot === t.symbols.length } function r(t, e) { this.grammar = t, this.index = e, this.states = [], this.wants = {}, this.scannable = [], this.completed = {} } function i(t, e) { this.rules = t, this.start = e || this.rules[0].name; var r = this.byName = {}; this.rules.forEach((function (t) { r.hasOwnProperty(t.name) || (r[t.name] = []), r[t.name].push(t) })) } function n() { this.reset("") } function s(t, e, s) { if (t instanceof i) { var o = t; s = e } else o = i.fromCompiled(t, e); for (var a in this.grammar = o, this.options = { keepHistory: !1, lexer: o.lexer || new n }, s || {}) this.options[a] = s[a]; this.lexer = this.options.lexer, this.lexerState = void 0; var h = new r(o, 0); this.table = [h]; h.wants[o.start] = [], h.predict(o.start), h.process(), this.current = 0 } function o(t) { var e = typeof t; if ("string" === e) return t; if ("object" === e) { if (t.literal) return JSON.stringify(t.literal); if (t instanceof RegExp) return t.toString(); if (t.type) return "%" + t.type; if (t.test) return "<" + String(t.test) + ">"; throw new Error("Unknown symbol type: " + t) } } return t.highestId = 0, t.prototype.toString = function (t) { var e = void 0 === t ? this.symbols.map(o).join(" ") : this.symbols.slice(0, t).map(o).join(" ") + " ● " + this.symbols.slice(t).map(o).join(" "); return this.name + " → " + e }, e.prototype.toString = function () { return "{" + this.rule.toString(this.dot) + "}, from: " + (this.reference || 0) }, e.prototype.nextState = function (t) { var r = new e(this.rule, this.dot + 1, this.reference, this.wantedBy); return r.left = this, r.right = t, r.isComplete && (r.data = r.build(), r.right = void 0), r }, e.prototype.build = function () { var t = [], e = this; do { t.push(e.right.data), e = e.left } while (e.left); return t.reverse(), t }, e.prototype.finish = function () { this.rule.postprocess && (this.data = this.rule.postprocess(this.data, this.reference, s.fail)) }, r.prototype.process = function (t) { for (var e = this.states, r = this.wants, i = this.completed, n = 0; n < e.length; n++) { var o = e[n]; if (o.isComplete) { if (o.finish(), o.data !== s.fail) { for (var a = o.wantedBy, h = a.length; h--;) { var l = a[h]; this.complete(l, o) } if (o.reference === this.index) { var p = o.rule.name; (this.completed[p] = this.completed[p] || []).push(o) } } } else { if ("string" != typeof (p = o.rule.symbols[o.dot])) { this.scannable.push(o); continue } if (r[p]) { if (r[p].push(o), i.hasOwnProperty(p)) { var u = i[p]; for (h = 0; h < u.length; h++) { var f = u[h]; this.complete(o, f) } } } else r[p] = [o], this.predict(p) } } }, r.prototype.predict = function (t) { for (var r = this.grammar.byName[t] || [], i = 0; i < r.length; i++) { var n = r[i], s = this.wants[t], o = new e(n, 0, this.index, s); this.states.push(o) } }, r.prototype.complete = function (t, e) { var r = t.nextState(e); this.states.push(r) }, i.fromCompiled = function (e, r) { var n = e.Lexer; e.ParserStart && (r = e.ParserStart, e = e.ParserRules); var s = new i(e = e.map((function (e) { return new t(e.name, e.symbols, e.postprocess) })), r); return s.lexer = n, s }, n.prototype.reset = function (t, e) { this.buffer = t, this.index = 0, this.line = e ? e.line : 1, this.lastLineBreak = e ? -e.col : 0 }, n.prototype.next = function () { if (this.index < this.buffer.length) { var t = this.buffer[this.index++]; return "\n" === t && (this.line += 1, this.lastLineBreak = this.index), { value: t } } }, n.prototype.save = function () { return { line: this.line, col: this.index - this.lastLineBreak } }, n.prototype.formatError = function (t, e) { var r = this.buffer; if ("string" == typeof r) { var i = r.split("\n").slice(Math.max(0, this.line - 5), this.line), n = r.indexOf("\n", this.index); -1 === n && (n = r.length); var s = this.index - this.lastLineBreak, o = String(this.line).length; return e += " at line " + this.line + " col " + s + ":\n\n", e += i.map((function (t, e) { return a(this.line - i.length + e + 1, o) + " " + t }), this).join("\n"), e += "\n" + a("", o + s) + "^\n" } return e + " at index " + (this.index - 1); function a(t, e) { var r = String(t); return Array(e - r.length + 1).join(" ") + r } }, s.fail = {}, s.prototype.feed = function (t) { var e, i = this.lexer; for (i.reset(t, this.lexerState); ;) { try { if (!(e = i.next())) break } catch (t) { var s = new r(this.grammar, this.current + 1); throw this.table.push(s), (h = new Error(this.reportLexerError(t))).offset = this.current, h.token = t.token, h } var o = this.table[this.current]; this.options.keepHistory || delete this.table[this.current - 1]; var a = this.current + 1; s = new r(this.grammar, a); this.table.push(s); for (var h, l = void 0 !== e.text ? e.text : e.value, p = i.constructor === n ? e.value : e, u = o.scannable, f = u.length; f--;) { var c = u[f], y = c.rule.symbols[c.dot]; if (y.test ? y.test(p) : y.type ? y.type === e.type : y.literal === l) { var m = c.nextState({ data: p, token: e, isToken: !0, reference: a - 1 }); s.states.push(m) } } if (s.process(), 0 === s.states.length) throw (h = new Error(this.reportError(e))).offset = this.current, h.token = e, h; this.options.keepHistory && (o.lexerState = i.save()), this.current++ } return o && (this.lexerState = i.save()), this.results = this.finish(), this }, s.prototype.reportLexerError = function (t) { var e, r, i = t.token; return i ? (e = "input " + JSON.stringify(i.text[0]) + " (lexer error)", r = this.lexer.formatError(i, "Syntax error")) : (e = "input (lexer error)", r = t.message), this.reportErrorCommon(r, e) }, s.prototype.reportError = function (t) { var e = (t.type ? t.type + " token: " : "") + JSON.stringify(void 0 !== t.value ? t.value : t), r = this.lexer.formatError(t, "Syntax error"); return this.reportErrorCommon(r, e) }, s.prototype.reportErrorCommon = function (t, e) { var r = []; r.push(t); var i = this.table.length - 2, n = this.table[i], s = n.states.filter((function (t) { var e = t.rule.symbols[t.dot]; return e && "string" != typeof e })); 0 === s.length ? (r.push("Unexpected " + e + ". I did not expect any more input. Here is the state of my parse table:\n"), this.displayStateStack(n.states, r)) : (r.push("Unexpected " + e + ". Instead, I was expecting to see one of the following:\n"), s.map((function (t) { return this.buildFirstStateStack(t, []) || [t] }), this).forEach((function (t) { var e = t[0], i = e.rule.symbols[e.dot], n = this.getSymbolDisplay(i); r.push("A " + n + " based on:"), this.displayStateStack(t, r) }), this)); return r.push(""), r.join("\n") }, s.prototype.displayStateStack = function (t, e) { for (var r, i = 0, n = 0; n < t.length; n++) { var s = t[n], o = s.rule.toString(s.dot); o === r ? i++ : (i > 0 && e.push("    ^ " + i + " more lines identical to this"), i = 0, e.push("    " + o)), r = o } }, s.prototype.getSymbolDisplay = function (t) { return function (t) { var e = typeof t; if ("string" === e) return t; if ("object" === e) { if (t.literal) return JSON.stringify(t.literal); if (t instanceof RegExp) return "character matching " + t; if (t.type) return t.type + " token"; if (t.test) return "token matching " + String(t.test); throw new Error("Unknown symbol type: " + t) } }(t) }, s.prototype.buildFirstStateStack = function (t, e) { if (-1 !== e.indexOf(t)) return null; if (0 === t.wantedBy.length) return [t]; var r = t.wantedBy[0], i = [t].concat(e), n = this.buildFirstStateStack(r, i); return null === n ? null : [t].concat(n) }, s.prototype.save = function () { var t = this.table[this.current]; return t.lexerState = this.lexerState, t }, s.prototype.restore = function (t) { var e = t.index; this.current = e, this.table[e] = t, this.table.splice(e + 1), this.lexerState = t.lexerState, this.results = this.finish() }, s.prototype.rewind = function (t) { if (!this.options.keepHistory) throw new Error("set option `keepHistory` to enable rewinding"); this.restore(this.table[t]) }, s.prototype.finish = function () { var t = [], e = this.grammar.start; return this.table[this.table.length - 1].states.forEach((function (r) { r.rule.name === e && r.dot === r.rule.symbols.length && 0 === r.reference && r.data !== s.fail && t.push(r) })), t.map((function (t) { return t.data })) }, { Parser: s, Grammar: i, Rule: t } }));
//# sourceMappingURL=/sm/dcbcaf46b820f08097a26578b7032019ac981a93bc4d02b191e641de97e3bd20.map;
/**
 * SINGLE SOURCE OF TRUTH FOR KEYWORDS AND FIAT CURRENCIES
 *
 * This module defines all keywords and fiat currency codes used by Instacalc.
 * Both the grammar (ic.ne) and parser CONFIG import from here.
 *
 * DO NOT define keywords elsewhere - import from this file!
 *
 * FIAT CURRENCIES: The FIAT_CURRENCY_CODES set contains ISO 4217 currency codes.
 * These ALWAYS take precedence over stock tickers. For example, "USD" is ProShares
 * Ultra Semiconductors on the stock market, but we ALWAYS treat it as US Dollars.
 *
 * Loaded as a global script before grammar and parser.
 */

(function () {
  'use strict';

  /**
   * GRAMMAR_KEYWORDS: Keywords the nearley grammar/lexer needs to recognize
   * These become specific token types in the lexer.
   */
  const GRAMMAR_KEYWORDS = {
    // Boolean/logical operators
    and: true,
    or: true,
    not: true,
    xor: true,

    // Math operators
    mod: true,

    // Control flow
    if: true,
    else: true,

    // Special tokens
    off: true,     // "10% off"
    of: true,      // "10% of 50"
    from: true,    // "3 from 10"
    function: true, // function definitions
  };

  /**
   * GRAMMAR_LITERALS: Literal values that are keywords at the grammar level
   * These are converted to their values, not used as identifiers
   */
  const GRAMMAR_LITERALS = {
    null: true,
    true: true,
    false: true,
  };

  /**
   * PREPROCESSOR_KEYWORDS: Keywords the preprocessor transforms before grammar parsing
   * e.g., "10 cm in inches" → "10 cm -> inches"
   */
  const PREPROCESSOR_KEYWORDS = {
    // Conversion keywords (transformed to ->)
    in: true,
    into: true,
    to: true,
    as: true,
    en: true,  // Spanish "en"

    // Rate/division keywords (transformed to /)
    per: true,
    every: true,
    until: true,

    // Context keywords
    using: true,
    with: true,
    at: true,
  };

  /**
   * ALL_KEYWORDS: Union of all keywords for user override warnings
   * When user defines "and = 5", we warn them they're overriding a keyword
   */
  const ALL_KEYWORDS = {
    ...GRAMMAR_KEYWORDS,
    ...PREPROCESSOR_KEYWORDS,
    // Note: GRAMMAR_LITERALS intentionally excluded - can't override null/true/false anyway
  };

  /**
   * GRAMMAR_KEYWORDS_SET: Set for fast lexer lookup
   * Includes literals since lexer needs to recognize them as keywords
   */
  const GRAMMAR_KEYWORDS_SET = new Set([
    ...Object.keys(GRAMMAR_KEYWORDS),
    ...Object.keys(GRAMMAR_LITERALS),
  ]);

  /**
   * FAMOUS_STOCK_TICKERS: Top ~100 tickers that auto-detect without $ prefix
   *
   * SOURCE OF TRUTH: /public/data/famous-tickers.json
   * Keep this list in sync with that file. The JSON file is used by
   * the backend (functions/index.js preloadStocks) to avoid duplication.
   *
   * These are the most commonly owned stocks/ETFs by retail investors.
   * All other tickers require explicit $TICKER syntax to avoid false positives (e.g., "asd" shouldn't trigger stock lookup).
   *
   * Criteria for inclusion:
   * - S&P 500 mega caps (AAPL, MSFT, GOOGL, etc.)
   * - Major ETFs (SPY, QQQ, VTI, VOO, etc.)
   * - Popular meme stocks / retail favorites
   * - Major index funds and sector ETFs
   *
   * NOTE: Tickers in STOCK_BLOCKLIST are excluded even if they appear here.
   */
  const FAMOUS_STOCK_TICKERS = new Set([
    // Magnificent 7 + FAANG
    'AAPL', 'MSFT', 'GOOGL', 'GOOG', 'AMZN', 'NVDA', 'TSLA', 'META', 'NFLX',

    // Major Tech
    'ORCL', 'ADBE', 'CRM', 'INTC', 'AMD', 'QCOM', 'AVGO', 'CSCO', 'ASML',

    // Major Indices ETFs
    'SPY', 'QQQ', 'IWM', 'DIA', 'VOO', 'VTI', 'VEA', 'VWO', 'EEM', 'AGG',

    // Sector ETFs
    'XLF', 'XLE', 'XLK', 'XLV', 'XLI', 'XLP', 'XLY', 'XLU', 'XLRE', 'XLC',

    // Major Financial (3+ letters only - use $V, $C, $GS, $MS, $MA for shorter ones)
    'BRK.A', 'BRK.B', 'JPM', 'BAC', 'WFC', 'AXP',

    // Major Healthcare
    'JNJ', 'UNH', 'PFE', 'ABBV', 'MRK', 'TMO', 'ABT', 'LLY', 'DHR',

    // Major Consumer (3+ letters only - use $HD, $PG, $KO for shorter ones)
    'WMT', 'PEP', 'COST', 'NKE', 'MCD', 'SBUX', 'DIS',

    // Major Industrial (3+ letters only - use $BA, $GE for shorter ones)
    'CAT', 'MMM', 'HON', 'UPS', 'RTX', 'LMT',

    // Major Energy
    'XOM', 'CVX', 'COP', 'SLB', 'EOG',

    // Popular Retail / Meme Stocks (3+ letters only - use $BB for BlackBerry)
    'GME', 'AMC', 'NOK', 'PLTR', 'COIN', 'HOOD', 'SOFI',

    // Crypto-related
    'MSTR', 'RIOT', 'MARA',

    // ARK Funds
    'ARKK', 'ARKQ', 'ARKW', 'ARKG', 'ARKF',

    // Bond ETFs
    'TLT', 'IEF', 'SHY', 'LQD', 'HYG', 'MUB',

    // Commodity ETFs
    'GLD', 'SLV', 'USO', 'UNG', 'DBA',
  ]);

  /**
   * FIAT_CURRENCY_CODES: ISO 4217 currency codes that ALWAYS take precedence over stocks
   *
   * Some fiat currency codes are also stock tickers (e.g., USD = ProShares Ultra Semiconductors).
   * This list ensures fiat currencies are NEVER treated as stocks, regardless of loading order.
   *
   * This is the SINGLE SOURCE OF TRUTH for fiat currency identification.
   * Check this BEFORE checking if something is a stock ticker.
   */
  const FIAT_CURRENCY_CODES = new Set([
    // Major world currencies (most likely to conflict with tickers)
    'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'CNY', 'HKD',
    'SGD', 'SEK', 'NOK', 'DKK', 'KRW', 'INR', 'RUB', 'BRL', 'MXN', 'ZAR',
    'TRY', 'PLN', 'THB', 'IDR', 'MYR', 'PHP', 'CZK', 'ILS', 'CLP', 'TWD',
    'ARS', 'COP', 'PEN', 'VND', 'UAH', 'EGP', 'PKR', 'BDT', 'NGN', 'KES',

    // European currencies
    'RON', 'HUF', 'BGN', 'HRK', 'ISK', 'ALL', 'MKD', 'RSD', 'BAM', 'MDL',
    'GEL', 'AMD', 'AZN', 'BYN',

    // Middle East & North Africa
    'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD', 'LBP', 'IQD', 'SYP',
    'YER', 'MAD', 'DZD', 'TND', 'LYD',

    // Africa
    'GHS', 'TZS', 'UGX', 'ZMW', 'BWP', 'MUR', 'NAD', 'SZL', 'LSL', 'MWK',
    'RWF', 'ETB', 'XOF', 'XAF', 'CDF', 'AOA', 'MZN', 'SCR', 'GMD', 'CVE',

    // Asia & Pacific
    'LKR', 'NPR', 'MMK', 'KHR', 'LAK', 'BND', 'MOP', 'FJD', 'PGK', 'WST',
    'TOP', 'VUV', 'SBD', 'MVR', 'BTN', 'KZT', 'UZS', 'TJS', 'KGS', 'TMT',
    'MNT', 'AFN',

    // Americas
    'GTQ', 'HNL', 'NIO', 'CRC', 'PAB', 'DOP', 'CUP', 'JMD', 'HTG', 'TTD',
    'BBD', 'BSD', 'BZD', 'GYD', 'SRD', 'AWG', 'ANG', 'XCD', 'BMD', 'KYD',
    'BOB', 'PYG', 'UYU', 'VES',

    // Precious metals (often used as currency)
    'XAU', 'XAG', 'XPT', 'XPD',

    // Special/supranational
    'SDR', 'XDR',
  ]);

  /**
   * MAJOR_CURRENCIES: A subset of fiat currencies that are "major" enough
   * that we assume the user means the currency, even if it conflicts with a stock ticker.
   *
   * For non-major currencies (e.g., AMD = Armenian Dram), if it's also a valid stock ticker,
   * we show a helpful tip: "Tip: AMD is currency; use $AMD for stock".
   */
  const MAJOR_CURRENCIES = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'NZD']);

  /**
   * CURRENCY_NAMES: Map of currency codes to their full names for display
   */
  const CURRENCY_NAMES = {
    'USD': 'US Dollar', 'EUR': 'Euro', 'GBP': 'British Pound', 'JPY': 'Japanese Yen', 'CHF': 'Swiss Franc',
    'CAD': 'Canadian Dollar', 'AUD': 'Australian Dollar', 'NZD': 'New Zealand Dollar', 'CNY': 'Chinese Yuan',
    'HKD': 'Hong Kong Dollar', 'SGD': 'Singapore Dollar', 'SEK': 'Swedish Krona', 'NOK': 'Norwegian Krone',
    'DKK': 'Danish Krone', 'KRW': 'South Korean Won', 'INR': 'Indian Rupee', 'RUB': 'Russian Ruble',
    'BRL': 'Brazilian Real', 'MXN': 'Mexican Peso', 'ZAR': 'South African Rand', 'TRY': 'Turkish Lira',
    'PLN': 'Polish Zloty', 'THB': 'Thai Baht', 'IDR': 'Indonesian Rupiah', 'MYR': 'Malaysian Ringgit',
    'PHP': 'Philippine Peso', 'CZK': 'Czech Koruna', 'ILS': 'Israeli New Shekel', 'CLP': 'Chilean Peso',
    'TWD': 'New Taiwan Dollar', 'ARS': 'Argentine Peso', 'COP': 'Colombian Peso', 'PEN': 'Peruvian Sol',
    'VND': 'Vietnamese Dong', 'UAH': 'Ukrainian Hryvnia', 'EGP': 'Egyptian Pound', 'PKR': 'Pakistani Rupee',
    'BDT': 'Bangladeshi Taka', 'NGN': 'Nigerian Naira', 'KES': 'Kenyan Shilling', 'RON': 'Romanian Leu',
    'HUF': 'Hungarian Forint', 'BGN': 'Bulgarian Lev', 'HRK': 'Croatian Kuna', 'ISK': 'Icelandic Krona',
    'ALL': 'Albanian Lek', 'MKD': 'Macedonian Denar', 'RSD': 'Serbian Dinar', 'BAM': 'Bosnian Convertible Mark',
    'MDL': 'Moldovan Leu', 'GEL': 'Georgian Lari', 'AMD': 'Armenian Dram', 'AZN': 'Azerbaijani Manat',
    'BYN': 'Belarusian Ruble', 'AED': 'UAE Dirham', 'SAR': 'Saudi Riyal', 'QAR': 'Qatari Riyal',
    'KWD': 'Kuwaiti Dinar', 'BHD': 'Bahraini Dinar', 'OMR': 'Omani Rial', 'JOD': 'Jordanian Dinar',
    'LBP': 'Lebanese Pound', 'IQD': 'Iraqi Dinar', 'SYP': 'Syrian Pound', 'YER': 'Yemeni Rial',
    'MAD': 'Moroccan Dirham', 'DZD': 'Algerian Dinar', 'TND': 'Tunisian Dinar', 'LYD': 'Libyan Dinar',
    'GHS': 'Ghanaian Cedi', 'TZS': 'Tanzanian Shilling', 'UGX': 'Ugandan Shilling', 'ZMW': 'Zambian Kwacha',
    'BWP': 'Botswana Pula', 'MUR': 'Mauritian Rupee', 'NAD': 'Namibian Dollar', 'SZL': 'Swazi Lilangeni',
    'LSL': 'Lesotho Loti', 'MWK': 'Malawian Kwacha', 'RWF': 'Rwandan Franc', 'ETB': 'Ethiopian Birr',
    'XOF': 'West African CFA Franc', 'XAF': 'Central African CFA Franc', 'CDF': 'Congolese Franc',
    'AOA': 'Angolan Kwanza', 'MZN': 'Mozambican Metical', 'SCR': 'Seychellois Rupee', 'GMD': 'Gambian Dalasi',
    'CVE': 'Cape Verdean Escudo', 'LKR': 'Sri Lankan Rupee', 'NPR': 'Nepalese Rupee', 'MMK': 'Myanmar Kyat',
    'KHR': 'Cambodian Riel', 'LAK': 'Lao Kip', 'BND': 'Brunei Dollar', 'MOP': 'Macanese Pataca',
    'FJD': 'Fijian Dollar', 'PGK': 'Papua New Guinean Kina', 'WST': 'Samoan Tala', 'TOP': "Tongan Pa'anga",
    'VUV': 'Vanuatu Vatu', 'SBD': 'Solomon Islands Dollar', 'MVR': 'Maldivian Rufiyaa', 'BTN': 'Bhutanese Ngultrum',
    'KZT': 'Kazakhstani Tenge', 'UZS': 'Uzbekistani Som', 'TJS': 'Tajikistani Somoni', 'KGS': 'Kyrgyzstani Som',
    'TMT': 'Turkmenistani Manat', 'MNT': 'Mongolian Tugrik', 'AFN': 'Afghan Afghani', 'GTQ': 'Guatemalan Quetzal',
    'HNL': 'Honduran Lempira', 'NIO': 'Nicaraguan Cordoba', 'CRC': 'Costa Rican Colon', 'PAB': 'Panamanian Balboa',
    'DOP': 'Dominican Peso', 'CUP': 'Cuban Peso', 'JMD': 'Jamaican Dollar', 'HTG': 'Haitian Gourde',
    'TTD': 'Trinidad and Tobago Dollar', 'BBD': 'Barbadian Dollar', 'BSD': 'Bahamian Dollar',
    'BZD': 'Belize Dollar', 'GYD': 'Guyanese Dollar', 'SRD': 'Surinamese Dollar', 'AWG': 'Aruban Florin',
    'ANG': 'Netherlands Antillean Guilder', 'XCD': 'East Caribbean Dollar', 'BMD': 'Bermudian Dollar',
    'KYD': 'Cayman Islands Dollar', 'BOB': 'Bolivian Boliviano', 'PYG': 'Paraguayan Guarani',
    'UYU': 'Uruguayan Peso', 'VES': 'Venezuelan Bolivar', 'XAU': 'Gold', 'XAG': 'Silver',
    'XPT': 'Platinum', 'XPD': 'Palladium', 'SDR': 'Special Drawing Rights', 'XDR': 'Special Drawing Rights'
  };

  /**
   * isFiatCurrency: Check if a symbol is a fiat currency code
   * @param {string} symbol - The symbol to check (case-insensitive)
   * @returns {boolean} - True if it's a fiat currency
   */
  function isFiatCurrency(symbol) {
    if (!symbol || typeof symbol !== 'string') return false;
    return FIAT_CURRENCY_CODES.has(symbol.toUpperCase());
  }

  /**
   * classifySymbol: SINGLE SOURCE OF TRUTH for symbol resolution.
   *
   * Determines what a symbol represents: variable, function, constant, fiat, stock, or unknown.
   * This centralizes ALL symbol classification logic that was previously scattered.
   *
   * Priority order:
   * 1. User-defined variables (always highest)
   * 2. Predefined functions (SIN, COS, LOG)
   * 3. Predefined constants (PI, TAU, E)
   * 4. Fiat vs Stock (depends on explicitStock flag)
   *    - Normal (no $): fiat wins over stock
   *    - Explicit ($SYMBOL): stock wins over fiat
   *
   * @param {string} symbol - The symbol to classify (case-insensitive for fiat/stock)
   * @param {Object} options
   * @param {boolean} options.explicitStock - True if user used $SYMBOL syntax (stock takes priority over fiat)
   * @param {Object} options.stockCache - Stock cache (window.Action?.fn?.stock_cache)
   * @param {Object} options.externalDataCache - ExternalDataCache instance
   * @param {Object} options.IC - IC parser object (for envIsVariableDefined, predefinedFunction, etc.)
   * @returns {{ type: string, symbol: string, loaded?: boolean, price?: number, ambiguous?: object }}
   *   - ambiguous: { isFiat: bool, isStock: bool, fiatName: string } if symbol could be both
   */
  function classifySymbol(symbol, options = {}) {
    if (!symbol || typeof symbol !== 'string') {
      return { type: 'unknown', symbol: '' };
    }

    const { explicitStock = false, stockCache, externalDataCache, IC } = options;
    const upper = symbol.toUpperCase();

    // 1. User-defined variables ALWAYS win
    if (IC?.envIsVariableDefined?.(symbol)) {
      return { type: 'variable', symbol };
    }

    // 2. Predefined functions (SIN, COS, LOG)
    if (IC?.predefinedFunction?.(symbol)) {
      return { type: 'function', symbol };
    }

    // 3. Predefined constants (PI, TAU, E)
    if (IC?.predefinedIdentifier?.(symbol)) {
      return { type: 'constant', symbol };
    }

    // 4. Fiat vs Stock resolution

    // Check if symbol has STOCK_ prefix (from $TICKER preprocessing: $DD → STOCK_DD)
    // This indicates explicit stock request even without explicitStock option
    const hasStockPrefix = upper.startsWith('STOCK_');
    const isExplicit = explicitStock || hasStockPrefix;
    const lookupSymbol = hasStockPrefix ? upper.substring(6).replace(/_/g, '.') : upper;

    // Check fiat/famous status on the actual ticker symbol (not STOCK_ prefixed version)
    const isFiat = FIAT_CURRENCY_CODES.has(lookupSymbol);
    const isFamousTicker = FAMOUS_STOCK_TICKERS.has(lookupSymbol);

    // For cached stocks: only consider as stock if explicit OR famous
    // This prevents cached DD from auto-detecting when user types "dd"
    const stockInCache = stockCache?.[lookupSymbol]?.loaded && stockCache[lookupSymbol].price;
    const stockLoaded = stockInCache && (isExplicit || isFamousTicker);

    const stockPossible = externalDataCache?.isPossibleStock?.(upper);
    // Fallback: check isFamousTicker directly when externalDataCache unavailable (e.g., tests)
    const isFamous = !externalDataCache && isFamousTicker;
    const isKnownStock = stockLoaded || stockPossible || isFamous;

    // Check for ambiguity: symbol is BOTH a fiat currency AND a possible/known stock
    // Only flag as ambiguous if not a major currency (USD, EUR, etc.)
    const isMajor = MAJOR_CURRENCIES.has(lookupSymbol);
    const isAmbiguous = isFiat && !isMajor;  // Minor fiat codes that look like tickers
    const fiatName = isAmbiguous ? (CURRENCY_NAMES[lookupSymbol] || 'currency') : null;

    if (isExplicit) {
      // $SYMBOL syntax: user explicitly wants stock
      // Any symbol that passed the variable/function/constant checks above is treated as stock
      // This includes fiat currency codes (user explicitly overrode with $)
      const result = stockInCache
        ? { type: 'stock', symbol: lookupSymbol, loaded: true, price: stockCache[lookupSymbol].price }
        : { type: 'stock', symbol: lookupSymbol, loaded: false };

      // Add ambiguity info if this fiat code was overridden to stock
      if (isAmbiguous) {
        result.ambiguous = { isFiat: true, isStock: true, fiatName, overrideUsed: '$' };
      }
      return result;
    } else {
      // Normal syntax: fiat wins over stock
      if (isFiat) {
        const result = { type: 'fiat', symbol: lookupSymbol };
        // Add ambiguity info if there's also a stock with this symbol
        if (isAmbiguous && (isKnownStock || stockPossible)) {
          result.ambiguous = { isFiat: true, isStock: true, fiatName, hint: `$${lookupSymbol}` };
        }
        return result;
      }
      if (isKnownStock) {
        return stockLoaded
          ? { type: 'stock', symbol: lookupSymbol, loaded: true, price: stockCache[lookupSymbol].price }
          : { type: 'stock', symbol: lookupSymbol, loaded: false };
      }
    }

    return { type: 'unknown', symbol: lookupSymbol };
  }

  /**
   * isFamousTicker: Check if symbol is a famous ticker that auto-detects without $ prefix
   * @param {string} symbol - The symbol to check
   * @returns {boolean}
   */
  function isFamousTicker(symbol) {
    if (!symbol || typeof symbol !== 'string') return false;
    return FAMOUS_STOCK_TICKERS.has(symbol.toUpperCase());
  }

  /**
   * getStockInfo: Convenience wrapper for classifySymbol when you only care about stocks.
   * Returns stock info if symbol is a stock, null otherwise (including fiat).
   *
   * @deprecated Use classifySymbol directly for new code
   */
  function getStockInfo(symbol, stockCache, externalDataCache) {
    const result = classifySymbol(symbol, { stockCache, externalDataCache });
    if (result.type === 'stock') {
      return { loaded: result.loaded, price: result.price, symbol: result.symbol };
    }
    return null;
  }

  // Single export object for all environments
  const exports = {
    GRAMMAR_KEYWORDS,
    GRAMMAR_LITERALS,
    PREPROCESSOR_KEYWORDS,
    ALL_KEYWORDS,
    GRAMMAR_KEYWORDS_SET,
    FIAT_CURRENCY_CODES,
    MAJOR_CURRENCIES,
    CURRENCY_NAMES,
    FAMOUS_STOCK_TICKERS,
    isFiatCurrency,
    isFamousTicker,
    classifySymbol,
    getStockInfo,  // Convenience wrapper, use classifySymbol for new code
  };

  // UMD export - works in browser, Node.js, CommonJS, and Web Workers
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = exports;
  }
  if (typeof window !== 'undefined') {
    window.IC_KEYWORDS = exports;
  } else if (typeof self !== 'undefined') {
    // Web Worker context (self is the worker global)
    self.IC_KEYWORDS = exports;
  } else if (typeof global !== 'undefined') {
    global.IC_KEYWORDS = exports;
  }
})();
;
// Generated automatically by nearley, version 2.20.1
// http://github.com/Hardmath123/nearley
(function () {
function id(x) { return x[0]; }

if (typeof moo === "undefined") {
    const moo = require("moo");
}

// turn a template interpolated string into a regex
function reg(strings, ...pieces) {
  return new RegExp(strings.raw.map((r, i) => r + (pieces[i] === undefined ? '' : pieces[i].source)).join(''), "u")
}

// note: moo requires non-capturing groups, (?:)
const ID_START = /(?:\p{Letter}|[_]|\\u[a-fA-F0-9]{4})/u;
const ID_REST = reg`(?:${ID_START}|[0-9]|['".])`;
const ID_FINAL = reg`${ID_START}(?:\s*${ID_REST})*`;

const DECIMAL_DIGITS = /[0-9]+/u;
const SIGNED_INTEGER = /[+-]?[0-9]+/u;
const EXPONENT_INDICATOR = /[eE]/u;
const EXPONENT_PART = reg`${EXPONENT_INDICATOR}${SIGNED_INTEGER}`;

const DECIMAL_INTEGER_LITERAL = reg`\d+(?:[,._]\d+)*(?:[,._]\d+)?[.]?`;
const DECIMAL_LITERAL = reg`(?:[.]?${DECIMAL_INTEGER_LITERAL})(?:${EXPONENT_PART})?`;
const DECIMAL_FRACTION_LITERAL = reg`(?:${DECIMAL_INTEGER_LITERAL}\s+)?${DECIMAL_DIGITS}\s*[\/]\s*${DECIMAL_DIGITS}`;

function caseInsensitive(input) {
    return Array.from(input).map(c => {
        if (/[a-zA-Z]/.test(c)) {
            return `[${c.toUpperCase()}${c.toLowerCase()}]`;
        }
        return c;
    }).join("");
}

function buildTimeStringRegex() {
    let numPart = "\\d+(?:\\.\\d+)?";
    let unitsPart = "(?:" + caseInsensitive("y") + "(?:" + caseInsensitive("ears") + "?)?" +
                        "|" + caseInsensitive("d") + "(?:" + caseInsensitive("ays") + "?)?" +
                        "|" + caseInsensitive("h") + "(?:" + caseInsensitive("ours") + "?)?" +
                        "|" + caseInsensitive("m") + "(?:" + caseInsensitive("inutes") + "?)?" +
                        "|" + caseInsensitive("s") + "(?:" + caseInsensitive("econds") + "?)?)";
    let timePart = numPart + "\\s*" + unitsPart;
    let timeString = "(?:" + timePart + "\\s*){2,}";
    let colonTimeString = "(?:\\d{1,2}:\\d{1,2}(?::\\d{1,2})?(?:\\.\\d+)?)";

    return new RegExp(timeString + "|" + colonTimeString, "u");
}

const TIMEZONEREGEX = "\\s*(?:UTC[+-][0-9]+|GMT[+-][0-9]+|[A-Za-z]{2,4})";                  // Timezone: PST, GMT+5, UTC+5

function buildTimeImpliedDateRegex() {
    let numPart = "\\d{1,2}";                                // Matches numbers
    let optionalMinutesPart = "(?::\\d{1,2})?";              // Matches optional minutes part after colon e.g. ":30"
    let optionalSecondsPart = "(?::\\d{1,2})?";              // Matches optional seconds part after another colon e.g. ":45"
    let amPmPart = "\\s*(?:" + caseInsensitive("am") + "\\b|" + caseInsensitive("pm") + "\\b)"; // am/pm, optional spaces
    let timezonePart = TIMEZONEREGEX;
    let timeString = numPart + optionalMinutesPart + optionalSecondsPart + amPmPart + "(?:" + timezonePart + ")?";
    return new RegExp(timeString, "u");
}

function buildDateStringRegex() {
    // Convert month names to case insensitive format
    const months = caseInsensitive('(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sept?(?:ember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)');
    
    // Weekdays (optional) followed by an optional comma
    const weekdays = caseInsensitive('(?:Mon(?:day)?|Tue(?:sday)?|Wed(?:nesday)?|Thu(?:rsday)?|Fri(?:day)?|Sat(?:urday)?|Sun(?:day)?)?,?');

    const day = '\\d{1,2}';
    //const year = '\\d{2}(?:\\d{2})?';

    const fullYear = '(?:19[0-9]{2}|20[0-9]{2})';  
    const twoDigitYear = '\\d{2}';
    const year = `(?:${fullYear}|${twoDigitYear})`;

    const time = '\\d{1,2}:\\d{2}(?::\\d{2})?\\s*[APap][Mm]?';  // Time (in the format hh:mm:ss AM/PM)
    const timezone = TIMEZONEREGEX;
    const textualMonthDay = `${months}\\s+${day}`; // "Nov 5" or "November 5" 

    // "Tue, Nov 5, 1994" or "November 5 1994"
    const textualMonthDayYear = `${weekdays}\\s*${textualMonthDay},?\\s+${year}(?:\\s+${time})?(?:\\s+${timezone})?`;

    // Numeric date formats with mandatory year
    const numericDateFormats = [
        `\\d{1,2}/\\d{1,2}/${year}\\b`,    // MM/DD/YY
        `${year}/\\d{1,2}/\\d{1,2}\\b`,     // YY/MM/DD
        `\\d{1,2}[.]\\d{1,2}[.]${year}\\b`,    // DD.MM.YYYY
        `${year}[.]\\d{1,2}[.]\\d{1,2}\\b`,     // YY.MM.DD
    ].join('|');

    // Construct the regex with the textual date formats first, followed by the numeric date formats
    const regexString = `${textualMonthDayYear}|${textualMonthDay}|${numericDateFormats}`;
    return new RegExp(regexString, 'u');
}

const TIME_IMPLIED_DATE = buildTimeImpliedDateRegex();
if (typeof window !== "undefined") {
    window.TIME_IMPLIED_DATE = TIME_IMPLIED_DATE;
}
const TIME_STRING = buildTimeStringRegex();
const DATE_STRING = buildDateStringRegex();

function makeKeywordRegexString(str) {
    return str.split("").map(c => {
        if (/[a-zA-Z]/.test(c)) {
            return `[${c.toUpperCase()}${c.toLowerCase()}]`;
        }
        return c;
    }).join('');
}

// wrap keyword with \b to ensure it is a word
function makeStandaloneKeywordRegex(str) {
    str = makeKeywordRegexString(str);
    return new RegExp("\\b" + str + "\\b", "u");
}

function createAlternativesRegex(keywords) {
    return new RegExp(keywords.map(keyword => makeKeywordRegexString(keyword)).join("|"), "u");
}

const footSymbol = `'`;
const inchesSymbol = `"`;

// longest match first
const footKeywords = [footSymbol, "foot", "feet", "ft"];
const inchesKeywords = [inchesSymbol, "inches", "inch", "in"];

const ANY_FOOT_SYMBOL = createAlternativesRegex(footKeywords);
const ANY_INCH_SYMBOL = createAlternativesRegex(inchesKeywords);

const FEET_AND_INCHES = reg`(?:${DECIMAL_LITERAL}|${DECIMAL_FRACTION_LITERAL})\s*(?:${ANY_FOOT_SYMBOL})\s*(?:and|[,])?\s*(?:${DECIMAL_LITERAL}|${DECIMAL_FRACTION_LITERAL})\s*(?:${ANY_INCH_SYMBOL})`;
const FOOTPART = reg`(?:${DECIMAL_LITERAL}|${DECIMAL_FRACTION_LITERAL})\s*'`;
const INCHESPART = reg`(?:${DECIMAL_LITERAL}|${DECIMAL_FRACTION_LITERAL})\s*"`;

const ISO_TIMESTAMP = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})/u;

// SINGLE SOURCE OF TRUTH: keywords from grammar/keywords.js
// DO NOT hardcode keywords here - edit keywords.js instead!
// Try global (browser), then require (Node.js), then fallback
const KEYWORDS = (typeof IC_KEYWORDS !== 'undefined' && IC_KEYWORDS.GRAMMAR_KEYWORDS_SET)
  || new Set("null true false if else and or not xor mod off of from function".split(" "));
const OPS = new Set("and or not xor mod");
let IGNORE = new Set("ws commentSingle commentMulti".split(" "));

const LEXER = moo.compile({
  timeImpliedDate: TIME_IMPLIED_DATE,
  isoTimestamp: ISO_TIMESTAMP,
  datestring: DATE_STRING,
  timestring: TIME_STRING,
    
  // allow underscores between chars: 0x1_2
  /*
  oct: /0[oO](?:[0-7]+_)*[0-7]+/u,
  bin: /0[bB](?:[01]+_)*[01]+/u,
  hex: /0[xX](?:[0-9a-fA-F]+_)*[0-9a-fA-F]+/u,
  */

  oct: /(?:0[oO](?:[0-7]+_)*[0-7]+(?:\.[0-7]+)?)/u,
  bin: /(?:0[bB](?:[01]+_)*[01]+(?:\.[01]+)?)/u,
  hex: /(?:0[xX](?:[0-9a-fA-F]+_)*[0-9a-fA-F]+(?:\.[0-9a-fA-F]+)?)/u,

  rowRange: /[rR]\d+[:][rR]?\d+/u,

  // "else if" must be two separate tokens, not a merged id token
  if: /\bif(?=\s*[(])\b/u,        // if is a keyword if followed by (

  else: makeStandaloneKeywordRegex("else"),
  and: makeStandaloneKeywordRegex("and"),
  or: makeStandaloneKeywordRegex("or"),
  not: makeStandaloneKeywordRegex("not"),
  xor: makeStandaloneKeywordRegex("xor"),
  mod: makeStandaloneKeywordRegex("mod"),
  off: makeStandaloneKeywordRegex("off"),
  of: makeStandaloneKeywordRegex("of"),
  from: makeStandaloneKeywordRegex("from"),
  function: makeStandaloneKeywordRegex("function"),
    
  // must happen before decimal
  feetAndInches: FEET_AND_INCHES,
  footPart: FOOTPART,
  inchesPart: INCHESPART,

  id: {match: ID_FINAL},
  decimal: DECIMAL_LITERAL,

  rarrow:                    /=>/u,
    
  teq: /[=][=][=]/u,
  deq: /[=][=]/u,
  eq: /[=]/u,
        
  strictlynotequals:         /!==/u,
  strictlyequals:            /===/u,
  unsignedrightshiftassign:  />>>=/u,
  leftshiftassign:           /<<=/u,
  rightshiftassign:          />>=/u,
  greaterthanorequal:        />=/u,
  lessthanorequal:           /<=/u,
  exponentiationassign: /\*\*=/u,
  exponentiation:            /\*\*/u,
  unsignedshiftright:        />>>/u,
  logicalor:                 /\|\|/u,
  logicaland:                /&&/u,
  shiftleft:                 /<</u,
  shiftright:                />>/u,
  doubleequals:              /==/u,
  notequals:                 /!=/u,
  plusequals:                /\+=/u,
  minusequals:               /-=/u,
  timesequals:               /\*=/u,
  divideequals:              /\/=/u,
  modulusequals:             /%=/u,
  andassign:                 /&=/u,
  orassign:                  /\|=/u,
  xorequals:                 /\^=/u,
  doubleplus:                /\+\+/u,
  doubleminus:               /--/u,
 
  // TODO: can have multiple matches with an array, all back to a comment type
  commentSingle: /(?:\/\/|[#])[^\n]*/u, // js style (//), or Python style (#)
  commentMulti: /\/\*[\s\S]*?\*\//u,

  // define with regex, avoid special chars
  conversionOp: /[-][>]/u,
  typesolveOp: /[@]/u,
  percentOp: /[%]/u,
    
  sc: ';',
  comma: ',',
  ws: { match: /\s+/u, lineBreaks: true },
    
  string_double: /"(?:\\["bfnrt\/\\]|\\u[a-fA-F0-9]{4}|[^"\\])*"/u,
  string_single: /'(?:\\['bfnrt\/\\]|\\u[a-fA-F0-9]{4}|[^'\\])*'/u,

  lp: '(',
  rp: ')',
  lb: '{',
  rb: '}',
  lbracket: '[',
  rbracket: ']',
  carat: '^',
  doublepipe: '||',
  pipe: '|',
        

  symbol: /[+\-*/%=!<>&|~?:]/u,
  squote: `'`,
  dquote: `"`,

  slashItem: /[\\]\w+/u,
                  
  // catchall 
  source: { match: /./u, lineBreaks: true },
});

LEXER.IGNORE = IGNORE;
LEXER.IGNORE_ENABLED = true;

LEXER.next = (next => () => {
    let tok;
    try {
        do {
            tok = next.call(LEXER); // Get the next token
            if (!tok) break; // Exit loop if no token (EOF or error handled inside)

            // Skip ignored tokens (inside the do-while loop)
            if (LEXER.IGNORE_ENABLED && IGNORE.has(tok.type)) {
                continue; // Go to the next iteration of the do-while loop to get another token
            } else {
                break; // If it's not an ignored token, exit the do-while loop
            }
        } while (true); // Loop until a non-ignored token or no token is found

        // turn keywords into keyword items (after the do-while loop)
        if (tok?.type === "id" && KEYWORDS.has(tok.value.toLowerCase())) {
            tok.type = "keyword";
            tok.value = tok.text = tok.value.toLowerCase();
        }

    } catch (error) {
        // Error occurred during next.call(LEXER) (handled outside the loop)
        // console.error("Error in LEXER.next (Safari?):", error);
        return undefined; // Or return null
    }

    return tok; // Return the token (or undefined if error or no token)
})(LEXER.next);

// unwrap single item arrays
function unwrap(x) {
    if (Array.isArray(x) && x.length == 1) {
        return unwrap(x[0]);
    }
    return x;
}

function hasContent(x) {
    return Array.isArray(x) ? x.length > 0 : x;
}

const CONVERSION_OP = {
  "*": "mul",
  "+": "add",
  "-": "sub"
};

var grammar = {
    Lexer: LEXER,
    ParserRules: [
    {"name": "main", "symbols": ["Program"], "postprocess": id},
    {"name": "nullLiteral", "symbols": [{"literal":"null"}], "postprocess": id},
    {"name": "booleanLiteral$subexpression$1", "symbols": [{"literal":"true"}]},
    {"name": "booleanLiteral$subexpression$1", "symbols": [{"literal":"false"}]},
    {"name": "booleanLiteral", "symbols": ["booleanLiteral$subexpression$1"], "postprocess": id},
    {"name": "fraction", "symbols": [(LEXER.has("decimal") ? {type: "decimal"} : decimal), (LEXER.has("decimal") ? {type: "decimal"} : decimal), {"literal":"/"}, (LEXER.has("decimal") ? {type: "decimal"} : decimal)], "postprocess":  d => {
        let src = d.join(" ").trim();
        return {type: "fraction", value: src, text: src}
        }
        },
    {"name": "numericLiteral$subexpression$1", "symbols": [(LEXER.has("oct") ? {type: "oct"} : oct)]},
    {"name": "numericLiteral$subexpression$1", "symbols": [(LEXER.has("bin") ? {type: "bin"} : bin)]},
    {"name": "numericLiteral$subexpression$1", "symbols": [(LEXER.has("hex") ? {type: "hex"} : hex)]},
    {"name": "numericLiteral$subexpression$1", "symbols": [(LEXER.has("decimal") ? {type: "decimal"} : decimal)]},
    {"name": "numericLiteral", "symbols": ["numericLiteral$subexpression$1"], "postprocess": id},
    {"name": "numericDivision", "symbols": ["numericLiteral", {"literal":"/"}, "numericLiteral"], "postprocess": d => ["div", d[0], d[2]].map(unwrap)},
    {"name": "stringLiteral$subexpression$1", "symbols": [(LEXER.has("string_double") ? {type: "string_double"} : string_double)]},
    {"name": "stringLiteral$subexpression$1", "symbols": [(LEXER.has("string_single") ? {type: "string_single"} : string_single)]},
    {"name": "stringLiteral", "symbols": ["stringLiteral$subexpression$1"], "postprocess": id},
    {"name": "timeLiteral", "symbols": [(LEXER.has("timestring") ? {type: "timestring"} : timestring)], "postprocess": id},
    {"name": "datetimeLiteral", "symbols": [(LEXER.has("isoTimestamp") ? {type: "isoTimestamp"} : isoTimestamp)]},
    {"name": "datetimeLiteral$ebnf$1$subexpression$1", "symbols": ["timeLiteral"]},
    {"name": "datetimeLiteral$ebnf$1$subexpression$1", "symbols": ["timeImpliedDateLiteral"]},
    {"name": "datetimeLiteral$ebnf$1", "symbols": ["datetimeLiteral$ebnf$1$subexpression$1"], "postprocess": id},
    {"name": "datetimeLiteral$ebnf$1", "symbols": [], "postprocess": function(d) {return null;}},
    {"name": "datetimeLiteral", "symbols": [(LEXER.has("datestring") ? {type: "datestring"} : datestring), "datetimeLiteral$ebnf$1"], "postprocess": d => { return {value: d.map(unwrap).filter(Boolean).join(" ") } }},
    {"name": "timeImpliedDateLiteral", "symbols": [(LEXER.has("timeImpliedDate") ? {type: "timeImpliedDate"} : timeImpliedDate)], "postprocess": id},
    {"name": "temporalLiteral", "symbols": ["timeLiteral"], "postprocess": d => ["timeLiteral", unwrap(d[0])]},
    {"name": "temporalLiteral", "symbols": ["datetimeLiteral"], "postprocess": d => ["datetimeLiteral", unwrap(d[0])]},
    {"name": "temporalLiteral", "symbols": ["timeImpliedDateLiteral"], "postprocess": d => ["timeImpliedDateLiteral", unwrap(d[0])]},
    {"name": "tapeMeasureLiteral", "symbols": [(LEXER.has("feetAndInches") ? {type: "feetAndInches"} : feetAndInches)], "postprocess": d => ["tapemeasure", {both: unwrap(d)}]},
    {"name": "tapeMeasureLiteral", "symbols": [(LEXER.has("footPart") ? {type: "footPart"} : footPart)], "postprocess": d => ["tapemeasure", {foot: unwrap(d)}]},
    {"name": "tapeMeasureLiteral", "symbols": [(LEXER.has("inchesPart") ? {type: "inchesPart"} : inchesPart)], "postprocess": d => ["tapemeasure", {inch: unwrap(d)}]},
    {"name": "literal", "symbols": ["nullLiteral"], "postprocess": d => ["nullLiteral", d[0]]},
    {"name": "literal", "symbols": ["booleanLiteral"], "postprocess": d => ["booleanLiteral", unwrap(d[0])]},
    {"name": "literal", "symbols": ["stringLiteral"], "postprocess": d => ["stringLiteral", unwrap(d[0])]},
    {"name": "literal$subexpression$1", "symbols": ["numericLiteral"]},
    {"name": "literal$subexpression$1", "symbols": ["fraction"]},
    {"name": "literal", "symbols": ["literal$subexpression$1"], "postprocess": id},
    {"name": "literal", "symbols": ["temporalLiteral"], "postprocess": id},
    {"name": "literal", "symbols": ["tapeMeasureLiteral"]},
    {"name": "ArrayLiteral$ebnf$1$subexpression$1$ebnf$1", "symbols": []},
    {"name": "ArrayLiteral$ebnf$1$subexpression$1$ebnf$1$subexpression$1", "symbols": [{"literal":","}, "AssignmentExp"]},
    {"name": "ArrayLiteral$ebnf$1$subexpression$1$ebnf$1", "symbols": ["ArrayLiteral$ebnf$1$subexpression$1$ebnf$1", "ArrayLiteral$ebnf$1$subexpression$1$ebnf$1$subexpression$1"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "ArrayLiteral$ebnf$1$subexpression$1", "symbols": ["AssignmentExp", "ArrayLiteral$ebnf$1$subexpression$1$ebnf$1"]},
    {"name": "ArrayLiteral$ebnf$1", "symbols": ["ArrayLiteral$ebnf$1$subexpression$1"], "postprocess": id},
    {"name": "ArrayLiteral$ebnf$1", "symbols": [], "postprocess": function(d) {return null;}},
    {"name": "ArrayLiteral", "symbols": [{"literal":"["}, "ArrayLiteral$ebnf$1", {"literal":"]"}], "postprocess": d => ['arrayLiteral', d[1] ? [d[1][0], ...(d[1][1] || []).map(item => item[1])] : []]},
    {"name": "ArrayAccess", "symbols": ["PrimaryExp", {"literal":"["}, "ConversionOpExp", {"literal":"]"}], "postprocess": d => ["arrayAccess", d[0], d[2]]},
    {"name": "ObjectLiteral$ebnf$1", "symbols": []},
    {"name": "ObjectLiteral$ebnf$1$subexpression$1", "symbols": [{"literal":","}, "ObjectItem"]},
    {"name": "ObjectLiteral$ebnf$1", "symbols": ["ObjectLiteral$ebnf$1", "ObjectLiteral$ebnf$1$subexpression$1"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "ObjectLiteral", "symbols": [{"literal":"{"}, "ObjectItem", "ObjectLiteral$ebnf$1", {"literal":"}"}], "postprocess": d => ['objectLiteral', [d[1], ...d[2].map(item => item[1])].map(unwrap)]},
    {"name": "ObjectLiteral", "symbols": [{"literal":"{"}, {"literal":"}"}], "postprocess": d => ['objectLiteral', []]},
    {"name": "ObjectItem$subexpression$1", "symbols": ["stringLiteral"]},
    {"name": "ObjectItem$subexpression$1", "symbols": [(LEXER.has("id") ? {type: "id"} : id)]},
    {"name": "ObjectItem", "symbols": ["ObjectItem$subexpression$1", {"literal":":"}, "ConversionOpExp"], "postprocess": d => ['objectItem', unwrap(d[0][0]), d[2]]},
    {"name": "ObjectAccess", "symbols": ["PrimaryExp", {"literal":"."}, (LEXER.has("id") ? {type: "id"} : id)], "postprocess": d => ["objectAccess", d[0], d[2]]},
    {"name": "PrimaryExp", "symbols": [(LEXER.has("id") ? {type: "id"} : id)], "postprocess": id},
    {"name": "PrimaryExp", "symbols": ["literal"], "postprocess": id},
    {"name": "PrimaryExp", "symbols": ["ArrayLiteral"], "postprocess": id},
    {"name": "PrimaryExp", "symbols": ["ArrayAccess"], "postprocess": id},
    {"name": "PrimaryExp", "symbols": ["ObjectLiteral"], "postprocess": id},
    {"name": "PrimaryExp", "symbols": ["ObjectAccess"], "postprocess": id},
    {"name": "PrimaryExp", "symbols": ["FunctionStatement"], "postprocess": id},
    {"name": "PrimaryExp", "symbols": [{"literal":"("}, "Exp", {"literal":")"}], "postprocess": d => ["parenExpr", unwrap(d[1])]},
    {"name": "PrimaryExp", "symbols": [{"literal":"|"}, "Exp", {"literal":"|"}], "postprocess": d => ["absValue", unwrap(d[1])]},
    {"name": "LValue", "symbols": ["PrimaryExp"], "postprocess": id /* assignable left hand side expression */},
    {"name": "RValue", "symbols": ["CallExp"]},
    {"name": "RValue", "symbols": ["LValue"], "postprocess": id /* executable right hand side expression */},
    {"name": "keywordFunctions$subexpression$1", "symbols": [(LEXER.has("and") ? {type: "and"} : and)]},
    {"name": "keywordFunctions$subexpression$1", "symbols": [(LEXER.has("or") ? {type: "or"} : or)]},
    {"name": "keywordFunctions$subexpression$1", "symbols": [{"literal":"if"}]},
    {"name": "keywordFunctions", "symbols": ["keywordFunctions$subexpression$1"], "postprocess": id},
    {"name": "keywordFunctionsUnary$subexpression$1", "symbols": [(LEXER.has("not") ? {type: "not"} : not)]},
    {"name": "keywordFunctionsUnary", "symbols": ["keywordFunctionsUnary$subexpression$1"], "postprocess": id},
    {"name": "CallExp", "symbols": [(LEXER.has("decimal") ? {type: "decimal"} : decimal), "CallExp"], "postprocess": d => ["scaledCallExp", d[0], d[1]].map(unwrap)},
    {"name": "CallExp", "symbols": [(LEXER.has("id") ? {type: "id"} : id), "Arguments"], "postprocess": d =>['callExp', d[0], d[1].map(unwrap).filter(hasContent)]},
    {"name": "CallExp", "symbols": ["ObjectAccess", "Arguments"], "postprocess": d => ['callExp', d[0][2], [d[0][1], ...d[1].map(unwrap).filter(hasContent)]]},
    {"name": "CallExp", "symbols": ["CallExp", {"literal":"."}, (LEXER.has("id") ? {type: "id"} : id), "Arguments"], "postprocess": d => ['callExp', d[2], [['parenExpr', d[0]], ...d[3].map(unwrap).filter(hasContent)]]},
    {"name": "CallExp", "symbols": ["keywordFunctions", "MultipleArguments"], "postprocess": d => ['callExpWithKeyword', unwrap(d[0]), d[1].map(unwrap).filter(hasContent)]},
    {"name": "CallExp", "symbols": ["keywordFunctionsUnary", "SingleArgument"], "postprocess": d => ['callExpWithKeyword', unwrap(d[0]), d[1].map(unwrap).filter(hasContent)]},
    {"name": "SingleArgument", "symbols": [{"literal":"("}, "Argument", {"literal":")"}], "postprocess": (d) => d[1]},
    {"name": "MultipleArguments$ebnf$1$subexpression$1", "symbols": [{"literal":","}, "Argument"]},
    {"name": "MultipleArguments$ebnf$1", "symbols": ["MultipleArguments$ebnf$1$subexpression$1"]},
    {"name": "MultipleArguments$ebnf$1$subexpression$2", "symbols": [{"literal":","}, "Argument"]},
    {"name": "MultipleArguments$ebnf$1", "symbols": ["MultipleArguments$ebnf$1", "MultipleArguments$ebnf$1$subexpression$2"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "MultipleArguments", "symbols": [{"literal":"("}, "Argument", "MultipleArguments$ebnf$1", {"literal":")"}], "postprocess": (d) => [d[1], ...(d[2] || []).map(([comma, arg]) => unwrap(arg))]},
    {"name": "Arguments$ebnf$1", "symbols": []},
    {"name": "Arguments$ebnf$1$subexpression$1", "symbols": [{"literal":","}, "Argument"]},
    {"name": "Arguments$ebnf$1", "symbols": ["Arguments$ebnf$1", "Arguments$ebnf$1$subexpression$1"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "Arguments", "symbols": [{"literal":"("}, "Argument", "Arguments$ebnf$1", {"literal":")"}], "postprocess": (d) => [d[1], ...(d[2] || []).map(([comma, arg]) => unwrap(arg))]},
    {"name": "Argument", "symbols": [(LEXER.has("rowRange") ? {type: "rowRange"} : rowRange)]},
    {"name": "Argument", "symbols": ["FunctionStatement"]},
    {"name": "Argument", "symbols": ["AssignmentExp"]},
    {"name": "Argument", "symbols": []},
    {"name": "PostfixExp$subexpression$1", "symbols": [{"literal":"++"}]},
    {"name": "PostfixExp$subexpression$1", "symbols": [{"literal":"--"}]},
    {"name": "PostfixExp", "symbols": ["LValue", "PostfixExp$subexpression$1"], "postprocess": d => ["postfix", d[0], d[1]].map(unwrap)},
    {"name": "PostfixExp", "symbols": ["RValue", {"literal":"!"}], "postprocess": d => ["factorial", d[0]].map(unwrap)},
    {"name": "PostfixExp", "symbols": ["PercentExp"], "postprocess": unwrap},
    {"name": "PostfixExp", "symbols": ["RValue"], "postprocess": unwrap},
    {"name": "PercentExp", "symbols": ["RValue", {"literal":"%"}], "postprocess": d => ["pctExpression", d[0]].map(unwrap)},
    {"name": "UnaryExp$subexpression$1", "symbols": [{"literal":"!"}]},
    {"name": "UnaryExp$subexpression$1", "symbols": [{"literal":"not"}]},
    {"name": "UnaryExp$subexpression$1", "symbols": [{"literal":"~"}]},
    {"name": "UnaryExp$subexpression$1", "symbols": [{"literal":"++"}]},
    {"name": "UnaryExp$subexpression$1", "symbols": [{"literal":"--"}]},
    {"name": "UnaryExp$subexpression$1", "symbols": [{"literal":"+"}]},
    {"name": "UnaryExp$subexpression$1", "symbols": [{"literal":"-"}]},
    {"name": "UnaryExp", "symbols": ["UnaryExp$subexpression$1", "UnaryExp"], "postprocess": d => ["unary",  id(d[0]).value, d[1]]},
    {"name": "UnaryExp", "symbols": ["PostfixExp"], "postprocess": unwrap},
    {"name": "ExponentExp$subexpression$1", "symbols": [{"literal":"^"}]},
    {"name": "ExponentExp$subexpression$1", "symbols": [{"literal":"**"}]},
    {"name": "ExponentExp", "symbols": ["UnaryExp", "ExponentExp$subexpression$1", "ExponentExp"], "postprocess": d => ["power", d[0], d[2]].map(unwrap)},
    {"name": "ExponentExp", "symbols": ["UnaryExp"], "postprocess": unwrap},
    {"name": "MultiplicativeExp", "symbols": ["MultiplicativeExp", (LEXER.has("mod") ? {type: "mod"} : mod), "ExponentExp"], "postprocess": d => ["mod", d[0], d[2]].map(unwrap)},
    {"name": "MultiplicativeExp", "symbols": ["ExponentExp"], "postprocess": unwrap},
    {"name": "ParenExp", "symbols": [{"literal":"("}, "Exp", {"literal":")"}], "postprocess": d => d[1]},
    {"name": "ParenExpWithExponent$subexpression$1", "symbols": [{"literal":"^"}]},
    {"name": "ParenExpWithExponent$subexpression$1", "symbols": [{"literal":"**"}]},
    {"name": "ParenExpWithExponent", "symbols": ["ParenExp", "ParenExpWithExponent$subexpression$1", "ExponentExp"], "postprocess": d => ["power2", d[0], d[2]].map(unwrap)},
    {"name": "ImplicitMultPrefix", "symbols": ["numericLiteral"], "postprocess": unwrap},
    {"name": "ImplicitMultPrefix", "symbols": ["PercentExp"], "postprocess": unwrap},
    {"name": "ImplicitMultPrefix$subexpression$1", "symbols": [{"literal":"^"}]},
    {"name": "ImplicitMultPrefix$subexpression$1", "symbols": [{"literal":"**"}]},
    {"name": "ImplicitMultPrefix", "symbols": ["numericLiteral", "ImplicitMultPrefix$subexpression$1", "numericLiteral"], "postprocess": d => ["power3", d[0], d[2]].map(unwrap)},
    {"name": "ImplicitMultPrefix", "symbols": ["numericDivision"], "postprocess": unwrap},
    {"name": "ImplicitMultPrefix", "symbols": ["ImplicitMultTerm"]},
    {"name": "ImplicitMultTerm", "symbols": ["ParenExp"]},
    {"name": "ImplicitMultTerm", "symbols": ["ParenExpWithExponent"], "postprocess": unwrap},
    {"name": "ParenMultExp$ebnf$1", "symbols": ["unitLabel"], "postprocess": id},
    {"name": "ParenMultExp$ebnf$1", "symbols": [], "postprocess": function(d) {return null;}},
    {"name": "ParenMultExp", "symbols": ["ImplicitMultSequence", "ParenMultExp$ebnf$1"], "postprocess": d => ["parenMult", ...(d.filter(x => x))].map(unwrap)},
    {"name": "ImplicitMultSequence$ebnf$1", "symbols": ["ImplicitMultTerm"]},
    {"name": "ImplicitMultSequence$ebnf$1", "symbols": ["ImplicitMultSequence$ebnf$1", "ImplicitMultTerm"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "ImplicitMultSequence", "symbols": ["ImplicitMultPrefix", "ImplicitMultSequence$ebnf$1"]},
    {"name": "ImplicitMultSequence$ebnf$2", "symbols": ["ImplicitMultTerm"]},
    {"name": "ImplicitMultSequence$ebnf$2", "symbols": ["ImplicitMultSequence$ebnf$2", "ImplicitMultTerm"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "ImplicitMultSequence", "symbols": ["ImplicitMultSequence$ebnf$2", "ImplicitMultSuffix"]},
    {"name": "ImplicitMultSuffix", "symbols": ["numericLiteral"]},
    {"name": "UnitAwareMultExp", "symbols": ["UnitAwareMultExp", {"literal":"*"}, "MultiplicativeOperand"], "postprocess": d => ["mul", d[0], d[2]].map(unwrap)},
    {"name": "UnitAwareMultExp", "symbols": ["UnitAwareMultExp", {"literal":"/"}, "MultiplicativeExpWithUnit"], "postprocess": d => ["divUnit", d[0], d[2]].map(unwrap)},
    {"name": "UnitAwareMultExp", "symbols": ["UnitAwareMultExp", {"literal":"/"}, "MultiplicativeExp"], "postprocess": d => ["div", d[0], d[2]].map(unwrap)},
    {"name": "UnitAwareMultExp", "symbols": ["ParenMultExp", {"literal":"/"}, "ParenMultExp"], "postprocess": d => ["div", d[0], d[2]].map(unwrap)},
    {"name": "UnitAwareMultExp", "symbols": ["UnitAwareMultExp", "customOp", "MultiplicativeExp"], "postprocess": d => ["customOp", d[0], d[1], d[2]].map(unwrap)},
    {"name": "UnitAwareMultExp", "symbols": ["ParenMultExp"], "postprocess": id},
    {"name": "UnitAwareMultExp", "symbols": ["MultiplicativeExpWithUnit"], "postprocess": id},
    {"name": "UnitAwareMultExp", "symbols": ["MultiplicativeExp"], "postprocess": id},
    {"name": "customOp", "symbols": [{"literal":":"}, (LEXER.has("id") ? {type: "id"} : id), {"literal":":"}], "postprocess": d => `:${d[1].value}:`},
    {"name": "MultiplicativeOperand", "symbols": ["MultiplicativeExpWithUnit"]},
    {"name": "MultiplicativeOperand", "symbols": ["MultiplicativeExp"]},
    {"name": "MultiplicativeOperand", "symbols": ["ParenMultExp"]},
    {"name": "MultiplicativeExpWithUnit", "symbols": ["MultiplicativeExp", "unitLabel"], "postprocess": d => ["unit", d[0], d[1]]},
    {"name": "unitLabel$ebnf$1", "symbols": []},
    {"name": "unitLabel$ebnf$1", "symbols": ["unitLabel$ebnf$1", "unitLabelParts"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "unitLabel", "symbols": ["unitID", "unitLabel$ebnf$1"], "postprocess": d => `${d[0]} ${d[1] && d[1].join(' ')}`.trim()},
    {"name": "unitID$ebnf$1", "symbols": ["unitLabelPower"], "postprocess": id},
    {"name": "unitID$ebnf$1", "symbols": [], "postprocess": function(d) {return null;}},
    {"name": "unitID", "symbols": [(LEXER.has("id") ? {type: "id"} : id), "unitID$ebnf$1"], "postprocess": d => `${d[0]}${d[1] ? d[1] : ''}`},
    {"name": "unitLabelParts", "symbols": ["unitID"]},
    {"name": "unitLabelParts", "symbols": [{"literal":"_"}]},
    {"name": "unitLabelParts", "symbols": [{"literal":"'"}]},
    {"name": "unitLabelParts", "symbols": [{"literal":" "}]},
    {"name": "unitLabelParts", "symbols": ["numericLiteral"], "postprocess": d => `${d[0]}`},
    {"name": "unitLabelPower$ebnf$1", "symbols": [{"literal":"-"}], "postprocess": id},
    {"name": "unitLabelPower$ebnf$1", "symbols": [], "postprocess": function(d) {return null;}},
    {"name": "unitLabelPower$ebnf$2", "symbols": [(LEXER.has("decimal") ? {type: "decimal"} : decimal)]},
    {"name": "unitLabelPower$ebnf$2", "symbols": ["unitLabelPower$ebnf$2", (LEXER.has("decimal") ? {type: "decimal"} : decimal)], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "unitLabelPower", "symbols": [{"literal":"^"}, "unitLabelPower$ebnf$1", "unitLabelPower$ebnf$2"], "postprocess": d => `${d[0]}${d[1] ? "-" : ''}${d[2]}`},
    {"name": "UnitExp", "symbols": ["UnitAwareMultExp"], "postprocess": id},
    {"name": "AdditiveExp$subexpression$1", "symbols": [{"literal":"+"}]},
    {"name": "AdditiveExp$subexpression$1", "symbols": [{"literal":"-"}]},
    {"name": "AdditiveExp", "symbols": ["AdditiveExp", "AdditiveExp$subexpression$1", "UnitExp"], "postprocess":  d => {
            if (Array.isArray(d[2]) && d[2][0] == "pctExpression") {
                return ["pctChange", d[0], d[1], d[2]].map(unwrap);
            }
            let op = d[1] == "+" ? "add" : "sub";
            
            return [op, d[0], d[2]].map(unwrap);
            
        }},
    {"name": "AdditiveExp$subexpression$2", "symbols": [(LEXER.has("off") ? {type: "off"} : off)]},
    {"name": "AdditiveExp$subexpression$2", "symbols": [(LEXER.has("of") ? {type: "of"} : of)]},
    {"name": "AdditiveExp$subexpression$2", "symbols": [(LEXER.has("from") ? {type: "from"} : from)]},
    {"name": "AdditiveExp", "symbols": ["RValue", {"literal":"%"}, "AdditiveExp$subexpression$2", "UnitExp"], "postprocess": d => ["pctOp", ...d].map(unwrap)},
    {"name": "AdditiveExp", "symbols": ["UnitExp"], "postprocess": unwrap},
    {"name": "ShiftExp", "symbols": ["ShiftExp", {"literal":"<<"}, "AdditiveExp"], "postprocess": d => ["lsl", d[0], d[2]].map(unwrap)},
    {"name": "ShiftExp", "symbols": ["ShiftExp", {"literal":">>>"}, "AdditiveExp"], "postprocess": d => ["lsr", d[0], d[2]].map(unwrap)},
    {"name": "ShiftExp", "symbols": ["ShiftExp", {"literal":">>"}, "AdditiveExp"], "postprocess": d => ["asr", d[0], d[2]].map(unwrap)},
    {"name": "ShiftExp", "symbols": ["AdditiveExp"], "postprocess": unwrap},
    {"name": "RelationalExp", "symbols": ["RelationalExp", {"literal":"<"}, "ShiftExp"], "postprocess": d => ["lt", d[0], d[2]].map(unwrap)},
    {"name": "RelationalExp", "symbols": ["RelationalExp", {"literal":">"}, "ShiftExp"], "postprocess": d => ["gt", d[0], d[2]].map(unwrap)},
    {"name": "RelationalExp", "symbols": ["RelationalExp", {"literal":"<="}, "ShiftExp"], "postprocess": d => ["le", d[0], d[2]].map(unwrap)},
    {"name": "RelationalExp", "symbols": ["RelationalExp", {"literal":">="}, "ShiftExp"], "postprocess": d => ["ge", d[0], d[2]].map(unwrap)},
    {"name": "RelationalExp", "symbols": ["ShiftExp"], "postprocess": unwrap},
    {"name": "EqualityExp", "symbols": ["EqualityExp", {"literal":"=="}, "RelationalExp"], "postprocess": d => ["equal", d[0], d[2]].map(unwrap)},
    {"name": "EqualityExp", "symbols": ["EqualityExp", {"literal":"!="}, "RelationalExp"], "postprocess": d => ["notEqual", d[0], d[2]].map(unwrap)},
    {"name": "EqualityExp", "symbols": ["EqualityExp", {"literal":"==="}, "RelationalExp"], "postprocess": d => ["eq", d[0], d[2]].map(unwrap)},
    {"name": "EqualityExp", "symbols": ["EqualityExp", {"literal":"!=="}, "RelationalExp"], "postprocess": d => ["notEq", d[0], d[2]].map(unwrap)},
    {"name": "EqualityExp", "symbols": ["RelationalExp"], "postprocess": id},
    {"name": "BitwiseANDExp$subexpression$1", "symbols": [(LEXER.has("and") ? {type: "and"} : and)]},
    {"name": "BitwiseANDExp$subexpression$1", "symbols": [{"literal":"&"}]},
    {"name": "BitwiseANDExp", "symbols": ["BitwiseANDExp", "BitwiseANDExp$subexpression$1", "EqualityExp"], "postprocess": d => ["band", d[0], d[2]].map(unwrap)},
    {"name": "BitwiseANDExp", "symbols": ["EqualityExp"], "postprocess": id},
    {"name": "BitwiseXORExp", "symbols": ["BitwiseXORExp", (LEXER.has("xor") ? {type: "xor"} : xor), "BitwiseANDExp"], "postprocess": d => ["bxor", d[0], d[2]].map(unwrap)},
    {"name": "BitwiseXORExp", "symbols": ["BitwiseANDExp"], "postprocess": id},
    {"name": "BitwiseORExp$subexpression$1", "symbols": [(LEXER.has("or") ? {type: "or"} : or)]},
    {"name": "BitwiseORExp$subexpression$1", "symbols": [{"literal":"|"}]},
    {"name": "BitwiseORExp", "symbols": ["BitwiseORExp", "BitwiseORExp$subexpression$1", "BitwiseXORExp"], "postprocess": d => ["bor", d[0], d[2]].map(unwrap)},
    {"name": "BitwiseORExp", "symbols": ["BitwiseXORExp"], "postprocess": id},
    {"name": "LogicalANDExp", "symbols": ["LogicalANDExp", {"literal":"&&"}, "BitwiseORExp"], "postprocess": d => ["land", d[0], d[2]].map(unwrap)},
    {"name": "LogicalANDExp", "symbols": ["BitwiseORExp"], "postprocess": id},
    {"name": "LogicalORExp", "symbols": ["LogicalORExp", {"literal":"||"}, "LogicalANDExp"], "postprocess": d => ["lor", d[0], d[2]].map(unwrap)},
    {"name": "LogicalORExp", "symbols": ["LogicalANDExp"], "postprocess": id},
    {"name": "ConditionalExp", "symbols": ["LogicalORExp", {"literal":"?"}, "AssignmentExp", {"literal":":"}, "AssignmentExp"], "postprocess": d => ["conditional", d[0], d[2], d[4]].map(unwrap)},
    {"name": "ConditionalExp", "symbols": ["LogicalORExp"], "postprocess": id},
    {"name": "ConversionOpExp", "symbols": ["ConversionWithArithmetic"], "postprocess": id},
    {"name": "ConversionOpExp$subexpression$1", "symbols": ["allowedConversionSymbols"]},
    {"name": "ConversionOpExp$subexpression$1", "symbols": ["UnitExp"]},
    {"name": "ConversionOpExp", "symbols": ["ConversionOpExp", (LEXER.has("conversionOp") ? {type: "conversionOp"} : conversionOp), "ConversionOpExp$subexpression$1"], "postprocess": d => ["conversion", d[0], d[2]].map(unwrap)},
    {"name": "ConversionOpExp$subexpression$2", "symbols": ["ConversionDivision"]},
    {"name": "ConversionOpExp", "symbols": ["ConversionOpExp", (LEXER.has("conversionOp") ? {type: "conversionOp"} : conversionOp), "ConversionOpExp$subexpression$2"], "postprocess": d => ["conversionDiv", d[0], d[2]].map(unwrap)},
    {"name": "ConversionOpExp", "symbols": ["ConversionOpExp", (LEXER.has("typesolveOp") ? {type: "typesolveOp"} : typesolveOp), "ConditionalExp"], "postprocess": d => ["typesolve", d[0], d[2]].map(unwrap)},
    {"name": "ConversionOpExp", "symbols": ["ConditionalExp"], "postprocess": id},
    {"name": "ConversionOpExp", "symbols": ["RateExp"], "postprocess": id},
    {"name": "ConversionWithArithmetic$subexpression$1", "symbols": ["allowedConversionSymbols"]},
    {"name": "ConversionWithArithmetic$subexpression$1", "symbols": ["ConditionalExp"]},
    {"name": "ConversionWithArithmetic$subexpression$2", "symbols": [{"literal":"+"}]},
    {"name": "ConversionWithArithmetic$subexpression$2", "symbols": [{"literal":"-"}]},
    {"name": "ConversionWithArithmetic$subexpression$2", "symbols": [{"literal":"*"}]},
    {"name": "ConversionWithArithmetic", "symbols": ["ConditionalExp", (LEXER.has("conversionOp") ? {type: "conversionOp"} : conversionOp), "ConversionWithArithmetic$subexpression$1", "ConversionWithArithmetic$subexpression$2", "AdditiveExp"], "postprocess": d => [CONVERSION_OP[d[3]], ["conversion", d[0], d[2]].map(unwrap), d[4]]},
    {"name": "ConversionWithArithmetic$subexpression$3", "symbols": ["ConversionDivision"]},
    {"name": "ConversionWithArithmetic$subexpression$4", "symbols": [{"literal":"+"}]},
    {"name": "ConversionWithArithmetic$subexpression$4", "symbols": [{"literal":"-"}]},
    {"name": "ConversionWithArithmetic$subexpression$4", "symbols": [{"literal":"*"}]},
    {"name": "ConversionWithArithmetic", "symbols": ["ConditionalExp", (LEXER.has("conversionOp") ? {type: "conversionOp"} : conversionOp), "ConversionWithArithmetic$subexpression$3", "ConversionWithArithmetic$subexpression$4", "AdditiveExp"], "postprocess": d => [CONVERSION_OP[d[3]], ["conversionDiv", d[0], d[2]].map(unwrap), d[4]]},
    {"name": "ConversionDivision", "symbols": [{"literal":"/"}, "ConditionalExp"]},
    {"name": "RateExp", "symbols": ["MultiplicativeExpWithUnit", {"literal":"="}, "Exp"], "postprocess": d=> ["rate", d[0], d[2]]},
    {"name": "allowedConversionSymbols", "symbols": [{"literal":"%"}]},
    {"name": "AssignmentExp", "symbols": ["ConversionOpExp"], "postprocess": id},
    {"name": "AssignmentExp", "symbols": ["PrimaryExp", "assignmentOperator", "AssignmentExp"], "postprocess": d => ["assignmentExp", d[0], d[1], d[2]].map(unwrap)},
    {"name": "AssignmentExp", "symbols": ["IfStatement"], "postprocess": id},
    {"name": "AssignmentExp", "symbols": ["FunctionStatement"], "postprocess": id},
    {"name": "assignmentOperator", "symbols": [{"literal":"="}]},
    {"name": "assignmentOperator", "symbols": [{"literal":"*="}]},
    {"name": "assignmentOperator", "symbols": [{"literal":"/="}]},
    {"name": "assignmentOperator", "symbols": [{"literal":"+="}]},
    {"name": "assignmentOperator", "symbols": [{"literal":"-="}], "postprocess": id},
    {"name": "Exp", "symbols": ["AssignmentExp"], "postprocess": unwrap},
    {"name": "Exp$ebnf$1", "symbols": ["commaExp"]},
    {"name": "Exp$ebnf$1", "symbols": ["Exp$ebnf$1", "commaExp"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "Exp", "symbols": ["AssignmentExp", "Exp$ebnf$1"], "postprocess": d => ["commaExp", d[0], ...d[1]]},
    {"name": "commaExp", "symbols": [{"literal":","}, "AssignmentExp"], "postprocess": d => d[1]},
    {"name": "StatementNotEmpty", "symbols": ["Block"], "postprocess": id},
    {"name": "StatementNotEmpty", "symbols": ["ExpStatement"], "postprocess": id},
    {"name": "StatementNotEmpty", "symbols": ["IfStatement"], "postprocess": id},
    {"name": "StatementNotEmpty", "symbols": ["FunctionStatement"], "postprocess": id},
    {"name": "Statement", "symbols": ["StatementNotEmpty"], "postprocess": id},
    {"name": "Statement", "symbols": ["EmptyStatement"], "postprocess": d => null},
    {"name": "IfStatement$ebnf$1$subexpression$1", "symbols": [{"literal":"else"}, "StatementNotEmpty"]},
    {"name": "IfStatement$ebnf$1", "symbols": ["IfStatement$ebnf$1$subexpression$1"], "postprocess": id},
    {"name": "IfStatement$ebnf$1", "symbols": [], "postprocess": function(d) {return null;}},
    {"name": "IfStatement", "symbols": [{"literal":"if"}, {"literal":"("}, "Exp", {"literal":")"}, "StatementNotEmpty", "IfStatement$ebnf$1"], "postprocess":  d => ({
            type: "ifStatement",
            ifcond: d[2],
            ifbody: d[4],
            elsebody: d[5] ? d[5][1] : null
        }) },
    {"name": "FunctionStatement", "symbols": [{"literal":"function"}, (LEXER.has("id") ? {type: "id"} : id), {"literal":"("}, "ParamList", {"literal":")"}, "Block"], "postprocess": d => ["functionDeclaration", d[1], d[3], d[5]]},
    {"name": "FunctionStatement", "symbols": [{"literal":"function"}, {"literal":"("}, "ParamList", {"literal":")"}, "Block"], "postprocess": d => ["functionDeclaration", "", d[2], d[4]] /* anonymous fn */},
    {"name": "FunctionStatement", "symbols": [(LEXER.has("id") ? {type: "id"} : id), (LEXER.has("rarrow") ? {type: "rarrow"} : rarrow), "ConditionalExp"], "postprocess": d => ["functionDeclaration", "", [d[0]], d[2]] /* x => expr */},
    {"name": "FunctionStatement", "symbols": [{"literal":"("}, "ParamList", {"literal":")"}, (LEXER.has("rarrow") ? {type: "rarrow"} : rarrow), "ConditionalExp"], "postprocess": d => ["functionDeclaration", "", d[1], d[4]] /* (x,y) => expr */},
    {"name": "ParamList$ebnf$1$subexpression$1$ebnf$1", "symbols": []},
    {"name": "ParamList$ebnf$1$subexpression$1$ebnf$1$subexpression$1", "symbols": [{"literal":","}, (LEXER.has("id") ? {type: "id"} : id)]},
    {"name": "ParamList$ebnf$1$subexpression$1$ebnf$1", "symbols": ["ParamList$ebnf$1$subexpression$1$ebnf$1", "ParamList$ebnf$1$subexpression$1$ebnf$1$subexpression$1"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "ParamList$ebnf$1$subexpression$1", "symbols": [(LEXER.has("id") ? {type: "id"} : id), "ParamList$ebnf$1$subexpression$1$ebnf$1"]},
    {"name": "ParamList$ebnf$1", "symbols": ["ParamList$ebnf$1$subexpression$1"], "postprocess": id},
    {"name": "ParamList$ebnf$1", "symbols": [], "postprocess": function(d) {return null;}},
    {"name": "ParamList", "symbols": ["ParamList$ebnf$1"], "postprocess": d => d[0] ? [d[0][0], ...(d[0][1] || []).map(item => item[1])] : []},
    {"name": "ExpStatement", "symbols": ["Exp"], "postprocess": id},
    {"name": "EmptyStatement", "symbols": []},
    {"name": "Block", "symbols": [{"literal":"{"}, "StatementList", {"literal":"}"}], "postprocess": d => d[1]},
    {"name": "StatementList", "symbols": ["Statement"], "postprocess": id},
    {"name": "StatementList$ebnf$1$subexpression$1", "symbols": [(LEXER.has("sc") ? {type: "sc"} : sc), "Statement"], "postprocess": d => d[1]},
    {"name": "StatementList$ebnf$1", "symbols": ["StatementList$ebnf$1$subexpression$1"]},
    {"name": "StatementList$ebnf$1$subexpression$2", "symbols": [(LEXER.has("sc") ? {type: "sc"} : sc), "Statement"], "postprocess": d => d[1]},
    {"name": "StatementList$ebnf$1", "symbols": ["StatementList$ebnf$1", "StatementList$ebnf$1$subexpression$2"], "postprocess": function arrpush(d) {return d[0].concat([d[1]]);}},
    {"name": "StatementList", "symbols": ["Statement", "StatementList$ebnf$1"], "postprocess": d => [d[0], ...d[1].filter(x => x)].map(unwrap)},
    {"name": "Program", "symbols": ["StatementList"], "postprocess": id}
]
  , ParserStart: "main"
}
if (typeof module !== 'undefined'&& typeof module.exports !== 'undefined') {
   module.exports = grammar;
} else {
   window.instacalc_grammar = grammar;
}
})();
;
function toFractionIterative(x, epsilon = 0.0001) {

  if (typeof x !== 'number' || isNaN(x)) return null;
  if (x === 0) return [0, 1];

  const sign = x < 0 ? -1 : 1;
  const a = Math.abs(x);

  let h1 = 1, k1 = 0;
  let h2 = 0, k2 = 1;
  let b = a;
  let maxIterations = 100;

  while (maxIterations-- > 0) {
    const intPart = Math.floor(b);
    const num = intPart * h1 + h2;
    const den = intPart * k1 + k2;

    if (Math.abs(a - num / den) <= a * epsilon) {
      return [sign * num, den];
    }

    h2 = h1; k2 = k1;
    h1 = num; k1 = den;

    if (b - intPart === 0) break;
    b = 1 / (b - intPart);
  }

  return [sign * h1, k1];

  /*
    if (typeof x !== "number" || isNaN(x)) return null;
    if (x == 0) return [0, 1];
    const a = Math.abs(x);
    let n = 0;
    let d = 1;
    let r;
    let maxtries = 100000;
    while (true && maxtries-- > 0) {
      r = n / d;
      if (Math.abs((r - a) / a) < epsilon) {
        break;
      }
      if (r < a) {
        n++;
      } else {
        d++;
      }
    }
  
    if (maxtries <= 0) {
      console.log("hit max tries");
    }
  
    return [x < 0 ? -n : n, d];
    */
}

// Global exposure
if (typeof window !== 'undefined') {
  window.toFractionIterative = toFractionIterative;
}

// text to number lib
var Small = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  dozen: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

var Magnitude = {
  thousand: 1000,
  million: 1000000,
  billion: 1000000000,
  trillion: 1000000000000,
  quadrillion: 1000000000000000,
  quintillion: 1000000000000000000,
  sextillion: 1000000000000000000000,
  septillion: 1000000000000000000000000,
  octillion: 1000000000000000000000000000,
  nonillion: 1000000000000000000000000000000,
  decillion: 1000000000000000000000000000000000,
};

function text2num(s) {
  let a, n, g;

  function feach(w) {
    var x = Small[w];
    if (x != null) {
      g = g + x;
    } else if (w == "hundred") {
      g = g * 100;
    } else {
      x = Magnitude[w];
      if (x != null) {
        n = n + g * x;
        g = 0;
      } else {
        return null;
      }
    }
  }

  a = s.toString().split(/[\s-]+/);
  n = 0;
  g = 0;
  a.forEach(feach);
  return n + g;
}

// Global exposure
if (typeof window !== 'undefined') {
  window.text2num = text2num;
}

var Inflector = {

  uncountableWords: [
    'equipment', 'information', 'rice', 'money', 'species', 'series',
    'fish', 'sheep', 'moose', 'deer', 'news', 'celsius', 'fahrenheit',
    'siemens', 'hertz', 'lux'
  ],

  pluralRules: [
    [new RegExp('(m)an$', 'gi'), '$1en'],
    [new RegExp('(pe)rson$', 'gi'), '$1ople'],
    [new RegExp('(child)$', 'gi'), '$1ren'],
    [new RegExp('^(ox)$', 'gi'), '$1en'],
    [new RegExp('(ax|test)is$', 'gi'), '$1es'],
    [new RegExp('(octop|vir)us$', 'gi'), '$1i'],
    [new RegExp('(alias|status)$', 'gi'), '$1es'],
    [new RegExp('(bu)s$', 'gi'), '$1ses'],
    [new RegExp('(buffal|tomat|potat)o$', 'gi'), '$1oes'],
    [new RegExp('([ti])um$', 'gi'), '$1a'],
    [new RegExp('sis$', 'gi'), 'ses'],
    [new RegExp('(?:([^f])fe|([lr])f)$', 'gi'), '$1$2ves'],
    [new RegExp('(hive)$', 'gi'), '$1s'],
    [new RegExp('([^aeiouy]|qu)y$', 'gi'), '$1ies'],
    [new RegExp('(x|ch|ss|sh)$', 'gi'), '$1es'],
    [new RegExp('(matr|vert|ind)ix|ex$', 'gi'), '$1ices'],
    [new RegExp('([m|l])ouse$', 'gi'), '$1ice'],
    [new RegExp('(quiz)$', 'gi'), '$1zes'],

    // extra ones
    [new RegExp('(foot)$', 'gi'), 'feet'],
    [new RegExp('(goose)$', 'gi'), 'geese'],
    [new RegExp('(tooth)$', 'gi'), 'teeth'],
    [new RegExp('(louse)$', 'gi'), 'lice'],
    [new RegExp('(mouse)$', 'gi'), 'mice'],
    [new RegExp('(woman)$', 'gi'), 'women'],
    [new RegExp('(man)$', 'gi'), 'men'],
    [new RegExp('(child)$', 'gi'), 'children'],
    [new RegExp('(ox)$', 'gi'), 'oxen'],
    [new RegExp('(leaf)$', 'gi'), 'leaves'],
    [new RegExp('(calf)$', 'gi'), 'calves'],
    [new RegExp('(half)$', 'gi'), 'halves'],
    [new RegExp('(knife)$', 'gi'), 'knives'],
    [new RegExp('(wife)$', 'gi'), 'wives'],
    [new RegExp('(life)$', 'gi'), 'lives'],
    [new RegExp('(loaf)$', 'gi'), 'loaves'],
    [new RegExp('(wolf)$', 'gi'), 'wolves'],
    [new RegExp('(shelf)$', 'gi'), 'shelves'],
    [new RegExp('(thief)$', 'gi'), 'thieves'],

    [new RegExp('s$', 'gi'), 's'],
    [new RegExp('$', 'gi'), 's']

  ],

  singularRules: [
    [new RegExp('(m)en$', 'gi'), '$1an'],
    [new RegExp('(pe)ople$', 'gi'), '$1rson'],
    [new RegExp('(child)ren$', 'gi'), '$1'],
    [new RegExp('([ti])a$', 'gi'), '$1um'],
    [new RegExp('((a)naly|(b)a|(d)iagno|(p)arenthe|(p)rogno|(s)ynop|(t)he)ses$', 'gi'), '$1$2sis'],
    [new RegExp('(hive)s$', 'gi'), '$1'],
    [new RegExp('(tive)s$', 'gi'), '$1'],
    [new RegExp('(curve)s$', 'gi'), '$1'],
    [new RegExp('([lr])ves$', 'gi'), '$1f'],
    [new RegExp('([^fo])ves$', 'gi'), '$1fe'],
    [new RegExp('([^aeiouy]|qu)ies$', 'gi'), '$1y'],
    [new RegExp('(s)eries$', 'gi'), '$1eries'],
    [new RegExp('(m)ovies$', 'gi'), '$1ovie'],
    [new RegExp('(x|ch|ss|sh)es$', 'gi'), '$1'],
    [new RegExp('([m|l])ice$', 'gi'), '$1ouse'],
    [new RegExp('(bus)es$', 'gi'), '$1'],
    [new RegExp('(o)es$', 'gi'), '$1'],
    [new RegExp('(shoe)s$', 'gi'), '$1'],
    [new RegExp('(cris|ax|test)es$', 'gi'), '$1is'],
    [new RegExp('(octop|vir)i$', 'gi'), '$1us'],
    [new RegExp('(alias|status)es$', 'gi'), '$1'],
    [new RegExp('^(ox)en', 'gi'), '$1'],
    [new RegExp('(vert|ind)ices$', 'gi'), '$1ex'],
    [new RegExp('(matr)ices$', 'gi'), '$1ix'],
    [new RegExp('(quiz)zes$', 'gi'), '$1'],

    // extra ones
    [new RegExp('(feet)$', 'gi'), 'foot'],
    [new RegExp('geese$', 'gi'), 'goose'],
    [new RegExp('teeth$', 'gi'), 'tooth'],
    [new RegExp('lice$', 'gi'), 'louse'],
    [new RegExp('mice$', 'gi'), 'mouse'],
    [new RegExp('women$', 'gi'), 'woman'],
    [new RegExp('men$', 'gi'), 'man'],
    [new RegExp('children$', 'gi'), 'child'],
    [new RegExp('oxen$', 'gi'), 'ox'],
    [new RegExp('leaves$', 'gi'), 'leaf'],
    [new RegExp('calves$', 'gi'), 'calf'],
    [new RegExp('halves$', 'gi'), 'half'],
    [new RegExp('knives$', 'gi'), 'knife'],
    [new RegExp('wives$', 'gi'), 'wife'],
    [new RegExp('lives$', 'gi'), 'life'],
    [new RegExp('loaves$', 'gi'), 'loaf'],
    [new RegExp('wolves$', 'gi'), 'wolf'],
    [new RegExp('shelves$', 'gi'), 'shelf'],
    [new RegExp('thieves$', 'gi'), 'thief'],
    [new RegExp('s$', 'gi'), '']
  ],

  nonTitlecasedWords: [
    'and', 'or', 'nor', 'a', 'an', 'the', 'so', 'but', 'to', 'of', 'at',
    'by', 'from', 'into', 'on', 'onto', 'off', 'out', 'in', 'over',
    'with', 'for'
  ],

  idSuffix: new RegExp('(_ids|_id)$', 'g'),
  underbar: new RegExp('_', 'g'),
  spaceOrUnderbar: new RegExp('[\ _]', 'g'),
  uppercase: new RegExp('([A-Z])', 'g'),
  underbarPrefix: new RegExp('^_'),

  applyRules: function (str, rules, skip, override) {
    if (override) {
      str = override;
    } else {
      var ignore = (skip.indexOf(str.toLowerCase()) > -1);
      if (!ignore) {
        for (var x = 0; x < rules.length; x++) {
          if (str.match(rules[x][0])) {
            str = str.replace(rules[x][0], rules[x][1]);
            break;
          }
        }
      }
    }
    return str;
  },


  /*
  Inflector.pluralize('person')           -> 'people'
  Inflector.pluralize('octopus')          -> 'octopi'
  Inflector.pluralize('Hat')              -> 'Hats'
  Inflector.pluralize('person', 'guys')   -> 'guys'    
  */
  pluralize: function (str, plural) {
    return this.applyRules(
      str,
      this.pluralRules,
      this.uncountableWords,
      plural
    );
  },

  /*
  Inflector.singularize('person')         -> 'person'
  Inflector.singularize('octopi')         -> 'octopus'
  Inflector.singularize('hats')           -> 'hat'
  Inflector.singularize('guys', 'person') -> 'person'
  */
  singularize: function (str, singular) {
    return this.applyRules(
      str,
      this.singularRules,
      this.uncountableWords,
      singular
    );
  },
}

// Global exposure
if (typeof window !== 'undefined') {
  window.Inflector = Inflector;
}

// mredkj.com
function NumberFormat(num, inputDecimal) {
  this.VERSION = "Number Format v1.5.4";
  this.COMMA = ",";
  this.PERIOD = ".";
  this.DASH = "-";
  this.LEFT_PAREN = "(";
  this.RIGHT_PAREN = ")";
  this.LEFT_OUTSIDE = 0;
  this.LEFT_INSIDE = 1;
  this.RIGHT_INSIDE = 2;
  this.RIGHT_OUTSIDE = 3;
  this.LEFT_DASH = 0;
  this.RIGHT_DASH = 1;
  this.PARENTHESIS = 2;
  this.NO_ROUNDING = -1;
  this.num;
  this.numOriginal;
  this.hasSeparators = false;
  this.separatorValue;
  this.inputDecimalValue;
  this.decimalValue;
  this.negativeFormat;
  this.negativeRed;
  this.hasCurrency;
  this.currencyPosition;
  this.currencyValue;
  this.places;
  this.roundToPlaces;
  this.truncate;
  this.setNumber = setNumberNF;
  this.toUnformatted = toUnformattedNF;
  this.setInputDecimal = setInputDecimalNF;
  this.setSeparators = setSeparatorsNF;
  this.setCommas = setCommasNF;
  this.setNegativeFormat = setNegativeFormatNF;
  this.setNegativeRed = setNegativeRedNF;
  this.setCurrency = setCurrencyNF;
  this.setCurrencyPrefix = setCurrencyPrefixNF;
  this.setCurrencyValue = setCurrencyValueNF;
  this.setCurrencyPosition = setCurrencyPositionNF;
  this.setPlaces = setPlacesNF;
  this.toFormatted = toFormattedNF;
  this.toPercentage = toPercentageNF;
  this.getOriginal = getOriginalNF;
  this.moveDecimalRight = moveDecimalRightNF;
  this.moveDecimalLeft = moveDecimalLeftNF;
  this.getRounded = getRoundedNF;
  this.preserveZeros = preserveZerosNF;
  this.justNumber = justNumberNF;
  this.expandExponential = expandExponentialNF;
  this.getZeros = getZerosNF;
  this.moveDecimalAsString = moveDecimalAsStringNF;
  this.moveDecimal = moveDecimalNF;
  this.addSeparators = addSeparatorsNF;
  if (inputDecimal == null) {
    this.setNumber(num, this.PERIOD);
  } else {
    this.setNumber(num, inputDecimal);
  }
  this.setCommas(true);
  this.setNegativeFormat(this.LEFT_DASH);
  this.setNegativeRed(false);
  this.setCurrency(false);
  this.setCurrencyPrefix("$");
  this.setPlaces(2);
}

function setInputDecimalNF(val) {
  this.inputDecimalValue = val;
}

function setNumberNF(num, inputDecimal) {
  if (inputDecimal != null) {
    this.setInputDecimal(inputDecimal);
  }
  this.numOriginal = num;
  this.num = this.justNumber(num);
}

function toUnformattedNF() {
  return this.num;
}

function getOriginalNF() {
  return this.numOriginal;
}

function setNegativeFormatNF(format) {
  this.negativeFormat = format;
}

function setNegativeRedNF(isRed) {
  this.negativeRed = isRed;
}

function setSeparatorsNF(isC, separator, decimal) {
  this.hasSeparators = isC;
  if (separator == null) separator = this.COMMA;
  if (decimal == null) decimal = this.PERIOD;
  if (separator == decimal) {
    this.decimalValue = decimal == this.PERIOD ? this.COMMA : this.PERIOD;
  } else {
    this.decimalValue = decimal;
  }
  this.separatorValue = separator;
}

function setCommasNF(isC) {
  this.setSeparators(isC, this.COMMA, this.PERIOD);
}

function setCurrencyNF(isC) {
  this.hasCurrency = isC;
}

function setCurrencyValueNF(val) {
  this.currencyValue = val;
}

function setCurrencyPrefixNF(cp) {
  this.setCurrencyValue(cp);
  this.setCurrencyPosition(this.LEFT_OUTSIDE);
}

function setCurrencyPositionNF(cp) {
  this.currencyPosition = cp;
}

function setPlacesNF(p, tr) {
  this.roundToPlaces = !(p == this.NO_ROUNDING);
  this.truncate = tr != null && tr;
  this.places = p < 0 ? 0 : p;
}

function addSeparatorsNF(nStr, inD, outD, sep) {
  nStr += "";
  var dpos = nStr.indexOf(inD);
  var nStrEnd = "";
  if (dpos != -1) {
    nStrEnd = outD + nStr.substring(dpos + 1, nStr.length);
    nStr = nStr.substring(0, dpos);
  }
  var rgx = /(\d+)(\d{3})/;
  while (rgx.test(nStr)) {
    nStr = nStr.replace(rgx, "$1" + sep + "$2");
  }
  return nStr + nStrEnd;
}

function toFormattedNF() {
  var pos;
  var nNum = this.num;
  var nStr;
  var splitString = new Array(2);
  if (this.roundToPlaces) {
    nNum = this.getRounded(nNum);
    nStr = this.preserveZeros(Math.abs(nNum));
  } else {
    nStr = this.expandExponential(Math.abs(nNum));
  }
  if (this.hasSeparators) {
    nStr = this.addSeparators(nStr, this.PERIOD, this.decimalValue, this.separatorValue);
  } else {
    nStr = nStr.replace(new RegExp("\\" + this.PERIOD), this.decimalValue);
  }
  var c0 = "";
  var n0 = "";
  var c1 = "";
  var n1 = "";
  var n2 = "";
  var c2 = "";
  var n3 = "";
  var c3 = "";
  var negSignL = this.negativeFormat == this.PARENTHESIS ? this.LEFT_PAREN : this.DASH;
  var negSignR = this.negativeFormat == this.PARENTHESIS ? this.RIGHT_PAREN : this.DASH;
  if (this.currencyPosition == this.LEFT_OUTSIDE) {
    if (nNum < 0) {
      if (this.negativeFormat == this.LEFT_DASH || this.negativeFormat == this.PARENTHESIS) n1 = negSignL;
      if (this.negativeFormat == this.RIGHT_DASH || this.negativeFormat == this.PARENTHESIS) n2 = negSignR;
    }
    if (this.hasCurrency) c0 = this.currencyValue;
  } else if (this.currencyPosition == this.LEFT_INSIDE) {
    if (nNum < 0) {
      if (this.negativeFormat == this.LEFT_DASH || this.negativeFormat == this.PARENTHESIS) n0 = negSignL;
      if (this.negativeFormat == this.RIGHT_DASH || this.negativeFormat == this.PARENTHESIS) n3 = negSignR;
    }
    if (this.hasCurrency) c1 = this.currencyValue;
  } else if (this.currencyPosition == this.RIGHT_INSIDE) {
    if (nNum < 0) {
      if (this.negativeFormat == this.LEFT_DASH || this.negativeFormat == this.PARENTHESIS) n0 = negSignL;
      if (this.negativeFormat == this.RIGHT_DASH || this.negativeFormat == this.PARENTHESIS) n3 = negSignR;
    }
    if (this.hasCurrency) c2 = this.currencyValue;
  } else if (this.currencyPosition == this.RIGHT_OUTSIDE) {
    if (nNum < 0) {
      if (this.negativeFormat == this.LEFT_DASH || this.negativeFormat == this.PARENTHESIS) n1 = negSignL;
      if (this.negativeFormat == this.RIGHT_DASH || this.negativeFormat == this.PARENTHESIS) n2 = negSignR;
    }
    if (this.hasCurrency) c3 = this.currencyValue;
  }
  nStr = c0 + n0 + c1 + n1 + nStr + n2 + c2 + n3 + c3;
  if (this.negativeRed && nNum < 0) {
    nStr = '<font color="red">' + nStr + "</font>";
  }
  return nStr;
}

function toPercentageNF() {
  nNum = this.num * 100;
  nNum = this.getRounded(nNum);
  return nNum + "%";
}

function getZerosNF(places) {
  var extraZ = "";
  var i;
  for (i = 0; i < places; i++) {
    extraZ += "0";
  }
  return extraZ;
}

function expandExponentialNF(origVal) {
  if (isNaN(origVal)) return origVal;
  var newVal = parseFloat(origVal) + "";
  var eLoc = newVal.toLowerCase().indexOf("e");
  if (eLoc != -1) {
    var plusLoc = newVal.toLowerCase().indexOf("+");
    var negLoc = newVal.toLowerCase().indexOf("-", eLoc);
    var justNumber = newVal.substring(0, eLoc);
    if (negLoc != -1) {
      var places = newVal.substring(negLoc + 1, newVal.length);
      justNumber = this.moveDecimalAsString(justNumber, true, parseInt(places));
    } else {
      if (plusLoc == -1) plusLoc = eLoc;
      var places = newVal.substring(plusLoc + 1, newVal.length);
      justNumber = this.moveDecimalAsString(justNumber, false, parseInt(places));
    }
    newVal = justNumber;
  }
  return newVal;
}

function moveDecimalRightNF(val, places) {
  var newVal = "";
  if (places == null) {
    newVal = this.moveDecimal(val, false);
  } else {
    newVal = this.moveDecimal(val, false, places);
  }
  return newVal;
}

function moveDecimalLeftNF(val, places) {
  var newVal = "";
  if (places == null) {
    newVal = this.moveDecimal(val, true);
  } else {
    newVal = this.moveDecimal(val, true, places);
  }
  return newVal;
}

function moveDecimalAsStringNF(val, left, places) {
  var spaces = arguments.length < 3 ? this.places : places;
  if (spaces <= 0) return val;
  var newVal = val + "";
  var extraZ = this.getZeros(spaces);
  var re1 = new RegExp("([0-9.]+)");
  if (left) {
    newVal = newVal.replace(re1, extraZ + "$1");
    var re2 = new RegExp("(-?)([0-9]*)([0-9]{" + spaces + "})(\\.?)");
    newVal = newVal.replace(re2, "$1$2.$3");
  } else {
    var reArray = re1.exec(newVal);
    if (reArray != null) {
      newVal =
        newVal.substring(0, reArray.index) + reArray[1] + extraZ + newVal.substring(reArray.index + reArray[0].length);
    }
    var re2 = new RegExp("(-?)([0-9]*)(\\.?)([0-9]{" + spaces + "})");
    newVal = newVal.replace(re2, "$1$2$4.");
  }
  newVal = newVal.replace(/\.$/, "");
  return newVal;
}

function moveDecimalNF(val, left, places) {
  var newVal = "";
  if (places == null) {
    newVal = this.moveDecimalAsString(val, left);
  } else {
    newVal = this.moveDecimalAsString(val, left, places);
  }
  return parseFloat(newVal);
}

function getRoundedNF(val) {
  val = this.moveDecimalRight(val);
  if (this.truncate) {
    val = val >= 0 ? Math.floor(val) : Math.ceil(val);
  } else {
    val = Math.round(val);
  }
  val = this.moveDecimalLeft(val);
  return val;
}

function preserveZerosNF(val) {
  var i;
  val = this.expandExponential(val);
  if (this.places <= 0) return val;
  var decimalPos = val.indexOf(".");
  if (decimalPos == -1) {
    val += ".";
    for (i = 0; i < this.places; i++) {
      val += "0";
    }
  } else {
    var actualDecimals = val.length - 1 - decimalPos;
    var difference = this.places - actualDecimals;
    for (i = 0; i < difference; i++) {
      val += "0";
    }
  }
  return val;
}

function justNumberNF(val) {
  var newVal = val + "";
  var isPercentage = false;
  if (newVal.indexOf("%") != -1) {
    newVal = newVal.replace(/\%/g, "");
    isPercentage = true;
  }
  var re = new RegExp("[^\\" + this.inputDecimalValue + "\\d\\-\\+\\(\\)eE]", "g");
  newVal = newVal.replace(re, "");
  var tempRe = new RegExp("[" + this.inputDecimalValue + "]", "g");
  var treArray = tempRe.exec(newVal);
  if (treArray != null) {
    var tempRight = newVal.substring(treArray.index + treArray[0].length);
    newVal = newVal.substring(0, treArray.index) + this.PERIOD + tempRight.replace(tempRe, "");
  }
  if (newVal.charAt(newVal.length - 1) == this.DASH) {
    newVal = newVal.substring(0, newVal.length - 1);
    newVal = "-" + newVal;
  } else if (newVal.charAt(0) == this.LEFT_PAREN && newVal.charAt(newVal.length - 1) == this.RIGHT_PAREN) {
    newVal = newVal.substring(1, newVal.length - 1);
    newVal = "-" + newVal;
  }
  newVal = parseFloat(newVal);
  if (!isFinite(newVal)) {
    newVal = 0;
  }
  if (isPercentage) {
    newVal = this.moveDecimalLeft(newVal, 2);
  }
  return newVal;
}
/* instacalc core library */

// locals for each calculator
function InstaCalc(e) {
  this.settings = {
    startcells: 7,
    maxcells: 100,
    embed: !1,
    gadget: !1,
    visibility: [],
    basic: !0,
    round: !1,
    base_url: "http://instacalc.com",
    embed_preview_url: "http://instacalc.com/embedpreview.html"
  },
    this.cells = 0,
    this.inputs = [],
    this.outputs = [],
    this.outputCache = [],
    this.outputCache.format = [],
    this.inputCache = [],
    this.titles = [],
    this.comments = [],
    this.current_row = -1,
    this.lib = {
      chart: []
    },
    this.format = {
      currency: !1,
      symbol: "$",
      angle: "deg",
      decimals: 2
    },
    this.createNumberFormat()
}
InstaCalc.isdefined = function (variable) {
  return typeof variable == "undefined" ? false : true;
};

InstaCalc.isNull = function (obj) {
  if (obj === null) {
    return true;
  }

  if (InstaCalc.isdefined(obj) === false) {
    return true;
  }

  if (obj === "") {
    return true;
  }

  return false;
};

// load currency symbols & decimal precision
InstaCalc.format = {};
InstaCalc.format.currencies = {};

InstaCalc.format.currencies["BRL"] = {}; // Brazilian real - above USD since happens first
InstaCalc.format.currencies["BRL"].symbol = "R$"; // Brazilian real
InstaCalc.format.currencies["BRL"].regex = /R[$]/gi; // Brazilian real
InstaCalc.format.currencies["BRL"].decimals = 2;
InstaCalc.format.currencies["BRL"].code = "BRL";

InstaCalc.format.currencies["USD"] = {}; // US dollar
InstaCalc.format.currencies["USD"].symbol = "$"; // used for printing strings
InstaCalc.format.currencies["USD"].regex = /[$]/gi; // US dollar; NOT preceded by R for BRL
InstaCalc.format.currencies["USD"].decimals = 2; // 2 digits of precision
InstaCalc.format.currencies["USD"].code = "USD";

InstaCalc.format.currencies["GBP"] = {}; // British Pound
InstaCalc.format.currencies["GBP"].symbol = "£"; // used for printing strings
InstaCalc.format.currencies["GBP"].regex = /[£]/gi; // US dollar; NOT preceded by R for BRL
InstaCalc.format.currencies["GBP"].decimals = 2; // 2 digits of precision
InstaCalc.format.currencies["GBP"].code = "GBP";

InstaCalc.format.currencies["INR"] = {}; // Indian rupee
InstaCalc.format.currencies["INR"].symbol = "Rs. ";
//InstaCalc.format.currencies["INR"].regex = /(Rs[.]|\u20b9)/gi; // Indian rupee
InstaCalc.format.currencies["INR"].regex = /(?<!\w)(?:Rs\.|₹)(?=\s*\d)/gi; // Indian rupee -- requires digits after, word break before
InstaCalc.format.currencies["INR"].decimals = 2;
InstaCalc.format.currencies["INR"].code = "INR";

InstaCalc.format.currencies["EUR"] = {}; // Euro
InstaCalc.format.currencies["EUR"].symbol = "\u20AC"; // Euro
InstaCalc.format.currencies["EUR"].regex = /\u20AC/gi; // Euro unicode: 20AC
InstaCalc.format.currencies["EUR"].decimals = 2;
InstaCalc.format.currencies["EUR"].code = "EUR";

InstaCalc.format.currencies["JPY"] = {}; // Japanese Yen
InstaCalc.format.currencies["JPY"].symbol = "¥";
InstaCalc.format.currencies["JPY"].regex = /\u00A5/gi; // Unicode 00A5
InstaCalc.format.currencies["JPY"].decimals = 0;
InstaCalc.format.currencies["JPY"].code = "JPY";

var Unit = {};

Unit.label = ""; // label used to describe units in comment

Unit.alias = {};
Unit.alias.mph = "miles/hour";
Unit.alias.mpm = "miles/minute";
Unit.alias.fpm = "feet/minute";
Unit.alias.mpg = "miles/gallon";
//Unit.alias.mpge = "miles/33.7 kWh";

Unit.alias.mpl = "miles/liter";
Unit.alias.kmpl = "km/liter";

Unit.alias.knot = Unit.alias.knots = "nautical mile / hour";

// frames per second more common?
Unit.alias.fps = "feet/second";
Unit.alias.kps = "km/second";

Unit.alias.cfs = "cubic feet/second";
Unit.alias.cfm = "cubic feet/minute";
Unit.alias.cfh = "cubic feet/hour";
Unit.alias.cfd = "cubic feet/day";

Unit.alias.lps = "liters/second";
Unit.alias.lpm = "liters/minute";
Unit.alias.lph = "liters/hour";
Unit.alias.lpd = "liters/day";

Unit.alias.gps = "gallons/second";
Unit.alias.gpm = "gallons/minute";
Unit.alias.gph = "gallons/hour";
Unit.alias.gpd = "gallons/day";

Unit.alias.m3s = "cubic meters/second";
Unit.alias.m3m = "cubic meters/minute";
Unit.alias.m3h = "cubic meters/hour";
Unit.alias.m3d = "cubic meters/day";

Unit.alias.kph = "km/hour";
Unit.alias.kmph = "km/hour";
Unit.alias.kmh = "km/hour";

Unit.alias.rpm = "revolutions/minute";
Unit.alias.rps = "revolutions/second";

Unit.alias.bpm = "beats/minute";
Unit.alias.baud = "bits/sec";

/*
Unit.alias.bps = "bit/sec";
Unit.alias.kbps = "thousand bit/sec";
Unit.alias.mbps = "million bit/sec";
Unit.alias.gbps = "billion bit/sec";
Unit.alias.tbps = "trillion bit/sec";
*/

//Unit.alias.pbps = "1e15 bit/sec";

Unit.alias.ips = "instructions/sec";
Unit.alias.kips = "thousand instructions/sec";
Unit.alias.mips = "million instructions/sec";
Unit.alias.gips = "billion instructions/sec";

// Unit.alias.hz = "cycles/second";
/* TODO
Unit.alias.khz = "1000 times/second";
Unit.alias.mhz = "1000000 times/second";
Unit.alias.ghz = "1000000000 times/second";
*/

//Unit.alias["watt-hour"] = Unit.alias["watt hour"] = "wh";

Unit.freq = {};
Unit.freq.SI = { cycle: 1, s: -1 };
Unit.freq.base = 1;
Unit.freq.name = "frequency";
Unit.freq.hz = Unit.freq.Hertz = Unit.freq.hertz = 1;
Unit.freq.khz = 1e3 * Unit.freq.hz;
Unit.freq.mhz = 1e6 * Unit.freq.hz;
Unit.freq.ghz = 1e9 * Unit.freq.hz;
Unit.freq.thz = 1e12 * Unit.freq.hz;
Unit.freq.phz = 1e15 * Unit.freq.hz;
Unit.freq.ehz = 1e18 * Unit.freq.hz;
Unit.freq.zhz = 1e21 * Unit.freq.hz;
Unit.freq.yhz = 1e24 * Unit.freq.hz;
Unit.freq.rhz = 1e27 * Unit.freq.hz;
Unit.freq.qhz = 1e30 * Unit.freq.hz;

Unit.datarate = {};
Unit.datarate.SI = { bit: 1, s: -1 };
Unit.datarate.base = 1;
Unit.datarate.name = "data rate";
Unit.datarate.bps = 1;
Unit.datarate.kbps = 1e3 * Unit.datarate.bps;
Unit.datarate.mbps = 1e6 * Unit.datarate.bps;
Unit.datarate.gbps = 1e9 * Unit.datarate.bps;
Unit.datarate.tbps = 1e12 * Unit.datarate.bps;
Unit.datarate.pbps = 1e15 * Unit.datarate.bps;
Unit.datarate.ebps = 1e18 * Unit.datarate.bps;
Unit.datarate.zbps = 1e21 * Unit.datarate.bps;
Unit.datarate.ybps = 1e24 * Unit.datarate.bps;
Unit.datarate.rbps = 1e27 * Unit.datarate.bps;
Unit.datarate.qbps = 1e30 * Unit.datarate.bps;

Unit.datarate.KBps = 8 * Unit.datarate.kbps;
Unit.datarate.MBps = 8 * Unit.datarate.mbps;
Unit.datarate.GBps = 8 * Unit.datarate.gbps;
Unit.datarate.TBps = 8 * Unit.datarate.tbps;
Unit.datarate.PBps = 8 * Unit.datarate.pbps;
Unit.datarate.EBps = 8 * Unit.datarate.ebps;
Unit.datarate.ZBps = 8 * Unit.datarate.zbps;
Unit.datarate.YBps = 8 * Unit.datarate.ybps;
Unit.datarate.RBps = 8 * Unit.datarate.rbps;
Unit.datarate.QBps = 8 * Unit.datarate.qbps;

Unit.concentration = {};
Unit.concentration.name = "concentration";
Unit.concentration.base = 1;
Unit.concentration.pph = 10000; // hundred
Unit.concentration.pcm = 10; // per cent mille (1 part in 100,000)
Unit.concentration.ppm = 1;   // million, default
Unit.concentration.ppb = 1e-3; // billion
Unit.concentration.ppt = 1e-6; // trillion
Unit.concentration.ppq = 1e-9; // quadrillion
Unit.concentration["basis point"] = Unit.concentration["basis points"] = Unit.concentration.bp = 100; // basis point, 1/100 of a percent
Unit.concentration["percentage point"] = Unit.concentration["percentage points"] = Unit.concentration.pp = 10000; // basis point, 1/100 of a percent

// mileage efficiency
Unit.distance_per_energy = {};
Unit.distance_per_energy.name = "distance/energy";
Unit.distance_per_energy.base = 1;
Unit.distance_per_energy.SI = { "kg": -1, "m": -1, "s": 2 } // mile / kWh as SI base
Unit.distance_per_energy.mpge = 1609.344 / (33.7 * 1000 * 3600); // miles / 33.7 kWh => meters / watt-second

// angle measure
Unit.angle = {};
Unit.angle.base = 1;
Unit.angle.SI = { rad: 1 };
Unit.angle.radian = Unit.angle.radians = Unit.angle.rad = 1;
Unit.angle.degree = Unit.angle.degrees = Unit.angle.deg = (Unit.angle.radian * Math.PI) / 180.0;
Unit.angle.quadrant = Unit.angle.quadrants = Unit.angle.degree * 90;

Unit.angle.gradian = Unit.angle.gradians = Unit.angle.grad = (Unit.angle.radian * Math.PI) / 200.0;
Unit.angle.arcsecond = Unit.angle.arcseconds = Unit.angle.arcsec = Unit.angle.arcsecs = Unit.angle["arc second"] = Unit.angle["arc seconds"] = Unit.angle.degree / 3600;
Unit.angle.arcminute = Unit.angle.arcminutes = Unit.angle.arcmin = Unit.angle.arcmins = Unit.angle["arc minute"] = Unit.angle["arc minutes"] = Unit.angle.degree / 60;
Unit.angle.milliradian = Unit.angle.milliradians = Unit.angle.mrad = Unit.angle.radian / 1000;
Unit.angle.microradian = Unit.angle.microradians = Unit.angle.urad = Unit.angle['µrad'] = Unit.angle.radian * 1e-6;
Unit.angle.turn = Unit.angle.turns = 2 * Math.PI * Unit.angle.radian;
Unit.angle.revolution = Unit.angle.revolutions = Unit.angle.rev = Unit.angle.revs = Unit.angle.turn;

Unit.angle.mil_nato = Unit.angle.mils_nato = 360 * Unit.angle.degree / 6400; // NATO mil
//Unit.angle.mil = Unit.angle.mils = Unit.angle.milliradian;
Unit.angle.point = Unit.angle.points = Unit.angle.compass_point = Unit.angle.degree * (360 / 32); // Compass point
Unit.angle.hourangle = Unit.angle.hourangles = Unit.angle.degree * 15; // Astronomical hour angle

Unit.angle.name = "angle";

Unit.solid_angle = ["\\^2", "sq", "squared", "square"];
Unit.solid_angle.SI = { rad: 2 };
Unit.solid_angle.name = "solid angle";
Unit.solid_angle.base = 1;
Unit.solid_angle.steradian = Unit.solid_angle.steradians = Unit.solid_angle.sr = 1;
Unit.solid_angle["square degree"] = Unit.solid_angle["square degrees"] = Unit.solid_angle["square deg"] = Math.pow((Unit.solid_angle.steradian * Math.PI) / 180.0, 2);

Unit.length = {};
Unit.length.SI = { m: 1 };

Unit.mass = {};
Unit.mass.SI = { kg: 1 };

Unit.time = {};
Unit.time.SI = { s: 1 };

Unit.area = ["\\^2", "sq", "squared", "square"]; // words that connote a square unit
Unit.area.SI = { m: 2 };

Unit.volume = ["\\^3", "cu", "cubed", "cubic"]; // ditto for cubic units
Unit.volume.SI = { m: 3 };

Unit.liquid = {};
Unit.liquid.SI = { m: 3 };

Unit.power = {};
Unit.force = {};
Unit.torque = {};
Unit.energy = {};
Unit.data = {};
Unit.currency = {};

// scientific ones
Unit.pressure = {};

Unit.pressure.base = 1;
Unit.pressure.SI = { m: -1, kg: 1, s: -2 };

Unit.pressure.name = "pressure";
Unit.pressure.pascal = Unit.pressure.pascals = Unit.pressure.pa = Unit.pressure.Pa = Unit.pressure.base;
Unit.pressure.bar = Unit.pressure.bars = 1e5;
Unit.pressure.torr = Unit.pressure.mmHg = Unit.pressure.mmhg = 133.3223684211;
Unit.pressure.atm = Unit.pressure.atmosphere = Unit.pressure.atmospheres = 101325;
Unit.pressure.psi = Unit.pressure["pounds per square inch"] = 6894.757293168361;
Unit.pressure.psf = Unit.pressure["pounds per square foot"] = 47.880258980335;
Unit.pressure.ksc = Unit.pressure["kilogram-force per square centimeter"] = 98066.5;
Unit.pressure.mmH2O = Unit.pressure["millimeters of water"] = 9.80665;
Unit.pressure.cmH2O = Unit.pressure["centimeters of water"] = 98.0665;
Unit.pressure.inH2O = Unit.pressure["inches of water"] = 249.08891;
Unit.pressure.inHg = Unit.pressure["inches of mercury"] = 3386.388666666667;

Unit.electric_charge = {};
Unit.electric_charge.SI = { a: 1, s: 1 };
Unit.electric_charge.name = "electric charge";
Unit.electric_charge.coulomb = Unit.electric_charge.coulombs = 1;

Unit.electric_charge.Ah = Unit.electric_charge.ah = Unit.electric_charge["ampere hour"] = Unit.electric_charge["ampere hours"] = 3600;
Unit.electric_charge["amp hour"] = Unit.electric_charge["amp hours"] = 3600;
Unit.electric_charge["ah"] = 3600;
Unit.electric_charge["mah"] = 3.6;

Unit.electric_potential = {};
Unit.electric_potential.SI = { m: 2, kg: 1, s: -3, a: -1 };
Unit.electric_potential.name = "electric potential";
Unit.electric_potential.volt = Unit.electric_potential.volts = Unit.electric_potential.v = 1;

Unit.electric_capacitance = {};
Unit.electric_capacitance.SI = { m: -2, kg: -1, s: 4, a: 2 };
Unit.electric_capacitance.name = "electric capacitance";
Unit.electric_capacitance.farad = Unit.electric_capacitance.farads = 1;

Unit.electric_resistance = {};
Unit.electric_resistance.SI = { m: 2, kg: 1, s: -3, a: -2 };
Unit.electric_resistance.name = "electric resistance";
Unit.electric_resistance.ohm = Unit.electric_resistance.ohms = Unit.electric_resistance["Ω"] = 1;

Unit.electric_conductance = {};
Unit.electric_conductance.SI = { m: -2, kg: -1, s: 3, a: 2 };
Unit.electric_conductance.name = "electric conductance";
Unit.electric_conductance.siemens = 1;

Unit.electric_current = {};
Unit.electric_current.SI = { a: 1 };
Unit.electric_current.name = "electric current";
Unit.electric_current.ampere = Unit.electric_current.amperes = Unit.electric_current.amp = Unit.electric_current.amps = Unit.electric_current.a = 1;
Unit.electric_current.ma = Unit.electric_current.milliampere = Unit.electric_current.milliamp = Unit.electric_current.milliamps = Unit.electric_current.mA = 1e-3;
Unit.electric_current.Ma = Unit.electric_current.megaampere = Unit.electric_current.megaamp = Unit.electric_current.megaamps = Unit.electric_current.MA = 1e6;

Unit.magnetic_flux = {};
Unit.magnetic_flux.SI = { m: 2, kg: 1, s: -2, a: -1 };
Unit.magnetic_flux.name = "magnetic flux";
Unit.magnetic_flux.weber = Unit.magnetic_flux.webers = Unit.magnetic_flux.wb = 1;

Unit.magnetic_flux_density = {};
Unit.magnetic_flux_density.SI = { kg: 1, s: -2, a: -1 };
Unit.magnetic_flux_density.name = "magnetic flux density";
Unit.magnetic_flux_density.tesla = Unit.magnetic_flux_density.teslas = 1;

Unit.electric_inductance = {};
Unit.electric_inductance.SI = { m: 2, kg: 1, s: -2, a: -2 };
Unit.electric_inductance.name = "electric inductance";
Unit.electric_inductance.henry = Unit.electric_inductance.henrys = 1;

Unit.luminous_flux = {};
Unit.luminous_flux.SI = { cd: 1, sr: 1 };
Unit.luminous_flux.name = "luminous flux";
Unit.luminous_flux.lumen = Unit.luminous_flux.lumens = Unit.luminous_flux.lm = 1;

Unit.illuminance = {};
Unit.illuminance.SI = { cd: 1, sr: 1, m: -2 };
Unit.illuminance.name = "illuminance";
Unit.illuminance.lux = Unit.illuminance.lx = 1;
Unit.illuminance["foot-candle"] = Unit.illuminance.foot_candle = Unit.illuminance.fc = 10.7639; // 1 fc = 10.7639 lux
Unit.illuminance.phot = Unit.illuminance.ph = 1e4; // 1 phot = 10,000 lux
Unit.illuminance.nox = 1e-3; // 1 nox = 0.001 lux
Unit.illuminance.millilux = Unit.illuminance.mlx = 1e-3;
Unit.illuminance.kilolux = Unit.illuminance.klx = 1e3;
Unit.illuminance.megalux = Unit.illuminance.Mlx = 1e6;

Unit.luminous_intensity = {};
Unit.luminous_intensity.SI = { cd: 1 };
Unit.luminous_intensity.name = "luminous intensity";
Unit.luminous_intensity.candela = Unit.luminous_intensity.candelas = Unit.luminous_intensity.cd = 1;
Unit.luminous_intensity.candle = Unit.luminous_intensity.candles = 1.019; // international standard candle

Unit.radioactivity = {};
Unit.radioactivity.SI = { s: -1 };
Unit.radioactivity.name = "radioactivity";
Unit.radioactivity.becquerel = Unit.radioactivity.becquerels = Unit.radioactivity.Bq = Unit.radioactivity.bq = 1;

Unit.absorbed_dose = {};
Unit.absorbed_dose.SI = { m: 2, s: -2 };
Unit.absorbed_dose.name = "absorbed dose";
Unit.absorbed_dose.gray = Unit.absorbed_dose.grays = Unit.absorbed_dose.Gy = Unit.absorbed_dose.gy = 1;

Unit.equivalent_dose = {};
Unit.equivalent_dose.SI = { m: 2, s: -2 };
Unit.equivalent_dose.name = "equivalent dose";
Unit.equivalent_dose.sievert = Unit.equivalent_dose.sieverts = Unit.equivalent_dose.Sv = Unit.equivalent_dose.sv = 1;

Unit.catalytic_activity = {};
Unit.catalytic_activity.SI = { mol: 1, s: -1 };
Unit.catalytic_activity.name = "catalytic activity";
Unit.catalytic_activity.katal = Unit.catalytic_activity.katals = Unit.catalytic_activity.kat = 1;

Unit.amount_of_substance = {};
Unit.amount_of_substance.SI = { mol: 1 };
Unit.amount_of_substance.name = "amount of substance";
Unit.amount_of_substance.mole = Unit.amount_of_substance.moles = Unit.amount_of_substance.mol = 1;
Unit.amount_of_substance.millimole = Unit.amount_of_substance.millimoles = Unit.amount_of_substance.mmol = 1e-3;
Unit.amount_of_substance.micromole = Unit.amount_of_substance.micromoles = Unit.amount_of_substance.umol = 1e-6;
Unit.amount_of_substance.nanomole = Unit.amount_of_substance.nanomoles = Unit.amount_of_substance.nmol = 1e-9;
Unit.amount_of_substance.picomole = Unit.amount_of_substance.picomoles = Unit.amount_of_substance.pmol = 1e-12;

Unit.molar_concentration = {};
Unit.molar_concentration.SI = { mol: 1, m: -3 };
Unit.molar_concentration.name = "molar concentration";
Unit.molar_concentration.molar = Unit.molar_concentration.molars = Unit.molar_concentration["molar concentration"] = 1e3; // mol/L

Unit.radioactive_exposure = {};
Unit.radioactive_exposure.SI = { kg: -1, s: 1, a: 1 };
Unit.radioactive_exposure.name = "radioactive exposure";
Unit.radioactive_exposure.roentgen = Unit.radioactive_exposure.roentgens = 1 / 3876;

// temperature - nonlinear conversions
Unit.temperature = {};
Unit.temperature.SI = { kelvin: 1 };
Unit.temperature.name = "temperature";
Unit.temperature.conversion = "function";

// conversion functions have a FROM and TO function, relative to the base unit (Celcius, in this case)
Unit.temperature.c =
  Unit.temperature.celsius =
  Unit.temperature.celcius =
  Unit.temperature.centigrade =
  [
    function (C) {
      return C;
    },
    function (C) {
      return C;
    },
  ]; // identity
Unit.temperature.f =
  Unit.temperature.fahrenheit = [
    function (F) {
      return ((F - 32.0) * 5) / 9;
    },
    function (C) {
      return (C * 9) / 5 + 32;
    },
  ]; // Fahrenheit to Celcius
Unit.temperature.k = Unit.temperature.kelvin = [
  function (K) {
    return K - 273.15;
  },
  function (C) {
    return C + 273.15;
  },
]; // Kelvin to Celcius

// "r" is too useful to be taken by rankine
Unit.temperature.rankine = [
  function (R) {
    return (R * 5) / 9 - 273.15;
  },
  function (C) {
    return ((C + 273.15) * 9) / 5;
  },
]; // Rankine to Celcius

Unit.data.base = 1; // 1 bit
Unit.data.name = "data";
Unit.data.SI = { bit: 1 };
Unit.data.bit = Unit.data.bits = 1;
Unit.data.nibble = Unit.data.nibbles = Unit.data.nybble = Unit.data.nybbles = 4 * Unit.data.bit;

Unit.data.kilobit = Unit.data.kilobits = Unit.data.kbit = Unit.data.kbits = 1024 * Unit.data.bit;
Unit.data.megabit = Unit.data.megabits = Unit.data.mbit = Unit.data.mbits = 1024 * Unit.data.kbit;
Unit.data.gigabit = Unit.data.gigabits = Unit.data.gbit = Unit.data.gbits = 1024 * Unit.data.mbit;
Unit.data.terabit = Unit.data.terabits = Unit.data.tbit = Unit.data.tbits = 1024 * Unit.data.gbit;
Unit.data.petabit = Unit.data.petabits = Unit.data.pbit = Unit.data.pbits = 1024 * Unit.data.tbit;
Unit.data.exabit = Unit.data.exabits = Unit.data.ebit = Unit.data.ebits = 1024 * Unit.data.pbit;
Unit.data.zettabit = Unit.data.zettabits = Unit.data.zbit = Unit.data.zbits = 1024 * Unit.data.ebit;
Unit.data.yottabit = Unit.data.yottabits = Unit.data.ybit = Unit.data.ybits = 1024 * Unit.data.zbit;
Unit.data["ronnabit"] = Unit.data["ronnabits"] = Unit.data["rbit"] = Unit.data["rbits"] = 1024 * Unit.data["ybit"];
Unit.data["quettabit"] = Unit.data["quettabits"] = Unit.data["qbit"] = Unit.data["qbits"] = 1e30 * Unit.data["rbit"];

Unit.data["byte"] = Unit.data["bytes"] = 8 * Unit.data.bit;
Unit.data["kilobyte"] = Unit.data["kilobytes"] = Unit.data["kb"] = Unit.data["kbs"] = 1024 * Unit.data["byte"];
Unit.data["megabyte"] = Unit.data["megabytes"] = Unit.data["mb"] = Unit.data["mbs"] = 1024 * Unit.data.kb;
Unit.data["gigabyte"] = Unit.data["gigabytes"] = Unit.data["gb"] = Unit.data["gbs"] = 1024 * Unit.data.mb;
Unit.data["terabyte"] = Unit.data["terabytes"] = Unit.data["tb"] = Unit.data["tbs"] = 1024 * Unit.data.gb;
Unit.data["petabyte"] = Unit.data["petabytes"] = Unit.data["pb"] = Unit.data["pbs"] = 1024 * Unit.data.tb;
Unit.data["exabyte"] = Unit.data["exabytes"] = Unit.data["eb"] = Unit.data["ebs"] = 1024 * Unit.data.pb;
Unit.data["zettabyte"] = Unit.data["zettabytes"] = Unit.data["zb"] = Unit.data["zbs"] = 1024 * Unit.data.eb;
Unit.data["yottabyte"] = Unit.data["yottabytes"] = Unit.data["yb"] = Unit.data["ybs"] = 1024 * Unit.data.zb;
Unit.data["ronnabyte"] = Unit.data["ronnabytes"] = Unit.data["rbyte"] = Unit.data["rbytes"] = 1024 * Unit.data.yottabyte;
Unit.data["quettabyte"] = Unit.data["quettabytes"] = Unit.data["qbyte"] = Unit.data["qbytes"] = 1024 * Unit.data.ronnabyte;


// sigh...
Unit.data["kibibit"] = Unit.data["kibit"] = Unit.data["kbit"] = Unit.data.kbit;
Unit.data["mibibit"] = Unit.data["mibit"] = Unit.data["mbit"] = Unit.data.mbit;
Unit.data["gibibit"] = Unit.data["gibit"] = Unit.data["gbit"] = Unit.data.gbit;
Unit.data["tibibit"] = Unit.data["tibit"] = Unit.data["tbit"] = Unit.data.tbit;
Unit.data["pibibit"] = Unit.data["pibit"] = Unit.data["pbit"] = Unit.data.pbit;
Unit.data["eibibit"] = Unit.data["eibit"] = Unit.data["ebit"] = Unit.data.ebit;
Unit.data["zibibit"] = Unit.data["zibit"] = Unit.data["zbit"] = Unit.data.zbit;
Unit.data["yibibit"] = Unit.data["yibit"] = Unit.data["ybit"] = Unit.data.ybit;
Unit.data["ribibit"] = Unit.data["ribit"] = Unit.data["rbit"] = Unit.data.rbit;
Unit.data["qibibit"] = Unit.data["qibit"] = Unit.data["qbit"] = Unit.data.qbit;

Unit.data["kibibyte"] = Unit.data["kibibytes"] = Unit.data["kibyte"] = Unit.data["kbyte"] = Unit.data["kbytes"] = Unit.data["kib"] = Unit.data["kb"];
Unit.data["mebibyte"] = Unit.data["mebibytes"] = Unit.data["mibyte"] = Unit.data["mbyte"] = Unit.data["mbytes"] = Unit.data["mib"] = Unit.data["mb"];
Unit.data["gibibyte"] = Unit.data["gibibytes"] = Unit.data["gibyte"] = Unit.data["gbyte"] = Unit.data["gbytes"] = Unit.data["gib"] = Unit.data["gb"];
Unit.data["tebibyte"] = Unit.data["tebibytes"] = Unit.data["tibyte"] = Unit.data["tbyte"] = Unit.data["tbytes"] = Unit.data["tib"] = Unit.data["tb"];
Unit.data["pebibyte"] = Unit.data["pebibytes"] = Unit.data["pibyte"] = Unit.data["pbyte"] = Unit.data["pbytes"] = Unit.data["pib"] = Unit.data["pb"];
Unit.data["exbibyte"] = Unit.data["exbibytes"] = Unit.data["eibyte"] = Unit.data["ebyte"] = Unit.data["ebytes"] = Unit.data["eib"] = Unit.data["eb"];
Unit.data["zebibyte"] = Unit.data["zebibytes"] = Unit.data["zibyte"] = Unit.data["zbyte"] = Unit.data["zbytes"] = Unit.data["zib"] = Unit.data["zb"];
Unit.data["yobibyte"] = Unit.data["yobibytes"] = Unit.data["yibyte"] = Unit.data["ybyte"] = Unit.data["ybytes"] = Unit.data["yib"] = Unit.data["yb"];

Unit.data["kilobit_si"] = Unit.data["kbit_si"] = 1e3 * Unit.data["bit"];
Unit.data["megabit_si"] = Unit.data["mbit_si"] = 1e6 * Unit.data["bit"];
Unit.data["gigabit_si"] = Unit.data["gbit_si"] = 1e9 * Unit.data["bit"];
Unit.data["terabit_si"] = Unit.data["tbit_si"] = 1e12 * Unit.data["bit"];
Unit.data["petabit_si"] = Unit.data["pbit_si"] = 1e15 * Unit.data["bit"];
Unit.data["exabit_si"] = Unit.data["ebit_si"] = 1e18 * Unit.data["bit"];
Unit.data["zettabit_si"] = Unit.data["zbit_si"] = 1e21 * Unit.data["bit"];
Unit.data["yottabit_si"] = Unit.data["ybit_si"] = 1e24 * Unit.data["bit"];
Unit.data["ronnabit_si"] = Unit.data["rbit_si"] = 1e27 * Unit.data["bit"];
Unit.data["quettabit_si"] = Unit.data["qbit_si"] = 1e30 * Unit.data["bit"];


Unit.data["kilobyte_si"] = Unit.data["kb_si"] = 1e3 * Unit.data["byte"];
Unit.data["megabyte_si"] = Unit.data["mb_si"] = 1e6 * Unit.data["byte"];
Unit.data["gigabyte_si"] = Unit.data["gb_si"] = 1e9 * Unit.data["byte"];
Unit.data["terabyte_si"] = Unit.data["tb_si"] = 1e12 * Unit.data["byte"];
Unit.data["petabyte_si"] = Unit.data["pb_si"] = 1e15 * Unit.data["byte"];
Unit.data["exabyte_si"] = Unit.data["eb_si"] = 1e18 * Unit.data["byte"];
Unit.data["zettabyte_si"] = Unit.data["zb_si"] = 1e21 * Unit.data["byte"];
Unit.data["yottabyte_si"] = Unit.data["yb_si"] = 1e24 * Unit.data["byte"];
Unit.data["ronnabyte_si"] = Unit.data["rbyte_si"] = 1e27 * Unit.data["byte"];
Unit.data["quettabyte_si"] = Unit.data["qbyte_si"] = 1e30 * Unit.data["byte"];

// this is removed after currencies are loaded
Unit.currency.base = 1; // 1 dollar
Unit.currency.name = "currency";
Unit.currency.dollar = Unit.currency.dollars = 1;
Unit.currency.penny = Unit.currency.pennies = Unit.currency.cent = Unit.currency.cents = 0.01 * Unit.currency.dollars;
Unit.currency.dime = Unit.currency.dimes = 0.1 * Unit.currency.dollars;
Unit.currency.nickel = Unit.currency.nickels = 0.05 * Unit.currency.dollars;
Unit.currency.quarter = Unit.currency.quarters = 0.25 * Unit.currency.dollars;
Unit.currency.usd = Unit.currency.dollar;

Unit.constant = {};
Unit.constant.base = 1; // unity
Unit.constant.unity = 1; // unity - use as default unit for no conversion
Unit.constant.times = Unit.constant.time = 1; // time(s) per second
Unit.constant.kilotimes = Unit.constant.kilotime = 1000 * Unit.constant.time; // time(s) per second

Unit.time.base = 1; // second
Unit.time.name = "time";
Unit.time.SI = { s: 1 };

Unit.time.second = Unit.time.seconds = Unit.time.s = Unit.time.sec = Unit.time.secs = 1;

Unit.time.ps = Unit.time.picosecond = Unit.time.picoseconds = 1e-12 * Unit.time.second;
Unit.time.ns = Unit.time.nanosecond = Unit.time.nanoseconds = 1e-9 * Unit.time.second;
Unit.time.us = Unit.time.microsecond = Unit.time.microseconds = 1e-6 * Unit.time.second;
Unit.time['\u03bcs'] = Unit.time.us;

Unit.time.ms = Unit.time.millisecond = Unit.time.milliseconds = 1e-3 * Unit.time.second;

Unit.time.minute = Unit.time.minutes = Unit.time.min = Unit.time.mins = 60 * Unit.time.second;

Unit.time.hour = Unit.time.hours = Unit.time.hr = Unit.time.hrs = Unit.time.h = 60 * Unit.time.minute;

Unit.time.day = 24 * Unit.time.hour;
Unit.time.days = 24 * Unit.time.hour;
Unit.time.dy = 24 * Unit.time.hour;
Unit.time.d = 24 * Unit.time.hour;

Unit.time.week = 7 * Unit.time.day;
Unit.time.weeks = 7 * Unit.time.day;
Unit.time.wk = 7 * Unit.time.day;

Unit.time["work day"] = Unit.time["work days"] = 8 * Unit.time.hour;
Unit.time["work week"] = Unit.time["work weeks"] = 40 * Unit.time.hour;
Unit.time["work year"] = Unit.time["work years"] = 50 * Unit.time["work week"];

Unit.time.fortnight = 2 * Unit.time.week;
Unit.time.fortnights = 2 * Unit.time.week;

Unit.time.month = Unit.time.months = Unit.time.mo = (365 / 12) * Unit.time.day;
Unit.time.year = Unit.time.years = Unit.time.yr = Unit.time.yrs = 365 * Unit.time.day;
Unit.time.quarter = Unit.time.quarters = 3 * Unit.time.month;
Unit.time["calendar year"] = Unit.time["calendar years"] = 365 * Unit.time.day;
Unit.time["leap year"] = Unit.time["leap years"] = 366 * Unit.time.day;
Unit.time["mean year"] = Unit.time["mean years"] = 365.2425 * Unit.time.day;
Unit.time["sidereal year"] = Unit.time["sidereal years"] = 365.25636 * Unit.time.day;
Unit.time["tropical year"] = Unit.time["tropical years"] = 365.242189 * Unit.time.day;
Unit.time["solar year"] = Unit.time["solar years"] = Unit.time["tropical year"];

Unit.time.decade = Unit.time.decades = 10 * Unit.time.year;
Unit.time.century = Unit.time.centuries = 100 * Unit.time.year;
Unit.time.millenium = Unit.time.milleniums = Unit.time.millenia = 1000 * Unit.time.year;

Unit.time_inverse = { base: 1, name: "time_inverse", SI: { s: -1 } };
Unit.time_inverse.yearly = 1 / Unit.time.year;
Unit.time_inverse.annually = 1 / Unit.time.year;
Unit.time_inverse.semiannually = 2 / (Unit.time.year);
Unit.time_inverse.quarterly = 1 / (Unit.time.quarter);
Unit.time_inverse.monthly = 1 / Unit.time.month;
Unit.time_inverse.weekly = 1 / Unit.time.week;
Unit.time_inverse.daily = 1 / Unit.time.day;
Unit.time_inverse.hourly = 1 / Unit.time.hour;
Unit.time_inverse.minutely = 1 / Unit.time.minute;
Unit.time_inverse.secondly = 1 / Unit.time.second;

// area measure - grab the corresponding conversion in length and square it

// energy measure
Unit.energy.base = 1; // joule
Unit.energy.name = "energy";
Unit.energy.SI = { kg: 1, m: 2, s: -2 };
Unit.energy.joule = Unit.energy.joules = Unit.energy.j = Unit.energy.J = 1;
Unit.energy.mJ = Unit.energy.mj = 1e-3 * Unit.energy.joule;
Unit.energy.MJ = Unit.energy.Mj = 1e6 * Unit.energy.joule;
Unit.energy.tnt = Unit.energy.TNT = Unit.energy["ton TNT"] = Unit.energy["tonne TNT"] = 4.184e9 * Unit.energy.joule;
Unit.energy.kiloton = Unit.energy.kilotons = Unit.energy["kiloton TNT"] = 1000 * Unit.energy.TNT;
Unit.energy.megaton = Unit.energy.megatons = Unit.energy["megaton TNT"] = 1000 * Unit.energy.kiloton;
Unit.energy.gigaton = Unit.energy.gigatons = Unit.energy["gigaton TNT"] = 1000 * Unit.energy.megaton;

Unit.energy["electron volt"] = Unit.energy["ev"] = 1.60217646e-19 * Unit.energy.joule;
Unit.energy["watt-hour"] = Unit.energy["watthour"] = Unit.energy["watt hour"] = Unit.energy["w h"] = Unit.energy["wh"] = 3600 * Unit.energy.joule;
Unit.energy["kilowatt-hour"] =
  Unit.energy["kilowatt hour"] =
  Unit.energy["kwh"] =
  Unit.energy["kwhr"] =
  Unit.energy["kw h"] =
  3600 * 1000 * Unit.energy.joule;

Unit.energy.erg = Unit.energy.ergs = 1e-7 * Unit.energy.joule;
Unit.energy.therm = Unit.energy.therms = 105505585.262 * Unit.energy.joule;


Unit.energy["megawatt-hour"] =
  Unit.energy["megawatt hour"] =
  Unit.energy["mwh"] =
  Unit.energy["mwhr"] =
  Unit.energy["mw h"] =
  3600 * 1e6 * Unit.energy.joule;

Unit.energy["gigawatt-hour"] =
  Unit.energy["gigawatt hour"] =
  Unit.energy["gwh"] =
  Unit.energy["gwhr"] =
  Unit.energy["gw h"] =
  3600 * 1e9 * Unit.energy.joule;

Unit.energy["terawatt-hour"] =
  Unit.energy["terawatt hour"] =
  Unit.energy["twh"] =
  Unit.energy["twhr"] =
  Unit.energy["tw h"] =
  3600 * 1e12 * Unit.energy.joule;

Unit.energy["petawatt-hour"] =
  Unit.energy["petawatt hour"] =
  Unit.energy["pwh"] =
  Unit.energy["pwhr"] =
  Unit.energy["pw h"] =
  3600 * 1e15 * Unit.energy.joule;

Unit.energy["btu"] =
  Unit.energy["btus"] =
  Unit.energy["british thermal unit"] =
  Unit.energy["british thermal units"] =
  1055.05585 * Unit.energy.joule;
Unit.energy["calorie"] = Unit.energy["calories"] = Unit.energy["cal"] = Unit.energy["cals"] = 4.184 * Unit.energy.joule;

Unit.energy["kilocalorie"] =
  Unit.energy["kilocalories"] =
  Unit.energy["kcal"] =
  Unit.energy["kcals"] =
  1000 * Unit.energy.calorie;

Unit.energy["Cal"] = Unit.energy["Cals"] = 1000 * Unit.energy.calorie;

// force measure
Unit.force.base = 1; // newton
Unit.force.SI = { kg: 1, m: 1, s: -2 };

Unit.force.name = "force";
Unit.force.newton = Unit.force.newtons = Unit.force.n = 1;

Unit.force["pound-force"] =
  Unit.force["pound force"] =
  Unit.force["lb force"] =
  Unit.force["lbf"] =
  4.44822162 * Unit.force.newton;

Unit.force["kilogram-force"] = Unit.force["kilogram force"] = Unit.force["kgf"] = 9.80665 * Unit.force.newton;
Unit.force["gram-force"] = Unit.force["gram force"] = Unit.force["gf"] = 9.80665e-3 * Unit.force.newton;

Unit.force.dyne = Unit.force.dyn = 1e-5 * Unit.force.newton;

// torque measure
Unit.torque.base = 1; // newton - meter
Unit.torque.SI = { kg: 1, m: 2, s: -2 };
Unit.torque.name = "torque";
Unit.torque["newton-meter"] = Unit.torque["newton meter"] = Unit.torque["newton meters"] = Unit.torque["n m"] = 1;

Unit.torque["foot lb"] =
  Unit.torque["ft lb"] =
  Unit.torque["lbft"] =
  Unit.torque["lb ft"] =
  Unit.torque["foot lbf"] =
  Unit.torque["ft lbf"] =
  Unit.torque["foot-pound"] =
  Unit.torque["foot pound"] =
  1.35581795 * Unit.torque["n m"];
Unit.torque["foot lbs"] =
  Unit.torque["ft lbs"] =
  Unit.torque["foot-pounds"] =
  Unit.torque["foot pounds"] =
  1.35581795 * Unit.torque["n m"];

// power measure
Unit.power.base = 1; // watt
Unit.power.SI = { kg: 1, m: 2, s: -3 };
Unit.power.name = "power";

Unit.power.watt = Unit.power.watts = Unit.power.w = 1;
Unit.power.milliwatt = Unit.power.milliwatts = Unit.power.mW = 1e-3 * Unit.power.watt;
Unit.power.kilowatt = Unit.power.kilowatts = Unit.power.kw = 1e3 * Unit.power.watt;
Unit.power.megawatt = Unit.power.megawatts = Unit.power.MW = Unit.power.mw = 1e6 * Unit.power.watt; // because MWh is more common...
Unit.power.gigawatt = Unit.power.gigawatts = Unit.power.gw = 1e9 * Unit.power.watt; // hrm... megawatt (caps) vs milliwatt (small). Don't have a difference. Case issues are too easy to happen.

Unit.power.horsepower = Unit.power.hp = 745.69987158227022 * Unit.power.watt;

// liquid measure
Unit.liquid.base = 1; // have the base be a cubic METER, which is the base for AREA
Unit.liquid.name = "liquid measure";
Unit.liquid.liter =
  Unit.liquid.liters =
  Unit.liquid.litre =
  Unit.liquid.litres =
  Unit.liquid.l =
  Unit.liquid.ls =
  0.001;
Unit.liquid.ml = Unit.liquid.mls = 1e-3 * Unit.liquid.liter;
Unit.liquid.milliliter =
  Unit.liquid.milliliters =
  Unit.liquid.millilitre =
  Unit.liquid.millilitres =
  1e-3 * Unit.liquid.liter;
Unit.liquid.cc = Unit.liquid.ccs = Unit.liquid.ml;

Unit.liquid.tsp = Unit.liquid.tsps = 4.92892161 * Unit.liquid.ml;
Unit.liquid.teaspoon = Unit.liquid.teaspoons = 4.92892161 * Unit.liquid.ml;

Unit.liquid.tbsp = Unit.liquid.tbsps = 3 * Unit.liquid.tsp;
Unit.liquid.tablespoon = Unit.liquid.tablespoons = 3 * Unit.liquid.tsp;

Unit.liquid["fluid ounce"] =
  Unit.liquid["fluid ounces"] =
  Unit.liquid["fluid oz"] =
  Unit.liquid["fluid ozs"] =
  Unit.liquid["fl oz"] =
  Unit.liquid["fl ozs"] =
  2 * Unit.liquid.tablespoon;
Unit.liquid["us fluid ounce"] =
  Unit.liquid["us fluid ounces"] =
  Unit.liquid["us fluid oz"] =
  Unit.liquid["us fluid ozs"] =
  Unit.liquid["us fl oz"] =
  Unit.liquid["us fl ozs"] =
  2 * Unit.liquid.tablespoon;

Unit.liquid.cup = Unit.liquid.cups = Unit.liquid.cp = Unit.liquid.cps = 16 * Unit.liquid.tablespoon;
Unit.liquid.pint = Unit.liquid.pints = Unit.liquid.pt = Unit.liquid.pts = 2 * Unit.liquid.cup;
Unit.liquid.quart = Unit.liquid.quarts = Unit.liquid.qt = Unit.liquid.qts = 4 * Unit.liquid.cup;

Unit.liquid.gallon = Unit.liquid.gallons = Unit.liquid.gal = Unit.liquid.gals = 16 * Unit.liquid.cup;
Unit.liquid.barrel = Unit.liquid.barrels = Unit.liquid.bbl = 42 * Unit.liquid.gallon;
Unit.liquid.hogshead = Unit.liquid.hogsheads = 63 * Unit.liquid.gallon; // simpsons

// US vs Imperial
Unit.liquid["us gallon"] = Unit.liquid["us gallons"] = Unit.liquid.gallon;
Unit.liquid["us pint"] = Unit.liquid["us pints"] = Unit.liquid.pint;
Unit.liquid["us cup"] = Unit.liquid["us cups"] = Unit.liquid["us cp"] = Unit.liquid["us cps"] = Unit.liquid.cup;
Unit.liquid["metric cup"] = Unit.liquid["metric cups"] = 250 * Unit.liquid.ml;

// US fluid ounces, see above
Unit.liquid["imperial gallon"] = Unit.liquid["imperial gallons"] = 4.54609 * Unit.liquid.liter;
Unit.liquid["imperial pint"] = Unit.liquid["imperial pints"] = Unit.liquid["imperial gallon"] / 8;
Unit.liquid["imperial cup"] =
  Unit.liquid["imperial cups"] =
  Unit.liquid["imperial cp"] =
  Unit.liquid["imperial cps"] =
  Unit.liquid["imperial pint"] / 2;
Unit.liquid["imperial quart"] = Unit.liquid["imperial quarts"] = Unit.liquid["imperial gallon"] / 4;
Unit.liquid["imperial fluid ounce"] =
  Unit.liquid["imperial fluid ounces"] =
  Unit.liquid["imperial fl oz"] =
  Unit.liquid["imperial gallon"] / 160;

Unit.liquid.cuin = 16.387064 * Unit.liquid.ml;
Unit.liquid.cuft = Unit.liquid.cf = Unit.liquid.cbf = Unit.liquid.cbft = 28.316846592 * Unit.liquid.liter;
Unit.liquid.cuyd = Unit.liquid.cuyds = 27 * Unit.liquid.cuft;
Unit.liquid.ccf = Unit.liquid.ccfs = 100 * Unit.liquid.cuft; // centum cubic feet
Unit.liquid.mcf = Unit.liquid.mcfs = 1000 * Unit.liquid.cuft; // mil cubic feet
Unit.liquid.mmcf = Unit.liquid.mmcf = 1e6 * Unit.liquid.cuft; // million cubic feet
Unit.liquid.bcf = Unit.liquid.bcfs = 1e9 * Unit.liquid.cuft; // billion cubic feet
Unit.liquid.tcf = Unit.liquid.tcfs = 1e12 * Unit.liquid.cuft; // trillion cubic feet

// weights in terms of grams
Unit.mass.base = 1; // kilogram base
Unit.mass.SI = { kg: 1 };
Unit.mass.name = "mass";

Unit.mass.kilogram = Unit.mass.kilograms = 1;
Unit.mass.kg = Unit.mass.kgs = Unit.mass.kilo = Unit.mass.kilos = 1;

Unit.mass.gram = Unit.mass.grams = 1e-3 * Unit.mass.kg;
Unit.mass.g = Unit.mass.gs = Unit.mass.gram;
Unit.mass.gm = Unit.mass.gms = Unit.mass.gram;

Unit.mass.mg = Unit.mass.milligram = Unit.mass.milligrams = 1e-3 * Unit.mass.gram;
Unit.mass.mgs = 1e-3 * Unit.mass.gram;

Unit.mass.ug = 1e-6 * Unit.mass.gram;
Unit.mass.ugs = 1e-6 * Unit.mass.gram;
Unit.mass.microgram = Unit.mass.micrograms = Unit.mass['\u03bcg'] = Unit.mass.mcg = Unit.mass.ug; // μg 

// english units
Unit.mass.lb = 0.45359237 * Unit.mass.kg;
Unit.mass.lbs = 0.45359237 * Unit.mass.kg;
Unit.mass.pound = 0.45359237 * Unit.mass.kg;
Unit.mass.pounds = 0.45359237 * Unit.mass.kg;

Unit.mass.slug = Unit.mass.slugs = 14.5939029 * Unit.mass.kg;

Unit.mass.oz = Unit.mass.lb * 0.0625;
Unit.mass.ozs = Unit.mass.lb * 0.0625;
Unit.mass.ounce = Unit.mass.lb * 0.0625;
Unit.mass.ounces = Unit.mass.lb * 0.0625;

Unit.mass["troy ounce"] = 31.1034768 * Unit.mass.gram;
Unit.mass["troy ounces"] = 31.1034768 * Unit.mass.gram;
Unit.mass["troy oz"] = 31.1034768 * Unit.mass.gram;
Unit.mass["troy ozs"] = 31.1034768 * Unit.mass.gram;

Unit.mass.dram = Unit.mass.oz * 0.0625;
Unit.mass.drams = Unit.mass.oz * 0.0625;

Unit.mass.stone = Unit.mass.lb * 14;
Unit.mass.stones = Unit.mass.lb * 14;

Unit.mass.ton = Unit.mass.lb * 2000;
Unit.mass.tons = Unit.mass.lb * 2000;
Unit.mass.shortton = Unit.mass.shorttons = Unit.mass["short ton"] = Unit.mass["short tons"] = Unit.mass.ton;
Unit.mass.longton = Unit.mass.longtons = Unit.mass["long ton"] = Unit.mass["long tons"] = 2240 * Unit.mass.lb;

Unit.mass["metric ton"] = Unit.mass["metric tons"] = Unit.mass["mt"] = 1000 * Unit.mass.kg;
Unit.mass.tonne = Unit.mass.tonnes = Unit.mass["mton"] = 1000 * Unit.mass.kg;

Unit.mass.dalton = Unit.mass.daltons = Unit.mass.Da = 1.660538921e-27 * Unit.mass.kg;

// everything in terms of meters
Unit.length.base = 1; // reference unit is meter
Unit.length.name = "length"; // reference unit is meter

Unit.length.meter = 1;
Unit.length.metre = 1;
Unit.length.meters = 1;
Unit.length.m = 1;

Unit.length.km = 1e3;
Unit.length.kms = 1e3;
Unit.length.kilometer = Unit.length.kilometre = 1e3;
Unit.length.kilometers = Unit.length.kilometres = 1e3;

Unit.length.au = 149597870.691 * Unit.length.km; // from: http://en.wikipedia.org/wiki/Astronomical_unit
Unit.length["light year"] = Unit.length["lightyear"] = Unit.length["light years"] = 9460730472580.8 * Unit.length.km;
Unit.length.ly = 9460730472580.8 * Unit.length.km;

Unit.length.parsec = Unit.length.parsecs = 3.08567758e16 * Unit.length.meter;

Unit.length.cm = 1e-2;
Unit.length.cms = 1e-2;
Unit.length.centimeter = Unit.length.centimetre = 1e-2;
Unit.length.centimeters = Unit.length.centimetres = 1e-2;

Unit.length.mm = 1e-3;
Unit.length.mms = 1e-3;
Unit.length.millimeter = Unit.length.millimetre = 1e-3;
Unit.length.millimeters = Unit.length.millimetres = 1e-3;

Unit.length.um = 1e-6; // micrometer
Unit.length['\u03bcm'] = 1e-6; // μm 
Unit.length.micrometer = 1e-6; // micrometer
Unit.length.micron = 1e-6; // micrometer
Unit.length.microns = 1e-6; // micrometer

Unit.length.nm = 1e-9;
Unit.length.nanometer = Unit.length.nanometre = 1e-9;
Unit.length.nanometers = Unit.length.nanometres = 1e-9;

Unit.length.pm = 1e-12; // picometer
Unit.length.picometer = Unit.length.picometre = 1e-12; // picometer
Unit.length.picometers = Unit.length.picometres = 1e-12; // picometer

// too easy to confuse with letter "a"
// Unit.length.a = 1e-10; // 10 A = 1 nm
Unit.length.angstrom = 1e-10;
Unit.length.angstroms = 1e-10;

// English units: see http://www.infoplease.com/ipa/A0001660.html

Unit.length.inch = 0.0254;
Unit.length.inches = 0.0254;
Unit.length["in"] = 0.0254;
Unit.length["ins"] = 0.0254;

Unit.length.mil = Unit.length.mils = 1e-3 * Unit.length.inch;

Unit.length.microinch = 1e-6 * Unit.length.inch;
Unit.length.microinches = 1e-6 * Unit.length.inch;

Unit.length.foot = Unit.length.inch * 12;
Unit.length.feet = Unit.length.inch * 12;
Unit.length.ft = Unit.length.inch * 12;

Unit.length.yard = Unit.length.foot * 3;
Unit.length.yards = Unit.length.foot * 3;
Unit.length.yd = Unit.length.foot * 3;
Unit.length.yds = Unit.length.foot * 3;

Unit.length.fathom = Unit.length.foot * 6;
Unit.length.fathoms = Unit.length.foot * 6;

Unit.length.rod = Unit.length.foot * 16.5;
Unit.length.rods = Unit.length.foot * 16.5;
Unit.length.rd = Unit.length.foot * 16.5;
Unit.length.rds = Unit.length.foot * 16.5;

Unit.length.furlong = Unit.length.foot * 660;
Unit.length.furlongs = Unit.length.foot * 660;

Unit.length.mile = Unit.length.mi = Unit.length.foot * 5280;
Unit.length.miles = Unit.length.foot * 5280;

Unit.length.league = Unit.length.mile * 3;
Unit.length.leagues = Unit.length.mile * 3;

Unit.length["nautical mile"] = Unit.length["nautical miles"] = Unit.length.foot * 6076.11549;

// area
Unit.area.base = 1;
Unit.area.name = "area";
Unit.area.acre = Unit.area.acres = Unit.area.ac = Unit.length.rod * Unit.length.rod * 160;
Unit.area.hectare = Unit.area.hectares = Unit.area.ha = Unit.length.meter * Unit.length.meter * 10000;

Unit.area.sqft = Unit.area.sft = Unit.area.sf = Unit.area.sqf = Unit.area.sqfoot = Unit.area.sqfeet = Unit.length.foot * Unit.length.foot;
Unit.area.sqin = Unit.area.sqinch = Unit.area.sqinches = Unit.length.inch * Unit.length.inch;
Unit.area.sqyd = Unit.area.sy = Unit.area.syd = Unit.area.sqyard = Unit.area.sqyards = Unit.length.yard * Unit.length.yard;
Unit.area.sqmi = Unit.area.sqmile = Unit.area.sqmiles = Unit.length.mile * Unit.length.mile;
Unit.area.sqmm = Unit.length.mm * Unit.length.mm;
Unit.area.sqcm = Unit.length.cm * Unit.length.cm;
Unit.area.sqm = Unit.length.meter * Unit.length.meter;
Unit.area.sqkm = Unit.length.km * Unit.length.km;

// speed
Unit.speed = {};
Unit.speed.base = 1;
Unit.speed.name = "speed";
Unit.speed.SI = { m: 1, s: -1 };
Unit.speed.mach = 331.46; // m/s

Unit.international_unit = {};
Unit.international_unit.base = 1;
Unit.international_unit.name = "international unit";
Unit.international_unit.SI = { iu: 1 };
Unit.international_unit.iu = Unit.international_unit["international unit"] = 1;
Unit.international_unit.miu = 1e-3;
Unit.international_unit.μIU = Unit.international_unit.µU = 1e-6;

Unit.log_ratio = {};
Unit.log_ratio.base = 1;
Unit.log_ratio.name = "power ratio";
Unit.log_ratio.SI = { bel: 1 };
Unit.log_ratio.bel = 1;
Unit.log_ratio.decibel = Unit.log_ratio.db = 0.1;
Unit.log_ratio.neper = Unit.log_ratio.np = 1 / (Math.LN10 / 2);

InstaCalc.Unit = Unit;
InstaCalc.Unit._updated = new Date().toISOString();

InstaCalc.updateUnitTimeStamp = function () {
  InstaCalc.Unit._updated = new Date().toISOString();
};

InstaCalc.getUnitTimeStamp = function () {
  return InstaCalc.Unit._updated;
};

/**
 * Sync stock prices from cache to unit system.
 * SINGLE CODE PATH: Called at start of executeRows() in both main thread and worker.
 * This ensures stocks are registered as currency units before any calculation runs.
 *
 * @returns {number} Number of stocks synced
 */
InstaCalc.syncStockUnits = function () {
  const cache = (typeof window !== 'undefined' && window.Action?.fn?.stock_cache) ||
    (typeof global !== 'undefined' && global.Action?.fn?.stock_cache) ||
    (typeof self !== 'undefined' && self.Action?.fn?.stock_cache);

  if (!cache) return 0;

  // Get isFiatCurrency check function
  const keywords = (typeof IC_KEYWORDS !== 'undefined') ? IC_KEYWORDS :
    (typeof window !== 'undefined' && window.IC_KEYWORDS) ? window.IC_KEYWORDS :
    (typeof self !== 'undefined' && self.IC_KEYWORDS) ? self.IC_KEYWORDS : null;

  const isFiatCheck = keywords?.isFiatCurrency?.bind(keywords);

  // Use MarketUnits if available
  if (InstaCalc.marketUnits) {
    // Count stocks before sync (store counts object to avoid iterating twice)
    const beforeCounts = InstaCalc.marketUnits.counts;
    const beforeCount = beforeCounts.stock;

    // Sync stocks to marketUnits
    InstaCalc.marketUnits.syncStocks(cache, isFiatCheck);

    // Count stocks after sync
    const afterCounts = InstaCalc.marketUnits.counts;
    const synced = afterCounts.stock - beforeCount;

    // DEBUG: Track stock sync
    if (typeof Debug !== 'undefined' && Debug && synced > 0) {
      Debug.log('stock:sync', `🔄 syncStockUnits() synced ${synced} stocks via MarketUnits`);
      Debug.log('stock:sync', `  Sample prices: AAPL=${InstaCalc.marketUnits.getPrice('AAPL')}, MSFT=${InstaCalc.marketUnits.getPrice('MSFT')}, GOOG=${InstaCalc.marketUnits.getPrice('GOOG')}`);
    }

    if (synced > 0) {
      InstaCalc.updateUnitTimeStamp();
    }

    return synced;
  }

  // Fallback: use old approach if MarketUnits not available
  const baseCurrencies = InstaCalc?.Unit?._baseCurrenciesFallback;
  const hasBaseCurrencies = baseCurrencies && Object.keys(baseCurrencies).length > 0;
  const fiatSource = hasBaseCurrencies ? baseCurrencies : InstaCalc?.Unit?._currenciesFallback || {};

  const stockPrices = {};
  let synced = 0;

  Object.entries(cache).forEach(([symbol, data]) => {
    if (data && data.loaded && data.price && !data.error) {
      const upper = symbol.toUpperCase();
      const lower = symbol.toLowerCase();

      if (isFiatCheck && isFiatCheck(upper)) {
        return;
      }

      stockPrices[upper] = data.price;
      stockPrices[lower] = data.price;

      const stockUnitName = 'STOCK_' + upper.replace(/\./g, '_');
      stockPrices[stockUnitName] = data.price;
      stockPrices[stockUnitName.toLowerCase()] = data.price;

      synced++;
    }
  });

  InstaCalc.Unit._currenciesFallback = { ...stockPrices, ...fiatSource };

  if (synced > 0) {
    InstaCalc.updateUnitTimeStamp();
  }

  return synced;
};

// NOTE: MarketUnits initialization moved to instacalc.parser.js (after class definition)
// to fix "Cannot access 'MarketUnits' before initialization" error

// Backward compatibility: currencies is a Proxy that forwards to marketUnits
// This allows old code that reads/writes InstaCalc.Unit.currencies to still work
InstaCalc.Unit._currenciesProxy = null;
InstaCalc.Unit._currenciesFallback = {};

// Create a Proxy for backward compatibility with currencies object
function createCurrenciesProxy() {
  const proxy = new Proxy({}, {
    get: function(target, prop) {
      // Handle well-known symbols for object operations
      if (prop === Symbol.toStringTag) return 'Object';
      if (prop === Symbol.iterator) return undefined;
      if (prop === Symbol.toPrimitive) return undefined;  // Prevent conversion errors
      if (typeof prop === 'symbol') return undefined;  // Ignore other symbols

      // Handle object methods
      if (prop === 'hasOwnProperty') {
        return function(key) {
          if (InstaCalc.marketUnits) {
            return InstaCalc.marketUnits.has(key);
          }
          return InstaCalc.Unit._currenciesFallback.hasOwnProperty(key);
        };
      }

      if (prop === 'valueOf') {
        return function() { return '[currencies]'; };  // Return a primitive string
      }
      if (prop === 'toString') {
        return function() { return '[object Currencies]'; };
      }

      // Get price from marketUnits or fallback
      if (InstaCalc.marketUnits) {
        return InstaCalc.marketUnits.getPrice(prop);
      }
      return InstaCalc.Unit._currenciesFallback[prop];
    },

    set: function(target, prop, value) {
      // Determine if this is fiat or stock based on IC_KEYWORDS if available
      const keywords = (typeof IC_KEYWORDS !== 'undefined') ? IC_KEYWORDS :
        (typeof window !== 'undefined' && window.IC_KEYWORDS) ? window.IC_KEYWORDS :
        (typeof self !== 'undefined' && self.IC_KEYWORDS) ? self.IC_KEYWORDS : null;

      const isFiat = keywords?.isFiatCurrency?.(prop) ?? false;

      if (InstaCalc.marketUnits) {
        InstaCalc.marketUnits.add(prop, value, isFiat ? 'fiat' : 'stock');
      } else {
        InstaCalc.Unit._currenciesFallback[prop] = value;
      }
      return true;
    },

    has: function(target, prop) {
      if (InstaCalc.marketUnits) {
        return InstaCalc.marketUnits.has(prop);
      }
      return prop in InstaCalc.Unit._currenciesFallback;
    },

    ownKeys: function(target) {
      if (InstaCalc.marketUnits) {
        return Object.keys(InstaCalc.marketUnits._units);
      }
      return Object.keys(InstaCalc.Unit._currenciesFallback);
    },

    getOwnPropertyDescriptor: function(target, prop) {
      const value = this.get(target, prop);
      if (value !== undefined) {
        return { enumerable: true, configurable: true, value };
      }
      return undefined;
    }
  });
  return proxy;
}

Object.defineProperty(InstaCalc.Unit, 'currencies', {
  get: function() {
    if (!this._currenciesProxy) {
      this._currenciesProxy = createCurrenciesProxy();
    }
    return this._currenciesProxy;
  },
  set: function(value) {
    // Bulk assignment - merge all properties into marketUnits or fallback
    if (typeof value === 'object' && value !== null) {
      for (const [key, price] of Object.entries(value)) {
        this.currencies[key] = price;
      }
    }
  },
  configurable: true
});

// Backward compat: baseCurrencies is now derived from marketUnits (fiat only)
Object.defineProperty(InstaCalc.Unit, 'baseCurrencies', {
  get: function() {
    if (InstaCalc.marketUnits) {
      const prices = {};
      for (const [key, unit] of Object.entries(InstaCalc.marketUnits._units)) {
        if (unit.type === 'fiat') {
          prices[key] = unit.price;
        }
      }
      return prices;
    }
    return this._baseCurrenciesFallback || {};
  },
  set: function(value) {
    this._baseCurrenciesFallback = value;
  },
  configurable: true
});

// Reset all currency/market unit state (for testing)
InstaCalc.Unit.resetAll = function() {
  if (InstaCalc.marketUnits) {
    InstaCalc.marketUnits.clear();
  }
  InstaCalc.Unit._currenciesFallback = {};
  InstaCalc.Unit._baseCurrenciesFallback = {};
  InstaCalc.Unit._currenciesProxy = null;  // Force proxy recreation
  InstaCalc.Unit.currencyRateTimestamp = null;
};

// Fractional currency patterns - researched from Wikipedia and currency standards
// Sources: https://en.wikipedia.org/wiki/Fils_(currency), https://en.wikipedia.org/wiki/Paisa
InstaCalc.Unit.currencyFractions = [
  // === 100 subunits ===
  { pattern: /^(\w+)\s+cents?$/i, divisor: 100 },  // USD, CAD, AUD, EUR, SGD, HKD, ZAR, etc.
  { pattern: /^(\w+)\s+pence$/i, divisor: 100, currencies: ['gbp'] },  // British plural
  { pattern: /^(\w+)\s+penn(?:y|ies)$/i, divisor: 100, currencies: ['usd', 'cad', 'gbp'] },  // US/CA/UK
  { pattern: /^(\w+)\s+centimes?$/i, divisor: 100, currencies: ['chf'] },  // Swiss franc only (EUR uses "cent")
  { pattern: /^(\w+)\s+centavos?$/i, divisor: 100, currencies: ['mxn', 'brl', 'ars', 'cop', 'php', 'cub'] },  // Latin America
  { pattern: /^(\w+)\s+paise$/i, divisor: 100, currencies: ['inr', 'pkr', 'npr'] },  // India, Pakistan, Nepal
  { pattern: /^(\w+)\s+paisa$/i, divisor: 100, currencies: ['inr', 'pkr', 'npr', 'bdt'] },  // + Bangladesh
  { pattern: /^(\w+)\s+kopecks?$/i, divisor: 100, currencies: ['rub'] },  // Russian ruble
  { pattern: /^(\w+)\s+satang$/i, divisor: 100, currencies: ['thb'] },  // Thai baht
  { pattern: /^(\w+)\s+sen$/i, divisor: 100, currencies: ['myr', 'idr', 'jpy'] },  // Malaysia, Indonesia, Japan (obsolete)
  { pattern: /^(\w+)\s+agorot$/i, divisor: 100, currencies: ['ils'] },  // Israeli shekel (plural)
  { pattern: /^(\w+)\s+agora$/i, divisor: 100, currencies: ['ils'] },  // Israeli shekel (singular)
  { pattern: /^(\w+)\s+fils$/i, divisor: 100, currencies: ['aed', 'yer'] },  // UAE dirham, Yemeni rial
  // === 1000 subunits ===
  { pattern: /^(\w+)\s+fils$/i, divisor: 1000, currencies: ['kwd', 'bhd', 'iqd', 'jod'] },  // Dinars (Kuwait, Bahrain, Iraq, Jordan)
  { pattern: /^(\w+)\s+baisa$/i, divisor: 1000, currencies: ['omr'] },  // Omani rial
  { pattern: /^(\w+)\s+millimes?$/i, divisor: 1000, currencies: ['tnd'] },  // Tunisian dinar
];

// Currency alias mappings - shared between MarketUnits and fallback paths
// Each entry is [alias, sourceCode] for simple aliases, or [alias, sourceCode, divisor] for fractions
InstaCalc.Unit._currencyAliases = [
  // Dollar aliases
  ['dollar', 'usd'], ['dollars', 'usd'],
  // Fractional USD (fixed values)
  ['penny', null, 0.01], ['pennies', null, 0.01],
  ['cent', null, 0.01], ['cents', null, 0.01],
  ['dime', null, 0.1], ['dimes', null, 0.1],
  ['nickel', null, 0.05], ['nickels', null, 0.05],
  ['quarter', null, 0.25], ['quarters', null, 0.25],
  // Major currency aliases
  ['yen', 'jpy'], ['euro', 'eur'], ['euros', 'eur'],
  ['yuan', 'cny'], ['rupee', 'inr'], ['peso', 'mxn'],
  ['ruble', 'rub'], ['ringgit', 'myr'], ['won', 'krw'],
  ['loonie', 'cad'], ['aussie', 'aud'], ['baht', 'thb'],
  ['dirham', 'aed'], ['lira', 'try'], ['riyal', 'sar'],
  ['lev', 'bgn'], ['zloty', 'pln'], ['krona', 'sek'],
  ['franc', 'chf'], ['quetzal', 'gtq'], ['shekel', 'ils'],
  ['dong', 'vnd'], ['dinar', 'kwd'], ['kuna', 'hrk'],
  ['birr', 'etb'], ['taka', 'bdt'], ['forint', 'huf'],
  ['rupiah', 'idr'], ['kyat', 'mmk'], ['naira', 'ngn'],
  ['bolivar', 'vef'], ['rmb', 'cny']
];

// Fractional currency aliases that need division (alias, sourceCode, divisor)
InstaCalc.Unit._currencyFractionAliases = [
  ['euro cent', 'eur', 100], ['euro cents', 'eur', 100],
  ['eur cent', 'eur', 100], ['eur cents', 'eur', 100],
  ['pence', 'gbp', 100]
];

/**
 * Apply currency aliases using the provided add function
 * @param {Function} addFn - Function(alias, price) to add a currency
 * @param {Function} getPrice - Function(code) to get price of a currency
 */
InstaCalc.Unit._applyCurrencyAliases = function(addFn, getPrice) {
  // Simple aliases and fixed values
  for (const entry of InstaCalc.Unit._currencyAliases) {
    const [alias, sourceCode, fixedValue] = entry;
    if (fixedValue !== undefined) {
      // Fixed value (e.g., penny = 0.01)
      addFn(alias, fixedValue);
    } else if (sourceCode) {
      // Alias to another currency
      const price = getPrice(sourceCode);
      if (price !== undefined) {
        addFn(alias, price);
      }
    }
  }

  // Fractional aliases (e.g., euro cent = eur / 100)
  for (const [alias, sourceCode, divisor] of InstaCalc.Unit._currencyFractionAliases) {
    const price = getPrice(sourceCode);
    if (price !== undefined) {
      addFn(alias, price / divisor);
    }
  }
};

InstaCalc.Unit.currenciesLoaded = function () {
  // Check if marketUnits has fiat currencies loaded
  if (InstaCalc.marketUnits) {
    return InstaCalc.marketUnits.hasFiatLoaded();
  }
  // Fallback for when MarketUnits isn't loaded yet
  return Object.keys(InstaCalc.Unit._currenciesFallback || {}).length > 0;
}
InstaCalc.loadCurrencies = function (data, force = false) {
  // NOTE: MarketUnits is now initialized in instacalc.parser.js after class definition

  // already loaded, unless we want to force a refresh
  if (!force && InstaCalc.Unit.currenciesLoaded()) {
    // But still update timestamp if we have one and it's missing
    if (data._rateTimestamp && !InstaCalc.Unit.currencyRateTimestamp) {
      InstaCalc.Unit.currencyRateTimestamp = data._rateTimestamp;
      if (InstaCalc.marketUnits) {
        InstaCalc.marketUnits.rateTimestamp = data._rateTimestamp;
      }
    }
    return;
  }

  // Store the actual rate date from OXR (Unix timestamp) for tooltip attribution
  InstaCalc.Unit.currencyRateTimestamp = data._rateTimestamp || null;

  // Use MarketUnits if available
  if (InstaCalc.marketUnits) {
    // Clear existing fiat if force refresh
    if (force) {
      // Keep stocks, clear fiat by rebuilding
      const existingStocks = {};
      for (const [key, unit] of Object.entries(InstaCalc.marketUnits._units)) {
        if (unit.type === 'stock') {
          existingStocks[key] = unit;
        }
      }
      InstaCalc.marketUnits.clear();
      // Restore stocks
      for (const [key, unit] of Object.entries(existingStocks)) {
        InstaCalc.marketUnits._units[key] = unit;
      }
    }

    // Set timestamp
    InstaCalc.marketUnits.rateTimestamp = data._rateTimestamp || null;

    // Load fiat currencies (filters out existing units like CUP)
    for (var code in data) {
      // Skip metadata keys (e.g., _fallback, _rateTimestamp, _note)
      if (code.startsWith('_')) continue;

      var lowerCode = code.toLowerCase();
      var rate = data[code];

      // make sure it's not an existing unit: CUP is cuban peso, and... cups
      const existingUnit = InstaCalc.prototype.isUnit(lowerCode, true);
      if (!existingUnit) {
        InstaCalc.marketUnits.add(code, 1.0 / parseFloat(rate), 'fiat');
      }
    }

    // Always ensure USD = 1
    InstaCalc.marketUnits.add('usd', 1, 'fiat');

    delete Unit.currency; // remove the placeholder entry once real currencies loaded

    // Apply currency aliases using shared helper
    InstaCalc.Unit._applyCurrencyAliases(
      (alias, price) => InstaCalc.marketUnits.add(alias, price, 'fiat'),
      (code) => InstaCalc.marketUnits.getPrice(code)
    );
  } else {
    // Fallback: use old currencies object approach
    var currencies = force ? { ...InstaCalc.Unit.currencies } : {};

    for (var i in data) {
      if (i.startsWith('_')) continue;
      var code = i.toLowerCase();
      var rate = data[i];
      const existingUnit = InstaCalc.prototype.isUnit(code, true);
      if (!existingUnit) {
        currencies[code] = 1.0 / parseFloat(rate);
      }
    }

    currencies.usd = 1;

    delete Unit.currency;

    // Apply currency aliases using shared helper
    InstaCalc.Unit._applyCurrencyAliases(
      (alias, price) => { currencies[alias] = price; },
      (code) => currencies[code]
    );

    InstaCalc.Unit._baseCurrenciesFallback = currencies;
    InstaCalc.Unit._currenciesFallback = currencies;
  }

  InstaCalc.updateUnitTimeStamp();
};

// operator afterwards, like 3!
InstaCalc.parseSuffixOperator = function (str, op, fn) {
  var split = str.indexOf(op);

  if (split < 0) {
    // not found
    return str;
  }

  var front = str.substring(0, split);
  front = InstaCalc.strtrim(front); // but there could be something right up in front... add a space as separator

  var fronttoken = InstaCalc.getLastTokenGroup(front);
  front = front.substring(0, front.length - fronttoken.length);

  // false positive on "!true"
  if (!fronttoken) {
    return str;
  }

  var back = str.substring(split + 1);

  var draft = " " + front + " " + fn + "(" + fronttoken + ")" + back; // extra parens for safety in parsing numbers

  if (draft.indexOf(op) > 0) {
    // still have another one left!
    return InstaCalc.parseSuffixOperator(draft, op, fn);
  }

  return draft;
};

// parse a binary operator - set op to fn
InstaCalc.parseBinaryOperator = function (str, op, fn) {
  var split = str.indexOf(op);

  if (split < 0) {
    // not found
    return str;
  }

  var front = str.substring(0, split);
  front = InstaCalc.strtrim(front); // but there could be something right up in front... add a space as separator

  // split character is ^
  var back = str.substring(split + 1);
  back = InstaCalc.strtrim(back);

  var fronttoken = InstaCalc.getLastTokenGroup(front);
  var backtoken = InstaCalc.getFirstTokenGroup(back);

  // remove tokens from front and back... and replace appropriately.
  front = front.substring(0, front.length - fronttoken.length);
  back = back.substring(backtoken.length);

  var draft = " " + front + " " + fn + "(" + "(" + fronttoken + "),(" + backtoken + ")) " + back; // extra parens for safety in parsing numbers - parsenumber will eat neighboring numbers like power(2,2)

  if (draft.indexOf(op) > 0) {
    // still have another one left!
    return InstaCalc.parseBinaryOperator(draft, op, fn);
  }

  return draft;
};

// remove extra whitespace from front and back
InstaCalc.strtrim = function (str) {
  str = str.replace(/^\s*/, "");
  str = str.replace(/\s*$/, "");
  return str;
};

InstaCalc.strrev = function (str) {
  var i;
  var rev = "";

  if (str == "") {
    return str;
  }

  for (i = 0; i < str.length; i++) {
    rev = str.charAt(i) + rev;
  }

  return rev;
};

// token library
InstaCalc.getFirstTokenGroup = function (str) {
  var token = "";

  if (str == "") {
    return str;
  }

  str = str.replace(/^\s*/, ""); // trim whitespace from front

  // 3 choices
  // 1: a regular variable or number: 123123, xasdasd, etc. a[0] etc. just any sequence of non-spaces
  // 2: a function: foo(...)
  // 3: beginning a paren (....)

  return InstaCalc.getToken(str);
};

// similar to above -- we are passed the END of a string and asked to pull out the last token
InstaCalc.getLastTokenGroup = function (str) {
  var token = InstaCalc.getLastToken(str);
  return token;
};

InstaCalc.getLastToken = function (str) {
  // ( ( ( ... foo ..

  var tokens;

  // ends with paren: function or parenthesis expression
  if ((tokens = str.match(/[)]$/))) {
    var parens = InstaCalc.getTokenParenRev(str);

    // get rid of the parens part
    var front = str.substring(0, str.length - parens.length); // front(...)

    var fn;
    if ((fn = front.match(/([a-zA-Z]+[a-zA-Z0-9]*)$/))) {
      // yes, was a function
      return fn[1] + parens;
    }

    return parens; // just the parens
  }

  // otherwise just a number or variable
  // only allow '-3' if at beginning of word ^, or beginning of paren
  if ((tokens = str.match(/((^|[(])\s*[\-])?[a-zA-Z0-9_,.]+$/))) {
    // may have a negative dash "-" in front
    return tokens[0];
  }

  return "";
};

// match function or variable
// assumes starts with a letter
InstaCalc.getToken = function (str) {
  // is this a function? does it have a paren?

  str = str.replace(/^\s*/, "");
  var tokens;

  // function
  if ((tokens = str.match(/^([a-zA-Z0-9_,.]+)([(].*[)])/))) {
    var fn = tokens[1];
    var params = InstaCalc.getTokenParen(tokens[2]);

    if (params == -1) {
      return "";
    }

    return fn + params;
  }

  // token starting with parens
  if ((tokens = str.match(/^[(].*/))) {
    var parens = InstaCalc.getTokenParen(str);

    return parens; // could be -1 if error
  }

  // otherwise just a number or variable
  if ((tokens = str.match(/^([\-]?[a-zA-Z0-9_,.]+)/))) {
    return tokens[1];
  }

  return ""; // error
};

// assume string starts with lparen
// returns string that matches it.
InstaCalc.getTokenParen = function (str) {
  var parens = 0;
  var i = 0;
  var length;

  if (str == "") {
    return str;
  }

  for (i = 0; i < str.length; i++) {
    if (str.charAt(i) == "(") {
      parens++;
    } else if (str.charAt(i) == ")") {
      parens--;
    }

    // note -- this actually works for "backwards" parens in a reversed string: "foo)....(" . It just dips negative.

    if (parens == 0) {
      // they are evened out
      length = i + 1;
      return str.substring(0, length);
    }
  }

  return ""; // error unmatch parens
};

InstaCalc.getTokenParenRev = function (str) {
  str = InstaCalc.strrev(str); // backwards.
  str = InstaCalc.getTokenParen(str);
  return InstaCalc.strrev(str);
};

/* Core InstaCalc Engine -- plotting, parsing, execution
 Copyright 2007 Kalid Azad
 */

// extract out the title from a string
InstaCalc.findTitle = function (str) {
  if (InstaCalc.isNull(str) || !str.match(/=/)) {
    return "";
  }

  // my title = 3434
  // if (...){my title = 34343}

  var raw = str.split(/=/)[0]; // everything before the equals
  var tmp = raw.split(/[{}()]/); // anything before
  raw = tmp[tmp.length - 1]; // take last item -- everything before one of these.

  var out = raw.match(/[^{}]*/)[0];
  var trimmed = InstaCalc.strtrim(out);
  var replaced = trimmed.replace(/_/g, " ").replace("$", ""); // r517 bugfix: global replace
  return replaced;
};

/* takes function name, returns its replacement */
InstaCalc.replacefn = function (name) {
  var replace = "";

  if (InstaCalc.allowedfn[name.toUpperCase()]) {
    replace = InstaCalc.allowedfn[name.toUpperCase()];
  } else {
    replace = InstaCalc.safeprepend + name;
    //"InstaCalc.localfunctions." + name; // allow any user-defined function for now
  }

  // prefix any banned functions with underscore?

  //if (name == "") { return true; }// just parens

  return replace; // allow nearly any function -- but prefix with localfunctions
};

InstaCalc.replacekeyword = function (name) {
  var keyword = InstaCalc.keywords[name.toUpperCase()];
  if (keyword) {
    return keyword;
  }

  return name;
};

InstaCalc.replacecn = function (name) {
  if (InstaCalc.allowedcn[name]) {
    return InstaCalc.allowedcn[name];
  }

  return "";
};

/* return true if valid constant -- must match name exactly */
InstaCalc.validcn = function (name) {
  if (name === "") {
    return false;
  }

  if (InstaCalc.allowedcn[name]) {
    return true;
  }

  return false;
};

/* return true if keyword, can be in any case */
InstaCalc.validkeyword = function (name) {
  if (name === "") {
    return false;
  }
  if (InstaCalc.keywords[name.toUpperCase()]) {
    return true;
  }
  return false;
};

/* each expression is as follows
  InstaCalc.expressions[0] = [regexp, replacement]
  return index of replacement
  */
InstaCalc.validExpression = function (word) {
  var i;
  for (i = 0; i < InstaCalc.expressions.length; i++) {
    if (word.match(InstaCalc.expressions[i][0])) {
      return i; // an matched expression
    }
  }
  return -1;
};

InstaCalc.replaceExpression = function (word) {
  var i;
  for (i = 0; i < InstaCalc.expressions.length; i++) {
    if (word.match(InstaCalc.expressions[i][0])) {
      return word.replace(InstaCalc.expressions[i][0], InstaCalc.expressions[i][1]); // an matched expression
    }
  }
  return word;
};

InstaCalc.replacevariable = function (word) {
  return InstaCalc.safeprepend + word;
};

InstaCalc.cleanVariable = function (word) {
  myregexp = new RegExp(InstaCalc.safeprepend, "g");
  return word.replace(myregexp, "");
};

// return type of token: function, constant, keyword or variable
InstaCalc.tokenType = function (token) {
  if (token.match(/[(]/)) {
    // contains a paren
    return "function";
  }

  if (InstaCalc.validcn(token)) {
    // PI
    return "constant";
  }

  if (InstaCalc.validkeyword(token)) {
    // if, else
    return "keyword";
  }

  if (InstaCalc.validExpression(token) != -1) {
    // ROW1, or ROW1:ROW3
    return "expression";
  }

  return "variable";
};

InstaCalc.findString = function (token) {
  var i;
  for (i = 0; i < token.length; i++) { }
};

// have it return true/false otherwise
InstaCalc.isOperator = function (str) {
  if (str === "") {
    return false;
  }

  var c = str[0];
};

// TODO: make parser more robust. For now, just substitute in.
InstaCalc.parser = function (str) {
  var i;

  for (i = 0; i < str.length; i++) { }
};

// the truly first filter to run - replace common expressions
InstaCalc.applyFirstFilter = function (str) {
  var i, re;
  for (i = 0; i < InstaCalc.firstfilter.length; i++) {
    re = InstaCalc.firstfilter[i][0];
    var replace = InstaCalc.firstfilter[i][1];

    str = str.replace(re, replace);
  }

  return str;
};

// sanitize user input - replace common expressions
InstaCalc.applyPreFilter = function (str) {
  var i, re;
  for (i = 0; i < InstaCalc.prefilter.length; i++) {
    re = InstaCalc.prefilter[i][0];
    var replace = InstaCalc.prefilter[i][1];

    str = str.replace(re, replace);
  }

  return str;
};

InstaCalc.customOperators = function (str) {
  str = InstaCalc.parseSuffixOperator(str, "!", "FAC"); // 3! with InstaCalc.factorial(3)
  str = InstaCalc.parseBinaryOperator(str, "^", "POWER"); // replace a^b  with power(a,b)

  // wow... try for add, subtract, etc?

  // TODO: need to parse million, billion, etc. BEFORE doing the addition parsing, etc.

  //str = parseBinaryOperator(str, "/", "DIVIDE");
  //str = parseBinaryOperator(str, "*", "MULTIPLY");
  //str = parseBinaryOperator(str, "+", "ADD");
  //str = parseBinaryOperator(str, "-", "SUBTRACT");

  return str;
};

InstaCalc.applyPostFilter = function (str) {
  var i, re;
  for (i = 0; i < InstaCalc.postfilter.length; i++) {
    re = InstaCalc.postfilter[i][0];
    var replace = InstaCalc.postfilter[i][1];

    str = str.replace(re, replace);
  }

  return str;
};

InstaCalc.extractStrings = function (str) {
  var i;
  var instring = false;
  var curstr = ""; // current string
  var nonstr = []; // items not in a string
  var strings = [];

  // str.match(/".*[^\\]"/) use tokens like this to grab string...
  var matches = str.match(/"([\\]"|[^"])+"/g); // make sure to ignore escape characters

  if (InstaCalc.isNull(matches)) {
    return [str, []];
  }

  for (i = 0; i < matches.length; i++) {
    var name = InstaCalc.stringprepend + i;
    var value = matches[i]; // trim off the quotes at the front and back.

    str = str.replace(value, name);
    strings.push([name, value]);
  }

  return [str, strings];
};

InstaCalc.replaceStrings = function (str, strArray) {
  var i;
  for (i = 0; i < strArray.length; i++) {
    var item = strArray[i];
    str = str.replace(item[0], item[1]);
  }

  return str;
};

// return tokens that need to be parsed
InstaCalc.tokenize = function (str) {
  return str.match(/[a-zA-Z_][a-zA-Z0-9_:]*\s*[(]*/g); // no dot
};

// escape all unicode characters, replace with _34 for character codes
InstaCalc.escapeUnicode = function (str) {
  var replace = "";

  for (var i = 0; i < str.length; i++) {
    var c = str.charAt(i);
    if (str.charCodeAt(i) > 127) {
      // not ASCII
      c = "_" + str.charCodeAt(i);
    }
    replace += c;
  }

  return replace;
};

InstaCalc.prototype.scanCurrencyFormatting = function (str) {
  var tmp = InstaCalc.format.currencies;
  for (var index in tmp) {
    var cur = tmp[index];
    if (str.match(cur.regex)) {
      str = str.replace(cur.regex, "");

      this.format.currency = true;
      this.format.currencySymbol = cur.symbol;
      this.format.decimals = cur.decimals;
    }
  }

  return str;
};

/* ***************************
Sanitize user input.
   1) replace functions with appropriate calls; SIN() -> Math.sin()
   2) replace constants (PI -> Math.PI) and keywords
   3) make variables safe. Put them in the InstaCalc.locals namespace
   ******************************/

InstaCalc.prototype.cleanup = function (str) {
  if (str == null || !InstaCalc.isdefined(str) || str === "") {
    return str;
  }

  // run the preparser on all tokens
  str = this.scanCurrencyFormatting(str); // get rid of formatting stuff EARLY
  str = InstaCalc.applyFirstFilter(str); // the very first filter to run

  // trim the string --> avoid bad regexes in the later stages
  str = InstaCalc.strtrim(str);

  // extract string tokens - no processing on them
  var ret = InstaCalc.extractStrings(str);
  str = ret[0];
  var strings = ret[1]; // string replacements

  str = InstaCalc.escapeUnicode(str); // replace unicode variable names
  str = InstaCalc.replaceVariableNames(str); // before the custom operators --> otherwise net sales^2 won't work

  // str = replaceOperatorShortcuts(str); // ignore for now. watch out for things like 400m where we mean 400 million
  str = InstaCalc.customOperators(str); // needs to go above prefilter - otherwise bin(3^5) will fail.
  str = InstaCalc.applyPreFilter(str); // CHANGING for now. seems like quotes handled properly by parsing, etc.

  // won't parse properly because of quotes introduced.

  // letter or underscore followed by letters... spaces... then opening paren
  var tokens = InstaCalc.tokenize(str);

  if (InstaCalc.isNull(tokens)) {
    // nothing to parse
    return str;
  }

  var token, type, replace, re;

  for (var i = 0; i < tokens.length; i++) {
    token = tokens[i];
    // clean off any leading/trailing whitespace
    token = token.match(/\S+/)[0];
    // log("INFO: Token: " + token);

    type = InstaCalc.tokenType(token);

    if (type == "function") {
      var fnname = token.match(/\w+/)[0]; // remove the opening paren and whitespace. TODO: should be (a-zA-Z0-9) for generality?
      replace = InstaCalc.replacefn(fnname); // replacement text

      re = new RegExp("([^.\\w]|^)" + fnname + "\\s*[(]", "g"); // function preceded by non-dot, non-word character ("a"sin). BUGFIX.

      //log("INFO: str before: " + str);
      str = str.replace(re, "$1" + replace + "(");
      //log("INFO: str after: " + str);
      continue;
    }

    // constant like PI
    if (type == "constant") {
      var constant = token.match(/\w+/)[0]; // remove the any whitespace
      re = new RegExp("([^.]|^)" + constant + "\\b", "");
      str = str.replace(re, "$1" + InstaCalc.replacecn(constant));
      continue;
    }

    if (type == "keyword") {
      // keep keywords as-is
      var keyword = token.match(/\w+/)[0];
      re = new RegExp("(\\s|^)" + keyword + "\\b", ""); // important -- want it to be separated from other items
      str = str.replace(re, " " + InstaCalc.replacekeyword(keyword));
      continue;
    }

    if (type == "expression") {
      // ROW1, or ROW1:ROW3
      var expression = InstaCalc.replaceExpression(token);
      re = new RegExp("\\b" + token + "\\b", ""); // only replace once
      str = str.replace(re, expression);

      continue;
    }

    // default type: is a variable
    var newtoken = InstaCalc.safeprepend + token.toLowerCase(); // make case-insensitive variables

    // wrap variable in parens - DOES NOT work because of left-hand-side assignment
    re = new RegExp("\\b" + token + "\\b", "g"); // change to replace all instances of token /// but then gets instances where token shares name with function.

    str = str.replace(re, newtoken);
  }

  str = InstaCalc.applyPostFilter(str);
  str = InstaCalc.replaceStrings(str, strings);

  return str;
};

// group similar variable names together
InstaCalc.replaceVariableNames = function (str) {
  // net proft => net_profit
  //([^0-9]+[\w_]+)\s+([^0-9]+[\w_]+)

  // replace expressions
  str = InstaCalc.replaceExpression(str);

  // bugfix: had [\w]+ which was ignoring 1-letter variables
  while (str.match(/\b([a-zA-Z_][\w]*)\s+([a-zA-Z_][\w]*)/)) {
    str = str.replace(/\b([a-zA-Z_][\w]*)\s+([a-zA-Z_][\w]*)/, "$1_$2");
  }

  return str;
};

// 3x => 3*x,  3(a+b) => 3 * (a+b) etc.
// watch out for 1e3 => 1 * e3 (variable name)
InstaCalc.replaceOperatorShortcuts = function (str) {
  // multiplication
  while (str.match(/\b([0-9.]+)([\(a-zA-Z])/)) {
    str = str.replace(/\b(\d+)([\(a-zA-Z])/, "$1 * $2");
  }

  return str;
};

// check whether string is a comment and should be ignored
InstaCalc.iscomment = function (str) {
  return IC.parser.splitAtComment(str).comment !== '';
};

// returns two items; the non-comment and the commented part of the string
InstaCalc.extractComment = function (str) {
  var parts = IC.parser.splitAtComment(str);
  if (parts.comment) {
    // remove leading delimiter chars
    var comment = parts.comment.replace(/^[/#]+/, '');
    return [parts.before, comment];
  }
  return [str, ""];
};

InstaCalc.prototype.reset_globals = function () {
  this.format.currency = false; // by default do not format as currency
  InstaCalc.unitlabel = ""; // clear unit label
};

// return empty calculation object
InstaCalc.blank_calc_object = function () {
  var ret = {};
  ret.comment = ""; // comment, if any
  ret.title = ""; // title for cell when hidden (if any)
  ret.input = ""; // input to evaluate
  ret.row = ""; // no row
  ret.parsed = ""; // parsed string, after cleanup ("3^2" => POWER(3,2))
  ret.eval_result = ""; // clear out previous value
  ret.final_result = ""; // result to show in output area
  return ret;
};

/* run cleanup() on the input string */
InstaCalc.prototype.parseCalculationObject = function (obj) {
  var i = obj.row;
  var user_input = obj.input;

  /* Transform string into text to execute. Load results from cache. */
  if (this.inputCache[i] != null && this.inputCache[i][0] == user_input) {
    // cache hit
    //log("INFO: hit cache: " + inputstr);
    obj.parsed = this.inputCache[i][1];

    // set formatting settings
    this.format.currency = this.outputCache.format[i].currency;
    this.format.currencySymbol = this.outputCache.format[i].currencySymbol;
    this.format.decimals = this.outputCache.format[i].decimals;
  } else {
    //log("INFO: missed cache: " + inputstr);
    obj.parsed = this.cleanup(user_input); // parse input, sanitize variables

    this.inputCache[i] = [user_input, obj.parsed];

    this.outputCache.format[i] = {}; // pull this out? should just create object normally.
    this.outputCache.format[i].currency = this.format.currency;
    this.outputCache.format[i].currencySymbol = this.format.currencySymbol;
    this.outputCache.format[i].decimals = this.format.decimals;
  }
};

// object has a "parsed" field which is to be evaluated
InstaCalc.prototype.evaluateCalculationObject = function (obj, settings) {
  function undefinedCount() {
    return Object.keys(InstaCalc.locals).filter(function (i) {
      return InstaCalc.locals[i] == undefined;
    }).length;
  }

  // BUGFIX - assigning local to undefined doesn't throw an error. Keep track of # of undefined variables;
  var undefined_start = undefinedCount();

  var ret = this.evaluateCalculation(obj.parsed);
  obj.eval_result = ret.eval_result; // get raw result

  var ret2 = this.cleanupEvalResult(ret.eval_result, settings); // clean it up
  obj.final_result = ret2.final_result;

  var undefined_end = undefinedCount();

  // if the count changed, we should redo the calculation
  if (undefined_start !== undefined_end) {
    obj.error = new ReferenceError();
    obj.status = "Undefined variable in row " + (obj.row + 1);
  }

  if (ret.error) {
    obj.error = ret.error;

    if (ret.error instanceof ReferenceError) {
      obj.status = "Waiting to define variable in row " + (obj.row + 1) + "...";
    } else {
      // some other type of error
      obj.status = "Waiting on row " + (obj.row + 1) + "...";
    }
  }

  if (ret2.error) {
    obj.error = ret2.error;
    obj.status = obj.status = "Waiting on row " + (obj.row + 1) + "...";
  }

  return obj;
};

// convert eval_result to a sanitized string
InstaCalc.prototype.cleanupEvalResult = function (eval_result, settings) {

  var ret = {};
  ret.final_result = "";

  if (eval_result == "NaN" || eval_result == "undefined") {
    eval_result = ""; // variable not recognized
    ret.error = "undefined";
  }

  if (typeof eval_result == "function") {
    // we defined a function -- hide this
    eval_result = "";
  }

  // format number //  && (InstaCalc.settings.round == true)
  if (typeof eval_result == "number") {
    if (eval_result == Infinity) {
      eval_result = "Infinity";
    } else if (eval_result == -Infinity) {
      eval_result = "-Infinity";
    } else if (isNaN(eval_result)) {
      // only way to check - can't do ==
      eval_result = "Number undefined";
      ret.error = true; // my_undefined_var + 34 ... leads to isNaN on the number
    } else {
      eval_result = this.formatNumber(eval_result, settings);
      if (eval_result == Infinity) {
        eval_result = "Infinity";
      }
    }
  }

  if (typeof eval_result == "boolean") {
    // true or false
    eval_result = eval_result.toString();
  }

  // only comment string
  if (!eval_result) {
    eval_result = "";
  }

  ret.final_result = eval_result;
  return ret;
};

// evaluates calculation, returns object
InstaCalc.prototype.evaluateCalculation = function (str) {
  var ret = {};
  ret.eval_result = "";

  if (!InstaCalc.isNull(str)) {
    try {
      ret.eval_result = eval(str);
    } catch (e) {
      ret.error = e;
      ret.eval_result = "";
    }
  }

  return ret;
};

// should we retry the calculation? references to other cells / undefined variables?
InstaCalc.prototype.shouldRedoCalculation = function (obj) {
  if (InstaCalc.isNull(obj)) {
    return false;
  }

  if (obj["error"] || typeof obj["eval_result"] === "undefined") {
    // has an error -- try again after vars defined
    return true;
  }

  if (obj["parsed"].match("getRowValue[(]")) {
    // references another cell
    return true;
  }

  if (obj["parsed"].match("getRowValues[(]")) {
    // references a row range
    return true;
  }

  return false;
};

InstaCalc.cache_count = 0;
InstaCalc.cache_max = 10000;
InstaCalc.cache = [];
InstaCalc.cache_hits = 0;
InstaCalc.cache_misses = 0;

InstaCalc.get_cache = function (key) {
  if (InstaCalc.cache[key]) {
    InstaCalc.cache_hits += 1;
    return InstaCalc.cache[key];
  }

  InstaCalc.cache_misses += 1;
  return false;
};

InstaCalc.clear_cache = function () {
  InstaCalc.cache = [];
  InstaCalc.cache_count = 0;
  // keep stats around for perf checking
};

InstaCalc.set_cache = function (key, value) {
  if (InstaCalc.cache_count > InstaCalc.cache_max) {
    InstaCalc.clear_cache();
  }

  InstaCalc.cache[key] = value;
  InstaCalc.cache_count += 1;

  return value;
};

// parse numeric string
InstaCalc.prototype.parseNumber = function (n) {
  // lookup this number if already there
  var cached = InstaCalc.get_cache(n);
  if (cached) {
    return cached;
  }

  if (n == Infinity || n == -Infinity) {
    return n;
  }

  var parsed = null;

  if (n.match(/^0x[0-9a-f]+/i)) {
    // parse hex numbers
    parsed = parseInt(n, 16);
  }

  if ((matches = n.toString().match(/^0b([01]+)/i))) {
    // parse binary numbers
    parsed = parseInt(matches[1], 2);
  }

  if ((matches = n.toString().match(/^0o([01234567]+)/i))) {
    // parse octal
    parsed = parseInt(matches[1], 8);
  }

  // base 10 by default
  if (!parsed) {
    // TODO: extract out this number cleanup
    var cleaned_up = n.replace(/[,]/g, "");
    parsed = parseFloat(cleaned_up);
    if (isNaN(parsed)) {
      // no processing was done, return original
      return n;
    }
  }

  var num = new NumberFormat();
  num.setInputDecimal(".");
  num.setNumber(parsed); // obj.value is '123,456,789.00001'
  num.setPlaces("-1", false);
  //num.setPlaces('10', false);
  num.setCurrencyValue("$");
  num.setCurrency(false);
  num.setCurrencyPosition(num.LEFT_OUTSIDE);
  num.setNegativeFormat(num.LEFT_DASH);
  num.setNegativeRed(false);
  num.setSeparators(false, ",", ",");
  var str = parseFloat(num.toFormatted());

  InstaCalc.set_cache(n, str);

  return str;
};

// global num to use

InstaCalc.prototype.createNumberFormat = function () {
  var num = new NumberFormat();
  num.setInputDecimal(".");

  //num.setCurrency(false);
  num.setCurrency(this.format.currency);
  num.setCurrencyPosition(num.LEFT_OUTSIDE);
  num.setNegativeFormat(num.LEFT_DASH);
  num.setNegativeRed(false);
  num.setSeparators(true, ",", ",");

  this.settings.numberFormat = {};
  this.settings.numberFormat.precisionLimitUpper = 1e15;
  this.settings.numberFormat.precisionLimitLower = 1e-11;
  this.settings.numberFormat.roundingLimit = 1e9;
  this.settings.numberFormat.precisionDefault = 15;
  this.settings.numberFormat.precisionExponential = 10;
  this.settings.numberFormat.fixedDefault = 11;

  this.num = num;
};

/* return number as formatted string */
InstaCalc.prototype.formatNumber = function (n, settings) {
  var str = n.toString();

  // if number is huge, just return.
  if (n >= this.settings.numberFormat.precisionLimitUpper) {
    // limit of precision
    return n.toPrecision(10).replace("e+", "e"); // turns to exponential, then remove extra quotes
    //return n.toExponential().toString().replace("e+", "e"); // but can't have e+... need just e
  }

  if (InstaCalc.unitlabel != "") {
    // this is a unit... no rounding else destroys precision
    if (n <= this.settings.numberFormat.precisionLimitLower) {
      return n.toString();
    }
  }

  n = parseFloat(n.toPrecision(this.settings.numberFormat.precisionDefault));

  if (this.format.currency) {
    n = n.toFixed(this.format.decimals);
  } else {
    n = n.toFixed(this.settings.numberFormat.fixedDefault); // round to 11 places; avoid bugs like 1.0 - 0.9 - 0.1
  }

  // no extra precision if a whole number
  //num.setPlaces('6', false);
  // do not round off

  // InstaCalc.settings.round === true
  if (n >= this.settings.numberFormat.roundingLimit) {
    // bug; 39.37B became 39,369,999,999.9996
    n = parseFloat(n).toPrecision(this.settings.numberFormat.precisionDefault); // was 16?
  } else {
    this.num.setPlaces("-1", false);
  }

  this.num.setPlaces("-1", false);

  if (this.format.currency) {
    this.num.setPlaces(this.format.decimals); // to fixed number of places;
    this.num.setCurrencyValue(this.format.currencySymbol);
  }

  // passed-in setting overrides all
  if (settings.rounding >= 0) {
    this.num.setPlaces(settings.rounding);
  }

  this.num.setNumber(n);

  // set number of places dynamically based on length of decimal?
  // TODO: Go through and do experiments with precision.
  //num.setPlaces('10', false);

  return this.num.toFormatted();
};

InstaCalc.prototype.getRowValue = function (i) {
  if (this.current_row == i) {
    return undefined;
  }

  return this.outputCache[i] ? this.parseNumber(this.outputCache[i]) : 0;
};

/* returns array of values from row "start" to row "end"
 uses real ROW LABELS; not the cell reference. Starts at "1"
 returns [[1,row1], [2,row2]... ]
 */
InstaCalc.prototype.getRowValues = function (start, end) {
  if (typeof start == "string") {
    start = parseInt(start, 10);
  }
  if (typeof end == "string") {
    end = parseInt(end, 10);
  }

  if (end < start) {
    return [];
  }
  if (start < 1 || end > this.cells) {
    return [];
  }

  var a = [];
  var t = [];

  for (row = start; row <= end; row++) {
    var cell = row - 1;
    var title = "Row " + row;
    var val;

    try {
      val = this.getRowValue(cell);

      // TODO: when we have charts, the title can be extracted from the row
      // title = this.getTitleText(cell);
    } catch (e) {
      val = 0;
      title = "Row " + row;
    }
    if (typeof val === "undefined") {
      val = 0;
      title = "Row " + row;
    }

    if (typeof val != "number") {
      val = this.parseNumber(val);
    }

    if (title === "") {
      title = "Row " + row;
    }

    a.push([row, val]);
    t.push([title, val]);
  }

  this.datatitles = t;
  this.data = a;

  return a;
};

InstaCalc.prototype.instacalc_compute = function (inputs, settings) {
  var outputs = new Array(inputs.length);
  this.cells = inputs.length;

  settings = settings || {};

  // ugly to have this here. but reset everything between computes.
  // clear output cache for everything...
  for (var i = 0; i < this.outputCache.length; i++) {
    this.outputCache[i] = 0;
  }

  InstaCalc.resetLocals();

  var calc = this;
  var redos = [];

  function compute(i) {
    calc.reset_globals();
    var ret = InstaCalc.blank_calc_object();
    ret.input = inputs[i]; // input to evaluate
    ret.row = i;

    calc.current_row = i; // for functions that need to row the row

    /* parse comments */
    var a = InstaCalc.extractComment(ret.input);
    ret.input = a[0]; // first result
    ret.comment = a[1].toString(); // BUGFIX: do not display comments directly - escapeHTML before display

    /* parse row title */
    ret.title = InstaCalc.findTitle(ret.input);

    calc.parseCalculationObject(ret); // do cleanup
    calc.evaluateCalculationObject(ret, settings); // do eval; errors are set here. sets final_result

    redos[i] = calc.shouldRedoCalculation(ret);

    /* Add labels for units -- determined after units have been evaluated */
    if (InstaCalc.unitlabel) {
      // has a unit label
      ret.comment = InstaCalc.unitlabel + ret.comment;
    }

    calc.outputCache[i] = ret.final_result; // cache the output value;
    ret.result = ret.final_result;

    return ret;
  }

  for (var i = 0; i < inputs.length; i++) {
    outputs[i] = compute(i);
  }

  var passes = redos.filter(function (redo) {
    return redo == true;
  }).length;
  var lines_to_redo = redos.slice(0);

  // TODO: Yes, ugly O(N^2) algo to resolve dependencies... OK for small amounts of broken refs
  for (var i = 0; i < passes; i++) {
    // resolve dependencies from bottom
    for (var j = redos.length - 1; j >= 0; j--) {
      if (redos[j]) {
        // compute sets the redo array as needed
        outputs[j] = compute(j);
      }
    }
  }
  return outputs;
};

/*********************************************
Math Constants and Functions
*********************************************/

var Unit = InstaCalc.Unit;

InstaCalc.prototype.cn = {}; // constants
InstaCalc.prototype.cn.PHI = (1 + Math.sqrt(5)) / 2;
InstaCalc.prototype.cn.MOL = 6.0221415e23;

/* container object for the instacalc functions; keep in a separate namespace */

var fn = { base: null }; // base object with format settings, etc.
function functionContainer(base) {
  fn.base = base;
  return fn;
}

// when accessing the methods, use this.fn().sin because we need to set the reference to the base
InstaCalc.prototype.fn = function () {
  this.fn.base = this.fn.base || this;
  return this.fn;
};
InstaCalc.prototype.fn.base = null;

InstaCalc.prototype.fn.tolerance = 1e-14;  // tolerance level for considering a value as zero or one

InstaCalc.prototype.fn.toleranceCheck = function (result) {
  if (Math.abs(result) < this.tolerance) return 0;
  // if result is close to one or negative one, round it
  if (Math.abs(1 - Math.abs(result)) < this.tolerance) return Math.round(result);

  return result;
}

// converts deg to radians
InstaCalc.prototype.fn.deg2rad = function (deg) {
  return (deg * Math.PI) / 180.0;
};

InstaCalc.prototype.fn.rad2deg = function (rad) {
  return (rad * 180.0) / Math.PI;
};

// convert n to radians if needed
InstaCalc.prototype.fn.autoRad = function (n) {
  return (this.base.format.angle == "rad") ? n : this.deg2rad(n);
};

InstaCalc.prototype.fn.sin = function (n) {
  if (Complex.isComplex(n)) return Complex.sin(n);

  let rad = this.autoRad(n);
  return this.toleranceCheck(Math.sin(rad));
};

InstaCalc.prototype.fn.cos = function (n) {
  if (Complex.isComplex(n)) return Complex.cos(n);

  let rad = this.autoRad(n);
  return this.toleranceCheck(Math.cos(rad));
};

InstaCalc.prototype.fn.tan = function (n) {
  if (Complex.isComplex(n)) return Complex.tan(n);

  let rad = this.autoRad(n);
  return this.toleranceCheck(Math.tan(rad));
};

InstaCalc.prototype.fn.cot = function (n) {
  if (Complex.isComplex(n)) return Complex.cot(n);

  return 1 / this.tan(n);
};

InstaCalc.prototype.fn.sec = function (n) {
  if (Complex.isComplex(n)) return Complex.sec(n);
  return 1 / this.cos(n);
};

InstaCalc.prototype.fn.csc = function (n) {
  if (Complex.isComplex(n)) return Complex.csc(n);
  return 1 / this.sin(n);
};

// return result in degrees
InstaCalc.prototype.fn.asin = function (n) {
  if (Complex.isComplex(n) || typeof n === "number" && Math.abs(n) > 1) return Complex.asin(n);
  return this.rad2deg(Math.asin(n));
};

InstaCalc.prototype.fn.acos = function (n) {
  if (Complex.isComplex(n) || typeof n === "number" && Math.abs(n) > 1) return Complex.acos(n);
  return this.rad2deg(Math.acos(n));
};

InstaCalc.prototype.fn.atan = function (n) {
  if (Complex.isComplex(n)) return Complex.atan(n);
  return this.rad2deg(Math.atan(n));
};

InstaCalc.prototype.fn.atan2 = function (y, x) {
  if (Complex.anyIsComplex(y, x)) return Complex.atan2(y, x);
  return this.rad2deg(Math.atan2(y, x));
};

InstaCalc.prototype.fn.asec = function (n) {
  if (Complex.isComplex(n)) return Complex.asec(n);
  return this.rad2deg(Math.acos(1 / n));
};

InstaCalc.prototype.fn.acsc = function (n) {
  if (Complex.isComplex(n)) return Complex.acsc(n);
  return this.rad2deg(Math.asin(1 / n));
};

InstaCalc.prototype.fn.acot = function (n) {
  if (Complex.isComplex(n)) return Complex.acot(n);
  return this.rad2deg(Math.atan(1 / n));
};

/*  
// hyperbolic functions - http://www.sosmath.com/trig/hyper/hyper01/hyper01.html
// Polyfill for IE
Math.sinh =
  Math.sinh ||
  function (x) {
    return (Math.exp(x) - Math.exp(-x)) / 2;
  };

Math.cosh =
  Math.cosh ||
  function (x) {
    return (Math.exp(x) + Math.exp(-x)) / 2;
  };

Math.tanh =
  Math.tanh ||
  function (x) {
    var a = Math.exp(+x),
      b = Math.exp(-x);
    return a == Infinity ? 1 : b == Infinity ? -1 : (a - b) / (a + b);
  };

Math.asinh =
  Math.asinh ||
  function (x) {
    if (x === -Infinity) {
      return x;
    } else {
      return Math.log(x + Math.sqrt(x * x + 1));
    }
  };

Math.acosh =
  Math.acosh ||
  function (x) {
    return Math.log(x + Math.sqrt(x * x - 1));
  };

Math.atanh =
  Math.atanh ||
  function (x) {
    return Math.log((1 + x) / (1 - x)) / 2;
  };
 */

InstaCalc.prototype.fn.sinh = function (n) {
  return Complex.sinh(n);
  //  return Math.sinh(n);
};

InstaCalc.prototype.fn.cosh = function (n) {
  return Complex.cosh(n);
  //  return Math.cosh(n);
};

InstaCalc.prototype.fn.tanh = function (n) {
  return Complex.tanh(n);
  //  return Math.tanh(n);
};

InstaCalc.prototype.fn.coth = function (n) {
  return Complex.coth(n);
  //  return 1 / this.tanh(n);
};

InstaCalc.prototype.fn.sech = function (n) {
  return Complex.sech(n);
  //  return 1 / this.cosh(n);
};

InstaCalc.prototype.fn.csch = function (n) {
  return Complex.csch(n);
  //  return 1 / this.sinh(n);
};

InstaCalc.prototype.fn.asinh = function (n) {
  return Complex.asinh(n);
  //  return Math.asinh(n);
};

// 2 log (sqrt((x+1)/2) + sqrt((x-1)/2))
InstaCalc.prototype.fn.acosh = function (n) {
  return Complex.acosh(n);
  //  return Math.acosh(n);
};

// (log (1+x) - log (1-x))/2
InstaCalc.prototype.fn.atanh = function (n) {
  return Complex.atanh(n);
  //  return Math.atanh(n);
};

InstaCalc.prototype.fn.asech = function (n) {
  return Complex.asech(n);
  //  return Math.acosh(1 / n);
};

InstaCalc.prototype.fn.acsch = function (n) {
  return Complex.acsch(n);
  //  return Math.asinh(1 / n);
};

InstaCalc.prototype.fn.acoth = function (n) {
  return Complex.acoth(n);
  //  return Math.atanh(1 / n);
};

/* Binet's formula: see http://en.wikipedia.org/wiki/Fibonacci_number */
InstaCalc.prototype.fn.fibonacci = function (n) {
  var t = (Math.pow(this.base.cn.PHI, n) - Math.pow(1 - this.base.cn.PHI, n)) / Math.sqrt(5);
  return Math.round(t);
};

InstaCalc.prototype.fn.factorial = function (n) {
  if (!InstaCalc.isdefined(n)) {
    return;
  }
  if (n < 0) {
    return "undefined";
  }
  if (n === 0 || n == 1) {
    return 1;
  }
  if (n > 170) {
    return "Way too big!";
  }

  var i = 1;
  n = Math.ceil(n); // round up
  while (n > 1) {
    i *= n;
    n--;
  }
  return i;
};

// stats helper functions

InstaCalc.prototype.fn.beta = function (a, b) {
  return this.gamma(a) * this.gamma(b) / this.gamma(a + b);
};

InstaCalc.prototype.fn.regularizedIncompleteBeta = function (x, a, b) {
  if (x < 0 || x > 1) return NaN;
  const bt = (x === 0 || x === 1) ? 0 :
    Math.exp(Math.log(x) * a + Math.log(1 - x) * b - Math.log(this.beta(a, b)));
  if (x < (a + 1) / (a + b + 2)) {
    return bt * this.betaCF(x, a, b) / a;
  } else {
    return 1 - bt * this.betaCF(1 - x, b, a) / b;
  }
};

InstaCalc.prototype.fn.incompleteBetaInverse = function (p, a, b) {
  var EPS = 1e-8;
  var a1 = a - 1;
  var b1 = b - 1;
  var j = 0;
  var lna, lnb, pp, t, u, err, x, al, h, w, afac;
  if (p <= 0)
    return 0;
  if (p >= 1)
    return 1;
  if (a >= 1 && b >= 1) {
    pp = (p < 0.5) ? p : 1 - p;
    t = Math.sqrt(-2 * Math.log(pp));
    x = (2.30753 + t * 0.27061) / (1 + t * (0.99229 + t * 0.04481)) - t;
    if (p < 0.5)
      x = -x;
    al = (x * x - 3) / 6;
    h = 2 / (1 / (2 * a - 1) + 1 / (2 * b - 1));
    w = (x * Math.sqrt(al + h) / h) - (1 / (2 * b - 1) - 1 / (2 * a - 1)) *
      (al + 5 / 6 - 2 / (3 * h));
    x = a / (a + b * Math.exp(2 * w));
  } else {
    lna = Math.log(a / (a + b));
    lnb = Math.log(b / (a + b));
    t = Math.exp(a * lna) / a;
    u = Math.exp(b * lnb) / b;
    w = t + u;
    if (p < t / w)
      x = Math.pow(a * w * p, 1 / a);
    else
      x = 1 - Math.pow(b * w * (1 - p), 1 / b);
  }
  afac = -this.logGamma(a) - this.logGamma(b) + this.logGamma(a + b);
  for (; j < 10; j++) {
    if (x === 0 || x === 1)
      return x;
    err = this.incompleteBeta(x, a, b) - p;
    t = Math.exp(a1 * Math.log(x) + b1 * Math.log(1 - x) + afac);
    u = err / t;
    x -= (t = u / (1 - 0.5 * Math.min(1, u * (a1 / x - b1 / (1 - x)))));
    if (x <= 0)
      x = 0.5 * (x + t);
    if (x >= 1)
      x = 0.5 * (x + t + 1);
    if (Math.abs(t) < EPS * x && j > 0)
      break;
  }
  return x;
};

// beta continued fraction
InstaCalc.prototype.fn.betaCF = function (x, a, b) {
  if (x < 0 || x > 1 || a <= 0 || b <= 0) {
    throw new Error("Invalid input parameters for betaCF");
  }

  const MAX_ITERATIONS = 200;
  const EPSILON = 1e-8;
  let qab = a + b;
  let qap = a + 1;
  let qam = a - 1;
  let c = 1;
  let d = 1 - qab * x / qap;
  if (Math.abs(d) < EPSILON) d = EPSILON;
  d = 1 / d;
  let h = d;
  for (let m = 1; m <= MAX_ITERATIONS; m++) {
    let m2 = 2 * m;
    let aa = m * (b - m) * x / ((qam + m2) * (a + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < EPSILON) d = EPSILON;
    c = 1 + aa / c;
    if (Math.abs(c) < EPSILON) c = EPSILON;
    d = 1 / d;
    h *= d * c;
    aa = -(a + m) * (qab + m) * x / ((a + m2) * (qap + m2));
    d = 1 + aa * d;
    if (Math.abs(d) < EPSILON) d = EPSILON;
    c = 1 + aa / c;
    if (Math.abs(c) < EPSILON) c = EPSILON;
    d = 1 / d;
    let del = d * c;
    h *= del;
    if (Math.abs(del - 1.0) < EPSILON) break;
  }
  return h;
};

InstaCalc.prototype.fn.gamma = function (z) {
  if (!InstaCalc.isdefined(z)) {
    return;
  }

  // For positive integers, gamma(n) = (n-1)!
  if (Number.isInteger(z) && z > 0) {
    return this.factorial(z - 1);
  }

  if (z < 0.5) {
    return Math.PI / (Math.sin(Math.PI * z) * this.gamma(1 - z));
  }

  z -= 1;

  var g = 5.0;  // Using g = 5 for Lanczos approximation
  var p = [
    1.000000000190015,
    76.18009172947146,
    -86.50532032941677,
    24.01409824083091,
    -1.231739572450155,
    1.208650973866179e-3,
    -5.395239384953e-6
  ];

  var x = z + g + 0.5;
  var sum = p[0];

  for (var i = 1; i < p.length; i++) {
    sum += p[i] / (z + i);
  }

  var approx = Math.sqrt(2 * Math.PI) * Math.pow(x, z + 0.5) * Math.exp(-x) * sum;

  return approx;
};

InstaCalc.prototype.fn.gammapInv = function (p, a) {
  var j = 0;
  var a1 = a - 1;
  var EPS = 1e-8;
  var gln = this.logGamma(a);
  var x, err, t, u, pp, lna1, afac;

  if (p >= 1)
    return Math.max(100, a + 100 * Math.sqrt(a));
  if (p <= 0)
    return 0;
  if (a > 1) {
    lna1 = Math.log(a1);
    afac = Math.exp(a1 * (lna1 - 1) - gln);
    pp = (p < 0.5) ? p : 1 - p;
    t = Math.sqrt(-2 * Math.log(pp));
    x = (2.30753 + t * 0.27061) / (1 + t * (0.99229 + t * 0.04481)) - t;
    if (p < 0.5)
      x = -x;
    x = Math.max(1e-3,
      a * Math.pow(1 - 1 / (9 * a) - x / (3 * Math.sqrt(a)), 3));
  } else {
    t = 1 - a * (0.253 + a * 0.12);
    if (p < t)
      x = Math.pow(p / t, 1 / a);
    else
      x = 1 - Math.log(1 - (p - t) / (1 - t));
  }

  for (; j < 12; j++) {
    if (x <= 0)
      return 0;
    err = this.gammaLowerRegularized(a, x) - p;
    if (a > 1)
      t = afac * Math.exp(-(x - a1) + a1 * (Math.log(x) - lna1));
    else
      t = Math.exp(-x + a1 * Math.log(x) - gln);
    u = err / t;
    x -= (t = u / (1 - 0.5 * Math.min(1, u * ((a - 1) / x - 1))));
    if (x <= 0)
      x = 0.5 * (x + t);
    if (Math.abs(t) < EPS * x)
      break;
  }

  return x;
};

InstaCalc.prototype.fn.logGamma = function (x) {
  if (x <= 0) {
    return Infinity;
  }

  const c = [
    76.18009172947146,
    -86.50532032941677,
    24.01409824083091,
    -1.231739572450155,
    0.1208650973866179e-2,
    -0.5395239384953e-5
  ];

  let sum = 1.000000000190015;
  let y = x;
  const z = y + 5.5;

  for (let i = 0; i < 6; i++) {
    y += 1;
    sum += c[i] / y;
  }

  return Math.log(2.5066282746310005 * sum / x) + (x + 0.5) * Math.log(z) - z;
};

InstaCalc.prototype.fn.gammaLowerRegularized = function (a, x) {
  if (x <= 0) {
    return 0;
  }
  if (x >= a + 1) {
    return 1 - this.gammaUpperRegularized(a, x);
  }

  const maxIterations = 1000;
  const epsilon = 1e-15;

  let sum = 1 / a;
  let term = sum;
  for (let n = 1; n < maxIterations; n++) {
    term *= x / (a + n);
    sum += term;
    if (Math.abs(term) < Math.abs(sum) * epsilon) {
      break;
    }
  }

  return Math.exp(a * Math.log(x) - x - this.logGamma(a)) * sum;
};

InstaCalc.prototype.fn.gammaLowerIncomplete = function (s, x) {
  if (x <= 0 || s <= 0) return 0;
  const epsilon = 1e-10;
  const maxIterations = 1000;
  let sum = 0;
  let term = 1 / s;
  let n = 1;

  while (Math.abs(term) > epsilon && n < maxIterations) {
    sum += term;
    term *= x / (s + n);
    n++;
  }

  return Math.pow(x, s) * Math.exp(-x) * sum;
};

InstaCalc.prototype.fn.gammaUpperRegularized = function (a, x) {
  if (x <= 0) {
    return 1;
  }
  if (x < a + 1) {
    return 1 - this.gammaLowerRegularized(a, x);
  }

  const maxIterations = 1000;
  const epsilon = 1e-15;

  let b = x + 1 - a;
  let c = 1 / 1e-30;
  let d = 1 / b;
  let h = d;

  for (let i = 1; i <= maxIterations; i++) {
    const an = -i * (i - a);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < 1e-30) {
      d = 1e-30;
    }
    c = b + an / c;
    if (Math.abs(c) < 1e-30) {
      c = 1e-30;
    }
    d = 1 / d;
    const delta = d * c;
    h *= delta;
    if (Math.abs(delta - 1) < epsilon) {
      break;
    }
  }

  return Math.exp(a * Math.log(x) - x - this.logGamma(a)) * h;
};

InstaCalc.prototype.fn.logBeta = function (a, b) {
  return this.logGamma(a) + this.logGamma(b) - this.logGamma(a + b);
};

InstaCalc.prototype.fn.incompleteBeta = function (x, a, b) {
  // Factors in front of the continued fraction.
  var bt = (x === 0 || x === 1) ? 0 :
    Math.exp(this.logGamma(a + b) - this.logGamma(a) -
      this.logGamma(b) + a * Math.log(x) + b *
      Math.log(1 - x));
  if (x < 0 || x > 1)
    return false;
  if (x < (a + 1) / (a + b + 2))
    // Use continued fraction directly.
    return bt * this.betaCF(x, a, b) / a;
  // else use continued fraction after making the symmetry transformation.
  return 1 - bt * this.betaCF(1 - x, b, a) / b;
};

/* binomial function: choose k items from n, order irrelevant */
InstaCalc.prototype.fn.combination = function (n, k) {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;

  return this.permutation(n, k) / this.factorial(k);
};

InstaCalc.prototype.fn.permutation = function (n, k) {
  return this.factorial(n) / this.factorial(n - k);
};



/*
InstaCalc.prototype.fn.round = function (n, digits = 0) {
  if (!InstaCalc.isdefined(n)) {
    return;
  }

  var p = Math.pow(10, digits);
  return Math.round(n * p) / p;
};
*/

InstaCalc.prototype.fn.round = function (n, digits = 0) {
  if (!InstaCalc.isdefined(n)) {
    return;
  }

  if (Number.isInteger(digits)) {
    var p = Math.pow(10, digits);
    return Math.round(n * p) / p;
  } else {
    return Math.round(n / digits) * digits;
  }
};

InstaCalc.prototype.fn.square = (n) => Complex.pow(n, 2);
InstaCalc.prototype.fn.cube = (n) => Complex.pow(n, 3);
InstaCalc.prototype.fn.sqrt = (n) => Complex.pow(n, 0.5);
InstaCalc.prototype.fn.cbrt = (n) => Complex.pow(n, 1 / 3);

InstaCalc.prototype.fn.inverse = (n) => Complex.inverse(n);

/* convert sequence of binary digits to decimal */

/* change base to make all the letters lowercase...then do ascii codes for a - z? */
InstaCalc.prototype.fn.convert = function (str, base) {
  var i = 0;
  var sum = 0;

  var digits = str.split("").reverse();

  for (var i = 0; i < digits.length; i++) {
    var digit = parseInt(digits[i]);
    sum += digit * Math.pow(base, i);
  }

  return sum;
};

// signed binary conversion
InstaCalc.prototype.fn.sbin = function (n) {
  var i = 0;
  var str = "";

  for (var i = 0; i < 32; i++) {
    var digit = (n & (1 << i)) >> i;

    if (digit == 0) {
      str += "0";
    } else {
      // overflow goes to -1; so just make a 1 here
      str += "1";
    }
  }

  str = str.split("").reverse().join(""); // reverse string
  return str;
};

InstaCalc.prototype.fn.bin = function (n) {
  // show binary in groups of 8
  var tmp;
  var paddingchar = "0";
  if (n < 0) {
    // two's complement
    return this.sbin(n);
  } else {
    tmp = n.toString(2);
  }

  if (isNaN(tmp) || tmp == "") {
    return "";
  }

  var i;
  var padding = "0b"; // padding of 0's; start with 0b
  var extradigits = (8 - (tmp.length % 8)) % 8;

  for (i = 0; i < extradigits; i++) {
    padding += paddingchar;
  }

  return padding + tmp;
};

InstaCalc.prototype.fn.oct = function (n) {
  let tmp;
  if (n < 0) {
    tmp = (n >>> 0).toString(8);
  } else {
    tmp = n.toString(8);
  }

  return "0o" + tmp;
};

InstaCalc.prototype.fn.hex = function (n) {
  let tmp;
  if (n < 0) {
    // For negative numbers, use two's complement
    tmp = (n >>> 0).toString(16); // Convert to 32-bit unsigned
  } else {
    tmp = n.toString(16);
  }

  // Calculate the number of leading zeros needed and pad tmp with them
  let paddingNeeded = (4 - (tmp.length % 4)) % 4;
  let padding = "0".repeat(paddingNeeded);
  return "0x" + padding + tmp;
};

InstaCalc.prototype.fn.dec = function (n) {
  return n;
};

InstaCalc.prototype.fn.exp = (z) => Complex.exp(z); // complex exponentiation
InstaCalc.prototype.fn.log = (n) => Complex.log(n);

InstaCalc.prototype.fn.log10 = (n) => Complex.log10(n);
InstaCalc.prototype.fn.log2 = (n) => Complex.log2(n);

// from binary -- have functions to aid with parsing, which isn't easy with regular expressions
// hex and oct can be written using 0x1234abcdef or 01234567
InstaCalc.prototype.fn.frombin = function (str) {
  return this.convert(str.replace("0b", ""), 2);
};
InstaCalc.prototype.fn.fromhex = function (str) {
  return this.convert(str.replace("0x", ""), 16);
};
InstaCalc.prototype.fn.fromoct = function (str) {
  return this.convert(str.replace("0o", ""), 8);
};

// radians to degrees; 2 * PI radians = 360 degrees, so radian = 360 / (2*PI) degrees
InstaCalc.prototype.fn.deg = function (n) {
  return (n * 180) / Math.PI;
};
InstaCalc.prototype.fn.rad = function (n) {
  return (n * Math.PI) / 180;
};

// string processing functions

InstaCalc.prototype.fn.trim = function (str) {
  return str.trim();
};

// python-style print()
InstaCalc.prototype.fn.print = function (...args) {
  // Check if last argument contains options
  let sep = " ";
  let end = "";

  if (args.length > 0 && typeof args[args.length - 1] === 'object' && args[args.length - 1] !== null) {
    const options = args.pop();
    sep = options.sep !== undefined ? options.sep : sep;
    end = options.end !== undefined ? options.end : end;
  }

  // Convert all args to strings and join them
  return args.map(String).join(sep) + end;
};

// return seconds in format HH:MM:SS
InstaCalc.prototype.fn.time_old = function (n) {
  years = days = hours = minutes = seconds = 0;
  var show = false; // set to true to display previous
  if (n < 0) {
    n = Math.abs(n);
  }

  if (n >= 31536000) {
    // years - not leap year. Just 365 days.
    years = Math.floor(n / 31536000);
    n -= Math.round(years * 31536000);
  }

  if (n >= 86400) {
    // days
    days = Math.floor(n / 86400);
    n -= Math.round(days * 86400);
  }

  if (n >= 3600) {
    // hours
    hours = Math.floor(n / 3600);
    n -= Math.round(hours * 3600);
  }

  if (n >= 60) {
    minutes = Math.floor(n / 60);
    n -= Math.round(minutes * 60);
  }
  seconds = n;

  str = "";

  if (years > 0) {
    str += years + "y ";
    show = true;
  }

  if (days > 0 || show) {
    str += days + "d ";
    show = true;
  }

  if (hours > 0 || show) {
    str += hours + "h ";
    show = true;
  }

  if (minutes > 0 || show) {
    str += minutes + "m ";
    show = true;
  }

  if (seconds > 0 || show) {
    str += seconds.toPrecision(4) + "s ";
    show = true;
  }

  return str;
  /*
     // Todo: add right amount of precision. Show 0h if there are days, etc. Show if any of preceding are shown.

          if (minutes < 10) //need leading 0
              str += "" + minutes + "m ";
          else
              str += minutes + "m ";

      // only show precision if we need it
      if (seconds < 10){
              str += "" + seconds.toFixed(2) + "s";
          }
          else{
              str += seconds.toFixed(2) + "s";
          }

     return str;
     */
};

InstaCalc.prototype.fn.time = function (n) {
  n = Math.abs(n);
  let str = "";

  const units = [
    { name: 'y', length: 31536000 }, // years
    { name: 'd', length: 86400 },    // days
    { name: 'h', length: 3600 },     // hours
    { name: 'm', length: 60 },       // minutes
    { name: 's', length: 1 },        // seconds
  ];

  for (let unit of units) {
    let amount = Math.floor(n / unit.length);

    if (amount > 0) {
      // Check if the number is a whole number
      if (Number.isInteger(amount)) {
        str += `${amount}${unit.name} `;
      } else {
        str += `${amount.toPrecision(4)}${unit.name} `;
      }
      n -= amount * unit.length;
    }
  }

  return str.trim();
};

// convert timestring like 3h 15m into seconds
InstaCalc.prototype.fn.parseTime = function (timeString) {
  // Regex to match the pattern "3m 15s", "3 minutes 15 seconds", "3:15", "3:15:23" etc.
  const regex = /(\d+(\.\d+)?)\s*(y(ears?)?|d(ays?)?|h(ours?)?|m(inutes?)?|s(econds?)?|:)?/gi;

  // Multipliers to convert each unit to seconds
  const unitsInSeconds = {
    y: 31536000, // years
    d: 86400,    // days
    h: 3600,     // hours
    m: 60,       // minutes
    s: 1         // seconds
  };

  let totalSeconds = 0;

  // Special case for "3:15:23" format
  let colonGroups = timeString.split(':').reverse();
  if (colonGroups.length > 1) {
    for (let i = 0; i < colonGroups.length; i++) {
      totalSeconds += parseFloat(colonGroups[i]) * unitsInSeconds[['s', 'm', 'h'][i]];
    }
    return totalSeconds;
  }

  // Extract all matched groups from the string
  let match;
  while ((match = regex.exec(timeString)) !== null) {
    let quantity = parseFloat(match[1]);
    let unit = match[3]?.charAt(0).toLowerCase(); // Get the first letter of the unit matched

    totalSeconds += quantity * (unitsInSeconds[unit] || 0);
  }

  return totalSeconds;
};

// Helper function to extract numeric values from an array
InstaCalc.prototype.fn.extractNumericValues = function (a) {
  // see if single-dimensional array, then return that
  if (a[0] && typeof a[0] === 'number') {
    return a.map(el => [null, el]);
  }

  return a.filter(el => typeof el[1] === 'number' && !isNaN(el[1]));
};

InstaCalc.prototype.fn.extractNumericValuesAsFlatArray = function (a) {
  if (!a) {
    return [];
  }

  return this.extractNumericValues(a).map(el => el[1]);
};

InstaCalc.prototype.fn.arraymax = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  let max = numericValues[0][1];
  for (let i = 0; i < numericValues.length; i++) {
    if (numericValues[i][1] > max) {
      max = numericValues[i][1];
    }
  }
  return max;
};

InstaCalc.prototype.fn.arraymin = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  let min = numericValues[0][1];
  for (let i = 0; i < numericValues.length; i++) {
    if (numericValues[i][1] < min) {
      min = numericValues[i][1];
    }
  }
  return min;
};

InstaCalc.prototype.fn.arraysum = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  let total = 0;
  for (let i = 0; i < numericValues.length; i++) {
    total += numericValues[i][1];
  }
  return total;
};

InstaCalc.prototype.fn.arrayproduct = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  let prod = 1;
  for (let i = 0; i < numericValues.length; i++) {
    prod *= numericValues[i][1];
  }
  return prod;
};

InstaCalc.prototype.fn.arraydifference = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  let total = numericValues[0][1];
  for (let i = 1; i < numericValues.length; i++) {
    total -= numericValues[i][1];
  }
  return total;
};

InstaCalc.prototype.fn.arrayavg = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  const total = this.arraysum(numericValues);
  const average = total / numericValues.length;
  return average;
};

InstaCalc.prototype.fn.arraymedian = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  const mid = Math.floor(numericValues.length / 2),
    nums = [...numericValues].sort((a, b) => a[1] - b[1]);
  return numericValues.length % 2 !== 0 ? nums[mid][1] : (nums[mid - 1][1] + nums[mid][1]) / 2;
};

InstaCalc.prototype.fn.arraymode = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  return numericValues.sort((a, b) =>
    numericValues.filter(v => v[1] === a[1]).length - numericValues.filter(v => v[1] === b[1]).length
  ).pop()[1];
};

InstaCalc.prototype.fn.arraygeomean = function (a) {
  const numericValues = this.extractNumericValuesAsFlatArray(a);
  const product = numericValues.reduce((acc, value) => acc * value, 1);
  const geometricMean = Math.pow(product, 1 / numericValues.length);
  return geometricMean;
};

InstaCalc.prototype.fn.arrayvariance = function (a) {
  const numericValues = this.extractNumericValuesAsFlatArray(a);

  if (numericValues.length < 2) {
    return null;
  }

  const mean = this.arrayavg(a);
  const sumSquaredDeviations = numericValues.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0);

  // Calculate sample variance (Excel VAR.S)
  return sumSquaredDeviations / (numericValues.length - 1);
};

InstaCalc.prototype.fn.arrayvariancePopulation = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return null;
  }

  const sampleVariance = this.arrayvariance(a);

  // Adjust sample variance to population variance
  const populationVariance = sampleVariance * (numericValues.length - 1) / numericValues.length;
  return populationVariance;
};


// covariance of two arrays
InstaCalc.prototype.fn.arraycovariance = function (a, b) {

  const numericValuesA = this.extractNumericValuesAsFlatArray(a);
  const numericValuesB = this.extractNumericValuesAsFlatArray(b);

  if (numericValuesA.length !== numericValuesB.length || numericValuesA.length < 2) {
    return null;
  }

  const meanA = this.arrayavg(a);
  const meanB = this.arrayavg(b);

  let covariance = 0;
  for (let i = 0; i < numericValuesA.length; i++) {
    covariance += (numericValuesA[i] - meanA) * (numericValuesB[i] - meanB);
  }

  covariance /= numericValuesA.length - 1;
  return covariance;
};

// population covariance
InstaCalc.prototype.fn.arraycovariancePopulation = function (a, b) {
  let covariance = this.arraycovariance(a, b);

  if (covariance === null) {
    return null;
  }

  let length = this.extractNumericValuesAsFlatArray(a).length;
  covariance *= (length - 1) / length;

  return covariance;
};

InstaCalc.prototype.fn.arraystddev = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return null;
  }

  return Math.sqrt(this.arrayvariance(numericValues));
};

InstaCalc.prototype.fn.arraystddevPopulation = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return null;
  }

  return Math.sqrt(this.arrayvariancePopulation(numericValues));
};

InstaCalc.prototype.fn.arrayskewness = function (a) {
  const numericValues = this.extractNumericValues(a);
  if (numericValues.length < 3) {
    return null;
  }

  const n = numericValues.length;
  const mean = this.arrayavg(numericValues);
  const standardDeviation = Math.sqrt(this.arrayvariance(numericValues));

  if (standardDeviation === 0) {
    return 0;
  }

  let sumCubedDeviations = 0;
  for (let i = 0; i < n; i++) {
    let val = numericValues[i][1];
    sumCubedDeviations += Math.pow(val - mean, 3);
  }

  const skewness = (n * sumCubedDeviations) / ((n - 1) * (n - 2) * Math.pow(standardDeviation, 3));
  return skewness;
};

InstaCalc.prototype.fn.arraykurtosis = function (a) {
  const numericValues = this.extractNumericValues(a);
  if (numericValues.length < 4) {
    return null;
  }

  const n = numericValues.length;
  const mean = this.arrayavg(numericValues);
  const variance = this.arrayvariance(numericValues);

  let sumFourthDeviations = 0;
  for (let i = 0; i < n; i++) {
    let val = typeof numericValues[i] === 'object' ? numericValues[i][1] : numericValues[i];
    sumFourthDeviations += Math.pow(val - mean, 4);
  }

  const kurtosis = ((n * (n + 1)) / ((n - 1) * (n - 2) * (n - 3))) * (sumFourthDeviations / Math.pow(variance, 2)) - (3 * (n - 1) * (n - 1)) / ((n - 2) * (n - 3));
  return kurtosis;
};

InstaCalc.prototype.fn.arrayrange = function (a) {
  const numericValues = this.extractNumericValues(a);

  if (numericValues.length === 0) {
    return "";
  }

  const min = this.arraymin(a);
  const max = this.arraymax(a);
  return max - min;
};

InstaCalc.prototype.fn.arraycount = function (a) {
  return this.extractNumericValues(a).length;
};

InstaCalc.prototype.fn.arraysort = function (a) {
  let values = this.extractNumericValuesAsFlatArray(a);
  values.sort((a, b) => a - b);
  return values;
};

InstaCalc.prototype.fn.arrayreverse = function (a) {
  let values = this.extractNumericValuesAsFlatArray(a);
  values.reverse();
  return values;
};

// function to return the value at a given percentile in an array
InstaCalc.prototype.fn.arraypercentile = function (array, p) {
  // Extract numeric values from the array
  let values = this.extractNumericValuesAsFlatArray(array);

  if (values.length === 0) {
    return "";
  }

  // k is the percentile (0 to 1, or 0 to 100)
  let percentile = p;

  // if percentile is less than 1, assume it's 0-1 scale, otherwise 0-100
  if (percentile < 1) {
    percentile *= 100;
  }

  if (percentile < 0 || percentile > 100) {
    return "";
  }

  // Sort values
  values.sort((a, b) => a - b);

  // Excel PERCENTILE formula: position = (percentile/100) * (n-1)
  const index = (percentile / 100) * (values.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const weight = index - lower;

  if (lower === upper) {
    return values[index];
  } else {
    return values[lower] * (1 - weight) + values[upper] * weight;
  }
};

// Helper function to evaluate a function with a value
InstaCalc.prototype.fn.evaluateFunction = function (fn, value) {
  if (fn && typeof fn === 'object' && fn.type === "function") {
    if (typeof IC !== 'undefined' && IC.evalUserFunction) {
      return IC.evalUserFunction(fn, [value]);
    }
  }
  return value; // fallback
};

// Helper function to validate array inputs
InstaCalc.prototype.fn.validateArrayInput = function (a, fn) {
  if (!a || !Array.isArray(a) || a.length === 0) {
    return { valid: false, error: "empty array" };
  }
  if (!fn) {
    return { valid: false, error: "no function" };
  }
  const numericValues = this.extractNumericValues(a);
  if (numericValues.length === 0) {
    return { valid: false, error: "no numeric values" };
  }
  return { valid: true, numericValues };
};

InstaCalc.prototype.fn.arraymap = function (a, fn) {
  const validation = this.validateArrayInput(a, fn);
  if (!validation.valid) {
    return "";
  }

  try {
    const results = [];
    for (let i = 0; i < validation.numericValues.length; i++) {
      const element = validation.numericValues[i];
      const value = element[1];
      const result = this.evaluateFunction(fn, value);
      results.push(result);
    }
    return results;
  } catch (error) {
    return "";
  }
};

InstaCalc.prototype.fn.arrayfilter = function (a, predicate) {
  const validation = this.validateArrayInput(a, predicate);
  if (!validation.valid) {
    return "";
  }

  try {
    const results = [];
    for (let i = 0; i < validation.numericValues.length; i++) {
      const element = validation.numericValues[i];
      const value = element[1];
      const shouldInclude = this.evaluateFunction(predicate, value);

      if (shouldInclude) {
        results.push(value);
      }
    }
    return results;
  } catch (error) {
    return "";
  }
};

InstaCalc.prototype.fn.arrayreduce = function (a, fn, initialValue) {
  const validation = this.validateArrayInput(a, fn);
  if (!validation.valid) {
    return "";
  }

  try {
    const values = validation.numericValues.map(element => element[1]);

    if (values.length === 0) {
      return initialValue !== undefined ? initialValue : "";
    }

    let accumulator;
    let startIndex;

    if (initialValue !== undefined) {
      accumulator = initialValue;
      startIndex = 0;
    } else {
      accumulator = values[0];
      startIndex = 1;
    }

    for (let i = startIndex; i < values.length; i++) {
      const currentValue = values[i];
      // For reduce, we need to pass both accumulator and current value
      if (fn && typeof fn === 'object' && fn.type === "function") {
        if (typeof IC !== 'undefined' && IC.evalUserFunction) {
          accumulator = IC.evalUserFunction(fn, [accumulator, currentValue]);
        }
      } else {
        accumulator = currentValue; // fallback
      }
    }

    return accumulator;
  } catch (error) {
    return "";
  }
};

InstaCalc.prototype.fn.arrayfind = function (a, fn) {
  const filtered = this.arrayfilter(a, fn);
  return (filtered && filtered.length > 0) ? filtered[0] : "";
};

InstaCalc.prototype.fn.sequence = function () {
  let count, start = 1, step = 1;

  // Handle object parameter: SEQUENCE({count: 10, start: 3, step: 2})
  if (arguments.length === 1 && typeof arguments[0] === 'object' && arguments[0] !== null && !Array.isArray(arguments[0])) {
    const params = arguments[0];

    // Find parameters with case-insensitive matching
    const getParam = (name) => {
      for (const key in params) {
        if (key.toLowerCase() === name.toLowerCase()) {
          return params[key];
        }
      }
      return undefined;
    };

    count = getParam('count');
    const startParam = getParam('start');
    const stepParam = getParam('step');

    start = startParam !== undefined ? startParam : 1;
    step = stepParam !== undefined ? stepParam : 1;
  }
  // Handle positional parameters: SEQUENCE(count, start, step)
  else {
    count = arguments[0];
    if (arguments.length > 1) start = arguments[1];
    if (arguments.length > 2) step = arguments[2];
  }

  // Validate inputs
  if (count === undefined || count === null || isNaN(count) || count < 0) {
    return "";
  }

  count = Math.floor(count);
  if (count === 0) {
    return [];
  }

  // Generate sequence
  const result = [];
  for (let i = 0; i < count; i++) {
    result.push(start + (i * step));
  }

  return result;
};

InstaCalc.prototype.fn.random = function (a) {
  return Math.ceil(Math.random() * a);
};

// custom functions
InstaCalc.prototype.fn.add = function (a, b) {
  return a + b;
};

InstaCalc.prototype.fn.subtract = function (a, b) {
  return a - b;
};

InstaCalc.prototype.fn.multiply = function (a, b) {
  return a * b;
};

InstaCalc.prototype.fn.divide = function (a, b) {
  return a / b;
};

InstaCalc.prototype.fn.mod = function (a, b) {
  return a % b;
};

InstaCalc.prototype.fn.abs = (a) => Complex.abs(a);

InstaCalc.prototype.fn.gcd = function (a, b) {
  // Ensure numbers are positive
  a = Math.abs(a);
  b = Math.abs(b);

  // Use Euclidean algorithm to find GCD
  while (b) {
    var temp = b;
    b = a % b;
    a = temp;
  }

  return a;
};

InstaCalc.prototype.fn.lcm = function (a, b) {
  // Ensure numbers are positive
  a = Math.abs(a);
  b = Math.abs(b);

  // Calculate LCM
  return (a * b) / this.gcd(a, b);
};

InstaCalc.prototype.fn.sign = function (a) {
  return Math.sign(a);
};

InstaCalc.prototype.fn.clamp = function (n, min, max) {
  return Math.min(Math.max(n, min), max);
};

// https://hewgill.com/picomath/javascript/erf.js.html
InstaCalc.prototype.fn.erf = function (x) {
  // Save the sign of x
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);

  // Constants
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  // Abramowitz and Stegun formula 7.1.26
  if (x < 3.75) {
    const t = 1.0 / (1.0 + p * x);
    return sign * (1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x));
  } else {
    // For large x, use a continued fraction expansion
    let t = 1 / (x + 0.5 + Math.sqrt(x * (x + 1)));
    let sum = t;
    for (let n = 1; n <= 100; n++) {  // Adjust the number of iterations for desired accuracy
      let lastSum = sum;
      t *= -n / (x + n + 0.5);
      sum += t;
      if (Math.abs(sum - lastSum) < 1e-15) break;  // Convergence check
    }
    return sign * (1 - sum * Math.exp(-x * x) / Math.sqrt(Math.PI));
  }
};

InstaCalc.prototype.fn.erfInverse = function (x) {
  if (x < -1 || x > 1) {
    throw new Error("erfInv is only defined for -1 < x < 1");
  }

  if (x === 0) return 0;

  const a = 0.147;
  const sign = x < 0 ? -1 : 1;
  const ln1x2 = Math.log(1 - x * x);

  let tau = sign * Math.sqrt(
    Math.sqrt(
      (2 / (Math.PI * a) + ln1x2 / 2) ** 2 - ln1x2 / a
    ) - (2 / (Math.PI * a) + ln1x2 / 2)
  );

  return tau - (this.erf(tau) - x) / (2 / Math.sqrt(Math.PI) * Math.exp(-tau * tau));
};

/* stats functions */
InstaCalc.prototype.fn.normSInv = function (probability) {
  if (probability <= 0 || probability >= 1) return null;
  return Math.sqrt(2) * this.erfInverse(2 * probability - 1);
};

InstaCalc.prototype.fn.normInv = function (probability, mean, standard_dev) {
  if (probability <= 0 || probability >= 1) return null;
  const z = this.normSInv(probability);
  return mean + z * standard_dev;
};

InstaCalc.prototype.fn.normDist = function (x, mean, standard_dev, cumulative) {
  const variance = Math.pow(standard_dev, 2);
  const factor = 1 / (Math.sqrt(2 * Math.PI * variance));
  const exponent = Math.exp(-Math.pow(x - mean, 2) / (2 * variance));
  if (cumulative) {
    return 0.5 * (1 + this.erf((x - mean) / (standard_dev * Math.sqrt(2))));
  }
  return factor * exponent;
};

InstaCalc.prototype.fn.normSDist = function (z, cumulative) {
  if (cumulative) {
    return 0.5 * (1 + this.erf(z / Math.sqrt(2)));
  }
  const factor = 1 / Math.sqrt(2 * Math.PI);
  const exponent = Math.exp(-0.5 * Math.pow(z, 2));
  return factor * exponent;
};

InstaCalc.prototype.fn.binomDist = function (x, trials, probability_s, cumulative) {
  if (cumulative) {
    let sum = 0;
    for (let k = 0; k <= x; k++) {
      sum += this.combination(trials, k) * Math.pow(probability_s, k) * Math.pow(1 - probability_s, trials - k);
    }
    return sum;
  }
  return this.combination(trials, x) * Math.pow(probability_s, x) * Math.pow(1 - probability_s, trials - x);
};

InstaCalc.prototype.fn.negbinomDist = function (x, r, p, cumulative) {
  if (cumulative) {
    let sum = 0;
    for (let k = 0; k <= x; k++) {
      sum += this.combination(k + r - 1, k) * Math.pow(p, r) * Math.pow(1 - p, k);
    }
    return sum;
  }
  return this.combination(x + r - 1, x) * Math.pow(p, r) * Math.pow(1 - p, x);
};

InstaCalc.prototype.fn.weibullDist = function (x, alpha, beta, cumulative) {
  if (x < 0) return 0;

  if (cumulative) {
    return 1 - Math.exp(-Math.pow(x / beta, alpha));
  }
  return (alpha / beta) * Math.pow(x / beta, alpha - 1) * Math.exp(-Math.pow(x / beta, alpha));
};

InstaCalc.prototype.fn.poissonDist = function (x, mean, cumulative) {
  const lambda = Math.exp(-mean);
  const poisson = Math.pow(mean, x) * lambda / this.factorial(x);
  if (cumulative) {
    let sum = 0;
    for (let k = 0; k <= x; k++) {
      sum += Math.pow(mean, k) * lambda / this.factorial(k);
    }
    return sum;
  }
  return poisson;
};

InstaCalc.prototype.fn.gammaPDF = function (x, alpha, beta) {
  if (x < 0) return 0;
  return Math.pow(x, alpha - 1) * Math.exp(-x / beta) / (Math.pow(beta, alpha) * this.gamma(alpha));
};

InstaCalc.prototype.fn.gammaCDF = function (x, alpha, beta) {
  if (x <= 0) return 0;
  return this.gammaLowerIncomplete(alpha, x / beta) / this.gamma(alpha);
};

InstaCalc.prototype.fn.gammaDist = function (x, alpha, beta, cumulative) {
  if (cumulative) {
    return this.gammaCDF(x, alpha, beta);
  }
  return this.gammaPDF(x, alpha, beta);
};

InstaCalc.prototype.fn.betaDist = function (x, alpha, beta, cumulative) {
  if (x < 0 || x > 1) return 0;

  if (cumulative) {
    return this.betaCDF(x, alpha, beta);
  }
  return this.betaPDF(x, alpha, beta);
};

InstaCalc.prototype.fn.betaPDF = function (x, alpha, beta) {
  if (x < 0 || x > 1) return 0;
  return Math.pow(x, alpha - 1) * Math.pow(1 - x, beta - 1) / this.beta(alpha, beta);
};

InstaCalc.prototype.fn.betaCDF = function (x, alpha, beta) {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  return this.regularizedIncompleteBeta(x, alpha, beta);
};

InstaCalc.prototype.fn.hypgeomDist = function (x, sample_size, number_of_successes, population_size, cumulative) {
  // Check for valid parameters
  if (!Number.isInteger(x) || !Number.isInteger(sample_size) || !Number.isInteger(number_of_successes) || !Number.isInteger(population_size)) {
    throw new Error("all parameters must be integers");
  }
  if (population_size < 0 || number_of_successes < 0 || sample_size < 0 || x < 0) {
    throw new Error("all parameters must be non-negative");
  }
  if (sample_size > population_size) {
    throw new Error("sample_size must not exceed population_size");
  }

  // Adjust number_of_successes if it exceeds population_size
  number_of_successes = Math.min(number_of_successes, population_size);

  if (x > Math.min(sample_size, number_of_successes)) {
    return cumulative ? 1 : 0;
  }

  if (cumulative) {
    let sum = 0;
    for (let k = 0; k <= x; k++) {
      sum += this.hypgeomPMF(k, sample_size, number_of_successes, population_size);
    }
    return sum;
  }
  return this.hypgeomPMF(x, sample_size, number_of_successes, population_size);
};

InstaCalc.prototype.fn.hypgeomPMF = function (x, sample_size, number_of_successes, population_size) {
  // Adjust number_of_successes if it exceeds population_size
  number_of_successes = Math.min(number_of_successes, population_size);

  // Additional check for valid x
  if (x > Math.min(sample_size, number_of_successes)) {
    return 0;
  }

  // Use regular combination function
  let numerator = this.combination(number_of_successes, x) * this.combination(population_size - number_of_successes, sample_size - x);
  let denominator = this.combination(population_size, sample_size);

  // Check for potential division by zero
  if (denominator === 0) {
    return 0; // Return 0 instead of throwing an error to match Excel behavior
  }

  return numerator / denominator;
};

InstaCalc.prototype.fn.lognormDist = function (x, mu, sigma, cumulative) {
  if (x <= 0) return 0;

  if (cumulative) {
    return 0.5 + 0.5 * this.erf((Math.log(x) - mu) / (Math.sqrt(2) * sigma));
  }
  return Math.exp(-Math.pow(Math.log(x) - mu, 2) / (2 * Math.pow(sigma, 2))) / (x * sigma * Math.sqrt(2 * Math.PI));
};

InstaCalc.prototype.fn.lognormInv = function (probability, mu, sigma) {
  return Math.exp(mu + sigma * this.normInv(probability, 0, 1));
};

InstaCalc.prototype.fn.tInv = function (probability, degrees_freedom) {
  if (probability <= 0 || probability >= 1) return null;
  return this.inverseT(probability, degrees_freedom);
};

InstaCalc.prototype.fn.inverseT = function (p, df) {
  var x = this.incompleteBetaInverse(2 * Math.min(p, 1 - p), 0.5 * df, 0.5);
  x = Math.sqrt(df * (1 - x) / x);
  return (p > 0.5) ? x : -x;
};

InstaCalc.prototype.fn.tDist = function (x, degrees_freedom, cumulative) {
  if (cumulative) {
    return this.tCDF(x, degrees_freedom);
  }
  return this.tPDF(x, degrees_freedom);
};

// Helper functions for T.DIST
InstaCalc.prototype.fn.tPDF = function (x, df) {
  df = df > 1e100 ? 1e100 : df;  // Limit degrees of freedom
  const numerator = 1 / (Math.sqrt(df) * this.beta(0.5, df / 2));
  const denominator = Math.pow(1 + (x * x) / df, (df + 1) / 2);
  return numerator / denominator;
};

InstaCalc.prototype.fn.tCDF = function (x, df) {
  // Handle negative x using symmetry
  if (x < 0) {
    return 1 - this.tCDF(-x, df);
  }

  var df2 = df / 2;
  return this.incompleteBeta((x + Math.sqrt(x * x + df)) /
    (2 * Math.sqrt(x * x + df)), df2, df2);
};

InstaCalc.prototype.fn.chisqDist = function (x, degrees_freedom, cumulative) {
  if (cumulative) {
    return this.chisqCDF(x, degrees_freedom);
  }
  return this.chisqPDF(x, degrees_freedom);
};

InstaCalc.prototype.fn.chisqPDF = function (x, df) {
  if (x < 0 || df <= 0) {
    return 0;
  }
  const halfDf = df / 2;
  return Math.exp((halfDf - 1) * Math.log(x) - x / 2 - halfDf * Math.log(2) - this.logGamma(halfDf));
};

InstaCalc.prototype.fn.chisqCDF = function (x, df) {
  if (x <= 0) {
    return 0;
  }
  if (df <= 0) {
    throw new Error("Degrees of freedom must be positive");
  }
  return this.gammaLowerRegularized(df / 2, x / 2);
};

InstaCalc.prototype.fn.fDist = function (x, df1, df2, cumulative) {

  // Main F-distribution function
  if (x < 0 || df1 <= 0 || df2 <= 0) {
    return NaN;
  }

  if (cumulative) {
    return this.incompleteBeta((df1 * x) / (df1 * x + df2), df1 / 2, df2 / 2);
  } else {
    // Probability density function
    const num = Math.pow(df1 * x, df1) * Math.pow(df2, df2);
    const den = Math.pow(df1 * x + df2, df1 + df2);
    return Math.sqrt(num / den) / (x * this.beta(df1 / 2, df2 / 2));
  }
};

InstaCalc.prototype.fn.zTest = function (array, x, sigma) {
  const mean = this.arrayavg(array);
  const n = this.extractNumericValues(array).length;

  // Use provided sigma or calculate sample standard deviation
  const stdDev = sigma !== undefined ? sigma : this.arraystddev(array);
  const zValue = (mean - x) / (stdDev / Math.sqrt(n));

  // Calculate the p-value for the Z-test, Excel returns the upper tail probability
  const pValue = 1 - this.normSDist(zValue, true);
  return pValue;
};

InstaCalc.prototype.fn.tTest = function (array1, array2, tails, type) {
  const numericValues1 = this.extractNumericValues(array1);
  const numericValues2 = this.extractNumericValues(array2);
  const mean1 = this.arrayavg(numericValues1);
  const mean2 = this.arrayavg(numericValues2);
  const variance1 = this.arrayvariance(numericValues1);
  const variance2 = this.arrayvariance(numericValues2);
  const n1 = numericValues1.length;
  const n2 = numericValues2.length;

  let t, df;
  if (type === 1) {
    // Paired t-test
    const differences = numericValues1.map((val, i) => val[1] - numericValues2[i][1]);
    const meanDiff = this.arrayavg(differences);
    const varianceDiff = this.arrayvariance(differences);
    t = meanDiff / Math.sqrt(varianceDiff / differences.length);
    df = differences.length - 1;
  } else if (type === 2) {
    // Two-sample t-test assuming equal variances
    const pooledVar = ((n1 - 1) * variance1 + (n2 - 1) * variance2) / (n1 + n2 - 2);
    t = (mean1 - mean2) / Math.sqrt(pooledVar * (1 / n1 + 1 / n2));
    df = n1 + n2 - 2;
  } else if (type === 3) {
    // Two-sample t-test assuming unequal variances
    t = (mean1 - mean2) / Math.sqrt(variance1 / n1 + variance2 / n2);
    df = Math.pow(variance1 / n1 + variance2 / n2, 2) /
      (Math.pow(variance1 / n1, 2) / (n1 - 1) + Math.pow(variance2 / n2, 2) / (n2 - 1));
  } else {
    throw new Error("Invalid t-test type");
  }

  let pValue = this.tCDF(t, df);
  if (tails === 2) {
    pValue = 2 * (1 - pValue);
  } else if (tails !== 1) {
    throw new Error("Invalid number of tails");
  }

  return pValue;

};

InstaCalc.prototype.fn.tDist2T = function (x, df) {
  if (x < 0) {
    x = -x;
  }
  return 2 * (1 - this.tCDF(x, df));
};

InstaCalc.prototype.fn.tDistRT = function (x, df) {
  return 1 - this.tCDF(x, df);
};

InstaCalc.prototype.fn.tInv2T = function (x, df) {
  return this.tInv(1 - x / 2, df);
};

InstaCalc.prototype.fn.chisqDistRT = function (x, df) {
  return 1 - this.chisqCDF(x, df);
};

InstaCalc.prototype.fn.chisqInv = function (x, df) {
  return this.gammapInv(x, df / 2) * 2;
};

InstaCalc.prototype.fn.chisqInvRT = function (x, df) {
  return this.chisqInv(1 - x, df);
};

InstaCalc.prototype.fn.fDistRT = function (x, df1, df2) {
  return 1 - this.fDist(x, df1, df2, true);
};

InstaCalc.prototype.fn.fInv = function (x, df1, df2) {
  return df2 / (df1 * (1 / this.incompleteBetaInverse(x, df1 / 2, df2 / 2) - 1));
};

InstaCalc.prototype.fn.fInvRT = function (x, df1, df2) {
  return this.fInv(1 - x, df1, df2);
};

InstaCalc.prototype.fn.arraypercentrank = function (array, x, significance = 3) {
  // Sort the array in ascending order
  const values = this.extractNumericValuesAsFlatArray(array);
  const sortedArray = values.slice().sort((a, b) => a - b);
  const n = sortedArray.length;

  // If x is less than the smallest value, return 0
  if (x < sortedArray[0]) return 0;
  // If x is greater than the largest value, return 1
  if (x > sortedArray[n - 1]) return 1;

  // Find the position of x in the sorted array
  let position = 0;
  for (let i = 0; i < n; i++) {
    if (sortedArray[i] === x) {
      position = i;
      break;
    }
    if (sortedArray[i] < x && sortedArray[i + 1] > x) {
      // Interpolate between the two surrounding values
      const lower = sortedArray[i];
      const upper = sortedArray[i + 1];
      position = i + (x - lower) / (upper - lower);
      break;
    }
  }

  // Calculate the percentage rank
  const percentRank = position / (n - 1);

  // Round to the specified number of decimal places
  return Math.round(percentRank * Math.pow(10, significance)) / Math.pow(10, significance);
};

InstaCalc.prototype.fn.arrayforecast = function (x, known_ys, known_xs) {
  if (known_ys.length !== known_xs.length) {
    throw new Error("The number of y-values must match the number of x-values");
  }

  known_xs = this.extractNumericValuesAsFlatArray(known_xs);
  known_ys = this.extractNumericValuesAsFlatArray(known_ys);

  const n = known_ys.length;
  let sum_x = 0, sum_y = 0, sum_xy = 0, sum_xx = 0;

  for (let i = 0; i < n; i++) {
    sum_x += known_xs[i];
    sum_y += known_ys[i];
    sum_xy += known_xs[i] * known_ys[i];
    sum_xx += known_xs[i] * known_xs[i];
  }

  const slope = (n * sum_xy - sum_x * sum_y) / (n * sum_xx - sum_x * sum_x);
  const intercept = (sum_y - slope * sum_x) / n;

  return slope * x + intercept;
};

InstaCalc.prototype.fn.confidence = function (alpha, standard_dev, size) {
  // Calculate the critical value for the given confidence level
  const confidence_level = 1 - alpha;
  const critical_value = this.normSInv((1 + confidence_level) / 2);

  // Calculate the margin of error
  const margin_of_error = critical_value * (standard_dev / Math.sqrt(size));

  return margin_of_error;
};

InstaCalc.prototype.fn.arraycorrelation = function (array1, array2) {
  const numericValues1 = this.extractNumericValuesAsFlatArray(array1);
  const numericValues2 = this.extractNumericValuesAsFlatArray(array2);

  if (numericValues1.length !== numericValues2.length || numericValues1.length < 2) {
    return null;
  }

  const mean1 = this.arrayavg(numericValues1);
  const mean2 = this.arrayavg(numericValues2);
  const stdDev1 = Math.sqrt(this.arrayvariance(numericValues1));
  const stdDev2 = Math.sqrt(this.arrayvariance(numericValues2));

  if (stdDev1 === 0 || stdDev2 === 0) {
    return null;
  }

  let covariance = 0;
  for (let i = 0; i < numericValues1.length; i++) {
    covariance += (numericValues1[i] - mean1) * (numericValues2[i] - mean2);
  }

  covariance /= (numericValues1.length - 1);

  return covariance / (stdDev1 * stdDev2);
};

InstaCalc.prototype.fn.arrayslope = function (known_ys, known_xs) {
  const numericYs = this.extractNumericValuesAsFlatArray(known_ys);
  const numericXs = this.extractNumericValuesAsFlatArray(known_xs);

  if (numericYs.length !== numericXs.length || numericYs.length < 2) {
    return null;
  }

  // Calculate means
  const meanY = this.arrayavg(numericYs);
  const meanX = this.arrayavg(numericXs);

  // Calculate covariance
  let covariance = 0;
  for (let i = 0; i < numericYs.length; i++) {
    covariance += (numericXs[i] - meanX) * (numericYs[i] - meanY);
  }
  covariance /= (numericYs.length - 1);

  // Calculate variance of X
  const varianceX = this.arrayvariance(numericXs);

  // Avoid division by zero
  if (varianceX === 0) {
    return null;
  }

  // Calculate slope
  return covariance / varianceX;
};

InstaCalc.prototype.fn.arrayintercept = function (known_ys, known_xs) {
  const numericYs = this.extractNumericValuesAsFlatArray(known_ys);
  const numericXs = this.extractNumericValuesAsFlatArray(known_xs);

  if (numericYs.length !== numericXs.length || numericYs.length < 2) {
    return null;
  }

  // Calculate means
  const meanY = this.arrayavg(numericYs);
  const meanX = this.arrayavg(numericXs);

  // Calculate slope using the slope function
  const slopeValue = this.arrayslope(numericYs, numericXs);

  if (slopeValue === null) {
    return null;
  }

  // Calculate intercept
  return meanY - slopeValue * meanX;
};

InstaCalc.prototype.fn.arraylinest = function (known_ys, known_xs, constant = true, stats = false) {
  const numericYs = this.extractNumericValuesAsFlatArray(known_ys);
  const numericXs = this.extractNumericValuesAsFlatArray(known_xs);

  if (numericYs.length !== numericXs.length || numericYs.length < 2) {
    return null;
  }

  const slope = this.arrayslope(known_ys, known_xs);
  const intercept = this.arrayintercept(known_ys, known_xs);

  if (!constant) {
    // If constant is false, return only the slope
    return [slope];
  }

  if (stats) {
    // Calculate additional statistics
    const meanX = this.arrayavg(numericXs);
    const meanY = this.arrayavg(numericYs);
    const varianceX = this.arrayvariance(numericXs);
    const ssTotal = numericYs.reduce((sum, value) => sum + Math.pow(value - meanY, 2), 0);
    const ssResidual = numericYs.reduce((sum, value, index) => sum + Math.pow(value - (slope * numericXs[index] + intercept), 2), 0);
    const rSquared = 1 - (ssResidual / ssTotal);

    return [slope, intercept, rSquared, ssTotal, ssResidual];
  } else {
    // Return slope and intercept only
    return [slope, intercept];
  }
};

// external data lookups
InstaCalc.prototype.fn.stock = function (symbol) {
  // save stock to locals variables... (or a local cache...)
  if (typeof symbol !== "string") {
    return undefined;
  }
  symbol = symbol.toUpperCase();

  // it's an async function, so Action.recalculate? ...
  // Check both Action (main thread) and window.Action (worker)
  var ActionRef = typeof Action !== "undefined" ? Action :
    (typeof window !== "undefined" && window.Action ? window.Action : null);
  if (ActionRef && ActionRef.fn && ActionRef.fn.stock) {
    return ActionRef.fn.stock(symbol);
  } else {
    return undefined;
  }
};

InstaCalc.prototype.fn.import = function (url) {
  if (typeof url !== "string") {
    return undefined;
  }

  // Check both Action (main thread) and window.Action (worker)
  var ActionRef = typeof Action !== "undefined" ? Action :
    (typeof window !== "undefined" && window.Action ? window.Action : null);
  if (ActionRef && ActionRef.fn && ActionRef.fn.import) {
    return ActionRef.fn.import(url);
  } else {
    return undefined;
  }
};

InstaCalc.prototype.fn.calc = function (id) {
  console.log("calc", id);
  if (typeof id !== "string" && typeof id !== "number") {
    return undefined;
  }

  // Check both Action (main thread) and window.Action (worker)
  var ActionRef = typeof Action !== "undefined" ? Action :
    (typeof window !== "undefined" && window.Action ? window.Action : null);
  if (ActionRef && ActionRef.fn && ActionRef.fn.import_calc) {
    return ActionRef.fn.import_calc(id);
  } else {
    return undefined;
  }
};

// Excel functions
InstaCalc.prototype.fn.PMT = function (rate, nperiod, pv, fv = 0, type = 0) {
  if (rate === 0) return -(pv + fv) / nperiod;

  var pvif = Math.pow(1 + rate, nperiod);
  var pmt = (rate / (pvif - 1)) * -(pv * pvif + fv);

  if (type === 1) {
    pmt /= 1 + rate;
  }

  return pmt;
};

InstaCalc.prototype.fn.IPMT = function (rate, per, nper, pv, fv = 0, type = 0) {
  let pmt = this.PMT(rate, nper, pv, fv, type);
  let ipmt = 0;

  if (per === 1) {
    if (type === 1) {
      ipmt = 0;
    } else {
      ipmt = -pv;
    }
  } else {
    if (type === 1) {
      ipmt = this.FV(rate, per - 2, pmt, pv, type) - pmt;
    } else {
      ipmt = this.FV(rate, per - 1, pmt, pv, type);
    }
  }

  return ipmt * rate;
};

InstaCalc.prototype.fn.PPMT = function (rate, per, nper, pv, fv = 0, type = 0) {
  if (per < 1 || per >= nper + 1) return null;
  let pmt = this.PMT(rate, nper, pv, fv, type);
  let ipmt = this.IPMT(rate, per, nper, pv, fv, type);
  return pmt - ipmt;
};

InstaCalc.prototype.fn.ISPMT = function (rate, per, nper, pv) {
  if (per < 1 || per > nper) {
    return "Error: Period must be between 1 and total number of periods";
  }

  return pv * rate * (per / nper - 1);
};

// PV (Present Value) function
InstaCalc.prototype.fn.PV = function (rate, nper, pmt, fv = 0, type = 0) {
  return rate === 0
    ? -pmt * nper - fv
    : (((1 - Math.pow(1 + rate, nper)) / rate) * pmt * (1 + rate * type) - fv) / Math.pow(1 + rate, nper)
};


// FV (Future Value) function
InstaCalc.prototype.fn.FV = function (rate, nper, pmt, pv = 0, type = 0) {
  if (rate === 0) {
    return -1 * (pv + pmt * nper);
  }
  var pvif = Math.pow(1 + rate, nper);
  var fv = (pmt * (1 + rate * type) * (1 - pvif) / rate) - pv * pvif;
  return fv;
};

InstaCalc.prototype.fn.RRI = function (nper, pv, fv) {
  return Math.pow(fv / pv, 1 / nper) - 1;
};

InstaCalc.prototype.fn.RATE = function (nper, pmt, pv, fv = 0, type = 0, guess = 0.1) {
  let result = InstaCalc.LIB.RATE(nper, pmt, pv, fv, type, guess);
  return typeof result === "number" ? result : undefined;
};

InstaCalc.prototype.fn.NPER = function (rate, pmt, pv, fv = 0, type = 0) {
  if (rate === 0) {
    return - (pv + fv) / pmt;
  }

  let pvif = (pmt * (1 + rate * type) - fv * rate) / (pv * rate + pmt * (1 + rate * type));
  return Math.log(pvif) / Math.log(1 + rate);
};

InstaCalc.prototype.fn.CAGR = function (startingValue, endingValue, periods) {
  if (startingValue > 0 && endingValue > 0 && periods > 0) {
    let value = Math.pow(endingValue / startingValue, 1 / periods) - 1;
    return IC.makeICObject(value, "", "percent");
  }

  return null;
};

InstaCalc.prototype.fn.PDURATION = function (rate, pv, fv) {
  if (rate === 0) {
    return undefined;
  }

  return Math.log(fv / pv) / Math.log(1 + rate);
};

// IRR (Internal Rate of Return) function
InstaCalc.prototype.fn.arrayIRR = function (data, guess = 0.1) {
  let values = this.extractNumericValuesAsFlatArray(data);
  let result = InstaCalc.LIB.IRR(values, guess);
  return typeof result === "number" ? result : undefined;
};

// net present value
InstaCalc.prototype.fn.arrayNPV = function (rate, cashFlows) {
  if (typeof rate !== 'number' || cashFlows.length === 0) {
    throw new Error("Invalid input: Need a numeric rate and at least one cash flow value");
  }

  let values = this.extractNumericValuesAsFlatArray(cashFlows);

  let npv = values.reduce((npv, cf, index) => {
    return npv + cf / Math.pow(1 + rate, index + 1);
  }, 0);

  // round to 2 decimal places
  npv = Math.round(npv * 100) / 100;
  return npv;
};

InstaCalc.prototype.fn.arraylength = function (a) {
  let values = this.extractNumericValuesAsFlatArray(a);
  return values.length;
};

InstaCalc.prototype.fn.arrayfirst = function (a) {
  let values = this.extractNumericValuesAsFlatArray(a);
  return values.length > 0 ? values[0] : undefined;
};

InstaCalc.prototype.fn.arraylast = function (a) {
  let values = this.extractNumericValuesAsFlatArray(a);
  return values.length > 0 ? values[values.length - 1] : undefined;
};

// do unit conversion on the base number. allows for functions in case you are doing things like fahrenheit to celcius, etc.
InstaCalc.prototype.conversion = function (a, b) {
  if (typeof b == "number") {
    // no conversion
    return a * b;
  }

  if (typeof b == "function") {
    return b(a);
  }

  return a;
};

// finds whether string exists a type of unit
// InstaCalc.isUnit("meter") returns distance object
InstaCalc.prototype.isUnit = function (str, nocache = false) {
  this.unitMatchCache ||= {};

  let key = `isUnit:${str}:${InstaCalc.getUnitTimeStamp()}`;

  if (!nocache && this.unitMatchCache.hasOwnProperty(key)) {
    return this.unitMatchCache[key];
  }

  // hasOwnProperty, so 'length' doesn't return true
  let ret;

  // default properties in the Unit object
  if (str.match(/^(name|base|SI)$/i)) {
    ret = false;
  }
  else {
    // Returns the unit object (e.g., Unit.distance for "meter") or false
    // This is used by convertUnitScale to get the unit definition
    ret = Object.values(InstaCalc.Unit).find(unit => unit && typeof unit === 'object' && unit.hasOwnProperty(str)) || false;
  }

  if (!nocache) {
    this.unitMatchCache[key] = ret;
  }

  return ret;
};


// get conversion factor when translating between "from" and "to"
InstaCalc.prototype.convertUnitScale = function (from, to) {
  var objFrom = this.isUnit(from);
  var objTo = this.isUnit(to);

  if (!objFrom || !objTo) {
    return 1; // no conversion
  } else {
    return objFrom[fromRate] / objTo[toRate];
  }
};

// return power of unit
InstaCalc.prototype.getUnitPower = function (str) {
  var Unit = InstaCalc.Unit;
  var re;

  this.unitMatchCache ||= {};
  let key = str + "getUnitPower:" + InstaCalc.getUnitTimeStamp();
  if (this.unitMatchCache.hasOwnProperty(key)) {
    return this.unitMatchCache[key];
  }

  // area units - replace ^2 with "sq"
  str = str.replace("^2", " sq");
  str = str.replace("^3", " cu");
  str = str.replace("sq.", "sq");
  str = str.replace("cu.", "cu");

  for (var i = 0; i < InstaCalc.Unit.area.length; i++) {
    re = new RegExp("\\b" + Unit.area[i] + "\\b", "gi");
    if (str.match(re)) {
      str = InstaCalc.strtrim(str.replace(re, ""));
      return this.unitMatchCache[key] = [str, 2];
    }
  }

  // volume units
  for (var i = 0; i < Unit.volume.length; i++) {
    re = new RegExp("\\b" + Unit.volume[i] + "\\b", "gi");
    if (str.match(re)) {
      str = InstaCalc.strtrim(str.replace(re, ""));
      return this.unitMatchCache[key] = [str, 3];
    }
  }

  return this.unitMatchCache[key] = [str, 1]; // same string, default power
};

// check whether from^fpow == to^topow in correct units (returns true)
// for example, m^2 can be compared with acre, cm^3 with ml
// otherwise, if same pluralized unit "bubbles" and "bubbles", then return true
InstaCalc.prototype.unitMatch = function (from, frompow, to, topow) {
  var fromObj = InstaCalc.prototype.isUnit(from); // get unit types
  var toObj = InstaCalc.prototype.isUnit(to);

  if (!fromObj || !toObj) {
    if (frompow != topow) {
      return false; // different dimensions
    }

    // units don't exist in library
    // are these pluralized units the same?
    // cow == cow == cows
    var fromPlural = Inflector.pluralize(Inflector.pluralize(from)); // pluralize twice because mouse -> mice -> mices
    var toPlural = Inflector.pluralize(Inflector.pluralize(to)); // want to compare mouse and mice

    var fromSingular = Inflector.singularize(from);
    var toSingular = Inflector.singularize(to);

    return (fromPlural == toPlural || fromSingular == toSingular);
  }

  // ok these are official units. If they are the same type and power, we are done.
  // check whether fromObj and toObj are the same??? Easy check, right? For currency as well
  if (fromObj == toObj && frompow == topow) {
    return true;
  }

  // see if names and powers match directly
  /*
    if (fromObj['name'] == toObj['name'] && frompow == topow){
      return true;
    }
    */

  // final check -- see if they are both SI units that can be converted
  if (!fromObj.SI || !toObj.SI) {
    return false; // not SI units... cannot be compared
  }

  // see if units * power are equal for every item in fromObj -- for example, acre into cm^2
  for (var i in fromObj.SI) {
    // for each SI unit...
    if (fromObj.SI[i] * frompow != toObj.SI[i] * topow) {
      // make sure powers match: meters * 3 = L (which has meters = 3)
      return false;
    }
  }

  // and now the reverse...
  for (var i in toObj.SI) {
    // for each SI unit...
    if (fromObj.SI[i] * frompow != toObj.SI[i] * topow) {
      // make sure powers match
      return false;
    }
  }

  return true;
};

InstaCalc.prototype.unitMatch = (function () {
  const cache = new Map();

  const logic = function (from, frompow, to, topow) {
    // original function body
    var fromObj = InstaCalc.prototype.isUnit(from);
    var toObj = InstaCalc.prototype.isUnit(to);
    if (!fromObj || !toObj) {
      if (frompow != topow) {
        return false;
      }

      var fromPlural = Inflector.pluralize(Inflector.pluralize(from));
      var toPlural = Inflector.pluralize(Inflector.pluralize(to));

      var fromSingular = Inflector.singularize(from);
      var toSingular = Inflector.singularize(to);

      return (fromPlural == toPlural || fromSingular == toSingular);
    }

    if (fromObj === toObj && frompow === topow) return true;
    if (!fromObj.SI || !toObj.SI) return false;

    const allKeys = [...new Set([...Object.keys(fromObj.SI), ...Object.keys(toObj.SI)])];
    return allKeys.every(i => fromObj.SI[i] * frompow === toObj.SI[i] * topow);
  };

  return function (from, frompow, to, topow) {
    const key = `${from},${frompow},${to},${topow}`;
    if (cache.has(key)) {
      return cache.get(key);
    }

    const result = logic(from, frompow, to, topow);
    cache.set(key, result);
    return result;
  };
})();

// parse string and populate unit sections
// "3 miles/2 gallons in 3 km/liter"
// return object with from, fromRate, to, toRate, label properties
// later these items become actual objects... but right now, from, etc. are text
InstaCalc.prototype.parseUnitObject = function (str) {
  var a = this.splitUnits(str);

  return {
    from: a[0],
    fromRate: a[1],
    to: a[2],
    toRate: a[3],
    label: a[4],
  };
};

// split string: from/fromRate into to/toRate
// fromRate and toRate are optional
// returns array: [from, fromRate, to, toRate]
InstaCalc.prototype._splitUnits = function (str) {
  var Unit = InstaCalc.Unit;

  //str = str.toLowerCase(); // make string lowercase; -- why?
  // remove safe prepend
  var re = new RegExp(InstaCalc.safeprepend, "g");
  str = str.replace(re, "");
  var tmp;

  // InstaCalc.unitlabel = "";        // reset unit label
  InstaCalc.unitlabel = ""; // reset the label for this item
  re = new RegExp(/\bper\b/gi); // replace per with slash
  str = str.replace(re, "/");

  str = str.replace(/\bfor\b/gi, "/"); // "every", "until", "for" are all units of rate
  str = str.replace(/\bevery\b/gi, "/");
  str = str.replace(/\beach\b/gi, "/");
  str = str.replace(/\buntil\b/gi, "/");

  // replace spelled out numbers
  str = str.replace(/\bthousand\b/, "1000");
  str = str.replace(/\bmillion\b/, "1000000");
  str = str.replace(/\bbillion\b/, "1000000000");
  str = str.replace(/\btrillion\b/, "1000000000000");

  // allow [in, into, as, to] for the converion verb
  if (str.match(/\s+in\s+/i)) {
    tmp = str.split(/\s+in\s+/i); // 300 x IN y
  } else if (str.match(/\s+into\s+/i)) {
    tmp = str.split(/\s+into\s+/i); // 300 x IN y
  } else if (str.match(/\s+as\s+/i)) {
    tmp = str.split(/\s+as\s+/i); // 300 x IN y
  } else if (str.match(/\s+to\s+/i)) {
    tmp = str.split(/\s+to\s+/i); // 300 x IN y
  } else if (str.match(/\s+en\s+/i)) {
    tmp = str.split(/\s+en\s+/i); // 300 x IN y
  } else {
    return null; // no conversion to do
  }

  if (tmp.length < 2) {
    return null; // no conversion
  }

  // BUGFIX: need to trim string, otherwise extra spaces causes errors & the lookup doesn't work
  var from = InstaCalc.strtrim(tmp[0].toLowerCase()); // from X
  var fromScale = 1; // miles/hour (hour) -- assume default of 1
  var to = InstaCalc.strtrim(tmp[1].toLowerCase()); // into Y
  var toScale = 1; // feet/sec (sec) -- assume default of 1
  // replace the aliases
  if (Unit.alias[from]) {
    from = Unit.alias[from];
  }
  if (Unit.alias[to]) {
    to = Unit.alias[to];
  }

  // now, from could be m/s (meters per sec) or mph
  var matchesFrom;
  var matchesTo;

  var fromRate = "unity";
  var toRate = "unity";

  if ((matchesFrom = from.match(/(.+)\/(.+)/)) && (matchesTo = to.match(/(.+)\/(.+)/))) {
    from = InstaCalc.strtrim(matchesFrom[1]);
    to = InstaCalc.strtrim(matchesTo[1]);

    fromRate = InstaCalc.strtrim(matchesFrom[2]); // set new units for fromRate, to Rate
    toRate = InstaCalc.strtrim(matchesTo[2]);
  }

  var label = tmp[1]; // before the upper/lower case
  return [from, fromRate, to, toRate, label];
};

InstaCalc.prototype.splitUnits = (function () {
  const cache = new Map();
  return function (str) {
    return cache.get(str) || cache.set(str, InstaCalc.prototype._splitUnits(str)).get(str);
  };
})();

// return true if from and to units are actually conversion functions
InstaCalc.prototype.isConversionFunction = function (from, to) {
  var fromObj = this.isUnit(from);
  var toObj = this.isUnit(to);

  // they are the same conversion object, and it is a conversion function
  if (fromObj == toObj && fromObj.conversion == "function") {
    return fromObj;
  }

  return false;
};

// Parses object and determines powers of units
// Input: object o with these properties: o.from, o.fromRate,  o.to, o.toRate
// And returns object with these properties: o.from.unit, o.from.power... (for each of the above)
InstaCalc.prototype.getUnitPowers = function (obj) {
  var conversion = {};
  conversion.from = {};
  conversion.to = {};
  conversion.fromRate = {};
  conversion.toRate = {};

  // get unit powers
  var a = this.getUnitPower(obj.from);
  conversion.from.unit = a[0];
  conversion.from.power = a[1];

  var b = this.getUnitPower(obj.to);
  conversion.to.unit = b[0];
  conversion.to.power = b[1];

  // get rate powers
  a = this.getUnitPower(obj.fromRate);
  conversion.fromRate.unit = a[0];
  conversion.fromRate.power = a[1];

  b = this.getUnitPower(obj.toRate);
  conversion.toRate.unit = b[0];
  conversion.toRate.power = b[1];

  return conversion;
};

// Takes conversion unit (from above) and returns an object with two scaling factors:
// obj.scale.from and obj.scale.to
// For example, 3 miles/2 hours into 3 miles / 8 hours will get a obj.scale.from (= 3 /2) and obj.scale.to (=3/8)
InstaCalc.prototype.getScalingFactors = function (inputObj) {
  var scale = {};
  scale.from = 1;
  scale.to = 1;

  var obj = {};

  obj = inputObj; // make a copy
  // from.unit == "3 cows". Parse out the 3 and scale appropriately
  obj.from.scale = 1;
  var num = obj.from.unit.match(/[.,\d]+/);
  if (num) {
    var tmp = this.parseNumber(num[0]); // need to scale entire result by this.
    obj.from.unit = obj.from.unit.replace(/[.,\d]+\s*/, "");

    obj.from.scale = tmp;
    scale.from *= tmp; // this increases scale
  }

  // being messed up by parseNumber currently...
  obj.fromRate.scale = 1;
  var num = obj.fromRate.unit.match(/[.,\d]+/);
  if (num) {
    var tmp = this.parseNumber(num[0]); // need to scale entire result by this.
    obj.fromRate.unit = obj.fromRate.unit.replace(/[.,\d]+\s*/, "");

    obj.fromRate.scale = tmp;
    scale.from *= 1 / tmp; // this diminishes
  }

  obj.to.scale = 1;
  num = obj.to.unit.match(/[.,\d]+/);
  if (num) {
    var tmp = this.parseNumber(num[0]); // need to scale entire result by this.
    obj.to.unit = obj.to.unit.replace(/[.,\d]+\s*/, "");

    obj.to.scale = tmp;
    scale.to *= tmp; // this increases scale?
  }

  obj.toRate.scale = 1;
  num = obj.toRate.unit.match(/[.,\d]+/);
  if (num) {
    var tmp = this.parseNumber(num[0]); // need to scale entire result by this.
    obj.toRate.unit = obj.toRate.unit.replace(/[.,\d]+\s*/, "");

    obj.toRate.scale = tmp;
    scale.to *= 1 / tmp; // this decreases scale the scale
  }

  obj.scale = scale;
  return obj;
};

// Takes object above and returns result - whether or not the units matched
//  result.match == (true or false)
//  result.invert == (true or false) // whether we had to invert the results in order to match
//     therefore, the scale needs to be inverted as well
InstaCalc.prototype.getUnitMatches = function (inputObj) {
  var matchFailed = false;
  var ret = {
    match: false,
    invert: false,
  };

  var obj = {};

  obj = inputObj;
  obj.inverted = false;

  if (!this.unitMatch(obj.from.unit, obj.from.power, obj.to.unit, obj.to.power)) {
    matchFailed = true;
  }

  if (!this.unitMatch(obj.fromRate.unit, obj.fromRate.power, obj.toRate.unit, obj.toRate.power)) {
    matchFailed = true;
  }

  // Ok, the match failed, but try to invert them to see if it works
  if (matchFailed) {
    var fromInverted = this.unitMatch(obj.from.unit, obj.from.power, obj.toRate.unit, obj.toRate.power);
    var toInverted = this.unitMatch(obj.to.unit, obj.to.power, obj.fromRate.unit, obj.fromRate.power);

    if (fromInverted && toInverted) {
      // they are both inverted... so invert units to make it normal again
      obj.inverted = true;
    } else {
      // nope, didn't work. error out
      return null;
    }
  }

  return obj;
};

// return unit conversion if found, 1 otherwise
InstaCalc.prototype.getUnitConversion = function (unitstr) {
  const obj = this.isUnit(unitstr) || this.isUnit(unitstr.toLowerCase());
  return obj ? (obj[unitstr] || obj[unitstr.toLowerCase()]) : 1;
};

// take object above, with all scaling information, and return a conversion unit
//  this conversion can be a function or simply a value to scale by
InstaCalc.prototype.getConversionFactor = function (inputObj) {
  var obj = inputObj;
  var n; // number we are to scale
  var unit;

  /***********************************************
      Scale * From Top                     Scale *  To Top
      ----------------------   into     ----------------------
       Scale * From Bottom                Scale * To Bottom
       ************************************************/
  n = this.getUnitConversion(obj.from.unit);
  var ft = Math.pow(n, obj.from.power); // from top
  n = this.getUnitConversion(obj.fromRate.unit);
  var fb = Math.pow(n, obj.fromRate.power); // from bottom
  n = this.getUnitConversion(obj.to.unit);
  var tt = Math.pow(n, obj.to.power); // to top
  n = this.getUnitConversion(obj.toRate.unit);
  var tb = Math.pow(n, obj.toRate.power); // to top
  var fromScale = (obj.from.scale * ft) / (obj.fromRate.scale * fb);
  var toScale = (obj.to.scale * tt) / (obj.toRate.scale * tb);
  var regularScale = fromScale / toScale; // how many to's fit in a from?
  var invertedScale;

  if (obj.inverted) {
    fromScale = fromScale; // figure out what it would be on a regular scale, and flip that
    toScale = (obj.toRate.scale * tb) / (obj.to.scale * tt); // flip toRate and to, since we are inverted
    invertedScale = fromScale / toScale; // this is the factor we'd use if a regular scale... but still need to do 1/x
  }

  // other scaling factors come into play differently
  if (obj.inverted) {
    return function (x) {
      return 1 / (x * invertedScale);
    }; // return function to muliply by x and invert
  } else {
    return regularScale; // return static conversion factor. can also return function, but not needed.
  }
};

// return a number returning how to scale these units
InstaCalc.prototype._getUnitScale = function (from, fromRate, to, toRate) {
  var obj = {
    from: from,
    fromRate: fromRate,
    to: to,
    toRate: toRate,
  };

  obj = this.getUnitPowers(obj);
  if (!obj) {
    return null; // check value in case of an error
  }

  obj = this.getScalingFactors(obj);
  if (!obj) {
    return null;
  }

  obj = this.getUnitMatches(obj);
  if (!obj) {
    return null; // no matches found
  }

  // return composite function if it's a non-linear conversion
  var fnObj;
  if ((fnObj = this.isConversionFunction(from, to))) {
    var fromFn = fnObj[from][0];
    var toFn = fnObj[to][1];
    var composite = function (x) {
      return toFn(fromFn(x));
    };
    return composite;
  }

  return this.getConversionFactor(obj);
};

InstaCalc.prototype.getUnitScale = (function () {
  const cache = new Map();
  return function (from, fromRate, to, toRate) {
    // Include unit timestamp in cache key so cache invalidates when currencies/units change
    const timestamp = InstaCalc.getUnitTimeStamp();
    const key = `${from}-${fromRate}-${to}-${toRate}-${timestamp}`;
    return cache.get(key) || cache.set(key, InstaCalc.prototype._getUnitScale(from, fromRate, to, toRate)).get(key);
  };
})();

// convert units. Example: 300 x in y
// 300 feet in meters
InstaCalc.conversionCache = {};

InstaCalc.prototype.convertUnit = function (str) {
  // Include unit timestamp in cache key so cache invalidates when currencies/units change
  var cacheKey = str + ':' + InstaCalc.getUnitTimeStamp();
  var cached = InstaCalc.conversionCache[cacheKey];
  if (cached) {
    InstaCalc.unitlabel = cached.unitlabel;
    return cached.conversion;
  }

  var ret = this.splitUnits(str);

  if (ret == null) {
    InstaCalc.unitlabel = "";
    return 1; // no conversion
  }

  var from = ret[0];
  var fromRate = ret[1];
  var to = ret[2];
  var toRate = ret[3];
  var label = ret[4];

  //setStatus("from:" + from + " fromRate:"+ fromRate + " to:" + to + " toRate:" + toRate);
  var conversion = this.getUnitScale(from, fromRate, to, toRate);

  if (conversion == null || typeof conversion == "undefined") {
    InstaCalc.unitlabel = "";
    return 1;
  } else {
    InstaCalc.unitlabel = label;
    InstaCalc.conversionCache[cacheKey] = {
      conversion: conversion,
      unitlabel: label,
    }; // save to cache
    return conversion;
  }
};

/*********************
  Allowed Functions / Keywords - items before parens. f(x,y)
  **********************/

InstaCalc.allowedfn = {};
InstaCalc.locals = {};
InstaCalc.MAGIC_STRING = "__qweasdzxc";

InstaCalc.safeprepend = "InstaCalc.locals." + InstaCalc.MAGIC_STRING; // make it unique; will be removed in strings
InstaCalc.stringprepend = "InstaCalc__String" + InstaCalc.MAGIC_STRING; // cannot have periods
InstaCalc.resetLocals = function () {
  InstaCalc.locals = {};

  // feature: have constants be variables predefined in the environment. Lets e and E work
  InstaCalc.locals[InstaCalc.MAGIC_STRING + "pi"] = Math.PI;
  InstaCalc.locals[InstaCalc.MAGIC_STRING + "e"] = Math.E;
  InstaCalc.locals[InstaCalc.MAGIC_STRING + "phi"] = InstaCalc.prototype.cn.PHI;
};

//closure with nice js variable scoping
(function () {
  var a = InstaCalc.allowedfn; // for efficiency in object lookup & smaller code size
  a.IF = "if";
  a.ELSE_IF = "else if ";

  a.ABS = "this.fn().abs";
  a.CEIL = "Math.ceil";

  a.EXP = "this.fn().exp";
  a.FLOOR = "Math.floor";
  a.LN = a.LOGARITHM = "this.fn().log";
  a.LOG = "this.fn().log10";
  a.LOG10 = "this.fn().log10";
  a.LOG2 = "this.fn().log2";
  a.LOG1P = "Math.log1p";
  a.MAX = "Math.max";
  a.MIN = "Math.min";
  a.POWER = a.POW = "Math.pow"; // disabled because we already have A**B

  // std trig
  a.SIN = a.SINE = "this.fn().sin";
  a.COS = a.COSINE = "this.fn().cos";
  a.TAN = a.TANGENT = "this.fn().tan";
  a.SEC = a.SECANT = "this.fn().sec";
  a.CSC = a.COSECANT = "this.fn().csc";
  a.COT = a.COTANGENT = "this.fn().cot";

  // inverse trig
  a.ASIN = a.ARCSINE = "this.fn().asin";
  a.ACOS = a.ARCCOSINE = "this.fn().acos";
  a.ATAN = a.ARCTANGENT = "this.fn().atan";
  a.ATAN2 = "this.fn().atan2";
  a.ASEC = a.ARCSECANT = "this.fn().asec";
  a.ACSC = a.ARCCOSECANT = "this.fn().acsc";
  a.ACOT = a.ARCCOTANGENT = "this.fn().acot";

  a.COSH = "this.fn().cosh";
  a.SINH = "this.fn().sinh";
  a.TANH = "this.fn().tanh";
  a.COTH = "this.fn().coth";
  a.SECH = "this.fn().sech";
  a.CSCH = "this.fn().csch";

  a.ASINH = "this.fn().asinh";
  a.ACOSH = "this.fn().acosh";
  a.ATANH = "this.fn().atanh";
  a.ASECH = "this.fn().asech";
  a.ACSCH = "this.fn().acsch";
  a.ACOTH = "this.fn().acoth";

  a.SUBTRACT = "this.fn().subtract";
  a.DIVIDE = a.QUOTIENT = "this.fn().divide";
  a.MODULO = a.MODULUS = "this.fn().mod";

  a.GCD = "this.fn().gcd";
  a.LCM = "this.fn().lcm";

  a.CONVERT = "this.convertUnit";

  a.ROOT = a.SQRT = "this.fn().sqrt";
  a.ROUND = "this.fn().round";
  a.ROUNDDOWN = "Math.floor";
  a.ROUNDUP = "Math.ceil";
  a.CBRT = a.CUBEROOT = "this.fn().cbrt";

  a.INVERSE = "this.fn().inverse";
  a.INVERT = "this.fn().inverse";

  a.SQUARE = "this.fn().square";
  a.CUBE = "this.fn().cube";
  a.CUBIC = "this.fn().cube";

  a.TRUNC = a.INT = a.INTEGER = "Math.trunc";
  a.HYPOT = "Math.hypot";

  // Custom functions
  a.CHARCODE = true;
  a.FROMCHARCODE = true;
  a.FIB = "this.fn().fibonacci";
  a.FIBONACCI = "this.fn().fibonacci";
  a.FAC = "this.fn().factorial";
  a.FACTORIAL = "this.fn().factorial";
  a.GAMMA = "this.fn().gamma";
  a.NCHOOSEK = true;

  a.PRINT = "this.fn().print";

  a.TIME = true;

  // base conversion
  a.BIN = "this.fn().bin";
  a.OCT = "this.fn().oct";
  a.HEX = "this.fn().hex";
  a.DEC = "this.fn().dec";

  a.FROMBIN = "this.fn().frombin";
  a.FROMOCT = "this.fn().fromoct";

  // other math functoins
  a.RANDOM = a.RAND = "this.fn().random";

  // combination & permutation
  a.C = a.COMBINATION = a.COMBIN = "this.fn().combination";
  a.P = a.PERMUTATION = a.PERMUT = "this.fn().permutation";

  // degrees and radians
  a.DEG = true;
  a.RAD = true;

  // number parsing & cleanup
  a.PARSENUMBER = "this.parseNumber";
  a.GETROWVALUES = "this.getRowValues";
  a.GETROWVALUE = "this.getRowValue";

  // array functions
  a.MAX = "this.fn().arraymax";
  a.MIN = "this.fn().arraymin";
  a.SUM = a.ADD = a.TOTAL = a.SUBTOTAL = "this.fn().arraysum";

  a.LENGTH = "this.fn().arraylength";
  a.FIRST = "this.fn().arrayfirst";
  a.LAST = "this.fn().arraylast";

  a.PRODUCT = "this.fn().arrayproduct";
  a.MULTIPLY = a.PRODUCT;
  a.SUB = "this.fn().arraydifference"; // subtract
  a.AVERAGE = "this.fn().arrayavg";
  a.AVG = "this.fn().arrayavg";
  a.MEAN = "this.fn().arrayavg";
  a.MEDIAN = "this.fn().arraymedian";
  a.MODE = "this.fn().arraymode";
  a.GEOMEAN = "this.fn().arraygeomean";

  a.VAR = a.VARIANCE = "this.fn().arrayvariance";
  a["VAR.S"] = "this.fn().arrayvariance";
  a["VAR.P"] = "this.fn().arrayvariancePopulation";

  a.COVARIANCE = "this.fn().arraycovariance";
  a.COVAR = "this.fn().arraycovariance";
  a["COVAR.S"] = "this.fn().arraycovariance";
  a["COVAR.P"] = "this.fn().arraycovariancePopulation";
  a["COVARIANCE.S"] = "this.fn().arraycovariance";
  a["COVARIANCE.P"] = "this.fn().arraycovariancePopulation";

  a.STDDEV = "this.fn().arraystddev";
  a.STDEV = "this.fn().arraystddev";

  a["STDEV.S"] = "this.fn().arraystddev";
  a["STDEV.P"] = "this.fn().arraystddevPopulation";

  a["STDDEV.S"] = "this.fn().arraystddev";
  a["STDDEV.P"] = "this.fn().arraystddevPopulation";

  a.SKEWNESS = "this.fn().arrayskewness";
  a.SKEW = "this.fn().arrayskewness";
  a.KURTOSIS = "this.fn().arraykurtosis";
  a.KURT = "this.fn().arraykurtosis";

  a.CORREL = "this.fn().arraycorrelation";
  a.CORRELATION = "this.fn().arraycorrelation";
  a.SLOPE = "this.fn().arrayslope";
  a.INTERCEPT = "this.fn().arrayintercept";
  a.LINEST = "this.fn().arraylinest";

  a.RANGE = "this.fn().arrayrange";

  a.COUNT = "this.fn().arraycount";
  a.SORT = "this.fn().arraysort";
  a.REVERSE = "this.fn().arrayreverse";
  a.PERCENTILE = "this.fn().arraypercentile";
  a.MAP = "this.fn().arraymap";
  a.FILTER = "this.fn().arrayfilter";
  a.REDUCE = "this.fn().arrayreduce";
  a.FIND = "this.fn().arrayfind";

  a.PERCENTRANK = "this.fn().arraypercentrank";
  a.FORECAST = "this.fn().arrayforecast";
  a.CONFIDENCE = "this.fn().confidence";

  a.SEQUENCE = "this.fn().sequence";
  a.SEQ = "this.fn().sequence";

  a.FUNCTION = "function";
  a.TIME = "this.fn().time";

  a.CONVERSION = "this.conversion";
  a.STOCK = "this.fn().stock";
  a.IMPORT = "this.fn().import";
  a.CALC = "this.fn().calc";

  a.PMT = "this.fn().PMT";
  a.IPMT = "this.fn().IPMT";
  a.PPMT = "this.fn().PPMT";
  a.ISPMT = "this.fn().ISPMT";
  a.IRR = "this.fn().arrayIRR";
  a.PV = "this.fn().PV";
  a.FV = "this.fn().FV";
  a.RRI = "this.fn().RRI";
  a.RATE = "this.fn().RATE";
  a.NPER = "this.fn().NPER";
  a.CAGR = "this.fn().CAGR";
  a.PDURATION = "this.fn().PDURATION";
  a.NPV = "this.fn().arrayNPV";


  a.SIGN = "this.fn().sign";
  a.CLAMP = "this.fn().clamp";

  a.ERF = "this.fn().erf";
  a.ERFINVERSE = "this.fn().erfInverse";

  a["NORM.S.INV"] = "this.fn().normSInv";
  a["NORMSINV"] = "this.fn().normSInv";
  a["NORM.INV"] = "this.fn().normInv";
  a["NORMINV"] = "this.fn().normInv";
  a["NORM.DIST"] = "this.fn().normDist";
  a["NORMDIST"] = "this.fn().normDist";
  a["NORM.S.DIST"] = "this.fn().normSDist";
  a["NORMSDIST"] = "this.fn().normSDist";
  a["T.INV"] = "this.fn().tInv";
  a["TINV"] = "this.fn().tInv";
  a["T.DIST"] = "this.fn().tDist";
  a["TDIST"] = "this.fn().tDist";
  a["CHISQ.DIST"] = "this.fn().chisqDist";
  a["CHISQDIST"] = "this.fn().chisqDist";
  a["F.DIST"] = "this.fn().fDist";
  a["FDIST"] = "this.fn().fDist";
  a["Z.TEST"] = "this.fn().zTest";
  a["ZTEST"] = "this.fn().zTest";
  a["T.TEST"] = "this.fn().tTest";
  a["TTEST"] = "this.fn().tTest";

  a["T.DIST.2T"] = "this.fn().tDist2T";
  a["TDIST2T"] = "this.fn().tDist2T";
  a["T.DIST.RT"] = "this.fn().tDistRT";
  a["TDISTRT"] = "this.fn().tDistRT";
  a["T.INV.2T"] = "this.fn().tInv2T";
  a["TINVT2T"] = "this.fn().tInv2T";
  a["CHISQ.DIST.RT"] = "this.fn().chisqDistRT";
  a["CHISQDISTRT"] = "this.fn().chisqDistRT";
  a["CHISQ.INV"] = "this.fn().chisqInv";
  a["CHISQINV"] = "this.fn().chisqInv";
  a["CHISQ.INV.RT"] = "this.fn().chisqInvRT";
  a["CHISQINVRT"] = "this.fn().chisqInvRT";
  a["F.DIST.RT"] = "this.fn().fDistRT";
  a["FDISTRT"] = "this.fn().fDistRT";
  a["F.INV"] = "this.fn().fInv";
  a["FINV"] = "this.fn().fInv";
  a["F.INV.RT"] = "this.fn().fInvRT";
  a["FINVRT"] = "this.fn().fInvRT";

  a["BINOM.DIST"] = "this.fn().binomDist";
  a["BINOMDIST"] = "this.fn().binomDist";
  a["POISSON.DIST"] = "this.fn().poissonDist";
  a["POISSONDIST"] = "this.fn().poissonDist";
  a["GAMMA.DIST"] = "this.fn().gammaDist";
  a["GAMMADIST"] = "this.fn().gammaDist";
  a["BETA.DIST"] = "this.fn().betaDist";
  a["BETADIST"] = "this.fn().betaDist";
  a["HYPGEOM.DIST"] = "this.fn().hypgeomDist";
  a["HYPGEOMDIST"] = "this.fn().hypgeomDist";
  a["LOGNORM.DIST"] = "this.fn().lognormDist";
  a["LOGNORMDIST"] = "this.fn().lognormDist";
  a["LOGNORM.INV"] = "this.fn().lognormInv";
  a["LOGNORMINV"] = "this.fn().lognormInv";
  a["NEGBINOM.DIST"] = "this.fn().negbinomDist";
  a["NEGBINOMDIST"] = "this.fn().negbinomDist";
  a["WEIBULL.DIST"] = "this.fn().weibullDist";
  a["WEIBULLDIST"] = "this.fn().weibullDist";

  /*
  a.AREA = "this.fn().area";
  a.VOLUME = "this.fn().volume";
  a.SURFACEAREA = "this.fn().surfacearea";
  a.PERIMETER = "this.fn().perimeter";
  */

  /* Allowed Constants */
  InstaCalc.allowedcn = {};

  var c = InstaCalc.allowedcn;
  c.RANDOM = "Math.random()"; // not really a constant, but replaced by a function without parameters

  // have it be case INSENSITIVE... make all words upper-case. eliminates bad keywords as well.
  /* keywords -- replaced wherever they appear */
  InstaCalc.keywords = {};
  var k = InstaCalc.keywords;

  k.IF = "if";
  k.ELSE = "else";
  k.ELSE_IF = "else if"; // because two keywords can be merged with underscore "else if" => else_if
  //k.FUNCTION = "function";
  //k.RETURN = "return"; // return is useful as a keyword
  k.TRUE = "true";
  k.FALSE = "false";

  // valid regular expressions and how to replace them; in this order
  InstaCalc.expressions = [];

  // string replacements
  var re = new RegExp("\\b" + InstaCalc.stringprepend + "(\\d+)\\b", "g");

  InstaCalc.expressions.push([re, InstaCalc.stringprepend + "$1"]); // string replacement

  InstaCalc.expressions.push([/\band\b/gi, " & "]);
  InstaCalc.expressions.push([/\bor\b/gi, " | "]);
  InstaCalc.expressions.push([/\bnot\b/gi, " ~ "]);

  InstaCalc.expressions.push([/\bxor\b/gi, " @@ "]); // avoid conflicting with ^ for power; replace it as a postfilter; can't use @XOR@ as it could get replaced again; can't use @1@ as it will get replaced...
  InstaCalc.expressions.push([/\bmod\b/gi, " @@@ "]); // avoid conflict with %, replace in post filter

  // The very earliest processing, before even the power/exponent parsing ^
  InstaCalc.firstfilter = [];
  var f = InstaCalc.firstfilter;

  // separate digits from unit labels 15km to miles => "15 km to miles" and similar
  f.push([/\b([1-9]|[1-9.][0-9,.]+)([a-z][a-z]+)\b/gi, "$1 $2"]);

  // filter out equality expressions
  f.push([/^(.*)=(.*[)]|.*\d)\s+(([a-zA-Z].*\s+(in|as|into|to|en)\s+.*))$/gi, '$1 = CONVERSION($2, CONVERT("$3") )']); // do not allow parens in description of units, otherwise gets confused with parseNumber
  // get regular text: 3 feet in cm
  f.push([/^\s*(-?[\d()\w.,]+)\s+(([a-zA-Z].*\s+(in|as|into|to|en)\s+.*))$/gi, 'CONVERSION($1, CONVERT("$2") )']); // do not allow parens in description of units, otherwise gets confused with parseNumber
  // get parens: (3 + 4) feet in cm
  f.push([/^\s*[(](.+)[)]\s+(([a-zA-Z].*\s+(in|as|into|to|en)\s+.*))$/gi, 'CONVERSION($1, CONVERT("$2"))']);

  // Stock syntax - convert to STOCK_TICKER units (enables conversions like "1M USD in STOCK_AAPL")
  // STOCK(AAPL) -> STOCK_AAPL, STOCK(BRK.A) -> STOCK_BRK_A
  f.push([/\bSTOCK\(([a-zA-Z]+(?:\.[A-Z]{1,2})?)\)/gi, (match, symbol) => 'STOCK_' + symbol.toUpperCase().replace(/\./g, '_')]);
  f.push([/\bSTOCK:([a-zA-Z]+(?:\.[A-Z]{1,2})?)/gi, (match, symbol) => 'STOCK_' + symbol.toUpperCase().replace(/\./g, '_')]);
  // NOTE: $TICKER syntax is handled in parser CONFIG.PREPROCESSING with variable/function/constant checks

  // arabic numerals treated as "constants"
  // 0-9 are unicode charcodes 1632-1641 (see http://www.jdstiles.com/java/cct.html)
  // 0x0660 - 0x0669
  f.push([/\u0660/gi, "0"]);
  f.push([/\u0661/gi, "1"]);
  f.push([/\u0662/gi, "2"]);
  f.push([/\u0663/gi, "3"]);
  f.push([/\u0664/gi, "4"]);
  f.push([/\u0665/gi, "5"]);
  f.push([/\u0666/gi, "6"]);
  f.push([/\u0667/gi, "7"]);
  f.push([/\u0668/gi, "8"]);
  f.push([/\u0669/gi, "9"]);

  // allow for sin(3 * pi rad) to do the right thing
  f.push([/pi rad/gi, "180"]);

  f.push([/!=/gi, " @@@@ "]); // avoid conflict with factorial: 3 != 2, replace in post

  // general clean up string before processing
  InstaCalc.prefilter = [];
  var p = InstaCalc.prefilter;

  // fix Excel-style assignment (=a * b)
  p.push([/^\s*=\s*/, ""]); // remove  leading equals sign (= 3 + 4)
  p.push([/\s*=\s*$/, ""]); // remove trailing equals sign (3 + 4 =)

  // add or subtract percent
  p.push([/[+]\s*([0-9.,]+)%/g, " * (1 + .01 * $1)"]);
  p.push([/-\s*([0-9.,]+)%/g, " * (1 - .01 * $1)"]);

  p.push([/%\s+of\s+/gi, " * .01 * "]); // 5% of 200
  p.push([/\s*([0-9.,]+)%/g, "($1 * .01)"]); // 3 / 7% ... the % should bind to the 7, not be (3/7)%
  p.push([/%/g, " * .01"]); // some other type of percent
  p.push([/[|]([^|]+)[|]/g, "ABS($1)"]); // absolute value

  // Kilobytes, Megabytes, Gigabytes, Terabytes, etc. Allow any number
  p.push([/\b((\d|[,.])+)\s*KB\b/gi, "($1 * 1024)"]); // KB
  p.push([/\b((\d|[,.])+)\s*MB\b/gi, "($1 * 1024 * 1024)"]); // MB
  p.push([/\b((\d|[,.])+)\s*GB\b/gi, "($1 * 1024 * 1024 * 1024)"]); // GB
  p.push([/\b((\d|[,.])+)\s*TB\b/gi, "($1 * 1024 * 1024 * 1024 * 1024)"]); // TB
  p.push([/\b((\d|[,.])+)\s*PB\b/gi, "($1 * 1024 * 1024 * 1024 * 1024 * 1024)"]); // Petabyte
  p.push([/\b((\d|[,.])+)\s*KBPS\b/gi, "($1 * 1024 / 8)"]); // bandwidth - kilobits per second
  p.push([/\b((\d|[,.])+)\s*MBPS\b/gi, "($1 * 1024 * 1024 / 8)"]); // bandwidth - kilobits per second
  p.push([/\b((\d|[,.])+)\s*GBPS\b/gi, "($1 * 1024 * 1024 * 1024 / 8)"]); // bandwidth - kilobits per second

  // bugfix: NEED the \b otherwise things like 0x123b won't work correctly!
  p.push([/\b((\d|[,.])+)\s*K\b/gi, "($1 * 1e3)"]); // thousand
  p.push([/\b((\d|[,.])+)\s*M\b/gi, "($1 * 1e6)"]); // million
  p.push([/\b((\d|[,.])+)\s*B\b/gi, "($1 * 1e9)"]); // billion
  p.push([/\b((\d|[,.])+)\s*T\b/gi, "($1 * 1e12)"]); // trillion
  p.push([/((\d|[,.])+)\s*thousand\b/gi, "($1 * 1e3)"]); // thousand
  p.push([/((\d|[,.])+)\s*million\b/gi, "($1 * 1e6)"]); // million
  p.push([/((\d|[,.])+)\s*billion\b/gi, "($1 * 1e9)"]); // billion
  p.push([/((\d|[,.])+)\s*trillion\b/gi, "($1 * 1e12)"]); // trillion
  p.push([/\s+rad\b/gi, "* 180 / PI"]); // radian
  p.push([/\b([0-9.,]+)rad\b/gi, "$1 * 180 / PI"]); // radian (no spaces, just number)

  p.push([/\s+deg\b/gi, ""]); // degrees - ignore

  // Are these wanted features? Allowing 34x, (34x + 4)y... etc.?
  // any identifier prefixed by numbers: 124x becomes 124 * x
  //p.push( [/((\d|[,.])+)\s*([_a-zA-Z]+)\b/ig, "($1 * $3)"]); // any identifier
  // problems happen when using hex - 0xfe looks for a variable "0 * xfe" ... need to filter out 0x..., since 0 is nonsensical anyway... hrm.
  // 34(3+4) becomes 34 * (3+4)

  p.push([/\b0[bB]([01]+)/g, "FROMBIN('0b$1')"]); // 0b101010101 binary format; still needs to be parsed
  p.push([/\b0[oO]([01234567]+)/g, "FROMOCT('0o$1')"]); // 0o101010101 octal format

  // convert log_2(x) to (1/log(2))*log(x) -- happens before parsenumber
  p.push([/\blog_(.+)[(](.+)[)]/gi, "(log($2)/log($1))"]);

  // capture either regular numbers (0-9 with decimals and commas) or those followed by scientific notation (e+10...)
  p.push([/((\b|[.])+[0-9.,]+((e|e-|e+)[0-9]+)?)\b/gi, 'PARSENUMBER("$1")']);

  // regular "e" not working... need to fix that.
  p.push([/\bR(\d+):R(\d+)\b/gi, "getRowValues($1,$2)"]); // ROW1:ROW3
  p.push([/\bR(\d+),R(\d+)\b/gi, "getRowValues($1,$2)"]); // R1,R2 (because colon substituted)
  p.push([/\bR(\d+)\b/gi, "getRowValue($1 - 1)"]); // R1 by itself
  p.push([/\brandom\b/gi, "RANDOM"]); // capitalize random

  InstaCalc.postfilter = [];

  // for units that aren't explicitly done -- "x = 37 smiles/day"
  InstaCalc.postfilter.push([/([)])\s+(([a-z][^0123456789]+))$/gi, '$1 * this.convertUnit("$2")']); // do not allow parens in description of units, otherwise gets confused with parseNumber
  InstaCalc.postfilter.push([/[=]\s*[?]\s/, " to "]); // "100 feet = ? miles" => "100 feet to miles"
  // TODO: clean this up. it's a last resort, doesn't quite handle the conversion properly!!!
  // InstaCalc.postfilter.push( [/^(-?[\d.,]+)\s+(([a-zA-Z].*\s+(in|as|into|to)\s+.*))$/g, "CONVERSION($1, CONVERT(\"$2\") )"]);
  // replace constants

  InstaCalc.postfilter.push([/@@@@/gi, " != "]); // return != back to normal
  InstaCalc.postfilter.push([/@@@/gi, " % "]); // return MOD back to normal
  InstaCalc.postfilter.push([/@@/gi, " ^ "]); // return XOR back to normal


  // add in unit prefixes for base units
  let Units = [
    "mass:gram|grams|g",
    "length:meter|meters|m",
    "time:second|seconds|sec|s",
    "liquid:liter|liters|litre|litres|L",
    "energy:joule|joules|eV|calorie|cal|J",
    "power:watt|watts|W",
    "force:newton|newtons|N",
    "pressure:pascal|pascals|bar|bars|Pa",
    "electric_charge:coulomb|coulombs|C",
    "electric_potential:volt|volts|V",
    "electric_capacitance:farad|farads|F",
    "electric_resistance:ohm|ohms|Ω",
    "electric_conductance:siemens",
    "electric_current:ampere|amperes|amp|amps|A",
    "magnetic_flux:weber|webers|Wb",
    "magnetic_flux_density:tesla|teslas",
    "electric_inductance:henry|henrys",
    "luminous_flux:lumen|lumens|lm",
    "illuminance:lux",
    "luminous_intensity:candela|cd",
    "radioactivity:becquerel|becquerels|Bq",
    "absorbed_dose:gray|grays|Gy",
    "equivalent_dose:sievert|sieverts|Sv",
    "catalytic_activity:katal|katals|kat",
    "molar_concentration:molar",

    "freq:hz|hertz",
  ];

  let Prefixes = [
    "quetta|Q|1e30",
    "ronna|R|1e27",
    "yotta|Y|1e24",
    "zetta|Z|1e21",
    "exa|E|1e18",
    "peta|P|1e15",
    "tera|T|1e12",
    "giga|G|1e9",
    "mega|M|1e6",
    "kilo|k|1e3",
    "hecto|h|1e2",
    "deca|da|1e1",
    "deci|d|1e-1",
    "centi|c|1e-2",
    "milli|m|1e-3",
    "micro|µ|1e-6",
    "micro|u|1e-6",
    "nano|n|1e-9",
    "pico|p|1e-12",
    "femto|f|1e-15",
    "atto|a|1e-18",
    "zepto|z|1e-21",
    "yocto|y|1e-24",
    "ronto|r|1e-27",
    "quecto|q|1e-30"
  ];

  // lowercase to uppercase
  let OrderedPrefixes = Prefixes.reverse();

  // do not populate these -- they must be defined explicitly
  let Exceptions = {
    "pa": 1, // pa = Pascal, not picoAmp
    "ma": 1, // ma = milliAmp, not megaAmp
    "ha": 1, // ha = hectare, not hectaAmp
    "f": 1, // fahrenheit, not Farad,
    "c": 1, // celsius, not coulomb

    "cf": 1, //cubic foot, not centi-farad

    "ac": 1, //acre, not charge
    "cc": 1, //cubic centimeter, not centi-coulomb
    "ev": 1, //electron volt, not exavolt
    "pv": 1, // present value, not picovolt
    "fv": 1, // future value, not femtovolt
    "gs": 1, //grams, not gigasecond
    "gm": 1, //grams, not gigameter
  };

  // official spelling: w => W
  Unit.canonicalName = (function () {
    const cache = new Map();

    return function (unit) {
      if (cache.has(unit)) {
        return cache.get(unit);
      }

      for (const unitDef of Units) {
        const [_, unitNames] = unitDef.split(":");
        const names = unitNames.split("|");

        for (const name of names) {
          if (name.toLowerCase() === unit.toLowerCase()) {
            const correctedUnit = name;
            cache.set(unit, correctedUnit);
            return correctedUnit;
          }
        }
      }

      // If no match found, return the original unit string
      cache.set(unit, unit);
      return unit;
    };
  })();

  // find any units without SI, which mightb a mistake
  Unit.findMissngSI = function () {
    Object.keys(Unit).forEach(key => !Unit[key].SI && console.log(`Unit.${key} does not have an SI property.`));
  };

  // log overlapping units to console
  Unit.findOverlaps = function () {
    let overlaps = {};
    let unitNames = {};

    for (let category in Unit) {
      if (Unit.hasOwnProperty(category) && typeof Unit[category] === 'object') {
        for (let unit in Unit[category]) {
          if (Unit[category].hasOwnProperty(unit) && unit !== 'SI' && unit !== 'name') {
            if (!unitNames[unit]) {
              unitNames[unit] = [category];
            } else {
              unitNames[unit].push(category);
            }
          }
        }
      }
    }

    for (let unit in unitNames) {
      if (unitNames[unit].length > 1) {
        overlaps[unit] = unitNames[unit];
      }
    }

    for (let overlap in overlaps) {
      console.log(`Unit "${overlap}" appears in categories: ${overlaps[overlap].join(', ')}`);
    }
  };

  Units.forEach((unit) => {
    // split into type and names
    let [type, allnames] = unit.split(":");
    let names = allnames.split("|");

    OrderedPrefixes.forEach((SIprefix) => {
      let [prefix, symbol, multiplier] = SIprefix.split("|");

      let baseScale = null;

      names.forEach((name) => {
        let fullname = prefix + name;
        let fullsymbol = symbol + name;

        if (Exceptions[fullsymbol.toLowerCase()]) {
          return;
        }

        // take first entry to lookup scale
        baseScale = Unit[type][name] || Unit[type][name.toLowerCase()] || baseScale;

        // if a unit entry is already defined in the Unit object, no assignments are needed
        if (!Unit[type][fullname]) {
          // otherwise, fill in the unit definition. be case sensitive, so MPa is Megapascal, and mPa is micropascal.
          Unit[type][fullname] = baseScale * Number(multiplier);
        }

        if (symbol) {
          Unit[type][fullsymbol] ||= baseScale * Number(multiplier);
          Unit[type][fullsymbol.toLowerCase()] ||= baseScale * Number(multiplier);
        }
      });
    });
  });

  /*
    Units.forEach((unit) => {
      // split into type and names
      let [type, allnames] = unit.split(":");
      let names = allnames.split("|");
  
      // fill out prefixes -- go from lowercase to uppercase
      Prefixes.reverse().forEach((SIprefix) => {
        let [prefix, symbol, multiplier] = SIprefix.split("|");
  
        let baseScale = null;
  
        names.forEach((name) => {
          let fullname = (prefix + name).toLowerCase();
          let fullsymbol = (symbol + name).toLowerCase();
  
          if (Exceptions[fullsymbol]) {
            return;
          }
  
          // take first entry to lookup scale
          baseScale = Unit[type][name] || Unit[type][name.toLowerCase()] || baseScale;
  
          Unit[type][fullname] ||= baseScale * Number(multiplier);
          if (symbol) {
            Unit[type][fullsymbol] ||= baseScale * Number(multiplier);
          }
        });
      });
    });
    */

})();

// https://github.com/formulajs/formulajs/blob/master/src/financial.js
InstaCalc.LIB ||= {};
/**
 * Returns the internal rate of return for a series of cash flows.
 *
 * Category: Financial
 *
 * @param {*} values An array or a reference to values that contain numbers for which you want to calculate the internal rate of return.
 - Values must contain at least one positive value and one negative value to calculate the internal rate of return.
 - IRR uses the order of values to interpret the order of cash flows. Be sure to enter your payment and income values in the sequence you want.
 - If an array or reference argument contains text, logical values, or empty values, those values are ignored.
 * @param {*} guess Optional. A number that you guess is close to the result of IRR.
 - Microsoft Excel uses an iterative technique for calculating IRR. Starting with guess, IRR cycles through the calculation until the result is accurate within 0.00001 percent. If IRR can't find a result that works after 20 tries, the #NUM! error value is returned.
 - In most cases you do not need to provide guess for the IRR calculation. If guess is omitted, it is assumed to be 0.1 (10 percent).
 - If IRR gives the #NUM! error value, or if the result is not close to what you expected, try again with a different value for guess.
 * @returns
 */

InstaCalc.LIB.IRR = function (values, guess) {
  // Credits: algorithm inspired by Apache OpenOffice
  guess = guess || 0

  // Calculates the resulting amount
  const irrResult = (values, dates, rate) => {
    const r = rate + 1
    let result = values[0]

    for (let i = 1; i < values.length; i++) {
      result += values[i] / Math.pow(r, (dates[i] - dates[0]) / 365)
    }

    return result
  }

  // Calculates the first derivation
  const irrResultDeriv = (values, dates, rate) => {
    const r = rate + 1
    let result = 0

    for (let i = 1; i < values.length; i++) {
      const frac = (dates[i] - dates[0]) / 365
      result -= (frac * values[i]) / Math.pow(r, frac + 1)
    }

    return result
  }

  // Initialize dates and check that values contains at least one positive value and one negative value
  const dates = []
  let positive = false
  let negative = false

  for (let i = 0; i < values.length; i++) {
    dates[i] = i === 0 ? 0 : dates[i - 1] + 365

    if (values[i] > 0) {
      positive = true
    }

    if (values[i] < 0) {
      negative = true
    }
  }

  // Return error if values does not contain at least one positive value and one negative value
  if (!positive || !negative) {
    return undefined;
  }

  // Initialize guess and resultRate
  guess = guess === undefined ? 0.1 : guess
  let resultRate = guess

  // Set maximum epsilon for end of iteration
  const epsMax = 1e-10

  // Implement Newton's method
  let newRate, epsRate, resultValue
  let contLoop = true
  do {
    resultValue = irrResult(values, dates, resultRate)
    newRate = resultRate - resultValue / irrResultDeriv(values, dates, resultRate)
    epsRate = Math.abs(newRate - resultRate)
    resultRate = newRate
    contLoop = epsRate > epsMax && Math.abs(resultValue) > epsMax
  } while (contLoop)

  // Return internal rate of return
  return resultRate
}

/**
 * Returns the interest rate per period of an annuity.
 *
 * Category: Financial
 *
 * @param {*} nper The total number of payment periods in an annuity.
 * @param {*} pmt The payment made each period and cannot change over the life of the annuity. Typically, pmt includes principal and interest but no other fees or taxes. If pmt is omitted, you must include the fv argument.
 * @param {*} pv The present value — the total amount that a series of future payments is worth now.
 * @param {*} fv Optional. The future value, or a cash balance you want to attain after the last payment is made. If fv is omitted, it is assumed to be 0 (the future value of a loan, for example, is 0). If fv is omitted, you must include the pmt argument.
 * @param {*} type Optional. The number 0 or 1 and indicates when payments are due.
 * @param {*} guess Optional. Your guess for what the rate will be. If you omit guess, it is assumed to be 10 percent. If RATE does not converge, try different values for guess. RATE usually converges if guess is between 0 and 1.
 - If you omit guess, it is assumed to be 10 percent.
 - If RATE does not converge, try different values for guess. RATE usually converges if guess is between 0 and 1.
 * @returns
 */
InstaCalc.LIB.RATE = function (nper, pmt, pv, fv, type, guess) {
  guess = guess === undefined ? 0.01 : guess
  fv = fv === undefined ? 0 : fv
  type = type === undefined ? 0 : type

  const epsMax = 1e-10
  const iterMax = 20
  let rate = guess

  type = type ? 1 : 0

  for (let i = 0; i < iterMax; i++) {
    if (rate <= -1) {
      return undefined
    }

    let y, f

    if (Math.abs(rate) < epsMax) {
      y = pv * (1 + nper * rate) + pmt * (1 + rate * type) * nper + fv
    } else {
      f = Math.pow(1 + rate, nper)
      y = pv * f + pmt * (1 / rate + type) * (f - 1) + fv
    }

    if (Math.abs(y) < epsMax) {
      return rate
    }

    let dy

    if (Math.abs(rate) < epsMax) {
      dy = pv * nper + pmt * type * nper
    } else {
      f = Math.pow(1 + rate, nper)
      const df = nper * Math.pow(1 + rate, nper - 1)
      dy = pv * df + pmt * (1 / rate + type) * df + pmt * (-1 / (rate * rate)) * (f - 1)
    }

    rate -= y / dy
  }

  return rate
}

InstaCalc.LIB.area = function area(shape, ...dimensions) {
  let area;
  switch (shape) {
    case 'square':
      if (dimensions.length !== 1) return new Error("A square requires one dimension: side length.");
      area = dimensions[0] * dimensions[0];
      break;
    case 'circle':
      if (dimensions.length !== 1) return new Error("A circle requires one dimension: radius.");
      area = Math.PI * dimensions[0] * dimensions[0];
      break;
    case 'triangle':
      if (dimensions.length === 1) {
        // Assume equilateral triangle
        let s = dimensions[0];
        area = (Math.sqrt(3) / 4) * s * s;  // area of an equilateral triangle
      } else if (dimensions.length === 2) {
        // Assume right triangle
        area = 0.5 * dimensions[0] * dimensions[1];  // area of a right triangle
      } else if (dimensions.length === 3) {
        // Use Heron's formula for arbitrary triangle
        let s = (dimensions[0] + dimensions[1] + dimensions[2]) / 2;  // semi-perimeter
        area = Math.sqrt(s * (s - dimensions[0]) * (s - dimensions[1]) * (s - dimensions[2]));  // Heron's formula
      } else {
        return new Error("A triangle requires one, two, or three dimensions. Please provide valid dimensions.");
      }
      break;
    case 'rectangle':
      if (dimensions.length !== 2) return new Error("A rectangle requires two dimensions: width and height.");
      area = dimensions[0] * dimensions[1];  // area of a rectangle
      break;
    case 'ellipse':
      if (dimensions.length !== 2) return new Error("An ellipse requires two dimensions: major axis and minor axis.");
      area = Math.PI * dimensions[0] * dimensions[1];  // area of an ellipse
      break;
    default:
      return new Error("Shape not recognized. Please provide a valid shape: square, circle, triangle, rectangle or ellipse.");
  }
  return area;
}

InstaCalc.LIB.rational_approximation = function (t) {
  // Abramowitz and Stegun formula 26.2.23.
  // The absolute value of the error should be less than 4.5 e-4.
  var c = [2.515517, 0.802853, 0.010328];
  var d = [1.432788, 0.189269, 0.001308];
  var numerator = (c[2] * t + c[1]) * t + c[0];
  var denominator = ((d[2] * t + d[1]) * t + d[0]) * t + 1.0;
  return t - numerator / denominator;
};

InstaCalc.LIB.normal_cdf_inverse = function (p) {
  // See article above for explanation of this section.
  if (p < 0.5) {
    // F^-1(p) = - G^-1(p)
    return -this.rational_approximation(Math.sqrt(-2.0 * Math.log(p)));
  } else {
    // F^-1(p) = G^-1(1-p)
    return this.rational_approximation(Math.sqrt(-2.0 * Math.log(1.0 - p)));
  }
};

/* complex number class */
/* {
r: real part,
i: imaginary part
}
*/

class Complex {

  static tolerance = 1e-14;
  static precision = 1e-12;
  static displayPrecision = 1e-8;

  constructor(r, i) {
    if (typeof r !== "number" || typeof i !== "number") {
      return null;
    }

    this.r = r ?? 0; // real part
    this.i = i ?? 0; // imaginary part

    let adjust = (c) => {
      // Determine the number of decimal places from the rounding level.
      let decimals = Math.round(-Math.log10(Complex.precision));
      if (Math.abs(c.r) < Complex.tolerance) {
        c.r = 0;
      } else {
        c.r = Number(c.r.toFixed(decimals));
      }
      if (Math.abs(c.i) < Complex.tolerance) {
        c.i = 0;
      } else {
        c.i = Number(c.i.toFixed(decimals));
      }
      return c;
    };

    return adjust(this);
  }

  static phasor(mag, angle) {
    if (typeof mag !== "number" || typeof angle !== "number") {
      return null;
    }

    let autoAngle = InstaCalc.prototype.fn.autoRad(angle);

    let real = mag * Math.cos(autoAngle);
    let imag = mag * Math.sin(autoAngle);
    let c = new Complex(real, imag);
    return c;
  }

  // pare 5∠53.13 back into complex number
  static fromPhasorString(str) {
    let parts = str.split("∠");
    if (parts.length !== 2) {
      return null;
    }
    let mag = parseFloat(parts[0]);
    let angle = parseFloat(parts[1]);
    if (isNaN(mag) || isNaN(angle)) {
      return null;
    }
    return this.phasor(mag, angle * Math.PI / 180); // convert to radians
  }

  // return in format: 15∠30 (degrees assumed)
  static toPhasorString(c) {
    if (!this.isComplex(c)) {
      return null;
    }

    let mag = Complex.abs(c);
    let angle = Complex.angle(c) * 180 / Math.PI; // convert to degrees
    return IC.parser.toFixedIfNecessary(mag, 4) + "∠" + IC.parser.toFixedIfNecessary(angle, 2);
  }

  static toString(c) {
    const decimals = Math.round(-Math.log10(Complex.displayPrecision));
    function format(num) {
      return Number(num.toFixed(decimals)).toString();
    }

    if (typeof c === "number") {
      return format(c);
    }
    if (c.i === 0) {
      return format(c.r);
    }
    if (c.r === 0) {
      const imagAbs = Math.abs(c.i);
      const formattedImag = format(imagAbs);
      return (c.i < 0 ? "-" : "") + (imagAbs === 1 ? "i" : formattedImag + "i");
    }
    const formattedReal = format(c.r);
    let imagPart = "";
    if (c.i < 0) {
      imagPart = (Math.abs(c.i) === 1 ? " - i" : " - " + format(-c.i) + "i");
    } else {
      imagPart = (Math.abs(c.i) === 1 ? " + i" : " + " + format(c.i) + "i");
    }
    return formattedReal + imagPart;
  }

  static anyIsComplex(a, b) {
    return this.isComplex(a) || this.isComplex(b);
  }

  static isComplex(obj) {
    if (typeof obj !== 'object') {
      return false;
    }

    return obj instanceof Object && obj.hasOwnProperty('r') && obj.hasOwnProperty('i');
  }

  static toComplex(obj) {
    if (this.isComplex(obj)) {
      return obj;
    }

    let real = IC.val(obj);
    if (isNaN(real)) {
      return null;
    }

    return {
      r: real,
      i: 0
    };
  }

  static equalityTolerance = 1e-6;

  static isEqual(z1, z2) {
    if (typeof z1 === "number" && typeof z2 === "number") {
      return Math.abs(z1 - z2) < Complex.equalityTolerance;
    }
    let c1 = Complex.isComplex(z1) ? z1 : new Complex(z1, 0);
    let c2 = Complex.isComplex(z2) ? z2 : new Complex(z2, 0);
    return Math.abs(c1.r - c2.r) < Complex.equalityTolerance &&
      Math.abs(c1.i - c2.i) < Complex.equalityTolerance;
  }

  static isNotEqual(z1, z2) {
    return !this.isEqual(z1, z2);
  }

  static lessThan(z1, z2) {
    if (typeof z1 === "number" && typeof z2 === "number") {
      return z1 < z2;
    }
    if (typeof z1 === "number") {
      return z1 < z2.r && 0 === z2.i;
    }
    if (typeof z2 === "number") {
      return z1.r < z2 && 0 === z1.i;
    }
    return z1.r < z2.r && z1.i < z2.i;
  }

  static re(z) {
    return typeof z === "number" ? z : z.r;
  }

  static im(z) {
    return typeof z === "number" ? 0 : z.i;
  }

  static abs(z) {
    if (typeof z === "number") {
      return Math.abs(z);
    }

    return Math.sqrt(z.r * z.r + z.i * z.i);
  }

  // don't like the "arg" name...
  static angle(z) {
    if (typeof z === "number") {
      return 0;
    }

    return Math.atan2(z.i, z.r);
  }

  static conjugate(z) {
    if (typeof z === "number") {
      return z;
    }
    return new Complex(z.r, -z.i);
  }

  static add(a, b) {
    if (typeof a === "number" && typeof b === "number") {
      return IC.mathAdd(a, b);
    }
    let ca = Complex.isComplex(a) ? a : new Complex(a, 0);
    let cb = Complex.isComplex(b) ? b : new Complex(b, 0);
    let result = new Complex(ca.r + cb.r, ca.i + cb.i);
    return result.i === 0 ? result.r : result;
  }

  static subtract(a, b) {
    if (typeof a === "number" && typeof b === "number") {
      return IC.mathSub(a, b);
    }
    let ca = Complex.isComplex(a) ? a : new Complex(a, 0);
    let cb = Complex.isComplex(b) ? b : new Complex(b, 0);
    let result = new Complex(ca.r - cb.r, ca.i - cb.i);
    return result.i === 0 ? result.r : result;
  }

  static multiply(a, b) {
    if (typeof a === "number" && typeof b === "number") {
      return IC.mathMul(a, b);
    }
    let ca = Complex.isComplex(a) ? a : new Complex(a, 0);
    let cb = Complex.isComplex(b) ? b : new Complex(b, 0);
    let realPart = ca.r * cb.r - ca.i * cb.i;
    let imagPart = ca.r * cb.i + ca.i * cb.r;
    let result = new Complex(realPart, imagPart);
    return result.i === 0 ? result.r : result;
  }

  static inverse(z) {
    if (typeof z === "number") {
      return IC.mathInverse(z);
    }

    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let denominator = cz.r * cz.r + cz.i * cz.i;
    if (denominator === 0) {
      return Infinity;
    }
    let result = new Complex(cz.r / denominator, -cz.i / denominator);
    return result.i === 0 ? result.r : result;
  }

  static divide(a, b) {
    if (typeof a === "number" && typeof b === "number") {
      return IC.mathDiv(a, b);
    }
    return Complex.multiply(a, Complex.inverse(b));
  }

  static sqrt(z) {
    return Complex.pow(z, 0.5);
  }

  static pow(base, exponent) {
    if (typeof base === "number" && typeof exponent === "number" && (base >= 0 || Number.isInteger(exponent))) {
      return Math.pow(base, exponent);
    }

    // Convert base and exponent to complex objects.
    const b = Complex.isComplex(base) ? base : new Complex(base, 0);
    const e = Complex.isComplex(exponent) ? exponent : new Complex(exponent, 0);

    // Use the identity: z^w = exp(w * log(z))
    const result = Complex.exp(Complex.multiply(e, Complex.log(b)));

    // If the imaginary part is effectively zero, return a real number.
    return Math.abs(result.i) < Complex.tolerance ? result.r : result;
  }

  static exp(z) {
    if (typeof z === "number") {
      return Math.exp(z);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let expRe = Math.exp(cz.r);
    return new Complex(expRe * Math.cos(cz.i), expRe * Math.sin(cz.i));
  }

  // natural log
  static log(z) {
    if (typeof z === "number") {
      if (z > 0) {
        return Math.log(z);
      } else if (z < 0) {
        return new Complex(Math.log(-z), Math.PI);
      } else {
        return new Complex(-Infinity, 0); // log(0) should be -Infinity + 0i
      }
    }

    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);

    // Handle special case: log(0) = -Infinity + 0i
    if (cz.r === 0 && cz.i === 0) {
      return new Complex(-Infinity, 0);
    }

    // Handle positive real numbers: log(positive real) = log(r) + 0i
    if (cz.i === 0 && cz.r > 0) {
      return new Complex(Math.log(cz.r), 0);
    }

    // Handle negative real numbers: log(negative real) = log(|r|) + πi
    if (cz.i === 0 && cz.r < 0) {
      return new Complex(Math.log(-cz.r), Math.PI);
    }

    // General complex case: log(z) = log(|z|) + i*arg(z)
    let absVal = this.abs(cz);
    let angleVal = this.angle(cz);
    return new Complex(Math.log(absVal), angleVal);
  }

  static log10(z) {
    return Complex.logBase(z, 10);
  }

  static log2(z) {
    return Complex.logBase(z, 2);
  }

  static logBase(z, base) {
    if (typeof z === "number" && typeof base === "number" && z > 0 && base > 1) {
      return Math.log(z) / Math.log(base);
    }

    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let cbase = Complex.isComplex(base) ? base : new Complex(base, 0);
    return Complex.divide(Complex.log(cz), Complex.log(cbase));
  }

  static sin(z) {
    if (typeof z === "number") {
      return Math.sin(z);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    return Complex.divide(
      Complex.subtract(
        Complex.exp(Complex.multiply(new Complex(0, 1), cz)),
        Complex.exp(Complex.multiply(new Complex(0, -1), cz))
      ),
      new Complex(0, 2)
    );
  }

  static cos(z) {
    if (typeof z === "number") {
      return Math.cos(z);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    return Complex.divide(
      Complex.add(
        Complex.exp(Complex.multiply(new Complex(0, 1), cz)),
        Complex.exp(Complex.multiply(new Complex(0, -1), cz))
      ),
      new Complex(2, 0)
    );
  }

  static tan(z) {
    if (typeof z === "number") {
      return Math.tan(z);
    }
    // tan(z) = sin(z)/cos(z)
    return Complex.divide(Complex.sin(z), Complex.cos(z));
  }

  static cot(z) {
    if (typeof z === "number") {
      return 1 / Math.tan(z);
    }
    // cot(z) = cos(z)/sin(z)
    return Complex.divide(Complex.cos(z), Complex.sin(z));
  }

  static sec(z) {
    if (typeof z === "number") {
      return 1 / Math.cos(z);
    }
    // sec(z) = 1/cos(z)
    return Complex.inverse(Complex.cos(z));
  }

  static csc(z) {
    if (typeof z === "number") {
      return 1 / Math.sin(z);
    }
    // csc(z) = 1/sin(z)
    return Complex.inverse(Complex.sin(z));
  }

  static asin(z) {
    if (typeof z === "number") {
      if (Math.abs(z) <= 1) return Math.asin(z);
      // For z > 1 or z < -1
      const s = z > 1 ? 1 : -1;
      return new Complex(s * Math.PI / 2, -Math.log(Math.abs(z) + Math.sqrt(z * z - 1)));
    }
    // For complex values: asin(z) = -i * ln(i*z + √(1 - z²))
    const cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    const iTimesZ = Complex.multiply(new Complex(0, 1), cz);
    const sqrtTerm = Complex.pow(Complex.subtract(new Complex(1, 0), Complex.multiply(cz, cz)), 0.5);
    return Complex.multiply(new Complex(0, -1), Complex.log(Complex.add(iTimesZ, sqrtTerm)));
  }

  static acos(z) {
    if (typeof z === "number") {
      if (Math.abs(z) <= 1) {
        return Math.acos(z);
      } else if (z > 1) {
        // For z > 1, acos(z) = i * ln(z + √(z² - 1))
        return new Complex(0, Math.log(z + Math.sqrt(z * z - 1)));
      } else { // z < -1
        // For z < -1, acos(z) = π - i * ln(-z + √(z² - 1))
        return new Complex(Math.PI, -Math.log(-z + Math.sqrt(z * z - 1)));
      }
    }
    // For complex values use the standard formula: acos(z) = π/2 - asin(z)
    return Complex.subtract(new Complex(Math.PI / 2, 0), Complex.asin(z));
  }

  static atan(z) {
    if (typeof z === "number") {
      return Math.atan(z);
    }

    // For complex values: atan(z) = 0.5 * i * ln((1 - iz) / (1 + iz))
    const cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    const iTimesZ = Complex.multiply(new Complex(0, 1), cz);
    const numerator = Complex.subtract(new Complex(1, 0), iTimesZ);
    const denominator = Complex.add(new Complex(1, 0), iTimesZ);
    return Complex.multiply(new Complex(0.5, 0), Complex.log(Complex.divide(numerator, denominator)));
  }

  static atan2(y, x) {
    if (typeof y === "number" && typeof x === "number") {
      return Math.atan2(y, x);
    }

    const cz = Complex.isComplex(x) ? x : new Complex(x, 0);
    const cy = Complex.isComplex(y) ? y : new Complex(y, 0);
    return Complex.atan(Complex.divide(cy, cz));
  }

  static asec(z) {
    if (typeof z === "number") {
      if (Math.abs(z) < 1) return undefined; // asec is undefined for |z| < 1
      return Math.acos(1 / z);
    }
    // For complex values: asec(z) = acos(1/z)
    const cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    return Complex.acos(Complex.inverse(cz));
  }

  static acsc(z) {
    if (typeof z === "number") {
      if (Math.abs(z) < 1) return undefined; // acsc is undefined for |z| < 1
      return Math.asin(1 / z);
    }
    // For complex values: acsc(z) = asin(1/z)
    const cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    return Complex.asin(Complex.inverse(cz));
  }

  static acot(z) {
    if (typeof z === "number") {
      return Math.atan(1 / z);
    }
    // For complex values: acot(z) = atan(1/z)
    const cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    return Complex.atan(Complex.inverse(cz));
  }

  static sinh(z) {
    if (typeof z === "number") {
      return (Math.exp(z) - Math.exp(-z)) / 2;
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let expPos = Complex.exp(cz);
    let expNeg = Complex.exp(Complex.multiply(cz, -1));
    return Complex.divide(Complex.subtract(expPos, expNeg), 2);
  }

  static cosh(z) {
    if (typeof z === "number") {
      return (Math.exp(z) + Math.exp(-z)) / 2;
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let expPos = Complex.exp(cz);
    let expNeg = Complex.exp(Complex.multiply(cz, -1));
    return Complex.divide(Complex.add(expPos, expNeg), 2);
  }

  static tanh(z) {
    if (typeof z === "number") {
      let ePos = Math.exp(z);
      let eNeg = Math.exp(-z);
      return ePos === Infinity ? 1 : eNeg === Infinity ? -1 : (ePos - eNeg) / (ePos + eNeg);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let sinhVal = this.sinh(cz);
    let coshVal = this.cosh(cz);
    return Complex.divide(sinhVal, coshVal);
  }

  static sech(z) {
    if (typeof z === "number") {
      return 1 / ((Math.exp(z) + Math.exp(-z)) / 2);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let coshVal = this.cosh(cz);
    return Complex.inverse(coshVal);
  }

  static csch(z) {
    if (typeof z === "number") {
      return 1 / ((Math.exp(z) - Math.exp(-z)) / 2);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let sinhVal = this.sinh(cz);
    return Complex.inverse(sinhVal);
  }

  static coth(z) {
    if (typeof z === "number") {
      return 1 / Math.tanh(z);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let sinhVal = this.sinh(cz);
    let coshVal = this.cosh(cz);
    return Complex.divide(coshVal, sinhVal);
  }

  static asinh(z) {
    if (typeof z === "number") {
      return Math.log(z + Math.sqrt(z * z + 1));
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let zSquared = Complex.multiply(cz, cz);
    let underSqrt = Complex.add(zSquared, 1);
    let sqrtVal = Complex.sqrt(underSqrt);
    return Complex.log(Complex.add(cz, sqrtVal));
  }

  static acosh(z) {
    if (typeof z === "number") {
      if (z < 1) return undefined;
      return Math.log(z + Math.sqrt(z * z - 1));
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let zSquared = Complex.multiply(cz, cz);
    let underSqrt = Complex.subtract(zSquared, 1);
    let sqrtVal = Complex.sqrt(underSqrt);
    return Complex.log(Complex.add(cz, sqrtVal));
  }

  static atanh(z) {
    if (typeof z === "number") {
      return Math.log((1 + z) / (1 - z)) / 2;
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let numerator = Complex.add(cz, 1);
    let denominator = Complex.subtract(1, cz);
    return Complex.multiply(Complex.log(Complex.divide(numerator, denominator)), 0.5);
  }

  static asech(z) {
    if (typeof z === "number") {
      if (z <= 0 || z > 1) return undefined;
      return this.acosh(1 / z);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    return this.acosh(Complex.inverse(cz));
  }

  static acsch(z) {
    if (typeof z === "number") {
      if (z === 0) return undefined;
      return this.asinh(1 / z);
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    return this.asinh(Complex.inverse(cz));
  }

  static acoth(z) {
    if (typeof z === "number") {
      if (Math.abs(z) < 1) return undefined;
      return 0.5 * Math.log((z + 1) / (z - 1));
    }
    let cz = Complex.isComplex(z) ? z : new Complex(z, 0);
    let numerator = Complex.add(cz, 1);
    let denominator = Complex.subtract(cz, 1);
    return Complex.multiply(Complex.log(Complex.divide(numerator, denominator)), 0.5);
  }
}

InstaCalc.Complex = Complex;

// UMD-style export: attach to global for browsers, and support CommonJS for Node/tests
(function (root, factory) {
  // Browser globals
  root.InstaCalc = factory().InstaCalc;
  // Preserve Unit on global if defined in this file
  if (typeof root.Unit === 'undefined' && typeof Unit !== 'undefined') {
    root.Unit = Unit;
  }
  // CommonJS (Node/tests)
  if (typeof module === 'object' && module && module.exports) {
    module.exports = { default: root.InstaCalc, InstaCalc: root.InstaCalc, Unit: root.Unit };
  }
})(typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : (typeof global !== 'undefined' ? global : this)), function () {
  return { InstaCalc };
});

;
/*

## General processing flow 
        
  [Preprocess]          Apply string/regex changes in CONFIG (i.e. "in" becomes " -> ")
  [Parse]               Parse into AST using grammar (Nearley.js) 
  [Evaluate]            Execute AST with various modules (Math, Type, Unit, Eval)
  [Postprocess]         Format final result (decimal places, help text, logs, etc.)

## Components

- CONFIG      Goal: data driven where possible
- IC          main facade, with submodules for
  Core
  Logs
  Environment   - Execution state
  Object        - internal ICObject 
  Literal       - base primitives
  Type          - type system
  String
  Parse
  Semantic      - AST operations
  Math
  Eval          

- IC.parser     - apply preprocess/parse rules with config

## Classes

- ICUnit      
- ICTime      
- ICDate      
- ICLib
*/


/** @global */
var LIB = {};

LIB.nearley = (typeof nearley !== 'undefined' ? nearley : null) || (typeof window !== 'undefined' ? window?.nearley : null) || (typeof self !== 'undefined' ? self?.nearley : null);
LIB.text2num = (typeof text2num !== 'undefined' ? text2num : null) || (typeof window !== 'undefined' ? window?.text2num : null) || (typeof self !== 'undefined' ? self?.text2num : null);
LIB.Inflector = (typeof Inflector !== 'undefined' ? Inflector : null) || (typeof window !== 'undefined' ? window?.Inflector : null) || (typeof self !== 'undefined' ? self?.Inflector : null);
LIB.toFractionIterative = (typeof toFractionIterative !== 'undefined' ? toFractionIterative : null) || (typeof window !== 'undefined' ? window?.toFractionIterative : null) || (typeof self !== 'undefined' ? self?.toFractionIterative : null);

LIB.InstaCalc = (typeof InstaCalc !== 'undefined' ? InstaCalc : null) || window?.InstaCalc;
LIB.Unit = (typeof Unit !== 'undefined' ? Unit : null) || window?.Unit;
LIB.Calc = typeof Calc !== "undefined" ? Calc : window?.Calc;
LIB.Complex = LIB.InstaCalc.Complex;

/** @global */
// @ts-ignore
const GRAMMAR = (typeof window !== 'undefined' ? window.instacalc_grammar : null) || (typeof self !== 'undefined' ? self.instacalc_grammar : null) || (typeof global !== 'undefined' ? global.instacalc_grammar : null);
const LEXER = GRAMMAR.Lexer;
const GRAMMAR_FROM_COMPILED = LIB.nearley.Grammar.fromCompiled(GRAMMAR);

const CONFIG = {

  SETTINGS: {
    MAX_ARRAY_SIZE: 1000,
  },

  // warns user if overridden
  // SINGLE SOURCE OF TRUTH: imported from grammar/keywords.js (loaded before this file)
  // DO NOT add keywords here - edit grammar/keywords.js instead!
  KEYWORDS: (typeof IC_KEYWORDS !== 'undefined' && IC_KEYWORDS.ALL_KEYWORDS) || {
    // Fallback for tests/environments where keywords.js isn't loaded
    and: true, or: true, not: true, xor: true,
    mod: true, off: true, of: true, from: true,
    in: true, into: true, to: true, as: true, en: true, if: true, else: true,
    using: true, with: true, at: true, per: true, every: true, until: true,
  },

  // 10 sales per 3 days => 10 sales / 3 days
  UNIT_DIVISION_REGEX: {
    find: /\b(per|for|every|each|until)\b/gi,
    replace: "/",
  },

  STOP_WORDS: {
    a: true,
    an: true,
    and: true,
    are: true,
    as: true,
    at: true,
    be: true,
    but: true,
    by: true,
    for: true,
    if: true,
    in: true,
    into: true,
    is: true,
    it: true,
    no: true,
    not: true,
    of: true,
    on: true,
    or: true,
    such: true,
    that: true,
    the: true,
    their: true,
    then: true,
    there: true,
    these: true,
    they: true,
    this: true,
    to: true,
    was: true,
    will: true,
    with: true,

    // for loan: pay off over 
    over: true,
    off: true,
  },

  FN_REMOVE_STOP_WORDS: (str) => {
    // delete stop words from string; loop through
    let SYMBOLS = ["@", "->"];
    let words = str.split(/\s+/);
    let newWords = [];
    for (let i = 0; i < words.length; i++) {
      if (!CONFIG.STOP_WORDS[words[i].toLowerCase()] && !SYMBOLS.includes(words[i])) {
        newWords.push(words[i]);
      }
    }

    return newWords.join(" ");
  },

  // prefixed with \
  SYMBOLS: {
    // operators - https://www.classe.cornell.edu/~dms79/LectureNotes/formulae/list-of-math-symbols-extended.htm 
    plus: "\u002B",
    minus: "\u2212",
    times: "\u00D7",
    mult: "\u00D7",
    div: "\u00F7",

    lt: "<",
    gt: ">",
    ne: "≠",
    "!=": "≠",
    eq: "=",
    le: "≤",
    "<=": "≤",
    ge: "≥",
    ">=": "≥",

    root: "\u221A",
    sqrt: "\u221A",
    cbrt: "\u221B",

    // exponents
    "0": "\u2070",
    "1": "\u00B9", // ¹
    "2": "\u00B2", // ² 
    "3": "\u00B3", // ³ 
    "4": "\u2074",
    "5": "\u2075",
    "6": "\u2076",
    "7": "\u2077",
    "8": "\u2078",
    "9": "\u2079",

    // neg exponents
    "-0": "\u207B\u2070",
    "-1": "\u207B\u00B9", // ¹
    "-2": "\u207B\u00B2", // ² 
    "-3": "\u207B\u00B3", // ³ 
    "-4": "\u207B\u2074",
    "-5": "\u207B\u2075",
    "-6": "\u207B\u2076",
    "-7": "\u207B\u2077",
    "-8": "\u207B\u2078",
    "-9": "\u207B\u2079",

    // subscripts
    "_0": "\u2080",
    "_1": "\u2081",
    "_2": "\u2082",
    "_3": "\u2083",
    "_4": "\u2084",
    "_5": "\u2085",
    "_6": "\u2086",
    "_7": "\u2087",
    "_8": "\u2088",
    "_9": "\u2089",

    // latex symbols
    dot: "·",
    bullet: "\u2022",
    cdot: "·",
    deg: "\u00B0",

    // calc symbols
    del: "∂",
    partial: "∂",
    grad: "∇",
    nabla: "∇",
    angle: "∠",
    triangle: "△",
    star: "★",
    sum: "\u03A3", // sigma
    int: "∫",
    oint: "∮",
    prop: "∝",
    propto: "∝",
    approx: "≈",

    // greeks - http://www.javascripter.net/faq/greekletters.htm
    Alpha: "\u0391", // Α,
    Beta: "\u0392", // Β,
    Gamma: "\u0393", // Γ,
    Delta: "\u0394", // Δ,
    Epsilon: "\u0395", // Ε,
    Zeta: "\u0396", // Ζ,
    Eta: "\u0397", // Η,
    Theta: "\u0398", // Θ,
    Iota: "\u0399", // Ι,
    Kappa: "\u039A", // Κ,
    Lambda: "\u039B", // Λ,
    Mu: "\u039C", // Μ,
    Nu: "\u039D", // Ν,
    Xi: "\u039E", // Ξ,
    Omicron: "\u039F", // Ο,
    Pi: "\u03A0", // Π,
    Rho: "\u03A1", // Ρ,
    Sigma: "\u03A3", // Σ,
    Tau: "\u03A4", // Τ,
    Upsilon: "\u03A5", // Υ,
    upsih: "\u03D2", // ϒ,
    Phi: "\u03A6", // Φ,
    Chi: "\u03A7", // Χ,
    Psi: "\u03A8", // Ψ,
    Omega: "\u03A9", // Ω,
    alpha: "\u03B1", // α,
    beta: "\u03B2", // β,
    gamma: "\u03B3", // γ,
    delta: "\u03B4", // δ,
    epsilon: "\u03B5", // ε,
    zeta: "\u03B6", // ζ,
    eta: "\u03B7", // η,
    theta: "\u03B8", // θ,
    thetasym: "\u03D1", // ϑ,
    iota: "\u03B9", // ι,
    kappa: "\u03BA", // κ,
    lambda: "\u03BB", // λ,
    mu: "\u03BC", // μ,
    nu: "\u03BD", // ν,
    xi: "\u03BE", // ξ,
    omicron: "\u03BF", // ο,
    pi: "\u03C0", // π,
    piv: "\u03D6", // ϖ,
    rho: "\u03C1", // ρ,
    sigmaf: "\u03C2", // ς,
    sigma: "\u03C3", // σ,
    tau: "\u03C4", // τ,
    upsilon: "\u03C5", // υ,
    phi: "\u03C6", // φ,
    chi: "\u03C7", // χ,
    psi: "\u03C8", // ψ,
    omega: "\u03C9", // ω,

    // other symbols
    rightarrow: "→",
    infty: "∞",

    micro: "µ",

    // curisve script
    // https://www.w3.org/TR/xml-entity-names/1D4.html
    // Idea: let people enter variables, constants, etc. that don't interfere
    // with built-in units (c), etc.
    A: "𝓐",
    B: "𝓑",
    C: "𝓒",
    D: "𝓓",
    E: "𝓔",
    F: "𝓕",
    G: "𝓖",
    H: "𝓗",
    I: "𝓘",
    J: "𝓙",
    K: "𝓚",
    L: "𝓛",
    M: "𝓜",
    N: "𝓝",
    O: "𝓞",
    P: "𝓟",
    Q: "𝓠",
    R: "𝓡",
    S: "𝓢",
    T: "𝓣",
    U: "𝓤",
    V: "𝓥",
    W: "𝓦",
    X: "𝓧",
    Y: "𝓨",
    Z: "𝓩",

    a: "𝒂",
    b: "𝒃",
    c: "𝒄",
    d: "𝒅",
    e: "𝒆",
    f: "𝒇",
    g: "𝒈",
    h: "𝒉",
    i: "𝒊",
    j: "𝒋",
    k: "𝒌",
    l: "𝒍",
    m: "𝒎",
    n: "𝒏",
    o: "𝒐",
    p: "𝒑",
    q: "𝒒",
    r: "𝒓",
    s: "𝒔",
    t: "𝒕",
    u: "𝒖",
    v: "𝒗",
    w: "𝒘",
    x: "𝒙",
    y: "𝒚",
    z: "𝒛"
  },

  // not shown in custom list, as it's built-in
  CUSTOM_UNIT_IGNORE: { cycle: true, cycles: true },

  SCALES: {
    k: 1e3,
    hundred: 1e2,
    thousand: 1e3,

    // m: 1e6, // bad m! gone, for stephen
    million: 1e6,
    mln: 1e6,
    mio: 1e6, // german, somehow getting more commmon

    // TODO: have language packs which can add new units/scales? 

    b: 1e9,
    billion: 1e9,
    bil: 1e9,
    bn: 1e9,
    bln: 1e9,
    milliard: 1e9,

    t: 1e12,
    trillion: 1e12,

    quadrillion: 1e15,
    quintillion: 1e18,
    sextillion: 1e21,
    septillion: 1e24,

    rad: 180 / Math.PI,
    radian: 180 / Math.PI,
    radians: 180 / Math.PI,

    googol: 1e100,
    googolplex: 1e1000,

    i: LIB.Complex.pow(-1, 0.5),  // implicit unit as well as variable. Would like to clean up this for a single def.
  },

  NUMBER_SCALES: {
    dozen: 12,
    lakh: 1e5,
    lac: 1e5,
    cr: 1e7,
    crore: 1e7,
    score: 20,
  },

  SCALES_CASE_SENSITIVE: {
    M: 1e6,
  },

  // convert text to a number: "million" -> 1e6, for use in "123456 to nearest million"
  nearestNumberScale: (text) => {
    let textLower = text.toLowerCase();

    // exception for M, which means million here
    if (textLower == "m") {
      return CONFIG.SCALES_CASE_SENSITIVE["M"];
    }
    return CONFIG.SCALES[textLower] || CONFIG.NUMBER_SCALES[textLower] || null;
  },

  nearestNumberOrUnit: (x, multiple) => {
    if (typeof multiple != "number") {
      return x;
    }

    let newNum = Math.round(IC.val(x) / multiple) * multiple;

    if (IC.hasType(x)) {
      return IC.makeICObject(newNum, x.t, x.f);
    }
    else {
      return newNum;
    }
  },

  /* 3pi/2rad is interpreted as (3*pi/2) rad */
  SCALES_POST_CONVERT: {
    rad: "rad", // lookup key in the scales array
    radian: "rad",
    radians: "rad",
  },

  PREDEFINED: {
    TAU: 2 * Math.PI,
    PI: Math.PI,
    E: Math.E,
    //    IM_UNIT: LIB.Complex.pow(-1, 0.5),
    LOG10E: Math.LOG10E,
    LOG2E: Math.LOG2E,
    LN10: Math.LN10,
    LN2: Math.LN2,
    SQRT1_2: Math.SQRT1_2,
    PHI: (1 + Math.sqrt(5)) / 2,
    RAD: 180 / Math.PI, // acts like a constant as well
    RADIAN: 180 / Math.PI,
    RADIANS: 180 / Math.PI,

    // phystics constants
    GRAVITY: () => IC.makeICObject(9.8, "m/s^2"),
    SPEED_OF_LIGHT: () => IC.makeICObject(299792458, "m/s"),
    C_LIGHT: () => IC.makeICObject(299792458, "m/s"),
    SMALLG: () => IC.makeICObject(9.8, "m/s^2"),
    BIGG: () => IC.makeICObject(6.67408e-11, "m^3/kg/s^2"),
    PLANCK: () => IC.makeICObject(6.62607015e-34, "J s"),
    PLANCK_REDUCED: () => IC.makeICObject(6.62607015e-34 / (2 * Math.PI), "J*s"),
    HBAR: () => IC.makeICObject(6.62607015e-34 / (2 * Math.PI), "J s"),
    ELECTRON_CHARGE: () => IC.makeICObject(1.602176634e-19, "coulomb"),
    ELECTRON_MASS: () => IC.makeICObject(9.1093837015e-31, "kg"),
    PROTON_MASS: () => IC.makeICObject(1.67262192369e-27, "kg"),
    NEUTRON_MASS: () => IC.makeICObject(1.67492749804e-27, "kg"),
    R_GAS: () => IC.makeICObject(8.31446261815324, "J/K/mol"),
    F_FARADAY: () => IC.makeICObject(96485.33212331001, "coulomb/mol"),
    AVOGADRO_CONST: () => IC.makeICObject(6.02214076e23, "1/mol"),
    K_BOLTZMANN: () => IC.makeICObject(1.380649e-23, "J/K"),
  },

  PREDEFINED_FALLBACK: {
    I: LIB.Complex.pow(-1, 0.5), // too common to always be there, but it exists as a fallback
  },

  PREDEFINED_FUNCTION: {
    RANDOM: () => Math.random(),
    NOW: () => ICDate.makeICObject((new Date()).toISOString()),
    TODAY:
      () => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);  // Set time to midnight in local timezone
        return ICDate.makeICObject(date.toISOString());
      },

    DATE: (year, month, day) => {
      if (year && month && day) {
        return ICDate.makeICObject(`${year}-${month}-${day}`);
      }

      return CONFIG.PREDEFINED_FUNCTION.TODAY();
    },
    TIME: () => CONFIG.PREDEFINED_FUNCTION.NOW(),

    YESTERDAY: () => {
      const date = new Date();
      date.setDate(date.getDate() - 1);
      date.setHours(0, 0, 0, 0);
      return ICDate.makeICObject(date.toISOString());
    },

    TOMORROW: () => {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      date.setHours(0, 0, 0, 0);
      return ICDate.makeICObject(date.toISOString());
    },

    // Excel-compatible date/time extraction functions
    YEAR: (dateValue) => {
      if (!dateValue) return null;
      // dateValue is already the plain value (ISO string for dates) thanks to IC.val()
      const date = new Date(dateValue);
      if (!date || isNaN(date.getTime())) return null;
      return date.getFullYear();
    },

    MONTH: (dateValue) => {
      if (!dateValue) return null;
      const date = new Date(dateValue);
      if (!date || isNaN(date.getTime())) return null;
      return date.getMonth() + 1; // JS months are 0-indexed, Excel uses 1-12
    },

    DAY: (dateValue) => {
      if (!dateValue) return null;
      const date = new Date(dateValue);
      if (!date || isNaN(date.getTime())) return null;
      return date.getDate();
    },

    WEEKDAY: (dateValue, returnType = 1) => {
      if (!dateValue) return null;
      const date = new Date(dateValue);
      if (!date || isNaN(date.getTime())) return null;

      const jsDay = date.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday

      // Convert based on return_type
      switch (returnType) {
        case 1: // 1=Sunday, 2=Monday, ..., 7=Saturday (Excel default)
          return jsDay + 1;
        case 2: // 1=Monday, 2=Tuesday, ..., 7=Sunday
          return jsDay === 0 ? 7 : jsDay;
        case 3: // 0=Monday, 1=Tuesday, ..., 6=Sunday
          return jsDay === 0 ? 6 : jsDay - 1;
        default:
          return jsDay + 1; // Default to type 1
      }
    },

    HOUR: (timeValue) => {
      if (timeValue === null || timeValue === undefined) return null;
      // If it's a number, treat it as seconds (time format)
      if (typeof timeValue === 'number') {
        return Math.floor(timeValue / 3600) % 24;
      }
      // Otherwise treat as datetime ISO string
      const date = new Date(timeValue);
      if (!date || isNaN(date.getTime())) return null;
      return date.getHours();
    },

    MINUTE: (timeValue) => {
      if (timeValue === null || timeValue === undefined) return null;
      // If it's a number, treat it as seconds (time format)
      if (typeof timeValue === 'number') {
        return Math.floor(timeValue / 60) % 60;
      }
      // Otherwise treat as datetime ISO string
      const date = new Date(timeValue);
      if (!date || isNaN(date.getTime())) return null;
      return date.getMinutes();
    },

    SECOND: (timeValue) => {
      if (timeValue === null || timeValue === undefined) return null;
      // If it's a number, treat it as seconds (time format)
      if (typeof timeValue === 'number') {
        return Math.floor(timeValue) % 60;
      }
      // Otherwise treat as datetime ISO string
      const date = new Date(timeValue);
      if (!date || isNaN(date.getTime())) return null;
      return date.getSeconds();
    },

    WEEKNUM: (dateValue, returnType = 1) => {
      if (!dateValue) return null;
      const date = new Date(dateValue);
      if (!date || isNaN(date.getTime())) return null;

      // Get January 1st of the same year
      const yearStart = new Date(date.getFullYear(), 0, 1);
      const yearStartDay = yearStart.getDay(); // 0=Sunday, 1=Monday, etc.

      // Calculate days since Jan 1
      const daysSinceYearStart = Math.floor((date - yearStart) / (24 * 60 * 60 * 1000));

      if (returnType === 2) {
        // Week starts on Monday
        // Adjust so Monday is day 0
        const adjustedStartDay = yearStartDay === 0 ? 6 : yearStartDay - 1;
        const adjustedDays = daysSinceYearStart + adjustedStartDay;
        return Math.floor(adjustedDays / 7) + 1;
      } else {
        // Week starts on Sunday (default, returnType=1)
        const adjustedDays = daysSinceYearStart + yearStartDay;
        return Math.floor(adjustedDays / 7) + 1;
      }
    },

    EOMONTH: (startDate, months) => {
      if (!startDate) return null;
      const date = new Date(startDate);
      if (!date || isNaN(date.getTime())) return null;

      // To avoid date overflow issues, set to day 1 first
      const newDate = new Date(date);
      newDate.setDate(1);
      // Add months to get target month
      newDate.setMonth(newDate.getMonth() + (months || 0));
      // Set to first day of next month, then subtract one day to get last day of target month
      newDate.setMonth(newDate.getMonth() + 1);
      newDate.setDate(0);

      return ICDate.makeICObject(newDate.toISOString());
    },

    EDATE: (startDate, months) => {
      if (!startDate) return null;
      const date = new Date(startDate);
      if (!date || isNaN(date.getTime())) return null;

      // To handle date overflow (e.g. Jan 31 + 1 month = Feb 28/29), we need to:
      // 1. Get the target month/year
      // 2. Get the last day of that month
      // 3. Use the minimum of original day and last day of target month
      const newDate = new Date(date);
      const targetDay = newDate.getDate();
      newDate.setMonth(newDate.getMonth() + (months || 0));

      // Check if date overflowed (e.g., went from Jan 31 -> Mar 3 instead of Feb 28)
      // If so, set to last day of the previous month
      if (newDate.getDate() !== targetDay) {
        newDate.setDate(0); // Go back to last day of previous month
      }

      return ICDate.makeICObject(newDate.toISOString());
    },

    DATEVALUE: (text) => {
      if (!text) return null;
      // ICDate.parseDate already returns a Date object
      const date = ICDate.parseDate(text);
      if (!date || isNaN(date.getTime())) return null;

      // Return as IC date object
      return ICDate.makeICObject(date.toISOString());
    },

    TIMEVALUE: (text) => {
      if (!text) return null;
      // For time-only strings like "14:30", prepend today's date
      // so they can be parsed as valid datetime
      let dateStr = text;
      if (!/\d{4}/.test(text)) { // No 4-digit year
        const today = new Date().toISOString().split('T')[0];
        dateStr = `${today}T${text}`;
      }
      const date = ICDate.parseDate(dateStr);
      if (!date || isNaN(date.getTime())) return null;

      // Return as IC date object (Instacalc handles time internally)
      return ICDate.makeICObject(date.toISOString());
    },

    // + 1 for 1-based array, + 1 for next
    RNEXT: () => IC.envGetVariable("r" + (IC.envGetRowIndex() + 1 + 1)),
    RPREV: () => IC.envGetVariable("r" + (IC.envGetRowIndex() + 1 - 1)),
    ANS: () => CONFIG.PREDEFINED_FUNCTION.RPREV(),

    GOALSEEK: (targetVar, targetVal, freeVarArray) => {
      return { vars: freeVarArray, targetVar: targetVar, targetValue: targetVal, type: "goalseek" };
    },

    SET: (varMap) => {
      return { vars: varMap, type: "setvars" };
    },

    // should this be here, or in the engine function list?
    PHASOR: (mag, angle) => {
      return LIB.Complex.phasor(mag, angle);
    },



  },

  KEYWORD_FUNCTION: {
    IF: (condition, trueVal, falseVal) => IC.eval(IC.ifConditionCleanup(condition)) ? IC.eval(trueVal) : IC.eval(falseVal),
    AND: (...args) => args.every(arg => IC.eval(IC.ifConditionCleanup(arg))),
    OR: (...args) => args.some(arg => IC.eval(IC.ifConditionCleanup(arg))),
    NOT: (arg) => !IC.eval(IC.ifConditionCleanup(arg)),
  },

  /* do not wrap in parens for converions, since (rad) is a unit */
  PREDEFINED_AND_UNIT: {
    RAD: true,
    RADIAN: true,
    RADIANS: true,
  },

  POSTCONVERT_FUNCTIONS: {
    fraction: (x, denom) => {
      let [n, d] = IC.mathToFraction(IC.val(x), denom) ?? ['', ''];
      return `${n}/${d}`;
    },

    frac: "fraction",

    // @ts-ignore
    dec: (x) => IC.evalFunction("dec", [x]),
    decimal: "dec",

    // @ts-ignore
    hex: (x) => IC.evalFunction("hex", [x]),
    hexadecimal: "hex",

    // @ts-ignore
    oct: (x) => IC.evalFunction("oct", [x]),
    octal: "oct",

    // @ts-ignore
    bin: (x) => IC.evalFunction("bin", [x]),
    binary: "bin",

    value: (x) => Number(IC.val(x)),
    "plain number": "value",
    "raw number": "value",
    number: "value",
    num: "value",
    val: "value",
    unitless: "value",
    numeric: "value",
    scalar: "value",
    dimensionless: "value",
    magnitude: "value",

    percent: (x) => IC.makeICObject(IC.val(x), null, "percent"),
    percentage: "percent",
    pct: "percent",
    "%": "percent",

    unit: (x) => {
      // If x is a string, try to create a unit from it
      if (typeof x === 'string') {
        // Parse the string as a unit identifier (e.g., "meter" -> 1 meter)
        const parsed = IC.parseAndEval(x);
        if (parsed && IC.hasType(parsed)) {
          return IC.makeICObject(1, parsed.t);
        }
        // If it doesn't parse as a unit, just return the string as-is
        return x;
      }
      // Otherwise, use existing logic: extract unit from typed value
      return IC.hasType(x) ? IC.makeICObject(1, x.t) : IC.val(x);
    },
    type: "unit",

    "feet and inches": "tape",
    "tape measure": "tape",
    tape: (x) => {
      if (IC.hasType(x)) {
        IC.ICObjectSetFormat(x, "tape");
      }
      return x;
    },

    "scientific notation": "scientific",

    scientific: (x) => {
      if (IC.hasType(x)) {
        IC.ICObjectSetFormat(x, "scientific");
        return x;
      }
      else {
        // native string, gets parsed in JS
        return IC.parser.formatAsScientific(IC.val(x));
      }
    },
    "exponential": "scientific",
    "science": "scientific",
    "sci": "scientific",

    "engineering": (x) => {
      if (IC.hasType(x)) {
        IC.ICObjectSetFormat(x, "engineering");
        return x;
      }
      else {
        // native string type, gets parsed in JS
        return IC.parser.formatAsEngineering(IC.val(x));
      }
    },

    "engineer": "engineering",
    "eng": "engineering",

    // significant figures
    sigfig: (x, n) => {
      let value = IC.val(x);
      if (typeof value !== 'number' || isNaN(value)) {
        return x;
      }

      // Convert to exponential notation
      let exp = value.toExponential();
      let parts = exp.split('e');
      let mantissa = parts[0];
      let exponent = parseInt(parts[1]);

      // Round the mantissa to n significant figures
      let roundedMantissa = parseFloat(mantissa).toPrecision(n);

      // Reconstruct the number
      let result = parseFloat(roundedMantissa + 'e' + exponent);

      if (IC.hasType(x)) {
        return IC.makeICObject(result, x.t, x.f);
      } else {
        return result;
      }
    },

    "finance": (x) => {
      if (IC.hasType(x)) {
        IC.ICObjectSetFormat(x, "finance");
        return x;
      }
      else {
        return IC.makeICObject(IC.val(x), null, "finance");
      }
    },
    "financial": "finance", // Put on the thousands, millions, billions scale 
    "money": "finance",
    "scale": "scaled",
    "scaled": (x) => {
      let isCurrencyType = IC.hasType(x) && ICUnit.isCurrency(IC.typestring(x.t));
      if (isCurrencyType) {
        return CONFIG.POSTCONVERT_FUNCTIONS.finance(x);
      }

      if (IC.hasType(x)) {
        // Bytes: scale to KB/MB/GB/TB etc.
        if (IC.typeIsByte(x.t)) {
          return ICUnit.scaleBytesToBestUnit(x);
        }

        // SI units: use SI prefix scaling
        let scaled = ICUnit.scaleToBestUnit(x);
        if (IC.typestring(scaled.t) !== IC.typestring(x.t)) {
          return scaled;
        }

        // Non-SI units (cycles, etc.): apply finance formatting (K, M, B, T) with unit preserved
        return IC.makeICObject(IC.val(x), x.t, "finance");
      }

      return IC.makeICObject(IC.val(x), null, "finance");
    },

    // "as bytes" - convert to bytes and prevent auto-scaling
    "bytes": (x) => {
      // If x is already a byte type, just set the format to prevent auto-scaling
      if (IC.typeIsByte(x?.t)) {
        let bytesResult = IC.ICObjectConvertToType(x, "bytes");
        return { ...bytesResult, f: "bytes_raw" };
      }
      // If x is a plain number, make it into bytes
      if (typeof x === "number" || (IC.isICObject(x) && IC.hasNoType(x))) {
        return IC.makeICObject(IC.val(x), { bytes: 1 }, "bytes_raw");
      }
      // For other types, convert to bytes
      let bytesResult = IC.ICObjectConvertToType(x, "bytes");
      if (bytesResult) {
        return { ...bytesResult, f: "bytes_raw" };
      }
      // Fallback: just make it bytes
      return IC.makeICObject(IC.val(x), { bytes: 1 }, "bytes_raw");
    },
    "byte": "bytes",

    "roman": (x) => {
      return IC.makeICObject(IC.val(x), null, "roman");
    },

    "roman numerals": "roman",
    "roman numeral": "roman",

    noformat: (x) => {
      // string may need to be converted back to a number
      if (typeof x === "string") {
        if (typeof x == "string") {
          let n = IC.parseLocaleNumber(x);
          if (n !== null)
            return n;
        }
      }

      if (IC.format(x)) {
        IC.ICObjectSetFormat(x, "");
        return x;
      }
      else {
        // Clear formatter for this specific row only
        IC.clearRowFormat();
        return IC.val(x);
      }
    },

    si: (v) => {
      if (IC.hasNoType(v)) {
        return v;
      }

      return ICUnit.autoCollapse(v);
    },

    // SI base unit
    base: "sibase",
    metric: "sibase",
    sibase: (v) => {
      if (IC.hasNoType(v)) {
        return v;
      }

      let [baseUnits, scale] = ICUnit.convertToBaseUnits(v.t);

      // this normally does an autocollapse
      let ret = IC.makeICObject(IC.mathPreciseMultiply(IC.val(v), scale), null);
      ret.t = IC.cloneObject(baseUnits);
      return ret;
    },

    decimal_places: (x, places) => {
      let newNum = Number(IC.val(x).toFixed(places));

      if (IC.hasType(x)) {
        return IC.makeICObject(newNum, x.t, x.f);
      }
      else {
        return newNum;
      }
    },

    nearest: (x, multiple) => {
      return CONFIG.nearestNumberOrUnit(x, multiple);
    },

    // work in progress
    mixed: (x, ...args) => {
      return ICUnit.makeMixedUnit(x, ...args);
    },

    string: (x) => IC.ICStringify(x),
    stringify: "string",
    str: "string",
    parse: (x) => IC.ICParseRelaxedJSON(x),
    parsejson: "parse",

    re: (z) => LIB.Complex.re(z),
    real: "re",
    im: (z) => LIB.Complex.im(z),
    imaginary: "im",
    arg: (z) => LIB.Complex.angle(z),
    angle: "arg",
    conj: (z) => LIB.Complex.conjugate(z),
    conjugate: "conj",
    phasor: (z) => { return IC.makeICObject(z, null, "phasor"); },
    polar: "phasor",

    // === Datetime output formats ===

    // Helper to check if a type is a pure time duration (only time units, no other units)
    // Returns false for compound types like μs/test
    _isPureTimeType: (typeObj) => {
      if (!typeObj || typeof typeObj !== 'object') return false;
      const units = Object.keys(typeObj);
      // Must have at least one unit and all units must be time units
      return units.length > 0 && units.every(unit => IC.typestringIsSameUnitCategory(unit, "second"));
    },

    // Helper to convert time duration to seconds
    _timeToSeconds: (x) => {
      if (!IC.hasType(x)) return null;
      // Only convert pure time types (not compound types like μs/test)
      if (!CONFIG.POSTCONVERT_FUNCTIONS._isPureTimeType(x.t)) return null;
      // Use type conversion scale to convert to seconds
      let scale = ICUnit.typeConversionScale(x.t, { s: 1 });
      if (typeof scale !== 'number' || !isFinite(scale)) return null;
      return IC.val(x) * scale;
    },

    // Unix timestamp (seconds since 1970-01-01 UTC)
    // Returns "raw" format to prevent comma formatting (1768118400 not 1,768,118,400)
    // Returns null if can't convert, to allow fallback to unit conversion
    unix: (x) => {
      if (ICDate.isDateTimeObject(x)) {
        let value = Math.floor(new Date(IC.val(x)).getTime() / 1000);
        return IC.makeICObject(value, null, "raw");
      }
      // For pure time durations (like 3h 15m), convert to total seconds
      // Only if the type converts cleanly to seconds (no non-time units)
      if (IC.hasType(x) && IC.typeHasUnitInCategory(x.t, "second")) {
        let seconds = CONFIG.POSTCONVERT_FUNCTIONS._timeToSeconds(x);
        if (seconds !== null) return IC.makeICObject(Math.floor(seconds), null, "raw");
      }
      // Return null to allow fallback to unit conversion
      return null;
    },
    unixtime: "unix",
    epoch: "unix",
    timestamp: "unix",
    posix: "unix",

    // Milliseconds since epoch (JavaScript's Date.now() format)
    // Returns "raw" format to prevent comma formatting
    // Returns null if can't convert, to allow fallback to unit conversion (e.g., "μs in ms")
    ms: (x) => {
      if (ICDate.isDateTimeObject(x)) {
        let value = new Date(IC.val(x)).getTime();
        return IC.makeICObject(value, null, "raw");
      }
      // For pure time durations (like 3h 15m), convert to total milliseconds
      // Only if the type converts cleanly to seconds (no non-time units)
      if (IC.hasType(x) && IC.typeHasUnitInCategory(x.t, "second")) {
        let seconds = CONFIG.POSTCONVERT_FUNCTIONS._timeToSeconds(x);
        if (seconds !== null) return IC.makeICObject(Math.floor(seconds * 1000), null, "raw");
      }
      // Return null to allow fallback to unit conversion
      return null;
    },
    milliseconds: "ms",
    unixmilli: "ms",
    epochms: "ms",
    jstime: "ms",

    // ISO 8601 format
    iso: (x) => {
      if (ICDate.isDateTimeObject(x)) {
        return IC.val(x); // Already ISO format
      }
      // For unix timestamp numbers, convert to ISO
      if (!IC.hasType(x) && typeof IC.val(x) === "number") {
        let ts = IC.val(x);
        // Auto-detect milliseconds (if > year 3000 in seconds)
        if (ts > 32503680000) {
          ts = ts / 1000;
        }
        return new Date(ts * 1000).toISOString();
      }
      return x;
    },
    iso8601: "iso",
    isoformat: "iso",

    // ISO date only (YYYY-MM-DD)
    iso_date: (x) => {
      if (ICDate.isDateTimeObject(x)) {
        return IC.val(x).split("T")[0];
      }
      if (!IC.hasType(x) && typeof IC.val(x) === "number") {
        let ts = IC.val(x);
        if (ts > 32503680000) ts = ts / 1000;
        return new Date(ts * 1000).toISOString().split("T")[0];
      }
      return x;
    },
    isodate: "iso_date",
    date_iso: "iso_date",

    // ISO time only (HH:MM:SS)
    iso_time: (x) => {
      if (ICDate.isDateTimeObject(x)) {
        let isoStr = IC.val(x);
        let timePart = isoStr.split("T")[1];
        return timePart ? timePart.split(".")[0].replace("Z", "") : x;
      }
      if (!IC.hasType(x) && typeof IC.val(x) === "number") {
        let ts = IC.val(x);
        if (ts > 32503680000) ts = ts / 1000;
        let isoStr = new Date(ts * 1000).toISOString();
        let timePart = isoStr.split("T")[1];
        return timePart ? timePart.split(".")[0].replace("Z", "") : x;
      }
      return x;
    },
    isotime: "iso_time",
    time_iso: "iso_time",

    // Sortable format (YYYY-MM-DD HH:MM:SS) - good for databases
    sortable: (x) => {
      if (ICDate.isDateTimeObject(x)) {
        let isoStr = IC.val(x);
        return isoStr.replace("T", " ").split(".")[0].replace("Z", "");
      }
      if (!IC.hasType(x) && typeof IC.val(x) === "number") {
        let ts = IC.val(x);
        if (ts > 32503680000) ts = ts / 1000;
        let isoStr = new Date(ts * 1000).toISOString();
        return isoStr.replace("T", " ").split(".")[0].replace("Z", "");
      }
      return x;
    },
    sql: "sortable",
    mysql: "sortable",

    // Filename-safe format (YYYYMMDD_HHMMSS)
    filestamp: (x) => {
      if (ICDate.isDateTimeObject(x)) {
        let isoStr = IC.val(x);
        let date = isoStr.split("T")[0].replace(/-/g, "");
        let time = isoStr.split("T")[1].split(".")[0].replace(/:/g, "").replace("Z", "");
        return date + "_" + time;
      }
      if (!IC.hasType(x) && typeof IC.val(x) === "number") {
        let ts = IC.val(x);
        if (ts > 32503680000) ts = ts / 1000;
        let isoStr = new Date(ts * 1000).toISOString();
        let date = isoStr.split("T")[0].replace(/-/g, "");
        let time = isoStr.split("T")[1].split(".")[0].replace(/:/g, "").replace("Z", "");
        return date + "_" + time;
      }
      return x;
    },
    filename: "filestamp",
    filesafe: "filestamp",

    // Relative time (e.g., "3 hours ago", "in 2 days")
    relative: (x) => {
      if (ICDate.isDateTimeObject(x)) {
        let date = new Date(IC.val(x));
        let now = new Date();
        let diffMs = date.getTime() - now.getTime();
        let diffSec = Math.round(diffMs / 1000);
        let absSec = Math.abs(diffSec);

        let value, unit;
        if (absSec < 60) {
          value = absSec;
          unit = value === 1 ? "second" : "seconds";
        } else if (absSec < 3600) {
          value = Math.round(absSec / 60);
          unit = value === 1 ? "minute" : "minutes";
        } else if (absSec < 86400) {
          value = Math.round(absSec / 3600);
          unit = value === 1 ? "hour" : "hours";
        } else if (absSec < 2592000) {
          value = Math.round(absSec / 86400);
          unit = value === 1 ? "day" : "days";
        } else if (absSec < 31536000) {
          value = Math.round(absSec / 2592000);
          unit = value === 1 ? "month" : "months";
        } else {
          value = Math.round(absSec / 31536000);
          unit = value === 1 ? "year" : "years";
        }

        if (diffSec < 0) {
          return `${value} ${unit} ago`;
        } else if (diffSec > 0) {
          return `in ${value} ${unit}`;
        } else {
          return "just now";
        }
      }
      return x;
    },
    ago: "relative",
    fromnow: "relative",
    timeago: "relative",
  },

  POSTCONVERT_FUNCTION_REGEX: {
    fraction: {
      regex: /fraction\s*[/]\s*(\d+)/,
      f: (x, match) => {
        let denom = parseInt(match[1]);
        return CONFIG.POSTCONVERT_FUNCTIONS.fraction(x, denom);
      }
    },

    decimal_places: {
      regex: /(\d+)\s+(decimal\s*places|decimals?|digits?|places?)/,
      f: (x, match) => {
        let places = parseInt(match[1]);
        return CONFIG.POSTCONVERT_FUNCTIONS.decimal_places(x, places);
      }
    },

    nearest: {
      regex: /(nearest|closest)\s+(\S+)/,
      f: (x, match) => {
        let multiple = IC.parseAndEval(match[2]);
        if (typeof multiple != "number") {
          multiple = CONFIG.nearestNumberScale(match[2]);
        }

        if (typeof multiple == "number") {
          return CONFIG.nearestNumberOrUnit(x, multiple);
        }
        IC.log(`couldn't round to nearest ${match[2]}`);
        return x;
      }
    },

    sigfig: {
      regex: /(\d+)\s+(?:sig(?:nificant)?\s+(?:fig(?:ure)?s?|digits?)|sigfig(?:s)?)/i,
      f: (x, match) => {
        let n = parseInt(match[1]);
        return CONFIG.POSTCONVERT_FUNCTIONS.sigfig(x, n);
      }
    },
  },

  SUFFIX_DICT: {
    squared: (val) => IC.mathPow(val, 2),
    cubed: (val) => IC.mathPow(val, 3),
    inverse: (val) => IC.mathPow(val, -1),
    invert: (val) => IC.mathPow(val, -1),

    // TODO: make some of these use the POSTCONVERT functions
    unitless: (val) => Number(IC.val(val)),
  },

  PREFIX_DICT: {
    // longer keys first (longer match wins)
    //    "square root of": "sqrt",
    //    "sqrt root of": "sqrt",
    "square root": "sqrt",
    //    "root of": "sqrt",
    "root": "sqrt",
    sqrt: (val) => IC.mathPow(val, 0.5),

    //   "cube root of": "cuberoot",
    "cube root": "cuberoot",
    "cbrt": "cuberoot",
    cuberoot: (val) => IC.mathPow(val, 1 / 3),

    //"square": (x) => IC.pow(x, 2),
    //"cube": (x) => IC.pow(x, 3),

    invert: (val) => IC.mathPow(val, -1),
    inverse: "invert",
    reciprocal: "invert"
  },

  UNIT_AWARE_FUNCTIONS: {
    SQUARE: (v) => IC.mathPow(v, 2),
    CUBE: (v) => IC.mathPow(v, 3),
    CUBIC: (v) => IC.mathPow(v, 3),
    SQRT: (v) => IC.mathPow(v, 0.5),
    CBRT: (v) => IC.mathPow(v, 1 / 3),
    INVERT: (v) => IC.mathPow(v, -1),
    INVERSE: (v) => IC.mathPow(v, -1),
    RECIPROCAL: (v) => IC.mathPow(v, -1),
    NEGATE: (v) => IC.mathMul(-1, v),
    ROUND: (v) => IC.makeICObject(Math.round(IC.val(v)), v.t),
    ROUNDDOWN: (v) => IC.makeICObject(Math.floor(IC.val(v)), v.t),
    FLOOR: (v) => IC.makeICObject(Math.floor(IC.val(v)), v.t),
    CEIL: (v) => IC.makeICObject(Math.ceil(IC.val(v)), v.t),
    ROUNDUP: (v) => IC.makeICObject(Math.ceil(IC.val(v)), v.t),
    UNIT: (v) => CONFIG.POSTCONVERT_FUNCTIONS.unit(v),
    VALUE: (v) => CONFIG.POSTCONVERT_FUNCTIONS.value(v),
    TIME: (v) => {
      let seconds = IC.val(v);
      // if not same type as second, convert to second if possible
      if (IC.hasType(v)) {
        seconds = IC.val(IC.ICObjectConvertToType(v, "s"));
      }

      return IC.CALC.fn()["time"](seconds);
    },
  },

  // these functions actually get help messages
  OTHER_FUNCTIONS: {
    CHART: (...args) => {
      return Chart.getChartData(args[0], Chart.LINE, args[1]);
    },

    BARCHART: (...args) => {
      return Chart.getChartData(args[0], Chart.BAR, args[1]);
    },

    LINECHART: (...args) => {
      return Chart.getChartData(args[0], Chart.LINE, args[1]);
    },

    PIECHART: (...args) => {
      return Chart.getChartData(args[0], Chart.PIE, args[1]);
    },

    HISTOGRAM: (data, bins) => {
      const dataArr = Array.isArray(data) ? data.map(v => IC.val(v)) : [IC.val(data)];
      const binsArg = IC.val(bins);

      // Calculate bin edges
      let binEdges;
      const minVal = Math.min(...dataArr);
      const maxVal = Math.max(...dataArr);

      if (typeof binsArg === 'number') {
        // Auto-generate N equal-width bins
        const width = (maxVal - minVal) / binsArg;
        binEdges = Array.from({ length: binsArg }, (_, i) => minVal + width * (i + 1));
      } else if (Array.isArray(binsArg)) {
        binEdges = binsArg.map(v => IC.val(v));
      } else {
        // Default to 10 bins
        const width = (maxVal - minVal) / 10;
        binEdges = Array.from({ length: 10 }, (_, i) => minVal + width * (i + 1));
      }

      // Count values in each bin (Excel-style: ≤ edge)
      const counts = new Array(binEdges.length + 1).fill(0);
      for (const val of dataArr) {
        let placed = false;
        for (let i = 0; i < binEdges.length; i++) {
          if (val <= binEdges[i]) {
            counts[i]++;
            placed = true;
            break;
          }
        }
        if (!placed) counts[binEdges.length]++; // overflow bin
      }

      // Build chart data with labels
      // Format: "≤X" (up to and including X) - compact Excel-style
      const chartData = [];
      const fmt = (n) => Number.isInteger(n) ? String(n) : n.toFixed(1);

      for (let i = 0; i < binEdges.length; i++) {
        chartData.push({
          label: `≤${fmt(binEdges[i])}`,
          value: counts[i]
        });
      }

      // Add overflow bin only if it has values
      if (counts[binEdges.length] > 0) {
        chartData.push({
          label: `>${fmt(binEdges[binEdges.length - 1])}`,
          value: counts[binEdges.length]
        });
      }

      return Chart.getChartData(chartData, Chart.BAR);
    },
  },

  // custom functions better done here, vs in instacalc.engine.js
  ENGLISH_FUNCTION: {
    LOAN: (v, rate, term) => {
      return ICLib.loan(v, rate, term);
    },

    INVEST: (v, rate, term) => {
      return ICLib.invest(v, rate, term);
    },

    INTEREST: (v, rate, term) => {
      return ICLib.interest(v, rate, term);
    },

    TOTALPAYMENT: (v, rate, term) => {
      return ICLib.totalPayment(v, rate, term);
    },

    SAVINGSGOAL: (v, rate, term) => {
      return ICLib.savingsGoal(v, rate, term);
    },

    PRESENTVALUE: (v, rate, term) => {
      return ICLib.presentValue(v, rate, term);
    },
  },

  UNIT_HELP_MSG: {
    kb: "(2^10 bytes), see also: kb_si (thousand bytes)",
    mb: "(2^20 bytes), see also: mb_si (million bytes)",
    gb: "(2^30 bytes), see also: gb_si (billion bytes)",
    tb: "(2^40 bytes), see also: tb_si (trillion bytes)",
    pb: "(2^50 bytes), see also: pb_si (quadrillion bytes)",
    eb: "(2^60 bytes), see also: eb_si (1e18 bytes)",
    zb: "(2^70 bytes), see also: zb_si (1e21 bytes)",
    yb: "(2^80 bytes), see also: yb_si (1e24 bytes)",
    rb: "(2^90 bytes), see also: rb_si (1e27 bytes)",
    qb: "(2^100 bytes), see also: qb_si (1e30 bytes)",

    kb_si: "(thousand bytes)",
    mb_si: "(million bytes)",
    gb_si: "(billion bytes)",
    tb_si: "(trillion bytes)",
    pb_si: "(quadrillion bytes)",
    eb_si: "(1e18 bytes)",
    zb_si: "(1e21 bytes)",
    yb_si: "(1e24 bytes)",
    rb_si: "(1e27 bytes)",
    qb_si: "(1e30 bytes)",

    kib: "alias:kb",
    mib: "alias:mb",
    gib: "alias:gb",
    tib: "alias:tb",
    pib: "alias:pb",
    eib: "alias:eb",
    zib: "alias:zb",
    yib: "alias:yb",
    rib: "alias:rb",
    qib: "alias:qb",

    kibibyte: "alias:kb",
    mebibyte: "alias:mb",
    gibibyte: "alias:gb",
    tebibyte: "alias:tb",
    pebibyte: "alias:pb",
    exbibyte: "alias:eb",
    zebibyte: "alias:zb",
    yobibyte: "alias:yb",
    ronnibyte: "alias:rb",
    quettibyte: "alias:qb",

    kilobyte: "alias:kb",
    megabyte: "alias:mb",
    gigabyte: "alias:gb",
    terabyte: "alias:tb",
    petabyte: "alias:pb",
    exabyte: "alias:eb",
    zettabyte: "alias:zb",
    yottabyte: "alias:yb",
    ronnabyte: "alias:rb",
    quettabyte: "alias:qb",

    kilobytes: "alias:kb",
    megabytes: "alias:mb",
    gigabytes: "alias:gb",
    terabytes: "alias:tb",
    petabytes: "alias:pb",
    exabytes: "alias:eb",
    zettabytes: "alias:zb",
    yottabytes: "alias:yb",
    ronnabytes: "alias:rb",
    quettabytes: "alias:qb",

    kilobit: "(2^10 bits)",
    megabit: "(2^20 bits)",
    gigabit: "(2^30 bits)",
    terabit: "(2^40 bits)",
    petabit: "(2^50 bits)",
    exabit: "(2^60 bits)",
    zettabit: "(2^70 bits)",
    yottabit: "(2^80 bits)",
    ronnabit: "(2^90 bits)",
    quettabit: "(2^100 bits)",

    kilobits: "(2^10 bits)",
    megabits: "(2^20 bits)",
    gigabits: "(2^30 bits)",
    terabits: "(2^40 bits)",
    petabits: "(2^50 bits)",
    exabits: "(2^60 bits)",
    zettabits: "(2^70 bits)",
    yottabits: "(2^80 bits)",
    ronnabits: "(2^90 bits)",
    quettabits: "(2^100 bits)",

    kbit: "alias:kilobit",
    mbit: "alias:megabit",
    gbit: "alias:gigabit",
    tbit: "alias:terabit",
    pbit: "alias:petabit",
    ebit: "alias:exabit",
    zbit: "alias:zettabit",
    ybit: "alias:yottabit",
    rbit: "alias:ronnabit",
    qbit: "alias:quettabit",

    kbits: "alias:kilobit",
    mbits: "alias:megabit",
    gbits: "alias:gigabit",
    tbits: "alias:terabit",
    pbits: "alias:petabit",
    ebits: "alias:exabit",
    zbits: "alias:zettabit",
    ybits: "alias:yottabit",
    rbits: "alias:ronnabit",
    qbits: "alias:quettabit",

    kbps: "(thousand bits/sec), see also: KBps (bytes/sec)",
    mbps: "(million bits/sec), see also: MBps (bytes/sec)",
    gbps: "(billion bits/sec), see also: GBps (bytes/sec)",
    tbps: "(trillion bits/sec), see also: TBps (bytes/sec)",
    pbps: "(quadrillion bits/sec), see also: PBps (bytes/sec)",
    ebps: "(1e18 bits/sec), see also: EBps (bytes/sec)",
    zbps: "(1e21 bits/sec), see also: ZBps (bytes/sec)",
    ybps: "(1e24 bits/sec), see also: YBps (bytes/sec)",
    rbps: "(1e27 bits/sec), see also: RBps (bytes/sec)",
    qbps: "(1e30 bits/sec), see also: QBps (bytes/sec)",

    KBps: "(thousand bytes/sec), see also: kbps (bits/sec)",
    MBps: "(million bytes/sec), see also: mbps (bits/sec)",
    GBps: "(billion bytes/sec), see also: gbps (bits/sec)",
    TBps: "(trillion bytes/sec), see also: tbps (bits/sec)",
    PBps: "(quadrillion bytes/sec), see also: pbps (bits/sec)",
    EBps: "(1e18 bytes/sec), see also: ebps (bits/sec)",
    ZBps: "(1e21 bytes/sec), see also: zbps (bits/sec)",
    YBps: "(1e24 bytes/sec), see also: ybps (bits/sec)",
    RBps: "(1e27 bytes/sec), see also: rbps (bits/sec)",
    QBps: "(1e30 bytes/sec), see also: qbps (bits/sec)",

    flop: "(op, flops = ops/sec)",
    flops: "(ops/sec, flop=op count)",
    mflop: "(million ops, mflops=ops/sec)",
    mflops: "(million ops/sec, mflop=ops count)",
    gflop: "(billion ops, gflops=ops/sec)",
    gflops: "(billion ops/sec, gflop=ops count)",
    tflop: "(trillion ops, tflops=ops/sec)",
    tflops: "(trillion ops/sec, tflop=ops count)",
    pflop: "(1e15 ops, pflops=ops/sec)",
    pflops: "(1e15 ops/sec, pflop=ops count)",
    eflop: "(1e18 ops, eflops=ops/sec)",
    eflops: "(1e18 ops/sec, eflop=ops count)",
    zflop: "(1e21 ops, zflops=ops/sec)",
    zflops: "(1e21 ops/sec, zflop=ops count)",
    yflop: "(1e24 ops, yflops=ops/sec)",
    yflops: "(1e24 ops/sec, yflop=ops count)",

    // Unit help is lowercase first
    m: "(m=meter, M=million)",
    mw: "(megawatt, mW=milliwatt)", // covers mw, MW, Mw
    mW: "(milliwatt, MW=megawatt)",

    f: "(Fahrenheit), see also: Farad",
    c: "(Celsius), see also: Coulomb, c_light",

    g: "(gram), see also: gravity [SmallG], g_const [BigG]",

    i: "(imaginary unit), √-1",

    cal: "(calorie [small] = 4.184 J)",
    Cal: "(Calorie [big] = kcal = 4184 J)",

    time: "(time formats hh:mm, hh:mm:ss, 1y 2d 3h 4m 5s)",

    mil: "(mil = 1/1000 inch, see: mil_nato [angle])",

    //    dollar: "(generic money label, use USD for currency conversions)",
    yen: "(JPY, Japanese Yen)",
    euro: "(EUR, Euro)",
    yuan: "(CNY, Chinese Yuan)",
    rupee: "(INR, Indian Rupee)",
    peso: "(MXN, Mexican Peso)",
    ruble: "(RUB, Russian Ruble)",
    ringgit: "(MYR, Malaysian Ringgit)",
    won: "(KRW, South Korean Won)",
    loonie: "(CAD, Canadian Dollar)",
    aussie: "(AUD, Australian Dollar)",
    baht: "(THB, Thai Baht)",
    dirham: "(AED, United Arab Emirates Dirham)",
    lira: "(TRY, Turkish Lira)",
    riyal: "(SAR, Saudi Riyal)",
    lev: "(BGN, Bulgarian Lev)",
    zloty: "(PLN, Polish Zloty)",
    krona: "(SEK, Swedish Krona)",
    franc: "(CHF, Swiss Franc)",
    quetzal: "(GTQ Guatemalan Quetzal)",
    shekel: "(ILS, Israeli Shekel)",
    dong: "(VND, Vietnamese Dong)",
    dinar: "(KWD, Kuwaiti Dinar)",
    kuna: "(HRK, Croatian Kuna)",
    birr: "(ETB, Ethiopian Birr)",
    taka: "(BDT, Bangladeshi Taka)",
    forint: "(HUF, Hungarian Forint)",
    rupiah: "(IDR, Indonesian Rupiah)",
    kyat: "(MMK, Burmese Kyat)",
    naira: "(NGN, Nigerian Naira)",
    bolivar: "(VES, Venezuelan Bolivar)",
  },

  getUnitHelpMessage(unit, compact = false) {
    let message = this.UNIT_HELP_MSG[unit] || this.UNIT_HELP_MSG[unit.toLowerCase()] || null;

    // alias lookup
    if (message && message.startsWith("alias:")) {
      const aliasKey = message.split(":")[1];
      message = this.UNIT_HELP_MSG[aliasKey];
    }

    // Check FLEX_UNIT_ALIAS (e.g. petaflop) 
    if (!message) {
      const flexUnit = this.FLEX_UNIT_ALIAS[unit.toLowerCase()];
      if (flexUnit) {
        const { scale, type } = flexUnit;
        const formattedScale = scale > 1000 ? scale.toExponential().replace("+", "") : scale;
        message = `(${formattedScale} ${type})`;
      }
    }

    if (message && compact) {
      // take everything up to the first )
      message = message.split(")")[0] + ")";
    }

    return message;
  },

  FUNCTION_HELP_MSG: {
    sin: "sin uses deg, for radians use sin(2pi rad)",
    cos: "cos uses deg, for radians use cos(2pi rad)",
    tan: "tan uses deg, for radians use tan(2pi rad)",
    sec: "sec uses deg, for radians use sec(2pi rad)",
    csc: "csc uses deg, for radians use csc(2pi rad)",
    cot: "cot uses deg, for radians use cot(2pi rad)",
  },

  FUNCTION_USAGE_MSG: {

    "hex": "hex(x) | convert x to hexadecimal, hex(255) = 0xff",
    "oct": "oct(x) | convert x to octal, oct(255) = 0377",
    "bin": "bin(x) | convert x to binary, bin(255) = 0b11111111",
    "dec": "dec(x) | convert x to decimal, dec(0xFF) = 255",

    if: "if (condition) { result } else { result } | if (age >= 18) { \"adult\" } else { \"child\" } \n IF(condition, true result, false result) | IF(age >= 18, \"adult\", \"child\") // excel-style",

    sin: "sin(x) | sine of x (degrees) | sin(2pi rad) for radians",
    cos: "cos(x) | cosine of x (degrees) | cos(2pi rad) for radians",
    tan: "tan(x) | tangent of x (degrees) | tan(2pi rad) for radians",
    sec: "sec(x) | secant of x (degrees) | sec(2pi rad) for radians",
    csc: "csc(x) | cosecant of x (degrees) | csc(2pi rad) for radians",
    cot: "cot(x) | cotangent of x (degrees) | cot(2pi rad) for radians",

    asin: "asin(x) | arcsine of x (result in degrees)",
    acos: "acos(x) | arccosine of x (result in degrees)",
    atan: "atan(x) | arctangent of x (result in degrees)",
    atan2: "atan2(y, x) | arctangent of y/x (result in degrees)",
    asec: "asec(x) | arcsecant of x (result in degrees)",
    acsc: "acsc(x) | arccosecant of x (result in degrees)",
    acot: "acot(x) | arccotangent of x (result in degrees)",

    sinh: "sinh(x) | hyperbolic sine",
    cosh: "cosh(x) | hyperbolic cosine",
    tanh: "tanh(x) | hyperbolic tangent",
    sech: "sech(x) | hyperbolic secant",
    csch: "csch(x) | hyperbolic cosecant",
    coth: "coth(x) | hyperbolic cotangent",
    asinh: "asinh(x) | hyperbolic arcsine of x",
    acosh: "acosh(x) | hyperbolic arccosine of x",
    atanh: "atanh(x) | hyperbolic arctangent of x",
    asech: "asech(x) | hyperbolic arcsecant of x",
    acsch: "acsch(x) | hyperbolic arccosecant of x",
    acoth: "acoth(x) | hyperbolic arccotangent of x",

    re: "re(z) | real part of complex number",
    im: "im(z) | imaginary part of complex number",
    arg: "arg(z) | angle of complex number (radians)",
    angle: "angle(z) | angle of complex number (radians)",
    phasor: "phasor(magnitude, angle) | build complex number (default degrees) | phasor(3, 90), phasor(2, pi/2 rad)",

    abs: "abs(x) | absolute value of x",
    ceil: "ceil(x) | ceiling of x",
    floor: "floor(x) | floor of x",
    round: "round(x, [decimal places]) | rounds number: round(pi) = 3, round(pi, 2) = 3.14, round(pi, .01) = 3.14",
    roundup: "roundup(x) | round up to nearest integer",
    rounddown: "rounddown(x) | round down to nearest integer",
    trunc: "trunc(x) | truncate to integer",
    int: "int(x) | truncate to integer",
    integer: "integer(x) | truncate to integer",

    hypot: "hypot(x, y) | sqrt(x^2 + y^2)",
    exp: "exp(x) | e^x",
    ln: "ln(x) | natural log of x",
    log: "log(x) | log base 10 of x",
    log2: "log2(x) | log base 2 of x",
    log_2: "log2(x) | log base 2 of x",
    log10: "log10(x) | log base 10 of x",
    log_10: "log10(x) | log base 10 of x",
    sqrt: "sqrt(x) | square root of x",
    cbrt: "cuberoot(x) | cube root of x",
    cuberoot: "cuberoot(x) | cube root of x",
    pow: "pow(x, y) | x to power y: x^y",
    root: "root(x) | square root of x",

    time: "time(seconds) | convert seconds to HMS, time(10000) = 2h 46m 40s",

    mod: "mod(x, y) | x modulo (remainder) y",
    gcd: "gcd(x, y) | greatest common divisor of x and y",
    lcm: "lcm(x, y) | least common multiple of x and y",
    sign: "sign(x) | sign of x (-1, 0, or 1)",
    clamp: "clamp(x, min, max) | min <= x <= max",
    erf: "erf(x) | error function",
    erfinverse: "erfinverse(x) | inverse error function",
    gamma: "gamma(x) | gamma function",

    square: "square(x) | x^2",
    cube: "cube(x) | x^3",

    max: "max(r1:r3) or max(x, y, ...) | maximum of values",
    min: "min(r1:r3) or min(x, y, ...) | minimum of values",
    avg: "avg(r1:r3) or avg(x, y, ...) | average of values",
    sum: "sum(r1:r3) or sum(x, y, ...) | sum of values",
    total: "total(r1:r3) or total(x, y, ...) | sum of values",
    average: "average(r1:r3) or average(x, y, ...) | average of values",
    product: "product(r1:r3) or product(x, y, ...) | product of values",
    sub: "sub(x, y) | subtract y from x",
    subtract: "subtract(x, y) | subtract y from x",
    divide: "divide(x, y) | divide x by y",
    quotient: "quotient(x, y) | divide x by y",

    mean: "mean(r1:r3) or mean(x, y, ...) | mean of values",
    mode: "mode(r1:r3) or mode(x, y, ...) | mode of values",
    median: "median(r1:r3) or median(x, y, ...) | median of values",
    geomean: "geomean(r1:r3) or geomean(x, y, ...) | geometric mean of values",

    var: "var(r1:r3) or var(x, y, ...) | sample variance of values",
    variance: "variance(r1:r3) or variance(x, y, ...) | sample variance of values",

    "var.s": "var.s(r1:r3) or var.s(x, y, ...) | sample variance of values",
    "var.p": "var.p(r1:r3) or var.p(x, y, ...) | population variance of values",

    covar: "covar(r1:r3) or var(x, y, ...) | population covariance of values",
    covariance: "covariance(r1:r3) or variance(x, y, ...) | population covariance of values",

    "covar.s": "var.s(r1:r3) or var.s(x, y, ...) | sample variance of values",
    "covar.p": "var.p(r1:r3) or var.p(x, y, ...) | population variance of values",
    "covariance.s": "var.s(r1:r3) or var.s(x, y, ...) | sample variance of values",
    "covariance.p": "var.p(r1:r3) or var.p(x, y, ...) | population variance of values",

    slope: "slope(known_y's, known_x's) | slope of the linear regression line (Excel)",
    intercept: "intercept(known_y's, known_x's) | y-intercept of the linear regression line (Excel)",
    correl: "correl(array1, array2) | correlation coefficient between two data sets (Excel)",
    linest: "linest(known_y's, known_x's, [const], [stats]) | linear regression line (Excel)",
    forecast: "forecast(x, known_y's, known_x's) | forecast value on the linear regression line (Excel)",
    confidence: "confidence(alpha, standard_dev, size) | confidence interval for population mean (Excel)",

    stddev: "stddev(r1:r3) or stddev(x, y, ...) | sample standard deviation of values",
    stdev: "stdev(r1:r3) or stddev(x, y, ...) | sample standard deviation of values",

    "stdev.s": "stdev.s(r1:r3) or stdev.s(x, y, ...) | sample standard deviation of values",
    "stdev.p": "stdev.p(r1:r3) or stdev.p(x, y, ...) | population standard deviation of values",

    kurt: "kurtosis(r1:r3) | sample kurtosis of values (Excel)",
    kurtosis: "kurtosis(r1:r3) | sample kurtosis of values (Excel)",
    skewness: "skew(array) | sample skewness of values (Excel)",
    skew: "skew(array) | sample skewness of values (Excel)",

    "norm.s.inv": "norm.s.inv(p) | inverse of standard normal cumulative distribution function (Excel)",
    "normsinv": "norm.s.inv(p) | inverse of standard normal cumulative distribution function (Excel)",
    "norminv": "norm.inv(p) | inverse of normal cumulative distribution function (Excel)",
    "norm.dist": "norm.dist(x, mean, standard_dev, cumulative) | normal distribution function (Excel)",
    "normdist": "norm.dist(x, mean, standard_dev, cumulative) | normal distribution function (Excel)",
    "norm.s.dist": "norm.s.dist(z, cumulative) | standard normal distribution function (Excel)",
    "normsdist": "norm.s.dist(z, cumulative) | standard normal distribution function (Excel)",
    "t.inv": "t.inv(probability, degrees_freedom) | inverse of Student's t-distribution (Excel)",
    "tinv": "t.inv(probability, degrees_freedom) | inverse of Student's t-distribution (Excel)",
    "t.dist": "t.dist(x, degrees_freedom, cumulative) | Student's t-distribution function (Excel)",
    "tdist": "t.dist(x, degrees_freedom, cumulative) | Student's t-distribution function (Excel)",
    "chisq.dist": "chisq.dist(x, degrees_freedom, cumulative) | chi-square distribution function (Excel)",
    "chisqdist": "chisq.dist(x, degrees_freedom, cumulative) | chi-square distribution function (Excel)",
    "f.dist": "f.dist(x, degrees_freedom1, degrees_freedom2, cumulative) | F-distribution function (Excel)",
    "fdist": "f.dist(x, degrees_freedom1, degrees_freedom2, cumulative) | F-distribution function (Excel)",
    "z.test": "z.test(array, x, [sigma]) | z-test for mean (Excel)",
    "ztest": "z.test(array, x, [sigma]) | z-test for mean (Excel)",
    "t.test": "t.test(array1, array2, [tails], [type]) | t-test for means (Excel)",
    "ttest": "t.test(array1, array2, [tails], [type]) | t-test for means (Excel)",

    "t.dist.2t": "t.dist.2t(x, degrees_freedom) | two-tailed Student's t-distribution function (Excel)",
    "tdist2t": "t.dist.2t(x, degrees_freedom) | two-tailed Student's t-distribution function (Excel)",
    "t.dist.rt": "t.dist.rt(x, degrees_freedom) | right-tailed Student's t-distribution function (Excel)",
    "tdistrt": "t.dist.rt(x, degrees_freedom) | right-tailed Student's t-distribution function (Excel)",
    "t.inv.2t": "t.inv.2t(probability, degrees_freedom) | two-tailed inverse of Student's t-distribution (Excel)",
    "tinv2t": "t.inv.2t(probability, degrees_freedom) | two-tailed inverse of Student's t-distribution (Excel)",
    "chisq.dist.rt": "chisq.dist.rt(x, degrees_freedom) | right-tailed chi-square distribution function (Excel)",
    "chisqdistrt": "chisq.dist.rt(x, degrees_freedom) | right-tailed chi-square distribution function (Excel)",
    "chisq.inv": "chisq.inv(probability, degrees_freedom) | inverse of chi-square distribution function (Excel)",
    "chisqinv": "chisq.inv(probability, degrees_freedom) | inverse of chi-square distribution function (Excel)",
    "chisq.inv.rt": "chisq.inv.rt(probability, degrees_freedom) | right-tailed inverse of chi-square distribution function (Excel)",
    "chisqinvrt": "chisq.inv.rt(probability, degrees_freedom) | right-tailed inverse of chi-square distribution function (Excel)",
    "f.dist.rt": "f.dist.rt(x, degrees_freedom1, degrees_freedom2) | right-tailed F-distribution function (Excel)",
    "fdistrt": "f.dist.rt(x, degrees_freedom1, degrees_freedom2) | right-tailed F-distribution function (Excel)",
    "f.inv": "f.inv(probability, degrees_freedom1, degrees_freedom2) | inverse of F-distribution function (Excel)",
    "finv": "f.inv(probability, degrees_freedom1, degrees_freedom2) | inverse of F-distribution function (Excel)",
    "f.inv.rt": "f.inv.rt(probability, degrees_freedom1, degrees_freedom2) | right-tailed inverse of F-distribution function (Excel)",
    "finvrt": "f.inv.rt(probability, degrees_freedom1, degrees_freedom2) | right-tailed inverse of F-distribution function (Excel)",

    "binom.dist": "binom.dist(x, trials, probability_s, cumulative) | binomial distribution function (Excel)",
    "binomdist": "binom.dist(x, trials, probability_s, cumulative) | binomial distribution function (Excel)",
    "negbinom.dist": "negbinom.dist(x, r, p, cumulative) | negative binomial distribution function (Excel)",
    "negbinomdist": "negbinom.dist(x, r, p, cumulative) | negative binomial distribution function (Excel)",
    "weibull.dist": "weibull.dist(x, alpha, beta, cumulative) | Weibull distribution function (Excel)",
    "weibulldist": "weibull.dist(x, alpha, beta, cumulative) | Weibull distribution function (Excel)",
    "poisson.dist": "poisson.dist(x, mean, cumulative) | Poisson distribution function (Excel)",
    "poissondist": "poisson.dist(x, mean, cumulative) | Poisson distribution function (Excel)",
    "gamma.dist": "gamma.dist(x, alpha, beta, cumulative) | gamma distribution function (Excel)",
    "gammadist": "gamma.dist(x, alpha, beta, cumulative) | gamma distribution function (Excel)",

    range: "range(r1:r3) or range(x, y, ...) | range of values",
    count: "count(r1:r3) or count(x, y, ...) | count of values",
    percentile: "percentile(r1:r3, p) or percentile(x, y, ..., p) | pth percentile of values",
    percentrank: "percentrank(r1:r3, x, [sig (optional)]) or percentrank(x, y, ..., x) | percent rank of x in values, to [sig] digits (Excel)",
    sort: "sort(r1:r3) or sort(x, y, ...) | sort values in ascending order",
    reverse: "reverse(r1:r3) or reverse(x, y, ...) | reverse array of values",
    sequence: "sequence(count, [start], [step]) or sequence({count:, start:, step:}) | generate sequence | sequence(5) = [1,2,3,4,5]",
    seq: "seq(count, [start], [step]) or seq({count:, start:, step:}) | generate sequence | seq(5) = [1,2,3,4,5]",

    combin: "combination(n, k) | n choose k",
    combination: "combination(n, k) | n choose k",
    c: "combination(n, k) | n choose k",
    permut: "permutation(n, k) | n permute k",
    permutation: "permutation(n, k) | n permute k",
    p: "permutation(n, k) | n permute k",

    // excel functions
    pmt: "PMT(rate, nper, pv, [fv (optional)], [type]) | loan payment (Excel)",
    ipmt: "IPMT(rate, per, nper, pv, [fv (optional)], [type]) | interest payment (Excel)",
    ppmt: "PPMT(rate, per, nper, pv, [fv (optional)], [type]) | payment on the principal (Excel)",

    ispmt: "ISPMT(rate, per, nper, pv) | interest payment specific period (Excel)",

    pv: "PV(rate, nper, pmt, [fv (optional)], [type]) | present value of an investment (Excel)",
    fv: "FV(rate, nper, pmt, [pv (optional)], [type]) | future value of an investment (Excel)",
    irr: "IRR(values, [guess (optional]) | internal rate of return (Excel)",
    npv: "NPV(rate, r1:r5) or NPV(rate, val1, val2...) | net present value (Excel)",

    rate: "RATE(nper, pmt, pv, [fv (optional)], [type], [guess]) | interest rate per period of an annuity (Excel)",
    nper: "NPER(rate, pmt, pv, [fv (optional)], [type]) | number of periods for an annuity (Excel)",
    cagr: "CAGR(start, end value, n) | compound annual growth rate assuming n periods",

    rri: "RRI(nper, pv, fv) | interest rate per period of an annuity (Excel)",
    pduration: "PDURATION(rate, pv, fv) | number of periods for an annuity (Excel)",

    loan: "loan(amount, rate, term) | loan payment: loan(400k, 5%, 30y)",
    invest: "invest(amount, rate, term) | future value of investment: invest(200/month, 5%, 30y)",
    interest: "interest(amount, rate, term) | interest cost of loan: interest(200/month, 5%, 30y)",

    stock: "stock(symbol) | stock price: STOCK(AAPL)",
    import: "import(json_url) | import data from public URL to object",
    fib: "fib(n) | returns the nth Fibonacci number",
    fibonacci: "fibonacci(n) | returns the nth Fibonacci number",
    fac: "fac(n) | returns the factorial of n",
    factorial: "factorial(n) | returns the factorial of n",
    nchoosek: "nchoosek(n, k) | returns the binomial coefficient of n and k",

    sigfig: "sigfig(x, n) | round to n significant figures, sigfig(123.456, 2) = 120",
    date: "date(year, month, day) | create date (Excel)", /* todo: not appearing since date by itself renders */

    // Date/time extraction functions (Excel-compatible)
    year: "year(date) | extract year from date | year(12/25/2025) = 2025",
    month: "month(date) | extract month from date (1-12) | month(12/25/2025) = 12",
    day: "day(date) | extract day from date (1-31) | day(12/25/2025) = 25",
    weekday: "weekday(date, [return_type]) | day of week (Excel) | weekday(1/1/2025) = 4 (Wednesday), return_type: 1=Sun-Sat (default), 2=Mon-Sun, 3=Mon(0)-Sun(6)",
    hour: "hour(datetime) | extract hour from datetime (0-23) | hour(14:30) = 14",
    minute: "minute(datetime) | extract minute from datetime (0-59) | minute(14:30) = 30",
    second: "second(datetime) | extract second from datetime (0-59) | second(14:30:45) = 45",
    weeknum: "weeknum(date, [return_type]) | week number of year (1-53) | weeknum(1/15/2025) = 3, return_type: 1=week starts Sunday (default), 2=week starts Monday",
    eomonth: "eomonth(start_date, months) | end of month after adding months (Excel) | eomonth(1/15/2025, 1) = 2/28/2025",
    edate: "edate(start_date, months) | date after adding months (Excel) | edate(1/15/2025, 3) = 4/15/2025",
    datevalue: "datevalue(text) | parse text to date | datevalue('12/25/2025')",
    timevalue: "timevalue(text) | parse text to time | timevalue('14:30')",

    calc: "calc(id) | import calculator by local title ('my calc') or URL ('https://instacalc.com/123')",

    chart: "chart(r1:r3) | line chart of row range | linechart(r1:r3) piechart(r1:r3) barchart(r1:r3)",
    barchart: "barchart(r1:r3) | bar chart of row range",
    linechart: "linechart(r1:r3) | line chart of row range",
    piechart: "piechart(r1:r3) | pie chart of row range",
    histogram: "histogram(data, bins) | frequency distribution chart | histogram(samples, 10) or histogram(samples, [0, 50, 100])",
  },

  CONVERSION_WARNING_LIST: {
    oz: "fluid oz",
    ozs: "fluid ozs",
    ounce: "fluid ounce",
    ounces: "fluid ounces",
  },

  IMPLIED_CONVERSIONS: [
    {
      match: (toType) => /\bdollar(s)?|\bcent(s)?\b/i.test(IC.typestring(toType)),
      exclude: (fromType) => IC.typestring(fromType).toLowerCase().includes("dollar"),
      unit: "dollar"
    },
    {
      match: (toType) => /\bUSD\b/i.test(IC.typestring(toType)),
      exclude: (fromType) => IC.typestring(fromType).toLowerCase().includes("usd"),
      unit: "USD"
    },
    {
      match: (toType) => IC.typeHasUnitInCategory(toType, "byte"),
      exclude: (fromType) => IC.typeHasUnitInCategory(fromType, "byte"),
      unit: "byte"
    }
  ],

  FLEX_UNIT_CONVERSIONS: {
    cycle: true,
    cycles: true,
  },

  FLEX_UNIT_ALIAS: {

    // TODO: clean this up, move into regular flow
    hz: { scale: 1, type: "cycles/second" },
    khz: { scale: 1e3, type: "cycles/second" },
    mhz: { scale: 1e6, type: "cycles/second" },
    ghz: { scale: 1e9, type: "cycles/second" },
    thz: { scale: 1e12, type: "cycles/second" },
    phz: { scale: 1e15, type: "cycles/second" },

    bps: { scale: 1, type: "bits/sec" },
    kbps: { scale: 1e3, type: "bits/sec" },
    mbps: { scale: 1e6, type: "bits/sec" },
    gbps: { scale: 1e9, type: "bits/sec" },
    tbps: { scale: 1e12, type: "bits/sec" },

    // TODO: make the flex unit alias support singular/plural
    flop: { scale: 1, type: "ops" },
    flops: { scale: 1, type: "ops/second" },

    kiloflop: { scale: 1e3, type: "ops" },
    kiloflops: { scale: 1e3, type: "ops/second" },

    megaflop: { scale: 1e6, type: "ops" },
    megaflops: { scale: 1e6, type: "ops/second" },
    mflop: { scale: 1e6, type: "ops" },
    mflops: { scale: 1e6, type: "ops/second" },

    gigaflop: { scale: 1e9, type: "ops" },
    gigaflops: { scale: 1e9, type: "ops/second" },
    gflop: { scale: 1e9, type: "ops" },
    gflops: { scale: 1e9, type: "ops/second" },

    teraflop: { scale: 1e12, type: "ops" },
    teraflops: { scale: 1e12, type: "ops/second" },
    tflop: { scale: 1e12, type: "ops" },
    tflops: { scale: 1e12, type: "ops/second" },

    petaflop: { scale: 1e15, type: "ops" },
    petaflops: { scale: 1e15, type: "ops/second" },
    pflop: { scale: 1e15, type: "ops" },
    pflops: { scale: 1e15, type: "ops/second" },

    exaflop: { scale: 1e18, type: "ops" },
    exaflops: { scale: 1e18, type: "ops/second" },
    eflop: { scale: 1e18, type: "ops" },
    eflops: { scale: 1e18, type: "ops/second" },

    zettaflop: { scale: 1e21, type: "ops" },
    zettaflops: { scale: 1e21, type: "ops/second" },
    zflop: { scale: 1e21, type: "ops" },
    zflops: { scale: 1e21, type: "ops/second" },

    yottaflop: { scale: 1e24, type: "ops" },
    yottaflops: { scale: 1e24, type: "ops/second" },
    yflop: { scale: 1e24, type: "ops" },
    yflops: { scale: 1e24, type: "ops/second" },

    kops: { scale: 1e3, type: "ops/second" },
    mops: { scale: 1e6, type: "ops/second" },
    gops: { scale: 1e9, type: "ops/second" },
    tops: { scale: 1e12, type: "ops/second" },
    pops: { scale: 1e15, type: "ops/second" },
    xops: { scale: 1e18, type: "ops/second" },
    exops: { scale: 1e18, type: "ops/second" },
    zops: { scale: 1e21, type: "ops/second" },

    mpge: { scale: 1 / 33.7, type: "miles/kWh" }, // 1 mile per gallon equivalent = 33.7 kWh

    // "molar concentration": { scale: 1, type: "mol/L" },
  },

  PREFIX_REMOVE: ["what is the", "what is", "whats", "what's", "how much is", "how much", "how many", "find", "figure out", "calculate", "convert"],

  L1_REPLACEMENTS_REGEX: {
    // TODO: should only be triggered by a Trie match on a keyword
    // Or, split the input, scan the string, and try to resume
    "divide by": {
      find: /divide (\S+) by (\S+)/gi, replace: "$1 / $2"
    },

    "subtract from": {
      find: /subtract (\S+) from (\S+)/gi, replace: "$2 - $1"
    },

    "multiply and": {
      find: /multiply (\S+) (and|with) (\S+)/gi, replace: "$1 * $3"
    },

    "add to": {
      find: /add (\S+) (and|to) (\S+)/gi, replace: "$1 + $3"
    },

    // R1:10 => R1:R10 [handle before variable assignment for (R1):10]
    "shorthandRowRef": {
      find: /\br(\d+)[:](\d+)\b/gi, replace: function (match, start, end) {
        return `r${start}:r${end}`;
      }
    },

    "unicodeWhitespace": {
      find: /[\u00a0\u2000-\u200a\u202f\u205f\u3000]/gi, replace: " "
    },

    // $TICKER syntax moved to token-based processing: IC.parser.processTickerSyntaxTokenized()
    // See that function for the full implementation.

    // f(x) = x^2
    "functionShorthand": {
      find: /(\w+)\s*\(([\w\s,]*)\)\s*=\s*([^=].*)$/gm,
      replace: function (match, funcName, params, body) {
        const trimmedBody = body.trim();

        // ignore existing brackets
        // note that we may be split on semicolon, so only check first part
        const wrappedBody = trimmedBody.startsWith("{") ? trimmedBody : `{ ${trimmedBody} }`;

        // function declaration
        return `function ${funcName}(${params}) ${wrappedBody}`;
      }
    },

    "set": {
      // Matches any line that starts with "set" (ignoring case)
      find: /^\s*set\b(.*)$/i,
      replace: function (match, rest) {
        // make sure it's not a function call
        if (!rest.includes("=")) {
          return match;
        }

        // Trim the rest of the string.
        rest = rest.trim();
        // If the rest starts with a colon, remove it.
        if (rest.startsWith(':')) {
          rest = rest.slice(1).trim();
        }

        // Split the rest into individual assignments using commas.
        const assignments = rest.split(',');

        // For each assignment, split on "=" to extract key and value.
        const props = assignments.map(item => {
          let parts = item.split('=');
          if (parts.length < 2) return "";
          let key = parts[0].trim();
          // In case there are extra "=" in the value, join the remaining parts.
          let value = parts.slice(1).join('=').trim();
          return `${key}: ${value}`;
        }).filter(Boolean); // remove any empty results

        // Construct the final function call.
        return `set({${props.join(', ')}})`;
      }
    },

    "solve": {
      // Match a line starting with "solve" (ignoring case)
      find: /^\s*(?:solve|goalseek)\s+\b(.*)$/i,
      replace: function (match, rest) {
        // Split on the first "="
        const parts = rest.split('=');
        if (parts.length < 2) {
          // If there's no "=", leave the string unchanged.
          return match;
        }

        // remove commas
        parts[1] = parts[1].replace(/,/g, ' ');

        // Phase 1: Extract the target variable from the left-hand side.
        const targetVar = IC.canonicalName(parts[0].trim());

        // Phase 2: Process the right-hand side.
        // Join back any extra "=" that might have been in the RHS.
        const rhs = parts.slice(1).join('=').trim();
        // Split the RHS on whitespace
        const tokens = rhs.split(/\s+/);

        if (tokens.length === 0) {
          // If no tokens on the right-hand side, do nothing.
          return match;
        }

        // The first token is the target value.
        const targetValue = tokens[0];

        // Any subsequent tokens (filtered through IC.isDefinedVariable) are free parameters.
        const freeParams = tokens.slice(1).filter(token => IC.envIdentifierExists(token));

        // Build the function call string.
        if (freeParams.length > 0) {
          return `goalseek("${targetVar}", ${targetValue}, [${freeParams.map(p => '"' + p + '"').join(", ")}])`;
        } else {
          return `goalseek("${targetVar}", ${targetValue})`;
        }
      }
    }
  },

  L2_REPLACEMENTS_STRINGS: {
    "×": "*",
    "✕": "*", // multiplication X
    "÷": "/",
    "⁄": "/",
    "\u2212": "-",
    "–": "-",       // en dash
    "—": "-",       // em dash

    "**": "^",      // treat ** as ^ for all conversions, units, etc.

    "•": "*", // bullet
    "·": "*", // middot

    // annoying quotes: https://unicode-table.com/en/sets/quotation-marks/
    // https://gist.github.com/thanpolas/244d9a13151caf5a12e42208b6111aa6
    "\u201C": '"',
    "\u201D": '"',
    "\u201F": '"',
    "\u275D": '"',
    "\u275E": '"',
    "\u301D": '"',
    "\u301E": '"',

    "\u2018": "'",
    "\u2019": "'",
    "\u201B": "'",
    "\u275B": "'",
    "\u275C": "'",

    "√": "sqrt",
    "∛": "cbrt",

    "≠": "!=",
    "≤": "<=",
    "≥": ">=",

    "°": "deg", // degree

    // constants
    "\u03C0": "(PI)",
    "\u03C6": "(PHI)",
    "\u03C4": "(TAU)",
  },

  L3_REPLACEMENTS_REGEX: {

    // replace superscripts before we process identifiers
    "encodedSuperscriptExponents": {
      find: /((?:\\u207b)?(?:\\u00b9|\\u00b2|\\u00b3|\\u2074|\\u2075|\\u2076|\\u2077|\\u2078|\\u2079|\\u2070)+)/g,
      replace: function (match) {
        const superscripts = {
          '\\u00b9': '1',
          '\\u00b2': '2',
          '\\u00b3': '3',
          '\\u2074': '4',
          '\\u2075': '5',
          '\\u2076': '6',
          '\\u2077': '7',
          '\\u2078': '8',
          '\\u2079': '9',
          '\\u2070': '0',
          '\\u207b': '-'
        };

        let chunks = match.match(/.{6}/g); // Split the string into chunks of 6 characters
        let normalExponent = chunks.map(chunk => superscripts[chunk]).join('');

        return '^' + normalExponent;
      }
    },

    // sum r1:r3 => sum(r1:r3)
    "wrapRowRangeExpressionsInParens": {
      find: /(?<!\()(\br\d+\s*:\s*r\d+\b)(?!\))/g,
      replace: "($1)"
    }
  },

  L4_REPLACEMENTS_REGEX: {
    // TODO: make the "as|to|into|to" list a global config variable
    "as_percent": { find: /\s(as|to|into|in)\s+[%]/gi, replace: " -> percent " },
    "percent": { find: /\s(percent|pct)(\s+|\b)/gi, replace: "% " },

    // of becomes * for multiplication between two numbers. check for digit on either side
    "of": { find: /(\d)\s+(of)\s+(\d)/gi, replace: "$1 * $3" },

    // 12 inches = ? feet
    "=?": { find: /(=)\s*[?]/gi, replace: " -> " },

    // degrees F and C
    "degF": { find: /deg(ree(s)?)?\s*F(ahrenheit)?\b/gi, replace: "F" },
    "degC": { find: /deg(ree(s)?)?\s*C(el(c|s|ius))?\b/gi, replace: "C" },

    // colon assignment: x: 3 => x = 3
    // Excludes STOCK: syntax (handled separately)
    "colonAssignment": {
      find: /^\s*(?!STOCK:)(\b[a-zA-Z_]\w*)\s*[:]/i,
      replace: '$1 = '
    },

    // explicitly set to undefined
    "emptyAssignment": {
      find: /(^\s*\b[a-zA-Z_]\w*)\s*=\s*$/g,
      replace: '$1 = undefined'
    },

    // = 3 + 3, 3 + 3 =, 3 + 3?
    "leadingOrTrailingSymbols": { find: /(^\s*[=]|[\?=]\s*$)/gi, replace: "" },

    // stock functions - convert to STOCK_ units
    // STOCK(AAPL) -> STOCK_AAPL, STOCK(BRK.A) -> STOCK_BRK_A
    "STOCK()": {
      find: /\bSTOCK\(([a-zA-Z]+(?:\.[A-Z]{1,2})?)\)/gi,
      replace: (match, symbol) => 'STOCK_' + symbol.toUpperCase().replace(/\./g, '_')
    },
    "STOCK:": {
      find: /\bSTOCK:([a-zA-Z]+(?:\.[A-Z]{1,2})?)/gi,
      replace: (match, symbol) => 'STOCK_' + symbol.toUpperCase().replace(/\./g, '_')
    },

    // Strip "share(s)" after stock tickers: "1 STOCK_MSFT share" → "1 STOCK_MSFT"
    "stockShares": {
      find: /\b(STOCK_[A-Z_]+)\s+shares?\b/gi,
      replace: "$1"
    },
    // Strip "share(s)" after famous tickers: "1 MSFT share" → "1 MSFT"
    "famousTickerShares": {
      find: /\b([A-Z]{2,5})\s+shares?\b/g,
      replace: function(match, ticker) {
        const keywords = (typeof IC_KEYWORDS !== 'undefined') ? IC_KEYWORDS :
          (typeof window !== 'undefined' && window.IC_KEYWORDS) ? window.IC_KEYWORDS : null;
        if (keywords?.isFamousTicker?.(ticker)) {
          return ticker;
        }
        return match;
      }
    },

    // cm2 => cm^2
    "unitPower": {
      find: /(\s|^|\d|\W)([a-z]+)([2-9])(\s|\b)/gi, replace: function (match, space, unit, power, rest) {
        // rankine...
        if (!IC.predefinedUnit(unit) || !IC.typestringIsSameUnitCategory(unit, "cm")) {
          return match;
        }
        return `${space}${unit}^${power}${rest}`;
      }
    },

    // log10 and log2 should be log_10 and log_2
    "logPower": {
      find: /\b(log)(10|2)\b/gi, replace: "log_$2"
    },

    // combination like 10C3, 5C3, genertally [int]c[int]
    "combination": {
      find: /\b(\d+)c(\d+)/gi, replace: function (match, n, k) {
        return `combination(${n}, ${k})`;
      }
    },

    "permutation": {
      find: /\b(\d+)p(\d+)/gi, replace: function (match, n, k) {
        return `permutation(${n}, ${k})`;
      }
    },

    // double single quote after a number becomes a double quote: 13' 2'' => 13' 2"
    "doubleQuote": { find: /(\d)''/g, replace: "$1\"" },

    // multiple "->" are replaced by a single ->
    "->": { find: /(\s*->\s*){2,}/g, replace: " -> " },

    // collapse 2 or more whitespace
    "whitespace": { find: /\s{2,}/g, replace: " " },

    // remove apostrophes from mexican-style numbers: 3'500 => 3500, 3'500.000 => 3500.000
    // require 3 digits after the apostrophe, and do negative lookahead for ["] symbol
    "mexicanApostrophe": { find: /(\d)'(\d{3})(?!\d|")/g, replace: "$1,$2" },

    "currencyThenNumber": {
      find: /\b([a-z]{3})(\d+)/gi, replace: function (match, code, digits) {
        if (ICUnit.isCurrency(code) && !IC.envIsDefined(match)) {
          return `${code} ${digits}`;
        }
        return match;
      }
    },

    "unitless": {
      find: /\b(no|without|remove|strip|clear|drop|omit|exclude|ignore)\s+(units?|dimensions?)\b/gi,
      replace: "unitless"
    },

    // UTC+9 => UTCPLUS9, especially in conversions
    "UTCAndGMT": {
      find: /->\s*(UTC|GMT)([+-])(\d+)/g,
      replace: (match, prefix, sign, number) => {
        return `-> ${prefix}${sign === '+' ? 'PLUS' : 'MINUS'}${number}`;
      }
    },

    // replace angle symbol (\u2220) with :phaseangle: 
    // 5∠60.4 => 5 :phaseangle: 60.4
    "phasor": {
      find: /(\d+)\s*(\\u2220|angle)\s*(\d+)/gi,
      replace: (match, magnitude, symbol, angle) => {
        if (IC.envIsDefined(symbol)) {
          return match;
        }

        return `${magnitude} :phaseangle: ${angle}`;
      }
    }

  },

  L5_REPLACEMENTS_STRINGS: {

    /*
    // builtin constants
    // TODO: these constants should be under predefined variables, I think
    "c_light": "(299,792,458 meters/second)",
    // "speed of light": "(299,792,458 meters/second)",
 
    "g_accel": "(9.8 meters/second^2)", // gravitational acceleration
    //    "gravity": "(9.8 meters/second^2)",
    "smallG": "(9.8 meters/second^2)",
 
    "g_const": "(6.67430 * 10^-11 newton meter^2 / kilogram^2)", // gravitational constant
    "gravitational_constant": "(6.67430 * 10^-11 newton meter^2 / kilogram^2)",
    "bigG": "(6.67430 * 10^-11 newton meter^2 / kilogram^2)",
 
    "planck_const": "(6.62607015 * 10^-34 meter^2 kilogram / second)",
    "h_planck": "(6.62607015 * 10^-34 meter^2 kilogram / second)",
 
    "e_charge": "(1.602176634 * 10^-19 coulombs)", // electron charge
    "avogadro_const": "(6.02214076 * 10^23 per mole)",
 
    "boltzmann_const": "(1.380649 * 10^-23 joules/kelvin)",
    "k_boltzmann": "(1.380649 * 10^-23 joules/kelvin)",
 
    "r_gas": "(8.314462618 joules/(mole·kelvin))", // gas constant
 
    "faraday_const": "(96,485.33212 coulombs/mole)",
    "F_faraday": "(96,485.33212 coulombs/mole)",
 
    // Particle masses
    "electron mass": "(9.10938356 * 10^-31 kilograms)",
    "proton mass": "(1.6726219 * 10^-27 kilograms)",
 
    // Astronomical constants
    "earth mass": "(5.97237 * 10^24 kilograms)",
    "earth radius": "(6,371 kilometers)",
 
    "moon mass": "(7.342 * 10^22 kilograms)",
    "moon radius": "(1,737.5 kilometers)",
 
    "sun mass": "(1.989 * 10^30 kilograms)",
    "sun radius": "(696,340 kilometers)",
    */
  },

  PLAIN_ENGLISH_OPS: {
    add: "+",
    "added to": "+",
    plus: "+",
    minus: "-",
    times: "*",
    "multiply by": "*",
    "by": "*",
    mul: "*",
    multiply: "*",
    "multiplied by": "*",
    div: "/",
    "divide by": "/",
    "divided by": "/",
    "out of": "/",
    "over": "/",
    "subtracted from": "* -1 +",
    "take away": "-",
    "to the power of": "^",
    "to the power": "^",
    "raised to the power of": "^",
    "raised to the power": "^",
    "raised to": "^",
    "power": "^",
    "pow": "^",
    "half of": "0.5 *",
    "half": "0.5 *",
    "twice": "2 *",
    "double": "2 *",
    "triple": "3 *",
    "thrice": "3 *",
    "quadruple": "4 *",

    "is": " = ",
    "are": " = ",
    "equals": " = ",

    sub: "-",
    subtract: "-",
    negative: "-", // three plus negative two: 3+ -2
    modulo: " mod",

    // full names for math functions
    cosine: "cos ",
    sine: "sin ",
    tangent: "tan ",
    cotangent: "cot ",
    secant: "sec ",
    cosecant: "csc ",

    // remember: longest match first
    "is what percentage of": " :pctof: ",
    "is what percent of": " :pctof: ",
    "is what pct of": " :pctof: ",
    "is what % of": " :pctof: ",
    "is what of": " :pctof: ",
    "as a percentage of": " :pctof: ",
    "as a percent of": " :pctof: ",
    "as a pct of": " :pctof: ",
    "as a % of": " :pctof: ",

    "% of what is": "% inverse *", // 20% of what is 30 => 20% inverse * 30

    "choose": ":choose:",
    "pick": ":choose:", // wolfram alpha uses pick as choose
    "permute": ":permute:",

    "percentage of": "% of ",
    "percent of": "% of ",
    "pct of": "% of ",

    "square root": "sqrt",
    "cube root": "cbrt",

    // conversion words
    "into a": " -> ",
    "to a": " -> ",
    "as a": " -> ",
    "in a": " -> ",

    "converted to": " -> ",
    "convert to": " -> ",
    "convert": " -> ",
    "as": " -> ",
    "into": " -> ",
    "to": " -> ",
    // no "in" because it could be "inch"

    // typesolve words
    "using": " @ ",
    "with": " @ ",
    "at": " @ ",
    "for": " @ ",

    "per": "/",
    "every": "/",
    "until": "/",

    // simple replacement. TODO: have a proper "until" operator
    "days until": "now - ",

    /*
    "ago": "* -1 + now",
    "from now": " + now",
    "before": "* -1 + now",
    "after": " + now",
    */

    /*
    // TODO: scan the entire set of rows for an assignment, and use that reference. otherwise "daily edge = 3" will be transformed.
 
    "hourly": "/hour",
    "daily": "/day",
    "weekly": "/week",
    "monthly": "/month",
    "yearly": "/year",
    "annually": "/year",
    */
  }
};

const CoreModule = {
  CALC: new LIB.InstaCalc(),
  NOPARSE: { msg: "PARSE ERROR" },
  CACHE: {}, // initialied later

  // note: 0 and Infinity are valid; looking for null/undefined/failed parse
  isValidEvalResult(eval_result) {
    return eval_result != null
      && typeof eval_result !== "undefined"
      && eval_result != IC.NOPARSE;
  },

  arrayLimitCheck(arr, index) {
    if (Array.isArray(arr) && (index >= CONFIG.SETTINGS.MAX_ARRAY_SIZE || arr.length >= CONFIG.SETTINGS.MAX_ARRAY_SIZE)) {
      IC.log(`Array size limit ${CONFIG.SETTINGS.MAX_ARRAY_SIZE}`);
      return false;
    }
    return true;
  },

  // case insensitive key search in object
  ICObjectLiteralFindKey: function (obj, key = "") {
    if (obj && typeof obj === 'object') {
      // First, try exact match
      if (obj.hasOwnProperty(key)) {
        return key;
      }

      // If exact match not found, try case-insensitive match
      let lowercaseKey = key.toLowerCase();
      return Object.keys(obj).find(k => k.toLowerCase() === lowercaseKey);
    }

    return undefined;
  },

};

const LogModule = {

  _log_paused: false,

  /**
   * Structured logging for tooltips
   * Accepts either:
   *   - String (legacy): "unit: USD" - parsed by prefix
   *   - Object (new): { type: 'unit', name: 'USD', category: 'currency' }
   *
   * Supported types:
   *   - unit: { type: 'unit', name: string, category?: 'currency'|'standard' }
   *   - convert: { type: 'convert', from: string, to: string }
   *   - warning: { type: 'warning', message: string }
   *   - info: { type: 'info', message: string }
   *   - function: { type: 'function', name: string }
   *   - stock: { type: 'stock', symbol: string, price: number, date: string }
   */
  log(entry) {
    if (IC._log_paused || IC.info.rowIndex === undefined) {
      return;
    }

    IC.info.logs[IC.info.rowIndex] = IC.info.logs[IC.info.rowIndex] || [];
    IC.info.logs[IC.info.rowIndex].push(entry);
  },

  logNewUnit: function (name) {
    if (!name.trim()) {
      return;
    }

    name = IC.parser.unescapeUnicode(name);

    // "3 gigabytes" is just "gigabytes"
    if (ICUnit.unitStringHasNumber(name)) {
      name = ICUnit.unitStringExtractNumber(name)[1];
    }

    // Check if this is a loaded stock ticker (stocks are dynamically registered as units)
    // Get stock cache with worker fallback (window in main, global/self in worker)
    const stockCache = (typeof window !== 'undefined' && window.Action?.fn?.stock_cache) ||
      (typeof global !== 'undefined' && global.Action?.fn?.stock_cache) ||
      (typeof self !== 'undefined' && self.Action?.fn?.stock_cache);
    const isLoadedStock = stockCache?.[name]?.loaded && stockCache[name].price;

    let customUnitIgnore =
      IC.typestringIsPossibleUnitIncludingSlash(name) ||
      CONFIG.FLEX_UNIT_ALIAS[name.toLowerCase()] ||
      CONFIG.CUSTOM_UNIT_IGNORE[name.toLowerCase()] ||
      ICDate.getIANATimeZone(name.toUpperCase()) ||
      isLoadedStock;
    ;

    if (!customUnitIgnore) {
      IC.log({ type: 'unit', name, category: 'custom' });
      return;
    }

    // For stocks, we already log via logStockInfo - don't duplicate
    if (isLoadedStock) {
      return;
    }

    // Use IC_KEYWORDS.isFiatCurrency for display categorization (not ICUnit.isCurrency)
    // This ensures stocks in the currencies object (like DD) don't appear as currencies
    const keywords = (typeof IC_KEYWORDS !== 'undefined') ? IC_KEYWORDS : (typeof window !== 'undefined' ? window.IC_KEYWORDS : null);
    const isFiat = keywords?.isFiatCurrency?.(name) || false;
    IC.log({ type: 'unit', name: IC.unitDisplayName(name), category: isFiat ? 'currency' : 'standard' });
  },

  logFunctionReference(name) {
    let msg = IC.predefinedFunction(name) ? `function: ${name}` : `missing function: ${name}`;
    IC.log(msg);
  },

  logStockInfo(symbol, cacheData, options = {}) {
    if (!cacheData?.price || typeof cacheData.price !== 'number') return;
    // Use tradingDate (actual market close date) if available
    const timestamp = cacheData.tradingDate || cacheData.fetchedAt;
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

    IC.log({
      type: 'stock',
      symbol,
      price: cacheData.price,
      date: dateStr,
      autoDetected: options.autoDetected !== false
    });
  },

  logPause() {
    IC._log_paused = true;
  },

  logResume() {
    IC._log_paused = false;
  },

  logSetCurrentRow(log) {
    IC.info.logs[IC.info.rowIndex] = log;
  },

  logGetCurrentRow() {
    return IC.info.logs[IC.info.rowIndex];
  },

  logClear() {
    IC.info.logs[IC.info.rowIndex] = [];
  },

  /**
   * Process log entries into tooltip display strings
   *
   * Structured log types (use these for new code):
   *   { type: 'unit', name: string, category: 'currency'|'custom'|'standard' }
   *   { type: 'convert', from: string, to: string, assumed?: bool, fuzzy?: bool }
   *   { type: 'stock', symbol: string, price: number, date: string, autoDetected?: bool }
   *   { type: 'function', name: string }
   *   { type: 'warning', message: string }
   *   { type: 'tip', message: string }        - Helpful hints (shown last)
   *   { type: 'info', message: string }       - Informational (shown after main content)
   *   { type: 'debug', message: string }      - Hidden unless debug mode
   *
   * Priority order: warnings > stocks > units > converts > functions > info > tips
   */
  logProcess(log, result) {
    // Collect items by type with extended categories
    const items = {
      units: [],        // { name, category }
      converts: [],     // { from, to, assumed?, fuzzy? }
      warnings: [],     // { message }
      tips: [],         // { message } - helpful hints
      infos: [],        // { message }
      functions: [],    // { name }
      stocks: [],       // { symbol, price, date, autoDetected? }
      debug: [],        // { message } - only shown in debug mode
      other: []         // unparsed legacy strings
    };

    // Parse each entry
    log.forEach(entry => {
      if (typeof entry === 'object' && entry !== null) {
        this._parseStructuredEntry(entry, items);
      } else if (typeof entry === 'string') {
        this._parseLegacyString(entry, items);
      }
    });

    // Deduplicate and filter
    items.units = this._dedupeUnits(items.units);
    items.warnings = this._dedupeByMessage(items.warnings);
    items.infos = this._dedupeByMessage(items.infos);

    // Build output with priority ordering
    return this._buildOutput(items, result);
  },

  /** Parse structured log object into items collection */
  _parseStructuredEntry(entry, items) {
    switch (entry.type) {
      case 'unit':
        items.units.push({ name: entry.name, category: entry.category || 'standard' });
        break;
      case 'convert':
        items.converts.push({ from: entry.from, to: entry.to, assumed: entry.assumed, fuzzy: entry.fuzzy });
        break;
      case 'warning':
        items.warnings.push({ message: entry.message });
        break;
      case 'tip':
        items.tips.push({ message: entry.message });
        break;
      case 'info':
        items.infos.push({ message: entry.message });
        break;
      case 'function':
        items.functions.push({ name: entry.name });
        break;
      case 'stock':
        items.stocks.push({
          symbol: entry.symbol,
          price: entry.price,
          date: entry.date,
          autoDetected: entry.autoDetected
        });
        break;
      case 'debug':
        items.debug.push({ message: entry.message });
        break;
      default:
        items.other.push(JSON.stringify(entry));
    }
  },

  /** Parse legacy string format into items collection */
  _parseLegacyString(entry, items) {
    const colonIdx = entry.indexOf(':');
    if (colonIdx === -1) {
      // Check for common patterns without colons
      if (entry.startsWith('warning')) {
        items.warnings.push({ message: entry });
      } else {
        items.other.push(entry);
      }
      return;
    }

    const type = entry.substring(0, colonIdx).trim().toLowerCase();
    const msg = entry.substring(colonIdx + 1).trim();

    // Get IC_KEYWORDS for fiat currency checks (used by 'unit' case)
    // Moved outside switch to avoid const scoping issues
    const keywords = (typeof IC_KEYWORDS !== 'undefined') ? IC_KEYWORDS : (typeof window !== 'undefined' ? window.IC_KEYWORDS : null);

    switch (type) {
      case 'custom unit':
        items.units.push({ name: msg, category: 'custom' });
        break;
      case 'unit':
        // Use IC_KEYWORDS.isFiatCurrency for display categorization (not ICUnit.isCurrency)
        // This ensures stocks in the currencies object (like DD) don't appear as currencies
        const isFiatCurrency = keywords?.isFiatCurrency?.(msg) || false;
        items.units.push({ name: msg, category: isFiatCurrency ? 'currency' : 'standard' });
        break;
      case 'convert':
      case 'fuzzy convert':
      case 'assumed conversion':
        const match = msg.match(/(.+?)\s*→\s*(.+)/);
        if (match) {
          items.converts.push({
            from: match[1],
            to: match[2],
            fuzzy: type === 'fuzzy convert',
            assumed: type === 'assumed conversion'
          });
        }
        break;
      case 'warning':
        items.warnings.push({ message: msg });
        break;
      case 'tip':
        items.tips.push({ message: msg });
        break;
      case 'info':
        items.infos.push({ message: msg });
        break;
      case 'function':
      case 'fn':
        items.functions.push({ name: msg });
        break;
      default:
        // Check for patterns that look like warnings/tips
        if (entry.includes("can't") || entry.includes('cannot') || entry.includes('error')) {
          items.warnings.push({ message: entry });
        } else if (entry.includes('try') || entry.includes('use ') || entry.includes('hint')) {
          items.tips.push({ message: entry });
        } else {
          items.other.push(entry);
        }
    }
  },

  /** Deduplicate units by name (case-insensitive) */
  _dedupeUnits(units) {
    const seen = new Set();
    return units.filter(u => {
      const key = u.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  },

  /** Deduplicate items by message */
  _dedupeByMessage(items) {
    const seen = new Set();
    return items.filter(i => {
      if (seen.has(i.message)) return false;
      seen.add(i.message);
      return true;
    });
  },

  /** Build output array with priority ordering */
  _buildOutput(items, result) {
    const output = [];

    // Group units by category
    // Use IC_KEYWORDS.isFiatCurrency for display categorization (not ICUnit.isCurrency)
    // This ensures stocks in the currencies object (like DD) don't appear as currencies
    const keywords = (typeof IC_KEYWORDS !== 'undefined') ? IC_KEYWORDS : (typeof window !== 'undefined' ? window.IC_KEYWORDS : null);
    const isFiat = (name) => keywords?.isFiatCurrency?.(name) || false;
    const currencies = items.units.filter(u => u.category === 'currency' || isFiat(u.name));
    const customUnits = items.units.filter(u => u.category === 'custom' && !isFiat(u.name));
    const standardUnits = items.units.filter(u => u.category === 'standard' && !isFiat(u.name));
    const allAreCurrencies = items.units.length > 0 && currencies.length === items.units.length;

    // 1. WARNINGS (highest priority - shown first)
    items.warnings.forEach(w => output.push(`note: ${w.message}`));

    // 2. STOCKS
    items.stocks.forEach(s => {
      if (s.autoDetected) {
        output.push(`${s.symbol} → $${s.symbol}: $${s.price.toFixed(2)} (${s.date})`);
      } else {
        output.push(`${s.symbol}: $${s.price.toFixed(2)} (${s.date})`);
      }
    });

    // 3. UNITS - grouped by category
    if (currencies.length > 0) {
      const names = currencies.map(u => u.name.toUpperCase());
      output.push(`${currencies.length > 1 ? 'currencies' : 'currency'}: ${names.join(', ')}`);
    }

    if (customUnits.length > 0) {
      const names = customUnits.map(u => u.name);
      output.push(`custom ${customUnits.length > 1 ? 'units' : 'unit'}: ${names.join(', ')}`);
    }

    if (standardUnits.length > 0) {
      const formatted = standardUnits.map(u => {
        const helpMsg = CONFIG.getUnitHelpMessage(u.name, standardUnits.length > 1);
        const infoMsg = helpMsg ? '' : ICUnit.infoMessage(u.name);
        return `${u.name}${helpMsg ? ' ' + helpMsg : ''}${standardUnits.length === 1 && infoMsg ? ' ' + infoMsg : ''}`.trim();
      });
      output.push(`${standardUnits.length > 1 ? 'units' : 'unit'}: ${formatted.join(', ')}`);
    }

    // 4. CONVERSIONS - skip for pure currency (OXR attribution in output handles it)
    if (!allAreCurrencies && items.converts.length > 0) {
      // Show most significant conversion (first one that's not assumed)
      const mainConvert = items.converts.find(c => !c.assumed) || items.converts[0];
      let convertMsg = `${mainConvert.from} → ${mainConvert.to}`;
      if (mainConvert.assumed) convertMsg += ' (assumed)';
      if (mainConvert.fuzzy) convertMsg += ' (approx)';
      output.push(`convert: ${convertMsg}`);
    }

    // 5. FUNCTIONS - show help message if available
    if (items.functions.length > 0) {
      const fn = items.functions[0];
      const helpMsg = CONFIG.FUNCTION_HELP_MSG?.[fn.name.toLowerCase()];
      if (helpMsg) output.push(helpMsg);
    }

    // 6. INFO messages
    items.infos.forEach(i => output.push(i.message));

    // 7. OTHER (unclassified legacy strings)
    items.other.forEach(o => {
      // Skip redundant convert messages for currencies
      if (allAreCurrencies && o.toLowerCase().includes('convert')) return;
      output.push(o);
    });

    // 8. TIPS (lowest priority - shown last)
    items.tips.forEach(t => output.push(`tip: ${t.message}`));

    // Special handling for time format
    let format = IC.format(result);
    if (format.startsWith("time_") && output.some(s => s.includes('unit:') && s.includes(' s'))) {
      const timeHelp = CONFIG.getUnitHelpMessage("time");
      if (timeHelp) {
        return [timeHelp, ...output.filter(s => !(s.includes('unit:') && s.includes(' s')))];
      }
    }

    return output.filter(Boolean);
  },
};

const EnvModule = {
  // environment items
  env: {},
  info: {},

  envReset() {
    IC.env = {};
    IC.envResetRowInfo();
  },

  envResetRowInfo() {
    IC.info = {
      _temp: {},    // temp data for the row
      inputRows: [], // original inputs
      rowIndex: 0,
      rowRefs: {}, // rows with a reference
      varRefs: {}, // variables being referenced 
      formatters: {}, // rows with a format string
      titles: {},
      unitTitles: {},
      comments: {},
      identifiers: {}, // variable names in use
      logs: {}, // argument call log
      strings: {}, // strings which are replaced for easier processing
      cancelledUnits: {}, // whether a unit may have been cancalled during multiplication
      settings: {}, // calc and rounding settings
      unitOverride: false // if "miles" is defined as var, but treat as unit
    };
  },

  envResetRowInfoForExecution() {
    // see if we already have defined objects...
    if (typeof IC.info.logs !== "object") {
      IC.envResetRowInfo();
    }

    IC.info.logs[IC.info.rowIndex] = [];
    IC.info.formatters[IC.info.rowIndex] = {};
    IC.info.cancelledUnits[IC.info.rowIndex] = false;
    IC.info.titles[IC.info.rowIndex] = "";
    IC.info.comments[IC.info.rowIndex] = "";
    IC.info._temp = {};
  },

  envGetTemp(name) {
    return IC.info._temp[name];
  },

  envSetTemp(name, value) {
    IC.info._temp[name] = value;
  },

  envAddString(str) {
    let info = IC.envGetInfo();
    let i = Object.keys(info.strings).length;
    let key = `:string_${IC.envGetRowIndex()}_${i + 1}_`;
    info.strings[key] = str;
    return key;
  },

  envGetString(key) {
    let info = IC.envGetInfo();
    return info.strings[key];
  },

  envGetStrings() {
    return IC.envGetInfo().strings;
  },

  envSetStrings(strings) {
    IC.envGetInfo().strings = strings;
  },

  envReplaceStrings(input) {
    input = input.replace(/(:string_\d+_\d+_)/g, (match) => IC.envGetString(match));
    return input;
  },

  envGet() {
    return IC.env;
  },

  envGetVariablesOnly() {
    // return IC.env with r1 r2 etc filtered out, but the entire key/value.
    // it should return a new object
    let newObj = {};
    for (let key in IC.env) {
      if (key.match(/r\d+/i)) {
        continue;
      }
      newObj[key] = IC.env[key];
    }
    return newObj;
  },

  envNewStartEnv(i, settings) {
    IC.envSetRowIndex(i);
    IC.envSetSettings(settings);
    IC.envResetRowInfoForExecution();
    const startEnv = Object.assign({}, IC.envGet());
    return startEnv;
  },

  envSet(env) {
    IC.env = Object.assign({}, env);
  },

  envGetInfo() {
    return IC.info;
  },

  envSetInfo(info) {
    IC.info = Object.assign({}, info);
  },

  envGetRowIndex() {
    return IC.info.rowIndex;
  },

  envSetRowIndex(i) {
    IC.info.rowIndex = i;
  },

  envSetSettings(settings) {
    IC.info.settings = settings;
  },

  envGetSettings() {
    return IC.info.settings;
  },

  envSetFormatter: function (currencyObj) {
    IC.info.formatters[IC.info.rowIndex] = currencyObj;
  },

  envGetFormatter: function () {
    return IC.info.formatters[IC.info.rowIndex];
  },

  // Clear formatter for current row
  clearRowFormat: function () {
    IC.info.formatters[IC.info.rowIndex] = null;
  },

  // Set formatter for current row
  setRowFormat: function (currencyObj) {
    IC.info.formatters[IC.info.rowIndex] = currencyObj;
  },

  envSetInputRows(rows) {
    IC.info.inputRows = rows;
  },

  envGetInputRows() {
    return IC.info.inputRows;
  },

  envGetCurrentInput() {
    return IC.envGetInputRows()[IC.envGetRowIndex()];
  },

  envSetHasCancelledUnit() {
    IC.info.cancelledUnits[IC.info.rowIndex] = true;
  },

  envGetHasCancelledUnit() {
    return IC.info.cancelledUnits[IC.info.rowIndex];
  },

  envNormalizeTitle: function (title) {
    return title ? title.replace(/(_+| +)+/g, ' ').trim() : title;
  },

  envSetRowTitle: function (title) {
    IC.info.titles[IC.info.rowIndex] = IC.envNormalizeTitle(title);
  },

  envGetRowTitle: function () {
    return IC.info.titles[IC.info.rowIndex];
  },

  envSetUnitTitle: function (unit, title) {
    let key = `${IC.info.rowIndex}${unit}`;
    IC.info.unitTitles[key] = title;
  },

  envGetUnitTitle: function (unit) {
    let key = `${IC.info.rowIndex}${unit}`;
    return IC.info.unitTitles[key] || unit;
  },

  envSetRowComment: function (comment) {
    if (comment) {
      comment = comment.trim();
      IC.info.comments[IC.info.rowIndex] = comment;
    }
  },

  envSetIdentifier: function (id) {
    if (id) {
      id = IC.canonicalName(id);
      IC.info.identifiers[id] = true;
    }
  },

  envCheckOverlappingId(id) {
    id = id.trim();
    if (!id.includes(" ")) return;

    let parts = id.split(" ");
    if (parts.every((part) => IC.envIsDefined(part))) {
      IC.log({ type: 'warning', message: `var '${id}' overlaps with '${parts.join(", ")}'` });
    }
  },

  // TODO: need to consolidate these various checking functions
  envIsDefined: function (name) {
    if (!name) return false;
    return IC.envIsVariableDefined(name) || IC.predefinedIdentifier(name) || IC.envIdentifierExists(name);
  },

  // ugh, so many functions. this one does a global search
  envIsDefinedSearchAll: function (name) {
    return IC.envIsDefined(name) || IC.predefinedFunction(name) || IC.predefinedUnit(name);
  },

  envIdentifierExists: function (id) {
    id = IC.canonicalName(id);
    return IC.info.identifiers[id] ?? IC.env[id];
  },

  envGetIdentifiers: function () {
    return Object.keys(IC.info.identifiers);
  },

  envIsUserFunction: function (name) {
    return IC.envGetVariable(name)?.type === "function";
  },

  // look at input, find longest identifier it starts with
  envGetLongestIdentifierMatch: function (input) {
    let processed = input.trim();
    let idsLongestFirst = IC.envGetIdentifiers().sort((a, b) => b.length - a.length);
    let id = idsLongestFirst.find((id) => processed.startsWith(id));
    return id;
  },

  /**
   * Logs if there are any overrides
   * @param {string[]} ids - new identifiers for this row
   */
  envCheckIdentifierWarnings(ids) {
    ids.forEach((id) => {
      IC.envCheckOverlappingId(id);

      let unit, keyword, constant;
      if ((unit = IC.typestringIsPossibleUnitIncludingSlash(id)) && id.length >= 2) {
        // don't worry about a, n, etc. -- only larger units like cm, inch, and so on

        let typeMsg = unit?.name ? `:${unit.name}` : "";
        IC.log(`overriding '${id}${typeMsg}', be cautious in conversions`);
      } else if ((keyword = IC.stringIsKeyword(id))) {
        IC.log(`overriding keyword '${id}'`);
      } else if ((constant = IC.predefinedIdentifier(id))) {
        IC.log(`overriding predefined '${id}'`);
      }
    });
  },

  /**
    * Warns if this is an empty object and converts back to plain number
    * @param {ICObject} ret
    * @returns ICObject | number
    */
  envCheckCancelledUnits(ret) {
    // an array could be passed in, which is instance of "object"
    if (IC.hasNoType(ret) && IC.format(ret) == "" && IC.envGetHasCancelledUnit()) {
      IC.log(`units cancel to number`);
      return IC.val(ret);
    }
    return ret;
  },

  envGetRowComment: function () {
    return IC.info.comments[IC.info.rowIndex];
  },

  envGetVariableReferences: function () {
    return Object.keys(IC.info.varRefs);
  },

  envHasMissingRowReference: function (i) {
    return IC.info.rowRefs[i];
  },

  /**
    * Is the variable an autouint (cm => 1cm) or actually defined (x = 3)
    * @param {string} varname 
    * @returns boolean
    */
  envIsAutoUnit(varname) {
    return !IC.envIsDefined(varname) && IC.predefinedUnit(varname);
  },

  // split x[10] or a.b.c
  getVariableParts(name) {
    const hasTrailingDot = name.endsWith('.');

    // Split the variable name, but keep trailing dot if present
    let parts = name.split(/[.\[]/).map(part => part.replace(']', ''));
    if (hasTrailingDot) {
      parts[parts.length - 1] += '.'; // allow x. = 10 (as an assignment)
      delete parts[parts.length - 1];
    }

    return parts;
  },

  envAssignVariable(x, op, val) {
    x = IC.canonicalName(x);
    val = IC.envCheckCancelledUnits(val);

    let parts = IC.getVariableParts(x);

    const baseVar = parts[0];

    // If it's a simple assignment (no nested properties or array indices)
    if (parts.length === 1) {
      return IC.assignValueIntoObject(IC.env, x, op, val);
    }

    // Rest of the function remains the same for handling nested structures
    if (!(baseVar in IC.env)) {
      IC.env[baseVar] = parts[1] === '' ? [] : {};
    }

    let current = IC.env[baseVar];
    for (let i = 1; i < parts.length - 1; i++) {
      const part = parts[i];
      if (part === '') {
        if (!Array.isArray(current)) {
          current = [];
        }

        if (!IC.arrayLimitCheck(current, current.length)) return undefined;

        current.push(parts[i + 1] === '' ? [] : {});
        current = current[current.length - 1];
      } else if (!isNaN(part)) {
        const index = parseInt(part);
        if (!Array.isArray(current)) {
          current = [];
        }

        if (!IC.arrayLimitCheck(current, current.length)) return undefined;
        if (!(index in current)) {
          current[index] = parts[i + 1] === '' ? [] : {};
        }
        current = current[index];
      } else {
        if (!(part in current)) {
          current[part] = parts[i + 1] === '' ? [] : {};
        }
        current = current[part];
      }
    }

    const lastPart = parts[parts.length - 1];
    const lastIndex = lastPart === '' ? current.length : (!isNaN(lastPart) ? parseInt(lastPart) : lastPart);

    if (lastPart === '' || !isNaN(lastPart)) {
      if (!IC.arrayLimitCheck(current, current.length)) return undefined;
      if (!IC.arrayLimitCheck(current, lastIndex)) return undefined;
    }

    return IC.assignValueIntoObject(current, lastIndex, op, val);
  },

  assignValueIntoObject(obj, key, op, val) {
    if (op === '=') {
      if (Array.isArray(val)) {
        IC.arrayLimitCheck(val, val.length - 1);
      }

      if (Array.isArray(val) || typeof val === 'object') {
        try {
          obj[key] = structuredClone(val);
        } catch (e) {
          obj[key] = JSON.parse(JSON.stringify(val));
        }
      } else {
        obj[key] = val;
      }
    } else if (op.endsWith('=') && op.length > 1) {
      const baseOp = op.slice(0, -1);
      obj[key] = IC.semanticInfixOp(IC.val(obj[key]), baseOp, val);
    }
    return obj[key];
  },

  envAssignRowResult(result) {
    let rowName = "r" + (IC.info.rowIndex + 1);
    IC.envAssignVariable(rowName, "=", result ?? 0); // default value for any row
  },

  envIsVariableDefined(name) {
    return IC.canonicalName(name) in IC.env;
  },

  envFindInflectionVariant(name) {
    const canonicalName = IC.canonicalName(name);
    const variants = [
      canonicalName,
      IC.stringPluralize(canonicalName),
      IC.stringSingularize(canonicalName)
    ];

    for (const variant of variants) {
      if (IC.envIsDefined(variant)) {
        return variant;
      }
    }

    return null;
  },

  envSetVariable(name, value) {
    return IC.envAssignVariable(name, "=", value);
  },

  envGetVariable(name) {
    if (!name) {
      return undefined;
    }
    let x = IC.canonicalName(name);

    // special case: sin(2 * PI rad) or sin(x rad)
    // or [varname] [scalename]
    // radians need to be broken out separately from "pi rad" variable
    // same with x gbps
    let matches = name.trim().split(" ");
    if (matches.length > 1) {
      let suffix = matches.pop();
      let scale = IC.predefinedSuffixScale(suffix);
      let realName = matches.join(" ");

      if (scale !== 1 && IC.envIsDefined(realName)) {
        return IC.mathMul(scale, IC.envGetVariable(realName));
      }
    }

    // don't check for IC.env[x], which could be 0
    if (x in IC.env) {
      // successful var lookup
      IC.info.varRefs[x] = true;
      return IC.env[x] ?? 0; // null/undefined defaults to 0 when used 
    }

    // accessing a missing row ref
    if (x.match(/^r\d+$/i)) {
      IC.info.rowRefs[IC.info.rowIndex] = true;
      return 0;
    }

    var val = IC.predefinedIdentifier(x);
    return typeof val == "function" ? val() : val || undefined;
  },

  envGetRowRangeVariables(startIndex, endIndex) {
    let values = [];
    let maxIndex = Math.min(IC.envGetInputRows().length, endIndex);
    for (var i = startIndex; i <= maxIndex; i++) {
      if (i == IC.info.rowIndex + 1) {
        values.push(0);       // avoid circular reference to current row; SUM(R1:R5), which appears on R3
        continue;
      }

      values.push(IC.envGetVariable("r" + i));
    }

    IC.envSetTemp("startIndex", startIndex);
    IC.envSetTemp("endIndex", maxIndex);

    return values;
  },

  /** metadata for the last accessed rowrange */
  envGetRowRangeMeta() {
    let startIndex = IC.envGetTemp("startIndex");
    let endIndex = IC.envGetTemp("endIndex");

    let data = [];
    for (var i = startIndex; i <= endIndex; i++) {
      const idx = i - 1;
      data.push({ title: IC.info.titles[idx], comment: IC.info.comments[idx] });
    }

    return data;
  },


}

const ObjectModule = {
  val(a) {
    if (a instanceof Date) return a;
    if (a instanceof Array) return IC.ICArrayLiteralUnwrap(a); // must happen before object check
    // Use isICObject() - not just hasOwnProperty("v") - to avoid false positives
    // when custom objects happen to have a property named 'v' (e.g., ball = { mass: 2 kg, v: 10 m/s })
    if (IC.isICObject(a)) return a.v;

    return a;
  },

  // native IC object vs generic object
  isICObject: function (obj) {
    //required keys 'v' and 't', and no extra keys (except _meta and _debug which are ignored)
    return obj instanceof Object &&
      obj.hasOwnProperty('v') &&
      obj.hasOwnProperty('t') &&
      Object.keys(obj).every(key => ['v', 't', 'f', '_meta', '_debug'].includes(key));
  },

  isCustomObject: function (obj) {
    return obj instanceof Object && !IC.isICObject(obj);
  },

  isCustomFunction: function (obj) {
    return (obj instanceof Object) && obj?.type === "function";
  },

  /**
   * Check if a line contains a conversion operator (->)
   * Used to detect explicit unit conversions like "100 USD -> EUR"
   * @param {string} line - The processed input line
   * @returns {boolean} True if line contains conversion operator
   */
  containsConversion: function (line) {
    return line && line.includes('->');
  },

  anyIsObject: function (a, b) {
    return (IC.isICObject(a) && a) || (IC.isICObject(b) && b) || null;
  },

  anyIsCustomObject: function (a, b) {
    return (IC.isCustomObject(a) && a) || (IC.isCustomObject(b) && b) || null;
  },

  // add or subtract: 4 inches + 3cm shoudl convert everything to cm (if possible and add). otheriwse just add
  allAreObjects: function (a, b) {
    return IC.isICObject(a) && IC.isICObject(b);
  },

  // either is a valid datestring
  anyIsDate: function (aVal, bVal) {
    if (typeof aVal == "string" || typeof bVal == "string") {
      return ICDate.isDateString(aVal) || ICDate.isDateString(bVal);
    }
    return false;
  },

  /**
    *
    * @typedef {Object} ICObject
    * @property {number} v - value
    * @property {ICType} t - type {m: 2, s: 2}
    * @property {string|null} f - format
    */

  /**
   *
   * @typedef {Object.<string, number>} ICType - Maps unit to powers, {m: 2, s: 2}
   */

  _makeICObjectUnitType: function (type) {
    let val = 1;
    const createObject = (t = {}) => ({ v: val, t: t });

    type = type.trim();
    if (type.includes("\\")) {
      type = IC.parser.unescapeUnicode(type);
      type = (type || "").toString(); // for TS
    }

    // note: units are really multiplied and divided, and should use the same flow.

    // rates: "miles/hour", "m / s / kg"
    const [numerator, ...denominators] = type.split("/");
    if (denominators.length) {
      return denominators.reduce(
        // passing collapse = false: don't want hz/day turned into cycles / sec^2
        (result, unit) => IC.mathDiv(result, IC._makeICObjectTypeOnly(1, unit), false),
        IC._makeICObjectTypeOnly(val, numerator)
      );
    }

    // Handle "[currency] cents" pattern: "CAD cents" => 0.01 CAD, "AUD cents" => 0.01 AUD
    // Must come BEFORE space-separated multiplication logic to avoid CAD * cents = CAD²
    const currencyFraction = IC.parseCurrencyFraction(type);
    if (currencyFraction) {
      return { v: val * currencyFraction.factor, t: { [currencyFraction.currency]: 1 } };
    }

    // space-separated multiplications: "m^1 s^2", "kg s"
    const units = type.split(" ");
    if (units.length > 1 && units.every(IC.typestringIsPossibleUnitIncludingSlash)) {
      return units.reduce(
        (result, unit) => IC.mathMul(result, IC._makeICObjectTypeOnly(1, unit), false),
        val
      );
    }

    // cm, cm^2, sq cm...
    const [parsedType, power] = ICUnit.getUnitNameAndPower(type);
    return createObject({ [parsedType]: power });
  },

  /**
   * Create new ICObject
   * @param {number|string|Date|null} val
   * @param {string|ICType|null} type - typestring, like "cm/s"
   * @returns ICObject
   */
  _makeICObjectTypeOnly: function (val, type) {
    const createObject = (t = {}) => ({ v: val, t: t });

    if (!type || val == null) return createObject();
    if (type instanceof Object) return createObject(type);

    // Handle "[currency] cents" pattern BEFORE predefinedUnit check
    // This ensures "EUR cents" is parsed as 0.01 EUR, not as the predefined "eur cents" currency
    if (typeof type === "string") {
      const currencyFraction = IC.parseCurrencyFraction(type);
      if (currencyFraction) {
        return { v: val * currencyFraction.factor, t: { [currencyFraction.currency]: 1 } };
      }
    }

    if (IC.predefinedUnit(type)) return createObject({ [type]: 1 }); // quick return known type
    if (typeof type == "string" && IC.envGetVariable(type)) return createObject({ [type]: 1 }); // unknown type

    return IC.mathMul(val, IC._makeICObjectUnitType(type));
  },

  cloneObject: function (obj) {
    try {
      return structuredClone(obj);
    } catch (e) {
      return JSON.parse(JSON.stringify(obj));
    }
  },

  /**
   * Create new ICObject
   * @param {number|string|Date|null} val
   * @param {string|ICType|null} type
   * @param {string} format
   * @returns ICObject
   */
  makeICObject: function (val, type = {}, format = "") {
    let ret = IC._makeICObjectTypeOnly(val, type);

    // TODO: figure out why ret is coming back null
    if (ret.t) {
      for (let unit in ret.t) {
        if (IC.typestringHasUnitWarning(unit) && false) return null;
        IC.logNewUnit(unit);
      }
    }

    ret.f = format.toLowerCase().trim();
    return IC.cloneObject(ret);
  },

  ICObjectSetFormat(obj, format) {
    if (obj instanceof Object && typeof format === "string") {
      obj.f = format.toLowerCase().trim();
    }
  },

  ICObjectPrettyPrint(obj) {
    if (!IC.isICObject(obj)) {
      return String(obj);
    }

    let value = IC.val(obj);
    let type = IC.typestring(obj.t);
    let format = IC.format(obj);

    let result = value;

    // Apply formatting if available
    if (format) {
      result = IC.parser.formatResult(obj, IC.envGetSettings());
    }

    // Add type if available
    if (type) {
      result += ` ${type}`;
    }

    return result;
  },

  format(obj) {
    return obj?.f ?? "";
  },

  /**
   * Combine individual display formats 
   * @param {string} formatA 
   * @param {string} formatB 
   * @param {boolean} mergeOk -- whether to merge format if they both are defined
   * @returns 
   */
  ICObjectFormatMerge(formatA, formatB, mergeOk) {

    // combine percentages if they are both there
    if (formatA === "percent" || formatB === "percent") {
      return formatA === formatB ? formatA : "";
    }

    // If either formatA or formatB is not defined, return the other
    if (!formatA || !formatB) {
      return formatA || formatB;
    }

    // 3' + 4' = 7'
    if (mergeOk) {
      return formatA;
    }

    // 3' * 4' = 12 sq ft
    return "";
  },

  // if type is "inverse", flip it. 6 hour^-1 => 1/6 hour
  ICObjectOptionalInvert: function (obj) {
    if (IC.hasNoType(obj)) {
      return obj;
    }

    let allNegative = Object.values(obj.t).every((v) => v < 0);
    if (allNegative) {
      return IC.mathDiv(1, obj);
    }
    return obj;
  },

}

const LiteralModule = {
  makeNumber(n, settings = {}) {
    let locale = settings?.locale || "en-US";

    // Handle different input types
    if (typeof n === 'number') {
      return n;
    }
    if (typeof n === 'boolean') {
      return n ? 1 : 0;
    }
    if (typeof n !== 'string') {
      return NaN;
    }

    n = n.replace(/_/g, "");
    let float = IC.parseLocaleNumber(n, locale);
    return float;
  },

  makeBoolean: function (str) {
    return str.toLowerCase() == "true";
  },

  makeString: function (str) {
    return str;
  },

  makeNumberInBase: function (n, base) {
    let val;
    if (typeof n == "string") {
      let [intPart, fracPart] = n.replace(/_/g, "").split(".");
      val = parseInt(intPart, base);
      if (fracPart) {
        for (let i = 0; i < fracPart.length; i++) {
          val += parseInt(fracPart[i], base) / Math.pow(base, i + 1);
        }
      }
    } else {
      val = n;
    }
    return val;
  },

  makeHexadecimal: function (n) {
    let val = this.makeNumberInBase(n, 16);
    return IC.makeICObject(val, null, "hex");
  },

  makeOctal: function (n) {
    let val = this.makeNumberInBase(n, 8);
    return IC.makeICObject(val, null, "oct");
  },

  makeBinary: function (n) {
    let val = this.makeNumberInBase(n, 2);
    return IC.makeICObject(val, null, "bin");
  },

  /** 
     * Parse a localized number to a float. https://stackoverflow.com/questions/29255843/is-there-a-way-to-reverse-the-formatting-by-intl-numberformat-in-javascript
     * @param {string} stringNumber - the localized number
     * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
     */
  REGEX_SEP: {},
  parseLocaleNumber(stringNumber, locale) {
    IC.REGEX_SEP["t" + locale] ||= Intl.NumberFormat(locale).format(11111).replace(/\p{Number}/gu, '');
    IC.REGEX_SEP["d" + locale] ||= Intl.NumberFormat(locale).format(1.1).replace(/\p{Number}/gu, '');

    IC.REGEX_SEP["tregex" + locale] ||= new RegExp('\\' + IC.REGEX_SEP["t" + locale], 'g');
    IC.REGEX_SEP["dregex" + locale] ||= new RegExp('\\' + IC.REGEX_SEP["d" + locale]);

    return parseFloat(stringNumber
      .replace(IC.REGEX_SEP["tregex" + locale], '')
      .replace(IC.REGEX_SEP["dregex" + locale], '.')
    );
  },
}

const TypeModule = {
  // @param string parsedType - the type string
  // @returns Boolean
  typestringHasUnitWarning: function (parsedType) {
    if (IC.envIsVariableDefined(parsedType)) {
      const unitRet = IC.predefinedUnit(parsedType);
      const unitMsg = unitRet?.name ? `type: ${unitRet.name}` : "";
      const msg = unitRet ?
        `warning: using '${parsedType}' as unit, not var` : "";
      /* `error: cannot use ${parsedType} as unit`; */
      if (msg) IC.log(msg);
      if (!unitRet) return true;
    }

    return false;
  },

  typePowerCount: function (typeA = {}) {
    return Object.values(typeA)
      .map((v) => Math.abs(v))
      .reduce((a, b) => a + b, 0);
  },

  typePowerCountNet: function (typeA = {}) {
    return Object.values(typeA)
      .reduce((a, b) => a + b, 0);
  },

  // magic-solve for missing constraint
  // 15 mph @ 10 miles = time (10 miles / 15 mph)
  // 10 miles @ 15 mph = distance (10 miles * 15 mph)
  // 15 mph @ 10 minutes = distance (15 mph * 10 minutes)
  typeSolve: function (objA, objB, tries = 1) {

    let typeA = objA?.t || {}; // could be $15 / day @ $50  
    let typeB = objB?.t || {};

    let typestringA = IC.typestring(typeA);
    let typestringB = IC.typestring(typeB);

    let maxPowerCount = Math.max(IC.typePowerCount(typeA), IC.typePowerCount(typeB));

    // don't flatten information units - let the best matchup be used
    let AmulB = IC.mathMul(objA, objB, false);
    let AdivB = IC.ICObjectOptionalInvert(IC.mathDiv(objA, objB));
    let BdivA = IC.ICObjectOptionalInvert(IC.mathDiv(objB, objA));

    if (IC.hasNoType(objA) || IC.hasNoType(objB)) {
      let best = [AmulB, AdivB, BdivA].map((obj) => {
        return { obj: obj, netPowerCount: IC.typePowerCountNet(obj.t) }
      }).sort((a, b) => b.netPowerCount - a.netPowerCount)[0].obj; // reverse sort
      IC.log(`autosolve: ${typestringA || "unitless"} & ${typestringB || "unitless"} -> ${IC.typestring(best.t)}`);
      // remove currency formatting, likely used as unit...
      if (IC.envGetFormatter()?.code) {
        IC.envSetFormatter(null);
      }
      return best;
    }

    if (IC.typePowerCount(AmulB.t) <= maxPowerCount) {
      IC.log(`autosolve: ${typestringA} & ${typestringB} → ${IC.typestring(AmulB.t)}`);
      return AmulB;
    }

    if (IC.typePowerCount(AdivB.t) <= maxPowerCount) {
      IC.log(`autosolve: ${typestringA} & ${typestringB} → ${IC.typestring(AdivB.t)}`);
      return AdivB;
    }

    if (IC.typePowerCount(BdivA.t) <= maxPowerCount) {
      IC.log(`autosolve: ${typestringB} & ${typestringA} → ${IC.typestring(BdivA.t)}`);
      return BdivA;
    }

    let flexA = IC.ICObjectReplaceFlexibleUnits(objA);
    let flexB = IC.ICObjectReplaceFlexibleUnits(objB);

    if (tries) {
      if (
        IC.typeSimilarityByString(typestringA, IC.typestring(flexA.t)) == 0 ||
        IC.typeSimilarityByString(typestringB, IC.typestring(flexB.t)) == 0
      ) {
        return IC.typeSolve(flexA, flexB, tries - 1);
      }
    }

    IC.log({ type: 'warning', message: `cannot autosolve: ${typestringA} & ${typestringB}` });
    return null;
  },

  // combine types from A to B, with B having a change direction (+1 add, -1 to remove)
  // if similar types combine (cm & in), include scaling factor
  // changeDir: +1 or -1 (to subtract types)
  // 0 for smart convert (cm/s into inches => in/s)
  _typeMultiply: function (typeA, typeB, changeDir, consolidate = true, dateCacheKey = null) {
    let scale = 1;

    /** @type {ICType} */
    let merged = {};
    let processedKeys = {};

    const typeAKeys = Object.keys(typeA ?? {});
    const typeBKeys = Object.keys(typeB ?? {});

    function updateMatches(unit) {
      let foundInTypeB = false;
      typeBKeys.forEach((key) => {
        // check whether base units match, we handle the power cancel on our own
        // TODO: fix this to use the actual scale, like acre & m^2
        if (!processedKeys[key] && (IC.typestringIsSameUnitCategory(unit, key) || ICUnit.unitMatch(unit, 1, key, 1))) {
          let ratio = ICUnit.convertKnownUnitMatch(key, unit) || 1; // 1 for custom units, or functions

          let powerChange = typeB[key] * changeDir;
          scale *= Math.pow(ratio, powerChange);
          let resultPower = typeA[unit] + powerChange;

          if (powerChange == 0) {
            // smart conversion: cm/s to feet => feet/s [shrinks]
            // cm/s to min => cm/min [gets larger]
            // the actual direction of type B doesn't matter, just that it matches a unit type A
            scale *= Math.pow(ratio, -typeA[unit]);
            merged[key] = typeA[unit]; // key from b, with original power from A
          } else if (resultPower != 0) {
            merged[unit] = resultPower;
          }

          processedKeys[key] = true;
          foundInTypeB = true;
        }
      });
      if (!foundInTypeB) {
        merged[unit] = typeA[unit];
      }
    }

    // for every key in A, see if it matches units in B
    typeAKeys.forEach((key) => updateMatches(key));

    // include remaining keys from B
    typeBKeys.filter(key => !processedKeys[key]).forEach((key) => {
      merged[key] = typeB[key] * changeDir;
    });

    let ret = IC.makeICObject(scale, merged);
    let keycount = Object.keys(merged).length;
    if (keycount == 0 && typeAKeys.length && typeBKeys.length) {
      IC.envSetHasCancelledUnit();
    }

    // can lead to infinite loop with mul
    if (keycount && consolidate) {
      ret = IC.ICObjectConsolidateUnits(ret);
    }

    return ret;
  },

  typeMultiply: function (typeA, typeB, changeDir, consolidate = true) {
    return IC._typeMultiply(typeA, typeB, changeDir, consolidate, LIB.InstaCalc.Unit._updated);
  },

  checkFailedConversionWarnings(fromType, toType) {
    let parts = (fromType + " " + toType).split(/\W+/);
    let replacementUnit = parts
      .map((p) => CONFIG.CONVERSION_WARNING_LIST[p])
      .filter((r) => r)
      .join("");
    return replacementUnit;
  },

  // Does this conversion leave a difference of only flex types?
  isFlexTypeConversion: function (fromType, toType) {
    let typeMultiplyResult = IC.typeMultiply(fromType, toType, 1);
    let flexConversion = Object.keys(typeMultiplyResult.t).every(key => CONFIG.FLEX_UNIT_CONVERSIONS[key]);
    return [flexConversion, typeMultiplyResult];
  },

  /* 
   Attempt to convert to a flexible unit, if possible (Hz, cycles)
   */
  ICObjectConvertToFlexType(x, unitObj, typeMultiplyResult, unitName) {
    let tmUnitObj = IC.makeICObject(1, typeMultiplyResult.t);
    let positiveConversion = Object.values(tmUnitObj.t).some(v => v > 0);

    let conversionUnit = unitName;

    let sourceX;
    let toTypeExpanded = IC.ICObjectReplaceFlexibleUnits(unitObj).t;
    let toTypeKeysFuzzy = Object.keys(toTypeExpanded).some(key => CONFIG.FLEX_UNIT_CONVERSIONS[key]);

    // to type needs the fuzzy unit
    if (!toTypeKeysFuzzy) {
      sourceX = x;
      let conversionObject = positiveConversion ? IC.mathDiv(unitObj, tmUnitObj) : IC.mathMul(unitObj, tmUnitObj);
      conversionUnit = IC.typestring(conversionObject.t);
    }
    else {
      // from type needs the fuzzy unit
      sourceX = positiveConversion ? IC.mathDiv(x, tmUnitObj) : sourceX = IC.mathMul(tmUnitObj, x);
    }

    sourceX = IC.ICObjectReplaceFlexibleUnits(sourceX);
    let result = IC.ICObjectConvertToType(sourceX, conversionUnit, false);

    // convert units back to unitName if needed?
    return result;
  },


  /**
   * convert similar units, e.g "cm/hour to inches" will convert the cm portion, so you get inches/hour
   * @param {ICObject} x - object to convert
   * @param {string} unitName - string to convert to ("cm", "cm/s", etc)
   * @returns
   */
  ICObjectConvertToType(x, unitName, retryAllowed = true) {
    if (!IC.isValidEvalResult(x)) return null;

    unitName = ICUnit.processUnitName(unitName);

    let defaultConversionResult = ICUnit.convert(x, unitName);
    if (defaultConversionResult) {
      return defaultConversionResult;
    }

    let fromType = x.t || {};
    let unitObj = IC.makeICObject(1, unitName);
    let toType = unitObj?.t;

    if (!toType) {
      IC.log({ type: 'warning', message: `cannot convert: ${IC.typestring(fromType)} → '${unitName.trim()}'` });
      return x;
    }

    if (IC.typeIsVirtual(toType)) {
      return IC.typeConvertNumericToVirtual(IC.val(x), IC.typestring(toType));
    }

    if (unitName.match(/[+\-*]/)) {
      IC.log({ type: 'warning', message: `cannot convert: ${IC.typestring(fromType)} → '${unitName.trim()}', split up statements with ()` });
      return x;
    }

    if (IC.typestringIsSameUnitAndScale(IC.typestring(fromType), unitName)) {
      return x;
    }

    if (ICDate.isDateTimeObject(x)) {
      return ICDate.convertTo(x, unitName);
    }

    // at this point, we have a partial conversion, like "cm/s -> m"
    let ret, typeMultiplyResult, isFlexTypeConversion;
    [isFlexTypeConversion, typeMultiplyResult] = IC.isFlexTypeConversion(fromType, toType);

    // check for "fuzzy" units that need to be expanded
    if (isFlexTypeConversion && retryAllowed) {
      return IC.ICObjectConvertToFlexType(x, unitObj, typeMultiplyResult, unitName);
    }

    // if toType has numeric keys, we need to simplify it (400 w => w)
    const hasNumericKeys = Object.keys(toType).some(k => k.match(/\d/));
    const fullyNumericKey = Object.keys(toType).find(k => {
      if (ICUnit.unitStringHasNumber(k)) {
        let [n, unit] = ICUnit.unitStringExtractNumber(k);
        return unit === '';
      }
      return false;
    });

    if (hasNumericKeys) {
      let simplifiedObj = IC.typeSimplifyIntoObject(toType);
      typeMultiplyResult = IC.typeMultiply(fromType, simplifiedObj.t, 0);

      let ratio = IC.mathDiv(x, simplifiedObj); // x / obj
      ret = ratio;

      let inverseType = IC.hasNoType(IC.typeMultiply(fromType, simplifiedObj.t, 1));
      if (inverseType) {
        ret = IC.mathInverse(IC.mathMul(x, simplifiedObj)); // 1 / (x * obj)
      }

      // 10 mbit/sec in minutes/1 gb => finding time to get a single gb, not scaling the rate
      let isUnitRate = IC.val(simplifiedObj) === 1;
      if (isUnitRate) {
        const scaleVal = IC.mathPreciseDivide(IC.mathPreciseDivide(1, IC.val(typeMultiplyResult)), IC.val(x));
        const scale = IC.makeICObject(scaleVal, null);
        ret = scale;
      }

      // $25/hour in hours until 2000 : "2000" (unitless) becomes a key
      if (fullyNumericKey) {
        ret = IC.mathDiv(IC.mathInverse(simplifiedObj), x);
      }

      // clone toType, scan keys, see if any match variables in the environment, replace that
      // TODO: look in execution log for replaced variables, and use that for replacement
      let toTypeClone = { ...toType };

      // let identifiers = IC.envGetIdentifiers();
      let identifiers = IC.envGetVariablesOnly();

      for (let key in toTypeClone) {
        let currentInput = IC.envGetCurrentInput();
        let tokens = currentInput.split(" ");

        let [n, unit] = ICUnit.unitStringExtractNumber(key);
        if (!n) {
          continue;
        }

        let idKeys = Object.keys(identifiers);

        idKeys.forEach((id) => {
          if (!currentInput.toLowerCase().includes(id.toLowerCase())) {
            return;
          };

          let idValue = identifiers[id];
          let keyValue = IC.makeICObject(n, unit);

          // check equality -- division is 1
          let divResult = IC.mathDiv(idValue, keyValue);
          if (IC.mathEqualWithinThreshold(1, IC.val(divResult))) {
            toTypeClone[id] = toTypeClone[key];
            delete toTypeClone[key];
          }
        });
      }
      ret.t = { ...toTypeClone };

      // ret.t = { ...toType };
    }
    else {
      typeMultiplyResult = IC.typeMultiply(fromType, toType, 0);
      ret = IC.mathMul(IC.val(x), typeMultiplyResult);
    }

    // 15 -> cm is OK
    // 15 m/s to cm is OK
    // 15 m -> cm/s not OK
    let fromKeyCount = Object.keys(fromType).length;
    let toKeyCount = Object.keys(toType).length;
    let sameOrBetterTypeComplexity = fromKeyCount == 0 || toKeyCount <= fromKeyCount;
    let hasDifferentType = !IC.typeIsSameNormalizedObject(ret.t, fromType);

    // basic conversion
    if (sameOrBetterTypeComplexity && hasDifferentType) {
      IC.log({ type: 'convert', from: IC.typestring(fromType), to: IC.typestring(typeMultiplyResult.t) });
      return ret;
    }

    // inverse conversion
    let inverseObjA = IC.mathInverse(x);
    ret = IC.makeICObject(IC.val(IC.mathDiv(IC.val(inverseObjA), typeMultiplyResult)), toType);
    let numbersAreNotEqual = !IC.mathEqualWithinThreshold(IC.val(ret), IC.val(inverseObjA));
    if (numbersAreNotEqual && sameOrBetterTypeComplexity) {
      // TODO: this only triggers for hz and ms right now; move hz to IMPLIED_CONVERSIONS
      IC.log({ type: 'convert', from: IC.typestring(fromType), to: IC.typestring(typeMultiplyResult.t) });
      return ret;
    }

    // try switching oz with fl oz
    let [swappedConversionResult, swappedObj] = ICUnit.convertWithUnitSwap(x, unitName);
    if (swappedConversionResult) {
      IC.log({ type: 'convert', from: IC.typestring(swappedObj.t), to: IC.typestring(swappedConversionResult.t), assumed: true });
      return swappedConversionResult;
    }

    // implied units (dollars, bytes, USD), ie: $.15/mile in USD per 100 miles
    for (const impliedConversion of CONFIG.IMPLIED_CONVERSIONS) {
      if (impliedConversion.match(toType) && !impliedConversion.exclude(fromType)) {
        let objAWithUnit = IC.mathMul(x, IC.makeICObject(1, impliedConversion.unit));
        let result = IC.semanticConvert(objAWithUnit, unitName);
        return result;
      }
    }

    // same base units: N -> kg m s^-2
    let [fromBaseUnits, fromUnitScale] = ICUnit.convertToBaseUnits(fromType);
    let [toBaseUnits, toUnitScale] = ICUnit.convertToBaseUnits(toType);
    if (ICUnit.isSameUnitObject(fromBaseUnits, toBaseUnits)) {
      let finalScale = IC.mathPreciseMultiply(IC.val(x), fromUnitScale / toUnitScale);
      let final = IC.makeICObject(finalScale, unitName);

      IC.log({ type: 'convert', from: IC.typestring(fromType), to: IC.typestring(toType) });
      return final;
    };

    // "wh -> joule/sec" should not be allowed
    let isGainingComplexity = IC.typePowerCount(fromBaseUnits) < IC.typePowerCount(toBaseUnits);
    // TODO: this should be if the from type is CONTAINED in the target, no conversion possible
    // L -> cm is possible

    if (!isGainingComplexity) {

      // fuzzy conversion: 15 uW s / cm in nJ
      let collapsedResult = ICUnit.autoCollapse(IC.typeMultiply(fromType, toType, -1));
      if (collapsedResult && IC.typePowerCount(collapsedResult.t) <= IC.typePowerCount(fromType)) {
        let final = IC.mathMul(IC.val(x), collapsedResult);
        final.t = { ...final.t, ...toType };

        IC.log({ type: 'convert', from: IC.typestring(fromType), to: IC.typestring(final.t), fuzzy: true });
        return final;
      }
    }

    // no conversion
    let replacementUnit = IC.checkFailedConversionWarnings(fromType, unitName).trim();
    let replacementText = replacementUnit ? `, try ${replacementUnit}` : "";
    IC.log({ type: 'warning', message: `cannot convert: ${IC.typestring(fromType)} → ${unitName}${replacementText}` });
    return x;
  },

  typeIsVirtual: function (typeObjOrString) {
    const VIRTUAL_CATEGORIES = ["ppm", "bel"];
    let typestring = typeof typeObjOrString === "string" ? typeObjOrString : IC.typestring(typeObjOrString);

    for (let category of VIRTUAL_CATEGORIES) {
      if (IC.typestringIsSameUnitCategory(typestring, category)) {
        return category;
      }
    }

    return null;
  },

  typeConvertVirtualToNumeric: function (obj) {
    let ret;

    let category = IC.typeIsVirtual(obj?.t);
    if (category == "bel") {
      ret = IC.ICObjectConvertToType(obj, "bel");
      return Math.pow(10, IC.val(ret));
    }

    // all others assume ppm equivalent
    ret = IC.ICObjectConvertToType(obj, "ppm");
    return IC.val(ret) * 1e-6;
  },

  typeConvertNumericToVirtual: function (obj, targetUnit = "ppm") {
    let amount;
    if (IC.typestringIsSameUnitCategory(targetUnit, "bel")) {
      amount = Math.log10(IC.val(obj)); // converted to bels
    }
    else {
      amount = IC.mathPreciseDivide(IC.val(obj), 1e-6); // ppm default
    }

    let scale = ICUnit.getUnitScale(targetUnit);
    let scaledAmount = IC.mathPreciseDivide(amount, scale);
    let ret = IC.makeICObject(scaledAmount, targetUnit);

    return ret;
  },

  typeIsByte: function (typeObjOrString) {
    let typestring = typeof typeObjOrString === "string" ? typeObjOrString : IC.typestring(typeObjOrString);
    return IC.typestringIsSameUnitCategory(typestring, "byte");
  },

  typeIsCurrency: function (typeObjOrString) {
    let typestring = typeof typeObjOrString === "string" ? typeObjOrString : IC.typestring(typeObjOrString);
    return IC.typestringIsSameUnitCategory(typestring, "usd");
  },

  typeConvertByteToNumber: function (obj) {
    let ret = IC.ICObjectConvertToType(obj, "byte");
    IC.log(`treat bytes as number`);
    return IC.val(ret);
  },

  /*
   @param typeObj - unit type
   @param useTitle (default false) - use formatted title (gb -> GB)
   */
  typestring: function (typeObj, useTitle = false) {
    if (!typeObj) return "";
    let posTypes = "";
    let negTypes = "";

    Object.keys(typeObj).forEach(function (key) {
      let name = key;
      let pow = typeObj[key];
      let unitLabel = "";

      let powScale = Math.abs(pow);
      if (powScale > 1 && Number.isInteger(powScale)) {
        unitLabel = "^" + powScale;
      }

      // Strip STOCK_ prefix for display: STOCK_MSFT -> MSFT shares, STOCK_BRK_A -> BRK.A shares
      let isStock = false;
      if (name.startsWith('STOCK_')) {
        name = name.substring(6).replace(/_/g, '.');
        isStock = true;
      } else {
        // Check if it's a famous stock ticker (AAPL, MSFT, etc.)
        const keywords = (typeof IC_KEYWORDS !== 'undefined') ? IC_KEYWORDS :
          (typeof window !== 'undefined' && window.IC_KEYWORDS) ? window.IC_KEYWORDS : null;
        if (keywords?.isFamousTicker?.(name)) {
          isStock = true;
        }
      }
      // Add "shares" suffix when result represents share count (pow = 1)
      if (isStock && pow === 1) {
        name = name + ' shares';
      }

      name = IC.unitDisplayName(useTitle ? IC.envGetUnitTitle(name) : name, pow);

      if (pow > 0) {
        posTypes += name + unitLabel + " ";
      }
      if (pow < 0) {
        negTypes += name + unitLabel + " ";
      }
    });

    posTypes = posTypes.trim();
    negTypes = negTypes.trim();

    // no negatives
    if (!negTypes) {
      return posTypes;
    }

    return `${posTypes || "1"}/${negTypes}`;
  },

  // Finds shortest unit name: centimeters -> cm, cows -> cow 
  typestringNormalize: function (unitStr) {
    unitStr = unitStr.toLowerCase().trim();
    let obj = ICUnit.getUnit(unitStr);
    if (obj) {
      let value = obj[unitStr];

      // search object for all keys with the same value and take the shortest key by length
      let shortestKey = Object.keys(obj).filter(name => name != "base").reduce((a, b) =>
        (obj[b] === value && b.length < a.length) ? b : a, unitStr
      );

      return shortestKey;
    }

    return IC.stringSingularize(unitStr);
  },

  // just for hz currently
  // convert hz to cycles/second
  ICObjectReplaceFlexibleUnits: function (obj) {
    if (IC.hasNoType(obj)) {
      return obj;
    }

    // has no information units at multiple powers
    let hasFlexibleUnits = Object.keys(obj.t).some((unit) => CONFIG.FLEX_UNIT_ALIAS[unit.toLowerCase()]);
    if (!hasFlexibleUnits) {
      return obj;
    }

    let scale = IC.val(obj);
    let ret = scale; // will be multiplied

    Object.keys(obj.t).forEach((unit) => {
      let pow = obj.t[unit];
      let entry = CONFIG.FLEX_UNIT_ALIAS[unit.toLowerCase()];
      let newUnit = null;
      if (entry) {
        newUnit = IC.makeICObject(entry.scale, entry.type);
      } else {
        newUnit = IC.makeICObject(1, unit);
      }

      // do not consolidate, which can lead to infinite loop
      newUnit = IC.mathPow(newUnit, pow, false);
      ret = IC.mathMul(ret, newUnit, false);
    })

    // return orig or new
    return scale == ret ? obj : ret;
  },

  // flatten 1kb x 1kb into "bytes"
  ICObjectReplaceInformationUnits: function (obj) {
    if (IC.hasNoType(obj)) {
      return obj;
    }

    // has no information units at multiple powers
    let hasPowersOfInfoUnit = Object.keys(obj.t).some((unit) => obj.t[unit] > 1 && IC.typeIsByte(unit));
    if (!hasPowersOfInfoUnit) {
      return obj;
    }

    let scale = IC.val(obj);
    let ret = scale;

    Object.keys(obj.t).forEach((unit) => {
      let pow = obj.t[unit];
      let newUnit = null;
      if (pow > 1 && IC.typeIsByte(unit)) {
        let log = IC?.logGetCurrentRow()?.toString() || "";
        if (!log.includes("flattening")) {
          IC.log(`flattening ${unit}^${pow} → bytes`);
        }

        let typescale = ICUnit.typeConversionScale(unit, "byte");
        newUnit = IC.makeICObject(Math.pow(typescale, pow), "bytes");
      }
      else {
        newUnit = IC.makeICObject(1, unit);
      }

      ret = IC.mathMul(ret, newUnit, false);
    });

    return scale == ret ? obj : ret;
  },

  // if we have (meter/sec) * hour, reduce to ("meter") with the right scaling factor
  // or, if we have "hz" and second, try to reduce
  ICObjectConsolidateUnits: function (obj) {
    if (IC.hasNoType(obj)) {
      return obj;
    }

    let ret = IC.ICObjectReplaceFlexibleUnits(obj);
    ret = IC.ICObjectReplaceInformationUnits(ret);
    return ret;
  },

  // feet / cm => no type
  typeIsCancelled: function (type) {
    if (Object.keys(type).length < 2) {
      return false;
    }

    let typeDir = { pos: "", posPower: 0, neg: "", negPower: 0 };
    for (var key in type) {
      if (type[key] > 0) {
        typeDir.pos = key;
        typeDir.posPower = type[key];
      } else {
        typeDir.neg = key;
        typeDir.negPower = type[key];
      }
    }

    if (
      typeDir.pos &&
      typeDir.neg &&
      Math.abs(typeDir.posPower) == Math.abs(typeDir.negPower) &&
      IC.typestringIsSameUnitCategory(typeDir.pos, typeDir.neg)
    ) {
      return true;
    }

    return false;
  },

  /*
  @param {ICObject} obj
  */
  hasNoType(obj) {
    return !obj || !obj.t || Object.keys(obj.t).length === 0;
  },

  /*
  @param {ICObject} obj
  */
  hasType(obj) {
    return !IC.hasNoType(obj);
  },

  /* 
  @param {ICType|string|null} typeA
  @param {ICType|string|null} typeB
  @returns {boolean} - true if types can be compared: (days with weeks) == true
  */
  typeIsEquivalent: function (typeA, typeB) {
    if (typeof typeA === "string") {
      typeA = IC.makeICObject(1, typeA).t;
    }

    if (typeof typeB === "string") {
      typeB = IC.makeICObject(1, typeB).t;
    }

    let result = IC.typeMultiply(typeA, typeB, -1);
    return IC.hasNoType(result);
  },

  /*
  @param {string} typeStrA
  @param {string} typeStrB
  @returns {boolean} - true if identical scale in their Unit object cm and centimeter (but not cm and ft)
  */
  typestringIsSameUnitAndScale: function (typeStrA, typeStrB) {
    let unitA = ICUnit.getUnit(typeStrA);
    let unitB = ICUnit.getUnit(typeStrB);

    return unitA && unitB && unitA == unitB && unitA[typeStrA.trim().toLowerCase()] == unitB[typeStrB.trim().toLowerCase()];
  },

  typestringIsSameUnitCategory: function (typeStrA, typeStrB) {
    let unitA = ICUnit.getUnitCaseInsensitive(typeStrA);
    let unitB = ICUnit.getUnitCaseInsensitive(typeStrB);

    return unitA && unitB && unitA == unitB;
  },

  typeHasUnitInCategory: function (typeObj, category) {
    return Object.keys(typeObj).some((unit) => IC.typestringIsSameUnitCategory(unit, category));
  },

  // check if exists as combination of some units
  typestringIsPossibleUnitIncludingSlash: function (str) {

    if (!str) return false;
    str = str.trim();

    // could be "2"
    if (str.match(/^\d+$/)) {
      return false;
    }

    // miles/hour
    if (str.includes("/")) {
      let parts = str.split("/");
      return IC.typestringIsPossibleUnitIncludingSlash(parts[0]) && IC.typestringIsPossibleUnitIncludingSlash(parts[1]);
    }

    // exponent unit: cm^2
    if (str.includes("^")) {
      let base = str.split("^")[0];
      return IC.typestringIsPossibleUnitIncludingSlash(base);
    }

    return IC.predefinedUnit(str);
  },

  /*
   @param {ICType} typeA
   @param {ICType} typeB
   @returns {boolean} true if typeA and typeB are the same unit object
   */
  typeIsSameNormalizedObject(typeA = {}, typeB = {}) {

    // Function to normalize and filter out zero values and empty keys
    const normalizeAndFilter = (type) => {
      return Object.fromEntries(
        Object.entries(type)
          .map(([key, value]) => [IC.typestringNormalize(key) || key, value])
          .filter(([key, value]) => key && value !== 0)
      );
    };

    const normalizedTypeA = normalizeAndFilter(typeA);
    const normalizedTypeB = normalizeAndFilter(typeB);

    // Checking if both objects have same normalized keys with same values
    const keysA = Object.keys(normalizedTypeA);
    const keysB = Object.keys(normalizedTypeB);

    if (keysA.length !== keysB.length) return false;

    return keysA.every(key => key in normalizedTypeB && normalizedTypeB[key] === normalizedTypeA[key]);
  },


  /**
    * return 1 if units same, -1 if inverse, 0 otherwise 
    * @param {string} typeStrA 
    * @param {string} typeStrB 
    * @returns 
    */
  typeSimilarityByString(typeStrA, typeStrB) {
    if (!typeStrA || !typeStrB) {
      return 0;
    }

    if (typeStrA == typeStrB) {
      return 1;
    }

    const [numerator, denominator] = typeStrA.split("/");
    if (`${denominator}/${numerator}` === typeStrB) {
      return -1;
    }
    return 0;
  },

  typeSimilarityByObject(typeA, typeB) {
    const keysA = Object.keys(typeA);
    const keysB = Object.keys(typeB);

    if (keysA.length !== keysB.length) return 0;

    let similarity = 1;
    for (let key of keysA) {
      // keys are different...
      if (typeA[key] !== typeB[key]) {
        // as long as they are opposite, we are fine
        if (typeA[key] === -typeB[key]) {
          similarity = -1;
        } else {
          // not opposite, early return
          return 0;
        }
      }
    }

    return similarity;
  },

  typeInverse(typeObj) {
    let type = {};
    for (const [key, value] of Object.entries(typeObj)) {
      type[key] = value * -1;
    }
    return type;
  },

  // true if some type has negative power
  typeObjHasNegativeType(typeObj) {
    return Object.values(typeObj).some(value => value < 0);
  },

  // turn a type like {"400 w": 1} into {w: 1} with a scaling factor
  typeSimplifyIntoObject(typeObj) {
    let rate = 1;
    /* @type {ICType} */
    let simplifiedType = {};

    Object.entries(typeObj).forEach(([key, power]) => {
      if (ICUnit.unitStringHasNumber(key)) {
        let [n, restOfUnit] = ICUnit.unitStringExtractNumber(key);
        rate *= Math.pow(n, power);

        if (restOfUnit) {
          // If there's a remaining unit after extracting the number
          if (simplifiedType[restOfUnit]) {
            simplifiedType[restOfUnit] += power;
          } else {
            simplifiedType[restOfUnit] = power;
          }
        }
      } else {
        // If the key doesn't have a number, keep it as is
        if (simplifiedType[key]) {
          simplifiedType[key] += power;
        } else {
          simplifiedType[key] = power;
        }
      }
    });

    // Remove any keys with zero power
    Object.keys(simplifiedType).forEach(key => {
      if (simplifiedType[key] === 0) {
        delete simplifiedType[key];
      }
    });

    // Handle flexible units
    let simplifiedObj = IC.makeICObject(1, simplifiedType);
    simplifiedObj = IC.ICObjectReplaceFlexibleUnits(simplifiedObj);

    return IC.makeICObject(rate, simplifiedObj.t);
  },

  // turn a type like {"400 w": 1} into {w: 1} with a scaling factor
  typeSimplifyIntoObjectOld(typeObj) {
    let rate = 1;
    let swappedKey = {};
    let simplifiedType = { ...typeObj };
    Object.keys(typeObj).forEach((key) => {
      if (ICUnit.unitStringHasNumber(key)) {
        let [n, restOfUnit] = ICUnit.unitStringExtractNumber(key);
        rate *= Math.pow(n, -1 * typeObj[key]);
        swappedKey[key] = restOfUnit;
      }
    });

    if (Object.keys(swappedKey).length) {
      Object.keys(typeObj).forEach((key) => {
        let actualKey = swappedKey[key] || key;
        simplifiedType[actualKey] = typeObj[key];
      });

      let simplifiedObj = IC.makeICObject(1, simplifiedType);
      simplifiedObj = IC.ICObjectReplaceFlexibleUnits(simplifiedObj);
    }

    return IC.makeICObject(rate, simplifiedType);
  },



}

const StringModule = {
  stringSingularize: function (word = "") {
    if (IC.predefinedUnit(word) && word.endsWith("s")) {
      let testSingular = LIB.Inflector.singularize(word);
      if (IC.typestringIsSameUnitAndScale(testSingular, word)) {
        return testSingular;
      }
    }
    return LIB.Inflector.singularize(word);
  },

  stringPluralize: function (word = "") {
    if (IC.predefinedUnit(word) && !word.endsWith("s")) {
      let testPlural = LIB.Inflector.pluralize(word);
      if (IC.typestringIsSameUnitAndScale(testPlural, word)) {
        return testPlural;
      }
    }
    return LIB.Inflector.pluralize(word);
  },

  stringIsValidSingularUnit: function (unit = "") {
    return unit && unit.length >= 3 && !unit.match(/\d/);
  },

  unitDisplayName: function (unit, pow) {

    if (IC.predefinedUnit(unit)) {
      unit = ICUnit.formatSIUnit(unit);
    }

    // in denominator
    if (pow < 0 && IC.stringIsValidSingularUnit(unit)) {
      let baseName = IC.stringSingularize(unit);
      unit = baseName;
    }

    if (IC.stringIsCurrencyUnit(unit)) {
      if (unit.length == 3) {
        unit = unit.toUpperCase();
      }
    }

    return unit;
  },

  stringIsKeyword: function (str) {
    return CONFIG.KEYWORDS.hasOwnProperty(str.toLowerCase());
  },

  stringIsCurrencyUnit: function (str) {
    let name = str.trim().toLowerCase();

    // IRR => iranian rial, internal rate of return
    return LIB.InstaCalc.Unit?.currencies?.hasOwnProperty(name) && !IC.predefinedFunction(name);
  },

  /**
   * Parse currency fractional patterns like "CAD cents", "USD cent", "AUD cents"
   * Returns { currency: "cad", factor: 0.01 } or null if not a match
   * This handles the common case where users write "135 CAD cents" meaning 1.35 CAD
   */
  parseCurrencyFraction: function (str) {
    const FRACTIONAL_PATTERNS = LIB.InstaCalc.Unit.currencyFractions || [];

    for (const { pattern, divisor, currencies } of FRACTIONAL_PATTERNS) {
      const match = str.match(pattern);
      if (match && IC.stringIsCurrencyUnit(match[1])) {
        const currency = match[1].toLowerCase();
        // If specific currencies are defined, check if this currency is allowed
        if (currencies && !currencies.includes(currency)) {
          continue;
        }
        return { currency, factor: 1 / divisor };
      }
    }
    return null;
  },

  predefinedIdentifier(name) {
    let upper = name.toUpperCase();
    return CONFIG.PREDEFINED[upper];//  || CONFIG.PREDEFINED_FUNCTION[upper];
  },

  predefinedConfigFunction(name) {
    let upper = name.toUpperCase();
    return CONFIG.PREDEFINED_FUNCTION[upper];
  },

  // versions of other functions which are unit-aware
  predefinedFunction(name = "") {

    // dynamic functions
    if (name?.toLowerCase()?.match(/log_\w+/)) {
      return name;
    }

    if (IC.envIsUserFunction(name)) {
      return name;
    }

    let upper = name.toUpperCase();

    return LIB.InstaCalc.allowedfn[upper] || CONFIG.UNIT_AWARE_FUNCTIONS[upper] || CONFIG.OTHER_FUNCTIONS[upper] || CONFIG.ENGLISH_FUNCTION[upper];
  },

  // @returns Object | null
  predefinedUnit: function (unit = "") {
    if (!unit) return false;

    const checkUnit = (u) => {
      const unitObj = ICUnit.getUnit(u);
      return unitObj && unitObj.name && unitObj[u];
    };

    if (unit && IC.isNumberString(unit)) {
      return false;
    }

    const unitLower = unit.toLowerCase();
    if (checkUnit(unit) || checkUnit(unitLower)) {
      return true;
    }

    // the constant unit has aliases like "time"
    return LIB.Unit.alias[unitLower] || IC.stringIsCurrencyUnit(unit) || CONFIG.FLEX_UNIT_ALIAS[unitLower] || false;
  },

  /*
    // not quite ready, has british spellings: cm -> centimetre 
    longUnitName: function (unitStr) {
      unitStr = unitStr.toLowerCase().trim();
      let obj = ICUnit.getUnit(unitStr);
      if (obj) {
        let value = obj[unitStr];
        let longestKey = Object.keys(obj).reduce((a, b) =>
          (obj[b] === value && b.length > a.length) ? b : a, unitStr
        );
     
        return this.singularize(longestKey);
      }
     
      return this.singularize(unitStr);
    },
    */


  // get scale amount for a suffx (1 by default)
  predefinedSuffixScale: function (s) {
    if (!s) return 1;
    let unit = s.toLowerCase();
    return CONFIG.SCALES[unit] ?? CONFIG.SCALES_CASE_SENSITIVE[s] ?? 1;
  },

  // get the internal name for a variable: "Net Sales" => "net_sales"
  canonicalName(str) {
    if (!str) return null;

    // unicode remove accents: Café => cafe
    if (str.includes("\\u")) {
      str = IC.parser.unescapeUnicode(str);
      str = str?.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      str = IC.parser.escapeUnicode(str);
    }

    // removing leading/trailing space, parens, then collapse space to _
    return str.toLowerCase().replace(/^\s+|\s+$|[()]/g, "").replace(/\s+/g, "_");
  },

  predefinedPostConvertFunction(x, fnname) {
    let str = fnname.trim();
    let f = CONFIG.POSTCONVERT_FUNCTIONS[fnname.toLowerCase()];
    if (f) {
      if (typeof f === "string") {
        str = f;
        f = CONFIG.POSTCONVERT_FUNCTIONS[f];
      }

      IC.log(`converting to ${str}`);
      return f(x);
    }

    // match a regex postconvert
    for (const [, value] of Object.entries(CONFIG.POSTCONVERT_FUNCTION_REGEX)) {
      let match = fnname.match(value.regex);
      if (match) {
        return value.f(x, match);
      }
    }

    return null;
  },

  // radians are units that are automatically converted into decimal
  // TODO: should that be the case? keep radian as radian, have deg in help text?
  predefinedPostConversionUnit(str) {
    let lastUnit = str.trim().toLowerCase().split(" ").pop();
    return CONFIG.SCALES_POST_CONVERT[lastUnit];
  },
}

const ParseModule = {
  ICParseRelaxedJSON: function (str) {
    if (typeof str !== "string") return null;

    // raw string: "15 cm"
    if (!str.includes("{")) {
      try {
        return IC.parser.quickResult(str);
      } catch (e) {
        return null;
      }
    }

    // actual JSON string
    const finalJson = str
      .replace(/([{,])\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, "$1\"$2\":");
    try {
      return JSON.parse(finalJson, (key, value) => {
        // try to convert numbers, etc. to native ICObjects
        if (typeof value === "string") {
          // attempt to parse with regular engine
          try {
            let ret = IC.parser.quickResult(value);
            return ret ?? value;
          }
          catch (e) {

          }
        }
        return value;
      });
    } catch (e) {
      return null;
    }
  },

  ICStringifyOld: function (obj) {
    if (IC.isICObject(obj)) {
      return IC.parser.formatResultAsSingleLine(obj);
    }

    return JSON.stringify(obj);
  },

  ICStringify: function (obj, indent = 2) {
    const seen = new WeakSet();

    function stringify(val, depth = 0) {
      if (val === null) return 'null';
      if (typeof val === 'undefined') return 'undefined';
      if (typeof val === 'string') return JSON.stringify(val);
      if (typeof val !== 'object') return String(val);
      if (IC.isICObject(val)) return JSON.stringify(IC.parser.formatResultAsSingleLine(val));

      if (seen.has(val)) return '[Circular]';
      seen.add(val);

      if (Array.isArray(val)) {
        const elements = val.map(elem => stringify(elem, depth + 1)).join(', ');
        return `[${elements}]`;
      }

      const entries = Object.entries(val).map(([key, value]) => {
        return `${JSON.stringify(key)}:${stringify(value, depth + 1)}`;
      }).join(', ');

      return `{${entries}}`;
    }

    return stringify(obj);
  },

  // [10] => 10
  ICArrayLiteralUnwrap: function (value) {
    while (Array.isArray(value) && value.length === 1) {
      value = value[0];
    }
    return value;
  },
  parseKnownPrefix(str) {
    str = str.toLowerCase().trim();
    if (!str) {
      return;
    }

    if (CONFIG.PREFIX_DICT_TRIE.search(str).length == 0) {
      return;
    }

    const len = str.length;
    for (const prefix in CONFIG.PREFIX_DICT) {
      let prefixWithOf = prefix + " of";

      // longest prefix first
      if (len > prefix.length + 2 && str.startsWith(prefixWithOf)) {
        return prefixWithOf;
      }

      if (len > prefix.length && str.startsWith(prefix)) {
        return prefix;
      }

    }
  },

  // returns true for "2", "2.5", etc.
  isNumberString: function (str) {
    return str?.trim().match(/^[\d,.]+$/);
  },

  // as above, but also: "2.5k"
  isNumberStringIncludingScaleSuffix: function (str) {
    const scales = Object.keys(CONFIG.SCALES);
    const numberScales = Object.keys(CONFIG.NUMBER_SCALES);
    const scalesCaseSensitive = Object.keys(CONFIG.SCALES_CASE_SENSITIVE);
    const allScales = [...scales, ...numberScales, ...scalesCaseSensitive];

    // Create a regex to split the number and the possible suffix
    const match = str.trim().match(/^([\d,.]+)([a-zA-Z]*)$/);

    if (!match) {
      return false;
    }

    const numberPart = match[1];
    const suffixPart = match[2];

    // Validate the number part
    if (!IC.isNumberString(numberPart)) {
      return false;
    }

    // If there's no suffix, it's valid if the number part is valid
    if (suffixPart === '') {
      return true;
    }

    // Validate the suffix part
    return allScales.includes(suffixPart) || allScales.includes(suffixPart.toLowerCase());
  },

  // take a string and return [variable] [units]
  parseUnitsFromString(label) {
    if (!label) {
      return null;
    }

    // bugfix: 1kb => 1 kb
    label = label.replace(/^\s*([0-9,.]+)/, "$1 ");

    label = label.replace("/", " / ");
    let nameParts = [];
    let unitParts = [];

    label.split(" ").forEach((w) => {
      // by here, "per" must have been replaced already
      if (w == "/" || IC.typestringIsPossibleUnitIncludingSlash(w)) {
        unitParts.push(w);
      } else {
        nameParts.push(w);
      }
    });

    return {
      name: nameParts.join(" "),
      units: unitParts.join(" "),
    };
  },

  parseWrittenNumber(label) {
    let trimmed = label.trim();

    // ignore things that don't look like written numbers
    if (label.match(/[.,;!?'"():\-—–_‘’“”…{}*=<>&~+|]|^(\s*\d)/) || !trimmed || IC.predefinedFunction(trimmed) || IC.predefinedUnit(trimmed)) {
      return null;
    }

    label = trimmed.toLowerCase();
    let extract = IC.parseUnitsFromString(label);
    if (!extract) {
      return null;
    }
    let words = extract.name;

    if (words == "zero") {
      return 0;
    }

    // FUTURE IDEA: parse numbers like "square root of four" => square root of (4)

    let n = LIB.text2num(words);
    if (n == 0 || n == null) {
      return null;
    }

    return extract.units ? IC.makeICObject(n, extract.units) : n;
  },


}

// can walk the AST and understand meaning 
const SemanticModule = {
  semanticPrefixFromIdentifierString(str) {
    if (!str) {
      return null;
    }

    str = str.toLowerCase().trim();
    let prefix = IC.parseKnownPrefix(str);
    if (prefix) {
      let id = str.substr(prefix.length).trim();
      let ret;
      if (IC.envIsVariableDefined(id)) {
        ret = IC.envGetVariable(id);
      }
      else if (IC.isNumberString(id)) {
        ret = IC.makeNumber(id, IC.envGetSettings());
      } else if (IC.envIsAutoUnit(id)) {
        ret = IC.makeICObject(1, id);
      } else {
        // parse full expression...
        ret = IC.parseAndEval(id);
        if (!IC.isValidEvalResult(ret)) {
          ret = null;
        }
      }

      if (ret) {
        return IC.semanticSuffixOrPrefix(ret, prefix, CONFIG.PREFIX_DICT);
      }
    }

    return null;
  },

  // "x squared"
  semanticSuffixOrPrefix(val, label, dict) {
    if (!label || !dict || !IC.isValidEvalResult(val)) {
      return null;
    }

    // normalize "of"
    if (label.endsWith(" of")) {
      label = label.substr(0, label.length - 3);
    }

    let str = label.trim().toLowerCase();

    let fn = dict[str];
    // lookup alias
    fn = typeof fn === "string" ? dict[fn] : fn;

    if (typeof fn == "function") {
      return fn(val);
    }

    return null;
  },

  // turn item into a single unit if variable not defined
  semanticAutoUnit(a) {
    let aVal = IC.eval(a);
    let aSource = IC.semanticGetUnitName(a);

    if (!IC.isValidEvalResult(aVal) && !IC.envIsVariableDefined(aSource)) {
      aVal = IC.makeICObject(1, aSource);
    }

    // standalone units do not parse. Turn them into typed objects
    if (!IC.isValidEvalResult(aVal) || Number.isNaN(aVal)) {
      if (!aSource.match(/[+\-*/()]/)) {
        aVal = IC.makeICObject(1, aSource);
      }
    }
    return aVal;
  },

  checkVariableUnitOverlapWarnings(str) {

    // remove parens
    let names = str.replaceAll(/[()]/g, "");
    names.split(" ").forEach((name) => {
      if (IC.envIsDefined(name) && IC.predefinedUnit(name)) {
        IC.log({ type: 'warning', message: `treating '${name}' as variable, not unit` });
      }
    });
  },

  semanticApplyUnit(node, unitName) {
    let ret = IC.eval(node);
    if (!unitName) {
      return ret;
    }

    let varName = IC.semanticGetVarName(node);
    if (varName) {
      IC.checkVariableUnitOverlapWarnings(varName);
    }

    // check scale list: could be "10k" => 10,0000
    let scaleName = unitName.split(" ")[0];
    let scale = IC.predefinedSuffixScale(scaleName);
    if (scale != 1.0) {

      // apply rest of unit: 15k m => (15000) meters
      if (scaleName != unitName) {
        let restOfUnit = unitName.substr(scaleName.length).trim();
        ret = IC.mathMul(ret, scale);
        return IC.semanticApplyUnit(ret, restOfUnit);
      }

      return IC.mathMul(ret, scale);
    }

    // Lookup priority: fiat currencies > stocks > other units
    // Uses IC_KEYWORDS.classifySymbol as the SINGLE SOURCE OF TRUTH for fiat vs stock
    const classify = (typeof IC_KEYWORDS !== 'undefined' && IC_KEYWORDS.classifySymbol)
      ? IC_KEYWORDS.classifySymbol
      : null;
    // Get stock cache from window (main thread) or global (worker) - same pattern as syncStockUnits
    const stockCache = (typeof window !== 'undefined' && window.Action?.fn?.stock_cache) ||
      (typeof global !== 'undefined' && global.Action?.fn?.stock_cache) ||
      (typeof self !== 'undefined' && self.Action?.fn?.stock_cache);
    const extDataCache = (typeof window !== 'undefined' && window.externalDataCache) ||
      (typeof global !== 'undefined' && global.externalDataCache) ||
      (typeof self !== 'undefined' && self.externalDataCache);

    // DEBUG: Log stock lookup context for ticker-like symbols (uses Debug system when available)
    const Debug = (typeof window !== 'undefined' && window.Debug) || null;
    const isTickerLike = /^[A-Z]{2,5}$/.test(unitName.toUpperCase());
    if (isTickerLike && Debug) {
      const stockKeys = stockCache ? Object.keys(stockCache) : [];
      const hasStock = stockCache?.[unitName.toUpperCase()]?.loaded;
      Debug.log('stock', `🔍 LOOKUP "${unitName}" | cache=${stockKeys.length} stocks | loaded=${hasStock}`);
      if (hasStock) {
        Debug.log('stock', `💰 "${unitName}" price=$${stockCache[unitName.toUpperCase()]?.price}`);
      }
    }

    const symbolResult = classify
      ? classify(unitName, {
        stockCache: stockCache,
        externalDataCache: extDataCache,
        IC: IC
      })
      : null;

    // DEBUG: Log classification result for ticker-like symbols
    if (isTickerLike && Debug) {
      Debug.log('stock', `🏷️ "${unitName}" classified as: ${symbolResult?.type || 'null'}`);
    }

    // Handle fiat currencies - log ambiguity if also a stock
    if (symbolResult?.type === 'fiat') {
      if (symbolResult.ambiguous?.isStock) {
        IC.log({ type: 'info', message: `${symbolResult.symbol} is ${symbolResult.ambiguous.fiatName} (${symbolResult.ambiguous.hint} for stock)` });
      }
      // Fall through to handle as regular unit (fiat currencies are defined as units)
    }

    // Handle stocks: "200 GOOG" = 200 × $180 = $36,000 USD
    // Also handle STOCK_ prefix: "200 STOCK_GOOG" → same conversion
    // BUT: Skip if this is an explicit conversion (contains ->)
    const isStockUnit = unitName.toUpperCase().startsWith('STOCK_');
    const isConversion = IC.containsConversion(IC.envGetCurrentInput());

    if ((symbolResult?.type === 'stock' || isStockUnit) && !isConversion) {
      const upperUnitName = isStockUnit
        ? unitName.substring(6).replace(/_/g, '.').toUpperCase()  // STOCK_MSFT -> MSFT, STOCK_BRK_A -> BRK.A
        : symbolResult.symbol;
      if (Debug) Debug.log('stock', `✅ "${unitName}" → STOCK("${upperUnitName}") × ${IC.val(ret)}`);
      const stockPrice = IC.evalFunction("STOCK", [upperUnitName]);
      if (Debug) Debug.log('stock', `✅ "${unitName}" price=${IC.val(stockPrice)} USD`);
      IC.logStockInfo(upperUnitName, stockCache?.[upperUnitName]);
      return IC.mathMul(ret, stockPrice);
    }

    // looks like a real unit...
    if (IC.typestringIsPossibleUnitIncludingSlash(unitName)) {

      // simple unit creation
      if (IC.hasNoType(ret)) {
        return IC.mathMul(ret, IC.makeICObject(1, unitName));
      }

      // if same category, convert: (2.54cm) inches => 2.54 cm to inches
      let sameType = IC.typeIsEquivalent(ret.t, IC.makeICObject(1, unitName).t);
      if (sameType) {
        return IC.semanticConvert(ret, unitName);
      }

      // (joules/sec) hour => multiply
      // x hour => convert to hours
      if (!IC.envIsDefined(varName) && IC.envIsAutoUnit(unitName)) {
        return IC.mathMul(ret, IC.makeICObject(1, unitName));
      }

      // do a smart convert
      return IC.ICObjectConvertToType(ret, unitName);
    }

    // special suffix: 3 squared
    let result = IC.semanticSuffixOrPrefix(ret, unitName, CONFIG.SUFFIX_DICT);
    if (result) {
      return result;
    }

    // custom unit: "3 cows" 
    return IC.makeICObject(ret, unitName);
  },
  /*
   * Performs a semantic division operation on two inputs with smart unit handling.
   *
   * Examples:
   * - 1024 / 2kb results in (1024 bytes) / 2kb = .5, not in inverse bytes.
   * - 3 / 4 cm results in 0.75 (cm), not in inverse cm.
   * - 3pi/2rad results in (3pi/2) rad, not in inverse radians.
   */
  semanticDivUnit(a, b) {
    let aVal = IC.semanticAutoUnit(a);
    let bVal = IC.semanticAutoUnit(b);

    /*
    The last item could be a conversion phrase:
    a/b * b/c in a/c
   
    (b/c) INTO a/c
    */

    // 1024 / 2kb
    if (IC.hasNoType(aVal) && IC.typeIsByte(bVal.t)) {
      IC.log(`converting ${IC.val(aVal)} to bytes`);
      return IC.mathDiv(IC.makeICObject(IC.val(aVal), "bytes"), bVal);
    }

    // 3 / 4cm
    if (IC.hasNoType(aVal) && IC.hasType(bVal)) {
      let bUnit = IC.makeICObject(1, bVal.t);
      return IC.mathMul(IC.mathDiv(aVal, IC.val(bVal)), bUnit);
    }

    // example: 3pi/2rad => (3pi/2) rad
    let postConversionUnit;
    if (Array.isArray(b) && b[0] == "unit" && IC.hasNoType(bVal)) {
      let unitName = b[2];
      postConversionUnit = IC.predefinedPostConversionUnit(unitName);
      if (postConversionUnit) {
        let scale = CONFIG.SCALES[postConversionUnit];
        let origVal = bVal / scale;
        aVal = IC.eval(a);
        return IC.mathMul(IC.mathDiv(aVal, origVal), scale);
      }
    }

    return IC.mathDiv(aVal, bVal);
  },

  // need special handling for division, given unit possibilites
  semanticDiv(a, b) {
    let aVal = IC.semanticAutoUnit(a);
    let bVal = IC.semanticAutoUnit(b);

    // Regular division handles:
    // 3 / 4
    // 3cm / 4
    // 3cm / 4cm ==> (3cm) / (4cm)
    // hz / day => hz / day (no unit collapse)
    return IC.mathDiv(aVal, bVal, false);
  },

  semanticTypeCompareOp(a, op, b) {
    let aVal = IC.eval(a);
    let bVal = IC.eval(b);
    let OP = op;

    if (IC.hasType(aVal) && IC.hasType(bVal)) {
      // for same types, return difference
      if (ICUnit.areEquivalentBaseUnits(aVal.t, bVal.t)) {
        let result = IC.mathSub(aVal, bVal);
        return IC.mathSimpleComparison(IC.val(result), OP, 0);
      }

      // cannot be compared
      IC.log({ type: 'warning', message: `can't compare mixed units: ${IC.typestring(aVal.t)} & ${IC.typestring(bVal.t)}` });
      return undefined;
    }

    // might have a single typed object -- but type could be empty
    let varWithType = IC.anyIsObject(aVal, bVal);
    if (varWithType && IC.hasType(varWithType)) {

      // turn bytes into a number
      if (IC.typeIsByte(varWithType.t)) {
        let numericVal = IC.typeConvertByteToNumber(varWithType);
        aVal = (aVal == varWithType) ? numericVal : aVal;
        bVal = (bVal == varWithType) ? numericVal : bVal;
        return IC.mathSimpleComparison(aVal, OP, bVal);
      }

      let aUnitName = IC.semanticGetUnitName(a);
      let bUnitName = IC.semanticGetUnitName(b);
      IC.log(`comparing number with ${aUnitName || bUnitName} (type: ${IC.typestring(varWithType.t)})`);
    }

    // regular comparison -- could have remnants of types left over
    return IC.mathSimpleComparison(IC.val(aVal), OP, IC.val(bVal));
  },

  semanticPercentOp(a, op, b) {
    let OP = op.toLowerCase().trim();

    let aVal = IC.eval(a);
    let bVal = IC.eval(b);
    let pctChange = IC.val(aVal);
    // TODO: find a better way to get the actual source of the expression...

    switch (OP) {
      case "of":
      case "from":
        IC.log(`calculating ${IC.val(bVal)} * ${pctChange}%`);
        return IC.mathMul(IC.mathMul(0.01, pctChange), bVal);
      case "off":
        IC.log(`calculating ${IC.val(bVal)} - ${pctChange}%`);
        return IC.mathMul(1 - (IC.mathMul(.01, pctChange)), bVal);
    }
    return null;
  },

  semanticPercentChange(a, sign, b) {
    let aVal = IC.eval(a);
    let bVal = IC.val(IC.eval(b));
    let signStr = sign;

    if (IC.format(aVal) == "percent") {
      IC.log(`combine percent values: ${IC.val(aVal)} ${signStr} ${IC.val(bVal)}`);
      let ret = signStr == "+" ? IC.mathAdd(aVal, bVal) : IC.mathSub(aVal, bVal);
      return IC.makeICObject(IC.val(ret), null, "percent");
    }

    let scaleChange = 1 + (signStr == "+" ? 1 : -1) * bVal;

    let msg = `percent change: ${IC.val(aVal)} * ${scaleChange}`;
    IC.log(msg);
    return IC.mathMul(aVal, scaleChange);
  },

  // execute a op b in the grammar
  /* 
  @param a - left side of expression
  @param op - operator (lowercase and trimmed)
  @param b - right side of expression
  */
  semanticInfixOp(a, op, b) {
    let OP = op;

    let aVal = IC.eval(a);
    let bVal = IC.eval(b);

    if (LIB.Complex.isComplex(aVal) || LIB.Complex.isComplex(bVal)) {
      return IC.mathComplexInfixOp(aVal, op, bVal);
    }

    if (IC.anyIsCustomObject(aVal, bVal)) {
      return undefined;
    }

    function checkRet(op, ret) {
      if (op.length >= 2 && (op == "and" || op == "or" || op == "xor")) {
        IC.log(`binary operator: ${op}`);
        let format = IC.format(aVal) || IC.format(bVal) || "bin";
        ret = IC.makeICObject(IC.val(ret), "", format);
      }
      return ret;
    }

    switch (OP) {
      case "*":
        return IC.mathMul(aVal, bVal);
      case "/":
        return IC.semanticDiv(a, b);
      case "+":
        return IC.mathAdd(aVal, bVal);
      case "-":
        return IC.mathSub(aVal, bVal);

      case "<<":
        return checkRet("<<", IC.val(aVal) << IC.val(bVal));
      case ">>":
        return checkRet(">>", IC.val(aVal) >> IC.val(bVal));
      case ">>>":
        return checkRet(">>>", IC.val(aVal) >>> IC.val(bVal));

      case "<":
        return checkRet("<", IC.val(aVal) < IC.val(bVal));
      case "<=":
        return checkRet("<=", IC.val(aVal) <= IC.val(bVal));
      case ">":
        return checkRet(">", IC.val(aVal) > IC.val(bVal));
      case ">=":
        return checkRet(">=", IC.val(aVal) >= IC.val(bVal));

      case "==":
        return checkRet("==", IC.val(aVal) == IC.val(bVal));
      case "!=":
        return checkRet("!=", IC.val(aVal) != IC.val(bVal));
      case "===":
        return checkRet("===", IC.val(aVal) === IC.val(bVal));
      case "!==":
        return checkRet("!==", IC.val(aVal) !== IC.val(bVal));

      case "and":
        return checkRet("and", IC.val(aVal) & IC.val(bVal));
      case "xor":
        return checkRet("xor", IC.val(aVal) ^ IC.val(bVal));
      case "or":
        return checkRet("or", IC.val(aVal) | IC.val(bVal));

      case "&&":
        return checkRet("&&", IC.val(aVal) && IC.val(bVal));
      case "||":
        return checkRet("||", IC.val(aVal) || IC.val(bVal));

      // 15 as a percent of 30
      case ":pctof:":
        return IC.makeICObject(IC.mathDiv(IC.val(aVal), IC.val(bVal)), null, "percent");

      case ":choose:":
        return IC.evalFunction("COMBINATION", [aVal, bVal]);

      case ":permute:":
        return IC.evalFunction("PERMUTATION", [aVal, bVal]);

      case ":phaseangle:":
        return LIB.Complex.phasor(aVal, bVal);
    }
  },


  semanticConvert(expression, targetUnit) {
    let x = expression;

    // an object to eval
    if (typeof expression === "object" && expression.type && expression.value && expression.text) {
      x = IC.eval(expression);
    } else if (Array.isArray(expression)) {
      x = IC.eval(expression);
    }

    // Pipeline syntax: [1, 2, 3] -> map(x => x^2)
    // targetUnit is ["callExp", "map", [arrow_function]]
    if (Array.isArray(targetUnit) && targetUnit[0] === "callExp") {
      const functionName = targetUnit[1].value || targetUnit[1];
      const existingArgs = targetUnit[2] || [];
      // Evaluate arrow functions to function objects
      const evaluatedArgs = existingArgs.map(arg => {
        if (Array.isArray(arg) && arg[0] === "functionDeclaration") {
          return IC.eval(arg);
        }
        return IC.eval(arg);
      });
      const newArgs = [x, ...evaluatedArgs];
      return IC.evalFunction(functionName, newArgs);
    }

    // date range: jan 1 to jan 5
    if (Array.isArray(targetUnit) && targetUnit[0] == "datetimeLiteral") {
      targetUnit = IC.eval(targetUnit);

      if (ICDate.isDateTimeObject(x)) {
        return ICDate.subDates(targetUnit, x);
      }
    }

    /*
    // seconds in 2 years, swap order
    if (expression.type == "id" && IC.isAutoUnit(expression.value)) {
      IC.log(`converting ${unitName} to ${expression.value}`);
      // swap x and unitName
      let tmp = x;
      x = unitName;
      unitName = tmp;
    }
    */

    let unitName = typeof targetUnit == "string" ? targetUnit : IC.semanticGetUnitName(targetUnit);

    let varValue, prefix, final;
    let varInTargetUnit = null;
    let isRealVariable = null;

    // x = 10 miles; 15 mph in hours / x 
    varInTargetUnit = targetUnit?.[2]?.[1]?.type == "id";
    isRealVariable = IC.envIsVariableDefined(targetUnit?.[2]?.[1]?.value);
    if (Array.isArray(targetUnit) && targetUnit[0] == "div" && varInTargetUnit && isRealVariable) {
      let eval_result = IC.evalWithUnitOverride(targetUnit[2]);
      prefix = targetUnit[1].value + "/";
      varValue = IC.val(eval_result);
      final = IC.hasType(eval_result) ? IC.typestring(eval_result.t) : "";
    }

    // x = 10; 15 mph in hours / x miles
    varInTargetUnit = targetUnit?.[2]?.[1]?.[1]?.type == "id";
    isRealVariable = IC.envIsVariableDefined(targetUnit?.[2]?.[1]?.[1]?.value);
    if (Array.isArray(targetUnit) && targetUnit[0] == "divUnit" && varInTargetUnit && isRealVariable) {
      let eval_result = IC.evalWithUnitOverride(targetUnit[2]);
      prefix = targetUnit[1].value + "/";
      varValue = IC.val(eval_result);
      final = IC.hasType(eval_result) ? IC.typestring(eval_result.t) : "";
    }

    if (IC.isValidEvalResult(varValue)) {
      unitName = `${prefix} ${varValue} ${final}`;
    }

    if (!unitName) {
      return null;
    }

    // Stock ticker detection for conversion targets (e.g., "1M USD in AAPL")
    // Uses IC_KEYWORDS.classifySymbol as the SINGLE SOURCE OF TRUTH for fiat vs stock
    const classifyConvert = (typeof IC_KEYWORDS !== 'undefined' && IC_KEYWORDS.classifySymbol)
      ? IC_KEYWORDS.classifySymbol
      : null;
    // Get caches with worker fallback (window in main, global/self in worker)
    const convStockCache = (typeof window !== 'undefined' && window.Action?.fn?.stock_cache) ||
      (typeof global !== 'undefined' && global.Action?.fn?.stock_cache) ||
      (typeof self !== 'undefined' && self.Action?.fn?.stock_cache);
    const convExtCache = (typeof window !== 'undefined' && window.externalDataCache) ||
      (typeof global !== 'undefined' && global.externalDataCache) ||
      (typeof self !== 'undefined' && self.externalDataCache);
    const convertSymbolResult = classifyConvert
      ? classifyConvert(unitName, {
        stockCache: convStockCache,
        externalDataCache: convExtCache,
        IC: IC
      })
      : null;

    // Handle fiat currencies in conversion - log ambiguity if also a stock
    if (convertSymbolResult?.type === 'fiat' && convertSymbolResult.ambiguous?.isStock) {
      IC.log({ type: 'info', message: `${convertSymbolResult.symbol} is ${convertSymbolResult.ambiguous.fiatName} (${convertSymbolResult.ambiguous.hint} for stock)` });
    }

    // If target is a stock, trigger loading so it gets registered as a unit
    if (convertSymbolResult?.type === 'stock') {
      // Trigger stock loading (STOCK function handles caching/async)
      IC.evalFunction("STOCK", [convertSymbolResult.symbol]);
      IC.logStockInfo(convertSymbolResult.symbol, convStockCache?.[convertSymbolResult.symbol]);
    }

    // if a numeric unit like "ppm", convert to regular number before use
    let numericEquivalent = null;
    if (IC.typeIsVirtual(x?.t)) {
      numericEquivalent = IC.typeConvertVirtualToNumeric(x);
    }

    // check for postconversion function
    let functionResult = IC.predefinedPostConvertFunction(numericEquivalent ?? x, unitName);
    if (functionResult !== null) {
      return functionResult;
    }

    //  deg and rad are conversions, not functions to run
    if (IC.predefinedFunction(unitName) && !IC.predefinedUnit(unitName)) {
      if (IC.hasNoType(x)) {
        IC.log(`call function ${unitName}`);
      }
      return IC.evalFunction(unitName, [x]);
    }

    // 15 -> cm
    if (typeof x === "number") {

      // check for implied dollar unit
      let currentInput = IC.envGetCurrentInput();
      if (IC.stringIsCurrencyUnit(unitName)) {

        const currencies = Object.values(LIB.InstaCalc.format.currencies);
        // check for a currency symbol in the original input right before x
        let currencySymbol = currencies.find(c => {
          let escapedSymbol = ICLib.escapeRegExpString(c.symbol); // could be $ 
          return currentInput.match(new RegExp(`${escapedSymbol}\\s*${x}`));
        });

        if (currencySymbol) {
          IC.log({ type: 'convert', from: String(x), to: currencySymbol.code });
          x = IC.makeICObject(x, currencySymbol.code);
        }
        else {
          IC.log({ type: 'info', message: `plain number → ${unitName}` });
          return IC.makeICObject(x, unitName);
        }
      } else if (IC.typeIsVirtual(unitName)) {
        IC.log({ type: 'info', message: `plain number → ${unitName}` });
        return IC.typeConvertNumericToVirtual(x, unitName);
      }
      else {
        IC.log({ type: 'info', message: `plain number → ${unitName}` });
        return IC.makeICObject(x, unitName);
      }
    }

    // regular conversion
    let finalResult = IC.ICObjectConvertToType(x, unitName);

    // Fallback for currency→currency when core converter returns null (observed in bare currency arithmetic)
    if (!IC.isValidEvalResult(finalResult)
      && IC.hasType(x)
      && unitName
      && LIB?.InstaCalc?.Unit?.currencies) {
      const fromUnits = Object.keys(x.t || {});
      const toUnit = unitName.trim().toUpperCase();
      if (fromUnits.length === 1) {
        const fromUnit = fromUnits[0].toUpperCase();
        const rates = LIB.InstaCalc.Unit.currencies;
        const fromRate = rates?.[fromUnit];
        const toRate = rates?.[toUnit];
        if (fromRate && toRate) {
          const scaled = IC.mathMul(IC.val(x), toRate / fromRate);
          return IC.makeICObject(scaled, { [toUnit]: 1 }, x.f);
        }
      }
    }

    return finalResult;
  },

  semanticGetVarName(node) {
    if (Array.isArray(node)) {
      if (node[0] === "arrayAccess") {
        const arrayName = IC.semanticGetVarName(node[1]);
        const index = IC.eval(node[2]);
        return `${arrayName}[${index}]`;
      } else if (node[0] === "objectAccess") {
        const objName = IC.semanticGetVarName(node[1]);
        const propName = node[2].value;
        return `${objName}.${propName}`;
      }
    }

    switch (node[0]) {
      case "parenExpr":
      case "power":
        return IC.semanticGetVarName(node[1]);
    }

    return node?.value;
  },

  semanticGetUnitName(node) {
    if (typeof node === "string") {
      return node;
    }

    if (Array.isArray(node)) {

      switch (node[0]) {
        case "parenExpr":
          return IC.semanticGetUnitName(node[1]);
        case "power":
          return IC.semanticGetUnitName(node[1]) + "^" + IC.semanticGetUnitName(node[2]);
        case "div":
        case "divUnit":
          return IC.semanticGetUnitName(node[1]) + "/" + IC.semanticGetUnitName(node[2]);

        case "unit":
          return IC.semanticGetUnitName(node[1]) + " " + IC.semanticGetUnitName(node[2]);
      }

      // kg*m/s^2, but not fps * 2
      if (node[0] == "mul" && IC.predefinedUnit(node[1].value) && IC.predefinedUnit(node[2].value)) {
        return IC.semanticGetUnitName(node[1]) + " " + IC.semanticGetUnitName(node[2]);
      }
    }

    return node?.value;
  },
  semanticGetUnitNameFromString(str) {
    str = str.split("^")[0].trim();
    return str;
  },

  // sin(123,456) => true
  // sin((123,456)) => false
  semanticContainsDecimalWithCommas(tree) {
    if (Array.isArray(tree)) {
      return tree.flatMap(node => {
        if (node.type === 'parenExpr' && node[1].type === 'decimal') {
          // Skip decimal inside parenExpr
          return false;
        } else if (node.type === 'decimal' && node.value.includes(',')) {
          return true;
        } else {
          return IC.semanticContainsDecimalWithCommas(node);
        }
      }).some(v => v);
    } else {
      return false;
    }
  },


  // handle 200/2k => 200 / (2k)
  semanticNumericScaleFraction(tree) {
    let [n, d] = tree[1].value.split("/").map(val => IC.makeNumber(val, IC.envGetSettings()));
    let scale = IC.predefinedSuffixScale(tree[2]);

    return n / (d * scale);
  },


  ifConditionCleanup(tree) {
    tree = IC.replaceAssignmentWithEquality(tree);
    tree = IC.replaceBinaryWithLogical(tree);
    return tree;
  },

  replaceAssignmentWithEquality(tree) {
    if (Array.isArray(tree) && tree[0] == "assignmentExp") {
      IC.log(`treating = as equality (==)`);
      tree = ["equal", tree[1], tree[3]];
    }
    return tree;
  },

  // for use in "if" conditions
  replaceBinaryWithLogical(tree) {
    // replace binary operators with logical operators
    // change "band" to "land" and "bor" to "lor"
    if (Array.isArray(tree)) {
      if (tree[0] == "band") {
        tree[0] = "land";
      } else if (tree[0] == "bor") {
        tree[0] = "lor";
      }

      tree.forEach(t => IC.replaceBinaryWithLogical(t));
    }

    return tree;
  },
}

const MathModule = {

  // compare simple values
  mathSimpleComparison(a, op, b) {
    if (LIB.Complex.anyIsComplex(a, b)) {
      switch (op) {
        case "==": return LIB.Complex.isEqual(a, b);
        case "!=": return !LIB.Complex.isEqual(a, b);
        default:
          IC.log(`cannot compare complex numbers with ${op}`);
          return undefined;
      }
    }

    const operations = {
      "<": () => a < b,
      "<=": () => a <= b,
      ">": () => a > b,
      ">=": () => a >= b,
      "==": () => a == b,
      "!=": () => a != b,
      "===": () => a == b,
      "!==": () => a != b,
    };

    return operations[op.toLowerCase()]();
  },

  // poor man's high-precision math library...
  mathAddNumbersHighPrecision(num1, num2) {
    // Function to count the number of decimal places in a number
    function decimals(x) {
      var match = ('' + x).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
      if (!match) { return 0; }
      return Math.max(
        0,
        (match[1] ? match[1].length : 0) - (match[2] ? +match[2] : 0)
      );
    }

    var decimalsNum1 = decimals(num1);
    var decimalsNum2 = decimals(num2);

    var factor = Math.pow(10, Math.max(decimalsNum1, decimalsNum2));
    return Number(((num1 * factor + num2 * factor) / factor).toFixed(Math.max(decimalsNum1, decimalsNum2)));
  },



  mathComplexInfixOp(a, op, b) {
    let aVal = LIB.Complex.toComplex(a);
    let bVal = LIB.Complex.toComplex(b);

    let ret = LIB.Complex.toComplex(0);

    switch (op) {
      case "+":
        return LIB.Complex.add(a, b);
        break;
      case "-":
        return LIB.Complex.subtract(a, b);
        break;
      case "*":
        return LIB.Complex.multiply(a, b);
        break;
      case "/":
        let denom = bVal.r * bVal.r + bVal.i * bVal.i;
        if (denom == 0) {
          IC.log({ type: 'warning', message: 'division by zero' });
          return null;
        }
        return LIB.Complex.divide(a, b);
        break;
      case "==":
        return LIB.Complex.isEqual(a, b);
      case "!=":
        return LIB.Complex.isNotEqual(a, b);
      case "<":
      case "<=":
      case ">":
      case ">=":
        IC.log({ type: 'warning', message: `cannot compare complex numbers: ${op}` });
        return null;

      default:
        IC.log({ type: 'warning', message: `unknown complex operator: ${op}` });
        return null;
    }

    return ret;
  },

  // TODO: use units
  mathAdd(a, b) {
    if (a == null || b == null) {
      return null;
    }

    if (LIB.Complex.anyIsComplex(a, b)) {
      return LIB.Complex.add(a, b);
    }

    let aVal = IC.val(a);
    let bVal = IC.val(b);
    let sum = aVal + bVal;

    // if both are strings, add them together
    if (typeof aVal == 'string' && typeof bVal == 'string') {
      return sum;
    }

    // date operation
    if (IC.anyIsDate(aVal, bVal)) {
      return ICDate.addDates(a, b);
    }

    // If either is a decimal
    if (typeof (aVal) == 'number' && typeof (bVal) == 'number' && (!Number.isInteger(aVal) || !Number.isInteger(bVal))) {
      sum = IC.mathAddNumbersHighPrecision(aVal, bVal);
    }

    if (IC.allAreObjects(a, b)) {
      let scale = ICUnit.typeConversionScale(b.t, a.t); // 14 in + 3 cm => result in inches

      // lm == cd sr, where sr can be ignored
      if (scale == 1 && !IC.typeIsSameNormalizedObject(a.t, b.t) && !ICUnit.areEquivalentBaseUnits(a.t, b.t)) {
        // no conversion -- different types. 3 feet + 5 inches => 8
        IC.log(`mixed units ${IC.typestring(a.t)} & ${IC.typestring(b.t)}, using plain numbers`);
        return sum;
      }

      let val = aVal + bVal * scale;
      return IC.makeICObject(val, a.t, IC.ICObjectFormatMerge(a.f, b.f, true));
    }

    const varWithType = IC.anyIsObject(a, b);
    if (varWithType) {
      const otherObj = varWithType == a ? b : a;
      const typestring = (IC.typestring(varWithType.t) || "").trim();

      // could be a format-only item
      if (typestring) {
        IC.log(`converting '${IC.val(otherObj)}' to ${typestring}`);
      }
      return IC.makeICObject(sum, typestring, varWithType.f);
    }

    return sum;
  },

  mathSub(a, b) {
    if (IC.anyIsDate(IC.val(a), IC.val(b))) {
      return ICDate.subDates(a, b);
    }

    return IC.mathAdd(a, IC.mathNegate(b));
  },

  mathNegate(a) {
    return IC.mathMul(-1, a);
  },

  mathPreciseMultiply(a, b) {

    // no need for high precision
    if (Number.isInteger(a) && Number.isInteger(b)) return a * b;

    function getDecimals(num) {
      const numStr = String(num);
      if (numStr.includes('e-')) {
        const [base, exponent] = numStr.split('e-');
        const baseDecimals = (base.split('.')[1] || '').length;
        return baseDecimals + parseInt(exponent, 10);
      } else if (numStr.includes('e+')) {
        return 0; // For positive exponents, the number of decimals is effectively 0
      } else {
        return (numStr.split('.')[1] || '').length;
      }
    }

    const aDecimals = getDecimals(a);
    const bDecimals = getDecimals(b);

    const aInt = Math.round(a * Math.pow(10, aDecimals));
    const bInt = Math.round(b * Math.pow(10, bDecimals));

    const result = aInt * bInt;

    return result / (Math.pow(10, aDecimals) * Math.pow(10, bDecimals));
  },

  mathPreciseDivide(a, b) {
    if (b === 0) {
      return Infinity;
    }

    // No need for high precision if both numbers are integers
    if (Number.isInteger(a) && Number.isInteger(b)) return a / b;

    function getDecimals(num) {
      const numStr = String(num);
      if (numStr.includes('e-')) {
        const [base, exponent] = numStr.split('e-');
        const baseDecimals = (base.split('.')[1] || '').length;
        return baseDecimals + parseInt(exponent, 10);
      } else if (numStr.includes('e+')) {
        return 0; // For positive exponents, the number of decimals is effectively 0
      } else {
        return (numStr.split('.')[1] || '').length;
      }
    }

    const aDecimals = getDecimals(a);
    const bDecimals = getDecimals(b);

    const factor = Math.pow(10, Math.max(aDecimals, bDecimals));

    const aInt = Math.round(a * factor);
    const bInt = Math.round(b * factor);

    const result = aInt / bInt;

    return result;
  },

  // Normalize type entries that accidentally captured numeric factors
  // Example: { "25 ms": -1 } becomes value *= 25^-1 and type { ms: -1 }
  normalizeNumericUnitPrefixes(obj) {
    if (!IC.isICObject(obj) || !obj.t) return obj;

    let newVal = IC.val(obj);
    let changed = false;
    const newType = {};

    Object.entries(obj.t).forEach(([unitKey, exp]) => {
      const m = unitKey.match(/^([0-9]+(?:\\.[0-9]+)?)\\s+(.*)$/);
      if (m) {
        const factor = parseFloat(m[1]);
        const unitName = m[2].trim();
        if (isFinite(factor) && unitName) {
          // Move numeric factor into value (respect exponent sign)
          newVal = IC.mathPreciseMultiply(newVal, Math.pow(factor, exp));
          newType[unitName] = (newType[unitName] || 0) + exp;
          changed = true;
          return;
        }
      }
      newType[unitKey] = (newType[unitKey] || 0) + exp;
    });

    if (!changed) return obj;

    // Remove zero exponents
    Object.keys(newType).forEach((k) => {
      if (newType[k] === 0) delete newType[k];
    });

    return IC.makeICObject(newVal, newType, obj.f);
  },

  mathMul(a, b, consolidate = true) {
    if (LIB.Complex.anyIsComplex(a, b)) {
      return LIB.Complex.multiply(a, b);
    }

    let product = IC.mathPreciseMultiply(IC.val(a), IC.val(b));

    if (IC.allAreObjects(a, b)) {
      let typeResult = IC.typeMultiply(a.t, b.t, +1, consolidate);
      let scaledProduct = IC.mathPreciseMultiply(product, IC.val(typeResult));
      return IC.normalizeNumericUnitPrefixes(
        IC.makeICObject(scaledProduct, typeResult.t, IC.ICObjectFormatMerge(a.f, b.f, false))
      );
    }
    let obj = IC.anyIsObject(a, b);
    if (obj) {
      return IC.normalizeNumericUnitPrefixes(
        IC.makeICObject(product, obj.t, IC.ICObjectFormatMerge("", obj.f, false))
      );
    }

    return product;
  },

  mathInverse(a) {
    if (LIB.Complex.isComplex(a)) {
      return LIB.Complex.inverse(a);
    }

    let inverseScale = 1 / IC.val(a);
    if (a instanceof Object) {
      let inverseType = IC.mathDivideTypePowers(a.t, -1);
      return IC.makeICObject(inverseScale, inverseType, a.f);
    }
    return inverseScale;
  },

  // a/b == a * (1/b)
  mathDiv(a, b, consolidate = true) {
    if (LIB.Complex.anyIsComplex(a, b)) {
      return LIB.Complex.divide(a, b);
    }

    if (IC.anyIsObject(a, b)) {
      return IC.mathMul(a, IC.mathInverse(b), consolidate);
    }
    return IC.mathPreciseDivide(a, b); // separate paths for approximation purposes; 3/5 vs 3 * 1/5 is different in JS
  },

  /*
  * Divides all exponents in the type object by the number n. 
  * @param {ICType} type - The type object containing unit types and exponents
  * @param {number} n - The number to divide all type exponents by
  * @returns {ICType|null} The new type object with all exponents divided by n, or null if unsuccessful
  * 
  */
  mathDivideTypePowers(type, n) {
    if (!type || !n) {
      return null;
    }

    const isDivisible = Object.values(type).every(value => value % n === 0);
    if (!isDivisible) {
      return null;
    }

    return Object.fromEntries(
      Object.entries(type).map(([key, value]) => [key, value / n])
    );
  },

  mathPow(a, b, consolidate = true) {
    if (LIB.Complex.anyIsComplex(a, b)) {
      return LIB.Complex.pow(a, b);
    }

    let base = IC.val(a);
    let power = IC.val(b);
    let val = Math.pow(base, power);

    // handle units
    if (a instanceof Object) {
      if (power == 0) {
        return 1;
      }

      if (power == 1) {
        return a;
      }

      // neg power: x^-3 = 1/x^3
      if (power < 0) {
        let inverse = IC.mathInverse(IC.mathPow(a, Math.abs(power), consolidate));
        return inverse;
      }

      // pos power
      if (Number.isInteger(b)) {
        let type = IC.typeMultiply(a.t, a.t, b - 1, consolidate).t;
        return IC.makeICObject(val, type);
      }

      // fractional powers:
      // TODO: have a "epsilon" check for arbitrary fraction
      if (b == 1 / 2 || b == 1 / 3 || b == 1 / 4 || b == 1 / 5 || b == 1 / 6) {
        let type = IC.mathDivideTypePowers(a.t, 1 / b);
        if (type) {
          return IC.makeICObject(val, type);
        }
      }
    }

    return val;
  },

  mathEqualWithinThreshold(a, b, threshold = 1e-10) {
    return Math.abs(a - b) < threshold;
  },

  mathMod(a, b) {
    return IC.val(a) % IC.val(b);
  },

  /*
   @param {number|null} x - number
   @param {number|null} denom - denominator to use
   */
  mathToFraction(x, denom = 0) {
    if (x == null) {
      return null;
    }

    let [n, d] = LIB.toFractionIterative(x, 1e-6);

    if (denom) {
      n = Math.round(n * (denom / d));
      d = denom;
    }

    return [n, d];
  },

  mathReduceFraction(numerator, denominator) {
    var a = numerator;
    var b = denominator;
    var c;
    while (b) {
      c = a % b; a = b; b = c;
    }
    return [numerator / a, denominator / a];
  },

}

const EvalModule = {
  parseAndEval(str) {
    let cacheKey = "nearley" + str;
    let r = IC.parser.RESULT_CACHE.get(cacheKey);
    if (r) {
      return IC.eval(r);
    }

    // @ts-ignore
    let NEARLEY_PARSER = new LIB.nearley.Parser(GRAMMAR_FROM_COMPILED);
    try {
      NEARLEY_PARSER.feed(str);
      let r = NEARLEY_PARSER.results[0];
      IC.parser.RESULT_CACHE.set(cacheKey, r);
      if (r) {
        return IC.eval(r);
      }
    } catch (e) {
    }
    return IC.NOPARSE;
  },


  // evaluate an AST to a result (usually ICObject|string|number|null)
  eval(tree) {
    let name, result, results, n, d, value;

    let isLiteral = (node) => {
      let type = node?.type ?? (Array.isArray(node) ? node[0] : undefined);

      // quick exit
      if (type == "id") {
        return false;
      }

      switch (type) {
        case "decimal":
        case "hex":
        case "oct":
        case "bin":
        case "fraction":
        case "stringLiteral":
        case "booleanLiteral":
        case "nullLiteral":
        case "arrayLiteral":
        case "objectLiteral":
          return true;
      }

      return false;
    }

    if (typeof tree === "object" && !Array.isArray(tree)) {
      switch (tree?.type) {
        case "decimal":
          return IC.makeNumber(tree.value, IC.envGetSettings());
        case "hex":
          return IC.makeHexadecimal(tree.value.slice(2));
        case "oct":
          return IC.makeOctal(tree.value.slice(2));
        case "bin":
          return IC.makeBinary(tree.value.slice(2));

        case "fraction":
          let whole = 0, fraction = 0;

          // remove whitespace around fraction symbol
          let text = tree.value.trim().replace(/\s*[/]\s*/g, "/");

          if (text.includes(" ")) {
            [whole, text] = text.split(" ");
            whole = IC.makeNumber(whole, IC.envGetSettings());
          }

          [n, d] = text.split("/");
          fraction = IC.makeNumber(n, IC.envGetSettings()) / IC.makeNumber(d, IC.envGetSettings());
          return whole + fraction;

        case "id":
          return IC.evalIdentifier(tree.value);

        case "ifStatement":
          // = and & should be == and && 
          tree.ifcond = IC.replaceAssignmentWithEquality(tree.ifcond);
          tree.ifcond = IC.replaceBinaryWithLogical(tree.ifcond);

          result = IC.eval(tree.ifcond);
          let isTruthy = result;

          if (result == "false" || !result) {
            isTruthy = false;
          }

          if (isTruthy) {
            return IC.eval(tree.ifbody);
          } else if (tree.elsebody) {
            return IC.eval(tree.elsebody);
          }

          return null;

        case "rowRange":
          let [start, end] = tree.value.replace(/r/ig, '').split(":").map(n => parseInt(n));
          return IC.envGetRowRangeVariables(start, end);


      }
    }

    if (Array.isArray(tree)) {
      if (tree.length == 1) {
        return IC.eval(tree[0]);
      }

      let cmd = typeof (tree[0]) === "string" ? tree[0] : null;

      // list of items to eval
      if (!cmd) {
        let results = tree.map(t => IC.eval(t));
        return results[results.length - 1];
      }

      switch (cmd) {
        case "booleanLiteral":
          return IC.makeBoolean(tree[1].value);
        case "stringLiteral":
          return IC.makeString(tree[1].value.slice(1, -1));
        case "nullLiteral":
          return null;

        case "parenExpr":
          return IC.eval(tree[1]);
        case "squareBracketExpr":
          return IC.eval(tree[1]);
        case "absValue":
          let ret = IC.eval(tree[1]);
          return this.evalFunction("ABS", [ret]);

        case "pctExpression":
          n = IC.mathMul(0.01, IC.eval(tree[1]));
          return IC.makeICObject(IC.val(n), null, "percent");

        case "pctOp":
          return IC.semanticPercentOp(tree[1], tree[3].value, tree[4]);

        case "pctChange":
          return IC.semanticPercentChange(tree[1], tree[2].value, tree[3]);

        case "factorial":
          n = IC.eval(tree[1]);
          return ICLib.factorial(n);

        case "parenMult":
          results = ([tree[1][0], ...tree[1][1]]).map(r => IC.eval(r));
          n = 1;
          if (results[0]?.type === "function") {
            return IC.evalUserFunction(results[0], results.slice(1));
          }

          results.forEach(r => n = IC.mathMul(n, r));
          if (tree[2]) {
            n = IC.semanticApplyUnit(n, tree[2]);
          }
          return n;

        case "assignmentExp":
          if (isLiteral(tree[1])) {
            result = IC.eval(tree[1]);
            name = IC.semanticGetVarName(tree[3]);
            return IC.envAssignVariable(name, tree[2].value, result);
          }

          result = IC.eval(tree[3]);
          name = IC.semanticGetVarName(tree[1]);
          return IC.envAssignVariable(name, tree[2].value, result);

        case "commaExp":
          let [_, ...cmds] = [...tree];
          results = cmds.map(c => IC.eval(c));
          return results[results.length - 1];

        case "scaledCallExp":
          return IC.semanticInfixOp(tree[1], "*", tree[2]);


        case "callExpWithKeyword":
        case "callExp":
          name = tree[1]?.value;
          let rawArgs = tree[2];

          let userDefinedFunc = IC.envGetVariable(name);
          if (tree[1][0] == "functionDeclaration") {
            userDefinedFunc = IC.eval(tree[1]);
          }

          if (userDefinedFunc && userDefinedFunc.type === "function") {
            let args = rawArgs.map(x => IC.eval(x));
            return IC.evalUserFunction(userDefinedFunc, args);
          }

          // Excel-style IF, AND, OR, NOT 
          if (CONFIG.KEYWORD_FUNCTION[name?.toUpperCase()]) {
            return CONFIG.KEYWORD_FUNCTION[name.toUpperCase()](...rawArgs);
          }

          let args = rawArgs.map(t => IC.eval(t));
          if (IC.semanticContainsDecimalWithCommas(tree)) {
            IC.log(`Number commas found, use spaces or wrap in parens: 1,234 => 1, 234 or (1,234)`);
          }

          if (IC.envGetVariable(name)) {
            IC.log(`multiplying variable '${name}' instead of calling function`);
            result = IC.mathMul(IC.envGetVariable(name), args[0]);
          } else {
            // run function first, so "time()" gets called
            result = IC.evalFunction(name, args);
            if (!IC.isValidEvalResult(result) && IC.envIsAutoUnit(name)) {
              result = IC.mathMul(IC.makeICObject(1, name, ""), args[0]);
            }
          }
          return result;

        case "arrayLiteral":
          return tree[1].map(item => IC.eval(item));

        case "arrayAccess":
          // Bracket access supports both arrays and plain objects
          // Evaluate container and key expression
          {
            let container = IC.eval(tree[1]);
            let keyExpr = IC.eval(tree[2]);

            // Array access
            if (Array.isArray(container)) {
              return container[keyExpr];
            }

            // Object dynamic key access: scenarios[scenario]
            if (container && typeof container === 'object') {
              // Coerce key to string where appropriate
              let k = (typeof keyExpr === 'string') ? keyExpr : String(keyExpr);
              // Use existing fuzzy finder for object keys (case/format tolerant)
              let found = IC.ICObjectLiteralFindKey(container, k);
              return found ? container[found] : undefined;
            }

            return undefined;
          }

        case "objectLiteral":
          let obj = {};
          return tree[1].reduce((obj, item) => {
            let key = item[1].value ?? null;

            // remove quotes from key
            if (key && item[1].type?.includes("string")) {
              key = key.slice(1, -1);
            }

            if (key !== null) {
              obj[key] = IC.eval(item[2]);
            }
            return obj;
          }, {});

        case "objectAccess":
          let object = IC.eval(tree[1]);
          let key = tree[2].value;

          // if array literal, call function
          if (Array.isArray(object) && IC.predefinedFunction(key)) {
            return IC.evalFunction(key, [object]);
          }

          // regular lookup, search
          key = IC.ICObjectLiteralFindKey(object, key);
          return key ? object[key] : undefined;

        case "timeLiteral":
          return ICTime.makeICObject(tree[1].value);

        case "datetimeLiteral":
          return ICDate.makeICObject(tree[1].value);

        case "timeImpliedDateLiteral":
          return ICDate.makeICObjectTimeImpliedDate(tree[1].value);

        case "tapemeasure":
          let makeNumberObj = (val) => {
            let value = val.trim().replace(/[^0-9. /]/g, "");

            if (value.includes("/")) {
              return { type: "fraction", value: value }
            } else {
              return { type: "decimal", value: value }
            }
          }

          if (value = tree[1]?.foot?.value) {
            n = IC.eval(makeNumberObj(value));
            return IC.makeICObject(n, "ft", "tape");
          } else if (value = tree[1]?.inch?.value) {

            n = IC.eval(makeNumberObj(value));
            return IC.makeICObject(n, "in", "tape");
          } else {
            value = tree[1].both.value;
            let parts = value.split(/[']|ft|foot|feet/i);
            let nFeet = IC.eval(makeNumberObj(parts[0]));
            let nInches = IC.eval(makeNumberObj(parts[1]));

            return IC.mathAdd(
              IC.makeICObject(nFeet, "ft", "tape"),
              IC.makeICObject(nInches, "in", "tape"));
          }

        case "unit":
          if (tree[1]?.type == "fraction" && IC.predefinedSuffixScale(tree[2]) != 1) {
            // TODO: not being hit by code coverage
            return IC.semanticNumericScaleFraction(tree);
          }

          return IC.semanticApplyUnit(tree[1], tree[2]);

        case "conversion":
          return IC.semanticConvert(tree[1], tree[2]);

        case "conversionDiv":

          // "15 mph -> /s" becomes "15 mph -> s", which is what we want
          let inverseType = IC.typestring(IC.eval(tree[2])?.t);
          return IC.semanticConvert(tree[1], inverseType);

        case "rate":
          let numerator = IC.eval(tree[2]);
          let denominator = IC.eval(tree[1]);
          result = IC.mathDiv(numerator, denominator);
          return result;

        case "typesolve":
          let aVal = IC.eval(tree[1]);
          let bVal = IC.eval(tree[2]);
          let bUnit = IC.semanticGetUnitName(tree[2]);

          // check if b was an autoconverted unit: 15 miles/hour using feet
          if (IC.envIsAutoUnit(bUnit)) {
            return IC.semanticConvert(aVal, bUnit);
          }

          // make sure "feet" is an actual variable, for example
          if (bVal != null) {
            let ret = IC.typeSolve(aVal, bVal);
            if (ret != null) {
              return ret;
            }
          }

          // best guess -- multiply? 
          return IC.mathMul(aVal, bVal);
        // return IC.semanticConvert(aVal, bUnit);

        case "unary":
          switch (tree[1]) {
            case "!":
              return !IC.eval(tree[2]);
            case "not":
            case "~":
              result = IC.eval(tree[2]);
              return ~IC.val(result);
            case "++":
              name = IC.semanticGetVarName(tree[2]);
              IC.envAssignVariable(name, "+=", 1);
              return IC.envGetVariable(name);
            case "--":
              name = IC.semanticGetVarName(tree[2]);
              IC.envAssignVariable(name, "-=", 1);
              return IC.envGetVariable(name);
            case "+":
              return IC.eval(tree[2]);
            case "-":
              return IC.mathNegate(IC.eval(tree[2]));
          }
          return null;

        case "postfix":
          result = IC.eval(tree[1]);
          name = IC.semanticGetVarName(tree[1]);

          switch (tree[2].value) {
            case "++":
              IC.envAssignVariable(name, "+=", 1); break;
            case "--":
              IC.envAssignVariable(name, "-=", 1); break;
          }
          return result;

        case "power":
        case "power2":
        case "power3":
          let base = IC.eval(tree[1]);
          let source = tree[1]?.value;

          // cm => 1 cm
          if (!base && IC.typestringIsPossibleUnitIncludingSlash(source)) {
            base = IC.makeICObject(1, source);
          }
          let power = IC.eval(tree[2]);
          return IC.mathPow(base, power);

        case "add":
          return IC.semanticInfixOp(tree[1], "+", tree[2]);
        case "sub":
          return IC.semanticInfixOp(tree[1], "-", tree[2]);
        case "mul":
          return IC.semanticInfixOp(tree[1], "*", tree[2]);
        case "div":
          return IC.semanticInfixOp(tree[1], "/", tree[2]);
        case "divUnit":
          return IC.semanticDivUnit(tree[1], tree[2]);
        case "mod":
          return IC.mathMod(IC.eval(tree[1]), IC.eval(tree[2]));

        case "lsl":
          return IC.semanticInfixOp(tree[1], "<<", tree[2]);
        case "lsr":
          return IC.semanticInfixOp(tree[1], ">>", tree[2]);
        case "asr":
          return IC.semanticInfixOp(tree[1], ">>>", tree[2]);

        case "lt":
          return IC.semanticTypeCompareOp(tree[1], "<", tree[2]);
        case "gt":
          return IC.semanticTypeCompareOp(tree[1], ">", tree[2]);
        case "le":
          return IC.semanticTypeCompareOp(tree[1], "<=", tree[2]);
        case "ge":
          return IC.semanticTypeCompareOp(tree[1], ">=", tree[2]);

        case "equal":
          return IC.semanticTypeCompareOp(tree[1], "==", tree[2]);
        case "notEqual":
          return IC.semanticTypeCompareOp(tree[1], "!=", tree[2]);
        case "eq":
          return IC.semanticTypeCompareOp(tree[1], "===", tree[2]);
        case "notEq":
          return IC.semanticTypeCompareOp(tree[1], "!==", tree[2]);

        case "band":
          return IC.semanticInfixOp(tree[1], "and", tree[2]);
        case "bxor":
          return IC.semanticInfixOp(tree[1], "xor", tree[2]);
        case "bor":
          return IC.semanticInfixOp(tree[1], "or", tree[2]);
        case "land":
          return IC.semanticInfixOp(tree[1], "&&", tree[2]);
        case "lor":
          return IC.semanticInfixOp(tree[1], "||", tree[2]);

        case "conditional":
          result = IC.eval(tree[1]);
          let isTruthy = result;

          if (result == "false" || !result) {
            isTruthy = false;
          }

          return isTruthy ? IC.eval(tree[2]) : IC.eval(tree[3]);

        case "customOp":
          return IC.semanticInfixOp(tree[1], tree[2], tree[3]);

        case "functionDeclaration":
          let funcName = tree[1]?.value ?? null;      // named or anon
          let params = tree[2].map(param => param.value || param);  // handle both param objects and strings
          let body = tree[3];
          let funcObj = {
            type: "function",
            params: params,
            body: IC.cloneObject(body)
          };
          if (funcName) {
            IC.envAssignVariable(funcName, "=", funcObj);
          }
          return funcObj;
      }
    }

    return tree; // may be valid result
  },


  // Helper to detect ambiguous currency/stock symbols and log tips
  // Consolidates single source of truth for ambiguity resolution
  checkCurrencyStockAmbiguity(name) {
    const upperName = name.toUpperCase();

    // Robust access to keywords (Browser, Worker, Node)
    const keywords = (typeof IC_KEYWORDS !== 'undefined') ? IC_KEYWORDS
      : (typeof window !== 'undefined' && window.IC_KEYWORDS ? window.IC_KEYWORDS
        : (typeof self !== 'undefined' && self.IC_KEYWORDS ? self.IC_KEYWORDS
          : (typeof global !== 'undefined' && global.IC_KEYWORDS ? global.IC_KEYWORDS : null)));

    // Must be a fiat currency to be ambiguous in this context
    if (!keywords || !keywords.isFiatCurrency || !keywords.isFiatCurrency(upperName)) {
      return;
    }

    // Exclude major currencies (USD, EUR, etc.) - user preference
    const isMajor = keywords.MAJOR_CURRENCIES && keywords.MAJOR_CURRENCIES.has(upperName);

    // Check if it looks like a stock ticker (2-5 chars, optional class)
    const tickerPattern = /^[A-Z]{2,5}(?:\.[A-Z]{1,2})?$/;

    if (tickerPattern.test(upperName) && !isMajor) {
      const currencyName = keywords.CURRENCY_NAMES && keywords.CURRENCY_NAMES[upperName]
        ? keywords.CURRENCY_NAMES[upperName]
        : 'Currency';

      IC.log({ type: 'info', message: `${upperName} is ${currencyName} ($${upperName} for stock)` });
    }
  },

  evalIdentifier(name) {
    let ret = IC.envGetVariable(name);

    // Add debug trace for variables that match stock ticker pattern
    if (ret && IC.isICObject(ret) && name.match(/^[A-Z]{2,5}$/)) {
      if (!ret._debug) ret._debug = {};
      ret._debug.identifier = name;
      ret._debug.path = 'user-variable';
      ret._debug.isDefined = IC.envIsVariableDefined(name);
    }

    if (ret && IC.info.unitOverride && IC.predefinedUnit(name)) {
      // IC.log(`using ${name} as unit`); // already warned in output
      return IC.makeICObject(1, name);
    }

    if ((isNaN(ret) || !ret) && !IC.envIsVariableDefined(name)) {
      let semanticPrefix = IC.semanticPrefixFromIdentifierString(name);
      if (semanticPrefix !== null) {
        return semanticPrefix;
      }

      // Lookup priority: Use classifySymbol as SINGLE SOURCE OF TRUTH
      // This ensures proper fiat vs stock disambiguation

      const upperName = name.toUpperCase();

      // Use classifySymbol to determine what this identifier is
      const classifyIdent = (typeof IC_KEYWORDS !== 'undefined' && IC_KEYWORDS.classifySymbol)
        ? IC_KEYWORDS.classifySymbol
        : null;
      // Get caches with worker fallback (window in main, global/self in worker)
      const identStockCache = (typeof window !== 'undefined' && window.Action?.fn?.stock_cache) ||
        (typeof global !== 'undefined' && global.Action?.fn?.stock_cache) ||
        (typeof self !== 'undefined' && self.Action?.fn?.stock_cache);
      const identExtCache = (typeof window !== 'undefined' && window.externalDataCache) ||
        (typeof global !== 'undefined' && global.externalDataCache) ||
        (typeof self !== 'undefined' && self.externalDataCache);
      const identResult = classifyIdent
        ? classifyIdent(name, {
          stockCache: identStockCache,
          externalDataCache: identExtCache,
          IC: IC
        })
        : null;

      // Helper to add debug trace to result object
      const addDebugTrace = (result, trace) => {
        // Guard against primitives/undefined so we never throw when stocks are still loading
        if (!result || typeof result !== 'object') return result;
        if (name.match(/^[A-Z]{2,5}$/)) {
          if (!result._debug) result._debug = {};
          result._debug.identifier = name;
          result._debug.classifyResult = identResult ? {
            type: identResult.type,
            loaded: identResult.loaded,
            price: identResult.price
          } : null;
          result._debug.isPredefinedUnit = IC.predefinedUnit(name);
          result._debug.path = trace;
          result._debug.hasClassifyIdent = !!classifyIdent;
          result._debug.stockCacheKeys = identStockCache ? Object.keys(identStockCache) : [];
          result._debug.inCurrencyTable = IC.Unit?.currencies?.[name.toUpperCase()];
        }
        return result;
      };

      // DEBUG: Warn if classifyIdent not available
      if (typeof Debug !== 'undefined' && Debug && !classifyIdent && name.match(/^[A-Z]{2,5}$/)) {
        Debug.log('stock:ident', `⚠️  classifyIdent NOT AVAILABLE for "${name}" - will use fallback`);
      }

      // DEBUG: Log identifier classification for stock-like symbols
      if (typeof Debug !== 'undefined' && Debug && name.match(/^[A-Z]{2,5}$/)) {
        Debug.log('stock:ident', `🔍 Identifier "${name}":`);
        Debug.log('stock:ident', `  classifySymbol result: ${identResult ? JSON.stringify({ type: identResult.type, loaded: identResult.loaded, price: identResult.price }) : 'null'}`);
        Debug.log('stock:ident', `  isPredefinedUnit: ${IC.predefinedUnit(name)}`);
      }

      const hasUnit = IC.predefinedUnit(name);

      // Units should win over "famous ticker" guesses unless the stock is explicitly loaded
      if (hasUnit && identResult?.type === 'stock' && !identResult.loaded) {
        if (typeof Debug !== 'undefined' && Debug) Debug.log('stock:ident', `  🔄 Preferring unit for "${name}" over unloaded stock guess`);
        const unitResult = IC.makeICObject(1, name);
        return addDebugTrace(unitResult, 'predefined-unit');
      }

      // 1. Stocks - return price in USD, not "1 TICKER"
      // Also handle STOCK_ prefix from $TICKER syntax ($MSFT -> STOCK_MSFT -> price in USD)
      if (identResult?.type === 'stock') {
        if (typeof Debug !== 'undefined' && Debug) Debug.log('stock:ident', `  ✅ Taking stock path → STOCK("${identResult.symbol}")`);
        IC.logStockInfo(identResult.symbol, identStockCache?.[identResult.symbol]);
        const stockResult = IC.evalFunction("STOCK", [identResult.symbol]);
        return addDebugTrace(stockResult, 'stock-function');
      }

      // Handle STOCK_ prefix directly (from $TICKER syntax: $MSFT -> STOCK_MSFT)
      if (upperName.startsWith('STOCK_')) {
        const ticker = upperName.substring(6).replace(/_/g, '.');  // STOCK_MSFT -> MSFT, STOCK_BRK_A -> BRK.A
        if (typeof Debug !== 'undefined' && Debug) Debug.log('stock:ident', `  ✅ STOCK_ prefix detected → STOCK("${ticker}")`);
        IC.logStockInfo(ticker, identStockCache?.[ticker]);
        const stockResult = IC.evalFunction("STOCK", [ticker]);
        return addDebugTrace(stockResult, 'stock-prefix');
      }

      // 2. Fiat currencies - return 1 unit (e.g., "1 USD")
      if (identResult?.type === 'fiat') {
        if (typeof Debug !== 'undefined' && Debug) Debug.log('stock:ident', `  Taking fiat path → makeICObject(1, "${name}")`);
        IC.checkCurrencyStockAmbiguity(name);
        const fiatResult = IC.makeICObject(1, name);
        return addDebugTrace(fiatResult, 'fiat-currency');
      }

      // 3. Other predefined units (m, kg, EUR, MSFT, etc.) - return 1 unit
      // Stocks work like currencies - they're units with conversion rates
      if (hasUnit) {
        if (typeof Debug !== 'undefined' && Debug) Debug.log('stock:ident', `  ⚠️  Taking predefined unit path → makeICObject(1, "${name}")`);
        const unitResult = IC.makeICObject(1, name);
        return addDebugTrace(unitResult, 'predefined-unit');
      }

      // 4. Unknown identifiers that look like stock tickers
      // IMPORTANT: Do NOT auto-trigger stock lookup for every 2-5 letter identifier!
      // This would cause false positives like "NONE", "GOLD", "IRON" being treated as stocks.
      // Instead, require explicit $TICKER syntax OR famous ticker detection.
      // Famous tickers are handled by classifySymbol -> isPossibleStock -> isFamousTicker.
      // If we reach here with type='unknown', the symbol is NOT a famous ticker and needs $.
      if (identResult?.type === 'unknown' && typeof Debug !== 'undefined' && Debug) {
        const tickerPattern = /^[A-Z]{2,5}(?:\.[A-Z]{1,2})?$/;
        if (tickerPattern.test(upperName)) {
          Debug.log('stock:ident', `  ℹ️  "${upperName}" looks like a ticker but is not famous. Use $${upperName} for explicit stock lookup.`);
        }
      }
    }

    if (!IC.envIsVariableDefined(name) && CONFIG.PREDEFINED_FALLBACK[name?.toUpperCase()]) {
      return CONFIG.PREDEFINED_FALLBACK[name?.toUpperCase()];
    }

    if (ret === undefined && name.includes(" ")) {

      // sine of 30 => sine 30
      if (name.toLowerCase().includes(" of ")) {
        name = name.replace(/\bof\b/g, "");
      }

      // Possible chain of functions: "sin 30" => sin(30), "square sin 30 deg" => square sin(30 deg)
      let parts = name.split(/\s+/);
      let lastFunctionIndex = parts.findLastIndex(part => IC.predefinedFunction(part) && !IC.predefinedUnit(part));

      if (lastFunctionIndex !== -1) {

        // Split the parts into function names and the argument
        let fnNames = parts.slice(0, lastFunctionIndex + 1).join(" ");
        let potentialArg = parts.slice(lastFunctionIndex + 1).join(" ");

        let arg = IC.parseAndEval(potentialArg);
        arg = Array.isArray(arg) ? arg : [arg];

        ret = IC.evalFunction(fnNames, arg);
        if (IC.isValidEvalResult(ret)) {
          return ret;
        }
      }

      // Handle "[currency] cents" pattern: "CAD cents" => CAD/100, "AUD cents" => AUD/100
      // This must come before the generic "multiply all units" logic
      const currencyFraction = IC.parseCurrencyFraction(name);
      if (currencyFraction) {
        IC.log(`${name} => ${currencyFraction.currency}/${1 / currencyFraction.factor}`);
        return IC.makeICObject(currencyFraction.factor, currencyFraction.currency);
      }

      // if it's all units, make that unit: "m s" => m * s
      let possibleUnits = name.split(/\s+/);
      let isPredefinedUnit = possibleUnits.map(ICUnit.isPredefinedUnitCaseInsensitive).every(x => x) ? IC.makeICObject(1, name) : null;
      if (isPredefinedUnit) {
        IC.log(`processing units: ${possibleUnits.map(ICUnit.formatSIUnit).join(" * ")}`);
        return isPredefinedUnit;
      }
    }

    // TODO: add caching for isRomanNumeral
    if (ret === undefined && ICLib.isRomanNumeral(name)) {
      let val = ICLib.romanToDecimal(name);
      IC.log(`Roman numeral ${name} = ${val}`);
      return IC.makeICObject(val, null, "roman");
    }

    let fn = IC.predefinedFunction(name); // could be the actual function, not a string
    if (ret === undefined && (typeof (fn) === "string" && fn.match("array"))) {
      // total - do total from R1 to previous row
      let start = 1;
      let end = IC.envGetRowIndex();

      if (name == "subtotal") {
        // change start row to index of first blank row
        let inputRows = IC.envGetInputRows();
        let blankRowIndex = inputRows.slice(0, end).reverse().findIndex(row => row == null || row.trim() === '');
        if (blankRowIndex !== -1) {
          start = end - blankRowIndex + 1;
        }
      }

      IC.log(`using function ${name}(R${start}:R${end})`);
      return IC.evalFunction(name, IC.envGetRowRangeVariables(start, end));
    }

    if (ret === undefined && IC.predefinedConfigFunction(name)) {
      return IC.predefinedConfigFunction(name)();
    }

    // Stock ticker fallback: if undefined and in known ticker whitelist, treat as STOCK(name)
    // Uses IC_KEYWORDS.classifySymbol as the SINGLE SOURCE OF TRUTH for fiat vs stock
    if (ret === undefined) {
      const classifyFallback = (typeof IC_KEYWORDS !== 'undefined' && IC_KEYWORDS.classifySymbol)
        ? IC_KEYWORDS.classifySymbol
        : null;
      // Get caches with worker fallback (window in main, global/self in worker)
      const fallbackStockCache = (typeof window !== 'undefined' && window.Action?.fn?.stock_cache) ||
        (typeof global !== 'undefined' && global.Action?.fn?.stock_cache) ||
        (typeof self !== 'undefined' && self.Action?.fn?.stock_cache);
      const fallbackExtCache = (typeof window !== 'undefined' && window.externalDataCache) ||
        (typeof global !== 'undefined' && global.externalDataCache) ||
        (typeof self !== 'undefined' && self.externalDataCache);
      const fallbackResult = classifyFallback
        ? classifyFallback(name, {
          stockCache: fallbackStockCache,
          externalDataCache: fallbackExtCache,
          IC: IC
        })
        : null;
      if (fallbackResult?.type === 'stock') {
        IC.log(`Ticker fallback: ${name} → STOCK("${fallbackResult.symbol}")`);
        const fallbackStockResult = IC.evalFunction("STOCK", [fallbackResult.symbol]);
        // Add debug trace if it looks like a stock ticker
        if (name.match(/^[A-Z]{2,5}$/)) {
          if (!fallbackStockResult._debug) fallbackStockResult._debug = {};
          fallbackStockResult._debug.identifier = name;
          fallbackStockResult._debug.path = 'stock-fallback';
          fallbackStockResult._debug.fallbackResult = fallbackResult;
        }
        return fallbackStockResult;
      }
    }

    // lookup object access: var.x.y.z
    if (ret === undefined && name.includes(".")) {
      let parts = name.split(/\s+/);
      let objectPart = parts[0];
      let unitPart = parts.slice(1).join(" ");

      if (objectPart.includes(".")) {
        let objParts = objectPart.split(".");
        let obj = IC.envGetVariable(objParts[0]);

        for (let i = 1; i < objParts.length && obj; i++) {
          // array function: var.max
          if (Array.isArray(obj) && IC.predefinedFunction(objParts[i])?.match("array")) {
            obj = IC.evalFunction(objParts[i], obj);
            break;
          }

          let key = IC.ICObjectLiteralFindKey(obj, objParts[i]);
          obj = obj[key];
        }

        if (obj !== undefined) {
          if (unitPart && IC.predefinedUnit(unitPart)) {
            // Handle conversion: (var.field) to cm
            return IC.semanticConvert(obj, unitPart);
          }
          return obj;
        }
      }
    }

    // net sales USD => net_sales -> USD
    if (ret === undefined && name.includes(" ")) {
      let parts = name.split(/\s+/);
      let realName = null;
      let unit = null;
      for (var i = 0; i < parts.length; i++) {
        let builtUpName = parts.slice(0, i + 1).join("_");
        if (IC.envIsVariableDefined(builtUpName)) {
          realName = builtUpName;
          unit = parts.slice(i + 1).join(" ");
        }
      }

      // if a real unit, convert to that unit
      if (IC.predefinedUnit(unit)) {
        let val = IC.envGetVariable(realName);
        if (val !== undefined) {
          return IC.semanticConvert(val, unit);
        }
      }
    }

    return ret;
  },

  evalWithUnitOverride(tree) {
    IC.info.unitOverride = true;
    let ret = IC.eval(tree);
    IC.info.unitOverride = false;
    return ret;
  },

  evalUserFunction(nameOrFunctionObj, args) {
    let userDefinedFunc = nameOrFunctionObj;
    if (typeof nameOrFunctionObj === "string") {
      userDefinedFunc = IC.envGetVariable(nameOrFunctionObj);
    }

    if (!userDefinedFunc?.body) {
      return undefined;
    }

    // Initialize stackDepth if it doesn't exist
    if (IC.env.stackDepth === undefined) {
      IC.env.stackDepth = 0;
    }

    // Check if we've reached the stack limit
    const STACK_LIMIT = 20;
    if (IC.env.stackDepth >= STACK_LIMIT) {
      IC.log("Stack limit exceeded - check for function recursion");
      return "Stack limit exceeded";
    }

    let oldEnv = IC.env;
    IC.env = Object.create(IC.env);
    userDefinedFunc.params.forEach((param, index) => {
      IC.envAssignVariable(param, "=", args[index]);
    });

    IC.env.stackDepth++;
    let result = IC.eval(userDefinedFunc.body);
    IC.env.stackDepth--;

    IC.env = oldEnv;
    return result;
  },

  evalFunction(name, args) {
    if (IC.predefinedFunction(name)) {
      // Skip "fn: STOCK" log - stock info is logged separately via logStockInfo
      if (name.toUpperCase() !== 'STOCK') {
        IC.log({ type: 'function', name });
      }
    }

    if (IC.envIsUserFunction(name)) {
      return IC.evalUserFunction(name, args);
    }

    // compound function call: square sin(45) => square(sin(45))
    const functionParts = name.split(' ');
    if (functionParts.length > 1) {
      const result = functionParts.reverse().reduce((prevResult, part) => {
        return prevResult === undefined ? undefined : IC.evalFunction(part, [prevResult]);
      }, args[0]);

      if (result !== undefined) {
        return result;
      }
    }

    let ucName = name.toUpperCase();
    let lcName = name.toLowerCase();
    let str = LIB.InstaCalc.allowedfn[ucName] || "";

    if (CONFIG.UNIT_AWARE_FUNCTIONS[ucName] && IC.hasType(args[0])) {
      return CONFIG.UNIT_AWARE_FUNCTIONS[ucName](...args);
    }

    if (CONFIG.OTHER_FUNCTIONS[ucName]) {
      return CONFIG.OTHER_FUNCTIONS[ucName](...args);
    }

    if (CONFIG.ENGLISH_FUNCTION[ucName]) {
      return CONFIG.ENGLISH_FUNCTION[ucName](...args);
    }

    const possibleConversion = ICUnit.isPredefinedUnitCaseInsensitive(name);

    // remove types, convert to degrees if needed
    var processed_args = args.map((a) => {
      if (IC.hasNoType(a)) {
        return IC.val(a);
      }

      // functions take degrees by default
      if (IC.typestringIsSameUnitCategory(IC.typestring(a.t), "radian")) {
        let newObj = IC.ICObjectConvertToType(a, "degrees");
        return IC.val(newObj);
      } else {
        if (IC.typeIsByte(a.t)) {
          a = IC.typeConvertByteToNumber(a);
        }
        else if (IC.typeIsVirtual(a.t)) {
          a = IC.typeConvertVirtualToNumeric(a);
        }
        else {
          if (!possibleConversion) {
            IC.log(`removing units`);
          }
        }

        return IC.val(a);
      }
    });

    // dynamic log function: log_2 or log_x
    if (ucName.includes("LOG_")) {
      let base = ucName.split("LOG_")[1];
      let baseVal = IC.isNumberString(base) ? IC.makeNumber(base) : IC.envGetVariable(base);

      return LIB.Complex.logBase(processed_args[0], baseVal);
    }

    if (str.includes("fn().")) {
      let ret = undefined;

      // internal
      let localName = str.split("fn().")[1];

      /*
      if (localName.startsWith("array")) {
        if (args.every(a => Array.isArray(a))) {
          // Flatten each argument separately and map them
          processed_args = args.map((arg, argIndex) =>
            arg.flat(Infinity).map((a, i) => [i, IC.val(a)])
          );
          // Pass the processed arguments separately to the function
          ret = IC.CALC.fn()[localName](...processed_args);
        } else {
          // Handle the case where not every argument is an array
          processed_args = args.flat(Infinity).map((a, i) => [i, IC.val(a)]);
          ret = IC.CALC.fn()[localName](processed_args); // takes array
        }
      }
        */

      if (localName.startsWith("array")) {
        const hasArrayArg = args.some(Array.isArray);

        if (!hasArrayArg) {
          // something like IRR(val, val, val) => IRR([val, val, val])
          processed_args = [args.map((a, i) => [i, IC.val(a)])];
        } else {
          // If at least one argument is an array, process each argument separately
          processed_args = args.map(arg => {
            if (Array.isArray(arg)) {
              return arg.map((a, i) => [i, IC.val(a)]);
            }
            return IC.val(arg);
          });
        }

        ret = IC.CALC.fn()[localName](...processed_args);
      }

      // format result with these types
      if (localName == "bin") return IC.makeBinary(processed_args[0]);
      if (localName == "oct") return IC.makeOctal(processed_args[0]);
      if (localName == "hex") return IC.makeHexadecimal(processed_args[0]);

      // TODO: make excel functions like IRR return a % by default

      if (!IC.isValidEvalResult(ret)) {
        ret = IC.CALC.fn()[localName](...processed_args);
      }

      const pctFunctions = new Set("arrayIRR|RATE|RRI".split("|"));
      if (pctFunctions.has(localName)) {
        ret = IC.makeICObject(ret, null, "percent");
      }

      const roundFunctions = new Set("PMT|IPMT|PPMT|ISPMT|PV|FV|NPV".split("|"));
      if (roundFunctions.has(localName)) {
        ret = ICLib.roundToDecimal(IC.val(ret), 2);
      }

      return ret;
    }

    if (str.includes("Math.")) {
      let localName = str.split("Math.")[1];
      return Math[localName](...processed_args);
    }

    // check for predefined function BEFORE unit conversion
    // This allows functions like YEAR() to work even though "year" is also a unit
    if (IC.predefinedConfigFunction(ucName)) {
      return IC.predefinedConfigFunction(ucName)(...processed_args);
    }

    // unit name: cm(1 inch). Must come last, since C [unit] vs C (combination)
    if (possibleConversion) {
      IC.log(`convert to ${name}`);
      return IC.semanticConvert(args[0], name);
    }

    // must come last, since some functions call evalFunction
    if (CONFIG.POSTCONVERT_FUNCTIONS[lcName]) {
      return IC.predefinedPostConvertFunction(args[0], lcName);
    }

    IC.log(`missing fn: ${name}`);
    return undefined;
  },
}

var IC = {
  ...CoreModule,
  ...LogModule,
  ...EnvModule,
  ...ObjectModule,
  ...TypeModule,
  ...LiteralModule,
  ...StringModule,
  ...ParseModule,
  ...SemanticModule,
  ...MathModule,
  ...EvalModule,
};


IC.parser = {
  cache: {},

  escapeUnicode: function (str) {
    if (!str) return str;
    if (!/[^^u0000-\u007F]/.test(str)) return str; // replaced with escape-based check

    // Fast-path using unicode escape for ASCII range. Avoid running the
    // heavy replace if the string contains no non-ASCII characters.
    if (!/[^\u0000-\u007F]/.test(str)) return str;

    return str.replace(/[\u007f-\uffff]/g, c =>
      '\\u' + ('0000' + c.charCodeAt(0).toString(16)).slice(-4)
    );
  },

  /*
  @param {any} - candidate str (could be a number or other format passed in)
  @returns {string} 
  */
  unescapeUnicode: function (str) {

    // unfortunately, the memoize function turns Infinity into null. Don't use it.

    // need at least 6 characters: \u1234
    if (!str || typeof str !== "string" || str.length < 6) return str;

    return str.replace(/\\u([0-9a-f]{4})/gi, (_, first) =>
      String.fromCodePoint(parseInt(first, 16))
    );
  },

  // Central sanitization for input strings: remove invisible, control and
  // problematic whitespace characters that can break tokenization or
  // confuse users. This function uses Unicode property escapes where
  // possible to avoid listing every codepoint; it also maintains an
  // explicit list of 'space separator' characters to remove.
  sanitizeUnicodeString(str) {
    if (!str) return str;

    // Fast-path: ASCII-only strings are very common (numbers/operators).
    // A single regex check is surprisingly fast for ASCII-only inputs
    // in modern JS engines, and avoids a short JS loop for small
    // strings. Use a full-match ASCII regex here.
    if (/^[\u0000-\u007F]*$/.test(str)) return str;

    // Remove control characters (Cc) and format characters (Cf).
    // Cf includes directional markers, zero-width joiners, and similar.
    // The 'u' & 'g' flags are required for Unicode property escapes.
    // Preserve ZWJ in emoji sequences: many emoji are joined with a ZWJ
    // (U+200D) which is a Format (Cf) character - we *do* want to preserve
    // it when used to combine emoji glyphs. We'll temporarily replace
    // emoji+ZWJ+emoji with a placeholder, remove Cf characters, then
    // restore the placeholder back to ZWJ.
    const ZWJ_PLACEHOLDER = '<<IC-ZWJ>>';
    try {
      // Try to use Unicode Emoji property to identify emoji; fallback to
      // surrogate-pair detection when not available.
      try {
        // Protect ZWJ between emoji codepoints. Use Extended_Pictographic which
        // matches emoji-like glyphs more reliably than \p{Emoji} on some engines.
        str = str.replace(/(\p{Extended_Pictographic})\u200D(?=\p{Extended_Pictographic})/gu, `$1${ZWJ_PLACEHOLDER}`);
      } catch (e) {
        // Fallback heuristic: protect ZWJ when surrounded by surrogate pairs
        // Protect ZWJ when there's 1 or 2 surrogate pairs on each side
        // (handles base+modifier sequences like male+skin-tone + ZWJ + female+skin-tone)
        str = str.replace(/((?:[\uD800-\uDBFF][\uDC00-\uDFFF]){1,2})\u200D(?=(?:[\uD800-\uDBFF][\uDC00-\uDFFF]){1,2})/g, `$1${ZWJ_PLACEHOLDER}`);
      }

      // Also ensure any ZWJ that sat between surrogate pairs (emoji-like)
      // is also protected when property escapes were supported but didn't
      // catch our sequence (e.g., emoji + skin tone + ZWJ). Protect ZWJ
      // before we remove Cf characters so it isn't lost by the remover.
      str = str.replace(/((?:[\uD800-\uDBFF][\uDC00-\uDFFF]){1,2})\u200D(?=(?:[\uD800-\uDBFF][\uDC00-\uDFFF]){1,2})/g, `$1${ZWJ_PLACEHOLDER}`);

      str = str.replace(/\p{Cc}|\p{Cf}/gu, '');

      // Restore placeholder -> ZWJ
      if (str.includes(ZWJ_PLACEHOLDER)) {
        str = str.replace(new RegExp(ZWJ_PLACEHOLDER, 'g'), '\u200D');
      }
    } catch (e) {
      // If JS engine does not support \p escapes, fallback to explicit ranges.
      // Add other obscure control/combining characters like U+034F (Combining Grapheme Joiner)
      // which don't fall into the Cc/Cf classes on some engines.
      // Preserve ZWJ in fallback by using surrogate-pair heuristic
      const ZWJ_PLACEHOLDER_FALLBACK = '<<IC-ZWJ>>';
      str = str.replace(/((?:[\uD800-\uDBFF][\uDC00-\uDFFF]){1,2})\u200D(?=(?:[\uD800-\uDBFF][\uDC00-\uDFFF]){1,2})/g, `$1${ZWJ_PLACEHOLDER_FALLBACK}`);
      // Exclude U+200D (ZWJ) from the range we remove; keep it for emoji sequences
      str = str.replace(/[\0-\x1F\x7F-\x9F\u034F\u061C\u200B\u200C\u200E-\u200F\u2060-\u206F]/g, '');
      if (str.includes(ZWJ_PLACEHOLDER_FALLBACK)) {
        str = str.replace(new RegExp(ZWJ_PLACEHOLDER_FALLBACK, 'g'), '\u200D');
      }
    }

    // Also remove a curated set of Unicode 'space separators' (Zs) that
    // cause layout/parse issues *beyond* U+0020: NBSP, figure space,
    // ideographic space, thin/no-break spaces etc. Keep U+0020.
    // Exclude 200D (ZWJ) so emoji sequences are preserved
    str = str.replace(/[\u00A0\u1680\u180E\u2000-\u200C\u200E-\u200F\u2028-\u202F\u205F\u3000\uFEFF]+/g, '');

    // Remove some additional combining/invisible characters which are not
    // captured by \p{Cc} or \p{Cf}. This includes the combining grapheme
    // joiner U+034F and others that cause visual or parsing surprises.
    str = str.replace(/\u034F/g, '');

    // Optionally, normalization reduces equivalent characters to a canonical
    // form. We choose NFKC when available to fold compatibility forms.
    try {
      // Use NFC (canonical) normalization to keep semantic characters like
      // superscripts/symbols intact while still normalizing combining marks.
      // Do not use NFKC here — it maps compatibility characters (e.g. '²') to
      // their ASCII equivalents and changes parsing semantics.
      str = str.normalize && str.normalize('NFC');
    } catch (e) {
      // Ignore if normalize unsupported
    }

    return str;
  },

  // NOTE: `removeInvisibleUnicodeCharacters` removed in favor of
  // `sanitizeUnicodeString`; all call sites are updated to the new name.

  CURRENCY_COMBINED_REGEX: null,
  processCurrencySymbolsAndStrings: function (input) {

    function replaceCurrencies(s) {
      const currencies = Object.values(LIB.InstaCalc.format.currencies);
      if (!IC.parser.CURRENCY_COMBINED_REGEX) {
        const regexes = currencies.map(c => c.regex.source);
        IC.parser.CURRENCY_COMBINED_REGEX = new RegExp(regexes.join('|'), 'gi');
      }

      // Reset lastIndex before testing the regex
      IC.parser.CURRENCY_COMBINED_REGEX.lastIndex = 0;

      if (IC.parser.CURRENCY_COMBINED_REGEX.test(s)) {
        // If combinedRegex has a match, check each currency regex
        currencies.forEach((c) => {
          // Make sure to reset lastIndex before calling test on this regex as well
          c.regex.lastIndex = 0;
          if (s.match(c.regex)) {
            IC.envSetFormatter(c);
            s = s.replace(c.regex, ` (_${c.code}) `);
          }
        });
      }

      return s;
    }

    let allTokens = IC.parser.tokenize(input, LEXER);
    let tokens = allTokens.filter(t => t.type != "ws");

    // remove multiple $ ($$, $$$, etc.) from stream
    tokens = tokens.filter((t, i) => {
      let nextToken = tokens[i + 1];
      let prevToken = tokens[i - 1];
      let isRepeated = (t.text == "$" && (nextToken?.text == "$" || prevToken?.text == "$"));
      return !isRepeated;
    });

    let results = tokens.map(t => {
      // string_double or string_single
      if (t.type.indexOf("string_") >= 0) {
        let key = IC.envAddString(t.value);
        return `${key}`;
      }

      if (t.type == "id" || t.type == "source") {
        return replaceCurrencies(t.text);
      }

      return t.text;
    });

    let processed = IC.parser.tokenSmartJoin(allTokens, results);
    let format = IC.envGetFormatter();
    let strings = IC.envGetStrings();

    return { processed, format, strings };
  },

  processDateTimeExpressions: function (input) {
    const REPLACEMENTS = {
      BEFORE: { operation: '* -1 +' },
      AGO: { operation: '* - 1 +' },
      AFTER: { operation: '+' },
      FROM: { operation: '+' },
    };

    IC.CACHE.PROCESS_DATETIME_REGEX = IC.CACHE.PROCESS_DATETIME_REGEX || new RegExp(Object.keys(REPLACEMENTS).join('|'), 'i');

    // return unless we have a REPLACEMENT keyword
    if (!input.match(IC.CACHE.PROCESS_DATETIME_REGEX)) {
      return input;
    }

    let allTokens = this.tokenize(input, LEXER);
    let tokens = allTokens.filter(t => t.type !== "ws");

    let isTimePeriod = function (unit) {
      return IC.typestringIsSameUnitCategory("s", unit);
    };

    let processedTokens = tokens.map((token, i) => {
      if (token.type === 'id') {
        let parts = token.text.split(/\s+/);

        var indexes = parts.map((part, i) => { return isTimePeriod(part) ? i : -1 });
        let maxIndex = Math.max(...indexes);

        if (maxIndex == -1) {
          return token;
        }

        let keyword = parts[maxIndex + 1]?.toUpperCase();
        if (REPLACEMENTS[keyword]) {
          let replacement = REPLACEMENTS[keyword];
          parts[1] = replacement.operation;
          if (keyword == "AGO") {
            parts[1] += " TODAY";
          }

          token.text = token.value = parts.join(' ');
        }
      }
      return token;
    });

    // Remove empty tokens
    processedTokens = processedTokens.filter(token => token.text);

    return IC.parser.tokenSmartJoin(allTokens, processedTokens);
  },

  processFinalCurrencies: function (input) {
    if (!input.match(/\(_[A-Z]+\)/i)) {
      return input;
    }

    const currencies = Object.values(LIB.InstaCalc.format.currencies);
    const validCurrencies = {};
    currencies.forEach((c) => {
      validCurrencies[c.code] = c;
    });

    const lines = input.split(';').map(line => line.trim());

    let processedLines = lines.slice();

    // Handle cases like: $100 EUR in USD => (_USD) 100 EUR in USD => 100 EUR in USD
    processedLines = processedLines.map(line => {
      let parts = line.split(/\s+/);
      if (parts.length >= 3 &&
        parts[0].match(/^\(_[A-Z]+\)$/) &&
        parts[2].match(/^[A-Z]{3}$/)) {
        // Remove the initial (_XXX) if there's another currency code
        parts.shift();
        return parts.join(' ');
      }
      return line;
    });

    // treat $2 / 3 oz => ($2) / (3 oz)
    processedLines = processedLines.map(line => {
      let allTokens = IC.parser.tokenize(line, LEXER);
      let tokens = allTokens.filter(t => t.type != "ws");

      function setToken(i, value) {
        tokens[i].value = value;
        tokens[i].text = value;
      }

      for (let i = 0; i < tokens.length - 6; i++) {
        let [LP, CURRENCY_WITH_UNDERSCORE, RP, NUMBEROBJ1, DIVIDE, NUMBEROBJ2, UNIT] = tokens.slice(i, i + 7);
        let CURRENCY = CURRENCY_WITH_UNDERSCORE?.value?.slice(1);

        // (_USD) 4k / 3 oz 
        if (IC.isNumberStringIncludingScaleSuffix(NUMBEROBJ1?.value + DIVIDE?.value)) {
          DIVIDE = tokens[i + 5];
          NUMBEROBJ2 = tokens[i + 6];
          UNIT = tokens[i + 7];
        }

        if (LP.value === '(' &&
          validCurrencies[CURRENCY?.toUpperCase()] &&
          RP.value === ')' &&
          NUMBEROBJ1?.type === 'decimal' &&
          DIVIDE?.value === '/' &&
          NUMBEROBJ2?.type === 'decimal' &&
          ICUnit.isPredefinedUnitCaseInsensitive(UNIT?.value)
        ) {
          setToken(i, "");
          setToken(i + 1, CURRENCY);
          setToken(i + 2, "");
        }
      }

      line = IC.parser.tokenSmartJoin(allTokens, tokens);
      return line;
    });

    // $varname = 15$ in eur => varname = USD 15 in eur
    processedLines = processedLines.map(line => {
      if (line.match("=")) {
        let parts = line.split("=");
        let lhs = parts[0].trim();
        let rhs = parts[1].trim();

        // remove _CODE if start of lhs
        currencies.forEach((c) => {
          line = line.replace(new RegExp(`^\\(_${c.code}\\)`, 'i'), '');
        });

        return line;
      }

      return line;
    });

    processedLines = processedLines.map(line => {
      if (!line.match("->")) {
        // If no conversion is detected in the line, remove _CODE from the string for all currencies
        currencies.forEach((c) => {
          line = line.replace(new RegExp(`\\(_${c.code}\\)`, 'gi'), '');
        });
        return line;
      }

      // If a conversion is detected in the line, replace _CODE with the actual currency code
      currencies.forEach((c) => {
        line = line.replace(new RegExp(`\\(_${c.code}\\)`, 'gi'), c.code);
      });
      return line;
    });

    return processedLines.join('; ');
  },

  /*
  // now all
  processRegexReplacementsOld: function (input, REPLACEMENTS) {
    let processed = input;
 
    if (REPLACEMENTS == CONFIG.L2_REPLACEMENTS_STRINGS) {
      processed = CONFIG.L2_REPLACEMENTS_STRING_ONLY_TRIE.replaceInText(processed, false);
      return processed;
    }
 
    for (const [find, replace] of Object.entries(REPLACEMENTS)) {
      if (typeof replace === "object") {
        processed = processed.replace(replace.find, replace.replace);
      } else {
        processed = processed.replaceAll(find, replace);
      }
    }
    return processed.trim();
  },
  */

  processRegexReplacements: function (input, REPLACEMENTS) {
    // Special case for L2 replacements
    if (REPLACEMENTS == CONFIG.L2_REPLACEMENTS_STRINGS) {
      return CONFIG.L2_REPLACEMENTS_STRING_ONLY_TRIE.replaceInText(input, false);
    }

    // Split the input by semicolons
    let parts = input.split(';');

    // Process each part separately
    let processedParts = parts.map(part => {
      let processed = part;
      for (const [find, replace] of Object.entries(REPLACEMENTS)) {
        if (typeof replace === "object") {
          processed = processed.replace(replace.find, replace.replace);
        } else {
          processed = processed.replaceAll(find, replace);
        }
      }
      return processed.trim();
    });

    // Rejoin the processed parts with semicolons
    return processedParts.join(';');
  },

  // "x + 3 // note" becomes "x + 3"
  processComment: function (input) {
    const { before, comment: rawComment } = IC.parser.splitAtComment(input);
    const comment = rawComment.replace(/^[/#]+/, '').trim();
    return { processed: before, comment };
  },

  scanReferencesFromParseResult: function (ret) {
    let tree = ret.parseResult;

    const references = new Set();
    const custom_units = new Set();

    function processNode(node) {
      if (!node) return [];

      if (typeof node === "object") {
        switch (node.type) {
          case "ifStatement":
            return [node.ifcond, node.ifbody, node.elsebody];
          case "id":
            references.add(IC.canonicalName(node.value));
            return [];
          case "rowRange":
            const [r1, r2] = node.value.split(":").map(v => parseInt(v.replace(/r/i, "")));
            for (let i = r1; i <= r2; i++) {
              references.add(`r${i}`);
            }
            return [];
        }
      }

      if (Array.isArray(node)) {
        switch (node[0]) {
          case "assignmentExp":
            return [node[3]];
          case "callExp":
            return [node[2]];
          case "conversion":
            return [node[1]];
          case "unit":
            // TODO: have an "autounit" list, which we can check when vars are defined later
            // at this point, we don't know if it's a real unit or not
            if (!IC.typestringIsPossibleUnitIncludingSlash(node[2])) {
              custom_units.add(IC.semanticGetUnitNameFromString(node[2]));
            }
            return [];
          default:
            return node.filter(child => typeof child !== "string");
        }
      }

      return [];
    }

    function walkTree(node) {
      processNode(node).forEach(child => walkTree(child));
    }

    walkTree(tree);

    return { references: Array.from(references), custom_units: Array.from(custom_units) };
  },

  scanAssignedIdentifiersUsingLex: function (input) {
    if (!input.includes("=")) {
      return [];
    }

    let tokens = IC.parser.tokenize(input, LEXER).filter(t => t.type != "ws");

    let ids = tokens.map((t, i) => {
      let nextToken = tokens[i + 1];
      let prevToken = tokens[i - 1];

      // x = 3, but not "2 weeks = 3"
      if (t.type == "id" && nextToken?.type == "eq" && prevToken?.type != "decimal") {
        return {
          name: IC.canonicalName(t.text),
          orig: t.text
        }
      }
    }).filter(x => x);
    return ids;
  },

  processPrefixRegex: function (input) {
    if (!IC.PREFIX_COMBINED_REGEX) {
      const escapedPrefixes = CONFIG.PREFIX_REMOVE.map(prefix => prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      IC.PREFIX_COMBINED_REGEX = new RegExp('^\\s*(' + escapedPrefixes.join('|') + ')\\s+', 'i');
    }
    let prefixRegex = IC.PREFIX_COMBINED_REGEX;

    let s = input;
    if (s.match(prefixRegex)) {
      s = s.replace(prefixRegex, '');
      s = s.replace(/\?/g, '');  // replace all question marks globally
    }

    return s;
  },

  // parse input using lexer. Returns ALL tokens, including items on ignore list
  tokenize(input, lexer) {
    if (!input) return [];
    let key = "tokenize:" + input;
    let tokens = IC.CACHE.get(key);
    if (!tokens) {
      lexer.IGNORE_ENABLED = false;
      tokens = [...lexer.reset(input)];
      lexer.IGNORE_ENABLED = true;
      IC.CACHE.set(key, tokens);
    }

    return tokens;
  },

  // 250 billion dollars a month => 250 billion dollars / month
  processAasKeyword(input) {
    if (!input?.match(/\sa\s/i)) {
      return input;
    }

    const tokens = input.split(' ');
    const processedTokens = tokens.map((token, index) => {
      if (token.toLowerCase() === 'a') {
        const prevToken = tokens[index - 1];
        const nextToken = tokens[index + 1];

        if (
          prevToken &&
          nextToken &&
          IC.predefinedUnit(prevToken) &&
          IC.predefinedUnit(nextToken)
        ) {
          return '/';
        }
      }
      return token;
    });

    return processedTokens.join(' ');
  },

  // 50 miles/hour in feet/sec ... if in is surrounded by units, convert it
  processInAsKeyword(input) {
    if (!input.match(/\bin\b/i) || IC.envIsDefined("in")) {
      return input;
    }

    // split up by whitespace too
    let tokens = IC.parser.tokenize(input, LEXER);

    function replaceIn(id, tokenIdx, allTokens) {
      const parts = id.split(" ");
      const tokensWithIn = parts.map(p => p.toLowerCase() == "in");
      const restOfTokens = allTokens.slice(tokenIdx + 1).map(t => t.text).join('');

      // no conversion needed
      if (parts.length === 1 || !tokensWithIn.some(item => item)) {

        // ($0.75) / (1.74 oz) in $/lb => ( (_USD) 0.75) / (1.5 oz) in (_USD) /lb
        // rest of tokens matches a paren
        if (id == "in" && restOfTokens.match(/^\s*[(]/i)) {
          return "->";
        }

        return id;
      }

      // TODO: clean this up a bit...
      let replacement = parts.map((val, i) => {
        if (!tokensWithIn[i]) {
          return val;
        }

        let ti = tokenIdx;
        let all = allTokens;

        let lastIndex = parts.length - 1;
        let nextIndex = i + 1;

        // next token is "in", and is not last, so skip me
        if (tokensWithIn[nextIndex] && nextIndex != lastIndex) {
          return val;
        }

        // if we're in a conversionDiv, and next token is " /s" convert "in" to ->
        if (allTokens[tokenIdx + 1]?.type == 'ws' && allTokens[tokenIdx + 2]?.value == '/') {
          return '->';
        }

        // search next 3 tokens for (_USD): whitespace, left paren, then _USD
        if (allTokens[ti + 1]?.type == 'ws' && allTokens[ti + 2]?.value == '(' && allTokens[ti + 3]?.value.match(/_[A-Z]+/)) {
          return "->";
        }

        // if next token is a paren, convert "in" to ->
        // example: 15 m/s in (cm)/day, where "cm" was a variable
        if (restOfTokens.match(/\s+?\(/)) {
          return "->";
        }

        // convert "in" to "->"", except for last position
        return i == lastIndex ? val : "->";
      }).join(" ");

      return replacement;
    }

    let replacements = tokens.map((t, i) => {
      if (t.type != "id") return t.text;

      return replaceIn(t.text, i, tokens);
    });

    return replacements.join("");
  },

  /*
  Insert -> operator where needed in paren expressions, ex:
    (10) cm / kg / day => (10) ->  cm / kg / day 
  */
  processParenUnitExpressions: function (input) {

    // Check if the string contains both parentheses and a slash.
    if (!/\(.*\).*\//.test(input)) {
      return input;
    }

    // quick and dirty...
    let tokens = input.split(/(\(|\))/); // split on parens
    let replacements = tokens.map((currentToken, i) => {
      let prevToken = tokens[i - 1];

      if (currentToken.includes("/") && prevToken == ")" && IC.typestringIsPossibleUnitIncludingSlash(currentToken)) {
        return " -> " + currentToken;
      }
      return currentToken;
    });

    // not real tokens, just use regular join
    return replacements.join('');
  },

  // Handle more natural input for conversions, i.e.
  // this: 30 miles per gallon in dollars per mile at $3/gallon  [A -> B @ C]
  // becomes: 30 miles per gallon at $3 per gallon in dollars per mile [C @ A -> B]
  processConversionAtOrder: function (input) {
    if (!input.includes("@")) {
      return input;
    }

    // Regular expression to match "A -> B @ C" pattern with spaces
    const conversionAtRegex = /(.+?)\s*->\s*(.+?)\s*@\s*(.+)/;

    // Check if the input contains the "->" followed by "@" pattern
    if (conversionAtRegex.test(input)) {
      // If the pattern is found, reorder the parts
      return input.replace(conversionAtRegex, (match, A, B, C) => {
        // Check if the matched parts contain parentheses
        if (A.includes('(') || B.includes('(') || C.includes('(')) {
          // If any part contains parentheses, do not reorder
          return match;
        }

        // Reorder the parts to "C @ A -> B"
        return `${C} @ ${A} -> ${B}`;
      });
    }

    // If the pattern is not found, return the original input
    return input;
  },

  checkFailedResult(input = "", result) {
    if (input.trim() == "") {
      let env = IC.envGet();
      let index = IC.envGetRowIndex();
      let inputRows = IC.envGetInputRows();
      let numberOfPreviousRows = 3; // Number of previous rows to check
      let previousRows = Array.from({ length: numberOfPreviousRows }, (_, i) => env['r' + (index - i)]);

      // valid number row
      function isValidRow(row, rowIndex) {
        return row != null && !isNaN(row) && inputRows[index - rowIndex]?.trim() !== "";
      }

      if (previousRows.every((row, i) => isValidRow(row, i + 1))) {
        let numericItems = IC.envGetRowRangeVariables(1, index).map(IC.val).filter(val => typeof (IC.val(val)) == 'number')
        let total = IC.evalFunction('total', numericItems);
        let result = {
          final_result: total
        };

        let formattedTotal = IC.parser.applyRawNumberFormatting(total, IC.envGetSettings()?.locale);
        let msg = `total: ${formattedTotal}`;
        IC.log(msg);
        return;
      }
    }

    if (IC.isValidEvalResult(result) || input == "") {
      return;
    }

    var unit = IC.predefinedUnit(input.trim());
    if (unit) {
      let msg = "";
      if (typeof unit == "object") {
        let suffix = unit.name ? ` (${unit.name})` : "";
        msg = `unit ${input}${suffix}, usage: '1 ${input}' or '1 ${input} to {unit}'`;
      }
      else {
        msg = `unit alias for: ${unit}`;
      }
      IC.log(msg);
      return;
    }

    // check for % being used as modulo
    if (input.match(/\w\s+%\s+\w/) || input.match(/\bMOD[(]/)) {
      IC.log({ type: 'tip', message: 'Modulo (remainder): use `15 mod 7`' });
      return;
    }

    if (input.match(/\w\s+[\^]\s+\w/)) {
      IC.log({ type: 'tip', message: '^ for exponent, `xor` for bitwise XOR' });
      return;
    }

    if (input.match(/\w\s+[\&]\s+\w/)) {
      IC.log({ type: 'tip', message: 'Bitwise AND: use `0x01 and 0x02`' });
      return;
    }

    if (input.match(/\w\s+[\|]\s+\w/)) {
      IC.log({ type: 'tip', message: 'Bitwise OR: use `0x01 or 0x02`' });
      return;
    }

    if (input.match(/\w\s+[~]\s+\w/)) {
      IC.log({ type: 'tip', message: 'Bitwise NOT: use `~value`' });
      return;
    }

    if (input.includes("=")) {
      // check for starting with keyword (use CONFIG.KEYWORDS as source of truth)
      const keywords = Object.keys(CONFIG.KEYWORDS);
      const kwregex = new RegExp("^\\s*(" + keywords.join("|") + ")\\s+(\\w+)", "i");
      let kwmatch = input.match(kwregex);
      if (kwmatch) {
        IC.log({ type: 'tip', message: `Variable starts with keyword '${kwmatch[1]}', try: ${kwmatch[1]}_${kwmatch[2]} = ...` });
        return;
      }
    }

    let fnname = input.trim().toLowerCase().split("(")[0];
    let fn = IC.predefinedFunction(fnname);
    let help = "Usage: ";

    if (fn && fnname.length >= 2) {
      if (CONFIG.FUNCTION_USAGE_MSG[fnname]) {
        help = CONFIG.FUNCTION_USAGE_MSG[fnname];
      }
      else if (fn.includes("array")) {
        help += `${fnname}(r1:r3) or ${fnname}(value1, value2, ...)`;
      } else if (fn.includes("fn().")) {
        let localName = fn.split("fn().")[1];
        let argCount = IC.CALC.fn()[localName].length;

        // build example string from argcount
        let argHelp = argCount === 0 ? '()' :
          argCount === 1 ? '(value)' :
            `(${Array.from({ length: argCount }, (_, i) => `value${i + 1}`).join(', ')})`;

        help += `${fnname}${argHelp}`;
      }
      else {
        help += `${fnname}(value)`;
      }

      IC.log(help);
      return;
    }

    let suggestion = IC.parser.suggestFunction(input);
    let SUGGEST_IGNORE = ["c", "p"];
    if (suggestion && !SUGGEST_IGNORE.includes(suggestion.toLowerCase())) {
      help = "Suggest: " + CONFIG.FUNCTION_USAGE_MSG[suggestion];
      IC.log(help);
      return;
    }
  },

  // returns name of a function similar to the input string, i.e "payment" -> PMT
  suggestFunction(inputStr) {
    let functionList = CONFIG.FUNCTION_USAGE_MSG;

    // Convert input string to lowercase for case-insensitive matching
    const input = inputStr.toLowerCase();

    // Initialize variables to store the closest match
    let closestMatch = "";
    let closestMatchScore = 0;

    // Iterate over the function list and find the closest match
    for (const functionName in functionList) {
      const functionDesc = functionList[functionName].toLowerCase();

      // Calculate the similarity score using a simple matching algorithm
      let score = 0;
      for (const word of input.split(" ")) {
        // ignore single letter words
        if (word.length < 2) {
          continue;
        }

        // ignore symbols
        if (word.match(/^[^a-z0-9]+$/)) {
          continue;
        }

        // ignore row reference
        if (word.match(/^r[0-9]+/i)) {
          continue;
        }

        // skip stop words
        if (CONFIG.STOP_WORDS[word]) {
          continue;
        }

        if (functionDesc.includes(word)) {
          score++;
        }
      }

      // Update the closest match if the current score is higher
      if (score > closestMatchScore) {
        closestMatch = functionName;
        closestMatchScore = score;
      }
    }

    // Return the closest match if there is a match with a non-zero score
    return closestMatchScore >= 2 ? closestMatch : null;
  },

  scanExtraFormatting(input, result, format) {
    if (input.includes("$$")) {
      return "round2";
    }

    return format;
  },

  processIdentifiers: function (input, newIds = {}, assignedIDs = [], dateCache) {

    // Inner function to handle arrow function parameter wrapping
    function wrapFunctionParameters(input, tokens) {
      let arrowFunctionParams = new Set();
      let parametersInParenLists = new Set();

      // First pass: identify parameters that are already in parenthesized lists
      for (let i = 0; i < tokens.length - 1; i++) {
        const currentToken = tokens[i];
        const nextToken = tokens[i + 1];

        // Pattern: ")" "rarrow" for (a, b) =>
        if (currentToken.type === "rp" && nextToken.type === "rarrow") {
          // Walk backwards to find matching "(" and mark parameters as already in parens
          let depth = 1;
          let j = i - 1;
          while (j >= 0 && depth > 0) {
            if (tokens[j].type === "rp") depth++;
            if (tokens[j].type === "lp") depth--;
            if (depth > 0 && tokens[j].type === "id") {
              parametersInParenLists.add(tokens[j].value);
            }
            j--;
          }
        }
      }

      // Second pass: identify all arrow function parameters
      for (let i = 0; i < tokens.length - 1; i++) {
        const currentToken = tokens[i];
        const nextToken = tokens[i + 1];

        // Pattern 1: "id" "rarrow" (x =>)
        if (currentToken.type === "id" && nextToken.type === "rarrow") {
          arrowFunctionParams.add(currentToken.value);
        }

        // Pattern 2: ")" "rarrow" for (a, b) =>
        if (currentToken.type === "rp" && nextToken.type === "rarrow") {
          // Walk backwards to find matching "(" and extract parameters
          let depth = 1;
          let j = i - 1;
          while (j >= 0 && depth > 0) {
            if (tokens[j].type === "rp") depth++;
            if (tokens[j].type === "lp") depth--;
            if (depth > 0 && tokens[j].type === "id") {
              arrowFunctionParams.add(tokens[j].value);
            }
            j--;
          }
        }
      }

      // Only return parameters that are NOT already in parenthesized lists
      // This prevents double-wrapping: (acc, x) should not become ((acc), (x))
      let paramsToWrap = new Set();
      for (let param of arrowFunctionParams) {
        if (!parametersInParenLists.has(param)) {
          paramsToWrap.add(param);
        }
      }

      return paramsToWrap;
    }

    // Get token stream and extract arrow function parameters
    let arrowTokens = IC.parser.tokenize(input, LEXER).filter(t => t.type !== "ws");
    let arrowFunctionParams = wrapFunctionParameters(input, arrowTokens);

    // function params
    let params = {};
    function isDefinedVariable(name) {
      return IC.envIsDefined(name) || params[IC.canonicalName(name)] || IC.envIdentifierExists(name) || arrowFunctionParams.has(name);
    }

    function replaceAllSingleLetterIds(input) {
      // https://stackoverflow.com/questions/35223206/how-to-split-unicode-string-to-characters-in-javascript 

      let graphemes = IC.parser.unescapeUnicode(input).match(/./gu);
      let allLettersAreVariables = graphemes.every((id) => isDefinedVariable(IC.parser.escapeUnicode(id)));

      if (allLettersAreVariables && !isDefinedVariable(input)) {
        return graphemes.map((id) => "(" + IC.parser.escapeUnicode(id) + ")").join("");
      }
      return input;
    }

    function replaceMissingIdentifiers(input) {
      if (IC.typestringIsPossibleUnitIncludingSlash(input) || isDefinedVariable(input) || IC.predefinedFunction(input)) {
        return input;
      }

      // x=1; y=2; xy => (x)(y)
      let processed = replaceAllSingleLetterIds(input);
      return processed;
    }

    function replaceUnitAlias(input) {
      let parts = input.split(" ").map((word) => {
        if (isDefinedVariable(word)) {
          return word;
        }
        let lower = word.toLowerCase();
        return LIB.Unit.alias[lower] || word;
      });
      return parts.join(" ");
    }

    function wrapObjectAccess(input) {
      if (!input.includes(".") || input.startsWith("(")) {
        return input;
      }
      let parts = input.split(/\s+/);
      let objectParts = [];
      let remainingParts = [];
      let foundDot = false;

      for (let part of parts) {
        if (part.includes(".") || foundDot) {
          objectParts.push(part);
          foundDot = true;
        } else if (objectParts.length === 0) {
          objectParts.push(part);
        } else {
          remainingParts.push(part);
        }
      }

      let objectAccess = objectParts.join(" ");
      let dotParts = objectAccess.split(".");
      let firstPart = dotParts[0].trim();
      let canonicalFirstPart = IC.canonicalName(firstPart);

      if (isDefinedVariable(canonicalFirstPart)) {
        let wrappedAccess = "(" + canonicalFirstPart + "." + dotParts.slice(1).join(".") + ")";
        return [wrappedAccess, ...remainingParts].join(" ");
      }

      return input;
    }

    // original input has semicolon to have multiple assignemnts: x = 4; 4x
    const hasSemiColon = input.includes(";");
    function wrapKnownVariables(input) {
      let prefix = IC.parseKnownPrefix(input);
      // leave prefix unwrapped for now: "square root of x", not "square root of (x)"
      if (prefix) {
        return input;
      }

      // split into sections: 2 pi r^2 => 2, pi, r^2
      let parts = input.trim().split(" ");
      let replacements = parts.map((part) => {
        // hack for scenario:
        // net = pv 100k net amount
        // do not wrap second (net) since it's defined in this
        // but we want to allow "x = 3; 4x" => "4(x)"
        if (!hasSemiColon && assignedIDs.filter(id => id.name == part).length >= 1) {
          return part;
        }

        // quick return if the variable is found
        if (part && isDefinedVariable(part)) {
          if (CONFIG.PREDEFINED_AND_UNIT[part.trim().toUpperCase()] || IC.envIsUserFunction(part)) {
            return part;
          } else {
            return "(" + part.trim() + ")";
          }
        }

        // could be r^2  
        if (part.includes("^")) {
          return "(" + part.trim() + ")";
        }

        if (CONFIG.NUMBER_SCALES[part] && !isDefinedVariable(part)) {
          return "(" + CONFIG.NUMBER_SCALES[part] + ")";
        }

        let isVariableName = part.match(/^[a-z_]+$/i) && part?.length > 3;
        if (isVariableName && !IC.envIsDefinedSearchAll(part)) {
          let variant = IC.envFindInflectionVariant(part);
          if (variant) {
            IC.log(`treating '${part}' as ${variant}`);
            return "(" + variant + ")";
          }
        }

        return part;
      });

      let replacement = replacements.join(" ");
      return replacement;
    }

    let allTokens = IC.parser.tokenize(input, LEXER);
    let tokens = allTokens.filter(t => t.type != "ws");

    // for functions
    let inFunctionDeclaration = false;
    let parenDepth = 0;

    let results = tokens.map((t, i) => {

      // Start skipping at 'function' keyword, stop at first '{'
      if (t.type === 'function') {
        inFunctionDeclaration = true;
        params = {};
        return t.text;
      }

      if (inFunctionDeclaration) {
        if (t.type === 'lp') {
          parenDepth++;
        }

        if (t.type === 'id' && parenDepth > 0) {
          params[IC.canonicalName(t.text)] = true;
        }

        if (t.type === 'rp') {
          parenDepth--;
          if (parenDepth === 0) {
            inFunctionDeclaration = false;
          }
        }

        return t.text;
      }

      if (t.type != "id") {
        return t.text;
      }

      let nextToken = tokens[i + 1];
      let name = t.text;
      let canonical = IC.canonicalName(name);
      // in assignment - TODO, use moo rules to set state as id.assign = true
      if (nextToken?.type == "eq") {
        return canonical;
      }

      // if next token is :, we are in object properties, don't wrap
      if (nextToken?.text == ":") {
        return name;
      }

      // if previous token is ".", property access, don't wrap
      let prevToken = tokens[i - 1];
      if (prevToken?.text == ".") {
        return name;
      }

      if (isDefinedVariable(canonical)) {
        name = canonical;
      }

      name = replaceUnitAlias(name);
      name = wrapKnownVariables(name);
      name = wrapObjectAccess(name);
      name = replaceMissingIdentifiers(name);
      return name;
    });

    let processed = IC.parser.tokenSmartJoin(allTokens, results);

    return processed;

    // replace identifiers with underscore versions
    // wrap found identifier references in ()
    // break up single letter ids: xy => (x)(y)
    // everything else must be a unit (found) or plain-english operator (replace) plain-english number (replace)
  },

  // join tokens together, but keep original whitespace
  tokenSmartJoin(allTokens, resultTokens) {
    return allTokens
      .map(token => (token.type === 'ws' ? token.text : resultTokens.shift()))
      .join('');
  },

  // turns "x" into * as needed 
  processXToMultiply(input, identifiersProcessed = false) {
    let processed = input;
    if (!input.match(/x/i)) {
      return processed;
    }

    // \d\.?0 => 30x4, or 30.0x4, but not 0x4 (which is hex)  
    // [1-9] => 31x4 (regular digits)
    // \w\s => 3cm x 4 (word with space), not function like MAX(123, 456)
    // ['")%] => 3'x4 (quote or paren, or pct) - TODO: this stuff is fragile, have a better way to detect x
    processed = processed.replace(/(\d\.?0|[1-9]|\w\s|['")%])\s*x\s*(-?\d|[.(])/ig, "$1 * $2");

    if (identifiersProcessed) {
      processed = processed.replace(/(\)|\d)X(\w+\()/ig, (match, p1, p2) => {
        let funcName = p2.split('(')[0];
        if (IC.predefinedFunction(funcName)) {
          return `${p1} * ${p2}`;
        }
        return match;
      });

      // Handle cases like "3x total sale"
      processed = processed.replace(/(\d+)\s*x\s+(\w+(?:\s+\w+)*)/gi, (match, num, variable) => {
        if (IC.envIsDefined(variable.trim())) {
          return `${num} * (${variable.trim()})`;
        }
        return match;
      });
    }

    if (identifiersProcessed && !IC.envIsDefined('x')) {

      // replace X fn() with * fn()
      let tokens = processed.split(" ");

      // go through tokens. if current token is x, and next token is a function, replace x with *
      for (let i = 0; i < tokens.length - 1; i++) {
        let nextToken = tokens[i + 1];
        let prevToken = tokens[i - 1];
        let cleanedToken = nextToken.split(/[^a-z_]/)[0];

        // if prev token is something to ignore, skip it

        if (tokens[i].toLowerCase() == "x") {

          // function foo(x){ x as in} || the x should not be a mult
          if (!prevToken?.match(/(\w|[)])$/)) {
            continue;
          }

          // Look ahead to see if any combination of the next tokens forms a defined variable
          let variableName = '';
          for (let j = i + 1; j < tokens.length; j++) {
            variableName = tokens.slice(i + 1, j + 1).join(' ').trim();
            if (IC.envIsDefinedSearchAll(variableName)) {
              tokens[i] = '*';
              break;
            }
          }

          if (IC.envIsDefinedSearchAll(cleanedToken)) {
            tokens[i] = "*";
          }

          let isPlainEnglishNumber = IC.parser.processPlainEnglishNumbers(cleanedToken) !== cleanedToken;
          if (isPlainEnglishNumber) {
            tokens[i] = "*";
          }
        }
      };
      processed = tokens.join(" ");
    }

    if (processed != input && IC.envIsDefined('x')) {
      IC.log(`using 'x' as multplication (not variable)`);
    }

    return processed;
  },

  // sin^2(45) => sin(45)^2
  processFunctionWithExponent(input) {

    if (!input.match(/\w\w\^\d[(]/)) {
      return input;
    }

    if (!CONFIG.FUNCTION_WTIH_EXPONENT) {
      const trigFunctions = new Set(['sin', 'cos', 'tan', 'csc', 'sec', 'cot', 'sinh', 'cosh', 'tanh', 'csch', 'sech', 'coth']);
      const logFunctions = new Set(['log', 'ln']);
      CONFIG.FUNCTION_WTIH_EXPONENT = new Set([...trigFunctions, ...logFunctions]);
    }

    // Tokenize the input -- get cloned array
    let tokens = IC.parser.tokenize(input, LEXER).slice();

    for (let i = 0; i < tokens.length - 4; i++) {
      if (CONFIG.FUNCTION_WTIH_EXPONENT.has(tokens[i].value) && tokens[i + 1].value === '^' && tokens[i + 2].type === 'decimal' && tokens[i + 3].value === '(') {
        // Find the closing parenthesis
        let depth = 1;
        let j = i + 4;
        let lastParenIndex = j;

        while (j < tokens.length && depth > 0) {
          if (tokens[j].value === '(') depth++;
          if (tokens[j].value === ')') depth--;
          lastParenIndex = j;
          j++;
        }

        if (depth === 0) {
          let fn = tokens[i];
          let carat = tokens[i + 1];
          let power = tokens[i + 2];
          let startParen = tokens[i + 3];
          let endParen = tokens[lastParenIndex];

          // Remove the power and its preceding caret
          tokens.splice(i + 1, 2);

          // Insert the power and caret after the closing parenthesis
          tokens.splice(lastParenIndex - 1, 0, carat, power);

          // Note: we do NOT advance to the last token, we allow inner arguments to be processed also,
          // for example: sin^2(cos^2(45)) => sin(cos(45)^2)^2 
        }
      }
    }

    return tokens.map(t => t.text).join("");
  },

  /**
   * Token-based $TICKER processing.
   * Converts $AAPL → STOCK_AAPL using lexer tokens for robust context handling.
   *
   * Benefits over regex approach:
   * - Strings like "$AAPL" are single tokens, naturally protected
   * - Comments like "// $AAPL" are single tokens, naturally protected
   * - Clear token boundaries prevent false matches
   *
   * Token structure: "$AAPL" tokenizes as source("$") + id("AAPL")
   *
   * Rules:
   * - $TICKER must be 2-5 letters (optionally with .A or .B suffix for share classes)
   * - Variables, functions, and constants take precedence (e.g., $PI stays as $PI)
   * - Only known tickers from ticker data are converted
   * - The id must not be followed by ( (function call)
   */
  processTickerSyntaxTokenized(input) {
    // Quick check - if no $ in input, skip entirely
    if (!input.includes('$')) {
      return input;
    }

    // Tokenize to get proper token boundaries
    // Strings and comments become single tokens, naturally protected
    // Clone tokens to avoid mutating cached originals (tokenize caches results)
    const allTokens = IC.parser.tokenize(input, LEXER).map(t => ({ ...t }));
    const tokens = allTokens.filter(t => t.type !== 'ws');

    // Walk through tokens looking for source("$") + id pattern
    for (let i = 0; i < tokens.length - 1; i++) {
      const curr = tokens[i];
      const next = tokens[i + 1];

      // Skip if not a $ source token
      if (curr.type !== 'source' || curr.text !== '$') {
        continue;
      }

      // Next token must be an identifier
      if (next.type !== 'id') {
        continue;
      }

      const symbol = next.text;

      // Ticker must be 2-5 letters, optionally with .A or .B class suffix
      // The id token may contain the suffix already (e.g., "BRK.A")
      if (!/^[a-zA-Z]{2,5}(?:\.[A-Z]{1,2})?$/i.test(symbol)) {
        continue;
      }

      // Check if followed by ( which would indicate a function call like $AAPL()
      const afterNext = tokens[i + 2];
      if (afterNext && afterNext.text === '(') {
        continue;
      }

      const upper = symbol.toUpperCase();
      const lower = symbol.toLowerCase();

      // Check if symbol is a predefined function (SIN, COS, LOG, etc.)
      if (IC.predefinedFunction && IC.predefinedFunction(symbol)) {
        continue; // Keep as currency amount
      }

      // Check if symbol is a predefined constant (PI, TAU, E, PHI)
      if (IC.predefinedIdentifier && IC.predefinedIdentifier(symbol)) {
        continue; // Keep as currency amount
      }

      // Check if identifier exists in current calc context (case-insensitive)
      if (IC.envIdentifierExists && (IC.envIdentifierExists(symbol) || IC.envIdentifierExists(upper) || IC.envIdentifierExists(lower))) {
        continue; // Keep as currency amount with user variable
      }

      // Check if it's a known ticker
      if (!this.isKnownTicker(upper)) {
        continue;
      }

      // Convert: clear the $ token, change id to STOCK_XXX
      // We set text to empty string rather than removing, to preserve token count for tokenSmartJoin
      curr.text = curr.value = '';
      next.text = next.value = 'STOCK_' + upper.replace(/\./g, '_');
    }

    return IC.parser.tokenSmartJoin(allTokens, tokens);
  },

  /**
   * Check if a symbol is a known stock ticker.
   * Checks multiple sources: externalDataCache, knownTickerSet, stock_cache.
   */
  isKnownTicker(symbol) {
    const upper = symbol.toUpperCase();

    // Check externalDataCache (main thread)
    const extCache = (typeof window !== 'undefined' && window.externalDataCache) ||
      (typeof global !== 'undefined' && global.externalDataCache) ||
      (typeof self !== 'undefined' && self.externalDataCache);

    if (extCache?.isKnownTicker?.(upper)) {
      return true;
    }

    // Check knownTickerSet (worker - full ticker list)
    const tickerSet = (typeof window !== 'undefined' && window.knownTickerSet) ||
      (typeof global !== 'undefined' && global.knownTickerSet) ||
      (typeof self !== 'undefined' && self.knownTickerSet);

    if (tickerSet?.has?.(upper)) {
      return true;
    }

    // Check stock_cache (already loaded stocks)
    const stockCache = (typeof window !== 'undefined' && window.Action?.fn?.stock_cache) ||
      (typeof global !== 'undefined' && global.Action?.fn?.stock_cache) ||
      (typeof self !== 'undefined' && self.Action?.fn?.stock_cache);

    if (stockCache?.[upper]) {
      return true;
    }

    return false;
  },

  processUnitTypeBeforeNumber(input) {
    // test for letter followed by space and number or .
    if (!input.match(/\w\s+[0-9.,]/)) {
      return input;
    }

    // Tokenize the input
    let tokens = IC.parser.tokenize(input, LEXER).slice();

    // deep clone tokens array since we're modifying it
    // Use JSON.parse/stringify instead of structuredClone to handle tokens with function properties
    tokens = JSON.parse(JSON.stringify(tokens));

    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type === 'id') {
        // Split the token value into parts
        const parts = tokens[i].value.split(/\s+/);
        let possibleUnit = parts[0];
        let rest_of_token = parts.slice(1).join(' ');

        // see how many of the next tokens can be added to rest of string;
        let rest_of_string = rest_of_token;
        let possibleNumber = rest_of_string;
        let tokensUsed = 0;

        // Build the number string, including commas
        for (let j = i + 1; j < tokens.length; j++) {
          if (tokens[j].value === ',' && (j + 1 < tokens.length && tokens[j + 1].value.trim() === '')) {
            // Stop if we encounter a comma followed by a space or end of input
            break;
          }
          rest_of_string += tokens[j].value;
          if (IC.isNumberStringIncludingScaleSuffix(rest_of_string.replace(/,/g, ''))) {
            possibleNumber = rest_of_string;
            tokensUsed++;
          } else {
            break;
          }
        }

        if ((IC.stringIsCurrencyUnit(possibleUnit) || ICUnit.isPredefinedUnitCaseInsensitive(possibleUnit)) && IC.isNumberStringIncludingScaleSuffix(possibleNumber.replace(/,/g, ''))) {
          // Swap the currency unit and the number, ensuring a space between them
          tokens[i].value = possibleNumber + ' ' + possibleUnit;
          tokens[i].text = possibleNumber + ' ' + possibleUnit;

          // clear out the used tokens
          for (let j = 1; j <= tokensUsed; j++) {
            tokens[i + j].value = "";
            tokens[i + j].text = "";
          }
        }
      }
    }

    // Filter out empty tokens and join the remaining ones
    let ret = tokens.filter(t => t.text !== "").map(t => t.text).join("");
    return ret;
  },

  // 800 miles 3 feet 6 inches gets implicitly added
  processChainOfAddedUnits(input) {
    if (!input?.match(/\w\s+[0-9.,]/)) {
      return input;
    }

    // Tokenize the input
    let allTokens = IC.parser.tokenize(input, LEXER).slice();
    let tokens = allTokens.filter(t => t.type != "ws");

    tokens.forEach((t, index) => {
      if (t.type === 'id' && tokens[index - 1]?.type == 'decimal') {
        // insert a space between a number and a unit
        let val = t.value.replace(/([0-9])([a-zA-Z])/, "$1 $2");
        const parts = val.split(/\s+/);

        let newValue = "";
        let i = 0;

        let hasChain = false;

        while (i < parts.length) {
          let thisUnit = parts[i];
          let nextNumber = parts[i + 1];

          if (i + 1 < parts.length && IC.isNumberString(nextNumber) && ICUnit.isPredefinedUnitCaseInsensitive(thisUnit)) {
            newValue += " " + thisUnit + " + " + nextNumber;
            hasChain = true;
            i += 2;
          } else if (ICUnit.isPredefinedUnitCaseInsensitive(thisUnit)) {
            // Handle the last unit without a following number
            newValue += " " + thisUnit;
            i++;
          } else {
            newValue = ""; // Pattern broken, clear the newValue
            break;
          }
        }

        // add "(" to previious toknen, append ")" to this one
        if (hasChain) {
          let prevToken = tokens[index - 1];
          prevToken.value = prevToken.text = "(" + prevToken.text;
          newValue += ")";
        }

        if (newValue.trim().length > 0) {
          t.value = newValue.trim();
          t.text = newValue.trim();
        }
      }
    });

    return IC.parser.tokenSmartJoin(allTokens, tokens);
  },

  // gb-month to (gb month), kg*m/s^2 to (kg m/s^2), m^2*(kg*s)^-3*A^-2" 
  processDashUnitSeparator: function (input) {
    if (!input.includes("-") && !input.includes("*")) {
      return input;
    }

    // Tokenize the input
    let allTokens = IC.parser.tokenize(input, LEXER);
    let tokens = allTokens.filter(t => t.type != "ws");
    let unitSeparator = /^[*-]$/;

    // Process the tokens
    tokens.forEach((t, index) => {
      let separatorToken = tokens[index + 1];
      let nextUnitToken = tokens[index + 2];

      if (separatorToken?.value == "^") {
        // check for minus sign as next token: kg^-1*m => kg^-1 m
        if (nextUnitToken?.value == "-") {
          separatorToken = tokens[index + 4];
          nextUnitToken = tokens[index + 5];
        }
        else {
          separatorToken = tokens[index + 3];
          nextUnitToken = tokens[index + 4];
        }
      }

      // kg*m -> kg m, 10 watt-hour in joules => 10 watt hour in joules [hour_in_joules is a full id]
      if (t.type === 'id' && unitSeparator.test(separatorToken?.value) && IC.predefinedUnit(t.value) && IC.predefinedUnit(nextUnitToken?.value?.split(" ")[0])) {
        separatorToken.value = separatorToken.text = " ";
        //IC.log(`unit: ${tokens[index].text}*${tokens[index + 2].text}`);
      }
    });

    // Join the tokens back into a string
    return IC.parser.tokenSmartJoin(allTokens, tokens);

  },

  // replace "\symbol": \alpha => α
  processSymbols: function (str) {
    if (!str || !str.includes("\\")) {
      return str;
    }

    let processed = str;
    let foundSymbol = false;
    processed = str.replace(/[\\]+(\S+)\s?/gi, function (s, word) {
      // escape sequence: \\
      if (s.match(/[\\][\\]/)) {
        return s;
      }

      let sym = CONFIG.SYMBOLS[word];

      // single letter needs space
      if (sym) {
        foundSymbol = true;
        if (s.endsWith(" ")) {
          return sym;
        }
        IC.log(`press space to replace with ${sym}`);
      }

      // IC.log(`symbol \\${word} not defined`);
      return s;
    });

    // no replacement, but has \
    if (!foundSymbol && str == processed) {
      IC.log(`type symbol shortcut like \\pi (π) \\2 (²) \\Delta (Δ)...`);
    }

    return processed;
  },

  // invest 500/month for 10 years at 5% => invest(500/month, 10, 5)
  processPlainEnglishFunctions: function (input) {
    if (!input.includes("%")) {
      return input;
    }

    const config = {
      functionKeywords: {
        interest: ['total interest', 'interest'],
        totalpayment: ['total payment', 'total repayment', 'total paid', 'total cost', 'total mortgage cost', 'full cost', 'cost'],
        loan: ['loan', 'mortgage', 'pay', 'payment', 'repayment', 'borrow', 'finance', 'lend', 'debt'],
        savingsgoal: ['monthly savings', 'savings', 'save monthly', 'save up', 'save to reach', 'save to have'],
        invest: ['invest', 'invested', 'save', 'saved', 'deposit', 'future value'],
        presentvalue: ['present value', 'current value'],
      },

      extraStopWords: ['total'],
      percentageRegex: /\d+\s*%/,
      numberRegex: /^-?[\d.]+/,
      stopWords: CONFIG.FN_REMOVE_STOP_WORDS
    };

    function matchesKeywords(input) {
      const keywords = Object.values(config.functionKeywords).flat();
      const regex = new RegExp(`\\b(${keywords.join('|')})\\b`, 'i');
      return input.match(regex);
    }

    function removeNonUnitWords(input) {
      const words = input.split(/\s+/);

      // find all words ([a-z]+) and test them to be a predefinedUnit
      let filtered = words.filter(word => {
        if (word.match(/^[a-zA-Z]+$/) && !IC.predefinedUnit(word)) {
          return "";
        }
        return word;
      });

      return filtered.join(' ');
    }

    function removeFunctionKeywords(input) {
      const keywords = Object.values(config.functionKeywords).flat();
      let removeList = [...keywords, ...config.extraStopWords];
      const regex = new RegExp(`\\b(${removeList.join('|')})\\b`, 'gi');
      return input.replace(regex, '');
    }

    function isExistingFunctionCall(input) {
      const matches = input.match(/(\w+)[(]/);
      return matches && IC.predefinedFunction(matches[1]);
    }

    function turnIntoFunction(input) {
      const tokens = input.split(/\s+/);

      // Get the keys in the correct order
      const keys = Object.keys(config.functionKeywords);

      for (const token of tokens) {
        for (const fnName of keys) {
          const keywords = config.functionKeywords[fnName];
          for (const keyword of keywords) {
            if (keyword === token.toLowerCase() || input.toLowerCase().startsWith(keyword.toLowerCase())) {
              input = input.replace(new RegExp(`\\b${keyword}\\b`, 'gi'), '');
              input = config.stopWords(input);
              input = removeFunctionKeywords(input);
              input = removeNonUnitWords(input);
              return `${fnName} ${input}`;
            }
          }
        }
      }

      return input;
    }

    function separateParameters(str) {
      let words = str.split(/\s+/);
      const functionName = words[0];
      let params = [];
      let currentParam = [];
      let inCurrencyAmount = false;

      // for simplicity, remove currencies from the list of words
      words = words.filter(w => {
        let possibleCurrency = w.replace(/[(_)]/g, "");
        if (IC.stringIsCurrencyUnit(possibleCurrency)) {
          return false;
        }
        return true;
      })

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const nextWord = words[i + 1];

        if (config.numberRegex.test(word) || word.endsWith('%')) {
          if (currentParam.length > 0) {
            params.push(currentParam.join(' '));
            currentParam = [];
          }
        }

        currentParam.push(word);

        if (i === words.length - 1 ||
          (nextWord && (config.numberRegex.test(nextWord) || nextWord.endsWith('%')))) {
          params.push(currentParam.join(' '));
          currentParam = [];
        }
      }

      return { functionName, params };
    }

    function processInput(input) {
      let cleaned = config.stopWords(input);
      let turnedIntoFunction = turnIntoFunction(cleaned);
      let { functionName, params } = separateParameters(turnedIntoFunction);
      if (Object.keys(config.functionKeywords).includes(functionName) && params.length > 1) {
        return `${functionName}(${params.join(', ')})`;
      }
      return input;
    }

    if (!matchesKeywords(input) || isExistingFunctionCall(input)) {
      return input;
    }

    // Split the input by ';' first, then by '=' and process each part
    let processed = input.split(';').map(statement => {
      let parts = statement.split('=').map(part => part.trim());
      let processed = parts.map(processInput);
      return processed.join(' = ');
    }).join('; ');

    return processed;
  },

  processPlainEnglishNumbers: function (input) {

    // returns a list of tokens (ID vs non)
    function getIDTokensSimple(str) {
      return str.split(/([a-zA-Z0-9 _\.\,]+)/);
    }

    let tokens = getIDTokensSimple(input);

    // see which items can be parsed as written numbers
    let replacements = tokens.map(t => {
      if (!t || IC.envIsVariableDefined(t)) {
        return t;
      }

      let key = "plainEnglishNumber:" + t;
      let cachedValue = IC.CACHE.get(key);
      if (!cachedValue) {
        cachedValue = t;

        let parseResult = IC.parseWrittenNumber(t);
        if (parseResult != null) {
          let typeStr = IC.hasType(parseResult) ? " " + IC.typestring(parseResult.t) : "";
          cachedValue = IC.val(parseResult).toString() + typeStr;
        }
        IC.CACHE.set(key, cachedValue);
      }
      return cachedValue;
    });

    // not real tokens, simple join
    let processed = replacements.join('');
    return processed;
  },

  processPlainEnglishOp: function (input, newIDs = {}, env = {}) {
    // replace all items, since we're keeping track of the cursor
    const newIDsSet = new Set(Object.keys(newIDs));
    IC._plainEnglishOpRegexCache ||= {};

    const matches = CONFIG.PLAIN_ENGLISH_OPS_TRIE.search(input.toLowerCase());

    // order matches by length
    matches.sort((a, b) => b.length - a.length);

    matches.forEach((key) => {
      if (newIDsSet.has(key) || IC.envIsDefined(key)) {
        return;
      }

      if (!IC._plainEnglishOpRegexCache[key]) {
        IC._plainEnglishOpRegexCache[key] = new RegExp(`(^|\\s|\\b)(${key})(\\s+|[(])`, "gi");
      }

      const regex = IC._plainEnglishOpRegexCache[key];
      const match = input.match(regex);

      // also, make sure it's not a paren expression: (plus) or part of a variable _
      if (match && input[match.index + 1] !== ")") {
        input = input.replace(regex, (match, prefix, op, suffix) => {
          // Ensure it's not part of a variable name or a parenthesized expression
          if (prefix.endsWith('_') || (prefix.endsWith('(') && suffix === ')')) {
            return match;
          }

          // if we have a (, and it's a real function, don't replace 
          if (suffix === '(' && IC.predefinedFunction(op)) {
            return match;
          }

          return `${prefix}${CONFIG.PLAIN_ENGLISH_OPS[key]}${suffix}`;
        });

        // input = input.replace(regex, " " + CONFIG.PLAIN_ENGLISH_OPS[key] + " ");
      }
    });

    return input;
  },

  formatTypestring(result) {

    // hide type for certain formats
    if (IC.hasNoType(result) || IC.format(result) == "tape" || IC.format(result)?.startsWith("time_")) {
      return "";
    }

    // map negative powers in result to singular
    let newTypes = Object.fromEntries(
      Object.entries(result.t).map(([key, val]) => {
        // negative power
        if (val < 0 && IC.stringIsValidSingularUnit(key)) {
          // words like brass: brass => brasses => brass

          let baseName = IC.stringSingularize(key);
          return [baseName, val];
        }

        return [key, val];
      })
    );

    let typestring = IC.typestring(newTypes, true);

    // convert 3-digit currency code to uppercase
    if (typestring.length == 3 && LIB.InstaCalc.Unit?.currencies[typestring.toLowerCase()]) {
      typestring = typestring.toUpperCase();
    }

    // single unit: 1/s should be "per s"
    if (typestring.includes("1/")) {
      typestring = typestring.replace("1/", "/ ").trim();

      // 1 / kg s should just be / kg s
      if (!Object.keys(newTypes).every(IC.predefinedUnit)) {

        // make custom type singular
        let unitName = typestring.split("/")[1];
        // / eggs => "per egg" but "/ 2 weeks" stays as "per 2 weeks"
        if (!IC.predefinedUnit(unitName) && IC.stringIsValidSingularUnit(unitName)) {
          let baseName = IC.stringSingularize(unitName);
          typestring = typestring.replace(unitName, baseName);
        }
      }
    }

    typestring = IC.parser.typestringPluralize(typestring);
    typestring = IC.parser.typestringPrettyPrint(typestring);
    typestring = IC.parser.unescapeUnicode(typestring);

    return typestring;
  },

  typestringPluralize(typestring = "") {
    let typeObj = ICUnit.getUnit(typestring);

    // "dime" => dimes but not USD => USDs
    if (typeObj?.name == "currency" && typestring.length > 3) {
      return IC.stringPluralize(typestring);
    }

    let plural = IC.stringPluralize(typestring);

    if (typeObj?.name == "time" && typestring.length >= 3 && ICUnit.getUnit(plural) === typeObj) {
      // units larger than 1 hour get pluralized
      return ICUnit.getUnitScale(typestring) > 3600 ? plural : typestring;
    }

    return typestring;
  },

  typestringPrettyPrint(typestring) {
    let replacement = typestring.replace(/\^([2-9])\b/g, (match, exponent) => {
      const superscripts = {
        '2': '²',
        '3': '³',
        '4': '⁴',
        '5': '⁵',
        '6': '⁶',
        '7': '⁷',
        '8': '⁸',
        '9': '⁹',
      };
      return superscripts[exponent] || match;
    });
    return replacement;
  },

  formatNumber: function (n, format) {
    if (typeof n !== "number" || !format) {
      return n;
    }

    // allow local rounding to override default rounding
    if (format == "round2") {
      let locale = IC.envGetSettings().locale || "en-US";
      let nf = new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      });
      let displayString = nf.format(n);
      return displayString;
    }

    return n;
  },

  formatAsTime(result, format) {
    return ICTime.buildTimeString(IC.val(result), format);
  },

  formatAsTapeMeasure(obj, silent = false) {
    IC.logPause();
    let totalInches = IC.val(IC.ICObjectConvertToType(obj, "inches")).toPrecision(10);  // 13.5 inches
    IC.logResume();

    /*
    let feetQty = Math.floor(totalInches / 12); // 1 foot
    let inchesRemainder = totalInches - 12 * feetQty; // 1.5 inches left
    let inchesQty = Math.floor(inchesRemainder);
    let inchFraction = inchesRemainder - inchesQty;
    */

    if (isNaN(totalInches)) {
      // Handle cases where conversion might fail or result is NaN
      if (!silent) IC.log('warning: tape measure conversion resulted in NaN');
      return String(obj.v); // Or return a default error string
    }

    const isNegative = totalInches < 0;
    const absTotalInches = Math.abs(totalInches); // Work with the absolute value

    // Now calculations are done on the positive absolute value
    let feetQty = Math.floor(absTotalInches / 12);       // e.g., 3.937 / 12 = 0.328 => 0 feet
    let inchesRemainder = absTotalInches - 12 * feetQty; // e.g., 3.937 - 0 = 3.937 inches left
    let inchesQty = Math.floor(inchesRemainder);         // e.g., floor(3.937) => 3 inches
    let inchFraction = inchesRemainder - inchesQty;      // e.g., 3.937 - 3 = 0.937 fraction

    const epsilon = 1e-9; // Small threshold for floating point comparisons
    let [n, d] = IC.mathToFraction(inchFraction);

    // bit twiddling: https://stackoverflow.com/questions/30924280/what-is-the-best-way-to-determine-if-a-given-number-is-a-power-of-two 
    function isPowerOf2(x) {
      return ((x != 0) && !(x & (x - 1)));
    }

    // Compute nearest lower power of 2 for n in [1, 2**31-1]:
    function nearestPowerOf2(n) {
      return 1 << 31 - Math.clz32(n);
    }

    // not power of 2...
    if (!isPowerOf2(d)) {
      let denom = nearestPowerOf2(d);
      denom *= 2;

      if (denom > 64) {
        denom = 64;
      }

      [n, d] = IC.mathToFraction(inchFraction, denom);
      [n, d] = IC.mathReduceFraction(n, d);

      if (!silent) {
        if (n == 0) {
          IC.log(`small decimal: treated as zero`);
        } else if (Math.abs((n / d) - inchFraction) < 1e-6) {
          IC.log(`inexact fraction: 1/1000 inch (mil)`);
        }
      }
    }

    let str = "";
    if (feetQty) {
      str += `${feetQty}' `
    }
    if (inchesQty) {
      str += `${inchesQty}`;
    }

    if (inchFraction) {
      str += ` FRACTION{${n},${d}}`;
    }
    if (inchesQty || inchFraction) {
      str += `"`;
    }

    if (str == "" && inchesQty == 0 && feetQty == 0) {
      str = `0"`;
    }

    if (isNegative && absTotalInches >= epsilon) {
      str = "-" + str.trim();
    } else {
      str = str.trim();
    }

    return str.trim();
  },

  formatAsScientific(val) {
    // return Number(val).toExponential().replace("e+", "e");
    const num = Number(val);
    const exp = num.toExponential().replace("e+", "e");
    return exp.includes('.') && exp.split('.')[1].split('e')[0].length > 8
      ? num.toExponential(8).replace("e+", "e")
      : exp;
  },

  formatAsEngineering(val) {
    if (val === 0) return "0";
    let magnitude = Math.floor(Math.log10(Math.abs(val)) / 3) * 3;
    let mantissa = val / Math.pow(10, magnitude);
    // Use toFixed(9) to get up to 9 decimal points and parseFloat to remove trailing zeros
    return parseFloat(mantissa.toFixed(9)) + 'e' + magnitude;
  },

  isScaleableResult(result) {
    if (!result || IC.hasNoType(result) || Object.keys(result.t).length > 1) {
      return false;
    }

    const typestring = IC.typestring(result.t);
    let val = IC.val(result);

    // SI units: existing logic with special limits
    if (ICUnit.isSIUnit(typestring)) {
      const inNumberRange = (Math.abs(val) >= 1e3 || Math.abs(val) <= 1e-3);
      // above the limit for scaling
      const limits = {
        s: 1.0,
      };

      if (IC.typestringIsSameUnitCategory("s", typestring)) {
        val = IC.val(IC.ICObjectConvertToType(result, "s"));
      }

      if (limits[typestring] && Math.abs(val) >= limits[typestring]) {
        return false;
      }

      return inNumberRange;
    }

    // Non-SI units (cycles, etc.): show hint for large values >= 1000
    return Math.abs(val) >= 1e3;
  },

  formatAsScaledUnit(result) {
    const typestring = IC.typestring(result.t);

    // Bytes: scale to KB/MB/GB/TB etc.
    if (IC.typeIsByte(result.t)) {
      let scaled = ICUnit.scaleBytesToBestUnit(result);
      let number = IC.parser.toFixedIfNecessary(IC.val(scaled), 2);
      return `${number} ${IC.typestring(scaled.t)}`;
    }

    // SI units that can be properly scaled
    if (ICUnit.isSIUnit(typestring)) {
      let scaled = ICUnit.scaleToBestUnit(result);
      // Only use SI scaling if it actually changed the unit
      if (IC.typestring(scaled.t) !== typestring) {
        let number = IC.parser.toFixedIfNecessary(IC.val(scaled), 2);
        return `${number} ${IC.typestring(scaled.t)}`;
      }
    }

    // Non-SI units (cycles, etc.): use finance formatting (K, M, B, T)
    let financeFormatted = IC.parser.formatAsFinance(IC.val(result));
    return `${financeFormatted} ${typestring}`;
  },

  formatAsFinance(val) {
    if (typeof val !== "number") {
      return val;
    }

    const scales = new Map([
      [1.0e+12, 'T'],
      [1.0e+9, 'B'],
      [1.0e+6, 'M'],
      [1.0e+3, 'K']
    ]);

    for (let [scale, suffix] of scales) {
      if (Math.abs(val) >= scale) {
        let scaled = IC.parser.toFixedIfNecessary(val / scale, 1);
        return scaled + suffix;
      }
    }

    return val.toFixed(2);
  },

  formatAsRomanNumeral(val) {
    const n = Math.round(Number(val));
    if (isNaN(n) || n < 1 || n > 3999999 /* Vinculum */) {
      IC.log(`no Roman numeral for: ${val}`);
      return null;
    }
    return ICLib.decimalToRoman(n);
  },

  formatAsWattHour(result) {
    let msg = "";

    // display kWh 
    let joule = IC.val(result);
    let Wh = joule / 3600;
    let kWh = Wh / 1000;
    let MWh = kWh / 1000;
    let GWh = MWh / 1000;
    let TWh = GWh / 1000;

    // if small, display as Wh
    if (Math.abs(Wh) < 1000) {
      msg = IC.parser.toFixedIfNecessary(Wh, 4) + " Wh";
    }
    else if (Math.abs(kWh) < 1000) {
      msg = IC.parser.toFixedIfNecessary(kWh, 4) + " kWh";
    } else if (Math.abs(MWh) < 1000) {
      msg = IC.parser.toFixedIfNecessary(MWh, 4) + " MWh";
    } else if (Math.abs(GWh) < 1000) {
      msg = IC.parser.toFixedIfNecessary(GWh, 4) + " GWh";
    } else {
      msg = IC.parser.toFixedIfNecessary(TWh, 4) + " TWh";
    }

    return msg;
  },

  formatResultAsSingleLine(result) {
    // Auto-scale when result is exactly "bytes" (base unit) to a better unit like kb, mb, gb
    if (IC.typestring(result?.t) === "bytes" && Math.abs(IC.val(result)) >= 1024 && IC.format(result) !== "bytes_raw") {
      result = ICUnit.scaleBytesToBestUnit(result);
    }

    let formatted = IC.parser.formatResult(result);
    let typestring = IC.typestring(result?.t);
    return formatted + (typestring ? " " + typestring : "");
  },

  formatResult(result, settings = {}, rowFormat) {
    let val = IC.val(result);
    let format = IC.format(result);

    // no format required for complex numbers
    if (format == "phasor") {
      // it has been wrapped
      return LIB.Complex.toPhasorString(IC.val(result));
    }

    if (LIB.Complex.isComplex(result) && !format) {
      return LIB.Complex.toString(result);
    }

    if (!format || !IC.isValidEvalResult(result)) {
      return val;
    }

    if (format === "percent") {
      val = Number(val);
      if (Math.abs(val) < 1e-6) {
        return val.toLocaleString(settings.locale || 'en-US', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 14 });
      } else {
        return val.toLocaleString(settings.locale || 'en-US', { style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: 4 });
      }
    }

    if (format === "hex" || format === "oct" || format === "bin") {
      // @ts-ignore
      return IC.CALC.fn[format](val);
    }

    if (format === "tape") {
      if (IC.typestringIsSameUnitCategory("ft", IC.typestring(result.t))) {
        return IC.parser.formatAsTapeMeasure(result);
      } else {
        // not length unit; clear tape format
        IC.ICObjectSetFormat(result, "");
      }
    }

    if (format.startsWith("datetime_")) {
      return ICDate.toString(result);
    }

    if (format.startsWith("time_")) {
      if (IC.typestringIsSameUnitCategory("s", IC.typestring(result.t))) {
        return IC.parser.formatAsTime(result, format);
      } else {
        // not time unit; clear time format
        IC.ICObjectSetFormat(result, "");
      }
    }

    if (format === "scientific") {
      return IC.parser.formatAsScientific(val);
    }

    if (format === "engineering") {
      return IC.parser.formatAsEngineering(val);
    }

    if (format === "finance") {
      // for currency formatting
      let currencyPrefix = rowFormat?.symbol ?? "";
      let typestring = IC.typestring(result?.t);

      // $50M USD => $50M (no USD)
      if (result?.t) {
        Object.keys(result.t).forEach((key) => {
          if (IC.typeIsCurrency(key)) {
            // delete this key if we already have the currency?
            delete result.t[key];
          }
        });
      }

      return currencyPrefix + IC.parser.formatAsFinance(val);
    }

    if (format === "roman") {
      return IC.parser.formatAsRomanNumeral(val);
    }

    return val;
  },

  getOutputMessage(ret, result, input) {
    let msg = "";
    let processed = ret.processed;

    let isNumber = typeof ret.eval_result === "number" && !isNaN(ret.eval_result);

    let converted = false;
    let showFraction = true;

    if (LIB.Complex.isComplex(ret.eval_result)) {
      return "as phasor: " + LIB.Complex.toPhasorString(ret.eval_result);
    }

    // have empty message if we have a custom object
    if (IC.isCustomObject(result)) {
      return "";
    }

    if (processed.trim() != input.trim()) {
      msg += processed;
    }

    let val = IC.val(result);
    if (!IC.isValidEvalResult(val)) {
      return msg;
    }

    let typestring = IC.typestring(result?.t);
    let format = IC.format(result);
    if (format) {
      showFraction = false;
      if (format == "hex" || format == "bin" || format == "oct") {
        msg = " as decimal: " + IC.val(result);
        converted = true;
      }

      if (format == "bin") {
        // @ts-ignore
        msg += " | as hex: " + IC.CALC.fn.hex(IC.val(result));
      }

      if (format == "hex") {
        // @ts-ignore
        msg += " | as binary: " + IC.CALC.fn.bin(IC.val(result));
      }
    }

    // joule check. TODO: add energy unit check
    if (isNumber && format != "tape" && typestring.match(/(j)/i) && IC.typestringIsSameUnitCategory("joule", typestring.toLowerCase())) {
      msg += " = " + IC.parser.formatAsWattHour(result);
    }

    if (IC.parser.isScaleableResult(result)) {
      msg += " | as scaled: " + IC.parser.formatAsScaledUnit(result);
    }

    if (typeof ret.final_result === "string" && isNumber) {
      let typestringRaw = "";
      if (IC.hasType(result)) {
        typestringRaw = " " + typestring;
      }

      let retNumber = IC.makeNumber(ret.final_result, IC.envGetSettings());
      let isDifferentNumber = ret.final_result.match(/[a-z]/i) || (retNumber && Math.abs(ret.eval_result - retNumber) > 1e-10);

      // TODO: what about rounding and precision?
      if (!converted && isDifferentNumber) {
        msg += " = " + IC.parser.toFixedIfNecessary(ret.eval_result, 8) + typestringRaw;
      }

      // .125 feet/dollar gets shown as 8 dollars/foot
      if (val !== 0 && Math.abs(val) < 1 && typestringRaw.includes("/")) {
        let inverse = IC.mathInverse(result);
        let inverseVal = IC.val(inverse);
        let inverseTypeString = IC.parser.formatTypestring(inverse);
        msg += " = " + IC.parser.toFixedIfNecessary(inverseVal, 2) + " " + inverseTypeString;
      }
    }

    // tape measure check
    if (isNumber && format != "tape" && IC.hasType(result) && (IC.typestringIsSameUnitAndScale(IC.typestring(result.t), "inch")
      || IC.typestringIsSameUnitAndScale(IC.typestring(result.t), "foot"))) {
      let tapeMeasure = IC.parser.formatAsTapeMeasure(result, true);
      let simpleTapeMeasure = tapeMeasure.replace(/FRACTION{(\d+)[,]\s*(\d+)}/, (match, n, d) => {
        return `${n}/${d}`;
      });

      // 0/1" is not something to return
      if (!simpleTapeMeasure.includes(" 0/")) {
        msg += " | as tape measure: " + simpleTapeMeasure;
      }
    }

    // fraction check
    if (showFraction && isNumber) {
      let fracMatch = input.match(/\d[/]\d/gi);
      if (fracMatch && fracMatch.length > 1) {
        msg += " | as fraction: " + CONFIG.POSTCONVERT_FUNCTIONS.fraction(ret.eval_result);
      }
    }

    // time check
    if (isNumber && IC.hasType(result) && IC.typestringIsSameUnitCategory(IC.typestring(result.t), "second")) {
      let time = CONFIG.UNIT_AWARE_FUNCTIONS["TIME"](result);
      if (time) {
        msg += " | as time: " + time;
      }
    }

    // currency conversion to USD
    // check for stock types too (keys start with STOCK_)
    let isCurrencyOrStock = ICUnit.isCurrency(IC.typestring(result.t));
    if (!isCurrencyOrStock && result?.t) {
      isCurrencyOrStock = Object.keys(result.t).some(k => k.startsWith("STOCK_"));
    }

    if (isNumber && IC.hasType(result) && isCurrencyOrStock && !processed.includes("->")) {
      const currencies = ['USD', 'EUR'];
      const rateDate = ICUnit.formatCurrencyRateDate();
      const dateHint = rateDate ? ` (${rateDate})` : '';
      currencies.forEach(currency => {
        const conversionResult = IC.semanticConvert(result, currency);
        if (IC.isValidEvalResult(conversionResult) && currency != IC.typestring(result.t).toUpperCase()) {
          msg += ` | in ${currency} = ${ICLib.roundToDecimal(IC.val(conversionResult), 2)}${dateHint}`;
        }
      });
    }

    // Add OXR attribution for explicit currency conversions
    // Detect by checking for multiple currency units in log AND arrow in processed output
    const currentLog = IC.logGetCurrentRow() || [];
    const currencyUnits = new Set();
    currentLog.forEach(entry => {
      if (entry?.type === 'unit' && entry?.category === 'currency') {
        currencyUnits.add(entry.name?.toUpperCase());
      }
    });
    // Two different currencies + arrow in output = explicit currency conversion
    const hasCurrencyConversion = currencyUnits.size >= 2 && processed.includes('->');

    if (hasCurrencyConversion && IC.typeIsCurrency(result?.t)) {
      const rateDate = ICUnit.formatCurrencyRateDate();
      if (rateDate) {
        msg += ` | via OXR · ${rateDate}`;
      }
    }

    // remove leading " | "
    msg = msg.replace(/^\s*\|\s*/, "");

    return msg;
  },

  getResultCacheKey: function (input, env, settings, rowIndex = null) {
    // Use explicitly passed row index, or fall back to current row index
    const actualRowIndex = rowIndex !== null ? rowIndex : IC.envGetRowIndex();
    return JSON.stringify({ row: actualRowIndex, input: input, env: env, unit: LIB.InstaCalc.Unit._updated, settings: settings });
  },

  getResultCache: function ({ input, startEnv, settings, rowIndex = null }) {
    IC.parser.RESULT_CACHE ||= new LRUCache(10000);

    function hasMissingReferences(references) {
      return references.some((x) => !IC.envIsDefined(x));
    }

    // breaks the cache
    function isVolatile(input) {
      return input.match(/\brand|\bnow\b/i);
    }

    // check cache
    let quickKey = IC.parser.getResultCacheKey(input, startEnv, settings, rowIndex);
    let quickVal = IC.parser.RESULT_CACHE.get(quickKey);
    if (quickVal && !isVolatile(input) && !hasMissingReferences(quickVal.result.deps?.missing)) // using r13 when it's not defined yet; use normal process
    {
      IC.envSet({ ...startEnv, ...quickVal.changedEnv });
      return quickVal.result;
    }

    return null;
  },

  setResultCache: function ({ input, startEnv, settings, ret, rowIndex = null }) {
    let changedEnv = {};
    Object.entries(IC.envGet()).forEach(([key, value]) => {
      // only store helpful changes, where variables get defined
      if (JSON.stringify(value) != JSON.stringify(startEnv[key]) && value != null) {
        changedEnv[key] = value;
      }
    });

    IC.parser.RESULT_CACHE.set(IC.parser.getResultCacheKey(input, startEnv, settings, rowIndex), {
      result: ret,
      changedEnv: changedEnv,
      references: ret.references,
    });
  },

  /*
    Some grammar decisions are best handled in code (sensitive to neighboring tokens, variables defined in environment, etc.)
    */
  phasePreprocess: function ({ input, i, settings, startEnv }) {
    // --- Initial State & Logging ---
    let state = { processed: input, comment: "", title: "", format: null, strings: {}, identifiers: [], allAssignedIDs: [], tokens: [] };
    const process_debug = [["00_input", state.processed]]; // Start debug log
    let stepCounter = 0;

    // --- Inner Step Execution Function ---
    /**
     * Executes a processing step, updates state, logs, and times.
     * @param {string} description - Descriptive name for logging.
     * @param {Function} stepFunction - Receives current 'state', returns changes or new 'processed' string.
     * @param {Function | null} [resultFormatter=null] - Optional formatter for logged result.
     */
    const doStep = (description, stepFunction, resultFormatter = null) => {
      stepCounter++;
      const stepName = `${String(stepCounter).padStart(2, '0')}_${description}`;

      PerfLog.start(stepName);
      const resultChanges = stepFunction(state); // Pass current state
      PerfLog.end(stepName, process_debug);

      let logValue = resultChanges; // Default log value

      // --- Update State ---
      if (typeof resultChanges === 'string') {
        state.processed = resultChanges; // Update only 'processed'
        logValue = resultChanges;
      } else if (typeof resultChanges === 'object' && resultChanges !== null) {
        logValue = resultChanges.logValue !== undefined ? resultChanges.logValue : resultChanges;
        Object.assign(state, resultChanges); // Merge changes into state
        delete state.logValue; // Don't keep logValue in state
      }
      // If null/undefined return, assume side effects updated state where needed

      // --- Log Result ---
      const formattedLogValue = resultFormatter ? resultFormatter(logValue) : logValue;
      process_debug.push([stepName, formattedLogValue]);
    };

    // --- Define and Run Pipeline Steps ---
    PerfLog.start("phasePreprocess"); // Time the overall phase

    // Process Comment
    doStep('processComment',
      (s) => IC.parser.processComment(s.processed), // Operate on state.processed
      (result) => {
        // This step needs to update multiple state parts and env
        state.processed = result.processed;
        state.comment = result.comment;
        IC.envSetRowComment(result.comment);
        return result.processed; // Format log to show only processed string
      }
    );

    // L1 Regex
    doStep('processRegexReplacements(L1)', (s) => IC.parser.processRegexReplacements(s.processed, CONFIG.L1_REPLACEMENTS_REGEX));

    // Token-based $TICKER processing (replaces regex-based approach for better context-sensitivity)
    doStep('processTickerSyntaxTokenized', (s) => IC.parser.processTickerSyntaxTokenized(s.processed));

    // Currency & Strings
    doStep('processCurrencySymbolsAndStrings',
      (s) => IC.parser.processCurrencySymbolsAndStrings(s.processed),
      (result) => {
        // Update multiple state parts and env
        state.processed = result.processed;
        state.format = result.format;
        state.strings = result.strings;
        IC.envSetFormatter(result.format);
        IC.envSetStrings(result.strings);
        return result.processed; // Format log
      }
    );

    // Simple String Transformations
    doStep('processSymbols', (s) => IC.parser.processSymbols(s.processed));
    doStep('processRegexReplacements(L2)', (s) => IC.parser.processRegexReplacements(s.processed, CONFIG.L2_REPLACEMENTS_STRINGS));
    doStep('processXToMultiply_1', (s) => IC.parser.processXToMultiply(s.processed));
    doStep('removeInvisibleUnicode', (s) => IC.parser.sanitizeUnicodeString(s.processed));
    doStep('escapeUnicode', (s) => IC.parser.escapeUnicode(s.processed));

    // Scan Identifiers & Title
    doStep('scanAssignedIdentifiers',
      (s) => IC.parser.scanAssignedIdentifiersUsingLex(s.processed),
      (ids) => {
        // Update multiple state parts and env
        state.allAssignedIDs = ids;
        if (ids.length) {
          IC.envSetRowTitle(IC.parser.unescapeUnicode(ids[0].orig));
          state.title = IC.envGetRowTitle();
          process_debug.push(["09a_extractedTitle", state.title]); // Manual extra log
        }
        ids.forEach((id) => IC.envSetIdentifier(id.name));
        state.identifiers = IC.envGetIdentifiers();
        return `IDs: ${ids.map(id => id.name).join(', ')}`; // Format log
      }
    );

    //  More Transformations (using state.identifiers etc. via closure)
    doStep('processRegexReplacements(L3)', (s) => IC.parser.processRegexReplacements(s.processed, CONFIG.L3_REPLACEMENTS_REGEX));
    doStep('processIdentifiers', (s) => IC.parser.processIdentifiers(s.processed, s.identifiers, s.allAssignedIDs, startEnv));
    doStep('processXToMultiply_2', (s) => IC.parser.processXToMultiply(s.processed, true));
    doStep('processPrefixRegex', (s) => IC.parser.processPrefixRegex(s.processed));
    doStep('processPlainEnglishFunctions', (s) => IC.parser.processPlainEnglishFunctions(s.processed));
    doStep('processPlainEnglishOp', (s) => IC.parser.processPlainEnglishOp(s.processed, s.identifiers, startEnv));
    doStep('processInAsKeyword', (s) => IC.parser.processInAsKeyword(s.processed));
    doStep('processAasKeyword', (s) => IC.parser.processAasKeyword(s.processed));
    doStep('processRegexReplacements(L4)', (s) => IC.parser.processRegexReplacements(s.processed, CONFIG.L4_REPLACEMENTS_REGEX));
    doStep('processRegexReplacements(L5)', (s) => IC.parser.processRegexReplacements(s.processed, CONFIG.L5_REPLACEMENTS_STRINGS));
    doStep('processParenUnitExpressions', (s) => IC.parser.processParenUnitExpressions(s.processed));
    doStep('processPlainEnglishNumbers', (s) => IC.parser.processPlainEnglishNumbers(s.processed));
    doStep('processFunctionWithExponent', (s) => IC.parser.processFunctionWithExponent(s.processed));
    doStep('processFinalCurrencies', (s) => IC.parser.processFinalCurrencies(s.processed));
    doStep('processUnitTypeBeforeNumber', (s) => IC.parser.processUnitTypeBeforeNumber(s.processed));
    doStep('processDateTimeExpressions', (s) => IC.parser.processDateTimeExpressions(s.processed));
    doStep('preprocessRelativeDate', (s) => ICDate.preprocessRelativeDate(s.processed));
    doStep('processChainOfAddedUnits', (s) => IC.parser.processChainOfAddedUnits(s.processed));
    doStep('processConversionAtOrder', (s) => IC.parser.processConversionAtOrder(s.processed));
    doStep('processDashUnitSeparator', (s) => IC.parser.processDashUnitSeparator(s.processed));
    doStep('envReplaceStrings', (s) => IC.envReplaceStrings(s.processed)); // Reads strings from env

    // TODO: some of these require certain characters/strings (like "in", etc.). if we scan the input and get the charmap, we can see if we should even run the processor.

    // Tokenize
    state.tokens = IC.parser.tokenize(state.processed, LEXER); // Update state.tokens directly

    PerfLog.end("phasePreprocess", process_debug); // End overall phase timing

    // --- Return Final Result Object ---
    // Construct the final object explicitly from the state
    return {
      input, i, // Pass original input and index through
      processed: state.processed,
      comment: state.comment,
      title: state.title,
      format: state.format,
      strings: state.strings,
      process_debug, // Include the debug log
      identifiers: state.identifiers,
      allAssignedIDs: state.allAssignedIDs,
      tokens: state.tokens
    };
  },

  /*
  Some grammar decisions are best handled in code (sensitive to neighboring tokens, variables defined in environment, etc.)
  */
  phasePreprocessOld: function ({ input, i, settings, startEnv }) {
    let processed, comment, title, format, strings, process_debug = [], identifiers, allAssignedIDs;

    process_debug.push(["input", input]);
    processed = input;

    ({ processed, comment } = IC.parser.processComment(processed));
    IC.envSetRowComment(comment);
    processed = IC.parser.processRegexReplacements(processed, CONFIG.L1_REPLACEMENTS_REGEX);
    processed = IC.parser.processTickerSyntaxTokenized(processed);
    process_debug.push(["comment, L1, tickerSyntax", processed]);

    ({ processed, format, strings } = IC.parser.processCurrencySymbolsAndStrings(processed));
    IC.envSetFormatter(format);
    IC.envSetStrings(strings);
    process_debug.push(["currency|strings|spaces", processed]);

    processed = IC.parser.processSymbols(processed);
    processed = IC.parser.processRegexReplacements(processed, CONFIG.L2_REPLACEMENTS_STRINGS);
    processed = IC.parser.processXToMultiply(processed); // need 2 rounds, see x=2, 3 x 3 

    processed = IC.parser.sanitizeUnicodeString(processed);
    processed = IC.parser.escapeUnicode(processed);
    //    processed = processed.replace(/\s+/, " ");      // collapse whitespace

    process_debug.push(["symbols|L2|xAsMult|unicode", processed]);

    allAssignedIDs = IC.parser.scanAssignedIdentifiersUsingLex(processed);

    // title is first ID
    if (allAssignedIDs.length) {
      IC.envSetRowTitle(IC.parser.escapeUnicode(allAssignedIDs[0].orig));
      title = IC.envGetRowTitle();
    }

    allAssignedIDs.forEach((id) => IC.envSetIdentifier(id.name));
    identifiers = IC.envGetIdentifiers();

    processed = IC.parser.processRegexReplacements(processed, CONFIG.L3_REPLACEMENTS_REGEX);
    processed = IC.parser.processIdentifiers(processed, identifiers, startEnv);
    processed = IC.parser.processXToMultiply(processed, true);
    process_debug.push(["L3|identifiers|xAsMult", processed]);

    processed = IC.parser.processPrefixRegex(processed);
    processed = IC.parser.processPlainEnglishFunctions(processed);
    processed = IC.parser.processPlainEnglishOp(processed, identifiers, startEnv);
    process_debug.push(["PrefixRegex|PlainEngFn|PlainEnglishOp", processed]);

    processed = IC.parser.processInAsKeyword(processed);
    processed = IC.parser.processAasKeyword(processed);
    processed = IC.parser.processRegexReplacements(processed, CONFIG.L4_REPLACEMENTS_REGEX);
    processed = IC.parser.processRegexReplacements(processed, CONFIG.L5_REPLACEMENTS_STRINGS);
    process_debug.push(["InAsKeyword|L4|L5", processed]);

    processed = IC.parser.processParenUnitExpressions(processed);
    process_debug.push(["ParenUnitExpressions", processed]);

    // see tests for why this comes last
    processed = IC.parser.processPlainEnglishNumbers(processed);
    processed = IC.parser.processFunctionWithExponent(processed);
    processed = IC.parser.processFinalCurrencies(processed);
    processed = IC.parser.processUnitTypeBeforeNumber(processed); // must happen after currency swap
    processed = IC.parser.processDateTimeExpressions(processed);
    processed = ICDate.preprocessRelativeDate(processed);
    processed = IC.parser.processChainOfAddedUnits(processed);
    processed = IC.parser.processConversionAtOrder(processed);
    processed = IC.parser.processDashUnitSeparator(processed);
    process_debug.push(["EnglishNum|FnWithExponent|FinalCurrencies|UnitBeforeNumber|AddedUnits|conversionAtOrder|Dash", processed]);

    processed = IC.envReplaceStrings(processed);
    process_debug.push(["ReplaceStrings", processed]);

    let tokens = IC.parser.tokenize(processed, LEXER);

    return { input, i, processed, comment, title, format, strings, process_debug, identifiers, allAssignedIDs, tokens };
  },

  phaseParse: function ({ processed }) {
    let cacheKey = "nearley" + processed;
    let parseResult = IC.parser.RESULT_CACHE.get(cacheKey);

    if (!IC.parser.RESULT_CACHE.has(cacheKey)) {
      try {
        let NEARLEY_PARSER = new LIB.nearley.Parser(GRAMMAR_FROM_COMPILED);
        NEARLEY_PARSER.feed(processed);
        parseResult = NEARLEY_PARSER.results[0];
      } catch {
        parseResult = null;
      }
      IC.parser.RESULT_CACHE.set(cacheKey, parseResult);
    }

    return { parseResult };
  },

  phaseEval: function (params = {}) {
    let { input, parseResult, allAssignedIDs, format, processed } = params;
    let result, final_result;
    if (parseResult) {
      IC.envCheckIdentifierWarnings(allAssignedIDs.map(id => id.name));
      try {
        result = IC.eval(parseResult);
      }
      catch (e) {
        result = null;
        console.error(e);
      }

      // last-ditch save... try to extra numbers
      let failedResult = !IC.isValidEvalResult(result) || IC.parser.invalidNumber(result);
      if (failedResult && !IC.predefinedFunction(processed)) {
        let simpleString = IC.parser.parseSimple(processed);
        if (simpleString) {
          let origLog = IC.logGetCurrentRow();
          IC.logClear();
          parseResult = IC.parser.phaseParse({ processed: simpleString }).parseResult;
          result = IC.eval(parseResult);
          if (result == null) {
            IC.logSetCurrentRow(origLog);
          }
        }
      }
      // TODO: put this in output message, if simple string used
      // IC.log(`converted to: ${simpleString}`);
    }

    // auto-format result when needed (i.e., not a conversion)
    // TODO: is there better logic to detect when to auto-collapse final result? 
    if (!processed.includes("->") && IC.hasType(result) /* this is needed for Infinity being cached */) {
      let collapsed = ICUnit.autoCollapse(result);
      if (ICUnit.autoCollapseIgnore(result)) {
        // log the ignored collapse
        let formattedNumber = IC.parser.applyLogNumberFormatting(IC.val(collapsed));
        let logMsg = `= ${formattedNumber} ${IC.typestring(collapsed.t)}`;
        IC.log(logMsg);
      } else {
        result = collapsed;
      }
    }

    IC.envAssignRowResult(result || 0);

    // may have been changed during execution, for example: autosolve
    format = IC.parser.scanExtraFormatting(input, result, IC.envGetFormatter());

    // If result is a plain number (not an IC object), clear datetime/time formats
    // This prevents format inheritance when functions like YEAR() return plain numbers
    if (typeof result === 'number' && typeof format === 'string' && (format.startsWith('datetime_') || format.startsWith('time_'))) {
      format = '';
      // Also wrap result as IC object with no format to prevent inheritance in formatResult
      result = IC.makeICObject(result, null, '');
    }

    final_result = format ? IC.parser.formatNumber(result, format) : result;

    return { result, format, final_result };
  },

  phasePostprocess: function (params = {}, settings = {}, timeStart) {
    let { processed, input, process_debug, allAssignedIDs, i, comment, title, format, parseResult, tokens, result, final_result } = params;
    let ret = {};

    // {total} => total
    function interpolateString(str) {
      return str.replace(/{([^}]+)}/g, (match, expr) => {
        // Run plain-English substitutions so "as" → "->" conversion works
        // (e.g. {R1 as dollars} becomes {R1 -> dollars})
        let processed = IC.parser.processPlainEnglishOp(expr);
        let result = IC.parseAndEval(processed);
        if (result != IC.NOPARSE && IC.isValidEvalResult(result)) {
          // Format ICObjects (e.g. unit values like "15 USD") to their display string
          // instead of letting JS coerce them to "[object Object]"
          if (IC.isICObject(result)) {
            return IC.parser.formatResultAsSingleLine(result);
          }
          return result;
        }

        return match;
      });
    }

    try {
      // log errors
      IC.parser.checkFailedResult(processed, result);

      // get reference list
      let { references, custom_units } = IC.parser.scanReferencesFromParseResult(params);

      ret.input = input;
      const rawComment = (comment || "").trim();
      const looksLikeUIMetadata = UIMetadataParser.looksLikeUIMetadata(rawComment);
      // If the comment looks like JSON UI metadata, do NOT run interpolation on it
      // to avoid corrupting the JSON (e.g., turning it into [object Object]).
      ret.comment = looksLikeUIMetadata
        ? IC.parser.unescapeUnicode(rawComment)
        : IC.parser.unescapeUnicode(interpolateString(comment))?.trim();
      // Parse UI metadata from the raw (non-interpolated) comment if present
      const uiParseResult = UIMetadataParser.parse(rawComment);
      ret.ui = uiParseResult.ui;
      ret.ui_comment = uiParseResult.ui_comment;
      ret.presentation = uiParseResult.presentation;  // NEW: presentation hints (hero, icon, hidden, summary, theme)

      // cleanedComment: comment with all recognized @decorators and UI functions stripped
      // Use this in Present mode instead of manual regex stripping
      // IMPORTANT: Use ?? to preserve empty string (means "decorators-only comment")
      ret.cleanedComment = uiParseResult.cleanedComment ?? null;

      // Also interpolate cleanedComment so {expr} values resolve in display.comment
      if (ret.cleanedComment != null && !looksLikeUIMetadata) {
        ret.cleanedComment = interpolateString(ret.cleanedComment);
      }

      // Interpolate @note variable values if present
      // Note parts are: [{type:'text', value:'Based on '}, {type:'variable', value:'hours'}, ...]
      // For variable parts, evaluate and add evaluatedValue
      if (ret.presentation?.note && Array.isArray(ret.presentation.note)) {
        ret.presentation.note = ret.presentation.note.map(part => {
          if (part.type === 'variable') {
            // Evaluate the variable expression using IC.parseAndEval
            const evalResult = IC.parseAndEval(`{${part.value}}`);
            if (evalResult != IC.NOPARSE && IC.isValidEvalResult(evalResult)) {
              return { ...part, evaluatedValue: String(evalResult) };
            }
          }
          return part;
        });
      }

      const formatOverrideRaw = ret.presentation?.format;
      let formattedBase = result;
      if (formatOverrideRaw && IC.isValidEvalResult(result)) {
        const normalized = String(formatOverrideRaw).trim().toLowerCase();
        const formatAlias = {
          money: 'finance',
          currency: 'finance'
        };
        const formatName = formatAlias[normalized] || normalized;
        if (IC.isICObject(result)) {
          IC.ICObjectSetFormat(result, formatName);
        } else if (typeof result === 'number') {
          formattedBase = IC.makeICObject(result, null, formatName);
        }
      }

      // Auto-scale when result is exactly "bytes" (base unit) to a better unit like kb, mb, gb
      // Only applies when: 1) type is exactly "bytes", 2) value is >= 1024, 3) not opted out with "as bytes"
      let byteWasAutoScaled = false;
      if (IC.typestring(result?.t) === "bytes" && Math.abs(IC.val(result)) >= 1024 && IC.format(result) !== "bytes_raw") {
        result = ICUnit.scaleBytesToBestUnit(result);
        formattedBase = result;
        byteWasAutoScaled = true;
      }

      ret.title = IC.parser.unescapeUnicode(title)?.trim();
      ret.eval_result = IC.parser.unescapeUnicode(IC.val(result));
      if (!formatOverrideRaw && !byteWasAutoScaled) {
        formattedBase = final_result;
      }
      ret.final_result = IC.parser.unescapeUnicode(IC.parser.formatResult(formattedBase, settings, format));
      ret.format = format;
      ret.result = result;

      if (IC.isValidEvalResult(ret.eval_result) && IC.isValidEvalResult(result)) {
        ret.type = IC.parser.formatTypestring(result);
      }

      // must happen before log
      // TODO - this seems to interfere with caching if we return ret directly
      IC.parser.applyNumberFormatting(ret, settings);

      // Interpolate {expr} in string literal results (e.g. "The sun is up {sun.avg} of the time")
      // Only for actual string literals (where raw result is a string), not formatted numbers like FRACTION{11,16}
      if (typeof result === 'string' && typeof ret.final_result === 'string' && ret.final_result.includes('{')) {
        ret.final_result = interpolateString(ret.final_result);
      }

      ret.log = IC.logProcess(IC.logGetCurrentRow(), result);
      ret.processed = IC.parser.unescapeUnicode(processed);
      ret.process_debug = process_debug;
      ret.process_time_ms = performance.now() - timeStart;
      ret.output_msg = IC.parser.getOutputMessage(ret, result, input);

      ret.missing_references = references.filter(r => !IC.envIsDefined(r) /* && !IC.isRealUnit(r)  && !IC.isAllowedFunction(r)  && !(IC.suffix(r) != 1) */ && !CONFIG.POSTCONVERT_FUNCTIONS[r]);
      ret.assigned_references = [...allAssignedIDs, `r${i + 1}`];

      // update assigned references for object and array access
      let objAndArrayReferences = ret.assigned_references.map(id => {
        let parts = IC.getVariableParts(id?.name || id);
        if (parts.length > 1) {
          return parts[0];
        }
      }).filter(Boolean);

      ret.assigned_references = [...ret.assigned_references, ...objAndArrayReferences];

      ret.is_custom_type = ret.type && Object.keys(result?.t).find((t) => !IC.predefinedUnit(t));
      ret.is_custom_object = IC.isCustomObject(ret.final_result);
      ret.is_custom_function = IC.isCustomFunction(ret.final_result);
      ret.is_array = Array.isArray(result) && (result?.length > 1 || result?.length == 0);

      if (!ret.is_array && Array.isArray(result)) {
        ret.output_msg += "single-item treated as value";
        if (input.includes(",")) {
          ret.output_msg += "; separate with space: [1, 2, 3]";
        }
      }

      if (IC.parser.invalidResult(ret)) {
        let missingChar;
        const hasSymbols = tokens?.some(t => t.type == "symbol");

        if (ret?.missing_references.length && hasSymbols) {
          ret.output_msg = "undefined: " + ret.missing_references.join(", ");
        } else if (missingChar = IC.parser.hasMismatchedParensOrBraces(input)) {
          ret.output_msg = `unmatched ${missingChar}`;
        }
      }

      //ret.references = [...IC.getVarRefs()];
      ret.references = references;
      ret.custom_units = custom_units;

      ret.tokens = tokens;

      // UI write-back hints: provide explicit data for matching and write-back
      if (ret.ui) {
        const assignedRaw = IC.parser.extractAssignedSnippet(input);

        ret.ui_write = {
          value_raw: assignedRaw,           // Raw RHS: "10 mm", "0.5", "cap"
          value_eval: ret.eval_result,      // Evaluated: 10<mm>, 0.5, 0.8
        };

        const uiType = String(ret.ui.ui).toLowerCase();

        // For sliders, provide evaluated bounds as numbers
        if (uiType === 'slider' || uiType === 'range') {
          ret.ui_write.min_eval = (typeof ret.ui.min === 'number') ? ret.ui.min : Number(ret.ui.min) || 0;
          ret.ui_write.max_eval = (typeof ret.ui.max === 'number') ? ret.ui.max : Number(ret.ui.max) || 100;
          ret.ui_write.step_eval = (typeof ret.ui.step === 'number') ? ret.ui.step : Number(ret.ui.step) || 1;
        }

        // For checkboxes, provide boolean state
        if (uiType === 'checkbox') {
          let checked = false;
          if (typeof ret.eval_result === 'boolean') {
            checked = ret.eval_result;
          } else if (typeof ret.eval_result === 'string') {
            const s = ret.eval_result.toLowerCase();
            checked = s === 'true' || s === '1';
          }
          ret.ui_write.checkbox_checked = checked;
        }

        // For multiselect, provide parsed array of selected values
        if (uiType === 'multiselect' || uiType === 'multi' || uiType === 'select-multiple') {
          let selected = [];
          if (Array.isArray(ret.eval_result)) {
            selected = ret.eval_result.filter(item =>
              typeof item === 'number' || typeof item === 'boolean' || typeof item === 'string'
            );
          }
          ret.ui_write.multiselect_selected = selected;
        }
      }

      // UI helpers: provide canonical display values for date/time controls so UI can be dumb
      if (ret.ui && typeof ret.ui.ui === 'string') {
        const uiType = String(ret.ui.ui).toLowerCase();
        if (uiType === 'date' || uiType === 'time' || uiType === 'datetime' || uiType === 'datetime-local') {
          let currentRaw = '';

          // If the evaluated result is a date object (e.g., from date("2025-10-10")), extract the ISO string
          if (ICDate.isDateTimeObject(ret.eval_result)) {
            currentRaw = IC.val(ret.eval_result); // Get ISO string like "2025-10-10T00:00:00.000Z"
          } else {
            // Fall back to raw assigned text (for legacy quoted strings)
            const assignedRaw = IC.parser.extractAssignedSnippet(input);
            currentRaw = assignedRaw;
            if ((!currentRaw || currentRaw.length === 0) && typeof ret.eval_result === 'string') {
              currentRaw = ret.eval_result;
            }
          }

          const htmlType = (uiType === 'datetime') ? 'datetime-local' : uiType;
          ret.ui_display_value = IC.parser.canonicalizeDateInput(htmlType, currentRaw || '');
          ret.ui_input_type = htmlType;
        }
      }
    }
    catch (e) {
      console.error(e);
      ret = IC.parser.getBlankReturnObject();
    }

    // Finalize result: create clean grouped structure and remove working fields
    IC.parser.finalizeResult(ret);

    return ret;
  },

  hasMismatchedParensOrBraces: function (str) {
    const stack = [];
    const openBraces = { '{': '}', '(': ')' };
    const closeBraces = { '}': '{', ')': '(' };

    for (let char of str) {
      if (openBraces[char]) {
        stack.push(char);
      } else if (closeBraces[char]) {
        if (stack.length === 0 || stack.pop() !== closeBraces[char]) {
          return char;
        }
      }
    }

    if (stack.length > 0) {
      return stack.pop();
    }

    return null;
  },

  getInputTooLongResult: function () {
    // Return v2 format result for "input too long" error
    return {
      _meta: { v: 2 },
      input: { original: "", processed: "" },
      output: { raw: null, value: null, display: "" },
      type: { units: "", format: "", isArray: false, customUnit: null },
      comment: { original: "", cleaned: null },
      display: { label: null, comment: null },
      deps: { missing: [], defines: [], references: [], customUnits: [], tokens: [] },
      debug: { message: "Input too long (1000 max per row)", log: [], trace: [], timeMs: 0 },
      ui: null,
      presentation: null,
      status: "error"
    };
  },

  getBlankReturnObject: function () {
    return {
      // === Working fields (used internally during computation, removed by finalizeResult) ===
      // These use legacy names internally for minimal code changes, then get cleaned up
      comment: "",
      title: "",
      input: "",
      eval_result: "",
      final_result: "",
      result: "",
      type: "",
      log: [],
      processed: "",
      process_debug: [],
      process_time_ms: null,
      output_msg: "",
      missing_references: [],
      assigned_references: [],
      references: [],
      custom_units: []
    }
  },

  /**
   * Finalize result object by creating clean grouped structure and removing working fields.
   * This is called at the end of execute() to transform internal working fields into
   * the final clean API.
   * @param {object} ret - The result object with working fields populated
   * @returns {object} Clean result object with grouped structure
   */
  finalizeResult: function (ret) {
    // === Build clean grouped structure ===

    // Meta
    ret._meta = { v: 2 };

    // Input group
    const inputOriginal = ret.input || "";
    const inputProcessed = ret.processed || "";
    ret.input = {
      original: inputOriginal,
      processed: inputProcessed
    };

    // Output group
    ret.output = {
      raw: ret.result,
      value: ret.eval_result,
      display: ret.final_result || ""
    };

    // Type group
    const typeUnits = ret.type || "";
    ret.type = {
      units: typeUnits,
      format: ret.format || "",
      isArray: ret.is_array || false,
      customUnit: ret.is_custom_type || null,
      isCustomObject: ret.is_custom_object || false,
      isCustomFunction: ret.is_custom_function || false
    };

    // Comment group
    const commentOriginal = ret.comment || "";
    const commentCleaned = ret.cleanedComment ?? null;
    ret.comment = {
      original: commentOriginal,
      cleaned: commentCleaned
    };

    // Display group - computed values for rendering
    ret.display = {
      label: ret.title || ret.presentation?.label || null,
      comment: (commentCleaned ?? commentOriginal) || null
    };

    // UI group (restructured)
    if (ret.ui) {
      const uiType = String(ret.ui.ui || '').toLowerCase();
      const uiConfig = { ...ret.ui };
      delete uiConfig.ui; // Remove redundant 'ui' property from config
      ret.ui = {
        type: uiType || null,
        config: uiConfig,
        comment: ret.ui_comment || null,
        writeback: ret.ui_write ? {
          rawText: ret.ui_write.value_raw || "",
          value: ret.ui_write.value_eval,
          bounds: (uiType === 'slider' || uiType === 'range') ? {
            min: ret.ui_write.min_eval,
            max: ret.ui_write.max_eval,
            step: ret.ui_write.step_eval
          } : null,
          checkboxChecked: ret.ui_write.checkbox_checked,
          multiselectSelected: ret.ui_write.multiselect_selected,
          checklistSelected: ret.ui_write.checklist_selected
        } : null,
        html: (ret.ui_display_value !== undefined) ? {
          displayValue: ret.ui_display_value,
          inputType: ret.ui_input_type
        } : null
      };
    } else {
      ret.ui = null;
    }

    // Presentation group (pass through with normalized mode)
    if (ret.presentation) {
      ret.presentation = {
        ...ret.presentation,
        mode: ret.presentation.mode || ret.presentation.presentation || (ret.hidden ? 'hidden' : null)
      };
    } else {
      ret.presentation = ret.hidden ? { mode: 'hidden' } : null;
    }

    // Dependencies group
    // Serialize tokens to plain objects to remove lexer's toString method
    // (required for postMessage in web workers)
    const plainTokens = (ret.tokens || []).map(t => ({
      type: t.type,
      value: t.value,
      text: t.text,
      offset: t.offset,
      lineBreaks: t.lineBreaks,
      line: t.line,
      col: t.col
    }));
    ret.deps = {
      references: ret.references || [],
      missing: ret.missing_references || [],
      defines: ret.assigned_references || [],
      customUnits: ret.custom_units || [],
      tokens: plainTokens
    };

    // Debug group
    ret.debug = {
      message: ret.output_msg || "",
      log: ret.log || [],
      trace: ret.process_debug || [],
      timeMs: ret.process_time_ms
    };

    // === Remove working fields ===
    delete ret.title;
    delete ret.eval_result;
    delete ret.final_result;
    delete ret.result;
    delete ret.format;
    delete ret.is_array;
    delete ret.is_custom_type;
    delete ret.is_custom_object;
    delete ret.is_custom_function;
    delete ret.cleanedComment;
    delete ret.hidden;
    delete ret.ui_comment;
    delete ret.ui_write;
    delete ret.ui_display_value;
    delete ret.ui_input_type;
    delete ret.log;
    delete ret.processed;
    delete ret.process_debug;
    delete ret.process_time_ms;
    delete ret.output_msg;
    delete ret.missing_references;
    delete ret.assigned_references;
    delete ret.references;
    delete ret.custom_units;
    delete ret.tokens;
    delete ret.parseResult;  // Contains lexer tokens with non-cloneable methods

    return ret;
  },

  /**
   * Transform a legacy engine result (flat format) to v2 structure.
   * Unlike finalizeResult, this ADDS v2 properties while preserving the original
   * flat fields for backward compatibility.
   *
   * Used by grammar.test.js to transform InstaCalc legacy engine results.
   *
   * @param {object} ret - Legacy result object with flat properties
   * @returns {object} Result with both legacy and v2 structure
   */
  populateV2Structure: function (ret) {
    if (!ret) return ret;
    if (ret._meta?.v === 2) return ret; // Already v2
    if (ret.output) return ret; // Already has v2 structure

    // Add meta
    ret._meta = { v: 2 };

    // Input group - legacy has input as a string
    const inputOriginal = typeof ret.input === 'string' ? ret.input : (ret.input?.original || ret.input || "");
    const inputProcessed = ret.processed || ret.parsed || "";
    ret.input = {
      original: inputOriginal,
      processed: inputProcessed
    };

    // Output group
    ret.output = {
      raw: ret.result,
      value: ret.eval_result,
      display: ret.final_result || ""
    };

    // Type group - legacy may not have these
    const typeUnits = typeof ret.type === 'string' ? ret.type : "";
    ret.type = {
      units: typeUnits,
      format: ret.format || "",
      isArray: ret.is_array || false,
      customUnit: ret.is_custom_type || null,
      isCustomObject: ret.is_custom_object || false,
      isCustomFunction: ret.is_custom_function || false
    };

    // Comment group - legacy has comment as a string
    const commentOriginal = typeof ret.comment === 'string' ? ret.comment : (ret.comment?.original || "");
    ret.comment = {
      original: commentOriginal,
      cleaned: null
    };

    // Display group
    ret.display = {
      label: ret.title || null,
      comment: commentOriginal || null
    };

    // Dependencies group - legacy may not have these
    ret.deps = {
      missing: ret.missing_references || [],
      defines: ret.assigned_references || [],
      references: ret.references || [],
      customUnits: ret.custom_units || [],
      tokens: ret.tokens || []
    };

    // UI - legacy doesn't have UI widgets
    if (!ret.ui) {
      ret.ui = null;
    }

    // Presentation
    if (!ret.presentation) {
      ret.presentation = ret.hidden ? { mode: 'hidden' } : null;
    }

    // Debug group
    ret.debug = {
      message: ret.output_msg || "",
      log: ret.log || [],
      trace: ret.process_debug || [],
      timeMs: ret.process_time_ms
    };

    return ret;
  },

  // Split text at the first comment delimiter (// or #) that is NOT inside quotes.
  // Returns { before, comment } where comment includes the delimiter.
  // Uses the lexer/tokenizer so string tokens like "#FF0000" are correctly skipped.
  splitAtComment: function (text) {
    if (typeof text !== 'string') return { before: '', comment: '' };
    try {
      const tokens = IC.parser.tokenize(text, LEXER);
      for (const t of tokens) {
        if (t.type === 'commentSingle' || t.type === 'commentMulti') {
          return { before: text.substring(0, t.offset), comment: text.substring(t.offset) };
        }
      }
    } catch (e) {
      // Fallback for inputs the lexer can't handle
      const match = text.match(/\/\/|#/);
      if (match) return { before: text.substring(0, match.index), comment: text.substring(match.index) };
    }
    return { before: text, comment: '' };
  },

  // replaceTextBeforeComment("300 // some comment", "400") => "400 // some comment"
  replaceTextBeforeComment: function (originalText, newMainText) {
    const { comment } = IC.parser.splitAtComment(originalText);
    return comment ? newMainText + ' ' + comment : newMainText;
  },

  // extractAssignedSnippet("x = 10 mm // comment") => "10 mm"
  // extractAssignedSnippet("42 // comment") => "42"
  extractAssignedSnippet: function (input) {
    if (typeof input !== 'string') return '';
    const { before } = IC.parser.splitAtComment(input);
    const eqIdx = before.indexOf('=');
    if (eqIdx >= 0) {
      return before.substring(eqIdx + 1).trim();
    }
    return before.trim();
  },

  // Add text to the end of a line, but before the comment string (if present)
  appendTextBeforeComment(str, textToAdd) {
    const { before, comment } = IC.parser.splitAtComment(str);

    textToAdd = textToAdd.trimEnd();

    // maintain spacing
    if (before.endsWith(" ") && textToAdd.startsWith(" ")) {
      textToAdd = textToAdd.trim() + " ";
    }

    return before + textToAdd + comment;
  }
  ,

  // Canonicalize a date/time/datetime input string for HTML inputs and storage
  canonicalizeDateInput: function (htmlType, value) {
    const stripQuotes = (s) => {
      if (typeof s !== 'string') return s;
      const t = s.trim();
      if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) return t.slice(1, -1);
      return t;
    };
    const pad2 = (n) => String(n).padStart(2, '0');
    const to24h = (h, ampm) => {
      let hour = Number(h);
      const suffix = (ampm || '').toLowerCase();
      if (suffix === 'pm' && hour < 12) hour += 12;
      if (suffix === 'am' && hour === 12) hour = 0;
      return hour;
    };
    const normalizeDateLike = (raw) => {
      if (!raw || typeof raw !== 'string') return null;
      const s = raw.trim();
      let m = s.match(/^(\d{4})[-\/]?(\d{1,2})[-\/]?(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?:\s*(AM|PM))?)?$/i);
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]);
        const d = Number(m[3]);
        const hh = m[4] != null ? to24h(m[4], m[6]) : null;
        const mm = m[5] != null ? Number(m[5]) : null;
        return { y, mo, d, hh, mm };
      }
      m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?:\s*(AM|PM))?)?$/i);
      if (m) {
        const mo = Number(m[1]);
        const d = Number(m[2]);
        const y = Number(m[3]);
        const hh = m[4] != null ? to24h(m[4], m[6]) : null;
        const mm = m[5] != null ? Number(m[5]) : null;
        return { y, mo, d, hh, mm };
      }
      m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
      if (m) {
        return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]), hh: Number(m[4]), mm: Number(m[5]) };
      }
      m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) {
        return { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]), hh: null, mm: null };
      }
      // time-only HH:MM with optional AM/PM
      m = s.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
      if (m) {
        return { y: 1970, mo: 1, d: 1, hh: to24h(m[1], m[3]), mm: Number(m[2]) };
      }
      return null;
    };
    const formatForInput = (parts, htmlType) => {
      if (!parts) return '';
      if (htmlType === 'date') return `${parts.y}-${pad2(parts.mo)}-${pad2(parts.d)}`;
      if (htmlType === 'time') {
        const hh = parts.hh != null ? parts.hh : 0;
        const mm = parts.mm != null ? parts.mm : 0;
        return `${pad2(hh)}:${pad2(mm)}`;
      }
      const hh = parts.hh != null ? parts.hh : 0;
      const mm = parts.mm != null ? parts.mm : 0;
      return `${parts.y}-${pad2(parts.mo)}-${pad2(parts.d)}T${pad2(hh)}:${pad2(mm)}`;
    };

    const clean = stripQuotes(String(value || ''));
    const type = (htmlType === 'datetime') ? 'datetime-local' : htmlType;
    const parts = normalizeDateLike(clean);
    return formatForInput(parts, type);
  }
  ,

  // Convert ISO date/time format to native InstaCalc date literals
  // Used when writing UI control values back to the calc
  // e.g., "2025-11-06" → "2025/11/06", "2025-11-06T14:30" → "2025/11/06 14:30"
  dateToNativeLiteral: function (uiType, isoValue) {
    if (!isoValue || typeof isoValue !== 'string') return isoValue;

    if (uiType === 'date') {
      // Convert ISO date 2025-11-06 to 2025/11/06 (slashes for date objects, not subtraction)
      return isoValue.replace(/-/g, '/');
    } else if (uiType === 'datetime' || uiType === 'datetime-local') {
      // Convert ISO datetime 2025-11-06T14:30 to 2025/11/06 14:30
      return isoValue.replace(/-/g, '/').replace('T', ' ');
    }
    // time stays as-is: 14:30
    return isoValue;
  }
  ,

  // x = 123 => 123 is the "parameter" value
  // Accepts either (row) object or legacy (calc, rowIndex) for backward compatibility
  getRowParameter: function (rowOrCalc, rowIndex) {
    // Support both: getRowParameter(row) and getRowParameter(calc, index)
    const row = rowIndex !== undefined ? rowOrCalc?.rows?.[rowIndex] : rowOrCalc;
    if (!row?.hidden) {
      return null;
    }

    var text = row.text;
    // First, try direct parse
    let param = IC.parser.getRowParameterFromString(text);
    if (param != null) return param;

    // If that fails, normalize the LHS by stripping a single currency symbol
    // prefix/suffix from the identifier (e.g., "$x = 3" or "x$ = 3") and retry.
    const eqIndex = text.indexOf('=');
    if (eqIndex < 0) return null;

    let lhsRaw = (text.slice(0, eqIndex) || '').trim();
    const rhs = text.slice(eqIndex); // includes '='

    const currencies = Object.values(LIB.InstaCalc.format.currencies);
    const symbols = currencies.map(c => c.symbol).filter(Boolean);
    const escapeRegex = s => String(s).replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
    const alt = symbols.map(escapeRegex).join('|');

    let lhs = lhsRaw;
    if (alt) {
      lhs = lhs.replace(new RegExp(`^\\s*[+-]?(?:${alt})\\s*`, 'u'), '');
      lhs = lhs.replace(new RegExp(`\\s*(?:${alt})\\s*$`, 'u'), '');
    }
    // Also strip normalized currency codes from currency preprocessor, e.g., (_USD)
    lhs = lhs.replace(/^\s*[+-]?\(_[A-Z]{3}\)\s*/u, '');
    lhs = lhs.replace(/\s*\(_[A-Z]{3}\)\s*$/u, '');

    const candidate = lhs + rhs;
    const currencyResult = IC.parser.getRowParameterFromString(candidate);
    if (currencyResult != null) return currencyResult;

    // Last resort: value is temporarily unparseable during typing (e.g., "7:"
    // mid-way to "7:00"). Extract the raw RHS so callers see what the user
    // typed instead of null.
    const { before } = IC.parser.splitAtComment(text);
    return before.split('=').slice(1).join('=').trim();
  },

  // true if input is a variable assignment without calculation
  // turns out, better to let the main text be freeform for people. 
  getRowParameterFromString: function (input) {
    let allTokens = IC.parser.tokenize(input, LEXER);
    let tokens = allTokens.filter(t => t.type != "ws").filter(t => !t.type.includes("comment"));

    const isNumericLiteral = function (token) {
      return token.type == "decimal" || token.type == "hex" || token.type == "oct" || token.type == "bin";
    }

    const isOtherLiteral = function (token) {
      return token.type == "feetAndInches" || token.type == "datestring" || token.type == "timeImpliedDate" || token.type == "timestring" || token.type == "isoTimestamp";
    }

    const allowedSuffix = function (token) {
      return token.text == "%" || token.type == "id";
    }

    // general format
    // [currency]? id = [symbols]? [number] [suffix: %|type]?
    let idx = 0;
    if (!tokens.length) {
      return null;
    }

    // Skip leading currency symbols on LHS (e.g., "$bill = 50" or "$$amount = 50")
    const lhsCurrencies = Object.values(LIB.InstaCalc.format.currencies);
    const lhsCurrencySymbols = new Set(lhsCurrencies.map(c => c.symbol).filter(Boolean));
    while (idx < tokens.length && lhsCurrencySymbols.has(tokens[idx]?.text)) {
      idx++;
    }

    if (tokens[idx++]?.type != "id") {
      return null;
    }

    if (tokens[idx++]?.text != "=") {
      return null;
    }

    // if no parameter, empty string
    if (idx > tokens.length - 1) {
      return "";
    }

    // Allowed symbols before numeric literal
    let allowed = new Set(["+", "-"]);
    const currencies = Object.values(LIB.InstaCalc.format.currencies);
    currencies.forEach(c => allowed.add(c.symbol));

    // Consume allowed tokens before the numeric literal
    while (idx < tokens.length && allowed.has(tokens[idx].text)) {
      idx++;
    }

    // no more tokens -- just currency symbol
    if (idx > tokens.length - 1) {
      return tokens[idx - 1].text;
    }

    // Now, idx should be at the numeric literal, if it's the correct format
    if (!isNumericLiteral(tokens[idx]) && !isOtherLiteral(tokens[idx])) {
      return null; // No numeric literal where expected
    }
    idx += 1;

    // suffix like k, M, cm/s
    if (tokens[idx]) {
      if (!allowedSuffix(tokens[idx])) {
        return null;
      }

      idx++;
      // Check for second suffix
      if (idx < tokens.length && !allowedSuffix(tokens[idx])) {
        // check if the remaining tokens are a unit
        let remainingTokens = tokens.slice(idx - 1);
        let remainingText = remainingTokens.join('');
        if (!IC.typestringIsPossibleUnitIncludingSlash(remainingText)) {
          return null;
        }
      }
    }

    let full = IC.parser.tokenSmartJoin(allTokens, tokens);
    return full.split('=')[1]?.trimStart();
  },

  // pull variable assignment from string: "x = 3" returns "x"
  getRowParameterName: function (text) {
    // Extract LHS and normalize by stripping optional currency symbol prefix/suffix
    // so that "$x = 3" and "x$ = 3" both yield name "x".
    const eqIndex = text.indexOf('=');
    if (eqIndex < 0) return false;

    let lhsRaw = (text.slice(0, eqIndex) || '').trim();

    const currencies = Object.values(LIB.InstaCalc.format.currencies);
    const symbols = currencies.map(c => c.symbol).filter(Boolean);

    const escapeRegex = s => String(s).replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
    const alt = symbols.map(escapeRegex).join('|');

    let lhs = lhsRaw;
    if (alt) {
      lhs = lhs.replace(new RegExp(`^\\s*[+-]?(?:${alt})\\s*`, 'u'), ''); // strip leading symbol
      lhs = lhs.replace(new RegExp(`\\s*(?:${alt})\\s*$`, 'u'), '');         // strip trailing symbol
    }
    // Also strip normalized currency codes from currency preprocessor, e.g., (_USD)
    lhs = lhs.replace(/^\s*[+-]?\(_[A-Z]{3}\)\s*/u, '');
    lhs = lhs.replace(/\s*\(_[A-Z]{3}\)\s*$/u, '');

    // Reconstruct a candidate line with normalized LHS for validation
    const candidate = lhs + text.slice(eqIndex);
    if (!this.getRowParameterFromString(candidate)) {
      return false;
    }

    return lhs.trim();
  },

  // inject the assigned value into the variable assignment string
  buildRowParameterText: function (text, assigned_value, result = {}) {
    const { before, comment } = IC.parser.splitAtComment(text);

    // Preserve space after = by checking if original RHS started with space
    let ret = before.split("=").map((t, i) => {
      if (i == 0) {
        return t;
      }
      if (i == 1) {
        const hadLeadingSpace = t.length > 0 && t[0] === ' ';
        return hadLeadingSpace ? ' ' + assigned_value : assigned_value;
      }
    }).join('=');

    ret += comment;

    // replace multiple spaces with a single one
    ret = ret.replace(/\s+/g, " ");

    return ret;
  },

  // Convert a normalized option object (or primitive) to the text snippet for row write-back.
  // Option objects have {raw, autoQuoted, value, label}; primitives pass through as-is.
  optionToSnippet: function(opt) {
    if (opt?.autoQuoted) return JSON.stringify(String(opt.raw));
    const val = opt?.raw ?? opt;
    if (typeof val === 'number') return String(val);
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    return String(val);
  },

  stringCanBeIncremented: function (str) {
    if (!str || !str.match(/\d/)) {
      return false;
    }

    return IC.parser.incrementNumberInString(str, 1) != str;
  },

  // increment or decrement first number in input
  incrementNumberInString: function (input = "", direction = 1) {
    if (!input?.trim().length) {
      return input;
    }

    function stepSize(n) {
      let step;
      if (n === 0) {
        step = 1; // Default step if the number is exactly 0
      } else if (Math.abs(n) < 1) {
        // Find the number of decimal places of the first significant digit
        let decimalPlaces = Math.abs(n).toString().split('.')[1].search(/[1-9]/) + 1;
        step = Math.pow(10, -decimalPlaces);
      } else if (Math.abs(n) < 100) {
        step = 1; // For numbers between 1 and 99, step by 1
      } else {
        step = Math.pow(10, Math.floor(Math.log10(Math.abs(n))) - 1);
      }
      return step;
    }

    let allTokens = IC.parser.tokenize(input, LEXER);
    let sign = 1;
    let negativeSignIndex = -1;
    let skipTokenIndex = null;

    // if no decimal tokens, return input
    if (!allTokens.some(t => t.type == "decimal")) {
      return input;
    }

    let results = allTokens.map((t, index) => {
      if (t.type == "symbol" && t.text == "-") {
        sign = -1;
        negativeSignIndex = index;

        // delete this from the results; the actual value will be in changedN.toString();
        return '';
      }

      if (t.type == "decimal") {
        let n = sign * IC.makeNumber(t.text, IC.envGetSettings());
        let changedN = IC.mathAddNumbersHighPrecision(n, direction * stepSize(n));

        if (changedN === 0 && negativeSignIndex >= 0) {
          skipTokenIndex = negativeSignIndex;
        }

        // format number if it was formatted before 
        if (t.text.includes(",")) {
          let settings = IC.envGetSettings();
          return IC.parser.applyRawNumberFormatting(changedN, settings?.locale || "en-US");
        }
        return changedN.toString();
      }

      return t.text;
    });

    let processed = results.join('');
    return processed;
  },

  execute: function (input = "", i = 0, settings = {}) {
    const timeStart = performance.now();
    const ret = IC.parser.getBlankReturnObject();

    if (typeof input !== "string") {
      return ret;
    }

    // check for long input
    if (input?.length > 1000) {
      // check if input begins with comment
      if (!input.match(/^\s*(?:\/\/|#)/)) {
        // Return v2 format "input too long" result directly
        return IC.parser.getInputTooLongResult();
      }
    }

    const startEnv = IC.envNewStartEnv(i, settings);

    const cached = IC.parser.getResultCache({ input, startEnv, settings, rowIndex: i });
    if (cached) {
      // DEBUG: Track stock cache hits
      if (typeof Debug !== 'undefined' && Debug && input?.match(/^[A-Z]{2,5}$/)) {
        Debug.log('stock:cache', `🔄 CACHE HIT for "${input}" at row ${i}`);
        Debug.log('stock:cache', `  Cached result: v=${cached.final_result?.v}, t=${JSON.stringify(cached.final_result?.t)}`);
        Debug.log('stock:cache', `  Cached debug: ${JSON.stringify(cached.final_result?._debug)}`);
      }
      return cached;
    }

    let preprocessResult = IC.parser.phasePreprocess({ input, i, settings, startEnv });
    Object.assign(ret, preprocessResult);

    let parseResult = IC.parser.phaseParse(ret);
    Object.assign(ret, parseResult);

    let evalResult = IC.parser.phaseEval(ret);
    Object.assign(ret, evalResult);

    let postprocessResult = IC.parser.phasePostprocess(ret, settings, timeStart);
    Object.assign(ret, postprocessResult);

    // Clean up non-cloneable properties that were added during phases
    // (parseResult and tokens contain lexer objects with toString methods that can't be cloned for postMessage)
    delete ret.parseResult;
    delete ret.tokens;

    // DEBUG: Track what we're caching for stock identifiers
    if (typeof Debug !== 'undefined' && Debug && input?.match(/^[A-Z]{2,5}$/)) {
      Debug.log('stock:cache', `💾 CACHING result for "${input}" at row ${i}`);
      Debug.log('stock:cache', `  Caching: v=${ret.final_result?.v}, t=${JSON.stringify(ret.final_result?.t)}`);
      Debug.log('stock:cache', `  Debug trace: ${JSON.stringify(ret.final_result?._debug)}`);
    }

    IC.parser.setResultCache({ input, startEnv, settings, ret, rowIndex: i });
    return ret;
  },

  invalidResult(result) {
    const evalResult = result.output?.value;
    return !IC.isValidEvalResult(evalResult) || IC.parser.invalidNumber(evalResult);
  },

  invalidNumber(n) {
    return typeof n === "number" && (isNaN(n) || !Number.isFinite(n));
  },

  shouldRedo(result) {
    const processed = result.input?.processed;
    if (processed === "") {
      return false;
    }

    // custom unit -- maybe be defined now
    const isCustomType = result.type?.customUnit;
    if (isCustomType) {
      return true;
    }

    const missingRefs = result.deps?.missing || [];
    return IC.parser.invalidResult(result) || missingRefs.length;
  },

  quickEval: function (input) {
    return IC.parser.execute(input, 0)?.output?.value;
  },

  quickResult: function (input) {
    return IC.parser.execute(input, 0)?.output?.raw;
  },

  mergeRelatedLinesOld: function (rows) {
    const mergedRows = [...rows]; // Create a copy to maintain the same length

    for (let i = 1; i < rows.length; i++) {
      const trimmedRow = rows[i].trim();

      // Check if the line starts with 'else' or 'else if'
      if (/^else\b|^else if\b/.test(trimmedRow)) {
        let j = i - 1;

        // Find the previous non-empty line to merge with
        while (j >= 0 && mergedRows[j] === '') {
          j--;
        }

        if (j >= 0) {
          // Merge current line into the previous line
          mergedRows[j] += ' ' + trimmedRow;
          mergedRows[i] = ''; // Replace current line with an empty string
        }
      }
    }

    return mergedRows;
  },

  stripComment: function (line) {
    return IC.parser.splitAtComment(line).before.trim();
  },

  // Helper to extract assignment variable names from a line
  extractAssignmentsFromLine: function (line) {
    const trimmed = line.trim();

    // Skip lines that are just braces or control structures
    if (!trimmed || /^[{}]$/.test(trimmed) || /^(if|else)\s*[\({]/.test(trimmed)) {
      return [];
    }

    // Use existing tokenizer-based scanner to extract assignments
    // This handles edge cases like "2 weeks = 3" (not an assignment) properly
    const ids = IC.parser.scanAssignedIdentifiersUsingLex(trimmed);
    return ids.map(id => id.name);
  },

  mergeRelatedLines: function (rows) {
    const mergedRows = [...rows]; // Create a copy to maintain the same length
    const mergeMetadata = {}; // Track which rows were merged and their original content

    let i = 0;
    while (i < rows.length) {
      const trimmedRow = rows[i].trim();

      // Check if this line contains an if statement that opens a block
      if (/\bif\s*\(/.test(trimmedRow)) {
        let blockLines = [];
        let braceDepth = 0;
        let startIndex = i;
        let originalLines = []; // Track original content before merging

        // Process first line: strip comment, add to block
        const firstLineClean = IC.parser.stripComment(trimmedRow);
        blockLines.push(firstLineClean);
        originalLines.push({ index: i, content: rows[i].trim() });

        // Count braces in the first line (after stripping comment)
        for (let char of firstLineClean) {
          if (char === '{') braceDepth++;
          if (char === '}') braceDepth--;
        }

        // If the if statement spans multiple lines, collect them
        if (braceDepth > 0) {
          i++;
          while (i < rows.length && braceDepth > 0) {
            const currentLine = rows[i].trim();

            // Track original line before merging
            originalLines.push({ index: i, content: currentLine });

            // Strip comment from current line
            const cleanLine = IC.parser.stripComment(currentLine);
            if (cleanLine) { // Only add if not empty after stripping comment
              blockLines.push(cleanLine);
            }

            // Count braces in clean line
            for (let char of cleanLine) {
              if (char === '{') braceDepth++;
              if (char === '}') braceDepth--;
            }

            i++;
          }

          // Merge all lines into the first line
          mergedRows[startIndex] = blockLines.join('; ');

          // Store metadata about this merge
          mergeMetadata[startIndex] = {
            type: 'if-else-block',
            originalLines: originalLines,
            startIndex: startIndex,
            endIndex: i - 1
          };

          // Mark the rest as empty
          for (let j = startIndex + 1; j < i; j++) {
            mergedRows[j] = '';
          }

          // Don't increment i again, we're already at the right position
          continue;
        }
      }

      // Also handle standalone 'else' lines (from old logic)
      if (/^else\b/.test(trimmedRow)) {
        let j = i - 1;

        // Find the previous non-empty line to merge with
        while (j >= 0 && mergedRows[j] === '') {
          j--;
        }

        if (j >= 0) {
          // Merge current line into the previous line
          mergedRows[j] += ' ' + trimmedRow;
          mergedRows[i] = ''; // Replace current line with an empty string
        }
      }

      i++;
    }

    return { mergedRows, mergeMetadata };
  },

  executeRows: function (initialRows, settings = {}) {
    if (!Array.isArray(initialRows)) {
      throw new Error("Expected Array");
    }

    // UNIFIED CODE PATH: Sync stock prices to unit system before any calculation.
    // This runs identically on main thread and worker - no separate registration logic.
    const syncResult = { executed: false, synced: 0 };
    if (InstaCalc?.syncStockUnits) {
      syncResult.executed = true;
      syncResult.synced = InstaCalc.syncStockUnits();
      syncResult.samplesAfterSync = {
        MSFT: InstaCalc.Unit?.currencies?.MSFT,
        AAPL: InstaCalc.Unit?.currencies?.AAPL,
        GOOG: InstaCalc.Unit?.currencies?.GOOG
      };
    } else {
      syncResult.reason = !InstaCalc ? 'InstaCalc undefined' : 'syncStockUnits undefined';
    }

    // Add sync result to first stock row we encounter
    const addSyncTrace = (result, row) => {
      if (row?.match(/^[A-Z]{2,5}$/) && !result._syncTrace) {
        result._syncTrace = syncResult;
      }
    };

    let rows, mergeMetadata;
    const phaseSetup = () => {
      IC.envReset();
      IC.envSetInputRows(initialRows);
      const mergeResult = IC.parser.mergeRelatedLines(initialRows);
      rows = mergeResult.mergedRows;
      mergeMetadata = mergeResult.mergeMetadata;
    }

    let results = [];

    // --- First pass: execute all rows ---
    const phaseInitialResults = () => {
      results = rows.map((row, i) => {
        const result = IC.parser.execute(row, i, settings);

        // ADD DEBUG TRACE TO RESULT OBJECT (works in worker!)
        if (row?.match(/^[A-Z]{2,5}$/)) {
          if (!result._executionTrace) result._executionTrace = {};
          result._executionTrace.phase = 'initial';
          result._executionTrace.row = row;
          result._executionTrace.rowIndex = i;
          result._executionTrace.resultValue = result.final_result?.v;
          result._executionTrace.resultType = result.final_result?.t;
          result._executionTrace.hasDebugTrace = !!result.final_result?._debug;
          addSyncTrace(result, row);
        }

        // DEBUG: Track stock identifier results
        if (typeof Debug !== 'undefined' && Debug && row?.match(/^[A-Z]{2,5}$/)) {
          Debug.log('stock:executeRows', `Row ${i} "${row}":`);
          Debug.log('stock:executeRows', `  Result: v=${result.final_result?.v}, t=${JSON.stringify(result.final_result?.t)}`);
          Debug.log('stock:executeRows', `  Debug: ${JSON.stringify(result.final_result?._debug)}`);
        }

        return result;
      });
    };

    // --- Build dependency information ---
    const assignedRefsByRow = {};   // maps row index → array of variable names assigned in that row
    const assignedRefsByName = {};  // maps variable name → set of row indices where it is assigned
    const rowDependencyGraph = {}; // row index → set of row indices it depends on
    const missingReferences = {};
    const finalRedo = new Set();
    const redoBecauseCustomUnitDefined = {}; // if an autounit is defined, mark the row for re-execution

    const phaseBuildDependencies = () => {

      // store assigned refs (v2 format: deps.defines)
      results.forEach((result, i) => {
        const assignedRefs = result.deps?.defines || [];
        // Build mapping: row index → assigned variable names.
        assignedRefsByRow[i] = assignedRefs.map(refObj => refObj?.name ?? refObj);

        // Build mapping: variable name → row indices.
        assignedRefs.forEach(refObj => {
          let name = refObj?.name ?? refObj;
          assignedRefsByName[name] ||= new Set();
          assignedRefsByName[name].add(i);
        });
      });

      // For each row, add dependencies from missing references, custom units, and explicit references.
      results.forEach((result, currentRow) => {
        const missingRefs = result.deps?.missing || [];
        const customUnits = result.deps?.customUnits || [];
        const references = result.deps?.references || [];

        // Process missing references.
        missingRefs.forEach(missingRef => {
          const currentRowDependsOnRows = assignedRefsByName[missingRef];
          if (currentRowDependsOnRows) {
            currentRowDependsOnRows.forEach(rowIndex => {
              (rowDependencyGraph[currentRow] ||= new Set()).add(Number(rowIndex));
            });
          } else {
            missingReferences[missingRef] ||= new Set();
            missingReferences[missingRef].add(currentRow);
            // If the missing reference isn't one of the predefined units, mark for re-evaluation.
            if (!IC.predefinedUnit(missingRef)) {
              finalRedo.add(currentRow);
            }
          }
        });

        // Process custom units.
        customUnits.forEach(customUnitName => {
          const currentRowDependsOnRows = assignedRefsByName[customUnitName];
          if (currentRowDependsOnRows) {
            currentRowDependsOnRows.forEach(rowIndex => {
              (rowDependencyGraph[currentRow] ||= new Set()).add(Number(rowIndex));
            });
            redoBecauseCustomUnitDefined[currentRow] = true;
            finalRedo.add(currentRow);
          }

          // Also check for singular/plural forms.
          let singular = IC.stringSingularize(customUnitName);
          let plural = IC.stringPluralize(customUnitName);
          if (assignedRefsByName[singular] || assignedRefsByName[plural]) {
            redoBecauseCustomUnitDefined[currentRow] = true;
            finalRedo.add(currentRow);
          }
        });

        // Process regular references.
        references.forEach(ref => {
          (assignedRefsByName[ref] || []).forEach(rowIndex => {
            if (Number(rowIndex) !== currentRow) {
              (rowDependencyGraph[currentRow] ||= new Set()).add(Number(rowIndex));
            }
          });
        });
      });
    }

    // --- Resolve broken results (missing dependencies, etc.) ---
    const phaseResolveDependencies = () => {

      function topologicalSort(graph) {
        const result = [];
        const visited = new Set();

        function dfs(node) {
          if (visited.has(node)) {
            return;
          }
          visited.add(node);
          if (graph[node]) {
            graph[node].forEach(dfs);
          }
          result.push(Number(node));
        }

        Object.keys(graph).forEach(dfs);
        return result;
      }

      // Sort and collect rows that need to be redone.
      const sortedRedos = topologicalSort(rowDependencyGraph);
      Object.keys(missingReferences).forEach(missingRef => {
        if (assignedRefsByName[missingRef]) {
          assignedRefsByName[missingRef].forEach(rowIndex => {
            finalRedo.add(rowIndex);
          });
        }
      });

      let redone = new Set();

      // --- Second pass: re-execute rows that need updating ---
      // We run up to 2 passes to handle cascading dependencies.
      // Process rows in sequential order to ensure reassignments work correctly.
      for (var pass = 0; pass < 2; pass++) {
        const rowsReexecutedThisPass = new Set();
        const varsModifiedThisPass = new Set();

        // Process rows in order to maintain sequential execution semantics
        rows.forEach((row, rowId) => {
          // Check if this row should be re-executed
          const needsRedo = IC.parser.shouldRedo(results[rowId]);

          // Check if this row uses any variables that were modified earlier in this pass
          const rowReferences = results[rowId].deps?.references || results[rowId].references || [];
          const dependsOnModifiedVars = rowReferences.some(ref =>
            varsModifiedThisPass.has(ref)
          );

          // Check if any of this row's assigned variables were modified earlier in this pass
          const assignsModifiedVars = (assignedRefsByRow[rowId] || []).some(varName =>
            varsModifiedThisPass.has(varName)
          );

          const dependsOnRedoneRow = Array.from(rowDependencyGraph[rowId] || []).some(i =>
            redone.has(i) || rowsReexecutedThisPass.has(i)
          );
          const inFinalRedo = finalRedo.has(rowId);
          const dependsOnFinalRedo = Array.from(rowDependencyGraph[rowId] || []).some(i =>
            finalRedo.has(i)
          );
          const isCustomUnitRow = redoBecauseCustomUnitDefined[rowId];

          if (needsRedo || dependsOnModifiedVars || assignsModifiedVars || dependsOnRedoneRow ||
            inFinalRedo || dependsOnFinalRedo || isCustomUnitRow) {
            rowsReexecutedThisPass.add(rowId);
            redone.add(rowId);
            results[rowId] = IC.parser.execute(rows[rowId], rowId, settings);

            // ADD DEBUG TRACE TO RE-EXECUTED RESULT (works in worker!)
            if (rows[rowId]?.match(/^[A-Z]{2,5}$/)) {
              if (!results[rowId]._executionTrace) results[rowId]._executionTrace = {};
              results[rowId]._executionTrace.phase = `redo-pass${pass}`;
              results[rowId]._executionTrace.row = rows[rowId];
              results[rowId]._executionTrace.rowIndex = rowId;
              results[rowId]._executionTrace.resultValue = results[rowId].final_result?.v;
              results[rowId]._executionTrace.resultType = results[rowId].final_result?.t;
              results[rowId]._executionTrace.redoReason = {
                needsRedo,
                dependsOnModifiedVars,
                assignsModifiedVars,
                dependsOnRedoneRow,
                inFinalRedo,
                dependsOnFinalRedo,
                isCustomUnitRow
              };
            }

            // Track which variables were modified this pass
            (assignedRefsByRow[rowId] || []).forEach(varName => {
              varsModifiedThisPass.add(varName);
            });
          }
        });

        // If nothing was re-executed this pass, we're done
        if (rowsReexecutedThisPass.size === 0) {
          break;
        }
      }
    }

    // --- Goalseek solver ---
    const phaseSolveGoalseeks = () => {
      const debug = (...args) => {
        if (false && OPTION_DEBUG_INFO) {
          console.log(...args);
        }
      };

      // Find rows with goalseek commands
      const goalseekRows = results.filter(r => r?.result?.type === "goalseek").map(r => r.i);
      if (goalseekRows.length === 0) return;

      goalseekRows.forEach(gsRowIndex => {
        const savedEnv = Object.assign({}, IC.envGet()); // Save environment state

        const gsResult = results[gsRowIndex];
        const { targetVar, targetValue, vars: specifiedVars } = gsResult?.result ?? {};
        if (!gsResult?.result) return;

        // Find row that defines target variable
        const targetRowIndex = (assignedRefsByName[targetVar] ?? new Set()).values().next().value;
        if (targetRowIndex === undefined) {
          console.warn(`No row found that defines target variable ${targetVar}`);
          return;
        }

        // dependent parameters, like y = ax^2
        const paramsToRecompute = new Set();

        // Helper to check if a variable name is a row reference (e.g. "r1", "r2", etc.)
        const isNumericRowReference = ref => /^r\d+$/.test(ref);
        const findRootParameters = (rowIndex, visited = new Set()) => {
          if (visited.has(rowIndex)) {
            return new Set(); // cycle detected
          }
          visited.add(rowIndex);

          const localParams = new Set();
          const rowText = rows[rowIndex];
          const refs = (assignedRefsByRow[rowIndex] || []).filter(ref => typeof ref === 'string');
          const hasNoDependency = !rowDependencyGraph[rowIndex] || rowDependencyGraph[rowIndex].size === 0;

          // First, try to extract a proper named parameter (e.g. "x = 3000")
          const namedParam = IC.parser.getRowParameterName(rowText);
          if (hasNoDependency && namedParam) {
            debug(`Row ${rowIndex} has named parameter:`, namedParam);
            localParams.add(namedParam);
          } else {
            // extract references, add to recompute list
            const properRefs = refs.filter(ref => !isNumericRowReference(ref) && assignedRefsByName[ref] && hasNoDependency);
            if (properRefs.length > 0) {
              debug(`Row ${rowIndex} has proper references:`, properRefs);
              properRefs.forEach(ref => localParams.add(ref));
            } else {
              // Fallback: for each row reference, use the referenced row's text (adjusting for 0-index).
              refs.forEach(ref => {
                debug(`Row ${rowIndex} has row reference:`, ref);
                paramsToRecompute.add(ref);

                if (!hasNoDependency) {
                  debug(`Row ${rowIndex} has dependency, adding to recompute list:`, ref);
                  return;
                }

                // TODO: row references don't appear in our data structures, track this down
                if (isNumericRowReference(ref) && hasNoDependency) {
                  const refRowIndex = parseInt(ref.slice(1), 10) - 1; // adjust: r1 -> index 0, etc.
                  const refRowText = rows[refRowIndex];
                  const hasOtherRowReference = rowText.match(/\br\d+\b/g);
                  if (IC.parser.stringCanBeIncremented(refRowText) && !hasOtherRowReference) {
                    debug(`Row ${rowIndex} can be a param:`, ref, refRowText);
                    paramsToRecompute.delete(ref);
                    localParams.add(ref);
                  }
                }
              });
            }
          }

          // Process dependencies recursively
          const deps = rowDependencyGraph[rowIndex] || new Set();
          deps.forEach(depIndex => {
            const depParams = findRootParameters(depIndex, visited);
            depParams.forEach(p => localParams.add(p));
          });

          return localParams;
        };

        // Get parameters to vary - either specified or found by walking dependency tree
        const parameterNames = specifiedVars ?
          (Array.isArray(specifiedVars) ? specifiedVars : [specifiedVars]) :
          Array.from(findRootParameters(targetRowIndex));

        // Initialize parameter values
        const parameters = Object.fromEntries(
          parameterNames.map(name => [name, IC.envGetVariable(name)])
        );

        // debug(`Goalseek: ${targetVar} = ${targetValue}, parameters:`, parameters, 'paramsToRecompute:', paramsToRecompute);
        const originalValues = { ...parameters };

        // Solver configuration
        const solverConfig = {
          maxIterations: 30 * parameterNames.length,
          tolerance: 1.1e-6 * Math.abs(targetValue),
          tolerancePercent: 0.01,
          learningRate: Math.pow(2, -(parameterNames.length - 1))
        };

        // Helper to update a variable and its row reference
        const updateParameter = (name, value) => {
          IC.envSetVariable(name, value);
          assignedRefsByName[name]?.forEach(rowIndex => {
            IC.envSetVariable(`r${rowIndex + 1}`, value);
          });
        };

        // Helper to evaluate target with updated parameters
        const evaluateTarget = () => {
          // Get rows that need recalculation in dependency order
          const recalcRows = (() => {
            const chain = new Set();
            const visited = new Set();

            const visit = (index) => {
              if (visited.has(index)) return;
              visited.add(index);
              (rowDependencyGraph[index] ?? new Set()).forEach(visit);
              chain.add(index);
            };

            visit(targetRowIndex);
            return [...chain];
          })();

          // Only recalculate dependent rows
          recalcRows.forEach(index => {
            const hasDependentCalculation = assignedRefsByRow[index]?.filter(item => paramsToRecompute.has(item));
            if (hasDependentCalculation.length) {
              IC.parser.execute(rows[index], index, settings);
            }
          });

          return IC.val(IC.parser.execute(rows[targetRowIndex], targetRowIndex, settings).result);
        };

        // Helper to detect if a number is "nearly" a clean value
        const findCleanValue = (val) => {
          // Try rounding to nearest 0.5
          const nearestHalf = Math.round(val * 2) / 2;
          if (Math.abs(nearestHalf - val) < 0.0001) {
            return nearestHalf;
          }

          // Try rounding to nearest integer
          const nearestInt = Math.round(val);
          if (Math.abs(nearestInt - val) < 0.0001) {
            return nearestInt;
          }

          return ICLib.roundToDecimal(val, 6);
        };

        // Run solver
        let solved = false;
        let error = null;
        let iterations = 0;
        let solvedValue = null;

        for (; iterations < solverConfig.maxIterations; iterations++) {
          const currentValue = evaluateTarget();
          error = targetValue - currentValue;

          if (Math.abs(error) < solverConfig.tolerance) {
            solved = true;
            solvedValue = currentValue;
            break;
          }

          // Update each parameter using Newton-Raphson method
          const paramsToVary = Object.keys(parameters).filter(param => param !== targetVar);
          paramsToVary.forEach(param => {
            const currentParamValue = IC.val(IC.envGetVariable(param));
            const delta = 0.01 * (Math.abs(currentParamValue) + 0.001);

            // Calculate gradient using central difference
            updateParameter(param, currentParamValue + delta);
            const plusValue = evaluateTarget();

            updateParameter(param, currentParamValue - delta);
            const minusValue = evaluateTarget();

            const gradient = (plusValue - minusValue) / (2 * delta);

            // Update parameter
            const newValuePrecise = gradient === 0 ? currentParamValue :
              currentParamValue - solverConfig.learningRate * ((currentValue - targetValue) / gradient);

            let newValueRounded = findCleanValue(IC.val(newValuePrecise));

            // parameters[param] = ICLib.roundToDecimal(IC.val(newValue), 4);
            // parameters[param] = IC.val(newValue);
            parameters[param] = newValueRounded;
            updateParameter(param, parameters[param]);
          });
        }

        // Accept solution if close enough
        if (!solved && Math.abs(error ?? 0) / Math.abs(targetValue) < solverConfig.tolerancePercent) {
          solved = true;
        }

        // attempt rounded solution 
        if (solved) {
          // Save the original solution in case rounding doesn't work
          const originalSolution = { ...parameters };
          let roundedSolved = false;

          // Try rounding to different decimal places
          for (let decimals = 0; decimals <= 3 && !roundedSolved; decimals++) {
            // Round all parameters
            Object.keys(parameters).forEach(param => {
              const rounded = ICLib.roundToDecimal(parameters[param], decimals);
              parameters[param] = rounded;
              updateParameter(param, rounded);
            });

            // Check if rounded solution still works
            const roundedValue = evaluateTarget();
            const roundedError = Math.abs(targetValue - roundedValue);

            if (roundedError < solverConfig.tolerance) {
              roundedSolved = true;
            }
          }

          // If rounding didn't work, restore original solution
          if (!roundedSolved) {
            Object.entries(originalSolution).forEach(([param, value]) => {
              parameters[param] = value;
              updateParameter(param, value);
            });
          }
        }

        // Update goalseek result
        // Support both v2 format (output.*) and legacy format (eval_result, etc.)
        const goalseekResult = {
          vars: parameters,
          solved,
          type: "goalseek",
          targetValue,
          targetVar,
          solvedValue,
          dependentVars: parameterNames,
          error,
          errorPct: Math.abs((error ?? 0) / targetValue),
          steps: iterations
        };
        if (results[gsRowIndex].output) {
          results[gsRowIndex].output.value = goalseekResult;
          results[gsRowIndex].output.display = goalseekResult;
          results[gsRowIndex].output.raw = goalseekResult;
        }

        // TODO: update rows that reference this goalseek result?

        // Restore original state
        Object.entries(originalValues).forEach(([name, value]) => {
          IC.envSetVariable(name, value);
        });
        IC.envSet(savedEnv);
      });
    };

    // chart title data may be out of sync
    function phaseResolveChartData() {
      // scan each of the results, if it's an object with type 'chart', set the title
      // the reason is that title changes don't cause a recalc
      results.forEach((result, i) => {
        // v2 format: output.raw contains the chart object
        const rawResult = result.output?.raw;
        if (rawResult?.startIndex !== undefined) {
          rawResult.data.forEach((data, j) => {
            // v2 format: display.label contains the title
            data.label = results[rawResult.startIndex + j - 1]?.display?.label || "";
          });
        }
      });
    }

    // --- Populate assignment results for merged if/else blocks ---
    const phasePopulateMergedAssignments = () => {
      // For each merged if/else block, populate values on their original lines
      Object.keys(mergeMetadata).forEach(startIndexStr => {
        const startIndex = Number(startIndexStr);
        const metadata = mergeMetadata[startIndex];

        if (metadata.type !== 'if-else-block') return;

        // Determine which branch executed by checking assignments or re-evaluating condition
        let executedBranchStart = null;
        let executedBranchEnd = null;

        // Try to determine which branch by looking at the structure
        // Find where "} else {" appears in the original lines
        let elseIndex = null;
        for (let i = 0; i < metadata.originalLines.length; i++) {
          const trimmed = metadata.originalLines[i].content.trim();
          if (/^}\s*else\s*\{/.test(trimmed) || trimmed === '} else {') {
            elseIndex = i;
            break;
          }
        }

        // Get the variables that were assigned during execution of this block (v2 format: deps.defines)
        const assignedVars = results[startIndex].deps?.defines || [];

        // Try to determine which branch executed by checking if any assignments in the if-branch match
        let ifBranchExecuted = null;

        // First try: check assignments if they exist
        if (elseIndex !== null && assignedVars.length > 0) {
          // Check if-branch (between first line and else)
          for (let i = 1; i < elseIndex; i++) {
            const { index, content } = metadata.originalLines[i];
            const lineAssignments = IC.parser.extractAssignmentsFromLine(content);

            for (const varName of lineAssignments) {
              if (assignedVars.some(ref => (ref?.name || ref) === varName)) {
                try {
                  const envBefore = Object.assign({}, IC.envGet());
                  const lineResult = IC.parseAndEval(content);
                  IC.envSet(envBefore);
                  const actualValue = IC.envGetVariable(varName);

                  if (IC.isValidEvalResult(actualValue) &&
                    IC.isValidEvalResult(lineResult) &&
                    IC.val(actualValue) === IC.val(lineResult)) {
                    ifBranchExecuted = true;
                    break;
                  }
                } catch (e) { }
              }
            }
            if (ifBranchExecuted !== null) break;
          }

          // If no if-branch assignment matched, check else-branch
          if (ifBranchExecuted === null) {
            for (let i = elseIndex + 1; i < metadata.originalLines.length; i++) {
              const { index, content } = metadata.originalLines[i];
              const lineAssignments = IC.parser.extractAssignmentsFromLine(content);

              for (const varName of lineAssignments) {
                if (assignedVars.some(ref => (ref?.name || ref) === varName)) {
                  try {
                    const envBefore = Object.assign({}, IC.envGet());
                    const lineResult = IC.parseAndEval(content);
                    IC.envSet(envBefore);
                    const actualValue = IC.envGetVariable(varName);

                    if (IC.isValidEvalResult(actualValue) &&
                      IC.isValidEvalResult(lineResult) &&
                      IC.val(actualValue) === IC.val(lineResult)) {
                      ifBranchExecuted = false;
                      break;
                    }
                  } catch (e) { }
                }
              }
              if (ifBranchExecuted !== null) break;
            }
          }
        }

        // Second try: if no assignments or couldn't determine from assignments, parse and evaluate the condition
        if (ifBranchExecuted === null) {
          try {
            // Extract the condition from the if statement
            // Original first line looks like: "if (temperature > 70) {"
            const firstLine = metadata.originalLines[0].content;
            const condMatch = firstLine.match(/if\s*\((.*?)\)\s*\{/);
            if (condMatch) {
              const condition = condMatch[1];
              const condResult = IC.parseAndEval(condition);
              ifBranchExecuted = IC.val(condResult) ? true : false;
            }
          } catch (e) {
            // If we can't evaluate the condition, we can't determine which branch
          }
        }

        // Determine branch boundaries
        if (elseIndex !== null) {
          if (ifBranchExecuted === true) {
            executedBranchStart = 1; // After "if (cond) {"
            executedBranchEnd = elseIndex - 1; // Before "} else {"
          } else if (ifBranchExecuted === false) {
            executedBranchStart = elseIndex + 1; // After "} else {"
            executedBranchEnd = metadata.originalLines.length - 2; // Before final "}"
          }
        } else {
          // No else branch - if the if executed, show the if body
          if (ifBranchExecuted === true) {
            executedBranchStart = 1; // After "if (cond) {"
            executedBranchEnd = metadata.originalLines.length - 2; // Before final "}"
          }
        }

        // Clear the result for the if statement itself
        results[startIndex] = {
          ...results[startIndex],
          output: {
            ...results[startIndex].output,
            value: null,
            display: '',
            raw: null
          }
        };

        // If we determined which branch executed, populate all lines in that branch
        if (executedBranchStart !== null && executedBranchEnd !== null) {
          for (let i = executedBranchStart; i <= executedBranchEnd; i++) {
            const { index, content } = metadata.originalLines[i];
            const trimmed = content.trim();

            // Skip structural lines
            if (!trimmed || /^[{}]$/.test(trimmed)) continue;

            // Extract comment
            const { comment } = IC.parser.processComment(content);

            // Try to evaluate this line
            try {
              const envBefore = Object.assign({}, IC.envGet());
              const lineResult = IC.parseAndEval(content);
              IC.envSet(envBefore);

              if (IC.isValidEvalResult(lineResult)) {
                const evalValue = IC.val(lineResult);
                const displayValue = IC.parser.formatResult(lineResult, settings);
                results[index] = {
                  ...results[index],
                  output: {
                    ...results[index].output,
                    value: evalValue,
                    display: displayValue,
                    raw: lineResult
                  }
                };
              }
            } catch (e) {
              // If evaluation fails, skip this line
            }
          }
        }
      });
    };

    // REMOVED: phaseMergeConsecutiveComments
    // This was causing bugs where comment content from neighboring rows would accumulate.
    // The feature was never fully implemented (UI doesn't check mergedIntoRow),
    // and comment merging should be a view-layer concern, not parser logic.

    // -- main flow --

    // Option D: Initialize stock symbol collection for batch prefetch
    IC._detectedStockSymbols = new Set();

    phaseSetup();
    phaseInitialResults();

    // Option D: Batch prefetch uncached stock symbols discovered during preprocessing.
    // This kicks off all fetches in parallel instead of one-by-one during evaluation.
    // The existing notifyExternalDataLoaded mechanism handles recalculation when data arrives.
    // Note: Only run in browser context where real fetch is available (skip in Node.js tests).
    // Check for STOCK_URL to ensure we're in a properly configured browser environment.
    const canFetch = typeof window !== 'undefined' && typeof Action !== 'undefined' && Action.STOCK_URL;
    if (canFetch && IC._detectedStockSymbols.size > 0 && Action.fn?.stock) {
      const stockCache = Action.fn.stock_cache || {};
      IC._detectedStockSymbols.forEach(symbol => {
        const cached = stockCache[symbol];
        // Trigger fetch if not cached or still loading (will be deduped by existing logic)
        if (!cached || !cached.loaded) {
          if (typeof window !== 'undefined' && window.Debug) window.Debug.log('stock', `[PREFETCH] Batch-triggering fetch for ${symbol}`);
          Action.fn.stock(symbol);
        }
      });
    }
    IC._detectedStockSymbols.clear();

    phaseBuildDependencies();
    phaseResolveDependencies();
    phaseSolveGoalseeks();
    phaseResolveChartData();
    phasePopulateMergedAssignments();
    // REMOVED: phaseMergeConsecutiveComments() - comment merging is now a view-layer concern
    // (only presentation mode should merge comments, not the parser/data layer)

    return { results: results, env: IC.envGet() };
  },

  // 2 => 2, but 2.016 => 2.02
  toFixedIfNecessary(value, dp) {
    return +parseFloat(value).toFixed(dp);
  },

  // simple parse fallback for scratchpad-style parsing: food $15 + clothes $50
  parseSimple(inputStr) {

    // conversion or function call, ignore
    if (inputStr.match(/[-]>|[(]/)) {
      return null;
    }

    // is encoded unicode string, ignore
    if (inputStr.match(/\\u/)) {
      return null;
    }

    // lex string, remove all IDs. Keep operators and numbers
    const allowedTokens = { decimal: true, oct: true, bin: true, hex: true, symbol: true, percentOp: true, ws: true, id: true };
    const tokens = IC.parser.tokenize(inputStr, LEXER);
    let str = tokens.map((token) => {
      if (!allowedTokens[token.type]) {
        return '';
      }

      if (token.type == "id") {
        let num = token.text.match(/-?\d+\.?\d*/);
        if (num && num[0]) {
          return num[0]
        } else if (!IC.predefinedUnit(token.text.trim())) {
          return '';
        }
      }
      return token.text;
    }).join(' ');
    return str;
  },

  applyNumberFormatting: function (result, calc_settings) {
    let final_result = result.final_result;

    // no change
    if (typeof final_result === "string" || !IC.isValidEvalResult(final_result)) {
      return;
    }

    if (typeof final_result == "boolean" || final_result == Infinity || final_result == -Infinity) {
      result.final_result = final_result.toString();
      return;
    }

    // Skip locale formatting for "raw" numbers (unix timestamps, etc.)
    // These should display without thousand separators: 1768118400 not 1,768,118,400
    let objFormat = IC.format(result.result);
    if (objFormat === "raw") {
      result.final_result = final_result.toString();
      return;
    }

    if (typeof final_result == "number") {
      if (isNaN(final_result)) {
        result.final_result = "Number undefined";
        return;
      }

      let skipDollarSign = result.type.toLowerCase().match(/usd|dollar/i) || (IC.hasType(result.result) && IC.typeHasUnitInCategory(result?.result?.t, "dollar"));

      let opts = {};
      if (result?.format?.code && !skipDollarSign) {
        opts = { ...opts, style: 'currency', currency: result.format.code, maximumFractionDigits: 2 };

        // TODO: move this to the code detection
        IC.log(`${result.format.symbol}: currency format ($$: no symbol)`);
      } else {
        opts.maximumFractionDigits = 8;
        if (final_result != 0 && Math.abs(final_result) < 1e-7 || Math.abs(final_result) > 1e18) {
          opts.notation = "scientific";
        }
      }

      if (result.type && LIB.InstaCalc.Unit?.currencies[result.type.toLowerCase()]) {
        // 1 VND should be a decimal number of USD
        if (Math.abs(IC.val(final_result)) > 1) {
          opts.maximumFractionDigits = 2;
        }
      } else if (result.type && Math.abs(final_result) > 1) {
        // if a unit conversion or calculation, don't show full precision; unit label takes up room too
        opts.maximumFractionDigits = 4;
      }

      if (result.format == "round2") {
        opts.maximumFractionDigits = 2;
      }

      /*
      -1: no rounding
      0: round to whole number
      1: round to .1
      2: round to .01
      */
      let rounding = calc_settings.rounding;

      if (typeof (rounding) === "number" && rounding >= 0) {
        opts.maximumFractionDigits = rounding;
        opts.minimumFractionDigits = rounding;
      }

      // format number
      let locale = calc_settings.locale || 'en-US';
      let displayString = IC.parser.applyRawNumberFormatting(final_result, locale, opts);

      if (opts.notation == "scientific") {
        displayString = displayString.replace("E", "e");
      }

      result.final_result = displayString;
      return;
    }

    return;
  },

  // number formatting for log entry
  applyLogNumberFormatting: function (n) {
    return IC.parser.applyRawNumberFormatting(n, IC.envGetSettings()?.locale || "en-US", { maximumFractionDigits: 4 });
  },

  applyRawNumberFormatting: function (result, locale, opts = {}) {
    IC._nfCache ||= {};
    let key = JSON.stringify({ locale, opts });
    let nf = IC._nfCache[key] ||= new Intl.NumberFormat(locale, opts);
    let displayString = nf.format(result);
    return displayString;
  },

  recalculate: function (calc, includeEnv = false) {
    let rows = calc.rows.map((row) => row.text || "");

    var settings = {
      rounding: calc.rounding,
      // @ts-ignore
      locale: calc.locale || "en-US",
      id: calc.id
    };

    const { results, env } = IC.parser.executeRows(rows, settings);

    return includeEnv ? { results, env } : results;
  },
};

// IC.api - Proxy layer for external access to IC internals
IC.api = {
  // Calculation
  recalculate: function (calc, envOnly) {
    return IC.parser.recalculate(calc, envOnly);
  },

  // Formatting
  formatValue: function (value) {
    if (IC.isICObject(value)) {
      return IC.parser.formatResultAsSingleLine(value);
    }
    return value;
  },

  formatFinance: function (value) {
    return IC.parser.formatAsFinance(value);
  },

  formatNumber: function (value) {
    let n = (typeof value === "number") ? value : IC.makeNumber(value ?? 0);
    return n.toLocaleString(undefined, { maximumFractionDigits: (Math.abs(n) > 100 ? 2 : 6) });
  },

  // Parameters
  // Accepts either (row) object or legacy (calc, rowIndex) for backward compatibility
  getParameter: function (rowOrCalc, rowIndex) {
    return IC.parser.getRowParameter(rowOrCalc, rowIndex);
  },

  getParameterName: function (input) {
    return IC.parser.getRowParameterName(input);
  },

  getParameterNameForDisplay: function (input) {
    const name = IC.parser.getRowParameterName(input);
    return name ? IC.envNormalizeTitle(name) : name;
  },

  buildParameterText: function (text, value) {
    return IC.parser.buildRowParameterText(text, value);
  },

  optionToSnippet: function (opt) {
    return IC.parser.optionToSnippet(opt);
  },

  findParameterRow: function (calc, varName) {
    for (let rowIndex = 0; rowIndex < calc.rows.length; rowIndex++) {
      const row = calc.rows[rowIndex];
      const parameterName = IC.parser.getRowParameterName(row.text);
      if (parameterName && IC.canonicalName(parameterName) === varName) {
        return rowIndex;
      }
    }
    return null;
  },

  // Text Processing
  processSymbols: function (text) {
    return IC.parser.processSymbols(text);
  },

  appendBeforeComment: function (text, addition) {
    return IC.parser.appendTextBeforeComment(text, addition);
  },

  replaceBeforeComment: function (text, newText) {
    return IC.parser.replaceTextBeforeComment(text, newText);
  },

  extractAssignedSnippet: function (text) {
    return IC.parser.extractAssignedSnippet(text);
  },

  // Incrementing
  canIncrement: function (text) {
    return IC.parser.stringCanBeIncremented(text);
  },

  increment: function (text, direction) {
    return IC.parser.incrementNumberInString(text, direction);
  },

  // Utilities
  isWorkerLoaded: function () {
    return IC.WORKER_LOADED;
  }
};

const OPTION_DEBUG_INFO = window?.location?.href?.includes("debug") || window?.location?.href?.includes("src/grammar/") || false;

/* extra library functions */
const OPTION_MEMO_OFF = window?.location?.href?.includes("memo") ?? false;

const memoize = (func) => {
  const cache = new Map();
  return (...args) => {
    const argsKey = JSON.stringify(args);

    if (!cache.has(argsKey)) {
      cache.set(argsKey, func(...args));
    }

    return cache.get(argsKey);
  };
};

// Row-aware memoize that includes current row index in cache key
const memoizeRowAware = (func) => {
  const cache = new Map();
  return (...args) => {
    const argsKey = JSON.stringify([IC.info.rowIndex, ...args]);

    if (!cache.has(argsKey)) {
      cache.set(argsKey, func(...args));
    }

    return cache.get(argsKey);
  };
};

function memoizeWithNewObject(func) {
  const memoizedFunc = memoize(func);
  return function (...args) {
    const result = memoizedFunc.apply(this, args);
    if (typeof result === 'object' && result !== null) {
      try {
        return structuredClone(result);
      } catch (e) {
        // Fallback for objects containing functions
        return JSON.parse(JSON.stringify(result));
      }
    }
    return result;
  }
}

let OPTION_TIMING = false;
if (window && window?.location?.href?.includes("timing")) {
  OPTION_TIMING = true;
}

const ctime = (name) => {
  if (OPTION_TIMING) {
    console.time(name);
  }
};

const ctimeEnd = (name) => {
  if (OPTION_TIMING) {
    console.timeEnd(name);
  }
};

// UI Components metadata parsing
class UIMetadataParser {
  // Extract the leading JSON object from a comment, allowing trailing text like: { ... } some note
  static extractLeadingJSONObjectPos(s) {
    let i = s.indexOf('{');
    if (i < 0) return null;
    let depth = 0;
    let inStr = false;
    let esc = false;
    for (let j = i; j < s.length; j++) {
      const ch = s[j];
      if (inStr) {
        if (esc) { esc = false; continue; }
        if (ch === '\\') { esc = true; continue; }
        if (ch === '"') { inStr = false; }
        continue;
      }
      if (ch === '"') { inStr = true; continue; }
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) {
          return { start: i, end: j };
        }
      }
    }
    return null;
  }

  // Parse function-call syntax: checkbox(), dropdown([a,b,c]), slider(0, 100, step: 5)
  // Also handles presentation hints: hero(), icon(bank), theme(glass), summary(text), hidden()
  static parseFunctionCall(s) {
    // Match pattern: functionName(args) OR functionName (no parens for simple hints)
    // Support more complex names like paths: @public/demo/test.html(icon)
    const matchWithParens = s.match(/^([\w\.\/\-]+)\s*\((.*)\)\s*$/s);
    const matchNoParens = s.match(/^([\w\.\/\-]+)\s*$/);

    const match = matchWithParens || matchNoParens;
    if (!match) return null;

    let controlType = match[1].toLowerCase();
    const argsStr = matchWithParens ? match[2].trim() : '';

    // Map of recognized UI control types
    const uiControlTypes = ['checkbox', 'dropdown', 'radio', 'slider', 'range', 'date', 'time', 'datetime', 'multiselect', 'toggle', 'checklist', 'image-cards'];

    // Map of recognized presentation hint types
    const presentationTypes = [
      'hero', 'hidden', 'advanced', 'collapsed', 'icon', 'theme', 'summary', 'width',
      // Layout: row width fractions
      'half', 'third', 'wide',
      // Layout: section columns
      'col2',
      // Formatting
      'section', 'prefix', 'suffix', 'label', 'tooltip', 'note', 'split', 'format', 'tab'
    ];

    const isUIControl = uiControlTypes.includes(controlType.toLowerCase());

    const isPresentation = presentationTypes.includes(controlType.toLowerCase());

    if (!isUIControl && !isPresentation) return null;

    // Handle presentation hints

    if (isPresentation) {

      // Presentation hints are now additive - each sets its own flag

      // Primary display hints: hero, hidden, advanced (mutually exclusive main presentation)

      // Secondary hints: icon, theme, half, third, wide (can combine with primary)

      // Data hints: prefix, suffix, label, tooltip, note (add data, no display change)

      const primaryDisplayHints = ['hero', 'hidden', 'advanced'];

      const result = {};



      // Primary hints set the main presentation mode

      if (primaryDisplayHints.includes(controlType)) {

        result.presentation = controlType;
        result.mode = controlType;

      } else if (controlType !== 'collapsed') {

        // For other hints, use the type as the presentation name
        result.presentation = controlType;
        result.mode = controlType;

      }



      // All display hints set their own flag for data-* attribute rendering

      result[controlType] = true;



      if (controlType === 'icon' && argsStr) {

        // icon(bank) or icon("bank")

        result.icon = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'theme' && argsStr) {

        // theme(glass) or theme("glass")

        result.theme = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'summary' && argsStr) {

        // summary("With {cash} in the bank...") - preserve the template

        result.template = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'width' && argsStr) {

        // width(sm) or width("md") - Tailwind-style widths

        result.width = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'half') {

        // @half - 50% width row

        result.rowWidth = '1/2';

      } else if (controlType === 'third') {

        // @third - 33% width row

        result.rowWidth = '1/3';

      } else if (controlType === 'wide') {

        // @wide - full width row

        result.rowWidth = 'full';
      } else if (controlType === 'col2') {

        // @col2 - enable two-column layout for a section

        result.col2 = true;

      } else if (controlType === 'collapsed') {

        // @collapsed - collapse the section by default

        result.collapsed = true;

      } else if (controlType === 'tab') {
        // @tab - render section as a tab in present mode
        result.tab = true;
      } else if (controlType === 'layout' && argsStr) {
        // @layout(dense) - high density layout
        result.layout = argsStr.replace(/^["']|["']$/g, '');
      } else if (controlType === 'card') {
        // @card - wrap in card
        result.card = true;
      } else if (controlType === 'col' && argsStr) {
        // @col(1/4) - grid span
        const clean = argsStr.replace(/^["']|["']$/g, '');
        const parts = clean.split('/');
        if (parts.length === 2) {
          result.colSpan = parseInt(parts[0]);
          result.colTotal = parseInt(parts[1]);
        }
      } else if (controlType === 'span' && argsStr) {
        // @span(full)
        if (argsStr.includes('full')) {
          result.rowWidth = 'full';
        }
      } else if (controlType === 'format' && argsStr) {

        // @format(money) - force display format

        result.format = argsStr.replace(/^["']|["']$/g, '').trim();

      } else if (controlType === 'section' && argsStr) {

        // @section(Title) - section header

        result.section = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'prefix' && argsStr) {

        // @prefix($) - value prefix

        result.prefix = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'suffix' && argsStr) {

        // @suffix(mo) - value suffix

        result.suffix = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'label' && argsStr) {

        // @label(Custom Text) - custom label

        result.label = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'tooltip' && argsStr) {

        // @tooltip(Help text) - tooltip

        result.tooltip = argsStr.replace(/^["']|["']$/g, '');

      } else if (controlType === 'note' && argsStr) {

        // @note(Helper text with {variable} interpolation)

        // Parse into structured parts for rendering

        const noteText = argsStr.replace(/^["']|["']$/g, '');

        const parts = noteText.split(/(\{[^}]+\})/g).filter(p => p);

        result.note = parts.map(part => {

          if (part.startsWith('{') && part.endsWith('}')) {

            return { type: 'variable', value: part.slice(1, -1) };

          }

          return { type: 'text', value: part };

        });

      }



      return result;

    }

    // Build base result for UI controls
    const result = { ui: controlType };

    // Empty args → simple control like checkbox()
    if (!argsStr) return result;

    // Parse arguments using forgiving parser
    const parsedArgs = UIMetadataParser.parseFunctionArgs(argsStr);
    if (!parsedArgs) return result;

    // Handle specific UI control types
    if (controlType === 'slider' || controlType === 'range') {
      // slider can be: slider(min, max, step) or slider({min, max, step}) or slider(0, 100, step: 5)
      if (parsedArgs.isObject) {
        // Object style: slider({min: 0, max: 100, step: 1})
        if ('min' in parsedArgs.obj) result.min = parsedArgs.obj.min;
        if ('max' in parsedArgs.obj) result.max = parsedArgs.obj.max;
        if ('step' in parsedArgs.obj) result.step = parsedArgs.obj.step;
      } else {
        // Positional or named params
        const { positional, named } = parsedArgs;
        if (named.min !== undefined) result.min = named.min;
        else if (positional[0] !== undefined) result.min = positional[0];

        if (named.max !== undefined) result.max = named.max;
        else if (positional[1] !== undefined) result.max = positional[1];

        if (named.step !== undefined) result.step = named.step;
        else if (positional[2] !== undefined) result.step = positional[2];

        // Support dense flag: slider(0, 100, 1, dense) or slider(min:0, max:100, dense:true)
        if (named.dense === true || positional.includes('dense')) {
          result.dense = true;
        }
      }
    } else if (controlType === 'dropdown' || controlType === 'radio' || controlType === 'multiselect') {
      // Expect options array or object: dropdown([a,b,c]) or dropdown({Label: value})
      // OR comma-separated: dropdown(15, 20, 30)
      // OR named args: dropdown(Small=8, Medium=12, Large=16)
      if (Object.keys(parsedArgs.named).length > 0) {
        // Named args like @dropdown(Small=8, Medium=12, Large=16)
        // Convert to array of {label, value, raw} objects
        result.options = [];
        for (const [label, value] of Object.entries(parsedArgs.named)) {
          result.options.push({ label, value, raw: String(value) });
        }
      } else if (parsedArgs.isObject) {
        result.options = parsedArgs.obj;
      } else if (parsedArgs.positional.length > 0 && Array.isArray(parsedArgs.positional[0])) {
        result.options = parsedArgs.positional[0];
      } else if (parsedArgs.positional.length > 0 && typeof parsedArgs.positional[0] === 'object') {
        result.options = parsedArgs.positional[0];
      } else if (parsedArgs.positional.length > 0) {
        // Treat all positional args as options array
        result.options = parsedArgs.positional;
      }
    } else if (controlType === 'checklist') {
      // @checklist(Label1=value1, Label2=value2) - multi-select checkboxes that sum values
      // Parse: named args become {label: value} options
      if (Object.keys(parsedArgs.named).length > 0) {
        result.options = parsedArgs.named;
      } else if (parsedArgs.isObject) {
        result.options = parsedArgs.obj;
      }
    } else if (controlType === 'image-cards') {
      // @image-cards(Label=value|imageUrl, ...) - visual selection with images
      // Parse as named args where value contains pipe-separated value|url
      result.options = [];
      if (Object.keys(parsedArgs.named).length > 0) {
        for (const [label, valueStr] of Object.entries(parsedArgs.named)) {
          const str = String(valueStr);
          const pipeIdx = str.indexOf('|');
          if (pipeIdx > 0) {
            result.options.push({
              label,
              value: UIMetadataParser.parseArgValue(str.slice(0, pipeIdx).trim()),
              image: str.slice(pipeIdx + 1).trim()
            });
          } else {
            result.options.push({ label, value: valueStr, image: null });
          }
        }
      }
    }

    return result;
  }

  // Parse function arguments with forgiving syntax
  // Returns: { isObject: bool, obj: {...}, positional: [...], named: {...} }
  static parseFunctionArgs(argsStr) {
    // Check if it's a single object: {key: value, ...}
    if (argsStr.trim().startsWith('{')) {
      const parsed = UIMetadataParser.parseUIMetadata(argsStr);
      if (parsed && typeof parsed === 'object') {
        return { isObject: true, obj: parsed, positional: [], named: {} };
      }
    }

    // Parse comma-separated args with optional names
    const positional = [];
    const named = {};

    // Split by top-level commas (respecting brackets and braces)
    const args = UIMetadataParser.splitArgs(argsStr);

    for (const arg of args) {
      const colonIdx = arg.indexOf(':');
      if (colonIdx > 0) {
        // Named param: name: value
        const name = arg.slice(0, colonIdx).trim();
        const valueStr = arg.slice(colonIdx + 1).trim();
        named[name] = UIMetadataParser.parseArgValue(valueStr);
      } else {
        // Positional param
        positional.push(UIMetadataParser.parseArgValue(arg.trim()));
      }
    }

    return { isObject: false, obj: {}, positional, named };
  }

  // Split comma-separated arguments respecting brackets/braces
  static splitArgs(s) {
    const parts = [];
    let current = '';
    let depth = 0;
    let inStr = false;
    let esc = false;

    for (let i = 0; i < s.length; i++) {
      const ch = s[i];

      if (inStr) {
        current += ch;
        if (esc) { esc = false; continue; }
        if (ch === '\\') { esc = true; continue; }
        if (ch === '"' || ch === "'") { inStr = false; }
        continue;
      }

      if (ch === '"' || ch === "'") {
        inStr = true;
        current += ch;
        continue;
      }

      if (ch === '{' || ch === '[' || ch === '(') {
        depth++;
        current += ch;
      } else if (ch === '}' || ch === ']' || ch === ')') {
        depth--;
        current += ch;
      } else if (ch === ',' && depth === 0) {
        parts.push(current);
        current = '';
      } else {
        current += ch;
      }
    }

    if (current) parts.push(current);
    return parts.map(p => p.trim()).filter(p => p);
  }

  // Parse a single argument value (could be number, string, array, object, or identifier)
  static parseArgValue(s) {
    s = s.trim();
    if (!s) return undefined;

    // Try number
    if (/^-?\d+(\.\d+)?$/.test(s)) {
      return Number(s);
    }

    // Try boolean
    if (s === 'true') return true;
    if (s === 'false') return false;
    if (s === 'null') return null;

    // Try array: [a, b, c]
    if (s.startsWith('[') && s.endsWith(']')) {
      const inner = s.slice(1, -1);
      const elements = UIMetadataParser.splitArgs(inner);
      return elements.map(el => UIMetadataParser.parseArgValue(el));
    }

    // Try object: {key: value}
    if (s.startsWith('{') && s.endsWith('}')) {
      return UIMetadataParser.parseUIMetadata(s);
    }

    // Try quoted string - preserve quotes for raw form so write-back matches original format
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
      return s;  // Keep quotes to preserve user's formatting intent
    }

    // Bare identifier → keep as string (will be evaluated later via evalToPrimitive)
    return s;
  }

  // Parse UI metadata with multiple fallback strategies
  static parseUIMetadata(s) {
    // FIRST: Try function-call syntax: checkbox(), dropdown([a,b,c]), slider(0, 100, step: 5)
    const fnCallResult = UIMetadataParser.parseFunctionCall(s.trim());
    if (fnCallResult) return fnCallResult;

    // Skip IC.parseAndEval for UI metadata - it mangles object literals with unquoted keys
    // Go straight to JSON parsing with fallbacks

    // Try strict JSON
    try {
      return JSON.parse(s);
    } catch (_) { /* try relaxed */ }

    let t = s;
    // Quote bare keys: { ui: "checkbox" } -> { "ui": "checkbox" }
    t = t.replace(/([\{,]\s*)([A-Za-z_][A-Za-z0-9_-]*)\s*:/g, '$1"$2":');

    // Convert single-quoted strings to double-quoted
    t = t.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, function (_m, inner) {
      return '"' + inner.replace(/\\"/g, '"').replace(/"/g, '\\"') + '"';
    });

    // Quote bare string values (but not numbers, arrays, objects, or booleans)
    // Match: colon followed by optional whitespace, then bare identifier, then end/comma/whitespace
    t = t.replace(/:\s*([A-Za-z_][A-Za-z0-9_-]*)(\s*[,\}])/g, function (match, identifier, after) {
      // Don't quote true/false/null
      if (identifier === 'true' || identifier === 'false' || identifier === 'null') {
        return ': ' + identifier + after;
      }
      // Quote everything else
      return ': "' + identifier + '"' + after;
    });

    try {
      return JSON.parse(t);
    } catch (e) {
      // Final fallback: loose key:value parser allowing expression values
      return UIMetadataParser.looseParse(s);
    }
  }

  // Loose parser for key:value pairs with expression support
  static looseParse(block) {
    // Expect a leading { ... }
    let start = block.indexOf('{');
    let end = block.lastIndexOf('}');
    if (start < 0 || end <= start) return null;
    const inner = block.slice(start + 1, end);
    // Split by top-level commas
    const parts = [];
    let depth = 0, inStr = false, esc = false, cur = '';
    for (let i = 0; i < inner.length; i++) {
      const ch = inner[i];
      if (inStr) {
        cur += ch;
        if (esc) { esc = false; continue; }
        if (ch === '\\') { esc = true; continue; }
        if (ch === '"') inStr = false;
        continue;
      }
      if (ch === '"') { inStr = true; cur += ch; continue; }
      if (ch === '{' || ch === '[' || ch === '(') depth++;
      else if (ch === '}' || ch === ']' || ch === ')') depth--;
      if (ch === ',' && depth === 0) { parts.push(cur.trim()); cur = ''; continue; }
      cur += ch;
    }
    if (cur.trim()) parts.push(cur.trim());
    const obj = {};
    for (const p of parts) {
      // split on first colon at top level
      let d = 0, idx = -1; inStr = false; esc = false;
      for (let i = 0; i < p.length; i++) {
        const ch = p[i];
        if (inStr) {
          if (esc) { esc = false; continue; }
          if (ch === '\\') { esc = true; continue; }
          if (ch === '"') inStr = false;
          continue;
        }
        if (ch === '"') { inStr = true; continue; }
        if (ch === '{' || ch === '[' || ch === '(') d++;
        else if (ch === '}' || ch === ']' || ch === ')') d--;
        if (ch === ':' && d === 0) { idx = i; break; }
      }
      if (idx === -1) continue;
      const rawKey = p.slice(0, idx).trim();
      const rawVal = p.slice(idx + 1).trim();
      const key = rawKey.replace(/^['"]|['"]$/g, '');
      let val;
      // numeric literal
      if (/^-?\d+(?:\.\d+)?$/.test(rawVal)) {
        val = Number(rawVal);
      } else if ((rawVal.startsWith('"') && rawVal.endsWith('"')) || (rawVal.startsWith("'") && rawVal.endsWith("'"))) {
        val = rawVal.slice(1, -1);
      } else {
        // bare identifier or expression; keep as string for later evaluation if needed
        val = rawVal;
      }
      obj[key] = val;
    }
    return obj;
  }

  // Evaluate expression to primitive value
  static evalToPrimitive(raw) {
    try {
      if (typeof raw === 'string' && typeof IC?.parseAndEval === 'function') {
        const r = IC.parseAndEval(raw);
        if (r !== IC.NOPARSE && IC.isValidEvalResult(r)) {
          const v = IC.val(r);
          if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string') {
            // If the original was a bare identifier (no quotes) and it evaluated to 1,
            // it might be an undefined variable. We check if it's explicitly defined.
            // This is a heuristic to prevent auto-quoting from being "lost" if the engine
            // defaults unknown IDs to 1.
            const isQuoted = (raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"));
            if (!isQuoted && v === 1 && isNaN(Number(raw))) {
              return raw;
            }
            return v;
          }
        }
      }
    } catch (e) { /* ignore, fall through */ }
    return raw;
  }

  // Compute canonical raw form for option values
  static computeRawForm(rawVal, evalVal) {
    if (typeof rawVal === 'string') {
      // Keep raw value as-is for consistent matching with value_raw
      return rawVal;
    }
    // IC unit objects ({v, t, f}) - use the engine's formatter
    if (typeof rawVal === 'object' && rawVal !== null && IC?.isICObject?.(rawVal)) {
      return IC.parser.formatResultAsSingleLine(rawVal);
    }
    // Plain object or array - serialize back to IC expression syntax
    if (typeof rawVal === 'object' && rawVal !== null) {
      if (Array.isArray(rawVal)) {
        return '[' + rawVal.map(v => UIMetadataParser.computeRawForm(v, v)).join(', ') + ']';
      }
      const fmtKey = (k) => /^[A-Za-z_][A-Za-z0-9_]*$/.test(k) ? k : JSON.stringify(k);
      return '{' + Object.entries(rawVal).map(([k, v]) => `${fmtKey(k)}: ${UIMetadataParser.computeRawForm(v, v)}`).join(', ') + '}';
    }
    // Number or boolean - keep string representation
    return String(rawVal);
  }

  // Helper to strip quotes from a string for display purposes
  static stripQuotesForDisplay(s) {
    if (typeof s !== 'string') return s;
    const str = String(s);
    if ((str.startsWith('"') && str.endsWith('"')) || (str.startsWith("'") && str.endsWith("'"))) {
      return str.slice(1, -1);
    }
    return str;
  }

  // Normalize options array/object to standard format
  static normalizeOptions(opts) {
    if (!opts) return [];

    const finalizeOption = (rawVal, label = null) => {
      const isQuoted = typeof rawVal === 'string' && ((rawVal.startsWith('"') && rawVal.endsWith('"')) || (rawVal.startsWith("'") && rawVal.endsWith("'")));
      const evalVal = UIMetadataParser.evalToPrimitive(rawVal);

      // Auto-quoting detection: If the raw value is a string, not quoted,
      // AND the evaluated value matches the raw string, it was auto-quoted.
      // Exclude object/array expressions ({...} and [...]) — they are code, not bare identifiers.
      const looksLikeExpression = typeof rawVal === 'string' && /^\s*[\{\[]/.test(rawVal);
      const autoQuoted = typeof rawVal === 'string' && !isQuoted && !looksLikeExpression && evalVal === rawVal;

      return {
        label: label != null ? String(label) : UIMetadataParser.stripQuotesForDisplay(rawVal),
        value: evalVal,
        raw: UIMetadataParser.computeRawForm(rawVal, evalVal),
        autoQuoted: autoQuoted || undefined
      };
    };

    if (Array.isArray(opts)) {
      return opts.map((opt) => {
        if (typeof opt === 'object' && opt !== null && Object.prototype.hasOwnProperty.call(opt, 'value')) {
          return finalizeOption(opt.value, opt.label);
        }
        // Check for Label=value format (e.g., "Small=8", "Medium=12")
        if (typeof opt === 'string' && opt.includes('=')) {
          const eqIdx = opt.indexOf('=');
          const label = opt.slice(0, eqIdx).trim();
          const rawVal = opt.slice(eqIdx + 1).trim();
          return finalizeOption(rawVal, label);
        }
        return finalizeOption(opt);
      });
    }
    if (typeof opts === 'object') {
      return Object.entries(opts).map(([label, rawVal]) => {
        return finalizeOption(rawVal, label);
      });
    }
    return [];
  }

  /**
   * Extract all presentation hints from a string, handling balanced parentheses.
   * Returns array of { match, start, end, name, args } objects.
   *
   * This properly handles nested parentheses like @label(Waste Factor (10%))
   */
  static extractPresentationHints(s) {
    const hints = [];
    // Match @name or @w-size patterns
    const hintStartPattern = /@(w-(xs|sm|md|lg|xl|full|1\/2|1\/3|2\/3)|[\w\.\/\-]+)/g;
    let match;

    while ((match = hintStartPattern.exec(s)) !== null) {
      const start = match.index;
      const name = match[1];
      let end = start + match[0].length;
      let args = '';

      // Check if followed by opening paren (with optional whitespace)
      let parenStart = end;
      while (parenStart < s.length && /\s/.test(s[parenStart])) {
        parenStart++;
      }

      if (s[parenStart] === '(') {
        // Find matching closing paren with balanced paren matching
        let depth = 1;
        let endParen = -1;
        for (let i = parenStart + 1; i < s.length; i++) {
          if (s[i] === '(') depth++;
          else if (s[i] === ')') {
            depth--;
            if (depth === 0) {
              endParen = i;
              break;
            }
          }
        }

        if (endParen > 0) {
          args = s.substring(parenStart, endParen + 1).trim();
          end = endParen + 1;
        }
      }

      hints.push({
        match: s.substring(start, end),
        start,
        end: end - 1,
        name,
        args
      });
    }

    return hints;
  }

  /**
   * Strip all presentation hints from a string, handling balanced parentheses.
   * Returns the string with all @hints removed.
   */
  static stripPresentationHints(s) {
    const hints = UIMetadataParser.extractPresentationHints(s);
    // Remove hints in reverse order to preserve positions
    let result = s;
    for (let i = hints.length - 1; i >= 0; i--) {
      const hint = hints[i];
      result = result.substring(0, hint.start) + result.substring(hint.end + 1);
    }
    return result.replace(/\s+/g, ' ').trim();
  }

  /**
   * Find a UI function call anywhere in the comment string
   * Returns { match, start, end, funcName } or null
   *
   * This is smarter than checking if comment STARTS with a function call -
   * it finds slider(), checkbox(), etc. anywhere after decorators
   */
  static findUIFunctionCall(s) {
    // Match UI function name followed by opening paren - anywhere in string
    // Uses word boundary BUT we need to skip @-prefixed versions (those are decorators)
    const uiControlPattern = /\b(checkbox|dropdown|radio|slider|range|date|time|datetime|multiselect|toggle)\s*\(/gi;
    let match;

    while ((match = uiControlPattern.exec(s)) !== null) {
      // Skip if preceded by @ (that's the decorator syntax, handled separately)
      if (match.index > 0 && s[match.index - 1] === '@') {
        continue;
      }

      const funcName = match[1];
      const openParenPos = match.index + match[0].length - 1;

      // Find matching closing paren
      let depth = 1;
      let endParen = -1;
      for (let i = openParenPos + 1; i < s.length; i++) {
        if (s[i] === '(') depth++;
        else if (s[i] === ')') {
          depth--;
          if (depth === 0) {
            endParen = i;
            break;
          }
        }
      }

      if (endParen > 0) {
        return {
          match: s.substring(match.index, endParen + 1),
          start: match.index,
          end: endParen,
          funcName
        };
      }
    }
    return null;
  }

  // Parse and normalize UI metadata from a comment string
  static parse(rawComment) {
    const result = {
      ui: null,
      ui_comment: null,
      presentation: null,  // NEW: presentation hints (hero, icon, hidden, summary, theme)
      cleanedComment: null  // Comment with all recognized @decorators and UI functions stripped
    };

    const trimmed = rawComment.trim();

    // Check for UI control function-call syntax ANYWHERE in the comment
    // This finds slider(), checkbox(), etc. even after @decorators
    const uiFunctionMatch = UIMetadataParser.findUIFunctionCall(trimmed);
    const isUIFunctionCall = uiFunctionMatch !== null;

    // Check for presentation hint syntax with @ prefix: @hero, @icon(bank), @theme(glass), @summary(...), @hidden, @width(sm)
    // Can appear anywhere in the comment, possibly after UI controls or other text
    // Supports multiple hints: "slider(0,100) @icon(bank) @hero"
    // Also supports shorthand @w-sm, @w-md, @w-lg, @w-xl, @w-full
    // Row widths: @half, @third, @wide (or @w-1/2, @w-1/3, @w-2/3, @w-full)
    // Formatting: @section(Title), @tab, @format(money), @prefix($), @suffix(mo), @label(Custom Text), @tooltip(Help text), @note(Helper text)
    // UI controls can also use @ prefix: @slider(0,100), @checkbox, @toggle, @dropdown(...), etc.
    // NOTE: Uses balanced parenthesis matching to support nested parens like @label(Waste Factor (10%))
    const extractedHints = UIMetadataParser.extractPresentationHints(trimmed);
    const presentationHints = [];
    let commentWithoutHints = trimmed;

    for (const hintMatch of extractedHints) {
      const hintName = hintMatch.name;

      // Check if this is the shorthand @w-size syntax
      if (hintName.startsWith('w-')) {
        const sizeVal = hintName.substring(2); // Remove 'w-' prefix
        // Fraction widths (@w-1/2, @w-1/3, @w-2/3, @w-full) are rowWidth
        // Named sizes (@w-sm, @w-md, etc.) are calc width
        if (sizeVal.includes('/') || sizeVal === 'full') {
          presentationHints.push({ presentation: 'width', mode: 'width', rowWidth: sizeVal });
        } else {
          presentationHints.push({ presentation: 'width', mode: 'width', width: sizeVal });
        }
        commentWithoutHints = commentWithoutHints.replace(hintMatch.match, '').trim();
        continue;
      }

      const hintArgs = hintMatch.args;
      const hintToParse = hintArgs ? hintName + hintArgs : hintName;
      const parsedHint = UIMetadataParser.parseFunctionCall(hintToParse);

      if (parsedHint) {
        if ('presentation' in parsedHint || parsedHint.collapsed) {
          presentationHints.push(parsedHint);
        } else if (parsedHint.ui) {
          // UI control found via @ prefix (e.g., @slider, @checkbox, @toggle)
          // Store in result.ui consistently as an object
          // Normalize options for dropdown/radio/multiselect controls
          if (parsedHint.options !== undefined) {
            parsedHint.options = UIMetadataParser.normalizeOptions(parsedHint.options);
          }
          result.ui = parsedHint;
        }
      }

      // Remove this hint from the comment
      commentWithoutHints = commentWithoutHints.replace(hintMatch.match, '').trim();
    }

    // If we found presentation hints, merge them into a single presentation object
    if (presentationHints.length > 0) {
      result.presentation = presentationHints.reduce((acc, hint) => {
        // Filter out null/undefined values
        const filtered = Object.fromEntries(
          Object.entries(hint).filter(([_, v]) => v !== null && v !== undefined)
        );
        // Don't let secondary hints overwrite a primary presentation type
        // (e.g., @icon shouldn't overwrite @hero)
        const primaryDisplayHints = ['hero', 'hidden', 'advanced', 'section'];
        if (acc.presentation && filtered.presentation && acc.presentation !== filtered.presentation) {
          if (primaryDisplayHints.includes(acc.presentation) && !primaryDisplayHints.includes(filtered.presentation)) {
            // Keep the existing primary presentation and mode, merge other properties
            const { presentation, mode, ...rest } = filtered;
            return { ...acc, ...rest };
          }
        }
        // Preserve mode from primary hints - only if acc.mode is from a primary hint
        // and the new hint's mode is not primary
        if (acc.mode && filtered.mode &&
          primaryDisplayHints.includes(acc.mode) &&
          !primaryDisplayHints.includes(filtered.mode)) {
          const { mode, ...rest } = filtered;
          return { ...acc, ...rest };
        }
        return { ...acc, ...filtered };
      }, {});

      // Store remaining comment text (after removing @ hints)
      if (commentWithoutHints && commentWithoutHints !== trimmed) {
        result.ui_comment = commentWithoutHints || null;
      }
    }

    // Note: presentation is finalized later by finalizeResult()

    // Check for JSON syntax
    const isJSONSyntax = trimmed.startsWith("{") && (
      rawComment.includes("\"ui\"") || rawComment.includes("\"type\"") || /\b(ui|type)\s*:/i.test(rawComment)
    );

    const looksLikeUIMetadata = isUIFunctionCall || isJSONSyntax;

    // Set cleanedComment to what remains after stripping @decorators
    // (will be further updated below if UI function calls are stripped)
    // Note: Keep plain comments (no decorators) so they display as implicit notes
    // IMPORTANT: Use ?? instead of || to preserve empty string (means "decorators-only comment")
    // Empty string prevents fallback to raw comment at display.comment assignment
    result.cleanedComment = commentWithoutHints?.trim() ?? null;

    // If no UI controls to parse, return (but keep any presentation hints we found)
    if (!looksLikeUIMetadata) {
      return result;
    }

    // For function-call syntax, parse the whole thing; for JSON, extract the leading object
    let blockToParse = trimmed;
    let pos = null;

    if (isJSONSyntax && !isUIFunctionCall) {
      pos = UIMetadataParser.extractLeadingJSONObjectPos(rawComment);
      blockToParse = pos ? rawComment.substring(pos.start, pos.end + 1) : rawComment;
    } else if (uiFunctionMatch) {
      // Use the match we already found - it works for UI calls anywhere in the string
      blockToParse = uiFunctionMatch.match;
      // Collect text before and after the UI function as trailing comment
      const beforeUI = trimmed.substring(0, uiFunctionMatch.start).trim();
      const afterUI = trimmed.substring(uiFunctionMatch.end + 1).trim();
      // Combine non-empty parts, stripping @decorators already handled
      const trailParts = [beforeUI, afterUI].filter(p => p);
      if (trailParts.length > 0) {
        const strippedTrail = UIMetadataParser.stripPresentationHints(trailParts.join(' '));
        result.ui_comment = strippedTrail || null;
      }
    }

    const parsedRaw = UIMetadataParser.parseUIMetadata(blockToParse);
    if (parsedRaw && (parsedRaw.ui || parsedRaw.type)) {
      const uiType = parsedRaw.ui || parsedRaw.type;
      let normalized = { ui: uiType };
      // Copy through supported fields with evaluation as needed
      if (parsedRaw.min !== undefined) normalized.min = UIMetadataParser.evalToPrimitive(parsedRaw.min);
      if (parsedRaw.max !== undefined) normalized.max = UIMetadataParser.evalToPrimitive(parsedRaw.max);
      if (parsedRaw.step !== undefined) normalized.step = UIMetadataParser.evalToPrimitive(parsedRaw.step);
      if (parsedRaw.options !== undefined) normalized.options = UIMetadataParser.normalizeOptions(parsedRaw.options);
      if (parsedRaw.label !== undefined) normalized.label = parsedRaw.label;

      result.ui = normalized;
      // trailing comment after metadata block, preserved for UI display
      if (pos) {
        const trail = rawComment.substring(pos.end + 1).trim();
        result.ui_comment = trail || null;
      }
      // When a UI function is successfully parsed, cleanedComment should be:
      // - The ui_comment (text after UI function) if any, with @decorators stripped
      // - Or null if nothing remains
      // Note: ui_comment may contain @decorators that were part of the trailing text
      // Strip those from cleanedComment
      const trailingText = result.ui_comment || '';
      if (trailingText) {
        const strippedTrail = UIMetadataParser.stripPresentationHints(trailingText);
        // Use ?? to preserve empty string (prevents fallback to raw comment)
        result.cleanedComment = strippedTrail ?? null;
      } else {
        result.cleanedComment = null;
      }
    }

    return result;
  }

  // Check if a comment looks like UI metadata
  static looksLikeUIMetadata(rawComment) {
    return rawComment.trim().startsWith("{") && (
      rawComment.includes("\"ui\"") || rawComment.includes("\"type\"") || /\b(ui|type)\s*:/i.test(rawComment)
    );
  }

  // Find which option is selected, using InstaCalc's own comparison logic
  // Returns the index of the selected option, or 0 if no match
  static getSelectedOptionIndex(options, result) {
    if (!options || !Array.isArray(options)) return 0;

    const writeback = result?.ui?.writeback;
    const valueRaw = writeback?.rawText;
    const valueEval = writeback?.value ?? result?.output?.value;

    if (IC.debug?.ui) {
      console.log('[getSelectedOptionIndex] valueRaw:', valueRaw, 'valueEval:', valueEval);
      console.log('[getSelectedOptionIndex] options:', options.map(o => ({ label: o.label, raw: o.raw, value: o.value })));
    }

    // TWO-PASS STRATEGY (critical for units like F/C which evaluate to same number)

    // PASS 1: Try raw string match across ALL options first
    // This is ESSENTIAL for units like F/C which have same numeric value but different meanings
    if (valueRaw !== undefined) {
      const rawMatchIndex = options.findIndex((opt, idx) => {
        if (opt.raw) {
          const rawMatch = valueRaw === opt.raw;
          if (IC.debug?.ui) {
            console.log('[getSelectedOptionIndex] PASS 1 opt[' + idx + '] raw match:', rawMatch, 'valueRaw:', valueRaw, 'opt.raw:', opt.raw);
          }
          return rawMatch;
        }
        return false;
      });

      if (rawMatchIndex >= 0) {
        if (IC.debug?.ui) {
          console.log('[getSelectedOptionIndex] found raw match at index:', rawMatchIndex);
        }
        return rawMatchIndex;
      }
    }

    // PASS 2: Only if no raw match found, try eval matching
    // This handles numbers, booleans, etc. but won't work correctly for equivalent units
    if (valueEval !== undefined) {
      const evalMatchIndex = options.findIndex((opt, idx) => {
        if (opt.value !== undefined) {
          const optVal = IC.val(opt.value);
          const curVal = IC.val(valueEval);
          const evalMatch = (optVal == curVal);
          if (IC.debug?.ui) {
            console.log('[getSelectedOptionIndex] PASS 2 opt[' + idx + '] eval match:', evalMatch, 'optVal:', optVal, 'curVal:', curVal);
          }
          return evalMatch;
        }
        return false;
      });

      if (evalMatchIndex >= 0) {
        if (IC.debug?.ui) {
          console.log('[getSelectedOptionIndex] found eval match at index:', evalMatchIndex);
        }
        return evalMatchIndex;
      }
    }

    if (IC.debug?.ui) {
      console.log('[getSelectedOptionIndex] no match found, defaulting to 0');
    }
    return 0;
  }
}

// Expose UIMetadataParser for testing
IC.parser.UIMetadataParser = UIMetadataParser;

class LRUCache {
  constructor(capacity) {
    this.cache = new Map();
    this.capacity = capacity;
  }

  clear() {
    this.cache = new Map();
  }

  has(key) {
    return this.cache.has(key);
  }

  get(key) {
    if (!this.cache.has(key)) return null;

    let val = this.cache.get(key);

    this.cache.delete(key);
    this.cache.set(key, val);

    return val;
  }

  set(key, value) {
    this.cache.delete(key);

    if (this.cache.size === this.capacity) {
      this.cache.delete(this.cache.keys().next().value);
      this.cache.set(key, value);
    } else {
      this.cache.set(key, value);
    }
  }
}

IC.CACHE = new LRUCache(10000);

// @ts-ignore
if (typeof INSTACALC_GRAMMAR !== "undefined") {
  if (LIB.Calc) {
    LIB.Calc.pub();
  }
}
class TrieNode {
  constructor(value = null) {
    this.children = new Map();
    this.isEndOfWord = false;
    this.value = value;
  }
}

class Trie {
  constructor() {
    this.root = new TrieNode();
  }

  insert(word) {
    let currentNode = this.root;

    for (let i = 0; i < word.length; i++) {
      const char = word[i];
      if (!currentNode.children.has(char)) {
        currentNode.children.set(char, new TrieNode());
      }
      currentNode = currentNode.children.get(char);
    }

    currentNode.isEndOfWord = true;
  }

  search(haystack) {
    const matches = [];

    for (let i = 0; i < haystack.length; i++) {
      let currentNode = this.root;
      for (let j = i; j < haystack.length; j++) {
        const char = haystack[j];
        if (currentNode.children.has(char)) {
          currentNode = currentNode.children.get(char);
          if (currentNode.isEndOfWord) {
            const match = haystack.slice(i, j + 1);
            matches.push(match);
          }
        } else {
          break;
        }
      }
    }

    return matches;
  }

  // New methods
  insertKeyValue(key, value, caseSensitive = true) {
    let currentNode = this.root;
    if (!caseSensitive) {
      key = key.toLowerCase();
    }

    for (let i = 0; i < key.length; i++) {
      const char = key[i];
      if (!currentNode.children.has(char)) {
        currentNode.children.set(char, new TrieNode());
      }
      currentNode = currentNode.children.get(char);
    }

    currentNode.isEndOfWord = true;
    currentNode.value = value;
  }

  replaceInText(text, caseSensitive = true) {
    let replacedText = '';

    for (let i = 0; i < text.length;) {
      let currentNode = this.root;
      let found = null;
      let j = i;

      while (j < text.length && currentNode.children.has((caseSensitive) ? text[j] : text[j].toLowerCase())) {
        currentNode = currentNode.children.get((caseSensitive) ? text[j] : text[j].toLowerCase());
        j++;

        if (currentNode.isEndOfWord) {
          found = { index: j, value: currentNode.value };
        }
      }

      if (found) {
        replacedText += found.value;
        i = found.index;
      } else {
        replacedText += text[i];
        i++;
      }
    }

    return replacedText;
  }
}

const SETUP_TRIES = function () {
  CONFIG.L2_REPLACEMENTS_STRING_ONLY_TRIE = new Trie();
  for (const [find, replace] of Object.entries(CONFIG.L2_REPLACEMENTS_STRINGS)) {
    CONFIG.L2_REPLACEMENTS_STRING_ONLY_TRIE.insertKeyValue(find, replace, false);
  }

  CONFIG.PLAIN_ENGLISH_OPS_TRIE = new Trie();
  for (const [find, replace] of Object.entries(CONFIG.PLAIN_ENGLISH_OPS)) {
    CONFIG.PLAIN_ENGLISH_OPS_TRIE.insert(find);
  }

  CONFIG.PREFIX_DICT_TRIE = new Trie();
  for (const [find, replace] of Object.entries(CONFIG.PREFIX_DICT)) {
    CONFIG.PREFIX_DICT_TRIE.insert(find);
  }
}

SETUP_TRIES();

var ICLib = {};

ICLib.isRomanNumeral = function (s) {
  // Roman numeral regex pattern
  const romanRegex = /^(M{0,3})(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;

  // Return true if the string matches the regex, false otherwise
  return romanRegex.test(s);
}

/**
 * Convert a decimal number to a Roman numeral
 * @param {number} num - The decimal number to convert
 * @returns {string} The Roman numeral representation of the input number
 */
ICLib.decimalToRoman = function (num) {
  /**
   * Map of decimal values to their corresponding Roman numerals
   * @type {{[key: number]: string}}
   */
  const romanMap = {
    1000000: 'M̄', 900000: 'C̄M̄', 500000: 'D̄', 400000: 'C̄D̄', 100000: 'C̄',
    90000: 'X̄C̄', 50000: 'L̄', 40000: 'X̄L̄', 10000: 'X̄', 9000: 'ĪX̄',
    5000: 'V̄', 4000: 'ĪV̄', 1000: 'M', 900: 'CM', 500: 'D', 400: 'CD', 100: 'C',
    90: 'XC', 50: 'L', 40: 'XL', 10: 'X', 9: 'IX',
    5: 'V', 4: 'IV', 1: 'I'
  };

  let romanNumeral = '';
  const decimalValues = Object.keys(romanMap).sort((a, b) => Number(b) - Number(a)); // sorted in descending order

  for (let i = 0; i < decimalValues.length; i++) {
    let currentNum = Number(decimalValues[i]);
    while (num >= currentNum) {
      num -= currentNum;
      romanNumeral += romanMap[decimalValues[i]];
    }
  }

  return romanNumeral;
};

ICLib.romanToDecimal = function (roman) {
  const romanNumeralMap = {
    'M̄': 1000000,
    'D̄': 500000,
    'C̄': 100000,
    'L̄': 50000,
    'X̄': 10000,
    'V̄': 5000,
    'Ī': 1000,
    'M': 1000,
    'D': 500,
    'C': 100,
    'L': 50,
    'X': 10,
    'V': 5,
    'I': 1
  };

  let total = 0;
  let current, currentValue;
  let next, nextValue;

  roman = roman.toUpperCase();

  for (let i = 0; i < roman.length; i++) {
    current = roman[i];
    currentValue = romanNumeralMap[current];

    // If the current character is not a valid Roman numeral, return null
    if (currentValue === undefined) {
      return null;
    }

    next = roman[i + 1];
    nextValue = romanNumeralMap[next];

    if (currentValue < nextValue) {
      total -= currentValue;
    } else {
      total += currentValue;
    }
  }

  return total;
}

ICLib.roundToDecimal = function (number, decimalPlaces) {
  const factor = Math.pow(10, decimalPlaces);
  return Math.round(number * factor) / factor;
};

ICLib.factorial = function (n) {

  if (n > 170) {
    return Infinity;
  }

  // check if n is not a number or negative
  if (typeof n != "number" || n < 0) {
    return undefined;
  }

  if (n === 0 || n === 1) {
    return 1;
  }

  this.factcache ||= [];

  if (this.factcache[n] > 0) {
    return this.factcache[n];
  }

  let result = 1;
  for (let i = 1; i <= n; i++) {
    result *= i;
    this.factcache[i] = result;
  }

  return result;
};

ICLib.escapeRegExpString = function (string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

ICLib.paramRate = function (params) {
  let rate = 0;
  params.forEach((param, i) => {
    if (rate || param == null) return;

    let val = IC.val(param);
    if (typeof val === "number" && val > 0 && val < 1.0) {
      rate = val;
      params[i] = null;
    }
  });
  return rate;
};

ICLib.paramMonths = function (params) {
  let months = 0;
  params.forEach((param, i) => {
    if (months || param == null) return;

    // swap y for year
    if (param?.t?.y) {
      param.t.year = param.t.y;
      delete param.t.y;
      IC.log(`treating y as year`);
    }

    if (IC.hasType(param) && IC.typeHasUnitInCategory(param.t, "second")) {
      months = IC.val(IC.semanticConvert(param, "month"));
      params[i] = null;
    }
  });

  // if months not found, find the first number > 2 and assume it is years 
  if (!months) {
    for (let i = 0; i < params.length; i++) {
      let val = IC.val(params[i]);
      if (val > 2) {
        months = val * 12;
        params[i] = null;
        break;
      }
    }
  }

  return months;
};

ICLib.paramPaymentInfo = function (params) {
  let payment = 0;
  let paymentsPerYear = 12; // default to monthly
  let isLumpSum = false;
  let paymentUnit = null;

  // Check for payment (value with time-based unit in denominator)
  params.forEach((param, i) => {
    if (payment || param == null) return;

    if (IC.hasType(param)) {
      let typeObj = param.t;

      // single-letter time shorthands that aren't automatically a unit
      // TODO: extract to unit function to convert time shorthands to longer equivalents?
      const typeMapping = {
        d: 'day',
        w: 'week',
        m: 'month',
        y: 'year',
      };

      Object.keys(typeMapping).forEach(key => {
        if (typeObj[key]) {
          typeObj[typeMapping[key]] = typeObj[key];
          delete typeObj[key];
          IC.log(`treating ${key} as ${typeMapping[key]}`);
        }
      });

      let hasTimeUnit = Object.keys(typeObj).some(unit =>
        IC.typeHasUnitInCategory({ [unit]: typeObj[unit] }, "second") && typeObj[unit] < 0
      );

      if (hasTimeUnit) {
        let timeUnit = Object.keys(typeObj).find(unit =>
          IC.typeHasUnitInCategory({ [unit]: typeObj[unit] }, "second") && typeObj[unit] < 0
        );
        let unitScale = ICUnit.getUnitScale(timeUnit);
        paymentsPerYear = ICUnit.getUnitScale("year") / unitScale;
        payment = IC.val(param);

        // Determine the payment unit (currency)
        paymentUnit = Object.keys(typeObj).find(unit => typeObj[unit] > 0);

        params[i] = null;
      }

      // regular payment type 
      paymentUnit = Object.keys(typeObj).find(unit => typeObj[unit] > 0);
      payment = IC.val(param);
      params[i] = null;
    } else {
      let val = IC.val(param);
      if (val > 100) { // check for lump sum
        payment = val;
        isLumpSum = true;
        params[i] = null;
      }
    }
  });
  return { payment, paymentUnit, paymentsPerYear, isLumpSum };
};

// automatically re-orders parameters to match the function signature
ICLib.loan = function (a, b, c) {
  let params = [a, b, c];

  let principal;
  let rate = ICLib.paramRate(params);
  let months = ICLib.paramMonths(params);
  let paymentObj = ICLib.paramPaymentInfo(params);
  principal = IC.val(paymentObj.payment);

  let payment = Math.round(Math.abs(LIB.InstaCalc.prototype.fn.PMT(rate / 12, months, principal)));

  if (params.some(p => p !== null)) {
    let msg = "";
    let missingVars = [];

    !rate && missingVars.push("rate");
    !months && missingVars.push("time");
    !principal && missingVars.push("principal");

    let missingVarsStr = missingVars.join(", ");

    msg += `missing ${missingVarsStr}, try: loan 400k for 10 years at 5%`;
    IC.log(msg);
    return null;
  }

  // calculate interest and principal for total 
  let totalPaid = payment * months;
  let totalInterest = Math.round(totalPaid - principal);
  IC.log(`total cost: ${IC.parser.formatAsFinance(totalPaid)} = ${IC.parser.formatAsFinance(totalInterest)} (interest) + ${IC.parser.formatAsFinance(principal)} (principal)`);

  return IC.makeICObject(payment, "/month");
};


ICLib.interest = function (a, b, c) {
  let params = [a, b, c];

  let principal;
  let rate = ICLib.paramRate(params);
  let months = ICLib.paramMonths(params);
  let paymentObj = ICLib.paramPaymentInfo(params);
  principal = IC.val(paymentObj.payment);

  // Calculate the monthly payment using the PMT function
  let monthlyPayment = Math.abs(LIB.InstaCalc.prototype.fn.PMT(rate / 12, months, principal));

  // Total amount paid over the loan period
  let totalPaid = monthlyPayment * months;

  // Total interest paid
  let totalInterest = Math.round(totalPaid - principal);
  IC.log(`total cost: ${IC.parser.formatAsFinance(totalPaid)} = ${IC.parser.formatAsFinance(totalPaid - principal)} (interest) + ${IC.parser.formatAsFinance(principal)} (principal)`);
  return totalInterest;
};

ICLib.presentValue = function (a, b, c) {
  let params = [a, b, c];
  let futureValue;
  let rate = ICLib.paramRate(params);
  let months = ICLib.paramMonths(params);
  let paymentObj = ICLib.paramPaymentInfo(params);
  futureValue = IC.val(paymentObj.payment);

  // Calculate the present value using the PV function
  let presentValue = LIB.InstaCalc.prototype.fn.PV(rate / 12, months, 0, -futureValue);

  // Round the present value to two decimal places
  presentValue = Math.round(presentValue);

  // Calculate the difference (potential gain)
  let potentialGain = futureValue - presentValue;

  IC.log(`present value: ${IC.parser.formatAsFinance(presentValue)} | future value: ${IC.parser.formatAsFinance(futureValue)} | gain: ${IC.parser.formatAsFinance(potentialGain)}`);

  return presentValue;
};

ICLib.totalPayment = function (a, b, c) {
  let params = [a, b, c];

  let principal;
  let rate = ICLib.paramRate(params);
  let months = ICLib.paramMonths(params);
  let paymentObj = ICLib.paramPaymentInfo(params);
  principal = IC.val(paymentObj.payment);

  // Calculate the monthly payment using the PMT function
  let monthlyPayment = Math.abs(LIB.InstaCalc.prototype.fn.PMT(rate / 12, months, principal));

  // Total amount paid over the loan period
  let totalPaid = Math.round(monthlyPayment * months);

  IC.log(`total cost: ${IC.parser.formatAsFinance(totalPaid)} = ${IC.parser.formatAsFinance(totalPaid - principal)} (interest) + ${IC.parser.formatAsFinance(principal)} (principal)`);
  return totalPaid;
};

ICLib.savingsGoal = function (a, b, c) {
  let params = [a, b, c];

  let paymentObj = ICLib.paramPaymentInfo(params);
  let futureValue = IC.val(paymentObj.payment);
  let annualRate = ICLib.paramRate(params); // annual interest rate
  let months = ICLib.paramMonths(params); // time period in months

  // Convert annual interest rate to monthly interest rate
  let monthlyRate = annualRate / 12;

  // Calculate the required monthly savings using the PMT function
  // Note: The PMT function typically calculates payments for loans (which are negative), 
  // so we use a negative future value to get a positive savings amount.
  let monthlySavings = Math.abs(LIB.InstaCalc.prototype.fn.PMT(monthlyRate, months, 0, -futureValue));

  monthlySavings = Math.round(monthlySavings);

  IC.log(`monthly savings: ${IC.parser.formatAsFinance(monthlySavings)} to reach ${IC.parser.formatAsFinance(futureValue)} in ${months} months at ${annualRate * 100}% annual interest rate`);
  return IC.makeICObject(monthlySavings, { month: -1 });
};

ICLib.invest = function (a, b, c) {
  let params = [a, b, c];

  let rate = ICLib.paramRate(params);

  let paymentObj = ICLib.paramPaymentInfo(params);
  let payment = paymentObj.payment;
  let paymentsPerYear = paymentObj.paymentsPerYear;
  let paymentUnit = paymentObj.paymentUnit;
  let isLumpSum = paymentObj.isLumpSum;

  let months = ICLib.paramMonths(params);

  if (params.some(p => p !== null)) {
    let msg = "";
    let missingVars = [];

    !rate && missingVars.push("rate");
    !months && missingVars.push("time");
    !payment && missingVars.push("payment");

    let missingVarsStr = missingVars.join(", ");

    msg += `missing ${missingVarsStr}, try: invest 1000/year for 10 years at 5%`;
    IC.log(msg);
    return null;
  }

  // Calculate future value
  let annualRate = rate;
  let periodicRate = annualRate / paymentsPerYear;
  let totalPeriods = months * (paymentsPerYear / 12);
  let futureValue = Math.round(LIB.InstaCalc.prototype.fn.FV(periodicRate, totalPeriods, -payment, 0, 1));
  let totalContribution = payment * totalPeriods;

  if (isLumpSum) {
    futureValue = Math.round(payment * Math.pow(1 + annualRate, months / 12));
    totalContribution = payment;
  }

  const totalGrowth = futureValue - totalContribution;

  IC.log(`future value: ${IC.parser.formatAsFinance(futureValue)} = ${IC.parser.formatAsFinance(totalGrowth)} (gain) + ${IC.parser.formatAsFinance(totalContribution)} (deposits)`);

  // Return the result with the same unit as the input payment
  if (paymentUnit) {
    return IC.makeICObject(futureValue, paymentUnit);
  } else {
    return futureValue;
  }
};

/**
 * LogManager - Helper class for structured logging
 *
 * Provides a fluent API for logging with type-safe entries and automatic
 * processing into display strings. Can be used standalone for testing
 * or integrated with IC.log() for parser integration.
 *
 * Usage:
 *   const logger = new LogManager();
 *   logger.unit('USD', 'currency').unit('EUR', 'currency').convert('USD', 'EUR');
 *   const output = logger.process();
 *
 * Log Types (priority order for display):
 *   warning  - Errors/issues (shown first)
 *   stock    - Stock price info
 *   unit     - Unit info (currency/custom/standard)
 *   convert  - Conversion messages
 *   function - Function calls
 *   info     - Informational messages
 *   tip      - Helpful hints (shown last)
 *   debug    - Hidden unless debug mode
 */
class LogManager {
  constructor(options = {}) {
    this.entries = [];
    this.debugMode = options.debugMode || false;
    this.config = options.config || {};
  }

  // ========== Logging Methods ==========

  /** Log a unit usage */
  unit(name, category = 'standard') {
    this.entries.push({ type: 'unit', name, category });
    return this;
  }

  /** Log a conversion */
  convert(from, to, options = {}) {
    this.entries.push({
      type: 'convert',
      from,
      to,
      assumed: options.assumed || false,
      fuzzy: options.fuzzy || false
    });
    return this;
  }

  /** Log a stock lookup */
  stock(symbol, price, date, options = {}) {
    this.entries.push({
      type: 'stock',
      symbol,
      price,
      date,
      autoDetected: options.autoDetected !== false
    });
    return this;
  }

  /** Log a function call */
  fn(name) {
    this.entries.push({ type: 'function', name });
    return this;
  }

  /** Log a warning */
  warning(message) {
    this.entries.push({ type: 'warning', message });
    return this;
  }

  /** Log a tip/hint */
  tip(message) {
    this.entries.push({ type: 'tip', message });
    return this;
  }

  /** Log an info message */
  info(message) {
    this.entries.push({ type: 'info', message });
    return this;
  }

  /** Log a debug message (only shown in debug mode) */
  debug(message) {
    this.entries.push({ type: 'debug', message });
    return this;
  }

  /** Log a raw entry (for backward compat with IC.log) */
  log(entry) {
    if (typeof entry === 'string') {
      this.entries.push(entry);
    } else if (typeof entry === 'object' && entry !== null) {
      this.entries.push(entry);
    }
    return this;
  }

  // ========== State Management ==========

  /** Clear all log entries */
  clear() {
    this.entries = [];
    return this;
  }

  /** Get raw entries (before processing) */
  getEntries() {
    return [...this.entries];
  }

  /** Get entry count */
  get length() {
    return this.entries.length;
  }

  /** Check if empty */
  isEmpty() {
    return this.entries.length === 0;
  }

  // ========== Processing ==========

  /**
   * Process entries into display strings
   * @param {Object} options - Processing options
   * @param {Function} options.isCurrency - Function to check if unit is currency
   * @param {Function} options.getUnitHelp - Function to get unit help message
   * @param {Function} options.getUnitInfo - Function to get unit info message
   * @param {Function} options.getFunctionHelp - Function to get function help
   * @returns {string[]} Array of display strings
   */
  process(options = {}) {
    const items = this._categorize(options);
    return this._buildOutput(items, options);
  }

  /** Categorize entries by type */
  _categorize(options = {}) {
    const isCurrency = options.isCurrency || ((name) => {
      // Default: use ICUnit if available
      return typeof ICUnit !== 'undefined' && ICUnit.isCurrency ? ICUnit.isCurrency(name) : false;
    });

    const items = {
      units: [],
      converts: [],
      warnings: [],
      tips: [],
      infos: [],
      functions: [],
      stocks: [],
      debug: [],
      other: []
    };

    this.entries.forEach(entry => {
      if (typeof entry === 'object' && entry !== null) {
        this._categorizeStructured(entry, items);
      } else if (typeof entry === 'string') {
        this._categorizeLegacy(entry, items, isCurrency);
      }
    });

    // Deduplicate
    items.units = this._dedupeUnits(items.units);
    items.warnings = this._dedupeByField(items.warnings, 'message');
    items.infos = this._dedupeByField(items.infos, 'message');

    return items;
  }

  /** Categorize a structured entry */
  _categorizeStructured(entry, items) {
    switch (entry.type) {
      case 'unit':
        items.units.push({ name: entry.name, category: entry.category || 'standard' });
        break;
      case 'convert':
        items.converts.push({
          from: entry.from,
          to: entry.to,
          assumed: entry.assumed,
          fuzzy: entry.fuzzy
        });
        break;
      case 'warning':
        items.warnings.push({ message: entry.message });
        break;
      case 'tip':
        items.tips.push({ message: entry.message });
        break;
      case 'info':
        items.infos.push({ message: entry.message });
        break;
      case 'function':
        items.functions.push({ name: entry.name });
        break;
      case 'stock':
        items.stocks.push({
          symbol: entry.symbol,
          price: entry.price,
          date: entry.date,
          autoDetected: entry.autoDetected
        });
        break;
      case 'debug':
        items.debug.push({ message: entry.message });
        break;
      default:
        items.other.push(JSON.stringify(entry));
    }
  }

  /** Categorize a legacy string entry */
  _categorizeLegacy(entry, items, isCurrency) {
    const colonIdx = entry.indexOf(':');
    if (colonIdx === -1) {
      if (entry.toLowerCase().includes('warning') || entry.includes("can't") || entry.includes('cannot')) {
        items.warnings.push({ message: entry });
      } else {
        items.other.push(entry);
      }
      return;
    }

    const type = entry.substring(0, colonIdx).trim().toLowerCase();
    const msg = entry.substring(colonIdx + 1).trim();

    switch (type) {
      case 'custom unit':
        items.units.push({ name: msg, category: 'custom' });
        break;
      case 'unit':
        items.units.push({ name: msg, category: isCurrency(msg) ? 'currency' : 'standard' });
        break;
      case 'convert':
      case 'fuzzy convert':
      case 'assumed conversion': {
        const match = msg.match(/(.+?)\s*→\s*(.+)/);
        if (match) {
          items.converts.push({
            from: match[1],
            to: match[2],
            fuzzy: type === 'fuzzy convert',
            assumed: type === 'assumed conversion'
          });
        }
        break;
      }
      case 'warning':
        items.warnings.push({ message: msg });
        break;
      case 'tip':
        items.tips.push({ message: msg });
        break;
      case 'info':
        items.infos.push({ message: msg });
        break;
      case 'function':
      case 'fn':
        items.functions.push({ name: msg });
        break;
      default:
        if (entry.includes('try') || entry.includes('use ') || entry.includes('hint')) {
          items.tips.push({ message: entry });
        } else {
          items.other.push(entry);
        }
    }
  }

  /** Build output array with priority ordering */
  _buildOutput(items, options = {}) {
    const output = [];
    const isCurrency = options.isCurrency || ((name) => {
      return typeof ICUnit !== 'undefined' && ICUnit.isCurrency ? ICUnit.isCurrency(name) : false;
    });
    const getUnitHelp = options.getUnitHelp || (() => '');
    const getUnitInfo = options.getUnitInfo || (() => '');
    const getFunctionHelp = options.getFunctionHelp || (() => null);

    // Group units by category
    const currencies = items.units.filter(u => u.category === 'currency' || isCurrency(u.name));
    const customUnits = items.units.filter(u => u.category === 'custom' && !isCurrency(u.name));
    const standardUnits = items.units.filter(u => u.category === 'standard' && !isCurrency(u.name));
    const allAreCurrencies = items.units.length > 0 && currencies.length === items.units.length;

    // 1. WARNINGS (highest priority)
    items.warnings.forEach(w => output.push(`note: ${w.message}`));

    // 2. STOCKS
    items.stocks.forEach(s => {
      const priceStr = typeof s.price === 'number' ? `$${s.price.toFixed(2)}` : s.price;
      if (s.autoDetected) {
        output.push(`${s.symbol} → $${s.symbol}: ${priceStr} (${s.date})`);
      } else {
        output.push(`${s.symbol}: ${priceStr} (${s.date})`);
      }
    });

    // 3. UNITS
    if (currencies.length > 0) {
      const names = currencies.map(u => u.name.toUpperCase());
      output.push(`${currencies.length > 1 ? 'currencies' : 'currency'}: ${names.join(', ')}`);
    }

    if (customUnits.length > 0) {
      const names = customUnits.map(u => u.name);
      output.push(`custom ${customUnits.length > 1 ? 'units' : 'unit'}: ${names.join(', ')}`);
    }

    if (standardUnits.length > 0) {
      const formatted = standardUnits.map(u => {
        const help = getUnitHelp(u.name, standardUnits.length > 1);
        const info = help ? '' : getUnitInfo(u.name);
        return `${u.name}${help ? ' ' + help : ''}${standardUnits.length === 1 && info ? ' ' + info : ''}`.trim();
      });
      output.push(`${standardUnits.length > 1 ? 'units' : 'unit'}: ${formatted.join(', ')}`);
    }

    // 4. CONVERSIONS (skip for pure currency)
    if (!allAreCurrencies && items.converts.length > 0) {
      const conv = items.converts.find(c => !c.assumed) || items.converts[0];
      let msg = `${conv.from} → ${conv.to}`;
      if (conv.assumed) msg += ' (assumed)';
      if (conv.fuzzy) msg += ' (approx)';
      output.push(`convert: ${msg}`);
    }

    // 5. FUNCTIONS
    if (items.functions.length > 0) {
      const fn = items.functions[0];
      const help = getFunctionHelp(fn.name);
      if (help) output.push(help);
    }

    // 6. INFO
    items.infos.forEach(i => output.push(i.message));

    // 7. OTHER
    items.other.forEach(o => {
      if (allAreCurrencies && o.toLowerCase().includes('convert')) return;
      output.push(o);
    });

    // 8. DEBUG (only in debug mode)
    if (this.debugMode) {
      items.debug.forEach(d => output.push(`[debug] ${d.message}`));
    }

    // 9. TIPS (lowest priority)
    items.tips.forEach(t => output.push(`tip: ${t.message}`));

    return output.filter(Boolean);
  }

  /** Deduplicate units by name (case-insensitive) */
  _dedupeUnits(units) {
    const seen = new Set();
    return units.filter(u => {
      const key = u.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  /** Deduplicate by a specific field */
  _dedupeByField(items, field) {
    const seen = new Set();
    return items.filter(i => {
      const key = i[field];
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // ========== Utility Methods ==========

  /** Get a summary of entry counts by type */
  summary() {
    const counts = {};
    this.entries.forEach(entry => {
      const type = typeof entry === 'object' ? entry.type : 'legacy';
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  }

  /** Clone this logger */
  clone() {
    const copy = new LogManager({ debugMode: this.debugMode, config: this.config });
    copy.entries = [...this.entries];
    return copy;
  }

  /** Create a child logger that shares state */
  child() {
    const child = new LogManager({ debugMode: this.debugMode, config: this.config });
    child.entries = this.entries; // Share reference
    return child;
  }

  /** Format for console output */
  toString() {
    return this.process().join('\n');
  }

  /** Format as JSON */
  toJSON() {
    return {
      entries: this.entries,
      summary: this.summary()
    };
  }
}

// Export LogManager for tests
if (typeof module !== 'undefined' && module.exports) {
  module.exports.LogManager = LogManager;
}
if (typeof window !== 'undefined') {
  window.LogManager = LogManager;
}

/**
 * MarketUnits - Manages fiat currencies and stock prices
 *
 * Both fiat currencies (USD, EUR) and stocks (AAPL, MSFT) are "market units" -
 * they have prices denominated in USD that change over time.
 *
 * This class provides a clean API to:
 * - Add/lookup prices for both types
 * - Distinguish fiat from stocks for display purposes
 * - Serialize/deserialize for worker sync
 *
 * Usage:
 *   InstaCalc.marketUnits = new MarketUnits();
 *   InstaCalc.marketUnits.loadFiat(currencyData, timestamp);
 *   InstaCalc.marketUnits.syncStocks(stockCache);
 *   InstaCalc.marketUnits.getPrice('usd');  // 1
 *   InstaCalc.marketUnits.isFiat('usd');    // true
 *   InstaCalc.marketUnits.isStock('AAPL');  // true
 */
class MarketUnits {
  constructor() {
    this._units = {};
    this._rateTimestamp = null;
    this._cachedCounts = null;  // Cached counts for performance
  }

  /**
   * Invalidate cached counts (call when units change)
   * @private
   */
  _invalidateCounts() {
    this._cachedCounts = null;
  }

  /**
   * Add a market unit (fiat currency or stock)
   * @param {string} symbol - Currency code or stock ticker
   * @param {number} price - Price in USD
   * @param {'fiat'|'stock'} type - Unit type
   */
  add(symbol, price, type) {
    if (!symbol || typeof symbol !== 'string') return;
    const lower = symbol.toLowerCase();
    const upper = symbol.toUpperCase();

    this._units[lower] = { price, type };
    this._units[upper] = { price, type };
    this._invalidateCounts();
  }

  /**
   * Get the USD price of a symbol
   * @param {string} symbol - Currency code or stock ticker
   * @returns {number|undefined} Price in USD, or undefined if not found
   */
  getPrice(symbol) {
    if (!symbol || typeof symbol !== 'string') return undefined;
    return this._units[symbol.toLowerCase()]?.price;
  }

  /**
   * Check if a symbol is a fiat currency
   * @param {string} symbol - Currency code
   * @returns {boolean}
   */
  isFiat(symbol) {
    if (!symbol || typeof symbol !== 'string') return false;
    return this._units[symbol.toLowerCase()]?.type === 'fiat';
  }

  /**
   * Check if a symbol is a stock
   * @param {string} symbol - Stock ticker
   * @returns {boolean}
   */
  isStock(symbol) {
    if (!symbol || typeof symbol !== 'string') return false;
    return this._units[symbol.toLowerCase()]?.type === 'stock';
  }

  /**
   * Check if a symbol exists in marketUnits
   * @param {string} symbol - Currency code or stock ticker
   * @returns {boolean}
   */
  has(symbol) {
    if (!symbol || typeof symbol !== 'string') return false;
    return symbol.toLowerCase() in this._units;
  }

  /**
   * Get the rate timestamp (when fiat rates were last updated)
   * @returns {string|null} ISO timestamp or null
   */
  get rateTimestamp() {
    return this._rateTimestamp;
  }

  /**
   * Set the rate timestamp
   * @param {string|number} timestamp - ISO string or Unix timestamp
   */
  set rateTimestamp(timestamp) {
    this._rateTimestamp = timestamp;
  }

  /**
   * Bulk load fiat currencies from OXR-style data
   * @param {Object} data - Currency data { USD: 1, EUR: 0.84, ... }
   * @param {string} [timestamp] - Rate timestamp
   */
  loadFiat(data, timestamp) {
    if (timestamp) {
      this._rateTimestamp = timestamp;
    }

    for (const [code, rate] of Object.entries(data)) {
      // Skip metadata keys (e.g., _fallback, _rateTimestamp)
      if (code.startsWith('_')) continue;

      // Convert OXR rate (units per USD) to our format (USD per unit)
      const price = 1.0 / parseFloat(rate);
      this.add(code, price, 'fiat');
    }

    // Always ensure USD = 1
    this.add('usd', 1, 'fiat');
  }

  /**
   * Sync stocks from stock cache
   * @param {Object} stockCache - Stock cache { AAPL: { price: 185, loaded: true }, ... }
   * @param {Function} [isFiatCheck] - Function to check if symbol is fiat (to skip)
   */
  syncStocks(stockCache, isFiatCheck) {
    if (!stockCache) return;

    for (const [symbol, data] of Object.entries(stockCache)) {
      if (!data?.loaded || !data?.price) continue;

      // Skip if this is a fiat currency code (e.g., USD ETF)
      if (isFiatCheck && isFiatCheck(symbol)) continue;

      this.add(symbol, data.price, 'stock');

      // Also add STOCK_ prefixed version for explicit $TICKER syntax
      const stockUnitName = 'STOCK_' + symbol.toUpperCase().replace(/\./g, '_');
      this.add(stockUnitName, data.price, 'stock');
    }
  }

  /**
   * Get count of loaded units (cached for performance)
   * @returns {{ fiat: number, stock: number, total: number }}
   */
  get counts() {
    if (this._cachedCounts) {
      return this._cachedCounts;
    }

    let fiat = 0, stock = 0;
    const seen = new Set();

    for (const [key, unit] of Object.entries(this._units)) {
      const lower = key.toLowerCase();
      if (seen.has(lower)) continue;
      seen.add(lower);

      if (unit.type === 'fiat') fiat++;
      else if (unit.type === 'stock') stock++;
    }

    this._cachedCounts = { fiat, stock, total: fiat + stock };
    return this._cachedCounts;
  }

  /**
   * Check if any fiat currencies are loaded
   * @returns {boolean}
   */
  hasFiatLoaded() {
    return Object.values(this._units).some(u => u.type === 'fiat');
  }

  /**
   * Backward compatibility: Get price-only object (like old currencies object)
   * @returns {Object} { usd: 1, eur: 0.84, AAPL: 185, ... }
   */
  toPriceObject() {
    const prices = {};
    for (const [key, unit] of Object.entries(this._units)) {
      prices[key] = unit.price;
    }
    return prices;
  }

  /**
   * Serialize for worker sync
   * @returns {Object}
   */
  toJSON() {
    return {
      units: this._units,
      rateTimestamp: this._rateTimestamp
    };
  }

  /**
   * Deserialize from worker sync
   * @param {Object} data - Serialized data
   * @returns {MarketUnits}
   */
  static fromJSON(data) {
    const instance = new MarketUnits();
    if (data?.units) {
      instance._units = data.units;
    }
    if (data?.rateTimestamp) {
      instance._rateTimestamp = data.rateTimestamp;
    }
    return instance;
  }

  /**
   * Clear all units (for testing)
   */
  clear() {
    this._units = {};
    this._rateTimestamp = null;
    this._invalidateCounts();
  }
}

// Export MarketUnits
if (typeof module !== 'undefined' && module.exports) {
  module.exports.MarketUnits = MarketUnits;
}
if (typeof window !== 'undefined') {
  window.MarketUnits = MarketUnits;
}

// Initialize MarketUnits instance on InstaCalc (moved from instacalc.engine.js to fix initialization order)
if (typeof InstaCalc !== 'undefined') {
  InstaCalc.marketUnits = new MarketUnits();
}

class ICUnit {

  static COMPOUND_UNITS = {
    "newton": { m: 1, kg: 1, s: -2 },
    "joule": { m: 2, kg: 1, s: -2 },
    "watt": { m: 2, kg: 1, s: -3 },
    "pascal": { m: -1, kg: 1, s: -2 },
    "coulomb": { a: 1, s: 1 },
    "mah": { a: 1, s: 1 },
    "volt": { m: 2, kg: 1, s: -3, a: -1 },
    "ohm": { m: 2, kg: 1, s: -3, a: -2 },
    "farad": { m: -2, kg: -1, s: 4, a: 2 },
    "weber": { m: 2, kg: 1, s: -2, a: -1 },
    "tesla": { m: 1, kg: 1, s: -2, a: -1 },
    "henry": { m: 2, kg: 1, s: -2, a: -2 },
    "siemens": { m: -2, kg: -1, s: 3, a: 2 },
    //  "hertz": { s: -1 },
    "lux": { m: -2, cd: 1, sr: 1 },
    "lumen": { cd: 1, sr: 1 },
    //"candela": { cd: 1 },
    "gray": { m: 2, s: -2 },
    "sievert": { m: 2, s: -2 },
    "katal": { mol: 1, s: -1 },
    //  "molar": { mol: 1, m: -3 },
    //  "becquerel": { s: -1 },
    //  "mole": { mol: 1 },
    // "molarity": { mol: 1 },
    "erg": { m: 2, kg: 1, s: -2 },
    "dyne": { m: 1, kg: 1, s: -2 },
    "kelvin": { k: 1 },
  }

  // units that we shouldn't simple reduce to, like bigG should not be in terms of Gray
  static COMPOUND_UNITS_AUTOCOLLAPSE_IGNORE = {
    "gray": true,
    "sievert": true,
    "erg": true,
    "dyne": true,
  }

  static SI_SYMBOLS = {
    "m": "meter",
    "kg": "kilogram",
    "s": "second",
    "a": "ampere",
    "ah": "ampere hour",
    "k": "kelvin",
    "mol": "mole",
    "cd": "candela",
    "rad": "radian",
    "sr": "steradian",
    "hz": "hertz",
    "n": "newton",
    "pa": "pascal",
    "j": "joule",
    "w": "watt",
    "c": "coulomb",
    "v": "volt",
    "f": "farad",
    "wb": "weber",
    "t": "tesla",
    "h": "henry",
    "lm": "lumen",
    "lx": "lux",
    "bq": "becquerel",
    "gy": "gray",
    "sv": "sievert",
    "kat": "katal",
  }

  static areEquivalentBaseUnits(unitObject1, unitObject2) {
    let [type1, scale1] = ICUnit.convertToBaseUnits(unitObject1);
    let [type2, scale2] = ICUnit.convertToBaseUnits(unitObject2);

    // filter out rad and sr for comparison
    type1 = Object.keys(type1).filter((key) => key != 'rad' && key != 'sr').reduce((obj, key) => {
      obj[key] = type1[key];
      return obj;
    }, {});

    type2 = Object.keys(type2).filter((key) => key != 'rad' && key != 'sr').reduce((obj, key) => {
      obj[key] = type2[key];
      return obj;
    }, {});

    return ICUnit.isSameUnitObject(type1, type2);
  }

  // return keys in mass, length, time order
  static sortUnitMLT(baseUnitObject = {}) {
    if (Object.keys(baseUnitObject).length < 2) {
      return baseUnitObject;
    }

    // create a new object in order of MLT for SI units
    let orderedBaseUnits = {};
    ["kg", "m", "a", "s", "k", "mol", "cd"].forEach(unit => {
      if (baseUnitObject.hasOwnProperty(unit)) {
        orderedBaseUnits[unit] = baseUnitObject[unit];
        delete baseUnitObject[unit];
      }
    });

    // append remaining non-SI units
    for (let unit in baseUnitObject) {
      orderedBaseUnits[unit] = baseUnitObject[unit];
    }

    return orderedBaseUnits;
  }

  // "3 months"
  static unitStringHasNumber(str) {
    return str.match(/^[0-9,._]+/);
  }

  static unitStringExtractNumber(str) {
    let unit = ICUnit.unitStringNormalize(str);
    let [numberString, ...parts] = unit.split(/[\s]+/);

    // Extract the suffix if it exists
    let scaleMatch = numberString.match(/^([0-9,._]+)([a-zA-Z]+)$/);
    if (scaleMatch) {
      let [, number, scaleSuffix] = scaleMatch;
      let scale = IC.predefinedSuffixScale(scaleSuffix) || 1;
      numberString = (parseFloat(number) * scale).toString();
    }

    let n = IC.makeNumber(numberString, IC.envGetSettings());
    let extractedName = parts.join(" ");
    return [n, extractedName];
  }


  // 2 k USD => 2k USD
  static unitStringNormalize(str) {
    if (!str) {
      return str;
    }

    let parts = str.split(" ");

    if (parts.length < 2) {
      return str;
    }

    let number = parts[0];
    let possibleScale = parts[1];

    if (possibleScale?.length == 1 && IC.isNumberStringIncludingScaleSuffix(number + possibleScale)) {
      return number + possibleScale + " " + parts.slice(2).join(" ");
    }

    return str;
  }


  // try to convert to base units (Newton = kg m / s^2)

  /**
   * Convert given units to their base units.
   * 
   * @param {ICType} type - An object where keys represent the unit type, and the values represent the power to which the unit is raised.
   * @returns {[ICType, number]} - A tuple. The first element is an object with base units as keys and their respective powers as values. The second element is the scale.
   */
  static convertToBaseUnits(type = {}) {

    function getBaseUnitNameAndScale(unitStr) {
      unitStr = unitStr.toLowerCase();
      let unitObj = ICUnit.getUnit(unitStr);
      let scale = unitObj ? unitObj[unitStr] : 1;

      // Check for scale prefixes like "billion miles"
      let parts = unitStr.split(/\s+/);
      if (parts.length > 1) {
        // Look for scale prefix in first part
        let scalePrefix = CONFIG.nearestNumberScale(parts[0]);
        if (scalePrefix) {
          // Get rest of unit name and look it up
          let restOfUnit = parts.slice(1).join(" ");
          let restUnitObj = ICUnit.getUnit(restOfUnit);
          if (restUnitObj) {
            scale = scalePrefix * (restUnitObj[restOfUnit] || 1);
            unitStr = restOfUnit; // Use base unit name
            unitObj = restUnitObj;
          }
        }
      }

      // Check for flexible unit aliases like flop/flops, hz, etc.
      const flexMatch = CONFIG.FLEX_UNIT_ALIAS[unitStr];
      if (flexMatch) {
        return [flexMatch.type, flexMatch.scale];
      }

      if (!unitObj) {
        return [unitStr, scale];
      }

      // treat "byte" as base unit, not "bit"
      if (IC.typeIsByte(unitStr)) {
        scale = ICUnit.typeConversionScale(unitStr, "byte");
        return ["byte", scale];
      }

      // currencies become USD (includes dynamically-added stocks via stringIsCurrencyUnit)
      if (IC.typeIsCurrency(unitStr) || IC.stringIsCurrencyUnit(unitStr)) {
        return ["USD", scale];
      }

      // find shortest key with value 1 in the unit object
      let shortestKey = null;
      Object.keys(unitObj).filter(key => key != "base").forEach((key) => {
        if (unitObj[key] == 1 && (!shortestKey || key.length < shortestKey.length)) {
          shortestKey = key;
        }
      });

      return [shortestKey, scale];
    }

    let scale = 1;

    let baseUnits = [];
    // TODO: support custom units here. For now, just break apart known units
    Object.keys(type).forEach((key) => {
      let power = type[key];

      let unitObj = ICUnit.getUnitCaseInsensitive(key);
      if (unitObj && unitObj.SI) {
        let baseUnitItems = Object.fromEntries(
          Object.entries(unitObj.SI).map(([k, v]) => [k, v * power])
        );

        baseUnits.push(baseUnitItems);
        let localScale = ICUnit.getUnitScale(key);

        // Kelvin is a special case, could be a function conversion
        if (typeof localScale == 'number') {
          scale *= localScale ** power;
        }
        return;
      } else {
        let keyIndex = key;

        // if we have a number... could be "6 months"
        if (ICUnit.unitStringHasNumber(key)) {
          let [n, extractedName] = ICUnit.unitStringExtractNumber(key);
          scale *= n ** power;
          keyIndex = extractedName;
        }

        keyIndex = IC.stringSingularize(keyIndex);
        let [newKey, newScale] = getBaseUnitNameAndScale(keyIndex);
        if (newScale) {
          keyIndex = newKey;
          scale *= newScale ** power;
        }

        baseUnits.push({ [keyIndex]: power });
      }
    });

    // combine values for all entries in baseUnits
    /** @type {ICType} */
    let finalBaseUnits = {};
    baseUnits.forEach((baseUnit) => {
      Object.keys(baseUnit).forEach((key) => {
        finalBaseUnits[key] = (finalBaseUnits[key] || 0) + baseUnit[key];
      });
    });

    // remove entries with 0
    /** @type {ICType} */
    let finalBaseUnitsWithoutZeros = {};
    Object.keys(finalBaseUnits).forEach((key) => {
      if (finalBaseUnits[key] !== 0) {
        finalBaseUnitsWithoutZeros[key] = finalBaseUnits[key];
      }
    });
    finalBaseUnits = finalBaseUnitsWithoutZeros;
    finalBaseUnits = ICUnit.sortUnitMLT(finalBaseUnits);

    return [finalBaseUnits, scale];
  }

  static convertToCompoundUnits(units) {

    // not enough units to compound
    if (Object.keys(units).length < 2) {
      return units;
    }

    let result = {};
    ICUnit.SORTED_COMPOUND_UNITS ||= Object.keys(ICUnit.COMPOUND_UNITS)
      .filter(unit => !ICUnit.COMPOUND_UNITS_AUTOCOLLAPSE_IGNORE[unit])
      .sort((a, b) => {
        let aCount = Object.values(ICUnit.COMPOUND_UNITS[a]).reduce((a, b) => Math.abs(a) + Math.abs(b), 0);
        let bCount = Object.values(ICUnit.COMPOUND_UNITS[b]).reduce((a, b) => Math.abs(a) + Math.abs(b), 0);
        return bCount - aCount;
      });

    for (let compound of ICUnit.SORTED_COMPOUND_UNITS) {
      while (ICUnit.canSubtract(units, ICUnit.COMPOUND_UNITS[compound])) {
        ICUnit.subtract(units, ICUnit.COMPOUND_UNITS[compound]);
        result[compound] = (result[compound] || 0) + 1;
      }
    }

    // if there are units left, merge them in
    for (let unit in units) {
      result[unit] = (result[unit] || 0) + units[unit];
    }

    for (let unit in result) {
      if (result[unit] === 0) {
        delete result[unit];
      }
    }

    return result;
  }

  static canSubtract(units, compoundUnit) {
    for (let unit in compoundUnit) {
      // Check if the unit exists and its value is greater or equal in `units` and they have the same sign
      if (!(unit in units) || Math.abs(units[unit]) < Math.abs(compoundUnit[unit]) || Math.sign(units[unit]) !== Math.sign(compoundUnit[unit])) {
        return false;
      }
    }
    return true;
  }

  static subtract(units, compoundUnit) {
    for (let unit in compoundUnit) {
      units[unit] -= compoundUnit[unit];
      if (units[unit] === 0) {
        delete units[unit];
      }
    }
  }


  static isSameUnitObject(typeObjA, typeObjB) {
    if (Object.keys(typeObjA).length != Object.keys(typeObjB).length) {
      return false;
    }

    for (let key in typeObjA) {
      if (typeObjA[key] != typeObjB[key]) {
        return false;
      }
    }
    return true;
  }

  static isInverseUnitObject(typeObjA, typeObjB) {
    if (Object.keys(typeObjA).length != Object.keys(typeObjB).length) {
      return false;
    }

    for (let key in typeObjA) {
      if (typeObjA[key] != -typeObjB[key]) {
        return false;
      }
    }
    return true;
  }

  static isCurrency(unit) {
    let unitObj = ICUnit.getUnitCaseInsensitive(unit);
    return unitObj.usd || unitObj == LIB.InstaCalc.Unit.currencies;
  }

  /**
   * Format the currency rate date for tooltip attribution
   * @returns {string|null} Date string like "Jan 30"
   */
  static formatCurrencyRateDate() {
    const timestamp = LIB.InstaCalc.Unit.currencyRateTimestamp;
    if (!timestamp) return null;

    // Support both ISO strings (new format) and Unix seconds (legacy)
    const d = typeof timestamp === 'string'
      ? new Date(timestamp)  // ISO string: "2026-01-31T10:00:02.000Z"
      : new Date(timestamp * 1000);  // Unix seconds: convert to milliseconds

    // Use abbreviated month name to avoid confusion with calculations (e.g., "1/30")
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  static makeMixedUnit(obj, ...units) {

    // change format to "mixedunits"
    return obj;
  }

  // T and S are too common for formatting
  static UNITS_WITH_CAPITAL_LETTER = new Set(
    ["N", "Pa", "J", "K", "W", "C", "V", "F", "Ohm", "Wb", "Hz", "lm", "lx", "Bq", "Gy", "Sv", "kat", "A", "Ah", "L"]
  )

  static VALID_METRIC_PREFIXES = new Set(
    ['Y', 'Z', 'E', 'P', 'T', 'G', 'M', 'k', 'h', 'da', 'd', 'c', 'm', 'µ', 'n', 'p', 'f', 'a', 'z', 'y', '']
  )

  static FORMAT_MAPPING = {
    ah: "Ah",
    wh: "Wh", // kWh, GWh, etc. are auto-formatted from formatSIUnit

    pa: "Pa", // Pascal, not pico-amp
    ev: "eV",

    ha: "ha", // hectare, not hecto-amp
    cc: "cc", // cubic centimeter, not centi-coulomb
    cf: "cf", // cubic foot, not centi-farad

    iu: "IU",
    miu: "mIU",

    watt: "watt",
    newton: "newton",
    joule: "joule",
    volt: "volt",
    ampere: "ampere",
    ohm: "ohm",
    farad: "farad",
    coulomb: "coulomb",
    tesla: "tesla",
    henry: "henry",
    hertz: "hertz",
    lumen: "lumen",
    lux: "lux",
    becquerel: "becquerel",
    gray: "gray",
    sievert: "sievert",
    katal: "katal",
    siemens: "siemens",
    weber: "weber",
  };

  static FORMAT_MAPPING_CASE_SENSITIVE = {
    "mw": "MW",
    "mpge": "MPGe"
  }

  static CUSTOM_UNIT_INFO_MESSAGE = {
    "distance/energy": "(MPGe: miles per gallon equivalent)",
    "pp": "(percentage point = 1% = .01)",
    "bp": "(basis point = .01% = .0001)",
    "basis point": "(basis point = .01% = .0001)",
    "basis points": "(basis point = .01% = .0001)",
    "pph": "(parts per hundred)",
    "pcm": "(parts per 100,000 [cent mille])",
    "ppm": "(parts per million)",
    "ppb": "(parts per billion)",
    "ppt": "(parts per trillion)",
    "ppq": "(parts per quadrillion)",
  }


  static formatSIUnit(unitStr) {

    if (unitStr in ICUnit.FORMAT_MAPPING_CASE_SENSITIVE) {
      return ICUnit.FORMAT_MAPPING_CASE_SENSITIVE[unitStr];
    }

    let lower = unitStr.toLowerCase();
    if (lower in ICUnit.FORMAT_MAPPING) {
      return ICUnit.FORMAT_MAPPING[lower];
    }

    // if the last letter is an s, remove it and check again
    if (lower.endsWith("s")) {
      let withoutS = lower.slice(0, -1);
      if (withoutS in ICUnit.FORMAT_MAPPING) {
        return ICUnit.FORMAT_MAPPING[withoutS] + "s";
      }
    }

    return unitStr.replace(/([a-z]?)([a-z]+)$/i, function (match, p1, p2) {
      let p2Lower = p2.toLowerCase();
      let p2Upper = p2.toUpperCase();

      // nw => nW
      if (ICUnit.VALID_METRIC_PREFIXES.has(p1) && ICUnit.UNITS_WITH_CAPITAL_LETTER.has(p2Upper)) {
        return p1 + p2Upper;
      }

      // npa => nPa
      if (ICUnit.VALID_METRIC_PREFIXES.has(p1) && ICUnit.FORMAT_MAPPING[p2Lower]) {
        return p1 + ICUnit.FORMAT_MAPPING[p2Lower];
      }

      // gpa => GPa
      if (ICUnit.VALID_METRIC_PREFIXES.has(p1.toUpperCase()) && ICUnit.FORMAT_MAPPING[p2Lower]) {
        return p1.toUpperCase() + ICUnit.FORMAT_MAPPING[p2Lower];
      }
      return match;
    });
  }



  // cm => cm (length: 1e-2 meter)
  static infoMessage(unitStr) {

    // timezone
    if (ICDate.getIANATimeZone(unitStr)) {
      return `(timezone ${ICDate.getIANATimeZone(unitStr)})`;
    }

    let unitObj = ICUnit.getUnitCaseInsensitive(unitStr);
    if (!unitObj) return "";

    let scale = ICUnit.getUnitScale(unitStr);

    // no scale
    if (typeof (scale) !== 'number' || unitObj.name == "temperature") {
      return `(${unitObj.name})`;
    }

    if (ICUnit.isCurrency(unitStr)) {
      return "(currency)";
    }

    // custom info message for unit category
    if (ICUnit.CUSTOM_UNIT_INFO_MESSAGE[unitObj?.name]) {
      return ICUnit.CUSTOM_UNIT_INFO_MESSAGE[unitObj?.name];
    }

    // for unit itself
    if (ICUnit.CUSTOM_UNIT_INFO_MESSAGE[unitStr]) {
      return ICUnit.CUSTOM_UNIT_INFO_MESSAGE[unitStr];
    }

    // missing SI units (currency, etc.)
    if (!unitObj.SI) return "";

    // if too big or small, use scientific notation
    let scaleStr;
    let isPowerOf10 = Math.log10(scale) % 1 === 0;

    // if too big or small, use scientific notation
    if (scale > 1e4 || scale < 1e-4) {
      scaleStr = scale.toExponential(isPowerOf10 ? 0 : 4);

      // cleanup: 123.000e+6 123e6
      scaleStr = scaleStr.replace(/e\+/, "e").replace(/\.?0+e$/, "e");
    } else {
      // if scale is not power of 10, round 
      scaleStr = isPowerOf10 || Number.isInteger(scale) ? scale.toString() : scale.toFixed(6);
    }

    let baseUnitName = IC.typestring(unitObj.SI);
    if (baseUnitName) {
      let categoryName = unitObj.name;
      return `(${categoryName}: ${scaleStr} ${baseUnitName})`;
    }

    return "";
  }


  static scaleToBestUnit(result) {
    if (IC.hasNoType(result) || typeof IC.val(result) !== "number") {
      return result;
    }

    const value = IC.val(result);
    const units = Object.keys(result.t);

    if (units.length !== 1) {
      return result;
    }

    const unit = units[0];

    const prefixes = ["y", "z", "a", "f", "p", "n", "µ", "m", "", "k", "M", "G", "T", "P", "E", "Z", "Y"];
    const basePrefixIndex = prefixes.indexOf("");
    const maxPrefixConfig = {
      "m": "k",  // meters should stop scaling at kilometers
      "g": "k",  // grams should stop scaling at kilograms
      "s": "",   // seconds should not scale beyond second 
      "L": "",  // liters stops scaling 
      // Add more units and their maximum prefixes as needed
    };

    const baseUnitObj = ICUnit.getUnitCaseInsensitive(unit);
    if (!baseUnitObj?.SI) {
      return result;
    }

    let baseUnitName = ICUnit.getBaseUnitName(baseUnitObj) || "";

    // check if "kg" should use "g" as actual base unit
    if (baseUnitName.length >= 2 && prefixes.includes(baseUnitName[0])) {
      // remove the first char if the rest is still a real unit
      let newBaseUnitObj = ICUnit.getUnitCaseInsensitive(baseUnitName.slice(1));
      if (newBaseUnitObj == baseUnitObj) {
        baseUnitName = baseUnitName.slice(1);
      }
    }

    const totalValue = value * ICUnit.getUnitScale(unit);
    const powersOfTenFromBase = Math.log10(totalValue / ICUnit.getUnitScale(baseUnitName));
    const prefixSteps = Math.floor(powersOfTenFromBase / 3);

    let prefixIndex = basePrefixIndex + prefixSteps;
    if (maxPrefixConfig[baseUnitName] !== undefined) {
      const maxPrefixIndex = prefixes.indexOf(maxPrefixConfig[baseUnitName]);
      prefixIndex = Math.min(prefixIndex, maxPrefixIndex);
    }

    const prefix = prefixes[prefixIndex];
    const scaledUnit = prefix + baseUnitName;

    if (!IC.predefinedUnit(scaledUnit)) {
      return result;
    }

    const scaledResult = IC.ICObjectConvertToType(result, scaledUnit);
    return scaledResult;
  }

  /**
   * Scale bytes to the best byte unit (KB, MB, GB, TB, etc.)
   * Uses 1024-based scaling.
   */
  static scaleBytesToBestUnit(result) {
    if (IC.hasNoType(result) || typeof IC.val(result) !== "number") {
      return result;
    }

    if (!IC.typeIsByte(result.t)) {
      return result;
    }

    // Convert to bytes first
    const bytesResult = IC.ICObjectConvertToType(result, "bytes");
    const bytesValue = IC.val(bytesResult);

    // Byte units in ascending order (1024-based)
    const byteUnits = ["bytes", "kb", "mb", "gb", "tb", "pb", "eb", "zb", "yb"];

    // Find the best unit (largest that keeps value >= 1)
    let bestUnit = "bytes";
    let bestValue = bytesValue;

    for (let i = 1; i < byteUnits.length; i++) {
      const unit = byteUnits[i];
      const converted = IC.ICObjectConvertToType(bytesResult, unit);
      const convertedValue = IC.val(converted);

      if (Math.abs(convertedValue) >= 1) {
        bestUnit = unit;
        bestValue = convertedValue;
      } else {
        break;
      }
    }

    return IC.ICObjectConvertToType(bytesResult, bestUnit);
  }


  // TODO: get this working
  static getFriendlyFormat(result) {
    this.config = null;

    if (!result || !result.t) return null;

    const value = result.v;
    const type = Object.keys(result.t)[0];

    if (!this.config) {
      const SI_PREFIXES = [
        { prefix: 'T', factor: 1e12 },
        { prefix: 'G', factor: 1e9 },
        { prefix: 'M', factor: 1e6 },
        { prefix: 'k', factor: 1e3 },
        { prefix: '', factor: 1 },
        { prefix: 'm', factor: 1e-3 },
        { prefix: 'µ', factor: 1e-6 },
        { prefix: 'n', factor: 1e-9 },
        { prefix: 'p', factor: 1e-12 }
      ];

      const UNITS = ['watts', 'kWh', 'joules', 'newton', 'pascal'];

      this.config = {};

      UNITS.forEach(unit => {
        // @ts-ignore
        this.config[unit] = SI_PREFIXES.map(prefix => {

          return {
            threshold: prefix.factor,
            divisor: prefix.factor,
            unit: prefix.prefix + unit
          };
        });
      });
    }

    if (!this.config[type]) return `${value} ${type}`; // Default to the original format if no config exists

    for (let format of this.config[type]) {
      if (value >= format.threshold) {
        return `${value / format.divisor} ${format.unit}`;
      }
    }

    return `${value} ${type}`; // Default to the original format if no thresholds are met
  };


  static autoCollapse(result) {
    if (IC.hasNoType(result) || Object.keys(result.t).length < 2) {
      return result;
    }

    let [baseUnits, scale] = ICUnit.convertToBaseUnits(result.t);

    // for purposes of conversion, auto-collapse bits into bytes.
    // change the scale based on the bit power
    if (baseUnits.bit) {
      baseUnits.byte = baseUnits.bit;
      delete baseUnits.bit;
      scale *= 8 ** -baseUnits.byte;
    }

    let collapsedUnits = ICUnit.convertToCompoundUnits(baseUnits);

    // only collapse if it makes the units simpler...
    if (IC.typePowerCount(collapsedUnits) < IC.typePowerCount(result.t)) {
      let collapsedResult = {
        ...result,
        v: IC.mathPreciseMultiply(IC.val(result), scale),
        t: collapsedUnits
      };

      return collapsedResult;
    }

    return result;
  }

  // TODO: instead of autocollapsing, show base units but have the autocollapse result as helper log text 
  static autoCollapseIgnore(result) {
    let IGNORE_LIST = ["kwh", "wh"];
    let resultTypes = Object.keys(result.t);

    // return true if any of the result types are in the ignore list
    return resultTypes.find(type => IGNORE_LIST.includes(type.toLowerCase()));
  }

  static isCompoundUnit(unitStr) {
    if (unitStr.endsWith('s') && unitStr !== "siemens") {
      // remove last letter
      unitStr = unitStr.slice(0, -1);
    }

    let lower = unitStr.toLowerCase();
    return (ICUnit.COMPOUND_UNITS[lower] || ICUnit.COMPOUND_UNITS[ICUnit.SI_SYMBOLS[lower]]);
  }

  static isSIUnit(unitStr) {
    // Check both original and lowercased versions of the unit string
    let unitObj = ICUnit.getUnitCaseInsensitive(unitStr);

    if (!unitObj) {
      return false;
    }

    // time, charge, etc. counts as SI
    if (unitObj.second || unitObj.ah) {
      return true;
    }

    let scale = unitObj[unitStr] || unitObj[unitStr.toLowerCase()];
    let power = Math.log10(scale);

    // exact power of 10
    if (power % 1 == 0)
      return true;

    return false;
  }

  static getUnit(unitStr) {
    return IC.CALC.isUnit(unitStr);
  }

  /*
  @param {string} unitStr - unit string, like "square m" or "m^2"
  @param {Set} charSet - Set of characters, used for early exit
  */
  static getUnitNameAndPower(unitStr) {

    if (unitStr.includes("^")) {
      let [unit, power] = unitStr.split("^");
      power = Number(power);
      return [unit, power];
    }

    // replace . and underscore with space, then trim
    // sq.ft. => sq ft, sq.meter => sq meter
    unitStr = unitStr.replace(/\.|_/g, " ").trim();

    // Helper function to process unit types
    const processUnitType = (unitType, power) => {
      const combinedRegex = new RegExp("\\b(" + unitType.join('|') + ")\\b", "gi");
      const match = unitStr.match(combinedRegex);
      if (match) {
        unitStr = unitStr.replace(match[0], "").trim();
        return [unitStr, power];
      }
      return null;
    };

    let result = processUnitType(LIB.InstaCalc.Unit.area, 2);
    if (result) return result;

    result = processUnitType(LIB.InstaCalc.Unit.volume, 3);
    if (result) return result;

    // Default: same string, power of 1
    return [unitStr, 1];
  }

  static unitMatch(from, frompow, to, topow) {
    // this one is pretty fast...
    let orig = IC.CALC.unitMatch(from, frompow, to, topow);
    return orig;
  }

  // this could be arbitrary unit, like tests/day -> tests/week
  static convertKnownUnitMatch(from, to) {
    if (from == to) {
      return 1;
    }

    let numerator = ICUnit.getUnitScale(from);
    let denominator = ICUnit.getUnitScale(to);

    return numerator / denominator;
  }

  static getUnitScale(unitName) {
    let obj = ICUnit.getUnitCaseInsensitive(unitName);
    return obj[unitName] ?? obj[unitName.toLowerCase()];
  }

  static getUnitTypeAndScale(unitName) {
    let obj = ICUnit.getUnitCaseInsensitive(unitName);
    let scale = obj[unitName] ?? obj[unitName.toLowerCase()];
    return [obj, scale];
  }

  static getBaseUnitName(unitObj) {
    // get shortest key with value 1
    let shortestKey = null;
    Object.keys(unitObj).forEach((key) => {
      if (unitObj[key] == 1 && (!shortestKey || key.length < shortestKey.length)) {
        shortestKey = key;
      }
    });
    return Unit.canonicalName(shortestKey);
  }

  static processUnitName(unitName) {
    return unitName.trim().replace(CONFIG.UNIT_DIVISION_REGEX.find, CONFIG.UNIT_DIVISION_REGEX.replace);
  }

  static convert(x, unitName) {
    // 1. convert to base units
    // 2. for the leftover units, replace with the right numerator / denom scale (2 cows, 3 cows, etc.)
    //  - handle plurals
    //  - handle numeric prefixes
    // 3. multiply the scales together

    if (!IC.isValidEvalResult(x) || IC.hasNoType(x)) {
      return null;
    }

    let value = IC.val(x);

    let processedUnitName = ICUnit.processUnitName(unitName);
    let target = IC.makeICObject(1, processedUnitName);

    /*
      let varValue = IC.envGetVariable(processedUnitName) ?? IC.envGetVariable(IC.stringSingularize(processedUnitName));
      // trying to convert to a variable 
      if (varValue) {
        IC.log(`treating ${processedUnitName} as unit`);
        let divResult = IC.mathDiv(x, varValue);
        let newObj = IC.makeICObject(IC.val(divResult), processedUnitName);
        // return IC.val(divResult);
        return newObj;
      }
        */

    // Handle complex unit expressions with variables
    let result = x;
    let hasVariable = false;
    for (let unit in target.t) {
      let canonicalUnit = IC.canonicalName(unit);
      let isRealUnit = ICUnit.getUnitCaseInsensitive(canonicalUnit);

      if (IC.envIdentifierExists(canonicalUnit) ?? IC.envIdentifierExists(IC.stringSingularize(canonicalUnit))) {

        // if user is overriding "cm"
        if (ICUnit.getUnitCaseInsensitive(canonicalUnit)) {
          break;
        }

        let varValue = IC.envGetVariable(unit) ?? IC.envGetVariable(IC.stringSingularize(unit));
        if (varValue) {
          hasVariable = true;
          IC.log(`treating ${unit} as unit`);
          let divisor = IC.mathPow(varValue, target.t[unit]);
          result = IC.mathDiv(result, divisor);
          delete target.t[unit];
        }
      }
    }
    if (hasVariable) {
      // divide by remaining units in target unit
      if (Object.keys(target.t).length) {
        let divisor = IC.makeICObject(1, target.t);
        result = IC.mathDiv(result, divisor);
      }

      let scale = IC.val(result);
      if (IC.hasType(result)) {
        // ft^2 / acre gets collapsed down 
        scale = IC.val(ICUnit.autoCollapse(result));
      }

      return IC.makeICObject(scale, processedUnitName);
    }

    // lowercase just for temp conversion
    let fromStr = IC.typestring(x.t).toLowerCase();
    let toStr = IC.typestring(target.t).toLowerCase();

    // check for temperature -- if a function, apply that
    const fnObj = IC.CALC.isConversionFunction(fromStr, toStr);
    if (fnObj) {
      const [fromFn, toFn] = [fnObj[fromStr][0], fnObj[toStr][1]];
      const composite = x => toFn(fromFn(x));
      return IC.makeICObject(composite(value), target.t);
    }

    let [fromBase, fromScale] = ICUnit.convertToBaseUnits(x.t);
    let [toBase, toScale] = ICUnit.convertToBaseUnits(target.t);

    let typeSimilarity = IC.typeSimilarityByObject(fromBase, toBase);

    if (typeSimilarity == 0) {
      return null; // no conversion
    }

    // base units are same, no need to type multiply them
    let highPrecisionScale = IC.mathPreciseMultiply(value, fromScale);

    // Determine value based on typeSimilarity
    let newValue = (typeSimilarity == 1)
      ? IC.mathPreciseMultiply((1 / toScale), highPrecisionScale)
      : 1 / IC.mathPreciseMultiply(toScale, highPrecisionScale);

    let typeToAssign = target.t;

    // Inversion logic for both value and type; see "50 hertz in ms per cycle"
    if (typeSimilarity == -1 && Object.keys(x.t).length == 1 && Object.keys(target.t).length == 1) {
      newValue = IC.mathPreciseMultiply(toScale, highPrecisionScale);
      typeToAssign = IC.mathDivideTypePowers(target.t, -1);
    }

    newValue = Number(newValue.toPrecision(12));
    let tmResultObj = IC.makeICObject(newValue, typeToAssign);

    return tmResultObj;
  };

  /**
   * Convert "fuzzy" units like "oz" to "fl oz".
   * @param {string|ICType} typeObj 
   * @returns {string|ICType}
   */
  static swapFuzzyTypes(typeObj) {

    let SWAPS = {
      "oz": "fl oz",
      "ounce": "fl oz",
      "ounces": "fl oz",
      "pound": "GBP",
      "pounds": "GBP",
      "nm": "Newton meter"
    };

    if (typeof typeObj == "string") {
      if (SWAPS[typeObj.toLowerCase()]) {
        return SWAPS[typeObj.toLowerCase()];
      }
      return typeObj;
    }

    // attempt to use "dollar" for "USD" and see if that works
    let clone = IC.cloneObject(typeObj);
    // replae dollar with USD
    Object.keys(clone).forEach((key) => {
      // use swap table
      if (SWAPS[key.toLowerCase()]) {
        clone[SWAPS[key.toLowerCase()]] = clone[key];
        delete clone[key];
      }
    });

    return clone;
  }

  static convertWithUnitSwap(x, unitName) {
    let newObj = IC.cloneObject(x);
    newObj.t = ICUnit.swapFuzzyTypes(x.t);
    let result = ICUnit.convert(newObj, unitName);
    let newUnitName = ICUnit.swapFuzzyTypes(unitName);

    // try swapping the result unit name too
    if (!result && newUnitName) {
      result = ICUnit.convert(newObj, newUnitName);
    }

    return [result, newObj];
  }

  static isPredefinedUnitCaseInsensitive(unitStr = "") {
    return IC.predefinedUnit(unitStr) || IC.predefinedUnit(unitStr.toLowerCase());
  }

  static getUnitCaseInsensitive(unitStr) {
    return ICUnit.getUnit(unitStr) || ICUnit.getUnit(unitStr.toLowerCase());
  }

  static typeConversionScale(fromType, toType) {
    if (!fromType || !toType) {
      return 1;
    }

    let fromTypeStr = typeof fromType == "string" ? fromType : IC.typestring(fromType);
    let toTypeStr = typeof toType == "string" ? toType : IC.typestring(toType);

    if (IC.typestringIsSameUnitCategory(fromTypeStr, toTypeStr)) {
      return ICUnit.getUnitScale(fromTypeStr) / ICUnit.getUnitScale(toTypeStr);
    }

    let [fromBase, fromScale] = ICUnit.convertToBaseUnits(fromType);
    let [toBase, toScale] = ICUnit.convertToBaseUnits(toType);

    if (!ICUnit.isSameUnitObject(fromBase, toBase)) {
      return 1;
    }

    return fromScale / toScale;
  }

}; // end class ICUnit


if (!OPTION_MEMO_OFF) {

  ["stringSingularize",
    "predefinedUnit",
    "typestring",
    "typestringIsPossibleUnitIncludingSlash",
    "typeSimilarityByString",
    "typestringNormalize",
    "parseUnitsFromString",
    "parseWrittenNumber",
    "stringPluralize",
    "_typeMultiply",
    //"_makeICObjectTypeOnly",
    "_makeICObjectUnitType",
    "makeNumber"].forEach(fnname => {
      IC[fnname] = memoize(IC[fnname].bind(IC));
    });

  // Row-aware memoization for functions that depend on row state
  // BUG: this was getting memoized but the row state for the info formatter was not being preserved.
  //  IC.predefinedPostConvertFunction = memoizeRowAware(IC.predefinedPostConvertFunction.bind(IC));

  ["suggestFunction",
    "typestringPluralize",
    "parseSimple",
    "processComment",
    "processCurrencySymbolsAndStrings",
    "sanitizeUnicodeString",
    "escapeUnicode",
    "processSymbols",
    "processPrefixRegex",
    // NOTE: processRegexReplacements is NOT memoized because $TICKER replacement
    // depends on IC.env (to check if symbol is already defined), which changes
    // between calls but isn't part of the memoization key.
    "scanAssignedIdentifiersUsingLex",
    "processIdentifiers",
    "processParenUnitExpressions",
    "processPlainEnglishNumbers",
    "processPlainEnglishFunctions",
    "processPlainEnglishOp",
    "processFunctionWithExponent",
    "processUnitTypeBeforeNumber",
    "scanReferencesFromParseResult",
    "getRowParameterFromString",
    "stringCanBeIncremented"
  ].forEach(fnname => {
    if (IC.parser[fnname]) {
      IC.parser[fnname] = memoize(IC.parser[fnname].bind(IC.parser));
    }
  });

  ["areEquivalentBaseUnits",
    /*"convert", */ /* disabled because we log */
    "convertToCompoundUnits",
    "formatSIUnit",
    "getUnitNameAndPower",
    "getUnitScale",
    "infoMessage",
    "isCompoundUnit",
    "isInverseUnitObject",
    "isSIUnit",
    "isSameUnitCategory",
    "isSameUnitObject",
    "sortUnitMLT",
    "typeConversionScale",
  ].forEach(fn => {
    ICUnit[fn] = memoize(ICUnit[fn]);
  });

  ["convertToBaseUnits", "autoCollapse"].forEach(fn => {
    ICUnit[fn] = memoizeWithNewObject(ICUnit[fn]);
  });

  LIB.toFractionIterative = memoize(LIB.toFractionIterative);
}

function SETUP_INSTRUMENTATION(obj, prefix = "") {
  // Object to store function call counts
  // @ts-expect-error
  let functionCallCounts = (typeof window !== 'undefined' ? (window.functionCallCounts = window.functionCallCounts || {}) : {});

  // Utility to wrap and instrument a function
  function instrument(func, funcName) {
    // Initialize counter for this function
    functionCallCounts[funcName] = 0;

    // Return a new function that increments the counter and calls the original function
    return function (...args) {
      functionCallCounts[funcName]++;
      return func.apply(this, args);
    };
  }

  // Loop through the keys of the object
  for (let key in obj) {
    // Prepare the full function name
    let fullName = prefix ? `${prefix}.${key}` : key;

    // Check if the property is a function
    if (typeof obj[key] === 'function') {
      // If it is, instrument it
      obj[key] = instrument(obj[key], fullName);
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      // If it's an object, recursively instrument its functions
      // SETUP_INSTRUMENTATION(obj[key], fullName);
    }
  }

  // Now, each time a function in the object or its sub-objects is called, 
  // the corresponding counter in functionCallCounts will be incremented.
}


class ICTime {
  constructor(years = 0, days = 0, hours = 0, minutes = 0, seconds = 0) {
    this.years = Number(years);
    this.days = Number(days);
    this.hours = Number(hours);
    this.minutes = Number(minutes);
    this.seconds = Number(seconds);
  }

  static makeICObject(timeString) {
    let timeObj = ICTime.parseTime(timeString);
    if (!timeObj) {
      return null;
    }

    let seconds = timeObj.toSeconds();
    let timeformat = timeString.includes(":") ? "time_compact" : "time_units";
    return IC.makeICObject(seconds, "s", timeformat);
  }

  /*
  @param {string} timeString - A string representing a time interval (3h 15m or 3:15).
  */
  static parseTime(timeString) {
    // Preprocess the time string to replace full words with abbreviations
    timeString = timeString.toLowerCase()
      .replace(/\byears?\b/g, 'y')
      .replace(/\bdays?\b/g, 'd')
      .replace(/\bhours?\b/g, 'h')
      .replace(/\bminutes?\b/g, 'm')
      .replace(/\bseconds?\b/g, 's')
      .replaceAll(" ", "");

    let parts = timeString.split(':');

    // Handle colon-separated format
    switch (parts.length) {
      case 2:
        if (parts[1].includes('.')) {
          let [minutes, seconds] = parts.map(Number);
          return new ICTime(0, 0, 0, minutes, seconds);
        }
        return new ICTime(0, 0, parts[0], parts[1]);
      case 3:
        return new ICTime(0, 0, parts[0], parts[1], parts[2]);
    }

    // Handle "XyYdZhZmZs" format
    let match = timeString.match(/(\d+(?:\.\d+)?)([ydhms])/gi);
    if (match) {
      let time = new ICTime();
      for (let part of match) {
        let value = parseFloat(part);
        let unit = part.slice(-1).toLowerCase();
        switch (unit) {
          case 'y':
            time.addYears(value);
            break;
          case 'd':
            time.addDays(value);
            break;
          case 'h':
            time.addHours(value);
            break;
          case 'm':
            time.addMinutes(value);
            break;
          case 's':
            time.addSeconds(value);
            break;
        }
      }
      return time;
    }
    return null;
  }

  /*
@param {number} seconds - The number of seconds to convert to a time string.
@param {string} format - The format to use for the time string (time_units => 3h 15m or time_compact => 3:15).
*/
  static buildTimeString(seconds, format = "time_units") {
    let time_str = LIB.InstaCalc.prototype.fn.time(seconds);

    if (format == "time_compact") {
      // Regular expression to capture years, days, hours, minutes, and seconds
      let regex = /(?:(\d+)y)?\s*(?:(\d+)d)?\s*(?:(\d+)h)?\s*(?:(\d+)m)?\s*(?:(\d+(?:\.\d+)?)s)?/;
      let match = time_str.match(regex);

      if (match) {
        let years = match[1] || "";
        let days = match[2] || "";
        let hours = match[3] || "0";
        let minutes = match[4] || "0";
        let seconds = match[5] || "0";

        // Transform hours, minutes, and seconds into HH:MM:SS format
        let compactTime = `${Number(hours) == 0 ? '00' : hours}:${minutes.padStart(2, '0')}`;
        if (seconds !== "0") {
          compactTime += `:${seconds}`;
        }

        // Reassemble the time string
        time_str = `${years ? years + "y " : ""}${days ? days + "d " : ""}${compactTime}`;
      }
    }
    return time_str.trim();
  }

  add(time) {
    if (time instanceof ICTime) {
      this.addSeconds(time.seconds);
      this.addMinutes(time.minutes);
      this.addHours(time.hours);
      this.addDays(time.days);
      this.addYears(time.years);
    } else {
      return null;
    }
  }

  addYears(years) {
    this.years += years;
  }

  addDays(days) {
    this.days += days;
  }

  addHours(hours) {
    this.days += Math.floor((this.hours + hours) / 24);
    this.hours = (this.hours + hours) % 24;
  }

  addMinutes(minutes) {
    this.addHours(Math.floor((this.minutes + minutes) / 60));
    this.minutes = (this.minutes + minutes) % 60;
  }

  addSeconds(seconds) {
    this.addMinutes(Math.floor((this.seconds + seconds) / 60));
    this.seconds = (this.seconds + seconds) % 60;
  }

  toSeconds() {
    return this.years * 31536000 + this.days * 86400 + this.hours * 3600 + this.minutes * 60 + this.seconds;
  }

  toString() {
    return `${this.years}y ${this.days}d ${this.hours}h ${this.minutes.toString().padStart(2, '0')} ${this.seconds.toString().padStart(2, '0')}`;
  }
}

class ICDate {

  static config = {
    dateSeparators: ['at', '@'],
    timePattern: '\\d{1,2}(?::\\d{2})?\\s*(?:am|pm)?',
    timezonePattern: '(?:\\s*[A-Z]{3,4})?',
    relativeDateKeywords: ['today', 'tomorrow', 'yesterday'],
  };

  static preprocessRelativeDate(input) {
    const { timePattern, timezonePattern, relativeDateKeywords, dateSeparators } = this.config;

    const relativeDatePattern = `(${relativeDateKeywords.join('|')})`;
    const timeWithOptionalTimezone = `(${timePattern}${timezonePattern})`;
    const separatorPattern = dateSeparators.length > 0 ?
      `(?:\\s+(?:${dateSeparators.join('|')})\\s+)?` : '\\s*';

    const patterns = [
      `\\b${timeWithOptionalTimezone}\\s+${relativeDatePattern}`,  // "5pm today"
      `\\b${relativeDatePattern}${separatorPattern}${timeWithOptionalTimezone}?`,  // "today", "today 5pm", "today at 5pm"
    ];

    const relativeDateRegex = new RegExp(patterns.join('|'), 'gi');

    return input.replace(relativeDateRegex, (match, time1, relativeDate1, relativeDate2, time2) => {
      const relativeDate = relativeDate1 || relativeDate2;
      const time = time1 || time2 || '';
      if (relativeDate && time) {
        const processedDate = this.processRelativeDate(relativeDate.toLowerCase(), time.trim());
        return `${processedDate.replace(/[-]/gi, "/")}`;
      }
      return match;
    });
  }

  static processRelativeDate(relativeDate, time) {
    let date = new Date();
    switch (relativeDate) {
      case 'tomorrow':
        date.setDate(date.getDate() + 1);
        break;
      case 'yesterday':
        date.setDate(date.getDate() - 1);
        break;
    }

    if (time) {
      const { timePattern, timezonePattern } = this.config;
      const timeRegex = new RegExp(`^(${timePattern})(${timezonePattern})?$`, 'i');
      const timeMatch = time.match(timeRegex);

      if (timeMatch) {
        const [, timeStr, timezone] = timeMatch;
        const [hourStr, minuteStr = '0'] = timeStr.split(':');
        let [hour, minute] = [parseInt(hourStr), parseInt(minuteStr)];

        const isPM = /pm/i.test(timeStr);
        const isAM = /am/i.test(timeStr);

        // Check if it's 12-hour format
        if (isPM || isAM) {
          // Adjust hour for 12-hour format
          if (hour > 12) {
            IC.log(`Invalid 12-hour time: ${timeStr}`);
            return date.toISOString().split('T')[0];
          }
          if (isPM && hour < 12) {
            hour += 12;
          } else if (isAM && hour === 12) {
            hour = 0;
          }
        } else {
          // 24-hour format
          if (hour >= 24) {
            IC.log(`Invalid 24-hour time: ${timeStr}`);
            return date.toISOString().split('T')[0];
          }
        }

        // Validate minute
        if (minute < 0 || minute > 59) {
          IC.log(`Invalid minutes: ${timeStr}`);
          return date.toISOString().split('T')[0];
        }

        date.setHours(hour, minute, 0, 0);

        let result = date.toISOString().split('T')[0] + ' ' +
          `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        if (timezone) {
          result += ' ' + timezone.trim();
        }
        return result;
      }
    }

    return date.toISOString().split('T')[0];
  }

  static makeICObject(dateString) {
    let dateObj = ICDate.parseDate(dateString);

    // crazy JS date check...
    if (!(dateObj instanceof Date) || isNaN(dateObj)) {
      dateObj = ICDate.parseDateWithSlashOrDotSeparator(dateString);
    }

    // still not a date: jun  
    if (!(dateObj instanceof Date) || isNaN(dateObj)) {
      // split into tokens
      let tokens = dateString.split(/[\s,]+/);
      let restOfString = "";

      // see how many combined tokens are still a valid date...
      for (let i = 1; i < tokens.length; i++) {
        let trialDateObj = ICDate.parseDate(tokens.slice(0, i).join(" "));
        if (trialDateObj instanceof Date && !isNaN(trialDateObj)) {
          dateObj = trialDateObj;
          restOfString = tokens.slice(i).join(" ");
        }
      }

      let timePart = ICDate.parseTimeImpliedDate(restOfString);
      // combine them
      if (timePart) {
        dateObj.setHours(timePart.getHours());
        dateObj.setMinutes(timePart.getMinutes());
        dateObj.setSeconds(timePart.getSeconds());

        // we need _implied to handle the hour display, otherwise 5pm PST becomes midnight and is not displayed
        return IC.makeICObject(dateObj.toISOString(), null, "datetime_ISO_implied");
      }
    }

    if (!dateObj) {
      return null;
    }

    return IC.makeICObject(dateObj.toISOString(), null, "datetime_ISO");
  }

  static convertTimeZoneToNativeDate(dateString) {
    // Extract the timezone abbreviation from the end of the string
    const match = dateString.match(/\b([A-Z]{2,4})\b$/);
    let offsetMinutes;
    let dateObj;

    if (match) {
      const abbreviation = match[1];
      offsetMinutes = ICDate.getTimezoneOffset(abbreviation);
      if (offsetMinutes !== null) {
        // Remove the timezone abbreviation since native JS Date doesn't recognize them
        dateString = dateString.replace(abbreviation, '').trim();
      }
    }

    // Detect German-style dates and convert them to ISO 8601 format
    const germanDateMatch = dateString.match(/^(\d+)\.(\d+)\.(\d+)$/);
    if (germanDateMatch) {
      let [, day, month, year] = germanDateMatch;
      if (day.length === 4) {
        [day, year] = [year, day];
      }

      if (year.length === 2) {
        year = year > 40 ? `19${year}` : `20${year}`;
      }

      if (month > 12) {
        [month, day] = [day, month];
      }

      // parse date manually: 2024.24.12 creates timezone issues
      dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    else {
      //May 25, 2024 3:00PM no supported natively, add space
      dateString = dateString.replace(/(AM|PM)/i, ' $1');
      let timestamp = Date.parse(dateString);
      dateObj = new Date(timestamp);

      // safari doesn't like: 2020-1-1
      if (isNaN(timestamp) && dateString.includes("-")) {
        const [year, month, day] = dateString.split(/[-\/]/).map(Number);
        dateObj = year ? new Date(year, month - 1, day) : new Date(dateString);
      }
    }

    if (offsetMinutes) {
      dateObj.setMinutes(dateObj.getMinutes() + offsetMinutes);
    }

    return dateObj;
  }

  static makeICObjectTimeImpliedDate(dateString) {
    let dateObj = ICDate.parseTimeImpliedDate(dateString);
    if (!dateObj) {
      return null;
    }
    return IC.makeICObject(dateObj.toISOString(), null, "datetime_ISO_implied");
  }

  static parseDate(dateString) {
    let dateObj = this.convertTimeZoneToNativeDate(dateString);

    // if dateString is a short date, like [Month] [day] without a year, add the current year
    if (dateString.match(/^\w+\s*[,]?\s+\d+$/)) {
      dateObj.setFullYear(new Date().getFullYear());
    }

    return dateObj;
  }

  // example: 3/4/23 10:00:00 PM
  static parseDateWithSlashOrDotSeparator(datetimeStr) {
    // Split the datetime string into date and time parts
    const [datePart, timePart] = datetimeStr.split(' ');

    if (!datePart || !timePart) {
      return null;
    }

    // Further split the date and time parts
    const [month, day, yearPart] = datePart.split(/[./]/);
    let year = parseInt(yearPart, 10);

    // Adjust for two-digit year format
    if (year < 100) {
      if (year > 40) {
        year += 1900;
      }
      else {
        year += 2000;
      }
    }

    const timeString = timePart.slice(0, -2);
    const amPm = timePart.slice(-2).toLowerCase();
    let [hour, minute, second = '0'] = timeString.split(':').map(num => parseInt(num, 10));

    // Convert 12-hour format to 24-hour format if needed
    if (amPm === 'pm' && hour !== 12) {
      hour += 12;
    } else if (amPm === 'am' && hour === 12) {
      hour = 0;
    }

    // Construct a Date object
    const date = new Date(year, parseInt(month, 10) - 1, parseInt(day, 10), hour, minute, second);

    // Return the ISO format string
    return date;
  }

  static TIME_ZONE_FROM_MOMENT = {
    "LMT": "Africa/Nairobi",
    "GMT": "Africa/Lagos",
    "EAT": "Africa/Nairobi",
    "PMT": "Europe/Paris",
    "WET": "Europe/Madrid",
    "WEST": "Europe/Madrid",
    "CET": "Europe/Berlin",
    "CEST": "Europe/Berlin",
    "WAT": "Africa/Lagos",
    "CAT": "Africa/Maputo",
    "EET": "Africa/Cairo",
    "EEST": "Africa/Cairo",
    "SAST": "Africa/Johannesburg",
    "CAST": "Africa/Juba",
    "MMT": "Europe/Moscow",
    "WAST": "Africa/Ndjamena",
    "NST": "America/Adak",
    "NWT": "America/Adak",
    "NPT": "America/Adak",
    "BST": "Europe/London",
    "BDT": "America/Adak",
    "AHST": "America/Adak",
    "HST": "America/Adak",
    "HDT": "America/Adak",
    "AST": "America/Anchorage",
    "AWT": "America/Anchorage",
    "APT": "America/Anchorage",
    "AHDT": "America/Anchorage",
    "YST": "America/Anchorage",
    "AKST": "America/Anchorage",
    "AKDT": "America/Anchorage",
    "CMT": "America/Argentina/Buenos_Aires",
    "AMT": "Europe/Athens",
    "EST": "America/Chicago",
    "MST": "America/Denver",
    "CST": "America/Chicago",
    "MDT": "America/Denver",
    "PST": "America/Los_Angeles",
    "CDT": "America/Chicago",
    "ADT": "America/Barbados",
    "CWT": "America/Chicago",
    "CPT": "America/Chicago",
    "BMT": "America/Bogota",
    "PDT": "America/Los_Angeles",
    "MWT": "America/Denver",
    "MPT": "America/Denver",
    "EDT": "America/Toronto",
    "SJMT": "America/Costa_Rica",
    "PWT": "America/Los_Angeles",
    "PPT": "America/Los_Angeles",
    "YDT": "America/Dawson",
    "YWT": "America/Dawson",
    "YPT": "America/Dawson",
    "YDDT": "America/Dawson",
    "EWT": "America/Toronto",
    "EPT": "America/Toronto",
    "NDT": "America/Goose_Bay",
    "ADDT": "America/Goose_Bay",
    "KMT": "America/Grand_Turk",
    "QMT": "America/Guayaquil",
    "HMT": "Asia/Dhaka",
    "FFMT": "America/Martinique",
    "PPMT": "America/Port-au-Prince",
    "SMT": "Asia/Kuala_Lumpur",
    "SDMT": "America/Santo_Domingo",
    "NDDT": "America/St_Johns",
    "PMMT": "Pacific/Port_Moresby",
    "AEST": "Australia/Sydney",
    "AEDT": "Australia/Sydney",
    "NZMT": "Pacific/Auckland",
    "NZST": "Pacific/Auckland",
    "NZDT": "Pacific/Auckland",
    "CEMT": "Europe/Berlin",
    "IST": "Asia/Kolkata",
    "IDT": "Asia/Gaza",
    "PLMT": "Asia/Ho_Chi_Minh",
    "HKT": "Asia/Hong_Kong",
    "HKST": "Asia/Hong_Kong",
    "HKWT": "Asia/Hong_Kong",
    "JST": "Asia/Hong_Kong",
    "IMT": "Europe/Istanbul",
    "WIB": "Asia/Jakarta",
    "WIT": "Asia/Jayapura",
    "JMT": "Asia/Jerusalem",
    "IDDT": "Asia/Jerusalem",
    "PKT": "Asia/Karachi",
    "PKST": "Asia/Karachi",
    "WITA": "Asia/Makassar",
    "KST": "Asia/Seoul",
    "RMT": "Europe/Rome",
    "KDT": "Asia/Seoul",
    "TBMT": "Asia/Tbilisi",
    "TMT": "Asia/Tehran",
    "JDT": "Asia/Tokyo",
    "FMT": "Atlantic/Madeira",
    "ACST": "Australia/Adelaide",
    "ACDT": "Australia/Adelaide",
    "AWST": "Australia/Perth",
    "AWDT": "Australia/Perth",
    "EMT": "Pacific/Easter",
    "DMT": "Europe/Dublin",
    "UTC": "Etc/UTC",
    "BDST": "Europe/London",
    "MSK": "Europe/Moscow",
    "MSD": "Europe/Moscow",
    "WEMT": "Europe/Madrid",
    "MDST": "Europe/Moscow",
    "LST": "Europe/Riga",
    "WMT": "Europe/Warsaw",
    "GST": "Pacific/Guam",
    "GDT": "Pacific/Guam",
    "ChST": "Pacific/Guam",
    "HWT": "Pacific/Honolulu",
    "HPT": "Pacific/Honolulu",
    "SST": "Pacific/Pago_Pago"
  };

  static TIME_ZONE_MAPPING_DEFAULT = {
    // North American time zones
    'EST': 'America/New_York',      // Eastern Standard Time
    'EDT': 'America/New_York',      // Eastern Daylight Time
    'ET': 'America/New_York',       // Eastern Time
    'PST': 'America/Los_Angeles',   // Pacific Standard Time
    'PDT': 'America/Los_Angeles',   // Pacific Daylight Time
    'PT': 'America/Los_Angeles',    // Pacific Time
    'CST': 'America/Chicago',       // Central Standard Time (also China Standard Time)
    'CDT': 'America/Chicago',       // Central Daylight Time
    'CT': 'America/Chicago',        // Central Time
    'MST': 'America/Denver',        // Mountain Standard Time
    'MDT': 'America/Denver',        // Mountain Daylight Time
    'MT': 'America/Denver',         // Mountain Time
    'AST': 'America/Halifax',       // Atlantic Standard Time
    'ADT': 'America/Halifax',       // Atlantic Daylight Time
    'HST': 'Pacific/Honolulu',      // Hawaii Standard Time
    'HAST': 'Pacific/Honolulu',     // Hawaii-Aleutian Standard Time
    'HADT': 'Pacific/Honolulu',     // Hawaii-Aleutian Daylight Time
    'AKST': 'America/Anchorage',    // Alaska Standard Time
    'AKDT': 'America/Anchorage',    // Alaska Daylight Time

    // Other common time zones
    'GMT': 'Etc/GMT',               // Greenwich Mean Time
    'UTC': 'Etc/UTC',               // Coordinated Universal Time
    'BST': 'Europe/London',         // British Summer Time
    'CET': 'Europe/Paris',          // Central European Time
    'CEST': 'Europe/Paris',         // Central European Summer Time
    'IST': 'Asia/Kolkata',          // Indian Standard Time (also Israel Standard Time)
    'AEST': 'Australia/Sydney',     // Australian Eastern Standard Time
    'AEDT': 'Australia/Sydney',     // Australian Eastern Daylight Time
    'AWST': 'Australia/Perth',      // Australian Western Standard Time
    'NZST': 'Pacific/Auckland',     // New Zealand Standard Time
    'NZDT': 'Pacific/Auckland',     // New Zealand Daylight Time

    'ICT': 'Asia/Bangkok',          // Indochina Time

    'MET': 'MET',                  // Middle European Time  
    'MEST': 'MET',

    // ... add more as needed
  };

  static getIANATimeZone(abbreviation) {
    if (!abbreviation) return null;

    // Normalize the abbreviation: UTCPLUS9, UTCMINUS9
    abbreviation = abbreviation.toUpperCase().replace(/PLUS/g, '+').replace(/MINUS/g, '-');
    const match = abbreviation.match(/^(UTC|GMT)([+-][0-9]+)?$/);
    if (match) {
      let offset = match[2] || '+0';
      // For 'Etc/GMT', reverse the sign
      offset = offset.replace(/^([+-])/, (sign) => (sign === '+' ? '-' : '+'));
      return `Etc/GMT${offset}`; // IANA time zone 
    }

    return this.TIME_ZONE_MAPPING_DEFAULT[abbreviation] || this.TIME_ZONE_FROM_MOMENT[abbreviation] || null;
  }

  static getTimezoneOffset(timezone) {
    if (!timezone) {
      return null;
    }
    const localTime = new Date();

    // Check for UTC±X or GMT±X format
    const match = timezone.match(/^(UTC|GMT)([+-])(\d+)$/i);
    if (match) {
      const [, prefix, sign, hours] = match;
      const offsetMinutes = (sign === '-' ? 1 : -1) * hours * 60; // Convert hours to minutes
      const localOffset = localTime.getTimezoneOffset(); // Local offset in minutes
      return offsetMinutes - localOffset; // Adjust for local timezone
    }

    let tz = this.getIANATimeZone(timezone);
    if (tz == null) {
      return null;
    }

    const timezoneTime = new Date(localTime.toLocaleString('en-US', { timeZone: tz }));
    const difference = Math.round((localTime.getTime() - timezoneTime.getTime()) / (60 * 1000));
    return difference;
  }

  static parseTimeImpliedDate(dateString) {
    // @ts-ignore
    const TIME_IMPLIED_DATE = (typeof window !== 'undefined' && window.TIME_IMPLIED_DATE) || null;
    if (!TIME_IMPLIED_DATE) return null;
    const match = dateString.match(TIME_IMPLIED_DATE);

    const TIMEZONEREGEX = "\\s*(?:UTC[+-][0-9]+|GMT[+-][0-9]+|[A-Za-z]{2,4})"; // Timezone: PST, GMT+5, UTC+5

    //    const [_, hour, minute, second, period, timezone] = match[0].match(/(\d{1,2})(?::(\d{1,2}))?(?::(\d{1,2}))?\s*(am|pm)?\s*([A-Z]{2,4}([+-]\d+)?)?/i) || [];

    const [_, hour, minute, second, period, timezone] =
      match[0].match(new RegExp(`(\\d{1,2})(?::(\\d{1,2}))?(?::(\\d{1,2}))?\\s*(am|pm)?\\s*(${TIMEZONEREGEX})?`, 'i')) || [];


    if (!hour) {
      return null;
    }

    let parsedHour = parseInt(hour, 10);
    let parsedMinute = parseInt(minute || '0', 10);
    let parsedSecond = parseInt(second || '0', 10);

    // Convert 12-hour format to 24-hour format
    if (period && period.toLowerCase() === 'pm' && parsedHour !== 12) {
      parsedHour += 12;
    } else if (period && period.toLowerCase() === 'am' && parsedHour === 12) {
      parsedHour = 0;
    }

    const dateObj = new Date();
    dateObj.setHours(parsedHour, parsedMinute, parsedSecond, 0);

    // If timezone is provided, adjust the dateObj based on the timezone offset
    if (timezone) {
      const offsetMinutes = ICDate.getTimezoneOffset(timezone);
      if (offsetMinutes == null) {
        return null;
      }
      dateObj.setMinutes(dateObj.getMinutes() + offsetMinutes); // Add the offset
    }

    return dateObj;
  }


  // convert date to unit
  // unit: timezone, or perhaps another date (give difference)
  static convertTo(result, unit) {
    let dateObj = new Date(IC.val(result));
    let tzConversion = ICDate.toTimeZone(dateObj, unit);
    return tzConversion;
  }

  static toTimeZone(dateObj, newTimeZone) {
    let tz = this.getIANATimeZone(newTimeZone);
    if (tz == null || !dateObj) {
      return null;
    }

    // Get the current time in the provided timezone
    const timezoneTime = dateObj.toLocaleString('en-US',
      {
        timeZone: tz,
        timeZoneName: 'short',
        hour12: true,
        hour: 'numeric',
        minute: '2-digit'
      });
    return this.cleanupDateTimeString(timezoneTime);
  }

  static cleanupDateTimeString(dateString) {
    return dateString;
  }

  static isDateTimeObject(obj) {
    let val = IC.val(obj);
    let format = IC.format(obj);
    return typeof val === "string" && format.includes("datetime");
  }

  static toString(dateObj) {
    let str = IC.val(dateObj);
    let date = new Date(str);
    let format = IC.format(dateObj);

    const dateOptions = {
      weekday: 'short', // "Mon"
      year: 'numeric',  // "1994"
      month: 'short',   // "Nov"
      day: 'numeric'    // "5"
    };

    const timeOptions = {
      hour12: true,
      hour: 'numeric',
      minute: '2-digit'
    };

    // Add seconds to timeOptions only if they are non-zero
    if (date.getSeconds() !== 0) {
      timeOptions.second = '2-digit';
    }

    // show timezone if format has _implied
    if (format.includes("_implied")) {
      timeOptions.timeZoneName = 'short';
    }

    // @ts-expect-error
    const formattedDate = date.toLocaleDateString('en-US', dateOptions);
    // @ts-expect-error
    const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

    let defaultRet = `${formattedDate} ${formattedTime}`;
    if (format.includes("_implied")) {
      return defaultRet;
    }

    // if date obj is T00:00:00.000Z, return just the date
    if (date.toISOString().includes("T00:00:00.000Z") || formattedTime === "12:00 AM") {
      return formattedDate;
    }

    return defaultRet;
  }

  static addDates(dateObj, timeObj) {
    // the order could be swapped
    if (IC.hasType(dateObj) && IC.typestringIsSameUnitCategory(IC.typestring(dateObj.t), "s")) {
      // swap
      let temp = dateObj;
      dateObj = timeObj;
      timeObj = temp;
    }

    let date = new Date(IC.val(dateObj));
    const originalHour = date.getUTCHours();
    const originalMinute = date.getUTCMinutes();
    const originalSecond = date.getUTCSeconds();
    const originalMillisecond = date.getUTCMilliseconds();

    // default to day addition
    if (typeof timeObj === "number") {
      timeObj = IC.makeICObject(timeObj, "days");
    }

    // convert time object to seconds
    let secondsResult = IC.ICObjectConvertToType(timeObj, "s");
    let seconds = IC.val(secondsResult);

    // TODO -- the tricky thing is dealing with time zone changes. Not sure how clever to be here.
    let typeString = IC.hasType(timeObj) ? IC.typestring(timeObj.t) : null;
    switch (typeString) {
      case "month":
      case "months":
        let months = IC.val(timeObj);
        let wholeMonths = Math.floor(months);
        let remainingDays = (months - wholeMonths) * 30; // Approximate the remaining fraction as days
        date.setMonth(date.getMonth() + wholeMonths);
        date.setDate(date.getDate() + remainingDays);
        break;

      case "year":
      case "years":
        date.setFullYear(date.getFullYear() + IC.val(timeObj))
        break;

      default:
        // add seconds to date
        date.setTime(date.getTime() + (seconds * 1000));
        if (seconds % 86400 === 0) {
          date.setUTCHours(originalHour);
          date.setUTCMinutes(originalMinute);
          date.setUTCSeconds(originalSecond);
          date.setUTCMilliseconds(originalMillisecond);
        }
    }

    // reset time component to original values only if the added seconds 
    // is a multiple of a day (i.e., 86400 seconds). Otherwise, keep the changed time.

    let clone = IC.cloneObject(dateObj);
    clone.v = date.toISOString();
    return clone;
  }

  static subDates(dateObj, otherObj) {
    let otherVal = IC.val(otherObj);

    // if other obj is a date string, find the difference
    if (this.isDateString(otherVal)) {
      let first = IC.val(dateObj)

      // get difference between dates. Sometimes there's a difference because of DST
      // @ts-expect-error
      let diff = new Date(first) - new Date(otherVal);

      // convert to an absolute number of days
      let days = Math.abs(Math.round(diff / (1000 * 60 * 60 * 24)));
      let type = days == 1 ? "day" : "days"
      return IC.makeICObject(days, type);
    }

    // assume it's a time offset
    return this.addDates(dateObj, IC.mathNegate(otherObj));
  }

  static isDateString(dateString) {
    // Check if the date string is purely numeric
    if (/^\d+$/.test(dateString)) {
      return false;
    }
    // Check if the date string is a valid date
    return !isNaN(Date.parse(dateString));
  }

  /*
  // @ts-ignore
  const timeZones = Intl.supportedValuesOf('timeZone');
 
  timeZones.forEach(timeZone => {
    const formatted = new Intl.DateTimeFormat('en-US', { timeZone: timeZone, timeZoneName: 'short' }).format(new Date());
    const match = formatted.match(/[A-Z]{2,4}$/); // Match 2 to 4 uppercase letters at the end of the string
    const abbreviation = match ? match[0] : undefined;
 
    if (abbreviation && abbreviation.length <= 4) {
      this.TIME_ZONE_MAPPING[abbreviation] ||= timeZone;
    }
  });
  */

}

/**
 * Simple Performance Logger
 *
 * Usage:
 * PerfLog.start("myTask");
 * // ... do the task ...
 * PerfLog.end("myTask", optionalDebugLogArray);
 *
 * To enable/disable, set PerfLog.enabled = true/false;
 * (Default is based on URL query param 'timing')
 */
const PerfLog = {
  // Enable logging if the URL contains "?timing" or "&timing"
  enabled: window?.location?.search?.includes('timing') || false,

  // Internal store for start times
  _startTimes: {},

  /**
   * Starts a performance timer for a given name.
   * @param {string} name - Unique identifier for the timer.
   */
  start: function (name) {
    if (this.enabled) {
      this._startTimes[name] = performance.now();
      // Optional: Log start event immediately if needed
    }
  },

  /**
   * Ends a performance timer, calculates duration, and logs the result.
   * @param {string} name - Unique identifier for the timer (must match a previous start call).
   * @param {Array<[string, any] | string>} [logArray=null] - Optional array to push the log entry into.
   *                                                        If provided, the entry will be `"[Perf] name: durationMs"`.
   *                                                        If null, logs directly to console.
   * @returns {number | null} The duration in milliseconds, or null if not enabled or timer not found.
   */
  end: function (name, logArray = null) {
    if (this.enabled && this._startTimes[name] !== undefined) {
      const endTime = performance.now();
      const duration = endTime - this._startTimes[name];
      const logEntry = `[Perf] ${name}: ${duration.toFixed(2)}ms`;

      if (logArray && Array.isArray(logArray)) {
        // Find the entry for this step's result and insert timing after it,
        // Or just push if the primary entry isn't easily found by name match.
        let inserted = false;
        const stepNameWithoutCounter = name.substring(name.indexOf('_') + 1); // Try to match description without counter
        for (let i = logArray.length - 1; i >= 0; i--) {
          if (Array.isArray(logArray[i]) && logArray[i][0] === name) { // Match full numbered name
            logArray.splice(i + 1, 0, logEntry);
            inserted = true;
            break;
          }
          // Fallback: Match description part if full name not found (less precise insertion)
          if (!inserted && Array.isArray(logArray[i]) && logArray[i][0].endsWith(stepNameWithoutCounter)) {
            logArray.splice(i + 1, 0, logEntry);
            inserted = true;
            // Keep searching in case the full name appears later
          }
        }
        // If still not inserted (e.g., very first timing), just push
        if (!inserted) {
          logArray.push(logEntry);
        }
      } else {
        // Fallback to console if no array provided or not an array
        console.log(logEntry);
      }

      delete this._startTimes[name]; // Clean up the start time
      return duration;
    }
    return null; // Return null if not enabled or timer wasn't started
  },

  /**
   * Wraps a function execution with start/end timing.
   * @param {string} name - Unique identifier for the timer.
   * @param {Function} func - The function to execute and time.
   * @param {Array<[string, any] | string>} [logArray=null] - Optional array to push the log entry into.
   * @returns {Function} A new function that, when called, executes the original function,
   *                     logs its performance, and returns the original function's result.
   */
  time: function (name, func, logArray = null) {
    if (!this.enabled) {
      return func; // Return original function if not enabled
    }

    const self = this; // Capture 'this' context of PerfLog
    return function (...args) {
      self.start(name);
      let result;
      try {
        result = func.apply(this, args); // Preserve original 'this' context for func call
      } finally {
        // Ensure end is called even if func throws an error
        self.end(name, logArray);
      }
      return result;
    };
  },

  /**
   * Resets the internal start times (useful for tests or restarts).
   */
  reset: function () {
    this._startTimes = {};
  }
};

class Chart {
  static BAR = "barchart";
  static LINE = "linechart";
  static PIE = "piechart";

  static getChartData(values, type, labels = null) {
    if (!Array.isArray(values)) return null;

    const meta = labels ? null : IC.envGetRowRangeMeta();

    const data = values.map((value, idx) => {
      let label;
      if (labels && Array.isArray(labels)) {
        label = IC.val(labels[idx]);
      } else {
        label = meta?.[idx]?.title ?? `#${idx + 1}`;
      }

      // If value is already a {label, value} object, use it (from histogram)
      if (value && typeof value === 'object' && 'value' in value) {
        return {
          label: value.label ?? String(idx + 1),
          value: IC.val(value.value)
        };
      }

      return {
        label: label,
        value: IC.val(value)
      };
    });

    return {
      data: data,
      type: type,
      startIndex: IC.envGetTemp("startIndex"),
      endIndex: IC.envGetTemp("endIndex")
    };
  }
}

// SETUP_INSTRUMENTATION(ICUnit);
//SETUP_INSTRUMENTATION(IC);
//SETUP_INSTRUMENTATION(IC.parser);
//SETUP_INSTRUMENTATION(ICUnit);
// semicolon required, cannot end with comment
;

// UMD wrapper for Node.js/Jest compatibility
(function (root) {
  if (typeof module === 'object' && module.exports) {
    module.exports = { IC, CONFIG, LIB };
  } else {
    // Browser globals
    root.IC = IC;
    root.CONFIG = CONFIG;
    root.LIB = LIB;
  }
})(typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : this));
;
var __ICREACT = (() => {
  var __create = Object.create;
  var __defProp = Object.defineProperty;
  var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
  var __getOwnPropNames = Object.getOwnPropertyNames;
  var __getProtoOf = Object.getPrototypeOf;
  var __hasOwnProp = Object.prototype.hasOwnProperty;
  var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
  var __esm = (fn, res) => function __init() {
    return fn && (res = (0, fn[__getOwnPropNames(fn)[0]])(fn = 0)), res;
  };
  var __commonJS = (cb, mod) => function __require() {
    return mod || (0, cb[__getOwnPropNames(cb)[0]])((mod = { exports: {} }).exports, mod), mod.exports;
  };
  var __export = (target, all) => {
    for (var name in all)
      __defProp(target, name, { get: all[name], enumerable: true });
  };
  var __copyProps = (to, from, except, desc) => {
    if (from && typeof from === "object" || typeof from === "function") {
      for (let key of __getOwnPropNames(from))
        if (!__hasOwnProp.call(to, key) && key !== except)
          __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
    }
    return to;
  };
  var __toESM = (mod, isNodeMode, target) => (target = mod != null ? __create(__getProtoOf(mod)) : {}, __copyProps(
    // If the importer is in node compatibility mode or this is not an ESM
    // file that has been converted to a CommonJS file using a Babel-
    // compatible transform (i.e. "__esModule" has not been set), then set
    // "default" to the CommonJS "module.exports" for node compatibility.
    isNodeMode || !mod || !mod.__esModule ? __defProp(target, "default", { value: mod, enumerable: true }) : target,
    mod
  ));
  var __toCommonJS = (mod) => __copyProps(__defProp({}, "__esModule", { value: true }), mod);

  // node_modules/react/cjs/react.production.min.js
  var require_react_production_min = __commonJS({
    "node_modules/react/cjs/react.production.min.js"(exports) {
      "use strict";
      var l = Symbol.for("react.element");
      var n = Symbol.for("react.portal");
      var p = Symbol.for("react.fragment");
      var q = Symbol.for("react.strict_mode");
      var r = Symbol.for("react.profiler");
      var t = Symbol.for("react.provider");
      var u = Symbol.for("react.context");
      var v = Symbol.for("react.forward_ref");
      var w = Symbol.for("react.suspense");
      var x = Symbol.for("react.memo");
      var y = Symbol.for("react.lazy");
      var z = Symbol.iterator;
      function A(a) {
        if (null === a || "object" !== typeof a) return null;
        a = z && a[z] || a["@@iterator"];
        return "function" === typeof a ? a : null;
      }
      __name(A, "A");
      var B = { isMounted: /* @__PURE__ */ __name(function() {
        return false;
      }, "isMounted"), enqueueForceUpdate: /* @__PURE__ */ __name(function() {
      }, "enqueueForceUpdate"), enqueueReplaceState: /* @__PURE__ */ __name(function() {
      }, "enqueueReplaceState"), enqueueSetState: /* @__PURE__ */ __name(function() {
      }, "enqueueSetState") };
      var C = Object.assign;
      var D = {};
      function E(a, b, e) {
        this.props = a;
        this.context = b;
        this.refs = D;
        this.updater = e || B;
      }
      __name(E, "E");
      E.prototype.isReactComponent = {};
      E.prototype.setState = function(a, b) {
        if ("object" !== typeof a && "function" !== typeof a && null != a) throw Error("setState(...): takes an object of state variables to update or a function which returns an object of state variables.");
        this.updater.enqueueSetState(this, a, b, "setState");
      };
      E.prototype.forceUpdate = function(a) {
        this.updater.enqueueForceUpdate(this, a, "forceUpdate");
      };
      function F() {
      }
      __name(F, "F");
      F.prototype = E.prototype;
      function G(a, b, e) {
        this.props = a;
        this.context = b;
        this.refs = D;
        this.updater = e || B;
      }
      __name(G, "G");
      var H = G.prototype = new F();
      H.constructor = G;
      C(H, E.prototype);
      H.isPureReactComponent = true;
      var I = Array.isArray;
      var J = Object.prototype.hasOwnProperty;
      var K = { current: null };
      var L = { key: true, ref: true, __self: true, __source: true };
      function M(a, b, e) {
        var d, c = {}, k = null, h = null;
        if (null != b) for (d in void 0 !== b.ref && (h = b.ref), void 0 !== b.key && (k = "" + b.key), b) J.call(b, d) && !L.hasOwnProperty(d) && (c[d] = b[d]);
        var g = arguments.length - 2;
        if (1 === g) c.children = e;
        else if (1 < g) {
          for (var f = Array(g), m = 0; m < g; m++) f[m] = arguments[m + 2];
          c.children = f;
        }
        if (a && a.defaultProps) for (d in g = a.defaultProps, g) void 0 === c[d] && (c[d] = g[d]);
        return { $$typeof: l, type: a, key: k, ref: h, props: c, _owner: K.current };
      }
      __name(M, "M");
      function N(a, b) {
        return { $$typeof: l, type: a.type, key: b, ref: a.ref, props: a.props, _owner: a._owner };
      }
      __name(N, "N");
      function O(a) {
        return "object" === typeof a && null !== a && a.$$typeof === l;
      }
      __name(O, "O");
      function escape(a) {
        var b = { "=": "=0", ":": "=2" };
        return "$" + a.replace(/[=:]/g, function(a2) {
          return b[a2];
        });
      }
      __name(escape, "escape");
      var P = /\/+/g;
      function Q(a, b) {
        return "object" === typeof a && null !== a && null != a.key ? escape("" + a.key) : b.toString(36);
      }
      __name(Q, "Q");
      function R(a, b, e, d, c) {
        var k = typeof a;
        if ("undefined" === k || "boolean" === k) a = null;
        var h = false;
        if (null === a) h = true;
        else switch (k) {
          case "string":
          case "number":
            h = true;
            break;
          case "object":
            switch (a.$$typeof) {
              case l:
              case n:
                h = true;
            }
        }
        if (h) return h = a, c = c(h), a = "" === d ? "." + Q(h, 0) : d, I(c) ? (e = "", null != a && (e = a.replace(P, "$&/") + "/"), R(c, b, e, "", function(a2) {
          return a2;
        })) : null != c && (O(c) && (c = N(c, e + (!c.key || h && h.key === c.key ? "" : ("" + c.key).replace(P, "$&/") + "/") + a)), b.push(c)), 1;
        h = 0;
        d = "" === d ? "." : d + ":";
        if (I(a)) for (var g = 0; g < a.length; g++) {
          k = a[g];
          var f = d + Q(k, g);
          h += R(k, b, e, f, c);
        }
        else if (f = A(a), "function" === typeof f) for (a = f.call(a), g = 0; !(k = a.next()).done; ) k = k.value, f = d + Q(k, g++), h += R(k, b, e, f, c);
        else if ("object" === k) throw b = String(a), Error("Objects are not valid as a React child (found: " + ("[object Object]" === b ? "object with keys {" + Object.keys(a).join(", ") + "}" : b) + "). If you meant to render a collection of children, use an array instead.");
        return h;
      }
      __name(R, "R");
      function S(a, b, e) {
        if (null == a) return a;
        var d = [], c = 0;
        R(a, d, "", "", function(a2) {
          return b.call(e, a2, c++);
        });
        return d;
      }
      __name(S, "S");
      function T(a) {
        if (-1 === a._status) {
          var b = a._result;
          b = b();
          b.then(function(b2) {
            if (0 === a._status || -1 === a._status) a._status = 1, a._result = b2;
          }, function(b2) {
            if (0 === a._status || -1 === a._status) a._status = 2, a._result = b2;
          });
          -1 === a._status && (a._status = 0, a._result = b);
        }
        if (1 === a._status) return a._result.default;
        throw a._result;
      }
      __name(T, "T");
      var U = { current: null };
      var V = { transition: null };
      var W = { ReactCurrentDispatcher: U, ReactCurrentBatchConfig: V, ReactCurrentOwner: K };
      function X() {
        throw Error("act(...) is not supported in production builds of React.");
      }
      __name(X, "X");
      exports.Children = { map: S, forEach: /* @__PURE__ */ __name(function(a, b, e) {
        S(a, function() {
          b.apply(this, arguments);
        }, e);
      }, "forEach"), count: /* @__PURE__ */ __name(function(a) {
        var b = 0;
        S(a, function() {
          b++;
        });
        return b;
      }, "count"), toArray: /* @__PURE__ */ __name(function(a) {
        return S(a, function(a2) {
          return a2;
        }) || [];
      }, "toArray"), only: /* @__PURE__ */ __name(function(a) {
        if (!O(a)) throw Error("React.Children.only expected to receive a single React element child.");
        return a;
      }, "only") };
      exports.Component = E;
      exports.Fragment = p;
      exports.Profiler = r;
      exports.PureComponent = G;
      exports.StrictMode = q;
      exports.Suspense = w;
      exports.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED = W;
      exports.act = X;
      exports.cloneElement = function(a, b, e) {
        if (null === a || void 0 === a) throw Error("React.cloneElement(...): The argument must be a React element, but you passed " + a + ".");
        var d = C({}, a.props), c = a.key, k = a.ref, h = a._owner;
        if (null != b) {
          void 0 !== b.ref && (k = b.ref, h = K.current);
          void 0 !== b.key && (c = "" + b.key);
          if (a.type && a.type.defaultProps) var g = a.type.defaultProps;
          for (f in b) J.call(b, f) && !L.hasOwnProperty(f) && (d[f] = void 0 === b[f] && void 0 !== g ? g[f] : b[f]);
        }
        var f = arguments.length - 2;
        if (1 === f) d.children = e;
        else if (1 < f) {
          g = Array(f);
          for (var m = 0; m < f; m++) g[m] = arguments[m + 2];
          d.children = g;
        }
        return { $$typeof: l, type: a.type, key: c, ref: k, props: d, _owner: h };
      };
      exports.createContext = function(a) {
        a = { $$typeof: u, _currentValue: a, _currentValue2: a, _threadCount: 0, Provider: null, Consumer: null, _defaultValue: null, _globalName: null };
        a.Provider = { $$typeof: t, _context: a };
        return a.Consumer = a;
      };
      exports.createElement = M;
      exports.createFactory = function(a) {
        var b = M.bind(null, a);
        b.type = a;
        return b;
      };
      exports.createRef = function() {
        return { current: null };
      };
      exports.forwardRef = function(a) {
        return { $$typeof: v, render: a };
      };
      exports.isValidElement = O;
      exports.lazy = function(a) {
        return { $$typeof: y, _payload: { _status: -1, _result: a }, _init: T };
      };
      exports.memo = function(a, b) {
        return { $$typeof: x, type: a, compare: void 0 === b ? null : b };
      };
      exports.startTransition = function(a) {
        var b = V.transition;
        V.transition = {};
        try {
          a();
        } finally {
          V.transition = b;
        }
      };
      exports.unstable_act = X;
      exports.useCallback = function(a, b) {
        return U.current.useCallback(a, b);
      };
      exports.useContext = function(a) {
        return U.current.useContext(a);
      };
      exports.useDebugValue = function() {
      };
      exports.useDeferredValue = function(a) {
        return U.current.useDeferredValue(a);
      };
      exports.useEffect = function(a, b) {
        return U.current.useEffect(a, b);
      };
      exports.useId = function() {
        return U.current.useId();
      };
      exports.useImperativeHandle = function(a, b, e) {
        return U.current.useImperativeHandle(a, b, e);
      };
      exports.useInsertionEffect = function(a, b) {
        return U.current.useInsertionEffect(a, b);
      };
      exports.useLayoutEffect = function(a, b) {
        return U.current.useLayoutEffect(a, b);
      };
      exports.useMemo = function(a, b) {
        return U.current.useMemo(a, b);
      };
      exports.useReducer = function(a, b, e) {
        return U.current.useReducer(a, b, e);
      };
      exports.useRef = function(a) {
        return U.current.useRef(a);
      };
      exports.useState = function(a) {
        return U.current.useState(a);
      };
      exports.useSyncExternalStore = function(a, b, e) {
        return U.current.useSyncExternalStore(a, b, e);
      };
      exports.useTransition = function() {
        return U.current.useTransition();
      };
      exports.version = "18.3.1";
    }
  });

  // node_modules/react/index.js
  var require_react = __commonJS({
    "node_modules/react/index.js"(exports, module) {
      "use strict";
      if (true) {
        module.exports = require_react_production_min();
      } else {
        module.exports = null;
      }
    }
  });

  // public/src/react/react-global.js
  var import_react, useState, useEffect, useContext, useReducer, useCallback, useMemo, useRef, useImperativeHandle, useLayoutEffect, useDebugValue, useSyncExternalStore, createContext, react_global_default;
  var init_react_global = __esm({
    "public/src/react/react-global.js"() {
      import_react = __toESM(require_react());
      ({
        useState,
        useEffect,
        useContext,
        useReducer,
        useCallback,
        useMemo,
        useRef,
        useImperativeHandle,
        useLayoutEffect,
        useDebugValue,
        useSyncExternalStore,
        createContext
      } = import_react.default);
      react_global_default = import_react.default;
    }
  });

  // node_modules/react/cjs/react-jsx-runtime.production.min.js
  var require_react_jsx_runtime_production_min = __commonJS({
    "node_modules/react/cjs/react-jsx-runtime.production.min.js"(exports) {
      "use strict";
      var f = require_react();
      var k = Symbol.for("react.element");
      var l = Symbol.for("react.fragment");
      var m = Object.prototype.hasOwnProperty;
      var n = f.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner;
      var p = { key: true, ref: true, __self: true, __source: true };
      function q(c, a, g) {
        var b, d = {}, e = null, h = null;
        void 0 !== g && (e = "" + g);
        void 0 !== a.key && (e = "" + a.key);
        void 0 !== a.ref && (h = a.ref);
        for (b in a) m.call(a, b) && !p.hasOwnProperty(b) && (d[b] = a[b]);
        if (c && c.defaultProps) for (b in a = c.defaultProps, a) void 0 === d[b] && (d[b] = a[b]);
        return { $$typeof: k, type: c, key: e, ref: h, props: d, _owner: n.current };
      }
      __name(q, "q");
      exports.Fragment = l;
      exports.jsx = q;
      exports.jsxs = q;
    }
  });

  // node_modules/react/jsx-runtime.js
  var require_jsx_runtime = __commonJS({
    "node_modules/react/jsx-runtime.js"(exports, module) {
      "use strict";
      if (true) {
        module.exports = require_react_jsx_runtime_production_min();
      } else {
        module.exports = null;
      }
    }
  });

  // public/src/react/components/common/Icon.jsx
  var import_jsx_runtime, Icon, Icons, Icon_default;
  var init_Icon = __esm({
    "public/src/react/components/common/Icon.jsx"() {
      init_react_global();
      import_jsx_runtime = __toESM(require_jsx_runtime());
      Icon = /* @__PURE__ */ __name(({ name, size: size4 = 16, className = "" }) => {
        const sizeClass = className || `w-${size4 / 4} h-${size4 / 4}`;
        return /* @__PURE__ */ (0, import_jsx_runtime.jsx)(
          "svg",
          {
            className: sizeClass,
            width: size4,
            height: size4,
            children: /* @__PURE__ */ (0, import_jsx_runtime.jsx)("use", { href: `#icon-${name}` })
          }
        );
      }, "Icon");
      Icons = {
        spinner: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "spinner", className }), "spinner"),
        chevronDown: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "chevronDown", className }), "chevronDown"),
        sparkles: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "sparkles", className }), "sparkles"),
        menu: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "menu", className }), "menu"),
        pencil: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "pencil", className }), "pencil"),
        lockOpen: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "lockOpen", className }), "lockOpen"),
        documentDuplicate: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "documentDuplicate", className }), "documentDuplicate"),
        copy: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "copy", className }), "copy"),
        trash: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "trash", className }), "trash"),
        download: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "download", className }), "download"),
        upload: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "upload", className }), "upload"),
        sort: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "sort", className }), "sort"),
        clock: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "clock", className }), "clock"),
        folderPlus: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "folderPlus", className }), "folderPlus"),
        folder: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "folder", className }), "folder"),
        google: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "google", className }), "google"),
        apple: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "apple", className }), "apple"),
        heart: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "heart", className }), "heart"),
        dollarSign: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "dollarSign", className }), "dollarSign"),
        beaker: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "beaker", className }), "beaker"),
        code: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "code", className }), "code"),
        rotate: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "rotate", className }), "rotate"),
        remix: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "remix", className }), "remix"),
        cube: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "cube", className }), "cube"),
        wrench: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "wrench", className }), "wrench"),
        puzzlePiece: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "puzzlePiece", className }), "puzzlePiece"),
        insertAbove: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "insertAbove", className }), "insertAbove"),
        insertBelow: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "insertBelow", className }), "insertBelow"),
        chevronLeft: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "chevronLeft", className }), "chevronLeft"),
        chevronRight: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "chevronRight", className }), "chevronRight"),
        chevronUp: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "chevronUp", className }), "chevronUp"),
        chevronDoubleDown: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "chevronDoubleDown", className }), "chevronDoubleDown"),
        chevronDoubleUp: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "chevronDoubleUp", className }), "chevronDoubleUp"),
        chevronDoubleRight: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "chevronDoubleRight", className }), "chevronDoubleRight"),
        xCircle: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "xCircle", className }), "xCircle"),
        info: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "info", className }), "info"),
        pin: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "pin", className }), "pin"),
        minimize: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "minimize", className }), "minimize"),
        maximize: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "maximize", className }), "maximize"),
        close: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "close", className }), "close"),
        plus: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "plus", className }), "plus"),
        minus: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "minus", className }), "minus"),
        plusCircle: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "plusCircle", className }), "plusCircle"),
        minusCircle: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "minusCircle", className }), "minusCircle"),
        globe: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "globe", className }), "globe"),
        sphere: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "sphere", className }), "sphere"),
        presentation: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "presentation", className }), "presentation"),
        calculator: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "calculator", className }), "calculator"),
        modeText: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "modeText", className }), "modeText"),
        modePresent: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "modePresent", className }), "modePresent"),
        target: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "target", className }), "target"),
        eraser: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "eraser", className }), "eraser"),
        history: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "history", className }), "history"),
        undo: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "undo", className }), "undo"),
        dotsHorizontal: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "dotsHorizontal", className }), "dotsHorizontal"),
        search: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "search", className }), "search"),
        sortDown: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "sortDown", className }), "sortDown"),
        check: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "check", className }), "check"),
        link: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "link", className }), "link"),
        shareUp: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "shareUp", className }), "shareUp"),
        infoCircle: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "infoCircle", className }), "infoCircle"),
        exclamationTriangle: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "exclamationTriangle", className }), "exclamationTriangle"),
        lightBulb: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "lightBulb", className }), "lightBulb"),
        bookOpen: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "bookOpen", className }), "bookOpen"),
        adjustments: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "adjustments", className }), "adjustments"),
        share: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "share", className }), "share"),
        documentText: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "documentText", className }), "documentText"),
        logout: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "logout", className }), "logout"),
        delta: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "delta", className }), "delta"),
        dragHandle: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "dragHandle", className }), "dragHandle"),
        gear: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "gear", className }), "gear"),
        lightning: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "lightning", className }), "lightning"),
        users: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "users", className }), "users"),
        chatBubble: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "chatBubble", className }), "chatBubble"),
        bookmark: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "bookmark", className }), "bookmark"),
        lock: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "lock", className }), "lock"),
        eye: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "eye", className }), "eye"),
        eyeOff: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "eyeOff", className }), "eyeOff"),
        userCircle: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "userCircle", className }), "userCircle"),
        heartFilled: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "heartFilled", className }), "heartFilled"),
        success: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "check", className }), "success"),
        hashSymbol: /* @__PURE__ */ __name((className = "w-4 h-4") => /* @__PURE__ */ (0, import_jsx_runtime.jsx)(Icon, { name: "hashSymbol", className }), "hashSymbol")
      };
      Icons.publicLink = Icons.link;
      Icons.privateLink = Icons.lockOpen;
      Icons.shareButton = Icons.upload;
      Icons.edit = Icons.pencil;
      Icons.delete = Icons.trash;
      Icons.reset = Icons.rotate;
      Icons.clear = Icons.eraser;
      Icons.history = Icons.clock;
      Icons.localeFormat = Icons.sphere;
      Icon_default = Icons;
    }
  });

  // node_modules/scheduler/cjs/scheduler.production.min.js
  var require_scheduler_production_min = __commonJS({
    "node_modules/scheduler/cjs/scheduler.production.min.js"(exports) {
      "use strict";
      function f(a, b) {
        var c = a.length;
        a.push(b);
        a: for (; 0 < c; ) {
          var d = c - 1 >>> 1, e = a[d];
          if (0 < g(e, b)) a[d] = b, a[c] = e, c = d;
          else break a;
        }
      }
      __name(f, "f");
      function h(a) {
        return 0 === a.length ? null : a[0];
      }
      __name(h, "h");
      function k(a) {
        if (0 === a.length) return null;
        var b = a[0], c = a.pop();
        if (c !== b) {
          a[0] = c;
          a: for (var d = 0, e = a.length, w = e >>> 1; d < w; ) {
            var m = 2 * (d + 1) - 1, C = a[m], n = m + 1, x = a[n];
            if (0 > g(C, c)) n < e && 0 > g(x, C) ? (a[d] = x, a[n] = c, d = n) : (a[d] = C, a[m] = c, d = m);
            else if (n < e && 0 > g(x, c)) a[d] = x, a[n] = c, d = n;
            else break a;
          }
        }
        return b;
      }
      __name(k, "k");
      function g(a, b) {
        var c = a.sortIndex - b.sortIndex;
        return 0 !== c ? c : a.id - b.id;
      }
      __name(g, "g");
      if ("object" === typeof performance && "function" === typeof performance.now) {
        l = performance;
        exports.unstable_now = function() {
          return l.now();
        };
      } else {
        p = Date, q = p.now();
        exports.unstable_now = function() {
          return p.now() - q;
        };
      }
      var l;
      var p;
      var q;
      var r = [];
      var t = [];
      var u = 1;
      var v = null;
      var y = 3;
      var z = false;
      var A = false;
      var B = false;
      var D = "function" === typeof setTimeout ? setTimeout : null;
      var E = "function" === typeof clearTimeout ? clearTimeout : null;
      var F = "undefined" !== typeof setImmediate ? setImmediate : null;
      "undefined" !== typeof navigator && void 0 !== navigator.scheduling && void 0 !== navigator.scheduling.isInputPending && navigator.scheduling.isInputPending.bind(navigator.scheduling);
      function G(a) {
        for (var b = h(t); null !== b; ) {
          if (null === b.callback) k(t);
          else if (b.startTime <= a) k(t), b.sortIndex = b.expirationTime, f(r, b);
          else break;
          b = h(t);
        }
      }
      __name(G, "G");
      function H(a) {
        B = false;
        G(a);
        if (!A) if (null !== h(r)) A = true, I(J);
        else {
          var b = h(t);
          null !== b && K(H, b.startTime - a);
        }
      }
      __name(H, "H");
      function J(a, b) {
        A = false;
        B && (B = false, E(L), L = -1);
        z = true;
        var c = y;
        try {
          G(b);
          for (v = h(r); null !== v && (!(v.expirationTime > b) || a && !M()); ) {
            var d = v.callback;
            if ("function" === typeof d) {
              v.callback = null;
              y = v.priorityLevel;
              var e = d(v.expirationTime <= b);
              b = exports.unstable_now();
              "function" === typeof e ? v.callback = e : v === h(r) && k(r);
              G(b);
            } else k(r);
            v = h(r);
          }
          if (null !== v) var w = true;
          else {
            var m = h(t);
            null !== m && K(H, m.startTime - b);
            w = false;
          }
          return w;
        } finally {
          v = null, y = c, z = false;
        }
      }
      __name(J, "J");
      var N = false;
      var O = null;
      var L = -1;
      var P = 5;
      var Q = -1;
      function M() {
        return exports.unstable_now() - Q < P ? false : true;
      }
      __name(M, "M");
      function R() {
        if (null !== O) {
          var a = exports.unstable_now();
          Q = a;
          var b = true;
          try {
            b = O(true, a);
          } finally {
            b ? S() : (N = false, O = null);
          }
        } else N = false;
      }
      __name(R, "R");
      var S;
      if ("function" === typeof F) S = /* @__PURE__ */ __name(function() {
        F(R);
      }, "S");
      else if ("undefined" !== typeof MessageChannel) {
        T = new MessageChannel(), U = T.port2;
        T.port1.onmessage = R;
        S = /* @__PURE__ */ __name(function() {
          U.postMessage(null);
        }, "S");
      } else S = /* @__PURE__ */ __name(function() {
        D(R, 0);
      }, "S");
      var T;
      var U;
      function I(a) {
        O = a;
        N || (N = true, S());
      }
      __name(I, "I");
      function K(a, b) {
        L = D(function() {
          a(exports.unstable_now());
        }, b);
      }
      __name(K, "K");
      exports.unstable_IdlePriority = 5;
      exports.unstable_ImmediatePriority = 1;
      exports.unstable_LowPriority = 4;
      exports.unstable_NormalPriority = 3;
      exports.unstable_Profiling = null;
      exports.unstable_UserBlockingPriority = 2;
      exports.unstable_cancelCallback = function(a) {
        a.callback = null;
      };
      exports.unstable_continueExecution = function() {
        A || z || (A = true, I(J));
      };
      exports.unstable_forceFrameRate = function(a) {
        0 > a || 125 < a ? console.error("forceFrameRate takes a positive int between 0 and 125, forcing frame rates higher than 125 fps is not supported") : P = 0 < a ? Math.floor(1e3 / a) : 5;
      };
      exports.unstable_getCurrentPriorityLevel = function() {
        return y;
      };
      exports.unstable_getFirstCallbackNode = function() {
        return h(r);
      };
      exports.unstable_next = function(a) {
        switch (y) {
          case 1:
          case 2:
          case 3:
            var b = 3;
            break;
          default:
            b = y;
        }
        var c = y;
        y = b;
        try {
          return a();
        } finally {
          y = c;
        }
      };
      exports.unstable_pauseExecution = function() {
      };
      exports.unstable_requestPaint = function() {
      };
      exports.unstable_runWithPriority = function(a, b) {
        switch (a) {
          case 1:
          case 2:
          case 3:
          case 4:
          case 5:
            break;
          default:
            a = 3;
        }
        var c = y;
        y = a;
        try {
          return b();
        } finally {
          y = c;
        }
      };
      exports.unstable_scheduleCallback = function(a, b, c) {
        var d = exports.unstable_now();
        "object" === typeof c && null !== c ? (c = c.delay, c = "number" === typeof c && 0 < c ? d + c : d) : c = d;
        switch (a) {
          case 1:
            var e = -1;
            break;
          case 2:
            e = 250;
            break;
          case 5:
            e = 1073741823;
            break;
          case 4:
            e = 1e4;
            break;
          default:
            e = 5e3;
        }
        e = c + e;
        a = { id: u++, callback: b, priorityLevel: a, startTime: c, expirationTime: e, sortIndex: -1 };
        c > d ? (a.sortIndex = c, f(t, a), null === h(r) && a === h(t) && (B ? (E(L), L = -1) : B = true, K(H, c - d))) : (a.sortIndex = e, f(r, a), A || z || (A = true, I(J)));
        return a;
      };
      exports.unstable_shouldYield = M;
      exports.unstable_wrapCallback = function(a) {
        var b = y;
        return function() {
          var c = y;
          y = b;
          try {
            return a.apply(this, arguments);
          } finally {
            y = c;
          }
        };
      };
    }
  });

  // node_modules/scheduler/index.js
  var require_scheduler = __commonJS({
    "node_modules/scheduler/index.js"(exports, module) {
      "use strict";
      if (true) {
        module.exports = require_scheduler_production_min();
      } else {
        module.exports = null;
      }
    }
  });

  // node_modules/react-dom/cjs/react-dom.production.min.js
  var require_react_dom_production_min = __commonJS({
    "node_modules/react-dom/cjs/react-dom.production.min.js"(exports) {
      "use strict";
      var aa = require_react();
      var ca = require_scheduler();
      function p(a) {
        for (var b = "https://reactjs.org/docs/error-decoder.html?invariant=" + a, c = 1; c < arguments.length; c++) b += "&args[]=" + encodeURIComponent(arguments[c]);
        return "Minified React error #" + a + "; visit " + b + " for the full message or use the non-minified dev environment for full errors and additional helpful warnings.";
      }
      __name(p, "p");
      var da = /* @__PURE__ */ new Set();
      var ea = {};
      function fa(a, b) {
        ha(a, b);
        ha(a + "Capture", b);
      }
      __name(fa, "fa");
      function ha(a, b) {
        ea[a] = b;
        for (a = 0; a < b.length; a++) da.add(b[a]);
      }
      __name(ha, "ha");
      var ia = !("undefined" === typeof window || "undefined" === typeof window.document || "undefined" === typeof window.document.createElement);
      var ja = Object.prototype.hasOwnProperty;
      var ka = /^[:A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD][:A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\-.0-9\u00B7\u0300-\u036F\u203F-\u2040]*$/;
      var la = {};
      var ma = {};
      function oa(a) {
        if (ja.call(ma, a)) return true;
        if (ja.call(la, a)) return false;
        if (ka.test(a)) return ma[a] = true;
        la[a] = true;
        return false;
      }
      __name(oa, "oa");
      function pa(a, b, c, d) {
        if (null !== c && 0 === c.type) return false;
        switch (typeof b) {
          case "function":
          case "symbol":
            return true;
          case "boolean":
            if (d) return false;
            if (null !== c) return !c.acceptsBooleans;
            a = a.toLowerCase().slice(0, 5);
            return "data-" !== a && "aria-" !== a;
          default:
            return false;
        }
      }
      __name(pa, "pa");
      function qa(a, b, c, d) {
        if (null === b || "undefined" === typeof b || pa(a, b, c, d)) return true;
        if (d) return false;
        if (null !== c) switch (c.type) {
          case 3:
            return !b;
          case 4:
            return false === b;
          case 5:
            return isNaN(b);
          case 6:
            return isNaN(b) || 1 > b;
        }
        return false;
      }
      __name(qa, "qa");
      function v(a, b, c, d, e, f, g) {
        this.acceptsBooleans = 2 === b || 3 === b || 4 === b;
        this.attributeName = d;
        this.attributeNamespace = e;
        this.mustUseProperty = c;
        this.propertyName = a;
        this.type = b;
        this.sanitizeURL = f;
        this.removeEmptyString = g;
      }
      __name(v, "v");
      var z = {};
      "children dangerouslySetInnerHTML defaultValue defaultChecked innerHTML suppressContentEditableWarning suppressHydrationWarning style".split(" ").forEach(function(a) {
        z[a] = new v(a, 0, false, a, null, false, false);
      });
      [["acceptCharset", "accept-charset"], ["className", "class"], ["htmlFor", "for"], ["httpEquiv", "http-equiv"]].forEach(function(a) {
        var b = a[0];
        z[b] = new v(b, 1, false, a[1], null, false, false);
      });
      ["contentEditable", "draggable", "spellCheck", "value"].forEach(function(a) {
        z[a] = new v(a, 2, false, a.toLowerCase(), null, false, false);
      });
      ["autoReverse", "externalResourcesRequired", "focusable", "preserveAlpha"].forEach(function(a) {
        z[a] = new v(a, 2, false, a, null, false, false);
      });
      "allowFullScreen async autoFocus autoPlay controls default defer disabled disablePictureInPicture disableRemotePlayback formNoValidate hidden loop noModule noValidate open playsInline readOnly required reversed scoped seamless itemScope".split(" ").forEach(function(a) {
        z[a] = new v(a, 3, false, a.toLowerCase(), null, false, false);
      });
      ["checked", "multiple", "muted", "selected"].forEach(function(a) {
        z[a] = new v(a, 3, true, a, null, false, false);
      });
      ["capture", "download"].forEach(function(a) {
        z[a] = new v(a, 4, false, a, null, false, false);
      });
      ["cols", "rows", "size", "span"].forEach(function(a) {
        z[a] = new v(a, 6, false, a, null, false, false);
      });
      ["rowSpan", "start"].forEach(function(a) {
        z[a] = new v(a, 5, false, a.toLowerCase(), null, false, false);
      });
      var ra = /[\-:]([a-z])/g;
      function sa(a) {
        return a[1].toUpperCase();
      }
      __name(sa, "sa");
      "accent-height alignment-baseline arabic-form baseline-shift cap-height clip-path clip-rule color-interpolation color-interpolation-filters color-profile color-rendering dominant-baseline enable-background fill-opacity fill-rule flood-color flood-opacity font-family font-size font-size-adjust font-stretch font-style font-variant font-weight glyph-name glyph-orientation-horizontal glyph-orientation-vertical horiz-adv-x horiz-origin-x image-rendering letter-spacing lighting-color marker-end marker-mid marker-start overline-position overline-thickness paint-order panose-1 pointer-events rendering-intent shape-rendering stop-color stop-opacity strikethrough-position strikethrough-thickness stroke-dasharray stroke-dashoffset stroke-linecap stroke-linejoin stroke-miterlimit stroke-opacity stroke-width text-anchor text-decoration text-rendering underline-position underline-thickness unicode-bidi unicode-range units-per-em v-alphabetic v-hanging v-ideographic v-mathematical vector-effect vert-adv-y vert-origin-x vert-origin-y word-spacing writing-mode xmlns:xlink x-height".split(" ").forEach(function(a) {
        var b = a.replace(
          ra,
          sa
        );
        z[b] = new v(b, 1, false, a, null, false, false);
      });
      "xlink:actuate xlink:arcrole xlink:role xlink:show xlink:title xlink:type".split(" ").forEach(function(a) {
        var b = a.replace(ra, sa);
        z[b] = new v(b, 1, false, a, "http://www.w3.org/1999/xlink", false, false);
      });
      ["xml:base", "xml:lang", "xml:space"].forEach(function(a) {
        var b = a.replace(ra, sa);
        z[b] = new v(b, 1, false, a, "http://www.w3.org/XML/1998/namespace", false, false);
      });
      ["tabIndex", "crossOrigin"].forEach(function(a) {
        z[a] = new v(a, 1, false, a.toLowerCase(), null, false, false);
      });
      z.xlinkHref = new v("xlinkHref", 1, false, "xlink:href", "http://www.w3.org/1999/xlink", true, false);
      ["src", "href", "action", "formAction"].forEach(function(a) {
        z[a] = new v(a, 1, false, a.toLowerCase(), null, true, true);
      });
      function ta(a, b, c, d) {
        var e = z.hasOwnProperty(b) ? z[b] : null;
        if (null !== e ? 0 !== e.type : d || !(2 < b.length) || "o" !== b[0] && "O" !== b[0] || "n" !== b[1] && "N" !== b[1]) qa(b, c, e, d) && (c = null), d || null === e ? oa(b) && (null === c ? a.removeAttribute(b) : a.setAttribute(b, "" + c)) : e.mustUseProperty ? a[e.propertyName] = null === c ? 3 === e.type ? false : "" : c : (b = e.attributeName, d = e.attributeNamespace, null === c ? a.removeAttribute(b) : (e = e.type, c = 3 === e || 4 === e && true === c ? "" : "" + c, d ? a.setAttributeNS(d, b, c) : a.setAttribute(b, c)));
      }
      __name(ta, "ta");
      var ua = aa.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
      var va = Symbol.for("react.element");
      var wa = Symbol.for("react.portal");
      var ya = Symbol.for("react.fragment");
      var za = Symbol.for("react.strict_mode");
      var Aa = Symbol.for("react.profiler");
      var Ba = Symbol.for("react.provider");
      var Ca = Symbol.for("react.context");
      var Da = Symbol.for("react.forward_ref");
      var Ea = Symbol.for("react.suspense");
      var Fa = Symbol.for("react.suspense_list");
      var Ga = Symbol.for("react.memo");
      var Ha = Symbol.for("react.lazy");
      Symbol.for("react.scope");
      Symbol.for("react.debug_trace_mode");
      var Ia = Symbol.for("react.offscreen");
      Symbol.for("react.legacy_hidden");
      Symbol.for("react.cache");
      Symbol.for("react.tracing_marker");
      var Ja = Symbol.iterator;
      function Ka(a) {
        if (null === a || "object" !== typeof a) return null;
        a = Ja && a[Ja] || a["@@iterator"];
        return "function" === typeof a ? a : null;
      }
      __name(Ka, "Ka");
      var A = Object.assign;
      var La;
      function Ma(a) {
        if (void 0 === La) try {
          throw Error();
        } catch (c) {
          var b = c.stack.trim().match(/\n( *(at )?)/);
          La = b && b[1] || "";
        }
        return "\n" + La + a;
      }
      __name(Ma, "Ma");
      var Na = false;
      function Oa(a, b) {
        if (!a || Na) return "";
        Na = true;
        var c = Error.prepareStackTrace;
        Error.prepareStackTrace = void 0;
        try {
          if (b) if (b = /* @__PURE__ */ __name(function() {
            throw Error();
          }, "b"), Object.defineProperty(b.prototype, "props", { set: /* @__PURE__ */ __name(function() {
            throw Error();
          }, "set") }), "object" === typeof Reflect && Reflect.construct) {
            try {
              Reflect.construct(b, []);
            } catch (l) {
              var d = l;
            }
            Reflect.construct(a, [], b);
          } else {
            try {
              b.call();
            } catch (l) {
              d = l;
            }
            a.call(b.prototype);
          }
          else {
            try {
              throw Error();
            } catch (l) {
              d = l;
            }
            a();
          }
        } catch (l) {
          if (l && d && "string" === typeof l.stack) {
            for (var e = l.stack.split("\n"), f = d.stack.split("\n"), g = e.length - 1, h = f.length - 1; 1 <= g && 0 <= h && e[g] !== f[h]; ) h--;
            for (; 1 <= g && 0 <= h; g--, h--) if (e[g] !== f[h]) {
              if (1 !== g || 1 !== h) {
                do
                  if (g--, h--, 0 > h || e[g] !== f[h]) {
                    var k = "\n" + e[g].replace(" at new ", " at ");
                    a.displayName && k.includes("<anonymous>") && (k = k.replace("<anonymous>", a.displayName));
                    return k;
                  }
                while (1 <= g && 0 <= h);
              }
              break;
            }
          }
        } finally {
          Na = false, Error.prepareStackTrace = c;
        }
        return (a = a ? a.displayName || a.name : "") ? Ma(a) : "";
      }
      __name(Oa, "Oa");
      function Pa(a) {
        switch (a.tag) {
          case 5:
            return Ma(a.type);
          case 16:
            return Ma("Lazy");
          case 13:
            return Ma("Suspense");
          case 19:
            return Ma("SuspenseList");
          case 0:
          case 2:
          case 15:
            return a = Oa(a.type, false), a;
          case 11:
            return a = Oa(a.type.render, false), a;
          case 1:
            return a = Oa(a.type, true), a;
          default:
            return "";
        }
      }
      __name(Pa, "Pa");
      function Qa(a) {
        if (null == a) return null;
        if ("function" === typeof a) return a.displayName || a.name || null;
        if ("string" === typeof a) return a;
        switch (a) {
          case ya:
            return "Fragment";
          case wa:
            return "Portal";
          case Aa:
            return "Profiler";
          case za:
            return "StrictMode";
          case Ea:
            return "Suspense";
          case Fa:
            return "SuspenseList";
        }
        if ("object" === typeof a) switch (a.$$typeof) {
          case Ca:
            return (a.displayName || "Context") + ".Consumer";
          case Ba:
            return (a._context.displayName || "Context") + ".Provider";
          case Da:
            var b = a.render;
            a = a.displayName;
            a || (a = b.displayName || b.name || "", a = "" !== a ? "ForwardRef(" + a + ")" : "ForwardRef");
            return a;
          case Ga:
            return b = a.displayName || null, null !== b ? b : Qa(a.type) || "Memo";
          case Ha:
            b = a._payload;
            a = a._init;
            try {
              return Qa(a(b));
            } catch (c) {
            }
        }
        return null;
      }
      __name(Qa, "Qa");
      function Ra(a) {
        var b = a.type;
        switch (a.tag) {
          case 24:
            return "Cache";
          case 9:
            return (b.displayName || "Context") + ".Consumer";
          case 10:
            return (b._context.displayName || "Context") + ".Provider";
          case 18:
            return "DehydratedFragment";
          case 11:
            return a = b.render, a = a.displayName || a.name || "", b.displayName || ("" !== a ? "ForwardRef(" + a + ")" : "ForwardRef");
          case 7:
            return "Fragment";
          case 5:
            return b;
          case 4:
            return "Portal";
          case 3:
            return "Root";
          case 6:
            return "Text";
          case 16:
            return Qa(b);
          case 8:
            return b === za ? "StrictMode" : "Mode";
          case 22:
            return "Offscreen";
          case 12:
            return "Profiler";
          case 21:
            return "Scope";
          case 13:
            return "Suspense";
          case 19:
            return "SuspenseList";
          case 25:
            return "TracingMarker";
          case 1:
          case 0:
          case 17:
          case 2:
          case 14:
          case 15:
            if ("function" === typeof b) return b.displayName || b.name || null;
            if ("string" === typeof b) return b;
        }
        return null;
      }
      __name(Ra, "Ra");
      function Sa(a) {
        switch (typeof a) {
          case "boolean":
          case "number":
          case "string":
          case "undefined":
            return a;
          case "object":
            return a;
          default:
            return "";
        }
      }
      __name(Sa, "Sa");
      function Ta(a) {
        var b = a.type;
        return (a = a.nodeName) && "input" === a.toLowerCase() && ("checkbox" === b || "radio" === b);
      }
      __name(Ta, "Ta");
      function Ua(a) {
        var b = Ta(a) ? "checked" : "value", c = Object.getOwnPropertyDescriptor(a.constructor.prototype, b), d = "" + a[b];
        if (!a.hasOwnProperty(b) && "undefined" !== typeof c && "function" === typeof c.get && "function" === typeof c.set) {
          var e = c.get, f = c.set;
          Object.defineProperty(a, b, { configurable: true, get: /* @__PURE__ */ __name(function() {
            return e.call(this);
          }, "get"), set: /* @__PURE__ */ __name(function(a2) {
            d = "" + a2;
            f.call(this, a2);
          }, "set") });
          Object.defineProperty(a, b, { enumerable: c.enumerable });
          return { getValue: /* @__PURE__ */ __name(function() {
            return d;
          }, "getValue"), setValue: /* @__PURE__ */ __name(function(a2) {
            d = "" + a2;
          }, "setValue"), stopTracking: /* @__PURE__ */ __name(function() {
            a._valueTracker = null;
            delete a[b];
          }, "stopTracking") };
        }
      }
      __name(Ua, "Ua");
      function Va(a) {
        a._valueTracker || (a._valueTracker = Ua(a));
      }
      __name(Va, "Va");
      function Wa(a) {
        if (!a) return false;
        var b = a._valueTracker;
        if (!b) return true;
        var c = b.getValue();
        var d = "";
        a && (d = Ta(a) ? a.checked ? "true" : "false" : a.value);
        a = d;
        return a !== c ? (b.setValue(a), true) : false;
      }
      __name(Wa, "Wa");
      function Xa(a) {
        a = a || ("undefined" !== typeof document ? document : void 0);
        if ("undefined" === typeof a) return null;
        try {
          return a.activeElement || a.body;
        } catch (b) {
          return a.body;
        }
      }
      __name(Xa, "Xa");
      function Ya(a, b) {
        var c = b.checked;
        return A({}, b, { defaultChecked: void 0, defaultValue: void 0, value: void 0, checked: null != c ? c : a._wrapperState.initialChecked });
      }
      __name(Ya, "Ya");
      function Za(a, b) {
        var c = null == b.defaultValue ? "" : b.defaultValue, d = null != b.checked ? b.checked : b.defaultChecked;
        c = Sa(null != b.value ? b.value : c);
        a._wrapperState = { initialChecked: d, initialValue: c, controlled: "checkbox" === b.type || "radio" === b.type ? null != b.checked : null != b.value };
      }
      __name(Za, "Za");
      function ab(a, b) {
        b = b.checked;
        null != b && ta(a, "checked", b, false);
      }
      __name(ab, "ab");
      function bb(a, b) {
        ab(a, b);
        var c = Sa(b.value), d = b.type;
        if (null != c) if ("number" === d) {
          if (0 === c && "" === a.value || a.value != c) a.value = "" + c;
        } else a.value !== "" + c && (a.value = "" + c);
        else if ("submit" === d || "reset" === d) {
          a.removeAttribute("value");
          return;
        }
        b.hasOwnProperty("value") ? cb(a, b.type, c) : b.hasOwnProperty("defaultValue") && cb(a, b.type, Sa(b.defaultValue));
        null == b.checked && null != b.defaultChecked && (a.defaultChecked = !!b.defaultChecked);
      }
      __name(bb, "bb");
      function db(a, b, c) {
        if (b.hasOwnProperty("value") || b.hasOwnProperty("defaultValue")) {
          var d = b.type;
          if (!("submit" !== d && "reset" !== d || void 0 !== b.value && null !== b.value)) return;
          b = "" + a._wrapperState.initialValue;
          c || b === a.value || (a.value = b);
          a.defaultValue = b;
        }
        c = a.name;
        "" !== c && (a.name = "");
        a.defaultChecked = !!a._wrapperState.initialChecked;
        "" !== c && (a.name = c);
      }
      __name(db, "db");
      function cb(a, b, c) {
        if ("number" !== b || Xa(a.ownerDocument) !== a) null == c ? a.defaultValue = "" + a._wrapperState.initialValue : a.defaultValue !== "" + c && (a.defaultValue = "" + c);
      }
      __name(cb, "cb");
      var eb = Array.isArray;
      function fb(a, b, c, d) {
        a = a.options;
        if (b) {
          b = {};
          for (var e = 0; e < c.length; e++) b["$" + c[e]] = true;
          for (c = 0; c < a.length; c++) e = b.hasOwnProperty("$" + a[c].value), a[c].selected !== e && (a[c].selected = e), e && d && (a[c].defaultSelected = true);
        } else {
          c = "" + Sa(c);
          b = null;
          for (e = 0; e < a.length; e++) {
            if (a[e].value === c) {
              a[e].selected = true;
              d && (a[e].defaultSelected = true);
              return;
            }
            null !== b || a[e].disabled || (b = a[e]);
          }
          null !== b && (b.selected = true);
        }
      }
      __name(fb, "fb");
      function gb(a, b) {
        if (null != b.dangerouslySetInnerHTML) throw Error(p(91));
        return A({}, b, { value: void 0, defaultValue: void 0, children: "" + a._wrapperState.initialValue });
      }
      __name(gb, "gb");
      function hb(a, b) {
        var c = b.value;
        if (null == c) {
          c = b.children;
          b = b.defaultValue;
          if (null != c) {
            if (null != b) throw Error(p(92));
            if (eb(c)) {
              if (1 < c.length) throw Error(p(93));
              c = c[0];
            }
            b = c;
          }
          null == b && (b = "");
          c = b;
        }
        a._wrapperState = { initialValue: Sa(c) };
      }
      __name(hb, "hb");
      function ib(a, b) {
        var c = Sa(b.value), d = Sa(b.defaultValue);
        null != c && (c = "" + c, c !== a.value && (a.value = c), null == b.defaultValue && a.defaultValue !== c && (a.defaultValue = c));
        null != d && (a.defaultValue = "" + d);
      }
      __name(ib, "ib");
      function jb(a) {
        var b = a.textContent;
        b === a._wrapperState.initialValue && "" !== b && null !== b && (a.value = b);
      }
      __name(jb, "jb");
      function kb(a) {
        switch (a) {
          case "svg":
            return "http://www.w3.org/2000/svg";
          case "math":
            return "http://www.w3.org/1998/Math/MathML";
          default:
            return "http://www.w3.org/1999/xhtml";
        }
      }
      __name(kb, "kb");
      function lb(a, b) {
        return null == a || "http://www.w3.org/1999/xhtml" === a ? kb(b) : "http://www.w3.org/2000/svg" === a && "foreignObject" === b ? "http://www.w3.org/1999/xhtml" : a;
      }
      __name(lb, "lb");
      var mb;
      var nb = (function(a) {
        return "undefined" !== typeof MSApp && MSApp.execUnsafeLocalFunction ? function(b, c, d, e) {
          MSApp.execUnsafeLocalFunction(function() {
            return a(b, c, d, e);
          });
        } : a;
      })(function(a, b) {
        if ("http://www.w3.org/2000/svg" !== a.namespaceURI || "innerHTML" in a) a.innerHTML = b;
        else {
          mb = mb || document.createElement("div");
          mb.innerHTML = "<svg>" + b.valueOf().toString() + "</svg>";
          for (b = mb.firstChild; a.firstChild; ) a.removeChild(a.firstChild);
          for (; b.firstChild; ) a.appendChild(b.firstChild);
        }
      });
      function ob(a, b) {
        if (b) {
          var c = a.firstChild;
          if (c && c === a.lastChild && 3 === c.nodeType) {
            c.nodeValue = b;
            return;
          }
        }
        a.textContent = b;
      }
      __name(ob, "ob");
      var pb = {
        animationIterationCount: true,
        aspectRatio: true,
        borderImageOutset: true,
        borderImageSlice: true,
        borderImageWidth: true,
        boxFlex: true,
        boxFlexGroup: true,
        boxOrdinalGroup: true,
        columnCount: true,
        columns: true,
        flex: true,
        flexGrow: true,
        flexPositive: true,
        flexShrink: true,
        flexNegative: true,
        flexOrder: true,
        gridArea: true,
        gridRow: true,
        gridRowEnd: true,
        gridRowSpan: true,
        gridRowStart: true,
        gridColumn: true,
        gridColumnEnd: true,
        gridColumnSpan: true,
        gridColumnStart: true,
        fontWeight: true,
        lineClamp: true,
        lineHeight: true,
        opacity: true,
        order: true,
        orphans: true,
        tabSize: true,
        widows: true,
        zIndex: true,
        zoom: true,
        fillOpacity: true,
        floodOpacity: true,
        stopOpacity: true,
        strokeDasharray: true,
        strokeDashoffset: true,
        strokeMiterlimit: true,
        strokeOpacity: true,
        strokeWidth: true
      };
      var qb = ["Webkit", "ms", "Moz", "O"];
      Object.keys(pb).forEach(function(a) {
        qb.forEach(function(b) {
          b = b + a.charAt(0).toUpperCase() + a.substring(1);
          pb[b] = pb[a];
        });
      });
      function rb(a, b, c) {
        return null == b || "boolean" === typeof b || "" === b ? "" : c || "number" !== typeof b || 0 === b || pb.hasOwnProperty(a) && pb[a] ? ("" + b).trim() : b + "px";
      }
      __name(rb, "rb");
      function sb(a, b) {
        a = a.style;
        for (var c in b) if (b.hasOwnProperty(c)) {
          var d = 0 === c.indexOf("--"), e = rb(c, b[c], d);
          "float" === c && (c = "cssFloat");
          d ? a.setProperty(c, e) : a[c] = e;
        }
      }
      __name(sb, "sb");
      var tb = A({ menuitem: true }, { area: true, base: true, br: true, col: true, embed: true, hr: true, img: true, input: true, keygen: true, link: true, meta: true, param: true, source: true, track: true, wbr: true });
      function ub(a, b) {
        if (b) {
          if (tb[a] && (null != b.children || null != b.dangerouslySetInnerHTML)) throw Error(p(137, a));
          if (null != b.dangerouslySetInnerHTML) {
            if (null != b.children) throw Error(p(60));
            if ("object" !== typeof b.dangerouslySetInnerHTML || !("__html" in b.dangerouslySetInnerHTML)) throw Error(p(61));
          }
          if (null != b.style && "object" !== typeof b.style) throw Error(p(62));
        }
      }
      __name(ub, "ub");
      function vb(a, b) {
        if (-1 === a.indexOf("-")) return "string" === typeof b.is;
        switch (a) {
          case "annotation-xml":
          case "color-profile":
          case "font-face":
          case "font-face-src":
          case "font-face-uri":
          case "font-face-format":
          case "font-face-name":
          case "missing-glyph":
            return false;
          default:
            return true;
        }
      }
      __name(vb, "vb");
      var wb = null;
      function xb(a) {
        a = a.target || a.srcElement || window;
        a.correspondingUseElement && (a = a.correspondingUseElement);
        return 3 === a.nodeType ? a.parentNode : a;
      }
      __name(xb, "xb");
      var yb = null;
      var zb = null;
      var Ab = null;
      function Bb(a) {
        if (a = Cb(a)) {
          if ("function" !== typeof yb) throw Error(p(280));
          var b = a.stateNode;
          b && (b = Db(b), yb(a.stateNode, a.type, b));
        }
      }
      __name(Bb, "Bb");
      function Eb(a) {
        zb ? Ab ? Ab.push(a) : Ab = [a] : zb = a;
      }
      __name(Eb, "Eb");
      function Fb() {
        if (zb) {
          var a = zb, b = Ab;
          Ab = zb = null;
          Bb(a);
          if (b) for (a = 0; a < b.length; a++) Bb(b[a]);
        }
      }
      __name(Fb, "Fb");
      function Gb(a, b) {
        return a(b);
      }
      __name(Gb, "Gb");
      function Hb() {
      }
      __name(Hb, "Hb");
      var Ib = false;
      function Jb(a, b, c) {
        if (Ib) return a(b, c);
        Ib = true;
        try {
          return Gb(a, b, c);
        } finally {
          if (Ib = false, null !== zb || null !== Ab) Hb(), Fb();
        }
      }
      __name(Jb, "Jb");
      function Kb(a, b) {
        var c = a.stateNode;
        if (null === c) return null;
        var d = Db(c);
        if (null === d) return null;
        c = d[b];
        a: switch (b) {
          case "onClick":
          case "onClickCapture":
          case "onDoubleClick":
          case "onDoubleClickCapture":
          case "onMouseDown":
          case "onMouseDownCapture":
          case "onMouseMove":
          case "onMouseMoveCapture":
          case "onMouseUp":
          case "onMouseUpCapture":
          case "onMouseEnter":
            (d = !d.disabled) || (a = a.type, d = !("button" === a || "input" === a || "select" === a || "textarea" === a));
            a = !d;
            break a;
          default:
            a = false;
        }
        if (a) return null;
        if (c && "function" !== typeof c) throw Error(p(231, b, typeof c));
        return c;
      }
      __name(Kb, "Kb");
      var Lb = false;
      if (ia) try {
        Mb = {};
        Object.defineProperty(Mb, "passive", { get: /* @__PURE__ */ __name(function() {
          Lb = true;
        }, "get") });
        window.addEventListener("test", Mb, Mb);
        window.removeEventListener("test", Mb, Mb);
      } catch (a) {
        Lb = false;
      }
      var Mb;
      function Nb(a, b, c, d, e, f, g, h, k) {
        var l = Array.prototype.slice.call(arguments, 3);
        try {
          b.apply(c, l);
        } catch (m) {
          this.onError(m);
        }
      }
      __name(Nb, "Nb");
      var Ob = false;
      var Pb = null;
      var Qb = false;
      var Rb = null;
      var Sb = { onError: /* @__PURE__ */ __name(function(a) {
        Ob = true;
        Pb = a;
      }, "onError") };
      function Tb(a, b, c, d, e, f, g, h, k) {
        Ob = false;
        Pb = null;
        Nb.apply(Sb, arguments);
      }
      __name(Tb, "Tb");
      function Ub(a, b, c, d, e, f, g, h, k) {
        Tb.apply(this, arguments);
        if (Ob) {
          if (Ob) {
            var l = Pb;
            Ob = false;
            Pb = null;
          } else throw Error(p(198));
          Qb || (Qb = true, Rb = l);
        }
      }
      __name(Ub, "Ub");
      function Vb(a) {
        var b = a, c = a;
        if (a.alternate) for (; b.return; ) b = b.return;
        else {
          a = b;
          do
            b = a, 0 !== (b.flags & 4098) && (c = b.return), a = b.return;
          while (a);
        }
        return 3 === b.tag ? c : null;
      }
      __name(Vb, "Vb");
      function Wb(a) {
        if (13 === a.tag) {
          var b = a.memoizedState;
          null === b && (a = a.alternate, null !== a && (b = a.memoizedState));
          if (null !== b) return b.dehydrated;
        }
        return null;
      }
      __name(Wb, "Wb");
      function Xb(a) {
        if (Vb(a) !== a) throw Error(p(188));
      }
      __name(Xb, "Xb");
      function Yb(a) {
        var b = a.alternate;
        if (!b) {
          b = Vb(a);
          if (null === b) throw Error(p(188));
          return b !== a ? null : a;
        }
        for (var c = a, d = b; ; ) {
          var e = c.return;
          if (null === e) break;
          var f = e.alternate;
          if (null === f) {
            d = e.return;
            if (null !== d) {
              c = d;
              continue;
            }
            break;
          }
          if (e.child === f.child) {
            for (f = e.child; f; ) {
              if (f === c) return Xb(e), a;
              if (f === d) return Xb(e), b;
              f = f.sibling;
            }
            throw Error(p(188));
          }
          if (c.return !== d.return) c = e, d = f;
          else {
            for (var g = false, h = e.child; h; ) {
              if (h === c) {
                g = true;
                c = e;
                d = f;
                break;
              }
              if (h === d) {
                g = true;
                d = e;
                c = f;
                break;
              }
              h = h.sibling;
            }
            if (!g) {
              for (h = f.child; h; ) {
                if (h === c) {
                  g = true;
                  c = f;
                  d = e;
                  break;
                }
                if (h === d) {
                  g = true;
                  d = f;
                  c = e;
                  break;
                }
                h = h.sibling;
              }
              if (!g) throw Error(p(189));
            }
          }
          if (c.alternate !== d) throw Error(p(190));
        }
        if (3 !== c.tag) throw Error(p(188));
        return c.stateNode.current === c ? a : b;
      }
      __name(Yb, "Yb");
      function Zb(a) {
        a = Yb(a);
        return null !== a ? $b(a) : null;
      }
      __name(Zb, "Zb");
      function $b(a) {
        if (5 === a.tag || 6 === a.tag) return a;
        for (a = a.child; null !== a; ) {
          var b = $b(a);
          if (null !== b) return b;
          a = a.sibling;
        }
        return null;
      }
      __name($b, "$b");
      var ac = ca.unstable_scheduleCallback;
      var bc = ca.unstable_cancelCallback;
      var cc = ca.unstable_shouldYield;
      var dc = ca.unstable_requestPaint;
      var B = ca.unstable_now;
      var ec = ca.unstable_getCurrentPriorityLevel;
      var fc = ca.unstable_ImmediatePriority;
      var gc = ca.unstable_UserBlockingPriority;
      var hc = ca.unstable_NormalPriority;
      var ic = ca.unstable_LowPriority;
      var jc = ca.unstable_IdlePriority;
      var kc = null;
      var lc = null;
      function mc(a) {
        if (lc && "function" === typeof lc.onCommitFiberRoot) try {
          lc.onCommitFiberRoot(kc, a, void 0, 128 === (a.current.flags & 128));
        } catch (b) {
        }
      }
      __name(mc, "mc");
      var oc = Math.clz32 ? Math.clz32 : nc;
      var pc = Math.log;
      var qc = Math.LN2;
      function nc(a) {
        a >>>= 0;
        return 0 === a ? 32 : 31 - (pc(a) / qc | 0) | 0;
      }
      __name(nc, "nc");
      var rc = 64;
      var sc = 4194304;
      function tc(a) {
        switch (a & -a) {
          case 1:
            return 1;
          case 2:
            return 2;
          case 4:
            return 4;
          case 8:
            return 8;
          case 16:
            return 16;
          case 32:
            return 32;
          case 64:
          case 128:
          case 256:
          case 512:
          case 1024:
          case 2048:
          case 4096:
          case 8192:
          case 16384:
          case 32768:
          case 65536:
          case 131072:
          case 262144:
          case 524288:
          case 1048576:
          case 2097152:
            return a & 4194240;
          case 4194304:
          case 8388608:
          case 16777216:
          case 33554432:
          case 67108864:
            return a & 130023424;
          case 134217728:
            return 134217728;
          case 268435456:
            return 268435456;
          case 536870912:
            return 536870912;
          case 1073741824:
            return 1073741824;
          default:
            return a;
        }
      }
      __name(tc, "tc");
      function uc(a, b) {
        var c = a.pendingLanes;
        if (0 === c) return 0;
        var d = 0, e = a.suspendedLanes, f = a.pingedLanes, g = c & 268435455;
        if (0 !== g) {
          var h = g & ~e;
          0 !== h ? d = tc(h) : (f &= g, 0 !== f && (d = tc(f)));
        } else g = c & ~e, 0 !== g ? d = tc(g) : 0 !== f && (d = tc(f));
        if (0 === d) return 0;
        if (0 !== b && b !== d && 0 === (b & e) && (e = d & -d, f = b & -b, e >= f || 16 === e && 0 !== (f & 4194240))) return b;
        0 !== (d & 4) && (d |= c & 16);
        b = a.entangledLanes;
        if (0 !== b) for (a = a.entanglements, b &= d; 0 < b; ) c = 31 - oc(b), e = 1 << c, d |= a[c], b &= ~e;
        return d;
      }
      __name(uc, "uc");
      function vc(a, b) {
        switch (a) {
          case 1:
          case 2:
          case 4:
            return b + 250;
          case 8:
          case 16:
          case 32:
          case 64:
          case 128:
          case 256:
          case 512:
          case 1024:
          case 2048:
          case 4096:
          case 8192:
          case 16384:
          case 32768:
          case 65536:
          case 131072:
          case 262144:
          case 524288:
          case 1048576:
          case 2097152:
            return b + 5e3;
          case 4194304:
          case 8388608:
          case 16777216:
          case 33554432:
          case 67108864:
            return -1;
          case 134217728:
          case 268435456:
          case 536870912:
          case 1073741824:
            return -1;
          default:
            return -1;
        }
      }
      __name(vc, "vc");
      function wc(a, b) {
        for (var c = a.suspendedLanes, d = a.pingedLanes, e = a.expirationTimes, f = a.pendingLanes; 0 < f; ) {
          var g = 31 - oc(f), h = 1 << g, k = e[g];
          if (-1 === k) {
            if (0 === (h & c) || 0 !== (h & d)) e[g] = vc(h, b);
          } else k <= b && (a.expiredLanes |= h);
          f &= ~h;
        }
      }
      __name(wc, "wc");
      function xc(a) {
        a = a.pendingLanes & -1073741825;
        return 0 !== a ? a : a & 1073741824 ? 1073741824 : 0;
      }
      __name(xc, "xc");
      function yc() {
        var a = rc;
        rc <<= 1;
        0 === (rc & 4194240) && (rc = 64);
        return a;
      }
      __name(yc, "yc");
      function zc(a) {
        for (var b = [], c = 0; 31 > c; c++) b.push(a);
        return b;
      }
      __name(zc, "zc");
      function Ac(a, b, c) {
        a.pendingLanes |= b;
        536870912 !== b && (a.suspendedLanes = 0, a.pingedLanes = 0);
        a = a.eventTimes;
        b = 31 - oc(b);
        a[b] = c;
      }
      __name(Ac, "Ac");
      function Bc(a, b) {
        var c = a.pendingLanes & ~b;
        a.pendingLanes = b;
        a.suspendedLanes = 0;
        a.pingedLanes = 0;
        a.expiredLanes &= b;
        a.mutableReadLanes &= b;
        a.entangledLanes &= b;
        b = a.entanglements;
        var d = a.eventTimes;
        for (a = a.expirationTimes; 0 < c; ) {
          var e = 31 - oc(c), f = 1 << e;
          b[e] = 0;
          d[e] = -1;
          a[e] = -1;
          c &= ~f;
        }
      }
      __name(Bc, "Bc");
      function Cc(a, b) {
        var c = a.entangledLanes |= b;
        for (a = a.entanglements; c; ) {
          var d = 31 - oc(c), e = 1 << d;
          e & b | a[d] & b && (a[d] |= b);
          c &= ~e;
        }
      }
      __name(Cc, "Cc");
      var C = 0;
      function Dc(a) {
        a &= -a;
        return 1 < a ? 4 < a ? 0 !== (a & 268435455) ? 16 : 536870912 : 4 : 1;
      }
      __name(Dc, "Dc");
      var Ec;
      var Fc;
      var Gc;
      var Hc;
      var Ic;
      var Jc = false;
      var Kc = [];
      var Lc = null;
      var Mc = null;
      var Nc = null;
      var Oc = /* @__PURE__ */ new Map();
      var Pc = /* @__PURE__ */ new Map();
      var Qc = [];
      var Rc = "mousedown mouseup touchcancel touchend touchstart auxclick dblclick pointercancel pointerdown pointerup dragend dragstart drop compositionend compositionstart keydown keypress keyup input textInput copy cut paste click change contextmenu reset submit".split(" ");
      function Sc(a, b) {
        switch (a) {
          case "focusin":
          case "focusout":
            Lc = null;
            break;
          case "dragenter":
          case "dragleave":
            Mc = null;
            break;
          case "mouseover":
          case "mouseout":
            Nc = null;
            break;
          case "pointerover":
          case "pointerout":
            Oc.delete(b.pointerId);
            break;
          case "gotpointercapture":
          case "lostpointercapture":
            Pc.delete(b.pointerId);
        }
      }
      __name(Sc, "Sc");
      function Tc(a, b, c, d, e, f) {
        if (null === a || a.nativeEvent !== f) return a = { blockedOn: b, domEventName: c, eventSystemFlags: d, nativeEvent: f, targetContainers: [e] }, null !== b && (b = Cb(b), null !== b && Fc(b)), a;
        a.eventSystemFlags |= d;
        b = a.targetContainers;
        null !== e && -1 === b.indexOf(e) && b.push(e);
        return a;
      }
      __name(Tc, "Tc");
      function Uc(a, b, c, d, e) {
        switch (b) {
          case "focusin":
            return Lc = Tc(Lc, a, b, c, d, e), true;
          case "dragenter":
            return Mc = Tc(Mc, a, b, c, d, e), true;
          case "mouseover":
            return Nc = Tc(Nc, a, b, c, d, e), true;
          case "pointerover":
            var f = e.pointerId;
            Oc.set(f, Tc(Oc.get(f) || null, a, b, c, d, e));
            return true;
          case "gotpointercapture":
            return f = e.pointerId, Pc.set(f, Tc(Pc.get(f) || null, a, b, c, d, e)), true;
        }
        return false;
      }
      __name(Uc, "Uc");
      function Vc(a) {
        var b = Wc(a.target);
        if (null !== b) {
          var c = Vb(b);
          if (null !== c) {
            if (b = c.tag, 13 === b) {
              if (b = Wb(c), null !== b) {
                a.blockedOn = b;
                Ic(a.priority, function() {
                  Gc(c);
                });
                return;
              }
            } else if (3 === b && c.stateNode.current.memoizedState.isDehydrated) {
              a.blockedOn = 3 === c.tag ? c.stateNode.containerInfo : null;
              return;
            }
          }
        }
        a.blockedOn = null;
      }
      __name(Vc, "Vc");
      function Xc(a) {
        if (null !== a.blockedOn) return false;
        for (var b = a.targetContainers; 0 < b.length; ) {
          var c = Yc(a.domEventName, a.eventSystemFlags, b[0], a.nativeEvent);
          if (null === c) {
            c = a.nativeEvent;
            var d = new c.constructor(c.type, c);
            wb = d;
            c.target.dispatchEvent(d);
            wb = null;
          } else return b = Cb(c), null !== b && Fc(b), a.blockedOn = c, false;
          b.shift();
        }
        return true;
      }
      __name(Xc, "Xc");
      function Zc(a, b, c) {
        Xc(a) && c.delete(b);
      }
      __name(Zc, "Zc");
      function $c() {
        Jc = false;
        null !== Lc && Xc(Lc) && (Lc = null);
        null !== Mc && Xc(Mc) && (Mc = null);
        null !== Nc && Xc(Nc) && (Nc = null);
        Oc.forEach(Zc);
        Pc.forEach(Zc);
      }
      __name($c, "$c");
      function ad(a, b) {
        a.blockedOn === b && (a.blockedOn = null, Jc || (Jc = true, ca.unstable_scheduleCallback(ca.unstable_NormalPriority, $c)));
      }
      __name(ad, "ad");
      function bd(a) {
        function b(b2) {
          return ad(b2, a);
        }
        __name(b, "b");
        if (0 < Kc.length) {
          ad(Kc[0], a);
          for (var c = 1; c < Kc.length; c++) {
            var d = Kc[c];
            d.blockedOn === a && (d.blockedOn = null);
          }
        }
        null !== Lc && ad(Lc, a);
        null !== Mc && ad(Mc, a);
        null !== Nc && ad(Nc, a);
        Oc.forEach(b);
        Pc.forEach(b);
        for (c = 0; c < Qc.length; c++) d = Qc[c], d.blockedOn === a && (d.blockedOn = null);
        for (; 0 < Qc.length && (c = Qc[0], null === c.blockedOn); ) Vc(c), null === c.blockedOn && Qc.shift();
      }
      __name(bd, "bd");
      var cd = ua.ReactCurrentBatchConfig;
      var dd = true;
      function ed(a, b, c, d) {
        var e = C, f = cd.transition;
        cd.transition = null;
        try {
          C = 1, fd(a, b, c, d);
        } finally {
          C = e, cd.transition = f;
        }
      }
      __name(ed, "ed");
      function gd(a, b, c, d) {
        var e = C, f = cd.transition;
        cd.transition = null;
        try {
          C = 4, fd(a, b, c, d);
        } finally {
          C = e, cd.transition = f;
        }
      }
      __name(gd, "gd");
      function fd(a, b, c, d) {
        if (dd) {
          var e = Yc(a, b, c, d);
          if (null === e) hd(a, b, d, id, c), Sc(a, d);
          else if (Uc(e, a, b, c, d)) d.stopPropagation();
          else if (Sc(a, d), b & 4 && -1 < Rc.indexOf(a)) {
            for (; null !== e; ) {
              var f = Cb(e);
              null !== f && Ec(f);
              f = Yc(a, b, c, d);
              null === f && hd(a, b, d, id, c);
              if (f === e) break;
              e = f;
            }
            null !== e && d.stopPropagation();
          } else hd(a, b, d, null, c);
        }
      }
      __name(fd, "fd");
      var id = null;
      function Yc(a, b, c, d) {
        id = null;
        a = xb(d);
        a = Wc(a);
        if (null !== a) if (b = Vb(a), null === b) a = null;
        else if (c = b.tag, 13 === c) {
          a = Wb(b);
          if (null !== a) return a;
          a = null;
        } else if (3 === c) {
          if (b.stateNode.current.memoizedState.isDehydrated) return 3 === b.tag ? b.stateNode.containerInfo : null;
          a = null;
        } else b !== a && (a = null);
        id = a;
        return null;
      }
      __name(Yc, "Yc");
      function jd(a) {
        switch (a) {
          case "cancel":
          case "click":
          case "close":
          case "contextmenu":
          case "copy":
          case "cut":
          case "auxclick":
          case "dblclick":
          case "dragend":
          case "dragstart":
          case "drop":
          case "focusin":
          case "focusout":
          case "input":
          case "invalid":
          case "keydown":
          case "keypress":
          case "keyup":
          case "mousedown":
          case "mouseup":
          case "paste":
          case "pause":
          case "play":
          case "pointercancel":
          case "pointerdown":
          case "pointerup":
          case "ratechange":
          case "reset":
          case "resize":
          case "seeked":
          case "submit":
          case "touchcancel":
          case "touchend":
          case "touchstart":
          case "volumechange":
          case "change":
          case "selectionchange":
          case "textInput":
          case "compositionstart":
          case "compositionend":
          case "compositionupdate":
          case "beforeblur":
          case "afterblur":
          case "beforeinput":
          case "blur":
          case "fullscreenchange":
          case "focus":
          case "hashchange":
          case "popstate":
          case "select":
          case "selectstart":
            return 1;
          case "drag":
          case "dragenter":
          case "dragexit":
          case "dragleave":
          case "dragover":
          case "mousemove":
          case "mouseout":
          case "mouseover":
          case "pointermove":
          case "pointerout":
          case "pointerover":
          case "scroll":
          case "toggle":
          case "touchmove":
          case "wheel":
          case "mouseenter":
          case "mouseleave":
          case "pointerenter":
          case "pointerleave":
            return 4;
          case "message":
            switch (ec()) {
              case fc:
                return 1;
              case gc:
                return 4;
              case hc:
              case ic:
                return 16;
              case jc:
                return 536870912;
              default:
                return 16;
            }
          default:
            return 16;
        }
      }
      __name(jd, "jd");
      var kd = null;
      var ld = null;
      var md = null;
      function nd() {
        if (md) return md;
        var a, b = ld, c = b.length, d, e = "value" in kd ? kd.value : kd.textContent, f = e.length;
        for (a = 0; a < c && b[a] === e[a]; a++) ;
        var g = c - a;
        for (d = 1; d <= g && b[c - d] === e[f - d]; d++) ;
        return md = e.slice(a, 1 < d ? 1 - d : void 0);
      }
      __name(nd, "nd");
      function od(a) {
        var b = a.keyCode;
        "charCode" in a ? (a = a.charCode, 0 === a && 13 === b && (a = 13)) : a = b;
        10 === a && (a = 13);
        return 32 <= a || 13 === a ? a : 0;
      }
      __name(od, "od");
      function pd() {
        return true;
      }
      __name(pd, "pd");
      function qd() {
        return false;
      }
      __name(qd, "qd");
      function rd(a) {
        function b(b2, d, e, f, g) {
          this._reactName = b2;
          this._targetInst = e;
          this.type = d;
          this.nativeEvent = f;
          this.target = g;
          this.currentTarget = null;
          for (var c in a) a.hasOwnProperty(c) && (b2 = a[c], this[c] = b2 ? b2(f) : f[c]);
          this.isDefaultPrevented = (null != f.defaultPrevented ? f.defaultPrevented : false === f.returnValue) ? pd : qd;
          this.isPropagationStopped = qd;
          return this;
        }
        __name(b, "b");
        A(b.prototype, { preventDefault: /* @__PURE__ */ __name(function() {
          this.defaultPrevented = true;
          var a2 = this.nativeEvent;
          a2 && (a2.preventDefault ? a2.preventDefault() : "unknown" !== typeof a2.returnValue && (a2.returnValue = false), this.isDefaultPrevented = pd);
        }, "preventDefault"), stopPropagation: /* @__PURE__ */ __name(function() {
          var a2 = this.nativeEvent;
          a2 && (a2.stopPropagation ? a2.stopPropagation() : "unknown" !== typeof a2.cancelBubble && (a2.cancelBubble = true), this.isPropagationStopped = pd);
        }, "stopPropagation"), persist: /* @__PURE__ */ __name(function() {
        }, "persist"), isPersistent: pd });
        return b;
      }
      __name(rd, "rd");
      var sd = { eventPhase: 0, bubbles: 0, cancelable: 0, timeStamp: /* @__PURE__ */ __name(function(a) {
        return a.timeStamp || Date.now();
      }, "timeStamp"), defaultPrevented: 0, isTrusted: 0 };
      var td = rd(sd);
      var ud = A({}, sd, { view: 0, detail: 0 });
      var vd = rd(ud);
      var wd;
      var xd;
      var yd;
      var Ad = A({}, ud, { screenX: 0, screenY: 0, clientX: 0, clientY: 0, pageX: 0, pageY: 0, ctrlKey: 0, shiftKey: 0, altKey: 0, metaKey: 0, getModifierState: zd, button: 0, buttons: 0, relatedTarget: /* @__PURE__ */ __name(function(a) {
        return void 0 === a.relatedTarget ? a.fromElement === a.srcElement ? a.toElement : a.fromElement : a.relatedTarget;
      }, "relatedTarget"), movementX: /* @__PURE__ */ __name(function(a) {
        if ("movementX" in a) return a.movementX;
        a !== yd && (yd && "mousemove" === a.type ? (wd = a.screenX - yd.screenX, xd = a.screenY - yd.screenY) : xd = wd = 0, yd = a);
        return wd;
      }, "movementX"), movementY: /* @__PURE__ */ __name(function(a) {
        return "movementY" in a ? a.movementY : xd;
      }, "movementY") });
      var Bd = rd(Ad);
      var Cd = A({}, Ad, { dataTransfer: 0 });
      var Dd = rd(Cd);
      var Ed = A({}, ud, { relatedTarget: 0 });
      var Fd = rd(Ed);
      var Gd = A({}, sd, { animationName: 0, elapsedTime: 0, pseudoElement: 0 });
      var Hd = rd(Gd);
      var Id = A({}, sd, { clipboardData: /* @__PURE__ */ __name(function(a) {
        return "clipboardData" in a ? a.clipboardData : window.clipboardData;
      }, "clipboardData") });
      var Jd = rd(Id);
      var Kd = A({}, sd, { data: 0 });
      var Ld = rd(Kd);
      var Md = {
        Esc: "Escape",
        Spacebar: " ",
        Left: "ArrowLeft",
        Up: "ArrowUp",
        Right: "ArrowRight",
        Down: "ArrowDown",
        Del: "Delete",
        Win: "OS",
        Menu: "ContextMenu",
        Apps: "ContextMenu",
        Scroll: "ScrollLock",
        MozPrintableKey: "Unidentified"
      };
      var Nd = {
        8: "Backspace",
        9: "Tab",
        12: "Clear",
        13: "Enter",
        16: "Shift",
        17: "Control",
        18: "Alt",
        19: "Pause",
        20: "CapsLock",
        27: "Escape",
        32: " ",
        33: "PageUp",
        34: "PageDown",
        35: "End",
        36: "Home",
        37: "ArrowLeft",
        38: "ArrowUp",
        39: "ArrowRight",
        40: "ArrowDown",
        45: "Insert",
        46: "Delete",
        112: "F1",
        113: "F2",
        114: "F3",
        115: "F4",
        116: "F5",
        117: "F6",
        118: "F7",
        119: "F8",
        120: "F9",
        121: "F10",
        122: "F11",
        123: "F12",
        144: "NumLock",
        145: "ScrollLock",
        224: "Meta"
      };
      var Od = { Alt: "altKey", Control: "ctrlKey", Meta: "metaKey", Shift: "shiftKey" };
      function Pd(a) {
        var b = this.nativeEvent;
        return b.getModifierState ? b.getModifierState(a) : (a = Od[a]) ? !!b[a] : false;
      }
      __name(Pd, "Pd");
      function zd() {
        return Pd;
      }
      __name(zd, "zd");
      var Qd = A({}, ud, { key: /* @__PURE__ */ __name(function(a) {
        if (a.key) {
          var b = Md[a.key] || a.key;
          if ("Unidentified" !== b) return b;
        }
        return "keypress" === a.type ? (a = od(a), 13 === a ? "Enter" : String.fromCharCode(a)) : "keydown" === a.type || "keyup" === a.type ? Nd[a.keyCode] || "Unidentified" : "";
      }, "key"), code: 0, location: 0, ctrlKey: 0, shiftKey: 0, altKey: 0, metaKey: 0, repeat: 0, locale: 0, getModifierState: zd, charCode: /* @__PURE__ */ __name(function(a) {
        return "keypress" === a.type ? od(a) : 0;
      }, "charCode"), keyCode: /* @__PURE__ */ __name(function(a) {
        return "keydown" === a.type || "keyup" === a.type ? a.keyCode : 0;
      }, "keyCode"), which: /* @__PURE__ */ __name(function(a) {
        return "keypress" === a.type ? od(a) : "keydown" === a.type || "keyup" === a.type ? a.keyCode : 0;
      }, "which") });
      var Rd = rd(Qd);
      var Sd = A({}, Ad, { pointerId: 0, width: 0, height: 0, pressure: 0, tangentialPressure: 0, tiltX: 0, tiltY: 0, twist: 0, pointerType: 0, isPrimary: 0 });
      var Td = rd(Sd);
      var Ud = A({}, ud, { touches: 0, targetTouches: 0, changedTouches: 0, altKey: 0, metaKey: 0, ctrlKey: 0, shiftKey: 0, getModifierState: zd });
      var Vd = rd(Ud);
      var Wd = A({}, sd, { propertyName: 0, elapsedTime: 0, pseudoElement: 0 });
      var Xd = rd(Wd);
      var Yd = A({}, Ad, {
        deltaX: /* @__PURE__ */ __name(function(a) {
          return "deltaX" in a ? a.deltaX : "wheelDeltaX" in a ? -a.wheelDeltaX : 0;
        }, "deltaX"),
        deltaY: /* @__PURE__ */ __name(function(a) {
          return "deltaY" in a ? a.deltaY : "wheelDeltaY" in a ? -a.wheelDeltaY : "wheelDelta" in a ? -a.wheelDelta : 0;
        }, "deltaY"),
        deltaZ: 0,
        deltaMode: 0
      });
      var Zd = rd(Yd);
      var $d = [9, 13, 27, 32];
      var ae = ia && "CompositionEvent" in window;
      var be = null;
      ia && "documentMode" in document && (be = document.documentMode);
      var ce = ia && "TextEvent" in window && !be;
      var de = ia && (!ae || be && 8 < be && 11 >= be);
      var ee = String.fromCharCode(32);
      var fe = false;
      function ge(a, b) {
        switch (a) {
          case "keyup":
            return -1 !== $d.indexOf(b.keyCode);
          case "keydown":
            return 229 !== b.keyCode;
          case "keypress":
          case "mousedown":
          case "focusout":
            return true;
          default:
            return false;
        }
      }
      __name(ge, "ge");
      function he(a) {
        a = a.detail;
        return "object" === typeof a && "data" in a ? a.data : null;
      }
      __name(he, "he");
      var ie = false;
      function je(a, b) {
        switch (a) {
          case "compositionend":
            return he(b);
          case "keypress":
            if (32 !== b.which) return null;
            fe = true;
            return ee;
          case "textInput":
            return a = b.data, a === ee && fe ? null : a;
          default:
            return null;
        }
      }
      __name(je, "je");
      function ke(a, b) {
        if (ie) return "compositionend" === a || !ae && ge(a, b) ? (a = nd(), md = ld = kd = null, ie = false, a) : null;
        switch (a) {
          case "paste":
            return null;
          case "keypress":
            if (!(b.ctrlKey || b.altKey || b.metaKey) || b.ctrlKey && b.altKey) {
              if (b.char && 1 < b.char.length) return b.char;
              if (b.which) return String.fromCharCode(b.which);
            }
            return null;
          case "compositionend":
            return de && "ko" !== b.locale ? null : b.data;
          default:
            return null;
        }
      }
      __name(ke, "ke");
      var le = { color: true, date: true, datetime: true, "datetime-local": true, email: true, month: true, number: true, password: true, range: true, search: true, tel: true, text: true, time: true, url: true, week: true };
      function me(a) {
        var b = a && a.nodeName && a.nodeName.toLowerCase();
        return "input" === b ? !!le[a.type] : "textarea" === b ? true : false;
      }
      __name(me, "me");
      function ne(a, b, c, d) {
        Eb(d);
        b = oe(b, "onChange");
        0 < b.length && (c = new td("onChange", "change", null, c, d), a.push({ event: c, listeners: b }));
      }
      __name(ne, "ne");
      var pe = null;
      var qe = null;
      function re(a) {
        se(a, 0);
      }
      __name(re, "re");
      function te(a) {
        var b = ue(a);
        if (Wa(b)) return a;
      }
      __name(te, "te");
      function ve(a, b) {
        if ("change" === a) return b;
      }
      __name(ve, "ve");
      var we = false;
      if (ia) {
        if (ia) {
          ye = "oninput" in document;
          if (!ye) {
            ze = document.createElement("div");
            ze.setAttribute("oninput", "return;");
            ye = "function" === typeof ze.oninput;
          }
          xe = ye;
        } else xe = false;
        we = xe && (!document.documentMode || 9 < document.documentMode);
      }
      var xe;
      var ye;
      var ze;
      function Ae() {
        pe && (pe.detachEvent("onpropertychange", Be), qe = pe = null);
      }
      __name(Ae, "Ae");
      function Be(a) {
        if ("value" === a.propertyName && te(qe)) {
          var b = [];
          ne(b, qe, a, xb(a));
          Jb(re, b);
        }
      }
      __name(Be, "Be");
      function Ce(a, b, c) {
        "focusin" === a ? (Ae(), pe = b, qe = c, pe.attachEvent("onpropertychange", Be)) : "focusout" === a && Ae();
      }
      __name(Ce, "Ce");
      function De(a) {
        if ("selectionchange" === a || "keyup" === a || "keydown" === a) return te(qe);
      }
      __name(De, "De");
      function Ee(a, b) {
        if ("click" === a) return te(b);
      }
      __name(Ee, "Ee");
      function Fe(a, b) {
        if ("input" === a || "change" === a) return te(b);
      }
      __name(Fe, "Fe");
      function Ge(a, b) {
        return a === b && (0 !== a || 1 / a === 1 / b) || a !== a && b !== b;
      }
      __name(Ge, "Ge");
      var He = "function" === typeof Object.is ? Object.is : Ge;
      function Ie(a, b) {
        if (He(a, b)) return true;
        if ("object" !== typeof a || null === a || "object" !== typeof b || null === b) return false;
        var c = Object.keys(a), d = Object.keys(b);
        if (c.length !== d.length) return false;
        for (d = 0; d < c.length; d++) {
          var e = c[d];
          if (!ja.call(b, e) || !He(a[e], b[e])) return false;
        }
        return true;
      }
      __name(Ie, "Ie");
      function Je(a) {
        for (; a && a.firstChild; ) a = a.firstChild;
        return a;
      }
      __name(Je, "Je");
      function Ke(a, b) {
        var c = Je(a);
        a = 0;
        for (var d; c; ) {
          if (3 === c.nodeType) {
            d = a + c.textContent.length;
            if (a <= b && d >= b) return { node: c, offset: b - a };
            a = d;
          }
          a: {
            for (; c; ) {
              if (c.nextSibling) {
                c = c.nextSibling;
                break a;
              }
              c = c.parentNode;
            }
            c = void 0;
          }
          c = Je(c);
        }
      }
      __name(Ke, "Ke");
      function Le(a, b) {
        return a && b ? a === b ? true : a && 3 === a.nodeType ? false : b && 3 === b.nodeType ? Le(a, b.parentNode) : "contains" in a ? a.contains(b) : a.compareDocumentPosition ? !!(a.compareDocumentPosition(b) & 16) : false : false;
      }
      __name(Le, "Le");
      function Me() {
        for (var a = window, b = Xa(); b instanceof a.HTMLIFrameElement; ) {
          try {
            var c = "string" === typeof b.contentWindow.location.href;
          } catch (d) {
            c = false;
          }
          if (c) a = b.contentWindow;
          else break;
          b = Xa(a.document);
        }
        return b;
      }
      __name(Me, "Me");
      function Ne(a) {
        var b = a && a.nodeName && a.nodeName.toLowerCase();
        return b && ("input" === b && ("text" === a.type || "search" === a.type || "tel" === a.type || "url" === a.type || "password" === a.type) || "textarea" === b || "true" === a.contentEditable);
      }
      __name(Ne, "Ne");
      function Oe(a) {
        var b = Me(), c = a.focusedElem, d = a.selectionRange;
        if (b !== c && c && c.ownerDocument && Le(c.ownerDocument.documentElement, c)) {
          if (null !== d && Ne(c)) {
            if (b = d.start, a = d.end, void 0 === a && (a = b), "selectionStart" in c) c.selectionStart = b, c.selectionEnd = Math.min(a, c.value.length);
            else if (a = (b = c.ownerDocument || document) && b.defaultView || window, a.getSelection) {
              a = a.getSelection();
              var e = c.textContent.length, f = Math.min(d.start, e);
              d = void 0 === d.end ? f : Math.min(d.end, e);
              !a.extend && f > d && (e = d, d = f, f = e);
              e = Ke(c, f);
              var g = Ke(
                c,
                d
              );
              e && g && (1 !== a.rangeCount || a.anchorNode !== e.node || a.anchorOffset !== e.offset || a.focusNode !== g.node || a.focusOffset !== g.offset) && (b = b.createRange(), b.setStart(e.node, e.offset), a.removeAllRanges(), f > d ? (a.addRange(b), a.extend(g.node, g.offset)) : (b.setEnd(g.node, g.offset), a.addRange(b)));
            }
          }
          b = [];
          for (a = c; a = a.parentNode; ) 1 === a.nodeType && b.push({ element: a, left: a.scrollLeft, top: a.scrollTop });
          "function" === typeof c.focus && c.focus();
          for (c = 0; c < b.length; c++) a = b[c], a.element.scrollLeft = a.left, a.element.scrollTop = a.top;
        }
      }
      __name(Oe, "Oe");
      var Pe = ia && "documentMode" in document && 11 >= document.documentMode;
      var Qe = null;
      var Re = null;
      var Se = null;
      var Te = false;
      function Ue(a, b, c) {
        var d = c.window === c ? c.document : 9 === c.nodeType ? c : c.ownerDocument;
        Te || null == Qe || Qe !== Xa(d) || (d = Qe, "selectionStart" in d && Ne(d) ? d = { start: d.selectionStart, end: d.selectionEnd } : (d = (d.ownerDocument && d.ownerDocument.defaultView || window).getSelection(), d = { anchorNode: d.anchorNode, anchorOffset: d.anchorOffset, focusNode: d.focusNode, focusOffset: d.focusOffset }), Se && Ie(Se, d) || (Se = d, d = oe(Re, "onSelect"), 0 < d.length && (b = new td("onSelect", "select", null, b, c), a.push({ event: b, listeners: d }), b.target = Qe)));
      }
      __name(Ue, "Ue");
      function Ve(a, b) {
        var c = {};
        c[a.toLowerCase()] = b.toLowerCase();
        c["Webkit" + a] = "webkit" + b;
        c["Moz" + a] = "moz" + b;
        return c;
      }
      __name(Ve, "Ve");
      var We = { animationend: Ve("Animation", "AnimationEnd"), animationiteration: Ve("Animation", "AnimationIteration"), animationstart: Ve("Animation", "AnimationStart"), transitionend: Ve("Transition", "TransitionEnd") };
      var Xe = {};
      var Ye = {};
      ia && (Ye = document.createElement("div").style, "AnimationEvent" in window || (delete We.animationend.animation, delete We.animationiteration.animation, delete We.animationstart.animation), "TransitionEvent" in window || delete We.transitionend.transition);
      function Ze(a) {
        if (Xe[a]) return Xe[a];
        if (!We[a]) return a;
        var b = We[a], c;
        for (c in b) if (b.hasOwnProperty(c) && c in Ye) return Xe[a] = b[c];
        return a;
      }
      __name(Ze, "Ze");
      var $e = Ze("animationend");
      var af = Ze("animationiteration");
      var bf = Ze("animationstart");
      var cf = Ze("transitionend");
      var df = /* @__PURE__ */ new Map();
      var ef = "abort auxClick cancel canPlay canPlayThrough click close contextMenu copy cut drag dragEnd dragEnter dragExit dragLeave dragOver dragStart drop durationChange emptied encrypted ended error gotPointerCapture input invalid keyDown keyPress keyUp load loadedData loadedMetadata loadStart lostPointerCapture mouseDown mouseMove mouseOut mouseOver mouseUp paste pause play playing pointerCancel pointerDown pointerMove pointerOut pointerOver pointerUp progress rateChange reset resize seeked seeking stalled submit suspend timeUpdate touchCancel touchEnd touchStart volumeChange scroll toggle touchMove waiting wheel".split(" ");
      function ff(a, b) {
        df.set(a, b);
        fa(b, [a]);
      }
      __name(ff, "ff");
      for (gf = 0; gf < ef.length; gf++) {
        hf = ef[gf], jf = hf.toLowerCase(), kf = hf[0].toUpperCase() + hf.slice(1);
        ff(jf, "on" + kf);
      }
      var hf;
      var jf;
      var kf;
      var gf;
      ff($e, "onAnimationEnd");
      ff(af, "onAnimationIteration");
      ff(bf, "onAnimationStart");
      ff("dblclick", "onDoubleClick");
      ff("focusin", "onFocus");
      ff("focusout", "onBlur");
      ff(cf, "onTransitionEnd");
      ha("onMouseEnter", ["mouseout", "mouseover"]);
      ha("onMouseLeave", ["mouseout", "mouseover"]);
      ha("onPointerEnter", ["pointerout", "pointerover"]);
      ha("onPointerLeave", ["pointerout", "pointerover"]);
      fa("onChange", "change click focusin focusout input keydown keyup selectionchange".split(" "));
      fa("onSelect", "focusout contextmenu dragend focusin keydown keyup mousedown mouseup selectionchange".split(" "));
      fa("onBeforeInput", ["compositionend", "keypress", "textInput", "paste"]);
      fa("onCompositionEnd", "compositionend focusout keydown keypress keyup mousedown".split(" "));
      fa("onCompositionStart", "compositionstart focusout keydown keypress keyup mousedown".split(" "));
      fa("onCompositionUpdate", "compositionupdate focusout keydown keypress keyup mousedown".split(" "));
      var lf = "abort canplay canplaythrough durationchange emptied encrypted ended error loadeddata loadedmetadata loadstart pause play playing progress ratechange resize seeked seeking stalled suspend timeupdate volumechange waiting".split(" ");
      var mf = new Set("cancel close invalid load scroll toggle".split(" ").concat(lf));
      function nf(a, b, c) {
        var d = a.type || "unknown-event";
        a.currentTarget = c;
        Ub(d, b, void 0, a);
        a.currentTarget = null;
      }
      __name(nf, "nf");
      function se(a, b) {
        b = 0 !== (b & 4);
        for (var c = 0; c < a.length; c++) {
          var d = a[c], e = d.event;
          d = d.listeners;
          a: {
            var f = void 0;
            if (b) for (var g = d.length - 1; 0 <= g; g--) {
              var h = d[g], k = h.instance, l = h.currentTarget;
              h = h.listener;
              if (k !== f && e.isPropagationStopped()) break a;
              nf(e, h, l);
              f = k;
            }
            else for (g = 0; g < d.length; g++) {
              h = d[g];
              k = h.instance;
              l = h.currentTarget;
              h = h.listener;
              if (k !== f && e.isPropagationStopped()) break a;
              nf(e, h, l);
              f = k;
            }
          }
        }
        if (Qb) throw a = Rb, Qb = false, Rb = null, a;
      }
      __name(se, "se");
      function D(a, b) {
        var c = b[of];
        void 0 === c && (c = b[of] = /* @__PURE__ */ new Set());
        var d = a + "__bubble";
        c.has(d) || (pf(b, a, 2, false), c.add(d));
      }
      __name(D, "D");
      function qf(a, b, c) {
        var d = 0;
        b && (d |= 4);
        pf(c, a, d, b);
      }
      __name(qf, "qf");
      var rf = "_reactListening" + Math.random().toString(36).slice(2);
      function sf(a) {
        if (!a[rf]) {
          a[rf] = true;
          da.forEach(function(b2) {
            "selectionchange" !== b2 && (mf.has(b2) || qf(b2, false, a), qf(b2, true, a));
          });
          var b = 9 === a.nodeType ? a : a.ownerDocument;
          null === b || b[rf] || (b[rf] = true, qf("selectionchange", false, b));
        }
      }
      __name(sf, "sf");
      function pf(a, b, c, d) {
        switch (jd(b)) {
          case 1:
            var e = ed;
            break;
          case 4:
            e = gd;
            break;
          default:
            e = fd;
        }
        c = e.bind(null, b, c, a);
        e = void 0;
        !Lb || "touchstart" !== b && "touchmove" !== b && "wheel" !== b || (e = true);
        d ? void 0 !== e ? a.addEventListener(b, c, { capture: true, passive: e }) : a.addEventListener(b, c, true) : void 0 !== e ? a.addEventListener(b, c, { passive: e }) : a.addEventListener(b, c, false);
      }
      __name(pf, "pf");
      function hd(a, b, c, d, e) {
        var f = d;
        if (0 === (b & 1) && 0 === (b & 2) && null !== d) a: for (; ; ) {
          if (null === d) return;
          var g = d.tag;
          if (3 === g || 4 === g) {
            var h = d.stateNode.containerInfo;
            if (h === e || 8 === h.nodeType && h.parentNode === e) break;
            if (4 === g) for (g = d.return; null !== g; ) {
              var k = g.tag;
              if (3 === k || 4 === k) {
                if (k = g.stateNode.containerInfo, k === e || 8 === k.nodeType && k.parentNode === e) return;
              }
              g = g.return;
            }
            for (; null !== h; ) {
              g = Wc(h);
              if (null === g) return;
              k = g.tag;
              if (5 === k || 6 === k) {
                d = f = g;
                continue a;
              }
              h = h.parentNode;
            }
          }
          d = d.return;
        }
        Jb(function() {
          var d2 = f, e2 = xb(c), g2 = [];
          a: {
            var h2 = df.get(a);
            if (void 0 !== h2) {
              var k2 = td, n = a;
              switch (a) {
                case "keypress":
                  if (0 === od(c)) break a;
                case "keydown":
                case "keyup":
                  k2 = Rd;
                  break;
                case "focusin":
                  n = "focus";
                  k2 = Fd;
                  break;
                case "focusout":
                  n = "blur";
                  k2 = Fd;
                  break;
                case "beforeblur":
                case "afterblur":
                  k2 = Fd;
                  break;
                case "click":
                  if (2 === c.button) break a;
                case "auxclick":
                case "dblclick":
                case "mousedown":
                case "mousemove":
                case "mouseup":
                case "mouseout":
                case "mouseover":
                case "contextmenu":
                  k2 = Bd;
                  break;
                case "drag":
                case "dragend":
                case "dragenter":
                case "dragexit":
                case "dragleave":
                case "dragover":
                case "dragstart":
                case "drop":
                  k2 = Dd;
                  break;
                case "touchcancel":
                case "touchend":
                case "touchmove":
                case "touchstart":
                  k2 = Vd;
                  break;
                case $e:
                case af:
                case bf:
                  k2 = Hd;
                  break;
                case cf:
                  k2 = Xd;
                  break;
                case "scroll":
                  k2 = vd;
                  break;
                case "wheel":
                  k2 = Zd;
                  break;
                case "copy":
                case "cut":
                case "paste":
                  k2 = Jd;
                  break;
                case "gotpointercapture":
                case "lostpointercapture":
                case "pointercancel":
                case "pointerdown":
                case "pointermove":
                case "pointerout":
                case "pointerover":
                case "pointerup":
                  k2 = Td;
              }
              var t = 0 !== (b & 4), J = !t && "scroll" === a, x = t ? null !== h2 ? h2 + "Capture" : null : h2;
              t = [];
              for (var w = d2, u; null !== w; ) {
                u = w;
                var F = u.stateNode;
                5 === u.tag && null !== F && (u = F, null !== x && (F = Kb(w, x), null != F && t.push(tf(w, F, u))));
                if (J) break;
                w = w.return;
              }
              0 < t.length && (h2 = new k2(h2, n, null, c, e2), g2.push({ event: h2, listeners: t }));
            }
          }
          if (0 === (b & 7)) {
            a: {
              h2 = "mouseover" === a || "pointerover" === a;
              k2 = "mouseout" === a || "pointerout" === a;
              if (h2 && c !== wb && (n = c.relatedTarget || c.fromElement) && (Wc(n) || n[uf])) break a;
              if (k2 || h2) {
                h2 = e2.window === e2 ? e2 : (h2 = e2.ownerDocument) ? h2.defaultView || h2.parentWindow : window;
                if (k2) {
                  if (n = c.relatedTarget || c.toElement, k2 = d2, n = n ? Wc(n) : null, null !== n && (J = Vb(n), n !== J || 5 !== n.tag && 6 !== n.tag)) n = null;
                } else k2 = null, n = d2;
                if (k2 !== n) {
                  t = Bd;
                  F = "onMouseLeave";
                  x = "onMouseEnter";
                  w = "mouse";
                  if ("pointerout" === a || "pointerover" === a) t = Td, F = "onPointerLeave", x = "onPointerEnter", w = "pointer";
                  J = null == k2 ? h2 : ue(k2);
                  u = null == n ? h2 : ue(n);
                  h2 = new t(F, w + "leave", k2, c, e2);
                  h2.target = J;
                  h2.relatedTarget = u;
                  F = null;
                  Wc(e2) === d2 && (t = new t(x, w + "enter", n, c, e2), t.target = u, t.relatedTarget = J, F = t);
                  J = F;
                  if (k2 && n) b: {
                    t = k2;
                    x = n;
                    w = 0;
                    for (u = t; u; u = vf(u)) w++;
                    u = 0;
                    for (F = x; F; F = vf(F)) u++;
                    for (; 0 < w - u; ) t = vf(t), w--;
                    for (; 0 < u - w; ) x = vf(x), u--;
                    for (; w--; ) {
                      if (t === x || null !== x && t === x.alternate) break b;
                      t = vf(t);
                      x = vf(x);
                    }
                    t = null;
                  }
                  else t = null;
                  null !== k2 && wf(g2, h2, k2, t, false);
                  null !== n && null !== J && wf(g2, J, n, t, true);
                }
              }
            }
            a: {
              h2 = d2 ? ue(d2) : window;
              k2 = h2.nodeName && h2.nodeName.toLowerCase();
              if ("select" === k2 || "input" === k2 && "file" === h2.type) var na = ve;
              else if (me(h2)) if (we) na = Fe;
              else {
                na = De;
                var xa = Ce;
              }
              else (k2 = h2.nodeName) && "input" === k2.toLowerCase() && ("checkbox" === h2.type || "radio" === h2.type) && (na = Ee);
              if (na && (na = na(a, d2))) {
                ne(g2, na, c, e2);
                break a;
              }
              xa && xa(a, h2, d2);
              "focusout" === a && (xa = h2._wrapperState) && xa.controlled && "number" === h2.type && cb(h2, "number", h2.value);
            }
            xa = d2 ? ue(d2) : window;
            switch (a) {
              case "focusin":
                if (me(xa) || "true" === xa.contentEditable) Qe = xa, Re = d2, Se = null;
                break;
              case "focusout":
                Se = Re = Qe = null;
                break;
              case "mousedown":
                Te = true;
                break;
              case "contextmenu":
              case "mouseup":
              case "dragend":
                Te = false;
                Ue(g2, c, e2);
                break;
              case "selectionchange":
                if (Pe) break;
              case "keydown":
              case "keyup":
                Ue(g2, c, e2);
            }
            var $a;
            if (ae) b: {
              switch (a) {
                case "compositionstart":
                  var ba = "onCompositionStart";
                  break b;
                case "compositionend":
                  ba = "onCompositionEnd";
                  break b;
                case "compositionupdate":
                  ba = "onCompositionUpdate";
                  break b;
              }
              ba = void 0;
            }
            else ie ? ge(a, c) && (ba = "onCompositionEnd") : "keydown" === a && 229 === c.keyCode && (ba = "onCompositionStart");
            ba && (de && "ko" !== c.locale && (ie || "onCompositionStart" !== ba ? "onCompositionEnd" === ba && ie && ($a = nd()) : (kd = e2, ld = "value" in kd ? kd.value : kd.textContent, ie = true)), xa = oe(d2, ba), 0 < xa.length && (ba = new Ld(ba, a, null, c, e2), g2.push({ event: ba, listeners: xa }), $a ? ba.data = $a : ($a = he(c), null !== $a && (ba.data = $a))));
            if ($a = ce ? je(a, c) : ke(a, c)) d2 = oe(d2, "onBeforeInput"), 0 < d2.length && (e2 = new Ld("onBeforeInput", "beforeinput", null, c, e2), g2.push({ event: e2, listeners: d2 }), e2.data = $a);
          }
          se(g2, b);
        });
      }
      __name(hd, "hd");
      function tf(a, b, c) {
        return { instance: a, listener: b, currentTarget: c };
      }
      __name(tf, "tf");
      function oe(a, b) {
        for (var c = b + "Capture", d = []; null !== a; ) {
          var e = a, f = e.stateNode;
          5 === e.tag && null !== f && (e = f, f = Kb(a, c), null != f && d.unshift(tf(a, f, e)), f = Kb(a, b), null != f && d.push(tf(a, f, e)));
          a = a.return;
        }
        return d;
      }
      __name(oe, "oe");
      function vf(a) {
        if (null === a) return null;
        do
          a = a.return;
        while (a && 5 !== a.tag);
        return a ? a : null;
      }
      __name(vf, "vf");
      function wf(a, b, c, d, e) {
        for (var f = b._reactName, g = []; null !== c && c !== d; ) {
          var h = c, k = h.alternate, l = h.stateNode;
          if (null !== k && k === d) break;
          5 === h.tag && null !== l && (h = l, e ? (k = Kb(c, f), null != k && g.unshift(tf(c, k, h))) : e || (k = Kb(c, f), null != k && g.push(tf(c, k, h))));
          c = c.return;
        }
        0 !== g.length && a.push({ event: b, listeners: g });
      }
      __name(wf, "wf");
      var xf = /\r\n?/g;
      var yf = /\u0000|\uFFFD/g;
      function zf(a) {
        return ("string" === typeof a ? a : "" + a).replace(xf, "\n").replace(yf, "");
      }
      __name(zf, "zf");
      function Af(a, b, c) {
        b = zf(b);
        if (zf(a) !== b && c) throw Error(p(425));
      }
      __name(Af, "Af");
      function Bf() {
      }
      __name(Bf, "Bf");
      var Cf = null;
      var Df = null;
      function Ef(a, b) {
        return "textarea" === a || "noscript" === a || "string" === typeof b.children || "number" === typeof b.children || "object" === typeof b.dangerouslySetInnerHTML && null !== b.dangerouslySetInnerHTML && null != b.dangerouslySetInnerHTML.__html;
      }
      __name(Ef, "Ef");
      var Ff = "function" === typeof setTimeout ? setTimeout : void 0;
      var Gf = "function" === typeof clearTimeout ? clearTimeout : void 0;
      var Hf = "function" === typeof Promise ? Promise : void 0;
      var Jf = "function" === typeof queueMicrotask ? queueMicrotask : "undefined" !== typeof Hf ? function(a) {
        return Hf.resolve(null).then(a).catch(If);
      } : Ff;
      function If(a) {
        setTimeout(function() {
          throw a;
        });
      }
      __name(If, "If");
      function Kf(a, b) {
        var c = b, d = 0;
        do {
          var e = c.nextSibling;
          a.removeChild(c);
          if (e && 8 === e.nodeType) if (c = e.data, "/$" === c) {
            if (0 === d) {
              a.removeChild(e);
              bd(b);
              return;
            }
            d--;
          } else "$" !== c && "$?" !== c && "$!" !== c || d++;
          c = e;
        } while (c);
        bd(b);
      }
      __name(Kf, "Kf");
      function Lf(a) {
        for (; null != a; a = a.nextSibling) {
          var b = a.nodeType;
          if (1 === b || 3 === b) break;
          if (8 === b) {
            b = a.data;
            if ("$" === b || "$!" === b || "$?" === b) break;
            if ("/$" === b) return null;
          }
        }
        return a;
      }
      __name(Lf, "Lf");
      function Mf(a) {
        a = a.previousSibling;
        for (var b = 0; a; ) {
          if (8 === a.nodeType) {
            var c = a.data;
            if ("$" === c || "$!" === c || "$?" === c) {
              if (0 === b) return a;
              b--;
            } else "/$" === c && b++;
          }
          a = a.previousSibling;
        }
        return null;
      }
      __name(Mf, "Mf");
      var Nf = Math.random().toString(36).slice(2);
      var Of = "__reactFiber$" + Nf;
      var Pf = "__reactProps$" + Nf;
      var uf = "__reactContainer$" + Nf;
      var of = "__reactEvents$" + Nf;
      var Qf = "__reactListeners$" + Nf;
      var Rf = "__reactHandles$" + Nf;
      function Wc(a) {
        var b = a[Of];
        if (b) return b;
        for (var c = a.parentNode; c; ) {
          if (b = c[uf] || c[Of]) {
            c = b.alternate;
            if (null !== b.child || null !== c && null !== c.child) for (a = Mf(a); null !== a; ) {
              if (c = a[Of]) return c;
              a = Mf(a);
            }
            return b;
          }
          a = c;
          c = a.parentNode;
        }
        return null;
      }
      __name(Wc, "Wc");
      function Cb(a) {
        a = a[Of] || a[uf];
        return !a || 5 !== a.tag && 6 !== a.tag && 13 !== a.tag && 3 !== a.tag ? null : a;
      }
      __name(Cb, "Cb");
      function ue(a) {
        if (5 === a.tag || 6 === a.tag) return a.stateNode;
        throw Error(p(33));
      }
      __name(ue, "ue");
      function Db(a) {
        return a[Pf] || null;
      }
      __name(Db, "Db");
      var Sf = [];
      var Tf = -1;
      function Uf(a) {
        return { current: a };
      }
      __name(Uf, "Uf");
      function E(a) {
        0 > Tf || (a.current = Sf[Tf], Sf[Tf] = null, Tf--);
      }
      __name(E, "E");
      function G(a, b) {
        Tf++;
        Sf[Tf] = a.current;
        a.current = b;
      }
      __name(G, "G");
      var Vf = {};
      var H = Uf(Vf);
      var Wf = Uf(false);
      var Xf = Vf;
      function Yf(a, b) {
        var c = a.type.contextTypes;
        if (!c) return Vf;
        var d = a.stateNode;
        if (d && d.__reactInternalMemoizedUnmaskedChildContext === b) return d.__reactInternalMemoizedMaskedChildContext;
        var e = {}, f;
        for (f in c) e[f] = b[f];
        d && (a = a.stateNode, a.__reactInternalMemoizedUnmaskedChildContext = b, a.__reactInternalMemoizedMaskedChildContext = e);
        return e;
      }
      __name(Yf, "Yf");
      function Zf(a) {
        a = a.childContextTypes;
        return null !== a && void 0 !== a;
      }
      __name(Zf, "Zf");
      function $f() {
        E(Wf);
        E(H);
      }
      __name($f, "$f");
      function ag(a, b, c) {
        if (H.current !== Vf) throw Error(p(168));
        G(H, b);
        G(Wf, c);
      }
      __name(ag, "ag");
      function bg(a, b, c) {
        var d = a.stateNode;
        b = b.childContextTypes;
        if ("function" !== typeof d.getChildContext) return c;
        d = d.getChildContext();
        for (var e in d) if (!(e in b)) throw Error(p(108, Ra(a) || "Unknown", e));
        return A({}, c, d);
      }
      __name(bg, "bg");
      function cg(a) {
        a = (a = a.stateNode) && a.__reactInternalMemoizedMergedChildContext || Vf;
        Xf = H.current;
        G(H, a);
        G(Wf, Wf.current);
        return true;
      }
      __name(cg, "cg");
      function dg(a, b, c) {
        var d = a.stateNode;
        if (!d) throw Error(p(169));
        c ? (a = bg(a, b, Xf), d.__reactInternalMemoizedMergedChildContext = a, E(Wf), E(H), G(H, a)) : E(Wf);
        G(Wf, c);
      }
      __name(dg, "dg");
      var eg = null;
      var fg = false;
      var gg = false;
      function hg(a) {
        null === eg ? eg = [a] : eg.push(a);
      }
      __name(hg, "hg");
      function ig(a) {
        fg = true;
        hg(a);
      }
      __name(ig, "ig");
      function jg() {
        if (!gg && null !== eg) {
          gg = true;
          var a = 0, b = C;
          try {
            var c = eg;
            for (C = 1; a < c.length; a++) {
              var d = c[a];
              do
                d = d(true);
              while (null !== d);
            }
            eg = null;
            fg = false;
          } catch (e) {
            throw null !== eg && (eg = eg.slice(a + 1)), ac(fc, jg), e;
          } finally {
            C = b, gg = false;
          }
        }
        return null;
      }
      __name(jg, "jg");
      var kg = [];
      var lg = 0;
      var mg = null;
      var ng = 0;
      var og = [];
      var pg = 0;
      var qg = null;
      var rg = 1;
      var sg = "";
      function tg(a, b) {
        kg[lg++] = ng;
        kg[lg++] = mg;
        mg = a;
        ng = b;
      }
      __name(tg, "tg");
      function ug(a, b, c) {
        og[pg++] = rg;
        og[pg++] = sg;
        og[pg++] = qg;
        qg = a;
        var d = rg;
        a = sg;
        var e = 32 - oc(d) - 1;
        d &= ~(1 << e);
        c += 1;
        var f = 32 - oc(b) + e;
        if (30 < f) {
          var g = e - e % 5;
          f = (d & (1 << g) - 1).toString(32);
          d >>= g;
          e -= g;
          rg = 1 << 32 - oc(b) + e | c << e | d;
          sg = f + a;
        } else rg = 1 << f | c << e | d, sg = a;
      }
      __name(ug, "ug");
      function vg(a) {
        null !== a.return && (tg(a, 1), ug(a, 1, 0));
      }
      __name(vg, "vg");
      function wg(a) {
        for (; a === mg; ) mg = kg[--lg], kg[lg] = null, ng = kg[--lg], kg[lg] = null;
        for (; a === qg; ) qg = og[--pg], og[pg] = null, sg = og[--pg], og[pg] = null, rg = og[--pg], og[pg] = null;
      }
      __name(wg, "wg");
      var xg = null;
      var yg = null;
      var I = false;
      var zg = null;
      function Ag(a, b) {
        var c = Bg(5, null, null, 0);
        c.elementType = "DELETED";
        c.stateNode = b;
        c.return = a;
        b = a.deletions;
        null === b ? (a.deletions = [c], a.flags |= 16) : b.push(c);
      }
      __name(Ag, "Ag");
      function Cg(a, b) {
        switch (a.tag) {
          case 5:
            var c = a.type;
            b = 1 !== b.nodeType || c.toLowerCase() !== b.nodeName.toLowerCase() ? null : b;
            return null !== b ? (a.stateNode = b, xg = a, yg = Lf(b.firstChild), true) : false;
          case 6:
            return b = "" === a.pendingProps || 3 !== b.nodeType ? null : b, null !== b ? (a.stateNode = b, xg = a, yg = null, true) : false;
          case 13:
            return b = 8 !== b.nodeType ? null : b, null !== b ? (c = null !== qg ? { id: rg, overflow: sg } : null, a.memoizedState = { dehydrated: b, treeContext: c, retryLane: 1073741824 }, c = Bg(18, null, null, 0), c.stateNode = b, c.return = a, a.child = c, xg = a, yg = null, true) : false;
          default:
            return false;
        }
      }
      __name(Cg, "Cg");
      function Dg(a) {
        return 0 !== (a.mode & 1) && 0 === (a.flags & 128);
      }
      __name(Dg, "Dg");
      function Eg(a) {
        if (I) {
          var b = yg;
          if (b) {
            var c = b;
            if (!Cg(a, b)) {
              if (Dg(a)) throw Error(p(418));
              b = Lf(c.nextSibling);
              var d = xg;
              b && Cg(a, b) ? Ag(d, c) : (a.flags = a.flags & -4097 | 2, I = false, xg = a);
            }
          } else {
            if (Dg(a)) throw Error(p(418));
            a.flags = a.flags & -4097 | 2;
            I = false;
            xg = a;
          }
        }
      }
      __name(Eg, "Eg");
      function Fg(a) {
        for (a = a.return; null !== a && 5 !== a.tag && 3 !== a.tag && 13 !== a.tag; ) a = a.return;
        xg = a;
      }
      __name(Fg, "Fg");
      function Gg(a) {
        if (a !== xg) return false;
        if (!I) return Fg(a), I = true, false;
        var b;
        (b = 3 !== a.tag) && !(b = 5 !== a.tag) && (b = a.type, b = "head" !== b && "body" !== b && !Ef(a.type, a.memoizedProps));
        if (b && (b = yg)) {
          if (Dg(a)) throw Hg(), Error(p(418));
          for (; b; ) Ag(a, b), b = Lf(b.nextSibling);
        }
        Fg(a);
        if (13 === a.tag) {
          a = a.memoizedState;
          a = null !== a ? a.dehydrated : null;
          if (!a) throw Error(p(317));
          a: {
            a = a.nextSibling;
            for (b = 0; a; ) {
              if (8 === a.nodeType) {
                var c = a.data;
                if ("/$" === c) {
                  if (0 === b) {
                    yg = Lf(a.nextSibling);
                    break a;
                  }
                  b--;
                } else "$" !== c && "$!" !== c && "$?" !== c || b++;
              }
              a = a.nextSibling;
            }
            yg = null;
          }
        } else yg = xg ? Lf(a.stateNode.nextSibling) : null;
        return true;
      }
      __name(Gg, "Gg");
      function Hg() {
        for (var a = yg; a; ) a = Lf(a.nextSibling);
      }
      __name(Hg, "Hg");
      function Ig() {
        yg = xg = null;
        I = false;
      }
      __name(Ig, "Ig");
      function Jg(a) {
        null === zg ? zg = [a] : zg.push(a);
      }
      __name(Jg, "Jg");
      var Kg = ua.ReactCurrentBatchConfig;
      function Lg(a, b, c) {
        a = c.ref;
        if (null !== a && "function" !== typeof a && "object" !== typeof a) {
          if (c._owner) {
            c = c._owner;
            if (c) {
              if (1 !== c.tag) throw Error(p(309));
              var d = c.stateNode;
            }
            if (!d) throw Error(p(147, a));
            var e = d, f = "" + a;
            if (null !== b && null !== b.ref && "function" === typeof b.ref && b.ref._stringRef === f) return b.ref;
            b = /* @__PURE__ */ __name(function(a2) {
              var b2 = e.refs;
              null === a2 ? delete b2[f] : b2[f] = a2;
            }, "b");
            b._stringRef = f;
            return b;
          }
          if ("string" !== typeof a) throw Error(p(284));
          if (!c._owner) throw Error(p(290, a));
        }
        return a;
      }
      __name(Lg, "Lg");
      function Mg(a, b) {
        a = Object.prototype.toString.call(b);
        throw Error(p(31, "[object Object]" === a ? "object with keys {" + Object.keys(b).join(", ") + "}" : a));
      }
      __name(Mg, "Mg");
      function Ng(a) {
        var b = a._init;
        return b(a._payload);
      }
      __name(Ng, "Ng");
      function Og(a) {
        function b(b2, c2) {
          if (a) {
            var d2 = b2.deletions;
            null === d2 ? (b2.deletions = [c2], b2.flags |= 16) : d2.push(c2);
          }
        }
        __name(b, "b");
        function c(c2, d2) {
          if (!a) return null;
          for (; null !== d2; ) b(c2, d2), d2 = d2.sibling;
          return null;
        }
        __name(c, "c");
        function d(a2, b2) {
          for (a2 = /* @__PURE__ */ new Map(); null !== b2; ) null !== b2.key ? a2.set(b2.key, b2) : a2.set(b2.index, b2), b2 = b2.sibling;
          return a2;
        }
        __name(d, "d");
        function e(a2, b2) {
          a2 = Pg(a2, b2);
          a2.index = 0;
          a2.sibling = null;
          return a2;
        }
        __name(e, "e");
        function f(b2, c2, d2) {
          b2.index = d2;
          if (!a) return b2.flags |= 1048576, c2;
          d2 = b2.alternate;
          if (null !== d2) return d2 = d2.index, d2 < c2 ? (b2.flags |= 2, c2) : d2;
          b2.flags |= 2;
          return c2;
        }
        __name(f, "f");
        function g(b2) {
          a && null === b2.alternate && (b2.flags |= 2);
          return b2;
        }
        __name(g, "g");
        function h(a2, b2, c2, d2) {
          if (null === b2 || 6 !== b2.tag) return b2 = Qg(c2, a2.mode, d2), b2.return = a2, b2;
          b2 = e(b2, c2);
          b2.return = a2;
          return b2;
        }
        __name(h, "h");
        function k(a2, b2, c2, d2) {
          var f2 = c2.type;
          if (f2 === ya) return m(a2, b2, c2.props.children, d2, c2.key);
          if (null !== b2 && (b2.elementType === f2 || "object" === typeof f2 && null !== f2 && f2.$$typeof === Ha && Ng(f2) === b2.type)) return d2 = e(b2, c2.props), d2.ref = Lg(a2, b2, c2), d2.return = a2, d2;
          d2 = Rg(c2.type, c2.key, c2.props, null, a2.mode, d2);
          d2.ref = Lg(a2, b2, c2);
          d2.return = a2;
          return d2;
        }
        __name(k, "k");
        function l(a2, b2, c2, d2) {
          if (null === b2 || 4 !== b2.tag || b2.stateNode.containerInfo !== c2.containerInfo || b2.stateNode.implementation !== c2.implementation) return b2 = Sg(c2, a2.mode, d2), b2.return = a2, b2;
          b2 = e(b2, c2.children || []);
          b2.return = a2;
          return b2;
        }
        __name(l, "l");
        function m(a2, b2, c2, d2, f2) {
          if (null === b2 || 7 !== b2.tag) return b2 = Tg(c2, a2.mode, d2, f2), b2.return = a2, b2;
          b2 = e(b2, c2);
          b2.return = a2;
          return b2;
        }
        __name(m, "m");
        function q(a2, b2, c2) {
          if ("string" === typeof b2 && "" !== b2 || "number" === typeof b2) return b2 = Qg("" + b2, a2.mode, c2), b2.return = a2, b2;
          if ("object" === typeof b2 && null !== b2) {
            switch (b2.$$typeof) {
              case va:
                return c2 = Rg(b2.type, b2.key, b2.props, null, a2.mode, c2), c2.ref = Lg(a2, null, b2), c2.return = a2, c2;
              case wa:
                return b2 = Sg(b2, a2.mode, c2), b2.return = a2, b2;
              case Ha:
                var d2 = b2._init;
                return q(a2, d2(b2._payload), c2);
            }
            if (eb(b2) || Ka(b2)) return b2 = Tg(b2, a2.mode, c2, null), b2.return = a2, b2;
            Mg(a2, b2);
          }
          return null;
        }
        __name(q, "q");
        function r(a2, b2, c2, d2) {
          var e2 = null !== b2 ? b2.key : null;
          if ("string" === typeof c2 && "" !== c2 || "number" === typeof c2) return null !== e2 ? null : h(a2, b2, "" + c2, d2);
          if ("object" === typeof c2 && null !== c2) {
            switch (c2.$$typeof) {
              case va:
                return c2.key === e2 ? k(a2, b2, c2, d2) : null;
              case wa:
                return c2.key === e2 ? l(a2, b2, c2, d2) : null;
              case Ha:
                return e2 = c2._init, r(
                  a2,
                  b2,
                  e2(c2._payload),
                  d2
                );
            }
            if (eb(c2) || Ka(c2)) return null !== e2 ? null : m(a2, b2, c2, d2, null);
            Mg(a2, c2);
          }
          return null;
        }
        __name(r, "r");
        function y(a2, b2, c2, d2, e2) {
          if ("string" === typeof d2 && "" !== d2 || "number" === typeof d2) return a2 = a2.get(c2) || null, h(b2, a2, "" + d2, e2);
          if ("object" === typeof d2 && null !== d2) {
            switch (d2.$$typeof) {
              case va:
                return a2 = a2.get(null === d2.key ? c2 : d2.key) || null, k(b2, a2, d2, e2);
              case wa:
                return a2 = a2.get(null === d2.key ? c2 : d2.key) || null, l(b2, a2, d2, e2);
              case Ha:
                var f2 = d2._init;
                return y(a2, b2, c2, f2(d2._payload), e2);
            }
            if (eb(d2) || Ka(d2)) return a2 = a2.get(c2) || null, m(b2, a2, d2, e2, null);
            Mg(b2, d2);
          }
          return null;
        }
        __name(y, "y");
        function n(e2, g2, h2, k2) {
          for (var l2 = null, m2 = null, u = g2, w = g2 = 0, x = null; null !== u && w < h2.length; w++) {
            u.index > w ? (x = u, u = null) : x = u.sibling;
            var n2 = r(e2, u, h2[w], k2);
            if (null === n2) {
              null === u && (u = x);
              break;
            }
            a && u && null === n2.alternate && b(e2, u);
            g2 = f(n2, g2, w);
            null === m2 ? l2 = n2 : m2.sibling = n2;
            m2 = n2;
            u = x;
          }
          if (w === h2.length) return c(e2, u), I && tg(e2, w), l2;
          if (null === u) {
            for (; w < h2.length; w++) u = q(e2, h2[w], k2), null !== u && (g2 = f(u, g2, w), null === m2 ? l2 = u : m2.sibling = u, m2 = u);
            I && tg(e2, w);
            return l2;
          }
          for (u = d(e2, u); w < h2.length; w++) x = y(u, e2, w, h2[w], k2), null !== x && (a && null !== x.alternate && u.delete(null === x.key ? w : x.key), g2 = f(x, g2, w), null === m2 ? l2 = x : m2.sibling = x, m2 = x);
          a && u.forEach(function(a2) {
            return b(e2, a2);
          });
          I && tg(e2, w);
          return l2;
        }
        __name(n, "n");
        function t(e2, g2, h2, k2) {
          var l2 = Ka(h2);
          if ("function" !== typeof l2) throw Error(p(150));
          h2 = l2.call(h2);
          if (null == h2) throw Error(p(151));
          for (var u = l2 = null, m2 = g2, w = g2 = 0, x = null, n2 = h2.next(); null !== m2 && !n2.done; w++, n2 = h2.next()) {
            m2.index > w ? (x = m2, m2 = null) : x = m2.sibling;
            var t2 = r(e2, m2, n2.value, k2);
            if (null === t2) {
              null === m2 && (m2 = x);
              break;
            }
            a && m2 && null === t2.alternate && b(e2, m2);
            g2 = f(t2, g2, w);
            null === u ? l2 = t2 : u.sibling = t2;
            u = t2;
            m2 = x;
          }
          if (n2.done) return c(
            e2,
            m2
          ), I && tg(e2, w), l2;
          if (null === m2) {
            for (; !n2.done; w++, n2 = h2.next()) n2 = q(e2, n2.value, k2), null !== n2 && (g2 = f(n2, g2, w), null === u ? l2 = n2 : u.sibling = n2, u = n2);
            I && tg(e2, w);
            return l2;
          }
          for (m2 = d(e2, m2); !n2.done; w++, n2 = h2.next()) n2 = y(m2, e2, w, n2.value, k2), null !== n2 && (a && null !== n2.alternate && m2.delete(null === n2.key ? w : n2.key), g2 = f(n2, g2, w), null === u ? l2 = n2 : u.sibling = n2, u = n2);
          a && m2.forEach(function(a2) {
            return b(e2, a2);
          });
          I && tg(e2, w);
          return l2;
        }
        __name(t, "t");
        function J(a2, d2, f2, h2) {
          "object" === typeof f2 && null !== f2 && f2.type === ya && null === f2.key && (f2 = f2.props.children);
          if ("object" === typeof f2 && null !== f2) {
            switch (f2.$$typeof) {
              case va:
                a: {
                  for (var k2 = f2.key, l2 = d2; null !== l2; ) {
                    if (l2.key === k2) {
                      k2 = f2.type;
                      if (k2 === ya) {
                        if (7 === l2.tag) {
                          c(a2, l2.sibling);
                          d2 = e(l2, f2.props.children);
                          d2.return = a2;
                          a2 = d2;
                          break a;
                        }
                      } else if (l2.elementType === k2 || "object" === typeof k2 && null !== k2 && k2.$$typeof === Ha && Ng(k2) === l2.type) {
                        c(a2, l2.sibling);
                        d2 = e(l2, f2.props);
                        d2.ref = Lg(a2, l2, f2);
                        d2.return = a2;
                        a2 = d2;
                        break a;
                      }
                      c(a2, l2);
                      break;
                    } else b(a2, l2);
                    l2 = l2.sibling;
                  }
                  f2.type === ya ? (d2 = Tg(f2.props.children, a2.mode, h2, f2.key), d2.return = a2, a2 = d2) : (h2 = Rg(f2.type, f2.key, f2.props, null, a2.mode, h2), h2.ref = Lg(a2, d2, f2), h2.return = a2, a2 = h2);
                }
                return g(a2);
              case wa:
                a: {
                  for (l2 = f2.key; null !== d2; ) {
                    if (d2.key === l2) if (4 === d2.tag && d2.stateNode.containerInfo === f2.containerInfo && d2.stateNode.implementation === f2.implementation) {
                      c(a2, d2.sibling);
                      d2 = e(d2, f2.children || []);
                      d2.return = a2;
                      a2 = d2;
                      break a;
                    } else {
                      c(a2, d2);
                      break;
                    }
                    else b(a2, d2);
                    d2 = d2.sibling;
                  }
                  d2 = Sg(f2, a2.mode, h2);
                  d2.return = a2;
                  a2 = d2;
                }
                return g(a2);
              case Ha:
                return l2 = f2._init, J(a2, d2, l2(f2._payload), h2);
            }
            if (eb(f2)) return n(a2, d2, f2, h2);
            if (Ka(f2)) return t(a2, d2, f2, h2);
            Mg(a2, f2);
          }
          return "string" === typeof f2 && "" !== f2 || "number" === typeof f2 ? (f2 = "" + f2, null !== d2 && 6 === d2.tag ? (c(a2, d2.sibling), d2 = e(d2, f2), d2.return = a2, a2 = d2) : (c(a2, d2), d2 = Qg(f2, a2.mode, h2), d2.return = a2, a2 = d2), g(a2)) : c(a2, d2);
        }
        __name(J, "J");
        return J;
      }
      __name(Og, "Og");
      var Ug = Og(true);
      var Vg = Og(false);
      var Wg = Uf(null);
      var Xg = null;
      var Yg = null;
      var Zg = null;
      function $g() {
        Zg = Yg = Xg = null;
      }
      __name($g, "$g");
      function ah(a) {
        var b = Wg.current;
        E(Wg);
        a._currentValue = b;
      }
      __name(ah, "ah");
      function bh(a, b, c) {
        for (; null !== a; ) {
          var d = a.alternate;
          (a.childLanes & b) !== b ? (a.childLanes |= b, null !== d && (d.childLanes |= b)) : null !== d && (d.childLanes & b) !== b && (d.childLanes |= b);
          if (a === c) break;
          a = a.return;
        }
      }
      __name(bh, "bh");
      function ch(a, b) {
        Xg = a;
        Zg = Yg = null;
        a = a.dependencies;
        null !== a && null !== a.firstContext && (0 !== (a.lanes & b) && (dh = true), a.firstContext = null);
      }
      __name(ch, "ch");
      function eh(a) {
        var b = a._currentValue;
        if (Zg !== a) if (a = { context: a, memoizedValue: b, next: null }, null === Yg) {
          if (null === Xg) throw Error(p(308));
          Yg = a;
          Xg.dependencies = { lanes: 0, firstContext: a };
        } else Yg = Yg.next = a;
        return b;
      }
      __name(eh, "eh");
      var fh = null;
      function gh(a) {
        null === fh ? fh = [a] : fh.push(a);
      }
      __name(gh, "gh");
      function hh(a, b, c, d) {
        var e = b.interleaved;
        null === e ? (c.next = c, gh(b)) : (c.next = e.next, e.next = c);
        b.interleaved = c;
        return ih(a, d);
      }
      __name(hh, "hh");
      function ih(a, b) {
        a.lanes |= b;
        var c = a.alternate;
        null !== c && (c.lanes |= b);
        c = a;
        for (a = a.return; null !== a; ) a.childLanes |= b, c = a.alternate, null !== c && (c.childLanes |= b), c = a, a = a.return;
        return 3 === c.tag ? c.stateNode : null;
      }
      __name(ih, "ih");
      var jh = false;
      function kh(a) {
        a.updateQueue = { baseState: a.memoizedState, firstBaseUpdate: null, lastBaseUpdate: null, shared: { pending: null, interleaved: null, lanes: 0 }, effects: null };
      }
      __name(kh, "kh");
      function lh(a, b) {
        a = a.updateQueue;
        b.updateQueue === a && (b.updateQueue = { baseState: a.baseState, firstBaseUpdate: a.firstBaseUpdate, lastBaseUpdate: a.lastBaseUpdate, shared: a.shared, effects: a.effects });
      }
      __name(lh, "lh");
      function mh(a, b) {
        return { eventTime: a, lane: b, tag: 0, payload: null, callback: null, next: null };
      }
      __name(mh, "mh");
      function nh(a, b, c) {
        var d = a.updateQueue;
        if (null === d) return null;
        d = d.shared;
        if (0 !== (K & 2)) {
          var e = d.pending;
          null === e ? b.next = b : (b.next = e.next, e.next = b);
          d.pending = b;
          return ih(a, c);
        }
        e = d.interleaved;
        null === e ? (b.next = b, gh(d)) : (b.next = e.next, e.next = b);
        d.interleaved = b;
        return ih(a, c);
      }
      __name(nh, "nh");
      function oh(a, b, c) {
        b = b.updateQueue;
        if (null !== b && (b = b.shared, 0 !== (c & 4194240))) {
          var d = b.lanes;
          d &= a.pendingLanes;
          c |= d;
          b.lanes = c;
          Cc(a, c);
        }
      }
      __name(oh, "oh");
      function ph(a, b) {
        var c = a.updateQueue, d = a.alternate;
        if (null !== d && (d = d.updateQueue, c === d)) {
          var e = null, f = null;
          c = c.firstBaseUpdate;
          if (null !== c) {
            do {
              var g = { eventTime: c.eventTime, lane: c.lane, tag: c.tag, payload: c.payload, callback: c.callback, next: null };
              null === f ? e = f = g : f = f.next = g;
              c = c.next;
            } while (null !== c);
            null === f ? e = f = b : f = f.next = b;
          } else e = f = b;
          c = { baseState: d.baseState, firstBaseUpdate: e, lastBaseUpdate: f, shared: d.shared, effects: d.effects };
          a.updateQueue = c;
          return;
        }
        a = c.lastBaseUpdate;
        null === a ? c.firstBaseUpdate = b : a.next = b;
        c.lastBaseUpdate = b;
      }
      __name(ph, "ph");
      function qh(a, b, c, d) {
        var e = a.updateQueue;
        jh = false;
        var f = e.firstBaseUpdate, g = e.lastBaseUpdate, h = e.shared.pending;
        if (null !== h) {
          e.shared.pending = null;
          var k = h, l = k.next;
          k.next = null;
          null === g ? f = l : g.next = l;
          g = k;
          var m = a.alternate;
          null !== m && (m = m.updateQueue, h = m.lastBaseUpdate, h !== g && (null === h ? m.firstBaseUpdate = l : h.next = l, m.lastBaseUpdate = k));
        }
        if (null !== f) {
          var q = e.baseState;
          g = 0;
          m = l = k = null;
          h = f;
          do {
            var r = h.lane, y = h.eventTime;
            if ((d & r) === r) {
              null !== m && (m = m.next = {
                eventTime: y,
                lane: 0,
                tag: h.tag,
                payload: h.payload,
                callback: h.callback,
                next: null
              });
              a: {
                var n = a, t = h;
                r = b;
                y = c;
                switch (t.tag) {
                  case 1:
                    n = t.payload;
                    if ("function" === typeof n) {
                      q = n.call(y, q, r);
                      break a;
                    }
                    q = n;
                    break a;
                  case 3:
                    n.flags = n.flags & -65537 | 128;
                  case 0:
                    n = t.payload;
                    r = "function" === typeof n ? n.call(y, q, r) : n;
                    if (null === r || void 0 === r) break a;
                    q = A({}, q, r);
                    break a;
                  case 2:
                    jh = true;
                }
              }
              null !== h.callback && 0 !== h.lane && (a.flags |= 64, r = e.effects, null === r ? e.effects = [h] : r.push(h));
            } else y = { eventTime: y, lane: r, tag: h.tag, payload: h.payload, callback: h.callback, next: null }, null === m ? (l = m = y, k = q) : m = m.next = y, g |= r;
            h = h.next;
            if (null === h) if (h = e.shared.pending, null === h) break;
            else r = h, h = r.next, r.next = null, e.lastBaseUpdate = r, e.shared.pending = null;
          } while (1);
          null === m && (k = q);
          e.baseState = k;
          e.firstBaseUpdate = l;
          e.lastBaseUpdate = m;
          b = e.shared.interleaved;
          if (null !== b) {
            e = b;
            do
              g |= e.lane, e = e.next;
            while (e !== b);
          } else null === f && (e.shared.lanes = 0);
          rh |= g;
          a.lanes = g;
          a.memoizedState = q;
        }
      }
      __name(qh, "qh");
      function sh(a, b, c) {
        a = b.effects;
        b.effects = null;
        if (null !== a) for (b = 0; b < a.length; b++) {
          var d = a[b], e = d.callback;
          if (null !== e) {
            d.callback = null;
            d = c;
            if ("function" !== typeof e) throw Error(p(191, e));
            e.call(d);
          }
        }
      }
      __name(sh, "sh");
      var th = {};
      var uh = Uf(th);
      var vh = Uf(th);
      var wh = Uf(th);
      function xh(a) {
        if (a === th) throw Error(p(174));
        return a;
      }
      __name(xh, "xh");
      function yh(a, b) {
        G(wh, b);
        G(vh, a);
        G(uh, th);
        a = b.nodeType;
        switch (a) {
          case 9:
          case 11:
            b = (b = b.documentElement) ? b.namespaceURI : lb(null, "");
            break;
          default:
            a = 8 === a ? b.parentNode : b, b = a.namespaceURI || null, a = a.tagName, b = lb(b, a);
        }
        E(uh);
        G(uh, b);
      }
      __name(yh, "yh");
      function zh() {
        E(uh);
        E(vh);
        E(wh);
      }
      __name(zh, "zh");
      function Ah(a) {
        xh(wh.current);
        var b = xh(uh.current);
        var c = lb(b, a.type);
        b !== c && (G(vh, a), G(uh, c));
      }
      __name(Ah, "Ah");
      function Bh(a) {
        vh.current === a && (E(uh), E(vh));
      }
      __name(Bh, "Bh");
      var L = Uf(0);
      function Ch(a) {
        for (var b = a; null !== b; ) {
          if (13 === b.tag) {
            var c = b.memoizedState;
            if (null !== c && (c = c.dehydrated, null === c || "$?" === c.data || "$!" === c.data)) return b;
          } else if (19 === b.tag && void 0 !== b.memoizedProps.revealOrder) {
            if (0 !== (b.flags & 128)) return b;
          } else if (null !== b.child) {
            b.child.return = b;
            b = b.child;
            continue;
          }
          if (b === a) break;
          for (; null === b.sibling; ) {
            if (null === b.return || b.return === a) return null;
            b = b.return;
          }
          b.sibling.return = b.return;
          b = b.sibling;
        }
        return null;
      }
      __name(Ch, "Ch");
      var Dh = [];
      function Eh() {
        for (var a = 0; a < Dh.length; a++) Dh[a]._workInProgressVersionPrimary = null;
        Dh.length = 0;
      }
      __name(Eh, "Eh");
      var Fh = ua.ReactCurrentDispatcher;
      var Gh = ua.ReactCurrentBatchConfig;
      var Hh = 0;
      var M = null;
      var N = null;
      var O = null;
      var Ih = false;
      var Jh = false;
      var Kh = 0;
      var Lh = 0;
      function P() {
        throw Error(p(321));
      }
      __name(P, "P");
      function Mh(a, b) {
        if (null === b) return false;
        for (var c = 0; c < b.length && c < a.length; c++) if (!He(a[c], b[c])) return false;
        return true;
      }
      __name(Mh, "Mh");
      function Nh(a, b, c, d, e, f) {
        Hh = f;
        M = b;
        b.memoizedState = null;
        b.updateQueue = null;
        b.lanes = 0;
        Fh.current = null === a || null === a.memoizedState ? Oh : Ph;
        a = c(d, e);
        if (Jh) {
          f = 0;
          do {
            Jh = false;
            Kh = 0;
            if (25 <= f) throw Error(p(301));
            f += 1;
            O = N = null;
            b.updateQueue = null;
            Fh.current = Qh;
            a = c(d, e);
          } while (Jh);
        }
        Fh.current = Rh;
        b = null !== N && null !== N.next;
        Hh = 0;
        O = N = M = null;
        Ih = false;
        if (b) throw Error(p(300));
        return a;
      }
      __name(Nh, "Nh");
      function Sh() {
        var a = 0 !== Kh;
        Kh = 0;
        return a;
      }
      __name(Sh, "Sh");
      function Th() {
        var a = { memoizedState: null, baseState: null, baseQueue: null, queue: null, next: null };
        null === O ? M.memoizedState = O = a : O = O.next = a;
        return O;
      }
      __name(Th, "Th");
      function Uh() {
        if (null === N) {
          var a = M.alternate;
          a = null !== a ? a.memoizedState : null;
        } else a = N.next;
        var b = null === O ? M.memoizedState : O.next;
        if (null !== b) O = b, N = a;
        else {
          if (null === a) throw Error(p(310));
          N = a;
          a = { memoizedState: N.memoizedState, baseState: N.baseState, baseQueue: N.baseQueue, queue: N.queue, next: null };
          null === O ? M.memoizedState = O = a : O = O.next = a;
        }
        return O;
      }
      __name(Uh, "Uh");
      function Vh(a, b) {
        return "function" === typeof b ? b(a) : b;
      }
      __name(Vh, "Vh");
      function Wh(a) {
        var b = Uh(), c = b.queue;
        if (null === c) throw Error(p(311));
        c.lastRenderedReducer = a;
        var d = N, e = d.baseQueue, f = c.pending;
        if (null !== f) {
          if (null !== e) {
            var g = e.next;
            e.next = f.next;
            f.next = g;
          }
          d.baseQueue = e = f;
          c.pending = null;
        }
        if (null !== e) {
          f = e.next;
          d = d.baseState;
          var h = g = null, k = null, l = f;
          do {
            var m = l.lane;
            if ((Hh & m) === m) null !== k && (k = k.next = { lane: 0, action: l.action, hasEagerState: l.hasEagerState, eagerState: l.eagerState, next: null }), d = l.hasEagerState ? l.eagerState : a(d, l.action);
            else {
              var q = {
                lane: m,
                action: l.action,
                hasEagerState: l.hasEagerState,
                eagerState: l.eagerState,
                next: null
              };
              null === k ? (h = k = q, g = d) : k = k.next = q;
              M.lanes |= m;
              rh |= m;
            }
            l = l.next;
          } while (null !== l && l !== f);
          null === k ? g = d : k.next = h;
          He(d, b.memoizedState) || (dh = true);
          b.memoizedState = d;
          b.baseState = g;
          b.baseQueue = k;
          c.lastRenderedState = d;
        }
        a = c.interleaved;
        if (null !== a) {
          e = a;
          do
            f = e.lane, M.lanes |= f, rh |= f, e = e.next;
          while (e !== a);
        } else null === e && (c.lanes = 0);
        return [b.memoizedState, c.dispatch];
      }
      __name(Wh, "Wh");
      function Xh(a) {
        var b = Uh(), c = b.queue;
        if (null === c) throw Error(p(311));
        c.lastRenderedReducer = a;
        var d = c.dispatch, e = c.pending, f = b.memoizedState;
        if (null !== e) {
          c.pending = null;
          var g = e = e.next;
          do
            f = a(f, g.action), g = g.next;
          while (g !== e);
          He(f, b.memoizedState) || (dh = true);
          b.memoizedState = f;
          null === b.baseQueue && (b.baseState = f);
          c.lastRenderedState = f;
        }
        return [f, d];
      }
      __name(Xh, "Xh");
      function Yh() {
      }
      __name(Yh, "Yh");
      function Zh(a, b) {
        var c = M, d = Uh(), e = b(), f = !He(d.memoizedState, e);
        f && (d.memoizedState = e, dh = true);
        d = d.queue;
        $h(ai.bind(null, c, d, a), [a]);
        if (d.getSnapshot !== b || f || null !== O && O.memoizedState.tag & 1) {
          c.flags |= 2048;
          bi(9, ci.bind(null, c, d, e, b), void 0, null);
          if (null === Q) throw Error(p(349));
          0 !== (Hh & 30) || di(c, b, e);
        }
        return e;
      }
      __name(Zh, "Zh");
      function di(a, b, c) {
        a.flags |= 16384;
        a = { getSnapshot: b, value: c };
        b = M.updateQueue;
        null === b ? (b = { lastEffect: null, stores: null }, M.updateQueue = b, b.stores = [a]) : (c = b.stores, null === c ? b.stores = [a] : c.push(a));
      }
      __name(di, "di");
      function ci(a, b, c, d) {
        b.value = c;
        b.getSnapshot = d;
        ei(b) && fi(a);
      }
      __name(ci, "ci");
      function ai(a, b, c) {
        return c(function() {
          ei(b) && fi(a);
        });
      }
      __name(ai, "ai");
      function ei(a) {
        var b = a.getSnapshot;
        a = a.value;
        try {
          var c = b();
          return !He(a, c);
        } catch (d) {
          return true;
        }
      }
      __name(ei, "ei");
      function fi(a) {
        var b = ih(a, 1);
        null !== b && gi(b, a, 1, -1);
      }
      __name(fi, "fi");
      function hi(a) {
        var b = Th();
        "function" === typeof a && (a = a());
        b.memoizedState = b.baseState = a;
        a = { pending: null, interleaved: null, lanes: 0, dispatch: null, lastRenderedReducer: Vh, lastRenderedState: a };
        b.queue = a;
        a = a.dispatch = ii.bind(null, M, a);
        return [b.memoizedState, a];
      }
      __name(hi, "hi");
      function bi(a, b, c, d) {
        a = { tag: a, create: b, destroy: c, deps: d, next: null };
        b = M.updateQueue;
        null === b ? (b = { lastEffect: null, stores: null }, M.updateQueue = b, b.lastEffect = a.next = a) : (c = b.lastEffect, null === c ? b.lastEffect = a.next = a : (d = c.next, c.next = a, a.next = d, b.lastEffect = a));
        return a;
      }
      __name(bi, "bi");
      function ji() {
        return Uh().memoizedState;
      }
      __name(ji, "ji");
      function ki(a, b, c, d) {
        var e = Th();
        M.flags |= a;
        e.memoizedState = bi(1 | b, c, void 0, void 0 === d ? null : d);
      }
      __name(ki, "ki");
      function li(a, b, c, d) {
        var e = Uh();
        d = void 0 === d ? null : d;
        var f = void 0;
        if (null !== N) {
          var g = N.memoizedState;
          f = g.destroy;
          if (null !== d && Mh(d, g.deps)) {
            e.memoizedState = bi(b, c, f, d);
            return;
          }
        }
        M.flags |= a;
        e.memoizedState = bi(1 | b, c, f, d);
      }
      __name(li, "li");
      function mi(a, b) {
        return ki(8390656, 8, a, b);
      }
      __name(mi, "mi");
      function $h(a, b) {
        return li(2048, 8, a, b);
      }
      __name($h, "$h");
      function ni(a, b) {
        return li(4, 2, a, b);
      }
      __name(ni, "ni");
      function oi(a, b) {
        return li(4, 4, a, b);
      }
      __name(oi, "oi");
      function pi(a, b) {
        if ("function" === typeof b) return a = a(), b(a), function() {
          b(null);
        };
        if (null !== b && void 0 !== b) return a = a(), b.current = a, function() {
          b.current = null;
        };
      }
      __name(pi, "pi");
      function qi(a, b, c) {
        c = null !== c && void 0 !== c ? c.concat([a]) : null;
        return li(4, 4, pi.bind(null, b, a), c);
      }
      __name(qi, "qi");
      function ri() {
      }
      __name(ri, "ri");
      function si(a, b) {
        var c = Uh();
        b = void 0 === b ? null : b;
        var d = c.memoizedState;
        if (null !== d && null !== b && Mh(b, d[1])) return d[0];
        c.memoizedState = [a, b];
        return a;
      }
      __name(si, "si");
      function ti(a, b) {
        var c = Uh();
        b = void 0 === b ? null : b;
        var d = c.memoizedState;
        if (null !== d && null !== b && Mh(b, d[1])) return d[0];
        a = a();
        c.memoizedState = [a, b];
        return a;
      }
      __name(ti, "ti");
      function ui(a, b, c) {
        if (0 === (Hh & 21)) return a.baseState && (a.baseState = false, dh = true), a.memoizedState = c;
        He(c, b) || (c = yc(), M.lanes |= c, rh |= c, a.baseState = true);
        return b;
      }
      __name(ui, "ui");
      function vi(a, b) {
        var c = C;
        C = 0 !== c && 4 > c ? c : 4;
        a(true);
        var d = Gh.transition;
        Gh.transition = {};
        try {
          a(false), b();
        } finally {
          C = c, Gh.transition = d;
        }
      }
      __name(vi, "vi");
      function wi() {
        return Uh().memoizedState;
      }
      __name(wi, "wi");
      function xi(a, b, c) {
        var d = yi(a);
        c = { lane: d, action: c, hasEagerState: false, eagerState: null, next: null };
        if (zi(a)) Ai(b, c);
        else if (c = hh(a, b, c, d), null !== c) {
          var e = R();
          gi(c, a, d, e);
          Bi(c, b, d);
        }
      }
      __name(xi, "xi");
      function ii(a, b, c) {
        var d = yi(a), e = { lane: d, action: c, hasEagerState: false, eagerState: null, next: null };
        if (zi(a)) Ai(b, e);
        else {
          var f = a.alternate;
          if (0 === a.lanes && (null === f || 0 === f.lanes) && (f = b.lastRenderedReducer, null !== f)) try {
            var g = b.lastRenderedState, h = f(g, c);
            e.hasEagerState = true;
            e.eagerState = h;
            if (He(h, g)) {
              var k = b.interleaved;
              null === k ? (e.next = e, gh(b)) : (e.next = k.next, k.next = e);
              b.interleaved = e;
              return;
            }
          } catch (l) {
          } finally {
          }
          c = hh(a, b, e, d);
          null !== c && (e = R(), gi(c, a, d, e), Bi(c, b, d));
        }
      }
      __name(ii, "ii");
      function zi(a) {
        var b = a.alternate;
        return a === M || null !== b && b === M;
      }
      __name(zi, "zi");
      function Ai(a, b) {
        Jh = Ih = true;
        var c = a.pending;
        null === c ? b.next = b : (b.next = c.next, c.next = b);
        a.pending = b;
      }
      __name(Ai, "Ai");
      function Bi(a, b, c) {
        if (0 !== (c & 4194240)) {
          var d = b.lanes;
          d &= a.pendingLanes;
          c |= d;
          b.lanes = c;
          Cc(a, c);
        }
      }
      __name(Bi, "Bi");
      var Rh = { readContext: eh, useCallback: P, useContext: P, useEffect: P, useImperativeHandle: P, useInsertionEffect: P, useLayoutEffect: P, useMemo: P, useReducer: P, useRef: P, useState: P, useDebugValue: P, useDeferredValue: P, useTransition: P, useMutableSource: P, useSyncExternalStore: P, useId: P, unstable_isNewReconciler: false };
      var Oh = { readContext: eh, useCallback: /* @__PURE__ */ __name(function(a, b) {
        Th().memoizedState = [a, void 0 === b ? null : b];
        return a;
      }, "useCallback"), useContext: eh, useEffect: mi, useImperativeHandle: /* @__PURE__ */ __name(function(a, b, c) {
        c = null !== c && void 0 !== c ? c.concat([a]) : null;
        return ki(
          4194308,
          4,
          pi.bind(null, b, a),
          c
        );
      }, "useImperativeHandle"), useLayoutEffect: /* @__PURE__ */ __name(function(a, b) {
        return ki(4194308, 4, a, b);
      }, "useLayoutEffect"), useInsertionEffect: /* @__PURE__ */ __name(function(a, b) {
        return ki(4, 2, a, b);
      }, "useInsertionEffect"), useMemo: /* @__PURE__ */ __name(function(a, b) {
        var c = Th();
        b = void 0 === b ? null : b;
        a = a();
        c.memoizedState = [a, b];
        return a;
      }, "useMemo"), useReducer: /* @__PURE__ */ __name(function(a, b, c) {
        var d = Th();
        b = void 0 !== c ? c(b) : b;
        d.memoizedState = d.baseState = b;
        a = { pending: null, interleaved: null, lanes: 0, dispatch: null, lastRenderedReducer: a, lastRenderedState: b };
        d.queue = a;
        a = a.dispatch = xi.bind(null, M, a);
        return [d.memoizedState, a];
      }, "useReducer"), useRef: /* @__PURE__ */ __name(function(a) {
        var b = Th();
        a = { current: a };
        return b.memoizedState = a;
      }, "useRef"), useState: hi, useDebugValue: ri, useDeferredValue: /* @__PURE__ */ __name(function(a) {
        return Th().memoizedState = a;
      }, "useDeferredValue"), useTransition: /* @__PURE__ */ __name(function() {
        var a = hi(false), b = a[0];
        a = vi.bind(null, a[1]);
        Th().memoizedState = a;
        return [b, a];
      }, "useTransition"), useMutableSource: /* @__PURE__ */ __name(function() {
      }, "useMutableSource"), useSyncExternalStore: /* @__PURE__ */ __name(function(a, b, c) {
        var d = M, e = Th();
        if (I) {
          if (void 0 === c) throw Error(p(407));
          c = c();
        } else {
          c = b();
          if (null === Q) throw Error(p(349));
          0 !== (Hh & 30) || di(d, b, c);
        }
        e.memoizedState = c;
        var f = { value: c, getSnapshot: b };
        e.queue = f;
        mi(ai.bind(
          null,
          d,
          f,
          a
        ), [a]);
        d.flags |= 2048;
        bi(9, ci.bind(null, d, f, c, b), void 0, null);
        return c;
      }, "useSyncExternalStore"), useId: /* @__PURE__ */ __name(function() {
        var a = Th(), b = Q.identifierPrefix;
        if (I) {
          var c = sg;
          var d = rg;
          c = (d & ~(1 << 32 - oc(d) - 1)).toString(32) + c;
          b = ":" + b + "R" + c;
          c = Kh++;
          0 < c && (b += "H" + c.toString(32));
          b += ":";
        } else c = Lh++, b = ":" + b + "r" + c.toString(32) + ":";
        return a.memoizedState = b;
      }, "useId"), unstable_isNewReconciler: false };
      var Ph = {
        readContext: eh,
        useCallback: si,
        useContext: eh,
        useEffect: $h,
        useImperativeHandle: qi,
        useInsertionEffect: ni,
        useLayoutEffect: oi,
        useMemo: ti,
        useReducer: Wh,
        useRef: ji,
        useState: /* @__PURE__ */ __name(function() {
          return Wh(Vh);
        }, "useState"),
        useDebugValue: ri,
        useDeferredValue: /* @__PURE__ */ __name(function(a) {
          var b = Uh();
          return ui(b, N.memoizedState, a);
        }, "useDeferredValue"),
        useTransition: /* @__PURE__ */ __name(function() {
          var a = Wh(Vh)[0], b = Uh().memoizedState;
          return [a, b];
        }, "useTransition"),
        useMutableSource: Yh,
        useSyncExternalStore: Zh,
        useId: wi,
        unstable_isNewReconciler: false
      };
      var Qh = { readContext: eh, useCallback: si, useContext: eh, useEffect: $h, useImperativeHandle: qi, useInsertionEffect: ni, useLayoutEffect: oi, useMemo: ti, useReducer: Xh, useRef: ji, useState: /* @__PURE__ */ __name(function() {
        return Xh(Vh);
      }, "useState"), useDebugValue: ri, useDeferredValue: /* @__PURE__ */ __name(function(a) {
        var b = Uh();
        return null === N ? b.memoizedState = a : ui(b, N.memoizedState, a);
      }, "useDeferredValue"), useTransition: /* @__PURE__ */ __name(function() {
        var a = Xh(Vh)[0], b = Uh().memoizedState;
        return [a, b];
      }, "useTransition"), useMutableSource: Yh, useSyncExternalStore: Zh, useId: wi, unstable_isNewReconciler: false };
      function Ci(a, b) {
        if (a && a.defaultProps) {
          b = A({}, b);
          a = a.defaultProps;
          for (var c in a) void 0 === b[c] && (b[c] = a[c]);
          return b;
        }
        return b;
      }
      __name(Ci, "Ci");
      function Di(a, b, c, d) {
        b = a.memoizedState;
        c = c(d, b);
        c = null === c || void 0 === c ? b : A({}, b, c);
        a.memoizedState = c;
        0 === a.lanes && (a.updateQueue.baseState = c);
      }
      __name(Di, "Di");
      var Ei = { isMounted: /* @__PURE__ */ __name(function(a) {
        return (a = a._reactInternals) ? Vb(a) === a : false;
      }, "isMounted"), enqueueSetState: /* @__PURE__ */ __name(function(a, b, c) {
        a = a._reactInternals;
        var d = R(), e = yi(a), f = mh(d, e);
        f.payload = b;
        void 0 !== c && null !== c && (f.callback = c);
        b = nh(a, f, e);
        null !== b && (gi(b, a, e, d), oh(b, a, e));
      }, "enqueueSetState"), enqueueReplaceState: /* @__PURE__ */ __name(function(a, b, c) {
        a = a._reactInternals;
        var d = R(), e = yi(a), f = mh(d, e);
        f.tag = 1;
        f.payload = b;
        void 0 !== c && null !== c && (f.callback = c);
        b = nh(a, f, e);
        null !== b && (gi(b, a, e, d), oh(b, a, e));
      }, "enqueueReplaceState"), enqueueForceUpdate: /* @__PURE__ */ __name(function(a, b) {
        a = a._reactInternals;
        var c = R(), d = yi(a), e = mh(c, d);
        e.tag = 2;
        void 0 !== b && null !== b && (e.callback = b);
        b = nh(a, e, d);
        null !== b && (gi(b, a, d, c), oh(b, a, d));
      }, "enqueueForceUpdate") };
      function Fi(a, b, c, d, e, f, g) {
        a = a.stateNode;
        return "function" === typeof a.shouldComponentUpdate ? a.shouldComponentUpdate(d, f, g) : b.prototype && b.prototype.isPureReactComponent ? !Ie(c, d) || !Ie(e, f) : true;
      }
      __name(Fi, "Fi");
      function Gi(a, b, c) {
        var d = false, e = Vf;
        var f = b.contextType;
        "object" === typeof f && null !== f ? f = eh(f) : (e = Zf(b) ? Xf : H.current, d = b.contextTypes, f = (d = null !== d && void 0 !== d) ? Yf(a, e) : Vf);
        b = new b(c, f);
        a.memoizedState = null !== b.state && void 0 !== b.state ? b.state : null;
        b.updater = Ei;
        a.stateNode = b;
        b._reactInternals = a;
        d && (a = a.stateNode, a.__reactInternalMemoizedUnmaskedChildContext = e, a.__reactInternalMemoizedMaskedChildContext = f);
        return b;
      }
      __name(Gi, "Gi");
      function Hi(a, b, c, d) {
        a = b.state;
        "function" === typeof b.componentWillReceiveProps && b.componentWillReceiveProps(c, d);
        "function" === typeof b.UNSAFE_componentWillReceiveProps && b.UNSAFE_componentWillReceiveProps(c, d);
        b.state !== a && Ei.enqueueReplaceState(b, b.state, null);
      }
      __name(Hi, "Hi");
      function Ii(a, b, c, d) {
        var e = a.stateNode;
        e.props = c;
        e.state = a.memoizedState;
        e.refs = {};
        kh(a);
        var f = b.contextType;
        "object" === typeof f && null !== f ? e.context = eh(f) : (f = Zf(b) ? Xf : H.current, e.context = Yf(a, f));
        e.state = a.memoizedState;
        f = b.getDerivedStateFromProps;
        "function" === typeof f && (Di(a, b, f, c), e.state = a.memoizedState);
        "function" === typeof b.getDerivedStateFromProps || "function" === typeof e.getSnapshotBeforeUpdate || "function" !== typeof e.UNSAFE_componentWillMount && "function" !== typeof e.componentWillMount || (b = e.state, "function" === typeof e.componentWillMount && e.componentWillMount(), "function" === typeof e.UNSAFE_componentWillMount && e.UNSAFE_componentWillMount(), b !== e.state && Ei.enqueueReplaceState(e, e.state, null), qh(a, c, e, d), e.state = a.memoizedState);
        "function" === typeof e.componentDidMount && (a.flags |= 4194308);
      }
      __name(Ii, "Ii");
      function Ji(a, b) {
        try {
          var c = "", d = b;
          do
            c += Pa(d), d = d.return;
          while (d);
          var e = c;
        } catch (f) {
          e = "\nError generating stack: " + f.message + "\n" + f.stack;
        }
        return { value: a, source: b, stack: e, digest: null };
      }
      __name(Ji, "Ji");
      function Ki(a, b, c) {
        return { value: a, source: null, stack: null != c ? c : null, digest: null != b ? b : null };
      }
      __name(Ki, "Ki");
      function Li(a, b) {
        try {
          console.error(b.value);
        } catch (c) {
          setTimeout(function() {
            throw c;
          });
        }
      }
      __name(Li, "Li");
      var Mi = "function" === typeof WeakMap ? WeakMap : Map;
      function Ni(a, b, c) {
        c = mh(-1, c);
        c.tag = 3;
        c.payload = { element: null };
        var d = b.value;
        c.callback = function() {
          Oi || (Oi = true, Pi = d);
          Li(a, b);
        };
        return c;
      }
      __name(Ni, "Ni");
      function Qi(a, b, c) {
        c = mh(-1, c);
        c.tag = 3;
        var d = a.type.getDerivedStateFromError;
        if ("function" === typeof d) {
          var e = b.value;
          c.payload = function() {
            return d(e);
          };
          c.callback = function() {
            Li(a, b);
          };
        }
        var f = a.stateNode;
        null !== f && "function" === typeof f.componentDidCatch && (c.callback = function() {
          Li(a, b);
          "function" !== typeof d && (null === Ri ? Ri = /* @__PURE__ */ new Set([this]) : Ri.add(this));
          var c2 = b.stack;
          this.componentDidCatch(b.value, { componentStack: null !== c2 ? c2 : "" });
        });
        return c;
      }
      __name(Qi, "Qi");
      function Si(a, b, c) {
        var d = a.pingCache;
        if (null === d) {
          d = a.pingCache = new Mi();
          var e = /* @__PURE__ */ new Set();
          d.set(b, e);
        } else e = d.get(b), void 0 === e && (e = /* @__PURE__ */ new Set(), d.set(b, e));
        e.has(c) || (e.add(c), a = Ti.bind(null, a, b, c), b.then(a, a));
      }
      __name(Si, "Si");
      function Ui(a) {
        do {
          var b;
          if (b = 13 === a.tag) b = a.memoizedState, b = null !== b ? null !== b.dehydrated ? true : false : true;
          if (b) return a;
          a = a.return;
        } while (null !== a);
        return null;
      }
      __name(Ui, "Ui");
      function Vi(a, b, c, d, e) {
        if (0 === (a.mode & 1)) return a === b ? a.flags |= 65536 : (a.flags |= 128, c.flags |= 131072, c.flags &= -52805, 1 === c.tag && (null === c.alternate ? c.tag = 17 : (b = mh(-1, 1), b.tag = 2, nh(c, b, 1))), c.lanes |= 1), a;
        a.flags |= 65536;
        a.lanes = e;
        return a;
      }
      __name(Vi, "Vi");
      var Wi = ua.ReactCurrentOwner;
      var dh = false;
      function Xi(a, b, c, d) {
        b.child = null === a ? Vg(b, null, c, d) : Ug(b, a.child, c, d);
      }
      __name(Xi, "Xi");
      function Yi(a, b, c, d, e) {
        c = c.render;
        var f = b.ref;
        ch(b, e);
        d = Nh(a, b, c, d, f, e);
        c = Sh();
        if (null !== a && !dh) return b.updateQueue = a.updateQueue, b.flags &= -2053, a.lanes &= ~e, Zi(a, b, e);
        I && c && vg(b);
        b.flags |= 1;
        Xi(a, b, d, e);
        return b.child;
      }
      __name(Yi, "Yi");
      function $i(a, b, c, d, e) {
        if (null === a) {
          var f = c.type;
          if ("function" === typeof f && !aj(f) && void 0 === f.defaultProps && null === c.compare && void 0 === c.defaultProps) return b.tag = 15, b.type = f, bj(a, b, f, d, e);
          a = Rg(c.type, null, d, b, b.mode, e);
          a.ref = b.ref;
          a.return = b;
          return b.child = a;
        }
        f = a.child;
        if (0 === (a.lanes & e)) {
          var g = f.memoizedProps;
          c = c.compare;
          c = null !== c ? c : Ie;
          if (c(g, d) && a.ref === b.ref) return Zi(a, b, e);
        }
        b.flags |= 1;
        a = Pg(f, d);
        a.ref = b.ref;
        a.return = b;
        return b.child = a;
      }
      __name($i, "$i");
      function bj(a, b, c, d, e) {
        if (null !== a) {
          var f = a.memoizedProps;
          if (Ie(f, d) && a.ref === b.ref) if (dh = false, b.pendingProps = d = f, 0 !== (a.lanes & e)) 0 !== (a.flags & 131072) && (dh = true);
          else return b.lanes = a.lanes, Zi(a, b, e);
        }
        return cj(a, b, c, d, e);
      }
      __name(bj, "bj");
      function dj(a, b, c) {
        var d = b.pendingProps, e = d.children, f = null !== a ? a.memoizedState : null;
        if ("hidden" === d.mode) if (0 === (b.mode & 1)) b.memoizedState = { baseLanes: 0, cachePool: null, transitions: null }, G(ej, fj), fj |= c;
        else {
          if (0 === (c & 1073741824)) return a = null !== f ? f.baseLanes | c : c, b.lanes = b.childLanes = 1073741824, b.memoizedState = { baseLanes: a, cachePool: null, transitions: null }, b.updateQueue = null, G(ej, fj), fj |= a, null;
          b.memoizedState = { baseLanes: 0, cachePool: null, transitions: null };
          d = null !== f ? f.baseLanes : c;
          G(ej, fj);
          fj |= d;
        }
        else null !== f ? (d = f.baseLanes | c, b.memoizedState = null) : d = c, G(ej, fj), fj |= d;
        Xi(a, b, e, c);
        return b.child;
      }
      __name(dj, "dj");
      function gj(a, b) {
        var c = b.ref;
        if (null === a && null !== c || null !== a && a.ref !== c) b.flags |= 512, b.flags |= 2097152;
      }
      __name(gj, "gj");
      function cj(a, b, c, d, e) {
        var f = Zf(c) ? Xf : H.current;
        f = Yf(b, f);
        ch(b, e);
        c = Nh(a, b, c, d, f, e);
        d = Sh();
        if (null !== a && !dh) return b.updateQueue = a.updateQueue, b.flags &= -2053, a.lanes &= ~e, Zi(a, b, e);
        I && d && vg(b);
        b.flags |= 1;
        Xi(a, b, c, e);
        return b.child;
      }
      __name(cj, "cj");
      function hj(a, b, c, d, e) {
        if (Zf(c)) {
          var f = true;
          cg(b);
        } else f = false;
        ch(b, e);
        if (null === b.stateNode) ij(a, b), Gi(b, c, d), Ii(b, c, d, e), d = true;
        else if (null === a) {
          var g = b.stateNode, h = b.memoizedProps;
          g.props = h;
          var k = g.context, l = c.contextType;
          "object" === typeof l && null !== l ? l = eh(l) : (l = Zf(c) ? Xf : H.current, l = Yf(b, l));
          var m = c.getDerivedStateFromProps, q = "function" === typeof m || "function" === typeof g.getSnapshotBeforeUpdate;
          q || "function" !== typeof g.UNSAFE_componentWillReceiveProps && "function" !== typeof g.componentWillReceiveProps || (h !== d || k !== l) && Hi(b, g, d, l);
          jh = false;
          var r = b.memoizedState;
          g.state = r;
          qh(b, d, g, e);
          k = b.memoizedState;
          h !== d || r !== k || Wf.current || jh ? ("function" === typeof m && (Di(b, c, m, d), k = b.memoizedState), (h = jh || Fi(b, c, h, d, r, k, l)) ? (q || "function" !== typeof g.UNSAFE_componentWillMount && "function" !== typeof g.componentWillMount || ("function" === typeof g.componentWillMount && g.componentWillMount(), "function" === typeof g.UNSAFE_componentWillMount && g.UNSAFE_componentWillMount()), "function" === typeof g.componentDidMount && (b.flags |= 4194308)) : ("function" === typeof g.componentDidMount && (b.flags |= 4194308), b.memoizedProps = d, b.memoizedState = k), g.props = d, g.state = k, g.context = l, d = h) : ("function" === typeof g.componentDidMount && (b.flags |= 4194308), d = false);
        } else {
          g = b.stateNode;
          lh(a, b);
          h = b.memoizedProps;
          l = b.type === b.elementType ? h : Ci(b.type, h);
          g.props = l;
          q = b.pendingProps;
          r = g.context;
          k = c.contextType;
          "object" === typeof k && null !== k ? k = eh(k) : (k = Zf(c) ? Xf : H.current, k = Yf(b, k));
          var y = c.getDerivedStateFromProps;
          (m = "function" === typeof y || "function" === typeof g.getSnapshotBeforeUpdate) || "function" !== typeof g.UNSAFE_componentWillReceiveProps && "function" !== typeof g.componentWillReceiveProps || (h !== q || r !== k) && Hi(b, g, d, k);
          jh = false;
          r = b.memoizedState;
          g.state = r;
          qh(b, d, g, e);
          var n = b.memoizedState;
          h !== q || r !== n || Wf.current || jh ? ("function" === typeof y && (Di(b, c, y, d), n = b.memoizedState), (l = jh || Fi(b, c, l, d, r, n, k) || false) ? (m || "function" !== typeof g.UNSAFE_componentWillUpdate && "function" !== typeof g.componentWillUpdate || ("function" === typeof g.componentWillUpdate && g.componentWillUpdate(d, n, k), "function" === typeof g.UNSAFE_componentWillUpdate && g.UNSAFE_componentWillUpdate(d, n, k)), "function" === typeof g.componentDidUpdate && (b.flags |= 4), "function" === typeof g.getSnapshotBeforeUpdate && (b.flags |= 1024)) : ("function" !== typeof g.componentDidUpdate || h === a.memoizedProps && r === a.memoizedState || (b.flags |= 4), "function" !== typeof g.getSnapshotBeforeUpdate || h === a.memoizedProps && r === a.memoizedState || (b.flags |= 1024), b.memoizedProps = d, b.memoizedState = n), g.props = d, g.state = n, g.context = k, d = l) : ("function" !== typeof g.componentDidUpdate || h === a.memoizedProps && r === a.memoizedState || (b.flags |= 4), "function" !== typeof g.getSnapshotBeforeUpdate || h === a.memoizedProps && r === a.memoizedState || (b.flags |= 1024), d = false);
        }
        return jj(a, b, c, d, f, e);
      }
      __name(hj, "hj");
      function jj(a, b, c, d, e, f) {
        gj(a, b);
        var g = 0 !== (b.flags & 128);
        if (!d && !g) return e && dg(b, c, false), Zi(a, b, f);
        d = b.stateNode;
        Wi.current = b;
        var h = g && "function" !== typeof c.getDerivedStateFromError ? null : d.render();
        b.flags |= 1;
        null !== a && g ? (b.child = Ug(b, a.child, null, f), b.child = Ug(b, null, h, f)) : Xi(a, b, h, f);
        b.memoizedState = d.state;
        e && dg(b, c, true);
        return b.child;
      }
      __name(jj, "jj");
      function kj(a) {
        var b = a.stateNode;
        b.pendingContext ? ag(a, b.pendingContext, b.pendingContext !== b.context) : b.context && ag(a, b.context, false);
        yh(a, b.containerInfo);
      }
      __name(kj, "kj");
      function lj(a, b, c, d, e) {
        Ig();
        Jg(e);
        b.flags |= 256;
        Xi(a, b, c, d);
        return b.child;
      }
      __name(lj, "lj");
      var mj = { dehydrated: null, treeContext: null, retryLane: 0 };
      function nj(a) {
        return { baseLanes: a, cachePool: null, transitions: null };
      }
      __name(nj, "nj");
      function oj(a, b, c) {
        var d = b.pendingProps, e = L.current, f = false, g = 0 !== (b.flags & 128), h;
        (h = g) || (h = null !== a && null === a.memoizedState ? false : 0 !== (e & 2));
        if (h) f = true, b.flags &= -129;
        else if (null === a || null !== a.memoizedState) e |= 1;
        G(L, e & 1);
        if (null === a) {
          Eg(b);
          a = b.memoizedState;
          if (null !== a && (a = a.dehydrated, null !== a)) return 0 === (b.mode & 1) ? b.lanes = 1 : "$!" === a.data ? b.lanes = 8 : b.lanes = 1073741824, null;
          g = d.children;
          a = d.fallback;
          return f ? (d = b.mode, f = b.child, g = { mode: "hidden", children: g }, 0 === (d & 1) && null !== f ? (f.childLanes = 0, f.pendingProps = g) : f = pj(g, d, 0, null), a = Tg(a, d, c, null), f.return = b, a.return = b, f.sibling = a, b.child = f, b.child.memoizedState = nj(c), b.memoizedState = mj, a) : qj(b, g);
        }
        e = a.memoizedState;
        if (null !== e && (h = e.dehydrated, null !== h)) return rj(a, b, g, d, h, e, c);
        if (f) {
          f = d.fallback;
          g = b.mode;
          e = a.child;
          h = e.sibling;
          var k = { mode: "hidden", children: d.children };
          0 === (g & 1) && b.child !== e ? (d = b.child, d.childLanes = 0, d.pendingProps = k, b.deletions = null) : (d = Pg(e, k), d.subtreeFlags = e.subtreeFlags & 14680064);
          null !== h ? f = Pg(h, f) : (f = Tg(f, g, c, null), f.flags |= 2);
          f.return = b;
          d.return = b;
          d.sibling = f;
          b.child = d;
          d = f;
          f = b.child;
          g = a.child.memoizedState;
          g = null === g ? nj(c) : { baseLanes: g.baseLanes | c, cachePool: null, transitions: g.transitions };
          f.memoizedState = g;
          f.childLanes = a.childLanes & ~c;
          b.memoizedState = mj;
          return d;
        }
        f = a.child;
        a = f.sibling;
        d = Pg(f, { mode: "visible", children: d.children });
        0 === (b.mode & 1) && (d.lanes = c);
        d.return = b;
        d.sibling = null;
        null !== a && (c = b.deletions, null === c ? (b.deletions = [a], b.flags |= 16) : c.push(a));
        b.child = d;
        b.memoizedState = null;
        return d;
      }
      __name(oj, "oj");
      function qj(a, b) {
        b = pj({ mode: "visible", children: b }, a.mode, 0, null);
        b.return = a;
        return a.child = b;
      }
      __name(qj, "qj");
      function sj(a, b, c, d) {
        null !== d && Jg(d);
        Ug(b, a.child, null, c);
        a = qj(b, b.pendingProps.children);
        a.flags |= 2;
        b.memoizedState = null;
        return a;
      }
      __name(sj, "sj");
      function rj(a, b, c, d, e, f, g) {
        if (c) {
          if (b.flags & 256) return b.flags &= -257, d = Ki(Error(p(422))), sj(a, b, g, d);
          if (null !== b.memoizedState) return b.child = a.child, b.flags |= 128, null;
          f = d.fallback;
          e = b.mode;
          d = pj({ mode: "visible", children: d.children }, e, 0, null);
          f = Tg(f, e, g, null);
          f.flags |= 2;
          d.return = b;
          f.return = b;
          d.sibling = f;
          b.child = d;
          0 !== (b.mode & 1) && Ug(b, a.child, null, g);
          b.child.memoizedState = nj(g);
          b.memoizedState = mj;
          return f;
        }
        if (0 === (b.mode & 1)) return sj(a, b, g, null);
        if ("$!" === e.data) {
          d = e.nextSibling && e.nextSibling.dataset;
          if (d) var h = d.dgst;
          d = h;
          f = Error(p(419));
          d = Ki(f, d, void 0);
          return sj(a, b, g, d);
        }
        h = 0 !== (g & a.childLanes);
        if (dh || h) {
          d = Q;
          if (null !== d) {
            switch (g & -g) {
              case 4:
                e = 2;
                break;
              case 16:
                e = 8;
                break;
              case 64:
              case 128:
              case 256:
              case 512:
              case 1024:
              case 2048:
              case 4096:
              case 8192:
              case 16384:
              case 32768:
              case 65536:
              case 131072:
              case 262144:
              case 524288:
              case 1048576:
              case 2097152:
              case 4194304:
              case 8388608:
              case 16777216:
              case 33554432:
              case 67108864:
                e = 32;
                break;
              case 536870912:
                e = 268435456;
                break;
              default:
                e = 0;
            }
            e = 0 !== (e & (d.suspendedLanes | g)) ? 0 : e;
            0 !== e && e !== f.retryLane && (f.retryLane = e, ih(a, e), gi(d, a, e, -1));
          }
          tj();
          d = Ki(Error(p(421)));
          return sj(a, b, g, d);
        }
        if ("$?" === e.data) return b.flags |= 128, b.child = a.child, b = uj.bind(null, a), e._reactRetry = b, null;
        a = f.treeContext;
        yg = Lf(e.nextSibling);
        xg = b;
        I = true;
        zg = null;
        null !== a && (og[pg++] = rg, og[pg++] = sg, og[pg++] = qg, rg = a.id, sg = a.overflow, qg = b);
        b = qj(b, d.children);
        b.flags |= 4096;
        return b;
      }
      __name(rj, "rj");
      function vj(a, b, c) {
        a.lanes |= b;
        var d = a.alternate;
        null !== d && (d.lanes |= b);
        bh(a.return, b, c);
      }
      __name(vj, "vj");
      function wj(a, b, c, d, e) {
        var f = a.memoizedState;
        null === f ? a.memoizedState = { isBackwards: b, rendering: null, renderingStartTime: 0, last: d, tail: c, tailMode: e } : (f.isBackwards = b, f.rendering = null, f.renderingStartTime = 0, f.last = d, f.tail = c, f.tailMode = e);
      }
      __name(wj, "wj");
      function xj(a, b, c) {
        var d = b.pendingProps, e = d.revealOrder, f = d.tail;
        Xi(a, b, d.children, c);
        d = L.current;
        if (0 !== (d & 2)) d = d & 1 | 2, b.flags |= 128;
        else {
          if (null !== a && 0 !== (a.flags & 128)) a: for (a = b.child; null !== a; ) {
            if (13 === a.tag) null !== a.memoizedState && vj(a, c, b);
            else if (19 === a.tag) vj(a, c, b);
            else if (null !== a.child) {
              a.child.return = a;
              a = a.child;
              continue;
            }
            if (a === b) break a;
            for (; null === a.sibling; ) {
              if (null === a.return || a.return === b) break a;
              a = a.return;
            }
            a.sibling.return = a.return;
            a = a.sibling;
          }
          d &= 1;
        }
        G(L, d);
        if (0 === (b.mode & 1)) b.memoizedState = null;
        else switch (e) {
          case "forwards":
            c = b.child;
            for (e = null; null !== c; ) a = c.alternate, null !== a && null === Ch(a) && (e = c), c = c.sibling;
            c = e;
            null === c ? (e = b.child, b.child = null) : (e = c.sibling, c.sibling = null);
            wj(b, false, e, c, f);
            break;
          case "backwards":
            c = null;
            e = b.child;
            for (b.child = null; null !== e; ) {
              a = e.alternate;
              if (null !== a && null === Ch(a)) {
                b.child = e;
                break;
              }
              a = e.sibling;
              e.sibling = c;
              c = e;
              e = a;
            }
            wj(b, true, c, null, f);
            break;
          case "together":
            wj(b, false, null, null, void 0);
            break;
          default:
            b.memoizedState = null;
        }
        return b.child;
      }
      __name(xj, "xj");
      function ij(a, b) {
        0 === (b.mode & 1) && null !== a && (a.alternate = null, b.alternate = null, b.flags |= 2);
      }
      __name(ij, "ij");
      function Zi(a, b, c) {
        null !== a && (b.dependencies = a.dependencies);
        rh |= b.lanes;
        if (0 === (c & b.childLanes)) return null;
        if (null !== a && b.child !== a.child) throw Error(p(153));
        if (null !== b.child) {
          a = b.child;
          c = Pg(a, a.pendingProps);
          b.child = c;
          for (c.return = b; null !== a.sibling; ) a = a.sibling, c = c.sibling = Pg(a, a.pendingProps), c.return = b;
          c.sibling = null;
        }
        return b.child;
      }
      __name(Zi, "Zi");
      function yj(a, b, c) {
        switch (b.tag) {
          case 3:
            kj(b);
            Ig();
            break;
          case 5:
            Ah(b);
            break;
          case 1:
            Zf(b.type) && cg(b);
            break;
          case 4:
            yh(b, b.stateNode.containerInfo);
            break;
          case 10:
            var d = b.type._context, e = b.memoizedProps.value;
            G(Wg, d._currentValue);
            d._currentValue = e;
            break;
          case 13:
            d = b.memoizedState;
            if (null !== d) {
              if (null !== d.dehydrated) return G(L, L.current & 1), b.flags |= 128, null;
              if (0 !== (c & b.child.childLanes)) return oj(a, b, c);
              G(L, L.current & 1);
              a = Zi(a, b, c);
              return null !== a ? a.sibling : null;
            }
            G(L, L.current & 1);
            break;
          case 19:
            d = 0 !== (c & b.childLanes);
            if (0 !== (a.flags & 128)) {
              if (d) return xj(a, b, c);
              b.flags |= 128;
            }
            e = b.memoizedState;
            null !== e && (e.rendering = null, e.tail = null, e.lastEffect = null);
            G(L, L.current);
            if (d) break;
            else return null;
          case 22:
          case 23:
            return b.lanes = 0, dj(a, b, c);
        }
        return Zi(a, b, c);
      }
      __name(yj, "yj");
      var zj;
      var Aj;
      var Bj;
      var Cj;
      zj = /* @__PURE__ */ __name(function(a, b) {
        for (var c = b.child; null !== c; ) {
          if (5 === c.tag || 6 === c.tag) a.appendChild(c.stateNode);
          else if (4 !== c.tag && null !== c.child) {
            c.child.return = c;
            c = c.child;
            continue;
          }
          if (c === b) break;
          for (; null === c.sibling; ) {
            if (null === c.return || c.return === b) return;
            c = c.return;
          }
          c.sibling.return = c.return;
          c = c.sibling;
        }
      }, "zj");
      Aj = /* @__PURE__ */ __name(function() {
      }, "Aj");
      Bj = /* @__PURE__ */ __name(function(a, b, c, d) {
        var e = a.memoizedProps;
        if (e !== d) {
          a = b.stateNode;
          xh(uh.current);
          var f = null;
          switch (c) {
            case "input":
              e = Ya(a, e);
              d = Ya(a, d);
              f = [];
              break;
            case "select":
              e = A({}, e, { value: void 0 });
              d = A({}, d, { value: void 0 });
              f = [];
              break;
            case "textarea":
              e = gb(a, e);
              d = gb(a, d);
              f = [];
              break;
            default:
              "function" !== typeof e.onClick && "function" === typeof d.onClick && (a.onclick = Bf);
          }
          ub(c, d);
          var g;
          c = null;
          for (l in e) if (!d.hasOwnProperty(l) && e.hasOwnProperty(l) && null != e[l]) if ("style" === l) {
            var h = e[l];
            for (g in h) h.hasOwnProperty(g) && (c || (c = {}), c[g] = "");
          } else "dangerouslySetInnerHTML" !== l && "children" !== l && "suppressContentEditableWarning" !== l && "suppressHydrationWarning" !== l && "autoFocus" !== l && (ea.hasOwnProperty(l) ? f || (f = []) : (f = f || []).push(l, null));
          for (l in d) {
            var k = d[l];
            h = null != e ? e[l] : void 0;
            if (d.hasOwnProperty(l) && k !== h && (null != k || null != h)) if ("style" === l) if (h) {
              for (g in h) !h.hasOwnProperty(g) || k && k.hasOwnProperty(g) || (c || (c = {}), c[g] = "");
              for (g in k) k.hasOwnProperty(g) && h[g] !== k[g] && (c || (c = {}), c[g] = k[g]);
            } else c || (f || (f = []), f.push(
              l,
              c
            )), c = k;
            else "dangerouslySetInnerHTML" === l ? (k = k ? k.__html : void 0, h = h ? h.__html : void 0, null != k && h !== k && (f = f || []).push(l, k)) : "children" === l ? "string" !== typeof k && "number" !== typeof k || (f = f || []).push(l, "" + k) : "suppressContentEditableWarning" !== l && "suppressHydrationWarning" !== l && (ea.hasOwnProperty(l) ? (null != k && "onScroll" === l && D("scroll", a), f || h === k || (f = [])) : (f = f || []).push(l, k));
          }
          c && (f = f || []).push("style", c);
          var l = f;
          if (b.updateQueue = l) b.flags |= 4;
        }
      }, "Bj");
      Cj = /* @__PURE__ */ __name(function(a, b, c, d) {
        c !== d && (b.flags |= 4);
      }, "Cj");
      function Dj(a, b) {
        if (!I) switch (a.tailMode) {
          case "hidden":
            b = a.tail;
            for (var c = null; null !== b; ) null !== b.alternate && (c = b), b = b.sibling;
            null === c ? a.tail = null : c.sibling = null;
            break;
          case "collapsed":
            c = a.tail;
            for (var d = null; null !== c; ) null !== c.alternate && (d = c), c = c.sibling;
            null === d ? b || null === a.tail ? a.tail = null : a.tail.sibling = null : d.sibling = null;
        }
      }
      __name(Dj, "Dj");
      function S(a) {
        var b = null !== a.alternate && a.alternate.child === a.child, c = 0, d = 0;
        if (b) for (var e = a.child; null !== e; ) c |= e.lanes | e.childLanes, d |= e.subtreeFlags & 14680064, d |= e.flags & 14680064, e.return = a, e = e.sibling;
        else for (e = a.child; null !== e; ) c |= e.lanes | e.childLanes, d |= e.subtreeFlags, d |= e.flags, e.return = a, e = e.sibling;
        a.subtreeFlags |= d;
        a.childLanes = c;
        return b;
      }
      __name(S, "S");
      function Ej(a, b, c) {
        var d = b.pendingProps;
        wg(b);
        switch (b.tag) {
          case 2:
          case 16:
          case 15:
          case 0:
          case 11:
          case 7:
          case 8:
          case 12:
          case 9:
          case 14:
            return S(b), null;
          case 1:
            return Zf(b.type) && $f(), S(b), null;
          case 3:
            d = b.stateNode;
            zh();
            E(Wf);
            E(H);
            Eh();
            d.pendingContext && (d.context = d.pendingContext, d.pendingContext = null);
            if (null === a || null === a.child) Gg(b) ? b.flags |= 4 : null === a || a.memoizedState.isDehydrated && 0 === (b.flags & 256) || (b.flags |= 1024, null !== zg && (Fj(zg), zg = null));
            Aj(a, b);
            S(b);
            return null;
          case 5:
            Bh(b);
            var e = xh(wh.current);
            c = b.type;
            if (null !== a && null != b.stateNode) Bj(a, b, c, d, e), a.ref !== b.ref && (b.flags |= 512, b.flags |= 2097152);
            else {
              if (!d) {
                if (null === b.stateNode) throw Error(p(166));
                S(b);
                return null;
              }
              a = xh(uh.current);
              if (Gg(b)) {
                d = b.stateNode;
                c = b.type;
                var f = b.memoizedProps;
                d[Of] = b;
                d[Pf] = f;
                a = 0 !== (b.mode & 1);
                switch (c) {
                  case "dialog":
                    D("cancel", d);
                    D("close", d);
                    break;
                  case "iframe":
                  case "object":
                  case "embed":
                    D("load", d);
                    break;
                  case "video":
                  case "audio":
                    for (e = 0; e < lf.length; e++) D(lf[e], d);
                    break;
                  case "source":
                    D("error", d);
                    break;
                  case "img":
                  case "image":
                  case "link":
                    D(
                      "error",
                      d
                    );
                    D("load", d);
                    break;
                  case "details":
                    D("toggle", d);
                    break;
                  case "input":
                    Za(d, f);
                    D("invalid", d);
                    break;
                  case "select":
                    d._wrapperState = { wasMultiple: !!f.multiple };
                    D("invalid", d);
                    break;
                  case "textarea":
                    hb(d, f), D("invalid", d);
                }
                ub(c, f);
                e = null;
                for (var g in f) if (f.hasOwnProperty(g)) {
                  var h = f[g];
                  "children" === g ? "string" === typeof h ? d.textContent !== h && (true !== f.suppressHydrationWarning && Af(d.textContent, h, a), e = ["children", h]) : "number" === typeof h && d.textContent !== "" + h && (true !== f.suppressHydrationWarning && Af(
                    d.textContent,
                    h,
                    a
                  ), e = ["children", "" + h]) : ea.hasOwnProperty(g) && null != h && "onScroll" === g && D("scroll", d);
                }
                switch (c) {
                  case "input":
                    Va(d);
                    db(d, f, true);
                    break;
                  case "textarea":
                    Va(d);
                    jb(d);
                    break;
                  case "select":
                  case "option":
                    break;
                  default:
                    "function" === typeof f.onClick && (d.onclick = Bf);
                }
                d = e;
                b.updateQueue = d;
                null !== d && (b.flags |= 4);
              } else {
                g = 9 === e.nodeType ? e : e.ownerDocument;
                "http://www.w3.org/1999/xhtml" === a && (a = kb(c));
                "http://www.w3.org/1999/xhtml" === a ? "script" === c ? (a = g.createElement("div"), a.innerHTML = "<script><\/script>", a = a.removeChild(a.firstChild)) : "string" === typeof d.is ? a = g.createElement(c, { is: d.is }) : (a = g.createElement(c), "select" === c && (g = a, d.multiple ? g.multiple = true : d.size && (g.size = d.size))) : a = g.createElementNS(a, c);
                a[Of] = b;
                a[Pf] = d;
                zj(a, b, false, false);
                b.stateNode = a;
                a: {
                  g = vb(c, d);
                  switch (c) {
                    case "dialog":
                      D("cancel", a);
                      D("close", a);
                      e = d;
                      break;
                    case "iframe":
                    case "object":
                    case "embed":
                      D("load", a);
                      e = d;
                      break;
                    case "video":
                    case "audio":
                      for (e = 0; e < lf.length; e++) D(lf[e], a);
                      e = d;
                      break;
                    case "source":
                      D("error", a);
                      e = d;
                      break;
                    case "img":
                    case "image":
                    case "link":
                      D(
                        "error",
                        a
                      );
                      D("load", a);
                      e = d;
                      break;
                    case "details":
                      D("toggle", a);
                      e = d;
                      break;
                    case "input":
                      Za(a, d);
                      e = Ya(a, d);
                      D("invalid", a);
                      break;
                    case "option":
                      e = d;
                      break;
                    case "select":
                      a._wrapperState = { wasMultiple: !!d.multiple };
                      e = A({}, d, { value: void 0 });
                      D("invalid", a);
                      break;
                    case "textarea":
                      hb(a, d);
                      e = gb(a, d);
                      D("invalid", a);
                      break;
                    default:
                      e = d;
                  }
                  ub(c, e);
                  h = e;
                  for (f in h) if (h.hasOwnProperty(f)) {
                    var k = h[f];
                    "style" === f ? sb(a, k) : "dangerouslySetInnerHTML" === f ? (k = k ? k.__html : void 0, null != k && nb(a, k)) : "children" === f ? "string" === typeof k ? ("textarea" !== c || "" !== k) && ob(a, k) : "number" === typeof k && ob(a, "" + k) : "suppressContentEditableWarning" !== f && "suppressHydrationWarning" !== f && "autoFocus" !== f && (ea.hasOwnProperty(f) ? null != k && "onScroll" === f && D("scroll", a) : null != k && ta(a, f, k, g));
                  }
                  switch (c) {
                    case "input":
                      Va(a);
                      db(a, d, false);
                      break;
                    case "textarea":
                      Va(a);
                      jb(a);
                      break;
                    case "option":
                      null != d.value && a.setAttribute("value", "" + Sa(d.value));
                      break;
                    case "select":
                      a.multiple = !!d.multiple;
                      f = d.value;
                      null != f ? fb(a, !!d.multiple, f, false) : null != d.defaultValue && fb(
                        a,
                        !!d.multiple,
                        d.defaultValue,
                        true
                      );
                      break;
                    default:
                      "function" === typeof e.onClick && (a.onclick = Bf);
                  }
                  switch (c) {
                    case "button":
                    case "input":
                    case "select":
                    case "textarea":
                      d = !!d.autoFocus;
                      break a;
                    case "img":
                      d = true;
                      break a;
                    default:
                      d = false;
                  }
                }
                d && (b.flags |= 4);
              }
              null !== b.ref && (b.flags |= 512, b.flags |= 2097152);
            }
            S(b);
            return null;
          case 6:
            if (a && null != b.stateNode) Cj(a, b, a.memoizedProps, d);
            else {
              if ("string" !== typeof d && null === b.stateNode) throw Error(p(166));
              c = xh(wh.current);
              xh(uh.current);
              if (Gg(b)) {
                d = b.stateNode;
                c = b.memoizedProps;
                d[Of] = b;
                if (f = d.nodeValue !== c) {
                  if (a = xg, null !== a) switch (a.tag) {
                    case 3:
                      Af(d.nodeValue, c, 0 !== (a.mode & 1));
                      break;
                    case 5:
                      true !== a.memoizedProps.suppressHydrationWarning && Af(d.nodeValue, c, 0 !== (a.mode & 1));
                  }
                }
                f && (b.flags |= 4);
              } else d = (9 === c.nodeType ? c : c.ownerDocument).createTextNode(d), d[Of] = b, b.stateNode = d;
            }
            S(b);
            return null;
          case 13:
            E(L);
            d = b.memoizedState;
            if (null === a || null !== a.memoizedState && null !== a.memoizedState.dehydrated) {
              if (I && null !== yg && 0 !== (b.mode & 1) && 0 === (b.flags & 128)) Hg(), Ig(), b.flags |= 98560, f = false;
              else if (f = Gg(b), null !== d && null !== d.dehydrated) {
                if (null === a) {
                  if (!f) throw Error(p(318));
                  f = b.memoizedState;
                  f = null !== f ? f.dehydrated : null;
                  if (!f) throw Error(p(317));
                  f[Of] = b;
                } else Ig(), 0 === (b.flags & 128) && (b.memoizedState = null), b.flags |= 4;
                S(b);
                f = false;
              } else null !== zg && (Fj(zg), zg = null), f = true;
              if (!f) return b.flags & 65536 ? b : null;
            }
            if (0 !== (b.flags & 128)) return b.lanes = c, b;
            d = null !== d;
            d !== (null !== a && null !== a.memoizedState) && d && (b.child.flags |= 8192, 0 !== (b.mode & 1) && (null === a || 0 !== (L.current & 1) ? 0 === T && (T = 3) : tj()));
            null !== b.updateQueue && (b.flags |= 4);
            S(b);
            return null;
          case 4:
            return zh(), Aj(a, b), null === a && sf(b.stateNode.containerInfo), S(b), null;
          case 10:
            return ah(b.type._context), S(b), null;
          case 17:
            return Zf(b.type) && $f(), S(b), null;
          case 19:
            E(L);
            f = b.memoizedState;
            if (null === f) return S(b), null;
            d = 0 !== (b.flags & 128);
            g = f.rendering;
            if (null === g) if (d) Dj(f, false);
            else {
              if (0 !== T || null !== a && 0 !== (a.flags & 128)) for (a = b.child; null !== a; ) {
                g = Ch(a);
                if (null !== g) {
                  b.flags |= 128;
                  Dj(f, false);
                  d = g.updateQueue;
                  null !== d && (b.updateQueue = d, b.flags |= 4);
                  b.subtreeFlags = 0;
                  d = c;
                  for (c = b.child; null !== c; ) f = c, a = d, f.flags &= 14680066, g = f.alternate, null === g ? (f.childLanes = 0, f.lanes = a, f.child = null, f.subtreeFlags = 0, f.memoizedProps = null, f.memoizedState = null, f.updateQueue = null, f.dependencies = null, f.stateNode = null) : (f.childLanes = g.childLanes, f.lanes = g.lanes, f.child = g.child, f.subtreeFlags = 0, f.deletions = null, f.memoizedProps = g.memoizedProps, f.memoizedState = g.memoizedState, f.updateQueue = g.updateQueue, f.type = g.type, a = g.dependencies, f.dependencies = null === a ? null : { lanes: a.lanes, firstContext: a.firstContext }), c = c.sibling;
                  G(L, L.current & 1 | 2);
                  return b.child;
                }
                a = a.sibling;
              }
              null !== f.tail && B() > Gj && (b.flags |= 128, d = true, Dj(f, false), b.lanes = 4194304);
            }
            else {
              if (!d) if (a = Ch(g), null !== a) {
                if (b.flags |= 128, d = true, c = a.updateQueue, null !== c && (b.updateQueue = c, b.flags |= 4), Dj(f, true), null === f.tail && "hidden" === f.tailMode && !g.alternate && !I) return S(b), null;
              } else 2 * B() - f.renderingStartTime > Gj && 1073741824 !== c && (b.flags |= 128, d = true, Dj(f, false), b.lanes = 4194304);
              f.isBackwards ? (g.sibling = b.child, b.child = g) : (c = f.last, null !== c ? c.sibling = g : b.child = g, f.last = g);
            }
            if (null !== f.tail) return b = f.tail, f.rendering = b, f.tail = b.sibling, f.renderingStartTime = B(), b.sibling = null, c = L.current, G(L, d ? c & 1 | 2 : c & 1), b;
            S(b);
            return null;
          case 22:
          case 23:
            return Hj(), d = null !== b.memoizedState, null !== a && null !== a.memoizedState !== d && (b.flags |= 8192), d && 0 !== (b.mode & 1) ? 0 !== (fj & 1073741824) && (S(b), b.subtreeFlags & 6 && (b.flags |= 8192)) : S(b), null;
          case 24:
            return null;
          case 25:
            return null;
        }
        throw Error(p(156, b.tag));
      }
      __name(Ej, "Ej");
      function Ij(a, b) {
        wg(b);
        switch (b.tag) {
          case 1:
            return Zf(b.type) && $f(), a = b.flags, a & 65536 ? (b.flags = a & -65537 | 128, b) : null;
          case 3:
            return zh(), E(Wf), E(H), Eh(), a = b.flags, 0 !== (a & 65536) && 0 === (a & 128) ? (b.flags = a & -65537 | 128, b) : null;
          case 5:
            return Bh(b), null;
          case 13:
            E(L);
            a = b.memoizedState;
            if (null !== a && null !== a.dehydrated) {
              if (null === b.alternate) throw Error(p(340));
              Ig();
            }
            a = b.flags;
            return a & 65536 ? (b.flags = a & -65537 | 128, b) : null;
          case 19:
            return E(L), null;
          case 4:
            return zh(), null;
          case 10:
            return ah(b.type._context), null;
          case 22:
          case 23:
            return Hj(), null;
          case 24:
            return null;
          default:
            return null;
        }
      }
      __name(Ij, "Ij");
      var Jj = false;
      var U = false;
      var Kj = "function" === typeof WeakSet ? WeakSet : Set;
      var V = null;
      function Lj(a, b) {
        var c = a.ref;
        if (null !== c) if ("function" === typeof c) try {
          c(null);
        } catch (d) {
          W(a, b, d);
        }
        else c.current = null;
      }
      __name(Lj, "Lj");
      function Mj(a, b, c) {
        try {
          c();
        } catch (d) {
          W(a, b, d);
        }
      }
      __name(Mj, "Mj");
      var Nj = false;
      function Oj(a, b) {
        Cf = dd;
        a = Me();
        if (Ne(a)) {
          if ("selectionStart" in a) var c = { start: a.selectionStart, end: a.selectionEnd };
          else a: {
            c = (c = a.ownerDocument) && c.defaultView || window;
            var d = c.getSelection && c.getSelection();
            if (d && 0 !== d.rangeCount) {
              c = d.anchorNode;
              var e = d.anchorOffset, f = d.focusNode;
              d = d.focusOffset;
              try {
                c.nodeType, f.nodeType;
              } catch (F) {
                c = null;
                break a;
              }
              var g = 0, h = -1, k = -1, l = 0, m = 0, q = a, r = null;
              b: for (; ; ) {
                for (var y; ; ) {
                  q !== c || 0 !== e && 3 !== q.nodeType || (h = g + e);
                  q !== f || 0 !== d && 3 !== q.nodeType || (k = g + d);
                  3 === q.nodeType && (g += q.nodeValue.length);
                  if (null === (y = q.firstChild)) break;
                  r = q;
                  q = y;
                }
                for (; ; ) {
                  if (q === a) break b;
                  r === c && ++l === e && (h = g);
                  r === f && ++m === d && (k = g);
                  if (null !== (y = q.nextSibling)) break;
                  q = r;
                  r = q.parentNode;
                }
                q = y;
              }
              c = -1 === h || -1 === k ? null : { start: h, end: k };
            } else c = null;
          }
          c = c || { start: 0, end: 0 };
        } else c = null;
        Df = { focusedElem: a, selectionRange: c };
        dd = false;
        for (V = b; null !== V; ) if (b = V, a = b.child, 0 !== (b.subtreeFlags & 1028) && null !== a) a.return = b, V = a;
        else for (; null !== V; ) {
          b = V;
          try {
            var n = b.alternate;
            if (0 !== (b.flags & 1024)) switch (b.tag) {
              case 0:
              case 11:
              case 15:
                break;
              case 1:
                if (null !== n) {
                  var t = n.memoizedProps, J = n.memoizedState, x = b.stateNode, w = x.getSnapshotBeforeUpdate(b.elementType === b.type ? t : Ci(b.type, t), J);
                  x.__reactInternalSnapshotBeforeUpdate = w;
                }
                break;
              case 3:
                var u = b.stateNode.containerInfo;
                1 === u.nodeType ? u.textContent = "" : 9 === u.nodeType && u.documentElement && u.removeChild(u.documentElement);
                break;
              case 5:
              case 6:
              case 4:
              case 17:
                break;
              default:
                throw Error(p(163));
            }
          } catch (F) {
            W(b, b.return, F);
          }
          a = b.sibling;
          if (null !== a) {
            a.return = b.return;
            V = a;
            break;
          }
          V = b.return;
        }
        n = Nj;
        Nj = false;
        return n;
      }
      __name(Oj, "Oj");
      function Pj(a, b, c) {
        var d = b.updateQueue;
        d = null !== d ? d.lastEffect : null;
        if (null !== d) {
          var e = d = d.next;
          do {
            if ((e.tag & a) === a) {
              var f = e.destroy;
              e.destroy = void 0;
              void 0 !== f && Mj(b, c, f);
            }
            e = e.next;
          } while (e !== d);
        }
      }
      __name(Pj, "Pj");
      function Qj(a, b) {
        b = b.updateQueue;
        b = null !== b ? b.lastEffect : null;
        if (null !== b) {
          var c = b = b.next;
          do {
            if ((c.tag & a) === a) {
              var d = c.create;
              c.destroy = d();
            }
            c = c.next;
          } while (c !== b);
        }
      }
      __name(Qj, "Qj");
      function Rj(a) {
        var b = a.ref;
        if (null !== b) {
          var c = a.stateNode;
          switch (a.tag) {
            case 5:
              a = c;
              break;
            default:
              a = c;
          }
          "function" === typeof b ? b(a) : b.current = a;
        }
      }
      __name(Rj, "Rj");
      function Sj(a) {
        var b = a.alternate;
        null !== b && (a.alternate = null, Sj(b));
        a.child = null;
        a.deletions = null;
        a.sibling = null;
        5 === a.tag && (b = a.stateNode, null !== b && (delete b[Of], delete b[Pf], delete b[of], delete b[Qf], delete b[Rf]));
        a.stateNode = null;
        a.return = null;
        a.dependencies = null;
        a.memoizedProps = null;
        a.memoizedState = null;
        a.pendingProps = null;
        a.stateNode = null;
        a.updateQueue = null;
      }
      __name(Sj, "Sj");
      function Tj(a) {
        return 5 === a.tag || 3 === a.tag || 4 === a.tag;
      }
      __name(Tj, "Tj");
      function Uj(a) {
        a: for (; ; ) {
          for (; null === a.sibling; ) {
            if (null === a.return || Tj(a.return)) return null;
            a = a.return;
          }
          a.sibling.return = a.return;
          for (a = a.sibling; 5 !== a.tag && 6 !== a.tag && 18 !== a.tag; ) {
            if (a.flags & 2) continue a;
            if (null === a.child || 4 === a.tag) continue a;
            else a.child.return = a, a = a.child;
          }
          if (!(a.flags & 2)) return a.stateNode;
        }
      }
      __name(Uj, "Uj");
      function Vj(a, b, c) {
        var d = a.tag;
        if (5 === d || 6 === d) a = a.stateNode, b ? 8 === c.nodeType ? c.parentNode.insertBefore(a, b) : c.insertBefore(a, b) : (8 === c.nodeType ? (b = c.parentNode, b.insertBefore(a, c)) : (b = c, b.appendChild(a)), c = c._reactRootContainer, null !== c && void 0 !== c || null !== b.onclick || (b.onclick = Bf));
        else if (4 !== d && (a = a.child, null !== a)) for (Vj(a, b, c), a = a.sibling; null !== a; ) Vj(a, b, c), a = a.sibling;
      }
      __name(Vj, "Vj");
      function Wj(a, b, c) {
        var d = a.tag;
        if (5 === d || 6 === d) a = a.stateNode, b ? c.insertBefore(a, b) : c.appendChild(a);
        else if (4 !== d && (a = a.child, null !== a)) for (Wj(a, b, c), a = a.sibling; null !== a; ) Wj(a, b, c), a = a.sibling;
      }
      __name(Wj, "Wj");
      var X = null;
      var Xj = false;
      function Yj(a, b, c) {
        for (c = c.child; null !== c; ) Zj(a, b, c), c = c.sibling;
      }
      __name(Yj, "Yj");
      function Zj(a, b, c) {
        if (lc && "function" === typeof lc.onCommitFiberUnmount) try {
          lc.onCommitFiberUnmount(kc, c);
        } catch (h) {
        }
        switch (c.tag) {
          case 5:
            U || Lj(c, b);
          case 6:
            var d = X, e = Xj;
            X = null;
            Yj(a, b, c);
            X = d;
            Xj = e;
            null !== X && (Xj ? (a = X, c = c.stateNode, 8 === a.nodeType ? a.parentNode.removeChild(c) : a.removeChild(c)) : X.removeChild(c.stateNode));
            break;
          case 18:
            null !== X && (Xj ? (a = X, c = c.stateNode, 8 === a.nodeType ? Kf(a.parentNode, c) : 1 === a.nodeType && Kf(a, c), bd(a)) : Kf(X, c.stateNode));
            break;
          case 4:
            d = X;
            e = Xj;
            X = c.stateNode.containerInfo;
            Xj = true;
            Yj(a, b, c);
            X = d;
            Xj = e;
            break;
          case 0:
          case 11:
          case 14:
          case 15:
            if (!U && (d = c.updateQueue, null !== d && (d = d.lastEffect, null !== d))) {
              e = d = d.next;
              do {
                var f = e, g = f.destroy;
                f = f.tag;
                void 0 !== g && (0 !== (f & 2) ? Mj(c, b, g) : 0 !== (f & 4) && Mj(c, b, g));
                e = e.next;
              } while (e !== d);
            }
            Yj(a, b, c);
            break;
          case 1:
            if (!U && (Lj(c, b), d = c.stateNode, "function" === typeof d.componentWillUnmount)) try {
              d.props = c.memoizedProps, d.state = c.memoizedState, d.componentWillUnmount();
            } catch (h) {
              W(c, b, h);
            }
            Yj(a, b, c);
            break;
          case 21:
            Yj(a, b, c);
            break;
          case 22:
            c.mode & 1 ? (U = (d = U) || null !== c.memoizedState, Yj(a, b, c), U = d) : Yj(a, b, c);
            break;
          default:
            Yj(a, b, c);
        }
      }
      __name(Zj, "Zj");
      function ak(a) {
        var b = a.updateQueue;
        if (null !== b) {
          a.updateQueue = null;
          var c = a.stateNode;
          null === c && (c = a.stateNode = new Kj());
          b.forEach(function(b2) {
            var d = bk.bind(null, a, b2);
            c.has(b2) || (c.add(b2), b2.then(d, d));
          });
        }
      }
      __name(ak, "ak");
      function ck(a, b) {
        var c = b.deletions;
        if (null !== c) for (var d = 0; d < c.length; d++) {
          var e = c[d];
          try {
            var f = a, g = b, h = g;
            a: for (; null !== h; ) {
              switch (h.tag) {
                case 5:
                  X = h.stateNode;
                  Xj = false;
                  break a;
                case 3:
                  X = h.stateNode.containerInfo;
                  Xj = true;
                  break a;
                case 4:
                  X = h.stateNode.containerInfo;
                  Xj = true;
                  break a;
              }
              h = h.return;
            }
            if (null === X) throw Error(p(160));
            Zj(f, g, e);
            X = null;
            Xj = false;
            var k = e.alternate;
            null !== k && (k.return = null);
            e.return = null;
          } catch (l) {
            W(e, b, l);
          }
        }
        if (b.subtreeFlags & 12854) for (b = b.child; null !== b; ) dk(b, a), b = b.sibling;
      }
      __name(ck, "ck");
      function dk(a, b) {
        var c = a.alternate, d = a.flags;
        switch (a.tag) {
          case 0:
          case 11:
          case 14:
          case 15:
            ck(b, a);
            ek(a);
            if (d & 4) {
              try {
                Pj(3, a, a.return), Qj(3, a);
              } catch (t) {
                W(a, a.return, t);
              }
              try {
                Pj(5, a, a.return);
              } catch (t) {
                W(a, a.return, t);
              }
            }
            break;
          case 1:
            ck(b, a);
            ek(a);
            d & 512 && null !== c && Lj(c, c.return);
            break;
          case 5:
            ck(b, a);
            ek(a);
            d & 512 && null !== c && Lj(c, c.return);
            if (a.flags & 32) {
              var e = a.stateNode;
              try {
                ob(e, "");
              } catch (t) {
                W(a, a.return, t);
              }
            }
            if (d & 4 && (e = a.stateNode, null != e)) {
              var f = a.memoizedProps, g = null !== c ? c.memoizedProps : f, h = a.type, k = a.updateQueue;
              a.updateQueue = null;
              if (null !== k) try {
                "input" === h && "radio" === f.type && null != f.name && ab(e, f);
                vb(h, g);
                var l = vb(h, f);
                for (g = 0; g < k.length; g += 2) {
                  var m = k[g], q = k[g + 1];
                  "style" === m ? sb(e, q) : "dangerouslySetInnerHTML" === m ? nb(e, q) : "children" === m ? ob(e, q) : ta(e, m, q, l);
                }
                switch (h) {
                  case "input":
                    bb(e, f);
                    break;
                  case "textarea":
                    ib(e, f);
                    break;
                  case "select":
                    var r = e._wrapperState.wasMultiple;
                    e._wrapperState.wasMultiple = !!f.multiple;
                    var y = f.value;
                    null != y ? fb(e, !!f.multiple, y, false) : r !== !!f.multiple && (null != f.defaultValue ? fb(
                      e,
                      !!f.multiple,
                      f.defaultValue,
                      true
                    ) : fb(e, !!f.multiple, f.multiple ? [] : "", false));
                }
                e[Pf] = f;
              } catch (t) {
                W(a, a.return, t);
              }
            }
            break;
          case 6:
            ck(b, a);
            ek(a);
            if (d & 4) {
              if (null === a.stateNode) throw Error(p(162));
              e = a.stateNode;
              f = a.memoizedProps;
              try {
                e.nodeValue = f;
              } catch (t) {
                W(a, a.return, t);
              }
            }
            break;
          case 3:
            ck(b, a);
            ek(a);
            if (d & 4 && null !== c && c.memoizedState.isDehydrated) try {
              bd(b.containerInfo);
            } catch (t) {
              W(a, a.return, t);
            }
            break;
          case 4:
            ck(b, a);
            ek(a);
            break;
          case 13:
            ck(b, a);
            ek(a);
            e = a.child;
            e.flags & 8192 && (f = null !== e.memoizedState, e.stateNode.isHidden = f, !f || null !== e.alternate && null !== e.alternate.memoizedState || (fk = B()));
            d & 4 && ak(a);
            break;
          case 22:
            m = null !== c && null !== c.memoizedState;
            a.mode & 1 ? (U = (l = U) || m, ck(b, a), U = l) : ck(b, a);
            ek(a);
            if (d & 8192) {
              l = null !== a.memoizedState;
              if ((a.stateNode.isHidden = l) && !m && 0 !== (a.mode & 1)) for (V = a, m = a.child; null !== m; ) {
                for (q = V = m; null !== V; ) {
                  r = V;
                  y = r.child;
                  switch (r.tag) {
                    case 0:
                    case 11:
                    case 14:
                    case 15:
                      Pj(4, r, r.return);
                      break;
                    case 1:
                      Lj(r, r.return);
                      var n = r.stateNode;
                      if ("function" === typeof n.componentWillUnmount) {
                        d = r;
                        c = r.return;
                        try {
                          b = d, n.props = b.memoizedProps, n.state = b.memoizedState, n.componentWillUnmount();
                        } catch (t) {
                          W(d, c, t);
                        }
                      }
                      break;
                    case 5:
                      Lj(r, r.return);
                      break;
                    case 22:
                      if (null !== r.memoizedState) {
                        gk(q);
                        continue;
                      }
                  }
                  null !== y ? (y.return = r, V = y) : gk(q);
                }
                m = m.sibling;
              }
              a: for (m = null, q = a; ; ) {
                if (5 === q.tag) {
                  if (null === m) {
                    m = q;
                    try {
                      e = q.stateNode, l ? (f = e.style, "function" === typeof f.setProperty ? f.setProperty("display", "none", "important") : f.display = "none") : (h = q.stateNode, k = q.memoizedProps.style, g = void 0 !== k && null !== k && k.hasOwnProperty("display") ? k.display : null, h.style.display = rb("display", g));
                    } catch (t) {
                      W(a, a.return, t);
                    }
                  }
                } else if (6 === q.tag) {
                  if (null === m) try {
                    q.stateNode.nodeValue = l ? "" : q.memoizedProps;
                  } catch (t) {
                    W(a, a.return, t);
                  }
                } else if ((22 !== q.tag && 23 !== q.tag || null === q.memoizedState || q === a) && null !== q.child) {
                  q.child.return = q;
                  q = q.child;
                  continue;
                }
                if (q === a) break a;
                for (; null === q.sibling; ) {
                  if (null === q.return || q.return === a) break a;
                  m === q && (m = null);
                  q = q.return;
                }
                m === q && (m = null);
                q.sibling.return = q.return;
                q = q.sibling;
              }
            }
            break;
          case 19:
            ck(b, a);
            ek(a);
            d & 4 && ak(a);
            break;
          case 21:
            break;
          default:
            ck(
              b,
              a
            ), ek(a);
        }
      }
      __name(dk, "dk");
      function ek(a) {
        var b = a.flags;
        if (b & 2) {
          try {
            a: {
              for (var c = a.return; null !== c; ) {
                if (Tj(c)) {
                  var d = c;
                  break a;
                }
                c = c.return;
              }
              throw Error(p(160));
            }
            switch (d.tag) {
              case 5:
                var e = d.stateNode;
                d.flags & 32 && (ob(e, ""), d.flags &= -33);
                var f = Uj(a);
                Wj(a, f, e);
                break;
              case 3:
              case 4:
                var g = d.stateNode.containerInfo, h = Uj(a);
                Vj(a, h, g);
                break;
              default:
                throw Error(p(161));
            }
          } catch (k) {
            W(a, a.return, k);
          }
          a.flags &= -3;
        }
        b & 4096 && (a.flags &= -4097);
      }
      __name(ek, "ek");
      function hk(a, b, c) {
        V = a;
        ik(a, b, c);
      }
      __name(hk, "hk");
      function ik(a, b, c) {
        for (var d = 0 !== (a.mode & 1); null !== V; ) {
          var e = V, f = e.child;
          if (22 === e.tag && d) {
            var g = null !== e.memoizedState || Jj;
            if (!g) {
              var h = e.alternate, k = null !== h && null !== h.memoizedState || U;
              h = Jj;
              var l = U;
              Jj = g;
              if ((U = k) && !l) for (V = e; null !== V; ) g = V, k = g.child, 22 === g.tag && null !== g.memoizedState ? jk(e) : null !== k ? (k.return = g, V = k) : jk(e);
              for (; null !== f; ) V = f, ik(f, b, c), f = f.sibling;
              V = e;
              Jj = h;
              U = l;
            }
            kk(a, b, c);
          } else 0 !== (e.subtreeFlags & 8772) && null !== f ? (f.return = e, V = f) : kk(a, b, c);
        }
      }
      __name(ik, "ik");
      function kk(a) {
        for (; null !== V; ) {
          var b = V;
          if (0 !== (b.flags & 8772)) {
            var c = b.alternate;
            try {
              if (0 !== (b.flags & 8772)) switch (b.tag) {
                case 0:
                case 11:
                case 15:
                  U || Qj(5, b);
                  break;
                case 1:
                  var d = b.stateNode;
                  if (b.flags & 4 && !U) if (null === c) d.componentDidMount();
                  else {
                    var e = b.elementType === b.type ? c.memoizedProps : Ci(b.type, c.memoizedProps);
                    d.componentDidUpdate(e, c.memoizedState, d.__reactInternalSnapshotBeforeUpdate);
                  }
                  var f = b.updateQueue;
                  null !== f && sh(b, f, d);
                  break;
                case 3:
                  var g = b.updateQueue;
                  if (null !== g) {
                    c = null;
                    if (null !== b.child) switch (b.child.tag) {
                      case 5:
                        c = b.child.stateNode;
                        break;
                      case 1:
                        c = b.child.stateNode;
                    }
                    sh(b, g, c);
                  }
                  break;
                case 5:
                  var h = b.stateNode;
                  if (null === c && b.flags & 4) {
                    c = h;
                    var k = b.memoizedProps;
                    switch (b.type) {
                      case "button":
                      case "input":
                      case "select":
                      case "textarea":
                        k.autoFocus && c.focus();
                        break;
                      case "img":
                        k.src && (c.src = k.src);
                    }
                  }
                  break;
                case 6:
                  break;
                case 4:
                  break;
                case 12:
                  break;
                case 13:
                  if (null === b.memoizedState) {
                    var l = b.alternate;
                    if (null !== l) {
                      var m = l.memoizedState;
                      if (null !== m) {
                        var q = m.dehydrated;
                        null !== q && bd(q);
                      }
                    }
                  }
                  break;
                case 19:
                case 17:
                case 21:
                case 22:
                case 23:
                case 25:
                  break;
                default:
                  throw Error(p(163));
              }
              U || b.flags & 512 && Rj(b);
            } catch (r) {
              W(b, b.return, r);
            }
          }
          if (b === a) {
            V = null;
            break;
          }
          c = b.sibling;
          if (null !== c) {
            c.return = b.return;
            V = c;
            break;
          }
          V = b.return;
        }
      }
      __name(kk, "kk");
      function gk(a) {
        for (; null !== V; ) {
          var b = V;
          if (b === a) {
            V = null;
            break;
          }
          var c = b.sibling;
          if (null !== c) {
            c.return = b.return;
            V = c;
            break;
          }
          V = b.return;
        }
      }
      __name(gk, "gk");
      function jk(a) {
        for (; null !== V; ) {
          var b = V;
          try {
            switch (b.tag) {
              case 0:
              case 11:
              case 15:
                var c = b.return;
                try {
                  Qj(4, b);
                } catch (k) {
                  W(b, c, k);
                }
                break;
              case 1:
                var d = b.stateNode;
                if ("function" === typeof d.componentDidMount) {
                  var e = b.return;
                  try {
                    d.componentDidMount();
                  } catch (k) {
                    W(b, e, k);
                  }
                }
                var f = b.return;
                try {
                  Rj(b);
                } catch (k) {
                  W(b, f, k);
                }
                break;
              case 5:
                var g = b.return;
                try {
                  Rj(b);
                } catch (k) {
                  W(b, g, k);
                }
            }
          } catch (k) {
            W(b, b.return, k);
          }
          if (b === a) {
            V = null;
            break;
          }
          var h = b.sibling;
          if (null !== h) {
            h.return = b.return;
            V = h;
            break;
          }
          V = b.return;
        }
      }
      __name(jk, "jk");
      var lk = Math.ceil;
      var mk = ua.ReactCurrentDispatcher;
      var nk = ua.ReactCurrentOwner;
      var ok = ua.ReactCurrentBatchConfig;
      var K = 0;
      var Q = null;
      var Y = null;
      var Z = 0;
      var fj = 0;
      var ej = Uf(0);
      var T = 0;
      var pk = null;
      var rh = 0;
      var qk = 0;
      var rk = 0;
      var sk = null;
      var tk = null;
      var fk = 0;
      var Gj = Infinity;
      var uk = null;
      var Oi = false;
      var Pi = null;
      var Ri = null;
      var vk = false;
      var wk = null;
      var xk = 0;
      var yk = 0;
      var zk = null;
      var Ak = -1;
      var Bk = 0;
      function R() {
        return 0 !== (K & 6) ? B() : -1 !== Ak ? Ak : Ak = B();
      }
      __name(R, "R");
      function yi(a) {
        if (0 === (a.mode & 1)) return 1;
        if (0 !== (K & 2) && 0 !== Z) return Z & -Z;
        if (null !== Kg.transition) return 0 === Bk && (Bk = yc()), Bk;
        a = C;
        if (0 !== a) return a;
        a = window.event;
        a = void 0 === a ? 16 : jd(a.type);
        return a;
      }
      __name(yi, "yi");
      function gi(a, b, c, d) {
        if (50 < yk) throw yk = 0, zk = null, Error(p(185));
        Ac(a, c, d);
        if (0 === (K & 2) || a !== Q) a === Q && (0 === (K & 2) && (qk |= c), 4 === T && Ck(a, Z)), Dk(a, d), 1 === c && 0 === K && 0 === (b.mode & 1) && (Gj = B() + 500, fg && jg());
      }
      __name(gi, "gi");
      function Dk(a, b) {
        var c = a.callbackNode;
        wc(a, b);
        var d = uc(a, a === Q ? Z : 0);
        if (0 === d) null !== c && bc(c), a.callbackNode = null, a.callbackPriority = 0;
        else if (b = d & -d, a.callbackPriority !== b) {
          null != c && bc(c);
          if (1 === b) 0 === a.tag ? ig(Ek.bind(null, a)) : hg(Ek.bind(null, a)), Jf(function() {
            0 === (K & 6) && jg();
          }), c = null;
          else {
            switch (Dc(d)) {
              case 1:
                c = fc;
                break;
              case 4:
                c = gc;
                break;
              case 16:
                c = hc;
                break;
              case 536870912:
                c = jc;
                break;
              default:
                c = hc;
            }
            c = Fk(c, Gk.bind(null, a));
          }
          a.callbackPriority = b;
          a.callbackNode = c;
        }
      }
      __name(Dk, "Dk");
      function Gk(a, b) {
        Ak = -1;
        Bk = 0;
        if (0 !== (K & 6)) throw Error(p(327));
        var c = a.callbackNode;
        if (Hk() && a.callbackNode !== c) return null;
        var d = uc(a, a === Q ? Z : 0);
        if (0 === d) return null;
        if (0 !== (d & 30) || 0 !== (d & a.expiredLanes) || b) b = Ik(a, d);
        else {
          b = d;
          var e = K;
          K |= 2;
          var f = Jk();
          if (Q !== a || Z !== b) uk = null, Gj = B() + 500, Kk(a, b);
          do
            try {
              Lk();
              break;
            } catch (h) {
              Mk(a, h);
            }
          while (1);
          $g();
          mk.current = f;
          K = e;
          null !== Y ? b = 0 : (Q = null, Z = 0, b = T);
        }
        if (0 !== b) {
          2 === b && (e = xc(a), 0 !== e && (d = e, b = Nk(a, e)));
          if (1 === b) throw c = pk, Kk(a, 0), Ck(a, d), Dk(a, B()), c;
          if (6 === b) Ck(a, d);
          else {
            e = a.current.alternate;
            if (0 === (d & 30) && !Ok(e) && (b = Ik(a, d), 2 === b && (f = xc(a), 0 !== f && (d = f, b = Nk(a, f))), 1 === b)) throw c = pk, Kk(a, 0), Ck(a, d), Dk(a, B()), c;
            a.finishedWork = e;
            a.finishedLanes = d;
            switch (b) {
              case 0:
              case 1:
                throw Error(p(345));
              case 2:
                Pk(a, tk, uk);
                break;
              case 3:
                Ck(a, d);
                if ((d & 130023424) === d && (b = fk + 500 - B(), 10 < b)) {
                  if (0 !== uc(a, 0)) break;
                  e = a.suspendedLanes;
                  if ((e & d) !== d) {
                    R();
                    a.pingedLanes |= a.suspendedLanes & e;
                    break;
                  }
                  a.timeoutHandle = Ff(Pk.bind(null, a, tk, uk), b);
                  break;
                }
                Pk(a, tk, uk);
                break;
              case 4:
                Ck(a, d);
                if ((d & 4194240) === d) break;
                b = a.eventTimes;
                for (e = -1; 0 < d; ) {
                  var g = 31 - oc(d);
                  f = 1 << g;
                  g = b[g];
                  g > e && (e = g);
                  d &= ~f;
                }
                d = e;
                d = B() - d;
                d = (120 > d ? 120 : 480 > d ? 480 : 1080 > d ? 1080 : 1920 > d ? 1920 : 3e3 > d ? 3e3 : 4320 > d ? 4320 : 1960 * lk(d / 1960)) - d;
                if (10 < d) {
                  a.timeoutHandle = Ff(Pk.bind(null, a, tk, uk), d);
                  break;
                }
                Pk(a, tk, uk);
                break;
              case 5:
                Pk(a, tk, uk);
                break;
              default:
                throw Error(p(329));
            }
          }
        }
        Dk(a, B());
        return a.callbackNode === c ? Gk.bind(null, a) : null;
      }
      __name(Gk, "Gk");
      function Nk(a, b) {
        var c = sk;
        a.current.memoizedState.isDehydrated && (Kk(a, b).flags |= 256);
        a = Ik(a, b);
        2 !== a && (b = tk, tk = c, null !== b && Fj(b));
        return a;
      }
      __name(Nk, "Nk");
      function Fj(a) {
        null === tk ? tk = a : tk.push.apply(tk, a);
      }
      __name(Fj, "Fj");
      function Ok(a) {
        for (var b = a; ; ) {
          if (b.flags & 16384) {
            var c = b.updateQueue;
            if (null !== c && (c = c.stores, null !== c)) for (var d = 0; d < c.length; d++) {
              var e = c[d], f = e.getSnapshot;
              e = e.value;
              try {
                if (!He(f(), e)) return false;
              } catch (g) {
                return false;
              }
            }
          }
          c = b.child;
          if (b.subtreeFlags & 16384 && null !== c) c.return = b, b = c;
          else {
            if (b === a) break;
            for (; null === b.sibling; ) {
              if (null === b.return || b.return === a) return true;
              b = b.return;
            }
            b.sibling.return = b.return;
            b = b.sibling;
          }
        }
        return true;
      }
      __name(Ok, "Ok");
      function Ck(a, b) {
        b &= ~rk;
        b &= ~qk;
        a.suspendedLanes |= b;
        a.pingedLanes &= ~b;
        for (a = a.expirationTimes; 0 < b; ) {
          var c = 31 - oc(b), d = 1 << c;
          a[c] = -1;
          b &= ~d;
        }
      }
      __name(Ck, "Ck");
      function Ek(a) {
        if (0 !== (K & 6)) throw Error(p(327));
        Hk();
        var b = uc(a, 0);
        if (0 === (b & 1)) return Dk(a, B()), null;
        var c = Ik(a, b);
        if (0 !== a.tag && 2 === c) {
          var d = xc(a);
          0 !== d && (b = d, c = Nk(a, d));
        }
        if (1 === c) throw c = pk, Kk(a, 0), Ck(a, b), Dk(a, B()), c;
        if (6 === c) throw Error(p(345));
        a.finishedWork = a.current.alternate;
        a.finishedLanes = b;
        Pk(a, tk, uk);
        Dk(a, B());
        return null;
      }
      __name(Ek, "Ek");
      function Qk(a, b) {
        var c = K;
        K |= 1;
        try {
          return a(b);
        } finally {
          K = c, 0 === K && (Gj = B() + 500, fg && jg());
        }
      }
      __name(Qk, "Qk");
      function Rk(a) {
        null !== wk && 0 === wk.tag && 0 === (K & 6) && Hk();
        var b = K;
        K |= 1;
        var c = ok.transition, d = C;
        try {
          if (ok.transition = null, C = 1, a) return a();
        } finally {
          C = d, ok.transition = c, K = b, 0 === (K & 6) && jg();
        }
      }
      __name(Rk, "Rk");
      function Hj() {
        fj = ej.current;
        E(ej);
      }
      __name(Hj, "Hj");
      function Kk(a, b) {
        a.finishedWork = null;
        a.finishedLanes = 0;
        var c = a.timeoutHandle;
        -1 !== c && (a.timeoutHandle = -1, Gf(c));
        if (null !== Y) for (c = Y.return; null !== c; ) {
          var d = c;
          wg(d);
          switch (d.tag) {
            case 1:
              d = d.type.childContextTypes;
              null !== d && void 0 !== d && $f();
              break;
            case 3:
              zh();
              E(Wf);
              E(H);
              Eh();
              break;
            case 5:
              Bh(d);
              break;
            case 4:
              zh();
              break;
            case 13:
              E(L);
              break;
            case 19:
              E(L);
              break;
            case 10:
              ah(d.type._context);
              break;
            case 22:
            case 23:
              Hj();
          }
          c = c.return;
        }
        Q = a;
        Y = a = Pg(a.current, null);
        Z = fj = b;
        T = 0;
        pk = null;
        rk = qk = rh = 0;
        tk = sk = null;
        if (null !== fh) {
          for (b = 0; b < fh.length; b++) if (c = fh[b], d = c.interleaved, null !== d) {
            c.interleaved = null;
            var e = d.next, f = c.pending;
            if (null !== f) {
              var g = f.next;
              f.next = e;
              d.next = g;
            }
            c.pending = d;
          }
          fh = null;
        }
        return a;
      }
      __name(Kk, "Kk");
      function Mk(a, b) {
        do {
          var c = Y;
          try {
            $g();
            Fh.current = Rh;
            if (Ih) {
              for (var d = M.memoizedState; null !== d; ) {
                var e = d.queue;
                null !== e && (e.pending = null);
                d = d.next;
              }
              Ih = false;
            }
            Hh = 0;
            O = N = M = null;
            Jh = false;
            Kh = 0;
            nk.current = null;
            if (null === c || null === c.return) {
              T = 1;
              pk = b;
              Y = null;
              break;
            }
            a: {
              var f = a, g = c.return, h = c, k = b;
              b = Z;
              h.flags |= 32768;
              if (null !== k && "object" === typeof k && "function" === typeof k.then) {
                var l = k, m = h, q = m.tag;
                if (0 === (m.mode & 1) && (0 === q || 11 === q || 15 === q)) {
                  var r = m.alternate;
                  r ? (m.updateQueue = r.updateQueue, m.memoizedState = r.memoizedState, m.lanes = r.lanes) : (m.updateQueue = null, m.memoizedState = null);
                }
                var y = Ui(g);
                if (null !== y) {
                  y.flags &= -257;
                  Vi(y, g, h, f, b);
                  y.mode & 1 && Si(f, l, b);
                  b = y;
                  k = l;
                  var n = b.updateQueue;
                  if (null === n) {
                    var t = /* @__PURE__ */ new Set();
                    t.add(k);
                    b.updateQueue = t;
                  } else n.add(k);
                  break a;
                } else {
                  if (0 === (b & 1)) {
                    Si(f, l, b);
                    tj();
                    break a;
                  }
                  k = Error(p(426));
                }
              } else if (I && h.mode & 1) {
                var J = Ui(g);
                if (null !== J) {
                  0 === (J.flags & 65536) && (J.flags |= 256);
                  Vi(J, g, h, f, b);
                  Jg(Ji(k, h));
                  break a;
                }
              }
              f = k = Ji(k, h);
              4 !== T && (T = 2);
              null === sk ? sk = [f] : sk.push(f);
              f = g;
              do {
                switch (f.tag) {
                  case 3:
                    f.flags |= 65536;
                    b &= -b;
                    f.lanes |= b;
                    var x = Ni(f, k, b);
                    ph(f, x);
                    break a;
                  case 1:
                    h = k;
                    var w = f.type, u = f.stateNode;
                    if (0 === (f.flags & 128) && ("function" === typeof w.getDerivedStateFromError || null !== u && "function" === typeof u.componentDidCatch && (null === Ri || !Ri.has(u)))) {
                      f.flags |= 65536;
                      b &= -b;
                      f.lanes |= b;
                      var F = Qi(f, h, b);
                      ph(f, F);
                      break a;
                    }
                }
                f = f.return;
              } while (null !== f);
            }
            Sk(c);
          } catch (na) {
            b = na;
            Y === c && null !== c && (Y = c = c.return);
            continue;
          }
          break;
        } while (1);
      }
      __name(Mk, "Mk");
      function Jk() {
        var a = mk.current;
        mk.current = Rh;
        return null === a ? Rh : a;
      }
      __name(Jk, "Jk");
      function tj() {
        if (0 === T || 3 === T || 2 === T) T = 4;
        null === Q || 0 === (rh & 268435455) && 0 === (qk & 268435455) || Ck(Q, Z);
      }
      __name(tj, "tj");
      function Ik(a, b) {
        var c = K;
        K |= 2;
        var d = Jk();
        if (Q !== a || Z !== b) uk = null, Kk(a, b);
        do
          try {
            Tk();
            break;
          } catch (e) {
            Mk(a, e);
          }
        while (1);
        $g();
        K = c;
        mk.current = d;
        if (null !== Y) throw Error(p(261));
        Q = null;
        Z = 0;
        return T;
      }
      __name(Ik, "Ik");
      function Tk() {
        for (; null !== Y; ) Uk(Y);
      }
      __name(Tk, "Tk");
      function Lk() {
        for (; null !== Y && !cc(); ) Uk(Y);
      }
      __name(Lk, "Lk");
      function Uk(a) {
        var b = Vk(a.alternate, a, fj);
        a.memoizedProps = a.pendingProps;
        null === b ? Sk(a) : Y = b;
        nk.current = null;
      }
      __name(Uk, "Uk");
      function Sk(a) {
        var b = a;
        do {
          var c = b.alternate;
          a = b.return;
          if (0 === (b.flags & 32768)) {
            if (c = Ej(c, b, fj), null !== c) {
              Y = c;
              return;
            }
          } else {
            c = Ij(c, b);
            if (null !== c) {
              c.flags &= 32767;
              Y = c;
              return;
            }
            if (null !== a) a.flags |= 32768, a.subtreeFlags = 0, a.deletions = null;
            else {
              T = 6;
              Y = null;
              return;
            }
          }
          b = b.sibling;
          if (null !== b) {
            Y = b;
            return;
          }
          Y = b = a;
        } while (null !== b);
        0 === T && (T = 5);
      }
      __name(Sk, "Sk");
      function Pk(a, b, c) {
        var d = C, e = ok.transition;
        try {
          ok.transition = null, C = 1, Wk(a, b, c, d);
        } finally {
          ok.transition = e, C = d;
        }
        return null;
      }
      __name(Pk, "Pk");
      function Wk(a, b, c, d) {
        do
          Hk();
        while (null !== wk);
        if (0 !== (K & 6)) throw Error(p(327));
        c = a.finishedWork;
        var e = a.finishedLanes;
        if (null === c) return null;
        a.finishedWork = null;
        a.finishedLanes = 0;
        if (c === a.current) throw Error(p(177));
        a.callbackNode = null;
        a.callbackPriority = 0;
        var f = c.lanes | c.childLanes;
        Bc(a, f);
        a === Q && (Y = Q = null, Z = 0);
        0 === (c.subtreeFlags & 2064) && 0 === (c.flags & 2064) || vk || (vk = true, Fk(hc, function() {
          Hk();
          return null;
        }));
        f = 0 !== (c.flags & 15990);
        if (0 !== (c.subtreeFlags & 15990) || f) {
          f = ok.transition;
          ok.transition = null;
          var g = C;
          C = 1;
          var h = K;
          K |= 4;
          nk.current = null;
          Oj(a, c);
          dk(c, a);
          Oe(Df);
          dd = !!Cf;
          Df = Cf = null;
          a.current = c;
          hk(c, a, e);
          dc();
          K = h;
          C = g;
          ok.transition = f;
        } else a.current = c;
        vk && (vk = false, wk = a, xk = e);
        f = a.pendingLanes;
        0 === f && (Ri = null);
        mc(c.stateNode, d);
        Dk(a, B());
        if (null !== b) for (d = a.onRecoverableError, c = 0; c < b.length; c++) e = b[c], d(e.value, { componentStack: e.stack, digest: e.digest });
        if (Oi) throw Oi = false, a = Pi, Pi = null, a;
        0 !== (xk & 1) && 0 !== a.tag && Hk();
        f = a.pendingLanes;
        0 !== (f & 1) ? a === zk ? yk++ : (yk = 0, zk = a) : yk = 0;
        jg();
        return null;
      }
      __name(Wk, "Wk");
      function Hk() {
        if (null !== wk) {
          var a = Dc(xk), b = ok.transition, c = C;
          try {
            ok.transition = null;
            C = 16 > a ? 16 : a;
            if (null === wk) var d = false;
            else {
              a = wk;
              wk = null;
              xk = 0;
              if (0 !== (K & 6)) throw Error(p(331));
              var e = K;
              K |= 4;
              for (V = a.current; null !== V; ) {
                var f = V, g = f.child;
                if (0 !== (V.flags & 16)) {
                  var h = f.deletions;
                  if (null !== h) {
                    for (var k = 0; k < h.length; k++) {
                      var l = h[k];
                      for (V = l; null !== V; ) {
                        var m = V;
                        switch (m.tag) {
                          case 0:
                          case 11:
                          case 15:
                            Pj(8, m, f);
                        }
                        var q = m.child;
                        if (null !== q) q.return = m, V = q;
                        else for (; null !== V; ) {
                          m = V;
                          var r = m.sibling, y = m.return;
                          Sj(m);
                          if (m === l) {
                            V = null;
                            break;
                          }
                          if (null !== r) {
                            r.return = y;
                            V = r;
                            break;
                          }
                          V = y;
                        }
                      }
                    }
                    var n = f.alternate;
                    if (null !== n) {
                      var t = n.child;
                      if (null !== t) {
                        n.child = null;
                        do {
                          var J = t.sibling;
                          t.sibling = null;
                          t = J;
                        } while (null !== t);
                      }
                    }
                    V = f;
                  }
                }
                if (0 !== (f.subtreeFlags & 2064) && null !== g) g.return = f, V = g;
                else b: for (; null !== V; ) {
                  f = V;
                  if (0 !== (f.flags & 2048)) switch (f.tag) {
                    case 0:
                    case 11:
                    case 15:
                      Pj(9, f, f.return);
                  }
                  var x = f.sibling;
                  if (null !== x) {
                    x.return = f.return;
                    V = x;
                    break b;
                  }
                  V = f.return;
                }
              }
              var w = a.current;
              for (V = w; null !== V; ) {
                g = V;
                var u = g.child;
                if (0 !== (g.subtreeFlags & 2064) && null !== u) u.return = g, V = u;
                else b: for (g = w; null !== V; ) {
                  h = V;
                  if (0 !== (h.flags & 2048)) try {
                    switch (h.tag) {
                      case 0:
                      case 11:
                      case 15:
                        Qj(9, h);
                    }
                  } catch (na) {
                    W(h, h.return, na);
                  }
                  if (h === g) {
                    V = null;
                    break b;
                  }
                  var F = h.sibling;
                  if (null !== F) {
                    F.return = h.return;
                    V = F;
                    break b;
                  }
                  V = h.return;
                }
              }
              K = e;
              jg();
              if (lc && "function" === typeof lc.onPostCommitFiberRoot) try {
                lc.onPostCommitFiberRoot(kc, a);
              } catch (na) {
              }
              d = true;
            }
            return d;
          } finally {
            C = c, ok.transition = b;
          }
        }
        return false;
      }
      __name(Hk, "Hk");
      function Xk(a, b, c) {
        b = Ji(c, b);
        b = Ni(a, b, 1);
        a = nh(a, b, 1);
        b = R();
        null !== a && (Ac(a, 1, b), Dk(a, b));
      }
      __name(Xk, "Xk");
      function W(a, b, c) {
        if (3 === a.tag) Xk(a, a, c);
        else for (; null !== b; ) {
          if (3 === b.tag) {
            Xk(b, a, c);
            break;
          } else if (1 === b.tag) {
            var d = b.stateNode;
            if ("function" === typeof b.type.getDerivedStateFromError || "function" === typeof d.componentDidCatch && (null === Ri || !Ri.has(d))) {
              a = Ji(c, a);
              a = Qi(b, a, 1);
              b = nh(b, a, 1);
              a = R();
              null !== b && (Ac(b, 1, a), Dk(b, a));
              break;
            }
          }
          b = b.return;
        }
      }
      __name(W, "W");
      function Ti(a, b, c) {
        var d = a.pingCache;
        null !== d && d.delete(b);
        b = R();
        a.pingedLanes |= a.suspendedLanes & c;
        Q === a && (Z & c) === c && (4 === T || 3 === T && (Z & 130023424) === Z && 500 > B() - fk ? Kk(a, 0) : rk |= c);
        Dk(a, b);
      }
      __name(Ti, "Ti");
      function Yk(a, b) {
        0 === b && (0 === (a.mode & 1) ? b = 1 : (b = sc, sc <<= 1, 0 === (sc & 130023424) && (sc = 4194304)));
        var c = R();
        a = ih(a, b);
        null !== a && (Ac(a, b, c), Dk(a, c));
      }
      __name(Yk, "Yk");
      function uj(a) {
        var b = a.memoizedState, c = 0;
        null !== b && (c = b.retryLane);
        Yk(a, c);
      }
      __name(uj, "uj");
      function bk(a, b) {
        var c = 0;
        switch (a.tag) {
          case 13:
            var d = a.stateNode;
            var e = a.memoizedState;
            null !== e && (c = e.retryLane);
            break;
          case 19:
            d = a.stateNode;
            break;
          default:
            throw Error(p(314));
        }
        null !== d && d.delete(b);
        Yk(a, c);
      }
      __name(bk, "bk");
      var Vk;
      Vk = /* @__PURE__ */ __name(function(a, b, c) {
        if (null !== a) if (a.memoizedProps !== b.pendingProps || Wf.current) dh = true;
        else {
          if (0 === (a.lanes & c) && 0 === (b.flags & 128)) return dh = false, yj(a, b, c);
          dh = 0 !== (a.flags & 131072) ? true : false;
        }
        else dh = false, I && 0 !== (b.flags & 1048576) && ug(b, ng, b.index);
        b.lanes = 0;
        switch (b.tag) {
          case 2:
            var d = b.type;
            ij(a, b);
            a = b.pendingProps;
            var e = Yf(b, H.current);
            ch(b, c);
            e = Nh(null, b, d, a, e, c);
            var f = Sh();
            b.flags |= 1;
            "object" === typeof e && null !== e && "function" === typeof e.render && void 0 === e.$$typeof ? (b.tag = 1, b.memoizedState = null, b.updateQueue = null, Zf(d) ? (f = true, cg(b)) : f = false, b.memoizedState = null !== e.state && void 0 !== e.state ? e.state : null, kh(b), e.updater = Ei, b.stateNode = e, e._reactInternals = b, Ii(b, d, a, c), b = jj(null, b, d, true, f, c)) : (b.tag = 0, I && f && vg(b), Xi(null, b, e, c), b = b.child);
            return b;
          case 16:
            d = b.elementType;
            a: {
              ij(a, b);
              a = b.pendingProps;
              e = d._init;
              d = e(d._payload);
              b.type = d;
              e = b.tag = Zk(d);
              a = Ci(d, a);
              switch (e) {
                case 0:
                  b = cj(null, b, d, a, c);
                  break a;
                case 1:
                  b = hj(null, b, d, a, c);
                  break a;
                case 11:
                  b = Yi(null, b, d, a, c);
                  break a;
                case 14:
                  b = $i(null, b, d, Ci(d.type, a), c);
                  break a;
              }
              throw Error(p(
                306,
                d,
                ""
              ));
            }
            return b;
          case 0:
            return d = b.type, e = b.pendingProps, e = b.elementType === d ? e : Ci(d, e), cj(a, b, d, e, c);
          case 1:
            return d = b.type, e = b.pendingProps, e = b.elementType === d ? e : Ci(d, e), hj(a, b, d, e, c);
          case 3:
            a: {
              kj(b);
              if (null === a) throw Error(p(387));
              d = b.pendingProps;
              f = b.memoizedState;
              e = f.element;
              lh(a, b);
              qh(b, d, null, c);
              var g = b.memoizedState;
              d = g.element;
              if (f.isDehydrated) if (f = { element: d, isDehydrated: false, cache: g.cache, pendingSuspenseBoundaries: g.pendingSuspenseBoundaries, transitions: g.transitions }, b.updateQueue.baseState = f, b.memoizedState = f, b.flags & 256) {
                e = Ji(Error(p(423)), b);
                b = lj(a, b, d, c, e);
                break a;
              } else if (d !== e) {
                e = Ji(Error(p(424)), b);
                b = lj(a, b, d, c, e);
                break a;
              } else for (yg = Lf(b.stateNode.containerInfo.firstChild), xg = b, I = true, zg = null, c = Vg(b, null, d, c), b.child = c; c; ) c.flags = c.flags & -3 | 4096, c = c.sibling;
              else {
                Ig();
                if (d === e) {
                  b = Zi(a, b, c);
                  break a;
                }
                Xi(a, b, d, c);
              }
              b = b.child;
            }
            return b;
          case 5:
            return Ah(b), null === a && Eg(b), d = b.type, e = b.pendingProps, f = null !== a ? a.memoizedProps : null, g = e.children, Ef(d, e) ? g = null : null !== f && Ef(d, f) && (b.flags |= 32), gj(a, b), Xi(a, b, g, c), b.child;
          case 6:
            return null === a && Eg(b), null;
          case 13:
            return oj(a, b, c);
          case 4:
            return yh(b, b.stateNode.containerInfo), d = b.pendingProps, null === a ? b.child = Ug(b, null, d, c) : Xi(a, b, d, c), b.child;
          case 11:
            return d = b.type, e = b.pendingProps, e = b.elementType === d ? e : Ci(d, e), Yi(a, b, d, e, c);
          case 7:
            return Xi(a, b, b.pendingProps, c), b.child;
          case 8:
            return Xi(a, b, b.pendingProps.children, c), b.child;
          case 12:
            return Xi(a, b, b.pendingProps.children, c), b.child;
          case 10:
            a: {
              d = b.type._context;
              e = b.pendingProps;
              f = b.memoizedProps;
              g = e.value;
              G(Wg, d._currentValue);
              d._currentValue = g;
              if (null !== f) if (He(f.value, g)) {
                if (f.children === e.children && !Wf.current) {
                  b = Zi(a, b, c);
                  break a;
                }
              } else for (f = b.child, null !== f && (f.return = b); null !== f; ) {
                var h = f.dependencies;
                if (null !== h) {
                  g = f.child;
                  for (var k = h.firstContext; null !== k; ) {
                    if (k.context === d) {
                      if (1 === f.tag) {
                        k = mh(-1, c & -c);
                        k.tag = 2;
                        var l = f.updateQueue;
                        if (null !== l) {
                          l = l.shared;
                          var m = l.pending;
                          null === m ? k.next = k : (k.next = m.next, m.next = k);
                          l.pending = k;
                        }
                      }
                      f.lanes |= c;
                      k = f.alternate;
                      null !== k && (k.lanes |= c);
                      bh(
                        f.return,
                        c,
                        b
                      );
                      h.lanes |= c;
                      break;
                    }
                    k = k.next;
                  }
                } else if (10 === f.tag) g = f.type === b.type ? null : f.child;
                else if (18 === f.tag) {
                  g = f.return;
                  if (null === g) throw Error(p(341));
                  g.lanes |= c;
                  h = g.alternate;
                  null !== h && (h.lanes |= c);
                  bh(g, c, b);
                  g = f.sibling;
                } else g = f.child;
                if (null !== g) g.return = f;
                else for (g = f; null !== g; ) {
                  if (g === b) {
                    g = null;
                    break;
                  }
                  f = g.sibling;
                  if (null !== f) {
                    f.return = g.return;
                    g = f;
                    break;
                  }
                  g = g.return;
                }
                f = g;
              }
              Xi(a, b, e.children, c);
              b = b.child;
            }
            return b;
          case 9:
            return e = b.type, d = b.pendingProps.children, ch(b, c), e = eh(e), d = d(e), b.flags |= 1, Xi(a, b, d, c), b.child;
          case 14:
            return d = b.type, e = Ci(d, b.pendingProps), e = Ci(d.type, e), $i(a, b, d, e, c);
          case 15:
            return bj(a, b, b.type, b.pendingProps, c);
          case 17:
            return d = b.type, e = b.pendingProps, e = b.elementType === d ? e : Ci(d, e), ij(a, b), b.tag = 1, Zf(d) ? (a = true, cg(b)) : a = false, ch(b, c), Gi(b, d, e), Ii(b, d, e, c), jj(null, b, d, true, a, c);
          case 19:
            return xj(a, b, c);
          case 22:
            return dj(a, b, c);
        }
        throw Error(p(156, b.tag));
      }, "Vk");
      function Fk(a, b) {
        return ac(a, b);
      }
      __name(Fk, "Fk");
      function $k(a, b, c, d) {
        this.tag = a;
        this.key = c;
        this.sibling = this.child = this.return = this.stateNode = this.type = this.elementType = null;
        this.index = 0;
        this.ref = null;
        this.pendingProps = b;
        this.dependencies = this.memoizedState = this.updateQueue = this.memoizedProps = null;
        this.mode = d;
        this.subtreeFlags = this.flags = 0;
        this.deletions = null;
        this.childLanes = this.lanes = 0;
        this.alternate = null;
      }
      __name($k, "$k");
      function Bg(a, b, c, d) {
        return new $k(a, b, c, d);
      }
      __name(Bg, "Bg");
      function aj(a) {
        a = a.prototype;
        return !(!a || !a.isReactComponent);
      }
      __name(aj, "aj");
      function Zk(a) {
        if ("function" === typeof a) return aj(a) ? 1 : 0;
        if (void 0 !== a && null !== a) {
          a = a.$$typeof;
          if (a === Da) return 11;
          if (a === Ga) return 14;
        }
        return 2;
      }
      __name(Zk, "Zk");
      function Pg(a, b) {
        var c = a.alternate;
        null === c ? (c = Bg(a.tag, b, a.key, a.mode), c.elementType = a.elementType, c.type = a.type, c.stateNode = a.stateNode, c.alternate = a, a.alternate = c) : (c.pendingProps = b, c.type = a.type, c.flags = 0, c.subtreeFlags = 0, c.deletions = null);
        c.flags = a.flags & 14680064;
        c.childLanes = a.childLanes;
        c.lanes = a.lanes;
        c.child = a.child;
        c.memoizedProps = a.memoizedProps;
        c.memoizedState = a.memoizedState;
        c.updateQueue = a.updateQueue;
        b = a.dependencies;
        c.dependencies = null === b ? null : { lanes: b.lanes, firstContext: b.firstContext };
        c.sibling = a.sibling;
        c.index = a.index;
        c.ref = a.ref;
        return c;
      }
      __name(Pg, "Pg");
      function Rg(a, b, c, d, e, f) {
        var g = 2;
        d = a;
        if ("function" === typeof a) aj(a) && (g = 1);
        else if ("string" === typeof a) g = 5;
        else a: switch (a) {
          case ya:
            return Tg(c.children, e, f, b);
          case za:
            g = 8;
            e |= 8;
            break;
          case Aa:
            return a = Bg(12, c, b, e | 2), a.elementType = Aa, a.lanes = f, a;
          case Ea:
            return a = Bg(13, c, b, e), a.elementType = Ea, a.lanes = f, a;
          case Fa:
            return a = Bg(19, c, b, e), a.elementType = Fa, a.lanes = f, a;
          case Ia:
            return pj(c, e, f, b);
          default:
            if ("object" === typeof a && null !== a) switch (a.$$typeof) {
              case Ba:
                g = 10;
                break a;
              case Ca:
                g = 9;
                break a;
              case Da:
                g = 11;
                break a;
              case Ga:
                g = 14;
                break a;
              case Ha:
                g = 16;
                d = null;
                break a;
            }
            throw Error(p(130, null == a ? a : typeof a, ""));
        }
        b = Bg(g, c, b, e);
        b.elementType = a;
        b.type = d;
        b.lanes = f;
        return b;
      }
      __name(Rg, "Rg");
      function Tg(a, b, c, d) {
        a = Bg(7, a, d, b);
        a.lanes = c;
        return a;
      }
      __name(Tg, "Tg");
      function pj(a, b, c, d) {
        a = Bg(22, a, d, b);
        a.elementType = Ia;
        a.lanes = c;
        a.stateNode = { isHidden: false };
        return a;
      }
      __name(pj, "pj");
      function Qg(a, b, c) {
        a = Bg(6, a, null, b);
        a.lanes = c;
        return a;
      }
      __name(Qg, "Qg");
      function Sg(a, b, c) {
        b = Bg(4, null !== a.children ? a.children : [], a.key, b);
        b.lanes = c;
        b.stateNode = { containerInfo: a.containerInfo, pendingChildren: null, implementation: a.implementation };
        return b;
      }
      __name(Sg, "Sg");
      function al(a, b, c, d, e) {
        this.tag = b;
        this.containerInfo = a;
        this.finishedWork = this.pingCache = this.current = this.pendingChildren = null;
        this.timeoutHandle = -1;
        this.callbackNode = this.pendingContext = this.context = null;
        this.callbackPriority = 0;
        this.eventTimes = zc(0);
        this.expirationTimes = zc(-1);
        this.entangledLanes = this.finishedLanes = this.mutableReadLanes = this.expiredLanes = this.pingedLanes = this.suspendedLanes = this.pendingLanes = 0;
        this.entanglements = zc(0);
        this.identifierPrefix = d;
        this.onRecoverableError = e;
        this.mutableSourceEagerHydrationData = null;
      }
      __name(al, "al");
      function bl(a, b, c, d, e, f, g, h, k) {
        a = new al(a, b, c, h, k);
        1 === b ? (b = 1, true === f && (b |= 8)) : b = 0;
        f = Bg(3, null, null, b);
        a.current = f;
        f.stateNode = a;
        f.memoizedState = { element: d, isDehydrated: c, cache: null, transitions: null, pendingSuspenseBoundaries: null };
        kh(f);
        return a;
      }
      __name(bl, "bl");
      function cl(a, b, c) {
        var d = 3 < arguments.length && void 0 !== arguments[3] ? arguments[3] : null;
        return { $$typeof: wa, key: null == d ? null : "" + d, children: a, containerInfo: b, implementation: c };
      }
      __name(cl, "cl");
      function dl(a) {
        if (!a) return Vf;
        a = a._reactInternals;
        a: {
          if (Vb(a) !== a || 1 !== a.tag) throw Error(p(170));
          var b = a;
          do {
            switch (b.tag) {
              case 3:
                b = b.stateNode.context;
                break a;
              case 1:
                if (Zf(b.type)) {
                  b = b.stateNode.__reactInternalMemoizedMergedChildContext;
                  break a;
                }
            }
            b = b.return;
          } while (null !== b);
          throw Error(p(171));
        }
        if (1 === a.tag) {
          var c = a.type;
          if (Zf(c)) return bg(a, c, b);
        }
        return b;
      }
      __name(dl, "dl");
      function el(a, b, c, d, e, f, g, h, k) {
        a = bl(c, d, true, a, e, f, g, h, k);
        a.context = dl(null);
        c = a.current;
        d = R();
        e = yi(c);
        f = mh(d, e);
        f.callback = void 0 !== b && null !== b ? b : null;
        nh(c, f, e);
        a.current.lanes = e;
        Ac(a, e, d);
        Dk(a, d);
        return a;
      }
      __name(el, "el");
      function fl(a, b, c, d) {
        var e = b.current, f = R(), g = yi(e);
        c = dl(c);
        null === b.context ? b.context = c : b.pendingContext = c;
        b = mh(f, g);
        b.payload = { element: a };
        d = void 0 === d ? null : d;
        null !== d && (b.callback = d);
        a = nh(e, b, g);
        null !== a && (gi(a, e, g, f), oh(a, e, g));
        return g;
      }
      __name(fl, "fl");
      function gl(a) {
        a = a.current;
        if (!a.child) return null;
        switch (a.child.tag) {
          case 5:
            return a.child.stateNode;
          default:
            return a.child.stateNode;
        }
      }
      __name(gl, "gl");
      function hl(a, b) {
        a = a.memoizedState;
        if (null !== a && null !== a.dehydrated) {
          var c = a.retryLane;
          a.retryLane = 0 !== c && c < b ? c : b;
        }
      }
      __name(hl, "hl");
      function il(a, b) {
        hl(a, b);
        (a = a.alternate) && hl(a, b);
      }
      __name(il, "il");
      function jl() {
        return null;
      }
      __name(jl, "jl");
      var kl = "function" === typeof reportError ? reportError : function(a) {
        console.error(a);
      };
      function ll(a) {
        this._internalRoot = a;
      }
      __name(ll, "ll");
      ml.prototype.render = ll.prototype.render = function(a) {
        var b = this._internalRoot;
        if (null === b) throw Error(p(409));
        fl(a, b, null, null);
      };
      ml.prototype.unmount = ll.prototype.unmount = function() {
        var a = this._internalRoot;
        if (null !== a) {
          this._internalRoot = null;
          var b = a.containerInfo;
          Rk(function() {
            fl(null, a, null, null);
          });
          b[uf] = null;
        }
      };
      function ml(a) {
        this._internalRoot = a;
      }
      __name(ml, "ml");
      ml.prototype.unstable_scheduleHydration = function(a) {
        if (a) {
          var b = Hc();
          a = { blockedOn: null, target: a, priority: b };
          for (var c = 0; c < Qc.length && 0 !== b && b < Qc[c].priority; c++) ;
          Qc.splice(c, 0, a);
          0 === c && Vc(a);
        }
      };
      function nl(a) {
        return !(!a || 1 !== a.nodeType && 9 !== a.nodeType && 11 !== a.nodeType);
      }
      __name(nl, "nl");
      function ol(a) {
        return !(!a || 1 !== a.nodeType && 9 !== a.nodeType && 11 !== a.nodeType && (8 !== a.nodeType || " react-mount-point-unstable " !== a.nodeValue));
      }
      __name(ol, "ol");
      function pl() {
      }
      __name(pl, "pl");
      function ql(a, b, c, d, e) {
        if (e) {
          if ("function" === typeof d) {
            var f = d;
            d = /* @__PURE__ */ __name(function() {
              var a2 = gl(g);
              f.call(a2);
            }, "d");
          }
          var g = el(b, d, a, 0, null, false, false, "", pl);
          a._reactRootContainer = g;
          a[uf] = g.current;
          sf(8 === a.nodeType ? a.parentNode : a);
          Rk();
          return g;
        }
        for (; e = a.lastChild; ) a.removeChild(e);
        if ("function" === typeof d) {
          var h = d;
          d = /* @__PURE__ */ __name(function() {
            var a2 = gl(k);
            h.call(a2);
          }, "d");
        }
        var k = bl(a, 0, false, null, null, false, false, "", pl);
        a._reactRootContainer = k;
        a[uf] = k.current;
        sf(8 === a.nodeType ? a.parentNode : a);
        Rk(function() {
          fl(b, k, c, d);
        });
        return k;
      }
      __name(ql, "ql");
      function rl(a, b, c, d, e) {
        var f = c._reactRootContainer;
        if (f) {
          var g = f;
          if ("function" === typeof e) {
            var h = e;
            e = /* @__PURE__ */ __name(function() {
              var a2 = gl(g);
              h.call(a2);
            }, "e");
          }
          fl(b, g, a, e);
        } else g = ql(c, b, a, e, d);
        return gl(g);
      }
      __name(rl, "rl");
      Ec = /* @__PURE__ */ __name(function(a) {
        switch (a.tag) {
          case 3:
            var b = a.stateNode;
            if (b.current.memoizedState.isDehydrated) {
              var c = tc(b.pendingLanes);
              0 !== c && (Cc(b, c | 1), Dk(b, B()), 0 === (K & 6) && (Gj = B() + 500, jg()));
            }
            break;
          case 13:
            Rk(function() {
              var b2 = ih(a, 1);
              if (null !== b2) {
                var c2 = R();
                gi(b2, a, 1, c2);
              }
            }), il(a, 1);
        }
      }, "Ec");
      Fc = /* @__PURE__ */ __name(function(a) {
        if (13 === a.tag) {
          var b = ih(a, 134217728);
          if (null !== b) {
            var c = R();
            gi(b, a, 134217728, c);
          }
          il(a, 134217728);
        }
      }, "Fc");
      Gc = /* @__PURE__ */ __name(function(a) {
        if (13 === a.tag) {
          var b = yi(a), c = ih(a, b);
          if (null !== c) {
            var d = R();
            gi(c, a, b, d);
          }
          il(a, b);
        }
      }, "Gc");
      Hc = /* @__PURE__ */ __name(function() {
        return C;
      }, "Hc");
      Ic = /* @__PURE__ */ __name(function(a, b) {
        var c = C;
        try {
          return C = a, b();
        } finally {
          C = c;
        }
      }, "Ic");
      yb = /* @__PURE__ */ __name(function(a, b, c) {
        switch (b) {
          case "input":
            bb(a, c);
            b = c.name;
            if ("radio" === c.type && null != b) {
              for (c = a; c.parentNode; ) c = c.parentNode;
              c = c.querySelectorAll("input[name=" + JSON.stringify("" + b) + '][type="radio"]');
              for (b = 0; b < c.length; b++) {
                var d = c[b];
                if (d !== a && d.form === a.form) {
                  var e = Db(d);
                  if (!e) throw Error(p(90));
                  Wa(d);
                  bb(d, e);
                }
              }
            }
            break;
          case "textarea":
            ib(a, c);
            break;
          case "select":
            b = c.value, null != b && fb(a, !!c.multiple, b, false);
        }
      }, "yb");
      Gb = Qk;
      Hb = Rk;
      var sl = { usingClientEntryPoint: false, Events: [Cb, ue, Db, Eb, Fb, Qk] };
      var tl = { findFiberByHostInstance: Wc, bundleType: 0, version: "18.3.1", rendererPackageName: "react-dom" };
      var ul = { bundleType: tl.bundleType, version: tl.version, rendererPackageName: tl.rendererPackageName, rendererConfig: tl.rendererConfig, overrideHookState: null, overrideHookStateDeletePath: null, overrideHookStateRenamePath: null, overrideProps: null, overridePropsDeletePath: null, overridePropsRenamePath: null, setErrorHandler: null, setSuspenseHandler: null, scheduleUpdate: null, currentDispatcherRef: ua.ReactCurrentDispatcher, findHostInstanceByFiber: /* @__PURE__ */ __name(function(a) {
        a = Zb(a);
        return null === a ? null : a.stateNode;
      }, "findHostInstanceByFiber"), findFiberByHostInstance: tl.findFiberByHostInstance || jl, findHostInstancesForRefresh: null, scheduleRefresh: null, scheduleRoot: null, setRefreshHandler: null, getCurrentFiber: null, reconcilerVersion: "18.3.1-next-f1338f8080-20240426" };
      if ("undefined" !== typeof __REACT_DEVTOOLS_GLOBAL_HOOK__) {
        vl = __REACT_DEVTOOLS_GLOBAL_HOOK__;
        if (!vl.isDisabled && vl.supportsFiber) try {
          kc = vl.inject(ul), lc = vl;
        } catch (a) {
        }
      }
      var vl;
      exports.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED = sl;
      exports.createPortal = function(a, b) {
        var c = 2 < arguments.length && void 0 !== arguments[2] ? arguments[2] : null;
        if (!nl(b)) throw Error(p(200));
        return cl(a, b, null, c);
      };
      exports.createRoot = function(a, b) {
        if (!nl(a)) throw Error(p(299));
        var c = false, d = "", e = kl;
        null !== b && void 0 !== b && (true === b.unstable_strictMode && (c = true), void 0 !== b.identifierPrefix && (d = b.identifierPrefix), void 0 !== b.onRecoverableError && (e = b.onRecoverableError));
        b = bl(a, 1, false, null, null, c, false, d, e);
        a[uf] = b.current;
        sf(8 === a.nodeType ? a.parentNode : a);
        return new ll(b);
      };
      exports.findDOMNode = function(a) {
        if (null == a) return null;
        if (1 === a.nodeType) return a;
        var b = a._reactInternals;
        if (void 0 === b) {
          if ("function" === typeof a.render) throw Error(p(188));
          a = Object.keys(a).join(",");
          throw Error(p(268, a));
        }
        a = Zb(b);
        a = null === a ? null : a.stateNode;
        return a;
      };
      exports.flushSync = function(a) {
        return Rk(a);
      };
      exports.hydrate = function(a, b, c) {
        if (!ol(b)) throw Error(p(200));
        return rl(null, a, b, true, c);
      };
      exports.hydrateRoot = function(a, b, c) {
        if (!nl(a)) throw Error(p(405));
        var d = null != c && c.hydratedSources || null, e = false, f = "", g = kl;
        null !== c && void 0 !== c && (true === c.unstable_strictMode && (e = true), void 0 !== c.identifierPrefix && (f = c.identifierPrefix), void 0 !== c.onRecoverableError && (g = c.onRecoverableError));
        b = el(b, null, a, 1, null != c ? c : null, e, false, f, g);
        a[uf] = b.current;
        sf(a);
        if (d) for (a = 0; a < d.length; a++) c = d[a], e = c._getVersion, e = e(c._source), null == b.mutableSourceEagerHydrationData ? b.mutableSourceEagerHydrationData = [c, e] : b.mutableSourceEagerHydrationData.push(
          c,
          e
        );
        return new ml(b);
      };
      exports.render = function(a, b, c) {
        if (!ol(b)) throw Error(p(200));
        return rl(null, a, b, false, c);
      };
      exports.unmountComponentAtNode = function(a) {
        if (!ol(a)) throw Error(p(40));
        return a._reactRootContainer ? (Rk(function() {
          rl(null, null, a, false, function() {
            a._reactRootContainer = null;
            a[uf] = null;
          });
        }), true) : false;
      };
      exports.unstable_batchedUpdates = Qk;
      exports.unstable_renderSubtreeIntoContainer = function(a, b, c, d) {
        if (!ol(c)) throw Error(p(200));
        if (null == a || void 0 === a._reactInternals) throw Error(p(38));
        return rl(a, b, c, false, d);
      };
      exports.version = "18.3.1-next-f1338f8080-20240426";
    }
  });

  // node_modules/react-dom/index.js
  var require_react_dom = __commonJS({
    "node_modules/react-dom/index.js"(exports, module) {
      "use strict";
      function checkDCE() {
        if (typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ === "undefined" || typeof __REACT_DEVTOOLS_GLOBAL_HOOK__.checkDCE !== "function") {
          return;
        }
        if (false) {
          throw new Error("^_^");
        }
        try {
          __REACT_DEVTOOLS_GLOBAL_HOOK__.checkDCE(checkDCE);
        } catch (err) {
          console.error(err);
        }
      }
      __name(checkDCE, "checkDCE");
      if (true) {
        checkDCE();
        module.exports = require_react_dom_production_min();
      } else {
        module.exports = null;
      }
    }
  });

  // node_modules/react-dom/client.js
  var require_client = __commonJS({
    "node_modules/react-dom/client.js"(exports) {
      "use strict";
      var m = require_react_dom();
      if (true) {
        exports.createRoot = m.createRoot;
        exports.hydrateRoot = m.hydrateRoot;
      } else {
        i = m.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
        exports.createRoot = function(c, o) {
          i.usingClientEntryPoint = true;
          try {
            return m.createRoot(c, o);
          } finally {
            i.usingClientEntryPoint = false;
          }
        };
        exports.hydrateRoot = function(c, h, o) {
          i.usingClientEntryPoint = true;
          try {
            return m.hydrateRoot(c, h, o);
          } finally {
            i.usingClientEntryPoint = false;
          }
        };
      }
      var i;
    }
  });

  // public/src/stores/CalcType.js
  var require_CalcType = __commonJS({
    "public/src/stores/CalcType.js"(exports, module) {
      var CalcType8 = Object.freeze({
        // User's own calcs (localStorage + Firebase sync)
        USER: "user",
        // Remote collaborative calcs (session memory + Firebase sync)
        WIKI: "wiki",
        // instacalc.com/slug - curated gallery calc, API fetch
        LIVE: "live",
        // instacalc.com/slug - (deprecated, redirects to /slug)
        COLLAB: "collab",
        // instacalc.com/edit/TOKEN - private link
        // File-synced calcs (localStorage + file sync)
        FILE: "file",
        // Syncs to local .ic file via File System Access API
        // Ephemeral calcs (session memory only, no sync)
        URL_DEFINED: "url_defined",
        // Calc content defined by URL hash (#50+100)
        SHARED_VIEW: "shared_view",
        // Viewing someone's published calc (read-only session copy)
        /**
         * Derive CalcType from syncRef (syncRef is the source of truth)
         * @param {object} calc - Calc object with syncRef
         * @returns {string} - CalcType value
         */
        fromSyncRef(calc) {
          if (!calc) return CalcType8.USER;
          if (!calc.syncRef) {
            if (calc.ephemeral || calc.isUrlCalc) return CalcType8.URL_DEFINED;
            if (calc.isSharedView) return CalcType8.SHARED_VIEW;
            return CalcType8.USER;
          }
          if (calc.syncRef.type === "file") {
            return CalcType8.FILE;
          }
          if (calc.syncRef.type === "firebase") {
            const path = calc.syncRef.path || "";
            if (path.startsWith("wiki/") && !path.startsWith("wiki/live/")) {
              return CalcType8.WIKI;
            }
            if (path.startsWith("live/") || path.startsWith("wiki/live/")) {
              return CalcType8.LIVE;
            }
            if (path.startsWith("collab/")) {
              return CalcType8.COLLAB;
            }
          }
          return CalcType8.USER;
        },
        /**
         * Get storage location for a calc type
         * @param {string} type - CalcType value
         * @returns {'localStorage' | 'session' | 'collab'}
         */
        getStorage(type) {
          switch (type) {
            case CalcType8.USER:
            case CalcType8.FILE:
              return "localStorage";
            case CalcType8.COLLAB:
              return "collab";
            case CalcType8.WIKI:
            case CalcType8.LIVE:
            case CalcType8.URL_DEFINED:
            case CalcType8.SHARED_VIEW:
              return "session";
            default:
              return "session";
          }
        },
        /**
         * Check if type should sync to Firebase
         * @param {string} type - CalcType value
         * @returns {boolean}
         */
        shouldSyncToFirebase(type) {
          return [CalcType8.USER, CalcType8.WIKI, CalcType8.LIVE, CalcType8.COLLAB].includes(type);
        },
        /**
         * Check if calc syncs to Firebase (wiki, live, or collab)
         * @param {object} calc - Calc object with syncRef or legacy type
         * @returns {boolean}
         */
        isRemote(calc) {
          if (calc?.syncRef) {
            return calc.syncRef.type === "firebase";
          }
          if ([CalcType8.WIKI, CalcType8.LIVE, CalcType8.COLLAB].includes(calc?.type)) {
            return true;
          }
          return false;
        },
        /**
         * Check if calc is a private collab (shared via /edit/TOKEN link)
         * @param {object|string} calcOrType - Calc object or type string
         * @returns {boolean}
         */
        isCollab(calcOrType) {
          if (calcOrType && typeof calcOrType === "object") {
            if (calcOrType.syncRef) {
              return calcOrType.syncRef.path?.startsWith("collab/") ?? false;
            }
            return calcOrType.type === CalcType8.COLLAB;
          }
          return calcOrType === CalcType8.COLLAB;
        },
        /**
         * Check if calc is a wiki (public, curated)
         * @param {object|string} calcOrType - Calc object or type string
         * @returns {boolean}
         */
        isWiki(calcOrType) {
          if (calcOrType && typeof calcOrType === "object") {
            if (calcOrType.syncRef) {
              const path = calcOrType.syncRef.path;
              if (!path) return false;
              return path.startsWith("wiki/") && !path.startsWith("wiki/live/");
            }
            return calcOrType.type === CalcType8.WIKI;
          }
          return calcOrType === CalcType8.WIKI;
        },
        /**
         * Check if calc is a live collab
         * @param {object|string} calcOrType - Calc object or type string
         * @returns {boolean}
         */
        isLive(calcOrType) {
          if (calcOrType && typeof calcOrType === "object") {
            if (calcOrType.syncRef) {
              const path = calcOrType.syncRef.path;
              if (!path) return false;
              return path.startsWith("live/") || path.startsWith("wiki/live/");
            }
            return calcOrType.type === CalcType8.LIVE;
          }
          return calcOrType === CalcType8.LIVE;
        },
        /**
         * Check if calc uses /!slug URL format (wiki or live)
         * @param {object} calc - Calc object with syncRef
         * @returns {boolean}
         */
        isWikiOrLive(calc) {
          return CalcType8.isWiki(calc) || CalcType8.isLive(calc);
        },
        /**
         * Check if calc is local-only (no sync)
         * @param {object} calc - Calc object with syncRef
         * @returns {boolean}
         */
        isLocal(calc) {
          return !calc?.syncRef;
        },
        /**
         * Check if calc syncs to an external location
         * @param {object} calc - Calc object with syncRef
         * @returns {boolean}
         */
        isSynced(calc) {
          return !!calc?.syncRef;
        },
        /**
         * Check if calc syncs to local file
         * @param {object} calc - Calc object with syncRef
         * @returns {boolean}
         */
        isFile(calc) {
          return calc?.syncRef?.type === "file";
        },
        /**
         * Get the URL path for a calc - SINGLE SOURCE OF TRUTH
         * @param {Object} calc - Calc object with type, remoteId, published, id
         * @returns {string|null} - URL path or null if no URL
         *
         * URL Design (see specs/arch/url-design.md):
         * - Local calcs: null (use #my/<id> hash via UrlManager.localCalcHash())
         * - Shared calcs: /s/<id>
         * - Wiki/live: /<slug>
         * - Collab: /edit/<token>
         */
        getUrlPath(calc) {
          if (!calc) return null;
          if (CalcType8.isCollab(calc) && calc.remoteId) {
            return `/edit/${calc.remoteId}`;
          }
          if (CalcType8.isWikiOrLive(calc) && calc.remoteId) {
            return `/${calc.remoteId}`;
          }
          if (CalcType8.isUserOwned(calc.type)) {
            return null;
          }
          if (calc.published?.id) {
            return `/${calc.published.id}`;
          }
          if (calc.type === CalcType8.SHARED_VIEW) {
            const id = calc.sourceId || calc.remoteId;
            if (id) return `/${id}`;
          }
          if (CalcType8.isUrlDefined(calc.type)) {
            return null;
          }
          return null;
        },
        /**
         * Check if type is a URL-defined calc (content from URL hash)
         * @param {string} type - CalcType value
         * @returns {boolean}
         */
        isUrlDefined(type) {
          return type === CalcType8.URL_DEFINED;
        },
        /**
         * Check if type is ephemeral (no sync, session only)
         * @param {string} type - CalcType value
         * @returns {boolean}
         */
        isEphemeral(type) {
          return CalcType8.isUrlDefined(type) || type === CalcType8.SHARED_VIEW;
        },
        /**
         * Check if this is the user's own calc (not shared/remote)
         * @param {string} type - CalcType value
         * @returns {boolean}
         */
        isUserOwned(type) {
          return type === CalcType8.USER;
        },
        /**
         * Get display label for UI
         * @param {string} type - CalcType value
         * @returns {string}
         */
        getLabel(type) {
          const labels = {
            [CalcType8.USER]: "My Calc",
            [CalcType8.WIKI]: "Wiki",
            [CalcType8.LIVE]: "Live",
            [CalcType8.COLLAB]: "Private Link",
            [CalcType8.FILE]: "File Synced",
            [CalcType8.URL_DEFINED]: "Quick Calc",
            [CalcType8.SHARED_VIEW]: "Shared Calc"
          };
          return labels[type] || "Calc";
        },
        /**
         * Get all valid CalcType values
         * @returns {string[]}
         */
        values() {
          return [
            CalcType8.USER,
            CalcType8.WIKI,
            CalcType8.LIVE,
            CalcType8.COLLAB,
            CalcType8.FILE,
            CalcType8.URL_DEFINED,
            CalcType8.SHARED_VIEW
          ];
        },
        /**
         * Check if a value is a valid CalcType
         * @param {string} value
         * @returns {boolean}
         */
        isValid(value) {
          return CalcType8.values().includes(value);
        },
        // ==========================================================================
        // SYNCREF EXTRACTORS
        // ==========================================================================
        /**
         * Get the wiki slug from a calc
         * @param {object} calc - Calc object with syncRef
         * @returns {string|null}
         */
        getSlug(calc) {
          if (!CalcType8.isWiki(calc)) return null;
          return calc.syncRef.path.replace("wiki/", "");
        },
        /**
         * Get the live ID from a calc
         * @param {object} calc - Calc object with syncRef
         * @returns {string|null}
         */
        getLiveId(calc) {
          if (!CalcType8.isLive(calc)) return null;
          const path = calc.syncRef.path;
          if (path.startsWith("wiki/live/")) {
            return path.replace("wiki/live/", "");
          }
          return path.replace("live/", "");
        },
        /**
         * Get the collab token from a calc
         * @param {object} calc - Calc object with syncRef
         * @returns {string|null}
         */
        getCollabToken(calc) {
          if (!CalcType8.isCollab(calc)) return null;
          return calc.syncRef.path.replace("collab/", "");
        },
        /**
         * Get the filename from a file-synced calc
         * @param {object} calc - Calc object with syncRef
         * @returns {string|null}
         */
        getFilename(calc) {
          if (!CalcType8.isFile(calc)) return null;
          return calc.syncRef.path;
        },
        // ==========================================================================
        // SYNCREF CREATORS
        // ==========================================================================
        /**
         * Create a syncRef for wiki
         * @param {string} slug - The wiki slug
         * @returns {object}
         */
        syncRefForWiki(slug) {
          return { type: "firebase", path: `wiki/${slug}` };
        },
        /**
         * Create a syncRef for live collab
         * @param {string} id - The live ID
         * @returns {object}
         */
        syncRefForLive(id) {
          return { type: "firebase", path: `live/${id}` };
        },
        /**
         * Create a syncRef for private collab
         * @param {string} token - The collab token
         * @returns {object}
         */
        syncRefForCollab(token) {
          return { type: "firebase", path: `collab/${token}` };
        },
        /**
         * Create a syncRef for local file
         * @param {string} filename - The filename
         * @returns {object}
         */
        syncRefForFile(filename) {
          return { type: "file", path: filename };
        }
      });
      if (typeof module !== "undefined" && module.exports) {
        module.exports = CalcType8;
      }
      if (typeof window !== "undefined") {
        window.CalcType = CalcType8;
      }
    }
  });

  // public/src/utils/debug.js
  var require_debug = __commonJS({
    "public/src/utils/debug.js"(exports, module) {
      var Debug4 = typeof window !== "undefined" && window.Debug || {
        // Fallback for SSR/tests - minimal stub
        log: /* @__PURE__ */ __name(() => {
        }, "log"),
        warn: /* @__PURE__ */ __name(() => {
        }, "warn"),
        error: /* @__PURE__ */ __name((...args) => console.error(...args), "error"),
        group: /* @__PURE__ */ __name(() => {
        }, "group"),
        groupEnd: /* @__PURE__ */ __name(() => {
        }, "groupEnd"),
        time: /* @__PURE__ */ __name(() => {
        }, "time"),
        timeEnd: /* @__PURE__ */ __name(() => {
        }, "timeEnd"),
        isEnabled: /* @__PURE__ */ __name(() => false, "isEnabled"),
        toggle: /* @__PURE__ */ __name(() => false, "toggle"),
        enableAll: /* @__PURE__ */ __name(() => {
        }, "enableAll"),
        disableAll: /* @__PURE__ */ __name(() => {
        }, "disableAll"),
        categories: []
      };
      if (typeof module !== "undefined" && module.exports) {
        module.exports = Debug4;
      }
    }
  });

  // public/src/utils/DebugToolsStub.js
  var require_DebugToolsStub = __commonJS({
    "public/src/utils/DebugToolsStub.js"() {
      (function() {
        "use strict";
        if (typeof window !== "undefined" && window.DebugTools?._fullLoaded) {
          return;
        }
        const MAX_PENDING = 100;
        const pending = {
          events: [],
          mutations: [],
          errors: [],
          network: [],
          keys: []
        };
        function pushPending(arr, item) {
          arr.push(item);
          if (arr.length > MAX_PENDING) arr.shift();
        }
        __name(pushPending, "pushPending");
        const stubs = {
          _isStub: true,
          EventLogger: {
            logEvent(eventName, payload) {
              pushPending(pending.events, { time: Date.now(), event: eventName, payload });
            },
            getRecentEvents(n = 50) {
              return pending.events.slice(-n);
            },
            clear() {
              pending.events.length = 0;
            }
          },
          MutationHistory: {
            recordMutation(type, before, after) {
              pushPending(pending.mutations, {
                time: Date.now(),
                type,
                calcId: after?.id,
                before: before?.id,
                // Just store IDs in stub mode
                afterRowCount: after?.rows?.length
              });
            },
            getRecent(n = 20) {
              return pending.mutations.slice(-n);
            },
            getHistoryLength() {
              return 0;
            },
            getStateAt() {
              return null;
            },
            clear() {
              pending.mutations.length = 0;
            }
          },
          ErrorCapture: {
            captureError(error, componentStack, source) {
              pushPending(pending.errors, {
                time: Date.now(),
                message: error?.message || String(error),
                stack: error?.stack?.slice(0, 500),
                source
              });
            },
            getRecent(n = 10) {
              return pending.errors.slice(-n);
            },
            getCount() {
              return pending.errors.length;
            },
            clear() {
              pending.errors.length = 0;
            }
          },
          NetworkLogger: {
            logRequest(type, path, duration, success) {
              pushPending(pending.network, { time: Date.now(), type, path, duration, success });
            },
            getRecent(n = 20) {
              return pending.network.slice(-n);
            },
            clear() {
              pending.network.length = 0;
            }
          },
          KeyLogger: {
            logKey(key, handler, action) {
              pushPending(pending.keys, { time: Date.now(), key, handler, action });
            },
            getRecent(n = 30) {
              return pending.keys.slice(-n);
            },
            clear() {
              pending.keys.length = 0;
            }
          },
          SlowRenderTracker: {
            recordRender() {
            },
            // No-op in stub - perf tracking needs full implementation
            getSlowRenders() {
              return [];
            },
            getSlowRenderCount() {
              return 0;
            },
            clear() {
            }
          },
          SWStatus: {
            async getStatus() {
              return { stub: true };
            }
          },
          RowDebugger: {
            getRowDebugInfo() {
              return null;
            }
          },
          DebugSnapshot: {
            capture() {
              return {
                stub: true,
                message: "Full DebugTools not loaded yet. Open debug panel to load.",
                pendingEvents: pending.events.length,
                pendingMutations: pending.mutations.length,
                pendingErrors: pending.errors.length
              };
            },
            copyToClipboard() {
              return 0;
            },
            exportTestCase() {
              return "// DebugTools not loaded";
            }
          },
          // Get pending data (used by full implementation when it loads)
          _getPending() {
            return pending;
          },
          clearAll() {
            for (const arr of Object.values(pending)) {
              arr.length = 0;
            }
          }
        };
        if (typeof window !== "undefined") {
          window.DebugTools = stubs;
          window._debugToolsPending = pending;
          window.addEventListener("error", (event) => {
            stubs.ErrorCapture.captureError(event.error || event.message, null, "window.onerror");
          });
          window.addEventListener("unhandledrejection", (event) => {
            stubs.ErrorCapture.captureError(event.reason, null, "unhandledrejection");
          });
        }
      })();
    }
  });

  // public/src/utils/featureFlags.js
  function getFeatureFlags() {
    const configFlags = typeof window !== "undefined" ? window.IC_CONFIG?.features || {} : {};
    return { ...DEFAULT_FEATURE_FLAGS, ...configFlags };
  }
  function isFeatureEnabled(flagName, fallbackValue = false) {
    const flags = getFeatureFlags();
    if (Object.hasOwn(flags, flagName)) {
      return Boolean(flags[flagName]);
    }
    return Boolean(fallbackValue);
  }
  var DEFAULT_FEATURE_FLAGS;
  var init_featureFlags = __esm({
    "public/src/utils/featureFlags.js"() {
      DEFAULT_FEATURE_FLAGS = {
        gallery: false,
        goalseek: false,
        tour: true,
        privateShareLink: false,
        presentModeBeta: true,
        showPlaceholder: false,
        morphingShareBar: false,
        community: false
      };
      __name(getFeatureFlags, "getFeatureFlags");
      __name(isFeatureEnabled, "isFeatureEnabled");
    }
  });

  // functions/shared/rowViewModel.js
  var require_rowViewModel = __commonJS({
    "functions/shared/rowViewModel.js"(exports, module) {
      var RowType = {
        ROW: "row",
        // Normal row with input + result
        COMMENT: "comment",
        // Pure comment (// ...)
        COLLAPSED: "collapsed",
        // Hidden/collapsed parameter
        SECTION: "section",
        // Section header (# Title or @section)
        HERO: "hero",
        // Hero display (@hero)
        DIVIDER: "divider"
        // Visual divider (--- or //---)
      };
      function parseSectionDecorators2(titleText) {
        if (!titleText) return { cleanTitle: "", decorators: {} };
        const decorators = {};
        if (/\s@tab\b/i.test(titleText)) decorators.tab = true;
        if (/\s@collapsed\b/i.test(titleText)) decorators.collapsed = true;
        if (/\s@card\b/i.test(titleText)) decorators.card = true;
        const iconMatch = titleText.match(/\s@icon\s*\(([^)]+)\)/i);
        if (iconMatch) decorators.icon = iconMatch[1].trim().replace(/^["']|["']$/g, "");
        const layoutMatch = titleText.match(/\s@layout\s*\(([^)]+)\)/i);
        if (layoutMatch) decorators.layout = layoutMatch[1].trim().replace(/^["']|["']$/g, "");
        const colMatch = titleText.match(/\s@col\s*\(([^)]+)\)/i);
        if (colMatch) {
          const colVal = colMatch[1].trim();
          if (colVal === "full") {
            decorators.colSpan = 12;
            decorators.colTotal = 12;
          } else {
            const parts = colVal.split("/");
            if (parts.length === 2) {
              decorators.colSpan = parseInt(parts[0], 10);
              decorators.colTotal = parseInt(parts[1], 10);
            }
          }
        }
        const cleanTitle = titleText.replace(/\s@tab\b/ig, "").replace(/\s@collapsed\b/ig, "").replace(/\s@card\b/ig, "").replace(/\s@icon\s*\([^)]*\)/ig, "").replace(/\s@layout\s*\([^)]*\)/ig, "").replace(/\s@col\s*\([^)]*\)/ig, "").trim();
        return { cleanTitle, decorators };
      }
      __name(parseSectionDecorators2, "parseSectionDecorators");
      function detectAutoFormat3(text) {
        if (!text) return null;
        const trimmed = text.trim();
        if (trimmed.startsWith("# ") && !trimmed.includes("=")) {
          const { cleanTitle, decorators } = parseSectionDecorators2(trimmed.slice(2).trim());
          return {
            type: "section",
            title: cleanTitle,
            isSubsection: false,
            ...decorators
          };
        }
        if (trimmed.startsWith("## ") && !trimmed.includes("=")) {
          const { cleanTitle, decorators } = parseSectionDecorators2(trimmed.slice(3).trim());
          return {
            type: "section",
            title: cleanTitle,
            isSubsection: true,
            ...decorators
          };
        }
        if (trimmed.startsWith("//") && /^\/\/\s*-{3,}/.test(trimmed)) {
          return { type: "divider" };
        }
        if (/^-{3,}\s*$/.test(trimmed)) {
          return { type: "divider" };
        }
        if (/^={3,}\s*$/.test(trimmed)) {
          return { type: "divider" };
        }
        return null;
      }
      __name(detectAutoFormat3, "detectAutoFormat");
      function isParameterRow2(text) {
        if (!text) return false;
        const trimmed = text.trim();
        if (trimmed.startsWith("//")) return false;
        const match = trimmed.match(/^([^=]+)=([^=].*)?$/);
        if (!match) return false;
        const left = match[1].trim();
        if (!/^[\w_][\w\d_]*$/.test(left)) return false;
        return true;
      }
      __name(isParameterRow2, "isParameterRow");
      function getParameterName(text) {
        if (!text) return null;
        const trimmed = text.trim();
        if (trimmed.startsWith("//")) return null;
        const match = trimmed.match(/^([\w_][\w\d_]*)\s*=/);
        return match ? match[1] : null;
      }
      __name(getParameterName, "getParameterName");
      function getParameterLabel2(text) {
        const name = getParameterName(text) || text || "";
        return name.replace(/_/g, " ").trim();
      }
      __name(getParameterLabel2, "getParameterLabel");
      function deriveRowViewModel(row, result, index2, viewMode) {
        if (!row) return null;
        const text = row.text || "";
        const presentation = result?.presentation || {};
        const isHidden3 = !!row.hidden;
        const base = {
          index: index2,
          text,
          hidden: isHidden3
        };
        if (viewMode === "text") {
          return {
            ...base,
            type: RowType.ROW,
            result: text
          };
        }
        const autoFormat = detectAutoFormat3(text);
        const isExplicitSection = !!presentation.section;
        const isAutoSection = autoFormat?.type === "section";
        if (isExplicitSection || isAutoSection) {
          const sectionTitle = presentation.section || autoFormat?.title || text.replace(/^#+\s*/, "");
          return {
            ...base,
            type: RowType.SECTION,
            title: sectionTitle,
            isSubsection: autoFormat?.isSubsection || false,
            // Section decorators
            isTab: presentation.tab || autoFormat?.tab || false,
            isCollapsed: presentation.collapsed || autoFormat?.collapsed || false,
            isCard: presentation.card || autoFormat?.card || false,
            icon: presentation.icon || autoFormat?.icon || null,
            layout: presentation.layout || autoFormat?.layout || null,
            colSpan: presentation.colSpan || autoFormat?.colSpan || null,
            colTotal: presentation.colTotal || autoFormat?.colTotal || null
          };
        }
        if (autoFormat?.type === "divider") {
          return {
            ...base,
            type: RowType.DIVIDER
          };
        }
        const isPureComment = result?.isComment && !result?.display;
        if (isPureComment) {
          return {
            ...base,
            type: RowType.COMMENT,
            comment: result.comment || text.replace(/^\/\/\s*/, "")
          };
        }
        const isHero2 = presentation.hero || presentation.mode === "hero";
        if (isHero2) {
          return {
            ...base,
            type: RowType.HERO,
            label: result?.label || result?.comment || getParameterLabel2(text),
            result: result?.display || "",
            icon: presentation.icon || null
          };
        }
        if (presentation.mode === "hidden" && viewMode === "present") {
          return {
            ...base,
            type: RowType.ROW,
            skip: true
          };
        }
        if (presentation.mode === "advanced" && viewMode === "present") {
          return {
            ...base,
            type: RowType.ROW,
            isAdvanced: true,
            label: result?.label || getParameterLabel2(text),
            result: result?.display || "",
            isParam: isParameterRow2(text),
            skip: true
            // Hidden until section is expanded
          };
        }
        if (isHidden3 && viewMode === "calc") {
          return {
            ...base,
            type: RowType.COLLAPSED,
            label: result?.label || getParameterLabel2(text),
            result: result?.display || ""
          };
        }
        const isParam = isParameterRow2(text);
        return {
          ...base,
          type: RowType.ROW,
          label: result?.label || (isParam ? getParameterLabel2(text) : null),
          result: result?.display || "",
          comment: result?.comment || null,
          isParam,
          // Presentation hints for styling
          half: presentation.half || presentation.rowWidth === "1/2" || false,
          third: presentation.third || presentation.rowWidth === "1/3" || false,
          wide: presentation.wide || presentation.rowWidth === "full" || false,
          prefix: presentation.prefix || null,
          suffix: presentation.suffix || null
        };
      }
      __name(deriveRowViewModel, "deriveRowViewModel");
      function deriveViewModels2(rows, results, viewMode, options = {}) {
        const { includeSkipped = false, includeAdvanced = false } = options;
        const viewModels = rows.map((row, i) => {
          const result = results[i] || {};
          return deriveRowViewModel(row, result, i, viewMode);
        }).filter((vm) => vm !== null);
        if (includeSkipped && includeAdvanced) {
          return viewModels;
        }
        return viewModels.filter((vm) => {
          if (vm.skip && !includeSkipped) return false;
          if (vm.isAdvanced && !includeAdvanced) return false;
          return true;
        });
      }
      __name(deriveViewModels2, "deriveViewModels");
      function groupBySection2(viewModels, options = {}) {
        const { includeHiddenRows = false } = options;
        const sections = [];
        let currentSection = createDefaultSection();
        viewModels.forEach((vm) => {
          if (vm.type === RowType.SECTION) {
            if (hasContent2(currentSection)) {
              sections.push(currentSection);
            }
            const title = vm.title || "";
            currentSection = {
              id: `section_${vm.index}_${title.replace(/\s+/g, "_").toLowerCase()}`,
              sectionIndex: vm.index,
              title,
              isSubsection: vm.isSubsection || false,
              // Tab properties
              isTab: vm.isTab || false,
              tabLabel: vm.isTab ? title : null,
              tabIcon: vm.isTab ? vm.icon : null,
              // Collapse/expand
              isCollapsedByDefault: vm.isCollapsed || false,
              // Layout properties
              isSplit: false,
              // Can be promoted from child rows
              isCol2: false,
              // Can be promoted from child rows or auto-detected
              isCard: vm.isCard || false,
              icon: vm.icon || null,
              layout: vm.layout || null,
              colSpan: vm.colSpan || null,
              colTotal: vm.colTotal || null,
              // Row arrays
              rows: [],
              advancedRows: []
            };
            return;
          }
          if (vm.type === RowType.DIVIDER) {
            return;
          }
          if (vm.half || vm.third) {
          }
          if (vm.wide && !currentSection.isSplit) {
          }
          if (vm.isAdvanced) {
            currentSection.advancedRows.push(vm);
          } else if (!vm.skip || includeHiddenRows) {
            currentSection.rows.push(vm);
          }
        });
        if (hasContent2(currentSection) || currentSection.sectionIndex !== null) {
          sections.push(currentSection);
        }
        sections.forEach((section) => {
          if (section.isCol2 || section.isSplit || section.layout) return;
          const allRows = [...section.rows, ...section.advancedRows];
          if (allRows.length === 0) return;
          const paramCount = allRows.filter((vm) => vm.isParam).length;
          if (paramCount >= 2) {
            section.isCol2 = true;
          }
        });
        return sections;
      }
      __name(groupBySection2, "groupBySection");
      function createDefaultSection() {
        return {
          id: "__default__",
          sectionIndex: null,
          title: null,
          isSubsection: false,
          isTab: false,
          tabLabel: null,
          tabIcon: null,
          isCollapsedByDefault: false,
          isSplit: false,
          isCol2: false,
          isCard: false,
          icon: null,
          layout: null,
          colSpan: null,
          colTotal: null,
          rows: [],
          advancedRows: []
        };
      }
      __name(createDefaultSection, "createDefaultSection");
      function hasContent2(section) {
        return section.rows.length > 0 || section.advancedRows.length > 0;
      }
      __name(hasContent2, "hasContent");
      if (typeof module !== "undefined" && module.exports) {
        module.exports = {
          // Types
          RowType,
          // Detection
          detectAutoFormat: detectAutoFormat3,
          parseSectionDecorators: parseSectionDecorators2,
          isParameterRow: isParameterRow2,
          getParameterName,
          getParameterLabel: getParameterLabel2,
          // View model derivation
          deriveRowViewModel,
          deriveViewModels: deriveViewModels2,
          // Section grouping
          groupBySection: groupBySection2
        };
      }
    }
  });

  // public/src/utils/tokenGenerator.js
  var require_tokenGenerator = __commonJS({
    "public/src/utils/tokenGenerator.js"(exports, module) {
      function generateCollabToken2(length = 12) {
        const array = new Uint8Array(Math.ceil(length * 0.75));
        crypto.getRandomValues(array);
        return btoa(String.fromCharCode(...array)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "").slice(0, length);
      }
      __name(generateCollabToken2, "generateCollabToken");
      function generateSlug(title) {
        if (!title || typeof title !== "string") {
          return "untitled";
        }
        return title.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 50) || "untitled";
      }
      __name(generateSlug, "generateSlug");
      function isValidSlug2(slug) {
        if (!slug || typeof slug !== "string") return false;
        return /^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$|^[a-z0-9]$/.test(slug);
      }
      __name(isValidSlug2, "isValidSlug");
      async function checkSlugAvailable(slug) {
        if (!slug) return false;
        const baseUrl = typeof window !== "undefined" && window.FirebasePaths ? window.FirebasePaths.getWikiUrl(slug) : `https://instacalc.firebaseio.com/wiki/${slug}.json`;
        try {
          const response = await fetch(baseUrl);
          const data = await response.json();
          return data === null;
        } catch (err) {
          console.error("Error checking slug availability:", err);
          return false;
        }
      }
      __name(checkSlugAvailable, "checkSlugAvailable");
      async function generateUniqueSlug(baseSlug, maxAttempts = 10) {
        if (await checkSlugAvailable(baseSlug)) {
          return baseSlug;
        }
        for (let i = 2; i <= maxAttempts + 1; i++) {
          const candidate = `${baseSlug}-${i}`;
          if (candidate.length <= 50 && await checkSlugAvailable(candidate)) {
            return candidate;
          }
        }
        const randomSuffix = generateCollabToken2(4).toLowerCase();
        const randomCandidate = `${baseSlug.slice(0, 44)}-${randomSuffix}`;
        if (await checkSlugAvailable(randomCandidate)) {
          return randomCandidate;
        }
        return null;
      }
      __name(generateUniqueSlug, "generateUniqueSlug");
      if (typeof module !== "undefined" && module.exports) {
        module.exports = {
          generateCollabToken: generateCollabToken2,
          generateSlug,
          isValidSlug: isValidSlug2,
          checkSlugAvailable,
          generateUniqueSlug
        };
      }
      if (typeof window !== "undefined") {
        window.TokenGenerator = {
          generateCollabToken: generateCollabToken2,
          generateSlug,
          isValidSlug: isValidSlug2,
          checkSlugAvailable,
          generateUniqueSlug
        };
      }
    }
  });

  // public/src/stores/FirebasePaths.js
  var require_FirebasePaths = __commonJS({
    "public/src/stores/FirebasePaths.js"(exports, module) {
      var ARCHIVE_THRESHOLD = 5e4;
      var DEFAULT_BASE_URL = "https://instacalc.firebaseio.com";
      var getCalcType2 = /* @__PURE__ */ __name(() => {
        if (typeof window !== "undefined" && window.CalcStore?.CALC_TYPE) {
          return window.CalcStore.CALC_TYPE;
        }
        return { LIVE: "live", WIKI: "wiki", PRIVATE: "private" };
      }, "getCalcType");
      var FirebasePaths = {
        // Exported constant for documentation/testing
        ARCHIVE_THRESHOLD,
        // Mutable base URL for testing
        _baseUrl: DEFAULT_BASE_URL,
        /**
         * Get the Firebase path for a shared calc ID
         * IDs below threshold are in /archive/ (legacy), others in /shared/
         *
         * @param {string|number} id - The calc ID
         * @returns {string} - '/archive/' or '/shared/'
         * @throws {Error} - If ID is not numeric
         */
        getSharedPath(id) {
          const numId = parseInt(id, 10);
          if (isNaN(numId)) {
            throw new Error(`Invalid shared calc ID: ${id}. Must be numeric.`);
          }
          return numId < ARCHIVE_THRESHOLD ? "/archive/" : "/shared/";
        },
        /**
         * Get the full Firebase URL for a shared calc
         *
         * @param {string|number} id - The calc ID
         * @returns {string} - Full URL like 'https://instacalc.firebaseio.com/shared/12345.json'
         */
        getSharedUrl(id) {
          const path = this.getSharedPath(id);
          return `${this._baseUrl}${path}${id}.json`;
        },
        /**
         * Get the Firebase path for a user's calcs
         *
         * @param {string} uid - Firebase user ID
         * @returns {string} - Path like '/users/abc123/calcs/'
         */
        getUserCalcsPath(uid) {
          if (!uid) throw new Error("User ID is required");
          return `/users/${uid}/calcs/`;
        },
        /**
         * Get the Firebase path for publishing a calc
         *
         * @param {string|number} id - The calc ID to publish
         * @returns {string} - Path like '/shared/12345'
         */
        getPublishPath(id) {
          return `/shared/${id}`;
        },
        // ==================== Unified Remote Path ====================
        /**
         * Get the Firebase path for a remote calc based on type and remoteId
         * Single source of truth for all remote calc path resolution.
         *
         * @param {string} type - 'live', 'wiki', 'private', or 'collab'
         * @param {string} remoteId - The slug or token
         * @returns {string|null} - Path like '/wiki/live/slug' or null if invalid
         */
        getRemotePath(type, remoteId) {
          if (!remoteId) return null;
          const CALC_TYPE = getCalcType2();
          switch (type) {
            case CALC_TYPE.LIVE:
              return this.getLivePath(remoteId);
            case CALC_TYPE.WIKI:
              return this.getWikiPath(remoteId);
            case CALC_TYPE.PRIVATE:
              return this.getCollabPath(remoteId);
            case "collab":
              return this.getCollabPath(remoteId);
            // CalcType.COLLAB alias
            default:
              return null;
          }
        },
        /**
         * Get the full Firebase URL for a remote calc
         *
         * @param {string} type - CalcStore.CALC_TYPE.LIVE, WIKI, PRIVATE, or 'collab'
         * @param {string} remoteId - The slug or token
         * @returns {string|null} - Full URL or null if invalid
         */
        getRemoteUrl(type, remoteId) {
          if (!remoteId) return null;
          const CALC_TYPE = getCalcType2();
          switch (type) {
            case CALC_TYPE.LIVE:
              return this.getLiveUrl(remoteId);
            case CALC_TYPE.WIKI:
              return this.getWikiUrl(remoteId);
            case CALC_TYPE.PRIVATE:
              return this.getCollabUrl(remoteId);
            case "collab":
              return this.getCollabUrl(remoteId);
            // CalcType.COLLAB alias
            default:
              return null;
          }
        },
        // ==================== Wiki Paths ====================
        /**
         * Get the Firebase path for a wiki calc
         *
         * @param {string} slug - The wiki slug (e.g., 'tip-calculator')
         * @returns {string} - Path like '/wiki/tip-calculator'
         */
        getWikiPath(slug) {
          if (!slug) throw new Error("Wiki slug is required");
          return `/wiki/${slug}`;
        },
        /**
         * Get the full Firebase URL for a wiki calc
         *
         * @param {string} slug - The wiki slug
         * @returns {string} - Full URL like 'https://instacalc.firebaseio.com/wiki/tip-calculator.json'
         */
        getWikiUrl(slug) {
          return `${this._baseUrl}/wiki/${slug}.json`;
        },
        // ==================== Collab Paths ====================
        /**
         * Get the Firebase path for a collab (private collaborative) calc
         *
         * @param {string} token - The collab token (e.g., 'xK9mPqR2v4Yz')
         * @returns {string} - Path like '/collab/xK9mPqR2v4Yz'
         */
        getCollabPath(token) {
          if (!token) throw new Error("Collab token is required");
          return `/collab/${token}`;
        },
        /**
         * Get the full Firebase URL for a collab calc
         *
         * @param {string} token - The collab token
         * @returns {string} - Full URL like 'https://instacalc.firebaseio.com/collab/xK9mPqR2v4Yz.json'
         */
        getCollabUrl(token) {
          return `${this._baseUrl}/collab/${token}.json`;
        },
        // ==================== Live Wiki Paths ====================
        /**
         * Get the Firebase path for a live wiki calc
         *
         * @param {string} slug - The wiki slug (e.g., 'grocery-list')
         * @returns {string} - Path like '/wiki/live/grocery-list'
         */
        getLivePath(slug) {
          if (!slug) throw new Error("Live wiki slug is required");
          return `/wiki/live/${slug}`;
        },
        /**
         * Get the full Firebase URL for a live wiki calc
         *
         * @param {string} slug - The wiki slug
         * @returns {string} - Full URL like 'https://instacalc.firebaseio.com/wiki/live/grocery-list.json'
         */
        getLiveUrl(slug) {
          return `${this._baseUrl}/wiki/live/${slug}.json`;
        },
        // ==================== Loadout Paths ====================
        /**
         * Get path for loadouts collection under a calc
         * @param {string} calcId - Remote calc ID (numeric)
         * @returns {string} Firebase path
         */
        getLoadoutsPath(calcId) {
          return `/calcs/${calcId}/loadouts`;
        },
        /**
         * Get path for a specific loadout
         * @param {string} calcId - Remote calc ID
         * @param {string} loadoutId - Loadout ID
         * @returns {string} Firebase path
         */
        getLoadoutPath(calcId, loadoutId) {
          return `/calcs/${calcId}/loadouts/${loadoutId}`;
        },
        // ==================== Test Utilities ====================
        /**
         * Set custom base URL for testing
         * @param {string} url - The base URL to use
         */
        setBaseUrl(url) {
          this._baseUrl = url;
        },
        /**
         * Reset to production base URL
         */
        resetBaseUrl() {
          this._baseUrl = DEFAULT_BASE_URL;
        },
        /**
         * Get current base URL (for debugging/testing)
         * @returns {string}
         */
        getBaseUrl() {
          return this._baseUrl;
        }
      };
      if (typeof module !== "undefined" && module.exports) {
        module.exports = FirebasePaths;
      }
      if (typeof window !== "undefined") {
        window.FirebasePaths = FirebasePaths;
      }
    }
  });

  // public/src/react/plugins/registry.js
  function createRegistry(name) {
    const items = /* @__PURE__ */ new Map();
    return {
      name,
      /**
       * Register a plugin
       * @param {Object} plugin - Plugin with id field
       */
      register(plugin) {
        if (!plugin.id) {
          throw new Error(`Plugin missing id in ${name} registry`);
        }
        items.set(plugin.id, plugin);
        if (plugin.aliases) {
          plugin.aliases.forEach((alias) => {
            items.set(alias, plugin);
          });
        }
      },
      /**
       * Get plugin by id
       * @param {string} id
       * @returns {Object|undefined}
       */
      get(id) {
        return items.get(id);
      },
      /**
       * Get all registered plugins (excluding aliases)
       * @returns {Object[]}
       */
      getAll() {
        const seen = /* @__PURE__ */ new Set();
        const result = [];
        for (const plugin of items.values()) {
          if (!seen.has(plugin)) {
            seen.add(plugin);
            result.push(plugin);
          }
        }
        return result;
      },
      /**
       * Check if plugin exists
       * @param {string} id
       * @returns {boolean}
       */
      has(id) {
        return items.has(id);
      },
      /**
       * Get count of unique plugins
       * @returns {number}
       */
      size() {
        return this.getAll().length;
      },
      /**
       * Debug helper - log all plugins
       */
      debug() {
        console.group(`Registry: ${name}`);
        console.table(
          this.getAll().map((p) => ({
            id: p.id,
            name: p.name,
            aliases: p.aliases?.join(", ") || "-"
          }))
        );
        console.groupEnd();
      }
    };
  }
  var init_registry = __esm({
    "public/src/react/plugins/registry.js"() {
      __name(createRegistry, "createRegistry");
    }
  });

  // public/src/react/plugins/strategies/editor.js
  var editor_default;
  var init_editor = __esm({
    "public/src/react/plugins/strategies/editor.js"() {
      editor_default = {
        id: "editor",
        aliases: ["calc"],
        name: "Editor",
        // ═══════════════════════════════════════════════════════════════════
        // AVAILABLE VIEWS (what views can be selected)
        // ═══════════════════════════════════════════════════════════════════
        views: ["regular", "text", "present"],
        // ═══════════════════════════════════════════════════════════════════
        // OVERRIDES
        // Omitted = let views control their own tools/share/panels
        // ═══════════════════════════════════════════════════════════════════
        // toolsOverride: undefined,
        // shareActionsOverride: undefined,
        // panelsOverride: undefined,
        // ═══════════════════════════════════════════════════════════════════
        // FEATURE FLAGS
        // ═══════════════════════════════════════════════════════════════════
        features: {
          showRowNumbers: true,
          showRowContext: true,
          showExpandButton: true,
          showToolbar: true,
          showShareBar: true,
          showCalcUrlPreview: true,
          autoInsertRows: true,
          showAddRowButton: true,
          allowTitleEdit: true
        },
        // ═══════════════════════════════════════════════════════════════════
        // LAYOUT
        // ═══════════════════════════════════════════════════════════════════
        layout: {
          density: "normal",
          footer: null
        }
      };
    }
  });

  // public/src/react/plugins/strategies/public.js
  var public_default;
  var init_public = __esm({
    "public/src/react/plugins/strategies/public.js"() {
      public_default = {
        id: "public",
        name: "Public View",
        // ═══════════════════════════════════════════════════════════════════
        // AVAILABLE VIEWS
        // ═══════════════════════════════════════════════════════════════════
        views: ["regular", "text", "present"],
        // ═══════════════════════════════════════════════════════════════════
        // OVERRIDES
        // ═══════════════════════════════════════════════════════════════════
        toolsOverride: ["mode-switcher"],
        // Only mode switcher, no editing tools
        shareActionsOverride: ["public-link", "copy-text", "remix"],
        // ═══════════════════════════════════════════════════════════════════
        // FEATURE FLAGS
        // ═══════════════════════════════════════════════════════════════════
        features: {
          showRowNumbers: true,
          showRowContext: false,
          showExpandButton: true,
          showToolbar: false,
          showShareBar: true,
          showCalcUrlPreview: true,
          autoInsertRows: false,
          showAddRowButton: false,
          // Hide helper text for clean view
          allowTitleEdit: false
        },
        // ═══════════════════════════════════════════════════════════════════
        // LAYOUT
        // ═══════════════════════════════════════════════════════════════════
        layout: {
          density: "normal",
          footer: null
        }
      };
    }
  });

  // public/src/react/plugins/strategies/embed.js
  var embed_default;
  var init_embed = __esm({
    "public/src/react/plugins/strategies/embed.js"() {
      embed_default = {
        id: "embed",
        name: "Embed",
        // ═══════════════════════════════════════════════════════════════════
        // AVAILABLE VIEWS (restricted)
        // ═══════════════════════════════════════════════════════════════════
        views: ["regular", "present"],
        // No text mode in embed
        // ═══════════════════════════════════════════════════════════════════
        // OVERRIDES - No tools, share, or panels regardless of view
        // ═══════════════════════════════════════════════════════════════════
        toolsOverride: [],
        shareActionsOverride: [],
        panelsOverride: [],
        // ═══════════════════════════════════════════════════════════════════
        // FEATURE FLAGS
        // ═══════════════════════════════════════════════════════════════════
        features: {
          showRowNumbers: false,
          showRowContext: false,
          showExpandButton: true,
          showToolbar: false,
          showShareBar: false,
          showCalcUrlPreview: false,
          autoInsertRows: true,
          // Enter key can still add rows
          showAddRowButton: false,
          // Hide helper text for clean view
          allowTitleEdit: false
        },
        // ═══════════════════════════════════════════════════════════════════
        // LAYOUT
        // ═══════════════════════════════════════════════════════════════════
        layout: {
          density: "compact",
          footer: "embed"
          // "Powered by Instacalc"
        }
      };
    }
  });

  // public/src/react/plugins/strategies/index.js
  var strategies_exports = {};
  __export(strategies_exports, {
    default: () => strategies_default,
    getComposedFeatures: () => getComposedFeatures,
    getStrategy: () => getStrategy,
    strategyRegistry: () => strategyRegistry
  });
  function getStrategy(viewMode) {
    return strategyRegistry.get(viewMode) || strategyRegistry.get("editor");
  }
  function getComposedFeatures(strategy, view) {
    return {
      // View features first (base)
      ...view?.features,
      // Strategy features override
      ...strategy.features,
      // Compose layout
      layout: {
        ...view?.grid,
        ...strategy.layout
      }
    };
  }
  var strategyRegistry, strategies_default;
  var init_strategies = __esm({
    "public/src/react/plugins/strategies/index.js"() {
      init_registry();
      init_editor();
      init_public();
      init_embed();
      strategyRegistry = createRegistry("strategies");
      [editor_default, public_default, embed_default].forEach((s) => strategyRegistry.register(s));
      __name(getStrategy, "getStrategy");
      __name(getComposedFeatures, "getComposedFeatures");
      strategies_default = strategyRegistry;
    }
  });

  // public/src/react/plugins/views/regular.js
  var regular_default;
  var init_regular = __esm({
    "public/src/react/plugins/views/regular.js"() {
      init_Icon();
      regular_default = {
        id: "regular",
        name: "Calc",
        icon: Icon_default.calculator,
        description: "Interactive spreadsheet.\nRow-by-row editing.",
        order: 10,
        // ═══════════════════════════════════════════════════════════════════
        // CHILDREN - What this view wants (strategy can override)
        // ═══════════════════════════════════════════════════════════════════
        tools: ["clear", "history", "format", "goalseek", "mode-switcher"],
        decorators: ["all"],
        // Supports all decorators
        controls: ["all"],
        // Supports all controls
        // ═══════════════════════════════════════════════════════════════════
        // GRID & LAYOUT
        // ═══════════════════════════════════════════════════════════════════
        grid: {
          columns: 3,
          layoutStrategy: "integrated",
          // Row number + input + result in one row
          type: "3-col"
        },
        // ═══════════════════════════════════════════════════════════════════
        // COMPONENTS - Lazy loaded to avoid circular imports
        // ═══════════════════════════════════════════════════════════════════
        components: {
          // These are resolved at runtime via getViewComponent()
          container: "CalcModeRows",
          row: "CalcRow"
        },
        // ═══════════════════════════════════════════════════════════════════
        // VIEW-SPECIFIC FEATURES
        // ═══════════════════════════════════════════════════════════════════
        features: {
          showRowNumbers: true,
          editable: true,
          supportsDragDrop: true,
          supportsUIControls: true
        }
      };
    }
  });

  // public/src/react/plugins/views/text.js
  var text_default;
  var init_text = __esm({
    "public/src/react/plugins/views/text.js"() {
      init_Icon();
      text_default = {
        id: "text",
        name: "Text",
        icon: Icon_default.modeText,
        description: "Edit as plain text.\nGreat for copy/paste.",
        order: 20,
        // ═══════════════════════════════════════════════════════════════════
        // CHILDREN - What this view wants
        // ═══════════════════════════════════════════════════════════════════
        tools: ["clear", "history", "format", "mode-switcher"],
        // No goalseek in text mode
        decorators: [],
        // No decorators in text mode
        controls: [],
        // No controls in text mode
        // ═══════════════════════════════════════════════════════════════════
        // GRID & LAYOUT
        // ═══════════════════════════════════════════════════════════════════
        grid: {
          columns: 1,
          layoutStrategy: "stacked",
          type: "text"
        },
        // ═══════════════════════════════════════════════════════════════════
        // COMPONENTS
        // ═══════════════════════════════════════════════════════════════════
        components: {
          container: "TextEditView",
          row: null
          // Text mode doesn't use per-row rendering
        },
        // ═══════════════════════════════════════════════════════════════════
        // VIEW-SPECIFIC FEATURES
        // ═══════════════════════════════════════════════════════════════════
        features: {
          showRowNumbers: true,
          editable: true,
          bulkEdit: true,
          supportsDragDrop: false,
          supportsUIControls: false
        }
      };
    }
  });

  // public/src/react/plugins/views/present.js
  var present_default;
  var init_present = __esm({
    "public/src/react/plugins/views/present.js"() {
      init_Icon();
      present_default = {
        id: "present",
        name: "Present",
        icon: Icon_default.modePresent,
        description: "Visually clean.\nGreat for sharing.",
        badge: "Beta",
        order: 30,
        // ═══════════════════════════════════════════════════════════════════
        // CHILDREN - Minimal tools in present mode
        // ═══════════════════════════════════════════════════════════════════
        tools: ["mode-switcher"],
        // Only way to exit present mode
        decorators: [
          "hero",
          "icon",
          "section",
          "tab",
          "hidden",
          "advanced",
          "note",
          "width",
          "prefix",
          "suffix",
          "tooltip"
        ],
        controls: ["slider", "dropdown"],
        // Interactive controls still work
        // ═══════════════════════════════════════════════════════════════════
        // GRID & LAYOUT
        // ═══════════════════════════════════════════════════════════════════
        grid: {
          columns: 6,
          layoutStrategy: "stacked",
          type: "6-col"
        },
        // ═══════════════════════════════════════════════════════════════════
        // COMPONENTS
        // ═══════════════════════════════════════════════════════════════════
        components: {
          container: "PresentModeRows",
          row: "PresentRow"
        },
        // ═══════════════════════════════════════════════════════════════════
        // VIEW-SPECIFIC FEATURES
        // ═══════════════════════════════════════════════════════════════════
        features: {
          showRowNumbers: false,
          editable: false,
          supportsSections: true,
          supportsTabs: true,
          supportsDragDrop: false,
          supportsUIControls: true
        }
      };
    }
  });

  // public/src/react/plugins/views/index.js
  var views_exports = {};
  __export(views_exports, {
    default: () => views_default,
    getView: () => getView,
    getViewsForStrategy: () => getViewsForStrategy,
    viewRegistry: () => viewRegistry,
    viewSupportsControl: () => viewSupportsControl,
    viewSupportsDecorator: () => viewSupportsDecorator
  });
  function getView(id) {
    return viewRegistry.get(id) || viewRegistry.get("regular");
  }
  function getViewsForStrategy(strategy) {
    const allowedViews = strategy.views || viewRegistry.getAll().map((v) => v.id);
    return viewRegistry.getAll().filter((v) => allowedViews.includes(v.id)).sort((a, b) => a.order - b.order);
  }
  function viewSupportsDecorator(view, decoratorId) {
    if (!view.decorators) return false;
    if (view.decorators.includes("all")) return true;
    return view.decorators.includes(decoratorId);
  }
  function viewSupportsControl(view, controlId) {
    if (!view.controls) return false;
    if (view.controls.includes("all")) return true;
    return view.controls.includes(controlId);
  }
  var viewRegistry, views_default;
  var init_views = __esm({
    "public/src/react/plugins/views/index.js"() {
      init_registry();
      init_regular();
      init_text();
      init_present();
      viewRegistry = createRegistry("views");
      [regular_default, text_default, present_default].forEach((v) => viewRegistry.register(v));
      __name(getView, "getView");
      __name(getViewsForStrategy, "getViewsForStrategy");
      __name(viewSupportsDecorator, "viewSupportsDecorator");
      __name(viewSupportsControl, "viewSupportsControl");
      views_default = viewRegistry;
    }
  });

  // public/src/react/plugins/tools/clear.js
  var clear_default;
  var init_clear = __esm({
    "public/src/react/plugins/tools/clear.js"() {
      init_Icon();
      clear_default = {
        id: "clear",
        name: "Clear",
        icon: Icon_default.eraser,
        position: "menu",
        order: 10,
        tooltip: {
          title: "Clear Rows",
          description: "Reset all values"
        },
        // Action handler - receives ctx with calc, Action, etc.
        action: /* @__PURE__ */ __name((ctx) => {
          if (ctx.calc) {
            ctx.Action.clear(ctx.calc);
          }
        }, "action"),
        // Self-filter: always enabled when requested by view
        enabled: /* @__PURE__ */ __name(() => true, "enabled")
      };
    }
  });

  // public/src/react/plugins/tools/history.js
  var history_default;
  var init_history = __esm({
    "public/src/react/plugins/tools/history.js"() {
      init_Icon();
      history_default = {
        id: "history",
        name: "History",
        icon: Icon_default.history,
        position: "menu",
        order: 20,
        tooltip: {
          title: "History",
          description: "View calculation history"
        },
        // Opens modal - handled by component
        opensModal: "history",
        // Self-filter: always enabled
        enabled: /* @__PURE__ */ __name(() => true, "enabled")
      };
    }
  });

  // public/src/react/plugins/tools/format.js
  var format_default;
  var init_format = __esm({
    "public/src/react/plugins/tools/format.js"() {
      init_Icon();
      format_default = {
        id: "format",
        name: "Format",
        icon: Icon_default.settings2,
        position: "menu",
        order: 30,
        tooltip: {
          title: "Format & Rounding",
          description: "Change number format"
        },
        // Uses custom component (FormatDropdown)
        component: "FormatDropdown",
        // Self-filter: always enabled
        enabled: /* @__PURE__ */ __name(() => true, "enabled")
      };
    }
  });

  // public/src/react/plugins/tools/goalseek.js
  var goalseek_default;
  var init_goalseek = __esm({
    "public/src/react/plugins/tools/goalseek.js"() {
      init_Icon();
      init_featureFlags();
      goalseek_default = {
        id: "goalseek",
        name: "Goal Seek",
        icon: Icon_default.target,
        position: "left",
        order: 40,
        tooltip: {
          title: "Goal Seek",
          description: "Find input value for target result"
        },
        // Opens modal - handled by component
        opensModal: "goalseek",
        // Self-filter: only enabled if feature flag is on
        enabled: /* @__PURE__ */ __name(() => isFeatureEnabled("goalseek"), "enabled")
      };
    }
  });

  // public/src/react/plugins/tools/mode-switcher.js
  var mode_switcher_default;
  var init_mode_switcher = __esm({
    "public/src/react/plugins/tools/mode-switcher.js"() {
      init_Icon();
      mode_switcher_default = {
        id: "mode-switcher",
        name: "View Mode",
        icon: Icon_default.calculator,
        // Dynamic based on current mode
        position: "right",
        order: 10,
        tooltip: {
          title: "Change View",
          description: "Switch between Calculator, Text, and Present modes"
        },
        // Uses custom component (ModeDropdown)
        component: "ModeDropdown",
        // Self-filter: always enabled
        enabled: /* @__PURE__ */ __name(() => true, "enabled")
      };
    }
  });

  // public/src/react/plugins/tools/index.js
  var tools_exports = {};
  __export(tools_exports, {
    default: () => tools_default,
    getTools: () => getTools,
    getToolsByPosition: () => getToolsByPosition,
    toolRegistry: () => toolRegistry
  });
  function getTools(ctx) {
    const { strategy, view } = ctx;
    if (strategy.toolsOverride !== void 0) {
      return resolveToolIds(strategy.toolsOverride, ctx);
    }
    return resolveToolIds(view.tools || [], ctx);
  }
  function resolveToolIds(toolIds, ctx) {
    return toolIds.map((id) => toolRegistry.get(id)).filter(Boolean).filter((tool) => tool.enabled?.(ctx) ?? true).sort((a, b) => a.order - b.order);
  }
  function getToolsByPosition(ctx) {
    const tools = getTools(ctx);
    return {
      left: tools.filter((t) => t.position === "left"),
      right: tools.filter((t) => t.position === "right"),
      menu: tools.filter((t) => t.position === "menu")
    };
  }
  var toolRegistry, tools_default;
  var init_tools = __esm({
    "public/src/react/plugins/tools/index.js"() {
      init_registry();
      init_clear();
      init_history();
      init_format();
      init_goalseek();
      init_mode_switcher();
      toolRegistry = createRegistry("tools");
      [clear_default, history_default, format_default, goalseek_default, mode_switcher_default].forEach(
        (t) => toolRegistry.register(t)
      );
      __name(getTools, "getTools");
      __name(resolveToolIds, "resolveToolIds");
      __name(getToolsByPosition, "getToolsByPosition");
      tools_default = toolRegistry;
    }
  });

  // public/src/react/plugins/share/public-link.js
  var public_link_default;
  var init_public_link = __esm({
    "public/src/react/plugins/share/public-link.js"() {
      init_Icon();
      public_link_default = {
        id: "public-link",
        name: "Copy public link",
        icon: Icon_default.link,
        section: "primary",
        order: 10,
        // Show URL preview
        showUrlPreview: true,
        // Dynamic label based on state
        getLabel: /* @__PURE__ */ __name((ctx) => ctx.isCopied ? "Copied!" : "Copy public link", "getLabel"),
        // Action handler
        action: /* @__PURE__ */ __name(async (ctx) => {
          await ctx.ensurePublishedAndCopy();
        }, "action"),
        // Self-filter: always enabled
        enabled: /* @__PURE__ */ __name(() => true, "enabled")
      };
    }
  });

  // public/src/react/plugins/share/copy-text.js
  var copy_text_default;
  var init_copy_text = __esm({
    "public/src/react/plugins/share/copy-text.js"() {
      init_Icon();
      copy_text_default = {
        id: "copy-text",
        name: "Text",
        icon: Icon_default.fileText,
        section: "secondary",
        order: 10,
        // Action handler
        action: /* @__PURE__ */ __name(async (ctx) => {
          await ctx.copyAsText();
        }, "action"),
        // Self-filter: always enabled
        enabled: /* @__PURE__ */ __name(() => true, "enabled")
      };
    }
  });

  // public/src/react/plugins/share/embed-code.js
  var embed_code_default;
  var init_embed_code = __esm({
    "public/src/react/plugins/share/embed-code.js"() {
      init_Icon();
      embed_code_default = {
        id: "embed-code",
        name: "Embed",
        icon: Icon_default.code,
        section: "secondary",
        order: 20,
        // Action handler - opens embed modal
        action: /* @__PURE__ */ __name((ctx) => {
          ctx.openModal("embed");
        }, "action"),
        // Self-filter: always enabled
        enabled: /* @__PURE__ */ __name(() => true, "enabled")
      };
    }
  });

  // public/src/react/plugins/share/remix.js
  var remix_default;
  var init_remix = __esm({
    "public/src/react/plugins/share/remix.js"() {
      init_Icon();
      remix_default = {
        id: "remix",
        name: "Save a copy",
        icon: Icon_default.copy,
        section: "primary",
        order: 30,
        // Action handler
        action: /* @__PURE__ */ __name(async (ctx) => {
          await ctx.Action.cloneAndOpen(ctx.calc, { suffix: "" });
        }, "action"),
        // Self-filter: only show if viewing someone else's calc
        enabled: /* @__PURE__ */ __name((ctx) => ctx.isPublicView && !ctx.isOwner, "enabled")
      };
    }
  });

  // public/src/react/plugins/share/index.js
  var share_exports = {};
  __export(share_exports, {
    default: () => share_default,
    getShareActions: () => getShareActions,
    getShareActionsBySection: () => getShareActionsBySection,
    shareRegistry: () => shareRegistry
  });
  function getShareActions(ctx) {
    const { strategy, view } = ctx;
    if (strategy.shareActionsOverride !== void 0) {
      return resolveActionIds(strategy.shareActionsOverride, ctx);
    }
    const actionIds = view.shareActions || DEFAULT_SHARE_ACTIONS;
    return resolveActionIds(actionIds, ctx);
  }
  function resolveActionIds(actionIds, ctx) {
    return actionIds.map((id) => shareRegistry.get(id)).filter(Boolean).filter((action) => action.enabled?.(ctx) ?? true).sort((a, b) => a.order - b.order);
  }
  function getShareActionsBySection(ctx) {
    const actions = getShareActions(ctx);
    return {
      primary: actions.filter((a) => a.section === "primary"),
      secondary: actions.filter((a) => a.section === "secondary")
    };
  }
  var shareRegistry, DEFAULT_SHARE_ACTIONS, share_default;
  var init_share = __esm({
    "public/src/react/plugins/share/index.js"() {
      init_registry();
      init_public_link();
      init_copy_text();
      init_embed_code();
      init_remix();
      shareRegistry = createRegistry("share");
      [public_link_default, copy_text_default, embed_code_default, remix_default].forEach((a) => shareRegistry.register(a));
      DEFAULT_SHARE_ACTIONS = ["public-link", "copy-text", "embed-code"];
      __name(getShareActions, "getShareActions");
      __name(resolveActionIds, "resolveActionIds");
      __name(getShareActionsBySection, "getShareActionsBySection");
      share_default = shareRegistry;
    }
  });

  // public/src/instacalc.v2.jsx
  var instacalc_v2_exports = {};
  __export(instacalc_v2_exports, {
    ActionProvider: () => ActionProvider,
    App: () => App_default,
    CalcInfo: () => CalcInfo_default,
    CalcList: () => CalcList_default,
    CalcLoadingBoundary: () => CalcLoadingBoundary_default,
    CalcProvider: () => CalcProvider,
    CalcStore: () => CalcStore_default,
    CalcToolbar: () => CalcToolbar_default,
    Chart: () => Chart_default,
    GoalseekModal: () => GoalseekModal_default,
    HistoryModal: () => HistoryModal_default,
    InstaCalc: () => InstaCalc_default,
    LIB: () => LIB,
    Modal: () => Modal_default,
    NewCalcButton: () => NewCalcButton_default,
    React: () => import_react6.default,
    ReactDOM: () => react_dom_global_default,
    RemixButton: () => RemixButton,
    Row: () => Row_default,
    ShareMenu: () => ShareMenu_default,
    SharedInstaCalc: () => SharedInstaCalc_default,
    TextEditView: () => TextEditView_default,
    ToolbarButton: () => ToolbarButton_default,
    UIResult: () => UIResult_default,
    UIlog: () => UIlog,
    UserInfo: () => UserInfo_default,
    VariableAssignmentPanel: () => VariableAssignmentPanel_default,
    debounce: () => debounce,
    default: () => instacalc_v2_default,
    useAction: () => useAction,
    useCalc: () => useCalc,
    useDebounce: () => useDebounce,
    useDebouncedCallback: () => useDebouncedCallback,
    useLocalStorage: () => useLocalStorage,
    useSessionStorage: () => useSessionStorage
  });

  // public/src/react/App.jsx
  init_react_global();

  // public/src/react/components/calclist/CalcList.jsx
  init_react_global();
  init_Icon();

  // public/src/react/context/ActionContext.jsx
  init_react_global();
  var import_jsx_runtime2 = __toESM(require_jsx_runtime());
  var ActionContext = react_global_default.createContext(null);
  function ActionProvider({ children, action }) {
    if (!action) {
      console.warn("ActionProvider: action prop is required");
    }
    const val = react_global_default.useMemo(() => {
      if (!action) return null;
      if (!action.logEvent) {
        action.logEvent = (eventName, payload) => {
          if (typeof window !== "undefined" && window.DebugTools?.EventLogger?.logEvent) {
            window.DebugTools.EventLogger.logEvent(eventName, payload);
          }
        };
      }
      return action;
    }, [action]);
    return /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(ActionContext.Provider, { value: val, children });
  }
  __name(ActionProvider, "ActionProvider");
  function useAction() {
    const context = react_global_default.useContext(ActionContext);
    if (context === null) {
      throw new Error("useAction must be used within ActionProvider. Wrap your component with <ActionProvider action={...}>.");
    }
    return context;
  }
  __name(useAction, "useAction");
  var ActionContext_default = ActionContext;

  // public/src/react/utils/classNames.js
  function flattenClasses(value, collector) {
    if (!value) return;
    if (typeof value === "string") {
      if (value.trim()) collector.push(value.trim());
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => flattenClasses(item, collector));
      return;
    }
    if (typeof value === "object") {
      Object.entries(value).forEach(([key, condition]) => {
        if (condition) collector.push(key);
      });
    }
  }
  __name(flattenClasses, "flattenClasses");
  function cn(...inputs) {
    const parts = [];
    inputs.forEach((value) => flattenClasses(value, parts));
    return parts.join(" ").replace(/\s+/g, " ").trim();
  }
  __name(cn, "cn");
  function mergeClassNames(defaults = {}, overrides = {}) {
    const merged = {};
    const keys = /* @__PURE__ */ new Set([
      ...Object.keys(defaults || {}),
      ...Object.keys(overrides || {})
    ]);
    keys.forEach((key) => {
      merged[key] = cn(defaults?.[key], overrides?.[key]);
    });
    return merged;
  }
  __name(mergeClassNames, "mergeClassNames");
  function createSlotProps(defaultClasses = {}, slotOverrides = {}) {
    return /* @__PURE__ */ __name(function getSlotProps(slot, ownProps = {}) {
      const override = slotOverrides?.[slot] || {};
      const className = cn(defaultClasses?.[slot], ownProps.className, override.className);
      const style = {
        ...ownProps.style || {},
        ...override.style || {}
      };
      const combined = {
        ...ownProps,
        ...override,
        className
      };
      if (Object.keys(style).length > 0) {
        combined.style = style;
      }
      return combined;
    }, "getSlotProps");
  }
  __name(createSlotProps, "createSlotProps");

  // public/src/react/components/calclist/CalcDropdown.jsx
  init_react_global();
  init_Icon();

  // public/src/react/components/common/Dropdown.jsx
  init_react_global();

  // node_modules/@radix-ui/react-primitive/dist/index.mjs
  var React4 = __toESM(require_react(), 1);
  var ReactDOM = __toESM(require_react_dom(), 1);

  // node_modules/@radix-ui/react-slot/dist/index.mjs
  var React3 = __toESM(require_react(), 1);

  // node_modules/@radix-ui/react-compose-refs/dist/index.mjs
  var React2 = __toESM(require_react(), 1);
  function setRef(ref, value) {
    if (typeof ref === "function") {
      return ref(value);
    } else if (ref !== null && ref !== void 0) {
      ref.current = value;
    }
  }
  __name(setRef, "setRef");
  function composeRefs(...refs) {
    return (node) => {
      let hasCleanup = false;
      const cleanups = refs.map((ref) => {
        const cleanup = setRef(ref, node);
        if (!hasCleanup && typeof cleanup == "function") {
          hasCleanup = true;
        }
        return cleanup;
      });
      if (hasCleanup) {
        return () => {
          for (let i = 0; i < cleanups.length; i++) {
            const cleanup = cleanups[i];
            if (typeof cleanup == "function") {
              cleanup();
            } else {
              setRef(refs[i], null);
            }
          }
        };
      }
    };
  }
  __name(composeRefs, "composeRefs");
  function useComposedRefs(...refs) {
    return React2.useCallback(composeRefs(...refs), refs);
  }
  __name(useComposedRefs, "useComposedRefs");

  // node_modules/@radix-ui/react-slot/dist/index.mjs
  var import_jsx_runtime3 = __toESM(require_jsx_runtime(), 1);
  // @__NO_SIDE_EFFECTS__
  function createSlot(ownerName) {
    const SlotClone = /* @__PURE__ */ createSlotClone(ownerName);
    const Slot2 = React3.forwardRef((props, forwardedRef) => {
      const { children, ...slotProps } = props;
      const childrenArray = React3.Children.toArray(children);
      const slottable = childrenArray.find(isSlottable);
      if (slottable) {
        const newElement = slottable.props.children;
        const newChildren = childrenArray.map((child) => {
          if (child === slottable) {
            if (React3.Children.count(newElement) > 1) return React3.Children.only(null);
            return React3.isValidElement(newElement) ? newElement.props.children : null;
          } else {
            return child;
          }
        });
        return /* @__PURE__ */ (0, import_jsx_runtime3.jsx)(SlotClone, { ...slotProps, ref: forwardedRef, children: React3.isValidElement(newElement) ? React3.cloneElement(newElement, void 0, newChildren) : null });
      }
      return /* @__PURE__ */ (0, import_jsx_runtime3.jsx)(SlotClone, { ...slotProps, ref: forwardedRef, children });
    });
    Slot2.displayName = `${ownerName}.Slot`;
    return Slot2;
  }
  __name(createSlot, "createSlot");
  // @__NO_SIDE_EFFECTS__
  function createSlotClone(ownerName) {
    const SlotClone = React3.forwardRef((props, forwardedRef) => {
      const { children, ...slotProps } = props;
      if (React3.isValidElement(children)) {
        const childrenRef = getElementRef(children);
        const props2 = mergeProps(slotProps, children.props);
        if (children.type !== React3.Fragment) {
          props2.ref = forwardedRef ? composeRefs(forwardedRef, childrenRef) : childrenRef;
        }
        return React3.cloneElement(children, props2);
      }
      return React3.Children.count(children) > 1 ? React3.Children.only(null) : null;
    });
    SlotClone.displayName = `${ownerName}.SlotClone`;
    return SlotClone;
  }
  __name(createSlotClone, "createSlotClone");
  var SLOTTABLE_IDENTIFIER = Symbol("radix.slottable");
  function isSlottable(child) {
    return React3.isValidElement(child) && typeof child.type === "function" && "__radixId" in child.type && child.type.__radixId === SLOTTABLE_IDENTIFIER;
  }
  __name(isSlottable, "isSlottable");
  function mergeProps(slotProps, childProps) {
    const overrideProps = { ...childProps };
    for (const propName in childProps) {
      const slotPropValue = slotProps[propName];
      const childPropValue = childProps[propName];
      const isHandler = /^on[A-Z]/.test(propName);
      if (isHandler) {
        if (slotPropValue && childPropValue) {
          overrideProps[propName] = (...args) => {
            const result = childPropValue(...args);
            slotPropValue(...args);
            return result;
          };
        } else if (slotPropValue) {
          overrideProps[propName] = slotPropValue;
        }
      } else if (propName === "style") {
        overrideProps[propName] = { ...slotPropValue, ...childPropValue };
      } else if (propName === "className") {
        overrideProps[propName] = [slotPropValue, childPropValue].filter(Boolean).join(" ");
      }
    }
    return { ...slotProps, ...overrideProps };
  }
  __name(mergeProps, "mergeProps");
  function getElementRef(element) {
    let getter = Object.getOwnPropertyDescriptor(element.props, "ref")?.get;
    let mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
    if (mayWarn) {
      return element.ref;
    }
    getter = Object.getOwnPropertyDescriptor(element, "ref")?.get;
    mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
    if (mayWarn) {
      return element.props.ref;
    }
    return element.props.ref || element.ref;
  }
  __name(getElementRef, "getElementRef");

  // node_modules/@radix-ui/react-primitive/dist/index.mjs
  var import_jsx_runtime4 = __toESM(require_jsx_runtime(), 1);
  var NODES = [
    "a",
    "button",
    "div",
    "form",
    "h2",
    "h3",
    "img",
    "input",
    "label",
    "li",
    "nav",
    "ol",
    "p",
    "select",
    "span",
    "svg",
    "ul"
  ];
  var Primitive = NODES.reduce((primitive, node) => {
    const Slot2 = createSlot(`Primitive.${node}`);
    const Node2 = React4.forwardRef((props, forwardedRef) => {
      const { asChild, ...primitiveProps } = props;
      const Comp = asChild ? Slot2 : node;
      if (typeof window !== "undefined") {
        window[Symbol.for("radix-ui")] = true;
      }
      return /* @__PURE__ */ (0, import_jsx_runtime4.jsx)(Comp, { ...primitiveProps, ref: forwardedRef });
    });
    Node2.displayName = `Primitive.${node}`;
    return { ...primitive, [node]: Node2 };
  }, {});
  function dispatchDiscreteCustomEvent(target, event) {
    if (target) ReactDOM.flushSync(() => target.dispatchEvent(event));
  }
  __name(dispatchDiscreteCustomEvent, "dispatchDiscreteCustomEvent");

  // node_modules/@radix-ui/react-context/dist/index.mjs
  var React5 = __toESM(require_react(), 1);
  var import_jsx_runtime5 = __toESM(require_jsx_runtime(), 1);
  function createContextScope(scopeName, createContextScopeDeps = []) {
    let defaultContexts = [];
    function createContext32(rootComponentName, defaultContext) {
      const BaseContext = React5.createContext(defaultContext);
      const index2 = defaultContexts.length;
      defaultContexts = [...defaultContexts, defaultContext];
      const Provider = /* @__PURE__ */ __name((props) => {
        const { scope, children, ...context } = props;
        const Context = scope?.[scopeName]?.[index2] || BaseContext;
        const value = React5.useMemo(() => context, Object.values(context));
        return /* @__PURE__ */ (0, import_jsx_runtime5.jsx)(Context.Provider, { value, children });
      }, "Provider");
      Provider.displayName = rootComponentName + "Provider";
      function useContext22(consumerName, scope) {
        const Context = scope?.[scopeName]?.[index2] || BaseContext;
        const context = React5.useContext(Context);
        if (context) return context;
        if (defaultContext !== void 0) return defaultContext;
        throw new Error(`\`${consumerName}\` must be used within \`${rootComponentName}\``);
      }
      __name(useContext22, "useContext2");
      return [Provider, useContext22];
    }
    __name(createContext32, "createContext3");
    const createScope = /* @__PURE__ */ __name(() => {
      const scopeContexts = defaultContexts.map((defaultContext) => {
        return React5.createContext(defaultContext);
      });
      return /* @__PURE__ */ __name(function useScope(scope) {
        const contexts = scope?.[scopeName] || scopeContexts;
        return React5.useMemo(
          () => ({ [`__scope${scopeName}`]: { ...scope, [scopeName]: contexts } }),
          [scope, contexts]
        );
      }, "useScope");
    }, "createScope");
    createScope.scopeName = scopeName;
    return [createContext32, composeContextScopes(createScope, ...createContextScopeDeps)];
  }
  __name(createContextScope, "createContextScope");
  function composeContextScopes(...scopes) {
    const baseScope = scopes[0];
    if (scopes.length === 1) return baseScope;
    const createScope = /* @__PURE__ */ __name(() => {
      const scopeHooks = scopes.map((createScope2) => ({
        useScope: createScope2(),
        scopeName: createScope2.scopeName
      }));
      return /* @__PURE__ */ __name(function useComposedScopes(overrideScopes) {
        const nextScopes = scopeHooks.reduce((nextScopes2, { useScope, scopeName }) => {
          const scopeProps = useScope(overrideScopes);
          const currentScope = scopeProps[`__scope${scopeName}`];
          return { ...nextScopes2, ...currentScope };
        }, {});
        return React5.useMemo(() => ({ [`__scope${baseScope.scopeName}`]: nextScopes }), [nextScopes]);
      }, "useComposedScopes");
    }, "createScope");
    createScope.scopeName = baseScope.scopeName;
    return createScope;
  }
  __name(composeContextScopes, "composeContextScopes");

  // node_modules/@radix-ui/react-collection/dist/index.mjs
  var import_react2 = __toESM(require_react(), 1);
  var import_jsx_runtime6 = __toESM(require_jsx_runtime(), 1);
  var import_react3 = __toESM(require_react(), 1);
  var import_jsx_runtime7 = __toESM(require_jsx_runtime(), 1);
  function createCollection(name) {
    const PROVIDER_NAME = name + "CollectionProvider";
    const [createCollectionContext, createCollectionScope3] = createContextScope(PROVIDER_NAME);
    const [CollectionProviderImpl, useCollectionContext] = createCollectionContext(
      PROVIDER_NAME,
      { collectionRef: { current: null }, itemMap: /* @__PURE__ */ new Map() }
    );
    const CollectionProvider = /* @__PURE__ */ __name((props) => {
      const { scope, children } = props;
      const ref = import_react2.default.useRef(null);
      const itemMap = import_react2.default.useRef(/* @__PURE__ */ new Map()).current;
      return /* @__PURE__ */ (0, import_jsx_runtime6.jsx)(CollectionProviderImpl, { scope, itemMap, collectionRef: ref, children });
    }, "CollectionProvider");
    CollectionProvider.displayName = PROVIDER_NAME;
    const COLLECTION_SLOT_NAME = name + "CollectionSlot";
    const CollectionSlotImpl = createSlot(COLLECTION_SLOT_NAME);
    const CollectionSlot = import_react2.default.forwardRef(
      (props, forwardedRef) => {
        const { scope, children } = props;
        const context = useCollectionContext(COLLECTION_SLOT_NAME, scope);
        const composedRefs = useComposedRefs(forwardedRef, context.collectionRef);
        return /* @__PURE__ */ (0, import_jsx_runtime6.jsx)(CollectionSlotImpl, { ref: composedRefs, children });
      }
    );
    CollectionSlot.displayName = COLLECTION_SLOT_NAME;
    const ITEM_SLOT_NAME = name + "CollectionItemSlot";
    const ITEM_DATA_ATTR = "data-radix-collection-item";
    const CollectionItemSlotImpl = createSlot(ITEM_SLOT_NAME);
    const CollectionItemSlot = import_react2.default.forwardRef(
      (props, forwardedRef) => {
        const { scope, children, ...itemData } = props;
        const ref = import_react2.default.useRef(null);
        const composedRefs = useComposedRefs(forwardedRef, ref);
        const context = useCollectionContext(ITEM_SLOT_NAME, scope);
        import_react2.default.useEffect(() => {
          context.itemMap.set(ref, { ref, ...itemData });
          return () => void context.itemMap.delete(ref);
        });
        return /* @__PURE__ */ (0, import_jsx_runtime6.jsx)(CollectionItemSlotImpl, { ...{ [ITEM_DATA_ATTR]: "" }, ref: composedRefs, children });
      }
    );
    CollectionItemSlot.displayName = ITEM_SLOT_NAME;
    function useCollection3(scope) {
      const context = useCollectionContext(name + "CollectionConsumer", scope);
      const getItems = import_react2.default.useCallback(() => {
        const collectionNode = context.collectionRef.current;
        if (!collectionNode) return [];
        const orderedNodes = Array.from(collectionNode.querySelectorAll(`[${ITEM_DATA_ATTR}]`));
        const items = Array.from(context.itemMap.values());
        const orderedItems = items.sort(
          (a, b) => orderedNodes.indexOf(a.ref.current) - orderedNodes.indexOf(b.ref.current)
        );
        return orderedItems;
      }, [context.collectionRef, context.itemMap]);
      return getItems;
    }
    __name(useCollection3, "useCollection");
    return [
      { Provider: CollectionProvider, Slot: CollectionSlot, ItemSlot: CollectionItemSlot },
      useCollection3,
      createCollectionScope3
    ];
  }
  __name(createCollection, "createCollection");

  // node_modules/@radix-ui/primitive/dist/index.mjs
  var canUseDOM = !!(typeof window !== "undefined" && window.document && window.document.createElement);
  function composeEventHandlers(originalEventHandler, ourEventHandler, { checkForDefaultPrevented = true } = {}) {
    return /* @__PURE__ */ __name(function handleEvent(event) {
      originalEventHandler?.(event);
      if (checkForDefaultPrevented === false || !event.defaultPrevented) {
        return ourEventHandler?.(event);
      }
    }, "handleEvent");
  }
  __name(composeEventHandlers, "composeEventHandlers");

  // node_modules/@radix-ui/react-use-controllable-state/dist/index.mjs
  var React8 = __toESM(require_react(), 1);

  // node_modules/@radix-ui/react-use-layout-effect/dist/index.mjs
  var React7 = __toESM(require_react(), 1);
  var useLayoutEffect22 = globalThis?.document ? React7.useLayoutEffect : () => {
  };

  // node_modules/@radix-ui/react-use-controllable-state/dist/index.mjs
  var React23 = __toESM(require_react(), 1);
  var useInsertionEffect = React8[" useInsertionEffect ".trim().toString()] || useLayoutEffect22;
  function useControllableState({
    prop,
    defaultProp,
    onChange = /* @__PURE__ */ __name(() => {
    }, "onChange"),
    caller
  }) {
    const [uncontrolledProp, setUncontrolledProp, onChangeRef] = useUncontrolledState({
      defaultProp,
      onChange
    });
    const isControlled = prop !== void 0;
    const value = isControlled ? prop : uncontrolledProp;
    if (true) {
      const isControlledRef = React8.useRef(prop !== void 0);
      React8.useEffect(() => {
        const wasControlled = isControlledRef.current;
        if (wasControlled !== isControlled) {
          const from = wasControlled ? "controlled" : "uncontrolled";
          const to = isControlled ? "controlled" : "uncontrolled";
          console.warn(
            `${caller} is changing from ${from} to ${to}. Components should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled value for the lifetime of the component.`
          );
        }
        isControlledRef.current = isControlled;
      }, [isControlled, caller]);
    }
    const setValue = React8.useCallback(
      (nextValue) => {
        if (isControlled) {
          const value2 = isFunction(nextValue) ? nextValue(prop) : nextValue;
          if (value2 !== prop) {
            onChangeRef.current?.(value2);
          }
        } else {
          setUncontrolledProp(nextValue);
        }
      },
      [isControlled, prop, setUncontrolledProp, onChangeRef]
    );
    return [value, setValue];
  }
  __name(useControllableState, "useControllableState");
  function useUncontrolledState({
    defaultProp,
    onChange
  }) {
    const [value, setValue] = React8.useState(defaultProp);
    const prevValueRef = React8.useRef(value);
    const onChangeRef = React8.useRef(onChange);
    useInsertionEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);
    React8.useEffect(() => {
      if (prevValueRef.current !== value) {
        onChangeRef.current?.(value);
        prevValueRef.current = value;
      }
    }, [value, prevValueRef]);
    return [value, setValue, onChangeRef];
  }
  __name(useUncontrolledState, "useUncontrolledState");
  function isFunction(value) {
    return typeof value === "function";
  }
  __name(isFunction, "isFunction");
  var SYNC_STATE = Symbol("RADIX:SYNC_STATE");

  // node_modules/@radix-ui/react-presence/dist/index.mjs
  var React24 = __toESM(require_react(), 1);
  var React9 = __toESM(require_react(), 1);
  function useStateMachine(initialState, machine) {
    return React9.useReducer((state, event) => {
      const nextState = machine[state][event];
      return nextState ?? state;
    }, initialState);
  }
  __name(useStateMachine, "useStateMachine");
  var Presence = /* @__PURE__ */ __name((props) => {
    const { present, children } = props;
    const presence = usePresence(present);
    const child = typeof children === "function" ? children({ present: presence.isPresent }) : React24.Children.only(children);
    const ref = useComposedRefs(presence.ref, getElementRef2(child));
    const forceMount = typeof children === "function";
    return forceMount || presence.isPresent ? React24.cloneElement(child, { ref }) : null;
  }, "Presence");
  Presence.displayName = "Presence";
  function usePresence(present) {
    const [node, setNode] = React24.useState();
    const stylesRef = React24.useRef(null);
    const prevPresentRef = React24.useRef(present);
    const prevAnimationNameRef = React24.useRef("none");
    const initialState = present ? "mounted" : "unmounted";
    const [state, send] = useStateMachine(initialState, {
      mounted: {
        UNMOUNT: "unmounted",
        ANIMATION_OUT: "unmountSuspended"
      },
      unmountSuspended: {
        MOUNT: "mounted",
        ANIMATION_END: "unmounted"
      },
      unmounted: {
        MOUNT: "mounted"
      }
    });
    React24.useEffect(() => {
      const currentAnimationName = getAnimationName(stylesRef.current);
      prevAnimationNameRef.current = state === "mounted" ? currentAnimationName : "none";
    }, [state]);
    useLayoutEffect22(() => {
      const styles = stylesRef.current;
      const wasPresent = prevPresentRef.current;
      const hasPresentChanged = wasPresent !== present;
      if (hasPresentChanged) {
        const prevAnimationName = prevAnimationNameRef.current;
        const currentAnimationName = getAnimationName(styles);
        if (present) {
          send("MOUNT");
        } else if (currentAnimationName === "none" || styles?.display === "none") {
          send("UNMOUNT");
        } else {
          const isAnimating = prevAnimationName !== currentAnimationName;
          if (wasPresent && isAnimating) {
            send("ANIMATION_OUT");
          } else {
            send("UNMOUNT");
          }
        }
        prevPresentRef.current = present;
      }
    }, [present, send]);
    useLayoutEffect22(() => {
      if (node) {
        let timeoutId;
        const ownerWindow = node.ownerDocument.defaultView ?? window;
        const handleAnimationEnd = /* @__PURE__ */ __name((event) => {
          const currentAnimationName = getAnimationName(stylesRef.current);
          const isCurrentAnimation = currentAnimationName.includes(CSS.escape(event.animationName));
          if (event.target === node && isCurrentAnimation) {
            send("ANIMATION_END");
            if (!prevPresentRef.current) {
              const currentFillMode = node.style.animationFillMode;
              node.style.animationFillMode = "forwards";
              timeoutId = ownerWindow.setTimeout(() => {
                if (node.style.animationFillMode === "forwards") {
                  node.style.animationFillMode = currentFillMode;
                }
              });
            }
          }
        }, "handleAnimationEnd");
        const handleAnimationStart = /* @__PURE__ */ __name((event) => {
          if (event.target === node) {
            prevAnimationNameRef.current = getAnimationName(stylesRef.current);
          }
        }, "handleAnimationStart");
        node.addEventListener("animationstart", handleAnimationStart);
        node.addEventListener("animationcancel", handleAnimationEnd);
        node.addEventListener("animationend", handleAnimationEnd);
        return () => {
          ownerWindow.clearTimeout(timeoutId);
          node.removeEventListener("animationstart", handleAnimationStart);
          node.removeEventListener("animationcancel", handleAnimationEnd);
          node.removeEventListener("animationend", handleAnimationEnd);
        };
      } else {
        send("ANIMATION_END");
      }
    }, [node, send]);
    return {
      isPresent: ["mounted", "unmountSuspended"].includes(state),
      ref: React24.useCallback((node2) => {
        stylesRef.current = node2 ? getComputedStyle(node2) : null;
        setNode(node2);
      }, [])
    };
  }
  __name(usePresence, "usePresence");
  function getAnimationName(styles) {
    return styles?.animationName || "none";
  }
  __name(getAnimationName, "getAnimationName");
  function getElementRef2(element) {
    let getter = Object.getOwnPropertyDescriptor(element.props, "ref")?.get;
    let mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
    if (mayWarn) {
      return element.ref;
    }
    getter = Object.getOwnPropertyDescriptor(element, "ref")?.get;
    mayWarn = getter && "isReactWarning" in getter && getter.isReactWarning;
    if (mayWarn) {
      return element.props.ref;
    }
    return element.props.ref || element.ref;
  }
  __name(getElementRef2, "getElementRef");

  // node_modules/@radix-ui/react-id/dist/index.mjs
  var React10 = __toESM(require_react(), 1);
  var useReactId = React10[" useId ".trim().toString()] || (() => void 0);
  var count = 0;
  function useId(deterministicId) {
    const [id, setId] = React10.useState(useReactId());
    useLayoutEffect22(() => {
      if (!deterministicId) setId((reactId) => reactId ?? String(count++));
    }, [deterministicId]);
    return deterministicId || (id ? `radix-${id}` : "");
  }
  __name(useId, "useId");

  // node_modules/@radix-ui/react-direction/dist/index.mjs
  var React11 = __toESM(require_react(), 1);
  var import_jsx_runtime8 = __toESM(require_jsx_runtime(), 1);
  var DirectionContext = React11.createContext(void 0);
  function useDirection(localDir) {
    const globalDir = React11.useContext(DirectionContext);
    return localDir || globalDir || "ltr";
  }
  __name(useDirection, "useDirection");

  // node_modules/@radix-ui/react-dismissable-layer/dist/index.mjs
  var React14 = __toESM(require_react(), 1);

  // node_modules/@radix-ui/react-use-callback-ref/dist/index.mjs
  var React12 = __toESM(require_react(), 1);
  function useCallbackRef(callback) {
    const callbackRef = React12.useRef(callback);
    React12.useEffect(() => {
      callbackRef.current = callback;
    });
    return React12.useMemo(() => (...args) => callbackRef.current?.(...args), []);
  }
  __name(useCallbackRef, "useCallbackRef");

  // node_modules/@radix-ui/react-use-escape-keydown/dist/index.mjs
  var React13 = __toESM(require_react(), 1);
  function useEscapeKeydown(onEscapeKeyDownProp, ownerDocument = globalThis?.document) {
    const onEscapeKeyDown = useCallbackRef(onEscapeKeyDownProp);
    React13.useEffect(() => {
      const handleKeyDown = /* @__PURE__ */ __name((event) => {
        if (event.key === "Escape") {
          onEscapeKeyDown(event);
        }
      }, "handleKeyDown");
      ownerDocument.addEventListener("keydown", handleKeyDown, { capture: true });
      return () => ownerDocument.removeEventListener("keydown", handleKeyDown, { capture: true });
    }, [onEscapeKeyDown, ownerDocument]);
  }
  __name(useEscapeKeydown, "useEscapeKeydown");

  // node_modules/@radix-ui/react-dismissable-layer/dist/index.mjs
  var import_jsx_runtime9 = __toESM(require_jsx_runtime(), 1);
  var DISMISSABLE_LAYER_NAME = "DismissableLayer";
  var CONTEXT_UPDATE = "dismissableLayer.update";
  var POINTER_DOWN_OUTSIDE = "dismissableLayer.pointerDownOutside";
  var FOCUS_OUTSIDE = "dismissableLayer.focusOutside";
  var originalBodyPointerEvents;
  var DismissableLayerContext = React14.createContext({
    layers: /* @__PURE__ */ new Set(),
    layersWithOutsidePointerEventsDisabled: /* @__PURE__ */ new Set(),
    branches: /* @__PURE__ */ new Set()
  });
  var DismissableLayer = React14.forwardRef(
    (props, forwardedRef) => {
      const {
        disableOutsidePointerEvents = false,
        onEscapeKeyDown,
        onPointerDownOutside,
        onFocusOutside,
        onInteractOutside,
        onDismiss,
        ...layerProps
      } = props;
      const context = React14.useContext(DismissableLayerContext);
      const [node, setNode] = React14.useState(null);
      const ownerDocument = node?.ownerDocument ?? globalThis?.document;
      const [, force] = React14.useState({});
      const composedRefs = useComposedRefs(forwardedRef, (node2) => setNode(node2));
      const layers = Array.from(context.layers);
      const [highestLayerWithOutsidePointerEventsDisabled] = [...context.layersWithOutsidePointerEventsDisabled].slice(-1);
      const highestLayerWithOutsidePointerEventsDisabledIndex = layers.indexOf(highestLayerWithOutsidePointerEventsDisabled);
      const index2 = node ? layers.indexOf(node) : -1;
      const isBodyPointerEventsDisabled = context.layersWithOutsidePointerEventsDisabled.size > 0;
      const isPointerEventsEnabled = index2 >= highestLayerWithOutsidePointerEventsDisabledIndex;
      const pointerDownOutside = usePointerDownOutside((event) => {
        const target = event.target;
        const isPointerDownOnBranch = [...context.branches].some((branch) => branch.contains(target));
        if (!isPointerEventsEnabled || isPointerDownOnBranch) return;
        onPointerDownOutside?.(event);
        onInteractOutside?.(event);
        if (!event.defaultPrevented) onDismiss?.();
      }, ownerDocument);
      const focusOutside = useFocusOutside((event) => {
        const target = event.target;
        const isFocusInBranch = [...context.branches].some((branch) => branch.contains(target));
        if (isFocusInBranch) return;
        onFocusOutside?.(event);
        onInteractOutside?.(event);
        if (!event.defaultPrevented) onDismiss?.();
      }, ownerDocument);
      useEscapeKeydown((event) => {
        const isHighestLayer = index2 === context.layers.size - 1;
        if (!isHighestLayer) return;
        onEscapeKeyDown?.(event);
        if (!event.defaultPrevented && onDismiss) {
          event.preventDefault();
          onDismiss();
        }
      }, ownerDocument);
      React14.useEffect(() => {
        if (!node) return;
        if (disableOutsidePointerEvents) {
          if (context.layersWithOutsidePointerEventsDisabled.size === 0) {
            originalBodyPointerEvents = ownerDocument.body.style.pointerEvents;
            ownerDocument.body.style.pointerEvents = "none";
          }
          context.layersWithOutsidePointerEventsDisabled.add(node);
        }
        context.layers.add(node);
        dispatchUpdate();
        return () => {
          if (disableOutsidePointerEvents && context.layersWithOutsidePointerEventsDisabled.size === 1) {
            ownerDocument.body.style.pointerEvents = originalBodyPointerEvents;
          }
        };
      }, [node, ownerDocument, disableOutsidePointerEvents, context]);
      React14.useEffect(() => {
        return () => {
          if (!node) return;
          context.layers.delete(node);
          context.layersWithOutsidePointerEventsDisabled.delete(node);
          dispatchUpdate();
        };
      }, [node, context]);
      React14.useEffect(() => {
        const handleUpdate = /* @__PURE__ */ __name(() => force({}), "handleUpdate");
        document.addEventListener(CONTEXT_UPDATE, handleUpdate);
        return () => document.removeEventListener(CONTEXT_UPDATE, handleUpdate);
      }, []);
      return /* @__PURE__ */ (0, import_jsx_runtime9.jsx)(
        Primitive.div,
        {
          ...layerProps,
          ref: composedRefs,
          style: {
            pointerEvents: isBodyPointerEventsDisabled ? isPointerEventsEnabled ? "auto" : "none" : void 0,
            ...props.style
          },
          onFocusCapture: composeEventHandlers(props.onFocusCapture, focusOutside.onFocusCapture),
          onBlurCapture: composeEventHandlers(props.onBlurCapture, focusOutside.onBlurCapture),
          onPointerDownCapture: composeEventHandlers(
            props.onPointerDownCapture,
            pointerDownOutside.onPointerDownCapture
          )
        }
      );
    }
  );
  DismissableLayer.displayName = DISMISSABLE_LAYER_NAME;
  var BRANCH_NAME = "DismissableLayerBranch";
  var DismissableLayerBranch = React14.forwardRef((props, forwardedRef) => {
    const context = React14.useContext(DismissableLayerContext);
    const ref = React14.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    React14.useEffect(() => {
      const node = ref.current;
      if (node) {
        context.branches.add(node);
        return () => {
          context.branches.delete(node);
        };
      }
    }, [context.branches]);
    return /* @__PURE__ */ (0, import_jsx_runtime9.jsx)(Primitive.div, { ...props, ref: composedRefs });
  });
  DismissableLayerBranch.displayName = BRANCH_NAME;
  function usePointerDownOutside(onPointerDownOutside, ownerDocument = globalThis?.document) {
    const handlePointerDownOutside = useCallbackRef(onPointerDownOutside);
    const isPointerInsideReactTreeRef = React14.useRef(false);
    const handleClickRef = React14.useRef(() => {
    });
    React14.useEffect(() => {
      const handlePointerDown = /* @__PURE__ */ __name((event) => {
        if (event.target && !isPointerInsideReactTreeRef.current) {
          let handleAndDispatchPointerDownOutsideEvent2 = /* @__PURE__ */ __name(function() {
            handleAndDispatchCustomEvent(
              POINTER_DOWN_OUTSIDE,
              handlePointerDownOutside,
              eventDetail,
              { discrete: true }
            );
          }, "handleAndDispatchPointerDownOutsideEvent2");
          var handleAndDispatchPointerDownOutsideEvent = handleAndDispatchPointerDownOutsideEvent2;
          const eventDetail = { originalEvent: event };
          if (event.pointerType === "touch") {
            ownerDocument.removeEventListener("click", handleClickRef.current);
            handleClickRef.current = handleAndDispatchPointerDownOutsideEvent2;
            ownerDocument.addEventListener("click", handleClickRef.current, { once: true });
          } else {
            handleAndDispatchPointerDownOutsideEvent2();
          }
        } else {
          ownerDocument.removeEventListener("click", handleClickRef.current);
        }
        isPointerInsideReactTreeRef.current = false;
      }, "handlePointerDown");
      const timerId = window.setTimeout(() => {
        ownerDocument.addEventListener("pointerdown", handlePointerDown);
      }, 0);
      return () => {
        window.clearTimeout(timerId);
        ownerDocument.removeEventListener("pointerdown", handlePointerDown);
        ownerDocument.removeEventListener("click", handleClickRef.current);
      };
    }, [ownerDocument, handlePointerDownOutside]);
    return {
      // ensures we check React component tree (not just DOM tree)
      onPointerDownCapture: /* @__PURE__ */ __name(() => isPointerInsideReactTreeRef.current = true, "onPointerDownCapture")
    };
  }
  __name(usePointerDownOutside, "usePointerDownOutside");
  function useFocusOutside(onFocusOutside, ownerDocument = globalThis?.document) {
    const handleFocusOutside = useCallbackRef(onFocusOutside);
    const isFocusInsideReactTreeRef = React14.useRef(false);
    React14.useEffect(() => {
      const handleFocus = /* @__PURE__ */ __name((event) => {
        if (event.target && !isFocusInsideReactTreeRef.current) {
          const eventDetail = { originalEvent: event };
          handleAndDispatchCustomEvent(FOCUS_OUTSIDE, handleFocusOutside, eventDetail, {
            discrete: false
          });
        }
      }, "handleFocus");
      ownerDocument.addEventListener("focusin", handleFocus);
      return () => ownerDocument.removeEventListener("focusin", handleFocus);
    }, [ownerDocument, handleFocusOutside]);
    return {
      onFocusCapture: /* @__PURE__ */ __name(() => isFocusInsideReactTreeRef.current = true, "onFocusCapture"),
      onBlurCapture: /* @__PURE__ */ __name(() => isFocusInsideReactTreeRef.current = false, "onBlurCapture")
    };
  }
  __name(useFocusOutside, "useFocusOutside");
  function dispatchUpdate() {
    const event = new CustomEvent(CONTEXT_UPDATE);
    document.dispatchEvent(event);
  }
  __name(dispatchUpdate, "dispatchUpdate");
  function handleAndDispatchCustomEvent(name, handler, detail, { discrete }) {
    const target = detail.originalEvent.target;
    const event = new CustomEvent(name, { bubbles: false, cancelable: true, detail });
    if (handler) target.addEventListener(name, handler, { once: true });
    if (discrete) {
      dispatchDiscreteCustomEvent(target, event);
    } else {
      target.dispatchEvent(event);
    }
  }
  __name(handleAndDispatchCustomEvent, "handleAndDispatchCustomEvent");

  // node_modules/@radix-ui/react-focus-scope/dist/index.mjs
  var React15 = __toESM(require_react(), 1);
  var import_jsx_runtime10 = __toESM(require_jsx_runtime(), 1);
  var AUTOFOCUS_ON_MOUNT = "focusScope.autoFocusOnMount";
  var AUTOFOCUS_ON_UNMOUNT = "focusScope.autoFocusOnUnmount";
  var EVENT_OPTIONS = { bubbles: false, cancelable: true };
  var FOCUS_SCOPE_NAME = "FocusScope";
  var FocusScope = React15.forwardRef((props, forwardedRef) => {
    const {
      loop = false,
      trapped = false,
      onMountAutoFocus: onMountAutoFocusProp,
      onUnmountAutoFocus: onUnmountAutoFocusProp,
      ...scopeProps
    } = props;
    const [container, setContainer] = React15.useState(null);
    const onMountAutoFocus = useCallbackRef(onMountAutoFocusProp);
    const onUnmountAutoFocus = useCallbackRef(onUnmountAutoFocusProp);
    const lastFocusedElementRef = React15.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, (node) => setContainer(node));
    const focusScope = React15.useRef({
      paused: false,
      pause() {
        this.paused = true;
      },
      resume() {
        this.paused = false;
      }
    }).current;
    React15.useEffect(() => {
      if (trapped) {
        let handleFocusIn2 = /* @__PURE__ */ __name(function(event) {
          if (focusScope.paused || !container) return;
          const target = event.target;
          if (container.contains(target)) {
            lastFocusedElementRef.current = target;
          } else {
            focus(lastFocusedElementRef.current, { select: true });
          }
        }, "handleFocusIn2"), handleFocusOut2 = /* @__PURE__ */ __name(function(event) {
          if (focusScope.paused || !container) return;
          const relatedTarget = event.relatedTarget;
          if (relatedTarget === null) return;
          if (!container.contains(relatedTarget)) {
            focus(lastFocusedElementRef.current, { select: true });
          }
        }, "handleFocusOut2"), handleMutations2 = /* @__PURE__ */ __name(function(mutations) {
          const focusedElement = document.activeElement;
          if (focusedElement !== document.body) return;
          for (const mutation of mutations) {
            if (mutation.removedNodes.length > 0) focus(container);
          }
        }, "handleMutations2");
        var handleFocusIn = handleFocusIn2, handleFocusOut = handleFocusOut2, handleMutations = handleMutations2;
        document.addEventListener("focusin", handleFocusIn2);
        document.addEventListener("focusout", handleFocusOut2);
        const mutationObserver = new MutationObserver(handleMutations2);
        if (container) mutationObserver.observe(container, { childList: true, subtree: true });
        return () => {
          document.removeEventListener("focusin", handleFocusIn2);
          document.removeEventListener("focusout", handleFocusOut2);
          mutationObserver.disconnect();
        };
      }
    }, [trapped, container, focusScope.paused]);
    React15.useEffect(() => {
      if (container) {
        focusScopesStack.add(focusScope);
        const previouslyFocusedElement = document.activeElement;
        const hasFocusedCandidate = container.contains(previouslyFocusedElement);
        if (!hasFocusedCandidate) {
          const mountEvent = new CustomEvent(AUTOFOCUS_ON_MOUNT, EVENT_OPTIONS);
          container.addEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
          container.dispatchEvent(mountEvent);
          if (!mountEvent.defaultPrevented) {
            focusFirst(removeLinks(getTabbableCandidates(container)), { select: true });
            if (document.activeElement === previouslyFocusedElement) {
              focus(container);
            }
          }
        }
        return () => {
          container.removeEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
          setTimeout(() => {
            const unmountEvent = new CustomEvent(AUTOFOCUS_ON_UNMOUNT, EVENT_OPTIONS);
            container.addEventListener(AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
            container.dispatchEvent(unmountEvent);
            if (!unmountEvent.defaultPrevented) {
              focus(previouslyFocusedElement ?? document.body, { select: true });
            }
            container.removeEventListener(AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
            focusScopesStack.remove(focusScope);
          }, 0);
        };
      }
    }, [container, onMountAutoFocus, onUnmountAutoFocus, focusScope]);
    const handleKeyDown = React15.useCallback(
      (event) => {
        if (!loop && !trapped) return;
        if (focusScope.paused) return;
        const isTabKey = event.key === "Tab" && !event.altKey && !event.ctrlKey && !event.metaKey;
        const focusedElement = document.activeElement;
        if (isTabKey && focusedElement) {
          const container2 = event.currentTarget;
          const [first, last] = getTabbableEdges(container2);
          const hasTabbableElementsInside = first && last;
          if (!hasTabbableElementsInside) {
            if (focusedElement === container2) event.preventDefault();
          } else {
            if (!event.shiftKey && focusedElement === last) {
              event.preventDefault();
              if (loop) focus(first, { select: true });
            } else if (event.shiftKey && focusedElement === first) {
              event.preventDefault();
              if (loop) focus(last, { select: true });
            }
          }
        }
      },
      [loop, trapped, focusScope.paused]
    );
    return /* @__PURE__ */ (0, import_jsx_runtime10.jsx)(Primitive.div, { tabIndex: -1, ...scopeProps, ref: composedRefs, onKeyDown: handleKeyDown });
  });
  FocusScope.displayName = FOCUS_SCOPE_NAME;
  function focusFirst(candidates, { select = false } = {}) {
    const previouslyFocusedElement = document.activeElement;
    for (const candidate of candidates) {
      focus(candidate, { select });
      if (document.activeElement !== previouslyFocusedElement) return;
    }
  }
  __name(focusFirst, "focusFirst");
  function getTabbableEdges(container) {
    const candidates = getTabbableCandidates(container);
    const first = findVisible(candidates, container);
    const last = findVisible(candidates.reverse(), container);
    return [first, last];
  }
  __name(getTabbableEdges, "getTabbableEdges");
  function getTabbableCandidates(container) {
    const nodes = [];
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
      acceptNode: /* @__PURE__ */ __name((node) => {
        const isHiddenInput = node.tagName === "INPUT" && node.type === "hidden";
        if (node.disabled || node.hidden || isHiddenInput) return NodeFilter.FILTER_SKIP;
        return node.tabIndex >= 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      }, "acceptNode")
    });
    while (walker.nextNode()) nodes.push(walker.currentNode);
    return nodes;
  }
  __name(getTabbableCandidates, "getTabbableCandidates");
  function findVisible(elements, container) {
    for (const element of elements) {
      if (!isHidden(element, { upTo: container })) return element;
    }
  }
  __name(findVisible, "findVisible");
  function isHidden(node, { upTo }) {
    if (getComputedStyle(node).visibility === "hidden") return true;
    while (node) {
      if (upTo !== void 0 && node === upTo) return false;
      if (getComputedStyle(node).display === "none") return true;
      node = node.parentElement;
    }
    return false;
  }
  __name(isHidden, "isHidden");
  function isSelectableInput(element) {
    return element instanceof HTMLInputElement && "select" in element;
  }
  __name(isSelectableInput, "isSelectableInput");
  function focus(element, { select = false } = {}) {
    if (element && element.focus) {
      const previouslyFocusedElement = document.activeElement;
      element.focus({ preventScroll: true });
      if (element !== previouslyFocusedElement && isSelectableInput(element) && select)
        element.select();
    }
  }
  __name(focus, "focus");
  var focusScopesStack = createFocusScopesStack();
  function createFocusScopesStack() {
    let stack = [];
    return {
      add(focusScope) {
        const activeFocusScope = stack[0];
        if (focusScope !== activeFocusScope) {
          activeFocusScope?.pause();
        }
        stack = arrayRemove(stack, focusScope);
        stack.unshift(focusScope);
      },
      remove(focusScope) {
        stack = arrayRemove(stack, focusScope);
        stack[0]?.resume();
      }
    };
  }
  __name(createFocusScopesStack, "createFocusScopesStack");
  function arrayRemove(array, item) {
    const updatedArray = [...array];
    const index2 = updatedArray.indexOf(item);
    if (index2 !== -1) {
      updatedArray.splice(index2, 1);
    }
    return updatedArray;
  }
  __name(arrayRemove, "arrayRemove");
  function removeLinks(items) {
    return items.filter((item) => item.tagName !== "A");
  }
  __name(removeLinks, "removeLinks");

  // node_modules/@radix-ui/react-portal/dist/index.mjs
  var React16 = __toESM(require_react(), 1);
  var import_react_dom = __toESM(require_react_dom(), 1);
  var import_jsx_runtime11 = __toESM(require_jsx_runtime(), 1);
  var PORTAL_NAME = "Portal";
  var Portal = React16.forwardRef((props, forwardedRef) => {
    const { container: containerProp, ...portalProps } = props;
    const [mounted, setMounted] = React16.useState(false);
    useLayoutEffect22(() => setMounted(true), []);
    const container = containerProp || mounted && globalThis?.document?.body;
    return container ? import_react_dom.default.createPortal(/* @__PURE__ */ (0, import_jsx_runtime11.jsx)(Primitive.div, { ...portalProps, ref: forwardedRef }), container) : null;
  });
  Portal.displayName = PORTAL_NAME;

  // node_modules/@radix-ui/react-focus-guards/dist/index.mjs
  var React17 = __toESM(require_react(), 1);
  var count2 = 0;
  function useFocusGuards() {
    React17.useEffect(() => {
      const edgeGuards = document.querySelectorAll("[data-radix-focus-guard]");
      document.body.insertAdjacentElement("afterbegin", edgeGuards[0] ?? createFocusGuard());
      document.body.insertAdjacentElement("beforeend", edgeGuards[1] ?? createFocusGuard());
      count2++;
      return () => {
        if (count2 === 1) {
          document.querySelectorAll("[data-radix-focus-guard]").forEach((node) => node.remove());
        }
        count2--;
      };
    }, []);
  }
  __name(useFocusGuards, "useFocusGuards");
  function createFocusGuard() {
    const element = document.createElement("span");
    element.setAttribute("data-radix-focus-guard", "");
    element.tabIndex = 0;
    element.style.outline = "none";
    element.style.opacity = "0";
    element.style.position = "fixed";
    element.style.pointerEvents = "none";
    return element;
  }
  __name(createFocusGuard, "createFocusGuard");

  // node_modules/tslib/tslib.es6.mjs
  var __assign = /* @__PURE__ */ __name(function() {
    __assign = Object.assign || /* @__PURE__ */ __name(function __assign2(t) {
      for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
      return t;
    }, "__assign");
    return __assign.apply(this, arguments);
  }, "__assign");
  function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
      t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
      for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
        if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
          t[p[i]] = s[p[i]];
      }
    return t;
  }
  __name(__rest, "__rest");
  function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
      if (ar || !(i in from)) {
        if (!ar) ar = Array.prototype.slice.call(from, 0, i);
        ar[i] = from[i];
      }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
  }
  __name(__spreadArray, "__spreadArray");

  // node_modules/react-remove-scroll/dist/es2015/Combination.js
  var React27 = __toESM(require_react());

  // node_modules/react-remove-scroll/dist/es2015/UI.js
  var React20 = __toESM(require_react());

  // node_modules/react-remove-scroll-bar/dist/es2015/constants.js
  var zeroRightClassName = "right-scroll-bar-position";
  var fullWidthClassName = "width-before-scroll-bar";
  var noScrollbarsClassName = "with-scroll-bars-hidden";
  var removedBarSizeVariable = "--removed-body-scroll-bar-size";

  // node_modules/use-callback-ref/dist/es2015/assignRef.js
  function assignRef(ref, value) {
    if (typeof ref === "function") {
      ref(value);
    } else if (ref) {
      ref.current = value;
    }
    return ref;
  }
  __name(assignRef, "assignRef");

  // node_modules/use-callback-ref/dist/es2015/useRef.js
  var import_react4 = __toESM(require_react());
  function useCallbackRef2(initialValue, callback) {
    var ref = (0, import_react4.useState)(function() {
      return {
        // value
        value: initialValue,
        // last callback
        callback,
        // "memoized" public interface
        facade: {
          get current() {
            return ref.value;
          },
          set current(value) {
            var last = ref.value;
            if (last !== value) {
              ref.value = value;
              ref.callback(value, last);
            }
          }
        }
      };
    })[0];
    ref.callback = callback;
    return ref.facade;
  }
  __name(useCallbackRef2, "useCallbackRef");

  // node_modules/use-callback-ref/dist/es2015/useMergeRef.js
  var React18 = __toESM(require_react());
  var useIsomorphicLayoutEffect = typeof window !== "undefined" ? React18.useLayoutEffect : React18.useEffect;
  var currentValues = /* @__PURE__ */ new WeakMap();
  function useMergeRefs(refs, defaultValue) {
    var callbackRef = useCallbackRef2(defaultValue || null, function(newValue) {
      return refs.forEach(function(ref) {
        return assignRef(ref, newValue);
      });
    });
    useIsomorphicLayoutEffect(function() {
      var oldValue = currentValues.get(callbackRef);
      if (oldValue) {
        var prevRefs_1 = new Set(oldValue);
        var nextRefs_1 = new Set(refs);
        var current_1 = callbackRef.current;
        prevRefs_1.forEach(function(ref) {
          if (!nextRefs_1.has(ref)) {
            assignRef(ref, null);
          }
        });
        nextRefs_1.forEach(function(ref) {
          if (!prevRefs_1.has(ref)) {
            assignRef(ref, current_1);
          }
        });
      }
      currentValues.set(callbackRef, refs);
    }, [refs]);
    return callbackRef;
  }
  __name(useMergeRefs, "useMergeRefs");

  // node_modules/use-sidecar/dist/es2015/medium.js
  function ItoI(a) {
    return a;
  }
  __name(ItoI, "ItoI");
  function innerCreateMedium(defaults, middleware) {
    if (middleware === void 0) {
      middleware = ItoI;
    }
    var buffer = [];
    var assigned = false;
    var medium = {
      read: /* @__PURE__ */ __name(function() {
        if (assigned) {
          throw new Error("Sidecar: could not `read` from an `assigned` medium. `read` could be used only with `useMedium`.");
        }
        if (buffer.length) {
          return buffer[buffer.length - 1];
        }
        return defaults;
      }, "read"),
      useMedium: /* @__PURE__ */ __name(function(data) {
        var item = middleware(data, assigned);
        buffer.push(item);
        return function() {
          buffer = buffer.filter(function(x) {
            return x !== item;
          });
        };
      }, "useMedium"),
      assignSyncMedium: /* @__PURE__ */ __name(function(cb) {
        assigned = true;
        while (buffer.length) {
          var cbs = buffer;
          buffer = [];
          cbs.forEach(cb);
        }
        buffer = {
          push: /* @__PURE__ */ __name(function(x) {
            return cb(x);
          }, "push"),
          filter: /* @__PURE__ */ __name(function() {
            return buffer;
          }, "filter")
        };
      }, "assignSyncMedium"),
      assignMedium: /* @__PURE__ */ __name(function(cb) {
        assigned = true;
        var pendingQueue = [];
        if (buffer.length) {
          var cbs = buffer;
          buffer = [];
          cbs.forEach(cb);
          pendingQueue = buffer;
        }
        var executeQueue = /* @__PURE__ */ __name(function() {
          var cbs2 = pendingQueue;
          pendingQueue = [];
          cbs2.forEach(cb);
        }, "executeQueue");
        var cycle = /* @__PURE__ */ __name(function() {
          return Promise.resolve().then(executeQueue);
        }, "cycle");
        cycle();
        buffer = {
          push: /* @__PURE__ */ __name(function(x) {
            pendingQueue.push(x);
            cycle();
          }, "push"),
          filter: /* @__PURE__ */ __name(function(filter) {
            pendingQueue = pendingQueue.filter(filter);
            return buffer;
          }, "filter")
        };
      }, "assignMedium")
    };
    return medium;
  }
  __name(innerCreateMedium, "innerCreateMedium");
  function createSidecarMedium(options) {
    if (options === void 0) {
      options = {};
    }
    var medium = innerCreateMedium(null);
    medium.options = __assign({ async: true, ssr: false }, options);
    return medium;
  }
  __name(createSidecarMedium, "createSidecarMedium");

  // node_modules/use-sidecar/dist/es2015/exports.js
  var React19 = __toESM(require_react());
  var SideCar = /* @__PURE__ */ __name(function(_a) {
    var sideCar = _a.sideCar, rest = __rest(_a, ["sideCar"]);
    if (!sideCar) {
      throw new Error("Sidecar: please provide `sideCar` property to import the right car");
    }
    var Target = sideCar.read();
    if (!Target) {
      throw new Error("Sidecar medium not found");
    }
    return React19.createElement(Target, __assign({}, rest));
  }, "SideCar");
  SideCar.isSideCarExport = true;
  function exportSidecar(medium, exported) {
    medium.useMedium(exported);
    return SideCar;
  }
  __name(exportSidecar, "exportSidecar");

  // node_modules/react-remove-scroll/dist/es2015/medium.js
  var effectCar = createSidecarMedium();

  // node_modules/react-remove-scroll/dist/es2015/UI.js
  var nothing = /* @__PURE__ */ __name(function() {
    return;
  }, "nothing");
  var RemoveScroll = React20.forwardRef(function(props, parentRef) {
    var ref = React20.useRef(null);
    var _a = React20.useState({
      onScrollCapture: nothing,
      onWheelCapture: nothing,
      onTouchMoveCapture: nothing
    }), callbacks = _a[0], setCallbacks = _a[1];
    var forwardProps = props.forwardProps, children = props.children, className = props.className, removeScrollBar = props.removeScrollBar, enabled = props.enabled, shards = props.shards, sideCar = props.sideCar, noRelative = props.noRelative, noIsolation = props.noIsolation, inert = props.inert, allowPinchZoom = props.allowPinchZoom, _b = props.as, Container = _b === void 0 ? "div" : _b, gapMode = props.gapMode, rest = __rest(props, ["forwardProps", "children", "className", "removeScrollBar", "enabled", "shards", "sideCar", "noRelative", "noIsolation", "inert", "allowPinchZoom", "as", "gapMode"]);
    var SideCar2 = sideCar;
    var containerRef = useMergeRefs([ref, parentRef]);
    var containerProps = __assign(__assign({}, rest), callbacks);
    return React20.createElement(
      React20.Fragment,
      null,
      enabled && React20.createElement(SideCar2, { sideCar: effectCar, removeScrollBar, shards, noRelative, noIsolation, inert, setCallbacks, allowPinchZoom: !!allowPinchZoom, lockRef: ref, gapMode }),
      forwardProps ? React20.cloneElement(React20.Children.only(children), __assign(__assign({}, containerProps), { ref: containerRef })) : React20.createElement(Container, __assign({}, containerProps, { className, ref: containerRef }), children)
    );
  });
  RemoveScroll.defaultProps = {
    enabled: true,
    removeScrollBar: true,
    inert: false
  };
  RemoveScroll.classNames = {
    fullWidth: fullWidthClassName,
    zeroRight: zeroRightClassName
  };

  // node_modules/react-remove-scroll/dist/es2015/SideEffect.js
  var React26 = __toESM(require_react());

  // node_modules/react-remove-scroll-bar/dist/es2015/component.js
  var React25 = __toESM(require_react());

  // node_modules/react-style-singleton/dist/es2015/hook.js
  var React21 = __toESM(require_react());

  // node_modules/get-nonce/dist/es2015/index.js
  var currentNonce;
  var getNonce = /* @__PURE__ */ __name(function() {
    if (currentNonce) {
      return currentNonce;
    }
    if (typeof __webpack_nonce__ !== "undefined") {
      return __webpack_nonce__;
    }
    return void 0;
  }, "getNonce");

  // node_modules/react-style-singleton/dist/es2015/singleton.js
  function makeStyleTag() {
    if (!document)
      return null;
    var tag = document.createElement("style");
    tag.type = "text/css";
    var nonce = getNonce();
    if (nonce) {
      tag.setAttribute("nonce", nonce);
    }
    return tag;
  }
  __name(makeStyleTag, "makeStyleTag");
  function injectStyles(tag, css) {
    if (tag.styleSheet) {
      tag.styleSheet.cssText = css;
    } else {
      tag.appendChild(document.createTextNode(css));
    }
  }
  __name(injectStyles, "injectStyles");
  function insertStyleTag(tag) {
    var head = document.head || document.getElementsByTagName("head")[0];
    head.appendChild(tag);
  }
  __name(insertStyleTag, "insertStyleTag");
  var stylesheetSingleton = /* @__PURE__ */ __name(function() {
    var counter = 0;
    var stylesheet = null;
    return {
      add: /* @__PURE__ */ __name(function(style) {
        if (counter == 0) {
          if (stylesheet = makeStyleTag()) {
            injectStyles(stylesheet, style);
            insertStyleTag(stylesheet);
          }
        }
        counter++;
      }, "add"),
      remove: /* @__PURE__ */ __name(function() {
        counter--;
        if (!counter && stylesheet) {
          stylesheet.parentNode && stylesheet.parentNode.removeChild(stylesheet);
          stylesheet = null;
        }
      }, "remove")
    };
  }, "stylesheetSingleton");

  // node_modules/react-style-singleton/dist/es2015/hook.js
  var styleHookSingleton = /* @__PURE__ */ __name(function() {
    var sheet = stylesheetSingleton();
    return function(styles, isDynamic) {
      React21.useEffect(function() {
        sheet.add(styles);
        return function() {
          sheet.remove();
        };
      }, [styles && isDynamic]);
    };
  }, "styleHookSingleton");

  // node_modules/react-style-singleton/dist/es2015/component.js
  var styleSingleton = /* @__PURE__ */ __name(function() {
    var useStyle = styleHookSingleton();
    var Sheet = /* @__PURE__ */ __name(function(_a) {
      var styles = _a.styles, dynamic = _a.dynamic;
      useStyle(styles, dynamic);
      return null;
    }, "Sheet");
    return Sheet;
  }, "styleSingleton");

  // node_modules/react-remove-scroll-bar/dist/es2015/utils.js
  var zeroGap = {
    left: 0,
    top: 0,
    right: 0,
    gap: 0
  };
  var parse = /* @__PURE__ */ __name(function(x) {
    return parseInt(x || "", 10) || 0;
  }, "parse");
  var getOffset = /* @__PURE__ */ __name(function(gapMode) {
    var cs = window.getComputedStyle(document.body);
    var left = cs[gapMode === "padding" ? "paddingLeft" : "marginLeft"];
    var top = cs[gapMode === "padding" ? "paddingTop" : "marginTop"];
    var right = cs[gapMode === "padding" ? "paddingRight" : "marginRight"];
    return [parse(left), parse(top), parse(right)];
  }, "getOffset");
  var getGapWidth = /* @__PURE__ */ __name(function(gapMode) {
    if (gapMode === void 0) {
      gapMode = "margin";
    }
    if (typeof window === "undefined") {
      return zeroGap;
    }
    var offsets = getOffset(gapMode);
    var documentWidth = document.documentElement.clientWidth;
    var windowWidth = window.innerWidth;
    return {
      left: offsets[0],
      top: offsets[1],
      right: offsets[2],
      gap: Math.max(0, windowWidth - documentWidth + offsets[2] - offsets[0])
    };
  }, "getGapWidth");

  // node_modules/react-remove-scroll-bar/dist/es2015/component.js
  var Style = styleSingleton();
  var lockAttribute = "data-scroll-locked";
  var getStyles = /* @__PURE__ */ __name(function(_a, allowRelative, gapMode, important) {
    var left = _a.left, top = _a.top, right = _a.right, gap = _a.gap;
    if (gapMode === void 0) {
      gapMode = "margin";
    }
    return "\n  .".concat(noScrollbarsClassName, " {\n   overflow: hidden ").concat(important, ";\n   padding-right: ").concat(gap, "px ").concat(important, ";\n  }\n  body[").concat(lockAttribute, "] {\n    overflow: hidden ").concat(important, ";\n    overscroll-behavior: contain;\n    ").concat([
      allowRelative && "position: relative ".concat(important, ";"),
      gapMode === "margin" && "\n    padding-left: ".concat(left, "px;\n    padding-top: ").concat(top, "px;\n    padding-right: ").concat(right, "px;\n    margin-left:0;\n    margin-top:0;\n    margin-right: ").concat(gap, "px ").concat(important, ";\n    "),
      gapMode === "padding" && "padding-right: ".concat(gap, "px ").concat(important, ";")
    ].filter(Boolean).join(""), "\n  }\n  \n  .").concat(zeroRightClassName, " {\n    right: ").concat(gap, "px ").concat(important, ";\n  }\n  \n  .").concat(fullWidthClassName, " {\n    margin-right: ").concat(gap, "px ").concat(important, ";\n  }\n  \n  .").concat(zeroRightClassName, " .").concat(zeroRightClassName, " {\n    right: 0 ").concat(important, ";\n  }\n  \n  .").concat(fullWidthClassName, " .").concat(fullWidthClassName, " {\n    margin-right: 0 ").concat(important, ";\n  }\n  \n  body[").concat(lockAttribute, "] {\n    ").concat(removedBarSizeVariable, ": ").concat(gap, "px;\n  }\n");
  }, "getStyles");
  var getCurrentUseCounter = /* @__PURE__ */ __name(function() {
    var counter = parseInt(document.body.getAttribute(lockAttribute) || "0", 10);
    return isFinite(counter) ? counter : 0;
  }, "getCurrentUseCounter");
  var useLockAttribute = /* @__PURE__ */ __name(function() {
    React25.useEffect(function() {
      document.body.setAttribute(lockAttribute, (getCurrentUseCounter() + 1).toString());
      return function() {
        var newCounter = getCurrentUseCounter() - 1;
        if (newCounter <= 0) {
          document.body.removeAttribute(lockAttribute);
        } else {
          document.body.setAttribute(lockAttribute, newCounter.toString());
        }
      };
    }, []);
  }, "useLockAttribute");
  var RemoveScrollBar = /* @__PURE__ */ __name(function(_a) {
    var noRelative = _a.noRelative, noImportant = _a.noImportant, _b = _a.gapMode, gapMode = _b === void 0 ? "margin" : _b;
    useLockAttribute();
    var gap = React25.useMemo(function() {
      return getGapWidth(gapMode);
    }, [gapMode]);
    return React25.createElement(Style, { styles: getStyles(gap, !noRelative, gapMode, !noImportant ? "!important" : "") });
  }, "RemoveScrollBar");

  // node_modules/react-remove-scroll/dist/es2015/aggresiveCapture.js
  var passiveSupported = false;
  if (typeof window !== "undefined") {
    try {
      options = Object.defineProperty({}, "passive", {
        get: /* @__PURE__ */ __name(function() {
          passiveSupported = true;
          return true;
        }, "get")
      });
      window.addEventListener("test", options, options);
      window.removeEventListener("test", options, options);
    } catch (err) {
      passiveSupported = false;
    }
  }
  var options;
  var nonPassive = passiveSupported ? { passive: false } : false;

  // node_modules/react-remove-scroll/dist/es2015/handleScroll.js
  var alwaysContainsScroll = /* @__PURE__ */ __name(function(node) {
    return node.tagName === "TEXTAREA";
  }, "alwaysContainsScroll");
  var elementCanBeScrolled = /* @__PURE__ */ __name(function(node, overflow) {
    if (!(node instanceof Element)) {
      return false;
    }
    var styles = window.getComputedStyle(node);
    return (
      // not-not-scrollable
      styles[overflow] !== "hidden" && // contains scroll inside self
      !(styles.overflowY === styles.overflowX && !alwaysContainsScroll(node) && styles[overflow] === "visible")
    );
  }, "elementCanBeScrolled");
  var elementCouldBeVScrolled = /* @__PURE__ */ __name(function(node) {
    return elementCanBeScrolled(node, "overflowY");
  }, "elementCouldBeVScrolled");
  var elementCouldBeHScrolled = /* @__PURE__ */ __name(function(node) {
    return elementCanBeScrolled(node, "overflowX");
  }, "elementCouldBeHScrolled");
  var locationCouldBeScrolled = /* @__PURE__ */ __name(function(axis, node) {
    var ownerDocument = node.ownerDocument;
    var current = node;
    do {
      if (typeof ShadowRoot !== "undefined" && current instanceof ShadowRoot) {
        current = current.host;
      }
      var isScrollable = elementCouldBeScrolled(axis, current);
      if (isScrollable) {
        var _a = getScrollVariables(axis, current), scrollHeight = _a[1], clientHeight = _a[2];
        if (scrollHeight > clientHeight) {
          return true;
        }
      }
      current = current.parentNode;
    } while (current && current !== ownerDocument.body);
    return false;
  }, "locationCouldBeScrolled");
  var getVScrollVariables = /* @__PURE__ */ __name(function(_a) {
    var scrollTop = _a.scrollTop, scrollHeight = _a.scrollHeight, clientHeight = _a.clientHeight;
    return [
      scrollTop,
      scrollHeight,
      clientHeight
    ];
  }, "getVScrollVariables");
  var getHScrollVariables = /* @__PURE__ */ __name(function(_a) {
    var scrollLeft = _a.scrollLeft, scrollWidth = _a.scrollWidth, clientWidth = _a.clientWidth;
    return [
      scrollLeft,
      scrollWidth,
      clientWidth
    ];
  }, "getHScrollVariables");
  var elementCouldBeScrolled = /* @__PURE__ */ __name(function(axis, node) {
    return axis === "v" ? elementCouldBeVScrolled(node) : elementCouldBeHScrolled(node);
  }, "elementCouldBeScrolled");
  var getScrollVariables = /* @__PURE__ */ __name(function(axis, node) {
    return axis === "v" ? getVScrollVariables(node) : getHScrollVariables(node);
  }, "getScrollVariables");
  var getDirectionFactor = /* @__PURE__ */ __name(function(axis, direction) {
    return axis === "h" && direction === "rtl" ? -1 : 1;
  }, "getDirectionFactor");
  var handleScroll = /* @__PURE__ */ __name(function(axis, endTarget, event, sourceDelta, noOverscroll) {
    var directionFactor = getDirectionFactor(axis, window.getComputedStyle(endTarget).direction);
    var delta = directionFactor * sourceDelta;
    var target = event.target;
    var targetInLock = endTarget.contains(target);
    var shouldCancelScroll = false;
    var isDeltaPositive = delta > 0;
    var availableScroll = 0;
    var availableScrollTop = 0;
    do {
      if (!target) {
        break;
      }
      var _a = getScrollVariables(axis, target), position = _a[0], scroll_1 = _a[1], capacity = _a[2];
      var elementScroll = scroll_1 - capacity - directionFactor * position;
      if (position || elementScroll) {
        if (elementCouldBeScrolled(axis, target)) {
          availableScroll += elementScroll;
          availableScrollTop += position;
        }
      }
      var parent_1 = target.parentNode;
      target = parent_1 && parent_1.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? parent_1.host : parent_1;
    } while (
      // portaled content
      !targetInLock && target !== document.body || // self content
      targetInLock && (endTarget.contains(target) || endTarget === target)
    );
    if (isDeltaPositive && (noOverscroll && Math.abs(availableScroll) < 1 || !noOverscroll && delta > availableScroll)) {
      shouldCancelScroll = true;
    } else if (!isDeltaPositive && (noOverscroll && Math.abs(availableScrollTop) < 1 || !noOverscroll && -delta > availableScrollTop)) {
      shouldCancelScroll = true;
    }
    return shouldCancelScroll;
  }, "handleScroll");

  // node_modules/react-remove-scroll/dist/es2015/SideEffect.js
  var getTouchXY = /* @__PURE__ */ __name(function(event) {
    return "changedTouches" in event ? [event.changedTouches[0].clientX, event.changedTouches[0].clientY] : [0, 0];
  }, "getTouchXY");
  var getDeltaXY = /* @__PURE__ */ __name(function(event) {
    return [event.deltaX, event.deltaY];
  }, "getDeltaXY");
  var extractRef = /* @__PURE__ */ __name(function(ref) {
    return ref && "current" in ref ? ref.current : ref;
  }, "extractRef");
  var deltaCompare = /* @__PURE__ */ __name(function(x, y) {
    return x[0] === y[0] && x[1] === y[1];
  }, "deltaCompare");
  var generateStyle = /* @__PURE__ */ __name(function(id) {
    return "\n  .block-interactivity-".concat(id, " {pointer-events: none;}\n  .allow-interactivity-").concat(id, " {pointer-events: all;}\n");
  }, "generateStyle");
  var idCounter = 0;
  var lockStack = [];
  function RemoveScrollSideCar(props) {
    var shouldPreventQueue = React26.useRef([]);
    var touchStartRef = React26.useRef([0, 0]);
    var activeAxis = React26.useRef();
    var id = React26.useState(idCounter++)[0];
    var Style2 = React26.useState(styleSingleton)[0];
    var lastProps = React26.useRef(props);
    React26.useEffect(function() {
      lastProps.current = props;
    }, [props]);
    React26.useEffect(function() {
      if (props.inert) {
        document.body.classList.add("block-interactivity-".concat(id));
        var allow_1 = __spreadArray([props.lockRef.current], (props.shards || []).map(extractRef), true).filter(Boolean);
        allow_1.forEach(function(el) {
          return el.classList.add("allow-interactivity-".concat(id));
        });
        return function() {
          document.body.classList.remove("block-interactivity-".concat(id));
          allow_1.forEach(function(el) {
            return el.classList.remove("allow-interactivity-".concat(id));
          });
        };
      }
      return;
    }, [props.inert, props.lockRef.current, props.shards]);
    var shouldCancelEvent = React26.useCallback(function(event, parent) {
      if ("touches" in event && event.touches.length === 2 || event.type === "wheel" && event.ctrlKey) {
        return !lastProps.current.allowPinchZoom;
      }
      var touch = getTouchXY(event);
      var touchStart = touchStartRef.current;
      var deltaX = "deltaX" in event ? event.deltaX : touchStart[0] - touch[0];
      var deltaY = "deltaY" in event ? event.deltaY : touchStart[1] - touch[1];
      var currentAxis;
      var target = event.target;
      var moveDirection = Math.abs(deltaX) > Math.abs(deltaY) ? "h" : "v";
      if ("touches" in event && moveDirection === "h" && target.type === "range") {
        return false;
      }
      var selection = window.getSelection();
      var anchorNode = selection && selection.anchorNode;
      var isTouchingSelection = anchorNode ? anchorNode === target || anchorNode.contains(target) : false;
      if (isTouchingSelection) {
        return false;
      }
      var canBeScrolledInMainDirection = locationCouldBeScrolled(moveDirection, target);
      if (!canBeScrolledInMainDirection) {
        return true;
      }
      if (canBeScrolledInMainDirection) {
        currentAxis = moveDirection;
      } else {
        currentAxis = moveDirection === "v" ? "h" : "v";
        canBeScrolledInMainDirection = locationCouldBeScrolled(moveDirection, target);
      }
      if (!canBeScrolledInMainDirection) {
        return false;
      }
      if (!activeAxis.current && "changedTouches" in event && (deltaX || deltaY)) {
        activeAxis.current = currentAxis;
      }
      if (!currentAxis) {
        return true;
      }
      var cancelingAxis = activeAxis.current || currentAxis;
      return handleScroll(cancelingAxis, parent, event, cancelingAxis === "h" ? deltaX : deltaY, true);
    }, []);
    var shouldPrevent = React26.useCallback(function(_event) {
      var event = _event;
      if (!lockStack.length || lockStack[lockStack.length - 1] !== Style2) {
        return;
      }
      var delta = "deltaY" in event ? getDeltaXY(event) : getTouchXY(event);
      var sourceEvent = shouldPreventQueue.current.filter(function(e) {
        return e.name === event.type && (e.target === event.target || event.target === e.shadowParent) && deltaCompare(e.delta, delta);
      })[0];
      if (sourceEvent && sourceEvent.should) {
        if (event.cancelable) {
          event.preventDefault();
        }
        return;
      }
      if (!sourceEvent) {
        var shardNodes = (lastProps.current.shards || []).map(extractRef).filter(Boolean).filter(function(node) {
          return node.contains(event.target);
        });
        var shouldStop = shardNodes.length > 0 ? shouldCancelEvent(event, shardNodes[0]) : !lastProps.current.noIsolation;
        if (shouldStop) {
          if (event.cancelable) {
            event.preventDefault();
          }
        }
      }
    }, []);
    var shouldCancel = React26.useCallback(function(name, delta, target, should) {
      var event = { name, delta, target, should, shadowParent: getOutermostShadowParent(target) };
      shouldPreventQueue.current.push(event);
      setTimeout(function() {
        shouldPreventQueue.current = shouldPreventQueue.current.filter(function(e) {
          return e !== event;
        });
      }, 1);
    }, []);
    var scrollTouchStart = React26.useCallback(function(event) {
      touchStartRef.current = getTouchXY(event);
      activeAxis.current = void 0;
    }, []);
    var scrollWheel = React26.useCallback(function(event) {
      shouldCancel(event.type, getDeltaXY(event), event.target, shouldCancelEvent(event, props.lockRef.current));
    }, []);
    var scrollTouchMove = React26.useCallback(function(event) {
      shouldCancel(event.type, getTouchXY(event), event.target, shouldCancelEvent(event, props.lockRef.current));
    }, []);
    React26.useEffect(function() {
      lockStack.push(Style2);
      props.setCallbacks({
        onScrollCapture: scrollWheel,
        onWheelCapture: scrollWheel,
        onTouchMoveCapture: scrollTouchMove
      });
      document.addEventListener("wheel", shouldPrevent, nonPassive);
      document.addEventListener("touchmove", shouldPrevent, nonPassive);
      document.addEventListener("touchstart", scrollTouchStart, nonPassive);
      return function() {
        lockStack = lockStack.filter(function(inst) {
          return inst !== Style2;
        });
        document.removeEventListener("wheel", shouldPrevent, nonPassive);
        document.removeEventListener("touchmove", shouldPrevent, nonPassive);
        document.removeEventListener("touchstart", scrollTouchStart, nonPassive);
      };
    }, []);
    var removeScrollBar = props.removeScrollBar, inert = props.inert;
    return React26.createElement(
      React26.Fragment,
      null,
      inert ? React26.createElement(Style2, { styles: generateStyle(id) }) : null,
      removeScrollBar ? React26.createElement(RemoveScrollBar, { noRelative: props.noRelative, gapMode: props.gapMode }) : null
    );
  }
  __name(RemoveScrollSideCar, "RemoveScrollSideCar");
  function getOutermostShadowParent(node) {
    var shadowParent = null;
    while (node !== null) {
      if (node instanceof ShadowRoot) {
        shadowParent = node.host;
        node = node.host;
      }
      node = node.parentNode;
    }
    return shadowParent;
  }
  __name(getOutermostShadowParent, "getOutermostShadowParent");

  // node_modules/react-remove-scroll/dist/es2015/sidecar.js
  var sidecar_default = exportSidecar(effectCar, RemoveScrollSideCar);

  // node_modules/react-remove-scroll/dist/es2015/Combination.js
  var ReactRemoveScroll = React27.forwardRef(function(props, ref) {
    return React27.createElement(RemoveScroll, __assign({}, props, { ref, sideCar: sidecar_default }));
  });
  ReactRemoveScroll.classNames = RemoveScroll.classNames;
  var Combination_default = ReactRemoveScroll;

  // node_modules/aria-hidden/dist/es2015/index.js
  var getDefaultParent = /* @__PURE__ */ __name(function(originalTarget) {
    if (typeof document === "undefined") {
      return null;
    }
    var sampleTarget = Array.isArray(originalTarget) ? originalTarget[0] : originalTarget;
    return sampleTarget.ownerDocument.body;
  }, "getDefaultParent");
  var counterMap = /* @__PURE__ */ new WeakMap();
  var uncontrolledNodes = /* @__PURE__ */ new WeakMap();
  var markerMap = {};
  var lockCount = 0;
  var unwrapHost = /* @__PURE__ */ __name(function(node) {
    return node && (node.host || unwrapHost(node.parentNode));
  }, "unwrapHost");
  var correctTargets = /* @__PURE__ */ __name(function(parent, targets) {
    return targets.map(function(target) {
      if (parent.contains(target)) {
        return target;
      }
      var correctedTarget = unwrapHost(target);
      if (correctedTarget && parent.contains(correctedTarget)) {
        return correctedTarget;
      }
      console.error("aria-hidden", target, "in not contained inside", parent, ". Doing nothing");
      return null;
    }).filter(function(x) {
      return Boolean(x);
    });
  }, "correctTargets");
  var applyAttributeToOthers = /* @__PURE__ */ __name(function(originalTarget, parentNode, markerName, controlAttribute) {
    var targets = correctTargets(parentNode, Array.isArray(originalTarget) ? originalTarget : [originalTarget]);
    if (!markerMap[markerName]) {
      markerMap[markerName] = /* @__PURE__ */ new WeakMap();
    }
    var markerCounter = markerMap[markerName];
    var hiddenNodes = [];
    var elementsToKeep = /* @__PURE__ */ new Set();
    var elementsToStop = new Set(targets);
    var keep = /* @__PURE__ */ __name(function(el) {
      if (!el || elementsToKeep.has(el)) {
        return;
      }
      elementsToKeep.add(el);
      keep(el.parentNode);
    }, "keep");
    targets.forEach(keep);
    var deep = /* @__PURE__ */ __name(function(parent) {
      if (!parent || elementsToStop.has(parent)) {
        return;
      }
      Array.prototype.forEach.call(parent.children, function(node) {
        if (elementsToKeep.has(node)) {
          deep(node);
        } else {
          try {
            var attr = node.getAttribute(controlAttribute);
            var alreadyHidden = attr !== null && attr !== "false";
            var counterValue = (counterMap.get(node) || 0) + 1;
            var markerValue = (markerCounter.get(node) || 0) + 1;
            counterMap.set(node, counterValue);
            markerCounter.set(node, markerValue);
            hiddenNodes.push(node);
            if (counterValue === 1 && alreadyHidden) {
              uncontrolledNodes.set(node, true);
            }
            if (markerValue === 1) {
              node.setAttribute(markerName, "true");
            }
            if (!alreadyHidden) {
              node.setAttribute(controlAttribute, "true");
            }
          } catch (e) {
            console.error("aria-hidden: cannot operate on ", node, e);
          }
        }
      });
    }, "deep");
    deep(parentNode);
    elementsToKeep.clear();
    lockCount++;
    return function() {
      hiddenNodes.forEach(function(node) {
        var counterValue = counterMap.get(node) - 1;
        var markerValue = markerCounter.get(node) - 1;
        counterMap.set(node, counterValue);
        markerCounter.set(node, markerValue);
        if (!counterValue) {
          if (!uncontrolledNodes.has(node)) {
            node.removeAttribute(controlAttribute);
          }
          uncontrolledNodes.delete(node);
        }
        if (!markerValue) {
          node.removeAttribute(markerName);
        }
      });
      lockCount--;
      if (!lockCount) {
        counterMap = /* @__PURE__ */ new WeakMap();
        counterMap = /* @__PURE__ */ new WeakMap();
        uncontrolledNodes = /* @__PURE__ */ new WeakMap();
        markerMap = {};
      }
    };
  }, "applyAttributeToOthers");
  var hideOthers = /* @__PURE__ */ __name(function(originalTarget, parentNode, markerName) {
    if (markerName === void 0) {
      markerName = "data-aria-hidden";
    }
    var targets = Array.from(Array.isArray(originalTarget) ? originalTarget : [originalTarget]);
    var activeParentNode = parentNode || getDefaultParent(originalTarget);
    if (!activeParentNode) {
      return function() {
        return null;
      };
    }
    targets.push.apply(targets, Array.from(activeParentNode.querySelectorAll("[aria-live], script")));
    return applyAttributeToOthers(targets, activeParentNode, markerName, "aria-hidden");
  }, "hideOthers");

  // node_modules/@radix-ui/react-use-size/dist/index.mjs
  var React28 = __toESM(require_react(), 1);
  function useSize(element) {
    const [size4, setSize] = React28.useState(void 0);
    useLayoutEffect22(() => {
      if (element) {
        setSize({ width: element.offsetWidth, height: element.offsetHeight });
        const resizeObserver = new ResizeObserver((entries) => {
          if (!Array.isArray(entries)) {
            return;
          }
          if (!entries.length) {
            return;
          }
          const entry = entries[0];
          let width;
          let height;
          if ("borderBoxSize" in entry) {
            const borderSizeEntry = entry["borderBoxSize"];
            const borderSize = Array.isArray(borderSizeEntry) ? borderSizeEntry[0] : borderSizeEntry;
            width = borderSize["inlineSize"];
            height = borderSize["blockSize"];
          } else {
            width = element.offsetWidth;
            height = element.offsetHeight;
          }
          setSize({ width, height });
        });
        resizeObserver.observe(element, { box: "border-box" });
        return () => resizeObserver.unobserve(element);
      } else {
        setSize(void 0);
      }
    }, [element]);
    return size4;
  }
  __name(useSize, "useSize");

  // node_modules/@radix-ui/react-menu/dist/index.mjs
  var React33 = __toESM(require_react(), 1);

  // node_modules/@radix-ui/react-popper/dist/index.mjs
  var React31 = __toESM(require_react(), 1);

  // node_modules/@floating-ui/utils/dist/floating-ui.utils.mjs
  var sides = ["top", "right", "bottom", "left"];
  var min = Math.min;
  var max = Math.max;
  var round = Math.round;
  var floor = Math.floor;
  var createCoords = /* @__PURE__ */ __name((v) => ({
    x: v,
    y: v
  }), "createCoords");
  var oppositeSideMap = {
    left: "right",
    right: "left",
    bottom: "top",
    top: "bottom"
  };
  var oppositeAlignmentMap = {
    start: "end",
    end: "start"
  };
  function clamp(start, value, end) {
    return max(start, min(value, end));
  }
  __name(clamp, "clamp");
  function evaluate(value, param) {
    return typeof value === "function" ? value(param) : value;
  }
  __name(evaluate, "evaluate");
  function getSide(placement) {
    return placement.split("-")[0];
  }
  __name(getSide, "getSide");
  function getAlignment(placement) {
    return placement.split("-")[1];
  }
  __name(getAlignment, "getAlignment");
  function getOppositeAxis(axis) {
    return axis === "x" ? "y" : "x";
  }
  __name(getOppositeAxis, "getOppositeAxis");
  function getAxisLength(axis) {
    return axis === "y" ? "height" : "width";
  }
  __name(getAxisLength, "getAxisLength");
  var yAxisSides = /* @__PURE__ */ new Set(["top", "bottom"]);
  function getSideAxis(placement) {
    return yAxisSides.has(getSide(placement)) ? "y" : "x";
  }
  __name(getSideAxis, "getSideAxis");
  function getAlignmentAxis(placement) {
    return getOppositeAxis(getSideAxis(placement));
  }
  __name(getAlignmentAxis, "getAlignmentAxis");
  function getAlignmentSides(placement, rects, rtl) {
    if (rtl === void 0) {
      rtl = false;
    }
    const alignment = getAlignment(placement);
    const alignmentAxis = getAlignmentAxis(placement);
    const length = getAxisLength(alignmentAxis);
    let mainAlignmentSide = alignmentAxis === "x" ? alignment === (rtl ? "end" : "start") ? "right" : "left" : alignment === "start" ? "bottom" : "top";
    if (rects.reference[length] > rects.floating[length]) {
      mainAlignmentSide = getOppositePlacement(mainAlignmentSide);
    }
    return [mainAlignmentSide, getOppositePlacement(mainAlignmentSide)];
  }
  __name(getAlignmentSides, "getAlignmentSides");
  function getExpandedPlacements(placement) {
    const oppositePlacement = getOppositePlacement(placement);
    return [getOppositeAlignmentPlacement(placement), oppositePlacement, getOppositeAlignmentPlacement(oppositePlacement)];
  }
  __name(getExpandedPlacements, "getExpandedPlacements");
  function getOppositeAlignmentPlacement(placement) {
    return placement.replace(/start|end/g, (alignment) => oppositeAlignmentMap[alignment]);
  }
  __name(getOppositeAlignmentPlacement, "getOppositeAlignmentPlacement");
  var lrPlacement = ["left", "right"];
  var rlPlacement = ["right", "left"];
  var tbPlacement = ["top", "bottom"];
  var btPlacement = ["bottom", "top"];
  function getSideList(side, isStart, rtl) {
    switch (side) {
      case "top":
      case "bottom":
        if (rtl) return isStart ? rlPlacement : lrPlacement;
        return isStart ? lrPlacement : rlPlacement;
      case "left":
      case "right":
        return isStart ? tbPlacement : btPlacement;
      default:
        return [];
    }
  }
  __name(getSideList, "getSideList");
  function getOppositeAxisPlacements(placement, flipAlignment, direction, rtl) {
    const alignment = getAlignment(placement);
    let list = getSideList(getSide(placement), direction === "start", rtl);
    if (alignment) {
      list = list.map((side) => side + "-" + alignment);
      if (flipAlignment) {
        list = list.concat(list.map(getOppositeAlignmentPlacement));
      }
    }
    return list;
  }
  __name(getOppositeAxisPlacements, "getOppositeAxisPlacements");
  function getOppositePlacement(placement) {
    return placement.replace(/left|right|bottom|top/g, (side) => oppositeSideMap[side]);
  }
  __name(getOppositePlacement, "getOppositePlacement");
  function expandPaddingObject(padding) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      ...padding
    };
  }
  __name(expandPaddingObject, "expandPaddingObject");
  function getPaddingObject(padding) {
    return typeof padding !== "number" ? expandPaddingObject(padding) : {
      top: padding,
      right: padding,
      bottom: padding,
      left: padding
    };
  }
  __name(getPaddingObject, "getPaddingObject");
  function rectToClientRect(rect) {
    const {
      x,
      y,
      width,
      height
    } = rect;
    return {
      width,
      height,
      top: y,
      left: x,
      right: x + width,
      bottom: y + height,
      x,
      y
    };
  }
  __name(rectToClientRect, "rectToClientRect");

  // node_modules/@floating-ui/core/dist/floating-ui.core.mjs
  function computeCoordsFromPlacement(_ref, placement, rtl) {
    let {
      reference,
      floating
    } = _ref;
    const sideAxis = getSideAxis(placement);
    const alignmentAxis = getAlignmentAxis(placement);
    const alignLength = getAxisLength(alignmentAxis);
    const side = getSide(placement);
    const isVertical = sideAxis === "y";
    const commonX = reference.x + reference.width / 2 - floating.width / 2;
    const commonY = reference.y + reference.height / 2 - floating.height / 2;
    const commonAlign = reference[alignLength] / 2 - floating[alignLength] / 2;
    let coords;
    switch (side) {
      case "top":
        coords = {
          x: commonX,
          y: reference.y - floating.height
        };
        break;
      case "bottom":
        coords = {
          x: commonX,
          y: reference.y + reference.height
        };
        break;
      case "right":
        coords = {
          x: reference.x + reference.width,
          y: commonY
        };
        break;
      case "left":
        coords = {
          x: reference.x - floating.width,
          y: commonY
        };
        break;
      default:
        coords = {
          x: reference.x,
          y: reference.y
        };
    }
    switch (getAlignment(placement)) {
      case "start":
        coords[alignmentAxis] -= commonAlign * (rtl && isVertical ? -1 : 1);
        break;
      case "end":
        coords[alignmentAxis] += commonAlign * (rtl && isVertical ? -1 : 1);
        break;
    }
    return coords;
  }
  __name(computeCoordsFromPlacement, "computeCoordsFromPlacement");
  async function detectOverflow(state, options) {
    var _await$platform$isEle;
    if (options === void 0) {
      options = {};
    }
    const {
      x,
      y,
      platform: platform2,
      rects,
      elements,
      strategy
    } = state;
    const {
      boundary = "clippingAncestors",
      rootBoundary = "viewport",
      elementContext = "floating",
      altBoundary = false,
      padding = 0
    } = evaluate(options, state);
    const paddingObject = getPaddingObject(padding);
    const altContext = elementContext === "floating" ? "reference" : "floating";
    const element = elements[altBoundary ? altContext : elementContext];
    const clippingClientRect = rectToClientRect(await platform2.getClippingRect({
      element: ((_await$platform$isEle = await (platform2.isElement == null ? void 0 : platform2.isElement(element))) != null ? _await$platform$isEle : true) ? element : element.contextElement || await (platform2.getDocumentElement == null ? void 0 : platform2.getDocumentElement(elements.floating)),
      boundary,
      rootBoundary,
      strategy
    }));
    const rect = elementContext === "floating" ? {
      x,
      y,
      width: rects.floating.width,
      height: rects.floating.height
    } : rects.reference;
    const offsetParent = await (platform2.getOffsetParent == null ? void 0 : platform2.getOffsetParent(elements.floating));
    const offsetScale = await (platform2.isElement == null ? void 0 : platform2.isElement(offsetParent)) ? await (platform2.getScale == null ? void 0 : platform2.getScale(offsetParent)) || {
      x: 1,
      y: 1
    } : {
      x: 1,
      y: 1
    };
    const elementClientRect = rectToClientRect(platform2.convertOffsetParentRelativeRectToViewportRelativeRect ? await platform2.convertOffsetParentRelativeRectToViewportRelativeRect({
      elements,
      rect,
      offsetParent,
      strategy
    }) : rect);
    return {
      top: (clippingClientRect.top - elementClientRect.top + paddingObject.top) / offsetScale.y,
      bottom: (elementClientRect.bottom - clippingClientRect.bottom + paddingObject.bottom) / offsetScale.y,
      left: (clippingClientRect.left - elementClientRect.left + paddingObject.left) / offsetScale.x,
      right: (elementClientRect.right - clippingClientRect.right + paddingObject.right) / offsetScale.x
    };
  }
  __name(detectOverflow, "detectOverflow");
  var computePosition = /* @__PURE__ */ __name(async (reference, floating, config) => {
    const {
      placement = "bottom",
      strategy = "absolute",
      middleware = [],
      platform: platform2
    } = config;
    const validMiddleware = middleware.filter(Boolean);
    const rtl = await (platform2.isRTL == null ? void 0 : platform2.isRTL(floating));
    let rects = await platform2.getElementRects({
      reference,
      floating,
      strategy
    });
    let {
      x,
      y
    } = computeCoordsFromPlacement(rects, placement, rtl);
    let statefulPlacement = placement;
    let middlewareData = {};
    let resetCount = 0;
    for (let i = 0; i < validMiddleware.length; i++) {
      var _platform$detectOverf;
      const {
        name,
        fn
      } = validMiddleware[i];
      const {
        x: nextX,
        y: nextY,
        data,
        reset
      } = await fn({
        x,
        y,
        initialPlacement: placement,
        placement: statefulPlacement,
        strategy,
        middlewareData,
        rects,
        platform: {
          ...platform2,
          detectOverflow: (_platform$detectOverf = platform2.detectOverflow) != null ? _platform$detectOverf : detectOverflow
        },
        elements: {
          reference,
          floating
        }
      });
      x = nextX != null ? nextX : x;
      y = nextY != null ? nextY : y;
      middlewareData = {
        ...middlewareData,
        [name]: {
          ...middlewareData[name],
          ...data
        }
      };
      if (reset && resetCount <= 50) {
        resetCount++;
        if (typeof reset === "object") {
          if (reset.placement) {
            statefulPlacement = reset.placement;
          }
          if (reset.rects) {
            rects = reset.rects === true ? await platform2.getElementRects({
              reference,
              floating,
              strategy
            }) : reset.rects;
          }
          ({
            x,
            y
          } = computeCoordsFromPlacement(rects, statefulPlacement, rtl));
        }
        i = -1;
      }
    }
    return {
      x,
      y,
      placement: statefulPlacement,
      strategy,
      middlewareData
    };
  }, "computePosition");
  var arrow = /* @__PURE__ */ __name((options) => ({
    name: "arrow",
    options,
    async fn(state) {
      const {
        x,
        y,
        placement,
        rects,
        platform: platform2,
        elements,
        middlewareData
      } = state;
      const {
        element,
        padding = 0
      } = evaluate(options, state) || {};
      if (element == null) {
        return {};
      }
      const paddingObject = getPaddingObject(padding);
      const coords = {
        x,
        y
      };
      const axis = getAlignmentAxis(placement);
      const length = getAxisLength(axis);
      const arrowDimensions = await platform2.getDimensions(element);
      const isYAxis = axis === "y";
      const minProp = isYAxis ? "top" : "left";
      const maxProp = isYAxis ? "bottom" : "right";
      const clientProp = isYAxis ? "clientHeight" : "clientWidth";
      const endDiff = rects.reference[length] + rects.reference[axis] - coords[axis] - rects.floating[length];
      const startDiff = coords[axis] - rects.reference[axis];
      const arrowOffsetParent = await (platform2.getOffsetParent == null ? void 0 : platform2.getOffsetParent(element));
      let clientSize = arrowOffsetParent ? arrowOffsetParent[clientProp] : 0;
      if (!clientSize || !await (platform2.isElement == null ? void 0 : platform2.isElement(arrowOffsetParent))) {
        clientSize = elements.floating[clientProp] || rects.floating[length];
      }
      const centerToReference = endDiff / 2 - startDiff / 2;
      const largestPossiblePadding = clientSize / 2 - arrowDimensions[length] / 2 - 1;
      const minPadding = min(paddingObject[minProp], largestPossiblePadding);
      const maxPadding = min(paddingObject[maxProp], largestPossiblePadding);
      const min$1 = minPadding;
      const max2 = clientSize - arrowDimensions[length] - maxPadding;
      const center = clientSize / 2 - arrowDimensions[length] / 2 + centerToReference;
      const offset4 = clamp(min$1, center, max2);
      const shouldAddOffset = !middlewareData.arrow && getAlignment(placement) != null && center !== offset4 && rects.reference[length] / 2 - (center < min$1 ? minPadding : maxPadding) - arrowDimensions[length] / 2 < 0;
      const alignmentOffset = shouldAddOffset ? center < min$1 ? center - min$1 : center - max2 : 0;
      return {
        [axis]: coords[axis] + alignmentOffset,
        data: {
          [axis]: offset4,
          centerOffset: center - offset4 - alignmentOffset,
          ...shouldAddOffset && {
            alignmentOffset
          }
        },
        reset: shouldAddOffset
      };
    }
  }), "arrow");
  var flip = /* @__PURE__ */ __name(function(options) {
    if (options === void 0) {
      options = {};
    }
    return {
      name: "flip",
      options,
      async fn(state) {
        var _middlewareData$arrow, _middlewareData$flip;
        const {
          placement,
          middlewareData,
          rects,
          initialPlacement,
          platform: platform2,
          elements
        } = state;
        const {
          mainAxis: checkMainAxis = true,
          crossAxis: checkCrossAxis = true,
          fallbackPlacements: specifiedFallbackPlacements,
          fallbackStrategy = "bestFit",
          fallbackAxisSideDirection = "none",
          flipAlignment = true,
          ...detectOverflowOptions
        } = evaluate(options, state);
        if ((_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) {
          return {};
        }
        const side = getSide(placement);
        const initialSideAxis = getSideAxis(initialPlacement);
        const isBasePlacement = getSide(initialPlacement) === initialPlacement;
        const rtl = await (platform2.isRTL == null ? void 0 : platform2.isRTL(elements.floating));
        const fallbackPlacements = specifiedFallbackPlacements || (isBasePlacement || !flipAlignment ? [getOppositePlacement(initialPlacement)] : getExpandedPlacements(initialPlacement));
        const hasFallbackAxisSideDirection = fallbackAxisSideDirection !== "none";
        if (!specifiedFallbackPlacements && hasFallbackAxisSideDirection) {
          fallbackPlacements.push(...getOppositeAxisPlacements(initialPlacement, flipAlignment, fallbackAxisSideDirection, rtl));
        }
        const placements2 = [initialPlacement, ...fallbackPlacements];
        const overflow = await platform2.detectOverflow(state, detectOverflowOptions);
        const overflows = [];
        let overflowsData = ((_middlewareData$flip = middlewareData.flip) == null ? void 0 : _middlewareData$flip.overflows) || [];
        if (checkMainAxis) {
          overflows.push(overflow[side]);
        }
        if (checkCrossAxis) {
          const sides2 = getAlignmentSides(placement, rects, rtl);
          overflows.push(overflow[sides2[0]], overflow[sides2[1]]);
        }
        overflowsData = [...overflowsData, {
          placement,
          overflows
        }];
        if (!overflows.every((side2) => side2 <= 0)) {
          var _middlewareData$flip2, _overflowsData$filter;
          const nextIndex = (((_middlewareData$flip2 = middlewareData.flip) == null ? void 0 : _middlewareData$flip2.index) || 0) + 1;
          const nextPlacement = placements2[nextIndex];
          if (nextPlacement) {
            const ignoreCrossAxisOverflow = checkCrossAxis === "alignment" ? initialSideAxis !== getSideAxis(nextPlacement) : false;
            if (!ignoreCrossAxisOverflow || // We leave the current main axis only if every placement on that axis
            // overflows the main axis.
            overflowsData.every((d) => getSideAxis(d.placement) === initialSideAxis ? d.overflows[0] > 0 : true)) {
              return {
                data: {
                  index: nextIndex,
                  overflows: overflowsData
                },
                reset: {
                  placement: nextPlacement
                }
              };
            }
          }
          let resetPlacement = (_overflowsData$filter = overflowsData.filter((d) => d.overflows[0] <= 0).sort((a, b) => a.overflows[1] - b.overflows[1])[0]) == null ? void 0 : _overflowsData$filter.placement;
          if (!resetPlacement) {
            switch (fallbackStrategy) {
              case "bestFit": {
                var _overflowsData$filter2;
                const placement2 = (_overflowsData$filter2 = overflowsData.filter((d) => {
                  if (hasFallbackAxisSideDirection) {
                    const currentSideAxis = getSideAxis(d.placement);
                    return currentSideAxis === initialSideAxis || // Create a bias to the `y` side axis due to horizontal
                    // reading directions favoring greater width.
                    currentSideAxis === "y";
                  }
                  return true;
                }).map((d) => [d.placement, d.overflows.filter((overflow2) => overflow2 > 0).reduce((acc, overflow2) => acc + overflow2, 0)]).sort((a, b) => a[1] - b[1])[0]) == null ? void 0 : _overflowsData$filter2[0];
                if (placement2) {
                  resetPlacement = placement2;
                }
                break;
              }
              case "initialPlacement":
                resetPlacement = initialPlacement;
                break;
            }
          }
          if (placement !== resetPlacement) {
            return {
              reset: {
                placement: resetPlacement
              }
            };
          }
        }
        return {};
      }
    };
  }, "flip");
  function getSideOffsets(overflow, rect) {
    return {
      top: overflow.top - rect.height,
      right: overflow.right - rect.width,
      bottom: overflow.bottom - rect.height,
      left: overflow.left - rect.width
    };
  }
  __name(getSideOffsets, "getSideOffsets");
  function isAnySideFullyClipped(overflow) {
    return sides.some((side) => overflow[side] >= 0);
  }
  __name(isAnySideFullyClipped, "isAnySideFullyClipped");
  var hide = /* @__PURE__ */ __name(function(options) {
    if (options === void 0) {
      options = {};
    }
    return {
      name: "hide",
      options,
      async fn(state) {
        const {
          rects,
          platform: platform2
        } = state;
        const {
          strategy = "referenceHidden",
          ...detectOverflowOptions
        } = evaluate(options, state);
        switch (strategy) {
          case "referenceHidden": {
            const overflow = await platform2.detectOverflow(state, {
              ...detectOverflowOptions,
              elementContext: "reference"
            });
            const offsets = getSideOffsets(overflow, rects.reference);
            return {
              data: {
                referenceHiddenOffsets: offsets,
                referenceHidden: isAnySideFullyClipped(offsets)
              }
            };
          }
          case "escaped": {
            const overflow = await platform2.detectOverflow(state, {
              ...detectOverflowOptions,
              altBoundary: true
            });
            const offsets = getSideOffsets(overflow, rects.floating);
            return {
              data: {
                escapedOffsets: offsets,
                escaped: isAnySideFullyClipped(offsets)
              }
            };
          }
          default: {
            return {};
          }
        }
      }
    };
  }, "hide");
  var originSides = /* @__PURE__ */ new Set(["left", "top"]);
  async function convertValueToCoords(state, options) {
    const {
      placement,
      platform: platform2,
      elements
    } = state;
    const rtl = await (platform2.isRTL == null ? void 0 : platform2.isRTL(elements.floating));
    const side = getSide(placement);
    const alignment = getAlignment(placement);
    const isVertical = getSideAxis(placement) === "y";
    const mainAxisMulti = originSides.has(side) ? -1 : 1;
    const crossAxisMulti = rtl && isVertical ? -1 : 1;
    const rawValue = evaluate(options, state);
    let {
      mainAxis,
      crossAxis,
      alignmentAxis
    } = typeof rawValue === "number" ? {
      mainAxis: rawValue,
      crossAxis: 0,
      alignmentAxis: null
    } : {
      mainAxis: rawValue.mainAxis || 0,
      crossAxis: rawValue.crossAxis || 0,
      alignmentAxis: rawValue.alignmentAxis
    };
    if (alignment && typeof alignmentAxis === "number") {
      crossAxis = alignment === "end" ? alignmentAxis * -1 : alignmentAxis;
    }
    return isVertical ? {
      x: crossAxis * crossAxisMulti,
      y: mainAxis * mainAxisMulti
    } : {
      x: mainAxis * mainAxisMulti,
      y: crossAxis * crossAxisMulti
    };
  }
  __name(convertValueToCoords, "convertValueToCoords");
  var offset = /* @__PURE__ */ __name(function(options) {
    if (options === void 0) {
      options = 0;
    }
    return {
      name: "offset",
      options,
      async fn(state) {
        var _middlewareData$offse, _middlewareData$arrow;
        const {
          x,
          y,
          placement,
          middlewareData
        } = state;
        const diffCoords = await convertValueToCoords(state, options);
        if (placement === ((_middlewareData$offse = middlewareData.offset) == null ? void 0 : _middlewareData$offse.placement) && (_middlewareData$arrow = middlewareData.arrow) != null && _middlewareData$arrow.alignmentOffset) {
          return {};
        }
        return {
          x: x + diffCoords.x,
          y: y + diffCoords.y,
          data: {
            ...diffCoords,
            placement
          }
        };
      }
    };
  }, "offset");
  var shift = /* @__PURE__ */ __name(function(options) {
    if (options === void 0) {
      options = {};
    }
    return {
      name: "shift",
      options,
      async fn(state) {
        const {
          x,
          y,
          placement,
          platform: platform2
        } = state;
        const {
          mainAxis: checkMainAxis = true,
          crossAxis: checkCrossAxis = false,
          limiter = {
            fn: /* @__PURE__ */ __name((_ref) => {
              let {
                x: x2,
                y: y2
              } = _ref;
              return {
                x: x2,
                y: y2
              };
            }, "fn")
          },
          ...detectOverflowOptions
        } = evaluate(options, state);
        const coords = {
          x,
          y
        };
        const overflow = await platform2.detectOverflow(state, detectOverflowOptions);
        const crossAxis = getSideAxis(getSide(placement));
        const mainAxis = getOppositeAxis(crossAxis);
        let mainAxisCoord = coords[mainAxis];
        let crossAxisCoord = coords[crossAxis];
        if (checkMainAxis) {
          const minSide = mainAxis === "y" ? "top" : "left";
          const maxSide = mainAxis === "y" ? "bottom" : "right";
          const min2 = mainAxisCoord + overflow[minSide];
          const max2 = mainAxisCoord - overflow[maxSide];
          mainAxisCoord = clamp(min2, mainAxisCoord, max2);
        }
        if (checkCrossAxis) {
          const minSide = crossAxis === "y" ? "top" : "left";
          const maxSide = crossAxis === "y" ? "bottom" : "right";
          const min2 = crossAxisCoord + overflow[minSide];
          const max2 = crossAxisCoord - overflow[maxSide];
          crossAxisCoord = clamp(min2, crossAxisCoord, max2);
        }
        const limitedCoords = limiter.fn({
          ...state,
          [mainAxis]: mainAxisCoord,
          [crossAxis]: crossAxisCoord
        });
        return {
          ...limitedCoords,
          data: {
            x: limitedCoords.x - x,
            y: limitedCoords.y - y,
            enabled: {
              [mainAxis]: checkMainAxis,
              [crossAxis]: checkCrossAxis
            }
          }
        };
      }
    };
  }, "shift");
  var limitShift = /* @__PURE__ */ __name(function(options) {
    if (options === void 0) {
      options = {};
    }
    return {
      options,
      fn(state) {
        const {
          x,
          y,
          placement,
          rects,
          middlewareData
        } = state;
        const {
          offset: offset4 = 0,
          mainAxis: checkMainAxis = true,
          crossAxis: checkCrossAxis = true
        } = evaluate(options, state);
        const coords = {
          x,
          y
        };
        const crossAxis = getSideAxis(placement);
        const mainAxis = getOppositeAxis(crossAxis);
        let mainAxisCoord = coords[mainAxis];
        let crossAxisCoord = coords[crossAxis];
        const rawOffset = evaluate(offset4, state);
        const computedOffset = typeof rawOffset === "number" ? {
          mainAxis: rawOffset,
          crossAxis: 0
        } : {
          mainAxis: 0,
          crossAxis: 0,
          ...rawOffset
        };
        if (checkMainAxis) {
          const len = mainAxis === "y" ? "height" : "width";
          const limitMin = rects.reference[mainAxis] - rects.floating[len] + computedOffset.mainAxis;
          const limitMax = rects.reference[mainAxis] + rects.reference[len] - computedOffset.mainAxis;
          if (mainAxisCoord < limitMin) {
            mainAxisCoord = limitMin;
          } else if (mainAxisCoord > limitMax) {
            mainAxisCoord = limitMax;
          }
        }
        if (checkCrossAxis) {
          var _middlewareData$offse, _middlewareData$offse2;
          const len = mainAxis === "y" ? "width" : "height";
          const isOriginSide = originSides.has(getSide(placement));
          const limitMin = rects.reference[crossAxis] - rects.floating[len] + (isOriginSide ? ((_middlewareData$offse = middlewareData.offset) == null ? void 0 : _middlewareData$offse[crossAxis]) || 0 : 0) + (isOriginSide ? 0 : computedOffset.crossAxis);
          const limitMax = rects.reference[crossAxis] + rects.reference[len] + (isOriginSide ? 0 : ((_middlewareData$offse2 = middlewareData.offset) == null ? void 0 : _middlewareData$offse2[crossAxis]) || 0) - (isOriginSide ? computedOffset.crossAxis : 0);
          if (crossAxisCoord < limitMin) {
            crossAxisCoord = limitMin;
          } else if (crossAxisCoord > limitMax) {
            crossAxisCoord = limitMax;
          }
        }
        return {
          [mainAxis]: mainAxisCoord,
          [crossAxis]: crossAxisCoord
        };
      }
    };
  }, "limitShift");
  var size = /* @__PURE__ */ __name(function(options) {
    if (options === void 0) {
      options = {};
    }
    return {
      name: "size",
      options,
      async fn(state) {
        var _state$middlewareData, _state$middlewareData2;
        const {
          placement,
          rects,
          platform: platform2,
          elements
        } = state;
        const {
          apply = /* @__PURE__ */ __name(() => {
          }, "apply"),
          ...detectOverflowOptions
        } = evaluate(options, state);
        const overflow = await platform2.detectOverflow(state, detectOverflowOptions);
        const side = getSide(placement);
        const alignment = getAlignment(placement);
        const isYAxis = getSideAxis(placement) === "y";
        const {
          width,
          height
        } = rects.floating;
        let heightSide;
        let widthSide;
        if (side === "top" || side === "bottom") {
          heightSide = side;
          widthSide = alignment === (await (platform2.isRTL == null ? void 0 : platform2.isRTL(elements.floating)) ? "start" : "end") ? "left" : "right";
        } else {
          widthSide = side;
          heightSide = alignment === "end" ? "top" : "bottom";
        }
        const maximumClippingHeight = height - overflow.top - overflow.bottom;
        const maximumClippingWidth = width - overflow.left - overflow.right;
        const overflowAvailableHeight = min(height - overflow[heightSide], maximumClippingHeight);
        const overflowAvailableWidth = min(width - overflow[widthSide], maximumClippingWidth);
        const noShift = !state.middlewareData.shift;
        let availableHeight = overflowAvailableHeight;
        let availableWidth = overflowAvailableWidth;
        if ((_state$middlewareData = state.middlewareData.shift) != null && _state$middlewareData.enabled.x) {
          availableWidth = maximumClippingWidth;
        }
        if ((_state$middlewareData2 = state.middlewareData.shift) != null && _state$middlewareData2.enabled.y) {
          availableHeight = maximumClippingHeight;
        }
        if (noShift && !alignment) {
          const xMin = max(overflow.left, 0);
          const xMax = max(overflow.right, 0);
          const yMin = max(overflow.top, 0);
          const yMax = max(overflow.bottom, 0);
          if (isYAxis) {
            availableWidth = width - 2 * (xMin !== 0 || xMax !== 0 ? xMin + xMax : max(overflow.left, overflow.right));
          } else {
            availableHeight = height - 2 * (yMin !== 0 || yMax !== 0 ? yMin + yMax : max(overflow.top, overflow.bottom));
          }
        }
        await apply({
          ...state,
          availableWidth,
          availableHeight
        });
        const nextDimensions = await platform2.getDimensions(elements.floating);
        if (width !== nextDimensions.width || height !== nextDimensions.height) {
          return {
            reset: {
              rects: true
            }
          };
        }
        return {};
      }
    };
  }, "size");

  // node_modules/@floating-ui/utils/dist/floating-ui.utils.dom.mjs
  function hasWindow() {
    return typeof window !== "undefined";
  }
  __name(hasWindow, "hasWindow");
  function getNodeName(node) {
    if (isNode(node)) {
      return (node.nodeName || "").toLowerCase();
    }
    return "#document";
  }
  __name(getNodeName, "getNodeName");
  function getWindow(node) {
    var _node$ownerDocument;
    return (node == null || (_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.defaultView) || window;
  }
  __name(getWindow, "getWindow");
  function getDocumentElement(node) {
    var _ref;
    return (_ref = (isNode(node) ? node.ownerDocument : node.document) || window.document) == null ? void 0 : _ref.documentElement;
  }
  __name(getDocumentElement, "getDocumentElement");
  function isNode(value) {
    if (!hasWindow()) {
      return false;
    }
    return value instanceof Node || value instanceof getWindow(value).Node;
  }
  __name(isNode, "isNode");
  function isElement(value) {
    if (!hasWindow()) {
      return false;
    }
    return value instanceof Element || value instanceof getWindow(value).Element;
  }
  __name(isElement, "isElement");
  function isHTMLElement(value) {
    if (!hasWindow()) {
      return false;
    }
    return value instanceof HTMLElement || value instanceof getWindow(value).HTMLElement;
  }
  __name(isHTMLElement, "isHTMLElement");
  function isShadowRoot(value) {
    if (!hasWindow() || typeof ShadowRoot === "undefined") {
      return false;
    }
    return value instanceof ShadowRoot || value instanceof getWindow(value).ShadowRoot;
  }
  __name(isShadowRoot, "isShadowRoot");
  var invalidOverflowDisplayValues = /* @__PURE__ */ new Set(["inline", "contents"]);
  function isOverflowElement(element) {
    const {
      overflow,
      overflowX,
      overflowY,
      display
    } = getComputedStyle2(element);
    return /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) && !invalidOverflowDisplayValues.has(display);
  }
  __name(isOverflowElement, "isOverflowElement");
  var tableElements = /* @__PURE__ */ new Set(["table", "td", "th"]);
  function isTableElement(element) {
    return tableElements.has(getNodeName(element));
  }
  __name(isTableElement, "isTableElement");
  var topLayerSelectors = [":popover-open", ":modal"];
  function isTopLayer(element) {
    return topLayerSelectors.some((selector) => {
      try {
        return element.matches(selector);
      } catch (_e) {
        return false;
      }
    });
  }
  __name(isTopLayer, "isTopLayer");
  var transformProperties = ["transform", "translate", "scale", "rotate", "perspective"];
  var willChangeValues = ["transform", "translate", "scale", "rotate", "perspective", "filter"];
  var containValues = ["paint", "layout", "strict", "content"];
  function isContainingBlock(elementOrCss) {
    const webkit = isWebKit();
    const css = isElement(elementOrCss) ? getComputedStyle2(elementOrCss) : elementOrCss;
    return transformProperties.some((value) => css[value] ? css[value] !== "none" : false) || (css.containerType ? css.containerType !== "normal" : false) || !webkit && (css.backdropFilter ? css.backdropFilter !== "none" : false) || !webkit && (css.filter ? css.filter !== "none" : false) || willChangeValues.some((value) => (css.willChange || "").includes(value)) || containValues.some((value) => (css.contain || "").includes(value));
  }
  __name(isContainingBlock, "isContainingBlock");
  function getContainingBlock(element) {
    let currentNode = getParentNode(element);
    while (isHTMLElement(currentNode) && !isLastTraversableNode(currentNode)) {
      if (isContainingBlock(currentNode)) {
        return currentNode;
      } else if (isTopLayer(currentNode)) {
        return null;
      }
      currentNode = getParentNode(currentNode);
    }
    return null;
  }
  __name(getContainingBlock, "getContainingBlock");
  function isWebKit() {
    if (typeof CSS === "undefined" || !CSS.supports) return false;
    return CSS.supports("-webkit-backdrop-filter", "none");
  }
  __name(isWebKit, "isWebKit");
  var lastTraversableNodeNames = /* @__PURE__ */ new Set(["html", "body", "#document"]);
  function isLastTraversableNode(node) {
    return lastTraversableNodeNames.has(getNodeName(node));
  }
  __name(isLastTraversableNode, "isLastTraversableNode");
  function getComputedStyle2(element) {
    return getWindow(element).getComputedStyle(element);
  }
  __name(getComputedStyle2, "getComputedStyle");
  function getNodeScroll(element) {
    if (isElement(element)) {
      return {
        scrollLeft: element.scrollLeft,
        scrollTop: element.scrollTop
      };
    }
    return {
      scrollLeft: element.scrollX,
      scrollTop: element.scrollY
    };
  }
  __name(getNodeScroll, "getNodeScroll");
  function getParentNode(node) {
    if (getNodeName(node) === "html") {
      return node;
    }
    const result = (
      // Step into the shadow DOM of the parent of a slotted node.
      node.assignedSlot || // DOM Element detected.
      node.parentNode || // ShadowRoot detected.
      isShadowRoot(node) && node.host || // Fallback.
      getDocumentElement(node)
    );
    return isShadowRoot(result) ? result.host : result;
  }
  __name(getParentNode, "getParentNode");
  function getNearestOverflowAncestor(node) {
    const parentNode = getParentNode(node);
    if (isLastTraversableNode(parentNode)) {
      return node.ownerDocument ? node.ownerDocument.body : node.body;
    }
    if (isHTMLElement(parentNode) && isOverflowElement(parentNode)) {
      return parentNode;
    }
    return getNearestOverflowAncestor(parentNode);
  }
  __name(getNearestOverflowAncestor, "getNearestOverflowAncestor");
  function getOverflowAncestors(node, list, traverseIframes) {
    var _node$ownerDocument2;
    if (list === void 0) {
      list = [];
    }
    if (traverseIframes === void 0) {
      traverseIframes = true;
    }
    const scrollableAncestor = getNearestOverflowAncestor(node);
    const isBody = scrollableAncestor === ((_node$ownerDocument2 = node.ownerDocument) == null ? void 0 : _node$ownerDocument2.body);
    const win = getWindow(scrollableAncestor);
    if (isBody) {
      const frameElement = getFrameElement(win);
      return list.concat(win, win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : [], frameElement && traverseIframes ? getOverflowAncestors(frameElement) : []);
    }
    return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor, [], traverseIframes));
  }
  __name(getOverflowAncestors, "getOverflowAncestors");
  function getFrameElement(win) {
    return win.parent && Object.getPrototypeOf(win.parent) ? win.frameElement : null;
  }
  __name(getFrameElement, "getFrameElement");

  // node_modules/@floating-ui/dom/dist/floating-ui.dom.mjs
  function getCssDimensions(element) {
    const css = getComputedStyle2(element);
    let width = parseFloat(css.width) || 0;
    let height = parseFloat(css.height) || 0;
    const hasOffset = isHTMLElement(element);
    const offsetWidth = hasOffset ? element.offsetWidth : width;
    const offsetHeight = hasOffset ? element.offsetHeight : height;
    const shouldFallback = round(width) !== offsetWidth || round(height) !== offsetHeight;
    if (shouldFallback) {
      width = offsetWidth;
      height = offsetHeight;
    }
    return {
      width,
      height,
      $: shouldFallback
    };
  }
  __name(getCssDimensions, "getCssDimensions");
  function unwrapElement(element) {
    return !isElement(element) ? element.contextElement : element;
  }
  __name(unwrapElement, "unwrapElement");
  function getScale(element) {
    const domElement = unwrapElement(element);
    if (!isHTMLElement(domElement)) {
      return createCoords(1);
    }
    const rect = domElement.getBoundingClientRect();
    const {
      width,
      height,
      $
    } = getCssDimensions(domElement);
    let x = ($ ? round(rect.width) : rect.width) / width;
    let y = ($ ? round(rect.height) : rect.height) / height;
    if (!x || !Number.isFinite(x)) {
      x = 1;
    }
    if (!y || !Number.isFinite(y)) {
      y = 1;
    }
    return {
      x,
      y
    };
  }
  __name(getScale, "getScale");
  var noOffsets = /* @__PURE__ */ createCoords(0);
  function getVisualOffsets(element) {
    const win = getWindow(element);
    if (!isWebKit() || !win.visualViewport) {
      return noOffsets;
    }
    return {
      x: win.visualViewport.offsetLeft,
      y: win.visualViewport.offsetTop
    };
  }
  __name(getVisualOffsets, "getVisualOffsets");
  function shouldAddVisualOffsets(element, isFixed, floatingOffsetParent) {
    if (isFixed === void 0) {
      isFixed = false;
    }
    if (!floatingOffsetParent || isFixed && floatingOffsetParent !== getWindow(element)) {
      return false;
    }
    return isFixed;
  }
  __name(shouldAddVisualOffsets, "shouldAddVisualOffsets");
  function getBoundingClientRect(element, includeScale, isFixedStrategy, offsetParent) {
    if (includeScale === void 0) {
      includeScale = false;
    }
    if (isFixedStrategy === void 0) {
      isFixedStrategy = false;
    }
    const clientRect = element.getBoundingClientRect();
    const domElement = unwrapElement(element);
    let scale = createCoords(1);
    if (includeScale) {
      if (offsetParent) {
        if (isElement(offsetParent)) {
          scale = getScale(offsetParent);
        }
      } else {
        scale = getScale(element);
      }
    }
    const visualOffsets = shouldAddVisualOffsets(domElement, isFixedStrategy, offsetParent) ? getVisualOffsets(domElement) : createCoords(0);
    let x = (clientRect.left + visualOffsets.x) / scale.x;
    let y = (clientRect.top + visualOffsets.y) / scale.y;
    let width = clientRect.width / scale.x;
    let height = clientRect.height / scale.y;
    if (domElement) {
      const win = getWindow(domElement);
      const offsetWin = offsetParent && isElement(offsetParent) ? getWindow(offsetParent) : offsetParent;
      let currentWin = win;
      let currentIFrame = getFrameElement(currentWin);
      while (currentIFrame && offsetParent && offsetWin !== currentWin) {
        const iframeScale = getScale(currentIFrame);
        const iframeRect = currentIFrame.getBoundingClientRect();
        const css = getComputedStyle2(currentIFrame);
        const left = iframeRect.left + (currentIFrame.clientLeft + parseFloat(css.paddingLeft)) * iframeScale.x;
        const top = iframeRect.top + (currentIFrame.clientTop + parseFloat(css.paddingTop)) * iframeScale.y;
        x *= iframeScale.x;
        y *= iframeScale.y;
        width *= iframeScale.x;
        height *= iframeScale.y;
        x += left;
        y += top;
        currentWin = getWindow(currentIFrame);
        currentIFrame = getFrameElement(currentWin);
      }
    }
    return rectToClientRect({
      width,
      height,
      x,
      y
    });
  }
  __name(getBoundingClientRect, "getBoundingClientRect");
  function getWindowScrollBarX(element, rect) {
    const leftScroll = getNodeScroll(element).scrollLeft;
    if (!rect) {
      return getBoundingClientRect(getDocumentElement(element)).left + leftScroll;
    }
    return rect.left + leftScroll;
  }
  __name(getWindowScrollBarX, "getWindowScrollBarX");
  function getHTMLOffset(documentElement, scroll) {
    const htmlRect = documentElement.getBoundingClientRect();
    const x = htmlRect.left + scroll.scrollLeft - getWindowScrollBarX(documentElement, htmlRect);
    const y = htmlRect.top + scroll.scrollTop;
    return {
      x,
      y
    };
  }
  __name(getHTMLOffset, "getHTMLOffset");
  function convertOffsetParentRelativeRectToViewportRelativeRect(_ref) {
    let {
      elements,
      rect,
      offsetParent,
      strategy
    } = _ref;
    const isFixed = strategy === "fixed";
    const documentElement = getDocumentElement(offsetParent);
    const topLayer = elements ? isTopLayer(elements.floating) : false;
    if (offsetParent === documentElement || topLayer && isFixed) {
      return rect;
    }
    let scroll = {
      scrollLeft: 0,
      scrollTop: 0
    };
    let scale = createCoords(1);
    const offsets = createCoords(0);
    const isOffsetParentAnElement = isHTMLElement(offsetParent);
    if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
      if (getNodeName(offsetParent) !== "body" || isOverflowElement(documentElement)) {
        scroll = getNodeScroll(offsetParent);
      }
      if (isHTMLElement(offsetParent)) {
        const offsetRect = getBoundingClientRect(offsetParent);
        scale = getScale(offsetParent);
        offsets.x = offsetRect.x + offsetParent.clientLeft;
        offsets.y = offsetRect.y + offsetParent.clientTop;
      }
    }
    const htmlOffset = documentElement && !isOffsetParentAnElement && !isFixed ? getHTMLOffset(documentElement, scroll) : createCoords(0);
    return {
      width: rect.width * scale.x,
      height: rect.height * scale.y,
      x: rect.x * scale.x - scroll.scrollLeft * scale.x + offsets.x + htmlOffset.x,
      y: rect.y * scale.y - scroll.scrollTop * scale.y + offsets.y + htmlOffset.y
    };
  }
  __name(convertOffsetParentRelativeRectToViewportRelativeRect, "convertOffsetParentRelativeRectToViewportRelativeRect");
  function getClientRects(element) {
    return Array.from(element.getClientRects());
  }
  __name(getClientRects, "getClientRects");
  function getDocumentRect(element) {
    const html = getDocumentElement(element);
    const scroll = getNodeScroll(element);
    const body = element.ownerDocument.body;
    const width = max(html.scrollWidth, html.clientWidth, body.scrollWidth, body.clientWidth);
    const height = max(html.scrollHeight, html.clientHeight, body.scrollHeight, body.clientHeight);
    let x = -scroll.scrollLeft + getWindowScrollBarX(element);
    const y = -scroll.scrollTop;
    if (getComputedStyle2(body).direction === "rtl") {
      x += max(html.clientWidth, body.clientWidth) - width;
    }
    return {
      width,
      height,
      x,
      y
    };
  }
  __name(getDocumentRect, "getDocumentRect");
  var SCROLLBAR_MAX = 25;
  function getViewportRect(element, strategy) {
    const win = getWindow(element);
    const html = getDocumentElement(element);
    const visualViewport = win.visualViewport;
    let width = html.clientWidth;
    let height = html.clientHeight;
    let x = 0;
    let y = 0;
    if (visualViewport) {
      width = visualViewport.width;
      height = visualViewport.height;
      const visualViewportBased = isWebKit();
      if (!visualViewportBased || visualViewportBased && strategy === "fixed") {
        x = visualViewport.offsetLeft;
        y = visualViewport.offsetTop;
      }
    }
    const windowScrollbarX = getWindowScrollBarX(html);
    if (windowScrollbarX <= 0) {
      const doc = html.ownerDocument;
      const body = doc.body;
      const bodyStyles = getComputedStyle(body);
      const bodyMarginInline = doc.compatMode === "CSS1Compat" ? parseFloat(bodyStyles.marginLeft) + parseFloat(bodyStyles.marginRight) || 0 : 0;
      const clippingStableScrollbarWidth = Math.abs(html.clientWidth - body.clientWidth - bodyMarginInline);
      if (clippingStableScrollbarWidth <= SCROLLBAR_MAX) {
        width -= clippingStableScrollbarWidth;
      }
    } else if (windowScrollbarX <= SCROLLBAR_MAX) {
      width += windowScrollbarX;
    }
    return {
      width,
      height,
      x,
      y
    };
  }
  __name(getViewportRect, "getViewportRect");
  var absoluteOrFixed = /* @__PURE__ */ new Set(["absolute", "fixed"]);
  function getInnerBoundingClientRect(element, strategy) {
    const clientRect = getBoundingClientRect(element, true, strategy === "fixed");
    const top = clientRect.top + element.clientTop;
    const left = clientRect.left + element.clientLeft;
    const scale = isHTMLElement(element) ? getScale(element) : createCoords(1);
    const width = element.clientWidth * scale.x;
    const height = element.clientHeight * scale.y;
    const x = left * scale.x;
    const y = top * scale.y;
    return {
      width,
      height,
      x,
      y
    };
  }
  __name(getInnerBoundingClientRect, "getInnerBoundingClientRect");
  function getClientRectFromClippingAncestor(element, clippingAncestor, strategy) {
    let rect;
    if (clippingAncestor === "viewport") {
      rect = getViewportRect(element, strategy);
    } else if (clippingAncestor === "document") {
      rect = getDocumentRect(getDocumentElement(element));
    } else if (isElement(clippingAncestor)) {
      rect = getInnerBoundingClientRect(clippingAncestor, strategy);
    } else {
      const visualOffsets = getVisualOffsets(element);
      rect = {
        x: clippingAncestor.x - visualOffsets.x,
        y: clippingAncestor.y - visualOffsets.y,
        width: clippingAncestor.width,
        height: clippingAncestor.height
      };
    }
    return rectToClientRect(rect);
  }
  __name(getClientRectFromClippingAncestor, "getClientRectFromClippingAncestor");
  function hasFixedPositionAncestor(element, stopNode) {
    const parentNode = getParentNode(element);
    if (parentNode === stopNode || !isElement(parentNode) || isLastTraversableNode(parentNode)) {
      return false;
    }
    return getComputedStyle2(parentNode).position === "fixed" || hasFixedPositionAncestor(parentNode, stopNode);
  }
  __name(hasFixedPositionAncestor, "hasFixedPositionAncestor");
  function getClippingElementAncestors(element, cache) {
    const cachedResult = cache.get(element);
    if (cachedResult) {
      return cachedResult;
    }
    let result = getOverflowAncestors(element, [], false).filter((el) => isElement(el) && getNodeName(el) !== "body");
    let currentContainingBlockComputedStyle = null;
    const elementIsFixed = getComputedStyle2(element).position === "fixed";
    let currentNode = elementIsFixed ? getParentNode(element) : element;
    while (isElement(currentNode) && !isLastTraversableNode(currentNode)) {
      const computedStyle = getComputedStyle2(currentNode);
      const currentNodeIsContaining = isContainingBlock(currentNode);
      if (!currentNodeIsContaining && computedStyle.position === "fixed") {
        currentContainingBlockComputedStyle = null;
      }
      const shouldDropCurrentNode = elementIsFixed ? !currentNodeIsContaining && !currentContainingBlockComputedStyle : !currentNodeIsContaining && computedStyle.position === "static" && !!currentContainingBlockComputedStyle && absoluteOrFixed.has(currentContainingBlockComputedStyle.position) || isOverflowElement(currentNode) && !currentNodeIsContaining && hasFixedPositionAncestor(element, currentNode);
      if (shouldDropCurrentNode) {
        result = result.filter((ancestor) => ancestor !== currentNode);
      } else {
        currentContainingBlockComputedStyle = computedStyle;
      }
      currentNode = getParentNode(currentNode);
    }
    cache.set(element, result);
    return result;
  }
  __name(getClippingElementAncestors, "getClippingElementAncestors");
  function getClippingRect(_ref) {
    let {
      element,
      boundary,
      rootBoundary,
      strategy
    } = _ref;
    const elementClippingAncestors = boundary === "clippingAncestors" ? isTopLayer(element) ? [] : getClippingElementAncestors(element, this._c) : [].concat(boundary);
    const clippingAncestors = [...elementClippingAncestors, rootBoundary];
    const firstClippingAncestor = clippingAncestors[0];
    const clippingRect = clippingAncestors.reduce((accRect, clippingAncestor) => {
      const rect = getClientRectFromClippingAncestor(element, clippingAncestor, strategy);
      accRect.top = max(rect.top, accRect.top);
      accRect.right = min(rect.right, accRect.right);
      accRect.bottom = min(rect.bottom, accRect.bottom);
      accRect.left = max(rect.left, accRect.left);
      return accRect;
    }, getClientRectFromClippingAncestor(element, firstClippingAncestor, strategy));
    return {
      width: clippingRect.right - clippingRect.left,
      height: clippingRect.bottom - clippingRect.top,
      x: clippingRect.left,
      y: clippingRect.top
    };
  }
  __name(getClippingRect, "getClippingRect");
  function getDimensions(element) {
    const {
      width,
      height
    } = getCssDimensions(element);
    return {
      width,
      height
    };
  }
  __name(getDimensions, "getDimensions");
  function getRectRelativeToOffsetParent(element, offsetParent, strategy) {
    const isOffsetParentAnElement = isHTMLElement(offsetParent);
    const documentElement = getDocumentElement(offsetParent);
    const isFixed = strategy === "fixed";
    const rect = getBoundingClientRect(element, true, isFixed, offsetParent);
    let scroll = {
      scrollLeft: 0,
      scrollTop: 0
    };
    const offsets = createCoords(0);
    function setLeftRTLScrollbarOffset() {
      offsets.x = getWindowScrollBarX(documentElement);
    }
    __name(setLeftRTLScrollbarOffset, "setLeftRTLScrollbarOffset");
    if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
      if (getNodeName(offsetParent) !== "body" || isOverflowElement(documentElement)) {
        scroll = getNodeScroll(offsetParent);
      }
      if (isOffsetParentAnElement) {
        const offsetRect = getBoundingClientRect(offsetParent, true, isFixed, offsetParent);
        offsets.x = offsetRect.x + offsetParent.clientLeft;
        offsets.y = offsetRect.y + offsetParent.clientTop;
      } else if (documentElement) {
        setLeftRTLScrollbarOffset();
      }
    }
    if (isFixed && !isOffsetParentAnElement && documentElement) {
      setLeftRTLScrollbarOffset();
    }
    const htmlOffset = documentElement && !isOffsetParentAnElement && !isFixed ? getHTMLOffset(documentElement, scroll) : createCoords(0);
    const x = rect.left + scroll.scrollLeft - offsets.x - htmlOffset.x;
    const y = rect.top + scroll.scrollTop - offsets.y - htmlOffset.y;
    return {
      x,
      y,
      width: rect.width,
      height: rect.height
    };
  }
  __name(getRectRelativeToOffsetParent, "getRectRelativeToOffsetParent");
  function isStaticPositioned(element) {
    return getComputedStyle2(element).position === "static";
  }
  __name(isStaticPositioned, "isStaticPositioned");
  function getTrueOffsetParent(element, polyfill) {
    if (!isHTMLElement(element) || getComputedStyle2(element).position === "fixed") {
      return null;
    }
    if (polyfill) {
      return polyfill(element);
    }
    let rawOffsetParent = element.offsetParent;
    if (getDocumentElement(element) === rawOffsetParent) {
      rawOffsetParent = rawOffsetParent.ownerDocument.body;
    }
    return rawOffsetParent;
  }
  __name(getTrueOffsetParent, "getTrueOffsetParent");
  function getOffsetParent(element, polyfill) {
    const win = getWindow(element);
    if (isTopLayer(element)) {
      return win;
    }
    if (!isHTMLElement(element)) {
      let svgOffsetParent = getParentNode(element);
      while (svgOffsetParent && !isLastTraversableNode(svgOffsetParent)) {
        if (isElement(svgOffsetParent) && !isStaticPositioned(svgOffsetParent)) {
          return svgOffsetParent;
        }
        svgOffsetParent = getParentNode(svgOffsetParent);
      }
      return win;
    }
    let offsetParent = getTrueOffsetParent(element, polyfill);
    while (offsetParent && isTableElement(offsetParent) && isStaticPositioned(offsetParent)) {
      offsetParent = getTrueOffsetParent(offsetParent, polyfill);
    }
    if (offsetParent && isLastTraversableNode(offsetParent) && isStaticPositioned(offsetParent) && !isContainingBlock(offsetParent)) {
      return win;
    }
    return offsetParent || getContainingBlock(element) || win;
  }
  __name(getOffsetParent, "getOffsetParent");
  var getElementRects = /* @__PURE__ */ __name(async function(data) {
    const getOffsetParentFn = this.getOffsetParent || getOffsetParent;
    const getDimensionsFn = this.getDimensions;
    const floatingDimensions = await getDimensionsFn(data.floating);
    return {
      reference: getRectRelativeToOffsetParent(data.reference, await getOffsetParentFn(data.floating), data.strategy),
      floating: {
        x: 0,
        y: 0,
        width: floatingDimensions.width,
        height: floatingDimensions.height
      }
    };
  }, "getElementRects");
  function isRTL(element) {
    return getComputedStyle2(element).direction === "rtl";
  }
  __name(isRTL, "isRTL");
  var platform = {
    convertOffsetParentRelativeRectToViewportRelativeRect,
    getDocumentElement,
    getClippingRect,
    getOffsetParent,
    getElementRects,
    getClientRects,
    getDimensions,
    getScale,
    isElement,
    isRTL
  };
  function rectsAreEqual(a, b) {
    return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
  }
  __name(rectsAreEqual, "rectsAreEqual");
  function observeMove(element, onMove) {
    let io = null;
    let timeoutId;
    const root = getDocumentElement(element);
    function cleanup() {
      var _io;
      clearTimeout(timeoutId);
      (_io = io) == null || _io.disconnect();
      io = null;
    }
    __name(cleanup, "cleanup");
    function refresh(skip, threshold) {
      if (skip === void 0) {
        skip = false;
      }
      if (threshold === void 0) {
        threshold = 1;
      }
      cleanup();
      const elementRectForRootMargin = element.getBoundingClientRect();
      const {
        left,
        top,
        width,
        height
      } = elementRectForRootMargin;
      if (!skip) {
        onMove();
      }
      if (!width || !height) {
        return;
      }
      const insetTop = floor(top);
      const insetRight = floor(root.clientWidth - (left + width));
      const insetBottom = floor(root.clientHeight - (top + height));
      const insetLeft = floor(left);
      const rootMargin = -insetTop + "px " + -insetRight + "px " + -insetBottom + "px " + -insetLeft + "px";
      const options = {
        rootMargin,
        threshold: max(0, min(1, threshold)) || 1
      };
      let isFirstUpdate = true;
      function handleObserve(entries) {
        const ratio = entries[0].intersectionRatio;
        if (ratio !== threshold) {
          if (!isFirstUpdate) {
            return refresh();
          }
          if (!ratio) {
            timeoutId = setTimeout(() => {
              refresh(false, 1e-7);
            }, 1e3);
          } else {
            refresh(false, ratio);
          }
        }
        if (ratio === 1 && !rectsAreEqual(elementRectForRootMargin, element.getBoundingClientRect())) {
          refresh();
        }
        isFirstUpdate = false;
      }
      __name(handleObserve, "handleObserve");
      try {
        io = new IntersectionObserver(handleObserve, {
          ...options,
          // Handle <iframe>s
          root: root.ownerDocument
        });
      } catch (_e) {
        io = new IntersectionObserver(handleObserve, options);
      }
      io.observe(element);
    }
    __name(refresh, "refresh");
    refresh(true);
    return cleanup;
  }
  __name(observeMove, "observeMove");
  function autoUpdate(reference, floating, update, options) {
    if (options === void 0) {
      options = {};
    }
    const {
      ancestorScroll = true,
      ancestorResize = true,
      elementResize = typeof ResizeObserver === "function",
      layoutShift = typeof IntersectionObserver === "function",
      animationFrame = false
    } = options;
    const referenceEl = unwrapElement(reference);
    const ancestors = ancestorScroll || ancestorResize ? [...referenceEl ? getOverflowAncestors(referenceEl) : [], ...getOverflowAncestors(floating)] : [];
    ancestors.forEach((ancestor) => {
      ancestorScroll && ancestor.addEventListener("scroll", update, {
        passive: true
      });
      ancestorResize && ancestor.addEventListener("resize", update);
    });
    const cleanupIo = referenceEl && layoutShift ? observeMove(referenceEl, update) : null;
    let reobserveFrame = -1;
    let resizeObserver = null;
    if (elementResize) {
      resizeObserver = new ResizeObserver((_ref) => {
        let [firstEntry] = _ref;
        if (firstEntry && firstEntry.target === referenceEl && resizeObserver) {
          resizeObserver.unobserve(floating);
          cancelAnimationFrame(reobserveFrame);
          reobserveFrame = requestAnimationFrame(() => {
            var _resizeObserver;
            (_resizeObserver = resizeObserver) == null || _resizeObserver.observe(floating);
          });
        }
        update();
      });
      if (referenceEl && !animationFrame) {
        resizeObserver.observe(referenceEl);
      }
      resizeObserver.observe(floating);
    }
    let frameId;
    let prevRefRect = animationFrame ? getBoundingClientRect(reference) : null;
    if (animationFrame) {
      frameLoop();
    }
    function frameLoop() {
      const nextRefRect = getBoundingClientRect(reference);
      if (prevRefRect && !rectsAreEqual(prevRefRect, nextRefRect)) {
        update();
      }
      prevRefRect = nextRefRect;
      frameId = requestAnimationFrame(frameLoop);
    }
    __name(frameLoop, "frameLoop");
    update();
    return () => {
      var _resizeObserver2;
      ancestors.forEach((ancestor) => {
        ancestorScroll && ancestor.removeEventListener("scroll", update);
        ancestorResize && ancestor.removeEventListener("resize", update);
      });
      cleanupIo == null || cleanupIo();
      (_resizeObserver2 = resizeObserver) == null || _resizeObserver2.disconnect();
      resizeObserver = null;
      if (animationFrame) {
        cancelAnimationFrame(frameId);
      }
    };
  }
  __name(autoUpdate, "autoUpdate");
  var offset2 = offset;
  var shift2 = shift;
  var flip2 = flip;
  var size2 = size;
  var hide2 = hide;
  var arrow2 = arrow;
  var limitShift2 = limitShift;
  var computePosition2 = /* @__PURE__ */ __name((reference, floating, options) => {
    const cache = /* @__PURE__ */ new Map();
    const mergedOptions = {
      platform,
      ...options
    };
    const platformWithCache = {
      ...mergedOptions.platform,
      _c: cache
    };
    return computePosition(reference, floating, {
      ...mergedOptions,
      platform: platformWithCache
    });
  }, "computePosition");

  // node_modules/@floating-ui/react-dom/dist/floating-ui.react-dom.mjs
  var React29 = __toESM(require_react(), 1);
  var import_react5 = __toESM(require_react(), 1);
  var ReactDOM3 = __toESM(require_react_dom(), 1);
  var isClient = typeof document !== "undefined";
  var noop = /* @__PURE__ */ __name(function noop2() {
  }, "noop");
  var index = isClient ? import_react5.useLayoutEffect : noop;
  function deepEqual(a, b) {
    if (a === b) {
      return true;
    }
    if (typeof a !== typeof b) {
      return false;
    }
    if (typeof a === "function" && a.toString() === b.toString()) {
      return true;
    }
    let length;
    let i;
    let keys;
    if (a && b && typeof a === "object") {
      if (Array.isArray(a)) {
        length = a.length;
        if (length !== b.length) return false;
        for (i = length; i-- !== 0; ) {
          if (!deepEqual(a[i], b[i])) {
            return false;
          }
        }
        return true;
      }
      keys = Object.keys(a);
      length = keys.length;
      if (length !== Object.keys(b).length) {
        return false;
      }
      for (i = length; i-- !== 0; ) {
        if (!{}.hasOwnProperty.call(b, keys[i])) {
          return false;
        }
      }
      for (i = length; i-- !== 0; ) {
        const key = keys[i];
        if (key === "_owner" && a.$$typeof) {
          continue;
        }
        if (!deepEqual(a[key], b[key])) {
          return false;
        }
      }
      return true;
    }
    return a !== a && b !== b;
  }
  __name(deepEqual, "deepEqual");
  function getDPR(element) {
    if (typeof window === "undefined") {
      return 1;
    }
    const win = element.ownerDocument.defaultView || window;
    return win.devicePixelRatio || 1;
  }
  __name(getDPR, "getDPR");
  function roundByDPR(element, value) {
    const dpr = getDPR(element);
    return Math.round(value * dpr) / dpr;
  }
  __name(roundByDPR, "roundByDPR");
  function useLatestRef(value) {
    const ref = React29.useRef(value);
    index(() => {
      ref.current = value;
    });
    return ref;
  }
  __name(useLatestRef, "useLatestRef");
  function useFloating(options) {
    if (options === void 0) {
      options = {};
    }
    const {
      placement = "bottom",
      strategy = "absolute",
      middleware = [],
      platform: platform2,
      elements: {
        reference: externalReference,
        floating: externalFloating
      } = {},
      transform = true,
      whileElementsMounted,
      open
    } = options;
    const [data, setData] = React29.useState({
      x: 0,
      y: 0,
      strategy,
      placement,
      middlewareData: {},
      isPositioned: false
    });
    const [latestMiddleware, setLatestMiddleware] = React29.useState(middleware);
    if (!deepEqual(latestMiddleware, middleware)) {
      setLatestMiddleware(middleware);
    }
    const [_reference, _setReference] = React29.useState(null);
    const [_floating, _setFloating] = React29.useState(null);
    const setReference = React29.useCallback((node) => {
      if (node !== referenceRef.current) {
        referenceRef.current = node;
        _setReference(node);
      }
    }, []);
    const setFloating = React29.useCallback((node) => {
      if (node !== floatingRef.current) {
        floatingRef.current = node;
        _setFloating(node);
      }
    }, []);
    const referenceEl = externalReference || _reference;
    const floatingEl = externalFloating || _floating;
    const referenceRef = React29.useRef(null);
    const floatingRef = React29.useRef(null);
    const dataRef = React29.useRef(data);
    const hasWhileElementsMounted = whileElementsMounted != null;
    const whileElementsMountedRef = useLatestRef(whileElementsMounted);
    const platformRef = useLatestRef(platform2);
    const openRef = useLatestRef(open);
    const update = React29.useCallback(() => {
      if (!referenceRef.current || !floatingRef.current) {
        return;
      }
      const config = {
        placement,
        strategy,
        middleware: latestMiddleware
      };
      if (platformRef.current) {
        config.platform = platformRef.current;
      }
      computePosition2(referenceRef.current, floatingRef.current, config).then((data2) => {
        const fullData = {
          ...data2,
          // The floating element's position may be recomputed while it's closed
          // but still mounted (such as when transitioning out). To ensure
          // `isPositioned` will be `false` initially on the next open, avoid
          // setting it to `true` when `open === false` (must be specified).
          isPositioned: openRef.current !== false
        };
        if (isMountedRef.current && !deepEqual(dataRef.current, fullData)) {
          dataRef.current = fullData;
          ReactDOM3.flushSync(() => {
            setData(fullData);
          });
        }
      });
    }, [latestMiddleware, placement, strategy, platformRef, openRef]);
    index(() => {
      if (open === false && dataRef.current.isPositioned) {
        dataRef.current.isPositioned = false;
        setData((data2) => ({
          ...data2,
          isPositioned: false
        }));
      }
    }, [open]);
    const isMountedRef = React29.useRef(false);
    index(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);
    index(() => {
      if (referenceEl) referenceRef.current = referenceEl;
      if (floatingEl) floatingRef.current = floatingEl;
      if (referenceEl && floatingEl) {
        if (whileElementsMountedRef.current) {
          return whileElementsMountedRef.current(referenceEl, floatingEl, update);
        }
        update();
      }
    }, [referenceEl, floatingEl, update, whileElementsMountedRef, hasWhileElementsMounted]);
    const refs = React29.useMemo(() => ({
      reference: referenceRef,
      floating: floatingRef,
      setReference,
      setFloating
    }), [setReference, setFloating]);
    const elements = React29.useMemo(() => ({
      reference: referenceEl,
      floating: floatingEl
    }), [referenceEl, floatingEl]);
    const floatingStyles = React29.useMemo(() => {
      const initialStyles = {
        position: strategy,
        left: 0,
        top: 0
      };
      if (!elements.floating) {
        return initialStyles;
      }
      const x = roundByDPR(elements.floating, data.x);
      const y = roundByDPR(elements.floating, data.y);
      if (transform) {
        return {
          ...initialStyles,
          transform: "translate(" + x + "px, " + y + "px)",
          ...getDPR(elements.floating) >= 1.5 && {
            willChange: "transform"
          }
        };
      }
      return {
        position: strategy,
        left: x,
        top: y
      };
    }, [strategy, transform, elements.floating, data.x, data.y]);
    return React29.useMemo(() => ({
      ...data,
      update,
      refs,
      elements,
      floatingStyles
    }), [data, update, refs, elements, floatingStyles]);
  }
  __name(useFloating, "useFloating");
  var arrow$1 = /* @__PURE__ */ __name((options) => {
    function isRef(value) {
      return {}.hasOwnProperty.call(value, "current");
    }
    __name(isRef, "isRef");
    return {
      name: "arrow",
      options,
      fn(state) {
        const {
          element,
          padding
        } = typeof options === "function" ? options(state) : options;
        if (element && isRef(element)) {
          if (element.current != null) {
            return arrow2({
              element: element.current,
              padding
            }).fn(state);
          }
          return {};
        }
        if (element) {
          return arrow2({
            element,
            padding
          }).fn(state);
        }
        return {};
      }
    };
  }, "arrow$1");
  var offset3 = /* @__PURE__ */ __name((options, deps) => ({
    ...offset2(options),
    options: [options, deps]
  }), "offset");
  var shift3 = /* @__PURE__ */ __name((options, deps) => ({
    ...shift2(options),
    options: [options, deps]
  }), "shift");
  var limitShift3 = /* @__PURE__ */ __name((options, deps) => ({
    ...limitShift2(options),
    options: [options, deps]
  }), "limitShift");
  var flip3 = /* @__PURE__ */ __name((options, deps) => ({
    ...flip2(options),
    options: [options, deps]
  }), "flip");
  var size3 = /* @__PURE__ */ __name((options, deps) => ({
    ...size2(options),
    options: [options, deps]
  }), "size");
  var hide3 = /* @__PURE__ */ __name((options, deps) => ({
    ...hide2(options),
    options: [options, deps]
  }), "hide");
  var arrow3 = /* @__PURE__ */ __name((options, deps) => ({
    ...arrow$1(options),
    options: [options, deps]
  }), "arrow");

  // node_modules/@radix-ui/react-arrow/dist/index.mjs
  var React30 = __toESM(require_react(), 1);
  var import_jsx_runtime12 = __toESM(require_jsx_runtime(), 1);
  var NAME = "Arrow";
  var Arrow = React30.forwardRef((props, forwardedRef) => {
    const { children, width = 10, height = 5, ...arrowProps } = props;
    return /* @__PURE__ */ (0, import_jsx_runtime12.jsx)(
      Primitive.svg,
      {
        ...arrowProps,
        ref: forwardedRef,
        width,
        height,
        viewBox: "0 0 30 10",
        preserveAspectRatio: "none",
        children: props.asChild ? children : /* @__PURE__ */ (0, import_jsx_runtime12.jsx)("polygon", { points: "0,0 30,0 15,10" })
      }
    );
  });
  Arrow.displayName = NAME;
  var Root = Arrow;

  // node_modules/@radix-ui/react-popper/dist/index.mjs
  var import_jsx_runtime13 = __toESM(require_jsx_runtime(), 1);
  var POPPER_NAME = "Popper";
  var [createPopperContext, createPopperScope] = createContextScope(POPPER_NAME);
  var [PopperProvider, usePopperContext] = createPopperContext(POPPER_NAME);
  var Popper = /* @__PURE__ */ __name((props) => {
    const { __scopePopper, children } = props;
    const [anchor, setAnchor] = React31.useState(null);
    return /* @__PURE__ */ (0, import_jsx_runtime13.jsx)(PopperProvider, { scope: __scopePopper, anchor, onAnchorChange: setAnchor, children });
  }, "Popper");
  Popper.displayName = POPPER_NAME;
  var ANCHOR_NAME = "PopperAnchor";
  var PopperAnchor = React31.forwardRef(
    (props, forwardedRef) => {
      const { __scopePopper, virtualRef, ...anchorProps } = props;
      const context = usePopperContext(ANCHOR_NAME, __scopePopper);
      const ref = React31.useRef(null);
      const composedRefs = useComposedRefs(forwardedRef, ref);
      const anchorRef = React31.useRef(null);
      React31.useEffect(() => {
        const previousAnchor = anchorRef.current;
        anchorRef.current = virtualRef?.current || ref.current;
        if (previousAnchor !== anchorRef.current) {
          context.onAnchorChange(anchorRef.current);
        }
      });
      return virtualRef ? null : /* @__PURE__ */ (0, import_jsx_runtime13.jsx)(Primitive.div, { ...anchorProps, ref: composedRefs });
    }
  );
  PopperAnchor.displayName = ANCHOR_NAME;
  var CONTENT_NAME = "PopperContent";
  var [PopperContentProvider, useContentContext] = createPopperContext(CONTENT_NAME);
  var PopperContent = React31.forwardRef(
    (props, forwardedRef) => {
      const {
        __scopePopper,
        side = "bottom",
        sideOffset = 0,
        align = "center",
        alignOffset = 0,
        arrowPadding = 0,
        avoidCollisions = true,
        collisionBoundary = [],
        collisionPadding: collisionPaddingProp = 0,
        sticky = "partial",
        hideWhenDetached = false,
        updatePositionStrategy = "optimized",
        onPlaced,
        ...contentProps
      } = props;
      const context = usePopperContext(CONTENT_NAME, __scopePopper);
      const [content, setContent] = React31.useState(null);
      const composedRefs = useComposedRefs(forwardedRef, (node) => setContent(node));
      const [arrow4, setArrow] = React31.useState(null);
      const arrowSize = useSize(arrow4);
      const arrowWidth = arrowSize?.width ?? 0;
      const arrowHeight = arrowSize?.height ?? 0;
      const desiredPlacement = side + (align !== "center" ? "-" + align : "");
      const collisionPadding = typeof collisionPaddingProp === "number" ? collisionPaddingProp : { top: 0, right: 0, bottom: 0, left: 0, ...collisionPaddingProp };
      const boundary = Array.isArray(collisionBoundary) ? collisionBoundary : [collisionBoundary];
      const hasExplicitBoundaries = boundary.length > 0;
      const detectOverflowOptions = {
        padding: collisionPadding,
        boundary: boundary.filter(isNotNull),
        // with `strategy: 'fixed'`, this is the only way to get it to respect boundaries
        altBoundary: hasExplicitBoundaries
      };
      const { refs, floatingStyles, placement, isPositioned, middlewareData } = useFloating({
        // default to `fixed` strategy so users don't have to pick and we also avoid focus scroll issues
        strategy: "fixed",
        placement: desiredPlacement,
        whileElementsMounted: /* @__PURE__ */ __name((...args) => {
          const cleanup = autoUpdate(...args, {
            animationFrame: updatePositionStrategy === "always"
          });
          return cleanup;
        }, "whileElementsMounted"),
        elements: {
          reference: context.anchor
        },
        middleware: [
          offset3({ mainAxis: sideOffset + arrowHeight, alignmentAxis: alignOffset }),
          avoidCollisions && shift3({
            mainAxis: true,
            crossAxis: false,
            limiter: sticky === "partial" ? limitShift3() : void 0,
            ...detectOverflowOptions
          }),
          avoidCollisions && flip3({ ...detectOverflowOptions }),
          size3({
            ...detectOverflowOptions,
            apply: /* @__PURE__ */ __name(({ elements, rects, availableWidth, availableHeight }) => {
              const { width: anchorWidth, height: anchorHeight } = rects.reference;
              const contentStyle = elements.floating.style;
              contentStyle.setProperty("--radix-popper-available-width", `${availableWidth}px`);
              contentStyle.setProperty("--radix-popper-available-height", `${availableHeight}px`);
              contentStyle.setProperty("--radix-popper-anchor-width", `${anchorWidth}px`);
              contentStyle.setProperty("--radix-popper-anchor-height", `${anchorHeight}px`);
            }, "apply")
          }),
          arrow4 && arrow3({ element: arrow4, padding: arrowPadding }),
          transformOrigin({ arrowWidth, arrowHeight }),
          hideWhenDetached && hide3({ strategy: "referenceHidden", ...detectOverflowOptions })
        ]
      });
      const [placedSide, placedAlign] = getSideAndAlignFromPlacement(placement);
      const handlePlaced = useCallbackRef(onPlaced);
      useLayoutEffect22(() => {
        if (isPositioned) {
          handlePlaced?.();
        }
      }, [isPositioned, handlePlaced]);
      const arrowX = middlewareData.arrow?.x;
      const arrowY = middlewareData.arrow?.y;
      const cannotCenterArrow = middlewareData.arrow?.centerOffset !== 0;
      const [contentZIndex, setContentZIndex] = React31.useState();
      useLayoutEffect22(() => {
        if (content) setContentZIndex(window.getComputedStyle(content).zIndex);
      }, [content]);
      return /* @__PURE__ */ (0, import_jsx_runtime13.jsx)(
        "div",
        {
          ref: refs.setFloating,
          "data-radix-popper-content-wrapper": "",
          style: {
            ...floatingStyles,
            transform: isPositioned ? floatingStyles.transform : "translate(0, -200%)",
            // keep off the page when measuring
            minWidth: "max-content",
            zIndex: contentZIndex,
            ["--radix-popper-transform-origin"]: [
              middlewareData.transformOrigin?.x,
              middlewareData.transformOrigin?.y
            ].join(" "),
            // hide the content if using the hide middleware and should be hidden
            // set visibility to hidden and disable pointer events so the UI behaves
            // as if the PopperContent isn't there at all
            ...middlewareData.hide?.referenceHidden && {
              visibility: "hidden",
              pointerEvents: "none"
            }
          },
          dir: props.dir,
          children: /* @__PURE__ */ (0, import_jsx_runtime13.jsx)(
            PopperContentProvider,
            {
              scope: __scopePopper,
              placedSide,
              onArrowChange: setArrow,
              arrowX,
              arrowY,
              shouldHideArrow: cannotCenterArrow,
              children: /* @__PURE__ */ (0, import_jsx_runtime13.jsx)(
                Primitive.div,
                {
                  "data-side": placedSide,
                  "data-align": placedAlign,
                  ...contentProps,
                  ref: composedRefs,
                  style: {
                    ...contentProps.style,
                    // if the PopperContent hasn't been placed yet (not all measurements done)
                    // we prevent animations so that users's animation don't kick in too early referring wrong sides
                    animation: !isPositioned ? "none" : void 0
                  }
                }
              )
            }
          )
        }
      );
    }
  );
  PopperContent.displayName = CONTENT_NAME;
  var ARROW_NAME = "PopperArrow";
  var OPPOSITE_SIDE = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right"
  };
  var PopperArrow = React31.forwardRef(/* @__PURE__ */ __name(function PopperArrow2(props, forwardedRef) {
    const { __scopePopper, ...arrowProps } = props;
    const contentContext = useContentContext(ARROW_NAME, __scopePopper);
    const baseSide = OPPOSITE_SIDE[contentContext.placedSide];
    return (
      // we have to use an extra wrapper because `ResizeObserver` (used by `useSize`)
      // doesn't report size as we'd expect on SVG elements.
      // it reports their bounding box which is effectively the largest path inside the SVG.
      /* @__PURE__ */ (0, import_jsx_runtime13.jsx)(
        "span",
        {
          ref: contentContext.onArrowChange,
          style: {
            position: "absolute",
            left: contentContext.arrowX,
            top: contentContext.arrowY,
            [baseSide]: 0,
            transformOrigin: {
              top: "",
              right: "0 0",
              bottom: "center 0",
              left: "100% 0"
            }[contentContext.placedSide],
            transform: {
              top: "translateY(100%)",
              right: "translateY(50%) rotate(90deg) translateX(-50%)",
              bottom: `rotate(180deg)`,
              left: "translateY(50%) rotate(-90deg) translateX(50%)"
            }[contentContext.placedSide],
            visibility: contentContext.shouldHideArrow ? "hidden" : void 0
          },
          children: /* @__PURE__ */ (0, import_jsx_runtime13.jsx)(
            Root,
            {
              ...arrowProps,
              ref: forwardedRef,
              style: {
                ...arrowProps.style,
                // ensures the element can be measured correctly (mostly for if SVG)
                display: "block"
              }
            }
          )
        }
      )
    );
  }, "PopperArrow2"));
  PopperArrow.displayName = ARROW_NAME;
  function isNotNull(value) {
    return value !== null;
  }
  __name(isNotNull, "isNotNull");
  var transformOrigin = /* @__PURE__ */ __name((options) => ({
    name: "transformOrigin",
    options,
    fn(data) {
      const { placement, rects, middlewareData } = data;
      const cannotCenterArrow = middlewareData.arrow?.centerOffset !== 0;
      const isArrowHidden = cannotCenterArrow;
      const arrowWidth = isArrowHidden ? 0 : options.arrowWidth;
      const arrowHeight = isArrowHidden ? 0 : options.arrowHeight;
      const [placedSide, placedAlign] = getSideAndAlignFromPlacement(placement);
      const noArrowAlign = { start: "0%", center: "50%", end: "100%" }[placedAlign];
      const arrowXCenter = (middlewareData.arrow?.x ?? 0) + arrowWidth / 2;
      const arrowYCenter = (middlewareData.arrow?.y ?? 0) + arrowHeight / 2;
      let x = "";
      let y = "";
      if (placedSide === "bottom") {
        x = isArrowHidden ? noArrowAlign : `${arrowXCenter}px`;
        y = `${-arrowHeight}px`;
      } else if (placedSide === "top") {
        x = isArrowHidden ? noArrowAlign : `${arrowXCenter}px`;
        y = `${rects.floating.height + arrowHeight}px`;
      } else if (placedSide === "right") {
        x = `${-arrowHeight}px`;
        y = isArrowHidden ? noArrowAlign : `${arrowYCenter}px`;
      } else if (placedSide === "left") {
        x = `${rects.floating.width + arrowHeight}px`;
        y = isArrowHidden ? noArrowAlign : `${arrowYCenter}px`;
      }
      return { data: { x, y } };
    }
  }), "transformOrigin");
  function getSideAndAlignFromPlacement(placement) {
    const [side, align = "center"] = placement.split("-");
    return [side, align];
  }
  __name(getSideAndAlignFromPlacement, "getSideAndAlignFromPlacement");
  var Root2 = Popper;
  var Anchor = PopperAnchor;
  var Content = PopperContent;
  var Arrow2 = PopperArrow;

  // node_modules/@radix-ui/react-roving-focus/dist/index.mjs
  var React32 = __toESM(require_react(), 1);
  var import_jsx_runtime14 = __toESM(require_jsx_runtime(), 1);
  var ENTRY_FOCUS = "rovingFocusGroup.onEntryFocus";
  var EVENT_OPTIONS2 = { bubbles: false, cancelable: true };
  var GROUP_NAME = "RovingFocusGroup";
  var [Collection, useCollection, createCollectionScope] = createCollection(GROUP_NAME);
  var [createRovingFocusGroupContext, createRovingFocusGroupScope] = createContextScope(
    GROUP_NAME,
    [createCollectionScope]
  );
  var [RovingFocusProvider, useRovingFocusContext] = createRovingFocusGroupContext(GROUP_NAME);
  var RovingFocusGroup = React32.forwardRef(
    (props, forwardedRef) => {
      return /* @__PURE__ */ (0, import_jsx_runtime14.jsx)(Collection.Provider, { scope: props.__scopeRovingFocusGroup, children: /* @__PURE__ */ (0, import_jsx_runtime14.jsx)(Collection.Slot, { scope: props.__scopeRovingFocusGroup, children: /* @__PURE__ */ (0, import_jsx_runtime14.jsx)(RovingFocusGroupImpl, { ...props, ref: forwardedRef }) }) });
    }
  );
  RovingFocusGroup.displayName = GROUP_NAME;
  var RovingFocusGroupImpl = React32.forwardRef((props, forwardedRef) => {
    const {
      __scopeRovingFocusGroup,
      orientation,
      loop = false,
      dir,
      currentTabStopId: currentTabStopIdProp,
      defaultCurrentTabStopId,
      onCurrentTabStopIdChange,
      onEntryFocus,
      preventScrollOnEntryFocus = false,
      ...groupProps
    } = props;
    const ref = React32.useRef(null);
    const composedRefs = useComposedRefs(forwardedRef, ref);
    const direction = useDirection(dir);
    const [currentTabStopId, setCurrentTabStopId] = useControllableState({
      prop: currentTabStopIdProp,
      defaultProp: defaultCurrentTabStopId ?? null,
      onChange: onCurrentTabStopIdChange,
      caller: GROUP_NAME
    });
    const [isTabbingBackOut, setIsTabbingBackOut] = React32.useState(false);
    const handleEntryFocus = useCallbackRef(onEntryFocus);
    const getItems = useCollection(__scopeRovingFocusGroup);
    const isClickFocusRef = React32.useRef(false);
    const [focusableItemsCount, setFocusableItemsCount] = React32.useState(0);
    React32.useEffect(() => {
      const node = ref.current;
      if (node) {
        node.addEventListener(ENTRY_FOCUS, handleEntryFocus);
        return () => node.removeEventListener(ENTRY_FOCUS, handleEntryFocus);
      }
    }, [handleEntryFocus]);
    return /* @__PURE__ */ (0, import_jsx_runtime14.jsx)(
      RovingFocusProvider,
      {
        scope: __scopeRovingFocusGroup,
        orientation,
        dir: direction,
        loop,
        currentTabStopId,
        onItemFocus: React32.useCallback(
          (tabStopId) => setCurrentTabStopId(tabStopId),
          [setCurrentTabStopId]
        ),
        onItemShiftTab: React32.useCallback(() => setIsTabbingBackOut(true), []),
        onFocusableItemAdd: React32.useCallback(
          () => setFocusableItemsCount((prevCount) => prevCount + 1),
          []
        ),
        onFocusableItemRemove: React32.useCallback(
          () => setFocusableItemsCount((prevCount) => prevCount - 1),
          []
        ),
        children: /* @__PURE__ */ (0, import_jsx_runtime14.jsx)(
          Primitive.div,
          {
            tabIndex: isTabbingBackOut || focusableItemsCount === 0 ? -1 : 0,
            "data-orientation": orientation,
            ...groupProps,
            ref: composedRefs,
            style: { outline: "none", ...props.style },
            onMouseDown: composeEventHandlers(props.onMouseDown, () => {
              isClickFocusRef.current = true;
            }),
            onFocus: composeEventHandlers(props.onFocus, (event) => {
              const isKeyboardFocus = !isClickFocusRef.current;
              if (event.target === event.currentTarget && isKeyboardFocus && !isTabbingBackOut) {
                const entryFocusEvent = new CustomEvent(ENTRY_FOCUS, EVENT_OPTIONS2);
                event.currentTarget.dispatchEvent(entryFocusEvent);
                if (!entryFocusEvent.defaultPrevented) {
                  const items = getItems().filter((item) => item.focusable);
                  const activeItem = items.find((item) => item.active);
                  const currentItem = items.find((item) => item.id === currentTabStopId);
                  const candidateItems = [activeItem, currentItem, ...items].filter(
                    Boolean
                  );
                  const candidateNodes = candidateItems.map((item) => item.ref.current);
                  focusFirst2(candidateNodes, preventScrollOnEntryFocus);
                }
              }
              isClickFocusRef.current = false;
            }),
            onBlur: composeEventHandlers(props.onBlur, () => setIsTabbingBackOut(false))
          }
        )
      }
    );
  });
  var ITEM_NAME = "RovingFocusGroupItem";
  var RovingFocusGroupItem = React32.forwardRef(
    (props, forwardedRef) => {
      const {
        __scopeRovingFocusGroup,
        focusable = true,
        active = false,
        tabStopId,
        children,
        ...itemProps
      } = props;
      const autoId = useId();
      const id = tabStopId || autoId;
      const context = useRovingFocusContext(ITEM_NAME, __scopeRovingFocusGroup);
      const isCurrentTabStop = context.currentTabStopId === id;
      const getItems = useCollection(__scopeRovingFocusGroup);
      const { onFocusableItemAdd, onFocusableItemRemove, currentTabStopId } = context;
      React32.useEffect(() => {
        if (focusable) {
          onFocusableItemAdd();
          return () => onFocusableItemRemove();
        }
      }, [focusable, onFocusableItemAdd, onFocusableItemRemove]);
      return /* @__PURE__ */ (0, import_jsx_runtime14.jsx)(
        Collection.ItemSlot,
        {
          scope: __scopeRovingFocusGroup,
          id,
          focusable,
          active,
          children: /* @__PURE__ */ (0, import_jsx_runtime14.jsx)(
            Primitive.span,
            {
              tabIndex: isCurrentTabStop ? 0 : -1,
              "data-orientation": context.orientation,
              ...itemProps,
              ref: forwardedRef,
              onMouseDown: composeEventHandlers(props.onMouseDown, (event) => {
                if (!focusable) event.preventDefault();
                else context.onItemFocus(id);
              }),
              onFocus: composeEventHandlers(props.onFocus, () => context.onItemFocus(id)),
              onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
                if (event.key === "Tab" && event.shiftKey) {
                  context.onItemShiftTab();
                  return;
                }
                if (event.target !== event.currentTarget) return;
                const focusIntent = getFocusIntent(event, context.orientation, context.dir);
                if (focusIntent !== void 0) {
                  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
                  event.preventDefault();
                  const items = getItems().filter((item) => item.focusable);
                  let candidateNodes = items.map((item) => item.ref.current);
                  if (focusIntent === "last") candidateNodes.reverse();
                  else if (focusIntent === "prev" || focusIntent === "next") {
                    if (focusIntent === "prev") candidateNodes.reverse();
                    const currentIndex = candidateNodes.indexOf(event.currentTarget);
                    candidateNodes = context.loop ? wrapArray(candidateNodes, currentIndex + 1) : candidateNodes.slice(currentIndex + 1);
                  }
                  setTimeout(() => focusFirst2(candidateNodes));
                }
              }),
              children: typeof children === "function" ? children({ isCurrentTabStop, hasTabStop: currentTabStopId != null }) : children
            }
          )
        }
      );
    }
  );
  RovingFocusGroupItem.displayName = ITEM_NAME;
  var MAP_KEY_TO_FOCUS_INTENT = {
    ArrowLeft: "prev",
    ArrowUp: "prev",
    ArrowRight: "next",
    ArrowDown: "next",
    PageUp: "first",
    Home: "first",
    PageDown: "last",
    End: "last"
  };
  function getDirectionAwareKey(key, dir) {
    if (dir !== "rtl") return key;
    return key === "ArrowLeft" ? "ArrowRight" : key === "ArrowRight" ? "ArrowLeft" : key;
  }
  __name(getDirectionAwareKey, "getDirectionAwareKey");
  function getFocusIntent(event, orientation, dir) {
    const key = getDirectionAwareKey(event.key, dir);
    if (orientation === "vertical" && ["ArrowLeft", "ArrowRight"].includes(key)) return void 0;
    if (orientation === "horizontal" && ["ArrowUp", "ArrowDown"].includes(key)) return void 0;
    return MAP_KEY_TO_FOCUS_INTENT[key];
  }
  __name(getFocusIntent, "getFocusIntent");
  function focusFirst2(candidates, preventScroll = false) {
    const PREVIOUSLY_FOCUSED_ELEMENT = document.activeElement;
    for (const candidate of candidates) {
      if (candidate === PREVIOUSLY_FOCUSED_ELEMENT) return;
      candidate.focus({ preventScroll });
      if (document.activeElement !== PREVIOUSLY_FOCUSED_ELEMENT) return;
    }
  }
  __name(focusFirst2, "focusFirst");
  function wrapArray(array, startIndex) {
    return array.map((_, index2) => array[(startIndex + index2) % array.length]);
  }
  __name(wrapArray, "wrapArray");
  var Root3 = RovingFocusGroup;
  var Item = RovingFocusGroupItem;

  // node_modules/@radix-ui/react-menu/dist/index.mjs
  var import_jsx_runtime15 = __toESM(require_jsx_runtime(), 1);
  var SELECTION_KEYS = ["Enter", " "];
  var FIRST_KEYS = ["ArrowDown", "PageUp", "Home"];
  var LAST_KEYS = ["ArrowUp", "PageDown", "End"];
  var FIRST_LAST_KEYS = [...FIRST_KEYS, ...LAST_KEYS];
  var SUB_OPEN_KEYS = {
    ltr: [...SELECTION_KEYS, "ArrowRight"],
    rtl: [...SELECTION_KEYS, "ArrowLeft"]
  };
  var SUB_CLOSE_KEYS = {
    ltr: ["ArrowLeft"],
    rtl: ["ArrowRight"]
  };
  var MENU_NAME = "Menu";
  var [Collection2, useCollection2, createCollectionScope2] = createCollection(MENU_NAME);
  var [createMenuContext, createMenuScope] = createContextScope(MENU_NAME, [
    createCollectionScope2,
    createPopperScope,
    createRovingFocusGroupScope
  ]);
  var usePopperScope = createPopperScope();
  var useRovingFocusGroupScope = createRovingFocusGroupScope();
  var [MenuProvider, useMenuContext] = createMenuContext(MENU_NAME);
  var [MenuRootProvider, useMenuRootContext] = createMenuContext(MENU_NAME);
  var Menu = /* @__PURE__ */ __name((props) => {
    const { __scopeMenu, open = false, children, dir, onOpenChange, modal = true } = props;
    const popperScope = usePopperScope(__scopeMenu);
    const [content, setContent] = React33.useState(null);
    const isUsingKeyboardRef = React33.useRef(false);
    const handleOpenChange = useCallbackRef(onOpenChange);
    const direction = useDirection(dir);
    React33.useEffect(() => {
      const handleKeyDown = /* @__PURE__ */ __name(() => {
        isUsingKeyboardRef.current = true;
        document.addEventListener("pointerdown", handlePointer, { capture: true, once: true });
        document.addEventListener("pointermove", handlePointer, { capture: true, once: true });
      }, "handleKeyDown");
      const handlePointer = /* @__PURE__ */ __name(() => isUsingKeyboardRef.current = false, "handlePointer");
      document.addEventListener("keydown", handleKeyDown, { capture: true });
      return () => {
        document.removeEventListener("keydown", handleKeyDown, { capture: true });
        document.removeEventListener("pointerdown", handlePointer, { capture: true });
        document.removeEventListener("pointermove", handlePointer, { capture: true });
      };
    }, []);
    return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Root2, { ...popperScope, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
      MenuProvider,
      {
        scope: __scopeMenu,
        open,
        onOpenChange: handleOpenChange,
        content,
        onContentChange: setContent,
        children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
          MenuRootProvider,
          {
            scope: __scopeMenu,
            onClose: React33.useCallback(() => handleOpenChange(false), [handleOpenChange]),
            isUsingKeyboardRef,
            dir: direction,
            modal,
            children
          }
        )
      }
    ) });
  }, "Menu");
  Menu.displayName = MENU_NAME;
  var ANCHOR_NAME2 = "MenuAnchor";
  var MenuAnchor = React33.forwardRef(
    (props, forwardedRef) => {
      const { __scopeMenu, ...anchorProps } = props;
      const popperScope = usePopperScope(__scopeMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Anchor, { ...popperScope, ...anchorProps, ref: forwardedRef });
    }
  );
  MenuAnchor.displayName = ANCHOR_NAME2;
  var PORTAL_NAME2 = "MenuPortal";
  var [PortalProvider, usePortalContext] = createMenuContext(PORTAL_NAME2, {
    forceMount: void 0
  });
  var MenuPortal = /* @__PURE__ */ __name((props) => {
    const { __scopeMenu, forceMount, children, container } = props;
    const context = useMenuContext(PORTAL_NAME2, __scopeMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(PortalProvider, { scope: __scopeMenu, forceMount, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Portal, { asChild: true, container, children }) }) });
  }, "MenuPortal");
  MenuPortal.displayName = PORTAL_NAME2;
  var CONTENT_NAME2 = "MenuContent";
  var [MenuContentProvider, useMenuContentContext] = createMenuContext(CONTENT_NAME2);
  var MenuContent = React33.forwardRef(
    (props, forwardedRef) => {
      const portalContext = usePortalContext(CONTENT_NAME2, props.__scopeMenu);
      const { forceMount = portalContext.forceMount, ...contentProps } = props;
      const context = useMenuContext(CONTENT_NAME2, props.__scopeMenu);
      const rootContext = useMenuRootContext(CONTENT_NAME2, props.__scopeMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Collection2.Provider, { scope: props.__scopeMenu, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Collection2.Slot, { scope: props.__scopeMenu, children: rootContext.modal ? /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(MenuRootContentModal, { ...contentProps, ref: forwardedRef }) : /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(MenuRootContentNonModal, { ...contentProps, ref: forwardedRef }) }) }) });
    }
  );
  var MenuRootContentModal = React33.forwardRef(
    (props, forwardedRef) => {
      const context = useMenuContext(CONTENT_NAME2, props.__scopeMenu);
      const ref = React33.useRef(null);
      const composedRefs = useComposedRefs(forwardedRef, ref);
      React33.useEffect(() => {
        const content = ref.current;
        if (content) return hideOthers(content);
      }, []);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        MenuContentImpl,
        {
          ...props,
          ref: composedRefs,
          trapFocus: context.open,
          disableOutsidePointerEvents: context.open,
          disableOutsideScroll: true,
          onFocusOutside: composeEventHandlers(
            props.onFocusOutside,
            (event) => event.preventDefault(),
            { checkForDefaultPrevented: false }
          ),
          onDismiss: /* @__PURE__ */ __name(() => context.onOpenChange(false), "onDismiss")
        }
      );
    }
  );
  var MenuRootContentNonModal = React33.forwardRef((props, forwardedRef) => {
    const context = useMenuContext(CONTENT_NAME2, props.__scopeMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
      MenuContentImpl,
      {
        ...props,
        ref: forwardedRef,
        trapFocus: false,
        disableOutsidePointerEvents: false,
        disableOutsideScroll: false,
        onDismiss: /* @__PURE__ */ __name(() => context.onOpenChange(false), "onDismiss")
      }
    );
  });
  var Slot = createSlot("MenuContent.ScrollLock");
  var MenuContentImpl = React33.forwardRef(
    (props, forwardedRef) => {
      const {
        __scopeMenu,
        loop = false,
        trapFocus,
        onOpenAutoFocus,
        onCloseAutoFocus,
        disableOutsidePointerEvents,
        onEntryFocus,
        onEscapeKeyDown,
        onPointerDownOutside,
        onFocusOutside,
        onInteractOutside,
        onDismiss,
        disableOutsideScroll,
        ...contentProps
      } = props;
      const context = useMenuContext(CONTENT_NAME2, __scopeMenu);
      const rootContext = useMenuRootContext(CONTENT_NAME2, __scopeMenu);
      const popperScope = usePopperScope(__scopeMenu);
      const rovingFocusGroupScope = useRovingFocusGroupScope(__scopeMenu);
      const getItems = useCollection2(__scopeMenu);
      const [currentItemId, setCurrentItemId] = React33.useState(null);
      const contentRef = React33.useRef(null);
      const composedRefs = useComposedRefs(forwardedRef, contentRef, context.onContentChange);
      const timerRef = React33.useRef(0);
      const searchRef = React33.useRef("");
      const pointerGraceTimerRef = React33.useRef(0);
      const pointerGraceIntentRef = React33.useRef(null);
      const pointerDirRef = React33.useRef("right");
      const lastPointerXRef = React33.useRef(0);
      const ScrollLockWrapper = disableOutsideScroll ? Combination_default : React33.Fragment;
      const scrollLockWrapperProps = disableOutsideScroll ? { as: Slot, allowPinchZoom: true } : void 0;
      const handleTypeaheadSearch = /* @__PURE__ */ __name((key) => {
        const search = searchRef.current + key;
        const items = getItems().filter((item) => !item.disabled);
        const currentItem = document.activeElement;
        const currentMatch = items.find((item) => item.ref.current === currentItem)?.textValue;
        const values = items.map((item) => item.textValue);
        const nextMatch = getNextMatch(values, search, currentMatch);
        const newItem = items.find((item) => item.textValue === nextMatch)?.ref.current;
        (/* @__PURE__ */ __name((function updateSearch(value) {
          searchRef.current = value;
          window.clearTimeout(timerRef.current);
          if (value !== "") timerRef.current = window.setTimeout(() => updateSearch(""), 1e3);
        }), "updateSearch"))(search);
        if (newItem) {
          setTimeout(() => newItem.focus());
        }
      }, "handleTypeaheadSearch");
      React33.useEffect(() => {
        return () => window.clearTimeout(timerRef.current);
      }, []);
      useFocusGuards();
      const isPointerMovingToSubmenu = React33.useCallback((event) => {
        const isMovingTowards = pointerDirRef.current === pointerGraceIntentRef.current?.side;
        return isMovingTowards && isPointerInGraceArea(event, pointerGraceIntentRef.current?.area);
      }, []);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        MenuContentProvider,
        {
          scope: __scopeMenu,
          searchRef,
          onItemEnter: React33.useCallback(
            (event) => {
              if (isPointerMovingToSubmenu(event)) event.preventDefault();
            },
            [isPointerMovingToSubmenu]
          ),
          onItemLeave: React33.useCallback(
            (event) => {
              if (isPointerMovingToSubmenu(event)) return;
              contentRef.current?.focus();
              setCurrentItemId(null);
            },
            [isPointerMovingToSubmenu]
          ),
          onTriggerLeave: React33.useCallback(
            (event) => {
              if (isPointerMovingToSubmenu(event)) event.preventDefault();
            },
            [isPointerMovingToSubmenu]
          ),
          pointerGraceTimerRef,
          onPointerGraceIntentChange: React33.useCallback((intent) => {
            pointerGraceIntentRef.current = intent;
          }, []),
          children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(ScrollLockWrapper, { ...scrollLockWrapperProps, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
            FocusScope,
            {
              asChild: true,
              trapped: trapFocus,
              onMountAutoFocus: composeEventHandlers(onOpenAutoFocus, (event) => {
                event.preventDefault();
                contentRef.current?.focus({ preventScroll: true });
              }),
              onUnmountAutoFocus: onCloseAutoFocus,
              children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
                DismissableLayer,
                {
                  asChild: true,
                  disableOutsidePointerEvents,
                  onEscapeKeyDown,
                  onPointerDownOutside,
                  onFocusOutside,
                  onInteractOutside,
                  onDismiss,
                  children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
                    Root3,
                    {
                      asChild: true,
                      ...rovingFocusGroupScope,
                      dir: rootContext.dir,
                      orientation: "vertical",
                      loop,
                      currentTabStopId: currentItemId,
                      onCurrentTabStopIdChange: setCurrentItemId,
                      onEntryFocus: composeEventHandlers(onEntryFocus, (event) => {
                        if (!rootContext.isUsingKeyboardRef.current) event.preventDefault();
                      }),
                      preventScrollOnEntryFocus: true,
                      children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
                        Content,
                        {
                          role: "menu",
                          "aria-orientation": "vertical",
                          "data-state": getOpenState(context.open),
                          "data-radix-menu-content": "",
                          dir: rootContext.dir,
                          ...popperScope,
                          ...contentProps,
                          ref: composedRefs,
                          style: { outline: "none", ...contentProps.style },
                          onKeyDown: composeEventHandlers(contentProps.onKeyDown, (event) => {
                            const target = event.target;
                            const isKeyDownInside = target.closest("[data-radix-menu-content]") === event.currentTarget;
                            const isModifierKey = event.ctrlKey || event.altKey || event.metaKey;
                            const isCharacterKey = event.key.length === 1;
                            if (isKeyDownInside) {
                              if (event.key === "Tab") event.preventDefault();
                              if (!isModifierKey && isCharacterKey) handleTypeaheadSearch(event.key);
                            }
                            const content = contentRef.current;
                            if (event.target !== content) return;
                            if (!FIRST_LAST_KEYS.includes(event.key)) return;
                            event.preventDefault();
                            const items = getItems().filter((item) => !item.disabled);
                            const candidateNodes = items.map((item) => item.ref.current);
                            if (LAST_KEYS.includes(event.key)) candidateNodes.reverse();
                            focusFirst3(candidateNodes);
                          }),
                          onBlur: composeEventHandlers(props.onBlur, (event) => {
                            if (!event.currentTarget.contains(event.target)) {
                              window.clearTimeout(timerRef.current);
                              searchRef.current = "";
                            }
                          }),
                          onPointerMove: composeEventHandlers(
                            props.onPointerMove,
                            whenMouse((event) => {
                              const target = event.target;
                              const pointerXHasChanged = lastPointerXRef.current !== event.clientX;
                              if (event.currentTarget.contains(target) && pointerXHasChanged) {
                                const newDir = event.clientX > lastPointerXRef.current ? "right" : "left";
                                pointerDirRef.current = newDir;
                                lastPointerXRef.current = event.clientX;
                              }
                            })
                          )
                        }
                      )
                    }
                  )
                }
              )
            }
          ) })
        }
      );
    }
  );
  MenuContent.displayName = CONTENT_NAME2;
  var GROUP_NAME2 = "MenuGroup";
  var MenuGroup = React33.forwardRef(
    (props, forwardedRef) => {
      const { __scopeMenu, ...groupProps } = props;
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Primitive.div, { role: "group", ...groupProps, ref: forwardedRef });
    }
  );
  MenuGroup.displayName = GROUP_NAME2;
  var LABEL_NAME = "MenuLabel";
  var MenuLabel = React33.forwardRef(
    (props, forwardedRef) => {
      const { __scopeMenu, ...labelProps } = props;
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Primitive.div, { ...labelProps, ref: forwardedRef });
    }
  );
  MenuLabel.displayName = LABEL_NAME;
  var ITEM_NAME2 = "MenuItem";
  var ITEM_SELECT = "menu.itemSelect";
  var MenuItem = React33.forwardRef(
    (props, forwardedRef) => {
      const { disabled = false, onSelect, ...itemProps } = props;
      const ref = React33.useRef(null);
      const rootContext = useMenuRootContext(ITEM_NAME2, props.__scopeMenu);
      const contentContext = useMenuContentContext(ITEM_NAME2, props.__scopeMenu);
      const composedRefs = useComposedRefs(forwardedRef, ref);
      const isPointerDownRef = React33.useRef(false);
      const handleSelect = /* @__PURE__ */ __name(() => {
        const menuItem = ref.current;
        if (!disabled && menuItem) {
          const itemSelectEvent = new CustomEvent(ITEM_SELECT, { bubbles: true, cancelable: true });
          menuItem.addEventListener(ITEM_SELECT, (event) => onSelect?.(event), { once: true });
          dispatchDiscreteCustomEvent(menuItem, itemSelectEvent);
          if (itemSelectEvent.defaultPrevented) {
            isPointerDownRef.current = false;
          } else {
            rootContext.onClose();
          }
        }
      }, "handleSelect");
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        MenuItemImpl,
        {
          ...itemProps,
          ref: composedRefs,
          disabled,
          onClick: composeEventHandlers(props.onClick, handleSelect),
          onPointerDown: /* @__PURE__ */ __name((event) => {
            props.onPointerDown?.(event);
            isPointerDownRef.current = true;
          }, "onPointerDown"),
          onPointerUp: composeEventHandlers(props.onPointerUp, (event) => {
            if (!isPointerDownRef.current) event.currentTarget?.click();
          }),
          onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
            const isTypingAhead = contentContext.searchRef.current !== "";
            if (disabled || isTypingAhead && event.key === " ") return;
            if (SELECTION_KEYS.includes(event.key)) {
              event.currentTarget.click();
              event.preventDefault();
            }
          })
        }
      );
    }
  );
  MenuItem.displayName = ITEM_NAME2;
  var MenuItemImpl = React33.forwardRef(
    (props, forwardedRef) => {
      const { __scopeMenu, disabled = false, textValue, ...itemProps } = props;
      const contentContext = useMenuContentContext(ITEM_NAME2, __scopeMenu);
      const rovingFocusGroupScope = useRovingFocusGroupScope(__scopeMenu);
      const ref = React33.useRef(null);
      const composedRefs = useComposedRefs(forwardedRef, ref);
      const [isFocused, setIsFocused] = React33.useState(false);
      const [textContent, setTextContent] = React33.useState("");
      React33.useEffect(() => {
        const menuItem = ref.current;
        if (menuItem) {
          setTextContent((menuItem.textContent ?? "").trim());
        }
      }, [itemProps.children]);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        Collection2.ItemSlot,
        {
          scope: __scopeMenu,
          disabled,
          textValue: textValue ?? textContent,
          children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Item, { asChild: true, ...rovingFocusGroupScope, focusable: !disabled, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
            Primitive.div,
            {
              role: "menuitem",
              "data-highlighted": isFocused ? "" : void 0,
              "aria-disabled": disabled || void 0,
              "data-disabled": disabled ? "" : void 0,
              ...itemProps,
              ref: composedRefs,
              onPointerMove: composeEventHandlers(
                props.onPointerMove,
                whenMouse((event) => {
                  if (disabled) {
                    contentContext.onItemLeave(event);
                  } else {
                    contentContext.onItemEnter(event);
                    if (!event.defaultPrevented) {
                      const item = event.currentTarget;
                      item.focus({ preventScroll: true });
                    }
                  }
                })
              ),
              onPointerLeave: composeEventHandlers(
                props.onPointerLeave,
                whenMouse((event) => contentContext.onItemLeave(event))
              ),
              onFocus: composeEventHandlers(props.onFocus, () => setIsFocused(true)),
              onBlur: composeEventHandlers(props.onBlur, () => setIsFocused(false))
            }
          ) })
        }
      );
    }
  );
  var CHECKBOX_ITEM_NAME = "MenuCheckboxItem";
  var MenuCheckboxItem = React33.forwardRef(
    (props, forwardedRef) => {
      const { checked = false, onCheckedChange, ...checkboxItemProps } = props;
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(ItemIndicatorProvider, { scope: props.__scopeMenu, checked, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        MenuItem,
        {
          role: "menuitemcheckbox",
          "aria-checked": isIndeterminate(checked) ? "mixed" : checked,
          ...checkboxItemProps,
          ref: forwardedRef,
          "data-state": getCheckedState(checked),
          onSelect: composeEventHandlers(
            checkboxItemProps.onSelect,
            () => onCheckedChange?.(isIndeterminate(checked) ? true : !checked),
            { checkForDefaultPrevented: false }
          )
        }
      ) });
    }
  );
  MenuCheckboxItem.displayName = CHECKBOX_ITEM_NAME;
  var RADIO_GROUP_NAME = "MenuRadioGroup";
  var [RadioGroupProvider, useRadioGroupContext] = createMenuContext(
    RADIO_GROUP_NAME,
    { value: void 0, onValueChange: /* @__PURE__ */ __name(() => {
    }, "onValueChange") }
  );
  var MenuRadioGroup = React33.forwardRef(
    (props, forwardedRef) => {
      const { value, onValueChange, ...groupProps } = props;
      const handleValueChange = useCallbackRef(onValueChange);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(RadioGroupProvider, { scope: props.__scopeMenu, value, onValueChange: handleValueChange, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(MenuGroup, { ...groupProps, ref: forwardedRef }) });
    }
  );
  MenuRadioGroup.displayName = RADIO_GROUP_NAME;
  var RADIO_ITEM_NAME = "MenuRadioItem";
  var MenuRadioItem = React33.forwardRef(
    (props, forwardedRef) => {
      const { value, ...radioItemProps } = props;
      const context = useRadioGroupContext(RADIO_ITEM_NAME, props.__scopeMenu);
      const checked = value === context.value;
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(ItemIndicatorProvider, { scope: props.__scopeMenu, checked, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        MenuItem,
        {
          role: "menuitemradio",
          "aria-checked": checked,
          ...radioItemProps,
          ref: forwardedRef,
          "data-state": getCheckedState(checked),
          onSelect: composeEventHandlers(
            radioItemProps.onSelect,
            () => context.onValueChange?.(value),
            { checkForDefaultPrevented: false }
          )
        }
      ) });
    }
  );
  MenuRadioItem.displayName = RADIO_ITEM_NAME;
  var ITEM_INDICATOR_NAME = "MenuItemIndicator";
  var [ItemIndicatorProvider, useItemIndicatorContext] = createMenuContext(
    ITEM_INDICATOR_NAME,
    { checked: false }
  );
  var MenuItemIndicator = React33.forwardRef(
    (props, forwardedRef) => {
      const { __scopeMenu, forceMount, ...itemIndicatorProps } = props;
      const indicatorContext = useItemIndicatorContext(ITEM_INDICATOR_NAME, __scopeMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        Presence,
        {
          present: forceMount || isIndeterminate(indicatorContext.checked) || indicatorContext.checked === true,
          children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
            Primitive.span,
            {
              ...itemIndicatorProps,
              ref: forwardedRef,
              "data-state": getCheckedState(indicatorContext.checked)
            }
          )
        }
      );
    }
  );
  MenuItemIndicator.displayName = ITEM_INDICATOR_NAME;
  var SEPARATOR_NAME = "MenuSeparator";
  var MenuSeparator = React33.forwardRef(
    (props, forwardedRef) => {
      const { __scopeMenu, ...separatorProps } = props;
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        Primitive.div,
        {
          role: "separator",
          "aria-orientation": "horizontal",
          ...separatorProps,
          ref: forwardedRef
        }
      );
    }
  );
  MenuSeparator.displayName = SEPARATOR_NAME;
  var ARROW_NAME2 = "MenuArrow";
  var MenuArrow = React33.forwardRef(
    (props, forwardedRef) => {
      const { __scopeMenu, ...arrowProps } = props;
      const popperScope = usePopperScope(__scopeMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Arrow2, { ...popperScope, ...arrowProps, ref: forwardedRef });
    }
  );
  MenuArrow.displayName = ARROW_NAME2;
  var SUB_NAME = "MenuSub";
  var [MenuSubProvider, useMenuSubContext] = createMenuContext(SUB_NAME);
  var MenuSub = /* @__PURE__ */ __name((props) => {
    const { __scopeMenu, children, open = false, onOpenChange } = props;
    const parentMenuContext = useMenuContext(SUB_NAME, __scopeMenu);
    const popperScope = usePopperScope(__scopeMenu);
    const [trigger, setTrigger] = React33.useState(null);
    const [content, setContent] = React33.useState(null);
    const handleOpenChange = useCallbackRef(onOpenChange);
    React33.useEffect(() => {
      if (parentMenuContext.open === false) handleOpenChange(false);
      return () => handleOpenChange(false);
    }, [parentMenuContext.open, handleOpenChange]);
    return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Root2, { ...popperScope, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
      MenuProvider,
      {
        scope: __scopeMenu,
        open,
        onOpenChange: handleOpenChange,
        content,
        onContentChange: setContent,
        children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
          MenuSubProvider,
          {
            scope: __scopeMenu,
            contentId: useId(),
            triggerId: useId(),
            trigger,
            onTriggerChange: setTrigger,
            children
          }
        )
      }
    ) });
  }, "MenuSub");
  MenuSub.displayName = SUB_NAME;
  var SUB_TRIGGER_NAME = "MenuSubTrigger";
  var MenuSubTrigger = React33.forwardRef(
    (props, forwardedRef) => {
      const context = useMenuContext(SUB_TRIGGER_NAME, props.__scopeMenu);
      const rootContext = useMenuRootContext(SUB_TRIGGER_NAME, props.__scopeMenu);
      const subContext = useMenuSubContext(SUB_TRIGGER_NAME, props.__scopeMenu);
      const contentContext = useMenuContentContext(SUB_TRIGGER_NAME, props.__scopeMenu);
      const openTimerRef = React33.useRef(null);
      const { pointerGraceTimerRef, onPointerGraceIntentChange } = contentContext;
      const scope = { __scopeMenu: props.__scopeMenu };
      const clearOpenTimer = React33.useCallback(() => {
        if (openTimerRef.current) window.clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }, []);
      React33.useEffect(() => clearOpenTimer, [clearOpenTimer]);
      React33.useEffect(() => {
        const pointerGraceTimer = pointerGraceTimerRef.current;
        return () => {
          window.clearTimeout(pointerGraceTimer);
          onPointerGraceIntentChange(null);
        };
      }, [pointerGraceTimerRef, onPointerGraceIntentChange]);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(MenuAnchor, { asChild: true, ...scope, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        MenuItemImpl,
        {
          id: subContext.triggerId,
          "aria-haspopup": "menu",
          "aria-expanded": context.open,
          "aria-controls": subContext.contentId,
          "data-state": getOpenState(context.open),
          ...props,
          ref: composeRefs(forwardedRef, subContext.onTriggerChange),
          onClick: /* @__PURE__ */ __name((event) => {
            props.onClick?.(event);
            if (props.disabled || event.defaultPrevented) return;
            event.currentTarget.focus();
            if (!context.open) context.onOpenChange(true);
          }, "onClick"),
          onPointerMove: composeEventHandlers(
            props.onPointerMove,
            whenMouse((event) => {
              contentContext.onItemEnter(event);
              if (event.defaultPrevented) return;
              if (!props.disabled && !context.open && !openTimerRef.current) {
                contentContext.onPointerGraceIntentChange(null);
                openTimerRef.current = window.setTimeout(() => {
                  context.onOpenChange(true);
                  clearOpenTimer();
                }, 100);
              }
            })
          ),
          onPointerLeave: composeEventHandlers(
            props.onPointerLeave,
            whenMouse((event) => {
              clearOpenTimer();
              const contentRect = context.content?.getBoundingClientRect();
              if (contentRect) {
                const side = context.content?.dataset.side;
                const rightSide = side === "right";
                const bleed = rightSide ? -5 : 5;
                const contentNearEdge = contentRect[rightSide ? "left" : "right"];
                const contentFarEdge = contentRect[rightSide ? "right" : "left"];
                contentContext.onPointerGraceIntentChange({
                  area: [
                    // Apply a bleed on clientX to ensure that our exit point is
                    // consistently within polygon bounds
                    { x: event.clientX + bleed, y: event.clientY },
                    { x: contentNearEdge, y: contentRect.top },
                    { x: contentFarEdge, y: contentRect.top },
                    { x: contentFarEdge, y: contentRect.bottom },
                    { x: contentNearEdge, y: contentRect.bottom }
                  ],
                  side
                });
                window.clearTimeout(pointerGraceTimerRef.current);
                pointerGraceTimerRef.current = window.setTimeout(
                  () => contentContext.onPointerGraceIntentChange(null),
                  300
                );
              } else {
                contentContext.onTriggerLeave(event);
                if (event.defaultPrevented) return;
                contentContext.onPointerGraceIntentChange(null);
              }
            })
          ),
          onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
            const isTypingAhead = contentContext.searchRef.current !== "";
            if (props.disabled || isTypingAhead && event.key === " ") return;
            if (SUB_OPEN_KEYS[rootContext.dir].includes(event.key)) {
              context.onOpenChange(true);
              context.content?.focus();
              event.preventDefault();
            }
          })
        }
      ) });
    }
  );
  MenuSubTrigger.displayName = SUB_TRIGGER_NAME;
  var SUB_CONTENT_NAME = "MenuSubContent";
  var MenuSubContent = React33.forwardRef(
    (props, forwardedRef) => {
      const portalContext = usePortalContext(CONTENT_NAME2, props.__scopeMenu);
      const { forceMount = portalContext.forceMount, ...subContentProps } = props;
      const context = useMenuContext(CONTENT_NAME2, props.__scopeMenu);
      const rootContext = useMenuRootContext(CONTENT_NAME2, props.__scopeMenu);
      const subContext = useMenuSubContext(SUB_CONTENT_NAME, props.__scopeMenu);
      const ref = React33.useRef(null);
      const composedRefs = useComposedRefs(forwardedRef, ref);
      return /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Collection2.Provider, { scope: props.__scopeMenu, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Presence, { present: forceMount || context.open, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(Collection2.Slot, { scope: props.__scopeMenu, children: /* @__PURE__ */ (0, import_jsx_runtime15.jsx)(
        MenuContentImpl,
        {
          id: subContext.contentId,
          "aria-labelledby": subContext.triggerId,
          ...subContentProps,
          ref: composedRefs,
          align: "start",
          side: rootContext.dir === "rtl" ? "left" : "right",
          disableOutsidePointerEvents: false,
          disableOutsideScroll: false,
          trapFocus: false,
          onOpenAutoFocus: /* @__PURE__ */ __name((event) => {
            if (rootContext.isUsingKeyboardRef.current) ref.current?.focus();
            event.preventDefault();
          }, "onOpenAutoFocus"),
          onCloseAutoFocus: /* @__PURE__ */ __name((event) => event.preventDefault(), "onCloseAutoFocus"),
          onFocusOutside: composeEventHandlers(props.onFocusOutside, (event) => {
            if (event.target !== subContext.trigger) context.onOpenChange(false);
          }),
          onEscapeKeyDown: composeEventHandlers(props.onEscapeKeyDown, (event) => {
            rootContext.onClose();
            event.preventDefault();
          }),
          onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
            const isKeyDownInside = event.currentTarget.contains(event.target);
            const isCloseKey = SUB_CLOSE_KEYS[rootContext.dir].includes(event.key);
            if (isKeyDownInside && isCloseKey) {
              context.onOpenChange(false);
              subContext.trigger?.focus();
              event.preventDefault();
            }
          })
        }
      ) }) }) });
    }
  );
  MenuSubContent.displayName = SUB_CONTENT_NAME;
  function getOpenState(open) {
    return open ? "open" : "closed";
  }
  __name(getOpenState, "getOpenState");
  function isIndeterminate(checked) {
    return checked === "indeterminate";
  }
  __name(isIndeterminate, "isIndeterminate");
  function getCheckedState(checked) {
    return isIndeterminate(checked) ? "indeterminate" : checked ? "checked" : "unchecked";
  }
  __name(getCheckedState, "getCheckedState");
  function focusFirst3(candidates) {
    const PREVIOUSLY_FOCUSED_ELEMENT = document.activeElement;
    for (const candidate of candidates) {
      if (candidate === PREVIOUSLY_FOCUSED_ELEMENT) return;
      candidate.focus();
      if (document.activeElement !== PREVIOUSLY_FOCUSED_ELEMENT) return;
    }
  }
  __name(focusFirst3, "focusFirst");
  function wrapArray2(array, startIndex) {
    return array.map((_, index2) => array[(startIndex + index2) % array.length]);
  }
  __name(wrapArray2, "wrapArray");
  function getNextMatch(values, search, currentMatch) {
    const isRepeated = search.length > 1 && Array.from(search).every((char) => char === search[0]);
    const normalizedSearch = isRepeated ? search[0] : search;
    const currentMatchIndex = currentMatch ? values.indexOf(currentMatch) : -1;
    let wrappedValues = wrapArray2(values, Math.max(currentMatchIndex, 0));
    const excludeCurrentMatch = normalizedSearch.length === 1;
    if (excludeCurrentMatch) wrappedValues = wrappedValues.filter((v) => v !== currentMatch);
    const nextMatch = wrappedValues.find(
      (value) => value.toLowerCase().startsWith(normalizedSearch.toLowerCase())
    );
    return nextMatch !== currentMatch ? nextMatch : void 0;
  }
  __name(getNextMatch, "getNextMatch");
  function isPointInPolygon(point, polygon) {
    const { x, y } = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const ii = polygon[i];
      const jj = polygon[j];
      const xi = ii.x;
      const yi = ii.y;
      const xj = jj.x;
      const yj = jj.y;
      const intersect = yi > y !== yj > y && x < (xj - xi) * (y - yi) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }
  __name(isPointInPolygon, "isPointInPolygon");
  function isPointerInGraceArea(event, area) {
    if (!area) return false;
    const cursorPos = { x: event.clientX, y: event.clientY };
    return isPointInPolygon(cursorPos, area);
  }
  __name(isPointerInGraceArea, "isPointerInGraceArea");
  function whenMouse(handler) {
    return (event) => event.pointerType === "mouse" ? handler(event) : void 0;
  }
  __name(whenMouse, "whenMouse");
  var Root32 = Menu;
  var Anchor2 = MenuAnchor;
  var Portal2 = MenuPortal;
  var Content2 = MenuContent;
  var Group = MenuGroup;
  var Label = MenuLabel;
  var Item2 = MenuItem;
  var CheckboxItem = MenuCheckboxItem;
  var RadioGroup = MenuRadioGroup;
  var RadioItem = MenuRadioItem;
  var ItemIndicator = MenuItemIndicator;
  var Separator = MenuSeparator;
  var Arrow22 = MenuArrow;
  var Sub = MenuSub;
  var SubTrigger = MenuSubTrigger;
  var SubContent = MenuSubContent;

  // node_modules/@radix-ui/react-dropdown-menu/dist/index.mjs
  var dist_exports4 = {};
  __export(dist_exports4, {
    Arrow: () => Arrow23,
    CheckboxItem: () => CheckboxItem2,
    Content: () => Content22,
    DropdownMenu: () => DropdownMenu,
    DropdownMenuArrow: () => DropdownMenuArrow,
    DropdownMenuCheckboxItem: () => DropdownMenuCheckboxItem,
    DropdownMenuContent: () => DropdownMenuContent,
    DropdownMenuGroup: () => DropdownMenuGroup,
    DropdownMenuItem: () => DropdownMenuItem,
    DropdownMenuItemIndicator: () => DropdownMenuItemIndicator,
    DropdownMenuLabel: () => DropdownMenuLabel,
    DropdownMenuPortal: () => DropdownMenuPortal,
    DropdownMenuRadioGroup: () => DropdownMenuRadioGroup,
    DropdownMenuRadioItem: () => DropdownMenuRadioItem,
    DropdownMenuSeparator: () => DropdownMenuSeparator,
    DropdownMenuSub: () => DropdownMenuSub,
    DropdownMenuSubContent: () => DropdownMenuSubContent,
    DropdownMenuSubTrigger: () => DropdownMenuSubTrigger,
    DropdownMenuTrigger: () => DropdownMenuTrigger,
    Group: () => Group2,
    Item: () => Item22,
    ItemIndicator: () => ItemIndicator2,
    Label: () => Label2,
    Portal: () => Portal22,
    RadioGroup: () => RadioGroup2,
    RadioItem: () => RadioItem2,
    Root: () => Root22,
    Separator: () => Separator2,
    Sub: () => Sub2,
    SubContent: () => SubContent2,
    SubTrigger: () => SubTrigger2,
    Trigger: () => Trigger,
    createDropdownMenuScope: () => createDropdownMenuScope
  });
  var React34 = __toESM(require_react(), 1);
  var import_jsx_runtime16 = __toESM(require_jsx_runtime(), 1);
  var DROPDOWN_MENU_NAME = "DropdownMenu";
  var [createDropdownMenuContext, createDropdownMenuScope] = createContextScope(
    DROPDOWN_MENU_NAME,
    [createMenuScope]
  );
  var useMenuScope = createMenuScope();
  var [DropdownMenuProvider, useDropdownMenuContext] = createDropdownMenuContext(DROPDOWN_MENU_NAME);
  var DropdownMenu = /* @__PURE__ */ __name((props) => {
    const {
      __scopeDropdownMenu,
      children,
      dir,
      open: openProp,
      defaultOpen,
      onOpenChange,
      modal = true
    } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    const triggerRef = React34.useRef(null);
    const [open, setOpen] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen ?? false,
      onChange: onOpenChange,
      caller: DROPDOWN_MENU_NAME
    });
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(
      DropdownMenuProvider,
      {
        scope: __scopeDropdownMenu,
        triggerId: useId(),
        triggerRef,
        contentId: useId(),
        open,
        onOpenChange: setOpen,
        onOpenToggle: React34.useCallback(() => setOpen((prevOpen) => !prevOpen), [setOpen]),
        modal,
        children: /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Root32, { ...menuScope, open, onOpenChange: setOpen, dir, modal, children })
      }
    );
  }, "DropdownMenu");
  DropdownMenu.displayName = DROPDOWN_MENU_NAME;
  var TRIGGER_NAME = "DropdownMenuTrigger";
  var DropdownMenuTrigger = React34.forwardRef(
    (props, forwardedRef) => {
      const { __scopeDropdownMenu, disabled = false, ...triggerProps } = props;
      const context = useDropdownMenuContext(TRIGGER_NAME, __scopeDropdownMenu);
      const menuScope = useMenuScope(__scopeDropdownMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Anchor2, { asChild: true, ...menuScope, children: /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(
        Primitive.button,
        {
          type: "button",
          id: context.triggerId,
          "aria-haspopup": "menu",
          "aria-expanded": context.open,
          "aria-controls": context.open ? context.contentId : void 0,
          "data-state": context.open ? "open" : "closed",
          "data-disabled": disabled ? "" : void 0,
          disabled,
          ...triggerProps,
          ref: composeRefs(forwardedRef, context.triggerRef),
          onPointerDown: composeEventHandlers(props.onPointerDown, (event) => {
            if (!disabled && event.button === 0 && event.ctrlKey === false) {
              context.onOpenToggle();
              if (!context.open) event.preventDefault();
            }
          }),
          onKeyDown: composeEventHandlers(props.onKeyDown, (event) => {
            if (disabled) return;
            if (["Enter", " "].includes(event.key)) context.onOpenToggle();
            if (event.key === "ArrowDown") context.onOpenChange(true);
            if (["Enter", " ", "ArrowDown"].includes(event.key)) event.preventDefault();
          })
        }
      ) });
    }
  );
  DropdownMenuTrigger.displayName = TRIGGER_NAME;
  var PORTAL_NAME3 = "DropdownMenuPortal";
  var DropdownMenuPortal = /* @__PURE__ */ __name((props) => {
    const { __scopeDropdownMenu, ...portalProps } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Portal2, { ...menuScope, ...portalProps });
  }, "DropdownMenuPortal");
  DropdownMenuPortal.displayName = PORTAL_NAME3;
  var CONTENT_NAME3 = "DropdownMenuContent";
  var DropdownMenuContent = React34.forwardRef(
    (props, forwardedRef) => {
      const { __scopeDropdownMenu, ...contentProps } = props;
      const context = useDropdownMenuContext(CONTENT_NAME3, __scopeDropdownMenu);
      const menuScope = useMenuScope(__scopeDropdownMenu);
      const hasInteractedOutsideRef = React34.useRef(false);
      return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(
        Content2,
        {
          id: context.contentId,
          "aria-labelledby": context.triggerId,
          ...menuScope,
          ...contentProps,
          ref: forwardedRef,
          onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, (event) => {
            if (!hasInteractedOutsideRef.current) context.triggerRef.current?.focus();
            hasInteractedOutsideRef.current = false;
            event.preventDefault();
          }),
          onInteractOutside: composeEventHandlers(props.onInteractOutside, (event) => {
            const originalEvent = event.detail.originalEvent;
            const ctrlLeftClick = originalEvent.button === 0 && originalEvent.ctrlKey === true;
            const isRightClick = originalEvent.button === 2 || ctrlLeftClick;
            if (!context.modal || isRightClick) hasInteractedOutsideRef.current = true;
          }),
          style: {
            ...props.style,
            // re-namespace exposed content custom properties
            ...{
              "--radix-dropdown-menu-content-transform-origin": "var(--radix-popper-transform-origin)",
              "--radix-dropdown-menu-content-available-width": "var(--radix-popper-available-width)",
              "--radix-dropdown-menu-content-available-height": "var(--radix-popper-available-height)",
              "--radix-dropdown-menu-trigger-width": "var(--radix-popper-anchor-width)",
              "--radix-dropdown-menu-trigger-height": "var(--radix-popper-anchor-height)"
            }
          }
        }
      );
    }
  );
  DropdownMenuContent.displayName = CONTENT_NAME3;
  var GROUP_NAME3 = "DropdownMenuGroup";
  var DropdownMenuGroup = React34.forwardRef(
    (props, forwardedRef) => {
      const { __scopeDropdownMenu, ...groupProps } = props;
      const menuScope = useMenuScope(__scopeDropdownMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Group, { ...menuScope, ...groupProps, ref: forwardedRef });
    }
  );
  DropdownMenuGroup.displayName = GROUP_NAME3;
  var LABEL_NAME2 = "DropdownMenuLabel";
  var DropdownMenuLabel = React34.forwardRef(
    (props, forwardedRef) => {
      const { __scopeDropdownMenu, ...labelProps } = props;
      const menuScope = useMenuScope(__scopeDropdownMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Label, { ...menuScope, ...labelProps, ref: forwardedRef });
    }
  );
  DropdownMenuLabel.displayName = LABEL_NAME2;
  var ITEM_NAME3 = "DropdownMenuItem";
  var DropdownMenuItem = React34.forwardRef(
    (props, forwardedRef) => {
      const { __scopeDropdownMenu, ...itemProps } = props;
      const menuScope = useMenuScope(__scopeDropdownMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Item2, { ...menuScope, ...itemProps, ref: forwardedRef });
    }
  );
  DropdownMenuItem.displayName = ITEM_NAME3;
  var CHECKBOX_ITEM_NAME2 = "DropdownMenuCheckboxItem";
  var DropdownMenuCheckboxItem = React34.forwardRef((props, forwardedRef) => {
    const { __scopeDropdownMenu, ...checkboxItemProps } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(CheckboxItem, { ...menuScope, ...checkboxItemProps, ref: forwardedRef });
  });
  DropdownMenuCheckboxItem.displayName = CHECKBOX_ITEM_NAME2;
  var RADIO_GROUP_NAME2 = "DropdownMenuRadioGroup";
  var DropdownMenuRadioGroup = React34.forwardRef((props, forwardedRef) => {
    const { __scopeDropdownMenu, ...radioGroupProps } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(RadioGroup, { ...menuScope, ...radioGroupProps, ref: forwardedRef });
  });
  DropdownMenuRadioGroup.displayName = RADIO_GROUP_NAME2;
  var RADIO_ITEM_NAME2 = "DropdownMenuRadioItem";
  var DropdownMenuRadioItem = React34.forwardRef((props, forwardedRef) => {
    const { __scopeDropdownMenu, ...radioItemProps } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(RadioItem, { ...menuScope, ...radioItemProps, ref: forwardedRef });
  });
  DropdownMenuRadioItem.displayName = RADIO_ITEM_NAME2;
  var INDICATOR_NAME = "DropdownMenuItemIndicator";
  var DropdownMenuItemIndicator = React34.forwardRef((props, forwardedRef) => {
    const { __scopeDropdownMenu, ...itemIndicatorProps } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(ItemIndicator, { ...menuScope, ...itemIndicatorProps, ref: forwardedRef });
  });
  DropdownMenuItemIndicator.displayName = INDICATOR_NAME;
  var SEPARATOR_NAME2 = "DropdownMenuSeparator";
  var DropdownMenuSeparator = React34.forwardRef((props, forwardedRef) => {
    const { __scopeDropdownMenu, ...separatorProps } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Separator, { ...menuScope, ...separatorProps, ref: forwardedRef });
  });
  DropdownMenuSeparator.displayName = SEPARATOR_NAME2;
  var ARROW_NAME3 = "DropdownMenuArrow";
  var DropdownMenuArrow = React34.forwardRef(
    (props, forwardedRef) => {
      const { __scopeDropdownMenu, ...arrowProps } = props;
      const menuScope = useMenuScope(__scopeDropdownMenu);
      return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Arrow22, { ...menuScope, ...arrowProps, ref: forwardedRef });
    }
  );
  DropdownMenuArrow.displayName = ARROW_NAME3;
  var DropdownMenuSub = /* @__PURE__ */ __name((props) => {
    const { __scopeDropdownMenu, children, open: openProp, onOpenChange, defaultOpen } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    const [open, setOpen] = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen ?? false,
      onChange: onOpenChange,
      caller: "DropdownMenuSub"
    });
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(Sub, { ...menuScope, open, onOpenChange: setOpen, children });
  }, "DropdownMenuSub");
  var SUB_TRIGGER_NAME2 = "DropdownMenuSubTrigger";
  var DropdownMenuSubTrigger = React34.forwardRef((props, forwardedRef) => {
    const { __scopeDropdownMenu, ...subTriggerProps } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(SubTrigger, { ...menuScope, ...subTriggerProps, ref: forwardedRef });
  });
  DropdownMenuSubTrigger.displayName = SUB_TRIGGER_NAME2;
  var SUB_CONTENT_NAME2 = "DropdownMenuSubContent";
  var DropdownMenuSubContent = React34.forwardRef((props, forwardedRef) => {
    const { __scopeDropdownMenu, ...subContentProps } = props;
    const menuScope = useMenuScope(__scopeDropdownMenu);
    return /* @__PURE__ */ (0, import_jsx_runtime16.jsx)(
      SubContent,
      {
        ...menuScope,
        ...subContentProps,
        ref: forwardedRef,
        style: {
          ...props.style,
          // re-namespace exposed content custom properties
          ...{
            "--radix-dropdown-menu-content-transform-origin": "var(--radix-popper-transform-origin)",
            "--radix-dropdown-menu-content-available-width": "var(--radix-popper-available-width)",
            "--radix-dropdown-menu-content-available-height": "var(--radix-popper-available-height)",
            "--radix-dropdown-menu-trigger-width": "var(--radix-popper-anchor-width)",
            "--radix-dropdown-menu-trigger-height": "var(--radix-popper-anchor-height)"
          }
        }
      }
    );
  });
  DropdownMenuSubContent.displayName = SUB_CONTENT_NAME2;
  var Root22 = DropdownMenu;
  var Trigger = DropdownMenuTrigger;
  var Portal22 = DropdownMenuPortal;
  var Content22 = DropdownMenuContent;
  var Group2 = DropdownMenuGroup;
  var Label2 = DropdownMenuLabel;
  var Item22 = DropdownMenuItem;
  var CheckboxItem2 = DropdownMenuCheckboxItem;
  var RadioGroup2 = DropdownMenuRadioGroup;
  var RadioItem2 = DropdownMenuRadioItem;
  var ItemIndicator2 = DropdownMenuItemIndicator;
  var Separator2 = DropdownMenuSeparator;
  var Arrow23 = DropdownMenuArrow;
  var Sub2 = DropdownMenuSub;
  var SubTrigger2 = DropdownMenuSubTrigger;
  var SubContent2 = DropdownMenuSubContent;

  // public/src/react/components/common/Dropdown.jsx
  init_Icon();
  var import_jsx_runtime17 = __toESM(require_jsx_runtime());
  function Dropdown({
    trigger,
    children,
    classNames = {},
    align = "end",
    side = "bottom",
    sideOffset = 4,
    offset: offset4,
    // Alias for sideOffset
    modal = true,
    onOpenChange,
    open,
    isOpen,
    // Alias for open (React Aria style)
    forceOpen
    // Alias for open (test/debug mode)
  }) {
    const actualSideOffset = sideOffset ?? offset4 ?? 4;
    const actualOpen = forceOpen === true ? true : open ?? isOpen;
    const defaultClassNames4 = {
      trigger: "",
      content: "menu-surface hs-dropdown-menu select-none z-[400] min-w-[11rem]"
    };
    const rootProps = {};
    if (actualOpen !== void 0) {
      rootProps.open = actualOpen;
    }
    if (onOpenChange) {
      rootProps.onOpenChange = onOpenChange;
    }
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsxs)(dist_exports4.Root, { modal, ...rootProps, children: [
      /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.Trigger, { asChild: true, className: classNames.trigger || defaultClassNames4.trigger, children: trigger }),
      /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.Portal, { container: document.body, children: /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(
        dist_exports4.Content,
        {
          align: (
            /** @type {any} */
            align
          ),
          side: (
            /** @type {any} */
            side
          ),
          sideOffset: actualSideOffset,
          collisionPadding: 8,
          avoidCollisions: true,
          className: cn(
            defaultClassNames4.content,
            classNames.content,
            // Animations
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          ),
          children
        }
      ) })
    ] });
  }
  __name(Dropdown, "Dropdown");
  Dropdown.Item = /* @__PURE__ */ __name(function DropdownItem({
    children,
    onClick,
    onSelect,
    onAction,
    // React Aria style
    className = "",
    disabled = false,
    isDisabled = false,
    // React Aria style
    ...rest
  }) {
    const isActuallyDisabled = disabled || isDisabled;
    const handleSelect = /* @__PURE__ */ __name((e) => {
      const handler = onSelect || onAction || onClick;
      if (handler) {
        handler(e);
      }
    }, "handleSelect");
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(
      dist_exports4.Item,
      {
        onSelect: handleSelect,
        disabled: isActuallyDisabled,
        className: cn(
          "menu-item cursor-pointer select-none outline-none transition-colors",
          "focus:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent))_14%,transparent)]",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className
        ),
        ...rest,
        children
      }
    );
  }, "DropdownItem");
  Dropdown.Divider = /* @__PURE__ */ __name(function DropdownDivider() {
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(
      dist_exports4.Separator,
      {
        className: "h-px my-1 bg-[var(--border-color)]"
      }
    );
  }, "DropdownDivider");
  Dropdown.Separator = Dropdown.Divider;
  Dropdown.Section = /* @__PURE__ */ __name(function DropdownSection({ children, title, className = "" }) {
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsxs)(dist_exports4.Group, { className, children: [
      title && /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.Label, { className: "text-xs font-semibold text-[var(--text-dim)] px-3 py-2 block", children: title }),
      children
    ] });
  }, "DropdownSection");
  Dropdown.Sub = /* @__PURE__ */ __name(function DropdownSub({ children, trigger }) {
    if (trigger !== void 0) {
      return /* @__PURE__ */ (0, import_jsx_runtime17.jsxs)(dist_exports4.Sub, { children: [
        /* @__PURE__ */ (0, import_jsx_runtime17.jsxs)(
          dist_exports4.SubTrigger,
          {
            className: "menu-item cursor-pointer select-none outline-none transition-colors whitespace-nowrap px-3 py-2 text-sm !grid items-center gap-2 ![grid-template-columns:1.25rem_minmax(0,1fr)_auto] w-full flex-nowrap focus:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent))_14%,transparent)] data-[state=open]:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent))_14%,transparent)]",
            style: { display: "grid", gridTemplateColumns: "1.25rem minmax(0, 1fr) auto" },
            children: [
              trigger,
              /* @__PURE__ */ (0, import_jsx_runtime17.jsx)("span", { className: "flex-shrink-0 justify-self-end text-[var(--text-dim)] opacity-70", children: Icon_default.chevronRight("w-4 h-4") })
            ]
          }
        ),
        /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.Portal, { children: /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(
          dist_exports4.SubContent,
          {
            sideOffset: 2,
            alignOffset: -5,
            className: "menu-surface hs-dropdown-menu select-none z-[400] min-w-[12rem] w-max data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            children
          }
        ) })
      ] });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.Sub, { children });
  }, "DropdownSub");
  Dropdown.SubTrigger = /* @__PURE__ */ __name(function DropdownSubTrigger({ children, className = "" }) {
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsxs)(
      dist_exports4.SubTrigger,
      {
        className: cn(
          "menu-item cursor-pointer select-none outline-none transition-colors whitespace-nowrap",
          "py-2 px-3 text-sm !grid items-center gap-2 w-full ![grid-template-columns:1.25rem_minmax(0,1fr)_auto] flex-nowrap",
          "focus:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent))_14%,transparent)]",
          "data-[state=open]:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent))_14%,transparent)]",
          className
        ),
        style: { display: "grid", gridTemplateColumns: "1.25rem minmax(0, 1fr) auto" },
        children: [
          children,
          /* @__PURE__ */ (0, import_jsx_runtime17.jsx)("span", { className: "flex-shrink-0 justify-self-end text-[var(--text-dim)] opacity-70", children: Icon_default.chevronRight("w-4 h-4") })
        ]
      }
    );
  }, "DropdownSubTrigger");
  Dropdown.SubContent = /* @__PURE__ */ __name(function DropdownSubContent({ children, className = "" }) {
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.Portal, { children: /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(
      dist_exports4.SubContent,
      {
        sideOffset: 2,
        alignOffset: -5,
        className: cn(
          "menu-surface hs-dropdown-menu select-none z-[400] min-w-[12rem] w-max",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        ),
        children
      }
    ) });
  }, "DropdownSubContent");
  Dropdown.RadioGroup = /* @__PURE__ */ __name(function DropdownRadioGroup({ children, value, onValueChange }) {
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.RadioGroup, { value, onValueChange, children });
  }, "DropdownRadioGroup");
  Dropdown.RadioItem = /* @__PURE__ */ __name(function DropdownRadioItem({ children, value, className = "", ...rest }) {
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsxs)(
      dist_exports4.RadioItem,
      {
        value,
        className: cn(
          "menu-item cursor-pointer select-none outline-none transition-colors whitespace-nowrap px-3 py-2 text-sm flex items-center gap-2",
          "focus:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent))_14%,transparent)]",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className
        ),
        ...rest,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime17.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent)]", children: /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.ItemIndicator, { children: Icon_default.success("w-4 h-4") }) }),
          children
        ]
      }
    );
  }, "DropdownRadioItem");
  Dropdown.CheckboxItem = /* @__PURE__ */ __name(function DropdownCheckboxItem({ children, checked, onCheckedChange, className = "", ...rest }) {
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsxs)(
      dist_exports4.CheckboxItem,
      {
        checked,
        onCheckedChange,
        className: cn(
          "menu-item cursor-pointer select-none outline-none transition-colors whitespace-nowrap px-3 py-2 text-sm flex items-center gap-2",
          "focus:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent))_14%,transparent)]",
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
          className
        ),
        ...rest,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime17.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent)]", children: /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.ItemIndicator, { children: Icon_default.success("w-4 h-4") }) }),
          children
        ]
      }
    );
  }, "DropdownCheckboxItem");
  Dropdown.Label = /* @__PURE__ */ __name(function DropdownLabel({ children, className = "" }) {
    return /* @__PURE__ */ (0, import_jsx_runtime17.jsx)(dist_exports4.Label, { className: cn(
      "text-xs font-semibold text-[var(--text-dim)] px-3 py-2 block",
      className
    ), children });
  }, "DropdownLabel");

  // public/src/react/context/ModalContext.jsx
  init_react_global();

  // public/src/react/components/common/DeleteModal.jsx
  init_react_global();

  // public/src/react/components/common/Portal.jsx
  init_react_global();

  // public/src/react/react-dom-global.js
  var ReactDOMClient = __toESM(require_client());
  var ReactDOM4 = __toESM(require_react_dom());
  var Combined = {
    ...ReactDOM4,
    ...ReactDOMClient
  };
  var react_dom_global_default = Combined;

  // public/src/react/components/common/Portal.jsx
  var { useMemo: useMemo7, useEffect: useEffect17 } = react_global_default;
  function Portal3({ selector, children }) {
    const target = useMemo7(() => {
      if (typeof document === "undefined") return null;
      if (!selector || typeof selector !== "string") return null;
      const isBareName = !/[#.[\s:>+~]/.test(selector);
      try {
        let el = document.querySelector(selector);
        if (el) return el;
        if (isBareName) {
          el = document.querySelector(selector);
          if (el) return el;
          el = document.getElementById(selector);
          if (el) return el;
          el = document.querySelector(`[data-portal="${selector}"]`);
          if (el) return el;
          el = document.createElement(selector.toLowerCase());
          document.body.appendChild(el);
          return el;
        }
      } catch (_e) {
      }
      return null;
    }, [selector]);
    useEffect17(() => {
      if (!target) return;
      const ownerAttr2 = "data-ic-portal-owner";
      const ownerVal2 = selector || "Portal";
      const currentOwner2 = target.getAttribute(ownerAttr2);
      if (currentOwner2 && currentOwner2 !== ownerVal2) {
        console.warn(`[Portal] Another owner ('${currentOwner2}') already claimed target for selector "${selector}"; skipping render.`);
        return;
      }
      if (!currentOwner2) {
        target.setAttribute(ownerAttr2, ownerVal2);
      }
      return () => {
        try {
          if (target.getAttribute(ownerAttr2) === ownerVal2) {
            target.removeAttribute(ownerAttr2);
          }
        } catch (_e) {
        }
      };
    }, [target, selector]);
    if (!target) return null;
    const ownerAttr = "data-ic-portal-owner";
    const ownerVal = selector || "Portal";
    const currentOwner = target?.getAttribute?.(ownerAttr);
    if (currentOwner && currentOwner !== ownerVal) return null;
    return react_dom_global_default.createPortal(children, target);
  }
  __name(Portal3, "Portal");

  // public/src/react/components/common/Modal.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime18 = __toESM(require_jsx_runtime());
  var defaultClassNames = {
    overlay: "fixed inset-0 z-[500] flex items-center justify-center px-4 py-6 modal-overlay-enter",
    backdrop: "absolute inset-0 bg-[color-mix(in_srgb,var(--text)_30%,transparent)]",
    panel: "relative flex min-w-[200px] w-full max-w-3xl flex-col bg-secondary border border-soft shadow-sm rounded-xl px-1 pointer-events-auto modal-content-enter",
    header: "select-none flex items-center justify-between px-4 py-3",
    title: "select-none block w-full text-base font-bold text-center normal-case text-color-default",
    body: "px-4 py-3 overflow-y-auto max-h-[70vh]",
    footer: "select-none flex items-center justify-end gap-2 px-4 py-3",
    acceptButton: "select-none inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium align-middle transition-all border rounded-md shadow-sm border-soft text-bg bg-accent hover:brightness-110",
    closeButton: "select-none inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium align-middle transition-all border rounded-md shadow-sm border-soft text-color-default bg-secondary hover-bg-button",
    closeIconButton: "select-none"
  };
  function Modal({
    title,
    accept,
    close = "Close",
    onAccept,
    closeIcon,
    children,
    classNames = {},
    slotProps = {},
    open,
    onOpenChange
  }) {
    const [isVisible, setIsVisible] = react_global_default.useState(open);
    const [isExiting, setIsExiting] = react_global_default.useState(false);
    const classes = mergeClassNames(defaultClassNames, classNames);
    const getSlotProps = createSlotProps(classes, slotProps);
    react_global_default.useEffect(() => {
      if (open) {
        setIsVisible(true);
        setIsExiting(false);
      } else if (isVisible) {
        setIsExiting(true);
        const timer = setTimeout(() => {
          setIsVisible(false);
          setIsExiting(false);
        }, 150);
        return () => clearTimeout(timer);
      }
    }, [open, isVisible]);
    const handleClose = /* @__PURE__ */ __name(() => {
      if (onOpenChange) onOpenChange(false);
    }, "handleClose");
    const handleAccept = /* @__PURE__ */ __name(() => {
      if (onAccept) {
        onAccept();
      }
      handleClose();
    }, "handleAccept");
    react_global_default.useEffect(() => {
      if (!open) return;
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }, [open]);
    react_global_default.useEffect(() => {
      if (!open) return;
      const handleKeyDown = /* @__PURE__ */ __name((e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          handleClose();
        }
      }, "handleKeyDown");
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [open, onOpenChange]);
    if (!isVisible) return null;
    const acceptButton = accept ? /* @__PURE__ */ (0, import_jsx_runtime18.jsx)(
      "button",
      {
        ...getSlotProps("acceptButton", {
          type: "button",
          onClick: handleAccept,
          "data-id": "modal-accept-button"
        }),
        children: accept
      }
    ) : null;
    const closeButton = close && !closeIcon ? /* @__PURE__ */ (0, import_jsx_runtime18.jsx)(
      "button",
      {
        ...getSlotProps("closeButton", {
          type: "button",
          onClick: handleClose,
          "data-id": "modal-close-button"
        }),
        children: close
      }
    ) : null;
    const closeIconButton = closeIcon ? /* @__PURE__ */ (0, import_jsx_runtime18.jsx)(
      "button",
      {
        ...getSlotProps("closeIconButton", {
          type: "button",
          onClick: handleClose,
          "data-id": "modal-close-icon"
        }),
        children: Icon_default.close("w-3 h-3")
      }
    ) : null;
    const hasFooterButtons = closeButton || acceptButton;
    const overlayProps = getSlotProps("overlay");
    if (isExiting) {
      overlayProps.className = overlayProps.className.replace("modal-overlay-enter", "modal-overlay-exit");
    }
    const panelProps = getSlotProps("panel");
    if (isExiting) {
      panelProps.className = panelProps.className.replace("modal-content-enter", "modal-content-exit");
    }
    return /* @__PURE__ */ (0, import_jsx_runtime18.jsxs)("div", { ...overlayProps, children: [
      /* @__PURE__ */ (0, import_jsx_runtime18.jsx)("div", { ...getSlotProps("backdrop"), onClick: handleClose }),
      /* @__PURE__ */ (0, import_jsx_runtime18.jsxs)("div", { ...panelProps, children: [
        /* @__PURE__ */ (0, import_jsx_runtime18.jsxs)("div", { ...getSlotProps("header"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime18.jsx)("h3", { ...getSlotProps("title", { "data-ic-modal-title": "true" }), children: title }),
          closeIconButton
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime18.jsx)("div", { ...getSlotProps("body"), children }),
        hasFooterButtons && /* @__PURE__ */ (0, import_jsx_runtime18.jsxs)("div", { ...getSlotProps("footer"), children: [
          closeButton,
          acceptButton
        ] })
      ] })
    ] });
  }
  __name(Modal, "Modal");
  var Modal_default = react_global_default.memo(Modal);

  // public/src/react/components/common/DeleteModal.jsx
  var import_jsx_runtime19 = __toESM(require_jsx_runtime());
  function DeleteModal({ open, onOpenChange, title, message, onConfirm }) {
    const handleAccept = /* @__PURE__ */ __name(() => {
      onConfirm?.();
      onOpenChange?.(false);
    }, "handleAccept");
    return /* @__PURE__ */ (0, import_jsx_runtime19.jsx)(Portal3, { selector: "DeleteModal", children: /* @__PURE__ */ (0, import_jsx_runtime19.jsx)(
      Modal_default,
      {
        open,
        onOpenChange,
        title: title || "Delete calc?",
        accept: "Delete",
        close: "Cancel",
        onAccept: handleAccept,
        closeIcon: true,
        classNames: {
          panel: "relative flex min-w-[200px] w-full max-w-sm flex-col bg-secondary border border-soft shadow-sm rounded-xl px-1 pointer-events-auto",
          acceptButton: "select-none inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium align-middle transition-all border rounded-md shadow-sm border-transparent text-white bg-[var(--status-error,#dc2626)] hover:bg-[#b91c1c]"
        },
        children: message && /* @__PURE__ */ (0, import_jsx_runtime19.jsx)("p", { className: "text-sm text-color-default", children: message })
      }
    ) });
  }
  __name(DeleteModal, "DeleteModal");
  var DeleteModal_default = DeleteModal;

  // public/src/react/components/common/PasteLinesModal.jsx
  init_react_global();
  var import_jsx_runtime20 = __toESM(require_jsx_runtime());
  function PasteLinesModal({ open, onOpenChange, title, rows = [], onConfirm }) {
    const handleAccept = /* @__PURE__ */ __name(() => {
      onConfirm?.();
      onOpenChange?.(false);
    }, "handleAccept");
    return /* @__PURE__ */ (0, import_jsx_runtime20.jsx)(Portal3, { selector: "PasteLinesModal", children: /* @__PURE__ */ (0, import_jsx_runtime20.jsx)(
      Modal_default,
      {
        open,
        onOpenChange,
        title: title || "Paste rows?",
        accept: "Paste",
        close: "Cancel",
        onAccept: handleAccept,
        closeIcon: true,
        children: rows.length === 0 ? /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("p", { className: "text-sm text-color-default", children: "Nothing to paste." }) : /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("div", { className: "block overflow-auto max-h-[60vh]", children: /* @__PURE__ */ (0, import_jsx_runtime20.jsxs)("table", { className: "w-full text-sm border-collapse", children: [
          /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("thead", { children: /* @__PURE__ */ (0, import_jsx_runtime20.jsxs)("tr", { className: "text-left border-b border-soft", children: [
            /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("th", { className: "px-3 py-2 font-semibold", children: "Row" }),
            /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("th", { className: "px-3 py-2 font-semibold", children: "Current" }),
            /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("th", { className: "px-3 py-2 font-semibold", children: "New" })
          ] }) }),
          /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("tbody", { children: rows.map((row, index2) => /* @__PURE__ */ (0, import_jsx_runtime20.jsxs)("tr", { className: "border-b border-soft last:border-0", children: [
            /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("td", { className: "px-3 py-2 whitespace-nowrap", children: row.rowLabel }),
            /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("td", { className: "px-3 py-2 text-color-muted", children: row.currentValue }),
            /* @__PURE__ */ (0, import_jsx_runtime20.jsx)("td", { className: "px-3 py-2 font-mono", children: row.newValue })
          ] }, `${row.rowLabel}-${index2}`)) })
        ] }) })
      }
    ) });
  }
  __name(PasteLinesModal, "PasteLinesModal");
  var PasteLinesModal_default = PasteLinesModal;

  // public/src/react/components/common/PromptModal.jsx
  init_react_global();
  var import_jsx_runtime21 = __toESM(require_jsx_runtime());
  var { useState: useState16, useEffect: useEffect18, useRef: useRef15 } = react_global_default;
  function PromptModal({
    open,
    onOpenChange,
    title,
    label,
    defaultValue = "",
    placeholder = "",
    confirmLabel = "Confirm",
    onConfirm
  }) {
    const [value, setValue] = useState16(defaultValue);
    const inputRef = useRef15(null);
    useEffect18(() => {
      if (open) {
        setValue(defaultValue);
        const timer = setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        }, 50);
        return () => clearTimeout(timer);
      }
    }, [open, defaultValue]);
    const handleAccept = /* @__PURE__ */ __name(() => {
      onConfirm?.(value);
      onOpenChange?.(false);
    }, "handleAccept");
    const handleKeyDown = /* @__PURE__ */ __name((e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAccept();
      }
    }, "handleKeyDown");
    return /* @__PURE__ */ (0, import_jsx_runtime21.jsx)(Portal3, { selector: "PromptModal", children: /* @__PURE__ */ (0, import_jsx_runtime21.jsx)(
      Modal_default,
      {
        open,
        onOpenChange,
        title: title || "Enter value",
        accept: confirmLabel,
        close: "Cancel",
        onAccept: handleAccept,
        closeIcon: true,
        children: /* @__PURE__ */ (0, import_jsx_runtime21.jsxs)("div", { className: "flex flex-col gap-2 py-2", children: [
          label && /* @__PURE__ */ (0, import_jsx_runtime21.jsx)("label", { className: "text-sm font-medium text-color-default", children: label }),
          /* @__PURE__ */ (0, import_jsx_runtime21.jsx)(
            "input",
            {
              ref: inputRef,
              type: "text",
              className: "w-full px-3 py-2 text-sm border rounded-md bg-secondary border-soft text-color-default focus:border-[var(--accent)] outline-none",
              placeholder,
              value,
              onChange: (e) => setValue(e.target.value),
              onKeyDown: handleKeyDown
            }
          )
        ] })
      }
    ) });
  }
  __name(PromptModal, "PromptModal");
  var PromptModal_default = PromptModal;

  // public/src/react/context/ModalContext.jsx
  var import_jsx_runtime22 = __toESM(require_jsx_runtime());
  var { createContext: createContext5, useContext: useContext5, useMemo: useMemo8, useState: useState17, useCallback: useCallback11 } = react_global_default;
  var fallbackContext = {
    openDeleteModal: /* @__PURE__ */ __name(({ title, message, onConfirm }) => {
      onConfirm?.();
    }, "openDeleteModal"),
    closeDeleteModal: /* @__PURE__ */ __name(() => {
    }, "closeDeleteModal"),
    openPasteLinesModal: /* @__PURE__ */ __name(({ title, rows, onConfirm }) => {
      onConfirm?.();
    }, "openPasteLinesModal"),
    closePasteLinesModal: /* @__PURE__ */ __name(() => {
    }, "closePasteLinesModal"),
    openPromptModal: /* @__PURE__ */ __name(({ title, label, defaultValue, placeholder, confirmLabel, onConfirm }) => {
      onConfirm?.(defaultValue || "");
    }, "openPromptModal"),
    closePromptModal: /* @__PURE__ */ __name(() => {
    }, "closePromptModal")
  };
  var ModalContext = createContext5(fallbackContext);
  function ModalProvider({ children }) {
    const [deleteState, setDeleteState] = useState17({
      open: false,
      title: "",
      message: "",
      onConfirm: null
    });
    const [pasteState, setPasteState] = useState17({
      open: false,
      title: "",
      rows: [],
      onConfirm: null
    });
    const [promptState, setPromptState] = useState17({
      open: false,
      title: "",
      label: "",
      defaultValue: "",
      placeholder: "",
      confirmLabel: "Confirm",
      onConfirm: null
    });
    const openDeleteModal = useCallback11(({ title, message, onConfirm }) => {
      setDeleteState({
        open: true,
        title: title || "Delete calc?",
        message: message || "",
        onConfirm: onConfirm || null
      });
    }, []);
    const closeDeleteModal = useCallback11(() => {
      setDeleteState((prev) => ({
        ...prev,
        open: false
      }));
    }, []);
    const openPasteLinesModal = useCallback11(({ title, rows, onConfirm }) => {
      setPasteState({
        open: true,
        title: title || "Paste rows?",
        rows: Array.isArray(rows) ? rows : [],
        onConfirm: onConfirm || null
      });
    }, []);
    const closePasteLinesModal = useCallback11(() => {
      setPasteState((prev) => ({
        ...prev,
        open: false
      }));
    }, []);
    const openPromptModal = useCallback11(({ title, label, defaultValue, placeholder, confirmLabel, onConfirm }) => {
      setPromptState({
        open: true,
        title: title || "Enter value",
        label: label || "",
        defaultValue: defaultValue || "",
        placeholder: placeholder || "",
        confirmLabel: confirmLabel || "Confirm",
        onConfirm: onConfirm || null
      });
    }, []);
    const closePromptModal = useCallback11(() => {
      setPromptState((prev) => ({
        ...prev,
        open: false
      }));
    }, []);
    const contextValue = useMemo8(() => ({
      openDeleteModal,
      closeDeleteModal,
      openPasteLinesModal,
      closePasteLinesModal,
      openPromptModal,
      closePromptModal
    }), [openDeleteModal, closeDeleteModal, openPasteLinesModal, closePasteLinesModal, openPromptModal, closePromptModal]);
    return /* @__PURE__ */ (0, import_jsx_runtime22.jsxs)(ModalContext.Provider, { value: contextValue, children: [
      children,
      /* @__PURE__ */ (0, import_jsx_runtime22.jsx)(
        DeleteModal_default,
        {
          open: deleteState.open,
          onOpenChange: (open) => {
            if (!open) closeDeleteModal();
          },
          title: deleteState.title,
          message: deleteState.message,
          onConfirm: deleteState.onConfirm
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime22.jsx)(
        PasteLinesModal_default,
        {
          open: pasteState.open,
          onOpenChange: (open) => {
            if (!open) closePasteLinesModal();
          },
          title: pasteState.title,
          rows: pasteState.rows,
          onConfirm: pasteState.onConfirm
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime22.jsx)(
        PromptModal_default,
        {
          open: promptState.open,
          onOpenChange: (open) => {
            if (!open) closePromptModal();
          },
          title: promptState.title,
          label: promptState.label,
          defaultValue: promptState.defaultValue,
          placeholder: promptState.placeholder,
          confirmLabel: promptState.confirmLabel,
          onConfirm: promptState.onConfirm
        }
      )
    ] });
  }
  __name(ModalProvider, "ModalProvider");
  function useModalManager() {
    const context = useContext5(ModalContext);
    return context || fallbackContext;
  }
  __name(useModalManager, "useModalManager");

  // public/src/react/components/calclist/CalcDropdown.jsx
  var import_CalcType = __toESM(require_CalcType());
  var import_jsx_runtime23 = __toESM(require_jsx_runtime());
  var { useCallback: useCallback12 } = react_global_default;
  var isCollab = /* @__PURE__ */ __name((calc) => import_CalcType.default.isCollab(calc.type), "isCollab");
  function CalcDropdown({ calc, folders, onSelect, onRename, onOpenChange, className, style, classNames = {}, slotProps = {} }) {
    const Action = useAction();
    const { openDeleteModal, openPromptModal } = useModalManager();
    const handleOpenChange = useCallback12((open) => {
      if (onOpenChange) onOpenChange(open);
    }, [onOpenChange]);
    const handleNewFolder = useCallback12(() => {
      openPromptModal({
        title: "New Folder",
        label: "Folder name",
        placeholder: "Enter folder name...",
        confirmLabel: "Create",
        onConfirm: /* @__PURE__ */ __name((name) => {
          const trimmedName = name.trim();
          if (trimmedName) {
            Action.createFolder(trimmedName);
            Action.updateFolder(calc?.id, trimmedName);
          }
        }, "onConfirm")
      });
    }, [Action, calc?.id, openPromptModal]);
    const triggerButton = /* @__PURE__ */ (0, import_jsx_runtime23.jsx)(
      "button",
      {
        className: cn("inline-flex items-center justify-center w-6 h-6 rounded hover-bg-button transition-all duration-150", className),
        style,
        draggable: false,
        onDragStart: (e) => e.preventDefault(),
        "aria-label": "Calc actions",
        "data-ic-calc-actions": "menu",
        children: Icon_default.menu("w-3.5 h-3.5")
      }
    );
    const isCollabCalc = isCollab(calc);
    return /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(
      Dropdown,
      {
        trigger: triggerButton,
        align: "end",
        classNames,
        onOpenChange: handleOpenChange,
        children: [
          !isCollabCalc && /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(Dropdown.Sub, { children: [
            /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(Dropdown.SubTrigger, { "data-ic-move-trigger": "menu", children: [
              Icon_default.folder("w-4 h-4 flex-shrink-0"),
              "Move to..."
            ] }),
            /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(Dropdown.SubContent, { children: [
              /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(
                Dropdown.Item,
                {
                  disabled: !calc.folder,
                  "data-ic-move-item": "top-level",
                  onSelect: () => Action.updateFolder(calc?.id, ""),
                  children: [
                    !calc.folder ? Icon_default.success("w-4 h-4 flex-shrink-0") : /* @__PURE__ */ (0, import_jsx_runtime23.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
                    "Top-level"
                  ]
                }
              ),
              folders.length > 0 && /* @__PURE__ */ (0, import_jsx_runtime23.jsx)(Dropdown.Divider, {}),
              folders.map((folder) => /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(
                Dropdown.Item,
                {
                  disabled: calc.folder === folder.name,
                  "data-ic-move-item": `folder:${folder.name}`,
                  onSelect: () => Action.updateFolder(calc?.id, folder.name),
                  children: [
                    calc.folder === folder.name ? Icon_default.success("w-4 h-4 flex-shrink-0") : /* @__PURE__ */ (0, import_jsx_runtime23.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
                    folder.name
                  ]
                },
                folder.name
              ))
            ] })
          ] }),
          !isCollabCalc && /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(
            Dropdown.Item,
            {
              onClick: () => onRename && onRename(calc),
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime23.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
                "Rename"
              ]
            }
          ),
          !isCollabCalc && /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(
            Dropdown.Item,
            {
              onClick: () => Action.cloneAndOpen(calc),
              children: [
                Icon_default.documentDuplicate("w-4 h-4 flex-shrink-0"),
                "Duplicate"
              ]
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(
            Dropdown.Item,
            {
              onClick: () => Action.exportCalc(calc),
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime23.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
                "Export JSON"
              ]
            }
          ),
          isCollabCalc ? /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(
            Dropdown.Item,
            {
              onClick: () => openDeleteModal({
                title: `Stop tracking "${calc.name}"?`,
                message: "This will remove it from your list. You can rejoin using the link.",
                onConfirm: /* @__PURE__ */ __name(() => Action.delete(calc), "onConfirm")
              }),
              children: [
                Icon_default.xCircle("w-4 h-4 flex-shrink-0"),
                "Stop Tracking"
              ]
            }
          ) : /* @__PURE__ */ (0, import_jsx_runtime23.jsxs)(
            Dropdown.Item,
            {
              onClick: () => openDeleteModal({
                title: `Delete ${calc.name}?`,
                message: "",
                onConfirm: /* @__PURE__ */ __name(() => Action.delete(calc), "onConfirm")
              }),
              children: [
                Icon_default.delete("w-4 h-4 flex-shrink-0"),
                "Delete"
              ]
            }
          )
        ]
      }
    );
  }
  __name(CalcDropdown, "CalcDropdown");
  var CalcDropdown_default = CalcDropdown;

  // public/src/react/components/calclist/CalcItemRow.jsx
  init_react_global();
  init_Icon();
  var import_CalcType2 = __toESM(require_CalcType());
  var import_jsx_runtime24 = __toESM(require_jsx_runtime());
  var isCollab2 = /* @__PURE__ */ __name((calc) => import_CalcType2.default.isCollab(calc.type), "isCollab");
  var isShared = /* @__PURE__ */ __name((calc) => {
    return !!calc.source || !!calc.published?.id || import_CalcType2.default.isWiki(calc.type);
  }, "isShared");
  function CalcItemRow({
    calc,
    isActive,
    isEditing,
    editingCalcName,
    onEditingNameChange,
    onDragStart,
    onSelect,
    onRenameComplete,
    onRenameCancel,
    calcItemClassName,
    calcLinkClassName,
    openDropdownCalcId,
    renderDropdown
  }) {
    const ignoreClickRef = react_global_default.useRef(false);
    const date = window.Util.displayDate(calc.updated || calc.created);
    const displayName = calc.name || "New Calc (" + window.Util.displayDate(calc.updated || calc.created) + ")";
    const showTooltip = displayName.length > 20;
    const handleSelect = /* @__PURE__ */ __name((event) => {
      event.preventDefault();
      const detail = { id: calc.id, source: "calc-list" };
      if (window.InstacalcEvents?.dispatch) {
        window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_LIST_CLICK, detail);
      }
      onSelect(calc.id);
    }, "handleSelect");
    return /* @__PURE__ */ (0, import_jsx_runtime24.jsxs)(
      "li",
      {
        draggable: !isEditing,
        onDragStart: (e) => onDragStart(e, calc),
        className: calcItemClassName,
        children: [
          isEditing ? /* @__PURE__ */ (0, import_jsx_runtime24.jsx)(
            "input",
            {
              type: "text",
              className: cn(calcLinkClassName, "border border-soft rounded px-2 bg-theme-bg"),
              value: editingCalcName,
              onChange: (e) => onEditingNameChange(e.target.value),
              onKeyDown: (e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onRenameComplete();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  onRenameCancel();
                }
              },
              onBlur: onRenameComplete,
              autoFocus: true
            }
          ) : /* @__PURE__ */ (0, import_jsx_runtime24.jsx)(
            "a",
            {
              className: cn(calcLinkClassName, showTooltip && "tooltip tooltip-top !overflow-visible"),
              href: "#",
              "data-ic-calc-link": calc.id,
              "data-ic-calc-name": displayName,
              onPointerUp: (e) => {
                e.preventDefault();
                ignoreClickRef.current = true;
                handleSelect(e);
                setTimeout(() => {
                  ignoreClickRef.current = false;
                }, 0);
              },
              onClick: (e) => {
                e.preventDefault();
                if (ignoreClickRef.current) {
                  return;
                }
                handleSelect(e);
              },
              "data-tip": showTooltip ? displayName : void 0,
              children: /* @__PURE__ */ (0, import_jsx_runtime24.jsxs)("span", { className: "truncate", children: [
                displayName,
                calc.deleted ? " (deleted)" : ""
              ] })
            }
          ),
          !isActive && /* @__PURE__ */ (0, import_jsx_runtime24.jsx)("date", { className: "hidden", children: date }),
          isCollab2(calc) && /* @__PURE__ */ (0, import_jsx_runtime24.jsx)("span", { className: "absolute right-8 top-1/2 -translate-y-1/2 text-theme-text-muted", title: "Private share", children: Icon_default.lockOpen("w-3.5 h-3.5") }),
          /* @__PURE__ */ (0, import_jsx_runtime24.jsxs)(
            "div",
            {
              className: "absolute items-center justify-end gap-1 -translate-y-1/2 right-2 top-1/2 " + (isActive || openDropdownCalcId === calc.id ? "flex" : "hidden group-hover/calc:flex"),
              children: [
                isShared(calc) && /* @__PURE__ */ (0, import_jsx_runtime24.jsx)("span", { className: "text-theme-text-muted", title: "Shared", children: Icon_default.publicLink("w-3.5 h-3.5") }),
                renderDropdown(calc)
              ]
            }
          )
        ]
      },
      calc.id
    );
  }
  __name(CalcItemRow, "CalcItemRow");
  var CalcItemRow_default = CalcItemRow;

  // public/src/react/components/calclist/FolderSection.jsx
  init_react_global();
  init_Icon();

  // public/src/react/components/calclist/FolderDropdown.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime25 = __toESM(require_jsx_runtime());
  function FolderDropdown({ onRename, onDelete, isEmptyFolder, onCreate, folder, className, style, classNames = {}, slotProps = {} }) {
    const safeName = (folder.name || "").replace(/[^a-zA-Z0-9]/g, "-");
    const triggerButton = /* @__PURE__ */ (0, import_jsx_runtime25.jsx)(
      "button",
      {
        className: cn("inline-flex items-center justify-center w-6 h-6 rounded hover-bg-button transition-all duration-150", className),
        style,
        onClick: (e) => e.stopPropagation(),
        "aria-label": "Folder actions",
        children: Icon_default.menu("w-3.5 h-3.5")
      }
    );
    return /* @__PURE__ */ (0, import_jsx_runtime25.jsxs)(
      Dropdown,
      {
        trigger: triggerButton,
        align: "end",
        classNames,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime25.jsxs)(Dropdown.Item, { onClick: () => onCreate(), children: [
            /* @__PURE__ */ (0, import_jsx_runtime25.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
            "New Calc Here"
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime25.jsxs)(Dropdown.Item, { onClick: () => onRename(), children: [
            /* @__PURE__ */ (0, import_jsx_runtime25.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
            "Rename"
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime25.jsxs)(
            Dropdown.Item,
            {
              onClick: isEmptyFolder ? () => onDelete() : void 0,
              disabled: !isEmptyFolder,
              children: [
                Icon_default.delete("w-4 h-4 flex-shrink-0"),
                "Delete ",
                !isEmptyFolder && "(if empty)"
              ]
            }
          )
        ]
      }
    );
  }
  __name(FolderDropdown, "FolderDropdown");
  var FolderDropdown_default = FolderDropdown;

  // public/src/react/components/calclist/FolderSection.jsx
  var import_jsx_runtime26 = __toESM(require_jsx_runtime());
  function FolderSection(props) {
    const {
      folder,
      calcsInFolder,
      selectedCalcId,
      isOpen,
      isDraggedOver,
      editingCalcId,
      editingCalcName,
      editingFolder,
      editingFolderNewName,
      openDropdownCalcId,
      onToggleFolder,
      onDragOver,
      onDrop,
      onFolderEditComplete,
      onStopEditingFolder,
      onEditingFolderNameChange,
      onStartEditingFolder,
      onDeleteFolder,
      onCreateCalcInFolder,
      onCalcDragStart,
      onCalcSelect,
      onCalcRenameComplete,
      onCalcRenameCancel,
      onEditingCalcNameChange,
      calcItemClassName,
      calcLinkClassName,
      renderCalcDropdown,
      folderDropdownProps = {},
      classNames = {},
      getSlotProps = /* @__PURE__ */ __name((name, props2 = {}) => props2, "getSlotProps")
    } = (
      /** @type {FolderSectionProps} */
      props
    );
    const isEmptyFolder = calcsInFolder.length === 0;
    const editingThisFolder = editingFolder === folder.name;
    const defaultClassNames4 = {
      folderSection: "my-0.5 mb-3 folder",
      folderSummary: "relative flex items-center gap-1.5 list-none cursor-pointer select-none group/folder w-full text-left rounded-md transition-all duration-150 ease-out font-medium hover-bg-button py-1 px-2 pr-10 text-sm text-color-default",
      folderActions: "absolute flex items-center justify-end gap-1 -translate-y-1/2 right-1 top-1/2 opacity-0 group-hover/folder:opacity-100 transition-opacity",
      folderMain: "select-none",
      folderList: "pl-3 ml-2 calcs-list",
      folderListBordered: "",
      folderListEmpty: "rounded-lg border-dashed-tight p-2",
      folderEmptyState: "flex p-3 -ml-2 text-sm text-center text-[var(--text-dim)] italic"
    };
    const classes = {
      folderSection: classNames.folderSection || defaultClassNames4.folderSection,
      folderSummary: classNames.folderSummary || defaultClassNames4.folderSummary,
      folderActions: classNames.folderActions || defaultClassNames4.folderActions,
      folderMain: classNames.folderMain || defaultClassNames4.folderMain,
      folderList: classNames.folderList || defaultClassNames4.folderList,
      folderListBordered: classNames.folderListBordered || defaultClassNames4.folderListBordered,
      folderListEmpty: classNames.folderListEmpty || defaultClassNames4.folderListEmpty,
      folderEmptyState: classNames.folderEmptyState || defaultClassNames4.folderEmptyState
    };
    const folderDragStyles = isDraggedOver ? "border-dashed-tight bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] rounded-md p-2" : "";
    const folderDragStyle = isDraggedOver ? { "--dash-color": "var(--accent)" } : void 0;
    const folderSectionClasses = cn(classes.folderSection, folderDragStyles);
    const folderListClasses = cn(
      classes.folderList,
      isEmptyFolder ? classes.folderListEmpty : classes.folderListBordered
    );
    const folderEditInterface = /* @__PURE__ */ (0, import_jsx_runtime26.jsx)("form", { onSubmit: (e) => {
      e.preventDefault();
      onFolderEditComplete();
    }, children: /* @__PURE__ */ (0, import_jsx_runtime26.jsx)(
      "input",
      {
        className: "w-full p-1 border rounded-lg outline-none border-soft",
        value: editingFolderNewName || "",
        onBlur: onStopEditingFolder,
        onChange: (e) => onEditingFolderNameChange(e.target.value),
        maxLength: 50,
        placeholder: "Enter a title",
        autoFocus: true,
        type: "text",
        "aria-label": "Folder title",
        "data-hj-whitelist": true
      }
    ) });
    return /* @__PURE__ */ (0, import_jsx_runtime26.jsxs)(
      "details",
      {
        ...getSlotProps("folderSection", {
          className: folderSectionClasses,
          style: folderDragStyle,
          open: isOpen,
          onDragOver: /* @__PURE__ */ __name((e) => onDragOver(e, folder.name), "onDragOver"),
          onDrop: /* @__PURE__ */ __name((e) => onDrop(e, folder.name), "onDrop"),
          "data-folder-name": folder.name
        }),
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime26.jsxs)(
            "summary",
            {
              ...getSlotProps("folderSummary", {
                className: classes.folderSummary,
                onClick: /* @__PURE__ */ __name((e) => {
                  e.preventDefault();
                  if (!editingThisFolder) {
                    onToggleFolder(folder.name);
                  }
                }, "onClick")
              }),
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime26.jsx)("div", { className: (isOpen ? "" : "-rotate-90") + " w-3 h-3 transition-transform flex-shrink-0 origin-center flex items-center justify-center", children: Icon_default.chevronDown("w-3 h-3") }),
                editingThisFolder ? folderEditInterface : /* @__PURE__ */ (0, import_jsx_runtime26.jsx)("span", { className: "truncate", children: folder.name }),
                /* @__PURE__ */ (0, import_jsx_runtime26.jsx)("div", { ...getSlotProps("folderActions", { className: classes.folderActions }), children: /* @__PURE__ */ (0, import_jsx_runtime26.jsx)(
                  FolderDropdown_default,
                  {
                    onClick: (e) => e.preventDefault(),
                    folder,
                    isEmptyFolder,
                    onDelete: () => onDeleteFolder(folder),
                    onCreate: () => onCreateCalcInFolder(folder.name),
                    onRename: () => onStartEditingFolder(folder.name),
                    ...folderDropdownProps
                  }
                ) })
              ]
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime26.jsx)("main", { ...getSlotProps("folderMain", { className: classes.folderMain }), children: /* @__PURE__ */ (0, import_jsx_runtime26.jsxs)("ul", { className: folderListClasses, children: [
            isEmptyFolder ? /* @__PURE__ */ (0, import_jsx_runtime26.jsx)("li", { className: classes.folderEmptyState, children: "Drag calc here" }) : null,
            calcsInFolder.map((calc) => {
              const isActive = selectedCalcId == calc.id;
              const isEditing = editingCalcId === calc.id;
              return /* @__PURE__ */ (0, import_jsx_runtime26.jsx)(
                CalcItemRow_default,
                {
                  calc,
                  isActive,
                  isEditing,
                  editingCalcName,
                  onEditingNameChange: onEditingCalcNameChange,
                  onDragStart: onCalcDragStart,
                  onSelect: onCalcSelect,
                  onRenameComplete: onCalcRenameComplete,
                  onRenameCancel: onCalcRenameCancel,
                  calcItemClassName: calcItemClassName(isActive),
                  calcLinkClassName: calcLinkClassName(isActive),
                  openDropdownCalcId,
                  renderDropdown: renderCalcDropdown
                },
                calc.id
              );
            })
          ] }) })
        ]
      },
      folder.name
    );
  }
  __name(FolderSection, "FolderSection");
  var FolderSection_default = FolderSection;

  // public/src/react/components/calclist/AddFolderForm.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime27 = __toESM(require_jsx_runtime());
  var { useRef: useRef16, useEffect: useEffect19 } = react_global_default;
  function AddFolderForm(props) {
    const {
      folderName,
      onFolderNameChange,
      onSubmit,
      onCancel,
      classNames = {}
    } = (
      /** @type {AddFolderFormProps} */
      props
    );
    const inputRef = useRef16(null);
    useEffect19(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, []);
    const defaultClassNames4 = {
      form: "flex items-center mt-4 ml-1 text-sm",
      input: "flex-grow p-1 mr-2 border rounded outline-none border-soft",
      cancelButton: "inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-sm text-color-muted transition-all rounded-md hover:text-color-default"
    };
    const classes = {
      form: classNames.addFolderForm || defaultClassNames4.form,
      input: classNames.addFolderInput || defaultClassNames4.input,
      cancelButton: classNames.addFolderSecondaryButton || defaultClassNames4.cancelButton
    };
    return /* @__PURE__ */ (0, import_jsx_runtime27.jsxs)(
      "form",
      {
        className: classes.form,
        onSubmit,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime27.jsx)(
            "input",
            {
              ref: inputRef,
              type: "text",
              className: classes.input,
              value: folderName,
              onChange: onFolderNameChange,
              placeholder: "Folder name...",
              onKeyDown: (e) => {
                if (e.key === "Escape") onCancel();
                if (e.key === "Enter") {
                }
              }
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime27.jsx)("button", { type: "submit", style: { display: "none" }, "aria-hidden": "true" }),
          /* @__PURE__ */ (0, import_jsx_runtime27.jsx)(
            "button",
            {
              type: "button",
              className: classes.cancelButton,
              onClick: onCancel,
              "aria-label": "Cancel adding folder",
              children: Icon_default.close("w-3.5 h-3.5")
            }
          )
        ]
      }
    );
  }
  __name(AddFolderForm, "AddFolderForm");
  var AddFolderForm_default = AddFolderForm;

  // public/src/react/components/calclist/SharedCalcUI.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime28 = __toESM(require_jsx_runtime());
  var { useState: useState18 } = react_global_default;

  // public/src/react/components/calclist/CalcListHeader.jsx
  init_react_global();

  // public/src/react/components/calclist/CalcListDropdown.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime29 = __toESM(require_jsx_runtime());
  function CalcListDropdown({ sortby, setSortBy, toggleAddFolderInput, className, style, classNames = {}, slotProps = {} }) {
    const Action = useAction();
    const triggerButton = /* @__PURE__ */ (0, import_jsx_runtime29.jsx)(
      "button",
      {
        className: cn("inline-flex items-center justify-center w-6 h-6 rounded hover-bg-button transition-all duration-150 text-theme-text", className),
        style,
        onClick: (e) => e.stopPropagation(),
        "aria-label": "Calc list actions",
        children: Icon_default.menu("w-3.5 h-3.5")
      }
    );
    return /* @__PURE__ */ (0, import_jsx_runtime29.jsxs)(
      Dropdown,
      {
        trigger: triggerButton,
        align: "end",
        classNames,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime29.jsxs)(Dropdown.Item, { onAction: () => setSortBy("name"), children: [
            sortby === "name" ? Icon_default.success("w-4 h-4 flex-shrink-0") : /* @__PURE__ */ (0, import_jsx_runtime29.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
            "Sort by name"
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime29.jsxs)(Dropdown.Item, { onAction: () => setSortBy("-updated"), children: [
            sortby === "-updated" ? Icon_default.success("w-4 h-4 flex-shrink-0") : /* @__PURE__ */ (0, import_jsx_runtime29.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
            "Sort by date"
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime29.jsx)(Dropdown.Divider, {}),
          /* @__PURE__ */ (0, import_jsx_runtime29.jsxs)(Dropdown.Item, { onAction: () => toggleAddFolderInput(), children: [
            Icon_default.folderPlus("w-4 h-4 flex-shrink-0"),
            "New folder"
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime29.jsx)(Dropdown.Divider, {}),
          /* @__PURE__ */ (0, import_jsx_runtime29.jsxs)(Dropdown.Item, { onAction: () => Action.exportAllCalcs(), children: [
            /* @__PURE__ */ (0, import_jsx_runtime29.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
            "Export calcs"
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime29.jsxs)(Dropdown.Item, { onAction: () => Action.openFileImportUX(), children: [
            /* @__PURE__ */ (0, import_jsx_runtime29.jsx)("span", { className: "w-4 h-4 flex-shrink-0" }),
            "Import calcs"
          ] })
        ]
      }
    );
  }
  __name(CalcListDropdown, "CalcListDropdown");
  var CalcListDropdown_default = CalcListDropdown;

  // public/src/react/components/meta/UserInfo.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime30 = __toESM(require_jsx_runtime());
  var { useState: useState19, useEffect: useEffect20 } = react_global_default;
  function UserInfo(props) {
    const { loginText = "Login", loginClassName = "", loginStyle = {}, iconSize = "size-6" } = (
      /** @type {UserInfoProps} */
      props
    );
    const Action = useAction();
    const [, forceUpdate] = useState19(0);
    useEffect20(() => {
      if (!window.InstacalcEvents) return;
      const cleanup = window.InstacalcEvents.on(window.InstacalcEvents.USER_CHANGED, () => {
        forceUpdate((n) => n + 1);
      });
      return cleanup;
    }, []);
    const user = Action && typeof Action.getUser === "function" ? Action.getUser() : Action && typeof Action.getCurrentUser === "function" ? Action.getCurrentUser() : Action && Action.user || null;
    const isRealUser = user && !user.isAnonymous;
    if (!isRealUser) {
      const defaultClassName = "inline-flex items-center gap-1.5 text-sm transition-colors text-theme-text hover:text-theme-accent";
      const finalClassName = loginClassName || defaultClassName;
      return /* @__PURE__ */ (0, import_jsx_runtime30.jsx)("div", { className: "flex items-center gap-2", style: { overflow: "visible" }, children: /* @__PURE__ */ (0, import_jsx_runtime30.jsxs)("a", { href: "#", onClick: (e) => {
        e.preventDefault();
        Action.login();
      }, className: `${finalClassName} has-tooltip`, style: loginStyle, "data-id": "login-link", "data-tooltip": "Free account for backup/sync", children: [
        /* @__PURE__ */ (0, import_jsx_runtime30.jsx)("span", { children: "Login" }),
        Icon_default.google("w-3 h-3"),
        Icon_default.apple("w-4 h-4")
      ] }) });
    }
    const getAvatarContent = /* @__PURE__ */ __name(() => {
      if (user.photoURL) {
        return /* @__PURE__ */ (0, import_jsx_runtime30.jsx)("img", { src: user.photoURL, alt: "User avatar", className: "w-8 h-8 rounded-full cursor-pointer", "data-id": "user-avatar" });
      }
      const initials = (user.displayName || user.email || "?").charAt(0).toUpperCase();
      return /* @__PURE__ */ (0, import_jsx_runtime30.jsx)(
        "div",
        {
          className: "w-8 h-8 rounded-full cursor-pointer flex items-center justify-center text-sm font-medium text-bg bg-accent",
          "data-id": "user-avatar",
          title: user.displayName || user.email,
          children: initials
        }
      );
    }, "getAvatarContent");
    const menuItemClass = "flex items-center gap-2 w-full px-3 py-2 text-sm text-left cursor-pointer transition-colors hover:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent,#3b82f6))_14%,transparent)]";
    return /* @__PURE__ */ (0, import_jsx_runtime30.jsxs)(
      Dropdown,
      {
        trigger: getAvatarContent(),
        align: "end",
        raw: true,
        classNames: {
          content: "min-w-[15rem]"
        },
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime30.jsxs)("div", { className: "px-3 py-2 border-b border-[var(--border-color)]", children: [
            /* @__PURE__ */ (0, import_jsx_runtime30.jsx)("div", { className: "text-sm text-theme-text font-medium", children: user.displayName }),
            /* @__PURE__ */ (0, import_jsx_runtime30.jsx)("div", { className: "text-xs text-theme-text-muted", children: user.email })
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime30.jsxs)("div", { className: "py-1", children: [
            /* @__PURE__ */ (0, import_jsx_runtime30.jsxs)(
              "button",
              {
                className: menuItemClass,
                onClick: () => window.location.href = "/account.html",
                "data-id": "account-settings-button",
                children: [
                  Icon_default.adjustments("w-4 h-4"),
                  "Account Settings"
                ]
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime30.jsxs)(
              "button",
              {
                className: menuItemClass,
                onClick: () => Action.logout(),
                "data-id": "logout-button",
                children: [
                  Icon_default.logout("w-4 h-4"),
                  "Logout"
                ]
              }
            )
          ] })
        ]
      }
    );
  }
  __name(UserInfo, "UserInfo");
  var UserInfo_default = react_global_default.memo(UserInfo);

  // public/src/react/components/calclist/CalcListHeader.jsx
  init_Icon();
  var import_jsx_runtime31 = __toESM(require_jsx_runtime());
  function CalcListHeader({
    sortby,
    onSortChange,
    onToggleAddFolder,
    getSlotProps,
    dropdownProps,
    searchQuery,
    showSearch,
    showSearchToggle,
    onSearchToggle,
    onSearchChange,
    onSearchClear,
    searchInputRef,
    headerExtra,
    showLogin = false,
    loginProps = {},
    showNewButton = true,
    title = null,
    leftAction = null
    // NEW: React element for left slot (e.g., accordion toggle)
  }) {
    const Action = useAction();
    const NEW_CALC_BUTTON = /* @__PURE__ */ (0, import_jsx_runtime31.jsx)(
      "button",
      {
        ...getSlotProps("newCalcButton", {
          type: "button",
          onClick: /* @__PURE__ */ __name(() => Action.createAndOpen(), "onClick"),
          "data-tip": "New calc",
          "aria-label": "New calc"
        }),
        children: Icon_default.plus("w-4 h-4")
      }
    );
    const CALCLIST_DROPDOWN = /* @__PURE__ */ (0, import_jsx_runtime31.jsx)(
      CalcListDropdown_default,
      {
        sortby,
        toggleAddFolderInput: onToggleAddFolder,
        setSortBy: onSortChange,
        ...dropdownProps
      }
    );
    return /* @__PURE__ */ (0, import_jsx_runtime31.jsxs)(import_jsx_runtime31.Fragment, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime31.jsxs)("div", { ...getSlotProps("header"), children: [
        leftAction && /* @__PURE__ */ (0, import_jsx_runtime31.jsx)("div", { ...getSlotProps("headerLeftAction"), children: leftAction }),
        /* @__PURE__ */ (0, import_jsx_runtime31.jsxs)("div", { ...getSlotProps("headerTitle"), children: [
          showLogin && /* @__PURE__ */ (0, import_jsx_runtime31.jsx)(UserInfo_default, { ...loginProps }),
          !leftAction && title && /* @__PURE__ */ (0, import_jsx_runtime31.jsx)("span", { className: "text-xs font-medium text-theme-text-muted uppercase tracking-wider", children: title })
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime31.jsxs)("div", { ...getSlotProps("headerActions"), children: [
          showSearchToggle && /* @__PURE__ */ (0, import_jsx_runtime31.jsx)(
            "button",
            {
              ...getSlotProps("searchToggleButton", {
                type: "button",
                onClick: onSearchToggle,
                "aria-label": showSearch ? "Hide search" : "Search calcs",
                "data-ic-calclist-search-toggle": "true"
              }),
              children: Icon_default.search("w-4 h-4")
            }
          ),
          showNewButton && NEW_CALC_BUTTON,
          CALCLIST_DROPDOWN,
          headerExtra
        ] })
      ] }),
      showSearchToggle && /* @__PURE__ */ (0, import_jsx_runtime31.jsx)(
        "div",
        {
          ...getSlotProps("searchInputWrapper", {
            style: {
              maxHeight: showSearch ? "64px" : "0px",
              opacity: showSearch ? 1 : 0,
              pointerEvents: showSearch ? "auto" : "none"
            },
            "aria-hidden": !showSearch
          }),
          children: /* @__PURE__ */ (0, import_jsx_runtime31.jsxs)("div", { ...getSlotProps("searchInputContainer"), children: [
            Icon_default.search("w-4 h-4 text-color-muted"),
            /* @__PURE__ */ (0, import_jsx_runtime31.jsx)(
              "input",
              {
                ...getSlotProps("searchInput", {
                  type: "search",
                  ref: searchInputRef,
                  value: searchQuery,
                  placeholder: "Search your calcs...",
                  onChange: /* @__PURE__ */ __name((e) => onSearchChange(e.target.value), "onChange"),
                  onKeyDown: /* @__PURE__ */ __name((e) => {
                    if (e.key === "Escape") {
                      onSearchToggle();
                    }
                  }, "onKeyDown"),
                  "data-ic-calclist-search-input": "true"
                })
              }
            )
          ] })
        }
      )
    ] });
  }
  __name(CalcListHeader, "CalcListHeader");
  var CalcListHeader_default = CalcListHeader;

  // public/src/react/components/calclist/hooks/useCalcListState.js
  init_react_global();

  // public/src/react/components/calclist/utils/sortingUtils.js
  function naturalCompare(a, b) {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    const aStr = a == null ? "" : String(a);
    const bStr = b == null ? "" : String(b);
    const ax = [], bx = [];
    aStr.replace(/(\d+)|(\D+)/g, function(_, $1, $2) {
      ax.push([$1 || Infinity, $2 || ""]);
      return "";
    });
    bStr.replace(/(\d+)|(\D+)/g, function(_, $1, $2) {
      bx.push([$1 || Infinity, $2 || ""]);
      return "";
    });
    while (ax.length && bx.length) {
      const an = ax.shift();
      const bn = bx.shift();
      const nn = an[0] - bn[0] || an[1].localeCompare(bn[1]);
      if (nn) return nn;
    }
    return ax.length - bx.length;
  }
  __name(naturalCompare, "naturalCompare");
  function dynamicSort(property) {
    let sortOrder = 1;
    if (property[0] === "-") {
      sortOrder = -1;
      property = property.substr(1);
    }
    return function(a, b) {
      if (a == null && b == null) return 0;
      if (a == null) return -1;
      if (b == null) return 1;
      const aVal = a[property];
      const bVal = b[property];
      if (isNaN(aVal) || isNaN(bVal)) {
        return sortOrder * naturalCompare(aVal, bVal);
      } else {
        return sortOrder * (aVal - bVal);
      }
    };
  }
  __name(dynamicSort, "dynamicSort");
  function sortCalcs(inputCalcs, sortby) {
    const list = Array.isArray(inputCalcs) ? inputCalcs.slice() : [];
    const validList = list.filter((item) => item != null);
    return validList.sort(dynamicSort(sortby));
  }
  __name(sortCalcs, "sortCalcs");

  // public/src/react/components/calclist/hooks/useCalcListState.js
  var { useState: useState20, useCallback: useCallback13, useMemo: useMemo9 } = react_global_default;
  function useCalcListState(Action, calcs) {
    const [sortby, setSortBy] = useState20("-updated");
    const [newFolderName, setNewFolderName] = useState20("");
    const [showAddFolderInput, setShowAddFolderInput] = useState20(false);
    const getInitialFolders = useCallback13(() => {
      const storedFolders = Action.getLocalOption("folders") || [];
      const foldersMap = /* @__PURE__ */ new Map();
      storedFolders.forEach((folder) => {
        foldersMap.set(folder.name, folder.open);
      });
      (calcs || []).forEach((calc) => {
        if (calc.folder && !foldersMap.has(calc.folder)) {
          foldersMap.set(calc.folder, false);
        }
      });
      const foldersArray = Array.from(foldersMap, ([name, open]) => ({ name, open }));
      return foldersArray.sort((a, b) => a.name.localeCompare(b.name));
    }, [Action, calcs]);
    const [folders, setFolders] = useState20(() => getInitialFolders());
    const [searchQuery, setSearchQuery] = useState20("");
    const [showSearch, setShowSearch] = useState20(false);
    const [draggedItem, setDraggedItem] = useState20(null);
    const [draggedIntoFolder, setDraggedIntoFolder] = useState20(null);
    const [editingFolder, setEditingFolder] = useState20(null);
    const [editingFolderNewName, setEditingFolderNewName] = useState20(null);
    const [editingCalcId, setEditingCalcId] = useState20(null);
    const [editingCalcName, setEditingCalcName] = useState20("");
    const [openDropdownCalcId, setOpenDropdownCalcId] = useState20(null);
    const [isExpanded, setIsExpanded] = useState20(false);
    const sortedCalcs = useMemo9(() => sortCalcs(calcs || [], sortby), [calcs, sortby]);
    const filteredCalcs = useMemo9(() => {
      if (!showSearch || !searchQuery.trim()) {
        return sortedCalcs;
      }
      const query = searchQuery.trim().toLowerCase();
      return sortedCalcs.filter((calc) => (calc.name || "").toLowerCase().includes(query));
    }, [sortedCalcs, showSearch, searchQuery]);
    const rootCalcs = useMemo9(() => filteredCalcs.filter((calc) => !calc.folder), [filteredCalcs]);
    return {
      // Sort state
      sortby,
      setSortBy,
      // Folder state
      folders,
      setFolders,
      newFolderName,
      setNewFolderName,
      showAddFolderInput,
      setShowAddFolderInput,
      // Drag state
      draggedItem,
      setDraggedItem,
      draggedIntoFolder,
      setDraggedIntoFolder,
      // Editing state
      editingFolder,
      setEditingFolder,
      editingFolderNewName,
      setEditingFolderNewName,
      editingCalcId,
      setEditingCalcId,
      editingCalcName,
      setEditingCalcName,
      // UI state
      openDropdownCalcId,
      setOpenDropdownCalcId,
      isExpanded,
      setIsExpanded,
      searchQuery,
      setSearchQuery,
      showSearch,
      setShowSearch,
      // Derived data
      sortedCalcs,
      filteredCalcs,
      rootCalcs,
      totalCalcCount: (calcs || []).length
    };
  }
  __name(useCalcListState, "useCalcListState");

  // public/src/react/components/calclist/hooks/useCalcListHandlers.js
  init_react_global();
  var { useCallback: useCallback14 } = react_global_default;
  function useCalcListHandlers(Action, calcs, state) {
    const {
      folders,
      setFolders,
      newFolderName,
      setNewFolderName,
      setShowAddFolderInput,
      draggedItem,
      setDraggedItem,
      setDraggedIntoFolder,
      editingFolder,
      editingFolderNewName,
      setEditingFolder,
      setEditingFolderNewName,
      editingCalcId,
      editingCalcName,
      setEditingCalcId,
      setEditingCalcName,
      setOpenDropdownCalcId,
      setIsExpanded,
      setSearchQuery,
      searchQuery,
      showSearch,
      setShowSearch
    } = state;
    const getMergedFolders = useCallback14(() => {
      const storedFolders = Action.getFolders();
      const foldersMap = new Map(storedFolders.map((f) => [f.name, f.open]));
      (calcs || []).forEach((calc) => {
        if (calc.folder && !foldersMap.has(calc.folder)) {
          foldersMap.set(calc.folder, false);
        }
      });
      return Array.from(foldersMap, ([name, open]) => ({ name, open })).sort((a, b) => a.name.localeCompare(b.name));
    }, [Action, calcs]);
    const handleNewFolderNameChange = /* @__PURE__ */ __name((e) => setNewFolderName(e.target.value), "handleNewFolderNameChange");
    const toggleAddFolderInput = useCallback14(() => {
      setShowAddFolderInput((v) => !v);
    }, [setShowAddFolderInput]);
    const addNewFolder = useCallback14((e) => {
      e.preventDefault();
      console.log("--- addNewFolder CALLED ---", newFolderName);
      if (newFolderName.trim() === "") {
        alert("Please provide a folder name!");
        return;
      }
      const newFolder = Action.createFolder(newFolderName);
      if (!newFolder) {
        alert("Folder already exists!");
        return;
      }
      setFolders(getMergedFolders());
      setNewFolderName("");
      setShowAddFolderInput(false);
    }, [Action, getMergedFolders, newFolderName, setFolders, setNewFolderName, setShowAddFolderInput]);
    const onDragStart = useCallback14((e, item) => {
      setDraggedItem(item);
      if (e && e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    }, [setDraggedItem]);
    const onDragOver = useCallback14((e, folderName) => {
      if (e && e.preventDefault) e.preventDefault();
      setDraggedIntoFolder(folderName);
    }, [setDraggedIntoFolder]);
    const changeCalcFolder = useCallback14((calc, folder) => {
      Action.updateFolder(calc, folder);
    }, [Action]);
    const onDrop = useCallback14((e, folder) => {
      if (e && e.preventDefault) e.preventDefault();
      if (draggedItem) {
        setDraggedItem(null);
        setDraggedIntoFolder(null);
        changeCalcFolder(draggedItem, folder);
      }
    }, [changeCalcFolder, draggedItem, setDraggedItem, setDraggedIntoFolder]);
    const toggleFolder = useCallback14((folderName) => {
      const currentFolder = folders.find((f) => f.name === folderName);
      let newOpenState = Action.toggleFolder(folderName);
      if (newOpenState === null && currentFolder) {
        Action.createFolder(folderName);
        newOpenState = Action.toggleFolder(folderName);
      }
      if (currentFolder && newOpenState !== null) {
        const detailsElement = document.querySelector(`details[data-folder-name="${folderName}"]`);
        if (detailsElement) {
          if (newOpenState) {
            detailsElement.setAttribute("open", "");
          } else {
            detailsElement.removeAttribute("open");
          }
        }
      }
      setFolders(getMergedFolders());
    }, [Action, folders, getMergedFolders, setFolders]);
    const toggleExpanded = useCallback14(() => setIsExpanded((v) => !v), [setIsExpanded]);
    const toggleSearchInput = useCallback14(() => {
      setShowSearch((prev) => !prev);
    }, [setShowSearch]);
    const handleSearchInputChange = useCallback14((value) => {
      setSearchQuery(value);
    }, [setSearchQuery]);
    const clearSearchQuery = useCallback14(() => {
      setSearchQuery("");
    }, [setSearchQuery]);
    const deleteFolder = useCallback14((folder) => {
      const success = Action.deleteFolder(folder.name);
      if (success) {
        setFolders(getMergedFolders());
      }
    }, [Action, getMergedFolders, setFolders]);
    const createCalcInFolder = useCallback14((folder) => {
      Action.createAndOpen({ folder });
    }, [Action]);
    const startEditingFolder = useCallback14((folderName) => {
      setEditingFolder(folderName);
      setEditingFolderNewName(folderName);
    }, [setEditingFolder, setEditingFolderNewName]);
    const stopEditingFolder = useCallback14(() => {
      setEditingFolder(null);
      setEditingFolderNewName(null);
    }, [setEditingFolder, setEditingFolderNewName]);
    const handleFolderEditComplete = useCallback14(() => {
      const newName = editingFolderNewName || "";
      if (newName.trim() === "") {
        stopEditingFolder();
        return;
      }
      if (editingFolder) {
        const success = Action.renameFolder(editingFolder, newName);
        if (success) {
          setFolders(getMergedFolders());
          stopEditingFolder();
        } else {
          alert("Folder name already exists!");
        }
      }
    }, [Action, editingFolder, editingFolderNewName, getMergedFolders, stopEditingFolder, setFolders]);
    const startEditingCalc = useCallback14((calc) => {
      setEditingCalcId(calc.id);
      setEditingCalcName(calc.name || "");
    }, [setEditingCalcId, setEditingCalcName]);
    const stopEditingCalc = useCallback14(() => {
      setEditingCalcId(null);
      setEditingCalcName("");
    }, [setEditingCalcId, setEditingCalcName]);
    const handleCalcRenameComplete = useCallback14(() => {
      const newName = editingCalcName.trim();
      if (newName === "") {
        stopEditingCalc();
        return;
      }
      const calc = calcs.find((c) => c.id === editingCalcId);
      if (calc) {
        Action.updateName(calc, newName);
      }
      stopEditingCalc();
    }, [Action, calcs, editingCalcId, editingCalcName, stopEditingCalc]);
    const handleCalcDropdownOpenChange = useCallback14((calcId, isOpen) => {
      setOpenDropdownCalcId(isOpen ? calcId : null);
    }, [setOpenDropdownCalcId]);
    return {
      // Folder input handlers
      handleNewFolderNameChange,
      toggleAddFolderInput,
      addNewFolder,
      // Drag and drop handlers
      onDragStart,
      onDragOver,
      onDrop,
      // Folder management handlers
      toggleFolder,
      toggleExpanded,
      deleteFolder,
      createCalcInFolder,
      // Folder editing handlers
      startEditingFolder,
      stopEditingFolder,
      handleFolderEditComplete,
      // Calc editing handlers
      startEditingCalc,
      stopEditingCalc,
      handleCalcRenameComplete,
      // Dropdown handlers
      handleCalcDropdownOpenChange,
      // Search handlers
      toggleSearchInput,
      handleSearchInputChange,
      clearSearchQuery
    };
  }
  __name(useCalcListHandlers, "useCalcListHandlers");

  // public/src/react/components/calclist/CalcList.jsx
  var import_jsx_runtime32 = __toESM(require_jsx_runtime());
  var { useEffect: useEffect21, useRef: useRef17, useMemo: useMemo10 } = react_global_default;
  var defaultClassNames2 = {
    root: "mx-1 overflow-y-auto no-scrollbar",
    header: "flex justify-between items-center gap-3 p-2 pr-0 mb-1 text-[13px] min-h-[24px] calclist-header relative overflow-visible",
    headerLeftAction: "flex items-center flex-shrink-0",
    // NEW: slot for accordion toggle
    headerTitle: "flex-1 text-theme flex items-center gap-1.5",
    headerActions: "flex items-center gap-1 absolute right-2",
    searchToggleButton: "w-6 h-6 flex items-center justify-center rounded-full hover-bg-button transition-all duration-150 text-theme",
    searchInputWrapper: "px-2 overflow-hidden transition-all duration-300",
    searchInputContainer: "flex items-center gap-2 rounded-xl border border-soft bg-[var(--bg-secondary-mix-40)] px-2 py-1 shadow-sm",
    searchInput: "flex-1 bg-transparent outline-none text-sm text-color-default placeholder:text-color-muted",
    loginMessage: "if-logged-out flex items-center text-sm text-theme-dim",
    newCalcButton: "w-6 h-6 flex items-center justify-center rounded hover-bg-button transition-all duration-150 text-theme tooltip tooltip-bottom",
    folderButton: "w-6 h-6 flex items-center justify-center rounded hover-bg-button transition-all duration-150 text-theme tooltip tooltip-bottom",
    addFolderForm: "flex items-center mt-4 ml-1 text-sm",
    addFolderInput: "flex-grow p-1 mr-2 border rounded outline-none border-soft",
    addFolderPrimaryButton: "px-2 py-1 border rounded-md shadow-sm border-soft text-color-default",
    addFolderSecondaryButton: "inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-sm text-color-muted transition-all rounded-md hover:text-color-default",
    folderSection: "my-1 mb-1 folder",
    folderSummary: "relative flex items-center gap-2 list-none cursor-pointer select-none group/folder w-full text-left rounded-md transition-all duration-150 ease-out hover-bg-button py-1.5 px-3 pr-12 text-sm text-color-default",
    folderActions: "absolute flex items-center justify-end gap-1 -translate-y-1/2 right-2 top-1/2 opacity-0 group-hover/folder:opacity-100 transition-opacity",
    folderMain: "select-none",
    folderList: "pl-3 ml-2 calcs-list",
    folderListBordered: "",
    folderListEmpty: "bg-[var(--bg-secondary-mix-40)] rounded-lg",
    folderEmptyState: "flex p-4 -ml-2 text-sm text-center text-[var(--text-dim)] italic",
    listWrapper: "mb-4 ml-1 select-none folder",
    listContainer: "",
    listContainerScrollable: "max-h-[40rem] overflow-y-auto thin-scrollbar",
    calcList: "calcs-list",
    calcItem: "calc-item group/calc w-full rounded-md cursor-pointer flex items-center justify-between my-1 relative transition-all duration-150 ease-out hover-bg-button",
    calcLink: "calclist-item flex-1 min-w-0 inline-flex items-center gap-2 whitespace-nowrap overflow-hidden transition-all duration-150 ease-out py-1.5 px-3 pr-12 text-sm",
    calcPlaceholder: "flex p-4 -ml-2 text-sm text-center opacity-50 rounded-xl bg-secondary text-color-default",
    loadMoreButton: "flex items-center gap-1 px-3 py-1 mt-1 text-sm hover:underline"
  };
  function CalcList(props) {
    const Action = useAction();
    const {
      calcs = [],
      selected,
      onSelect,
      className,
      style,
      classNames: classNameOverrides = {},
      slotProps = {},
      dropdownProps = {},
      folderDropdownProps = {},
      calcDropdownProps = {},
      headerExtra,
      showLogin = false,
      loginProps = {},
      showNewButton = true,
      title = null,
      leftAction = null,
      // NEW: React element for left slot (e.g., accordion toggle)
      calcListHeaderProps = {}
      // New prop to pass down to header
    } = props;
    const classes = mergeClassNames(defaultClassNames2, classNameOverrides);
    const getSlotProps = createSlotProps(classes, slotProps);
    const rootProps = getSlotProps("root", { className, style });
    const state = useCalcListState(Action, calcs);
    const {
      sortby,
      setSortBy,
      folders,
      newFolderName,
      setNewFolderName,
      showAddFolderInput,
      draggedItem,
      draggedIntoFolder,
      editingFolder,
      editingFolderNewName,
      setEditingFolderNewName,
      editingCalcId,
      editingCalcName,
      setEditingCalcName,
      openDropdownCalcId,
      isExpanded,
      sortedCalcs,
      filteredCalcs,
      rootCalcs,
      searchQuery,
      showSearch,
      setShowSearch,
      totalCalcCount
    } = state;
    const handlers = useCalcListHandlers(Action, calcs, state);
    const {
      handleNewFolderNameChange,
      toggleAddFolderInput,
      addNewFolder,
      onDragStart,
      onDragOver,
      onDrop,
      toggleFolder,
      toggleExpanded,
      deleteFolder,
      createCalcInFolder,
      startEditingFolder,
      stopEditingFolder,
      handleFolderEditComplete,
      startEditingCalc,
      stopEditingCalc,
      handleCalcRenameComplete,
      handleCalcDropdownOpenChange,
      toggleSearchInput,
      handleSearchInputChange,
      clearSearchQuery
    } = handlers;
    const folderInput = useRef17(null);
    const searchInputRef = useRef17(null);
    useEffect21(() => {
      if (showAddFolderInput && folderInput.current) {
        folderInput.current.focus();
      }
    }, [showAddFolderInput]);
    useEffect21(() => {
      if (showSearch && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [showSearch]);
    useEffect21(() => {
      if (totalCalcCount <= 10 && showSearch) {
        setShowSearch(false);
      }
    }, [totalCalcCount, showSearch, setShowSearch]);
    const calcLinkClass = /* @__PURE__ */ __name((active) => cn(
      classes.calcLink,
      active ? "text-color-active" : "text-color-default"
    ), "calcLinkClass");
    const calcItemClass = /* @__PURE__ */ __name((active) => cn(
      classes.calcItem,
      "hover:bg-[color-mix(in_srgb,var(--accent)_10%,transparent)]",
      active && "bg-[color-mix(in_srgb,var(--accent)_15%,transparent)] selected animate-pop"
    ), "calcItemClass");
    const buildCalcDropdown = /* @__PURE__ */ __name((calc) => /* @__PURE__ */ (0, import_jsx_runtime32.jsx)(
      CalcDropdown_default,
      {
        calc,
        folders,
        onSelect,
        onRename: startEditingCalc,
        onOpenChange: (isOpen) => handleCalcDropdownOpenChange(calc.id, isOpen),
        ...calcDropdownProps
      },
      `calc-dropdown-${calc.id}`
    ), "buildCalcDropdown");
    const sortedFolders = useMemo10(() => [...folders].sort((a, b) => a.name.localeCompare(b.name)), [folders]);
    return /* @__PURE__ */ (0, import_jsx_runtime32.jsxs)("div", { ...rootProps, children: [
      /* @__PURE__ */ (0, import_jsx_runtime32.jsx)(
        CalcListHeader_default,
        {
          sortby,
          onSortChange: setSortBy,
          onToggleAddFolder: toggleAddFolderInput,
          getSlotProps,
          dropdownProps,
          searchQuery,
          showSearch,
          showSearchToggle: totalCalcCount > 10,
          onSearchToggle: toggleSearchInput,
          onSearchChange: handleSearchInputChange,
          onSearchClear: clearSearchQuery,
          searchInputRef,
          headerExtra,
          showLogin,
          loginProps,
          showNewButton,
          title,
          leftAction,
          ...calcListHeaderProps
        }
      ),
      showAddFolderInput && /* @__PURE__ */ (0, import_jsx_runtime32.jsx)(
        AddFolderForm_default,
        {
          folderName: newFolderName,
          onFolderNameChange: handleNewFolderNameChange,
          onSubmit: addNewFolder,
          onCancel: toggleAddFolderInput,
          classNames: classes
        }
      ),
      sortedFolders.map((folder) => {
        const calcsInFolder = filteredCalcs.filter((calc) => calc.folder === folder.name);
        if (showSearch && calcsInFolder.length === 0) {
          return null;
        }
        const isDraggedOver = draggedIntoFolder === folder.name;
        return /* @__PURE__ */ (0, import_jsx_runtime32.jsx)(
          FolderSection_default,
          {
            folder,
            calcsInFolder,
            selectedCalcId: selected,
            isOpen: folder.open,
            isDraggedOver,
            editingCalcId,
            editingCalcName,
            editingFolder,
            editingFolderNewName,
            openDropdownCalcId,
            onToggleFolder: toggleFolder,
            onDragOver,
            onDrop,
            onFolderEditComplete: handleFolderEditComplete,
            onStopEditingFolder: stopEditingFolder,
            onEditingFolderNameChange: setEditingFolderNewName,
            onStartEditingFolder: startEditingFolder,
            onDeleteFolder: deleteFolder,
            onCreateCalcInFolder: createCalcInFolder,
            onCalcDragStart: onDragStart,
            onCalcSelect: onSelect,
            onCalcRenameComplete: handleCalcRenameComplete,
            onCalcRenameCancel: stopEditingCalc,
            onEditingCalcNameChange: setEditingCalcName,
            calcItemClassName: calcItemClass,
            calcLinkClassName: calcLinkClass,
            renderCalcDropdown: buildCalcDropdown,
            folderDropdownProps,
            classNames: classes,
            getSlotProps
          },
          folder.name
        );
      }),
      /* @__PURE__ */ (0, import_jsx_runtime32.jsx)(
        "div",
        {
          ...getSlotProps("listWrapper", {
            onDragOver: /* @__PURE__ */ __name((e) => {
              onDragOver(e, null);
            }, "onDragOver"),
            onDrop: /* @__PURE__ */ __name((e) => onDrop(e, null), "onDrop")
          }),
          children: /* @__PURE__ */ (0, import_jsx_runtime32.jsxs)("div", { className: classes.listContainer, children: [
            draggedItem?.folder && /* @__PURE__ */ (0, import_jsx_runtime32.jsx)(
              "div",
              {
                className: "border-dashed-tight rounded-lg p-3 my-2 text-sm text-center text-[var(--text-dim)] italic",
                style: draggedIntoFolder == null ? { "--dash-color": "var(--accent)", backgroundColor: "color-mix(in srgb, var(--accent) 10%, transparent)" } : void 0,
                children: "Drop here to unfolder"
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime32.jsx)("ul", { className: cn(
              classes.calcList,
              "border border-transparent"
            ), children: rootCalcs.map((calc) => {
              const isActive = selected == calc.id;
              const isEditing = editingCalcId === calc.id;
              return /* @__PURE__ */ (0, import_jsx_runtime32.jsx)(
                CalcItemRow_default,
                {
                  calc,
                  isActive,
                  isEditing,
                  editingCalcName,
                  onEditingNameChange: setEditingCalcName,
                  onDragStart,
                  onSelect,
                  onRenameComplete: handleCalcRenameComplete,
                  onRenameCancel: stopEditingCalc,
                  calcItemClassName: calcItemClass(isActive),
                  calcLinkClassName: calcLinkClass(isActive),
                  openDropdownCalcId,
                  renderDropdown: buildCalcDropdown
                },
                calc.id
              );
            }) })
          ] })
        }
      )
    ] });
  }
  __name(CalcList, "CalcList");
  var CalcList_default = react_global_default.memo(CalcList);

  // public/src/react/components/calc/CalcModals.jsx
  init_react_global();

  // public/src/react/components/common/CopyButton.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime33 = __toESM(require_jsx_runtime());
  var { useState: useState21, useRef: useRef18, useEffect: useEffect22 } = react_global_default;
  function CopyButton(props) {
    const { message: initialMessage, target, className, style, classNames = {}, slotProps = {}, children, tooltip = false } = (
      /** @type {CopyButtonProps} */
      props
    );
    const [showCheck, setShowCheck] = useState21(false);
    const timeoutRef = useRef18(null);
    useEffect22(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);
    const defaultClassNames4 = {
      root: tooltip ? "flex copy-event tooltip" : "flex copy-event",
      message: "inline-block mx-2"
    };
    const classes = mergeClassNames(defaultClassNames4, classNames);
    const getSlotProps = createSlotProps(classes, slotProps);
    const onCopy = /* @__PURE__ */ __name(async (e) => {
      if (e && e.preventDefault) e.preventDefault();
      let textToCopy = "";
      try {
        if (target) {
          const element = document.querySelector(target);
          if (element) {
            textToCopy = element.value || element.innerText || "";
          }
        } else if (props.text) {
          textToCopy = props.text;
        }
        if (textToCopy) {
          await navigator.clipboard.writeText(textToCopy);
          setShowCheck(true);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          timeoutRef.current = setTimeout(() => {
            setShowCheck(false);
          }, 2e3);
        } else {
          console.warn("CopyButton: No text to copy found for target", target);
        }
      } catch (err) {
        console.error("Copy failed", err);
      }
    }, "onCopy");
    const content = children || Icon_default.copy("w-4 h-4");
    const copiedContent = /* @__PURE__ */ (0, import_jsx_runtime33.jsxs)(import_jsx_runtime33.Fragment, { children: [
      Icon_default.success("w-3.5 h-3.5"),
      "Copied!"
    ] });
    return /* @__PURE__ */ (0, import_jsx_runtime33.jsx)(
      "button",
      {
        type: "button",
        "data-tip": tooltip ? "Click to copy" : void 0,
        "data-ic-copy-state": showCheck ? "copied" : "idle",
        onClick: onCopy,
        ...getSlotProps("root", { className, style }),
        children: showCheck ? copiedContent : content
      }
    );
  }
  __name(CopyButton, "CopyButton");
  var CopyButton_default = CopyButton;

  // public/src/react/components/calc/CalcModals.jsx
  init_Icon();

  // public/src/react/context/CalcContext.jsx
  init_react_global();

  // public/src/react/hooks/usePerf.js
  function usePerf(componentName) {
    if (typeof window === "undefined") return;
    const perfMetricsEnabled = window.ReactPerfMetrics?.enabled;
    const profilerEnabled = window.PerfProfiler?.enabled;
    if (!perfMetricsEnabled && !profilerEnabled) return;
    const startTime = performance.now();
    queueMicrotask(() => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      if (perfMetricsEnabled && window.ReactPerfMetrics?.logRender) {
        window.ReactPerfMetrics.logRender(componentName, duration);
      }
      if (profilerEnabled && window.PerfProfiler) {
        const rowMatch = componentName.match(/CalcRow:(\d+)/);
        if (rowMatch) {
          window.PerfProfiler.recordRowRender(parseInt(rowMatch[1], 10));
        }
      }
    });
  }
  __name(usePerf, "usePerf");

  // public/src/react/types/ResultStatus.js
  var ResultStatus = Object.freeze({
    PENDING: "pending",
    // Not yet computed (placeholder)
    COMPUTED: "computed",
    // Has valid computed result
    ERROR: "error"
    // Computation failed
  });
  function isComputed(result) {
    return result?.status === ResultStatus.COMPUTED;
  }
  __name(isComputed, "isComputed");
  function hasComputedResults(results) {
    return Array.isArray(results) && results.some(isComputed);
  }
  __name(hasComputedResults, "hasComputedResults");

  // public/src/react/hooks/useResults.js
  init_react_global();
  var { useCallback: useCallback15, useSyncExternalStore: useSyncExternalStore2 } = react_global_default;
  var EMPTY_RESULTS = Object.freeze([]);
  function useResults(calcId) {
    const subscribe = useCallback15((callback) => {
      if (typeof window === "undefined" || !window.resultsStore) {
        return () => {
        };
      }
      return window.resultsStore.subscribe((updatedCalcId) => {
        if (updatedCalcId === calcId) {
          callback();
        }
      });
    }, [calcId]);
    const getSnapshot = useCallback15(() => {
      if (typeof window === "undefined" || !window.resultsStore) {
        return EMPTY_RESULTS;
      }
      return window.resultsStore.get(calcId);
    }, [calcId]);
    const getServerSnapshot = useCallback15(() => EMPTY_RESULTS, []);
    return useSyncExternalStore2(subscribe, getSnapshot, getServerSnapshot);
  }
  __name(useResults, "useResults");

  // public/src/react/constants/DisplayMode.js
  var DisplayMode = Object.freeze({
    REGULAR: "regular",
    TEXT: "text",
    PRESENT: "present"
  });

  // public/src/react/utils/calcDetection.js
  var DECORATOR_PATTERN = /@(hero|icon|section|theme|prefix|suffix|label|tooltip|note|half|third|wide|advanced|hidden|tab|layout|card|col|span|slider|w-)/i;
  function parseSectionDecorators(titleText) {
    if (!titleText) return { cleanTitle: "", decorators: {} };
    const decorators = {};
    if (/\s@tab\b/i.test(titleText)) decorators.tab = true;
    if (/\s@collapsed\b/i.test(titleText)) decorators.collapsed = true;
    if (/\s@card\b/i.test(titleText)) decorators.card = true;
    const iconMatch = titleText.match(/\s@icon\s*\(([^)]+)\)/i);
    if (iconMatch) decorators.icon = iconMatch[1].trim().replace(/^["']|["']$/g, "");
    const layoutMatch = titleText.match(/\s@layout\s*\(([^)]+)\)/i);
    if (layoutMatch) decorators.layout = layoutMatch[1].trim().replace(/^["']|["']$/g, "");
    const colMatch = titleText.match(/\s@col\s*\(([^)]+)\)/i);
    if (colMatch) {
      const colVal = colMatch[1].trim();
      if (colVal === "full") {
        decorators.colSpan = 12;
        decorators.colTotal = 12;
      } else {
        const parts = colVal.split("/");
        if (parts.length === 2) {
          decorators.colSpan = parseInt(parts[0]);
          decorators.colTotal = parseInt(parts[1]);
        }
      }
    }
    const cleanTitle = titleText.replace(/\s@tab\b/ig, "").replace(/\s@collapsed\b/ig, "").replace(/\s@card\b/ig, "").replace(/\s@icon\s*\([^)]*\)/ig, "").replace(/\s@layout\s*\([^)]*\)/ig, "").replace(/\s@col\s*\([^)]*\)/ig, "").trim();
    return { cleanTitle, decorators };
  }
  __name(parseSectionDecorators, "parseSectionDecorators");
  function rowHasDecorators(text) {
    if (!text) return false;
    return DECORATOR_PATTERN.test(text);
  }
  __name(rowHasDecorators, "rowHasDecorators");
  function hasPresentationDecorators(calc) {
    if (!calc?.rows) return false;
    return calc.rows.some((row) => {
      const text = row?.text || "";
      return rowHasDecorators(text);
    });
  }
  __name(hasPresentationDecorators, "hasPresentationDecorators");
  function detectAutoFormat(text) {
    if (!text) return null;
    const trimmed = text.trim();
    if (trimmed.startsWith("# ") && !trimmed.includes("=")) {
      const { cleanTitle, decorators } = parseSectionDecorators(trimmed.slice(2).trim());
      const result = {
        type: "section",
        autoSection: cleanTitle,
        isSubsection: false
      };
      if (decorators.tab) result.autoTab = true;
      if (decorators.collapsed) result.autoCollapsed = true;
      if (decorators.card) result.autoCard = true;
      if (decorators.icon) result.autoIcon = decorators.icon;
      if (decorators.layout) result.autoLayout = decorators.layout;
      if (decorators.colSpan) {
        result.autoColSpan = decorators.colSpan;
        result.autoColTotal = decorators.colTotal;
      }
      return result;
    }
    if (trimmed.startsWith("## ") && !trimmed.includes("=")) {
      const { cleanTitle, decorators } = parseSectionDecorators(trimmed.slice(3).trim());
      const result = {
        type: "section",
        autoSection: cleanTitle,
        isSubsection: true
      };
      if (decorators.tab) result.autoTab = true;
      if (decorators.collapsed) result.autoCollapsed = true;
      if (decorators.card) result.autoCard = true;
      if (decorators.icon) result.autoIcon = decorators.icon;
      if (decorators.layout) result.autoLayout = decorators.layout;
      if (decorators.colSpan) {
        result.autoColSpan = decorators.colSpan;
        result.autoColTotal = decorators.colTotal;
      }
      return result;
    }
    if (trimmed.startsWith("//") && /^\/\/\s*-{3,}/.test(trimmed)) {
      return {
        type: "divider",
        autoDivider: true
      };
    }
    if (/^-{3,}\s*$/.test(trimmed)) {
      return {
        type: "divider",
        autoDivider: true
      };
    }
    return null;
  }
  __name(detectAutoFormat, "detectAutoFormat");

  // public/src/react/strategies/ViewStrategy.js
  var strategies = {
    editor: {
      name: "editor",
      showRowNumbers: true,
      showRowContext: true,
      showExpandButton: true,
      showToolbar: true,
      showShareBar: true,
      showCalcUrlPreview: true,
      autoInsertRows: true,
      showAddRowButton: true,
      allowTitleEdit: true,
      density: "normal",
      footer: null,
      grid: { columns: 3, gapX: "1rem", gapY: "0.5rem" }
    },
    public: {
      name: "public",
      showRowNumbers: true,
      showRowContext: false,
      showExpandButton: true,
      showToolbar: false,
      showShareBar: true,
      showCalcUrlPreview: true,
      autoInsertRows: false,
      showAddRowButton: false,
      allowTitleEdit: false,
      density: "normal",
      footer: null,
      grid: { columns: 3, gapX: "1rem", gapY: "0.5rem" }
    },
    embed: {
      name: "embed",
      showRowNumbers: false,
      showRowContext: false,
      showExpandButton: true,
      showToolbar: false,
      showShareBar: false,
      showCalcUrlPreview: false,
      autoInsertRows: true,
      showAddRowButton: false,
      allowTitleEdit: false,
      density: "compact",
      footer: "embed",
      grid: { columns: 2, gapX: "0.5rem", gapY: "0.25rem" }
    }
  };
  strategies.calc = strategies.editor;
  function getViewStrategy(mode) {
    return strategies[mode] || strategies.editor;
  }
  __name(getViewStrategy, "getViewStrategy");

  // public/src/react/lib.jsx
  init_react_global();
  var import_jsx_runtime34 = __toESM(require_jsx_runtime());
  var globalScope = typeof window !== "undefined" ? window : globalThis;
  var LIB = globalScope.LIB || {};
  try {
    if (typeof window !== "undefined" && typeof URLSearchParams !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const debugParam = params.get("debug");
      if (debugParam) {
        globalScope.IC_DEBUG_NAMESPACES = debugParam.split(",").map((s) => s.trim().toLowerCase());
        globalScope.IC_UI_DEBUG = globalScope.IC_DEBUG_NAMESPACES.includes("all") || globalScope.IC_DEBUG_NAMESPACES.includes("ui");
      }
    }
    globalScope.IC_UI_DEBUG = globalScope.IC_UI_DEBUG || false;
    globalScope.IC_DEBUG_NAMESPACES = globalScope.IC_DEBUG_NAMESPACES || [];
  } catch (e) {
  }
  function UIlog(...args) {
    try {
      if (globalScope.IC_UI_DEBUG) {
        console.log(...args);
      }
    } catch (e) {
    }
  }
  __name(UIlog, "UIlog");
  LIB.tryToRender = /* @__PURE__ */ __name(function tryToRender(reactElement, querySelector) {
    const el = typeof document !== "undefined" ? document.querySelector(querySelector) : null;
    if (el) {
      try {
        if (!el.__ic_root) {
          el.__ic_root = react_dom_global_default.createRoot(el);
        }
        el.__ic_root.render(reactElement);
      } catch (e) {
        console.error("LIB.tryToRender failed:", e);
        throw e;
      }
    }
  }, "tryToRender");
  var extractImaginaryParts = /* @__PURE__ */ __name((formattedStr) => {
    if (typeof formattedStr !== "string") return null;
    const parts = [];
    let lastIndex = 0;
    const tokenRegex = /[-+]?(?:\d*\.?\d+|π)?i/g;
    let match;
    while ((match = tokenRegex.exec(formattedStr)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      const prev = formattedStr[start - 1];
      const next = formattedStr[end];
      if (prev && /[a-zA-Z]/.test(prev) || next && /[a-zA-Z]/.test(next)) {
        continue;
      }
      if (start > lastIndex) {
        parts.push(formattedStr.slice(lastIndex, start));
      }
      parts.push({ imag: match[0] });
      lastIndex = end;
    }
    if (!parts.length) return null;
    if (lastIndex < formattedStr.length) {
      parts.push(formattedStr.slice(lastIndex));
    }
    return parts;
  }, "extractImaginaryParts");
  var RESULT_TYPE_DETECTORS = [
    // Complex numbers (e.g., 3 + 2i, πi)
    {
      type: "complex",
      detect: /* @__PURE__ */ __name((rawResult, formattedStr) => {
        const Complex = globalScope.LIB?.Complex || globalScope.IC?.LIB?.Complex;
        if (Complex?.isComplex?.(rawResult)) return true;
        return !!extractImaginaryParts(formattedStr);
      }, "detect"),
      render: /* @__PURE__ */ __name((formattedStr) => {
        const parts = extractImaginaryParts(formattedStr);
        if (!parts) return null;
        return /* @__PURE__ */ (0, import_jsx_runtime34.jsx)("span", { children: parts.map((part, idx) => {
          if (typeof part === "object" && part?.imag) {
            const coef = part.imag.slice(0, -1);
            return /* @__PURE__ */ (0, import_jsx_runtime34.jsxs)("span", { children: [
              coef,
              /* @__PURE__ */ (0, import_jsx_runtime34.jsx)("span", { className: "imaginary-i", children: "i" })
            ] }, idx);
          }
          return part;
        }) });
      }, "render")
    },
    // Fractions (detected from formatted string pattern)
    {
      type: "fraction",
      detect: /* @__PURE__ */ __name((rawResult, formattedStr) => {
        return typeof formattedStr === "string" && /FRACTION{\d+,\s*\d+}/.test(formattedStr);
      }, "detect"),
      render: /* @__PURE__ */ __name((formattedStr) => {
        const fracRegex = /FRACTION{(\d+)[,]\s*(\d+)}/;
        const match = formattedStr.match(fracRegex);
        if (!match) return null;
        const [, numerator, denominator] = match;
        const parts = formattedStr.split(fracRegex);
        return /* @__PURE__ */ (0, import_jsx_runtime34.jsxs)("span", { children: [
          (parts[0] || "").trim(),
          /* @__PURE__ */ (0, import_jsx_runtime34.jsxs)("frac", { children: [
            /* @__PURE__ */ (0, import_jsx_runtime34.jsx)("span", { className: "fracspace", children: " " }),
            /* @__PURE__ */ (0, import_jsx_runtime34.jsx)("sup", { children: numerator }),
            "\u2044",
            /* @__PURE__ */ (0, import_jsx_runtime34.jsx)("sub", { children: denominator })
          ] }),
          (parts[parts.length - 1] || "").trim()
        ] });
      }, "render")
    }
    // Future: Add more type detectors here
    // { type: 'unit', detect: ..., render: ... },
    // { type: 'currency', detect: ..., render: ... },
    // { type: 'date', detect: ..., render: ... },
  ];
  LIB.getResultDisplayInfo = (rawResult, formattedResult) => {
    for (const detector of RESULT_TYPE_DETECTORS) {
      if (detector.detect(rawResult, formattedResult)) {
        return {
          type: detector.type,
          render: detector.render,
          meta: detector.meta
        };
      }
    }
    return null;
  };
  LIB.prettyPrintResultHTML = (formattedResult, rawResult = null) => {
    let resultDisplay = formattedResult;
    if (resultDisplay && typeof resultDisplay === "object") {
      try {
        resultDisplay = JSON.stringify(resultDisplay);
      } catch (e) {
        resultDisplay = String(resultDisplay);
      }
    }
    if (typeof resultDisplay === "string") {
      const displayInfo = LIB.getResultDisplayInfo(rawResult, resultDisplay);
      if (displayInfo?.render) {
        const rendered = displayInfo.render(resultDisplay);
        if (rendered) {
          resultDisplay = rendered;
        }
      }
    }
    return resultDisplay;
  };
  LIB.prettyPrintFunction = (funcObj) => {
    return `function(${funcObj.params.join(", ")})`;
  };
  LIB.prettyPrintObject = (obj, indent = 2) => {
    let result = "";
    const addIndent = /* @__PURE__ */ __name((level) => " ".repeat(level * indent), "addIndent");
    const isReactEl = /* @__PURE__ */ __name((v) => v && typeof v === "object" && (v.$$typeof || v.props), "isReactEl");
    const formatObject = /* @__PURE__ */ __name((o, level = 0) => {
      if (!o || typeof o !== "object") {
        result += `${addIndent(level)}${String(o)}
`;
        return;
      }
      if (isReactEl(o)) {
        result += `${addIndent(level)}[react-element]
`;
        return;
      }
      let entries = [];
      try {
        entries = Object.entries(o);
      } catch (e) {
        entries = [];
      }
      for (const [key, value] of entries) {
        if (typeof value === "object" && value !== null) {
          if (isReactEl(value)) {
            result += `${addIndent(level)}${key}: [react-element]
`;
            continue;
          }
          const formatted = IC.api.formatValue(value);
          if (formatted !== value && typeof formatted !== "object") {
            result += `${addIndent(level)}${key}: ${formatted}
`;
          } else {
            result += `${addIndent(level)}${key}:
`;
            formatObject(value, level + 1);
          }
        } else {
          result += `${addIndent(level)}${key}: ${value}
`;
        }
      }
    }, "formatObject");
    formatObject(obj);
    return result;
  };
  LIB.prettyPrintArray = (arr, indent = 2, level = 0) => {
    let result = "";
    const addIndent = /* @__PURE__ */ __name((lvl) => " ".repeat(lvl * indent), "addIndent");
    const isReactEl = /* @__PURE__ */ __name((v) => v && typeof v === "object" && (v.$$typeof || v.props), "isReactEl");
    const formatArray = /* @__PURE__ */ __name((a, currentLevel = 0) => {
      (a || []).forEach((item, index2) => {
        if (typeof item === "object" && item !== null) {
          if (isReactEl(item)) {
            result += `${addIndent(currentLevel)}[${index2}]: [react-element]
`;
            return;
          }
          const formatted = IC.api.formatValue(item);
          if (formatted !== item && typeof formatted !== "object") {
            result += `${addIndent(currentLevel)}[${index2}]: ${formatted}
`;
          } else if (Array.isArray(item)) {
            result += `${addIndent(currentLevel)}[${index2}]:
`;
            formatArray(item, currentLevel + 1);
          } else {
            result += `${addIndent(currentLevel)}[${index2}]:
`;
            result += LIB.prettyPrintObject(item, indent, currentLevel + 1);
          }
        } else {
          result += `${addIndent(currentLevel)}[${index2}]: ${item}
`;
        }
      });
    }, "formatArray");
    formatArray(arr, level);
    return result;
  };
  LIB.setVariablesInCalc = /* @__PURE__ */ __name(function setVariablesInCalc(Action, calc, vars, results) {
    const solution = vars;
    if (!solution || !calc) return;
    for (const varName in solution) {
      const solvedValue = solution[varName];
      let newText = null;
      let rowIndex = 0;
      if (/^r\d+$/i.test(varName)) {
        const varRowIndex = parseInt(varName.slice(1), 10) - 1;
        const currentRowText = calc.rows[varRowIndex].text;
        const newMainText = IC.api.formatValue(solvedValue);
        newText = IC.api.replaceBeforeComment(currentRowText, newMainText);
        Action.updateRowText(calc, varRowIndex, newText);
      } else {
        rowIndex = IC.api.findParameterRow(calc, varName);
        if (rowIndex !== null) {
          const row = calc.rows[rowIndex];
          newText = IC.api.buildParameterText(row.text, IC.api.formatValue(solvedValue));
        }
        if (newText) {
          Action.updateRowText(calc, rowIndex, newText);
        }
      }
    }
  }, "setVariablesInCalc");
  var debounce = /* @__PURE__ */ __name((callback, wait) => {
    let timeoutId = null;
    let lastArgs = null;
    const debouncedFn = /* @__PURE__ */ __name((...args) => {
      lastArgs = args;
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        lastArgs = null;
        callback.apply(null, args);
      }, wait);
    }, "debouncedFn");
    debouncedFn.flush = () => {
      if (timeoutId && lastArgs) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
        callback.apply(null, lastArgs);
        lastArgs = null;
      }
    };
    debouncedFn.cancel = () => {
      window.clearTimeout(timeoutId);
      timeoutId = null;
      lastArgs = null;
    };
    return debouncedFn;
  }, "debounce");
  var perfTimers = {};
  var perfCounts = {};
  function perfStart(label) {
    if (!globalScope.IC_DEBUG_NAMESPACES?.includes("perf") && !globalScope.IC_DEBUG_NAMESPACES?.includes("all")) {
      return;
    }
    perfTimers[label] = performance.now();
  }
  __name(perfStart, "perfStart");
  function perfEnd(label, extraInfo = "") {
    if (!globalScope.IC_DEBUG_NAMESPACES?.includes("perf") && !globalScope.IC_DEBUG_NAMESPACES?.includes("all")) {
      return;
    }
    const start = perfTimers[label];
    if (start) {
      const duration = (performance.now() - start).toFixed(2);
      console.log(`[perf] ${label}: ${duration}ms${extraInfo ? ` (${extraInfo})` : ""}`);
      delete perfTimers[label];
    }
  }
  __name(perfEnd, "perfEnd");
  function perfCount(label) {
    if (!globalScope.IC_DEBUG_NAMESPACES?.includes("perf") && !globalScope.IC_DEBUG_NAMESPACES?.includes("all")) {
      return;
    }
    perfCounts[label] = (perfCounts[label] || 0) + 1;
    console.log(`[perf] ${label} count: ${perfCounts[label]}`);
  }
  __name(perfCount, "perfCount");
  function perfReset() {
    Object.keys(perfCounts).forEach((k) => delete perfCounts[k]);
    Object.keys(perfTimers).forEach((k) => delete perfTimers[k]);
  }
  __name(perfReset, "perfReset");
  function afterNextPaint(callback) {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  }
  __name(afterNextPaint, "afterNextPaint");
  globalScope.LIB = LIB;
  globalScope.perfStart = perfStart;
  globalScope.perfEnd = perfEnd;
  globalScope.perfCount = perfCount;
  globalScope.perfReset = perfReset;
  function createLoopGuard(label, options = {}) {
    const { maxCalls = 100, windowMs = 1e3, onDetected = null, showDevWarning = true } = options;
    let callCount = 0;
    let windowStart = Date.now();
    let hasWarned = false;
    return {
      /**
       * Check if we're in an infinite loop. Call at start of function/render.
       * @returns {boolean} true if loop detected (caller should abort)
       */
      check() {
        const now = Date.now();
        if (now - windowStart > windowMs) {
          callCount = 0;
          windowStart = now;
          hasWarned = false;
        }
        callCount++;
        if (callCount > maxCalls) {
          if (!hasWarned) {
            hasWarned = true;
            const msg = `[LoopGuard] Infinite loop detected in "${label}": ${callCount} calls in ${windowMs}ms. Aborting to prevent crash.`;
            console.error(msg);
            if (showDevWarning && typeof document !== "undefined" && !document.querySelector(".ic-loop-warning")) {
              try {
                const banner = document.createElement("div");
                banner.className = "ic-loop-warning";
                banner.style.cssText = "position:fixed;top:0;left:0;right:0;background:#dc2626;color:white;padding:12px;z-index:99999;text-align:center;font-family:system-ui;font-size:14px;";
                banner.innerHTML = `<strong>Infinite loop detected in "${label}"</strong> - Check console for details. <button onclick="this.parentElement.remove()" style="margin-left:12px;padding:4px 8px;cursor:pointer;">Dismiss</button>`;
                document.body.appendChild(banner);
              } catch (e) {
              }
            }
            if (onDetected) {
              onDetected(label, callCount);
            }
          }
          return true;
        }
        return false;
      },
      /** Reset the guard (useful after intentional batch operations) */
      reset() {
        callCount = 0;
        windowStart = Date.now();
        hasWarned = false;
      },
      /** Get current stats for debugging */
      getStats() {
        return { label, callCount, windowStart, maxCalls, windowMs };
      }
    };
  }
  __name(createLoopGuard, "createLoopGuard");
  function boundedWhile(condition, body, maxIterations = 1e3, label = "unknown") {
    let i = 0;
    while (condition() && i < maxIterations) {
      body();
      i++;
    }
    if (i >= maxIterations) {
      console.error(`[boundedWhile] "${label}" hit max iterations (${maxIterations}). Possible infinite loop.`);
    }
    return i;
  }
  __name(boundedWhile, "boundedWhile");
  var actionLoopGuard = createLoopGuard("Action.mutations", {
    maxCalls: 200,
    windowMs: 1e3
  });
  globalScope.actionLoopGuard = actionLoopGuard;
  globalScope.createLoopGuard = createLoopGuard;
  globalScope.boundedWhile = boundedWhile;

  // public/src/react/utils/ResultUtils.js
  function hasContent(r) {
    const raw = r?.output?.raw;
    return raw != null && raw !== "";
  }
  __name(hasContent, "hasContent");
  function hasComment(r) {
    const comment = r?.comment?.original;
    return typeof comment === "string" && comment !== "";
  }
  __name(hasComment, "hasComment");
  function hasType(r) {
    return !!r?.type?.units;
  }
  __name(hasType, "hasType");
  function hasError(r) {
    return r?.status === "error" || !!r?.debug?.message;
  }
  __name(hasError, "hasError");
  function hasUI(r) {
    return r?.ui != null && r.ui.type != null;
  }
  __name(hasUI, "hasUI");
  function isHero(r) {
    const p = r?.presentation;
    return p?.hero === true || p?.mode === "hero";
  }
  __name(isHero, "isHero");
  function isHidden2(r) {
    return r?.presentation?.mode === "hidden";
  }
  __name(isHidden2, "isHidden");
  function isAdvanced(r) {
    const p = r?.presentation;
    return p?.advanced === true || p?.mode === "advanced";
  }
  __name(isAdvanced, "isAdvanced");
  function isChart(r) {
    const type = r?.output?.raw?.type;
    return typeof type === "string" && type.toLowerCase().endsWith("chart");
  }
  __name(isChart, "isChart");
  function isTable(r) {
    return r?.output?.raw?.type === "table";
  }
  __name(isTable, "isTable");
  function isArray(r) {
    return r?.type?.isArray === true;
  }
  __name(isArray, "isArray");
  function isCustomObject(r) {
    return r?.type?.isCustomObject === true;
  }
  __name(isCustomObject, "isCustomObject");
  function isCustomFunction(r) {
    return r?.type?.isCustomFunction === true;
  }
  __name(isCustomFunction, "isCustomFunction");
  function isSection(r) {
    return !!r?.presentation?.section;
  }
  __name(isSection, "isSection");
  function getDisplayValue(r) {
    return r?.output?.display ?? "";
  }
  __name(getDisplayValue, "getDisplayValue");
  function getRawValue(r) {
    return r?.output?.raw;
  }
  __name(getRawValue, "getRawValue");
  function getComputedValue(r) {
    return r?.output?.value;
  }
  __name(getComputedValue, "getComputedValue");
  function getUnits(r) {
    return r?.type?.units ?? "";
  }
  __name(getUnits, "getUnits");
  function getFormat(r) {
    return r?.type?.format ?? "";
  }
  __name(getFormat, "getFormat");
  function getLabel(r) {
    return r?.display?.label ?? r?.presentation?.label ?? null;
  }
  __name(getLabel, "getLabel");
  function getComment(r) {
    return r?.display?.comment ?? r?.comment?.original ?? null;
  }
  __name(getComment, "getComment");
  function getRawComment(r) {
    return r?.comment?.original ?? "";
  }
  __name(getRawComment, "getRawComment");
  function getUIType(r) {
    return r?.ui?.type ?? null;
  }
  __name(getUIType, "getUIType");
  function getUIConfig(r) {
    return r?.ui?.config ?? null;
  }
  __name(getUIConfig, "getUIConfig");
  function getUIWriteback(r) {
    return r?.ui?.writeback ?? null;
  }
  __name(getUIWriteback, "getUIWriteback");
  function getPresentationMode(r) {
    return r?.presentation?.mode ?? null;
  }
  __name(getPresentationMode, "getPresentationMode");
  function getIcon(r) {
    return r?.presentation?.icon ?? null;
  }
  __name(getIcon, "getIcon");
  function getInput(r) {
    return r?.input?.original ?? "";
  }
  __name(getInput, "getInput");
  function getProcessedInput(r) {
    return r?.input?.processed ?? "";
  }
  __name(getProcessedInput, "getProcessedInput");
  function getDebugMessage(r) {
    return r?.debug?.message ?? "";
  }
  __name(getDebugMessage, "getDebugMessage");
  function getProcessingTime(r) {
    return r?.debug?.timeMs ?? null;
  }
  __name(getProcessingTime, "getProcessingTime");
  function prettyPrint(r) {
    if (!r?.output) return "";
    const { raw, display } = r.output;
    return LIB.prettyPrintResultHTML(display ?? raw, raw);
  }
  __name(prettyPrint, "prettyPrint");
  function prettyPrintParseTree(r) {
    const tree = r?.deps?.tokens;
    if (!tree || !Array.isArray(tree)) return "";
    return JSON.stringify(tree, null, 2);
  }
  __name(prettyPrintParseTree, "prettyPrintParseTree");
  function formatAsText(r, options = {}) {
    const { includeUnits = true, includeComment = false } = options;
    let text = getDisplayValue(r);
    if (includeUnits && hasType(r)) {
      text += " " + getUnits(r);
    }
    if (includeComment && hasComment(r)) {
      text += " // " + getComment(r);
    }
    return text.trim();
  }
  __name(formatAsText, "formatAsText");
  function areValuesEqual(a, b) {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    if (typeof a !== "object" || typeof b !== "object") {
      return a === b;
    }
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch (e) {
      return a === b;
    }
  }
  __name(areValuesEqual, "areValuesEqual");
  function areEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.status === b.status && a.output?.display === b.output?.display && areValuesEqual(a.output?.raw, b.output?.raw) && a.type?.units === b.type?.units && a.display?.label === b.display?.label && a.display?.comment === b.display?.comment && a.comment?.original === b.comment?.original && a.comment?.cleaned === b.comment?.cleaned;
  }
  __name(areEqual, "areEqual");
  function makeBlank(status = "pending") {
    return {
      status,
      _meta: { v: 2 },
      input: { original: "", processed: "" },
      output: { raw: null, value: null, display: "" },
      type: {
        units: "",
        format: "",
        isArray: false,
        customUnit: null,
        isCustomObject: false,
        isCustomFunction: false
      },
      comment: { original: "", cleaned: null },
      display: { label: null, comment: null },
      ui: null,
      presentation: null,
      deps: { references: [], missing: [], defines: [], customUnits: [], tokens: [] },
      debug: { message: "", log: [], trace: [], timeMs: null }
    };
  }
  __name(makeBlank, "makeBlank");
  var Result = {
    // Predicates
    hasContent,
    hasComment,
    hasType,
    hasError,
    hasUI,
    isHero,
    isHidden: isHidden2,
    isAdvanced,
    isChart,
    isTable,
    isArray,
    isCustomObject,
    isCustomFunction,
    isSection,
    // Getters
    getDisplayValue,
    getRawValue,
    getComputedValue,
    getUnits,
    getFormat,
    getLabel,
    getComment,
    getRawComment,
    getUIType,
    getUIConfig,
    getUIWriteback,
    getPresentationMode,
    getIcon,
    getInput,
    getProcessedInput,
    getDebugMessage,
    getProcessingTime,
    // Formatting
    prettyPrint,
    prettyPrintParseTree,
    formatAsText,
    // Comparison
    areEqual,
    areValuesEqual,
    // Factory
    makeBlank
  };
  if (typeof window !== "undefined") {
    window.Result = Result;
  }

  // public/src/react/utils/locationParams.js
  function getParamFromLocation(paramName) {
    if (typeof window === "undefined") return null;
    try {
      const params = new URLSearchParams(window.location.search || "");
      const value = params.get(paramName);
      if (value) return value;
    } catch (_e) {
    }
    try {
      const hash = window.location.hash || "";
      const qIdx = hash.indexOf("?");
      if (qIdx !== -1) {
        const hashQuery = hash.slice(qIdx + 1);
        const params = new URLSearchParams(hashQuery);
        const value = params.get(paramName);
        if (value) return value;
      }
    } catch (_e) {
    }
    return null;
  }
  __name(getParamFromLocation, "getParamFromLocation");
  function normalizeViewParam(viewParam) {
    if (!viewParam) return null;
    const v = String(viewParam).toLowerCase();
    if (v === "presentation") return "present";
    return v;
  }
  __name(normalizeViewParam, "normalizeViewParam");

  // public/src/react/context/CalcContext.jsx
  var import_jsx_runtime35 = __toESM(require_jsx_runtime());
  var { useState: useState22, useMemo: useMemo11, useCallback: useCallback16, useEffect: useEffect23, useRef: useRef19, useSyncExternalStore: useSyncExternalStore3, createContext: createContext6, useContext: useContext6 } = react_global_default;
  var BLANK_RESULT = Object.freeze(Result.makeBlank());
  var CalcContext = createContext6({
    calc: null,
    calcId: null,
    viewMode: "calc",
    /** @type {import('../strategies/ViewStrategy.js').ViewStrategy} */
    strategy: getViewStrategy("calc"),
    selectedRow: 0,
    setSelectedRow: /* @__PURE__ */ __name((row) => {
    }, "setSelectedRow"),
    results: [],
    setResults: /* @__PURE__ */ __name((results) => {
    }, "setResults"),
    isReady: true,
    setIsReady: /* @__PURE__ */ __name((ready) => {
    }, "setIsReady"),
    // Display mode: DisplayMode.REGULAR | DisplayMode.TEXT | DisplayMode.PRESENT
    displayMode: DisplayMode.REGULAR,
    setDisplayMode: /* @__PURE__ */ __name((mode) => {
    }, "setDisplayMode")
  });
  function useCalcFromStore(calcId) {
    const subscribe = useCallback16((callback) => {
      if (typeof window === "undefined" || !window.calcStore) {
        return () => {
        };
      }
      return window.calcStore.subscribe((updatedCalc, detail) => {
        if (window.DebugLogger?.isEnabled("store")) {
          window.DebugLogger.log("store", `SUBSCRIBE calcId=${calcId} updatedId=${updatedCalc?.id} match=${updatedCalc?.id === calcId}`);
        }
        if (updatedCalc?.id === calcId) {
          callback();
        }
      });
    }, [calcId]);
    const getSnapshot = useCallback16(() => {
      if (typeof window === "undefined" || !window.calcStore) {
        return null;
      }
      return window.calcStore.get(calcId);
    }, [calcId]);
    const getServerSnapshot = useCallback16(() => null, []);
    return useSyncExternalStore3(subscribe, getSnapshot, getServerSnapshot);
  }
  __name(useCalcFromStore, "useCalcFromStore");
  function CalcProvider({ children, calcId: propsCalcId, calc: initialCalc = null, viewMode = "calc", selectedRow: initialSelectedRow = 0, initialDisplayMode = null }) {
    usePerf("CalcProvider");
    const calcId = propsCalcId || initialCalc?.id;
    const calcFromStore = useCalcFromStore(calcId);
    const calc = calcFromStore || initialCalc;
    const [selectedRow, setSelectedRow] = useState22(initialSelectedRow);
    const [isReady, setIsReady] = useState22(true);
    const Action = useAction();
    const [displayMode, setDisplayModeInternal] = useState22(() => {
      if (initialDisplayMode === "text") {
        return DisplayMode.TEXT;
      }
      if (initialDisplayMode === "present") {
        return DisplayMode.PRESENT;
      }
      if (initialDisplayMode === "calc") {
        return DisplayMode.REGULAR;
      }
      if (typeof window !== "undefined") {
        const viewParam = normalizeViewParam(getParamFromLocation("view"));
        if (viewParam === "text") return DisplayMode.TEXT;
        if (viewParam === "present") return DisplayMode.PRESENT;
        if (viewParam === "calc") return DisplayMode.REGULAR;
      }
      if (Action && calc) {
        const savedViewMode = Action.getCalcViewMode(calc);
        if (savedViewMode === "text") return DisplayMode.TEXT;
        if (savedViewMode === "present") return DisplayMode.PRESENT;
        if (savedViewMode === "calc") return DisplayMode.REGULAR;
      }
      return DisplayMode.REGULAR;
    });
    const setDisplayMode = useCallback16((mode) => {
      setDisplayModeInternal(mode);
    }, []);
    useEffect23(() => {
      if (typeof window !== "undefined") {
        window.__instacalcTextEditMode = displayMode === DisplayMode.TEXT;
        window.__instacalcDisplayMode = displayMode;
      }
    }, [displayMode]);
    const hasUserSetModeRef = useRef19(false);
    const wrappedSetDisplayMode = useCallback16((mode) => {
      hasUserSetModeRef.current = true;
      setDisplayMode(mode);
    }, [setDisplayMode]);
    const hadInitialDisplayModeRef = useRef19(initialDisplayMode !== null);
    const hasAutoFocusedRef = useRef19(false);
    useEffect23(() => {
      if (calc && calc.rows && !hasAutoFocusedRef.current && viewMode === "calc") {
        let firstFocusable = 0;
        for (let i = 0; i < calc.rows.length; i++) {
          if (!calc.rows[i].hidden) {
            firstFocusable = i;
            break;
          }
        }
        if (firstFocusable !== selectedRow) {
          setSelectedRow(firstFocusable);
        }
        hasAutoFocusedRef.current = true;
      }
    }, [calc, viewMode, selectedRow]);
    useEffect23(() => {
      if (!calc || !Array.isArray(calc.rows)) return;
      const rowCount = calc.rows.length;
      const clamped = rowCount > 0 ? Math.max(0, Math.min(selectedRow, rowCount - 1)) : 0;
      if (clamped !== selectedRow) {
        setSelectedRow(clamped);
      }
    }, [calc?.rows?.length, selectedRow]);
    useEffect23(() => {
      if (hasUserSetModeRef.current) return;
      if (hadInitialDisplayModeRef.current) return;
      if (typeof window !== "undefined") {
        if (getParamFromLocation("view")) return;
      }
      if (Action && calc) {
        const savedViewMode = Action.getCalcViewMode(calc);
        if (savedViewMode === "text" || savedViewMode === "present") {
          setDisplayMode(savedViewMode === "text" ? DisplayMode.TEXT : DisplayMode.PRESENT);
          return;
        }
        if (savedViewMode === "calc") return;
      }
      if (viewMode !== "calc" && calc && hasPresentationDecorators(calc)) {
        setDisplayMode(DisplayMode.PRESENT);
      }
    }, [calc, Action, viewMode]);
    const resultCache = useResults(calcId);
    const stableResultsRef = useRef19([]);
    const results = useMemo11(() => {
      const rowCount = calc?.rows?.length ?? 0;
      const newResults = [];
      for (let i = 0; i < rowCount; i++) {
        const incoming = resultCache[i] || BLANK_RESULT;
        const cached = stableResultsRef.current[i];
        if (cached && Result.areEqual(cached, incoming)) {
          newResults.push(cached);
        } else {
          newResults.push(incoming);
        }
      }
      stableResultsRef.current = newResults;
      return newResults;
    }, [calc?.rows?.length, resultCache]);
    const setResults = useCallback16((newResults) => {
      if (typeof window !== "undefined" && window.ReactPerfMetrics?.enabled) {
        window.ReactPerfMetrics.logRender("CalcProvider:setResults", 0, { type: "state-update" });
      }
      if (typeof window !== "undefined" && window.resultsStore && calcId) {
        const value2 = typeof newResults === "function" ? newResults(window.resultsStore.get(calcId)) : newResults;
        window.resultsStore.set(calcId, value2);
      }
    }, [calcId]);
    useEffect23(() => {
      if (Action) {
        try {
          Action.selectedRow = selectedRow;
        } catch (e) {
        }
      }
      if (typeof window !== "undefined" && window.Action) {
        try {
          window.Action.selectedRow = selectedRow;
        } catch (e) {
        }
      }
      if (typeof window !== "undefined" && window.InstacalcEvents && calcId) {
        window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_ROW_SELECT, {
          calcId,
          rowIndex: selectedRow,
          source: "react"
        });
      }
    }, [selectedRow, calcId]);
    useEffect23(() => {
      if (Action && calc && hasComputedResults(results)) {
        Action.setResults(calc, results);
      }
    }, [results, Action, calc]);
    const strategy = useMemo11(() => getViewStrategy(viewMode), [viewMode]);
    const value = useMemo11(() => ({
      calc,
      calcId,
      viewMode,
      strategy,
      selectedRow,
      setSelectedRow,
      results,
      setResults,
      isReady,
      setIsReady,
      displayMode,
      setDisplayMode: wrappedSetDisplayMode
    }), [calc, calcId, viewMode, strategy, selectedRow, results, isReady, displayMode, wrappedSetDisplayMode]);
    return /* @__PURE__ */ (0, import_jsx_runtime35.jsx)(CalcContext.Provider, { value, children });
  }
  __name(CalcProvider, "CalcProvider");
  function useCalc() {
    const context = useContext6(CalcContext);
    return context;
  }
  __name(useCalc, "useCalc");
  var CalcContext_default = CalcContext;

  // public/src/react/constants/ModalEvents.js
  var MODAL_EVENTS = {
    OPEN_HISTORY: "instacalc:openHistory",
    OPEN_GOALSEEK: "instacalc:openGoalseek",
    OPEN_FORMAT: "instacalc:openFormat",
    UPDATE_TEXT_VIEW: "instacalc:updateTextView",
    UPDATE_EMBED_CALC: "instacalc:updateEmbedCalc"
  };

  // public/src/react/components/common/HistoryModal.jsx
  init_react_global();
  init_Icon();

  // public/src/react/components/common/HistoryCalcPreview.jsx
  init_react_global();

  // public/src/react/components/common/ResultContent.jsx
  init_react_global();
  var import_jsx_runtime36 = __toESM(require_jsx_runtime());
  var hasResultContent = Result.hasContent;
  var hasComment2 = Result.hasComment;
  var hasType2 = Result.hasType;
  var formatResult = Result.prettyPrint;
  function ResultValue({ result, className, onClick }) {
    if (!hasResultContent(result)) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime36.jsx)(
      "result",
      {
        className: cn("inline cursor-pointer break-words break-anywhere", className),
        onClick,
        children: formatResult(result)
      }
    );
  }
  __name(ResultValue, "ResultValue");
  function ResultType({ result, className }) {
    if (!hasType2(result)) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime36.jsx)("type", { className: cn("flex-shrink-0 inline cursor-pointer whitespace-nowrap", className), children: Result.getUnits(result) });
  }
  __name(ResultType, "ResultType");
  function ResultComment({ result, className }) {
    if (!hasComment2(result)) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime36.jsx)("comment", { className: cn("flex-shrink-0 inline break-words cursor-pointer break-anywhere", className), children: Result.getComment(result) });
  }
  __name(ResultComment, "ResultComment");
  function ResultContent({
    result,
    sourceText = "",
    resultAlignment = "left",
    className = "",
    classNames = {},
    onClick,
    showSourceForComments = true
  }) {
    const hasResult = hasResultContent(result);
    const trimmedSource = sourceText?.trim() || "";
    const isCommentOnlyLine = trimmedSource.startsWith("//") && !hasResult;
    const isBlankLine = trimmedSource === "" && !hasResult;
    const flexDirection = resultAlignment === "right" ? "flex-row-reverse" : "flex-row";
    return /* @__PURE__ */ (0, import_jsx_runtime36.jsxs)("div", { className: cn("flex items-center min-w-0 max-w-full space-x-2", flexDirection, className), children: [
      /* @__PURE__ */ (0, import_jsx_runtime36.jsx)(
        ResultValue,
        {
          result,
          className: cn(
            resultAlignment === "right" ? "flex-auto text-right" : "flex-shrink-0",
            classNames.result
          ),
          onClick
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime36.jsx)(ResultType, { result, className: cn("ml-1", classNames.type) }),
      /* @__PURE__ */ (0, import_jsx_runtime36.jsx)(ResultComment, { result, className: cn("text-left", classNames.comment) }),
      showSourceForComments && isCommentOnlyLine && !hasComment2(result) && /* @__PURE__ */ (0, import_jsx_runtime36.jsxs)("span", { className: "opacity-40 italic text-xs", children: [
        trimmedSource.slice(0, 30),
        trimmedSource.length > 30 ? "..." : ""
      ] }),
      isBlankLine && /* @__PURE__ */ (0, import_jsx_runtime36.jsx)("span", { className: "opacity-20", children: "\xA0" })
    ] });
  }
  __name(ResultContent, "ResultContent");
  var ResultContent_default = ResultContent;

  // public/src/react/components/common/HistoryCalcPreview.jsx
  var import_jsx_runtime37 = __toESM(require_jsx_runtime());
  var { useState: useState23, useEffect: useEffect24 } = react_global_default;
  function HistoryCalcPreview({ snapshot, rawEntry }) {
    const [results, setResults] = useState23([]);
    const executeSnapshot = /* @__PURE__ */ __name(() => {
      if (!snapshot || !snapshot.rows) {
        setResults([]);
        return;
      }
      try {
        const rowTexts = snapshot.rows.filter((row) => row).map((row) => row.text || "");
        const resultData = IC.parser.executeRows(rowTexts);
        const resultsData = resultData.results || resultData || [];
        setResults(Array.isArray(resultsData) ? resultsData : []);
      } catch (e) {
        console.error("Error executing history snapshot:", e);
        setResults([]);
      }
    }, "executeSnapshot");
    useEffect24(() => {
      executeSnapshot();
    }, [snapshot]);
    const getChangedRows = /* @__PURE__ */ __name(() => {
      const changedRows2 = /* @__PURE__ */ new Map();
      if (!rawEntry || !rawEntry.patch || rawEntry.patch.type !== "patch") {
        return changedRows2;
      }
      const patches = rawEntry.patch.patches || [];
      patches.forEach((patch) => {
        const rowMatch = patch.path.match(/^\/rows\/(\d+)/);
        if (rowMatch) {
          const rowIndex = parseInt(rowMatch[1]);
          changedRows2.set(rowIndex, patch.op);
        }
      });
      return changedRows2;
    }, "getChangedRows");
    const getOpDotColor = /* @__PURE__ */ __name((op) => {
      switch (op) {
        case "add":
          return "bg-theme-status-public";
        case "remove":
          return "bg-theme-calc-result";
        case "replace":
          return "bg-theme-status-private";
        default:
          return "";
      }
    }, "getOpDotColor");
    if (!snapshot || !snapshot.rows) {
      return /* @__PURE__ */ (0, import_jsx_runtime37.jsx)("div", { className: "text-sm opacity-50 text-color-default", children: "No data to display" });
    }
    const rows = snapshot.rows.filter((row) => row);
    const lineCount = rows.length;
    const changedRows = getChangedRows();
    return /* @__PURE__ */ (0, import_jsx_runtime37.jsxs)("div", { className: "flex gap-2 text-sm", children: [
      /* @__PURE__ */ (0, import_jsx_runtime37.jsxs)("div", { className: "flex w-1/2 min-w-0", children: [
        /* @__PURE__ */ (0, import_jsx_runtime37.jsx)("div", { className: "select-none pr-2 text-[68.75%] opacity-60", children: rows.map((row, idx) => {
          const op = changedRows.get(idx);
          const dotColor = op ? getOpDotColor(op) : "";
          return /* @__PURE__ */ (0, import_jsx_runtime37.jsxs)("div", { style: { height: "27px", lineHeight: "27px" }, className: "relative", children: [
            dotColor && /* @__PURE__ */ (0, import_jsx_runtime37.jsx)("span", { className: `absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-1 h-1 rounded-full ${dotColor}` }),
            /* @__PURE__ */ (0, import_jsx_runtime37.jsxs)("span", { children: [
              "R",
              idx + 1
            ] })
          ] }, idx);
        }) }),
        /* @__PURE__ */ (0, import_jsx_runtime37.jsx)("div", { className: "flex-1 min-w-0 pr-2 border-r text-color-default border-soft", children: rows.map((row, idx) => /* @__PURE__ */ (0, import_jsx_runtime37.jsx)(
          "div",
          {
            style: { height: "27px", lineHeight: "27px" },
            className: "overflow-x-auto whitespace-nowrap no-scrollbar",
            children: row.text || ""
          },
          idx
        )) })
      ] }),
      /* @__PURE__ */ (0, import_jsx_runtime37.jsx)("div", { className: "w-1/2 min-w-0 pl-2", children: results.map((result, idx) => /* @__PURE__ */ (0, import_jsx_runtime37.jsxs)(
        "div",
        {
          style: { height: "27px", lineHeight: "27px" },
          className: "flex items-center gap-2 overflow-x-auto text-color-default whitespace-nowrap no-scrollbar",
          children: [
            hasResultContent(result) && /* @__PURE__ */ (0, import_jsx_runtime37.jsx)("result", { className: "flex-shrink-0", children: LIB.prettyPrintResultHTML(
              result.output?.display ?? result.output?.raw,
              result.output?.raw
            ) }),
            /* @__PURE__ */ (0, import_jsx_runtime37.jsx)(ResultType, { result, className: "opacity-60" }),
            /* @__PURE__ */ (0, import_jsx_runtime37.jsx)(ResultComment, { result, className: "opacity-75" })
          ]
        },
        idx
      )) })
    ] });
  }
  __name(HistoryCalcPreview, "HistoryCalcPreview");
  var HistoryCalcPreview_default = HistoryCalcPreview;

  // public/src/react/components/common/HistoryChangeSummary.jsx
  init_react_global();
  var import_jsx_runtime38 = __toESM(require_jsx_runtime());
  function HistoryChangeSummary({ rawEntry, label }) {
    const changeLabel = label || "What changed";
    if (!rawEntry || !rawEntry.patch) {
      return /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "overflow-hidden border rounded-lg border-soft bg-secondary", children: /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "p-3 text-sm opacity-75 text-color-default", children: "Loading..." }) });
    }
    const { patch } = rawEntry;
    if (patch.type === "full") {
      return /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "overflow-hidden border rounded-lg border-soft bg-secondary", children: /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "p-3 text-sm opacity-75 text-color-default", children: "Initial state (no previous version)" }) });
    }
    const patches = patch.patches || [];
    if (patches.length === 0) {
      return /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "overflow-hidden border rounded-lg border-soft bg-secondary", children: /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "p-3 text-sm opacity-75 text-color-default", children: "No changes" }) });
    }
    const formatPatch = /* @__PURE__ */ __name((patch2) => {
      const { op, path, value } = patch2;
      const rowObjectMatch = path.match(/^\/rows\/(\d+)$/);
      const rowFieldMatch = path.match(/^\/rows\/(\d+)\/(.+)$/);
      if (rowObjectMatch) {
        const rowIndex = parseInt(rowObjectMatch[1]);
        const rowNumber = rowIndex + 1;
        if (op === "add") {
          if (value && value.text && value.text.trim()) {
            return { label: `R${rowNumber}`, value: value.text, icon: "+" };
          }
          return { label: `R${rowNumber} added`, value: null, icon: "+" };
        } else if (op === "remove") {
          return { label: `R${rowNumber} removed`, value: null, icon: "\u2212" };
        } else if (op === "replace" && typeof value === "object" && value !== null) {
          if (!value.text || value.text.trim() === "") {
            return { label: `R${rowNumber} cleared`, value: null, icon: "\xD7" };
          }
          return { label: `R${rowNumber}`, value: value.text, icon: "~" };
        }
      }
      if (rowFieldMatch) {
        const rowIndex = parseInt(rowFieldMatch[1]);
        const rowNumber = rowIndex + 1;
        const field = rowFieldMatch[2];
        if (field === "text") {
          if (value === "" || value === null) {
            return { label: `R${rowNumber} cleared`, value: null, icon: "\xD7" };
          }
          return { label: `R${rowNumber}`, value, icon: "~" };
        } else if (field === "hidden") {
          const visibility = value ? "hidden" : "visible";
          return { label: `R${rowNumber}`, value: visibility, icon: "\u25D0" };
        }
        return { label: `R${rowNumber} \u2192 ${field}`, value, icon: "~" };
      }
      const label2 = path.replace(/^\//, "").replace(/\//g, " \u2192 ");
      return { label: label2, value, icon: null };
    }, "formatPatch");
    const formatValue = /* @__PURE__ */ __name((value) => {
      if (value === null || value === void 0) return "";
      if (typeof value === "object") {
        return JSON.stringify(value);
      }
      return String(value);
    }, "formatValue");
    const getOpColor = /* @__PURE__ */ __name((op) => {
      switch (op) {
        case "add":
          return "text-color-active";
        case "remove":
          return "text-color-muted";
        case "replace":
          return "text-color-calc";
        default:
          return "text-color-default";
      }
    }, "getOpColor");
    const getOpSymbol = /* @__PURE__ */ __name((op) => {
      switch (op) {
        case "add":
          return "+";
        case "remove":
          return "\u2212";
        case "replace":
          return "~";
        default:
          return "\u2022";
      }
    }, "getOpSymbol");
    return /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "overflow-hidden border rounded-lg border-soft bg-secondary", children: /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "p-3 font-mono text-[13px] leading-relaxed", children: /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("div", { className: "grid grid-cols-[1rem_1fr] gap-x-3 gap-y-1", children: patches.map((p, idx) => {
      const formatted = formatPatch(p);
      const icon = formatted.icon || getOpSymbol(p.op);
      const color = formatted.icon ? getOpColor(p.op) : getOpColor(p.op);
      return [
        /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("span", { className: color, children: icon }, idx + "-op"),
        /* @__PURE__ */ (0, import_jsx_runtime38.jsxs)("code", { className: "text-color-default", children: [
          formatted.label,
          formatted.value !== null && formatted.value !== void 0 && /* @__PURE__ */ (0, import_jsx_runtime38.jsxs)("span", { children: [
            " \u2192 ",
            /* @__PURE__ */ (0, import_jsx_runtime38.jsx)("span", { className: "font-semibold", children: formatValue(formatted.value) })
          ] })
        ] }, idx + "-code")
      ];
    }) }) }) });
  }
  __name(HistoryChangeSummary, "HistoryChangeSummary");
  var HistoryChangeSummary_default = HistoryChangeSummary;

  // public/src/react/components/common/HistoryModal.jsx
  var import_jsx_runtime39 = __toESM(require_jsx_runtime());
  var { useState: useState24, useEffect: useEffect25, useContext: useContext7 } = react_global_default;
  function HistoryModal({ id, clickCount, open = false, onOpenChange = /* @__PURE__ */ __name(() => {
  }, "onOpenChange"), Action: ActionProp, calc: calcProp }) {
    const stack = new Error().stack?.split("\n").slice(1, 4).join("\n") || "no stack";
    console.log("[HistoryModal] Rendering, open=", open, "ActionProp=", !!ActionProp, "calcProp=", !!calcProp, "\nStack:", stack);
    const contextAction = useContext7(ActionContext_default);
    const contextCalc = useContext7(CalcContext_default);
    const [history, setHistory] = useState24([]);
    const [selectedIndex, setSelectedIndex] = useState24(null);
    const [previewSnapshot, setPreviewSnapshot] = useState24(null);
    const [rawHistoryEntry, setRawHistoryEntry] = useState24(null);
    const [showChanges, setShowChanges] = useState24(false);
    const [showRawJson, setShowRawJson] = useState24(false);
    const Action = ActionProp || contextAction;
    const calc = calcProp || contextCalc?.calc;
    console.log("[HistoryModal] After deriving - Action=", !!Action, "calc=", !!calc);
    const toggleChanges = /* @__PURE__ */ __name(() => {
      setShowChanges(!showChanges);
    }, "toggleChanges");
    const toggleRawJson = /* @__PURE__ */ __name(() => {
      setShowRawJson(!showRawJson);
    }, "toggleRawJson");
    const getChangeCount = /* @__PURE__ */ __name((rawEntry) => {
      if (!rawEntry || !rawEntry.patch) return 0;
      if (rawEntry.patch.type === "full") return "Full snapshot";
      const patches = rawEntry.patch.patches || [];
      return patches.length;
    }, "getChangeCount");
    const getChangesFromHistory = /* @__PURE__ */ __name((displayIndex) => {
      if (displayIndex === 0) {
        const manager = Action.getHistoryManager(calc);
        if (!manager || !manager.canUndo()) return null;
        return manager.getDiffToCurrent(0);
      }
      return rawHistoryEntry;
    }, "getChangesFromHistory");
    const loadHistory = /* @__PURE__ */ __name(() => {
      if (!Action || !Action.getHistory) {
        console.error("Action or getHistory not available");
        return;
      }
      const historyStack = Action.getHistory();
      const liveEntry = {
        index: -1,
        timestamp: (/* @__PURE__ */ new Date()).toISOString(),
        operation: "current",
        isLive: true
      };
      const historyData = [liveEntry, ...historyStack];
      const nextSelectedIndex = historyStack.length > 0 ? 1 : 0;
      setHistory(historyData);
      setSelectedIndex(nextSelectedIndex);
      setPreviewSnapshot(null);
      setTimeout(() => {
        loadPreview(nextSelectedIndex);
      }, 0);
    }, "loadHistory");
    const loadPreview = /* @__PURE__ */ __name((displayIndex) => {
      if (!Action || !Action.getHistorySnapshot) {
        return;
      }
      const entry = history[displayIndex];
      if (entry && entry.isLive) {
        setSelectedIndex(displayIndex);
        setPreviewSnapshot(calc);
        setRawHistoryEntry(null);
        return;
      }
      const historyIndex = displayIndex - 1;
      const snapshot = Action.getHistorySnapshot(historyIndex);
      const rawEntry = Action.getHistoryEntry ? Action.getHistoryEntry(historyIndex) : null;
      setSelectedIndex(displayIndex);
      setPreviewSnapshot(snapshot);
      setRawHistoryEntry(rawEntry);
    }, "loadPreview");
    const formatTime = /* @__PURE__ */ __name((timestamp) => {
      const date = new Date(timestamp);
      return date.toLocaleString();
    }, "formatTime");
    const groupHistoryByTime = /* @__PURE__ */ __name((historyData) => {
      const now = /* @__PURE__ */ new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const groups = {
        "Today": [],
        "Yesterday": [],
        "Last 7 days": [],
        "Earlier": []
      };
      history.forEach((entry) => {
        const entryDate = new Date(entry.timestamp);
        const entryDay = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
        if (entryDay.getTime() === today.getTime()) {
          groups["Today"].push(entry);
        } else if (entryDay.getTime() === yesterday.getTime()) {
          groups["Yesterday"].push(entry);
        } else if (entryDate >= sevenDaysAgo) {
          groups["Last 7 days"].push(entry);
        } else {
          groups["Earlier"].push(entry);
        }
      });
      Object.keys(groups).forEach((key) => {
        if (groups[key].length === 0) {
          delete groups[key];
        }
      });
      return groups;
    }, "groupHistoryByTime");
    const formatHistoryTime = /* @__PURE__ */ __name((timestamp, groupName) => {
      const date = new Date(timestamp);
      if (groupName === "Today") {
        return date.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit"
        });
      } else if (groupName === "Yesterday") {
        return date.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit"
        });
      } else if (groupName === "Last 7 days") {
        const dayName = date.toLocaleDateString([], { weekday: "short" });
        const time = date.toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit"
        });
        return `${dayName} ${time}`;
      } else {
        return date.toLocaleDateString([], {
          day: "numeric",
          month: "2-digit",
          year: "2-digit"
        });
      }
    }, "formatHistoryTime");
    const restoreToVersion = /* @__PURE__ */ __name((displayIndex) => {
      if (!Action || !Action.jumpToHistory) {
        return;
      }
      const historyIndex = displayIndex - 1;
      Action.jumpToHistory(historyIndex);
      loadHistory();
      onOpenChange(false);
    }, "restoreToVersion");
    useEffect25(() => {
      if (!open) return;
      loadHistory();
    }, [clickCount, open, calc?.updated]);
    if (!Action || !calc) {
      return /* @__PURE__ */ (0, import_jsx_runtime39.jsx)(
        Modal_default,
        {
          title: "Revision History",
          closeIcon: true,
          open,
          onOpenChange,
          classNames: { body: "p-0" },
          children: /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "flex items-center justify-center h-64 text-sm opacity-50 text-color-default", children: "Loading..." })
        }
      );
    }
    return /* @__PURE__ */ (0, import_jsx_runtime39.jsx)(
      Modal_default,
      {
        title: "Revision History",
        closeIcon: true,
        open,
        onOpenChange,
        classNames: { body: "p-0" },
        children: /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("div", { className: "flex flex-col md:flex-row overflow-hidden no-scrollbar h-[80vh] md:h-[500px]", "data-id": "history-modal-content", children: [
          /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "w-full p-4 overflow-y-auto border-b h-1/3 md:h-auto md:w-1/3 md:border-b-0 border-soft", children: history.length === 0 ? /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "py-8 text-sm text-center opacity-50 text-color-default", children: "No history yet" }) : (() => {
            const groupedHistory = groupHistoryByTime(history);
            return /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { children: Object.entries(groupedHistory).map(([groupName, entries]) => /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("div", { className: "mb-4", children: [
              /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "px-2 py-2 text-xs font-semibold tracking-wide uppercase opacity-60 text-color-default", children: groupName }),
              /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "space-y-2", children: entries.map((entry) => {
                const actualIndex = history.indexOf(entry);
                const isSelected = selectedIndex === actualIndex;
                const isLive = entry.isLive;
                let changeCount = 0;
                if (!isLive) {
                  const historyIndex = actualIndex - 1;
                  const rawEntry = Action.getHistoryEntry ? Action.getHistoryEntry(historyIndex) : null;
                  changeCount = getChangeCount(rawEntry);
                }
                return /* @__PURE__ */ (0, import_jsx_runtime39.jsx)(
                  "div",
                  {
                    className: `p-3 border rounded-lg cursor-pointer transition-colors ${isSelected ? "border-soft bg-default" : "border-soft hover-bg-button"}`,
                    onClick: () => loadPreview(actualIndex),
                    children: /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("div", { className: "flex items-center justify-between gap-2", children: [
                      /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "text-sm font-medium text-color-default", children: formatHistoryTime(entry.timestamp, groupName) }),
                      isLive ? /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("span", { className: "px-2 py-0.5 text-xs rounded-full border border-soft bg-navlink text-color-calc flex-shrink-0", children: "current" }) : changeCount > 0 && /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("span", { className: "flex items-center flex-shrink-0 gap-1 text-xs opacity-50 text-color-default", children: [
                        changeCount,
                        Icon_default.delta("w-3 h-3")
                      ] })
                    ] })
                  },
                  actualIndex
                );
              }) })
            ] }, groupName)) });
          })() }),
          /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "flex-1 p-4 overflow-y-auto h-2/3 md:h-auto bg-primary no-scrollbar", children: previewSnapshot ? /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("div", { children: [
            /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("div", { className: "flex items-center justify-between h-10 mb-4", children: [
              /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("h4", { className: "text-sm font-semibold text-color-default", children: previewSnapshot.name || "Untitled" }),
              selectedIndex > 0 && /* @__PURE__ */ (0, import_jsx_runtime39.jsx)(
                "button",
                {
                  className: "inline-flex items-center justify-center gap-2 px-2 py-1 text-sm font-semibold transition-all border border-transparent rounded-md bg-theme-calc-result text-bg hover:brightness-110",
                  onClick: () => restoreToVersion(selectedIndex),
                  children: "Restore this version"
                }
              )
            ] }),
            /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "p-3 mb-4 border rounded-lg border-soft bg-secondary", children: /* @__PURE__ */ (0, import_jsx_runtime39.jsx)(
              HistoryCalcPreview_default,
              {
                snapshot: previewSnapshot,
                rawEntry: getChangesFromHistory(selectedIndex)
              }
            ) }),
            selectedIndex >= 0 && /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("div", { className: "mb-4", children: [
              /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)(
                "button",
                {
                  onClick: toggleChanges,
                  className: "flex items-center gap-2 mb-2 text-sm transition-opacity text-color-default opacity-80 hover:opacity-100",
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: (showChanges ? "" : "-rotate-90") + " transform transition duration-150", children: Icon_default.chevronDown("w-5 h-5") }),
                    /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("span", { children: (() => {
                      const diffEntry = getChangesFromHistory(selectedIndex);
                      const count3 = getChangeCount(diffEntry);
                      if (count3 === "Full snapshot") return count3;
                      if (count3 === 0) return "No changes";
                      return `${count3} ${count3 === 1 ? "change" : "changes"} from previous version`;
                    })() })
                  ]
                }
              ),
              showChanges && /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("div", { children: [
                /* @__PURE__ */ (0, import_jsx_runtime39.jsx)(
                  HistoryChangeSummary_default,
                  {
                    rawEntry: getChangesFromHistory(selectedIndex),
                    label: selectedIndex === 0 ? "Current changes" : "What changed"
                  }
                ),
                rawHistoryEntry && /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)("div", { className: "hidden mt-3", children: [
                  /* @__PURE__ */ (0, import_jsx_runtime39.jsxs)(
                    "button",
                    {
                      onClick: toggleRawJson,
                      className: "text-xs underline transition-opacity opacity-50 text-color-default hover:opacity-80",
                      children: [
                        showRawJson ? "Hide" : "View",
                        " raw history entry"
                      ]
                    }
                  ),
                  showRawJson && /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "mt-2 overflow-hidden border rounded-lg border-soft bg-primary", children: /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("pre", { className: "p-3 overflow-auto text-xs leading-relaxed max-h-72", children: /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("code", { className: "text-color-default", children: JSON.stringify(rawHistoryEntry, null, 2) }) }) })
                ] })
              ] })
            ] })
          ] }) : /* @__PURE__ */ (0, import_jsx_runtime39.jsx)("div", { className: "py-8 text-sm text-center opacity-50 text-color-default", children: "Select a history entry to preview" }) })
        ] })
      }
    );
  }
  __name(HistoryModal, "HistoryModal");
  var HistoryModal_default = HistoryModal;

  // public/src/react/components/common/GoalseekModal.jsx
  init_react_global();
  var import_jsx_runtime40 = __toESM(require_jsx_runtime());
  var { useState: useState25, useEffect: useEffect26, useRef: useRef20, useCallback: useCallback17 } = react_global_default;
  function GoalseekModal({ clickCount, title, open = false, onOpenChange = /* @__PURE__ */ __name(() => {
  }, "onOpenChange") }) {
    const Action = useAction();
    const { calc, results: contextResults } = useCalc();
    const [targetVariable, setTargetVariable] = useState25("");
    const [targetValue, setTargetValue] = useState25("");
    const [adjustmentVars, setAdjustmentVars] = useState25({});
    const [computedVars, setComputedVars] = useState25({});
    const [gsResult, setGsResult] = useState25(null);
    const [dependencyMap, setDependencyMap] = useState25({});
    const [newRowContents, setNewRowContents] = useState25(null);
    const pendingTimeoutsRef = useRef20(/* @__PURE__ */ new Set());
    const scheduleTimeout = useCallback17((fn, delay) => {
      const timeoutId = setTimeout(() => {
        pendingTimeoutsRef.current.delete(timeoutId);
        fn();
      }, delay);
      pendingTimeoutsRef.current.add(timeoutId);
      return timeoutId;
    }, []);
    useEffect26(() => {
      return () => {
        pendingTimeoutsRef.current.forEach((id) => clearTimeout(id));
        pendingTimeoutsRef.current.clear();
      };
    }, []);
    const loadVariables = /* @__PURE__ */ __name(() => {
      if (!calc) return;
      const results = contextResults;
      if (!results || results.length === 0) return;
      const newAdjustmentVars = {};
      const newComputedVars = {};
      results.forEach((r, idx) => {
        let rowName = r.display?.label;
        let isRawNumeric = false;
        if (!rowName && IC.api.canIncrement(r.input?.original)) {
          rowName = `r${idx + 1}`;
          isRawNumeric = true;
        }
        if (!rowName) return;
        const rowParameter = IC.api.getParameterName(r.input?.original);
        if (rowParameter || isRawNumeric) {
          newAdjustmentVars[rowParameter || rowName] = {
            selected: false,
            currentValue: r.output?.value,
            solvedValue: null
          };
        } else {
          newComputedVars[rowName] = r.output?.value;
        }
      });
      const firstComputedVar = Object.keys(newComputedVars)[0];
      setAdjustmentVars(newAdjustmentVars);
      setComputedVars(newComputedVars);
      setTargetVariable(firstComputedVar || "");
      setTargetValue(firstComputedVar ? newComputedVars[firstComputedVar] : "");
      if (firstComputedVar) {
        scheduleTimeout(() => computeDependencies(firstComputedVar), 0);
      }
    }, "loadVariables");
    const computeDependencies = /* @__PURE__ */ __name((targetVar) => {
      if (!targetVar) return;
      const cloneCalc = Object.assign({}, calc);
      const newRowContents2 = `solve ${targetVar} = ${targetValue || "0"}`;
      cloneCalc.rows = [...cloneCalc.rows, { text: newRowContents2 }];
      const newResults = Action.recalculate(cloneCalc);
      const result = newResults[newResults.length - 1]?.output?.raw;
      if (!result?.dependentVars) return;
      const updatedAdjustmentVars = { ...adjustmentVars };
      Object.keys(updatedAdjustmentVars).forEach((varName) => {
        updatedAdjustmentVars[varName] = {
          ...updatedAdjustmentVars[varName],
          selected: result.dependentVars.includes(varName),
          solvedValue: null
        };
      });
      setDependencyMap((prev) => ({
        ...prev,
        [targetVar]: result.dependentVars
      }));
      setAdjustmentVars(updatedAdjustmentVars);
      if (targetValue) {
        scheduleTimeout(() => computeGoalseek(), 0);
      }
    }, "computeDependencies");
    const computeGoalseek = /* @__PURE__ */ __name(() => {
      if (!targetVariable || !targetValue) return;
      const selectedVars = Object.entries(adjustmentVars).filter(([_, details]) => details.selected).map(([varName]) => varName);
      const cloneCalc = Object.assign({}, calc);
      const newRowContents2 = `solve ${targetVariable} = ${targetValue.replaceAll(",", "")} using ${selectedVars.join(", ")}`;
      cloneCalc.rows = [...cloneCalc.rows, { text: newRowContents2 }];
      const newResults = Action.recalculate(cloneCalc);
      const gsResultValue = newResults[newResults.length - 1].result;
      const updatedAdjustmentVars = { ...adjustmentVars };
      if (gsResultValue?.vars) {
        Object.entries(gsResultValue.vars).forEach(([varName, value]) => {
          if (updatedAdjustmentVars[varName]) {
            updatedAdjustmentVars[varName].solvedValue = value;
          }
        });
      }
      setGsResult(gsResultValue);
      setAdjustmentVars(updatedAdjustmentVars);
      setNewRowContents(newRowContents2);
    }, "computeGoalseek");
    const handleVariableChange = /* @__PURE__ */ __name((e) => {
      const newTarget = e.target.value;
      setTargetVariable(newTarget);
      setGsResult(null);
      scheduleTimeout(() => computeDependencies(newTarget), 0);
    }, "handleVariableChange");
    const handleTargetChange = /* @__PURE__ */ __name((e) => {
      setTargetValue(e.target.value);
      setGsResult(null);
      scheduleTimeout(() => computeGoalseek(), 0);
    }, "handleTargetChange");
    const toggleAdjustmentVar = /* @__PURE__ */ __name((variable) => {
      setAdjustmentVars((prev) => ({
        ...prev,
        [variable]: {
          ...prev[variable],
          selected: !prev[variable].selected
        }
      }));
      setGsResult(null);
      scheduleTimeout(() => computeGoalseek(), 0);
    }, "toggleAdjustmentVar");
    const handleApply = /* @__PURE__ */ __name(() => {
      const varsToSet = {};
      Object.entries(adjustmentVars).forEach(([varName, details]) => {
        varsToSet[varName] = details.selected ? details.solvedValue : details.currentValue;
      });
      LIB.setVariablesInCalc(Action, calc, varsToSet);
      onOpenChange(false);
    }, "handleApply");
    const handleInsertSolution = /* @__PURE__ */ __name(() => {
      Action.addRows(calc?.id, 1);
      Action.updateRowText(calc?.id, calc.rows.length - 1, newRowContents);
    }, "handleInsertSolution");
    const formatNumber = /* @__PURE__ */ __name((parameterName, value = 0) => {
      if (value == null) {
        return null;
      }
      return IC.api.formatNumber(value);
    }, "formatNumber");
    useEffect26(() => {
      if (open) {
        loadVariables();
      }
    }, [open]);
    useEffect26(() => {
      if (open) {
        loadVariables();
      }
    }, [clickCount, open]);
    const dependencies = dependencyMap[targetVariable] || [];
    return /* @__PURE__ */ (0, import_jsx_runtime40.jsx)(
      Modal_default,
      {
        title: title || "Goal Seek (Beta)",
        closeIcon: true,
        open,
        onOpenChange,
        children: /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("div", { className: "px-6 min-h-40 min-w-1/2", "data-id": "goalseek-modal-content", children: [
          /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("div", { className: "flex items-center gap-2 mb-6", children: [
            /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("span", { className: "text-sm", children: "I want" }),
            /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)(
              "select",
              {
                value: targetVariable,
                onChange: handleVariableChange,
                className: "px-3 py-1.5 text-sm border border-soft rounded outline-none min-w-32 bg-theme-bg text-theme-text",
                children: [
                  /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("option", { value: "", children: "Select variable" }),
                  Object.keys(computedVars).map((v) => /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("option", { value: v, children: v }, v))
                ]
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("span", { className: "text-sm", children: "=" }),
            /* @__PURE__ */ (0, import_jsx_runtime40.jsx)(
              "input",
              {
                type: "text",
                value: targetValue,
                onChange: handleTargetChange,
                placeholder: "Enter target value",
                className: "px-3 py-1.5 text-sm border border-soft rounded outline-none min-w-32 bg-theme-bg text-theme-text"
              }
            )
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("div", { children: [
            /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("div", { className: "mb-2 text-sm", children: "By adjusting:" }),
            /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("div", { className: "overflow-hidden border border-soft rounded-lg", children: /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("table", { className: "w-full", children: [
              /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("thead", { className: "bg-theme-bg-alt", children: /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("tr", { children: [
                /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("th", { className: "px-4 py-2 text-xs text-left text-theme-text-muted", children: "Variable" }),
                /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("th", { className: "px-4 py-2 text-xs text-left text-theme-text-muted", children: "Current" }),
                /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("th", { className: "px-4 py-2 text-xs text-left text-theme-text-muted", children: "Solution value" })
              ] }) }),
              /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("tbody", { className: "divide-y divide-[var(--border-color)]", children: Object.entries(adjustmentVars).filter(
                ([varName]) => varName !== targetVariable && dependencies.includes(varName)
              ).map(([varName, details]) => /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("tr", { children: [
                /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("td", { className: "px-4 py-2", children: [
                  /* @__PURE__ */ (0, import_jsx_runtime40.jsx)(
                    "input",
                    {
                      type: "checkbox",
                      checked: details.selected,
                      onChange: () => toggleAdjustmentVar(varName),
                      className: "mr-2 rounded-md"
                    }
                  ),
                  varName
                ] }),
                /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("td", { className: "px-4 py-2 text-sm", children: formatNumber(varName, details.currentValue) }),
                /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("td", { className: "px-4 py-2 text-sm", children: details.selected ? formatNumber(varName, details.solvedValue) : formatNumber(varName, details.currentValue) })
              ] }, varName)) })
            ] }) })
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime40.jsx)("div", { className: "flex items-center justify-center p-3 mb-6 text-xs", children: gsResult?.solved && /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("span", { children: [
            gsResult.errorPct >= 0.01 ? `Solution found (${(100 * (1 - Math.abs(gsResult.errorPct))).toFixed(1)}%)! ${targetVariable} = ` : `Solution found! ${targetVariable} = `,
            formatNumber(targetVariable, gsResult.solvedValue)
          ] }) }),
          /* @__PURE__ */ (0, import_jsx_runtime40.jsxs)("div", { className: "flex justify-end gap-2", children: [
            /* @__PURE__ */ (0, import_jsx_runtime40.jsx)(
              "button",
              {
                type: "button",
                onClick: handleInsertSolution,
                disabled: !targetVariable || !targetValue,
                className: "inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium align-middle transition-all border rounded-md shadow-sm border-soft text-color-default bg-secondary hover-bg-button",
                children: "Insert solution row"
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime40.jsx)(
              "button",
              {
                type: "button",
                onClick: handleApply,
                disabled: !targetVariable || !targetValue,
                className: "inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-all border border-transparent rounded-md bg-theme-calc-result text-bg hover:brightness-110",
                children: "Apply solution"
              }
            )
          ] })
        ] })
      }
    );
  }
  __name(GoalseekModal, "GoalseekModal");
  var GoalseekModal_default = GoalseekModal;

  // public/src/react/components/common/FormatModal.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime41 = __toESM(require_jsx_runtime());
  var { useState: useState26, useEffect: useEffect27, useContext: useContext8 } = react_global_default;
  function FormatModal({ index: index2, buttonProps = {}, customDisplayContent, open: controlledOpen, onOpenChange, modalOnly = false, Action: ActionProp }) {
    const ActionFromContext = useContext8(ActionContext_default);
    const Action = ActionProp || ActionFromContext || (typeof window !== "undefined" ? window.Action : null);
    if (!Action) {
      console.warn("[FormatModal] Action not found in context, props, or window");
    } else {
      console.log("[FormatModal] Action object identified:", {
        hasRecalculate: typeof Action.recalculate === "function",
        source: ActionProp ? "props" : ActionFromContext ? "context" : "window"
      });
    }
    const [formattedValues, setFormattedValues] = useState26({});
    const [inputValues, setInputValues] = useState26({});
    const [localOpen, setLocalOpen] = useState26(false);
    const isControlled = controlledOpen !== void 0;
    const isOpen = isControlled ? controlledOpen : localOpen;
    const handleOpenChange = /* @__PURE__ */ __name((newOpen) => {
      if (!isControlled) {
        setLocalOpen(newOpen);
      }
      if (onOpenChange) {
        onOpenChange(newOpen);
      }
    }, "handleOpenChange");
    const formatConfig = {
      helpText: "Type a format after the calculation:",
      helpExample: "3000 as money => 3K",
      categories: [
        {
          id: "common",
          title: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("span", { children: [
            Icon_default.heart("inline w-4 h-4 mr-2"),
            "Most Common"
          ] }),
          examples: [
            { display: "3000 as money", format: "as money", fallback: "$3K" },
            { display: ".15 as percent", format: "as percent", fallback: "15%" },
            { display: "0x123 as decimal", format: "as decimal", fallback: "291" }
          ]
        },
        {
          id: "currency",
          title: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("span", { children: [
            Icon_default.dollarSign("inline w-5 h-5"),
            "Currency Display"
          ] }),
          examples: [
            { display: "$22/7", format: "$", fallback: "$3.14" },
            { display: "$$22/7", format: "$$", fallback: "3.14" },
            { display: "$300000 as money", format: "as money", fallback: "$300K" }
          ]
        },
        {
          id: "science",
          title: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("span", { children: [
            Icon_default.beaker("inline w-4 h-4 mr-2"),
            "Science & Engineering"
          ] }),
          examples: [
            { display: "15000 as scientific", format: "as scientific", fallback: "1.5e4" },
            { display: "15000 as engineering", format: "as engineering", fallback: "15e3" },
            { display: "3+4i as phasor", format: "as phasor", fallback: "5\u222053.13\xB0" },
            { display: "123456 as 3 sigfig", format: "as 3 sigfig", fallback: "1.23e5" }
          ]
        },
        {
          id: "programming",
          title: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("span", { children: [
            Icon_default.code("inline w-4 h-4 mr-2"),
            "Data & Programming"
          ] }),
          examples: [
            { display: "100 to hex", format: "to hex", fallback: "0x64" },
            { display: "100 to oct", format: "to oct", fallback: "0o144" },
            { display: "100 to bin", format: "to bin", fallback: "0b1100100" },
            { display: "0x64 to dec", format: "to dec", fallback: "100" },
            { display: "pi -> int", format: "-> int", fallback: "3" }
          ]
        },
        {
          id: "rounding",
          title: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("span", { children: [
            Icon_default.reset("inline w-4 h-4 mr-2"),
            "Rounding"
          ] }),
          examples: [
            { display: "3.14159 -> round", format: "-> round", fallback: "3" },
            { display: "3.14159 -> roundup", format: "-> roundup", fallback: "4" },
            { display: "3.14159 -> rounddown", format: "-> rounddown", fallback: "3" },
            { display: "3.14159 as 2 decimal places", format: "as 2 decimal places", fallback: "3.14" }
          ]
        },
        {
          id: "construction",
          title: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("span", { children: [
            Icon_default.cube("inline w-4 h-4 mr-2"),
            "Construction"
          ] }),
          examples: [
            { display: ".15 as fraction", format: "as fraction", fallback: "3/20" },
            { display: ".625 as fraction over 16", format: "as fraction over 16", fallback: "10/16" },
            { display: "3.5 inches as tape", format: "as tape", fallback: '3 1/2"' }
          ]
        },
        {
          id: "utilities",
          title: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("span", { children: [
            Icon_default.wrench("inline w-4 h-4 mr-2"),
            "Utilities"
          ] }),
          examples: [
            { display: "100 cm -> value", format: "-> value", fallback: "100" },
            { display: "100 cm -> unit", format: "-> unit", fallback: "1 cm" },
            { display: "$12345.67 as noformat", format: "as noformat", fallback: "12345.67" },
            { display: "10000 s as time", format: "as time", fallback: "2h 46m 40s" },
            { display: "12345 as nearest 1000", format: "as nearest 1000", fallback: "12000" }
          ]
        },
        {
          id: "fun",
          title: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("span", { children: [
            Icon_default.puzzlePiece("inline w-4 h-4 mr-2"),
            "Fun & Special"
          ] }),
          examples: [
            { display: "42 as roman", format: "as roman", fallback: "XLII" },
            { display: "100 N as sibase", format: "as sibase", fallback: "100 kg\u22C5m\u22C5s\u207B\xB2" }
          ]
        }
      ]
    };
    const handleInputChange = /* @__PURE__ */ __name((categoryId, exampleIndex, value) => {
      const inputKey = `${categoryId}-${exampleIndex}`;
      setInputValues({
        ...inputValues,
        [inputKey]: value
      });
    }, "handleInputChange");
    const hasModifiedExamples = /* @__PURE__ */ __name(() => {
      for (const [inputKey, inputValue] of Object.entries(inputValues)) {
        const [categoryId, exampleIndex] = inputKey.split("-");
        const category = formatConfig.categories.find((cat) => cat.id === categoryId);
        if (category && category.examples[parseInt(exampleIndex)]) {
          const defaultValue = category.examples[parseInt(exampleIndex)].display;
          if (inputValue !== defaultValue) {
            return true;
          }
        }
      }
      return false;
    }, "hasModifiedExamples");
    const resetToDefaults = /* @__PURE__ */ __name(() => {
      setInputValues({});
    }, "resetToDefaults");
    const renderFormatExample = /* @__PURE__ */ __name((example, index3, categoryId) => {
      const inputKey = `${categoryId}-${index3}`;
      const inputValue = inputValues[inputKey] || example.display;
      const calculatedValue = getCalculatedValue(inputValue) || example.fallback;
      return /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)(
        "div",
        {
          className: "flex items-center justify-between w-full px-3 py-2 font-mono text-xs rounded bg-default",
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime41.jsx)(
              "input",
              {
                type: "text",
                value: inputValue,
                onChange: (e) => handleInputChange(categoryId, index3, e.target.value),
                className: "flex-1 mr-3 font-mono text-xs bg-transparent border-none outline-none text-color-default focus:outline-none",
                placeholder: example.display
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime41.jsx)("span", { className: "text-color-muted whitespace-nowrap", children: calculatedValue })
          ]
        },
        index3
      );
    }, "renderFormatExample");
    const renderCategory = /* @__PURE__ */ __name((category, index3) => {
      const isLastCategory = index3 === formatConfig.categories.length - 1;
      const marginClass = isLastCategory ? "mb-4" : "mb-6";
      return /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("div", { className: marginClass, children: [
        /* @__PURE__ */ (0, import_jsx_runtime41.jsx)("h4", { className: "mb-3 text-sm font-medium text-color-default", children: category.title }),
        /* @__PURE__ */ (0, import_jsx_runtime41.jsx)("div", { className: "space-y-1", children: category.examples.map((example, index4) => renderFormatExample(example, index4, category.id)) })
      ] }, category.id);
    }, "renderCategory");
    const getCalculatedValue = /* @__PURE__ */ __name((expression) => {
      if (!expression) {
        return "";
      }
      if (!Action || typeof Action.recalculate !== "function") {
        return "";
      }
      try {
        const singleRowCalc = {
          id: "format-preview",
          rows: [{
            text: expression,
            hidden: false
          }]
        };
        if (typeof Action.recalculate !== "function") {
          console.error("[FormatModal] Action.recalculate is NOT a function", Action);
          return "";
        }
        console.log("[FormatModal] Recalculating expression:", expression);
        const results = Action.recalculate(singleRowCalc);
        console.log("[FormatModal] Recalculate results:", results);
        if (!Array.isArray(results) || results.length === 0) {
          console.warn("[FormatModal] Recalculate returned empty or invalid results for:", expression);
          return "";
        }
        const rowResult = results[0];
        let displayValue = rowResult?.output?.display ?? "";
        const type = rowResult?.type?.units || "";
        if (!displayValue) return "";
        displayValue = displayValue.replace(/FRACTION\{(\d+),(\d+)\}/g, "$1/$2");
        return (displayValue + " " + type).trim();
      } catch (e) {
        console.error("[FormatModal] Recalculate failed for:", expression, e);
        return "";
      }
    }, "getCalculatedValue");
    const recalculateAllFormats = /* @__PURE__ */ __name(() => {
      const newFormattedValues = {};
      formatConfig.categories.forEach((category) => {
        category.examples.forEach((example) => {
          newFormattedValues[example.format] = getCalculatedValue(example.display) || example.fallback;
        });
      });
      setFormattedValues(newFormattedValues);
    }, "recalculateAllFormats");
    const handleHover = /* @__PURE__ */ __name(() => {
      recalculateAllFormats();
    }, "handleHover");
    useEffect27(() => {
      recalculateAllFormats();
    }, []);
    const { helpText, helpExample, categories } = formatConfig;
    const formatModal = /* @__PURE__ */ (0, import_jsx_runtime41.jsx)(Portal3, { selector: "FormatModal", children: /* @__PURE__ */ (0, import_jsx_runtime41.jsx)(
      Modal_default,
      {
        title: "Row Format Reference",
        closeIcon: true,
        open: isOpen,
        onOpenChange: handleOpenChange,
        classNames: { panel: "relative flex min-w-[200px] w-full max-w-5xl flex-col bg-secondary border border-soft shadow-sm rounded-xl px-1 pointer-events-auto" },
        children: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("div", { className: "px-3 pt-2 pb-2 w-full sm:px-6", "data-id": "format-modal-content", children: [
          /* @__PURE__ */ (0, import_jsx_runtime41.jsx)("div", { className: "mb-4", children: /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("p", { className: "mb-2 text-sm text-center text-color-default", children: [
            helpText,
            " ",
            /* @__PURE__ */ (0, import_jsx_runtime41.jsx)("span", { className: "px-1 font-mono rounded bg-secondary", children: helpExample })
          ] }) }),
          /* @__PURE__ */ (0, import_jsx_runtime41.jsx)("div", { className: "grid grid-cols-1 gap-6 lg:grid-cols-2", children: categories.map(renderCategory) }),
          /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("div", { className: "flex justify-end mt-2 h-7", children: [
            hasModifiedExamples() && /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)(
              "button",
              {
                onClick: resetToDefaults,
                className: "inline-flex items-center gap-1 px-2 py-1 text-xs transition-all rounded opacity-60 hover:opacity-100 text-color-muted hover:text-color-default hover:bg-secondary",
                title: "Reset all examples to defaults",
                children: [
                  Icon_default.reset("w-3 h-3"),
                  "Reset examples"
                ]
              }
            ),
            !hasModifiedExamples() && /* @__PURE__ */ (0, import_jsx_runtime41.jsx)("div", { className: "inline-flex items-center gap-1 px-2 py-1 text-xs transition-all rounded opacity-40 text-color-muted", children: "Type to edit examples above" })
          ] })
        ] })
      }
    ) });
    if (modalOnly) {
      return formatModal;
    }
    return /* @__PURE__ */ (0, import_jsx_runtime41.jsxs)("div", { children: [
      /* @__PURE__ */ (0, import_jsx_runtime41.jsx)(
        "button",
        {
          type: "button",
          className: cn("inline-flex items-center justify-center gap-2 px-2 py-1 text-xs capitalize align-middle transition-all rounded tooltip text-color-default hover-text-color-active hover-bg-button focus:outline-none", buttonProps.className),
          "data-tip": "See row formatting options",
          "data-id": buttonProps["data-id"],
          onMouseEnter: handleHover,
          onClick: () => handleOpenChange(true),
          children: Action.customDisplayContent || "Format"
        }
      ),
      formatModal
    ] });
  }
  __name(FormatModal, "FormatModal");
  var FormatModal_default = FormatModal;

  // public/src/react/components/calc/CalcModals.jsx
  var import_jsx_runtime42 = __toESM(require_jsx_runtime());
  var { useState: useState27, useEffect: useEffect28 } = react_global_default;
  function CalcModals() {
    const Action = useAction();
    const { calc } = useCalc();
    const [textView, setTextView] = useState27("");
    const [textViewStyle, setTextViewStyle] = useState27(null);
    const [embedCalcId, setEmbedCalcId] = useState27(null);
    const [embedTheme, setEmbedTheme] = useState27("");
    const [embedRadius, setEmbedRadius] = useState27("");
    const [embedShadow, setEmbedShadow] = useState27("");
    const [embedBorder, setEmbedBorder] = useState27("");
    const [styleOptionsExpanded, setStyleOptionsExpanded] = useState27(false);
    const [copyTextOpen, setCopyTextOpen] = useState27(false);
    const [copyEmbedOpen, setCopyEmbedOpen] = useState27(false);
    const [effectiveEmbedUrl, setEffectiveEmbedUrl] = useState27("");
    const [previewUrl, setPreviewUrl] = useState27("");
    const [historyOpen, setHistoryOpen] = useState27(false);
    const [goalseekOpen, setGoalseekOpen] = useState27(false);
    const [formatOpen, setFormatOpen] = useState27(false);
    const [numbersOnly, setNumbersOnly] = useState27(false);
    const [historyClickCount, setHistoryClickCount] = useState27(0);
    const [goalseekClickCount, setGoalseekClickCount] = useState27(0);
    const getEmbedThemes = /* @__PURE__ */ __name(() => {
      const allThemes = window.THEME_CONFIG_V2 || {};
      const list = Object.entries(allThemes).map(([id, cfg]) => ({
        id,
        name: cfg.displayName || id,
        category: cfg.category || "other",
        bg: cfg.surfaces?.bg || "#fff",
        text: cfg.surfaces?.text || "#000"
      }));
      if (list.length === 0) {
        return [
          { id: "theme-daylight", name: "Daylight", category: "defaults", bg: "#f7f8fa", text: "#444" },
          { id: "theme-night", name: "Night", category: "defaults", bg: "#1a1b26", text: "#c0caf5" }
        ];
      }
      return list.sort((a, b) => {
        if (a.category === "defaults" && b.category !== "defaults") return -1;
        if (a.category !== "defaults" && b.category === "defaults") return 1;
        return a.name.localeCompare(b.name);
      });
    }, "getEmbedThemes");
    const themes = getEmbedThemes();
    const getEffectiveTheme = /* @__PURE__ */ __name(() => {
      if (!embedTheme) return "theme-daylight";
      if (embedTheme === "auto") return "auto";
      return embedTheme;
    }, "getEffectiveTheme");
    const isPublished = !!calc?.published?.id;
    const getEmbedParams = /* @__PURE__ */ __name(() => {
      const params = [];
      if (embedTheme) {
        let param = embedTheme;
        if (param.startsWith("theme-omarchy-")) param = param.replace("theme-omarchy-", "");
        else if (param.startsWith("theme-")) param = param.replace("theme-", "");
        params.push(`theme=${param}`);
      }
      if (embedRadius) params.push(`radius=${embedRadius}`);
      if (embedShadow) params.push(`shadow=${embedShadow}`);
      if (embedBorder) params.push(`border=${embedBorder}`);
      return params.length > 0 ? "?" + params.join("&") : "";
    }, "getEmbedParams");
    const getDataUrlRelative = /* @__PURE__ */ __name(() => {
      const UrlManager = window.UrlManager;
      if (UrlManager?.toDataUrl) {
        return UrlManager.toDataUrl(calc);
      }
      return "/embed";
    }, "getDataUrlRelative");
    const insertParamsBeforeHash = /* @__PURE__ */ __name((url, params) => {
      if (!params) return url;
      const hashIndex = url.indexOf("#");
      if (hashIndex === -1) {
        return url + params;
      }
      return url.slice(0, hashIndex) + params + url.slice(hashIndex);
    }, "insertParamsBeforeHash");
    useEffect28(() => {
      const handleUpdateTextView = /* @__PURE__ */ __name(() => {
        console.log("[CalcModals] OPEN_TEXT_VIEW event received");
        setTextView(Action.getTextView(calc, textViewStyle));
        setCopyTextOpen(true);
      }, "handleUpdateTextView");
      const handleUpdateEmbedCalc = /* @__PURE__ */ __name(() => {
        console.log("[CalcModals] OPEN_EMBED event received");
        setEmbedCalcId(calc.id);
        setCopyEmbedOpen(true);
        setEmbedTheme("");
        setEmbedRadius("");
        setEmbedShadow("");
        setEmbedBorder("");
      }, "handleUpdateEmbedCalc");
      const handleOpenHistory = /* @__PURE__ */ __name(() => {
        console.log("[CalcModals] OPEN_HISTORY event received");
        setHistoryClickCount((c) => c + 1);
        setHistoryOpen(true);
      }, "handleOpenHistory");
      const handleOpenGoalseek = /* @__PURE__ */ __name(() => {
        console.log("[CalcModals] OPEN_GOALSEEK event received");
        setGoalseekClickCount((c) => c + 1);
        setGoalseekOpen(true);
      }, "handleOpenGoalseek");
      const handleOpenFormat = /* @__PURE__ */ __name(() => {
        console.log("[CalcModals] OPEN_FORMAT event received");
        setFormatOpen(true);
      }, "handleOpenFormat");
      document.addEventListener(MODAL_EVENTS.UPDATE_TEXT_VIEW, handleUpdateTextView);
      document.addEventListener(MODAL_EVENTS.UPDATE_EMBED_CALC, handleUpdateEmbedCalc);
      document.addEventListener(MODAL_EVENTS.OPEN_HISTORY, handleOpenHistory);
      document.addEventListener(MODAL_EVENTS.OPEN_GOALSEEK, handleOpenGoalseek);
      document.addEventListener(MODAL_EVENTS.OPEN_FORMAT, handleOpenFormat);
      return () => {
        document.removeEventListener(MODAL_EVENTS.UPDATE_TEXT_VIEW, handleUpdateTextView);
        document.removeEventListener(MODAL_EVENTS.UPDATE_EMBED_CALC, handleUpdateEmbedCalc);
        document.removeEventListener(MODAL_EVENTS.OPEN_HISTORY, handleOpenHistory);
        document.removeEventListener(MODAL_EVENTS.OPEN_GOALSEEK, handleOpenGoalseek);
        document.removeEventListener(MODAL_EVENTS.OPEN_FORMAT, handleOpenFormat);
      };
    }, [Action, calc, textViewStyle]);
    useEffect28(() => {
      if (!copyEmbedOpen || !calc) return;
      const updateUrls = /* @__PURE__ */ __name(async () => {
        const params = getEmbedParams();
        const UrlManager = window.UrlManager;
        let effective = "";
        let preview = "";
        if (isPublished) {
          effective = Action.getEmbedURL(calc) + params;
          preview = Action.getEmbedURL(calc).replace(/^https?:\/\/[^/]+/, "") + params;
        } else if (UrlManager?.toDataUrl) {
          const baseUrl = window.location.origin;
          const fullDataUrl = await UrlManager.toDataUrl(calc, { baseUrl });
          effective = insertParamsBeforeHash(fullDataUrl, params);
          const relativeDataUrl = await UrlManager.toDataUrl(calc);
          preview = insertParamsBeforeHash(relativeDataUrl, params);
        } else {
          effective = window.location.origin + "/embed" + params;
          preview = "/embed" + params;
        }
        setEffectiveEmbedUrl(effective);
        setPreviewUrl(preview);
      }, "updateUrls");
      updateUrls();
    }, [calc, embedTheme, embedRadius, embedShadow, embedBorder, isPublished, copyEmbedOpen, Action]);
    if (!calc) return null;
    const public_url = Action.getPublicURL(calc);
    const textViewTabs = [
      { value: "", label: "Inputs + Results" },
      { value: "inputs", label: "Inputs" },
      { value: "resultsAll", label: "Results" }
    ];
    const getEffectiveStyle = /* @__PURE__ */ __name((style, numOnly) => {
      if (style === "resultsAll" && numOnly) return "results";
      return style;
    }, "getEffectiveStyle");
    const copyTextModal = /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(Portal3, { selector: "CopyTextModal", children: /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(Modal_default, { title: "Export Text", closeIcon: true, open: copyTextOpen, onOpenChange: setCopyTextOpen, children: /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "flex flex-col gap-3", "data-id": "export-text-modal-content", children: [
      /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "flex items-center gap-3", children: [
        /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("div", { className: "flex gap-1 p-1 rounded-lg bg-theme-bg-alt", children: textViewTabs.map((tab) => /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
          "button",
          {
            type: "button",
            className: `px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-150 ${(textViewStyle || "") === tab.value ? "bg-accent text-bg" : "text-theme-text-muted hover:text-theme-text hover:bg-theme-bg"}`,
            onClick: () => {
              setTextViewStyle(tab.value);
              const effectiveStyle = getEffectiveStyle(tab.value, numbersOnly);
              setTextView(Action.getTextView(calc, effectiveStyle));
            },
            children: tab.label
          },
          tab.value
        )) }),
        textViewStyle === "resultsAll" && /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("label", { className: "flex items-center gap-1.5 text-xs text-theme-text-muted cursor-pointer select-none", children: [
          /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
            "input",
            {
              type: "checkbox",
              checked: numbersOnly,
              onChange: (e) => {
                setNumbersOnly(e.target.checked);
                const effectiveStyle = getEffectiveStyle("resultsAll", e.target.checked);
                setTextView(Action.getTextView(calc, effectiveStyle));
              },
              className: "w-3.5 h-3.5 rounded border-soft accent-[var(--accent)]"
            }
          ),
          "Numbers only (no types)"
        ] })
      ] }),
      /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "relative p-2 normal-case border rounded-lg min-w-md bg-secondary border-soft", children: [
        /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
          CopyButton_default,
          {
            target: `#copy-text-${calc.id}`,
            classNames: {
              root: "absolute top-2 right-2 select-none inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-alt transition-colors copy-event"
            }
          }
        ),
        /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("pre", { className: "text-xs min-w-[40vw] max-w-[90vw] font-mono py-2 px-4 pr-16 overflow-auto max-h-[60vh]", id: `copy-text-${calc.id}`, children: [
          calc.name,
          "\n\n",
          textView,
          public_url ? "\n---\n" + public_url : ""
        ] })
      ] })
    ] }) }) });
    const iframe = `<iframe src="${effectiveEmbedUrl}" width="450" height="350" frameborder="0"></iframe>`;
    const selectClass = "flex-1 px-2 py-1.5 text-sm border rounded-lg border-soft text-color-default bg-theme-bg";
    const copyEmbedModal = /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(Portal3, { selector: "CopyEmbedModal", children: /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(Modal_default, { title: "Embed Calculator", closeIcon: true, open: copyEmbedOpen, onOpenChange: setCopyEmbedOpen, classNames: { body: "no-scrollbar" }, children: /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "flex flex-col gap-5 normal-case min-w-[520px]", "data-id": "embed-modal-content", children: [
      !isPublished && /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "flex items-start gap-3 p-3 rounded-lg bg-[color-mix(in_srgb,var(--status-private-bg)_10%,transparent)] border border-[color-mix(in_srgb,var(--status-private-bg)_30%,transparent)]", children: [
        /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("span", { className: "text-[var(--status-private-fg)] mt-0.5", children: Icon_default.infoCircle("w-4 h-4") }),
        /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "flex-1 text-sm", children: [
          /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("p", { className: "font-medium text-[var(--status-private-fg)]", children: "This calc is not published" }),
          /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("p", { className: "mt-1 text-[color-mix(in_srgb,var(--status-private-fg)_80%,transparent)]", children: [
            "Using a data URL (content encoded in link). For a shorter URL,",
            " ",
            /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
              "button",
              {
                type: "button",
                className: "underline hover:no-underline font-medium",
                onClick: () => {
                  setCopyEmbedOpen(false);
                  Action.publish(calc);
                },
                children: "publish this calc"
              }
            ),
            "."
          ] })
        ] })
      ] }),
      /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "space-y-2", children: [
        /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("span", { className: "text-xs font-semibold text-color-muted uppercase tracking-wider", children: "Embed Code" }),
        /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "relative p-3 border rounded-lg border-soft bg-theme-bg", children: [
          /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
            CopyButton_default,
            {
              target: `#copy-embed-${calc.id}`,
              classNames: {
                root: "absolute top-2 right-2 select-none inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-alt transition-colors copy-event"
              }
            }
          ),
          embedCalcId === calc.id && /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
            "textarea",
            {
              readOnly: true,
              rows: 2,
              className: "w-full pr-16 font-mono text-xs leading-relaxed resize-none text-color-default bg-transparent focus:outline-none",
              id: `copy-embed-${calc.id}`,
              "data-embed": calc.selected,
              value: iframe
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "space-y-3", children: [
        /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "flex items-center justify-between", children: [
          /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("span", { className: "text-xs font-semibold text-color-muted uppercase tracking-wider", children: "Preview" }),
          /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)(
            "button",
            {
              type: "button",
              className: `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${styleOptionsExpanded ? "border-accent text-theme-accent bg-accent-soft" : "border-soft text-color-muted hover:text-color-default hover:border-color-default"}`,
              onClick: () => setStyleOptionsExpanded(!styleOptionsExpanded),
              children: [
                Icon_default.adjustments("w-3.5 h-3.5"),
                "Customize"
              ]
            }
          )
        ] }),
        styleOptionsExpanded && /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "space-y-3 p-3 rounded-lg border border-soft bg-secondary", children: [
          /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "space-y-2", children: [
            /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("label", { className: "text-xs text-color-muted uppercase tracking-wider font-semibold", children: "Theme" }),
            /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "grid grid-cols-5 gap-1.5", children: [
              /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
                "button",
                {
                  type: "button",
                  className: "px-2 py-1.5 text-xs font-medium rounded transition-all ring-2 ring-offset-1",
                  style: {
                    background: "linear-gradient(135deg, #f7f8fa 50%, #1a1b26 50%)",
                    color: "#666",
                    "--tw-ring-offset-color": "var(--bg-alt)",
                    "--tw-ring-color": embedTheme === "auto" ? "var(--accent)" : "transparent"
                  },
                  onClick: () => setEmbedTheme("auto"),
                  children: "Auto"
                }
              ),
              themes.map((t) => /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
                "button",
                {
                  type: "button",
                  className: "px-2 py-1.5 text-xs font-medium rounded transition-all ring-2 ring-offset-1 hover:opacity-80",
                  style: {
                    background: t.bg,
                    color: t.text,
                    "--tw-ring-offset-color": "var(--bg-alt)",
                    "--tw-ring-color": getEffectiveTheme() === t.id ? "var(--accent)" : "transparent"
                  },
                  onClick: () => setEmbedTheme(t.id === "theme-daylight" ? "" : t.id),
                  children: t.name
                },
                t.id
              ))
            ] })
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { className: "space-y-2", children: [
            /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("label", { className: "text-xs text-color-muted uppercase tracking-wider font-semibold", children: "Corners" }),
            /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("div", { className: "flex gap-1.5", children: [
              { value: "none", label: "None" },
              { value: "sm", label: "Small" },
              { value: "", label: "Medium" },
              { value: "lg", label: "Large" },
              { value: "xl", label: "XL" }
            ].map((opt) => /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
              "button",
              {
                type: "button",
                className: `px-3 py-1.5 text-xs font-medium rounded transition-all ${embedRadius === opt.value ? "bg-accent text-bg" : "bg-theme-bg text-theme-text border border-soft opacity-70 hover:opacity-100"}`,
                onClick: () => setEmbedRadius(opt.value),
                children: opt.label
              },
              opt.value
            )) })
          ] })
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)(
          "div",
          {
            className: "relative overflow-hidden border rounded-xl border-soft p-4 flex justify-center",
            style: { background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime42.jsx)("span", { className: "absolute bottom-2 right-3 text-[10px] text-white/50 select-none", children: "sample background" }),
              embedCalcId === calc.id && /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
                "iframe",
                {
                  src: previewUrl,
                  width: "450",
                  height: "350",
                  frameBorder: "0"
                },
                `${embedTheme}-${embedRadius}-${isPublished}`
              )
            ]
          }
        )
      ] })
    ] }) }) });
    const historyModal = historyOpen && /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(Portal3, { selector: "HistoryModal", children: /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
      HistoryModal_default,
      {
        Action,
        calc,
        clickCount: historyClickCount,
        open: historyOpen,
        onOpenChange: setHistoryOpen
      }
    ) });
    const goalseekModal = goalseekOpen && /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(Portal3, { selector: "GoalseekModal", children: /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
      GoalseekModal_default,
      {
        clickCount: goalseekClickCount,
        open: goalseekOpen,
        onOpenChange: setGoalseekOpen
      }
    ) });
    const formatModal = formatOpen && // FormatModal renders its own Portal, so we just render it here.
    /* @__PURE__ */ (0, import_jsx_runtime42.jsx)(
      FormatModal_default,
      {
        open: formatOpen,
        onOpenChange: setFormatOpen,
        modalOnly: true,
        Action
      }
    );
    return /* @__PURE__ */ (0, import_jsx_runtime42.jsxs)("div", { "data-component": "CalcModals", children: [
      copyTextModal,
      copyEmbedModal,
      historyModal,
      goalseekModal,
      formatModal
    ] });
  }
  __name(CalcModals, "CalcModals");
  var CalcModals_default = CalcModals;

  // public/src/react/components/meta/NewCalcButton.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime43 = __toESM(require_jsx_runtime());
  function NewCalcButton(props) {
    const { className = "", style = {}, mobile = false, label, showIcon = true, ...otherProps } = (
      /** @type {NewCalcButtonProps} */
      props
    );
    const Action = useAction();
    const baseClasses = mobile ? "relative inline-flex h-full items-center justify-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg border border-transparent cursor-pointer transition-all duration-200 active:scale-95" : "relative inline-flex items-center justify-center gap-1 px-2 py-1.5 lg:px-3 lg:py-2 text-xs lg:text-sm font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 overflow-hidden hover:brightness-110 active:scale-95";
    const defaultStyle = mobile ? {
      background: "linear-gradient(135deg, var(--accent), var(--accent-bright))",
      color: "var(--button-primary-fg, #fff)",
      boxShadow: "0 6px 16px color-mix(in srgb, var(--accent) 35%, transparent)",
      ...style
    } : {
      background: "linear-gradient(to bottom right, var(--accent), var(--accent-bright))",
      color: "var(--button-primary-fg, #fff)",
      boxShadow: "0 4px 15px color-mix(in srgb, var(--accent) 40%, transparent)",
      ...style
    };
    const handleClick = /* @__PURE__ */ __name((e) => {
      e.preventDefault();
      Action.createAndOpen();
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        window.location.href = "/";
      }
    }, "handleClick");
    const buttonLabel = label && label.trim().length > 0 ? label : mobile ? "\uFF0B" : "New Calc";
    return /* @__PURE__ */ (0, import_jsx_runtime43.jsxs)("a", { href: "#", onClick: handleClick, className: `${baseClasses} ${className}`, style: defaultStyle, "data-id": "new-calc-button", ...otherProps, children: [
      showIcon && Icon_default.plus("w-3 h-3 lg:w-4 lg:h-4"),
      /* @__PURE__ */ (0, import_jsx_runtime43.jsx)("span", { children: buttonLabel })
    ] });
  }
  __name(NewCalcButton, "NewCalcButton");
  var NewCalcButton_default = react_global_default.memo(NewCalcButton);

  // public/src/react/components/common/Toast.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime44 = __toESM(require_jsx_runtime());
  var { useState: useState28, useEffect: useEffect29, useCallback: useCallback18, useMemo: useMemo12, createContext: createContext7, useContext: useContext9 } = react_global_default;
  var ToastContext = createContext7(null);
  var TOAST_STYLES = {
    success: "bg-accent text-bg",
    error: "bg-theme-calc-result text-bg",
    info: "bg-accent text-bg",
    default: "bg-theme-bg-alt text-theme-text border border-soft"
  };
  var ToastIcon = /* @__PURE__ */ __name(({ type }) => {
    if (type === "success") return Icon_default.success("w-4 h-4");
    if (type === "error") return Icon_default.close("w-4 h-4");
    return null;
  }, "ToastIcon");
  function ToastMessage({ message, type = "default", onClose }) {
    useEffect29(() => {
      const timer = setTimeout(onClose, 2e3);
      return () => clearTimeout(timer);
    }, [onClose]);
    return /* @__PURE__ */ (0, import_jsx_runtime44.jsxs)(
      "div",
      {
        className: cn(
          "flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-sm font-medium",
          "animate-fade-in",
          TOAST_STYLES[type] || TOAST_STYLES.default
        ),
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime44.jsx)(ToastIcon, { type }),
          /* @__PURE__ */ (0, import_jsx_runtime44.jsx)("span", { children: message })
        ]
      }
    );
  }
  __name(ToastMessage, "ToastMessage");
  function ToastContainer({ toasts, removeToast }) {
    if (toasts.length === 0) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime44.jsx)("div", { className: "fixed inset-x-0 bottom-6 flex flex-col items-center gap-2 z-[100] pointer-events-none", children: toasts.map((toast) => /* @__PURE__ */ (0, import_jsx_runtime44.jsx)(
      ToastMessage,
      {
        message: toast.message,
        type: toast.type,
        onClose: () => removeToast(toast.id)
      },
      toast.id
    )) });
  }
  __name(ToastContainer, "ToastContainer");
  function ToastProvider({ children }) {
    const [toasts, setToasts] = useState28([]);
    const addToast = useCallback18((message, type = "default") => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, message, type }]);
      return id;
    }, []);
    const removeToast = useCallback18((id) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    const toastApi = useMemo12(() => ({
      show: /* @__PURE__ */ __name((message) => addToast(message, "default"), "show"),
      success: /* @__PURE__ */ __name((message) => addToast(message, "success"), "success"),
      error: /* @__PURE__ */ __name((message) => addToast(message, "error"), "error"),
      info: /* @__PURE__ */ __name((message) => addToast(message, "info"), "info")
    }), [addToast]);
    useEffect29(() => {
      setGlobalToast(toastApi);
      if (typeof window !== "undefined") {
        window.showToast = showToast;
      }
      return () => {
        setGlobalToast(null);
        if (typeof window !== "undefined") {
          delete window.showToast;
        }
      };
    }, [toastApi]);
    return /* @__PURE__ */ (0, import_jsx_runtime44.jsxs)(ToastContext.Provider, { value: toastApi, children: [
      children,
      /* @__PURE__ */ (0, import_jsx_runtime44.jsx)(ToastContainer, { toasts, removeToast })
    ] });
  }
  __name(ToastProvider, "ToastProvider");
  function useToast() {
    const context = useContext9(ToastContext);
    if (!context) {
      return {
        show: /* @__PURE__ */ __name((msg) => console.log("[Toast]", msg), "show"),
        success: /* @__PURE__ */ __name((msg) => console.log("[Toast:success]", msg), "success"),
        error: /* @__PURE__ */ __name((msg) => console.log("[Toast:error]", msg), "error"),
        info: /* @__PURE__ */ __name((msg) => console.log("[Toast:info]", msg), "info")
      };
    }
    return context;
  }
  __name(useToast, "useToast");
  var globalToast = null;
  function setGlobalToast(toastFn) {
    globalToast = toastFn;
  }
  __name(setGlobalToast, "setGlobalToast");
  function showToast(message, type = "default") {
    if (globalToast) {
      if (type === "success") globalToast.success(message);
      else if (type === "error") globalToast.error(message);
      else if (type === "info") globalToast.info(message);
      else globalToast(message);
    } else {
      console.log("[Toast]", message);
    }
  }
  __name(showToast, "showToast");

  // public/src/react/components/common/Island.jsx
  init_react_global();

  // public/src/react/components/common/MultiPortal.jsx
  init_react_global();
  var import_jsx_runtime45 = __toESM(require_jsx_runtime());
  var { useMemo: useMemo13, useEffect: useEffect30, useState: useState29 } = react_global_default;
  var globalObserver = null;
  var subscribers = /* @__PURE__ */ new Set();
  function initGlobalObserver() {
    if (globalObserver) return;
    if (typeof window === "undefined" || typeof MutationObserver === "undefined") return;
    let debounceTimer = null;
    const notifySubscribers = /* @__PURE__ */ __name(() => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        subscribers.forEach((cb) => cb());
      }, 200);
    }, "notifySubscribers");
    globalObserver = new MutationObserver((mutations) => {
      let shouldNotify = false;
      for (const mutation of mutations) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          const target = mutation.target;
          if (target?.hasAttribute?.("data-load") || target?.closest?.("[data-load]")) {
            shouldNotify = true;
            break;
          }
        }
      }
      if (shouldNotify) notifySubscribers();
    });
    globalObserver.observe(document.body, { childList: true, subtree: true });
  }
  __name(initGlobalObserver, "initGlobalObserver");
  function MultiPortal({ selector, children }) {
    const [forceCheck, setForceCheck] = useState29(0);
    useEffect30(() => {
      initGlobalObserver();
      const onUpdate = /* @__PURE__ */ __name(() => setForceCheck((n) => n + 1), "onUpdate");
      subscribers.add(onUpdate);
      return () => {
        subscribers.delete(onUpdate);
      };
    }, [selector]);
    const targets = useMemo13(() => {
      if (typeof document === "undefined") return [];
      if (!selector || typeof selector !== "string") return [];
      const isBareName = !/[#.[\s:>+~]/.test(selector);
      try {
        const _tick = forceCheck;
        let nodes = [];
        if (isBareName) {
          nodes = Array.from(document.getElementsByTagName(selector));
          const byId = document.getElementById(selector);
          if (byId) nodes.push(byId);
          nodes.push(...Array.from(document.querySelectorAll(`[data-portal="${selector}"]`)));
          const seen = /* @__PURE__ */ new Set();
          nodes = nodes.filter((n) => {
            if (!n || !n.nodeType) return false;
            const key = n;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return nodes;
        }
        return Array.from(document.querySelectorAll(selector));
      } catch (_e) {
        return [];
      }
    }, [selector, forceCheck]);
    useEffect30(() => {
      if (!targets || targets.length === 0) return;
      const ownerAttr = "data-ic-portal-owner";
      const ownerVal = selector || "MultiPortal";
      const forceOwner = selector && selector.toLowerCase().includes("calclist");
      targets.forEach((el) => {
        try {
          if (!el) return;
          const currentOwner = el.getAttribute(ownerAttr);
          if (currentOwner && currentOwner !== ownerVal && !forceOwner) {
            console.warn(`[MultiPortal] Another owner ('${currentOwner}') already claimed target for selector "${selector}"; skipping render for this target.`);
            return;
          }
          if (!currentOwner || forceOwner) {
            el.setAttribute(ownerAttr, ownerVal);
          }
        } catch (_e) {
        }
      });
      return () => {
        targets.forEach((el) => {
          try {
            if (el?.getAttribute?.(ownerAttr) === ownerVal) {
              el.removeAttribute(ownerAttr);
            }
          } catch (_e) {
          }
        });
      };
    }, [targets, selector]);
    if (!targets || targets.length === 0) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime45.jsx)(import_jsx_runtime45.Fragment, { children: targets.map((el, idx) => {
      const ownerAttr = "data-ic-portal-owner";
      const ownerVal = selector || "MultiPortal";
      const currentOwner = el?.getAttribute?.(ownerAttr);
      if (currentOwner && currentOwner !== ownerVal) return null;
      const content = typeof children === "function" ? children(el, idx) : children;
      const key = (
        /** @type {HTMLElement} */
        el.dataset?.portalKey || `${selector}-${idx}`
      );
      return react_dom_global_default.createPortal(content, el, key);
    }) });
  }
  __name(MultiPortal, "MultiPortal");

  // public/src/react/components/common/Island.jsx
  var import_jsx_runtime46 = __toESM(require_jsx_runtime());
  function Island({ target, selector, children, render, component: Component, props }) {
    const portalSelector = target || selector;
    if (Component) {
      return /* @__PURE__ */ (0, import_jsx_runtime46.jsx)(MultiPortal, { selector: portalSelector, children: (el) => /* @__PURE__ */ (0, import_jsx_runtime46.jsx)(Component, { ...props, portalEl: el }) });
    }
    if (render) {
      return /* @__PURE__ */ (0, import_jsx_runtime46.jsx)(MultiPortal, { selector: portalSelector, children: render });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime46.jsx)(MultiPortal, { selector: portalSelector, children });
  }
  __name(Island, "Island");

  // public/src/react/components/common/DOMSlot.jsx
  init_react_global();
  var import_jsx_runtime47 = __toESM(require_jsx_runtime());
  var { useRef: useRef21, useEffect: useEffect31, useState: useState30 } = react_global_default;
  function DOMSlot({ selector, className }) {
    const containerRef = useRef21(null);
    const [sourceEl, setSourceEl] = useState30(null);
    const originalParentRef = useRef21(null);
    useEffect31(() => {
      if (!selector) return;
      const el = document.querySelector(selector);
      if (el) {
        setSourceEl(el);
        originalParentRef.current = el.parentNode;
      }
    }, [selector]);
    useEffect31(() => {
      if (!sourceEl || !containerRef.current) return;
      const container = containerRef.current;
      while (sourceEl.firstChild) {
        container.appendChild(sourceEl.firstChild);
      }
      return () => {
        while (container.firstChild) {
          sourceEl.appendChild(container.firstChild);
        }
      };
    }, [sourceEl]);
    if (!selector) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime47.jsx)("div", { ref: containerRef, className });
  }
  __name(DOMSlot, "DOMSlot");

  // public/src/react/App.jsx
  var import_CalcType7 = __toESM(require_CalcType());
  var DebugModule3 = __toESM(require_debug());

  // public/src/react/components/layouts/EditorLayout.jsx
  init_react_global();

  // public/src/react/components/calc/InstaCalc.jsx
  init_react_global();

  // public/src/react/components/calc/Row.jsx
  init_react_global();

  // public/src/react/components/calc/row/CalcRow.jsx
  init_react_global();

  // public/src/react/components/common/UIResult.jsx
  init_react_global();

  // public/src/react/components/common/Chart.jsx
  init_react_global();
  var import_jsx_runtime48 = __toESM(require_jsx_runtime());
  var { useState: useState31, useEffect: useEffect32, useRef: useRef22 } = react_global_default;
  var _loadingPromise = null;
  function loadChartistResources() {
    if (typeof window === "undefined") {
      return Promise.reject(new Error("Chartist cannot load on the server"));
    }
    if (window.Chartist) {
      return Promise.resolve();
    }
    if (_loadingPromise) {
      return _loadingPromise;
    }
    _loadingPromise = new Promise((resolve, reject) => {
      if (!document.getElementById("chartist-css")) {
        const cssLink = document.createElement("link");
        cssLink.id = "chartist-css";
        cssLink.rel = "stylesheet";
        cssLink.href = "/vendor/chartist/chartist.min.css";
        document.head.insertBefore(cssLink, document.head.firstChild);
      }
      if (!document.getElementById("chartist-js")) {
        const script = document.createElement("script");
        script.id = "chartist-js";
        script.src = "/vendor/chartist/chartist.min.js";
        script.async = true;
        script.onload = () => {
          if (window.Chartist) {
            resolve();
          } else {
            console.error("Static Loader: Chartist JS loaded but window.Chartist not defined.");
            _loadingPromise = null;
            reject(new Error("Chartist loaded but failed to initialize."));
          }
        };
        script.onerror = (error) => {
          console.error("Static Loader: Failed to load Chartist JS:", error);
          _loadingPromise = null;
          reject(new Error("Failed to load Chartist script."));
        };
        document.body.appendChild(script);
      } else {
        console.warn("Static Loader: Script tag exists, checking window.Chartist again shortly...");
        setTimeout(() => {
          if (window.Chartist) {
            resolve();
          } else {
            console.error("Static Loader: window.Chartist still not defined after delay.");
            _loadingPromise = null;
            reject(new Error("Existing Chartist script tag found but library not initialized."));
          }
        }, 500);
      }
    });
    return _loadingPromise;
  }
  __name(loadChartistResources, "loadChartistResources");
  function Chart({ result, index: index2 }) {
    const [loadingStatus, setLoadingStatus] = useState31(
      typeof window !== "undefined" && window.Chartist ? "loaded" : "idle"
    );
    const chartRef = useRef22(null);
    const chartContainerRef = useRef22(null);
    const renderOrUpdateChart = /* @__PURE__ */ __name(() => {
      if (typeof window === "undefined" || !window.Chartist) {
        console.warn("Chartist library still not available when attempting to render chart");
        return;
      }
      const chartContainer = chartContainerRef.current;
      if (!chartContainer) {
        return;
      }
      chartContainer.innerHTML = "";
      if (!result) {
        chartContainer.innerHTML = '<p class="chart-empty">No data</p>';
        return;
      }
      const chartType = result.type;
      const chartData = result.data;
      if (!chartType || !chartData) {
        chartContainer.innerHTML = '<p class="chart-error">Invalid chart data</p>';
        return;
      }
      const ChartConstructor = {
        "linechart": window.Chartist.Line,
        "barchart": window.Chartist.Bar,
        "piechart": window.Chartist.Pie
      }[chartType];
      if (!ChartConstructor) {
        chartContainer.innerHTML = `<p class="chart-error">Unsupported chart type: ${chartType}</p>`;
        return;
      }
      try {
        const charLimit = 15;
        const rawLabels = result.labels || (Array.isArray(result.data) ? result.data.map((item, index3) => item.label || `Item ${index3 + 1}`) : []);
        const rawValues = result.data.map((item) => item.value);
        const chartOptions = {
          fullWidth: true,
          chartPadding: {
            right: 30,
            left: 40,
            top: 10,
            bottom: 10
          },
          low: 0,
          axisY: {
            labelInterpolationFnc: /* @__PURE__ */ __name((value) => {
              if (value >= 1e6) return (value / 1e6).toFixed(1) + "M";
              if (value >= 1e3) return (value / 1e3).toFixed(1) + "k";
              return value;
            }, "labelInterpolationFnc")
          },
          axisX: {
            labelInterpolationFnc: /* @__PURE__ */ __name((value, index3) => {
              if (rawLabels.length > charLimit) {
                const step = Math.ceil(rawLabels.length / 10);
                return index3 % step === 0 ? value : null;
              }
              return value;
            }, "labelInterpolationFnc")
          },
          ...result.options || {}
        };
        const responsiveOptions = result.responsiveOptions || null;
        if (chartType === "Pie" && !chartOptions.donut && result.data && result.data.length > 20) {
          chartOptions.donut = true;
          chartOptions.donutWidth = 40;
          chartOptions.showLabel = true;
        }
        const chartistData = {
          labels: rawLabels,
          series: chartType === "Pie" ? rawValues : [rawValues]
        };
        if (chartRef.current) {
          chartRef.current.update(chartistData, chartOptions);
        } else {
          chartRef.current = new ChartConstructor(
            chartContainer,
            chartistData,
            chartOptions,
            responsiveOptions
          );
        }
      } catch (error) {
        console.error(`Error rendering/updating Chartist ${chartType}:`, error);
        chartContainer.innerHTML = '<p class="chart-error">Could not render chart.</p>';
        if (chartRef.current) {
          try {
            chartRef.current.detach();
          } catch (e) {
          }
          chartRef.current = null;
        }
      }
    }, "renderOrUpdateChart");
    useEffect32(() => {
      if (loadingStatus === "loaded") {
        renderOrUpdateChart();
      } else if (loadingStatus === "idle") {
        setLoadingStatus("loading");
        loadChartistResources().then(() => {
          setLoadingStatus("loaded");
        }).catch((error) => {
          console.error("Error initializing chart:", error);
          setLoadingStatus("error");
        });
      }
    }, [loadingStatus]);
    useEffect32(() => {
      if (loadingStatus === "loaded") {
        renderOrUpdateChart();
      }
    }, [result]);
    useEffect32(() => {
      return () => {
        if (chartRef.current) {
          try {
            chartRef.current.detach();
          } catch (e) {
            console.warn("Error detaching Chartist chart:", e);
          }
          chartRef.current = null;
        }
      };
    }, []);
    return /* @__PURE__ */ (0, import_jsx_runtime48.jsx)("div", { ref: chartContainerRef, className: "ct-chart chart-component-container", style: { minHeight: "150px" } });
  }
  __name(Chart, "Chart");
  var Chart_default = Chart;

  // public/src/react/components/common/UIResult.jsx
  var import_jsx_runtime49 = __toESM(require_jsx_runtime());
  var UIResult = /* @__PURE__ */ __name((props) => {
    const { result, index: index2, label, children } = (
      /** @type {UIResultProps} */
      props
    );
    if (typeof result !== "object" || !result || !result.type) {
      return null;
    }
    if (result.type.endsWith("chart") && result?.data) {
      return /* @__PURE__ */ (0, import_jsx_runtime49.jsx)(Chart_default, { result, index: index2 });
    }
    if (result.type === "image") {
      return /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("img", { src: result.args[0], style: { maxHeight: "500px" }, alt: "Image" });
    }
    if (result.type === "embed") {
      const url = result.args[0];
      const id = url?.toString().match(/(\d+)/);
      if (id) {
        const embedURL = `https://instacalc.com/${id[0]}/embed`;
        let height = parseInt(result.args[1], 10) || 300;
        height = Math.min(Math.max(height, 100), 1e3);
        return /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("iframe", { className: "w-full tooltip", style: { height: `${height}px` }, src: embedURL, "data-tip": "Embedded calc", title: "Embedded calc" });
      }
      UIlog("[UIResultNew]", "embed usage error");
      return /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("div", { children: "Usage: embed(id), ex: embed(12345)" });
    }
    if (result.type === "hyperlink") {
      const title = result.args[1] || result.args[0];
      return /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("a", { href: result.args[0], target: "_blank", rel: "noreferrer", children: title });
    }
    if (result.type === "table") {
      return /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("table", { className: "w-full border-collapse table-auto", children: /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("tbody", { children: result.data.map((item, index3) => /* @__PURE__ */ (0, import_jsx_runtime49.jsxs)("tr", { className: "border border-b", children: [
        /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("td", { className: "px-4 py-2", children: item.title }),
        /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("td", { className: "px-4 py-2", children: result.args[index3] })
      ] }, index3)) }) });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime49.jsxs)("div", { className: "flex items-center space-x-2", children: [
      /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("div", { className: "w-1/4 text-right", children: label }),
      /* @__PURE__ */ (0, import_jsx_runtime49.jsx)("div", { className: "w-3/4", children })
    ] });
  }, "UIResult");
  var UIResult_default = UIResult;

  // public/src/react/components/calc/VariableAssignmentPanel.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime50 = __toESM(require_jsx_runtime());
  function VariableAssignmentPanel(props) {
    const { resultObj, goalseekObj, index: index2 } = (
      /** @type {VariableAssignmentPanelProps} */
      props
    );
    const Action = useAction();
    const { calc } = useCalc();
    const handleApplySolution = /* @__PURE__ */ __name(() => {
      LIB.setVariablesInCalc(Action, calc, resultObj.vars);
    }, "handleApplySolution");
    const varMap = resultObj?.vars;
    let varsDisplay = null;
    if (varMap) {
      const variablesToSet = Object.entries(varMap).map(([key, value]) => /* @__PURE__ */ (0, import_jsx_runtime50.jsxs)("li", { children: [
        key,
        " = ",
        IC.api.formatValue(value)
      ] }, key));
      varsDisplay = /* @__PURE__ */ (0, import_jsx_runtime50.jsxs)("div", { className: "flex items-center", children: [
        /* @__PURE__ */ (0, import_jsx_runtime50.jsx)("div", { className: "flex-none w-24 pr-1", children: /* @__PURE__ */ (0, import_jsx_runtime50.jsx)(
          "button",
          {
            type: "button",
            onClick: (e) => {
              handleApplySolution();
              e.stopPropagation();
            },
            className: "px-2 py-1 text-xs border rounded-md shadow-sm text-color-default border-soft hover-text-color-active",
            children: "Set values"
          }
        ) }),
        /* @__PURE__ */ (0, import_jsx_runtime50.jsx)("div", { className: "flex-grow overflow-x-auto", children: /* @__PURE__ */ (0, import_jsx_runtime50.jsx)("ul", { className: "block w-full p-2 overflow-auto text-xs", children: variablesToSet }) })
      ] });
    } else {
      varsDisplay = /* @__PURE__ */ (0, import_jsx_runtime50.jsx)("div", { className: "cursor-pointer", children: /* @__PURE__ */ (0, import_jsx_runtime50.jsxs)("details", { onClick: (e) => e.stopPropagation(), children: [
        /* @__PURE__ */ (0, import_jsx_runtime50.jsxs)("summary", { children: [
          Icon_default.plusCircle("inline-block mb-1 mr-1 opacity-75 icon-closed size-3"),
          Icon_default.minusCircle("inline-block mb-1 mr-1 opacity-75 icon-open size-3"),
          "Goalseek couldn't find a solution"
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime50.jsx)("main", { children: /* @__PURE__ */ (0, import_jsx_runtime50.jsx)("pre", { className: "text-xs", children: LIB.prettyPrintObject(goalseekObj) }) })
      ] }) });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime50.jsx)("div", { className: "cursor-pointer", children: varsDisplay });
  }
  __name(VariableAssignmentPanel, "VariableAssignmentPanel");
  var VariableAssignmentPanel_default = VariableAssignmentPanel;

  // public/src/react/context/LayoutContext.jsx
  init_react_global();
  var import_jsx_runtime51 = __toESM(require_jsx_runtime());
  var LayoutContext = createContext(null);
  var DENSITY_PRESETS = {
    compact: {
      gapX: "0.75rem",
      gapY: "0.5rem",
      // Increased from 0.125rem (2px) for better separation
      rowMinHeight: "2.25rem",
      paddingX: "1rem",
      // Increased for better breatheability
      paddingY: "0.75rem",
      // Added some vertical breathing room
      borderRadius: "0.5rem",
      heroPadding: "0.75rem 1rem",
      presentAffixSize: "0.8125rem",
      presentAffixSizeHero: "0.85rem"
    },
    normal: {
      gapX: "1.25rem",
      // Increased from 1rem
      gapY: "0.75rem",
      // Increased from 0.5rem for delightful spacing
      rowMinHeight: "2.75rem",
      paddingX: "1.5rem",
      // Increased from 1rem for desktop breathing room
      paddingY: "1.5rem",
      // Increased from 0.5rem
      borderRadius: "0.75rem",
      heroPadding: "1.25rem 1.75rem",
      presentAffixSize: "0.875rem",
      presentAffixSizeHero: "0.9rem"
    },
    spacious: {
      gapX: "1.5rem",
      gapY: "0.5rem",
      rowMinHeight: "3.5rem",
      paddingX: "1.5rem",
      paddingY: "1rem",
      borderRadius: "1.5rem",
      heroPadding: "1.5rem 2rem",
      presentAffixSize: "0.95rem",
      presentAffixSizeHero: "1rem"
    }
  };
  function LayoutProvider({ children, density = "normal", strategy, gridConfig: explicitGridConfig }) {
    const value = useMemo(() => {
      const tokens = DENSITY_PRESETS[density] || DENSITY_PRESETS.normal;
      const gridConfig = explicitGridConfig || strategy?.grid || { columns: 6, fullSpan: 6 };
      const getSpanValue = /* @__PURE__ */ __name((fraction, base = gridConfig.columns) => {
        if (fraction === "full" || !fraction) return gridConfig.fullSpan || base;
        if (fraction === "1/2" && gridConfig.halfSpan) return gridConfig.halfSpan;
        if (fraction === "1/3" && gridConfig.thirdSpan) return gridConfig.thirdSpan;
        const parts = fraction.split("/");
        if (parts.length !== 2) return base;
        const [num, den] = parts.map(Number);
        return Math.round(base * (num / den));
      }, "getSpanValue");
      const val = {
        columns: gridConfig.columns,
        density,
        gridConfig,
        // Expose full grid config
        // Visual design tokens (wrapped for CSS override support)
        tokens: {
          gapX: `var(--calc-gap-x, ${tokens.gapX})`,
          gapY: `var(--calc-gap-y, ${tokens.gapY})`,
          rowMinHeight: `var(--calc-row-height, ${tokens.rowMinHeight})`,
          paddingX: `var(--calc-padding-x, ${tokens.paddingX})`,
          paddingY: `var(--calc-padding-y, ${tokens.paddingY})`,
          borderRadius: `var(--calc-radius, ${tokens.borderRadius})`,
          heroPadding: `var(--calc-hero-padding, ${tokens.heroPadding})`,
          presentAffixSize: `var(--calc-present-affix-size, ${tokens.presentAffixSize})`,
          presentAffixSizeHero: `var(--calc-present-affix-size-hero, ${tokens.presentAffixSizeHero})`
        },
        // Raw token values (for defining the CSS variables in the first place)
        rawTokens: tokens,
        getSpan: /* @__PURE__ */ __name((fraction, base = gridConfig.columns) => {
          const span = getSpanValue(fraction, base);
          return `col-span-${span}`;
        }, "getSpan"),
        getSpanValue,
        getColCount: /* @__PURE__ */ __name((fraction, base = gridConfig.columns) => {
          return getSpanValue(fraction, base);
        }, "getColCount"),
        // NEW: CSS variable helpers
        getSpanVar: /* @__PURE__ */ __name((type) => {
          const varMap = {
            "full": "--grid-full-span",
            "half": "--grid-half-span",
            "third": "--grid-third-span"
          };
          return `var(${varMap[type] || "--grid-full-span"})`;
        }, "getSpanVar"),
        isParentGrid: gridConfig.type === "parent",
        isOwnGrid: gridConfig.type === "own",
        // NEW: GridAdapter helpers
        isGridMode: gridConfig.type !== "none",
        is3ColGrid: gridConfig.type === "3-col",
        is6ColGrid: gridConfig.type === "6-col",
        gridType: gridConfig.type
      };
      return val;
    }, [density, strategy, explicitGridConfig]);
    return /* @__PURE__ */ (0, import_jsx_runtime51.jsx)(LayoutContext.Provider, { value, children });
  }
  __name(LayoutProvider, "LayoutProvider");
  function useLayout() {
    const context = useContext(LayoutContext);
    if (!context) {
      return {
        columns: 6,
        density: "normal",
        gridConfig: { columns: 6, fullSpan: 6, type: "6-col" },
        tokens: {
          gapX: "1rem",
          gapY: "0.25rem",
          rowMinHeight: "2.75rem",
          paddingX: "1rem",
          paddingY: "0.5rem",
          borderRadius: "0.75rem",
          heroPadding: "1rem 1.5rem",
          presentAffixSize: "0.875rem",
          presentAffixSizeHero: "1rem"
        },
        rawTokens: DENSITY_PRESETS.normal,
        getSpan: /* @__PURE__ */ __name((f, base = 6) => {
          if (f === "full" || !f) return `col-span-${base}`;
          const [num, den] = f.split("/").map(Number);
          return `col-span-${Math.round(base * (num / den))}`;
        }, "getSpan"),
        getSpanValue: /* @__PURE__ */ __name((f, base = 6) => {
          if (f === "full" || !f) return base;
          const [num, den] = f.split("/").map(Number);
          return Math.round(base * (num / den));
        }, "getSpanValue"),
        getColCount: /* @__PURE__ */ __name((f, base = 6) => {
          if (f === "full" || !f) return base;
          const [num, den] = f.split("/").map(Number);
          return Math.round(base * (num / den));
        }, "getColCount"),
        getSpanVar: /* @__PURE__ */ __name((type) => {
          const varMap = {
            "full": "--grid-full-span",
            "half": "--grid-half-span",
            "third": "--grid-third-span"
          };
          return `var(${varMap[type] || "--grid-full-span"})`;
        }, "getSpanVar"),
        isParentGrid: false,
        isOwnGrid: false,
        isGridMode: true,
        is3ColGrid: false,
        is6ColGrid: true,
        gridType: "6-col"
      };
    }
    return context;
  }
  __name(useLayout, "useLayout");
  function useLayoutAdapter() {
    return useLayout();
  }
  __name(useLayoutAdapter, "useLayoutAdapter");

  // public/src/react/components/calc/row/ResultDisplay.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime52 = __toESM(require_jsx_runtime());
  function ResultDisplay({ result }) {
    if (!result?.output) {
      return null;
    }
    const { output, type: typeInfo = {} } = result;
    if (output.raw?.vars) {
      return null;
    }
    if (typeInfo?.isCustomFunction) {
      return LIB.prettyPrintFunction(output.raw);
    }
    if (typeInfo?.isCustomObject) {
      return /* @__PURE__ */ (0, import_jsx_runtime52.jsxs)("details", { onClick: (e) => e.stopPropagation(), children: [
        /* @__PURE__ */ (0, import_jsx_runtime52.jsxs)("summary", { children: [
          Icon_default.plusCircle("inline-block mb-1 mr-1 opacity-75 icon-closed size-3"),
          Icon_default.minusCircle("inline-block mb-1 mr-1 opacity-75 icon-open size-3"),
          "Object[",
          Object.keys(output.raw).length,
          "]"
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime52.jsx)("pre", { className: "inline-block w-full p-2 ml-2 overflow-auto text-xs max-h-[600px]", children: LIB.prettyPrintObject(output.raw) })
      ] });
    }
    if (typeInfo?.isArray) {
      return /* @__PURE__ */ (0, import_jsx_runtime52.jsxs)("details", { onClick: (e) => e.stopPropagation(), children: [
        /* @__PURE__ */ (0, import_jsx_runtime52.jsxs)("summary", { children: [
          Icon_default.plusCircle("inline-block mb-1 mr-1 opacity-75 icon-closed size-3"),
          Icon_default.minusCircle("inline-block mb-1 mr-1 opacity-75 icon-open size-3"),
          "Array[",
          output.raw.length,
          "]"
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime52.jsx)("pre", { className: "inline-block w-full p-2 ml-2 overflow-auto text-xs max-h-[500px]", children: LIB.prettyPrintArray(output.raw) })
      ] });
    }
    return LIB.prettyPrintResultHTML(output.display || output.raw, output.raw);
  }
  __name(ResultDisplay, "ResultDisplay");
  var ResultDisplay_default = ResultDisplay;

  // public/src/react/components/calc/row/EditableInputField.jsx
  init_react_global();
  init_Icon();

  // public/src/react/components/calc/row/inputUtils.js
  var processInputSymbols = /* @__PURE__ */ __name((value, selectionStart) => {
    if (!value.includes("\\") || !window.IC?.api?.processSymbols) {
      return { value, selectionStart, changed: false };
    }
    const processed = window.IC.api.processSymbols(value);
    if (processed !== value) {
      const diff = value.length - processed.length;
      const newSelectionStart = Math.max(0, selectionStart - diff);
      return {
        value: processed,
        selectionStart: newSelectionStart,
        changed: true
      };
    }
    return { value, selectionStart, changed: false };
  }, "processInputSymbols");

  // public/src/react/components/calc/row/shortcutUtils.js
  var WINDOW_MS = 2e3;
  var recentKeys = [];
  var SHORTCUTS = [
    { pattern: "iii", action: "insertBefore" },
    { pattern: "ooo", action: "insertAfter" },
    { pattern: "ddd", action: "deleteRow" },
    { pattern: "ccc", action: "clearLine" },
    { pattern: "DeleteDeleteDelete", action: "deleteEmptyRow" },
    { pattern: "BackspaceBackspaceBackspace", action: "deleteEmptyRow" }
  ];
  var ACTIONS = {
    insertBefore: /* @__PURE__ */ __name((ctx) => {
      const { Action, calcId, rowIndex, cleanedValue } = ctx;
      if (Action.insertRowWithTextCleanup) {
        const newCalc = Action.insertRowWithTextCleanup(calcId, rowIndex, cleanedValue, rowIndex);
        if (Action.setSelectedRow) {
          Action.setSelectedRow(rowIndex, { focus: true, calc: newCalc });
        }
      } else {
        const updatedCalc = Action.updateRowText(calcId, rowIndex, cleanedValue);
        Action.insertRow(updatedCalc, rowIndex, { focus: true });
      }
    }, "insertBefore"),
    insertAfter: /* @__PURE__ */ __name((ctx) => {
      const { Action, calcId, rowIndex, cleanedValue } = ctx;
      if (Action.insertRowWithTextCleanup) {
        const newCalc = Action.insertRowWithTextCleanup(calcId, rowIndex, cleanedValue, rowIndex + 1);
        if (Action.setSelectedRow) {
          Action.setSelectedRow(rowIndex + 1, { focus: true, calc: newCalc });
        }
      } else {
        const updatedCalc = Action.updateRowText(calcId, rowIndex, cleanedValue);
        Action.insertRow(updatedCalc, rowIndex + 1, { focus: true });
      }
    }, "insertAfter"),
    deleteRow: /* @__PURE__ */ __name((ctx) => {
      const { Action, calcId, rowIndex, cleanedValue } = ctx;
      const calc = Action.getCurrentCalc();
      const newLen = (calc?.rows?.length || 1) - 1;
      const nextIndex = newLen > 0 ? Math.min(rowIndex, newLen - 1) : 0;
      if (Action.deleteRowWithTextCleanup) {
        Action.deleteRowWithTextCleanup(calcId, rowIndex, cleanedValue);
      } else {
        const updatedCalc = Action.updateRowText(calcId, rowIndex, cleanedValue);
        Action.deleteRow(updatedCalc, rowIndex, { selectionHint: nextIndex });
      }
      if (Action.setSelectedRow) {
        Action.setSelectedRow(nextIndex, { focus: true });
      }
    }, "deleteRow"),
    clearLine: /* @__PURE__ */ __name((ctx) => {
      const { Action, calcId, rowIndex } = ctx;
      if (Action.updateRowTextFast) {
        Action.updateRowTextFast(calcId, rowIndex, "");
      } else {
        Action.updateRowText(calcId, rowIndex, "");
      }
    }, "clearLine"),
    deleteEmptyRow: /* @__PURE__ */ __name((ctx) => {
      const { Action, rowIndex } = ctx;
      const calc = Action.getCurrentCalc();
      const row = calc?.rows?.[rowIndex];
      if (row && row.text.trim() === "" && calc.rows.length > 1) {
        const newLen = calc.rows.length - 1;
        const nextIndex = newLen > 0 ? Math.min(rowIndex, newLen - 1) : 0;
        Action.deleteRow(calc, rowIndex, { selectionHint: nextIndex });
        if (Action.setSelectedRow) {
          Action.setSelectedRow(nextIndex, { focus: true });
        }
      }
    }, "deleteEmptyRow")
  };
  function recordKey(key, currentValue, context) {
    const now = Date.now();
    const normalizedKey = key === "Delete" || key === "Backspace" ? key : key.toLowerCase();
    recentKeys.push({ key: normalizedKey, time: now });
    recentKeys = recentKeys.filter((k) => now - k.time < WINDOW_MS);
    const sequence = recentKeys.map((k) => k.key).join("");
    for (const shortcut of SHORTCUTS) {
      if (sequence.endsWith(shortcut.pattern)) {
        console.log("[shortcut] MATCH:", shortcut.pattern, "\u2192", shortcut.action);
        if (typeof window !== "undefined" && window.DebugTools?.KeyLogger?.logKey) {
          window.DebugTools.KeyLogger.logKey(shortcut.pattern, "shortcutUtils", shortcut.action, false);
        }
        recentKeys = [];
        const calc = context.Action?.getCurrentCalc?.();
        const storeValue = calc?.rows?.[context.rowIndex]?.text ?? "";
        const textPattern = shortcut.pattern.replace(/Delete|Backspace/g, "");
        let cleanedValue = currentValue;
        if (textPattern) {
          const patternChar = textPattern[0];
          const sourceValue = storeValue.length >= currentValue.length ? storeValue : currentValue;
          let endIdx = sourceValue.length;
          while (endIdx > 0 && sourceValue[endIdx - 1] === patternChar) {
            endIdx--;
          }
          cleanedValue = sourceValue.slice(0, endIdx);
        }
        const calcBefore = context.Action?.getCurrentCalc?.();
        if (calcBefore) {
          const rowsBefore = calcBefore.rows.map((r, i) => `[${i}]: "${r.text}"`).join(", ");
          console.log("[shortcut] BEFORE:", { rowIndex: context.rowIndex, cleanedValue, rows: rowsBefore });
        }
        ACTIONS[shortcut.action]({ ...context, cleanedValue });
        const calcAfter = context.Action?.getCurrentCalc?.();
        if (calcAfter) {
          const rowsAfter = calcAfter.rows.map((r, i) => `[${i}]: "${r.text}"`).join(", ");
          console.log("[shortcut] AFTER:", { rows: rowsAfter });
        }
        return { matched: true, cleanedValue };
      }
    }
    return { matched: false };
  }
  __name(recordKey, "recordKey");
  function processShortcuts(newValue, context) {
    const lastChar = newValue.slice(-1);
    if (!lastChar) return { consumed: false, value: newValue };
    const result = recordKey(lastChar, newValue, context);
    if (result.matched) {
      return { consumed: true, value: result.cleanedValue };
    }
    return { consumed: false, value: newValue };
  }
  __name(processShortcuts, "processShortcuts");
  function processSpecialKey(key, currentValue, context) {
    if (key !== "Delete" && key !== "Backspace") return false;
    const result = recordKey(key, currentValue, context);
    return result.matched;
  }
  __name(processSpecialKey, "processSpecialKey");
  function resetShortcutTracking() {
    recentKeys = [];
  }
  __name(resetShortcutTracking, "resetShortcutTracking");

  // public/src/utils/DebugLogger.js
  var MAX_LOG_BUFFER = 1e4;
  var DEFAULT_CATEGORIES = {
    // From DebugLogger (React/component debugging)
    render: false,
    input: false,
    focus: false,
    cache: false,
    events: false,
    // From Debug (system-level debugging)
    store: false,
    // CalcStore operations
    storage: false,
    // IndexedDB/localStorage
    router: false,
    // URL routing and navigation
    loader: false,
    // CalcLoader operations (currencies, imports)
    stock: false,
    // Stock loading, caching, and unit registration
    parser: false,
    // Parser preprocessing, evaluation paths, symbol resolution
    url: false,
    // UrlManager, url-state
    ui: false,
    // React UI components
    auth: false,
    // Firebase auth operations
    plugins: false,
    // Plugin Registry operations
    perf: false,
    // Performance timing (controlled by perf stats toggle, excluded from ?debug=all)
    // Verbose categories (extra noisy, off by default)
    "store:verbose": false,
    // Per-calc save confirmations
    "offline:verbose": false,
    // Individual IndexedDB writes
    "portal:verbose": false,
    // MultiPortal updates
    "stock:verbose": false,
    // Extra stock details (cache contents, etc.)
    "parser:verbose": false
    // Extra parser details (all transformations)
  };
  var cachedSettings = null;
  var logBuffer = [];
  var originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error
  };
  function interceptConsole() {
    ["log", "warn", "error"].forEach((method) => {
      console[method] = function(...args) {
        const message = args.map(
          (a) => typeof a === "object" ? JSON.stringify(a) : String(a)
        ).join(" ");
        logBuffer.push({ time: Date.now(), type: method, message });
        if (logBuffer.length > MAX_LOG_BUFFER) {
          logBuffer.shift();
        }
        originalConsole[method].apply(console, args);
      };
    });
  }
  __name(interceptConsole, "interceptConsole");
  function getSettings() {
    if (cachedSettings) return cachedSettings;
    cachedSettings = { ...DEFAULT_CATEGORIES };
    return cachedSettings;
  }
  __name(getSettings, "getSettings");
  function saveSettings(settings) {
    cachedSettings = settings;
  }
  __name(saveSettings, "saveSettings");
  var DebugLogger = {
    /** All available log categories */
    categories: Object.keys(DEFAULT_CATEGORIES),
    /**
     * Check if a category is enabled
     * @param {string} category 
     * @returns {boolean}
     */
    isEnabled(category) {
      return getSettings()[category] ?? false;
    },
    /**
     * Log a message if the category is enabled
     * @param {string} category
     * @param  {...any} args
     */
    log(category, ...args) {
      if (!this.isEnabled(category)) return;
      const message = args.map(
        (a) => typeof a === "object" ? JSON.stringify(a) : String(a)
      ).join(" ");
      logBuffer.push({ time: Date.now(), category, message });
      if (logBuffer.length > MAX_LOG_BUFFER) {
        logBuffer.shift();
      }
      if (typeof window !== "undefined" && originalConsole.log) {
        originalConsole.log(`[${category}]`, ...args);
      } else {
        console.log(`[${category}]`, ...args);
      }
    },
    /**
     * Log a warning if the category is enabled
     * @param {string} category
     * @param  {...any} args
     */
    warn(category, ...args) {
      if (!this.isEnabled(category)) return;
      console.warn(`[${category}]`, ...args);
    },
    /**
     * Log an error - ALWAYS shows regardless of category setting
     * @param {string} category
     * @param  {...any} args
     */
    error(category, ...args) {
      console.error(`[${category}]`, ...args);
      const message = args.map(
        (a) => typeof a === "object" ? JSON.stringify(a) : String(a)
      ).join(" ");
      logBuffer.push({ time: Date.now(), category, type: "error", message });
      if (logBuffer.length > MAX_LOG_BUFFER) {
        logBuffer.shift();
      }
    },
    /**
     * Group related logs together
     * @param {string} category
     * @param {string} label
     */
    group(category, label) {
      if (this.isEnabled(category)) {
        console.group(`[${category}] ${label}`);
      }
    },
    /**
     * End a log group
     * @param {string} category
     */
    groupEnd(category) {
      if (this.isEnabled(category)) {
        console.groupEnd();
      }
    },
    /**
     * Start a timer
     * @param {string} category
     * @param {string} label
     */
    time(category, label) {
      if (this.isEnabled(category) || this.isEnabled("perf")) {
        console.time(`[${category}] ${label}`);
      }
    },
    /**
     * End a timer and log the result
     * @param {string} category
     * @param {string} label
     */
    timeEnd(category, label) {
      if (this.isEnabled(category) || this.isEnabled("perf")) {
        console.timeEnd(`[${category}] ${label}`);
      }
    },
    /**
     * Toggle a category on/off
     * @param {string} category 
     * @param {boolean} [enabled] - If not provided, toggles current value
     * @returns {boolean} New state
     */
    toggle(category, enabled) {
      const settings = getSettings();
      const newValue = enabled ?? !settings[category];
      settings[category] = newValue;
      saveSettings(settings);
      return newValue;
    },
    /**
     * Enable all categories
     */
    enableAll() {
      const settings = getSettings();
      for (const cat of this.categories) {
        settings[cat] = true;
      }
      saveSettings(settings);
    },
    /**
     * Disable all categories
     */
    disableAll() {
      const settings = getSettings();
      for (const cat of this.categories) {
        settings[cat] = false;
      }
      saveSettings(settings);
    },
    /**
     * Get all current settings
     * @returns {Record<string, boolean>}
     */
    getSettings() {
      return { ...getSettings() };
    },
    /**
     * Check if any category is enabled
     * @returns {boolean}
     */
    hasAnyEnabled() {
      const settings = getSettings();
      return Object.values(settings).some((v) => v);
    },
    /**
     * Get the current log buffer
     * @returns {Array<{time: number, category: string, message: string}>}
     */
    getLogBuffer() {
      return logBuffer;
    },
    /**
     * Clear the log buffer
     */
    clearLogBuffer() {
      logBuffer = [];
    },
    /**
     * Gets counts for all categories
     * @returns {Object} { category: count }
     */
    getCategoryCounts() {
      const counts = {};
      this.categories.forEach((cat) => counts[cat] = 0);
      const allCats = new Set(this.categories);
      logBuffer.forEach((entry) => {
        if (entry.category) {
          counts[entry.category] = (counts[entry.category] || 0) + 1;
        } else {
          const match = entry.message && entry.message.match(/^\[([^\]]+)\]/);
          if (match) {
            const potentialCat = match[1].toLowerCase();
            if (allCats.has(potentialCat)) {
              counts[potentialCat] = (counts[potentialCat] || 0) + 1;
            }
          }
        }
      });
      return counts;
    },
    /**
     * Filters log buffer and returns formatted string
     * @param {Record<string, boolean>} [enabledCategories] - Optional filter: only include logs from enabled categories
     * @returns {string}
     */
    getLogText(enabledCategories) {
      let filtered = logBuffer;
      if (enabledCategories) {
        const enabledSet = new Set(
          Object.entries(enabledCategories).filter(([_, enabled]) => enabled).map(([cat]) => cat)
        );
        const allCats = new Set(this.categories);
        filtered = logBuffer.filter((entry) => {
          if (entry.category) {
            return enabledSet.has(entry.category);
          }
          const match = entry.message && entry.message.match(/^\[([^\]]+)\]/);
          if (match) {
            const potentialCat = match[1].toLowerCase();
            if (allCats.has(potentialCat)) {
              return enabledSet.has(potentialCat);
            }
          }
          if (enabledSet.size === 0) return true;
          if (entry.type === "error" || entry.type === "warn") return true;
          return false;
        });
      }
      return filtered.map((entry) => {
        const d = new Date(entry.time);
        const time = d.toLocaleTimeString("en-US", { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
        const tag = entry.category || (entry.type !== "log" ? entry.type : "");
        return tag ? `${time} [${tag}] ${entry.message}` : `${time} ${entry.message}`;
      }).join("\n");
    }
  };
  function logBuildAge() {
    const script = document.querySelector('script[src*="instacalc-core.min.js?v="]');
    if (script) {
      const match = script.src.match(/\?v=(\d+)/);
      if (match) {
        const buildTime = parseInt(match[1]) * 1e3;
        const now = Date.now();
        const diff = now - buildTime;
        const seconds = Math.floor(diff / 1e3);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        let age;
        if (hours > 0) {
          age = `${hours}h ${minutes % 60}m ago`;
        } else if (minutes > 0) {
          age = `${minutes}m ${seconds % 60}s ago`;
        } else {
          age = `${seconds}s ago`;
        }
        const buildDate = new Date(buildTime).toLocaleTimeString("en-US", { hour12: false });
        DebugLogger.log("Build", `Age: ${age} (built at ${buildDate})`);
      }
    }
  }
  __name(logBuildAge, "logBuildAge");
  if (typeof window !== "undefined") {
    window.DebugLogger = DebugLogger;
    window.Debug = DebugLogger;
    try {
      const url = new URL(window.location.href);
      const debugParam = url.searchParams.get("debug");
      if (debugParam !== null) {
        interceptConsole();
        if (debugParam === "" || debugParam === "all") {
          const settings = getSettings();
          for (const cat of DebugLogger.categories) {
            if (cat !== "perf") {
              settings[cat] = true;
            }
          }
          saveSettings(settings);
          const enabledCats = Object.entries(settings).filter(([k, v]) => v).map(([k]) => k);
          console.log(`[Debug] Enabled ${enabledCats.length} categories:`, enabledCats.includes("stock") ? "\u2705 stock included" : "\u274C stock MISSING");
          console.log("[Debug] All categories:", DebugLogger.categories);
        } else if (debugParam === "none") {
          DebugLogger.disableAll();
        } else {
          const categories = debugParam.split(",").map((s) => s.trim().toLowerCase());
          categories.forEach((cat) => {
            if (DebugLogger.categories.includes(cat)) {
              DebugLogger.toggle(cat, true);
            }
          });
          console.log(`[Debug] Enabled categories: ${categories.join(", ")}`);
        }
        window.IC_DEBUG_NAMESPACES = DebugLogger.categories.filter((c) => DebugLogger.isEnabled(c));
        window.IC_UI_DEBUG = DebugLogger.isEnabled("ui");
        if (window.perfMetrics) {
          window.perfMetrics.enable();
          console.log("[Debug] Performance metrics enabled");
        }
      }
    } catch (e) {
    }
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", logBuildAge);
    } else {
      logBuildAge();
    }
    Promise.resolve().then(() => __toESM(require_DebugToolsStub())).catch(() => {
    });
    window.perfStats = function() {
      if (!window.perfMetrics) {
        console.log("Performance metrics not available. Enable with ?debug");
        return;
      }
      if (!window.perfMetrics.enabled) {
        console.log("Performance metrics disabled. Enable with ?debug");
        return;
      }
      console.log(window.perfMetrics.format());
    };
  }

  // public/src/react/components/calc/row/EditableInputField.jsx
  var import_jsx_runtime53 = __toESM(require_jsx_runtime());
  var { useRef: useRef23, useLayoutEffect: useLayoutEffect5, useState: useState32, useEffect: useEffect33 } = react_global_default;
  var defaultRowClassNames = {
    rowInput: "px-1 py-[3px] border border-[var(--row-border)] text-color-default rounded text-[87.5%] lg:text-[100%] outline-none focus:border-accent",
    rowTextarea: "w-full h-48 px-1 py-[3px] pr-6 border border-[var(--row-border)] text-color-default rounded text-[87.5%] lg:text-[100%] outline-none focus:border-accent"
  };
  var EditableInputField = (
    /** @type {React.MemoExoticComponent<(props: EditableInputFieldProps) => React.ReactElement>} */
    react_global_default.memo(/* @__PURE__ */ __name(function EditableInputField2(props) {
      const {
        rowData,
        index: index2,
        inputRef,
        handlers,
        onFocus,
        onBlur,
        toggleExpanded,
        rowClassName,
        rowStyle,
        inputClassNameOverride,
        textareaClassNameOverride,
        wrapperClassName,
        strategy,
        calc,
        Action
      } = (
        /** @type {EditableInputFieldProps} */
        props
      );
      const propsValue = rowData.value ?? "";
      const [localValue, setLocalValue] = useState32(propsValue);
      useEffect33(() => {
        setLocalValue(propsValue);
      }, [propsValue]);
      const value = localValue;
      if (rowData.isSelected) {
        DebugLogger.log("render", "EditableInputField render selected:", index2, !!handlers.onKeyDown);
      }
      const isCompactDensity = strategy?.density === "compact";
      const changeInput = /* @__PURE__ */ __name((direction = 1) => {
        const newValue = window.IC.api.increment(localValue, direction);
        setLocalValue(newValue);
        handlers.inputChanged({ target: { value: newValue } });
      }, "changeInput");
      const inputClassName = react_global_default.useMemo(() => cn(
        isCompactDensity ? "w-full" : rowData.hasParameterRow && rowData.isCollapsed ? "configField w-full" : "w-full",
        defaultRowClassNames.rowInput,
        rowData.hasParameterRow && rowData.isCollapsed && "focus:border-[var(--row-border)]",
        rowData.showExpandButton && "pr-6",
        rowData.numberCanBeIncremented && "lg:pr-7",
        rowData.isSelected && "bg-theme-calc-row-selected-bg",
        rowClassName,
        inputClassNameOverride
      ), [isCompactDensity, rowData.hasParameterRow, rowData.isCollapsed, rowData.showExpandButton, rowData.numberCanBeIncremented, rowData.isSelected, rowClassName, inputClassNameOverride]);
      const textareaClassName = react_global_default.useMemo(() => cn(
        defaultRowClassNames.rowTextarea,
        rowData.isSelected && "bg-theme-calc-row-selected-bg",
        rowClassName,
        textareaClassNameOverride
      ), [rowData.isSelected, rowClassName, textareaClassNameOverride]);
      const inputStyle = react_global_default.useMemo(() => rowStyle || {}, [rowStyle]);
      const cursorPositionRef = useRef23({ start: null, end: null });
      const isComposingRef = useRef23(false);
      const updateSelectionSnapshot = /* @__PURE__ */ __name((target) => {
        if (!target) return;
        const start = typeof target.selectionStart === "number" ? target.selectionStart : null;
        const end = typeof target.selectionEnd === "number" ? target.selectionEnd : start;
        if (start === null) return;
        cursorPositionRef.current = { start, end: end ?? start };
      }, "updateSelectionSnapshot");
      useLayoutEffect5(() => {
        const input = inputRef.current;
        if (!input) return;
        const selection = cursorPositionRef.current;
        if (selection.start === null) return;
        if (document.activeElement !== input) return;
        if (isComposingRef.current) return;
        const max2 = input.value.length;
        const start = Math.min(selection.start, max2);
        const end = Math.min(selection.end ?? selection.start, max2);
        if (input.selectionStart === start && input.selectionEnd === end) return;
        input.setSelectionRange(start, end);
      }, [value, rowData.isExpanded, inputRef]);
      const handleInput = /* @__PURE__ */ __name((e) => {
        let val = e.target.value;
        let selectionStart = typeof e.target.selectionStart === "number" ? e.target.selectionStart : val.length;
        let selectionEnd = typeof e.target.selectionEnd === "number" ? e.target.selectionEnd : selectionStart;
        if (e.nativeEvent?.isComposing || isComposingRef.current) {
          cursorPositionRef.current = { start: selectionStart, end: selectionEnd };
          setLocalValue(val);
          return;
        }
        const result = processInputSymbols(val, selectionStart);
        if (result.changed) {
          val = result.value;
          selectionStart = result.selectionStart;
          selectionEnd = selectionStart;
          e.target.value = val;
          e.target.setSelectionRange(selectionStart, selectionStart);
        }
        cursorPositionRef.current = { start: selectionStart, end: selectionEnd };
        setLocalValue(val);
        if (calc && Action) {
          queueMicrotask(() => {
            const shortcutResult = processShortcuts(val, {
              Action,
              calcId: calc.id,
              rowIndex: index2
            });
            if (shortcutResult.consumed) {
              setLocalValue(shortcutResult.value);
              return;
            }
            handlers.inputChanged({ target: { value: val } });
          });
        } else {
          handlers.inputChanged({ target: { value: val } });
        }
      }, "handleInput");
      const handleBlur = /* @__PURE__ */ __name((e) => {
        resetShortcutTracking();
        isComposingRef.current = false;
        if (onBlur) onBlur(e);
      }, "handleBlur");
      const handleClick = /* @__PURE__ */ __name((e) => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
        updateSelectionSnapshot(e.target);
        if (onFocus) {
          onFocus(e);
        }
      }, "handleClick");
      const handleSelect = /* @__PURE__ */ __name((e) => {
        updateSelectionSnapshot(e.target);
      }, "handleSelect");
      const handleCompositionStart = /* @__PURE__ */ __name(() => {
        isComposingRef.current = true;
      }, "handleCompositionStart");
      const handleCompositionEnd = /* @__PURE__ */ __name((e) => {
        isComposingRef.current = false;
        updateSelectionSnapshot(e.target);
      }, "handleCompositionEnd");
      const handleKeyDown = /* @__PURE__ */ __name((e) => {
        if (calc && Action && (e.key === "Delete" || e.key === "Backspace")) {
          const matched = processSpecialKey(e.key, value, {
            Action,
            calcId: calc.id,
            rowIndex: index2
          });
          if (matched) {
            e.preventDefault();
            return;
          }
        }
        if (handlers.onKeyDown) {
          handlers.onKeyDown(e);
        }
      }, "handleKeyDown");
      const wrapperClassNameValue = react_global_default.useMemo(() => cn(
        isCompactDensity ? "relative ml-0.5" : rowData.isCollapsed && rowData.hasParameterRow ? "relative flex ml-0.5 w-2/5 md:w-1/3 flex-shrink-0" : rowData.isCollapsed ? "contents" : "relative ml-0.5",
        wrapperClassName
      ), [isCompactDensity, rowData.isCollapsed, rowData.hasParameterRow, wrapperClassName]);
      return /* @__PURE__ */ (0, import_jsx_runtime53.jsxs)("div", { className: wrapperClassNameValue, children: [
        !rowData.isExpanded && /* @__PURE__ */ (0, import_jsx_runtime53.jsx)(
          "input",
          {
            ref: inputRef,
            value,
            type: "text",
            autoComplete: "off",
            autoCorrect: "off",
            autoCapitalize: "off",
            spellCheck: false,
            placeholder: rowData.placeholder,
            onPaste: handlers.onPaste,
            onInput: handleInput,
            onKeyDown: handleKeyDown,
            onSelect: handleSelect,
            onKeyUp: handleSelect,
            onMouseUp: handleSelect,
            onClick: handleClick,
            onCompositionStart: handleCompositionStart,
            onCompositionEnd: handleCompositionEnd,
            onFocus,
            onBlur: handleBlur,
            className: inputClassName,
            style: inputStyle,
            "aria-label": `Input row ${index2 + 1}`,
            "data-calc-input": true,
            "data-row-index": index2,
            "data-hj-whitelist": true
          }
        ),
        rowData.showExpandButton && /* @__PURE__ */ (0, import_jsx_runtime53.jsx)("button", { onClick: toggleExpanded, className: "!absolute top-1/2 -translate-y-1/2 right-0 p-1.5 md:p-2 opacity-50", "data-testid": "row-expand-button", children: Icon_default.chevronDoubleDown("w-4 h-4") }),
        !rowData.isCollapsed && rowData.uiMeta && !rowData.showExpandButton && !rowData.isExpanded && /* @__PURE__ */ (0, import_jsx_runtime53.jsx)(
          "button",
          {
            onClick: handlers.toggleHidden,
            className: "!absolute top-1/2 -translate-y-1/2 right-0 p-1.5 md:p-2 opacity-40 hover:opacity-100 transition-opacity cursor-pointer",
            children: Icon_default.chevronDoubleRight("w-4 h-4")
          }
        ),
        rowData.isExpanded && /* @__PURE__ */ (0, import_jsx_runtime53.jsxs)("div", { className: "relative top-0 bottom-0 left-0 right-0 z-50 flex items-center justify-center mb-1 bg-opacity-50", children: [
          /* @__PURE__ */ (0, import_jsx_runtime53.jsx)(
            "textarea",
            {
              ref: inputRef,
              value,
              onInput: handleInput,
              onKeyDown: handlers.onKeyDown,
              onSelect: handleSelect,
              onKeyUp: handleSelect,
              onMouseUp: handleSelect,
              onClick: handleClick,
              onCompositionStart: handleCompositionStart,
              onCompositionEnd: handleCompositionEnd,
              onFocus,
              onBlur: handleBlur,
              className: textareaClassName,
              style: inputStyle,
              "aria-label": `Expanded input row ${index2 + 1}`,
              "data-calc-input": true
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime53.jsx)("button", { className: "!absolute top-1/2 -translate-y-1/2 right-0 p-1.5 md:p-2", onClick: toggleExpanded, "data-testid": "row-collapse-button", children: Icon_default.chevronDoubleUp("w-4 h-4") })
        ] }),
        rowData.numberCanBeIncremented && /* @__PURE__ */ (0, import_jsx_runtime53.jsxs)("div", { className: "pointer-required:flex flex-col items-center justify-center opacity-50 absolute right-0.5 top-1/2 -translate-y-1/2 z-10 gap-1", children: [
          /* @__PURE__ */ (0, import_jsx_runtime53.jsx)("button", { tabIndex: -1, className: "p-0 hover:opacity-100 flex items-center justify-center h-3", onClick: () => changeInput(1), children: Icon_default.chevronUp("w-3.5 h-3.5") }),
          /* @__PURE__ */ (0, import_jsx_runtime53.jsx)("button", { tabIndex: -1, className: "p-0 hover:opacity-100 flex items-center justify-center h-3", onClick: () => changeInput(-1), children: Icon_default.chevronDown("w-3.5 h-3.5") })
        ] })
      ] });
    }, "EditableInputField"))
  );
  var EditableInputField_default = EditableInputField;

  // public/src/react/components/calc/row/RowNumber.jsx
  init_react_global();
  init_Icon();

  // public/src/react/components/common/HoverMenu.jsx
  init_react_global();
  var import_jsx_runtime54 = __toESM(require_jsx_runtime());
  function HoverMenu({
    trigger,
    children,
    align = "start",
    disabled = false,
    dragHandle
  }) {
    const [isOpen, setIsOpen] = react_global_default.useState(false);
    const timeoutRef = react_global_default.useRef(null);
    react_global_default.useEffect(() => {
      if (disabled && isOpen) {
        setIsOpen(false);
      }
    }, [disabled, isOpen]);
    const open = /* @__PURE__ */ __name(() => {
      if (disabled || typeof window !== "undefined" && window._isDragging) return;
      clearTimeout(timeoutRef.current);
      setIsOpen(true);
    }, "open");
    const close = /* @__PURE__ */ __name(() => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setIsOpen(false), 120);
    }, "close");
    const handleMouseDown = /* @__PURE__ */ __name((e) => {
      if (e.target.closest('[role="menuitem"]')) return;
      clearTimeout(timeoutRef.current);
      setIsOpen(false);
    }, "handleMouseDown");
    react_global_default.useEffect(() => {
      return () => clearTimeout(timeoutRef.current);
    }, []);
    return /* @__PURE__ */ (0, import_jsx_runtime54.jsxs)(
      "div",
      {
        className: "relative",
        onMouseEnter: disabled ? void 0 : open,
        onMouseLeave: close,
        onMouseMove: disabled ? close : void 0,
        onMouseDown: handleMouseDown,
        ...dragHandle ? { "data-drag-handle": dragHandle } : {},
        children: [
          trigger,
          isOpen && /* @__PURE__ */ (0, import_jsx_runtime54.jsx)(
            "div",
            {
              className: cn(
                "absolute top-full z-[400] menu-surface hs-dropdown-menu min-w-[14rem]",
                "animate-in fade-in zoom-in-95 duration-100",
                align === "start" ? "left-0" : "right-0"
              ),
              children: /* @__PURE__ */ (0, import_jsx_runtime54.jsx)("div", { role: "menu", className: "outline-none", children })
            }
          )
        ]
      }
    );
  }
  __name(HoverMenu, "HoverMenu");
  HoverMenu.Item = /* @__PURE__ */ __name(function HoverMenuItem({ children, onClick, className = "", isDisabled = false, disabled = false, ...rest }) {
    const isActuallyDisabled = isDisabled || disabled;
    const handleClick = /* @__PURE__ */ __name((e) => {
      if (isActuallyDisabled) return;
      if (onClick) {
        onClick(e);
      }
    }, "handleClick");
    return /* @__PURE__ */ (0, import_jsx_runtime54.jsx)(
      "div",
      {
        role: "menuitem",
        onClick: handleClick,
        "aria-disabled": isActuallyDisabled,
        className: cn(
          "menu-item cursor-pointer select-none outline-none transition-colors",
          "hover:bg-[color-mix(in_srgb,var(--theme-accent,var(--accent))_14%,transparent)]",
          isActuallyDisabled && "pointer-events-none opacity-50",
          className
        ),
        ...rest,
        children
      }
    );
  }, "HoverMenuItem");

  // public/src/react/components/calc/row/RowNumber.jsx
  var import_jsx_runtime55 = (
    // @ts-ignore - custom JSX element
    __toESM(require_jsx_runtime())
  );
  var { memo } = react_global_default;
  function RowNumber({
    index: index2,
    isCollapsed,
    minimal = false,
    insertRow,
    insertRowAfter,
    deleteRow,
    Action,
    calc,
    viewMode,
    isSelected,
    isAnyRowDragging
  }) {
    const rowNumber = "R" + (index2 + 1);
    const rowTitle = rowNumber;
    const numberHtmlId = Action?.name("row-target-" + index2 + "-", calc);
    if (minimal) {
      return /* @__PURE__ */ (0, import_jsx_runtime55.jsx)(
        "number",
        {
          id: numberHtmlId,
          "data-testid": "calc-row-number",
          className: "select-none align-middle pr-1 text-[68.75%] md:pr-2 w-4 md:w-5 opacity-20 cursor-default text-theme-calc-row-number",
          children: rowTitle
        }
      );
    }
    return /* @__PURE__ */ (0, import_jsx_runtime55.jsxs)(
      HoverMenu,
      {
        trigger: (
          // @ts-ignore - custom JSX element
          /* @__PURE__ */ (0, import_jsx_runtime55.jsx)(
            "number",
            {
              id: numberHtmlId,
              "data-testid": "calc-row-number",
              "data-react-drag-handle": "row",
              className: cn(
                "select-none align-middle pr-1 text-[68.75%] md:pr-2 w-4 md:w-5",
                "text-theme-calc-row-number",
                "hover-text-color-active transition-opacity duration-200",
                "cursor-grab active:cursor-grabbing",
                isSelected ? "opacity-100" : "opacity-60"
              ),
              children: rowTitle
            }
          )
        ),
        align: "start",
        disabled: isAnyRowDragging,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime55.jsxs)(HoverMenu.Item, { onClick: insertRow, children: [
            Icon_default.insertAbove("size-4"),
            "Insert row above ",
            rowNumber
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime55.jsxs)(HoverMenu.Item, { onClick: insertRowAfter, children: [
            Icon_default.insertBelow("size-4"),
            "Insert row below ",
            rowNumber
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime55.jsxs)(HoverMenu.Item, { onClick: () => Action?.toggleRowVisibility(calc?.id, index2), children: [
            isCollapsed ? Icon_default.chevronLeft("size-4") : Icon_default.chevronRight("size-4"),
            isCollapsed ? "Expand" : "Collapse",
            " ",
            rowNumber
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime55.jsxs)(HoverMenu.Item, { onClick: deleteRow, children: [
            Icon_default.delete("size-4"),
            "Delete ",
            rowNumber
          ] })
        ]
      }
    );
  }
  __name(RowNumber, "RowNumber");
  var RowNumber_default = memo(RowNumber);

  // public/src/react/components/calc/row/RowInputArea.jsx
  init_react_global();
  var import_jsx_runtime56 = __toESM(require_jsx_runtime());
  function RowInputArea({ rowData, inputField, handlers, className, viewMode }) {
    const { isCollapsed, collapsedLabel, isVeryLastRow, isSelected, logStr, isExpanded, index: index2 } = rowData;
    const { toggleHidden, insertRowAfter, formatLogStr: formatLogStr2 } = handlers;
    const showLabel = isCollapsed;
    return /* @__PURE__ */ (0, import_jsx_runtime56.jsxs)("c1", { className: cn(className), "data-testid": "calc-column-input", children: [
      showLabel ? /* @__PURE__ */ (0, import_jsx_runtime56.jsx)("div", { className: "relative ml-0.5 w-full flex justify-end", children: /* @__PURE__ */ (0, import_jsx_runtime56.jsx)("input", { type: "text", readOnly: true, onClick: toggleHidden, value: collapsedLabel || "\xA0", className: "px-1 py-[3px] border border-transparent text-right text-[87.5%] lg:text-[100%] cursor-pointer bg-transparent outline-none", style: { fieldSizing: "content" }, tabIndex: -1 }) }) : inputField,
      isCollapsed ? "" : /* @__PURE__ */ (0, import_jsx_runtime56.jsxs)(
        "rowcontext",
        {
          className: "hidden sm:flex absolute w-full items-center top-full left-0 right-0 -mt-[1px] z-10",
          style: {
            opacity: 0,
            transition: "opacity 200ms ease-in-out"
          },
          onMouseEnter: (e) => e.currentTarget.style.opacity = 1,
          onMouseLeave: (e) => e.currentTarget.style.opacity = 0,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime56.jsx)("div", { className: "flex-1 h-[2px] bg-gradient-to-r from-transparent via-[var(--accent)] to-[var(--accent)]" }),
            /* @__PURE__ */ (0, import_jsx_runtime56.jsx)(
              "button",
              {
                onClick: insertRowAfter,
                className: "flex items-center justify-center w-5 h-5 -my-2 mx-1 rounded-full bg-[var(--accent)] text-white hover:scale-110 transition-transform shadow-sm",
                "aria-label": "Insert row",
                children: /* @__PURE__ */ (0, import_jsx_runtime56.jsx)("span", { className: "text-sm leading-none font-light", children: "+" })
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime56.jsx)("div", { className: "flex-1 h-[2px] bg-gradient-to-l from-transparent via-[var(--accent)] to-[var(--accent)]" })
          ]
        }
      ),
      logStr && !isCollapsed && isSelected ? /* @__PURE__ */ (0, import_jsx_runtime56.jsx)(
        "log",
        {
          className: cn(
            isExpanded ? "block" : "absolute left-0 top-full mt-1",
            "z-40 px-1 py-0.5 text-xs rounded-md border",
            // Color, background, and border from CSS variables
            "text-theme-calc-row-log-text bg-theme-calc-row-log-bg border-[var(--calc-row-log-border-color)]",
            // Hidden by default, fade in on row hover (tooltip behavior - only on selected row)
            "invisible opacity-0 transition-opacity duration-300",
            "group-hover:visible group-hover:opacity-100",
            // Margin for long logs
            logStr.length > 60 && "mx-5"
          ),
          "aria-hidden": "false",
          children: formatLogStr2(logStr)
        }
      ) : ""
    ] });
  }
  __name(RowInputArea, "RowInputArea");
  var RowInputArea_default = react_global_default.memo(RowInputArea);

  // public/src/react/components/calc/row/RowResultArea.jsx
  init_react_global();
  var import_jsx_runtime57 = __toESM(require_jsx_runtime());
  function RowResultArea({ rowData, handlers, className, innerClassName, typeClassName, commentClassName, collapseButtonClassName }) {
    const { UIResult: UIResult2, regularResult, hideType, type, customType, title, uiMeta, extraUIComment, comment, isCollapsed, isSelected, result, processed, collapsedLabel, presentation } = rowData;
    const { toggleHidden, formattedComment } = handlers;
    return /* @__PURE__ */ (0, import_jsx_runtime57.jsxs)("c2", { className: cn(className), "data-testid": "calc-column-result", children: [
      /* @__PURE__ */ (0, import_jsx_runtime57.jsxs)("div", { className: cn(innerClassName), children: [
        UIResult2 ? UIResult2 : regularResult,
        !hideType && type && /* @__PURE__ */ (0, import_jsx_runtime57.jsx)("type", { className: cn(customType, typeClassName), title, onClick: toggleHidden, children: type }),
        uiMeta && extraUIComment && /* @__PURE__ */ (0, import_jsx_runtime57.jsx)("comment", { className: cn(commentClassName, "break-anywhere"), onClick: toggleHidden, title, "data-ic-row-comment": true, children: formattedComment(extraUIComment) }),
        !uiMeta && comment && /* @__PURE__ */ (0, import_jsx_runtime57.jsx)("comment", { className: cn(commentClassName, "break-anywhere"), onClick: toggleHidden, title, "data-ic-row-comment": true, children: formattedComment(comment) })
      ] }),
      processed && !isCollapsed && isSelected && result?.debug?.message && /* @__PURE__ */ (0, import_jsx_runtime57.jsx)(
        "parsed",
        {
          className: cn(
            "absolute top-full mt-1 z-50 px-1 py-0.5 left-0 text-xs rounded-md border pointer-events-none whitespace-pre-wrap",
            // Color, background, and border from CSS variables
            "text-theme-calc-row-log-text bg-theme-calc-row-log-bg border-[var(--calc-row-log-border-color)]",
            // Hidden by default, fade in on row hover (tooltip behavior - only on selected row)
            "invisible opacity-0 transition-opacity duration-300",
            "group-hover:visible group-hover:opacity-100"
          ),
          "aria-hidden": "false",
          children: result.debug.message
        }
      )
    ] });
  }
  __name(RowResultArea, "RowResultArea");
  var RowResultArea_default = react_global_default.memo(RowResultArea);

  // public/src/react/components/calc/row/RegularResult.jsx
  init_react_global();
  var import_jsx_runtime58 = __toESM(require_jsx_runtime());
  function RegularResult({ rowData, result, inputField, finalResultDisplay, resultClass, extraUIComment, handlers, uiHelpers }) {
    if (rowData.isCollapsed && rowData.uiMeta) {
      return uiHelpers.renderUIControl(rowData.uiMeta);
    }
    if (rowData.isCollapsed && rowData.hasParameterRow) {
      return inputField;
    }
    if (hasResultContent(result)) {
      const hasComment3 = !!(rowData.uiMeta && extraUIComment) || !!(!rowData.uiMeta && rowData.comment);
      const resultMaxWidth = hasComment3 ? "max-w-full" : "max-w-[100%]";
      return /* @__PURE__ */ (0, import_jsx_runtime58.jsx)(
        "result",
        {
          className: cn(
            resultClass,
            "inline-block whitespace-normal cursor-pointer shrink-0 overflow-x-auto no-scrollbar",
            resultMaxWidth
          ),
          onClick: handlers.toggleHidden,
          title: rowData.title,
          children: finalResultDisplay
        }
      );
    }
    return null;
  }
  __name(RegularResult, "RegularResult");
  var RegularResult_default = react_global_default.memo(RegularResult);

  // public/src/react/components/calc/row/extractExtraUIComment.js
  function extractExtraUIComment(rowData, result) {
    if (!rowData) return null;
    if (rowData.uiMeta && typeof result?.ui?.comment === "string" && result.ui.comment.length) {
      return result.ui.comment;
    }
    const rawComment = result?.comment?.original;
    if (rowData.uiMeta && typeof rawComment === "string" && rawComment.trim().startsWith("{")) {
      const extractLeadingJSONObject = /* @__PURE__ */ __name((s) => {
        let i = s.indexOf("{");
        if (i < 0) return null;
        let depth = 0, inStr = false, esc = false;
        for (let j = i; j < s.length; j++) {
          const ch = s[j];
          if (inStr) {
            if (esc) {
              esc = false;
              continue;
            }
            if (ch === "\\") {
              esc = true;
              continue;
            }
            if (ch === '"') {
              inStr = false;
            }
            continue;
          }
          if (ch === '"') {
            inStr = true;
            continue;
          }
          if (ch === "{") depth++;
          else if (ch === "}") {
            depth--;
            if (depth === 0) return { start: i, end: j };
          }
        }
        return null;
      }, "extractLeadingJSONObject");
      const pos = extractLeadingJSONObject(rawComment.trim());
      if (pos) {
        const after = rawComment.trim().slice(pos.end + 1).trim();
        return after || null;
      }
    }
    return null;
  }
  __name(extractExtraUIComment, "extractExtraUIComment");
  var extractExtraUIComment_default = extractExtraUIComment;

  // public/src/react/components/calc/row/useRowHandlers.js
  init_react_global();
  var { useState: useState33, useCallback: useCallback19 } = react_global_default;
  function useRowHandlers(calc, index2, Action, inputRef, hasRowParameterState, updateVariableState, onSelectRow, focusRow, forceRender, insertRowAfter, strategy) {
    const [lastkeypress, setLastkeypress] = useState33("");
    const { openPasteLinesModal } = useModalManager();
    const onPaste = useCallback19((event) => {
      const clipboardData = event.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData("text").trim();
      if (pastedText.includes("\n")) {
        event.preventDefault();
        const lines = pastedText.split("\n");
        const callback = /* @__PURE__ */ __name(() => {
          Action.pasteTextLines(calc, index2, lines);
        }, "callback");
        const rowDetails = lines.map((line, i) => ({
          rowLabel: "R" + (index2 + i + 1).toString(),
          currentValue: calc.rows[index2 + i]?.text || "",
          newValue: line
        }));
        openPasteLinesModal({
          title: `Paste ${lines.length} rows?`,
          rows: rowDetails,
          onConfirm: callback
        });
      }
    }, [calc, index2, Action, openPasteLinesModal]);
    const onFocus = useCallback19((event) => {
    }, []);
    const onBlur = useCallback19((event) => {
      if (Action.flushPendingChanges) {
        Action.flushPendingChanges();
      }
    }, [Action]);
    const toggleHidden = useCallback19((event) => {
      Action.toggleRowVisibility(calc?.id, index2);
      updateVariableState();
      forceRender((prev) => prev + 1);
    }, [calc?.id, index2, Action, updateVariableState, forceRender]);
    const inputChanged = useCallback19((event) => {
      DebugLogger.log("perf", `[1/5] INPUT row=${index2}`);
      const ctx = window.perfBreakdown?.start?.("keystroke");
      const perfStart2 = window.perfMetrics?.startKeypress();
      const __tDomEvent = window.__domInputTime || performance.now();
      const __tReactSyncEnd = window.__reactSyncEnd || __tDomEvent;
      const __tRafFired = performance.now();
      window.__domInputTime = null;
      window.__reactSyncEnd = null;
      window.perfBreakdown?.mark?.(ctx, "handler-start", "react");
      var newtext = event.target.value;
      const isHidden3 = calc.rows[index2]?.hidden;
      if (hasRowParameterState && isHidden3) {
        let currentText = calc.rows[index2].text;
        newtext = window.IC.api.buildParameterText(currentText, event.target.value);
      }
      window.perfBreakdown?.mark?.(ctx, "text-processed", "app");
      const calcId = calc?.id;
      if (Action.updateRowTextFast && calcId) {
        Action.updateRowTextFast(calcId, index2, newtext);
      } else {
        Action.updateRowText(calcId, index2, newtext, { immediate: true });
      }
      const __tAfterEngine = performance.now();
      window.perfBreakdown?.mark?.(ctx, "store-updated", "engine");
      const reactSyncUs = ((__tReactSyncEnd - __tDomEvent) * 1e3).toFixed(0);
      const zeroLagRafUs = ((__tRafFired - __tReactSyncEnd) * 1e3).toFixed(0);
      const engineUs = ((__tAfterEngine - __tRafFired) * 1e3).toFixed(0);
      const commitUs = ((__tAfterEngine - __tDomEvent) * 1e3).toFixed(0);
      DebugLogger.log("perf", `[TIMING] react_sync:${reactSyncUs}\u03BCs zero_lag_raf:${zeroLagRafUs}\u03BCs engine:${engineUs}\u03BCs | commit:${commitUs}\u03BCs`);
      if (perfStart2 !== null) {
        window.perfMetrics?.recordPhase("commit", perfStart2);
      }
      const reactSyncTime = __tReactSyncEnd - __tDomEvent;
      const zeroLagRafTime = __tRafFired - __tReactSyncEnd;
      const engineTime = __tAfterEngine - __tRafFired;
      requestAnimationFrame(() => {
        const __tVisible = performance.now();
        window.perfBreakdown?.mark?.(ctx, "visible", "browser");
        window.perfMetrics?.recordPhase("paint", perfStart2);
        const vsyncWait = __tVisible - __tAfterEngine;
        const asyncTotal = __tVisible - __tDomEvent;
        DebugLogger.log("perf", `[TIMING] vsync:${vsyncWait.toFixed(2)}ms | total:${asyncTotal.toFixed(2)}ms`);
        window.perfMetrics?.recordBreakdown?.({
          react_sync: reactSyncTime,
          zero_lag_raf: zeroLagRafTime,
          engine: engineTime,
          sync_total: reactSyncTime + zeroLagRafTime + engineTime,
          vsync_wait: vsyncWait,
          async_total: asyncTotal
        });
        window.perfBreakdown?.end?.(ctx);
      });
      if (event.charCode) {
        setLastkeypress(event.charCode);
      }
    }, [calc, index2, Action, hasRowParameterState]);
    const onKeyDown = useCallback19((event) => {
      const rows = calc.rows;
      let change = null;
      switch (event.key) {
        case "ArrowDown":
          return;
        case "Enter":
          DebugLogger.log("input", "useRowHandlers: Enter pressed", index2, calc.rows.length, strategy?.name);
          change = 1;
          if (index2 === calc.rows.length - 1 && strategy?.autoInsertRows) {
            DebugLogger.log("input", "useRowHandlers: Triggering insertRowAfter");
            event.preventDefault();
            event.stopPropagation();
            if (insertRowAfter) {
              insertRowAfter();
            }
            return;
          }
          break;
        case "ArrowUp":
          return;
        default:
          return;
      }
    }, [calc.rows, index2, strategy, insertRowAfter]);
    return react_global_default.useMemo(() => ({
      onPaste,
      onFocus,
      onBlur,
      toggleHidden,
      inputChanged,
      onKeyDown,
      lastkeypress
    }), [onPaste, onFocus, onBlur, toggleHidden, inputChanged, onKeyDown, lastkeypress]);
  }
  __name(useRowHandlers, "useRowHandlers");
  var useRowHandlers_default = useRowHandlers;

  // public/src/react/components/calc/row/useUIHelpers.jsx
  init_react_global();

  // public/src/react/components/common/UIControls.jsx
  init_react_global();
  var import_jsx_runtime59 = __toESM(require_jsx_runtime());
  var { useState: useState34, useEffect: useEffect34, useCallback: useCallback20 } = react_global_default;
  var defaultControlClassNames = {
    sliderWrapper: "flex items-center gap-2 sm:gap-3 w-full",
    sliderInput: "slider-styled flex-1 w-full h-2 rounded-full outline-none cursor-pointer appearance-none",
    sliderValue: "text-base md:text-lg font-semibold tabular-nums min-w-[3ch] text-right shrink-0",
    dropdownSelect: "px-3 py-2 border border-soft text-theme-text bg-theme-bg rounded-md text-base outline-none hover:bg-theme-bg-secondary transition",
    multiselect: "w-full sm:min-w-[180px] px-3 py-2 border border-soft text-color-default bg-secondary rounded-md text-base outline-none hover-bg-button transition",
    radioGroup: "flex flex-wrap items-center w-full gap-2 sm:gap-3",
    radioLabel: "inline-flex items-center gap-2 cursor-pointer",
    radioInput: "w-4 h-4",
    dateInput: "w-full md:w-1/2 sm:min-w-[180px] px-3 py-2 border border-soft text-color-default bg-secondary rounded-md text-base outline-none hover-bg-button transition",
    checkboxLabel: "inline-flex items-center gap-2",
    checkboxInput: "w-4 h-4",
    checkboxText: "text-base opacity-75"
  };
  var controlClassNames = { ...defaultControlClassNames };
  var getControlClass = /* @__PURE__ */ __name((slot) => controlClassNames[slot] || "", "getControlClass");
  function SliderControl({ min: min2, max: max2, step, current, decimals, fmt, hasRowParameter, onChange, prefix, suffix, isDense }) {
    const [localValue, setLocalValue] = useState34(null);
    const [isDragging, setIsDragging] = useState34(false);
    useEffect34(() => {
      if (localValue !== null && current === localValue) {
        setLocalValue(null);
        setIsDragging(false);
      } else if (!isDragging) {
        setLocalValue(null);
      }
    }, [current, localValue, isDragging]);
    const handleChange = useCallback20((e) => {
      const newValue = Number(e.target.value);
      setLocalValue(newValue);
      setIsDragging(true);
      const percent = (newValue - min2) / (max2 - min2) * 100;
      e.target.style.setProperty("--slider-progress", `${percent}%`);
      onChange(newValue);
    }, [onChange, min2, max2]);
    const handleMouseUp = useCallback20(() => {
      setIsDragging(false);
      setLocalValue(null);
    }, []);
    const displayValue = localValue !== null ? localValue : current;
    return /* @__PURE__ */ (0, import_jsx_runtime59.jsxs)(
      "div",
      {
        className: cn(
          "flex items-center w-full rounded-lg",
          isDense ? "gap-2 px-2 py-1" : "gap-3 px-3 py-2",
          hasRowParameter && "configField"
        ),
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("div", { className: cn(
            "flex-1 flex items-center relative",
            // Larger vertical padding for better touch targets on mobile
            isDense ? "py-1 h-8" : "py-2 h-10"
          ), children: /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
            "input",
            {
              type: "range",
              className: cn(
                "flex-1 w-full cursor-pointer accent-[var(--accent)]",
                isDense ? "dense-slider-track h-1" : "h-2"
              ),
              draggable: "false",
              min: min2,
              max: max2,
              step,
              value: displayValue,
              onChange: handleChange,
              onMouseUp: handleMouseUp,
              onTouchEnd: handleMouseUp,
              onMouseDown: (e) => e.stopPropagation(),
              onTouchStart: (e) => e.stopPropagation(),
              onDragStart: (e) => e.preventDefault()
            }
          ) }),
          /* @__PURE__ */ (0, import_jsx_runtime59.jsxs)(
            "span",
            {
              className: "flex items-baseline gap-0.5 shrink-0",
              style: { color: "var(--text)" },
              children: [
                prefix && /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("span", { className: cn(isDense ? "text-xs" : "text-base", "opacity-70"), children: prefix }),
                /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("span", { className: cn(
                  "font-semibold tabular-nums text-right",
                  isDense ? "text-sm min-w-[2ch]" : "text-base md:text-lg min-w-[2.5ch]"
                ), children: fmt(displayValue) }),
                suffix && /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("span", { className: cn(isDense ? "text-xs" : "text-base", "opacity-70"), children: suffix })
              ]
            }
          )
        ]
      }
    );
  }
  __name(SliderControl, "SliderControl");
  var UIControlRenderer = class _UIControlRenderer {
    static {
      __name(this, "UIControlRenderer");
    }
    // Helper to strip quotes from strings
    static stripQuotes(s) {
      if (typeof s !== "string") return s;
      const t = s.trim();
      if (t.startsWith('"') && t.endsWith('"') || t.startsWith("'") && t.endsWith("'")) {
        return t.slice(1, -1);
      }
      return t;
    }
    // Determine selected index for option-based controls
    static getSelectedIndex(options, result) {
      UIlog("[getSelectedIndex] options:", options, "result.ui.writeback:", result?.ui?.writeback);
      options.forEach((opt, idx) => {
        UIlog(
          "[getSelectedIndex] opt[" + idx + "]:",
          "label:",
          opt.label,
          "raw:",
          opt.raw,
          "value:",
          opt.value
        );
      });
      const selectedIndex = IC.parser.UIMetadataParser.getSelectedOptionIndex(options, result);
      UIlog("[getSelectedIndex] selectedIndex:", selectedIndex);
      return selectedIndex;
    }
    // Render dropdown control
    static renderDropdown(options, selectedIndex, hasRowParameter, onChange, assignedSnippet = null) {
      const baseClass = hasRowParameter ? "configField w-full lg:w-1/3 mr-1" : "w-full md:w-auto md:min-w-[180px]";
      let resolvedIndex = selectedIndex;
      if (assignedSnippet !== null && assignedSnippet !== void 0) {
        const matchIdx = options.findIndex((opt) => String(opt.raw) === String(assignedSnippet) || String(opt.value) === String(assignedSnippet));
        if (matchIdx >= 0) resolvedIndex = matchIdx;
      }
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
        "select",
        {
          className: cn(baseClass, getControlClass("dropdownSelect")),
          value: String(resolvedIndex),
          onChange: (e) => {
            const i = parseInt(e.target.value, 10) || 0;
            const opt = options[i] || options[0];
            onChange(opt.raw, opt);
          },
          children: options.map((opt, i) => /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("option", { value: String(i), children: opt.label }, i))
        }
      );
    }
    // Render multiselect control
    static renderMultiselect(options, result, hasRowParameter, onChange) {
      const selected = result?.ui?.writeback?.multiselectSelected ?? [];
      const normalizeKey = /* @__PURE__ */ __name((v) => JSON.stringify(v), "normalizeKey");
      const selectedKeys = new Set(selected.map(normalizeKey));
      const handleChange = /* @__PURE__ */ __name((e) => {
        const selected2 = Array.from(e.target.selectedOptions || []).map((opt) => Number(opt.value));
        const snippets = selected2.map((i) => {
          const opt = options[i];
          return opt?.raw;
        });
        const arraySnippet = `[${snippets.join(", ")}]`;
        onChange(arraySnippet);
      }, "handleChange");
      const baseClass = hasRowParameter ? "configField mr-1" : "";
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
        "select",
        {
          multiple: true,
          size: Math.min(6, Math.max(3, options.length)),
          className: cn(baseClass, getControlClass("multiselect")),
          value: options.map((opt, i) => selectedKeys.has(normalizeKey(opt.value)) ? String(i) : null).filter((v) => v !== null),
          onChange: handleChange,
          children: options.map((opt, i) => /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("option", { value: String(i), children: opt.label }, i))
        }
      );
    }
    // Render radio buttons control
    static renderRadio(options, selectedIndex, calcId, rowIndex, onChange, result = null, assignedSnippet = null) {
      const name = `row-radio-${calcId}-${rowIndex}`;
      UIlog("[renderRadio] options:", options, "selectedIndex:", selectedIndex);
      const valueRaw = result?.ui?.writeback?.rawText;
      const valueEval = result?.ui?.writeback?.value;
      let resolvedIndex = selectedIndex;
      if (assignedSnippet !== null && assignedSnippet !== void 0) {
        const matchIdx = options.findIndex((opt) => String(opt.raw) === String(assignedSnippet) || String(opt.value) === String(assignedSnippet));
        if (matchIdx >= 0) resolvedIndex = matchIdx;
      }
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
        "div",
        {
          className: cn(getControlClass("radioGroup")),
          "data-value-raw": valueRaw,
          "data-value-eval": valueEval,
          "data-selected-index": selectedIndex,
          children: options.map((opt, i) => /* @__PURE__ */ (0, import_jsx_runtime59.jsxs)(
            "label",
            {
              className: cn(getControlClass("radioLabel")),
              "data-opt-index": i,
              "data-opt-label": opt.label,
              "data-opt-raw": opt.raw,
              "data-opt-value": JSON.stringify(opt.value),
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
                  "input",
                  {
                    type: "radio",
                    name,
                    checked: i === resolvedIndex,
                    onChange: () => {
                      UIlog("[renderRadio] onChange clicked, opt:", opt, "opt.raw:", opt.raw);
                      onChange(opt);
                    },
                    className: cn(getControlClass("radioInput")),
                    "data-opt-index": i,
                    "data-opt-raw": opt.raw
                  }
                ),
                /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("span", { className: "text-base", children: opt.label })
              ]
            },
            i
          ))
        }
      );
    }
    // Render date/time input
    static renderDateTimeInput(type, result, hasRowParameter, onChange) {
      const htmlType = type === "datetime" ? "datetime-local" : type;
      const htmlMeta = result?.ui?.html;
      const current = htmlMeta?.inputType === htmlType && typeof htmlMeta?.displayValue === "string" ? htmlMeta.displayValue : "";
      const baseClass = hasRowParameter ? "configField mr-1" : "";
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
        "input",
        {
          type: htmlType,
          className: cn(baseClass, getControlClass("dateInput")),
          value: current || "",
          onChange: (e) => {
            try {
              const canon = window.IC && window.IC.parser && typeof window.IC.parser.canonicalizeDateInput === "function" ? window.IC.parser.canonicalizeDateInput(htmlType, e.target.value) : String(e.target.value || "");
              const literal = IC.parser.dateToNativeLiteral(type, canon);
              onChange(literal);
            } catch (_) {
              onChange(String(e.target.value || ""));
            }
          }
        }
      );
    }
    // Render slider/range control
    static renderSlider(result, hasRowParameter, assignedSnippet, onChange, presentation = {}) {
      const bounds = result?.ui?.writeback?.bounds || {};
      const min2 = bounds.min ?? 0;
      const max2 = bounds.max ?? 100;
      const step = bounds.step ?? 1;
      const evalVal = result?.ui?.writeback?.value;
      const assignedVal = Number(_UIControlRenderer.stripQuotes(assignedSnippet));
      const current = assignedSnippet !== null && !Number.isNaN(assignedVal) ? assignedVal : typeof evalVal === "number" ? evalVal : 0;
      const stepStr = typeof step === "number" && isFinite(step) ? String(step) : null;
      const decimals = stepStr && stepStr.includes(".") ? stepStr.length - stepStr.indexOf(".") - 1 : 0;
      const fmt = /* @__PURE__ */ __name((n) => {
        if (typeof n !== "number" || !isFinite(n)) return "";
        return n.toLocaleString(void 0, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });
      }, "fmt");
      const prefix = presentation.prefix || "";
      const suffix = presentation.suffix || "";
      const isDense = presentation.dense === true;
      const formatOverride = presentation.format ? String(presentation.format).trim().toLowerCase() : "";
      const formatAlias = { money: "finance", currency: "finance" };
      const formatName = formatAlias[formatOverride] || formatOverride;
      const formatValue = /* @__PURE__ */ __name((n) => {
        if (!formatName) return fmt(n);
        if (typeof n !== "number" || !isFinite(n)) return "";
        if (window.IC?.makeICObject && window.IC?.parser?.formatResult) {
          const obj = window.IC.makeICObject(n, null, formatName);
          return window.IC.parser.formatResult(obj);
        }
        return fmt(n);
      }, "formatValue");
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
        SliderControl,
        {
          min: min2,
          max: max2,
          step,
          current,
          decimals,
          fmt: formatValue,
          hasRowParameter,
          onChange,
          prefix,
          suffix,
          isDense
        },
        `slider-${min2}-${max2}-${step}`
      );
    }
    // Render checkbox control
    static renderCheckbox(result, uiMeta, hasRowParameter, onChange) {
      const checked = result?.ui?.writeback?.checkboxChecked ?? false;
      const baseClass = hasRowParameter ? "configField w-full md:w-1/3 mr-1" : "";
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsxs)("label", { className: cn(baseClass, getControlClass("checkboxLabel")), children: [
        /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
          "input",
          {
            type: "checkbox",
            checked,
            onChange: (e) => onChange(e.target.checked),
            className: cn(getControlClass("checkboxInput"))
          }
        ),
        /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("span", { className: cn(getControlClass("checkboxText")), children: uiMeta?.config?.label || "" })
      ] });
    }
    // Render toggle switch control (visual alternative to checkbox)
    static renderToggle(result, uiMeta, hasRowParameter, onChange) {
      const checked = result?.ui?.writeback?.checkboxChecked ?? false;
      const baseClass = hasRowParameter ? "configField" : "";
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsxs)("label", { className: cn(baseClass, "inline-flex items-center gap-3 cursor-pointer select-none"), children: [
        /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("span", { className: cn(getControlClass("checkboxText")), children: uiMeta?.config?.label || "" }),
        /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
          "button",
          {
            type: "button",
            role: "switch",
            "aria-checked": checked,
            onClick: () => onChange(!checked),
            className: cn(
              "relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none",
              checked ? "bg-accent" : "bg-theme-bg-alt"
            ),
            style: { borderWidth: "1px", borderColor: "var(--border-color)" },
            children: /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
              "span",
              {
                className: cn(
                  "inline-block h-4 w-4 rounded-full bg-theme-bg shadow transition-transform",
                  checked ? "translate-x-6" : "translate-x-1"
                )
              }
            )
          }
        )
      ] });
    }
    // Render checklist control - multi-select checkboxes that sum values
    // @checklist(Label1=value1, Label2=value2)
    static renderChecklist(result, uiMeta, hasRowParameter, onChange) {
      const options = uiMeta?.config?.options || {};
      const selected = result?.ui?.writeback?.checklistSelected ?? [];
      const selectedSet = new Set(selected.map(String));
      const baseClass = hasRowParameter ? "configField" : "";
      const handleToggle = /* @__PURE__ */ __name((label, value) => {
        const newSelected = selectedSet.has(label) ? selected.filter((l) => l !== label) : [...selected, label];
        const sum = newSelected.reduce((acc, l) => {
          return acc + (Number(options[l]) || 0);
        }, 0);
        onChange({ selected: newSelected, sum });
      }, "handleToggle");
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("div", { className: cn(baseClass, "flex flex-wrap gap-3"), children: Object.entries(options).map(([label, value]) => {
        const isChecked = selectedSet.has(label);
        return /* @__PURE__ */ (0, import_jsx_runtime59.jsxs)(
          "label",
          {
            className: "inline-flex items-center gap-2 cursor-pointer select-none",
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
                "input",
                {
                  type: "checkbox",
                  checked: isChecked,
                  onChange: () => handleToggle(label, value),
                  className: cn(getControlClass("checkboxInput"))
                }
              ),
              /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("span", { className: "text-base", children: label }),
              /* @__PURE__ */ (0, import_jsx_runtime59.jsxs)("span", { className: "text-xs opacity-60", children: [
                "+",
                value
              ] })
            ]
          },
          label
        );
      }) });
    }
    // Render image cards - visual selection grid with images
    // @image-cards(Label=value|imageUrl, ...)
    static renderImageCards(options, selectedIndex, onChange) {
      if (!options || !Array.isArray(options)) return null;
      return /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("div", { className: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3", children: options.map((opt, i) => {
        const isSelected = i === selectedIndex;
        return /* @__PURE__ */ (0, import_jsx_runtime59.jsxs)(
          "button",
          {
            type: "button",
            onClick: () => onChange(opt.value),
            className: cn(
              "relative flex flex-col items-center p-3 rounded-xl border-2 transition-all",
              "hover:border-accent focus:outline-none",
              isSelected ? "border-accent bg-[var(--accent-light)]" : "border-soft bg-theme-bg-alt"
            ),
            children: [
              opt.image && /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("div", { className: "w-full aspect-square mb-2 rounded-lg overflow-hidden bg-theme-bg", children: /* @__PURE__ */ (0, import_jsx_runtime59.jsx)(
                "img",
                {
                  src: opt.image,
                  alt: opt.label,
                  className: "w-full h-full object-cover",
                  onError: (e) => {
                    e.target.style.display = "none";
                  }
                }
              ) }),
              /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("span", { className: cn(
                "text-sm font-medium",
                isSelected ? "text-theme-accent" : "text-theme-text"
              ), children: opt.label }),
              isSelected && /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("div", { className: "absolute top-2 right-2 w-5 h-5 rounded-full bg-accent flex items-center justify-center", children: /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("svg", { className: "w-3 h-3 text-bg", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ (0, import_jsx_runtime59.jsx)("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 3, d: "M5 13l4 4L19 7" }) }) })
            ]
          },
          i
        );
      }) });
    }
    static configure({ classNames = {} } = {}) {
      controlClassNames = { ...controlClassNames, ...classNames };
    }
  };

  // public/src/react/components/calc/row/useUIHelpers.jsx
  var import_jsx_runtime60 = __toESM(require_jsx_runtime());
  var { useCallback: useCallback21 } = react_global_default;
  function useUIHelpers(calc, index2, Action, result, hasRowParameterState) {
    const calcId = calc?.id;
    const extractBeforeComment = /* @__PURE__ */ __name((text) => {
      if (typeof text !== "string") return "";
      if (!window.IC?.parser?.splitAtComment) {
        throw new Error("extractBeforeComment: IC.parser.splitAtComment not available");
      }
      return window.IC.parser.splitAtComment(text).before.trimEnd();
    }, "extractBeforeComment");
    const extractAssignedSnippet = /* @__PURE__ */ __name((text) => {
      if (!window.IC?.api?.extractAssignedSnippet) {
        throw new Error("extractAssignedSnippet: IC.api.extractAssignedSnippet not available");
      }
      return window.IC.api.extractAssignedSnippet(text);
    }, "extractAssignedSnippet");
    const replaceBeforeComment = /* @__PURE__ */ __name((originalText, newMainText) => {
      if (!window.IC?.parser?.replaceTextBeforeComment) {
        throw new Error("replaceBeforeComment: IC.parser.replaceTextBeforeComment not available");
      }
      return window.IC.parser.replaceTextBeforeComment(originalText, newMainText);
    }, "replaceBeforeComment");
    const renderUIControl = useCallback21((uiMeta) => {
      if (!uiMeta) return null;
      const type = String(uiMeta.type || "").toLowerCase();
      UIlog("[UIControl] type:", type, "uiMeta:", uiMeta, "result:", result, "rowText:", calc?.rows?.[index2]?.text);
      const rawOptions = Array.isArray(uiMeta?.config?.options) ? uiMeta.config.options : [];
      const options = rawOptions.map((opt) => {
        if (opt && typeof opt === "object" && "label" in opt) return opt;
        if (typeof opt === "string") {
          const eqIdx = opt.indexOf("=");
          if (eqIdx >= 0) {
            const label = opt.substring(0, eqIdx).trim();
            const value = opt.substring(eqIdx + 1).trim();
            return { label, value, raw: value };
          }
          return { label: opt.trim(), value: opt.trim(), raw: opt.trim() };
        }
        return opt;
      });
      const rowText = calc?.rows?.[index2]?.text || "";
      const localExtractAssignedSnippet = /* @__PURE__ */ __name((text) => {
        const before = extractBeforeComment(text).trim();
        const eqIdx = before.indexOf("=");
        if (eqIdx >= 0) {
          return before.substring(eqIdx + 1).trim();
        }
        return before;
      }, "localExtractAssignedSnippet");
      const assignedSnippet = localExtractAssignedSnippet(rowText);
      const selectedIndex = type === "checkbox" ? 0 : UIControlRenderer.getSelectedIndex(options, result);
      const applySelection = /* @__PURE__ */ __name((snippetOrOpt) => {
        UIlog("[applySelection] called with:", snippetOrOpt, "rowIdx:", index2);
        const snippet = typeof snippetOrOpt === "object" && snippetOrOpt !== null && "raw" in snippetOrOpt ? IC.api.optionToSnippet(snippetOrOpt) : String(snippetOrOpt);
        UIlog("[applySelection] snippet:", snippet);
        const currentRowText = calc?.rows?.[index2]?.text || "";
        const newText = IC.api.buildParameterText(currentRowText, snippet);
        UIlog("[applySelection] rowText:", currentRowText, "\u2192 newText:", newText);
        Action.updateRowText(calcId, index2, newText, { immediate: true });
      }, "applySelection");
      if (type === "dropdown") {
        const applyDropdownSelection = /* @__PURE__ */ __name((_optValue, opt) => applySelection(opt), "applyDropdownSelection");
        return UIControlRenderer.renderDropdown(options, selectedIndex, hasRowParameterState, applyDropdownSelection, assignedSnippet);
      }
      if (type === "multiselect" || type === "multi" || type === "select-multiple") {
        return UIControlRenderer.renderMultiselect(options, result, hasRowParameterState, applySelection);
      }
      if (type === "radio") {
        const applyRadioSelection = /* @__PURE__ */ __name((opt) => applySelection(opt), "applyRadioSelection");
        return UIControlRenderer.renderRadio(options, selectedIndex, calc.id, index2, applyRadioSelection, result, assignedSnippet);
      }
      if (type === "date" || type === "time" || type === "datetime" || type === "datetime-local") {
        return UIControlRenderer.renderDateTimeInput(type, result, hasRowParameterState, applySelection);
      }
      if (type === "range" || type === "slider") {
        UIlog("[UIControl] rendering range/slider");
        const presentation = result?.presentation || {};
        const isDense = uiMeta?.config?.dense === true || presentation?.dense === true;
        return UIControlRenderer.renderSlider(result, hasRowParameterState, assignedSnippet, applySelection, { ...presentation, dense: isDense });
      }
      if (type === "checkbox") {
        UIlog("[UIControl] rendering checkbox");
        return UIControlRenderer.renderCheckbox(result, uiMeta, hasRowParameterState, applySelection);
      }
      return null;
    }, [calcId, index2, result, hasRowParameterState, Action]);
    const formattedComment = useCallback21((comment) => {
      const config = [
        { regex: /^<br\s*\/?>$/i, create: /* @__PURE__ */ __name((key) => /* @__PURE__ */ (0, import_jsx_runtime60.jsx)("br", {}, `br-${key}`), "create") },
        { regex: /^<hr\s*\/?>$/i, create: /* @__PURE__ */ __name((key) => /* @__PURE__ */ (0, import_jsx_runtime60.jsx)("hr", {}, `hr-${key}`), "create") }
      ];
      const parts = comment.split(/(<br\s*\/?>|<hr\s*\/?>)/gi);
      return parts.map((part, idx) => {
        const rule = config.find(({ regex }) => regex.test(part));
        return rule ? rule.create(idx) : part;
      });
    }, []);
    return react_global_default.useMemo(() => ({
      extractBeforeComment,
      extractAssignedSnippet,
      replaceBeforeComment,
      renderUIControl,
      formattedComment
    }), [renderUIControl, formattedComment]);
  }
  __name(useUIHelpers, "useUIHelpers");
  var useUIHelpers_default = useUIHelpers;

  // public/src/react/components/calc/row/useRotatingPlaceholder.js
  init_react_global();
  init_featureFlags();
  var { useState: useState35, useEffect: useEffect35 } = react_global_default;
  var PLACEHOLDERS = [
    "Type your calculation (e.g. 300 * 200)",
    "Try: 15% of 500",
    "Try: 100 usd to eur",
    "Try: sum(10, 20, 30)",
    "Try: average(1, 2, 3, 4)",
    "Try: 50C to F"
  ];
  function useRotatingPlaceholder(index2, value) {
    const [placeholderIndex, setPlaceholderIndex] = useState35(0);
    if (index2 !== 0 || value) return "";
    if (!isFeatureEnabled("showPlaceholder")) return "";
    return PLACEHOLDERS[placeholderIndex];
  }
  __name(useRotatingPlaceholder, "useRotatingPlaceholder");

  // public/src/constants.js
  var MOBILE_BREAKPOINT_PX = 768;
  var MIN_ROWS = 7;
  var STORAGE_KEYS = {
    STORE: "store",
    SETTINGS: "settings"
  };
  if (typeof window !== "undefined") {
    window.MIN_ROWS = MIN_ROWS;
    window.STORAGE_KEYS = STORAGE_KEYS;
  }

  // public/src/react/components/calc/row/rowComparisonUtils.js
  function areBaseRowPropsEqual(prevProps, nextProps) {
    if (prevProps.index !== nextProps.index) return false;
    const prevRow = prevProps.row ?? prevProps.calc?.rows?.[prevProps.index];
    const nextRow = nextProps.row ?? nextProps.calc?.rows?.[nextProps.index];
    if (prevRow !== nextRow) {
      if (prevRow?.text !== nextRow?.text) return false;
      if (prevRow?.hidden !== nextRow?.hidden) return false;
      if (prevRow?.id !== nextRow?.id) return false;
    }
    const prevLength = prevProps.rowsLength ?? prevProps.calc?.rows?.length;
    const nextLength = nextProps.rowsLength ?? nextProps.calc?.rows?.length;
    if (prevLength !== nextLength) return false;
    const resPrev = prevProps.result;
    const resNext = nextProps.result;
    if (!Result.areValuesEqual(resPrev?.output?.raw, resNext?.output?.raw)) return false;
    if (resPrev?.output?.display !== resNext?.output?.display) return false;
    if (resPrev?.type?.units !== resNext?.type?.units) return false;
    if (resPrev?.display?.label !== resNext?.display?.label) return false;
    if (resPrev?.display?.comment !== resNext?.display?.comment) return false;
    if (resPrev?.comment?.original !== resNext?.comment?.original) return false;
    if (resPrev?.comment?.cleaned !== resNext?.comment?.cleaned) return false;
    return true;
  }
  __name(areBaseRowPropsEqual, "areBaseRowPropsEqual");

  // public/src/react/constants/ui-constants.js
  var LONG_TEXT_THRESHOLD_MOBILE = 20;
  var LONG_TEXT_THRESHOLD_DESKTOP = 30;

  // public/src/react/components/calc/row/icApiCache.js
  var MAX_CACHE_SIZE = 100;
  var caches = {
    getParameter: /* @__PURE__ */ new Map(),
    getParameterNameForDisplay: /* @__PURE__ */ new Map(),
    processSymbols: /* @__PURE__ */ new Map(),
    canIncrement: /* @__PURE__ */ new Map()
  };
  var clearScheduled = false;
  function scheduleCacheClear() {
    if (clearScheduled) return;
    clearScheduled = true;
    requestAnimationFrame(() => {
      Object.values(caches).forEach((cache) => cache.clear());
      clearScheduled = false;
    });
  }
  __name(scheduleCacheClear, "scheduleCacheClear");
  function getCached(cache, key, compute) {
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = compute();
    if (cache.size >= MAX_CACHE_SIZE) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    cache.set(key, result);
    scheduleCacheClear();
    return result;
  }
  __name(getCached, "getCached");
  function getParameterCached(rowOrCalc, index2) {
    if (!IC?.api?.getParameter) return null;
    const row = index2 !== void 0 ? rowOrCalc?.rows?.[index2] : rowOrCalc;
    if (!row) return null;
    const key = `${row.text ?? ""}|${row.hidden ? "1" : "0"}`;
    return getCached(caches.getParameter, key, () => {
      return IC.api.getParameter(row);
    });
  }
  __name(getParameterCached, "getParameterCached");
  function getParameterNameForDisplayCached(rowText) {
    if (!IC?.api?.getParameterNameForDisplay) return null;
    const key = rowText ?? "";
    return getCached(caches.getParameterNameForDisplay, key, () => {
      return IC.api.getParameterNameForDisplay(rowText);
    });
  }
  __name(getParameterNameForDisplayCached, "getParameterNameForDisplayCached");
  function processSymbolsCached(value) {
    if (!IC?.api?.processSymbols) return value;
    const key = value ?? "";
    return getCached(caches.processSymbols, key, () => {
      return IC.api.processSymbols(value);
    });
  }
  __name(processSymbolsCached, "processSymbolsCached");
  function canIncrementCached(value) {
    if (!IC?.api?.canIncrement) return false;
    const key = value ?? "";
    return getCached(caches.canIncrement, key, () => {
      return IC.api.canIncrement(value);
    });
  }
  __name(canIncrementCached, "canIncrementCached");

  // public/src/react/components/calc/row/CalcRow.jsx
  var import_jsx_runtime61 = __toESM(require_jsx_runtime());
  var { useState: useState36, useEffect: useEffect36, useRef: useRef24, useCallback: useCallback22, forwardRef: forwardRef13, useImperativeHandle: useImperativeHandle2, memo: memo2, useMemo: useMemo14 } = react_global_default;
  var defaultRowClassNames2 = {
    rowInputArea: "relative min-w-0 overflow-visible flex-1",
    rowInput: "px-1 py-[3px] border border-[var(--row-border)] text-color-default rounded text-[87.5%] lg:text-[100%] outline-none focus:border-accent",
    rowTextarea: "w-full h-48 px-1 py-[3px] pr-6 border border-[var(--row-border)] text-color-default rounded text-[87.5%] lg:text-[100%] outline-none focus:border-accent",
    rowResultArea: "relative min-w-0 overflow-visible flex-1",
    rowResultInner: "text-[87.5%] lg:text-[100%] flex flex-wrap items-baseline gap-x-1 gap-y-0.5",
    rowResultType: "inline cursor-pointer shrink-0 leading-tight",
    rowResultComment: "min-w-0 text-left break-words cursor-pointer"
  };
  function formatLogStr(logStr) {
    return logStr.split("\n").map((item, itemIndex) => /* @__PURE__ */ (0, import_jsx_runtime61.jsxs)("div", { children: [
      item,
      itemIndex < logStr.split("\n").length - 1 && /* @__PURE__ */ (0, import_jsx_runtime61.jsx)("br", {})
    ] }, itemIndex));
  }
  __name(formatLogStr, "formatLogStr");
  function computeRowData(row, index2, result, expanded, hasRowParameterState, lastkeypress, strategy, isSelected, placeholderText) {
    const t0 = window.DebugLogger?.isEnabled("perf") ? performance.now() : 0;
    if (!row) return null;
    const isCollapsed = !!row.hidden;
    const isExpanded = expanded;
    const rowTitle = "R" + (index2 + 1);
    const title = `Click to ${isCollapsed ? "expand" : "collapse"} ${rowTitle}`;
    const parameterValue = getParameterCached(row);
    const hasParameterRow = hasRowParameterState;
    let value;
    let hideType = false;
    if (hasParameterRow && isCollapsed) {
      value = parameterValue;
      if (result?.type?.units) {
        hideType = true;
      }
    } else {
      value = row.text;
      if (IC && lastkeypress == 32) {
        value = processSymbolsCached(value);
      }
    }
    const isLongText = value?.length > (window?.innerWidth <= MOBILE_BREAKPOINT_PX ? LONG_TEXT_THRESHOLD_MOBILE : LONG_TEXT_THRESHOLD_DESKTOP);
    const showExpandButton = isLongText && isSelected && !isCollapsed && !isExpanded && strategy.showExpandButton;
    const rowText = row.text || "";
    const rowParamName = getParameterNameForDisplayCached(rowText);
    const collapsedLabel = rowParamName && rowParamName.trim() || result?.display?.label;
    const uiMeta = result?.ui;
    if (uiMeta && isCollapsed) {
      hideType = true;
    }
    const numberCanBeIncremented = isSelected && isCollapsed && hasParameterRow && canIncrementCached(value);
    const hasLog = result && Array.isArray(result?.debug?.log) && result.debug.log.length;
    const logStr = hasLog ? result.debug.log.join(" \u2502 ") : null;
    const placeholder = index2 === 0 && !value ? placeholderText : "";
    const presentation = result?.presentation || null;
    const rowDataResult = {
      isCollapsed,
      isSelected,
      isExpanded,
      rowTitle,
      title,
      parameterValue,
      hasParameterRow,
      value,
      hideType,
      isLongText,
      showExpandButton,
      collapsedLabel,
      uiMeta,
      numberCanBeIncremented,
      logStr,
      processed: result?.input?.processed,
      comment: result?.display?.comment,
      customType: result?.type?.customUnit ? "custom" : "",
      type: result?.type?.units,
      result,
      placeholder,
      presentation
    };
    if (t0) {
      window.DebugLogger?.log("perf", `computeRowData row=${index2} time=${(performance.now() - t0).toFixed(2)}ms`);
    }
    return rowDataResult;
  }
  __name(computeRowData, "computeRowData");
  var CalcRow = /* @__PURE__ */ __name((props) => {
    const renderStartTime = window.DebugLogger?.isEnabled("perf") ? performance.now() : 0;
    const {
      // New optimized props - row passed directly for better memoization
      calcId: propCalcId,
      row: propRow,
      rowsLength: propRowsLength,
      // TODO: Remove calc prop once useRowHandlers and useUIHelpers are updated to use calcId/row.
      // Currently calc is still needed for: paste operations (useRowHandlers), UI control rendering (useUIHelpers).
      // computeRowData no longer depends on calc - the key memo optimization is complete.
      calc,
      index: index2,
      onSelectRow,
      onTextChange,
      result,
      focusRow,
      deleteRow,
      insertRow,
      insertRowAfter,
      rowClassName,
      rowStyle,
      className: customClassName,
      style: customStyle,
      classNames: classNameOverrides = {},
      viewMode,
      isSelected,
      // Received as props from Row.jsx to avoid context subscription
      Action,
      layout,
      rowRef
    } = props;
    const calcId = propCalcId ?? calc?.id;
    const row = propRow ?? calc?.rows?.[index2];
    const rowsLength = propRowsLength ?? calc?.rows?.length ?? 0;
    const { strategy = {} } = props;
    const handleDeleteRow = useCallback22(() => deleteRow?.(index2), [deleteRow, index2]);
    const handleInsertRow = useCallback22(() => insertRow?.(index2), [insertRow, index2]);
    const handleInsertRowAfter = useCallback22(() => insertRow?.(index2 + 1), [insertRow, index2]);
    usePerf(`CalcRow:${index2}`);
    window.DebugLogger?.log("render", `CalcRow R${index2 + 1} RENDER text="${row?.text?.slice(0, 20) || ""}"`);
    const safeLayout = layout || { gridType: "default", gridConfig: {} };
    const safeAction = Action || {
      updateRowText: /* @__PURE__ */ __name(() => {
      }, "updateRowText"),
      toggleRowVisibility: /* @__PURE__ */ __name(() => {
      }, "toggleRowVisibility"),
      deleteRow: /* @__PURE__ */ __name(() => {
      }, "deleteRow"),
      insertRow: /* @__PURE__ */ __name(() => {
      }, "insertRow"),
      pasteTextLines: /* @__PURE__ */ __name(() => {
      }, "pasteTextLines")
    };
    const isFlexLayout = safeLayout.gridType === "6-col" || safeLayout.gridConfig?.rowLayout === "flex";
    const gridRowClasses = useMemo14(() => {
      if (isFlexLayout) {
        return {
          row: "col-span-full flex gap-2 items-center",
          input: "flex-1",
          result: "flex-1"
        };
      }
      return {
        row: "contents",
        input: "",
        result: ""
      };
    }, [isFlexLayout]);
    const pickClass = /* @__PURE__ */ __name((slot) => cn(defaultRowClassNames2[slot], classNameOverrides?.[slot]), "pickClass");
    const inputRef = useRef24(null);
    const [selectionClassName, setSelectionClassName] = useState36("");
    const getRowParameterFromString = useCallback22((text) => {
      if (typeof IC === "undefined") return null;
      if (IC.parser?.getRowParameterFromString) {
        return IC.parser.getRowParameterFromString(text);
      }
      if (IC.api?.getParameterName) {
        return IC.api.getParameterName(text);
      }
      return null;
    }, []);
    const [hasRowParameterState, setHasRowParameterState] = useState36(() => {
      const rowText = row?.text || "";
      return getRowParameterFromString(rowText) !== null;
    });
    const prevCalcIdRef = useRef24(calcId);
    const prevRowLengthRef = useRef24(rowsLength);
    const [expanded, setExpanded] = useState36(false);
    const [, setRenderTrigger] = useState36(0);
    const rotatingPlaceholder = useRotatingPlaceholder(index2, row?.text);
    const updateVariableState = useCallback22((c, i) => {
      const currentIndex = i !== void 0 ? i : index2;
      const rowText = c ? c.rows?.[currentIndex]?.text : row?.text;
      setHasRowParameterState(getRowParameterFromString(rowText || "") !== null);
    }, [row, index2, getRowParameterFromString]);
    const isApplyingFocusRef = useRef24(false);
    useImperativeHandle2(rowRef, () => ({
      setFocus: /* @__PURE__ */ __name((options = {}) => {
        const { preventScroll = true, skipSelect = false, forceSelectAll = false } = options;
        if (!inputRef.current) return;
        if (isApplyingFocusRef.current) return;
        if (preventScroll) inputRef.current.focus({ preventScroll: true });
        else inputRef.current.focus();
        let mode = skipSelect ? "none" : options.selectionMode || "all";
        if (mode === "all" && inputRef.current.value && !forceSelectAll) mode = "end";
        if (mode === "none") return;
        const valueAtSchedule = inputRef.current.value;
        isApplyingFocusRef.current = true;
        queueMicrotask(() => {
          const input = inputRef.current;
          isApplyingFocusRef.current = false;
          if (!input || input.value !== valueAtSchedule && mode === "all") return;
          if (mode === "all") input.select();
          else if (mode === "end") {
            const len = input.value.length;
            input.setSelectionRange(len, len);
          } else if (mode === "start") input.setSelectionRange(0, 0);
        });
      }, "setFocus"),
      updateVariableState: /* @__PURE__ */ __name((c, i) => {
        updateVariableState(c, i);
      }, "updateVariableState"),
      inputRef
    }), [updateVariableState]);
    useEffect36(() => {
      if (calcId !== prevCalcIdRef.current || rowsLength !== prevRowLengthRef.current) {
        updateVariableState(null, index2);
        prevCalcIdRef.current = calcId;
        prevRowLengthRef.current = rowsLength;
      }
    }, [calcId, rowsLength, index2, updateVariableState]);
    const toggleExpanded = useCallback22((event) => {
      event.stopPropagation();
      setExpanded((prev) => !prev);
    }, []);
    const isVeryLastRow = index2 === rowsLength - 1;
    const onFocus = useCallback22((event) => {
      setSelectionClassName("selected");
      onSelectRow(index2);
    }, [onSelectRow, index2]);
    const onBlur = useCallback22((event) => {
      setSelectionClassName("");
    }, []);
    const handlers = useRowHandlers_default(
      calc,
      index2,
      safeAction,
      inputRef,
      hasRowParameterState,
      updateVariableState,
      onSelectRow,
      focusRow,
      setRenderTrigger,
      handleInsertRowAfter,
      strategy
    );
    const uiHelpers = useUIHelpers_default(calc, index2, safeAction, result, hasRowParameterState);
    const rowData = useMemo14(
      () => computeRowData(row, index2, result, expanded, hasRowParameterState, handlers.lastkeypress, strategy, isSelected, rotatingPlaceholder),
      [row, index2, result, expanded, hasRowParameterState, handlers.lastkeypress, strategy, isSelected, rotatingPlaceholder]
    );
    const changeInput = useCallback22((direction = 1) => {
      let newValue = IC.api.increment(rowData?.value, direction);
      handlers.inputChanged({ target: { value: newValue } });
    }, [rowData?.value, handlers]);
    const result_display = useMemo14(() => result ? /* @__PURE__ */ (0, import_jsx_runtime61.jsx)(ResultDisplay_default, { result }) : null, [result]);
    const domRowRef = useRef24(null);
    useEffect36(() => {
      if (domRowRef.current) {
        domRowRef.current.setAttribute("data-index", index2);
      }
    }, [index2]);
    const inputField = useMemo14(() => /* @__PURE__ */ (0, import_jsx_runtime61.jsx)(
      EditableInputField_default,
      {
        rowData,
        index: index2,
        inputRef,
        handlers,
        onFocus,
        onBlur,
        toggleExpanded,
        rowClassName,
        rowStyle,
        inputClassNameOverride: classNameOverrides?.rowInput,
        textareaClassNameOverride: classNameOverrides?.rowTextarea,
        wrapperClassName: classNameOverrides?.rowInputWrapper,
        strategy,
        calc,
        Action: safeAction
      }
    ), [rowData, index2, handlers, rowClassName, rowStyle, classNameOverrides?.rowInput, classNameOverrides?.rowTextarea, classNameOverrides?.rowInputWrapper, strategy, calc, safeAction]);
    const outputRaw = result?.output?.raw;
    const uiResultComponent = useMemo14(() => {
      if (outputRaw && typeof outputRaw === "object" && outputRaw.type) {
        try {
          return /* @__PURE__ */ (0, import_jsx_runtime61.jsx)(UIResult_default, { result: outputRaw, index: index2 });
        } catch (e) {
          UIlog("[CalcRow] Error rendering UIResult:", e);
        }
      }
      return null;
    }, [outputRaw, index2]);
    const regularResult = useMemo14(() => /* @__PURE__ */ (0, import_jsx_runtime61.jsx)(
      RegularResult_default,
      {
        rowData,
        result,
        inputField,
        finalResultDisplay: outputRaw?.vars ? /* @__PURE__ */ (0, import_jsx_runtime61.jsx)(VariableAssignmentPanel_default, { resultObj: outputRaw }) : result_display,
        resultClass: result && outputRaw == "Number undefined" ? "result_undefined overflow-auto no-scrollbar " : " overflow-auto no-scrollbar ",
        extraUIComment: extractExtraUIComment_default(rowData, result),
        handlers,
        uiHelpers
      }
    ), [rowData, result, inputField, result_display, handlers, uiHelpers]);
    const subRowData = useMemo14(() => ({ ...rowData, regularResult, UIResult: uiResultComponent, extraUIComment: rowData?.uiMeta ? extractExtraUIComment_default(rowData, result) : null, isVeryLastRow, index: index2 }), [rowData, regularResult, uiResultComponent, result, isVeryLastRow, index2]);
    const subHandlers = useMemo14(() => ({ ...handlers, onFocus, onBlur, toggleExpanded, changeInput, formatLogStr, formattedComment: uiHelpers.formattedComment, deleteRow: handleDeleteRow, insertRow: handleInsertRow, insertRowAfter: handleInsertRowAfter }), [handlers, onFocus, onBlur, toggleExpanded, changeInput, uiHelpers.formattedComment, handleDeleteRow, handleInsertRow, handleInsertRowAfter]);
    if (!rowData) return /* @__PURE__ */ (0, import_jsx_runtime61.jsx)("div", {});
    if (renderStartTime) {
      const elapsed = performance.now() - renderStartTime;
      if (elapsed > 5) {
        window.DebugLogger?.log("perf", `CalcRow:${index2} SLOW render=${elapsed.toFixed(1)}ms`);
      }
    }
    const rootClassName = cn(
      "group relative",
      gridRowClasses.row,
      // Add min-height and gap for non-contents layouts
      gridRowClasses.row !== "contents" && "min-h-[30px] md:min-h-[36px] lg:min-h-[44px]",
      // Hide original row when dragging (show ghost instead)
      props.isDragging && "opacity-30 grayscale",
      rowClassName,
      customClassName,
      selectionClassName || (subRowData.isSelected ? "selected" : "")
    );
    return /* @__PURE__ */ (0, import_jsx_runtime61.jsxs)(
      "row",
      {
        ref: domRowRef,
        "data-row": index2,
        "data-row-type": rowData.isCollapsed ? "collapsed" : rowData.hasParameterRow ? "parameter" : "calculated",
        "data-testid": "calc-row",
        "data-id": `row-${index2}`,
        className: rootClassName,
        style: customStyle,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime61.jsx)(
            RowNumber_default,
            {
              index: index2,
              isCollapsed: rowData.isCollapsed,
              insertRow: handleInsertRow,
              insertRowAfter: handleInsertRowAfter,
              deleteRow: handleDeleteRow,
              Action,
              calc,
              viewMode,
              isSelected,
              isAnyRowDragging: props.isAnyRowDragging
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime61.jsx)(RowInputArea_default, { rowData: subRowData, inputField, handlers: subHandlers, className: cn(gridRowClasses.input, pickClass("rowInputArea")), viewMode }),
          /* @__PURE__ */ (0, import_jsx_runtime61.jsx)(RowResultArea_default, { rowData: subRowData, handlers: subHandlers, className: cn(gridRowClasses.result, pickClass("rowResultArea")), innerClassName: pickClass("rowResultInner"), typeClassName: pickClass("rowResultType"), commentClassName: pickClass("rowResultComment"), collapseButtonClassName: classNameOverrides?.collapseHintButton })
        ]
      }
    );
  }, "CalcRow");
  CalcRow.displayName = "CalcRow";
  function areCalcRowPropsEqual(prevProps, nextProps) {
    try {
      if (!areBaseRowPropsEqual(prevProps, nextProps)) return false;
      if (prevProps.rowClassName !== nextProps.rowClassName) return false;
      if (prevProps.className !== nextProps.className) return false;
      if (prevProps.viewMode !== nextProps.viewMode) return false;
      if (prevProps.isSelected !== nextProps.isSelected) return false;
      if (prevProps.isDragging !== nextProps.isDragging) return false;
      return true;
    } catch (e) {
      console.error("[CalcRow MemoCheck] Error:", e);
      return false;
    }
  }
  __name(areCalcRowPropsEqual, "areCalcRowPropsEqual");
  var CalcRow_default = memo2(CalcRow, areCalcRowPropsEqual);

  // public/src/react/components/calc/row/PresentRow.jsx
  init_react_global();

  // public/src/react/components/calc/row/CalcIcons.jsx
  init_react_global();
  var import_jsx_runtime62 = __toESM(require_jsx_runtime());
  var { useState: useState37, useEffect: useEffect37, useRef: useRef25 } = react_global_default;
  var iconCache = /* @__PURE__ */ new Map();
  var pendingRequests = /* @__PURE__ */ new Map();
  var ICON_ALIASES = {
    "money": "dollar-sign",
    "cash": "dollar-sign",
    "currency": "dollar-sign",
    "usd": "dollar-sign",
    "dollar": "dollar-sign",
    "time": "clock",
    "watch": "clock",
    "duration": "clock",
    "date": "calendar",
    "schedule": "calendar",
    "finance": "landmark",
    "banking": "landmark",
    "bank": "landmark",
    "investment": "trending-up",
    "growth": "trending-up",
    "loss": "trending-down",
    "decline": "trending-down",
    "percentage": "percent",
    "rate": "percent",
    "chart": "bar-chart-3",
    "graph": "line-chart",
    "analytics": "pie-chart",
    "stats": "activity",
    "metric": "gauge",
    "goal": "target",
    "success": "check-circle",
    "warning": "alert-triangle",
    "error": "alert-circle",
    "help": "info",
    "favorite": "star",
    "important": "star",
    "energy": "zap",
    "power": "zap",
    "lightning": "zap",
    "fire": "flame",
    "hot": "flame",
    "magic": "sparkles",
    "company": "building-2",
    "office": "building-2",
    "team": "users",
    "people": "users",
    "person": "user",
    "up": "arrow-up",
    "down": "arrow-down",
    "left": "arrow-left",
    "right": "arrow-right",
    "reload": "refresh-cw",
    "sync": "refresh-cw",
    "location": "map-pin",
    "place": "map-pin",
    "world": "globe",
    "international": "globe",
    "shipping": "truck",
    "delivery": "truck",
    "box": "package",
    "product": "package",
    "present": "gift",
    "bonus": "gift",
    "love": "heart",
    "like": "heart",
    // Hardware/system
    "hdd": "hard-drive",
    "disk": "hard-drive",
    "storage": "hard-drive",
    "temp": "thermometer",
    "temperature": "thermometer",
    "processor": "cpu",
    "chip": "cpu",
    // More common aliases
    "bill": "banknote",
    "note": "banknote",
    "price": "tag",
    "label": "tag",
    "savings": "piggy-bank"
  };
  async function fetchIcon(iconName) {
    if (iconCache.has(iconName)) {
      return iconCache.get(iconName);
    }
    if (pendingRequests.has(iconName)) {
      return pendingRequests.get(iconName);
    }
    const url = `https://unpkg.com/lucide-static@latest/icons/${iconName}.svg`;
    const promise = fetch(url).then((response) => {
      if (!response.ok) {
        throw new Error(`Icon not found: ${iconName}`);
      }
      return response.text();
    }).then((svg) => {
      iconCache.set(iconName, svg);
      pendingRequests.delete(iconName);
      return svg;
    }).catch((err) => {
      pendingRequests.delete(iconName);
      iconCache.set(iconName, null);
      console.warn(`[CalcIcon] Failed to load icon: ${iconName}`, err.message);
      return null;
    });
    pendingRequests.set(iconName, promise);
    return promise;
  }
  __name(fetchIcon, "fetchIcon");
  function CalcIcon({ name, className, style }) {
    const [svgContent, setSvgContent] = useState37(null);
    const [loading, setLoading] = useState37(true);
    const mountedRef = useRef25(true);
    const normalizedName = name?.toLowerCase().replace(/[_\s]/g, "-") || "";
    const iconName = ICON_ALIASES[normalizedName] || normalizedName;
    useEffect37(() => {
      mountedRef.current = true;
      if (!iconName) {
        setLoading(false);
        return;
      }
      if (iconCache.has(iconName)) {
        setSvgContent(iconCache.get(iconName));
        setLoading(false);
        return;
      }
      setLoading(true);
      fetchIcon(iconName).then((svg) => {
        if (mountedRef.current) {
          setSvgContent(svg);
          setLoading(false);
        }
      });
      return () => {
        mountedRef.current = false;
      };
    }, [iconName]);
    if (!name) return null;
    if (loading) {
      return /* @__PURE__ */ (0, import_jsx_runtime62.jsx)(
        "span",
        {
          className: cn("calc-icon inline-block", className),
          style: { width: "1em", height: "1em", ...style },
          "aria-label": name
        }
      );
    }
    if (!svgContent) {
      return /* @__PURE__ */ (0, import_jsx_runtime62.jsx)(
        "svg",
        {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round",
          strokeLinejoin: "round",
          className: cn("calc-icon", className),
          style,
          "aria-label": name,
          children: /* @__PURE__ */ (0, import_jsx_runtime62.jsx)("circle", { cx: "12", cy: "12", r: "10", strokeDasharray: "4 2" })
        }
      );
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgContent, "image/svg+xml");
    const svgEl = doc.querySelector("svg");
    if (!svgEl) {
      return null;
    }
    const innerHTML = svgEl.innerHTML;
    return /* @__PURE__ */ (0, import_jsx_runtime62.jsx)(
      "svg",
      {
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        className: cn("calc-icon", className),
        style,
        "aria-label": name,
        dangerouslySetInnerHTML: { __html: innerHTML }
      }
    );
  }
  __name(CalcIcon, "CalcIcon");

  // public/src/react/components/calc/row/PresentRow.jsx
  init_Icon();

  // public/src/react/components/calc/row/ParameterField.jsx
  init_react_global();
  init_Icon();

  // public/src/react/components/calc/row/parameterDetection.js
  var DebugModule = __toESM(require_debug());
  var Debug = DebugModule.default || DebugModule;
  function detectParameter(rowText) {
    const strictValue = IC.parser.getRowParameterFromString(rowText);
    if (strictValue !== null) {
      const result2 = {
        isParameter: true,
        value: strictValue,
        confidence: "high"
      };
      if (Debug.isEnabled("ui")) {
        console.log("[detectParameter]", {
          input: rowText,
          output: result2,
          details: { strictValue }
        });
      }
      return result2;
    }
    const result = { isParameter: false };
    if (Debug.isEnabled("ui")) {
      console.log("[detectParameter]", {
        input: rowText,
        output: result,
        details: { strictValue: null }
      });
    }
    return result;
  }
  __name(detectParameter, "detectParameter");

  // public/src/react/components/calc/row/ParameterField.jsx
  var import_jsx_runtime63 = __toESM(require_jsx_runtime());
  var { memo: memo3, useCallback: useCallback23, useRef: useRef26 } = react_global_default;
  function useParameterData(calc, index2) {
    const rowText = calc.rows[index2]?.text || "";
    if (!IC?.parser?.getRowParameterFromString) {
      return {
        isParameter: false,
        value: null,
        canIncrement: false,
        label: "",
        rowText
      };
    }
    const detection = detectParameter(rowText);
    if (!detection.isParameter) {
      return {
        isParameter: false,
        value: null,
        canIncrement: false,
        label: IC.api.getParameterNameForDisplay?.(rowText) || "",
        rowText
      };
    }
    const value = IC.api.getParameter?.(calc, index2) ?? detection.value;
    const canIncrement = IC.api.canIncrement?.(value) || false;
    const label = IC.api.getParameterNameForDisplay?.(rowText) || "";
    return {
      isParameter: true,
      value,
      canIncrement,
      label,
      rowText
    };
  }
  __name(useParameterData, "useParameterData");
  function ParameterField({
    calc,
    index: index2,
    variant = "default",
    prefix,
    suffix,
    className,
    style,
    onFocus,
    onBlur,
    showFocusRing = true,
    displayValue
  }) {
    const Action = useAction();
    const inputRef = useRef26(null);
    const { isParameter, value, canIncrement } = useParameterData(calc, index2);
    const handleChange = useCallback23((e) => {
      const currentRowText = calc?.rows?.[index2]?.text || "";
      const newValue = e.target.value;
      const newText = IC.api.buildParameterText(currentRowText, newValue);
      Action.updateRowText(calc?.id, index2, newText, { immediate: true });
    }, [calc?.id, index2, Action]);
    const handleIncrement = useCallback23((direction) => {
      const freshCalc = typeof Calc !== "undefined" && Calc.get ? Calc.get(calc?.id) : calc;
      const currentRowText = freshCalc?.rows?.[index2]?.text || "";
      const currentValue = IC.parser.getRowParameterFromString(currentRowText);
      const newValue = IC.api.increment(currentValue, direction);
      const newText = IC.api.buildParameterText(currentRowText, newValue);
      Action.updateRowText(calc?.id, index2, newText, { immediate: true });
    }, [calc?.id, index2, Action]);
    if (!isParameter) {
      return null;
    }
    const variantStyles = {
      default: {
        input: cn(
          "w-full px-3 py-2 border border-[var(--row-border)] rounded-md",
          "text-base text-left",
          "bg-theme-calc-row-input-bg outline-none m-0",
          "configField",
          showFocusRing && "focus:border-accent transition-all"
        ),
        wrapper: "relative flex w-full group/param",
        showIncrement: true
      },
      hero: {
        input: cn(
          "bg-transparent border-0 outline-none text-right w-auto",
          "font-bold text-xl md:text-3xl tabular-nums tracking-tight",
          showFocusRing && "rounded px-1"
        ),
        wrapper: "relative inline-flex items-baseline",
        showIncrement: false
      },
      minimal: {
        input: cn(
          "outline-none text-left bg-theme-calc-row-input-bg border border-[var(--row-border)] rounded-md",
          "font-semibold text-base md:text-lg tabular-nums",
          "px-2 py-0.5",
          "cursor-text",
          "min-w-[3ch] max-w-[12ch]",
          showFocusRing && "focus:border-accent transition-all"
        ),
        wrapper: "relative inline-flex items-baseline group/param",
        showIncrement: true
      }
    };
    const styles = variantStyles[variant] || variantStyles.default;
    const inputStyle = {
      color: variant === "hero" ? "var(--accent)" : "var(--text)",
      ...style
    };
    const inputElement = /* @__PURE__ */ (0, import_jsx_runtime63.jsx)(
      "input",
      {
        ref: inputRef,
        type: "text",
        value: displayValue !== void 0 ? displayValue : value || "",
        onChange: handleChange,
        onFocus,
        onBlur,
        autoComplete: "off",
        autoCorrect: "off",
        autoCapitalize: "off",
        spellCheck: false,
        "data-calc-input": true,
        className: cn(styles.input, canIncrement && styles.showIncrement && "pr-8", className),
        style: inputStyle,
        "aria-label": `Parameter value for row ${index2 + 1}`
      }
    );
    const hasAffix = prefix || suffix;
    const affixClass = variant === "hero" ? "text-[var(--calc-present-affix-size-hero)] font-medium opacity-70" : "text-[var(--calc-present-affix-size)]";
    const affixStyle = {
      color: variant === "hero" ? "var(--accent)" : "var(--text-dim)"
    };
    const incrementButtons = styles.showIncrement && canIncrement ? /* @__PURE__ */ (0, import_jsx_runtime63.jsxs)("div", { className: cn(
      "absolute right-1 top-1/2 -translate-y-1/2 flex flex-col items-center justify-center gap-1",
      "opacity-0 invisible group-hover/param:opacity-100 group-hover/param:visible transition-all z-10"
    ), children: [
      /* @__PURE__ */ (0, import_jsx_runtime63.jsx)(
        "button",
        {
          tabIndex: -1,
          className: "p-0 hover:text-theme-accent transition-colors flex items-center justify-center h-3",
          onClick: () => handleIncrement(1),
          children: Icon_default.chevronUp("w-3.5 h-3.5")
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime63.jsx)(
        "button",
        {
          tabIndex: -1,
          className: "p-0 hover:text-theme-accent transition-colors flex items-center justify-center h-3",
          onClick: () => handleIncrement(-1),
          children: Icon_default.chevronDown("w-3.5 h-3.5")
        }
      )
    ] }) : null;
    return /* @__PURE__ */ (0, import_jsx_runtime63.jsxs)("span", { className: cn(styles.wrapper, className), style, children: [
      hasAffix && prefix && /* @__PURE__ */ (0, import_jsx_runtime63.jsx)("span", { className: cn(affixClass, "mr-1 self-center"), style: affixStyle, children: prefix }),
      /* @__PURE__ */ (0, import_jsx_runtime63.jsxs)("span", { className: cn("relative", variant === "default" && "flex-1 min-w-0"), children: [
        inputElement,
        incrementButtons
      ] }),
      hasAffix && suffix && /* @__PURE__ */ (0, import_jsx_runtime63.jsx)("span", { className: cn(affixClass, "ml-1 self-center"), style: affixStyle, children: suffix })
    ] });
  }
  __name(ParameterField, "ParameterField");
  ParameterField.displayName = "ParameterField";
  var ParameterField_default = memo3(ParameterField);

  // public/src/react/components/calc/row/rowUtils.js
  var DebugModule2 = __toESM(require_debug());
  var Debug2 = DebugModule2.default || DebugModule2;
  function cleanLabel(label) {
    if (!label) return "";
    return String(label).replace(/_/g, " ");
  }
  __name(cleanLabel, "cleanLabel");
  function getFormattedResult(result) {
    Debug2.log("ui", "getFormattedResult", result);
    if (!result) return "";
    if (result.status === "pending") {
      return "";
    }
    return Result.getDisplayValue(result);
  }
  __name(getFormattedResult, "getFormattedResult");
  function getParameterLabel(rowText) {
    if (!rowText) return null;
    const paramName = IC?.api?.getParameterNameForDisplay ? IC.api.getParameterNameForDisplay(rowText) : null;
    return paramName && paramName.trim() ? paramName.trim() : null;
  }
  __name(getParameterLabel, "getParameterLabel");
  function isParameterRow(rowText) {
    if (!rowText) return false;
    if (!IC.parser?.getRowParameterFromString) return false;
    return IC.parser.getRowParameterFromString(rowText) !== null;
  }
  __name(isParameterRow, "isParameterRow");

  // public/src/react/components/calc/row/PresentRow.jsx
  var import_jsx_runtime64 = __toESM(require_jsx_runtime());
  var { memo: memo4, useMemo: useMemo15 } = react_global_default;
  function detectAutoFormat2(rowText) {
    const result = detectAutoFormat(rowText);
    if (!result) return null;
    if (result.type === "section") {
      return {
        autoSection: result.autoSection,
        autoSectionLevel: result.isSubsection ? 2 : 1
      };
    }
    if (result.type === "divider") {
      return { autoDivider: true };
    }
    return null;
  }
  __name(detectAutoFormat2, "detectAutoFormat");
  function renderNote(noteParts) {
    if (!noteParts || !Array.isArray(noteParts)) return null;
    return noteParts.map((part, i) => {
      if (part.type === "variable") {
        return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
          "span",
          {
            className: "font-medium",
            style: { color: "var(--accent)" },
            children: part.evaluatedValue ?? part.value
          },
          i
        );
      }
      return part.value;
    });
  }
  __name(renderNote, "renderNote");
  function SectionDivider({ title, level = 1, icon, onToggle, isCollapsed, isCollapsible }) {
    const isSubsection = level === 2;
    const toggleButton = isCollapsible ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
      "button",
      {
        type: "button",
        onClick: onToggle,
        className: cn(
          "ml-auto inline-flex items-center justify-center rounded-md",
          "h-6 w-6 transition-colors",
          "text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-alt"
        ),
        "aria-label": isCollapsed ? "Expand section" : "Collapse section",
        children: isCollapsed ? Icon_default.chevronUp("w-3.5 h-3.5") : Icon_default.chevronDown("w-3.5 h-3.5")
      }
    ) : null;
    if (isSubsection) {
      return /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
        "div",
        {
          className: cn(
            "col-span-grid-full w-full",
            "pt-4 pb-1 md:pt-5 md:pb-1",
            "flex items-center gap-2"
          ),
          children: [
            icon && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
              CalcIcon,
              {
                name: icon,
                className: "w-3.5 h-3.5 flex-shrink-0",
                style: { color: "var(--text-dim)" }
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
              "span",
              {
                className: "font-medium text-xs tracking-wider lowercase first-letter:uppercase",
                style: { color: "var(--text-dim)" },
                children: title
              }
            ),
            toggleButton
          ]
        }
      );
    }
    return /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
      "div",
      {
        className: cn(
          "col-span-grid-full w-full",
          "flex items-center gap-3 md:gap-4",
          "pt-5 pb-2 md:pt-6 md:pb-2"
        ),
        children: [
          icon && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            CalcIcon,
            {
              name: icon,
              className: "w-4 h-4 flex-shrink-0",
              style: { color: "var(--accent)" }
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            "span",
            {
              className: "font-semibold text-xs md:text-sm tracking-wider lowercase first-letter:uppercase",
              style: { color: "var(--accent)" },
              children: title
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            "div",
            {
              className: "flex-1 h-0.5 rounded-full",
              style: { background: "linear-gradient(to right, var(--accent-mix-medium, rgba(58, 122, 217, 0.3)), transparent)" }
            }
          ),
          toggleButton
        ]
      }
    );
  }
  __name(SectionDivider, "SectionDivider");
  function AutoDivider() {
    return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
      "div",
      {
        className: "col-span-grid-full w-full py-3 md:py-4",
        children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
          "div",
          {
            className: "h-px w-full",
            style: { background: "var(--border-color, rgba(0,0,0,0.1))" }
          }
        )
      }
    );
  }
  __name(AutoDivider, "AutoDivider");
  function ArrayPills({ items, maxItems = 100 }) {
    const [isExpanded, setIsExpanded] = react_global_default.useState(false);
    const displayItems = isExpanded ? items : items.slice(0, maxItems);
    const remaining = items.length - displayItems.length;
    return /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("div", { className: "flex flex-wrap gap-1.5", "data-id": "present-array-pills", children: [
      displayItems.map((item, i) => /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
        "span",
        {
          className: "px-2 py-0.5 text-sm font-medium rounded-md",
          style: {
            background: "var(--accent-mix-light, rgba(58, 122, 217, 0.1))",
            color: "var(--text)"
          },
          children: typeof item === "object" ? JSON.stringify(item) : String(item)
        },
        i
      )),
      (remaining > 0 || isExpanded) && items.length > maxItems && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "w-full flex mt-1", children: remaining > 0 ? /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
        "button",
        {
          type: "button",
          onClick: (e) => {
            e.stopPropagation();
            setIsExpanded(true);
          },
          className: "px-3 py-1 bg-theme-bg hover:bg-[var(--accent-light)] border border-[var(--border-color,rgba(0,0,0,0.1))] rounded-full text-[10px] font-bold tracking-tight uppercase transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center gap-1.5",
          style: { color: "var(--text)" },
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("span", { children: [
              "+ ",
              remaining,
              " more"
            ] }),
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("svg", { className: "w-3 h-3 opacity-60", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 9l-7 7-7-7" }) })
          ]
        }
      ) : /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
        "button",
        {
          type: "button",
          onClick: (e) => {
            e.stopPropagation();
            setIsExpanded(false);
          },
          className: "px-3 py-1 bg-theme-bg hover:bg-[var(--accent-light)] border border-[var(--border-color,rgba(0,0,0,0.1))] rounded-full text-[10px] font-bold tracking-tight uppercase transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center gap-1.5",
          style: { color: "var(--text-dim)" },
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { children: "Show less" }),
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("svg", { className: "w-3 h-3 opacity-60", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M5 15l7-7 7 7" }) })
          ]
        }
      ) })
    ] });
  }
  __name(ArrayPills, "ArrayPills");
  function ObjectCard({ obj }) {
    const entries = Object.entries(obj).filter(([k]) => k !== "__proto__");
    if (entries.length === 0) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
      "div",
      {
        className: "rounded-lg border overflow-hidden",
        style: {
          background: "var(--calc-bg-secondary, rgba(255,255,255,0.5))",
          borderColor: "var(--row-border, rgba(0,0,0,0.1))"
        },
        children: entries.map(([key, value], i) => /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
          "div",
          {
            className: cn(
              "flex justify-between px-3 py-1.5 text-sm",
              i > 0 && "border-t"
            ),
            style: { borderColor: "var(--row-border, rgba(0,0,0,0.05))" },
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
                "span",
                {
                  className: "font-medium capitalize",
                  style: { color: "var(--text-dim)" },
                  children: cleanLabel(key)
                }
              ),
              /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
                "span",
                {
                  className: "font-semibold tabular-nums",
                  style: { color: "var(--text)" },
                  children: formatTableValue(value)
                }
              )
            ]
          },
          key
        ))
      }
    );
  }
  __name(ObjectCard, "ObjectCard");
  function ObjectTable({ items, maxRows = 100 }) {
    const [isExpanded, setIsExpanded] = react_global_default.useState(false);
    if (!items || items.length === 0) return null;
    const allKeys = [...new Set(items.flatMap(
      (obj) => typeof obj === "object" && obj !== null ? Object.keys(obj) : []
    ))].filter((k) => k !== "__proto__");
    if (allKeys.length === 0) return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(ArrayPills, { items });
    const displayRows = isExpanded ? items : items.slice(0, maxRows);
    const remainingCount = items.length - displayRows.length;
    const isNumericColumn = /* @__PURE__ */ __name((key) => {
      return items.slice(0, 5).every((row) => {
        const val = row?.[key];
        if (val === null || val === void 0 || val === "") return true;
        if (typeof val === "number") return true;
        const s = String(val).trim();
        return /^\$?[0-9,.]+%?$/.test(s) || /^\d+(\.\d+)?$/.test(s);
      });
    }, "isNumericColumn");
    const alignMap = {};
    allKeys.forEach((key) => {
      alignMap[key] = isNumericColumn(key) ? "text-right" : "text-left";
    });
    return /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("div", { className: "w-full overflow-hidden rounded-xl border border-[var(--border-color,rgba(0,0,0,0.1))] bg-theme-bg shadow-sm", children: [
      /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "overflow-x-auto", children: /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("table", { className: "w-full text-sm border-collapse", children: [
        /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("thead", { children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("tr", { className: "bg-[var(--bg-secondary-mix-40,rgba(0,0,0,0.03))]", children: allKeys.map((key) => /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
          "th",
          {
            className: cn(
              "px-4 py-3 font-semibold capitalize border-b border-[var(--border-color,rgba(0,0,0,0.1))]",
              alignMap[key]
            ),
            style: { color: "var(--accent)" },
            children: cleanLabel(key)
          },
          key
        )) }) }),
        /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("tbody", { className: "divide-y divide-[var(--border-color,rgba(0,0,0,0.05))]", children: displayRows.map((row, i) => /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
          "tr",
          {
            className: cn(
              "hover:bg-[var(--accent-light)] transition-colors",
              i % 2 === 1 ? "bg-black/[0.015]" : ""
            ),
            children: allKeys.map((key) => /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
              "td",
              {
                className: cn(
                  "px-4 py-2.5 tabular-nums whitespace-nowrap",
                  alignMap[key]
                ),
                style: { color: "var(--text)" },
                children: formatTableValue(row?.[key])
              },
              key
            ))
          },
          i
        )) })
      ] }) }),
      (remainingCount > 0 || isExpanded) && items.length > maxRows && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "p-3 bg-[var(--bg-secondary-mix-20,rgba(0,0,0,0.01))] border-t border-[var(--border-color,rgba(0,0,0,0.08))] flex justify-center", children: remainingCount > 0 ? /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
        "button",
        {
          type: "button",
          onClick: (e) => {
            e.stopPropagation();
            setIsExpanded(true);
          },
          className: "px-6 py-2 bg-theme-bg hover:bg-[var(--accent-light)] border border-[var(--border-color,rgba(0,0,0,0.1))] rounded-full text-[11px] font-bold tracking-tight uppercase transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center gap-2",
          style: { color: "var(--text)" },
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("span", { children: [
              "+ ",
              remainingCount,
              " more rows"
            ] }),
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("svg", { className: "w-3.5 h-3.5 opacity-60", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M19 9l-7 7-7-7" }) })
          ]
        }
      ) : /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
        "button",
        {
          type: "button",
          onClick: (e) => {
            e.stopPropagation();
            setIsExpanded(false);
          },
          className: "px-6 py-2 bg-theme-bg hover:bg-[var(--accent-light)] border border-[var(--border-color,rgba(0,0,0,0.1))] rounded-full text-[11px] font-bold tracking-tight uppercase transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center gap-2",
          style: { color: "var(--text-dim)" },
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { children: "Show less" }),
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("svg", { className: "w-3.5 h-3.5 opacity-60", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2.5", d: "M5 15l7-7 7 7" }) })
          ]
        }
      ) })
    ] });
  }
  __name(ObjectTable, "ObjectTable");
  function formatTableValue(val) {
    if (val === null || val === void 0 || val === "") return "";
    if (typeof val === "boolean") return val ? "true" : "false";
    if (typeof val === "object") return JSON.stringify(val);
    const cleanVal = typeof val === "string" ? val.replace(/[$,\s]/g, "") : val;
    const numVal = parseFloat(cleanVal);
    if (typeof numVal === "number" && !isNaN(numVal)) {
      const isYear = Number.isInteger(numVal) && numVal >= 1900 && numVal <= 2100;
      if (isYear) return String(numVal);
      return numVal.toLocaleString(void 0, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    return String(val);
  }
  __name(formatTableValue, "formatTableValue");
  function renderStructuredData(rawValue) {
    if (rawValue === null || rawValue === void 0) return null;
    if (Array.isArray(rawValue) && rawValue.length > 0) {
      const firstItem = rawValue[0];
      if (typeof firstItem === "object" && firstItem !== null && !Array.isArray(firstItem)) {
        return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(ObjectTable, { items: rawValue });
      }
      if (rawValue.length > 1) {
        return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(ArrayPills, { items: rawValue });
      }
    }
    if (typeof rawValue === "object" && !Array.isArray(rawValue) && rawValue !== null) {
      if ("v" in rawValue && "t" in rawValue) return null;
      const keys = Object.keys(rawValue).filter((k) => k !== "__proto__");
      if (keys.length > 1) {
        return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(ObjectCard, { obj: rawValue });
      }
    }
    return null;
  }
  __name(renderStructuredData, "renderStructuredData");
  function LabelCell({ label, icon, tooltip, isHero: isHero2, layout, isCompact, isDense }) {
    if (isHero2) {
      return null;
    }
    const textClass = cn(
      "text-[87.5%] lg:text-[100%] capitalize",
      isDense && "text-[75%] font-semibold opacity-70"
    );
    const textColor = "var(--text-dim)";
    const spanClass = isCompact ? "col-span-1" : "col-span-grid-half";
    return /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
      "div",
      {
        className: cn(spanClass, "flex items-center justify-end gap-1.5"),
        "data-row-label": label,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("div", { className: "flex items-center gap-1.5 min-w-0", children: [
            icon && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
              CalcIcon,
              {
                name: icon,
                className: "w-4 h-4 md:w-5 md:h-5 flex-shrink-0",
                style: { color: "var(--accent)" }
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: cn(textClass, "text-right truncate"), style: { color: textColor }, children: cleanLabel(label) })
          ] }),
          tooltip && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: "has-tooltip flex-shrink-0", "data-tooltip": tooltip, children: Icon_default.info("w-4 h-4 opacity-50 hover:opacity-100") })
        ]
      }
    );
  }
  __name(LabelCell, "LabelCell");
  function ValueCell({ value, prefix, suffix, type, comment, isParameter, hasUIControl, uiControlElement, isHero: isHero2, calc, index: index2, onTextChange, layout, isCompact, showFocusRing, displayValue, uiResultElement, structuredElement, label, icon, isDense }) {
    const hasValue = value !== "" && value !== null && value !== void 0;
    if (isHero2) {
      const heroValueClass = "font-bold text-2xl md:text-3xl tabular-nums tracking-tight";
      const heroAffixClass = "text-base md:text-lg font-medium opacity-70";
      return /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("div", { className: "col-span-full flex flex-col items-center justify-center text-center py-4", children: [
        uiResultElement || structuredElement ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "w-full", children: uiResultElement || structuredElement }) : /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("div", { className: "inline-flex items-baseline gap-1", children: [
          prefix && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: heroAffixClass, style: { color: "var(--accent)" }, children: prefix }),
          isParameter ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            ParameterField_default,
            {
              calc,
              index: index2,
              onTextChange,
              variant: "hero",
              showFocusRing,
              displayValue
            }
          ) : hasValue ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: heroValueClass, style: { color: "var(--text)" }, "data-ic-present-row-value": true, children: value }) : null,
          suffix && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: heroAffixClass, style: { color: "var(--accent)" }, children: suffix })
        ] }),
        label && /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
          "div",
          {
            className: "mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest",
            style: {
              background: "var(--accent-mix-light, rgba(58, 122, 217, 0.1))",
              color: "var(--accent)"
            },
            children: [
              icon && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
                CalcIcon,
                {
                  name: icon,
                  className: "w-3.5 h-3.5",
                  style: { color: "var(--accent)" }
                }
              ),
              cleanLabel(label)
            ]
          }
        )
      ] });
    }
    const valueClass = cn(
      "font-semibold tabular-nums",
      isDense ? "text-sm md:text-base" : "text-base md:text-lg"
    );
    const valueColor = "var(--text)";
    const affixClass = "text-xs md:text-sm opacity-75";
    const affixColor = "var(--text-dim)";
    const spanClass = isCompact ? "col-span-1" : layout?.getSpan ? layout.getSpan("1/2") : "col-span-3";
    return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
      "div",
      {
        className: cn(spanClass, "flex items-baseline"),
        children: hasUIControl && uiControlElement ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "w-full", children: uiControlElement }) : isParameter ? /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("span", { className: "flex items-baseline", children: [
          prefix && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: cn(affixClass, "mr-1"), style: { color: affixColor }, children: prefix }),
          /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            ParameterField_default,
            {
              calc,
              index: index2,
              onTextChange,
              variant: "minimal",
              showFocusRing,
              displayValue
            }
          ),
          suffix && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: cn(affixClass, "ml-1"), style: { color: affixColor }, children: suffix })
        ] }) : uiResultElement ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "w-full", children: uiResultElement }) : structuredElement ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "w-full", children: structuredElement }) : hasValue ? /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("span", { className: "flex items-baseline", children: [
          prefix && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: cn(affixClass, "mr-1"), style: { color: affixColor }, children: prefix }),
          /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: valueClass, style: { color: valueColor }, "data-ic-present-row-value": true, children: value }),
          suffix && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: cn(affixClass, "ml-1"), style: { color: affixColor }, children: suffix })
        ] }) : null
      }
    );
  }
  __name(ValueCell, "ValueCell");
  function getRowSpan(rowWidth, layout) {
    return layout.getSpan(rowWidth || "full");
  }
  __name(getRowSpan, "getRowSpan");
  function PresentRow({
    calc,
    index: index2,
    result,
    onTextChange,
    className,
    style,
    isInSplitLayout,
    isStacked,
    isSelected,
    onSelectRow,
    paramRowLockMap,
    sectionMeta,
    isDense,
    // Received as props from Row.jsx to avoid context subscription
    Action,
    layout
  }) {
    usePerf(`PresentRow:${index2}`);
    const safeLayout = layout || {
      gridType: "default",
      gridConfig: {},
      tokens: { gapX: "0.5rem" },
      getSpan: /* @__PURE__ */ __name((width) => width === "full" ? 12 : width === "1/2" ? 6 : width === "1/3" ? 4 : 12, "getSpan")
    };
    const safeAction = Action || {
      updateRowText: /* @__PURE__ */ __name(() => {
      }, "updateRowText"),
      toggleRowVisibility: /* @__PURE__ */ __name(() => {
      }, "toggleRowVisibility"),
      deleteRow: /* @__PURE__ */ __name(() => {
      }, "deleteRow"),
      insertRow: /* @__PURE__ */ __name(() => {
      }, "insertRow")
    };
    const { isParameter, label: paramLabel, value: paramValue } = useParameterData(calc, index2);
    const { renderUIControl } = useUIHelpers_default(calc, index2, safeAction, result, isParameter);
    const presentation = result?.presentation || {};
    const presentationType = presentation.mode;
    const rowWidth = presentation.rowWidth;
    const isHero2 = presentation.hero || presentationType === "hero";
    const isAdvanced2 = presentation.advanced || presentationType === "advanced";
    const isHalf = presentation.half || rowWidth === "1/2";
    const isThird = presentation.third || rowWidth === "1/3";
    const icon = presentation.icon || null;
    const prefix = presentation.prefix || "";
    const explicitSuffix = presentation.suffix || "";
    const tooltip = presentation.tooltip || "";
    const note = presentation.note || null;
    const label = presentation.label || paramLabel || result?.display?.label || "";
    const displayValue = getFormattedResult(result);
    const type = result?.type?.units || "";
    const comment = result?.display?.comment || "";
    const outputRaw = result?.output?.raw;
    const uiResultElement = outputRaw && typeof outputRaw === "object" && outputRaw.type ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(UIResult_default, { result: outputRaw, index: index2, label }) : null;
    const isChartResult = typeof outputRaw?.type === "string" && outputRaw.type.toLowerCase().endsWith("chart");
    const getRawValue2 = /* @__PURE__ */ __name((res) => {
      if (!res) return null;
      if (Array.isArray(res)) return res;
      if (typeof res === "object" && !res.type) return res;
      if (res.v && Array.isArray(res.v)) return res.v;
      return null;
    }, "getRawValue");
    const rawStructuredSource = getRawValue2(outputRaw);
    const structuredElement = renderStructuredData(rawStructuredSource);
    const suffix = explicitSuffix || type;
    const normalizedSuffix = typeof suffix === "string" ? suffix.trim() : "";
    const paramValueText = paramValue !== null && paramValue !== void 0 ? String(paramValue) : "";
    const escapeRegExp = /* @__PURE__ */ __name((value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "escapeRegExp");
    const normalizedSuffixLower = normalizedSuffix.toLowerCase();
    const finalStyle = useMemo15(() => {
      const base = {
        ...style,
        gap: `0 ${safeLayout.tokens.gapX}`
      };
      if (isHero2) {
        return {
          ...base,
          minHeight: "80px",
          background: "var(--accent-mix-light, rgba(58, 122, 217, 0.06))",
          border: "1px solid var(--accent-mix-light, rgba(58, 122, 217, 0.12))",
          borderRadius: "0.75rem"
        };
      }
      return base;
    }, [style, isHero2, safeLayout.tokens.gapX]);
    if (!calc.rows[index2]) return null;
    if (presentationType === "hidden") return null;
    const rowText = calc.rows[index2]?.text || "";
    const isParameterStable = paramRowLockMap?.get(index2) ?? isParameter;
    const displaySuffix = normalizedSuffix ? isParameterStable ? suffix : displayValue?.toLowerCase?.().includes(normalizedSuffixLower) ? "" : suffix : "";
    const parameterDisplayValue = isParameterStable && normalizedSuffix ? paramValueText.replace(new RegExp(`\\s*${escapeRegExp(normalizedSuffix)}\\s*$`, "i"), "").trim() : paramValueText;
    const autoFormat = !presentation.section ? detectAutoFormat2(rowText) : null;
    if (presentation.section || autoFormat?.autoSection) {
      const sectionTitle = presentation.section || autoFormat.autoSection;
      const sectionLevel = autoFormat?.autoSectionLevel || 1;
      return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
        "row",
        {
          "data-row": index2,
          "data-testid": "calc-row",
          "data-id": `row-${index2}`,
          "data-presentation": "section",
          "data-auto-detected": autoFormat ? "true" : void 0,
          className: cn("col-span-full", className),
          style,
          children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            SectionDivider,
            {
              title: sectionTitle,
              level: sectionLevel,
              icon: presentation.icon || autoFormat?.autoIcon || null,
              onToggle: sectionMeta?.onToggle,
              isCollapsed: sectionMeta?.isCollapsed,
              isCollapsible: Boolean(sectionMeta)
            }
          )
        }
      );
    }
    if (autoFormat?.autoDivider) {
      return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
        "row",
        {
          "data-row": index2,
          "data-testid": "calc-row",
          "data-id": `row-${index2}`,
          "data-presentation": "divider",
          "data-auto-detected": "true",
          className: cn("col-span-full", className),
          style,
          children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(AutoDivider, {})
        }
      );
    }
    const uiMeta = result?.ui;
    const uiControlElement = uiMeta ? renderUIControl(uiMeta) : null;
    const hasUIControl = !!uiControlElement;
    const isStandaloneComment = !label && !displayValue && !isParameterStable && !uiResultElement && !structuredElement && comment;
    const isEmpty = !rowText.trim() && !comment && !note && !hasUIControl && !uiResultElement && !structuredElement;
    if (isEmpty) return null;
    if (isStandaloneComment) {
      return /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
        "row",
        {
          "data-row": index2,
          "data-testid": "calc-row",
          "data-id": `row-${index2}`,
          "data-presentation": "comment",
          className: cn("col-span-full py-2", className),
          style,
          children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            "div",
            {
              className: "rounded-lg px-4 py-2.5",
              style: { background: "var(--accent-mix-light, rgba(58, 122, 217, 0.06))" },
              children: /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
                "p",
                {
                  className: "text-sm leading-relaxed",
                  style: { color: "var(--text)" },
                  children: comment
                }
              )
            }
          )
        }
      );
    }
    const isWide = presentation.rowWidth === "full" || presentation.wide;
    const shouldStack = (isStacked || isParameterStable || isChartResult || isWide || !!structuredElement) && !isHero2;
    if (shouldStack || isStacked && isInSplitLayout && isHero2) {
      const effectiveNote = note || (comment ? [{ type: "text", value: comment }] : null);
      const stackedValueClass = isHero2 ? "font-bold text-xl md:text-2xl tabular-nums tracking-tight" : isDense ? "font-semibold text-sm md:text-base tabular-nums" : "font-semibold text-base tabular-nums";
      const stackedValueWrapperClass = cn(
        "inline-flex items-center px-2 py-1 round-md",
        stackedValueClass
      );
      const stackedValueColor = isHero2 ? "var(--accent)" : "var(--text)";
      const stackedAffixClass = isHero2 ? "text-[var(--calc-present-affix-size-hero)] font-medium opacity-70" : "text-xs md:text-sm opacity-75";
      const stackedAffixColor = isHero2 ? "var(--accent)" : "var(--text-dim)";
      const stackedRowStyle = isHero2 ? isInSplitLayout ? {
        ...style,
        minHeight: layout.tokens.rowMinHeight,
        gap: `0 ${layout.tokens.gapX}`,
        background: "color-mix(in srgb, var(--calc-bg-secondary, var(--bg)) 88%, var(--accent) 12%)",
        borderRadius: layout.tokens.borderRadius,
        paddingLeft: layout.tokens.paddingX,
        paddingRight: layout.tokens.paddingX
      } : { ...finalStyle, padding: layout.tokens.heroPadding } : style;
      return /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
        "row",
        {
          "data-row": index2,
          "data-testid": "calc-row",
          "data-id": `row-${index2}`,
          "data-stacked": "true",
          "data-hero": isHero2 || void 0,
          className: cn(
            "flex flex-col w-full",
            isDense ? "space-y-0" : "space-y-1",
            className
          ),
          style: stackedRowStyle,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("div", { className: cn(
              "flex items-center gap-1.5",
              isParameterStable && "w-full justify-between"
            ), children: [
              /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("div", { className: "flex items-center gap-1.5 min-w-0", children: [
                icon && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
                  CalcIcon,
                  {
                    name: icon,
                    className: cn("flex-shrink-0", isDense ? "w-3 h-3" : "w-4 h-4"),
                    style: { color: "var(--accent)" }
                  }
                ),
                /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("label", { className: cn(
                  "font-bold uppercase tracking-wider truncate",
                  isDense ? "text-[10px] opacity-70" : "text-xs"
                ), children: cleanLabel(label) })
              ] }),
              tooltip && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: "has-tooltip flex-shrink-0", "data-tooltip": tooltip, children: Icon_default.info("w-4 h-4 opacity-50 hover:opacity-100") })
            ] }),
            /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { children: hasUIControl && uiControlElement ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "w-full", children: uiControlElement }) : isParameterStable ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
              ParameterField_default,
              {
                calc,
                index: index2,
                onTextChange,
                variant: isDense ? "minimal" : "default",
                showFocusRing: false,
                displayValue: parameterDisplayValue,
                prefix,
                suffix: displaySuffix
              }
            ) : uiResultElement ? /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: "w-full", children: uiResultElement }) : structuredElement ? structuredElement : displayValue ? /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)("span", { className: "flex items-baseline", children: [
              prefix && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: cn(stackedAffixClass, "mr-1"), style: { color: stackedAffixColor }, children: prefix }),
              /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: stackedValueWrapperClass, style: { color: stackedValueColor, minHeight: isDense ? "auto" : layout.tokens.rowMinHeight }, children: displayValue }),
              displaySuffix && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("span", { className: cn(stackedAffixClass, "ml-1"), style: { color: stackedAffixColor }, children: displaySuffix })
            ] }) : null }),
            effectiveNote && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)("div", { className: cn(isDense ? "text-xs" : "text-sm"), style: { color: "var(--text-dim)" }, children: renderNote(effectiveNote) })
          ]
        }
      );
    }
    const spanClass = getRowSpan(rowWidth, safeLayout);
    const isCompact = isHalf || isThird;
    const internalGridClass = "grid grid-cols-6";
    const rowClassName = cn(
      "group relative",
      spanClass,
      isHero2 && "col-span-grid-full",
      !shouldStack && !isHero2 && internalGridClass,
      "items-center",
      className
    );
    return /* @__PURE__ */ (0, import_jsx_runtime64.jsxs)(
      "row",
      {
        "data-row": index2,
        "data-testid": "calc-row",
        "data-id": `row-${index2}`,
        "data-hero": isHero2 || void 0,
        "data-advanced": isAdvanced2 || void 0,
        "data-half": isHalf || void 0,
        "data-third": isThird || void 0,
        "data-icon": icon || void 0,
        "data-selected": isSelected || void 0,
        className: cn(
          rowClassName,
          isHero2 ? "py-4" : "px-0"
        ),
        onClick: () => onSelectRow?.(index2),
        style: finalStyle,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            LabelCell,
            {
              label,
              icon,
              tooltip,
              isHero: isHero2,
              layout,
              isCompact: isStacked,
              isDense
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            ValueCell,
            {
              value: paramValue !== null ? parameterDisplayValue : displayValue,
              prefix,
              suffix: displaySuffix,
              type,
              displayValue: parameterDisplayValue,
              comment,
              isParameter: isParameterStable,
              hasUIControl,
              uiControlElement,
              uiResultElement,
              structuredElement,
              isHero: isHero2,
              calc,
              index: index2,
              onTextChange,
              layout,
              isCompact: isStacked,
              showFocusRing: isSelected,
              label,
              icon,
              isDense
            }
          ),
          (note || comment) && /* @__PURE__ */ (0, import_jsx_runtime64.jsx)(
            "div",
            {
              className: cn(
                isHero2 ? "col-span-grid-full text-center" : "col-span-grid-half",
                "text-sm",
                isDense ? "py-1" : "py-2"
              ),
              style: {
                color: "var(--text-dim)",
                gridColumnStart: isHero2 ? "auto" : 4
              },
              children: renderNote(note || [{ type: "text", value: comment }])
            }
          )
        ]
      }
    );
  }
  __name(PresentRow, "PresentRow");
  PresentRow.displayName = "PresentRow";
  function arePresentRowPropsEqual(prevProps, nextProps) {
    return areBaseRowPropsEqual(prevProps, nextProps);
  }
  __name(arePresentRowPropsEqual, "arePresentRowPropsEqual");
  var PresentRow_default = memo4(PresentRow, arePresentRowPropsEqual);

  // public/src/react/components/calc/Row.jsx
  var import_jsx_runtime65 = __toESM(require_jsx_runtime());
  var Row = /* @__PURE__ */ __name((props) => {
    const { isPresentMode = false, rowRef, Action: propsAction, layout: propsLayout, ...restProps } = props;
    const contextAction = useAction();
    const contextLayout = useLayoutAdapter();
    const Action = propsAction || contextAction;
    const layout = propsLayout || contextLayout;
    if (isPresentMode) {
      const { calc, index: index2, result, onTextChange, className, style, isInSplitLayout, isStacked, isSelected, onSelectRow, paramRowLockMap, sectionMeta } = props;
      return /* @__PURE__ */ (0, import_jsx_runtime65.jsx)(
        PresentRow_default,
        {
          calc,
          index: index2,
          result,
          onTextChange,
          className,
          style,
          isInSplitLayout,
          isStacked,
          isSelected,
          onSelectRow,
          paramRowLockMap,
          sectionMeta,
          Action,
          layout
        }
      );
    }
    return /* @__PURE__ */ (0, import_jsx_runtime65.jsx)(CalcRow_default, { rowRef, Action, layout, ...restProps });
  }, "Row");
  Row.displayName = "Row";
  function areRowPropsEqual(prevProps, nextProps) {
    try {
      if (!areBaseRowPropsEqual(prevProps, nextProps)) return false;
      if (prevProps.isPresentMode !== nextProps.isPresentMode) return false;
      if (prevProps.rowClassName !== nextProps.rowClassName) return false;
      if (prevProps.className !== nextProps.className) return false;
      if (prevProps.viewMode !== nextProps.viewMode) return false;
      if (prevProps.isSelected !== nextProps.isSelected) return false;
      return true;
    } catch (e) {
      console.error("[Row MemoCheck] Error:", e);
      return false;
    }
  }
  __name(areRowPropsEqual, "areRowPropsEqual");
  var Row_default = react_global_default.memo(Row, areRowPropsEqual);

  // public/src/react/components/calc/CalcModeRows.jsx
  init_react_global();

  // public/src/react/components/calc/AddRowButton.jsx
  init_react_global();
  var import_jsx_runtime66 = __toESM(require_jsx_runtime());
  function AddRowButton({ onClick, className }) {
    return /* @__PURE__ */ (0, import_jsx_runtime66.jsxs)(import_jsx_runtime66.Fragment, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime66.jsx)("number", { className: "contents" }),
      /* @__PURE__ */ (0, import_jsx_runtime66.jsx)("c1", { className: cn("flex items-center justify-center py-0.5", className), children: /* @__PURE__ */ (0, import_jsx_runtime66.jsxs)(
        "button",
        {
          onClick,
          className: cn(
            "flex items-center gap-1.5 px-2.5 py-1.5 rounded",
            "text-xs text-theme-text-muted hover:text-theme-text",
            "bg-transparent hover:bg-theme-bg-hover",
            "opacity-50 hover:opacity-100",
            "transition-all duration-200"
          ),
          "aria-label": "Add a new row (Enter)",
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime66.jsx)("span", { className: "text-sm leading-none font-light", children: "+" }),
            /* @__PURE__ */ (0, import_jsx_runtime66.jsx)("span", { className: "font-normal", children: "add row" }),
            /* @__PURE__ */ (0, import_jsx_runtime66.jsx)("kbd", { className: "ml-0.5 px-1 py-0.5 text-[10px] font-medium rounded border border-soft bg-theme-bg-secondary opacity-70", children: "enter" })
          ]
        }
      ) }),
      /* @__PURE__ */ (0, import_jsx_runtime66.jsx)("c2", { className: "contents" })
    ] });
  }
  __name(AddRowButton, "AddRowButton");
  var AddRowButton_default = react_global_default.memo(AddRowButton);

  // public/src/react/components/calc/DraggableRowsWrapper.jsx
  init_react_global();
  var import_jsx_runtime67 = __toESM(require_jsx_runtime());
  var { useCallback: useCallback24, useState: useState38, useRef: useRef27, useEffect: useEffect38 } = react_global_default;
  var DEBUG = typeof window !== "undefined" && window.location?.search?.includes("debug");
  function logDrag(phase, data) {
    if (DEBUG) console.log(`[DRAG] ${phase}`, data);
  }
  __name(logDrag, "logDrag");
  function dispatchDragEvent(name, detail) {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(`instacalc:drag:${name}`, { detail }));
      logDrag(name.toUpperCase(), detail);
    }
  }
  __name(dispatchDragEvent, "dispatchDragEvent");
  function DropPlaceholder({ visible }) {
    if (!visible) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime67.jsx)(
      "div",
      {
        className: "drop-placeholder",
        role: "separator",
        "aria-hidden": "true",
        style: {
          gridColumn: "1 / -1",
          minHeight: "32px",
          margin: "2px 0",
          border: "2px dashed var(--text-dim, #6a7193)",
          borderRadius: "6px",
          opacity: 0.5
        }
      }
    );
  }
  __name(DropPlaceholder, "DropPlaceholder");
  function DragPreview({ item, position, rows }) {
    if (!item) return null;
    const row = rows?.[item.index];
    const text = item.text || row?.text || "";
    const result = item.result || "";
    return react_dom_global_default.createPortal(
      /* @__PURE__ */ (0, import_jsx_runtime67.jsxs)(
        "div",
        {
          className: "drag-preview",
          style: {
            position: "fixed",
            left: position.x + 12,
            top: position.y - 20,
            pointerEvents: "none",
            zIndex: 1e4,
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            gap: "8px",
            padding: "8px 12px",
            background: "color-mix(in srgb, var(--main-bg, var(--bg, #fff)) 85%, transparent)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid color-mix(in srgb, var(--accent, #db8152) 40%, transparent)",
            borderRadius: "8px",
            alignItems: "center",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
            transform: "rotate(1deg)",
            opacity: 0.5,
            minWidth: "220px",
            maxWidth: "350px"
          },
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime67.jsxs)("span", { style: {
              fontSize: "11px",
              color: "var(--row-number-color, #6a7193)",
              fontWeight: 500
            }, children: [
              "R",
              item.index + 1
            ] }),
            /* @__PURE__ */ (0, import_jsx_runtime67.jsx)("span", { style: {
              padding: "4px 8px",
              background: "transparent",
              borderRadius: "4px",
              fontSize: "14px",
              color: "var(--text, #c5c9d4)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }, children: text || "Empty row" }),
            /* @__PURE__ */ (0, import_jsx_runtime67.jsx)("span", { style: {
              fontSize: "14px",
              fontWeight: 500,
              color: "var(--result-color, var(--accent, #db8152))"
            }, children: result })
          ]
        }
      ),
      document.body
    );
  }
  __name(DragPreview, "DragPreview");
  function DraggableRowsWrapper({ calc, results, onReorder, children }) {
    const [dragState, setDragState] = useState38({
      isDragging: false,
      draggedIndex: null,
      dropIndex: null,
      mousePos: { x: 0, y: 0 },
      draggedText: ""
      // Capture text at drag start
    });
    const containerRef = useRef27(null);
    const onReorderRef = useRef27(onReorder);
    useEffect38(() => {
      onReorderRef.current = onReorder;
    }, [onReorder]);
    useEffect38(() => {
      const handleMouseDown = /* @__PURE__ */ __name((e) => {
        if (!containerRef.current?.contains(e.target)) return;
        const dragHandle = e.target.closest("[data-react-drag-handle]");
        if (!dragHandle) {
          logDrag("SKIP", { reason: "not a drag handle", target: e.target.tagName });
          return;
        }
        if (e.target.closest('[role="menuitem"]') || e.target.closest(".hover-menu-item")) {
          logDrag("SKIP", { reason: "clicking menu item" });
          return;
        }
        const row = e.target.closest("row[data-row]");
        if (!row) {
          logDrag("SKIP", { reason: "no row[data-row] found", handle: dragHandle.outerHTML?.slice(0, 100) });
          return;
        }
        e.preventDefault();
        const index2 = parseInt(row.getAttribute("data-row"), 10);
        const inputEl = row.querySelector("input, [contenteditable], textarea");
        const rowText = inputEl?.value || inputEl?.textContent || row.textContent?.slice(0, 100) || "";
        if (inputEl && inputEl.focus) {
          inputEl.focus({ preventScroll: true });
        }
        logDrag("MOUSEDOWN", { index: index2, target: e.target.tagName, text: rowText });
        setDragState({
          isDragging: true,
          draggedIndex: index2,
          dropIndex: index2,
          mousePos: { x: e.clientX, y: e.clientY },
          draggedText: rowText.trim()
        });
        document.body.classList.add("is-dragging");
        document.body.style.cursor = "grabbing";
        window._isDragging = true;
        dispatchDragEvent("start", { fromIndex: index2 });
      }, "handleMouseDown");
      document.addEventListener("mousedown", handleMouseDown);
      return () => document.removeEventListener("mousedown", handleMouseDown);
    }, []);
    const handleMouseMove = useCallback24((e) => {
      if (!dragState.isDragging) return;
      const { draggedIndex } = dragState;
      setDragState((prev) => ({
        ...prev,
        mousePos: { x: e.clientX, y: e.clientY }
      }));
      const container = containerRef.current;
      if (!container) return;
      const calcRowCount = calc?.rows?.length || 0;
      const rowsContainer = container.querySelector("[data-calc-rows]") || container;
      const allRows = Array.from(rowsContainer.querySelectorAll("row[data-row]"));
      const calcRows = allRows.filter((row) => {
        const idx = parseInt(row.getAttribute("data-row"), 10);
        return !isNaN(idx) && idx >= 0 && idx < calcRowCount;
      }).sort((a, b) => {
        const idxA = parseInt(a.getAttribute("data-row"), 10);
        const idxB = parseInt(b.getAttribute("data-row"), 10);
        return idxA - idxB;
      });
      const validRowCount = calcRows.length;
      let dropIndex = validRowCount;
      for (let i = 0; i < calcRows.length; i++) {
        const rect = calcRows[i].getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        if (e.clientY < midY) {
          dropIndex = parseInt(calcRows[i].getAttribute("data-row"), 10);
          break;
        }
      }
      dropIndex = Math.max(0, Math.min(dropIndex, validRowCount));
      console.log("[DRAG] handleMouseMove:", {
        calcRowCount,
        allRowsFound: allRows.length,
        validCalcRows: calcRows.length,
        rowIndices: calcRows.map((r) => parseInt(r.getAttribute("data-row"), 10)),
        mouseY: e.clientY,
        dropIndex,
        draggedIndex,
        containerTag: rowsContainer.tagName,
        containerHasDataCalcRows: rowsContainer.hasAttribute?.("data-calc-rows")
      });
      setDragState((prev) => ({ ...prev, dropIndex }));
      dispatchDragEvent("move", { targetIndex: dropIndex, position: dropIndex > draggedIndex ? "after" : "before" });
    }, [dragState.isDragging, dragState.draggedIndex, calc?.rows?.length]);
    const handleMouseUp = useCallback24(() => {
      if (!dragState.isDragging) return;
      const { draggedIndex, dropIndex } = dragState;
      let toIndex = dropIndex;
      if (toIndex > draggedIndex) {
        toIndex--;
      }
      const success = draggedIndex !== toIndex;
      logDrag("MOUSEUP", { draggedIndex, dropIndex, toIndex, success });
      if (success && onReorderRef.current) {
        onReorderRef.current(draggedIndex, toIndex);
      }
      dispatchDragEvent("end", { fromIndex: draggedIndex, toIndex, success });
      setDragState({
        isDragging: false,
        draggedIndex: null,
        dropIndex: null,
        mousePos: { x: 0, y: 0 }
      });
      document.body.classList.remove("is-dragging");
      document.body.style.cursor = "";
      window._isDragging = false;
    }, [dragState]);
    useEffect38(() => {
      if (!dragState.isDragging) return;
      const onMove = /* @__PURE__ */ __name((e) => handleMouseMove(e), "onMove");
      const onUp = /* @__PURE__ */ __name(() => handleMouseUp(), "onUp");
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      return () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
    }, [dragState.isDragging, handleMouseMove, handleMouseUp]);
    const draggedResult = dragState.isDragging && results?.[dragState.draggedIndex] ? results[dragState.draggedIndex]?.output?.display || "" : "";
    const enhancedChildren = react_global_default.Children.map(children, (child) => {
      if (!react_global_default.isValidElement(child)) return child;
      if (child.type === "rows" || child.props?.["data-calc-rows"]) {
        const rowChildren = react_global_default.Children.toArray(child.props.children);
        const enhancedRows = [];
        rowChildren.forEach((rowChild, i) => {
          const showPlaceholder = dragState.isDragging && dragState.dropIndex === i && dragState.draggedIndex !== i && dragState.draggedIndex !== i - 1;
          if (showPlaceholder) {
            enhancedRows.push(
              /* @__PURE__ */ (0, import_jsx_runtime67.jsx)(DropPlaceholder, { visible: true }, `placeholder-${i}`)
            );
          }
          if (react_global_default.isValidElement(rowChild)) {
            const rowIndex = rowChild.props?.index;
            const isDragging = dragState.isDragging && dragState.draggedIndex === rowIndex;
            enhancedRows.push(
              react_global_default.cloneElement(rowChild, {
                key: rowChild.key || `row-${i}`,
                isDragging,
                isAnyRowDragging: dragState.isDragging
              })
            );
          } else {
            enhancedRows.push(rowChild);
          }
        });
        const showEndPlaceholder = dragState.isDragging && dragState.dropIndex === rowChildren.length;
        if (showEndPlaceholder) {
          enhancedRows.push(
            /* @__PURE__ */ (0, import_jsx_runtime67.jsx)(DropPlaceholder, { visible: true }, "placeholder-end")
          );
        }
        return react_global_default.cloneElement(child, {}, enhancedRows);
      }
      return child;
    });
    return /* @__PURE__ */ (0, import_jsx_runtime67.jsxs)(import_jsx_runtime67.Fragment, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime67.jsx)(
        "div",
        {
          ref: containerRef,
          style: { display: "contents" },
          children: enhancedChildren
        }
      ),
      dragState.isDragging && /* @__PURE__ */ (0, import_jsx_runtime67.jsx)(
        DragPreview,
        {
          item: { index: dragState.draggedIndex, result: draggedResult, text: dragState.draggedText },
          position: dragState.mousePos,
          rows: calc?.rows
        }
      )
    ] });
  }
  __name(DraggableRowsWrapper, "DraggableRowsWrapper");
  if (typeof window !== "undefined") {
    window.DragSimulator = {
      /**
       * Programmatically move a row from one position to another
       * @param {number} fromIndex - Source row index
       * @param {number} toIndex - Target row index
       * @returns {Promise<boolean>} - Resolves to true if move was successful
       */
      moveRow: /* @__PURE__ */ __name(async (fromIndex, toIndex) => {
        logDrag("SIMULATE", { fromIndex, toIndex });
        dispatchDragEvent("start", { fromIndex });
        await new Promise((r) => setTimeout(r, 50));
        dispatchDragEvent("move", { targetIndex: toIndex, position: toIndex > fromIndex ? "after" : "before" });
        await new Promise((r) => setTimeout(r, 50));
        const success = fromIndex !== toIndex;
        dispatchDragEvent("end", { fromIndex, toIndex, success });
        if (window.Action?.dragMove && success) {
          window.Action.dragMove(fromIndex, toIndex);
          return true;
        }
        console.warn("[DragSimulator] Action.dragMove not available");
        return false;
      }, "moveRow"),
      /**
       * Listen to drag events for debugging
       * @param {Function} callback - Called with (eventName, detail)
       * @returns {Function} - Cleanup function to remove listeners
       */
      listen: /* @__PURE__ */ __name((callback) => {
        const events = ["start", "move", "end"];
        const handlers = events.map((name) => {
          const handler = /* @__PURE__ */ __name((e) => callback(name, e.detail), "handler");
          window.addEventListener(`instacalc:drag:${name}`, handler);
          return { name, handler };
        });
        return () => {
          handlers.forEach(({ name, handler }) => {
            window.removeEventListener(`instacalc:drag:${name}`, handler);
          });
        };
      }, "listen")
    };
  }
  var DraggableRowsWrapper_default = DraggableRowsWrapper;

  // public/src/react/components/calc/CalcModeRows.jsx
  var import_jsx_runtime68 = __toESM(require_jsx_runtime());
  function CalcModeRows({ calc, strategy, results, selectedRow, onSelectRow, onTextChange, viewMode, rowRefs, focusRow, deleteRow, insertRow }) {
    const Action = useAction();
    const layout = useLayoutAdapter();
    const handleAddRow = react_global_default.useCallback(() => {
      if (insertRow && calc) {
        insertRow(calc.rows.length);
      }
    }, [insertRow, calc]);
    const handleReorder = react_global_default.useCallback((fromIndex, toIndex) => {
      if (Action?.dragMove) {
        Action.dragMove(fromIndex, toIndex);
      }
    }, [Action]);
    if (!results || !calc) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime68.jsx)(DraggableRowsWrapper_default, { calc, results, onReorder: handleReorder, children: /* @__PURE__ */ (0, import_jsx_runtime68.jsxs)(
      "rows",
      {
        "data-calc-rows": true,
        className: "contents",
        style: { display: "contents" },
        children: [
          results.map((result, i) => {
            const row = calc.rows?.[i];
            const rowKey = row?.id || `${calc.id}-${i}`;
            return /* @__PURE__ */ (0, import_jsx_runtime68.jsx)(
              Row_default,
              {
                calc,
                index: i,
                calcId: calc.id,
                row,
                rowsLength: calc.rows?.length || 0,
                rowRef: (el) => {
                  if (rowRefs) rowRefs.current["row" + i] = el;
                },
                result,
                viewMode,
                isSelected: selectedRow === i,
                onSelectRow,
                focusRow,
                onTextChange,
                deleteRow,
                insertRow,
                isPresentMode: false,
                strategy,
                Action,
                layout
              },
              rowKey
            );
          }),
          strategy.showAddRowButton && /* @__PURE__ */ (0, import_jsx_runtime68.jsx)(AddRowButton_default, { onClick: handleAddRow })
        ]
      }
    ) });
  }
  __name(CalcModeRows, "CalcModeRows");
  var CalcModeRows_default = react_global_default.memo(CalcModeRows);

  // public/src/react/components/calc/PresentModeRows.jsx
  init_react_global();

  // public/src/react/components/calc/row/AdvancedToggle.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime69 = __toESM(require_jsx_runtime());
  function AdvancedToggle({ count: count3, isExpanded, onToggle, className }) {
    const label = isExpanded ? `Hide advanced` : `${count3} more options`;
    return /* @__PURE__ */ (0, import_jsx_runtime69.jsxs)(
      "button",
      {
        type: "button",
        onClick: onToggle,
        className: cn(
          "col-span-grid-full",
          // Full width in grid
          "flex items-center justify-center gap-1",
          "text-xs",
          "transition-opacity duration-150",
          "opacity-50 hover:opacity-80",
          className
        ),
        style: {
          color: "var(--text-dim)"
        },
        "aria-expanded": isExpanded,
        "aria-label": label,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime69.jsx)("span", { className: "border-b border-dotted border-current", children: label }),
          /* @__PURE__ */ (0, import_jsx_runtime69.jsx)(
            "span",
            {
              className: cn(
                "transition-transform duration-200",
                isExpanded && "rotate-180"
              ),
              children: Icon_default.chevronDown("w-3 h-3")
            }
          )
        ]
      }
    );
  }
  __name(AdvancedToggle, "AdvancedToggle");
  AdvancedToggle.displayName = "AdvancedToggle";
  var AdvancedToggle_default = AdvancedToggle;

  // public/src/react/components/calc/presentTheme.js
  var PRESENT_THEMES = {
    soft: {
      id: "soft",
      label: "Soft",
      vars: {
        "--present-section-bg": "var(--accent-light)"
      },
      classes: {
        sectionTint: "present-section-tint"
      }
    },
    paper: {
      id: "paper",
      label: "Paper",
      vars: {
        "--present-section-bg": "color-mix(in srgb, var(--bg) 88%, var(--text) 12%)"
      },
      classes: {
        sectionTint: "present-section-tint"
      }
    }
  };
  function getPresentTheme(calc) {
    const themeId = calc?.presentationTheme || "soft";
    return PRESENT_THEMES[themeId] || PRESENT_THEMES.soft;
  }
  __name(getPresentTheme, "getPresentTheme");

  // public/src/react/components/calc/PresentModeRows.jsx
  var import_rowViewModel = __toESM(require_rowViewModel());
  var import_jsx_runtime70 = __toESM(require_jsx_runtime());
  function PresentModeRows({
    calc,
    results,
    rowRefs,
    viewMode,
    selectedRow,
    onSelectRow,
    focusRow,
    onTextChange,
    deleteRow,
    insertRow,
    stableRowClassName,
    stableRowStyle,
    stableRowClassNames,
    forceMobile = false,
    forceDesktop = false
  }) {
    const [expandedSections, setExpandedSections] = react_global_default.useState(/* @__PURE__ */ new Set());
    const [collapsedSections, setCollapsedSections] = react_global_default.useState(/* @__PURE__ */ new Set());
    const parameterLockMapRef = react_global_default.useRef(/* @__PURE__ */ new Map());
    const prevViewModeRef = react_global_default.useRef(null);
    const prevCalcIdRef = react_global_default.useRef(null);
    const sections = react_global_default.useMemo(() => {
      const viewModels = (0, import_rowViewModel.deriveViewModels)(calc?.rows || [], results, "present", {
        includeSkipped: true,
        includeAdvanced: true
      });
      return (0, import_rowViewModel.groupBySection)(viewModels);
    }, [results, calc]);
    const layoutMode = calc?.presentationLayout || "single";
    const presentTheme = getPresentTheme(calc);
    const gridGapClass = "gap-x-[var(--col-gap,1.5rem)] gap-y-[var(--row-gap,1.5rem)]";
    const isChartRow = /* @__PURE__ */ __name((rowResult) => {
      const type = rowResult?.result?.type;
      return typeof type === "string" && type.toLowerCase().endsWith("chart");
    }, "isChartRow");
    const isCompactWidth = /* @__PURE__ */ __name((rowResult) => {
      const rowWidth = rowResult?.presentation?.rowWidth;
      return rowResult?.presentation?.half || rowWidth === "1/2" || rowResult?.presentation?.third || rowWidth === "1/3";
    }, "isCompactWidth");
    const tabbedSections = react_global_default.useMemo(
      () => sections.filter((section) => section.isTab),
      [sections]
    );
    const hasTabs = tabbedSections.length >= 2;
    const firstTabId = hasTabs ? tabbedSections[0].id : null;
    const [activeTabId, setActiveTabId] = react_global_default.useState(firstTabId);
    const sectionBodyClass = /* @__PURE__ */ __name((isCollapsed) => cn(
      "col-span-full w-full grid grid-cols-1 transition-[grid-template-rows,opacity,transform] duration-medium ease-in-out",
      isCollapsed ? "grid-rows-[0fr] opacity-0 -translate-y-1 pointer-events-none" : "grid-rows-[1fr] opacity-100 translate-y-0"
    ), "sectionBodyClass");
    const renderSectionBody = /* @__PURE__ */ __name((isCollapsed, children) => /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: sectionBodyClass(isCollapsed), "aria-hidden": isCollapsed ? "true" : "false", children: /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: "min-h-0 overflow-visible", children }) }), "renderSectionBody");
    react_global_default.useEffect(() => {
      const enteringPresent = viewMode === "present" && prevViewModeRef.current !== "present";
      const calcChanged = viewMode === "present" && calc?.id && calc?.id !== prevCalcIdRef.current;
      if (enteringPresent || calcChanged) {
        const nextMap = /* @__PURE__ */ new Map();
        (calc?.rows || []).forEach((row, index2) => {
          nextMap.set(index2, isParameterRow(row?.text || ""));
        });
        parameterLockMapRef.current = nextMap;
        const nextCollapsed = /* @__PURE__ */ new Set();
        sections.forEach((section) => {
          if (section.isCollapsedByDefault) {
            nextCollapsed.add(section.id);
          }
        });
        setCollapsedSections(nextCollapsed);
      }
      prevViewModeRef.current = viewMode;
      prevCalcIdRef.current = calc?.id || null;
    }, [viewMode, calc?.id, results, sections]);
    react_global_default.useEffect(() => {
      if (!hasTabs) {
        if (activeTabId !== null) setActiveTabId(null);
        return;
      }
      if (!tabbedSections.find((section) => section.id === activeTabId)) {
        setActiveTabId(firstTabId);
      }
    }, [activeTabId, firstTabId, hasTabs, tabbedSections]);
    const renderTabBar = /* @__PURE__ */ __name(() => {
      if (!hasTabs) return null;
      return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: "col-span-full", children: /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: "inline-flex flex-wrap items-center gap-1 rounded-full border border-soft bg-[var(--bg-secondary-mix-40)] p-1", children: tabbedSections.map((section) => {
        const isActive = section.id === activeTabId;
        return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(
          "button",
          {
            type: "button",
            onClick: () => setActiveTabId(section.id),
            className: cn(
              "rounded-full px-3 py-1 text-sm font-medium transition-colors",
              isActive ? "bg-[var(--accent-light)] text-theme-text" : "text-theme-text-muted hover:text-theme-text"
            ),
            children: /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)("span", { className: "inline-flex items-center gap-2", children: [
              section.tabIcon && /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(
                CalcIcon,
                {
                  name: section.tabIcon,
                  className: "w-3.5 h-3.5 flex-shrink-0",
                  style: { color: isActive ? "var(--text)" : "var(--text-dim)" }
                }
              ),
              /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("span", { className: "lowercase first-letter:uppercase", children: section.tabLabel || "Section" })
            ] })
          },
          section.id
        );
      }) }) });
    }, "renderTabBar");
    const toggleSection = /* @__PURE__ */ __name((sectionId) => {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        if (next.has(sectionId)) {
          next.delete(sectionId);
        } else {
          next.add(sectionId);
        }
        return next;
      });
    }, "toggleSection");
    const toggleSectionCollapse = /* @__PURE__ */ __name((sectionId) => {
      setCollapsedSections((prev) => {
        const next = new Set(prev);
        if (next.has(sectionId)) {
          next.delete(sectionId);
        } else {
          next.add(sectionId);
        }
        return next;
      });
    }, "toggleSectionCollapse");
    const renderRow = /* @__PURE__ */ __name(({ index: index2, result, vm, isInSplitLayout, isStacked, sectionMeta }) => {
      const rowIndex = vm?.index ?? index2;
      const rowResult = result ?? results[rowIndex];
      if (!calc?.rows?.[rowIndex] || !rowResult) return null;
      if (!parameterLockMapRef.current.has(rowIndex)) {
        parameterLockMapRef.current.set(rowIndex, isParameterRow(calc.rows[rowIndex]?.text || ""));
      }
      const rowKey = calc.rows[rowIndex]?.id || `${calc.id}-${rowIndex}`;
      return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(
        Row_default,
        {
          index: rowIndex,
          calc,
          rowRef: (el) => {
            rowRefs.current["row" + rowIndex] = el;
          },
          result: rowResult,
          viewMode,
          isSelected: selectedRow === rowIndex,
          onSelectRow,
          focusRow,
          onTextChange,
          deleteRow: () => deleteRow(rowIndex),
          insertRow: () => insertRow(rowIndex),
          insertRowAfter: () => insertRow(rowIndex + 1),
          rowClassName: stableRowClassName,
          rowStyle: stableRowStyle,
          classNames: stableRowClassNames,
          isPresentMode: true,
          isInSplitLayout,
          isStacked,
          paramRowLockMap: parameterLockMapRef.current,
          sectionMeta,
          isDense: sectionMeta?.isDense
        },
        rowKey
      );
    }, "renderRow");
    return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(import_jsx_runtime70.Fragment, { children: sections.map((section) => {
      const isExpanded = expandedSections.has(section.id);
      const hasAdvanced = section.advancedRows.length > 0;
      const isCollapsed = collapsedSections.has(section.id);
      const isCollapsible = section.rows.length > 0 || section.advancedRows.length > 0;
      const sectionMeta = isCollapsible ? {
        id: section.id,
        isCollapsed,
        onToggle: /* @__PURE__ */ __name(() => toggleSectionCollapse(section.id), "onToggle"),
        isDense: section.layout === "dense"
      } : null;
      const sectionIsSplit = section.isSplit || layoutMode === "split" || layoutMode === "split-wide";
      const sectionIsCol2 = section.isCol2 || layoutMode === "col2";
      let sectionContent = null;
      if (sectionIsSplit) {
        const isWideSplit = layoutMode === "split-wide";
        const leftRows = [];
        const rightRows = [];
        section.rows.forEach((vm) => {
          const isHero2 = vm.type === "hero";
          const result = results[vm.index];
          const hasDisplayValue = result?.output?.display != null && result.output.display !== "";
          const isParam = isParameterRow(calc.rows[vm.index]?.text || "");
          if (isHero2 || !isParam && hasDisplayValue) {
            rightRows.push(vm);
          } else {
            leftRows.push(vm);
          }
        });
        const splitGridClass = forceDesktop ? "grid grid-cols-12 items-start gap-[var(--split-gap,2rem)]" : "grid grid-cols-1 lg:grid-cols-12 items-start lg:gap-[var(--split-gap,2rem)]";
        const leftColClass = forceDesktop ? `${isWideSplit ? "col-span-7" : "col-span-6"} space-y-[var(--row-gap,1.5rem)]` : `lg:${isWideSplit ? "col-span-7" : "col-span-6"} space-y-[var(--row-gap,1.5rem)]`;
        const rightColClass = forceDesktop ? `${isWideSplit ? "col-span-5" : "col-span-6"} sticky bg-[var(--bg-secondary-mix-40)] rounded-[var(--calc-radius,1rem)] empty:hidden top-[var(--sticky-offset,1rem)] p-[var(--section-padding,1.5rem)] space-y-[var(--row-gap,1.5rem)]` : `lg:${isWideSplit ? "col-span-5" : "col-span-6"} lg:sticky bg-[var(--bg-secondary-mix-40)] rounded-[var(--calc-radius,1rem)] empty:hidden lg:top-[var(--sticky-offset,1rem)] p-[var(--section-padding,1.5rem)] space-y-[var(--row-gap,1.5rem)]`;
        const isRowWide = /* @__PURE__ */ __name((vm) => {
          const isWide = vm.wide;
          const result = results[vm.index];
          const val = result?.result;
          const rawVal = val && typeof val === "object" && val.v && Array.isArray(val.v) ? val.v : val;
          const isStructured = Array.isArray(rawVal) && rawVal.length > 0 && typeof rawVal[0] === "object";
          return isWide || isStructured;
        }, "isRowWide");
        const wideRows = section.rows.filter(isRowWide);
        const nonWideRows = section.rows.filter((vm) => !isRowWide(vm));
        sectionContent = /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)(import_jsx_runtime70.Fragment, { children: [
          section.sectionIndex !== null && renderRow({
            index: section.sectionIndex,
            result: results[section.sectionIndex],
            sectionMeta
          }),
          renderSectionBody(isCollapsed, /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)("div", { className: cn("col-span-full", splitGridClass), children: [
            /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)("div", { className: leftColClass, children: [
              leftRows.filter((vm) => !isRowWide(vm)).map((vm) => renderRow({ vm, isInSplitLayout: true, isStacked: true })),
              hasAdvanced && /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)(import_jsx_runtime70.Fragment, { children: [
                /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(
                  AdvancedToggle_default,
                  {
                    count: section.advancedRows.length,
                    isExpanded,
                    onToggle: () => toggleSection(section.id)
                  }
                ),
                isExpanded && section.advancedRows.map((vm) => renderRow({ vm, isInSplitLayout: true, isStacked: true }))
              ] })
            ] }),
            /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: rightColClass, children: rightRows.filter((vm) => !isRowWide(vm)).map((vm) => {
              const isHero2 = vm.type === "hero";
              const isChart2 = isChartRow(results[vm.index]);
              return renderRow({ vm, isInSplitLayout: true, isStacked: isHero2 || isChart2 });
            }) }),
            wideRows.map((vm) => /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: "col-span-full mt-8 border-t border-soft pt-8", children: renderRow({ vm, isInSplitLayout: false, isStacked: true }) }, vm.index))
          ] }))
        ] });
      }
      if (!sectionIsSplit) {
        const sectionTintClass = "";
        const groups = [];
        let currentGroup = { type: null, items: [] };
        section.rows.forEach((vm) => {
          const isParam = isParameterRow(calc.rows[vm.index]?.text || "");
          const type = isParam ? "param" : "calc";
          if (currentGroup.type !== null && currentGroup.type !== type) {
            groups.push(currentGroup);
            currentGroup = { type, items: [] };
          }
          if (currentGroup.type === null) {
            currentGroup.type = type;
          }
          currentGroup.items.push(vm);
        });
        if (currentGroup.items.length > 0) {
          groups.push(currentGroup);
        }
        sectionContent = /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)(import_jsx_runtime70.Fragment, { children: [
          section.sectionIndex !== null && renderRow({
            index: section.sectionIndex,
            result: results[section.sectionIndex],
            sectionMeta
          }),
          renderSectionBody(isCollapsed, /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)(import_jsx_runtime70.Fragment, { children: [
            /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: cn("col-span-full", sectionTintClass), children: groups.map((group, groupIndex) => {
              const isFirst = groupIndex === 0;
              const isParamGroup = group.type === "param";
              if (isParamGroup) {
                const count3 = group.items.length;
                const useGrid = count3 >= 2;
                const force2x2 = count3 === 4;
                const autoFitStyle = useGrid && !forceMobile && !force2x2 ? { gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, max(280px, calc(33.33% - 1rem))), 1fr))" } : void 0;
                const gridColsClass = force2x2 && !forceMobile ? forceDesktop ? "grid-cols-2" : "grid-cols-1 md:grid-cols-2" : !useGrid ? "grid-cols-1" : "";
                return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(
                  "div",
                  {
                    className: cn(
                      "grid gap-x-[var(--col-gap,1.5rem)] gap-y-[var(--row-gap,1.5rem)]",
                      !isFirst && "mt-[var(--row-gap,1.5rem)]",
                      gridColsClass
                    ),
                    style: autoFitStyle,
                    children: group.items.map((vm) => {
                      const rowKey = calc.rows[vm.index]?.id || `${calc.id}-${vm.index}`;
                      return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { children: renderRow({ vm, isStacked: useGrid }) }, rowKey);
                    })
                  },
                  `group-${groupIndex}`
                );
              }
              return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(
                "div",
                {
                  className: cn(
                    "space-y-[var(--row-gap,1.5rem)]",
                    !isFirst && "mt-[var(--row-gap,1.5rem)]"
                  ),
                  children: group.items.map((vm) => {
                    const result = results[vm.index];
                    const isChart2 = isChartRow(result);
                    const isCompact = isCompactWidth(result);
                    const wrapperClass = isChart2 && !isCompact ? "w-full max-w-none" : "w-full";
                    const rowKey = calc.rows[vm.index]?.id || `${calc.id}-${vm.index}`;
                    return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: wrapperClass, children: renderRow({ vm, isStacked: isChart2 }) }, rowKey);
                  })
                },
                `group-${groupIndex}`
              );
            }) }),
            hasAdvanced && /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)(import_jsx_runtime70.Fragment, { children: [
              /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(
                AdvancedToggle_default,
                {
                  count: section.advancedRows.length,
                  isExpanded,
                  onToggle: () => toggleSection(section.id)
                }
              ),
              isExpanded && /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(
                "div",
                {
                  className: cn(
                    "col-span-full grid",
                    gridGapClass,
                    sectionIsCol2 && !forceMobile ? forceDesktop ? "grid-cols-3" : "grid-cols-1 md:grid-cols-3" : "grid-cols-1",
                    sectionTintClass
                  ),
                  children: section.advancedRows.map((vm) => {
                    const result = results[vm.index];
                    const isHero2 = vm.type === "hero";
                    const isChart2 = isChartRow(result);
                    const isCompact = isCompactWidth(result);
                    const isWide = vm.wide || isChart2 && !isCompact;
                    const isParam = isParameterRow(calc.rows[vm.index]?.text || "");
                    const wrapperClass = sectionIsCol2 && !forceMobile && (isHero2 || isWide) ? forceDesktop ? "col-span-3" : "md:col-span-3" : "";
                    const rowKey = calc.rows[vm.index]?.id || `${calc.id}-${vm.index}`;
                    return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: wrapperClass, children: renderRow({ vm, isStacked: sectionIsCol2 && isParam || isChart2 }) }, rowKey);
                  })
                }
              )
            ] })
          ] }))
        ] });
      }
      const isDense = section.layout === "dense";
      let sectionSpanClass = "col-span-full";
      if (section.colSpan && section.colTotal) {
        const spanMultiplier = 12 / section.colTotal;
        const span = Math.round(section.colSpan * spanMultiplier);
        sectionSpanClass = `col-span-${span}`;
      }
      const sectionWrapperClass = cn(
        sectionSpanClass,
        section.isCard && "present-card",
        isDense && "dense-layout"
      );
      if (!hasTabs || !section.isTab) {
        return /* @__PURE__ */ (0, import_jsx_runtime70.jsx)("div", { className: sectionWrapperClass, children: sectionContent }, section.id);
      }
      const tabBar = section.id === firstTabId ? renderTabBar() : null;
      if (section.id !== activeTabId) {
        return tabBar ? /* @__PURE__ */ (0, import_jsx_runtime70.jsx)(react_global_default.Fragment, { children: tabBar }, `${section.id}-tabbar`) : null;
      }
      return /* @__PURE__ */ (0, import_jsx_runtime70.jsxs)("div", { className: sectionWrapperClass, children: [
        tabBar,
        sectionContent
      ] }, section.id);
    }) });
  }
  __name(PresentModeRows, "PresentModeRows");
  PresentModeRows.displayName = "PresentModeRows";
  var PresentModeRows_default = PresentModeRows;

  // public/src/react/components/meta/TextEditView.jsx
  init_react_global();
  var import_jsx_runtime71 = __toESM(require_jsx_runtime());
  var syncLoopGuard = createLoopGuard("TextEditView.sync", { maxCalls: 50, windowMs: 1e3 });
  var { useState: useState39, useEffect: useEffect39, useRef: useRef28, useMemo: useMemo16, useCallback: useCallback25, memo: memo5 } = react_global_default;
  var DEBUG_KEY = "icDebugTextView";
  var debugEnabled = typeof window !== "undefined" && window.localStorage?.getItem(DEBUG_KEY) === "1";
  var renderCount = 0;
  var lastRenderTime = 0;
  function debugLog(category, message, data = {}) {
    if (!debugEnabled) return;
    const now = performance.now();
    const delta = lastRenderTime ? (now - lastRenderTime).toFixed(1) : "0";
    console.log(
      `%c[TextEditView:${category}]%c ${message} %c+${delta}ms`,
      "color: #8b5cf6; font-weight: bold",
      "color: inherit",
      "color: #6b7280",
      data
    );
    lastRenderTime = now;
  }
  __name(debugLog, "debugLog");
  function debugTime(label) {
    if (!debugEnabled) return { end: /* @__PURE__ */ __name(() => {
    }, "end") };
    const start = performance.now();
    return {
      end: /* @__PURE__ */ __name((extra = {}) => {
        const elapsed = (performance.now() - start).toFixed(2);
        debugLog("perf", `${label} took ${elapsed}ms`, extra);
      }, "end")
    };
  }
  __name(debugTime, "debugTime");
  if (typeof window !== "undefined") {
    window.debugTextEditView = (enable = true) => {
      debugEnabled = enable;
      localStorage.setItem(DEBUG_KEY, enable ? "1" : "0");
      console.log(`[TextEditView] Debug ${enable ? "enabled" : "disabled"}. Refresh not required.`);
      if (enable) {
        console.log("[TextEditView] Debug categories: render, input, measure, effect, sync, resultRow");
      }
    };
  }
  var resultRowRenderCount = 0;
  var MemoizedResultRow = memo5(/* @__PURE__ */ __name(function MemoizedResultRow2({ result, line, height, lineHeight, resultAlignment, rowIndex }) {
    resultRowRenderCount++;
    if (debugEnabled) {
      debugLog("resultRow", `Row ${rowIndex} rendered (#${resultRowRenderCount})`, {
        hasResult: Boolean(result?.output?.display),
        lineLen: line?.length
      });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
      "div",
      {
        style: { height: `${height}px`, minHeight: `${lineHeight}px` },
        className: "flex items-center text-color-default",
        children: /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
          ResultContent_default,
          {
            result,
            sourceText: line,
            resultAlignment,
            showSourceForComments: true
          }
        )
      }
    );
  }, "MemoizedResultRow"));
  var LINE_HEIGHT_PX = 27;
  var MIN_VISIBLE_LINES = 5;
  var MAX_VISIBLE_LINES = 15;
  var PADDING_PX = 8;
  function TextEditView(props) {
    const { calc, results, onTextChange, showLineNumbers = true, className } = (
      /** @type {TextEditViewProps & {className?: string}} */
      props
    );
    const Action = useAction();
    renderCount++;
    debugLog("render", `Render #${renderCount}`, {
      calcId: calc?.id?.slice(0, 8),
      rowCount: calc?.rows?.length,
      resultsCount: results?.length
    });
    const getTextFromRows = /* @__PURE__ */ __name((targetCalc) => {
      if (!targetCalc?.rows) return "";
      return targetCalc.rows.map((row) => row.text || "").join("\n");
    }, "getTextFromRows");
    const [textValue, setTextValue] = useState39(getTextFromRows(calc));
    const textareaRef = useRef28(null);
    const resultsRef = useRef28(null);
    const scrollRafRef = useRef28(null);
    const isSyncingRef = useRef28(false);
    const calcRef = useRef28(calc);
    const onTextChangeRef = useRef28(onTextChange);
    const textValueRef = useRef28(textValue);
    const lastSyncedTextRef = useRef28(null);
    useEffect39(() => {
      calcRef.current = calc;
    }, [calc]);
    useEffect39(() => {
      onTextChangeRef.current = onTextChange;
    }, [onTextChange]);
    useEffect39(() => {
      textValueRef.current = textValue;
    }, [textValue]);
    const doSyncTextToRowsRef = useRef28(null);
    doSyncTextToRowsRef.current = (text) => {
      if (syncLoopGuard.check()) {
        console.error("[TextEditView] Sync aborted - too many rapid calls. Check for infinite loop.");
        return;
      }
      const currentCalc = calcRef.current;
      const calcId = currentCalc?.id;
      if (!calcId) return;
      const nextText = String(text ?? "");
      const currentText = getTextFromRows(currentCalc);
      if (nextText === currentText) {
        lastSyncedTextRef.current = nextText;
        isSyncingRef.current = false;
        return;
      }
      debugLog("sync:start", { calcId, lines: nextText.split("\n").length });
      isSyncingRef.current = true;
      lastSyncedTextRef.current = nextText;
      Action.syncTextBlock(calcId, nextText, { source: "text-edit" });
      onTextChangeRef.current?.();
    };
    const debouncedSync = useMemo16(
      () => debounce((text) => doSyncTextToRowsRef.current?.(text), 150),
      []
    );
    const updateActiveLine = useCallback25((target = textareaRef.current) => {
      if (!target) return;
      const selectionStart = target.selectionStart ?? 0;
      const lineIndex = Math.max(0, target.value.slice(0, selectionStart).split("\n").length - 1);
      if (activeLineIndexRef.current === lineIndex) return;
      activeLineIndexRef.current = lineIndex;
      const container = lineNumbersContainerRef.current;
      if (container) {
        const lineNumbers = container.querySelectorAll("[data-line-number]");
        lineNumbers.forEach((el, i) => {
          el.classList.toggle("opacity-100", i === lineIndex);
          el.classList.toggle("opacity-30", i !== lineIndex);
        });
      }
    }, []);
    const handleTextChange = /* @__PURE__ */ __name((event) => {
      const newText = event.target.value;
      const oldLineCount = textValueRef.current.split("\n").length;
      const newLineCount = newText.split("\n").length;
      debugLog("input", "Text changed", {
        oldLen: textValueRef.current.length,
        newLen: newText.length,
        lineCountChanged: oldLineCount !== newLineCount,
        oldLines: oldLineCount,
        newLines: newLineCount
      });
      setTextValue(newText);
      debouncedSync(newText);
      updateActiveLine(event.target);
    }, "handleTextChange");
    const handleScroll2 = useCallback25((event) => {
      const scrollTop = event.target.scrollTop;
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
      scrollRafRef.current = requestAnimationFrame(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollTop = scrollTop;
        }
      });
    }, []);
    useEffect39(() => {
      return () => {
        if (scrollRafRef.current) {
          cancelAnimationFrame(scrollRafRef.current);
        }
      };
    }, []);
    useEffect39(() => {
      const nextTextValue = getTextFromRows(calc);
      if (nextTextValue === textValueRef.current) {
        debugLog("sync", "Calc prop changed but text identical - skipping");
        return;
      }
      if (isSyncingRef.current && nextTextValue === lastSyncedTextRef.current) {
        debugLog("sync", "Own echo received - clearing sync flag", {
          localDiverged: textValueRef.current !== nextTextValue
        });
        isSyncingRef.current = false;
        if (textValueRef.current !== nextTextValue) {
          debugLog("sync", "Local text diverged - keeping local version");
          return;
        }
        textValueRef.current = nextTextValue;
        setTextValue(nextTextValue);
        return;
      }
      debugLog("sync", "EXTERNAL change detected - updating local state", {
        calcId: calc?.id?.slice(0, 8),
        oldLen: textValueRef.current.length,
        newLen: nextTextValue.length
      });
      textValueRef.current = nextTextValue;
      setTextValue(nextTextValue);
    }, [calc]);
    useEffect39(() => {
      return () => {
        debouncedSync.flush?.();
      };
    }, [debouncedSync]);
    const [lineHeights, setLineHeights] = useState39([]);
    const activeLineIndexRef = useRef28(0);
    const lineNumbersContainerRef = useRef28(null);
    const lastMeasureRef = useRef28({ width: 0, text: "", lineCount: 0 });
    const measureRafRef = useRef28(null);
    const lines = useMemo16(() => textValue.split("\n"), [textValue]);
    const calculateLineHeights = useCallback25(() => {
      const timer = debugTime("calculateLineHeights");
      if (!textareaRef.current || typeof document === "undefined") {
        debugLog("measure", "Skipped - no textarea");
        return;
      }
      const textarea = textareaRef.current;
      const textareaWidth = textarea.clientWidth;
      if (!textareaWidth) {
        debugLog("measure", "Skipped - no width");
        return;
      }
      const prevMeasure = lastMeasureRef.current;
      const lineCount = lines.length;
      const WRAP_THRESHOLD = Math.floor(textareaWidth / 8);
      const hasLongLine = lines.some((line) => line.length > WRAP_THRESHOLD);
      if (prevMeasure.width === textareaWidth && prevMeasure.lineCount === lineCount && !hasLongLine) {
        debugLog("measure", "SKIPPED - no changes needed", {
          lineCount,
          hasLongLine,
          wrapThreshold: WRAP_THRESHOLD
        });
        if (lineHeights.length !== lineCount) {
          setLineHeights(Array(lineCount).fill(LINE_HEIGHT_PX));
        }
        timer.end({ skipped: true });
        return;
      }
      debugLog("measure", "FULL MEASUREMENT starting", {
        lineCount,
        hasLongLine,
        wrapThreshold: WRAP_THRESHOLD,
        prevLineCount: prevMeasure.lineCount,
        widthChanged: prevMeasure.width !== textareaWidth
      });
      const nextHeights = [];
      let measureElement = null;
      try {
        measureElement = document.createElement("div");
        measureElement.style.cssText = `
                position: absolute;
                visibility: hidden;
                white-space: pre-wrap;
                word-wrap: break-word;
                top: -9999px;
                left: -9999px;
                padding: 0;
                margin: 0;
                border: 0;
            `;
        const computedStyle = getComputedStyle(textarea);
        measureElement.style.font = computedStyle.font;
        measureElement.style.lineHeight = computedStyle.lineHeight || `${LINE_HEIGHT_PX}px`;
        measureElement.style.fontSize = computedStyle.fontSize;
        measureElement.style.fontFamily = computedStyle.fontFamily;
        measureElement.style.width = `${Math.max(textareaWidth - 16, 100)}px`;
        document.body.appendChild(measureElement);
        lines.forEach((line) => {
          if (line.length > WRAP_THRESHOLD) {
            measureElement.textContent = line || " ";
            const height = Math.max(measureElement.offsetHeight, LINE_HEIGHT_PX);
            nextHeights.push(height);
          } else {
            nextHeights.push(LINE_HEIGHT_PX);
          }
        });
      } catch (error) {
        console.warn("[TextEditView] Error calculating line heights:", error);
        lines.forEach(() => nextHeights.push(LINE_HEIGHT_PX));
      } finally {
        if (measureElement?.parentNode) {
          measureElement.parentNode.removeChild(measureElement);
        }
      }
      lastMeasureRef.current = { width: textareaWidth, lineCount };
      const measuredCount = nextHeights.filter((h) => h !== LINE_HEIGHT_PX).length;
      debugLog("measure", "Measurement complete", {
        totalLines: lineCount,
        measuredLines: measuredCount,
        skippedLines: lineCount - measuredCount
      });
      setLineHeights((prev) => {
        if (prev.length !== nextHeights.length) {
          debugLog("measure", "Heights updated (length changed)", { oldLen: prev.length, newLen: nextHeights.length });
          return nextHeights;
        }
        for (let i = 0; i < prev.length; i += 1) {
          if (prev[i] !== nextHeights[i]) {
            debugLog("measure", "Heights updated (values changed)");
            return nextHeights;
          }
        }
        debugLog("measure", "Heights unchanged");
        return prev;
      });
      timer.end({ measured: measuredCount });
    }, [lines, lineHeights.length]);
    useEffect39(() => {
      if (textareaRef.current) {
        textareaRef.current.style.setProperty("line-height", `${LINE_HEIGHT_PX}px`, "important");
      }
    }, []);
    const debouncedCalculateHeights = useMemo16(
      () => debounce(() => {
        if (measureRafRef.current) cancelAnimationFrame(measureRafRef.current);
        measureRafRef.current = requestAnimationFrame(() => {
          calculateLineHeights();
          measureRafRef.current = null;
        });
      }, 100),
      [calculateLineHeights]
    );
    const prevLineCountRef = useRef28(lines.length);
    useEffect39(() => {
      const currentLineCount = lines.length;
      const lineCountChanged = prevLineCountRef.current !== currentLineCount;
      prevLineCountRef.current = currentLineCount;
      if (lineCountChanged) {
        debugLog("effect", "Line count changed - immediate measure", {
          oldCount: prevLineCountRef.current,
          newCount: currentLineCount
        });
        calculateLineHeights();
      } else {
        debugLog("effect", "Same line count - debounced measure", { lineCount: currentLineCount });
        debouncedCalculateHeights();
      }
      return () => {
        debouncedCalculateHeights.cancel?.();
        if (measureRafRef.current) cancelAnimationFrame(measureRafRef.current);
      };
    }, [lines, debouncedCalculateHeights, calculateLineHeights]);
    useEffect39(() => {
      if (!textareaRef.current || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(() => {
        calculateLineHeights();
      });
      observer.observe(textareaRef.current);
      return () => observer.disconnect();
    }, [calculateLineHeights]);
    useEffect39(() => {
      updateActiveLine(textareaRef.current);
    }, [textValue, updateActiveLine]);
    useEffect39(() => {
      const handleSelectionChange = /* @__PURE__ */ __name(() => {
        if (document.activeElement !== textareaRef.current) return;
        updateActiveLine(textareaRef.current);
      }, "handleSelectionChange");
      document.addEventListener("selectionchange", handleSelectionChange);
      return () => document.removeEventListener("selectionchange", handleSelectionChange);
    }, [updateActiveLine]);
    const displayLineCount = Math.max(lines.length, MIN_VISIBLE_LINES);
    const maxContainerHeight = MAX_VISIBLE_LINES * LINE_HEIGHT_PX + PADDING_PX * 2;
    const measuredHeight = lineHeights.length ? lineHeights.reduce((sum, height) => sum + height, 0) : displayLineCount * LINE_HEIGHT_PX;
    const contentHeight = Math.max(displayLineCount * LINE_HEIGHT_PX, measuredHeight) + PADDING_PX * 2;
    const needsScrolling = contentHeight > maxContainerHeight;
    const containerHeight = needsScrolling ? maxContainerHeight : contentHeight;
    const resultAlignment = (
      /** @type {'left' | 'right'} */
      "left"
    );
    return /* @__PURE__ */ (0, import_jsx_runtime71.jsx)("div", { className: cn("text-edit-view text-sm lg:text-base", className), "data-testid": "calc-text-view", children: /* @__PURE__ */ (0, import_jsx_runtime71.jsxs)("div", { className: cn(
      "grid w-full gap-0 sm:gap-4",
      showLineNumbers ? "grid-cols-[minmax(20px,22px)_minmax(0,1fr)_minmax(0,1fr)]" : "grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
    ), children: [
      showLineNumbers && /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
        "div",
        {
          ref: lineNumbersContainerRef,
          className: "select-none text-right",
          style: {
            lineHeight: `${LINE_HEIGHT_PX}px`,
            paddingTop: `${PADDING_PX}px`,
            paddingBottom: `${PADDING_PX}px`,
            height: `${containerHeight}px`,
            overflow: "hidden"
          },
          children: Array.from({ length: displayLineCount }, (_, i) => {
            const height = lineHeights[i] || LINE_HEIGHT_PX;
            const isInitialActive = i === 0;
            return /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
              "div",
              {
                style: { height: `${height}px`, minHeight: `${LINE_HEIGHT_PX}px` },
                className: "flex items-center justify-end",
                children: /* @__PURE__ */ (0, import_jsx_runtime71.jsxs)(
                  "number",
                  {
                    "data-line-number": i,
                    className: cn(
                      "select-none align-middle pr-0 md:pr-2 text-[68.75%] tabular-nums transition-opacity cursor-default",
                      "text-theme-calc-row-number",
                      isInitialActive ? "opacity-100" : "opacity-30"
                    ),
                    children: [
                      "R",
                      i + 1
                    ]
                  }
                )
              },
              `ln-${i}`
            );
          })
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
        "div",
        {
          className: "relative flex w-full min-w-0 rounded-xl bg-theme-calc-row-input-bg group",
          style: { height: `${containerHeight}px` },
          onMouseDown: () => textareaRef.current?.focus(),
          children: /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
            "textarea",
            {
              ref: textareaRef,
              className: cn(
                "flex-1 p-0 md:p-2 bg-transparent outline-none resize-none no-scrollbar text-color-default"
              ),
              "data-testid": "calc-text-input",
              value: textValue,
              onChange: handleTextChange,
              onScroll: handleScroll2,
              onClick: (event) => updateActiveLine(event.target),
              onFocus: (event) => updateActiveLine(event.target),
              onKeyUp: (event) => updateActiveLine(event.target),
              onSelect: (event) => updateActiveLine(event.target),
              onMouseDown: () => requestAnimationFrame(() => updateActiveLine(textareaRef.current)),
              onKeyDown: (event) => event.stopPropagation(),
              placeholder: "",
              autoComplete: "off",
              autoCorrect: "off",
              autoCapitalize: "off",
              spellCheck: false,
              style: {
                lineHeight: `${LINE_HEIGHT_PX}px`,
                height: `${containerHeight}px`,
                overflowY: needsScrolling ? "auto" : "hidden",
                color: "var(--text)",
                caretColor: "var(--text)"
              }
            }
          )
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
        "div",
        {
          ref: resultsRef,
          className: "w-full min-w-0 pointer-events-none",
          "data-testid": "calc-text-results",
          style: {
            height: `${containerHeight}px`,
            overflowY: needsScrolling ? "auto" : "hidden"
          },
          children: /* @__PURE__ */ (0, import_jsx_runtime71.jsxs)("div", { className: "p-0 md:p-2", children: [
            lines.map((line, i) => /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
              MemoizedResultRow,
              {
                rowIndex: i,
                result: results[i],
                line,
                height: lineHeights[i] || LINE_HEIGHT_PX,
                lineHeight: LINE_HEIGHT_PX,
                resultAlignment
              },
              `res-${i}`
            )),
            lines.length < MIN_VISIBLE_LINES && Array.from({ length: MIN_VISIBLE_LINES - lines.length }, (_, i) => /* @__PURE__ */ (0, import_jsx_runtime71.jsx)(
              "div",
              {
                style: { height: `${LINE_HEIGHT_PX}px`, minHeight: `${LINE_HEIGHT_PX}px` },
                className: "text-color-dim",
                children: "\xA0"
              },
              `placeholder-${i}`
            ))
          ] })
        }
      )
    ] }) });
  }
  __name(TextEditView, "TextEditView");
  var TextEditView_default = TextEditView;

  // public/src/react/components/meta/ShareMenu.jsx
  init_react_global();

  // public/src/react/hooks/useShareMenu.js
  init_react_global();

  // public/src/utils/collabLinks.js
  var getTokenGenerator = /* @__PURE__ */ __name(() => {
    if (typeof window !== "undefined" && window.TokenGenerator) {
      return window.TokenGenerator;
    }
    try {
      return require_tokenGenerator();
    } catch (e) {
      return null;
    }
  }, "getTokenGenerator");
  var getFirebasePaths = /* @__PURE__ */ __name(() => {
    if (typeof window !== "undefined" && window.FirebasePaths) {
      return window.FirebasePaths;
    }
    try {
      return require_FirebasePaths();
    } catch (e) {
      return null;
    }
  }, "getFirebasePaths");
  var getCalcType = /* @__PURE__ */ __name(() => {
    if (typeof window !== "undefined" && window.CalcType) {
      return window.CalcType;
    }
    try {
      return require_CalcType();
    } catch (e) {
      throw new Error("CalcType not loaded");
    }
  }, "getCalcType");
  var PRIVATE_URL_PREFIX = "/edit/";
  var DEFAULT_TOKEN_LENGTH = 12;
  function getCollabURL(token, origin = null) {
    if (!token) return "";
    const base = origin || (typeof window !== "undefined" ? window.location.origin : "https://instacalc.com");
    return `${base}${PRIVATE_URL_PREFIX}${token}`;
  }
  __name(getCollabURL, "getCollabURL");
  function parseCollabToken(url) {
    if (!url) return null;
    const match = url.match(/\/edit\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  }
  __name(parseCollabToken, "parseCollabToken");
  function isCollabURL(url) {
    return parseCollabToken(url) !== null;
  }
  __name(isCollabURL, "isCollabURL");
  function generateCollabToken(length = DEFAULT_TOKEN_LENGTH) {
    const TokenGenerator = getTokenGenerator();
    if (TokenGenerator?.generateCollabToken) {
      return TokenGenerator.generateCollabToken(length);
    }
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
    const cryptoObj = typeof globalThis !== "undefined" && globalThis.crypto ? globalThis.crypto : typeof window !== "undefined" ? window.crypto : null;
    if (!cryptoObj?.getRandomValues) {
      let result = "";
      for (let i = 0; i < length; i++) {
        result += chars[Math.floor(Math.random() * chars.length)];
      }
      return result;
    }
    const array = new Uint8Array(length);
    cryptoObj.getRandomValues(array);
    return Array.from(array, (byte) => chars[byte % chars.length]).join("");
  }
  __name(generateCollabToken, "generateCollabToken");
  function getFirebasePath(token) {
    if (!token) throw new Error("Collab token is required");
    const FirebasePaths = getFirebasePaths();
    return FirebasePaths.getCollabPath(token);
  }
  __name(getFirebasePath, "getFirebasePath");
  function getFirebaseURL(token) {
    if (!token) throw new Error("Collab token is required");
    const FirebasePaths = getFirebasePaths();
    return FirebasePaths.getCollabUrl(token);
  }
  __name(getFirebaseURL, "getFirebaseURL");
  function isValidToken(token) {
    if (!token || typeof token !== "string") return false;
    return /^[A-Za-z0-9_-]{6,64}$/.test(token);
  }
  __name(isValidToken, "isValidToken");
  function getPrivateURLForCalc(calc, origin = null) {
    const CalcType8 = getCalcType();
    const isCollab3 = CalcType8.isCollab(calc);
    const token = calc?.token || (isCollab3 ? calc?.remoteId : null);
    if (!token) return "";
    return getCollabURL(token, origin);
  }
  __name(getPrivateURLForCalc, "getPrivateURLForCalc");
  function hasPrivateToken(calc) {
    const CalcType8 = getCalcType();
    const isCollab3 = CalcType8.isCollab(calc);
    const token = calc?.token || (isCollab3 ? calc?.remoteId : null);
    return !!(token && isValidToken(token));
  }
  __name(hasPrivateToken, "hasPrivateToken");
  var LIVE_URL_PREFIX = "/";
  var ADJECTIVES = [
    "happy",
    "swift",
    "bright",
    "calm",
    "bold",
    "clever",
    "eager",
    "gentle",
    "jolly",
    "keen",
    "lively",
    "merry",
    "noble",
    "proud",
    "quick",
    "sunny",
    "warm",
    "witty",
    "zesty",
    "cozy",
    "fresh",
    "golden",
    "silver",
    "cosmic"
  ];
  var NOUNS = [
    "panda",
    "falcon",
    "comet",
    "river",
    "peak",
    "ocean",
    "forest",
    "meadow",
    "thunder",
    "breeze",
    "crystal",
    "phoenix",
    "dragon",
    "tiger",
    "eagle",
    "wolf",
    "star",
    "moon",
    "cloud",
    "wave",
    "flame",
    "spark",
    "frost",
    "bloom"
  ];
  function generateFunSlug() {
    const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
    const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
    const num = Math.floor(Math.random() * 100);
    return `${adj}-${noun}-${num}`;
  }
  __name(generateFunSlug, "generateFunSlug");
  function getLiveURL(slug, origin = null) {
    if (!slug) return "";
    const base = origin || (typeof window !== "undefined" ? window.location.origin : "https://instacalc.com");
    return `${base}/${slug}`;
  }
  __name(getLiveURL, "getLiveURL");
  function parseLiveSlug(url) {
    if (!url) return null;
    let match = url.match(/\/([a-z][a-z0-9-]+)$/i);
    if (match) return match[1].toLowerCase();
    match = url.match(/\/!([a-z0-9-]+)/i);
    return match ? match[1].toLowerCase() : null;
  }
  __name(parseLiveSlug, "parseLiveSlug");
  function isLiveURL(url) {
    return parseLiveSlug(url) !== null;
  }
  __name(isLiveURL, "isLiveURL");
  function isValidSlug(slug) {
    if (!slug || typeof slug !== "string") return false;
    return /^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$/.test(slug);
  }
  __name(isValidSlug, "isValidSlug");
  function getLiveFirebasePath(slug) {
    if (!slug) throw new Error("Live wiki slug is required");
    const FirebasePaths = getFirebasePaths();
    return FirebasePaths.getLivePath(slug);
  }
  __name(getLiveFirebasePath, "getLiveFirebasePath");
  function getLiveFirebaseURL(slug) {
    if (!slug) throw new Error("Live wiki slug is required");
    const FirebasePaths = getFirebasePaths();
    return FirebasePaths.getLiveUrl(slug);
  }
  __name(getLiveFirebaseURL, "getLiveFirebaseURL");
  function getLiveURLForCalc(calc, origin = null) {
    const CalcType8 = getCalcType();
    const isLive = CalcType8.isLive(calc?.type);
    if (!isLive || !calc?.remoteId) return "";
    return getLiveURL(calc.remoteId, origin);
  }
  __name(getLiveURLForCalc, "getLiveURLForCalc");
  function isLiveCalc(calc) {
    const CalcType8 = getCalcType();
    const isLive = CalcType8.isLive(calc?.type);
    return isLive && !!(calc?.remoteId && isValidSlug(calc.remoteId));
  }
  __name(isLiveCalc, "isLiveCalc");
  var collabLinks_default = {
    PRIVATE_URL_PREFIX,
    DEFAULT_TOKEN_LENGTH,
    getCollabURL,
    parseCollabToken,
    isCollabURL,
    generateCollabToken,
    getFirebasePath,
    getFirebaseURL,
    isValidToken,
    getPrivateURLForCalc,
    hasPrivateToken,
    // Live wiki exports
    LIVE_URL_PREFIX,
    generateFunSlug,
    getLiveURL,
    parseLiveSlug,
    isLiveURL,
    isValidSlug,
    getLiveFirebasePath,
    getLiveFirebaseURL,
    getLiveURLForCalc,
    isLiveCalc
  };
  if (typeof window !== "undefined") {
    window.CollabLinks = {
      PRIVATE_URL_PREFIX,
      DEFAULT_TOKEN_LENGTH,
      getCollabURL,
      parseCollabToken,
      isCollabURL,
      generateCollabToken,
      getFirebasePath,
      getFirebaseURL,
      isValidToken,
      getPrivateURLForCalc,
      hasPrivateToken,
      // Live wiki exports
      LIVE_URL_PREFIX,
      generateFunSlug,
      getLiveURL,
      parseLiveSlug,
      isLiveURL,
      isValidSlug,
      getLiveFirebasePath,
      getLiveFirebaseURL,
      getLiveURLForCalc,
      isLiveCalc
    };
  }

  // public/src/react/hooks/useShareMenu.js
  var { useState: useState40, useCallback: useCallback26, useMemo: useMemo17, useRef: useRef29, useEffect: useEffect40 } = react_global_default;
  var extractAssignmentValue = /* @__PURE__ */ __name((text) => {
    const rawText = text || "";
    const eq = rawText.indexOf("=");
    const colon = rawText.indexOf(":");
    const idx = eq >= 0 ? eq : colon;
    if (idx < 0) return rawText.trim();
    const afterEq = rawText.substring(idx + 1);
    return (window.IC?.parser?.splitAtComment(afterEq)?.before ?? afterEq).trim();
  }, "extractAssignmentValue");
  function useShareMenu({ Action, calc, viewMode = "editor", strategy }) {
    const [copied, setCopied] = useState40(false);
    const [publishing, setPublishing] = useState40(false);
    const [shareWithValues, setShareWithValues] = useState40(false);
    const [shareMode, setShareMode] = useState40("public");
    const publishIntervalRef = useRef29(null);
    useEffect40(() => {
      return () => {
        if (publishIntervalRef.current) {
          clearInterval(publishIntervalRef.current);
          publishIntervalRef.current = null;
        }
      };
    }, []);
    const isPublished = useMemo17(() => {
      return !!calc?.published?.id;
    }, [calc]);
    const isAlreadySaved = useMemo17(() => {
      if (typeof window === "undefined" || !window.calcStore?.isAlreadySaved) return false;
      return window.calcStore.isAlreadySaved(calc);
    }, [calc, window.calcStore]);
    const calcType = useMemo17(() => {
      if (!calc) return "local";
      if (calc.type) return calc.type;
      if (calc.published) return "shared";
      return "local";
    }, [calc]);
    const canUnpublish = strategy ? strategy.allowTitleEdit : viewMode !== "public" && viewMode !== "embed";
    const getPublicURL = useCallback26((includeValues = false) => {
      const base = Action?.getPublicURL?.(calc) || "";
      if (!base || !includeValues) return base;
      const params = new URLSearchParams();
      if (calc?.rows?.length) {
        const usedNames = /* @__PURE__ */ new Set();
        let position = 0;
        calc.rows.forEach((row) => {
          const name = getParameterLabel(row?.text || "");
          if (!name) return;
          position += 1;
          const lowerName = name.toLowerCase();
          let key = name;
          if (usedNames.has(lowerName)) {
            key = `r${position}`;
          } else {
            usedNames.add(lowerName);
          }
          params.set(key, extractAssignmentValue(row?.text || ""));
        });
      }
      try {
        const urlObj = new URL(base);
        params.forEach((v, k) => urlObj.searchParams.set(k, v));
        return urlObj.toString();
      } catch (e) {
        return base;
      }
    }, [Action, calc]);
    const getPrivateURL = useCallback26(() => {
      return collabLinks_default.getPrivateURLForCalc(calc);
    }, [calc]);
    const getWikiURL = useCallback26(() => {
      const slug = calc?.slug || "my-calc";
      return `${window.location.origin}/wiki/${slug}`;
    }, [calc]);
    const getCurrentURL = useCallback26(() => {
      switch (shareMode) {
        case "private":
          return getPrivateURL();
        case "wiki":
          return getWikiURL();
        case "public":
        default:
          return getPublicURL(shareWithValues);
      }
    }, [shareMode, getPrivateURL, getWikiURL, getPublicURL, shareWithValues]);
    const currentURL = useMemo17(() => getCurrentURL(), [getCurrentURL]);
    const publicURL = useMemo17(() => getPublicURL(shareWithValues), [getPublicURL, shareWithValues]);
    const getShortPublicURL = useCallback26(() => {
      const url = getPublicURL();
      if (!url) return "instacalc.com/...";
      return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
    }, [getPublicURL]);
    const getShortPrivateURL = useCallback26(() => {
      const url = getPrivateURL();
      if (!url) return "instacalc.com/edit/...";
      return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
    }, [getPrivateURL]);
    const publish = useCallback26(() => {
      if (!Action?.publish || !calc) return;
      setPublishing(true);
      try {
        Action.publish(calc);
      } finally {
        setTimeout(() => setPublishing(false), 600);
      }
    }, [Action, calc]);
    const publishPrivate = useCallback26(async () => {
      if (!calc?.id) return null;
      if (calc.token) return calc.token;
      setPublishing(true);
      try {
        if (window.Calc?.publishAsCollab) {
          const token = await window.Calc.publishAsCollab(calc.id);
          return token;
        }
        if (Action?.publishAs) {
          Action.publishAs("private");
          await new Promise((resolve) => setTimeout(resolve, 500));
          const updated = Action?.getCurrentCalc?.();
          return updated?.token || updated?.published?.token;
        }
        return null;
      } finally {
        setPublishing(false);
      }
    }, [Action, calc]);
    const publishAs = useCallback26((mode) => {
      if (!Action || !calc) return;
      setPublishing(true);
      try {
        if (mode === "private") {
          publishPrivate();
          return;
        }
        if (Action.publishAs) {
          Action.publishAs(mode);
        } else if (mode === "public" && Action.publish) {
          Action.publish(calc);
        }
      } finally {
        setTimeout(() => setPublishing(false), 600);
      }
    }, [Action, calc]);
    const copyLink = useCallback26((overrideUrl) => {
      const url = typeof overrideUrl === "string" ? overrideUrl : getCurrentURL();
      if (!url) return;
      try {
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(url);
        } else {
          const ta = document.createElement("textarea");
          ta.value = url;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          ta.remove();
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (e) {
        console.warn("Copy failed:", e);
      }
    }, [getCurrentURL]);
    const ensurePublishedAndCopy = useCallback26(async () => {
      if (!Action) return null;
      const existing = getPublicURL(shareWithValues);
      if (existing) {
        copyLink(existing);
        return existing;
      }
      setPublishing(true);
      if (Action.publish) Action.publish(calc);
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 20;
        publishIntervalRef.current = setInterval(() => {
          attempts++;
          const current = Action.getCurrentCalc?.() || calc;
          const url = Action.getPublicURL?.(current);
          if (url || attempts >= maxAttempts) {
            clearInterval(publishIntervalRef.current);
            publishIntervalRef.current = null;
            setPublishing(false);
            if (url) {
              const finalUrl = getCurrentURL();
              copyLink(finalUrl);
              resolve(finalUrl);
            } else {
              resolve(null);
            }
          }
        }, 200);
      });
    }, [Action, calc, copyLink, getPublicURL, getCurrentURL, shareWithValues]);
    const ensurePrivateAndCopy = useCallback26(async () => {
      if (collabLinks_default.hasPrivateToken(calc)) {
        const url = collabLinks_default.getPrivateURLForCalc(calc);
        copyLink(url);
        return;
      }
      setPublishing(true);
      try {
        const token = await publishPrivate();
        if (token) {
          const url = collabLinks_default.getCollabURL(token);
          copyLink(url);
        }
      } finally {
        setPublishing(false);
      }
    }, [calc, copyLink, publishPrivate]);
    const copyText = useCallback26(() => {
      if (!calc?.rows) return;
      const text = calc.rows.map((r) => r.text).join("\n");
      copyLink(text);
    }, [calc, copyLink]);
    const getEmbedCode = useCallback26(() => {
      const id = calc?.published?.id;
      if (!id) return "";
      return `<iframe src="https://instacalc.com/embed/${id}" width="400" height="300" frameborder="0"></iframe>`;
    }, [calc]);
    const openEmbedModal = useCallback26(() => {
      document.dispatchEvent(new CustomEvent("instacalc:updateEmbedCalc", {
        detail: { calcId: calc?.id }
      }));
    }, [calc?.id]);
    const openTextModal = useCallback26(() => {
      document.dispatchEvent(new CustomEvent("instacalc:updateTextView", {
        detail: { calcId: calc?.id }
      }));
    }, [calc?.id]);
    const getDataLink = useCallback26(async () => {
      const UrlManager = window.UrlManager;
      if (UrlManager?.toDataUrl) {
        const baseUrl = window.location.origin;
        return await UrlManager.toDataUrl(calc, { baseUrl });
      }
      return null;
    }, [calc]);
    const copyDataLink = useCallback26(async () => {
      const url = await getDataLink();
      if (url) {
        setShareMode("data");
        copyLink(url);
      }
    }, [getDataLink, copyLink]);
    return {
      // State
      isPublished,
      calcType,
      publicURL,
      currentURL,
      // The active URL based on mode
      copied,
      publishing,
      shareWithValues,
      shareMode,
      canUnpublish,
      // Setters
      setShareWithValues,
      setShareMode,
      // Actions
      publish,
      publishAs,
      publishPrivate,
      copyLink,
      copyText,
      ensurePublishedAndCopy,
      ensurePrivateAndCopy,
      getPublicURL,
      getPrivateURL,
      getShortPublicURL,
      getShortPrivateURL,
      getWikiURL,
      getEmbedCode,
      openEmbedModal,
      openTextModal,
      getDataLink,
      copyDataLink,
      // Helpers
      isCopied: copied,
      isPublishing: publishing
    };
  }
  __name(useShareMenu, "useShareMenu");

  // public/src/react/components/meta/ShareMenu.jsx
  init_Icon();
  init_featureFlags();
  var import_jsx_runtime72 = __toESM(require_jsx_runtime());
  var { useState: useState41, useCallback: useCallback27, useMemo: useMemo18 } = react_global_default;
  function ShareMenu(props) {
    const { variant, forceLabel, forceOpen, triggerOnHover = false, sideOffset = 4, calc: calcProp, viewMode: viewModeProp, className, isOwner = true } = (
      /** @type {ShareMenuProps} */
      props
    );
    const Action = useAction();
    const calcContext = useCalc();
    const calc = calcProp || calcContext.calc;
    const viewMode = viewModeProp || calcContext.viewMode;
    const [menuOpen, setMenuOpen] = useState41(false);
    const {
      currentURL,
      copied,
      publishing,
      shareMode,
      setShareMode,
      copyLink,
      copyText,
      ensurePublishedAndCopy,
      ensurePrivateAndCopy,
      openEmbedModal,
      openTextModal,
      copyDataLink,
      isPublished,
      getPublicURL,
      getPrivateURL,
      shareWithValues,
      isAlreadySaved
    } = useShareMenu({ Action, calc, viewMode });
    const normalizedVariant = (variant || "").toLowerCase();
    const isIconVariant = normalizedVariant === "icon";
    const showPrivateLink = isFeatureEnabled("privateShareLink", true);
    const showAsPublished = isPublished && !forceLabel;
    const shareIcon = Icon_default.shareButton("w-4 h-4 flex-shrink-0");
    const iconTrigger = /* @__PURE__ */ (0, import_jsx_runtime72.jsx)(
      "button",
      {
        className: cn(
          "p-2 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-alt transition-colors",
          className
        ),
        "aria-label": "Share options",
        "data-id": "share-menu-button",
        "data-ic-share-state": showAsPublished ? "published" : "unpublished",
        "data-ic-share-id": showAsPublished ? calc?.published?.id : "",
        children: publishing ? /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin inline-block" }) : Icon_default.shareButton("w-5 h-5")
      }
    );
    const fullTrigger = /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(
      "button",
      {
        className: cn(
          "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition max-w-[240px]",
          showAsPublished ? "bg-theme-bg-alt border border-soft text-theme-text-muted hover:text-theme-text" : "hover:brightness-110",
          className
        ),
        style: !showAsPublished ? {
          background: "color-mix(in srgb, var(--accent) 12%, transparent)",
          color: "var(--accent)"
        } : {},
        "aria-label": "Share options",
        "data-id": "share-menu-button",
        "data-ic-share-state": showAsPublished ? "published" : "unpublished",
        "data-ic-share-id": showAsPublished ? calc?.published?.id : "",
        children: [
          shareIcon,
          forceLabel ? /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "whitespace-nowrap", children: forceLabel }) : isPublished ? /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(import_jsx_runtime72.Fragment, { children: [
            /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)("span", { className: "truncate", children: [
              /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "font-normal opacity-50", children: "instacalc.com/" }),
              /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { children: calc.published.id })
            ] }),
            Icon_default.chevronDown("w-3 h-3 opacity-50 flex-shrink-0")
          ] }) : /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "whitespace-nowrap", children: "Share" })
        ]
      }
    );
    const trigger = isIconVariant ? iconTrigger : fullTrigger;
    const publicURL = getPublicURL(shareWithValues) || "";
    const shortPublicURL = publicURL.replace("https://", "");
    const privateURL = getPrivateURL() || "";
    const shortPrivateURL = privateURL.replace("https://", "");
    return /* @__PURE__ */ (0, import_jsx_runtime72.jsx)(
      Dropdown,
      {
        trigger,
        align: "end",
        open: menuOpen,
        onOpenChange: setMenuOpen,
        forceOpen,
        triggerOnHover,
        sideOffset,
        classNames: {
          content: "w-80 p-0 !overflow-visible"
        },
        children: /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)("div", { className: "flex flex-col !overflow-visible", children: [
          /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(
            "button",
            {
              className: "w-full flex items-center gap-3 px-4 py-3 pt-4 hover:bg-theme-bg-alt transition-colors text-left",
              onClick: () => {
                setShareMode("public");
                ensurePublishedAndCopy();
              },
              disabled: publishing,
              "data-id": "share-copy-public-btn",
              children: [
                publishing && shareMode === "public" ? /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "w-4 h-4 border-2 border-[color-mix(in_srgb,var(--accent)_30%,transparent)] border-t-[var(--accent)] rounded-full animate-spin" }) : copied && shareMode === "public" ? Icon_default.success("w-4 h-4 text-theme-accent") : Icon_default.publicLink("w-4 h-4 text-theme-text-muted"),
                /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)("div", { className: "flex-1 min-w-0", children: [
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("div", { className: "text-sm font-medium text-theme-text", children: copied && shareMode === "public" ? "Link copied!" : "Copy public link" }),
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("div", { className: "text-xs text-theme-text-muted truncate", children: shortPublicURL || "instacalc.com/..." })
                ] })
              ]
            }
          ),
          isOwner && showPrivateLink && /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(
            "button",
            {
              className: "w-full flex items-center gap-3 px-4 py-3 hover:bg-theme-bg-alt transition-colors text-left",
              onClick: () => {
                setShareMode("private");
                ensurePrivateAndCopy();
              },
              disabled: publishing,
              "data-id": "share-copy-private-btn",
              children: [
                publishing && shareMode === "private" ? /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "w-4 h-4 border-2 border-[color-mix(in_srgb,var(--text-dim)_30%,transparent)] border-t-[var(--text-dim)] rounded-full animate-spin" }) : copied && shareMode === "private" ? Icon_default.success("w-4 h-4 text-theme-accent") : Icon_default.lockOpen("w-4 h-4 text-theme-text-muted"),
                /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)("div", { className: "flex-1 min-w-0", children: [
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("div", { className: "text-sm font-medium text-theme-text", children: copied && shareMode === "private" ? "Link copied!" : "Copy private link" }),
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("div", { className: "text-xs text-theme-text-muted truncate", children: shortPrivateURL || "instacalc.com/edit/..." })
                ] })
              ]
            }
          ),
          viewMode === "public" && !isAlreadySaved && /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(
            "button",
            {
              className: "w-full flex items-center gap-3 px-4 py-3 hover:bg-theme-bg-alt transition-colors text-left border-t border-soft",
              onClick: () => {
                setMenuOpen(false);
                if (window.Action?.cloneAndOpen) window.Action.cloneAndOpen(calc, { suffix: "" });
              },
              "data-id": "share-save-copy-btn",
              children: [
                Icon_default.heart("w-4 h-4 text-theme-text-muted"),
                /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)("div", { className: "flex-1 min-w-0", children: [
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("div", { className: "text-sm font-medium text-theme-text", children: "Save a copy" }),
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("div", { className: "text-xs text-theme-text-muted", children: "Add to My Calcs" })
                ] })
              ]
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("div", { className: "h-px bg-[var(--border-color)]" }),
          /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)("div", { className: "flex items-center justify-around px-2 py-2 pb-3 !overflow-visible", children: [
            /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(
              "button",
              {
                onClick: () => {
                  setMenuOpen(false);
                  openTextModal();
                },
                className: "flex items-center gap-2 px-3 py-2 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-alt transition-all tooltip tooltip-bottom",
                "data-tip": "Copy as plain text",
                children: [
                  Icon_default.documentText("w-4 h-4"),
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "text-xs font-medium", children: "Text" })
                ]
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(
              "button",
              {
                onClick: () => {
                  setMenuOpen(false);
                  openEmbedModal();
                },
                className: "flex items-center gap-2 px-3 py-2 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-alt transition-all tooltip tooltip-bottom",
                "data-tip": "HTML embed code",
                children: [
                  Icon_default.code("w-4 h-4"),
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "text-xs font-medium", children: "Embed" })
                ]
              }
            ),
            /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(
              "button",
              {
                onClick: () => {
                  copyDataLink();
                },
                className: "flex items-center gap-2 px-3 py-2 rounded-lg text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-alt transition-all tooltip tooltip-bottom",
                "data-tip": "URL with calc data encoded",
                children: [
                  copied && shareMode === "data" ? Icon_default.success("w-4 h-4 text-theme-accent") : Icon_default.link("w-4 h-4"),
                  /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "text-xs font-medium", children: copied && shareMode === "data" ? "Copied!" : "Data" })
                ]
              }
            )
          ] })
        ] })
      }
    );
  }
  __name(ShareMenu, "ShareMenu");
  function MobileShareButton({ calc: calcProp, viewMode: viewModeProp, className }) {
    const Action = useAction();
    const calcContext = useCalc();
    const calc = calcProp || calcContext.calc;
    const viewMode = viewModeProp || calcContext.viewMode;
    const {
      copied,
      publishing,
      ensurePublishedAndCopy
    } = useShareMenu({ Action, calc, viewMode });
    const handleShare = /* @__PURE__ */ __name(async () => {
      const url = await ensurePublishedAndCopy();
      if (navigator.share && url) {
        try {
          await navigator.share({
            title: calc?.name || "Calculation",
            url
          });
        } catch (e) {
        }
      }
    }, "handleShare");
    return /* @__PURE__ */ (0, import_jsx_runtime72.jsx)(
      "button",
      {
        type: "button",
        className: cn(
          "inline-flex items-center justify-center p-2 rounded-lg",
          "text-theme-text-muted hover:text-theme-text hover:bg-theme-bg-alt",
          "transition-all",
          publishing && "opacity-70 pointer-events-none",
          className
        ),
        "aria-label": copied ? "Copied!" : publishing ? "Publishing..." : "Share",
        title: copied ? "Copied!" : "Share",
        onClick: handleShare,
        disabled: publishing,
        "data-id": "mobile-share-button",
        children: publishing ? /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" }) : copied ? Icon_default.success("w-5 h-5 text-theme-accent") : Icon_default.shareButton("w-5 h-5")
      }
    );
  }
  __name(MobileShareButton, "MobileShareButton");
  function QuicklinkShareMenu({ calc, className }) {
    const [saving, setSaving] = useState41(false);
    const handleSave = useCallback27(async () => {
      if (!calc || saving) return;
      setSaving(true);
      try {
        if (window.Action?.cloneAndOpen) {
          window.Action.cloneAndOpen(calc, { suffix: "" });
        }
      } finally {
        setSaving(false);
      }
    }, [calc, saving]);
    return /* @__PURE__ */ (0, import_jsx_runtime72.jsxs)(
      "button",
      {
        onClick: handleSave,
        disabled: saving,
        className: cn(
          "inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold transition",
          "bg-accent text-bg hover:brightness-110",
          saving && "opacity-70 cursor-wait",
          "tooltip tooltip-bottom",
          className
        ),
        "data-id": "quicklink-save-btn",
        "data-tip": "Save a copy to My Calcs",
        children: [
          saving ? /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { className: "w-4 h-4 border-2 border-[color-mix(in_srgb,var(--bg)_30%,transparent)] border-t-[var(--bg)] rounded-full animate-spin" }) : Icon_default.heart("w-4 h-4"),
          /* @__PURE__ */ (0, import_jsx_runtime72.jsx)("span", { children: "Save" })
        ]
      }
    );
  }
  __name(QuicklinkShareMenu, "QuicklinkShareMenu");
  var ShareMenu_default = react_global_default.memo(ShareMenu);

  // public/src/react/components/calc/RemixButton.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime73 = __toESM(require_jsx_runtime());
  function RemixButton({ variant = "full", className = "", useHeart = true }) {
    const { calc } = useCalc();
    const handleRemix = /* @__PURE__ */ __name(() => {
      if (window.Action?.cloneAndOpen) {
        window.Action.cloneAndOpen(calc, { suffix: "" });
      }
    }, "handleRemix");
    const icon = useHeart ? Icon_default.heart : Icon_default.remix;
    const label = useHeart ? "Save" : "Remix";
    const tooltip = "Save a copy to My Calcs";
    if (variant === "icon") {
      return /* @__PURE__ */ (0, import_jsx_runtime73.jsx)(
        "button",
        {
          onClick: handleRemix,
          className: `p-2 rounded-lg text-theme-text hover:bg-[var(--accent-light)] transition-colors tooltip ${className}`,
          "data-tip": tooltip,
          "data-ic-remix-button": "icon",
          children: icon("w-5 h-5")
        }
      );
    }
    if (variant === "compact") {
      return /* @__PURE__ */ (0, import_jsx_runtime73.jsxs)(
        "button",
        {
          onClick: handleRemix,
          className: `inline-flex items-center gap-1.5 tooltip tooltip-bottom ${className}`,
          "data-tip": tooltip,
          "data-ic-remix-button": "compact",
          children: [
            icon("w-4 h-4"),
            /* @__PURE__ */ (0, import_jsx_runtime73.jsx)("span", { className: "text-sm font-medium", children: label })
          ]
        }
      );
    }
    return /* @__PURE__ */ (0, import_jsx_runtime73.jsxs)(
      "button",
      {
        onClick: handleRemix,
        className: `inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90 tooltip tooltip-bottom ${className}`,
        style: {
          background: "var(--button-primary-bg)",
          color: "var(--button-primary-fg)"
        },
        "data-tip": tooltip,
        "data-ic-remix-button": "full",
        children: [
          icon("w-4 h-4"),
          label
        ]
      }
    );
  }
  __name(RemixButton, "RemixButton");

  // public/src/react/components/calc/sharebar/index.jsx
  init_react_global();

  // public/src/react/components/calc/sharebar/MorphingShareButton.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime74 = __toESM(require_jsx_runtime());
  var { useState: useState42, useCallback: useCallback28, useEffect: useEffect41, useRef: useRef30 } = react_global_default;

  // public/src/react/hooks/useShareUXMode.js
  init_react_global();

  // public/src/react/hooks/useLocalStorage.js
  init_react_global();
  var { useState: useState43, useEffect: useEffect42 } = react_global_default;
  function useLocalStorage(key, initialValue) {
    const [storedValue, setStoredValue] = useState43(() => {
      try {
        const item = window.localStorage.getItem(key);
        return item ? JSON.parse(item) : initialValue;
      } catch (error) {
        console.warn(`Error reading localStorage key "${key}":`, error);
        return initialValue;
      }
    });
    const setValue = /* @__PURE__ */ __name((value) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    }, "setValue");
    return [storedValue, setValue];
  }
  __name(useLocalStorage, "useLocalStorage");
  function useSessionStorage(key, initialValue) {
    const [storedValue, setStoredValue] = useState43(() => {
      try {
        const item = window.sessionStorage.getItem(key);
        return item ? JSON.parse(item) : initialValue;
      } catch (error) {
        console.warn(`Error reading sessionStorage key "${key}":`, error);
        return initialValue;
      }
    });
    const setValue = /* @__PURE__ */ __name((value) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
      } catch (error) {
        console.warn(`Error setting sessionStorage key "${key}":`, error);
      }
    }, "setValue");
    return [storedValue, setValue];
  }
  __name(useSessionStorage, "useSessionStorage");

  // public/src/react/hooks/useShareUXMode.js
  init_featureFlags();
  var { useEffect: useEffect43 } = react_global_default;
  function useShareUXMode() {
    const urlParams = new URLSearchParams(window.location.search);
    const shareUX = urlParams.get("shareUX");
    const morphingParam = urlParams.get("morphing");
    let urlOverride = null;
    if (shareUX) urlOverride = shareUX;
    if (morphingParam === "false" || morphingParam === "0") urlOverride = "standard";
    if (morphingParam === "true" || morphingParam === "1") urlOverride = "morphing";
    const isMorphingEnabled = isFeatureEnabled("morphingShareBar");
    const defaultMode = isMorphingEnabled ? "morphing" : "standard";
    const [storedMode, setMode] = useLocalStorage("ic:shareUXMode", defaultMode);
    useEffect43(() => {
      if (urlOverride && urlOverride !== storedMode) {
        setMode(urlOverride);
      }
    }, [urlOverride, storedMode, setMode]);
    let activeMode = urlOverride || storedMode;
    if (!isMorphingEnabled && activeMode === "morphing" && !urlOverride) {
      activeMode = "standard";
    }
    return {
      shareUXMode: activeMode,
      setShareUXMode: setMode,
      isMorphing: activeMode === "morphing"
    };
  }
  __name(useShareUXMode, "useShareUXMode");

  // public/src/react/components/calc/sharebar/index.jsx
  var import_CalcType3 = __toESM(require_CalcType());

  // public/src/react/components/calc/sharebar/components/URLChip.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime75 = __toESM(require_jsx_runtime());
  var { useState: useState44, useCallback: useCallback29, useRef: useRef31, useEffect: useEffect44 } = react_global_default;
  function URLChip({
    url,
    displayUrl,
    mode = "public",
    onClick,
    className = ""
  }) {
    const [copied, setCopied] = useState44(false);
    const toast = useToast();
    const timeoutRef = useRef31(null);
    useEffect44(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);
    const modeConfig = {
      public: {
        icon: Icon_default.publicLink,
        iconClass: "text-theme-text-muted",
        label: "Public"
      },
      private: {
        icon: Icon_default.privateLink,
        iconClass: "text-theme-text-muted",
        label: "Private"
      },
      collab: {
        icon: Icon_default.lockOpen,
        iconClass: "text-theme-text-muted",
        label: "Collab"
      },
      wiki: {
        icon: Icon_default.users,
        iconClass: "text-theme-text-muted",
        label: "Wiki"
      },
      local: {
        icon: Icon_default.folder,
        iconClass: "text-theme-text-muted",
        label: "Local"
      },
      quicklink: {
        icon: Icon_default.link,
        iconClass: "text-theme-text-muted",
        label: "Quick Link"
      },
      shared: {
        icon: Icon_default.globe,
        iconClass: "text-theme-text-muted",
        label: "Shared"
      }
    };
    const config = modeConfig[mode] || modeConfig.public;
    const handleClick = useCallback29(async (e) => {
      e.stopPropagation();
      if (onClick) {
        onClick(e);
        return;
      }
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast?.success?.("Link copied");
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => setCopied(false), 2e3);
      } catch (err) {
        console.error("Failed to copy:", err);
        toast?.error?.("Failed to copy link");
      }
    }, [url, onClick, toast]);
    if (mode === "local" && !url) {
      return /* @__PURE__ */ (0, import_jsx_runtime75.jsxs)(
        "div",
        {
          className: cn(
            "flex items-center gap-1.5",
            "text-xs font-mono",
            "text-theme-text-muted opacity-60",
            className
          ),
          children: [
            config.icon("w-3 h-3"),
            /* @__PURE__ */ (0, import_jsx_runtime75.jsx)("span", { children: "My Calcs" })
          ]
        }
      );
    }
    const shortUrl = displayUrl || url?.replace("https://", "").replace(/\/$/, "") || "";
    return /* @__PURE__ */ (0, import_jsx_runtime75.jsxs)(
      "button",
      {
        onClick: handleClick,
        className: cn(
          "group flex items-center gap-1.5",
          "text-xs font-mono",
          "text-theme-text-muted hover:text-theme-text opacity-50 hover:opacity-100",
          "transition-colors cursor-pointer",
          "tooltip",
          className
        ),
        "data-tip": copied ? "Copied!" : "Click to copy",
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime75.jsx)("span", { className: "flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity", children: config.icon("w-3 h-3") }),
          /* @__PURE__ */ (0, import_jsx_runtime75.jsxs)("span", { className: "relative truncate max-w-[200px] lg:max-w-[280px]", children: [
            shortUrl,
            /* @__PURE__ */ (0, import_jsx_runtime75.jsx)("span", { className: "absolute -bottom-px left-0 w-full h-px bg-current scale-x-0 group-hover:scale-x-100 transition-transform origin-left" })
          ] }),
          copied && /* @__PURE__ */ (0, import_jsx_runtime75.jsx)("span", { className: "flex-shrink-0 text-theme-accent", children: Icon_default.success("w-3 h-3") })
        ]
      }
    );
  }
  __name(URLChip, "URLChip");
  var URLChip_default = URLChip;

  // public/src/react/components/calc/sharebar/index.jsx
  var import_jsx_runtime76 = __toESM(require_jsx_runtime());
  var { useMemo: useMemo19 } = react_global_default;
  function ShareBar({ calc, viewMode, className = "" }) {
    const Action = useAction();
    const { isMorphing } = useShareUXMode();
    const {
      isPublished,
      isAlreadySaved
    } = useShareMenu({ Action, calc, viewMode });
    const isOwner = useMemo19(() => {
      if (import_CalcType3.default.isRemote(calc)) return false;
      if (!calc?.author) return true;
      return window.User?.id && calc.author === window.User.id;
    }, [calc]);
    const isRemoteCalc = import_CalcType3.default.isRemote(calc);
    const isShared2 = viewMode === "public" || isPublished || calc?.published?.id || isRemoteCalc;
    const isWiki = import_CalcType3.default.isWiki(calc);
    const isUrlDefined = import_CalcType3.default.isUrlDefined(calc?.type);
    const showRemix = !isOwner && (viewMode === "public" || isWiki || isShared2) && !isAlreadySaved;
    if (viewMode === "embed") {
      return null;
    }
    return /* @__PURE__ */ (0, import_jsx_runtime76.jsxs)("div", { className: cn("flex items-center gap-1 lg:gap-3", className), children: [
      !isOwner && calc?.authorName && /* @__PURE__ */ (0, import_jsx_runtime76.jsxs)("div", { className: "hidden lg:flex items-center gap-1.5 text-sm mr-1", children: [
        /* @__PURE__ */ (0, import_jsx_runtime76.jsx)("span", { className: "text-theme-text-muted", children: "by" }),
        /* @__PURE__ */ (0, import_jsx_runtime76.jsxs)("span", { className: "font-medium text-theme-text", children: [
          "@",
          calc.authorName
        ] })
      ] }),
      showRemix && !isUrlDefined && /* @__PURE__ */ (0, import_jsx_runtime76.jsx)(
        RemixButton,
        {
          className: "px-3 py-1.5 text-sm font-medium text-[var(--button-primary-fg)] bg-[var(--button-primary-bg)] rounded-lg lg:hover:brightness-110 lg:transition-all",
          variant: "full"
        }
      ),
      isUrlDefined && /* @__PURE__ */ (0, import_jsx_runtime76.jsx)(QuicklinkShareMenu, { calc, className: "" }),
      /* @__PURE__ */ (0, import_jsx_runtime76.jsx)("div", { className: "lg:hidden", children: /* @__PURE__ */ (0, import_jsx_runtime76.jsx)(MobileShareButton, { calc, viewMode, className: "" }) }),
      /* @__PURE__ */ (0, import_jsx_runtime76.jsx)("div", { className: "hidden lg:block", children: /* @__PURE__ */ (0, import_jsx_runtime76.jsx)(
        ShareMenu_default,
        {
          calc,
          viewMode,
          variant: isPublished ? "icon" : void 0,
          isOwner,
          triggerOnHover: false,
          sideOffset: 2
        }
      ) })
    ] });
  }
  __name(ShareBar, "ShareBar");
  var sharebar_default = ShareBar;

  // public/src/react/components/calc/ModeDropdown.jsx
  init_react_global();
  init_Icon();

  // public/src/react/plugins/index.js
  init_strategies();
  init_views();
  init_tools();
  init_share();
  init_registry();
  function debugPlugins() {
    const { strategyRegistry: strategyRegistry2 } = (init_strategies(), __toCommonJS(strategies_exports));
    const { viewRegistry: viewRegistry2 } = (init_views(), __toCommonJS(views_exports));
    const { toolRegistry: toolRegistry2 } = (init_tools(), __toCommonJS(tools_exports));
    const { shareRegistry: shareRegistry2 } = (init_share(), __toCommonJS(share_exports));
    console.group("Instacalc Plugins");
    strategyRegistry2.debug();
    viewRegistry2.debug();
    toolRegistry2.debug();
    shareRegistry2.debug();
    console.groupEnd();
  }
  __name(debugPlugins, "debugPlugins");
  if (typeof window !== "undefined") {
    window.__IC_PLUGINS__ = {
      get strategies() {
        return (init_strategies(), __toCommonJS(strategies_exports)).strategyRegistry;
      },
      get views() {
        return (init_views(), __toCommonJS(views_exports)).viewRegistry;
      },
      get tools() {
        return (init_tools(), __toCommonJS(tools_exports)).toolRegistry;
      },
      get share() {
        return (init_share(), __toCommonJS(share_exports)).shareRegistry;
      },
      debug: debugPlugins
    };
  }

  // public/src/react/components/calc/ModeDropdown.jsx
  var import_jsx_runtime77 = __toESM(require_jsx_runtime());
  var { useState: useState45, useRef: useRef32, useEffect: useEffect45 } = react_global_default;
  function ModeDropdown({ isMobile, iconOnly = false, subtle = false, showLabels = false, icons = true, excludeModes = [] }) {
    const { calc, viewMode, displayMode, setDisplayMode } = useCalc();
    const Action = useAction();
    const [hoveredMode, setHoveredMode] = useState45(null);
    const dismissTimeoutRef = useRef32(null);
    useEffect45(() => {
      return () => {
        if (dismissTimeoutRef.current) {
          clearTimeout(dismissTimeoutRef.current);
        }
      };
    }, []);
    const strategy = getStrategy(viewMode);
    const availableViews = getViewsForStrategy(strategy);
    const views = availableViews.filter((v) => !excludeModes.includes(v.id));
    const currentView = views.find((v) => v.id === displayMode) || views[0];
    const handleModeChange = /* @__PURE__ */ __name((newViewId) => {
      if (newViewId === displayMode) return;
      setDisplayMode(newViewId);
      Action.setCalcViewMode(calc?.id, newViewId);
    }, "handleModeChange");
    const handlePreviewClick = /* @__PURE__ */ __name(() => {
      if (window.SidebarManager) {
        const manager = new window.SidebarManager();
        if (typeof manager.openRight === "function") {
          manager.openRight("preview");
          return;
        }
      }
      if (window.Alpine?.store?.("ui")) {
        window.Alpine.store("ui").openPanel("preview");
      }
    }, "handlePreviewClick");
    if (iconOnly) {
      return /* @__PURE__ */ (0, import_jsx_runtime77.jsx)(
        "button",
        {
          className: "select-none inline-flex items-center justify-center p-2 opacity-40 hover:opacity-100 transition-all rounded-lg text-color-default hover-text-color-active hover-bg-button",
          onClick: () => {
            const currentIndex = views.findIndex((v) => v.id === displayMode);
            const nextIndex = (currentIndex + 1) % views.length;
            handleModeChange(views[nextIndex].id);
          },
          "data-id": "mode-icon-button",
          children: currentView.icon("w-4 h-4")
        }
      );
    }
    const getViewTitle = /* @__PURE__ */ __name((viewId) => {
      const view = views.find((v) => v.id === viewId);
      if (!view) return "";
      if (viewId === "present") return "Presentation view";
      return `${view.name} view`;
    }, "getViewTitle");
    return /* @__PURE__ */ (0, import_jsx_runtime77.jsxs)("div", { className: "relative", children: [
      /* @__PURE__ */ (0, import_jsx_runtime77.jsx)("div", { className: cn(
        "inline-flex items-center gap-0.5 px-0.5 py-0.5 rounded-lg bg-[var(--bg-alt)]",
        isMobile && "px-0.5 py-0.5"
      ), children: views.map((view) => {
        const isActive = view.id === displayMode;
        return /* @__PURE__ */ (0, import_jsx_runtime77.jsxs)(
          "button",
          {
            type: "button",
            className: cn(
              "relative inline-flex items-center justify-center gap-1.5 transition-all duration-200 select-none",
              // Subtle mode: icon-only with pill styling
              subtle ? cn(
                "w-8 h-8 rounded-md",
                isActive ? "text-[var(--text)] bg-[var(--bg)] shadow-sm" : "text-[var(--text-dim)] hover:text-[var(--text)]"
              ) : cn(
                "text-xs font-medium rounded-md",
                isActive ? "text-[var(--text)] bg-[var(--bg)] shadow-sm" : "text-[var(--text-dim)] hover:text-[var(--text)]",
                isMobile ? "px-2.5 py-1.5" : "px-3 py-1.5"
              )
            ),
            onClick: () => handleModeChange(view.id),
            onMouseEnter: () => {
              if (dismissTimeoutRef.current) {
                clearTimeout(dismissTimeoutRef.current);
                dismissTimeoutRef.current = null;
              }
              setHoveredMode(view.id);
            },
            onMouseLeave: (e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const movingDown = e.clientY > rect.bottom;
              if (!movingDown) {
                dismissTimeoutRef.current = setTimeout(() => {
                  setHoveredMode(null);
                }, 100);
              }
            },
            "data-id": `mode-pill-${view.id}`,
            "data-tip": subtle ? view.description : void 0,
            children: [
              icons && (subtle ? view.icon("w-4 h-4") : view.icon("w-3.5 h-3.5")),
              !subtle && view.name
            ]
          },
          view.id
        );
      }) }),
      !isMobile && hoveredMode && /* @__PURE__ */ (0, import_jsx_runtime77.jsx)(
        "div",
        {
          className: "absolute left-1/2 -translate-x-1/2 top-full pt-2 z-[9999]",
          onMouseEnter: () => setHoveredMode(hoveredMode),
          onMouseLeave: () => setHoveredMode(null),
          children: /* @__PURE__ */ (0, import_jsx_runtime77.jsx)("div", { className: "bg-[var(--bg)] border border-[var(--border-color)] rounded-lg shadow-lg p-3 min-w-[200px] animate-in fade-in slide-in-from-top-1 duration-150", children: (() => {
            const view = views.find((v) => v.id === hoveredMode);
            if (!view) return null;
            return /* @__PURE__ */ (0, import_jsx_runtime77.jsxs)(import_jsx_runtime77.Fragment, { children: [
              /* @__PURE__ */ (0, import_jsx_runtime77.jsxs)("div", { className: "flex items-center gap-2 mb-1", children: [
                view.icon("w-4 h-4 text-[var(--accent)]"),
                /* @__PURE__ */ (0, import_jsx_runtime77.jsx)("span", { className: "text-sm font-medium text-[var(--text)]", children: getViewTitle(view.id) })
              ] }),
              /* @__PURE__ */ (0, import_jsx_runtime77.jsx)("p", { className: "text-xs text-[var(--text-dim)] mb-2 whitespace-pre-line", children: view.description }),
              hoveredMode === "present" && /* @__PURE__ */ (0, import_jsx_runtime77.jsxs)(
                "button",
                {
                  onClick: (e) => {
                    e.stopPropagation();
                    handlePreviewClick();
                    setHoveredMode(null);
                  },
                  className: "w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--accent-light)] text-[var(--accent)] hover:bg-accent hover:text-bg transition-all",
                  children: [
                    Icon_default.eye("w-3.5 h-3.5"),
                    /* @__PURE__ */ (0, import_jsx_runtime77.jsx)("span", { children: "Open Preview" })
                  ]
                }
              )
            ] });
          })() })
        }
      )
    ] });
  }
  __name(ModeDropdown, "ModeDropdown");
  var ModeDropdown_default = ModeDropdown;

  // public/src/react/components/calc/MoreMenu.jsx
  init_react_global();
  init_Icon();
  init_featureFlags();
  var import_jsx_runtime78 = __toESM(require_jsx_runtime());
  function InlineSelector({ label, value, options, onChange, renderOption, icon }) {
    return /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)(Dropdown.Sub, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)(Dropdown.SubTrigger, { className: "!grid items-center gap-2 ![grid-template-columns:1.25rem_minmax(0,1fr)_auto]", children: [
        icon,
        /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)("span", { className: "flex min-w-0 items-center justify-between text-sm", children: [
          /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { className: "min-w-0 truncate text-[var(--text)]", children: label }),
          /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { className: "ml-auto text-right text-[var(--text-dim)]", children: value })
        ] })
      ] }),
      /* @__PURE__ */ (0, import_jsx_runtime78.jsx)(Dropdown.SubContent, { children: options.map((option, idx) => /* @__PURE__ */ (0, import_jsx_runtime78.jsx)(
        Dropdown.Item,
        {
          onSelect: () => onChange(option.value),
          className: "!block",
          children: renderOption ? renderOption(option) : option.label
        },
        option.value ?? idx
      )) })
    ] });
  }
  __name(InlineSelector, "InlineSelector");
  function MoreMenu({ isMobile, showRowNumbers, onToggleRowNumbers }) {
    const { calc, viewMode, displayMode } = useCalc();
    const Action = useAction();
    if (isMobile) {
      return null;
    }
    const buttonClassName = cn(
      "select-none inline-flex items-center justify-center px-2 py-1.5 text-xs transition-all rounded-md text-color-default hover-text-color-active hover-bg-button pointer-events-auto"
    );
    const triggerButton = /* @__PURE__ */ (0, import_jsx_runtime78.jsx)(
      "button",
      {
        className: buttonClassName,
        "data-id": "more-menu-button",
        children: Icon_default.menu("w-3.5 h-3.5")
      }
    );
    const isGoalseekEnabled = isFeatureEnabled("goalseek");
    const locales = [
      { value: "en-US", label: "US", example: "123,456.78" },
      { value: "de-DE", label: "EU", example: "123.456,78" },
      { value: "hi-IN", label: "SA", example: "1,23,456.78" }
    ];
    const roundingOptions = [
      { value: -1, label: "Show all", example: "" },
      { value: 0, label: "Whole", example: "1" },
      { value: 1, label: "1 place", example: "0.1" },
      { value: 2, label: "2 places", example: "0.01" },
      { value: 3, label: "3 places", example: "0.001" }
    ];
    const effectiveLocale = calc?.locale || "en-US";
    const effectiveRounding = calc?.rounding ?? -1;
    const currentLocale = locales.find((l) => l.value === effectiveLocale);
    const currentRounding = roundingOptions.find((r) => r.value === effectiveRounding);
    const handleClear = /* @__PURE__ */ __name(() => {
      if (calc) {
        Action.clear(calc);
      }
    }, "handleClear");
    const handleLocaleChange = /* @__PURE__ */ __name((value) => {
      window.DebugLogger?.log("ui", `Menu: set_locale: ${value}`);
      Action.logEvent("menu_set_locale", { calcId: calc.id, locale: value });
      Action.setLocale(calc.id, value);
    }, "handleLocaleChange");
    const handleRoundingChange = /* @__PURE__ */ __name((value) => {
      window.DebugLogger?.log("ui", `Menu: set_rounding: ${value}`);
      Action.logEvent("menu_set_rounding", { calcId: calc.id, rounding: value });
      Action.setRounding(calc.id, value);
    }, "handleRoundingChange");
    return /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)(
      Dropdown,
      {
        trigger: triggerButton,
        align: "end",
        classNames: { content: "min-w-[17rem]" },
        children: [
          calc && currentLocale && /* @__PURE__ */ (0, import_jsx_runtime78.jsx)(
            InlineSelector,
            {
              label: "Number Format",
              icon: Icon_default.globe("w-4 h-4 opacity-60"),
              value: currentLocale.label,
              options: locales,
              onChange: handleLocaleChange,
              renderOption: (opt) => /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)("span", { className: "flex w-full items-center justify-between gap-4", children: [
                /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { className: "tabular-nums", children: opt.example }),
                /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { className: "text-[var(--text-dim)] text-right min-w-[2ch]", children: opt.label })
              ] })
            }
          ),
          calc && currentRounding && /* @__PURE__ */ (0, import_jsx_runtime78.jsx)(
            InlineSelector,
            {
              label: "Decimals",
              icon: /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { className: "w-4 h-4" }),
              value: currentRounding.label,
              options: roundingOptions,
              onChange: handleRoundingChange,
              renderOption: (opt) => /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)("span", { className: "flex w-full items-center justify-between gap-4", children: [
                /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { children: opt.label }),
                opt.example && /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { className: "text-[var(--text-dim)] tabular-nums text-right min-w-[5ch]", children: opt.example })
              ] })
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime78.jsx)(Dropdown.Separator, {}),
          onToggleRowNumbers && /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)(Dropdown.Item, { onSelect: onToggleRowNumbers, children: [
            Icon_default.hashSymbol("w-4 h-4"),
            /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { children: showRowNumbers ? "Hide row numbers" : "Show row numbers" })
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime78.jsx)(Dropdown.Separator, {}),
          isGoalseekEnabled && /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)(
            Dropdown.Item,
            {
              onSelect: (e) => {
                window.DebugLogger?.log("ui", "Menu: open_goalseek");
                Action.logEvent("menu_goalseek", { calcId: calc?.id });
                document.dispatchEvent(new CustomEvent(MODAL_EVENTS.OPEN_GOALSEEK));
              },
              "data-ic-moremenu-goalseek": true,
              children: [
                Icon_default.target("w-4 h-4"),
                /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { children: "Goal seek" })
              ]
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)(
            Dropdown.Item,
            {
              onSelect: (e) => {
                window.DebugLogger?.log("ui", "Menu: open_history");
                Action.logEvent("menu_history", { calcId: calc?.id });
                document.dispatchEvent(new CustomEvent(MODAL_EVENTS.OPEN_HISTORY));
              },
              children: [
                Icon_default.history("w-4 h-4"),
                /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { children: "History..." })
              ]
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime78.jsxs)(
            Dropdown.Item,
            {
              onSelect: handleClear,
              className: "text-red-600 hover:text-red-700 hover:bg-red-50",
              children: [
                Icon_default.eraser("w-4 h-4"),
                /* @__PURE__ */ (0, import_jsx_runtime78.jsx)("span", { children: "Clear all rows" })
              ]
            }
          )
        ]
      }
    );
  }
  __name(MoreMenu, "MoreMenu");
  var MoreMenu_default = MoreMenu;

  // public/src/react/components/calc/CalcHeaderMenuMobile.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime79 = __toESM(require_jsx_runtime());
  function CalcHeaderMenuMobile({ showRowNumbers, onToggleRowNumbers }) {
    const triggerButton = /* @__PURE__ */ (0, import_jsx_runtime79.jsx)(
      "button",
      {
        className: "w-7 h-7 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text)] transition-colors rounded-md flex-shrink-0 focus:outline-none focus-visible:outline-none",
        style: { touchAction: "manipulation" },
        "aria-label": "View options",
        "data-id": "calc-header-menu",
        children: Icon_default.menu("w-4 h-4")
      }
    );
    return /* @__PURE__ */ (0, import_jsx_runtime79.jsx)(Dropdown, { trigger: triggerButton, align: "start", classNames: { content: "min-w-[11rem]" }, children: /* @__PURE__ */ (0, import_jsx_runtime79.jsx)(Dropdown.Item, { onSelect: onToggleRowNumbers, children: /* @__PURE__ */ (0, import_jsx_runtime79.jsx)("span", { children: showRowNumbers ? "Hide row numbers" : "Show row numbers" }) }) });
  }
  __name(CalcHeaderMenuMobile, "CalcHeaderMenuMobile");
  var CalcHeaderMenuMobile_default = CalcHeaderMenuMobile;

  // public/src/react/components/calc/CalcURLPreview.jsx
  init_react_global();
  var import_CalcType4 = __toESM(require_CalcType());
  var import_jsx_runtime80 = __toESM(require_jsx_runtime());
  var { useMemo: useMemo20 } = react_global_default;
  function CalcURLPreview({ calc, viewMode }) {
    const Action = useAction();
    const {
      isPublished,
      getPublicURL,
      getPrivateURL
    } = useShareMenu({ Action, calc, viewMode });
    const isRemoteCalc = import_CalcType4.default.isRemote(calc?.type);
    const isCollab3 = import_CalcType4.default.isCollab(calc?.type);
    const isWiki = import_CalcType4.default.isWiki(calc?.type);
    const isUrlDefined = import_CalcType4.default.isUrlDefined(calc?.type);
    const hasPrivateToken2 = !!calc?.token;
    const isPrivateOnly = hasPrivateToken2 && !isPublished;
    const isShared2 = viewMode === "public" || isPublished || calc?.published?.id || isRemoteCalc || isPrivateOnly;
    const isOwner = useMemo20(() => {
      if (!calc?.author) return true;
      return window.User?.id && calc.author === window.User.id;
    }, [calc?.author]);
    const publicURL = getPublicURL();
    const privateURL = getPrivateURL();
    const fullURL = isCollab3 || isPrivateOnly ? privateURL : publicURL;
    const shortURL = useMemo20(() => {
      if (!fullURL) return "";
      if (calc?.published?.id) {
        return `instacalc.com/${calc.published.id}`;
      }
      return fullURL.replace(/^https?:\/\//, "").replace(/\/$/, "");
    }, [fullURL, calc?.published?.id]);
    const quicklinkURL = useMemo20(() => {
      if (!isUrlDefined) return "";
      return window.location.href;
    }, [isUrlDefined]);
    const mode = useMemo20(() => {
      if (isWiki) return "wiki";
      if (isCollab3) return "collab";
      if (isPrivateOnly) return "private";
      if (isUrlDefined) return "quicklink";
      if (isShared2 && !isOwner) return "shared";
      if (isShared2) return "public";
      return "local";
    }, [isWiki, isCollab3, isPrivateOnly, isUrlDefined, isShared2, isOwner]);
    if (isUrlDefined) {
      const hash = window.location.hash?.slice(1) || "";
      const shortHash = hash.length > 3 ? hash.slice(0, 3) + "\u2026" : hash;
      return /* @__PURE__ */ (0, import_jsx_runtime80.jsx)(
        URLChip_default,
        {
          url: quicklinkURL,
          displayUrl: `instacalc.com#${shortHash}`,
          mode: "quicklink"
        }
      );
    }
    if (!isShared2) {
      return /* @__PURE__ */ (0, import_jsx_runtime80.jsx)(
        URLChip_default,
        {
          url: "",
          mode: "local"
        }
      );
    }
    return /* @__PURE__ */ (0, import_jsx_runtime80.jsx)(
      URLChip_default,
      {
        url: fullURL,
        displayUrl: shortURL,
        mode
      }
    );
  }
  __name(CalcURLPreview, "CalcURLPreview");
  var CalcURLPreview_default = CalcURLPreview;

  // public/src/react/components/calc/InstaCalc.jsx
  init_Icon();

  // public/src/react/components/calc/FloatingToolbar.jsx
  init_react_global();
  init_Icon();

  // public/src/react/components/meta/ToolbarButton.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime81 = __toESM(require_jsx_runtime());
  var defaultClassNames3 = {
    button: "select-none inline-flex items-center justify-center px-2 py-1 text-xs align-middle transition-all rounded text-color-default hover-text-color-active hover-bg-button min-w-[36px]",
    borderedButton: "select-none inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium align-middle transition-all rounded-md shadow-sm bg-secondary text-color-default hover-text-color-active hover-bg-button min-w-[40px] border border-soft",
    dropdownButton: "select-none inline-flex items-center justify-center px-2 py-1 text-xs align-middle transition-all rounded text-color-default hover-text-color-active hover-bg-button min-w-[36px]",
    dropdownMenu: "select-none z-[60] bg-secondary shadow-md rounded-lg p-2 mt-2",
    dropdownItem: "select-none flex text-sm items-center gap-x-3.5 py-2 hover-bg-button rounded px-3",
    svgButton: "select-none inline-flex items-center justify-center text-xs transition-all rounded tooltip group text-color-default hover-text-color-active hover-bg-button"
  };
  var toolbarClassNames = { ...defaultClassNames3 };
  var getClassName = /* @__PURE__ */ __name((slot) => toolbarClassNames[slot] || "", "getClassName");
  var ToolbarButton = class _ToolbarButton {
    static {
      __name(this, "ToolbarButton");
    }
    static configure({ classNames = {} } = {}) {
      toolbarClassNames = mergeClassNames(defaultClassNames3, classNames);
    }
    // Helper to create consistent SVG action buttons
    static createSvgButton(width, text, ariaTitle, onClick, dataTip, showBorder = true) {
      const rectWidth = width - 6;
      const centerX = width / 2;
      return /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(
        "button",
        {
          className: cn(getClassName("svgButton"), dataTip ? "tooltip" : ""),
          "data-tip": dataTip,
          onClick,
          children: /* @__PURE__ */ (0, import_jsx_runtime81.jsxs)("svg", { width, height: "24", viewBox: `0 0 ${width} 24`, role: "img", "aria-labelledby": `${text.toLowerCase().replace(/\s+/g, "-")}-title`, xmlns: "http://www.w3.org/2000/svg", fill: "none", className: "text-color-default group-hover:text-color-active", children: [
            /* @__PURE__ */ (0, import_jsx_runtime81.jsx)("title", { id: `${text.toLowerCase().replace(/\s+/g, "-")}-title`, children: ariaTitle }),
            showBorder && /* @__PURE__ */ (0, import_jsx_runtime81.jsx)("rect", { x: "3", y: "4", width: rectWidth, height: "16", rx: "4", stroke: "currentColor", strokeWidth: "1.2", fill: "transparent" }),
            /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(
              "text",
              {
                x: centerX,
                y: "14.5",
                textAnchor: "middle",
                fontSize: "9",
                fontWeight: "600",
                fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
                fill: "currentColor",
                children: text
              }
            )
          ] })
        }
      );
    }
    static createButton(onClick, dataTip, displayContent, additionalProps = {}) {
      const mergedProps = { ...additionalProps };
      mergedProps.className = cn(getClassName("button"), dataTip && "tooltip", additionalProps.className);
      return /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(
        "button",
        {
          onClick,
          "data-tip": dataTip,
          "data-id": mergedProps["data-id"],
          ...mergedProps,
          children: displayContent
        }
      );
    }
    static createGoalseek(context, displayContent, additionalProps = {}) {
      const result = _ToolbarButton.createButton(
        context.clickGoalseekButton.bind(context),
        "Goal seek",
        displayContent,
        additionalProps
      );
      return result;
    }
    static createLocale(context, displayContent, additionalProps = {}) {
      const locale = context.calc.locale || "en-US";
      const triggerButton = _ToolbarButton.createButton(null, "Number format", displayContent, additionalProps);
      return /* @__PURE__ */ (0, import_jsx_runtime81.jsxs)(Dropdown, { trigger: triggerButton, align: "start", children: [
        /* @__PURE__ */ (0, import_jsx_runtime81.jsx)("div", { className: "text-xs font-medium text-center capitalize opacity-50 text-color-default", children: "Number format" }),
        /* @__PURE__ */ (0, import_jsx_runtime81.jsxs)(
          Dropdown.RadioGroup,
          {
            value: locale,
            onValueChange: (value) => context.Action.setLocale(context.calc, value),
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(Dropdown.RadioItem, { value: "en-US", children: "123,456.78 (US)" }),
              /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(Dropdown.RadioItem, { value: "de-DE", children: "123.456,78 (Europe)" }),
              /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(Dropdown.RadioItem, { value: "hi-IN", children: "1,23,456.78 (South Asia)" })
            ]
          }
        )
      ] });
    }
    static createRounding(context, displayContent, additionalProps = {}) {
      const rounding = context.calc.rounding ?? -1;
      const triggerButton = _ToolbarButton.createButton(null, "Decimals to show", displayContent, additionalProps);
      return /* @__PURE__ */ (0, import_jsx_runtime81.jsxs)(Dropdown, { trigger: triggerButton, align: "start", classNames: { content: "min-w-[15rem]" }, children: [
        /* @__PURE__ */ (0, import_jsx_runtime81.jsx)("div", { className: "text-xs font-medium text-center capitalize opacity-50 text-color-default", children: "Decimals to show" }),
        /* @__PURE__ */ (0, import_jsx_runtime81.jsxs)(
          Dropdown.RadioGroup,
          {
            value: rounding,
            onValueChange: (value) => context.Action.setRounding(context.calc, value),
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(Dropdown.RadioItem, { value: -1, children: "All" }),
              /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(Dropdown.RadioItem, { value: 2, children: "0.01" }),
              /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(Dropdown.RadioItem, { value: 1, children: "0.1" }),
              /* @__PURE__ */ (0, import_jsx_runtime81.jsx)(Dropdown.RadioItem, { value: 0, children: "1" })
            ]
          }
        )
      ] });
    }
    static createFormat(context, displayContent, additionalProps = {}) {
      const onClick = /* @__PURE__ */ __name(() => {
        if (typeof document !== "undefined") {
          document.dispatchEvent(new CustomEvent("instacalc:openFormat"));
        }
      }, "onClick");
      const result = _ToolbarButton.createButton(
        onClick,
        "See row formatting options",
        displayContent,
        additionalProps
      );
      return result;
    }
    static createPresent(context, displayContent, additionalProps = {}) {
      const show_display_mode = context.Action.shouldHideRows(context.calc);
      const result = _ToolbarButton.createButton(
        () => context.Action.presentCalc(context.calc),
        "Display mode",
        displayContent,
        additionalProps
      );
      return result;
    }
    static createClear(context, displayContent, additionalProps = {}) {
      const result = _ToolbarButton.createButton(
        () => context.Action.clear(context.calc),
        "Clear all rows",
        displayContent,
        additionalProps
      );
      return result;
    }
    static createEmbed(context, displayContent, additionalProps = {}) {
      const result = _ToolbarButton.createButton(
        context.clickEmbedButton,
        "Get HTML embed (for website)",
        displayContent,
        additionalProps
      );
      return result;
    }
  };
  var ToolbarButton_default = ToolbarButton;

  // public/src/react/components/calc/FloatingToolbar.jsx
  init_featureFlags();

  // public/src/react/components/calc/FormatDropdown.jsx
  init_react_global();
  var import_jsx_runtime82 = __toESM(require_jsx_runtime());
  function FormatDropdown({ calc, Action, isMobile }) {
    const locale = calc.locale || "en-US";
    const roundingRaw = calc.rounding ?? -1;
    const parsedRounding = parseInt(roundingRaw, 10);
    const roundingValue = Number.isFinite(parsedRounding) ? parsedRounding : -1;
    const localeEnabled = Action.isLegacyCalc(calc) === false;
    let localeText = "US";
    if (locale === "de-DE") {
      localeText = "EU";
    } else if (locale === "hi-IN") {
      localeText = "SA";
    }
    let roundingText = ".*";
    if (roundingValue === 0) {
      roundingText = ".1";
    } else if (roundingValue === 1) {
      roundingText = ".0";
    } else if (roundingValue === 2) {
      roundingText = ".00";
    }
    const displayText = localeEnabled ? `${localeText} ${roundingText}` : roundingText;
    const roundingRadioValue = String(roundingValue);
    const buttonClassName = cn(
      "select-none inline-flex items-center justify-center gap-1 px-2 py-1 h-8 text-xs transition-all rounded text-color-default hover-text-color-active hover-bg-button",
      isMobile && "min-h-[42px] px-3 py-2 text-sm rounded-lg"
    );
    const triggerButton = /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(
      "button",
      {
        className: buttonClassName,
        "data-id": "format-dropdown-button",
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("span", { className: "font-mono text-xs", children: displayText }),
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("svg", { className: "w-3 h-3 opacity-50", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" }) })
        ]
      }
    );
    const handleLocaleChange = /* @__PURE__ */ __name((value) => {
      console.log("[FormatDropdown] Locale changed:", { value, calcId: calc?.id });
      Action.setLocale(calc?.id, value);
    }, "handleLocaleChange");
    const handleRoundingChange = /* @__PURE__ */ __name((value) => {
      const nextRounding = parseInt(value, 10);
      console.log("[FormatDropdown] Rounding changed:", { value, rounding: nextRounding, calcId: calc?.id });
      Action.setRounding(calc?.id, nextRounding);
    }, "handleRoundingChange");
    return /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown, { trigger: triggerButton, align: "start", modal: false, classNames: { content: "min-w-[14rem]" }, children: [
      localeEnabled && /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Section, { title: "Number Format", children: [
        /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Item, { onAction: () => handleLocaleChange("en-US"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent-primary)]", children: locale === "en-US" && "\u2713" }),
          "123,456.78 (US)"
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Item, { onAction: () => handleLocaleChange("de-DE"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent-primary)]", children: locale === "de-DE" && "\u2713" }),
          "123.456,78 (EU)"
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Item, { onAction: () => handleLocaleChange("hi-IN"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent-primary)]", children: locale === "hi-IN" && "\u2713" }),
          "1,23,456.78 (SA)"
        ] })
      ] }),
      /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Section, { title: "Decimal Places", children: [
        /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Item, { onAction: () => handleRoundingChange("-1"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent-primary)]", children: roundingRadioValue === "-1" && "\u2713" }),
          "Show all"
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Item, { onAction: () => handleRoundingChange("2"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent-primary)]", children: roundingRadioValue === "2" && "\u2713" }),
          "2 places (0.01)"
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Item, { onAction: () => handleRoundingChange("1"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent-primary)]", children: roundingRadioValue === "1" && "\u2713" }),
          "1 place (0.1)"
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime82.jsxs)(Dropdown.Item, { onAction: () => handleRoundingChange("0"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime82.jsx)("span", { className: "w-4 h-4 flex-shrink-0 flex items-center justify-center text-[var(--accent-primary)]", children: roundingRadioValue === "0" && "\u2713" }),
          "Whole numbers (1)"
        ] })
      ] })
    ] });
  }
  __name(FormatDropdown, "FormatDropdown");
  var FormatDropdown_default = FormatDropdown;

  // public/src/react/components/calc/FloatingToolbar.jsx
  var import_jsx_runtime83 = __toESM(require_jsx_runtime());
  var { useState: useState46 } = react_global_default;
  function VisualTooltip({ title, description, badge, children, position = "above" }) {
    return /* @__PURE__ */ (0, import_jsx_runtime83.jsxs)("div", { className: cn(
      "absolute z-[9999] pointer-events-none",
      "bg-theme-bg rounded-xl shadow-lg border border-soft",
      "px-3 py-2.5 min-w-[140px]",
      "opacity-0 group-hover:opacity-100",
      "transition-all duration-150",
      position === "above" ? "bottom-full left-1/2 -translate-x-1/2 mb-2 translate-y-1 group-hover:translate-y-0" : "top-full left-1/2 -translate-x-1/2 mt-2 -translate-y-1 group-hover:translate-y-0"
    ), children: [
      /* @__PURE__ */ (0, import_jsx_runtime83.jsx)("div", { className: cn(
        "absolute left-1/2 -translate-x-1/2 w-0 h-0",
        "border-[6px] border-transparent",
        position === "above" ? "top-full border-t-[var(--bg)]" : "bottom-full border-b-[var(--bg)]"
      ) }),
      /* @__PURE__ */ (0, import_jsx_runtime83.jsxs)("div", { className: "flex items-center justify-center gap-2 mb-0.5", children: [
        /* @__PURE__ */ (0, import_jsx_runtime83.jsx)("div", { className: "text-xs font-semibold text-theme-text text-center", children: title }),
        badge && /* @__PURE__ */ (0, import_jsx_runtime83.jsx)("span", { className: cn(
          "inline-flex items-center px-2 py-0.5 rounded-full",
          "text-[9px] font-semibold uppercase tracking-wide",
          "border border-soft",
          "text-theme-text-muted bg-theme-bg-alt"
        ), children: badge })
      ] }),
      description && /* @__PURE__ */ (0, import_jsx_runtime83.jsx)("div", { className: "text-[11px] text-theme-text-muted leading-snug text-center", children: description }),
      children && /* @__PURE__ */ (0, import_jsx_runtime83.jsx)("div", { className: "mt-2 pt-2 border-t border-soft", children })
    ] });
  }
  __name(VisualTooltip, "VisualTooltip");
  function ToolButton({ tool, ctx, buttonSize, iconSize }) {
    const { calc, Action, onHistory, onGoalseek } = ctx;
    const handleClick = /* @__PURE__ */ __name(() => {
      if (tool.opensModal === "history") {
        onHistory?.();
      } else if (tool.opensModal === "goalseek") {
        onGoalseek?.();
      } else if (tool.action) {
        tool.action({ calc, Action });
      }
    }, "handleClick");
    if (tool.component === "FormatDropdown") {
      return /* @__PURE__ */ (0, import_jsx_runtime83.jsxs)("div", { className: "relative group", children: [
        /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(FormatDropdown_default, { calc, Action, isMobile: ctx.isMobile }),
        /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(VisualTooltip, { title: tool.tooltip?.title || tool.name })
      ] });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime83.jsxs)("div", { className: "relative group", children: [
      /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(
        "button",
        {
          onClick: handleClick,
          "aria-label": tool.tooltip?.description || tool.name,
          "data-id": `floating-${tool.id}-button`,
          className: cn(
            buttonSize,
            "flex items-center justify-center rounded-lg",
            "text-theme-text-muted",
            "hover:bg-theme-bg-alt hover:text-theme-text",
            "transition-all"
          ),
          children: tool.icon(iconSize)
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(VisualTooltip, { title: tool.tooltip?.title || tool.name })
    ] });
  }
  __name(ToolButton, "ToolButton");
  function ToolCluster({ tools, calc, Action, onHistory, onGoalseek, isMobile }) {
    const buttonSize = isMobile ? "w-10 h-10" : "w-8 h-8";
    const iconSize = isMobile ? "w-5 h-5" : "w-4 h-4";
    const leftTools = tools.filter((t) => t.position === "left");
    const actionTools = leftTools.filter((t) => t.id !== "format");
    const formatTool = leftTools.find((t) => t.id === "format");
    const ctx = { calc, Action, onHistory, onGoalseek, isMobile };
    return /* @__PURE__ */ (0, import_jsx_runtime83.jsxs)("div", { className: cn("flex gap-1 p-1.5"), children: [
      actionTools.map((tool) => /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(
        ToolButton,
        {
          tool,
          ctx,
          buttonSize,
          iconSize
        },
        tool.id
      )),
      formatTool && actionTools.length > 0 && /* @__PURE__ */ (0, import_jsx_runtime83.jsx)("div", { className: "w-px h-6 self-center bg-[var(--border-color)] opacity-50" }),
      formatTool && /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(
        ToolButton,
        {
          tool: formatTool,
          ctx,
          buttonSize,
          iconSize
        }
      )
    ] });
  }
  __name(ToolCluster, "ToolCluster");
  function FloatingToolbar({ calcViewMode, isMobile }) {
    const { calc, viewMode, displayMode, setDisplayMode } = useCalc();
    const Action = useAction();
    const layout = useLayout();
    const isMobileView = Boolean(isMobile);
    const isPublic = calcViewMode === "public";
    const isPresentMode = displayMode === DisplayMode.PRESENT;
    const strategy = getStrategy(viewMode);
    const view = getView(displayMode);
    const features = getComposedFeatures(strategy, view);
    const tools = getTools({ strategy, view });
    const [historyOpen, setHistoryOpen] = useState46(false);
    const [goalseekOpen, setGoalseekOpen] = useState46(false);
    const handleHistory = /* @__PURE__ */ __name(() => {
      setHistoryOpen(true);
    }, "handleHistory");
    const handleMore = /* @__PURE__ */ __name(() => {
      setGoalseekOpen(true);
    }, "handleMore");
    const handlePreview = /* @__PURE__ */ __name(() => {
      if (window.SidebarManager) {
        const manager = new window.SidebarManager();
        if (typeof manager.toggleRight === "function") {
          manager.toggleRight("preview");
          return;
        }
        if (typeof manager.openRight === "function") {
          manager.openRight("preview");
          return;
        }
      }
      if (window.Alpine?.store?.("ui")) {
        const store = window.Alpine.store("ui");
        if (store.activePanel === "preview") {
          store.closePanel();
        } else {
          store.openPanel("preview");
        }
      }
    }, "handlePreview");
    if (!calc) return null;
    const goalseekModal = /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(Portal3, { selector: "GoalseekModal", children: /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(
      GoalseekModal_default,
      {
        clickCount: 1,
        open: goalseekOpen,
        onOpenChange: setGoalseekOpen
      }
    ) });
    const leftTools = tools.filter((t) => t.position === "left");
    const hasToolsToShow = leftTools.length > 0;
    if (!hasToolsToShow) {
      return /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(import_jsx_runtime83.Fragment, { children: goalseekModal });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime83.jsxs)(import_jsx_runtime83.Fragment, { children: [
      goalseekModal,
      features.showToolbar && /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(
        "div",
        {
          "data-floating-toolbar": true,
          className: cn(
            "flex items-center justify-between",
            // Full span in both modes: CSS var adapts to grid
            "col-span-grid-full",
            // Hide on mobile (< 1200px) where mobile bottom toolbar is active
            "hidden lg:flex"
          ),
          children: /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(
            "div",
            {
              className: cn(
                "flex-shrink-0",
                "transition-all duration-300 ease-out",
                isPresentMode && "opacity-0 scale-95 pointer-events-none"
              ),
              children: /* @__PURE__ */ (0, import_jsx_runtime83.jsx)(
                ToolCluster,
                {
                  tools,
                  calc,
                  Action,
                  onHistory: handleHistory,
                  onGoalseek: handleMore,
                  isMobile: isMobileView
                }
              )
            }
          )
        }
      )
    ] });
  }
  __name(FloatingToolbar, "FloatingToolbar");
  var FloatingToolbar_default = FloatingToolbar;

  // public/src/react/components/calc/MobileSingleLayoutToolbar.jsx
  init_react_global();
  var import_jsx_runtime84 = __toESM(require_jsx_runtime());
  function MobileSingleLayoutToolbar() {
    return /* @__PURE__ */ (0, import_jsx_runtime84.jsx)(Portal3, { selector: "[data-slot='mobile-view-mode-switcher']", children: /* @__PURE__ */ (0, import_jsx_runtime84.jsx)(ModeDropdown_default, { isMobile: true }) });
  }
  __name(MobileSingleLayoutToolbar, "MobileSingleLayoutToolbar");

  // public/src/react/components/layout/GridTemplates.jsx
  init_react_global();
  var import_jsx_runtime85 = __toESM(require_jsx_runtime());
  function CalcModeHeader({ children, className }) {
    return /* @__PURE__ */ (0, import_jsx_runtime85.jsx)("div", { className: cn(
      "col-span-3",
      // Mobile: full width (all 3 cols)
      "lg:col-span-2",
      // Desktop: content area only (cols 2-3)
      "lg:col-start-2",
      // Desktop: start after row number column
      "max-lg:-mb-2",
      // Spacing adjustments
      "relative z-10",
      // Stack above rows so tooltips show above calc content
      className
    ), children });
  }
  __name(CalcModeHeader, "CalcModeHeader");
  function TextModeHeader({ children, className }) {
    return /* @__PURE__ */ (0, import_jsx_runtime85.jsx)("div", { className: cn(
      "w-full col-span-grid-full",
      "relative z-50",
      className
    ), children });
  }
  __name(TextModeHeader, "TextModeHeader");
  function PresentModeHeader({ children, className, isEmbed }) {
    return /* @__PURE__ */ (0, import_jsx_runtime85.jsx)("div", { className: cn(
      "col-span-6",
      // Full width in 6-col grid
      "relative z-50",
      className
    ), children });
  }
  __name(PresentModeHeader, "PresentModeHeader");
  function AdaptiveHeader({ children, className, viewMode, isEmbed, isPresentMode, isTextMode }) {
    if (isPresentMode) {
      return /* @__PURE__ */ (0, import_jsx_runtime85.jsx)(PresentModeHeader, { className, isEmbed, children });
    }
    if (isTextMode) {
      return /* @__PURE__ */ (0, import_jsx_runtime85.jsx)(TextModeHeader, { className, children });
    }
    if (isEmbed) {
      return /* @__PURE__ */ (0, import_jsx_runtime85.jsx)("div", { className: cn("col-span-grid-full", className), children });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime85.jsx)(CalcModeHeader, { className, children });
  }
  __name(AdaptiveHeader, "AdaptiveHeader");
  function AdaptiveLayout({
    header,
    main,
    footer,
    toolbar,
    rootProps,
    layoutStrategy = "grid",
    isEmbed = false
  }) {
    const paddingClass = isEmbed ? "px-3" : "px-3 md:px-0";
    return /* @__PURE__ */ (0, import_jsx_runtime85.jsxs)(
      "calc",
      {
        ...rootProps,
        "data-calc-wrapper": true,
        "data-layout-strategy": layoutStrategy,
        className: cn(
          "calc-ready block",
          paddingClass,
          "max-w-4xl mx-auto w-full",
          // Responsive gap sizing: tighter on mobile, normal at sm+
          "[--gap-x:0.25rem] [--gap-y:0.25rem]",
          "sm:[--gap-x:0.5rem] sm:[--gap-y:0.5rem]",
          rootProps.className
        ),
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime85.jsx)(
            "header",
            {
              "data-calc-header": true,
              "data-testid": "calc-header-container",
              className: "pb-2 lg:pb-5",
              children: header
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime85.jsx)(
            "main",
            {
              "data-calc-content": true,
              "data-testid": "calc-content-area",
              children: main
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime85.jsx)(
            "footer",
            {
              "data-calc-footer": true,
              "data-testid": "calc-footer-container",
              children: footer
            }
          ),
          toolbar
        ]
      }
    );
  }
  __name(AdaptiveLayout, "AdaptiveLayout");

  // public/src/react/hooks/useShowRowNumbers.js
  init_react_global();
  function useShowRowNumbers({ isEmbed = false, forceOff = false } = {}) {
    if (isEmbed || forceOff) return false;
    return true;
  }
  __name(useShowRowNumbers, "useShowRowNumbers");

  // public/src/utils/layoutDebug.js
  function debugLayout() {
    console.group("\u{1F4D0} Layout Debug");
    const wrapper = document.querySelector("[data-calc-wrapper]");
    if (!wrapper) {
      console.warn("No [data-calc-wrapper] found on page");
      console.groupEnd();
      return;
    }
    const wrapperRect = wrapper.getBoundingClientRect();
    const computedStyle = getComputedStyle(wrapper);
    console.log("=== Wrapper ===");
    console.log(`  Size: ${wrapperRect.width.toFixed(0)}px \xD7 ${wrapperRect.height.toFixed(0)}px`);
    console.log(`  Grid Template: ${computedStyle.gridTemplateColumns}`);
    console.log(`  Gap: ${computedStyle.gap}`);
    const calc = document.querySelector('calc[data-testid="calc-root"]');
    if (calc) {
      const calcRect = calc.getBoundingClientRect();
      const calcStyle = getComputedStyle(calc);
      console.log("=== Calc Element ===");
      console.log(`  Size: ${calcRect.width.toFixed(0)}px \xD7 ${calcRect.height.toFixed(0)}px`);
      console.log(`  Display: ${calcStyle.display}`);
      console.log(`  Grid Template: ${calcStyle.gridTemplateColumns || "N/A"}`);
      console.log(`  View Mode: ${calc.getAttribute("data-view-mode")}`);
      console.log(`  Grid Type: ${calc.getAttribute("data-grid-type")}`);
    }
    const rows = document.querySelectorAll('row[data-testid="calc-row"]');
    console.log(`=== Rows (${rows.length}) ===`);
    rows.forEach((row, i) => {
      const rowRect = row.getBoundingClientRect();
      const rowStyle = getComputedStyle(row);
      const isContents = rowStyle.display === "contents";
      const num = row.querySelector("number");
      const c1 = row.querySelector("c1, [data-calc-input]");
      const c2 = row.querySelector("c2, [data-calc-result]");
      const numRect = num?.getBoundingClientRect();
      const c1Rect = c1?.getBoundingClientRect();
      const c2Rect = c2?.getBoundingClientRect();
      const mainWidth = wrapperRect.width - (numRect?.width || 0);
      console.log(`  Row ${i + 1}:`);
      console.log(`    Display: ${isContents ? "contents" : rowStyle.display}`);
      if (numRect && numRect.width > 0) {
        console.log(`    Number: ${numRect.width.toFixed(0)}px (visible: ${numRect.height > 0})`);
      } else {
        console.log(`    Number: hidden or not rendered`);
      }
      if (c1Rect) {
        const c1Ratio = (c1Rect.width / mainWidth * 100).toFixed(1);
        console.log(`    Input: ${c1Rect.width.toFixed(0)}px (${c1Ratio}% of main)`);
      }
      if (c2Rect) {
        const c2Ratio = (c2Rect.width / mainWidth * 100).toFixed(1);
        console.log(`    Result: ${c2Rect.width.toFixed(0)}px (${c2Ratio}% of main)`);
      }
      if (c1Rect && c2Rect) {
        const isStacked = Math.abs(c1Rect.left - c2Rect.left) < 5;
        if (isStacked) {
          console.log(`    Layout: STACKED (1-column)`);
        } else {
          console.log(`    Layout: SIDE-BY-SIDE`);
        }
      }
    });
    console.log("=== Summary ===");
    const viewportWidth = window.innerWidth;
    console.log(`  Viewport: ${viewportWidth}px (${viewportWidth < 768 ? "Mobile" : "Desktop"})`);
    const isEmbed = document.body.classList.contains("embed") || document.querySelector("calc.view-embed") !== null;
    console.log(`  Context: ${isEmbed ? "Embed" : "Regular"}`);
    console.groupEnd();
  }
  __name(debugLayout, "debugLayout");
  function getLayoutRatios() {
    const wrapper = document.querySelector("[data-calc-wrapper]");
    if (!wrapper) return null;
    const wrapperWidth = wrapper.getBoundingClientRect().width;
    const firstRow = document.querySelector('row[data-testid="calc-row"]');
    if (!firstRow) return null;
    const num = firstRow.querySelector("number");
    const c1 = firstRow.querySelector("c1, [data-calc-input]");
    const c2 = firstRow.querySelector("c2, [data-calc-result]");
    const numWidth = num?.getBoundingClientRect().width || 0;
    const c1Width = c1?.getBoundingClientRect().width || 0;
    const c2Width = c2?.getBoundingClientRect().width || 0;
    const mainWidth = wrapperWidth - numWidth;
    return {
      wrapperWidth,
      numWidth,
      inputWidth: c1Width,
      resultWidth: c2Width,
      inputRatio: (c1Width / mainWidth).toFixed(2),
      resultRatio: (c2Width / mainWidth).toFixed(2),
      isStacked: c1Width === wrapperWidth || c1Width > 0 && c2Width > 0 && Math.abs(c1?.getBoundingClientRect().left - c2?.getBoundingClientRect().left) < 5
    };
  }
  __name(getLayoutRatios, "getLayoutRatios");
  window.debugLayout = debugLayout;
  window.getLayoutRatios = getLayoutRatios;
  if (new URLSearchParams(window.location.search).has("debug-layout")) {
    window.addEventListener("load", () => {
      setTimeout(debugLayout, 500);
    });
  }

  // public/src/react/hooks/useInstacalcEvent.js
  init_react_global();
  function useInstacalcEvent(eventName, handler) {
    const handlerRef = react_global_default.useRef(handler);
    handlerRef.current = handler;
    react_global_default.useEffect(() => {
      if (typeof window === "undefined" || !window.InstacalcEvents) {
        console.error("[useInstacalcEvent] FAILED TO SUBSCRIBE - InstacalcEvents not available!", {
          eventName,
          hasWindow: typeof window !== "undefined",
          hasInstacalcEvents: !!window?.InstacalcEvents
        });
        return;
      }
      const stableHandler = /* @__PURE__ */ __name((event) => {
        if (!handlerRef.current) {
          console.error("[useInstacalcEvent] Handler ref is null!", { eventName });
          return;
        }
        handlerRef.current(event);
      }, "stableHandler");
      const cleanup = window.InstacalcEvents.on(eventName, stableHandler);
      return cleanup;
    }, [eventName]);
  }
  __name(useInstacalcEvent, "useInstacalcEvent");

  // public/src/react/components/calc/InstaCalc.jsx
  var import_CalcType5 = __toESM(require_CalcType());

  // public/src/react/hooks/useViewportWidth.js
  init_react_global();

  // public/src/react/hooks/useCalcLayoutClasses.js
  init_react_global();
  function useCalcLayoutClasses(displayMode, viewMode, wrapperRef) {
    react_global_default.useEffect(() => {
      const wrapper = wrapperRef?.current || document.querySelector("[data-calc-wrapper]");
      if (!wrapper) return;
      const isTextMode = displayMode === DisplayMode.TEXT;
      const isPresentMode = displayMode === DisplayMode.PRESENT;
      const isEmbed = viewMode === "embed";
      wrapper.classList.remove("view-calc", "view-text", "view-present", "ctx-compact");
      if (isTextMode) {
        wrapper.classList.add("view-text");
      } else if (isPresentMode) {
        wrapper.classList.add("view-present");
      } else {
        wrapper.classList.add("view-calc");
      }
      if (isEmbed) {
        wrapper.classList.add("ctx-compact");
      }
    }, [displayMode, viewMode, wrapperRef]);
  }
  __name(useCalcLayoutClasses, "useCalcLayoutClasses");

  // public/src/react/hooks/useCalcRecalculation.js
  init_react_global();
  function useCalcRecalculation({ calcRef, Action, setResults, debounceMs = 16 }) {
    const recalculate = react_global_default.useCallback(() => {
      if (!calcRef.current?.id) return;
      const liveCalc = window.calcStore?.get(calcRef.current.id);
      DebugLogger.log("perf", `[5/5] recalculate (sync) calcId=${liveCalc?.id?.slice(0, 8)}`);
      if (liveCalc) setResults(Action.recalculate(liveCalc));
    }, [Action, calcRef, setResults]);
    const smartRecalculate = react_global_default.useMemo(() => debounce(() => {
      if (!calcRef.current?.id) return;
      const liveCalc = window.calcStore?.get(calcRef.current.id);
      if (!liveCalc) return;
      const useWorker = window.IC?.api?.isWorkerLoaded?.();
      DebugLogger.log("perf", `[5/5] smartRecalculate useWorker=${useWorker} calcId=${liveCalc?.id?.slice(0, 8)}`);
      if (useWorker) {
        Action.async_recalculate(liveCalc);
        return;
      }
      const doRecalc = /* @__PURE__ */ __name(() => {
        const freshCalc = window.calcStore?.get(calcRef.current?.id);
        if (freshCalc) setResults(Action.recalculate(freshCalc));
      }, "doRecalc");
      const schedule = typeof requestIdleCallback !== "undefined" ? (fn) => requestIdleCallback(fn, { timeout: 100 }) : (fn) => setTimeout(fn, 0);
      schedule(doRecalc);
    }, debounceMs), [Action, calcRef, debounceMs, setResults]);
    react_global_default.useEffect(() => () => smartRecalculate.cancel(), [smartRecalculate]);
    return { recalculate, smartRecalculate };
  }
  __name(useCalcRecalculation, "useCalcRecalculation");

  // public/src/react/components/calc/InstaCalc.jsx
  var import_jsx_runtime86 = __toESM(require_jsx_runtime());
  var instanceCounter = 0;
  function EmbedFooter({ embedId }) {
    const url = `https://instacalc.com/${embedId}`;
    return /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("div", { className: "flex items-center justify-center embed-footer", children: /* @__PURE__ */ (0, import_jsx_runtime86.jsxs)("a", { href: url, target: "_blank", rel: "noreferrer", className: "poweredby flex items-center gap-2 group transition-opacity opacity-70 hover:opacity-100", children: [
      /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("img", { src: "/img/icons/svg/instacalc.logo.svg", alt: "Instacalc", className: "inline-block w-4 h-4 ic-logo transition-all opacity-80 group-hover:opacity-100" }),
      /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("span", { className: "instacalc-title embed-title text-xs font-semibold tracking-wide ic-logo-text opacity-80 group-hover:opacity-100 group-hover:text-theme-text transition-colors", children: "instacalc" })
    ] }) });
  }
  __name(EmbedFooter, "EmbedFooter");
  var CalcContent = /* @__PURE__ */ __name(({
    rootRef,
    calcStyle,
    gridStyle,
    gridConfig,
    layoutConfig,
    beautifulTheme,
    isPresentMode,
    titleArea,
    viewMode,
    strategy,
    calc,
    mobileNewCalcButtonProps,
    textEditMode,
    smartRecalculate,
    results,
    onKeyDown,
    embedId,
    ShareBar: ShareBar2,
    NewCalcButton: NewCalcButton2,
    EmbedFooter: EmbedFooter2,
    rowRefs,
    selectedRow,
    onSelectRow,
    focusRow,
    deleteRow,
    insertRow,
    pendingFocusOptionsRef,
    stableRowClassName,
    stableRowStyle,
    stableRowClassNames,
    TextEditView: TextEditView2,
    didMountRef,
    pendingAutoFocusPreventScrollRef,
    finalClassName,
    viewModeAttr,
    isStacked,
    contentGridClassName,
    showRowNumbersOverride,
    handleToggleRowNumbers,
    isSmallScreen
  }) => {
    const layout = useLayout();
    const { setDisplayMode } = useCalc();
    const Action = useAction();
    const prevSelectedRowRef = react_global_default.useRef(selectedRow);
    const isPublicView = viewMode === "public";
    const isUrlDefinedCalc = import_CalcType5.default.isUrlDefined(calc?.type);
    const isSharedCalc = viewMode === "public" || import_CalcType5.default.isRemote(calc) || isUrlDefinedCalc || Boolean(calc?.published?.id);
    react_global_default.useLayoutEffect(() => {
      const isAutoFocusView = viewMode === "calc";
      const isFocusView = viewMode === "calc" || viewMode === "embed";
      const selectionChanged = prevSelectedRowRef.current !== selectedRow;
      prevSelectedRowRef.current = selectedRow;
      const isVisibleInViewport = /* @__PURE__ */ __name((el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
      }, "isVisibleInViewport");
      if (!didMountRef.current) {
        didMountRef.current = true;
        pendingAutoFocusPreventScrollRef.current = false;
        const row2 = rowRefs.current["row" + selectedRow];
        if (isAutoFocusView && row2?.setFocus) {
          const rowEl = row2.inputRef?.current;
          const shouldPreventScroll = !isVisibleInViewport(rowEl);
          row2.setFocus({ preventScroll: shouldPreventScroll, selectionMode: "end", forceSelectAll: false });
        }
        return;
      }
      if (!selectionChanged) return;
      const row = rowRefs.current["row" + selectedRow];
      if (isFocusView && row?.setFocus) {
        if (DebugLogger.isEnabled("focus")) {
          DebugLogger.log("focus", "[InstaCalc] focus row", {
            viewMode,
            selectedRow,
            hasInput: Boolean(row?.inputRef?.current)
          });
        }
        const pendingOptions = pendingFocusOptionsRef?.current || {};
        const hasPendingOptions = Object.keys(pendingOptions).length > 0;
        const hasText = Boolean(calc?.rows?.[selectedRow]?.text);
        const rowEl = row.inputRef?.current;
        const shouldPreventScroll = !isVisibleInViewport(rowEl);
        const forceAll = pendingOptions.forceSelectAll === true;
        const defaultOptions = {
          preventScroll: shouldPreventScroll,
          selectionMode: forceAll || !hasText ? "all" : "end",
          forceSelectAll: forceAll || !hasText
        };
        row.setFocus(hasPendingOptions ? { ...defaultOptions, ...pendingOptions } : defaultOptions);
        if (pendingFocusOptionsRef) pendingFocusOptionsRef.current = {};
      }
    }, [selectedRow, viewMode, calc, rowRefs, didMountRef, pendingAutoFocusPreventScrollRef, pendingFocusOptionsRef]);
    const presentTheme = react_global_default.useMemo(() => getPresentTheme(calc), [calc]);
    const bodyStyle = react_global_default.useMemo(() => ({
      ...calcStyle,
      ...gridStyle,
      gap: isPresentMode ? `${layout.tokens.gapY} ${layout.tokens.gapX}` : void 0,
      "--calc-gap-x": layout.rawTokens.gapX,
      "--calc-gap-y": layout.rawTokens.gapY,
      "--calc-row-height": layout.rawTokens.rowMinHeight,
      "--calc-padding-x": `var(--calc-padding-x-responsive, var(--calc-padding-x-mobile, ${layout.rawTokens.paddingX}))`,
      "--calc-padding-y": `var(--calc-padding-y-responsive, var(--calc-padding-y-mobile, ${layout.rawTokens.paddingY}))`,
      "--calc-radius": layout.rawTokens.borderRadius,
      "--calc-hero-padding": layout.rawTokens.heroPadding,
      "--calc-present-affix-size": layout.rawTokens.presentAffixSize,
      "--calc-present-affix-size-hero": layout.rawTokens.presentAffixSizeHero,
      // Core mix variables for present mode (ensure they are defined in JSX context)
      "--accent-mix-light": "color-mix(in srgb, var(--accent) 6%, transparent)",
      "--accent-mix-medium": "color-mix(in srgb, var(--accent) 12%, transparent)",
      "--accent-mix-heavy": "color-mix(in srgb, var(--accent) 18%, transparent)",
      "--accent-mix-strong": "color-mix(in srgb, var(--accent) 40%, transparent)",
      ...presentTheme?.vars || {}
    }), [calcStyle, gridStyle, isPresentMode, layout.tokens, layout.rawTokens, presentTheme]);
    const rowElements = isPresentMode ? /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(
      PresentModeRows_default,
      {
        calc,
        results,
        rowRefs,
        viewMode,
        selectedRow,
        onSelectRow,
        focusRow,
        onTextChange: smartRecalculate,
        deleteRow,
        insertRow,
        stableRowClassName,
        stableRowStyle,
        stableRowClassNames
      }
    ) : /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(
      CalcModeRows_default,
      {
        calc,
        strategy,
        results,
        selectedRow,
        onSelectRow,
        onTextChange: smartRecalculate,
        viewMode,
        rowRefs,
        focusRow,
        deleteRow,
        insertRow
      }
    );
    const presentStackStyle = calc?.presentationStackStyle || "tint";
    const headerSlot = /* @__PURE__ */ (0, import_jsx_runtime86.jsxs)(
      AdaptiveHeader,
      {
        "data-calc-header": true,
        "data-testid": "calc-header",
        className: cn(
          "flex items-start",
          isPublicView ? "justify-center" : "justify-between",
          isPublicView && "lg:hidden"
        ),
        viewMode,
        isEmbed: viewMode === "embed",
        isPresentMode,
        isTextMode: textEditMode,
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime86.jsxs)("div", { className: cn("flex-1 min-w-0", isPresentMode && "text-left", isPublicView && "flex flex-col items-center text-center"), children: [
            titleArea,
            strategy.showCalcUrlPreview && /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("div", { className: "hidden lg:block mt-1", children: /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(CalcURLPreview_default, { calc, viewMode }) })
          ] }),
          strategy.showShareBar && !isPublicView && /* @__PURE__ */ (0, import_jsx_runtime86.jsxs)("div", { className: "flex items-center gap-2 pl-2", children: [
            /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("div", { className: "hidden lg:flex pointer-events-auto", children: /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(ModeDropdown_default, { showLabels: true, icons: false }) }),
            /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(ShareBar2, { calc, viewMode }),
            /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("div", { className: "hidden lg:block pointer-events-auto", children: /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(
              MoreMenu_default,
              {
                isMobile: false,
                showRowNumbers: showRowNumbersOverride,
                onToggleRowNumbers: handleToggleRowNumbers
              }
            ) }),
            strategy.allowTitleEdit && !isSharedCalc && /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("div", { className: "lg:hidden", children: /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(NewCalcButton2, { mobile: true, label: "\uFF0B", showIcon: false, className: "h-10 px-3", ...mobileNewCalcButtonProps }) }),
            !isPresentMode && viewMode !== "embed" && /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("div", { className: "lg:hidden", children: /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(
              CalcHeaderMenuMobile_default,
              {
                showRowNumbers: showRowNumbersOverride,
                onToggleRowNumbers: handleToggleRowNumbers
              }
            ) }),
            isPresentMode && /* @__PURE__ */ (0, import_jsx_runtime86.jsxs)(
              "button",
              {
                className: "lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[var(--accent-light)] text-[var(--accent)] hover:bg-accent hover:text-bg transition-all",
                onClick: () => {
                  setDisplayMode(DisplayMode.REGULAR);
                  Action.setCalcViewMode(calc?.id, DisplayMode.REGULAR);
                },
                "data-id": "exit-present-mode",
                children: [
                  Icon_default.pencil("w-3.5 h-3.5"),
                  "Edit"
                ]
              }
            )
          ] })
        ]
      }
    );
    const showRowNumbersViewport = useShowRowNumbers({
      isEmbed: viewMode === "embed",
      forceOff: !showRowNumbersOverride
    });
    const showLineNumbers = textEditMode ? showRowNumbersViewport : layoutConfig.chrome.showRowNumbers;
    const contentSlot = textEditMode ? /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(
      TextEditView2,
      {
        calc,
        results,
        onTextChange: smartRecalculate,
        showLineNumbers,
        className: !isStacked ? "col-span-grid-full w-full" : "w-full"
      }
    ) : /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(
      "rows",
      {
        "data-calc-rows": true,
        className: "contents",
        style: { display: "contents" },
        children: rowElements
      }
    );
    const footerSlot = strategy.footer === "embed" && embedId ? /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(EmbedFooter2, { embedId }) : null;
    const isSharedCalcToolbar = viewMode !== "embed" && viewMode !== "editor";
    const toolbarSlot = strategy.showToolbar ? /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(FloatingToolbar_default, { calcViewMode: viewMode, isMobile: false }) : null;
    const mobileToolbarSlot = isSharedCalcToolbar ? /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(MobileSingleLayoutToolbar, {}) : null;
    const rootProps = {
      ref: rootRef,
      "data-testid": "calc-root",
      "data-view-mode": viewModeAttr,
      "data-grid-type": gridConfig.type,
      "data-layout-strategy": gridConfig.layoutStrategy,
      "data-responsive": showRowNumbersViewport ? void 0 : "true",
      "data-beautiful": beautifulTheme || void 0,
      "data-present-stack-style": presentStackStyle,
      "data-present-theme": presentTheme?.id || "soft",
      className: finalClassName,
      style: bodyStyle,
      onKeyDown
    };
    return /* @__PURE__ */ (0, import_jsx_runtime86.jsxs)(import_jsx_runtime86.Fragment, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(
        AdaptiveLayout,
        {
          header: headerSlot,
          main: contentSlot,
          footer: footerSlot,
          toolbar: toolbarSlot,
          rootProps,
          layoutStrategy: gridConfig.layoutStrategy,
          isEmbed: viewMode === "embed"
        }
      ),
      mobileToolbarSlot
    ] });
  }, "CalcContent");
  var InstaCalc = react_global_default.forwardRef((props, ref) => {
    const Action = useAction();
    const { calc, viewMode, strategy, selectedRow, setSelectedRow, results, setResults, displayMode, setDisplayMode } = useCalc();
    perfCount(`InstaCalc.render[${calc?.id?.slice(0, 8) || "none"}]`);
    const [editingTitle, setEditingTitle] = react_global_default.useState(false);
    const [isSmallScreen, setIsSmallScreen] = react_global_default.useState(
      () => typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT_PX : false
    );
    const [showRowNumbersOverride, setShowRowNumbersOverride] = react_global_default.useState(() => {
      if (typeof window === "undefined") return !isSmallScreen;
      if (window.SettingsManager) {
        const saved = window.SettingsManager.get("ui.showRowNumbers");
        return saved !== void 0 ? saved : !isSmallScreen;
      }
      return !isSmallScreen;
    });
    react_global_default.useEffect(() => {
      if (typeof window === "undefined") return;
      const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
      const onChange = /* @__PURE__ */ __name((e) => {
        const small = e.matches;
        setIsSmallScreen(small);
      }, "onChange");
      setIsSmallScreen(mq.matches);
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }, []);
    const [historyOpen, setHistoryOpen] = react_global_default.useState(false);
    const [goalseekOpen, setGoalseekOpen] = react_global_default.useState(false);
    const textEditMode = displayMode === DisplayMode.TEXT;
    const instanceId = react_global_default.useMemo(() => `ICalc_${calc?.id ?? "unknown"}_${++instanceCounter}`, [calc?.id]);
    const rootRef = react_global_default.useRef(null);
    const rowRefs = react_global_default.useRef({});
    const didMountRef = react_global_default.useRef(false);
    const pendingAutoFocusPreventScrollRef = react_global_default.useRef(false);
    const isProcessingKeyRef = react_global_default.useRef(false);
    const pendingFocusOptionsRef = react_global_default.useRef({});
    useCalcLayoutClasses(displayMode, viewMode, rootRef);
    const calcRef = react_global_default.useRef(calc);
    calcRef.current = calc;
    const stableRowClassName = react_global_default.useMemo(() => props.rowClassName || "", [props.rowClassName]);
    const stableRowStyle = react_global_default.useMemo(() => props.rowStyle || {}, [props.rowStyle]);
    const stableRowClassNames = react_global_default.useMemo(() => props.rowClassNames || {}, [props.rowClassNames]);
    const clampIndex = react_global_default.useCallback((i) => {
      const rowCount = Math.max(calcRef.current?.rows?.length || 0, results.length);
      return Math.max(0, Math.min(i, rowCount - 1));
    }, [results.length]);
    const onSelectRow = react_global_default.useCallback((i) => {
      if (document.body?.classList?.contains("isdragged")) return;
      const idx = clampIndex(i);
      setSelectedRow(idx);
      if (Action) Action.selectedRow = idx;
    }, [setSelectedRow, clampIndex, Action]);
    const handleToggleRowNumbers = react_global_default.useCallback(() => {
      const newValue = !showRowNumbersOverride;
      setShowRowNumbersOverride(newValue);
      if (typeof window !== "undefined" && window.SettingsManager) {
        window.SettingsManager.set("ui.showRowNumbers", newValue);
      }
    }, [showRowNumbersOverride]);
    const focusRow = react_global_default.useCallback((i, options = {}) => {
      const idx = clampIndex(i);
      pendingFocusOptionsRef.current = options;
      onSelectRow(idx);
    }, [onSelectRow, clampIndex]);
    const [editingTitleValue, setEditingTitleValue] = react_global_default.useState("");
    const handleEdit = react_global_default.useCallback((event) => {
      setEditingTitleValue(event.target.value);
    }, []);
    const editOpen = react_global_default.useCallback(() => {
      setEditingTitleValue(calc?.name || "");
      setEditingTitle(true);
    }, [calc?.name]);
    const editClose = react_global_default.useCallback((e) => {
      e.preventDefault();
      const newName = editingTitleValue.trim();
      if (newName !== "") {
        Action.updateName(calc, newName);
        setEditingTitle(false);
        focusRow(0);
      }
    }, [editingTitleValue, Action, calc, focusRow]);
    const { recalculate, smartRecalculate } = useCalcRecalculation({
      calcRef,
      Action,
      setResults,
      debounceMs: 50
    });
    const insertRow = react_global_default.useCallback((i) => {
      Action.insertRow(calc?.id, i);
      afterNextPaint(() => {
        focusRow(i, { forceSelectAll: true });
      });
      smartRecalculate();
    }, [Action, calc?.id, smartRecalculate, focusRow]);
    const deleteRow = react_global_default.useCallback((i) => {
      Action.deleteRow(calc?.id, i);
      setResults((prev) => {
        const filtered = prev.filter((_, idx) => idx !== i);
        if (calc?.id && window.resultsStore) {
          window.resultsStore.set(calc.id, filtered);
        }
        return filtered;
      });
      smartRecalculate();
    }, [Action, calc?.id, smartRecalculate, setResults]);
    const onKeyDown = react_global_default.useCallback((event) => {
      if (textEditMode) return;
      const targetTag = event.target?.tagName?.toLowerCase();
      if (DebugLogger.isEnabled("focus")) {
        const targetRow = event.target?.closest?.("row")?.getAttribute?.("data-row");
        DebugLogger.log("focus", "[InstaCalc] keydown", {
          key: event.key,
          viewMode,
          selectedRow,
          targetTag,
          targetRow
        });
      }
      if ((targetTag === "textarea" || targetTag === "input") && !event.target?.closest("row")) {
        return;
      }
      if (isProcessingKeyRef.current) {
        event.preventDefault();
        return;
      }
      const rowCount = Math.max(calcRef.current?.rows?.length || 0, results.length);
      if (rowCount === 0) return;
      let change = null;
      switch (event.key) {
        case "ArrowDown":
          if (selectedRow >= rowCount - 1) return;
          change = 1;
          break;
        case "Enter":
          change = 1;
          if (strategy.autoInsertRows && selectedRow === rowCount - 1) {
            insertRow(selectedRow + 1);
            return;
          }
          break;
        case "ArrowUp":
          if (selectedRow <= 0) return;
          change = -1;
          break;
        default:
          return;
      }
      if (change === null) return;
      const container = rootRef.current;
      if (container) {
        let targetRow = selectedRow + change;
        const rowCount2 = calcRef.current?.rows?.length || 0;
        while (targetRow >= 0 && targetRow < rowCount2) {
          const input = container.querySelector(`[data-row-index="${targetRow}"]`);
          if (input) {
            event.preventDefault();
            isProcessingKeyRef.current = true;
            input.focus();
            setTimeout(() => {
              if (input.value) {
                input.setSelectionRange(0, input.value.length);
              }
            }, 0);
            requestAnimationFrame(() => {
              isProcessingKeyRef.current = false;
            });
            return;
          }
          targetRow += change;
        }
      }
    }, [selectedRow, strategy.autoInsertRows, insertRow, onSelectRow, results.length, viewMode]);
    react_global_default.useImperativeHandle(ref, () => ({
      focusRow,
      onSelectRow,
      recalculate,
      smartRecalculate,
      rowRefs: rowRefs.current,
      getResults: /* @__PURE__ */ __name(() => results, "getResults"),
      getSelectedIndex: /* @__PURE__ */ __name(() => selectedRow, "getSelectedIndex"),
      getCalc: /* @__PURE__ */ __name(() => calc, "getCalc")
    }));
    react_global_default.useEffect(() => {
      if (typeof window !== "undefined" && window.ClipboardJS) new window.ClipboardJS(".copy-event");
    }, []);
    react_global_default.useEffect(() => {
      const el = rootRef.current;
      if (el && el.setAttribute) el.setAttribute("data-ic-instance", instanceId);
    }, [instanceId]);
    react_global_default.useEffect(() => {
      if (!calc || !calc.rows) return;
      setEditingTitle(calc.name == "");
      if (calc.rows.some((r) => r.text)) smartRecalculate();
    }, [calc?.id, Action, smartRecalculate]);
    const handleSelectionRequest = react_global_default.useCallback((event) => {
      const { calcId, rowIndex, options = {} } = event.detail || {};
      if (calcId && calc?.id && calcId !== calc.id) return;
      if (typeof rowIndex === "number") {
        onSelectRow(rowIndex);
        if (options.focus) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const container = rootRef.current;
              if (container) {
                const input = container.querySelector(`[data-row-index="${rowIndex}"]`);
                if (input) {
                  input.focus();
                  input.select();
                }
              }
            });
          });
        }
      }
    }, [calc?.id, onSelectRow]);
    useInstacalcEvent(
      "ic:calc-selection-request",
      handleSelectionRequest
    );
    const rowContentKey = react_global_default.useMemo(() => {
      if (!calc?.rows) return "";
      return calc.rows.map((r) => `${r.text || ""}|${r.hidden ? 1 : 0}`).join("\n");
    }, [calc?.rows]);
    const prevRowContentKeyRef = react_global_default.useRef(rowContentKey);
    react_global_default.useEffect(() => {
      const prevKey = prevRowContentKeyRef.current;
      prevRowContentKeyRef.current = rowContentKey;
      if (prevKey === rowContentKey) return;
      DebugLogger.log("perf", `[4/5] content-change detected, triggering smartRecalculate`);
      smartRecalculate();
    }, [rowContentKey, smartRecalculate]);
    const isPresentMode = displayMode === DisplayMode.PRESENT;
    const titleArea = editingTitle && strategy.allowTitleEdit ? /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("form", { onSubmit: editClose, children: /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("input", { className: "w-1/2 px-2 py-1 border rounded-lg outline-none border-soft focus:border-accent", value: editingTitleValue, onChange: handleEdit, onBlur: editClose, placeholder: "Enter a title", type: "text", "aria-label": "Calc title", autoFocus: true }) }) : /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("h2", { className: cn(
      "text-lg lg:text-3xl font-semibold tracking-tight text-color-default",
      strategy.allowTitleEdit && "cursor-pointer",
      viewMode == "public" ? "text-center" : "lg:max-w-full",
      props.headerClassName
    ), children: /* @__PURE__ */ (0, import_jsx_runtime86.jsx)("span", { "data-tip": "Click to edit title", className: strategy.allowTitleEdit ? "tooltip" : void 0, onClick: editOpen, children: calc?.name }) });
    const beautifulTheme = react_global_default.useMemo(() => {
      for (const r of results) if (r?.presentation?.theme) return r.presentation.theme;
      return calc?.meta?.theme || null;
    }, [results, calc?.meta?.theme]);
    const calcStyle = react_global_default.useMemo(() => {
      const style = { ...props.spacing && typeof props.spacing !== "string" ? props.spacing : {}, ...props.cssVars || {} };
      if (isPresentMode) {
        const widthMap = { xs: "100%", sm: "100%", md: "100%", lg: "48rem", xl: "56rem", "2xl": "64rem", full: "100%" };
        style["--calc-max-width"] = calc?.meta?.width ? widthMap[calc.meta.width] : "100%";
      } else {
        style["--calc-max-width"] = "100%";
      }
      return style;
    }, [props.spacing, props.cssVars, isPresentMode, calc?.meta?.width]);
    const gridConfig = react_global_default.useMemo(() => {
      const isEmbed = viewMode === "embed";
      if (isPresentMode) {
        return { type: "6-col", columns: 6, rowLayout: "flex", layoutStrategy: "stacked" };
      }
      if (textEditMode) {
        return { type: "text", columns: 1, rowLayout: "contents", layoutStrategy: "stacked" };
      }
      if (isEmbed) {
        return { type: "2-col", columns: 2, rowLayout: "contents", layoutStrategy: "stacked" };
      }
      return { type: "3-col", columns: 3, rowLayout: "contents", layoutStrategy: "integrated" };
    }, [isPresentMode, textEditMode, viewMode]);
    const gridStyle = {};
    const layoutConfig = react_global_default.useMemo(() => {
      return {
        chrome: {
          showRowNumbers: (strategy.showRowNumbers ?? true) && showRowNumbersOverride,
          showRowContext: strategy.showRowContext ?? true,
          showExpandButton: strategy.showExpandButton ?? true,
          showToolbar: strategy.showToolbar ?? true,
          showShareBar: strategy.showShareBar ?? true,
          showCalcUrlPreview: strategy.showCalcUrlPreview ?? true,
          autoInsertRows: strategy.autoInsertRows ?? true,
          allowTitleEdit: strategy.allowTitleEdit ?? true
        }
      };
    }, [strategy, showRowNumbersOverride]);
    const isStacked = gridConfig.layoutStrategy === "stacked";
    const finalClassName = cn(
      "w-full",
      // Ensure root always fills its container
      "[--calc-padding-x-responsive:var(--calc-padding-x-mobile)]",
      "[--calc-padding-y-responsive:var(--calc-padding-y-mobile)]",
      "lg:[--calc-padding-x-responsive:var(--calc-padding-x-desktop)]",
      "lg:[--calc-padding-y-responsive:var(--calc-padding-y-desktop)]",
      `view-${strategy.name}`,
      // view-editor, view-public, view-embed
      "icreact",
      isPresentMode && "view-present",
      props.className
    );
    const bodyProps = {
      rootRef,
      calcStyle,
      gridStyle,
      gridConfig,
      layoutConfig,
      beautifulTheme,
      isPresentMode,
      titleArea,
      viewMode,
      strategy,
      calc,
      mobileNewCalcButtonProps: props.mobileNewCalcButtonProps,
      textEditMode,
      smartRecalculate,
      results,
      onKeyDown,
      embedId: props.embedId,
      ShareBar: sharebar_default,
      NewCalcButton: NewCalcButton_default,
      EmbedFooter,
      rowRefs,
      selectedRow,
      onSelectRow,
      focusRow,
      deleteRow,
      insertRow,
      pendingFocusOptionsRef,
      stableRowClassName,
      stableRowStyle,
      stableRowClassNames,
      TextEditView: TextEditView_default,
      didMountRef,
      pendingAutoFocusPreventScrollRef,
      finalClassName,
      isStacked,
      // Pass isStacked explicitly
      contentGridClassName: "",
      // No grid classes - CSS handles it
      viewModeAttr: displayMode === DisplayMode.TEXT ? "text" : isPresentMode ? "present" : "calc",
      showRowNumbersOverride,
      // Pass state to content
      handleToggleRowNumbers,
      // Pass toggle handler to content
      isSmallScreen
      // Pass screen size state to content
    };
    return /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(LayoutProvider, { density: strategy.density, strategy, gridConfig, children: /* @__PURE__ */ (0, import_jsx_runtime86.jsx)(CalcContent, { ...bodyProps }) });
  });
  InstaCalc.displayName = "InstaCalc";
  var InstaCalc_default = InstaCalc;

  // public/src/react/components/calc/CalcToolbar.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime87 = __toESM(require_jsx_runtime());
  var { useState: useState47 } = react_global_default;
  function ToolbarSlots({ calc, position = "right" }) {
    const plugins = typeof window !== "undefined" ? window.__IC_PLUGINS__ : null;
    if (!plugins?.toolbarSlots) return null;
    const slots = plugins.toolbarSlots.getAllSorted().filter((slot) => slot.position === position);
    if (slots.length === 0) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(import_jsx_runtime87.Fragment, { children: slots.map((slot) => {
      const SlotComponent = slot.component;
      return /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(SlotComponent, { calc }, slot.id);
    }) });
  }
  __name(ToolbarSlots, "ToolbarSlots");
  function CalcToolbar({ calcViewMode, isMobile }) {
    const Action = useAction();
    const { calc, displayMode } = useCalc();
    const isMobileView = Boolean(isMobile);
    const isPublic = calcViewMode === "public";
    const isPresentMode = displayMode === DisplayMode.PRESENT;
    const [goalseekButtonClicks, setGoalseekButtonClicks] = useState47(0);
    const [goalseekOpen, setGoalseekOpen] = useState47(false);
    const [toolbarCollapsed, setToolbarCollapsed] = useState47(false);
    const clickGoalseekButton = /* @__PURE__ */ __name(() => {
      setGoalseekButtonClicks(goalseekButtonClicks + 1);
      setGoalseekOpen(true);
    }, "clickGoalseekButton");
    const clickHistoryButton = /* @__PURE__ */ __name(() => {
      if (typeof document !== "undefined") {
        document.dispatchEvent(new CustomEvent(MODAL_EVENTS.OPEN_HISTORY));
      }
    }, "clickHistoryButton");
    const renderResetButton = /* @__PURE__ */ __name(() => {
      const handleReset = /* @__PURE__ */ __name(() => {
        if (window.confirm("Discard your changes and reload the page?")) {
          window.location.reload();
        }
      }, "handleReset");
      return /* @__PURE__ */ (0, import_jsx_runtime87.jsxs)(
        "button",
        {
          type: "button",
          className: cn(
            "inline-flex items-center gap-1 px-2 py-1 text-xs transition-all rounded tooltip text-color-default hover-text-color-active hover-bg-button",
            isMobileView && "min-h-[42px] px-3 py-2 text-sm rounded-lg"
          ),
          "data-tip": "Discard your changes and reload",
          "data-id": "reset-button",
          onClick: handleReset,
          children: [
            Icon_default.reset("w-4 h-4"),
            /* @__PURE__ */ (0, import_jsx_runtime87.jsx)("span", { children: "Reset" })
          ]
        }
      );
    }, "renderResetButton");
    if (!calc) {
      return /* @__PURE__ */ (0, import_jsx_runtime87.jsx)("div", {});
    }
    const isPublicView = isPublic;
    const mobileButtonProps = isMobileView ? { className: "min-h-[42px] px-3 py-2 text-sm rounded-lg min-w-[44px]" } : {};
    const goalseekModal = /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(Portal3, { selector: "GoalseekModal", children: /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(
      GoalseekModal_default,
      {
        clickCount: goalseekButtonClicks,
        open: goalseekOpen,
        onOpenChange: setGoalseekOpen
      }
    ) });
    const toolbarClassName = cn(
      "col-start-2 col-span-3 text-sm",
      isMobileView && "flex flex-col gap-3"
    );
    const divider = /* @__PURE__ */ (0, import_jsx_runtime87.jsx)("div", { className: "h-5 w-px bg-[var(--border-color)] opacity-30 mx-1" });
    const handleOpenFormat = /* @__PURE__ */ __name(() => {
      if (typeof document !== "undefined") {
        document.dispatchEvent(new CustomEvent(MODAL_EVENTS.OPEN_FORMAT));
      }
    }, "handleOpenFormat");
    const handleToggleToolbar = /* @__PURE__ */ __name(() => {
      setToolbarCollapsed(!toolbarCollapsed);
    }, "handleToggleToolbar");
    const showMinimalToolbar = isPresentMode || toolbarCollapsed;
    if (isPresentMode && !isMobileView) {
      return /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(import_jsx_runtime87.Fragment, { children: goalseekModal });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime87.jsxs)(import_jsx_runtime87.Fragment, { children: [
      goalseekModal,
      /* @__PURE__ */ (0, import_jsx_runtime87.jsx)("calctoolbar", { "data-calc-toolbar": true, "data-mobile": isMobileView ? "true" : void 0, className: toolbarClassName, children: /* @__PURE__ */ (0, import_jsx_runtime87.jsx)("div", { children: /* @__PURE__ */ (0, import_jsx_runtime87.jsxs)("div", { className: cn(
        "flex flex-wrap items-center gap-2 min-h-[2.5rem]",
        isMobileView ? "justify-center gap-3" : "justify-between"
      ), children: [
        !toolbarCollapsed && /* @__PURE__ */ (0, import_jsx_runtime87.jsxs)("div", { className: "flex items-center gap-1", children: [
          isPublicView && renderResetButton(),
          !isPublicView && ToolbarButton_default.createClear(
            { state: {}, calc, Action },
            /* @__PURE__ */ (0, import_jsx_runtime87.jsx)("span", { className: "inline-flex items-center gap-1 rounded-md", children: Icon_default.clear("w-4 h-4") }),
            { ...mobileButtonProps, "data-id": "clear-button" }
          ),
          !isPublicView && ToolbarButton_default.createButton(
            clickHistoryButton,
            "History",
            Icon_default.history("w-4 h-4"),
            { ...mobileButtonProps, "data-id": "history-button" }
          )
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime87.jsxs)("div", { className: "flex items-center gap-1", children: [
          !toolbarCollapsed && /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(import_jsx_runtime87.Fragment, { children: /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(
            FormatDropdown_default,
            {
              calc,
              Action,
              isMobile: isMobileView,
              onOpenFormat: handleOpenFormat
            }
          ) }),
          !isPublic && isMobileView && /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(ModeDropdown_default, { isMobile: isMobileView })
        ] }),
        !isMobileView && /* @__PURE__ */ (0, import_jsx_runtime87.jsxs)("div", { className: cn("flex items-center gap-1", toolbarCollapsed && "ml-auto"), children: [
          /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(ToolbarSlots, { calc, position: "right" }),
          /* @__PURE__ */ (0, import_jsx_runtime87.jsx)(MoreMenu_default, { isMobile: isMobileView })
        ] })
      ] }) }) })
    ] });
  }
  __name(CalcToolbar, "CalcToolbar");
  var CalcToolbar_default = react_global_default.memo(CalcToolbar);

  // public/src/react/components/meta/CalcInfo.jsx
  init_react_global();
  var import_jsx_runtime88 = __toESM(require_jsx_runtime());
  var renderMarkdown = /* @__PURE__ */ __name((Action, text) => {
    if (!text) {
      return "";
    }
    if (Action && typeof Action.renderMarkdown === "function") {
      try {
        return Action.renderMarkdown(text);
      } catch (e) {
      }
    }
    return String(text).replace(/\n/g, "<br />");
  }, "renderMarkdown");
  var CalcInfo = /* @__PURE__ */ __name(() => {
    const Action = useAction();
    const { calc } = useCalc();
    if (!calc) {
      return /* @__PURE__ */ (0, import_jsx_runtime88.jsx)("div", {});
    }
    return /* @__PURE__ */ (0, import_jsx_runtime88.jsxs)("calcinfo", { className: "block p-4 text-sm text-color-default", children: [
      /* @__PURE__ */ (0, import_jsx_runtime88.jsxs)("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ (0, import_jsx_runtime88.jsxs)("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ (0, import_jsx_runtime88.jsx)("img", { src: "/img/icons/svg/instacalc.logo.svg", alt: "Instacalc", className: "w-6 h-6" }),
          /* @__PURE__ */ (0, import_jsx_runtime88.jsx)("span", { className: "text-lg font-semibold", "data-ic-calc-info-name": true, children: calc.name })
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime88.jsx)("div", { className: "text-xs text-color-muted", children: Util.displayDate(calc.updated || calc.created) })
      ] }),
      /* @__PURE__ */ (0, import_jsx_runtime88.jsx)("div", { className: "mt-2 prose text-color-default", dangerouslySetInnerHTML: { __html: renderMarkdown(Action, calc.description) } })
    ] });
  }, "CalcInfo");
  var CalcInfo_default = react_global_default.memo(CalcInfo);

  // public/src/react/components/calc/SingleCalcActions.jsx
  init_react_global();
  var import_jsx_runtime89 = __toESM(require_jsx_runtime());
  function SingleCalcActions() {
    const { calc } = useCalc();
    if (!calc) return null;
    return /* @__PURE__ */ (0, import_jsx_runtime89.jsxs)(import_jsx_runtime89.Fragment, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime89.jsx)("div", { className: "space-y-4", children: /* @__PURE__ */ (0, import_jsx_runtime89.jsxs)("div", { className: "flex flex-wrap items-start gap-4", children: [
        /* @__PURE__ */ (0, import_jsx_runtime89.jsx)("div", { className: "flex-1 min-w-[200px]", children: /* @__PURE__ */ (0, import_jsx_runtime89.jsx)(CalcInfo_default, {}) }),
        /* @__PURE__ */ (0, import_jsx_runtime89.jsx)("div", { className: "inline-flex hidden lg:inline-flex", children: /* @__PURE__ */ (0, import_jsx_runtime89.jsx)(ShareMenu_default, {}) })
      ] }) }),
      /* @__PURE__ */ (0, import_jsx_runtime89.jsxs)(
        "button",
        {
          type: "button",
          className: "fixed bottom-6 left-6 z-50 hidden lg:inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-theme-bg-alt border border-[var(--sidebar-card-border)] shadow-lg text-theme-text font-medium hover:brightness-110 active:scale-95 transition-all",
          onClick: () => {
            if (typeof window !== "undefined") window.location.href = "/";
          },
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime89.jsx)("img", { src: "/img/icons/svg/instacalc.logo.svg", alt: "Instacalc", className: "w-5 h-5 ic-logo" }),
            "Back to Instacalc"
          ]
        }
      )
    ] });
  }
  __name(SingleCalcActions, "SingleCalcActions");
  var SingleCalcActions_default = react_global_default.memo(SingleCalcActions);

  // public/src/react/components/common/CalcLoadingBoundary.jsx
  init_react_global();
  var import_jsx_runtime90 = __toESM(require_jsx_runtime());
  function CalcLoadingBoundary({ children }) {
    return /* @__PURE__ */ (0, import_jsx_runtime90.jsx)(import_jsx_runtime90.Fragment, { children });
  }
  __name(CalcLoadingBoundary, "CalcLoadingBoundary");
  var CalcLoadingBoundary_default = CalcLoadingBoundary;

  // public/src/react/components/layouts/EditorLayout.jsx
  var import_CalcType6 = __toESM(require_CalcType());

  // public/src/react/components/calc/PreviewPanelPortal.jsx
  init_react_global();
  init_Icon();
  init_featureFlags();
  var import_jsx_runtime91 = __toESM(require_jsx_runtime());
  var { useState: useState48, useEffect: useEffect46, useRef: useRef33 } = react_global_default;
  var getBeautifyModal = /* @__PURE__ */ __name(() => {
    if (typeof window !== "undefined" && window.__ICMODALS__) {
      return window.__ICMODALS__.BeautifyModal;
    }
    return null;
  }, "getBeautifyModal");
  var CHIP_BASE_CLASS = "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold border border-soft bg-theme-bg text-theme-text hover:bg-theme-bg-alt transition-colors";
  var CHIP_DISABLED_CLASS = "opacity-50 pointer-events-none";
  function getRowLabel(calc, results, rowIndex) {
    if (!calc || !results || rowIndex < 0) return "No row selected";
    const result = results[rowIndex];
    const explicitLabel = result?.presentation?.label || result?.display?.label;
    if (explicitLabel) return explicitLabel;
    const rowText = calc?.rows?.[rowIndex]?.text || "";
    const baseText = (window.IC?.parser?.splitAtComment(rowText)?.before ?? rowText).trim();
    return baseText || `Row ${rowIndex + 1}`;
  }
  __name(getRowLabel, "getRowLabel");
  function hasDecorator(text, name) {
    if (!text) return false;
    const safeName = name.replace(/^@/, "");
    const regex = new RegExp(`@${safeName}(\\b|\\()`, "i");
    return regex.test(text);
  }
  __name(hasDecorator, "hasDecorator");
  function appendDecorator(text, decorator) {
    if (!decorator) return text;
    const cleanDecorator = decorator.startsWith("@") ? decorator : `@${decorator}`;
    if (hasDecorator(text, cleanDecorator)) return text;
    const trimmed = (text || "").trimEnd();
    if (!trimmed) return `// ${cleanDecorator}`;
    if (window.IC?.parser?.splitAtComment(trimmed)?.comment) return `${trimmed} ${cleanDecorator}`;
    return `${trimmed} // ${cleanDecorator}`;
  }
  __name(appendDecorator, "appendDecorator");
  function PreviewBuilderPanel({ calc, results, selectedRow, setSelectedRow }) {
    const Action = useAction();
    const [lastAction, setLastAction] = useState48(null);
    const [isExpanded, setIsExpanded] = useState48(false);
    const [beautifyOpen, setBeautifyOpen] = useState48(false);
    const hasSelection = Number.isInteger(selectedRow) && selectedRow >= 0;
    const rowText = hasSelection ? calc?.rows?.[selectedRow]?.text || "" : "";
    const rowResult = hasSelection ? results?.[selectedRow] : null;
    const isSectionRow = Boolean(rowResult?.presentation?.section) || /@section\b/i.test(rowText);
    const isHeroRow = Boolean(rowResult?.presentation?.hero || rowResult?.presentation?.mode === "hero");
    const clearLastActionSoon = /* @__PURE__ */ __name(() => {
      setTimeout(() => setLastAction(null), 7e3);
    }, "clearLastActionSoon");
    useEffect46(() => {
      if (hasSelection) {
        setIsExpanded(true);
      } else {
        setIsExpanded(false);
      }
    }, [selectedRow, hasSelection]);
    const applyRowText = /* @__PURE__ */ __name((nextText, label) => {
      if (!hasSelection || nextText === rowText) return;
      Action.updateRowText(calc, selectedRow, nextText);
      if (typeof window !== "undefined" && window.InstacalcEvents && calc?.id) {
        window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_MUTATION, {
          calcId: calc.id,
          type: "decoratorApplied",
          forceRecalc: true
        });
      }
      setLastAction(label);
      clearLastActionSoon();
    }, "applyRowText");
    const insertSectionRow = /* @__PURE__ */ __name((withCol2 = false) => {
      if (!hasSelection) return;
      const sectionText = `// @section(Section)${withCol2 ? " @col2" : ""}`;
      const updatedCalc = Action.insertRow(calc, selectedRow, { focus: true });
      Action.updateRowText(updatedCalc, selectedRow, sectionText);
      if (typeof window !== "undefined" && window.InstacalcEvents && calc?.id) {
        window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_MUTATION, {
          calcId: calc.id,
          type: "sectionInserted",
          forceRecalc: true
        });
      }
      setSelectedRow(selectedRow);
      setLastAction("Inserted section");
      clearLastActionSoon();
    }, "insertSectionRow");
    const applyDecoratorToSelected = /* @__PURE__ */ __name((decorator, label) => {
      const nextText = appendDecorator(rowText, decorator);
      applyRowText(nextText, label);
    }, "applyDecoratorToSelected");
    const applyCol2 = /* @__PURE__ */ __name(() => {
      if (!hasSelection) return;
      if (isSectionRow) {
        applyDecoratorToSelected("@col2", "Added @col2");
        return;
      }
      insertSectionRow(true);
    }, "applyCol2");
    const applyHero = /* @__PURE__ */ __name(() => applyDecoratorToSelected("@hero", "Made hero"), "applyHero");
    const applyWide = /* @__PURE__ */ __name(() => applyDecoratorToSelected("@wide", "Made wide"), "applyWide");
    const applyIcon = /* @__PURE__ */ __name(() => applyDecoratorToSelected("@icon(sparkles)", "Added icon"), "applyIcon");
    const applyNote = /* @__PURE__ */ __name(() => applyDecoratorToSelected("@note(Short note)", "Added note"), "applyNote");
    const chips = [
      { id: "hero", label: "Make Hero", action: applyHero, disabled: !hasSelection || isSectionRow || isHeroRow },
      { id: "section", label: "Add Section", action: /* @__PURE__ */ __name(() => insertSectionRow(false), "action"), disabled: !hasSelection },
      { id: "col2", label: "Two-Column Section", action: applyCol2, disabled: !hasSelection },
      { id: "wide", label: "Full Width", action: applyWide, disabled: !hasSelection || isSectionRow },
      { id: "icon", label: "Add Icon", action: applyIcon, disabled: !hasSelection || isSectionRow },
      { id: "note", label: "Add Note", action: applyNote, disabled: !hasSelection || isSectionRow }
    ];
    return /* @__PURE__ */ (0, import_jsx_runtime91.jsxs)(import_jsx_runtime91.Fragment, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime91.jsxs)("div", { className: "rounded-xl border border-[var(--border-soft)] bg-[color-mix(in_srgb,var(--bg)_85%,transparent)] backdrop-blur-md shadow-sm px-2.5 py-2", children: [
        /* @__PURE__ */ (0, import_jsx_runtime91.jsxs)("div", { className: "flex items-center justify-between gap-2 pb-2 mb-2 border-b border-soft", children: [
          /* @__PURE__ */ (0, import_jsx_runtime91.jsxs)(
            "button",
            {
              onClick: () => setBeautifyOpen(true),
              className: cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg",
                "text-xs font-semibold",
                "border bg-accent-soft",
                "text-theme-accent border-accent hover:bg-accent-hover",
                "transition-colors"
              ),
              children: [
                Icon_default.sparkles("w-3.5 h-3.5"),
                /* @__PURE__ */ (0, import_jsx_runtime91.jsx)("span", { children: "Beautify Calc" })
              ]
            }
          ),
          /* @__PURE__ */ (0, import_jsx_runtime91.jsx)("span", { className: "text-[10px] text-theme-text-muted", children: "Auto-add decorators" })
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime91.jsxs)("div", { className: "flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ (0, import_jsx_runtime91.jsxs)("div", { className: "flex items-center gap-2 min-w-0", children: [
            /* @__PURE__ */ (0, import_jsx_runtime91.jsx)("span", { className: "text-[10px] font-semibold text-theme-text-muted px-1.5 py-0.5 rounded-full border border-soft", children: hasSelection ? `R${selectedRow + 1}` : "R\u2014" }),
            /* @__PURE__ */ (0, import_jsx_runtime91.jsx)("span", { className: "text-[11px] text-theme-text font-medium truncate", children: getRowLabel(calc, results, selectedRow) })
          ] }),
          /* @__PURE__ */ (0, import_jsx_runtime91.jsxs)("div", { className: "flex items-center gap-2", children: [
            lastAction && /* @__PURE__ */ (0, import_jsx_runtime91.jsx)(
              "button",
              {
                onClick: () => Action.undo(),
                className: "text-[10px] font-semibold text-theme-accent hover:underline",
                children: "Undo"
              }
            ),
            !hasSelection && /* @__PURE__ */ (0, import_jsx_runtime91.jsx)("span", { className: "text-[10px] font-semibold text-theme-text-muted", children: "Select a row" })
          ] })
        ] }),
        isExpanded && hasSelection && /* @__PURE__ */ (0, import_jsx_runtime91.jsx)(import_jsx_runtime91.Fragment, { children: /* @__PURE__ */ (0, import_jsx_runtime91.jsx)("div", { className: "mt-2 flex gap-1.5 overflow-x-auto pb-1", children: chips.map((chip) => /* @__PURE__ */ (0, import_jsx_runtime91.jsx)(
          "button",
          {
            onClick: chip.action,
            className: cn(CHIP_BASE_CLASS, chip.disabled && CHIP_DISABLED_CLASS, "whitespace-nowrap text-[11px] px-2 py-1"),
            children: chip.label
          },
          chip.id
        )) }) })
      ] }),
      beautifyOpen && (() => {
        const BeautifyModal = getBeautifyModal();
        return BeautifyModal ? /* @__PURE__ */ (0, import_jsx_runtime91.jsx)(
          BeautifyModal,
          {
            isOpen: beautifyOpen,
            onClose: () => setBeautifyOpen(false)
          }
        ) : null;
      })()
    ] });
  }
  __name(PreviewBuilderPanel, "PreviewBuilderPanel");
  function PreviewPanelPortal() {
    const { calc, results, selectedRow, setSelectedRow } = useCalc();
    const [isActive, setIsActive] = useState48(false);
    const [mountPoint, setMountPoint] = useState48(null);
    const [actionsMount, setActionsMount] = useState48(null);
    const [titleMount, setTitleMount] = useState48(null);
    const rowRefs = useRef33({});
    useEffect46(() => {
      const checkPanelState = /* @__PURE__ */ __name(() => {
        const store = window.Alpine?.store?.("ui");
        const active = store?.activePanel === "preview";
        setIsActive(active);
        if (active) {
          setTimeout(() => {
            const el = document.getElementById("preview-panel-mount");
            const actionsEl = document.getElementById("preview-panel-actions");
            const titleEl = document.getElementById("preview-panel-title");
            setMountPoint(el);
            setActionsMount(actionsEl);
            setTitleMount(titleEl);
          }, 50);
        } else {
          setMountPoint(null);
          setActionsMount(null);
          setTitleMount(null);
        }
      }, "checkPanelState");
      checkPanelState();
      const interval = setInterval(checkPanelState, 500);
      return () => {
        clearInterval(interval);
      };
    }, []);
    if (!isActive || !mountPoint || !calc || !results) {
      return null;
    }
    const presentTheme = getPresentTheme(calc);
    return /* @__PURE__ */ (0, import_jsx_runtime91.jsxs)(import_jsx_runtime91.Fragment, { children: [
      titleMount && react_dom_global_default.createPortal(
        /* @__PURE__ */ (0, import_jsx_runtime91.jsx)("h3", { className: "text-sm font-semibold text-theme-text truncate", children: calc?.name || "Untitled" }),
        titleMount
      ),
      actionsMount && react_dom_global_default.createPortal(
        /* @__PURE__ */ (0, import_jsx_runtime91.jsx)(
          PreviewBuilderPanel,
          {
            calc,
            results,
            selectedRow,
            setSelectedRow
          }
        ),
        actionsMount
      ),
      react_dom_global_default.createPortal(
        /* @__PURE__ */ (0, import_jsx_runtime91.jsx)(
          "div",
          {
            "data-present-stack-style": calc?.presentationStackStyle || "tint",
            "data-present-theme": presentTheme?.id || "soft",
            style: presentTheme?.vars || {},
            className: "contents",
            children: /* @__PURE__ */ (0, import_jsx_runtime91.jsx)(
              PresentModeRows_default,
              {
                calc,
                results,
                rowRefs,
                viewMode: "present",
                selectedRow,
                onSelectRow: setSelectedRow,
                focusRow: () => {
                },
                onTextChange: () => {
                },
                deleteRow: () => {
                },
                insertRow: () => {
                },
                stableRowClassName: "",
                stableRowStyle: {},
                stableRowClassNames: {},
                forceDesktop: true
              }
            )
          }
        ),
        mountPoint
      )
    ] });
  }
  __name(PreviewPanelPortal, "PreviewPanelPortal");
  PreviewPanelPortal.displayName = "PreviewPanelPortal";
  var PreviewPanelPortal_default = PreviewPanelPortal;

  // public/src/react/components/layouts/EditorLayout.jsx
  var import_jsx_runtime92 = __toESM(require_jsx_runtime());
  function EditorLayout({
    calc,
    viewMode: viewModeProp,
    minimal = false,
    rerenderKey,
    currenciesLoaded,
    instaCalcProps,
    newCalcButtonProps,
    calcHolderSelector,
    calcToolbarSelector,
    selectedId,
    calcToolbarProps,
    calcInfoProps,
    shareMenuProps,
    isUrlCalcView
  }) {
    if (!calc) return null;
    const isUrlDefined = import_CalcType6.default.isUrlDefined(calc?.type);
    const isSharedView = calc?.type === import_CalcType6.default.SHARED_VIEW;
    const isPublishedCalc = calc?.published?.id || isUrlDefined;
    const viewMode = viewModeProp || (isPublishedCalc || isSharedView ? "public" : "calc");
    return /* @__PURE__ */ (0, import_jsx_runtime92.jsxs)(CalcProvider, { calcId: calc?.id, viewMode, children: [
      /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(Portal3, { selector: calcHolderSelector, children: /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(CalcLoadingBoundary_default, { children: /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(
        InstaCalc_default,
        {
          currenciesLoaded,
          ...instaCalcProps,
          mobileNewCalcButtonProps: newCalcButtonProps
        },
        `${rerenderKey}-${calc?.id}`
      ) }) }),
      /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(Island, { target: "IdentityBar", children: viewMode === "public" && /* @__PURE__ */ (0, import_jsx_runtime92.jsxs)("div", { className: "flex items-center justify-between w-full", children: [
        /* @__PURE__ */ (0, import_jsx_runtime92.jsxs)("div", { className: "flex items-center gap-3", children: [
          /* @__PURE__ */ (0, import_jsx_runtime92.jsx)("img", { src: "/img/icons/svg/instacalc.logo.svg", alt: "Instacalc", className: "w-6 h-6 ic-logo" }),
          /* @__PURE__ */ (0, import_jsx_runtime92.jsxs)("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ (0, import_jsx_runtime92.jsx)("h1", { className: "text-lg font-bold truncate max-w-[300px] text-theme-text leading-tight", children: calc?.name }),
            /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(CalcURLPreview_default, { calc, viewMode })
          ] })
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime92.jsx)("div", { className: "flex items-center gap-4", children: /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(sharebar_default, { calc, viewMode }) })
      ] }) }),
      /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(Island, { target: "MobileCalcToolbar", children: /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(
        CalcToolbar_default,
        {
          selected: selectedId,
          isMobile: "mobile",
          calcViewMode: viewMode,
          ...calcToolbarProps
        },
        rerenderKey
      ) }),
      !minimal && /* @__PURE__ */ (0, import_jsx_runtime92.jsxs)(import_jsx_runtime92.Fragment, { children: [
        /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(Island, { target: "CalcInfo", children: /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(
          CalcInfo_default,
          {
            ...calcInfoProps
          },
          rerenderKey
        ) }),
        /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(Island, { target: "ShareButton", children: (el) => {
          const variant = el?.getAttribute?.("data-variant") || el?.getAttribute?.("variant") || void 0;
          return /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(
            ShareMenu_default,
            {
              variant,
              ...shareMenuProps
            },
            `${rerenderKey}-${variant || "default"}`
          );
        } }),
        !isUrlCalcView && /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(Island, { target: "SingleCalcActions", children: /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(SingleCalcActions_default, {}, `single-actions-${rerenderKey}`) }),
        /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(CalcModals_default, {}, `modals-${rerenderKey}`)
      ] }),
      /* @__PURE__ */ (0, import_jsx_runtime92.jsx)(PreviewPanelPortal_default, {})
    ] }, `calc-provider-${calc?.id}`);
  }
  __name(EditorLayout, "EditorLayout");
  function arePropsEqual(prevProps, nextProps) {
    return prevProps.calc?.id === nextProps.calc?.id && prevProps.viewMode === nextProps.viewMode && prevProps.minimal === nextProps.minimal && prevProps.currenciesLoaded === nextProps.currenciesLoaded && prevProps.selectedId === nextProps.selectedId && prevProps.isUrlCalcView === nextProps.isUrlCalcView;
  }
  __name(arePropsEqual, "arePropsEqual");
  var EditorLayout_default = react_global_default.memo(EditorLayout, arePropsEqual);

  // public/src/react/components/layouts/IslandPortals.jsx
  init_react_global();

  // public/src/react/hooks/useDOMAttributes.js
  init_react_global();
  function parseBool(value, defaultValue = false) {
    if (value === void 0 || value === null) return defaultValue;
    if (value === "" || value === true) return true;
    const normalized = String(value).toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
    return Boolean(value);
  }
  __name(parseBool, "parseBool");
  function getDOMAttribute(el, names) {
    if (!el || !names) return null;
    const nameList = Array.isArray(names) ? names : [names];
    for (const name of nameList) {
      const value = el.getAttribute ? el.getAttribute(name) : el[name];
      if (value !== null && value !== void 0) return value;
    }
    return void 0;
  }
  __name(getDOMAttribute, "getDOMAttribute");

  // public/src/react/components/layouts/IslandPortals.jsx
  var import_jsx_runtime93 = __toESM(require_jsx_runtime());
  function arePropsEqual2(prevProps, nextProps) {
    return prevProps.selected === nextProps.selected && prevProps.rerenderKey === nextProps.rerenderKey && prevProps.calcs === nextProps.calcs && prevProps.onSelect === nextProps.onSelect;
  }
  __name(arePropsEqual2, "arePropsEqual");
  var IslandPortals = react_global_default.memo(/* @__PURE__ */ __name(function IslandPortals2({
    calcs,
    selected,
    onSelect,
    rerenderKey,
    calcListSelector,
    calcListProps,
    userInfoProps,
    newCalcButtonProps
  }) {
    return /* @__PURE__ */ (0, import_jsx_runtime93.jsxs)(import_jsx_runtime93.Fragment, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(Island, { target: calcListSelector, children: (el) => {
        const showLoginAttr = getDOMAttribute(el, ["showLogin", "showlogin", "data-show-login"]);
        const showNewButtonAttr = getDOMAttribute(el, ["showNewButton", "shownewbutton", "data-show-new-button"]);
        const titleAttr = getDOMAttribute(el, ["title", "data-title"]);
        const leftActionSelector = getDOMAttribute(el, ["leftActionSelector", "data-left-action"]);
        const leftActionElement = leftActionSelector ? /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(DOMSlot, { selector: leftActionSelector }) : null;
        return /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(
          CalcList_default,
          {
            calcs,
            selected,
            onSelect,
            showLogin: parseBool(showLoginAttr, true),
            showNewButton: parseBool(showNewButtonAttr, true),
            title: titleAttr,
            leftAction: leftActionElement,
            loginProps: {
              loginText: "Log In",
              loginClassName: "text-xs text-theme-text-secondary hover:text-theme-accent transition-colors whitespace-nowrap"
            },
            ...calcListProps
          },
          rerenderKey
        );
      } }),
      /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(Island, { target: "MobileCalcList", children: /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(
        CalcList_default,
        {
          calcs,
          selected,
          onSelect: (id) => {
            onSelect(id);
            if (window.InstacalcEvents) {
              window.InstacalcEvents.dispatch(window.InstacalcEvents.MOBILE_CLOSE);
            }
          },
          showLogin: true,
          title: "my calcs",
          loginProps: {
            ...userInfoProps,
            loginText: "Login",
            iconSize: "size-4",
            loginClassName: "px-2 py-1 text-xs rounded-lg border border-soft bg-[var(--bg-secondary-mix-40)] hover:bg-theme-bg-secondary transition-all text-theme-text flex items-center gap-1 whitespace-nowrap"
          },
          ...calcListProps
        },
        rerenderKey
      ) }),
      /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(Island, { target: "UserInfo", children: (el) => {
        const loginText = el?.getAttribute?.("loginText") || el?.getAttribute?.("logintext") || void 0;
        const loginClassName = el?.getAttribute?.("loginClassName") || el?.getAttribute?.("loginclassname") || void 0;
        const loginStyle = el?.getAttribute?.("loginStyle") || el?.getAttribute?.("loginstyle") || void 0;
        return /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(UserInfo_default, { ...userInfoProps, loginText, loginClassName, loginStyle }, rerenderKey);
      } }),
      /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(Island, { target: "NewCalcButton", children: (el) => {
        const attrLabel = getDOMAttribute(el, ["data-label", "label", "data-text", "title"]);
        const attrMobile = parseBool(getDOMAttribute(el, ["data-mobile", "mobile"]), void 0);
        const attrShowIcon = parseBool(getDOMAttribute(el, ["data-icon", "data-show-icon", "icon", "show-icon"]), void 0);
        const resolvedMobile = typeof attrMobile === "boolean" ? attrMobile : typeof newCalcButtonProps.mobile === "boolean" ? newCalcButtonProps.mobile : false;
        const resolvedLabel = attrLabel ?? newCalcButtonProps.label;
        const resolvedShowIcon = typeof attrShowIcon === "boolean" ? attrShowIcon : typeof newCalcButtonProps.showIcon === "boolean" ? newCalcButtonProps.showIcon : true;
        return /* @__PURE__ */ (0, import_jsx_runtime93.jsx)(
          NewCalcButton_default,
          {
            ...newCalcButtonProps,
            mobile: resolvedMobile,
            label: resolvedLabel,
            showIcon: resolvedShowIcon
          },
          `newcalc-${rerenderKey}`
        );
      } })
    ] });
  }, "IslandPortals"), arePropsEqual2);
  var IslandPortals_default = IslandPortals;

  // public/src/react/App.jsx
  var import_jsx_runtime94 = __toESM(require_jsx_runtime());
  var Debug3 = DebugModule3.default || DebugModule3;
  var ViewMode = {
    CALC: "calc",
    TEXT: "text",
    PRESENT: "present"
  };
  Debug3.log("ui", "Module evaluated");
  var App = react_global_default.forwardRef((props, ref) => {
    const uiOverrides = props.uiOverrides || {};
    const {
      instaCalc: instaCalcProps = {},
      calcList: calcListProps = {},
      calcToolbar: calcToolbarProps = {},
      calcInfo: calcInfoProps = {},
      shareMenu: shareMenuProps = {},
      userInfo: userInfoProps = {},
      newCalcButton: newCalcButtonProps = {}
    } = uiOverrides;
    const initialSelection = props.initialCalcId || props.Action.getLocalOption("selected") || null;
    Debug3.log("ui", "INIT - layout:", props.layout, "initialCalcId:", props.initialCalcId, "computed initialSelection:", initialSelection);
    const initialViewMode = (() => {
      try {
        const url = new URL(window.location.href);
        if (url.pathname.includes("/embed")) return "embed";
      } catch (e) {
        Debug3.warn("ui", "Failed to parse view mode from URL:", e);
      }
      return "calc";
    })();
    const [selected, setSelected] = react_global_default.useState(initialSelection);
    const [calcViewMode, setCalcViewMode] = react_global_default.useState(initialViewMode);
    const [currenciesLoaded, setCurrenciesLoaded] = react_global_default.useState(false);
    const [rerenderKey, setRerenderKey] = react_global_default.useState(0);
    const [calcsVersion, setCalcsVersion] = react_global_default.useState(0);
    const externalForceRender = react_global_default.useCallback(() => {
      setRerenderKey((prev) => prev + 1);
    }, []);
    const onCalcDataChange = react_global_default.useCallback(() => {
      setCalcsVersion((prev) => prev + 1);
    }, []);
    const setStateExt = react_global_default.useCallback((next) => {
      if (next.selected !== void 0) setSelected(next.selected);
      if (next.calcViewMode !== void 0) setCalcViewMode(next.calcViewMode);
      if (next.currenciesLoaded !== void 0) setCurrenciesLoaded(next.currenciesLoaded);
      if (next.rerenderKey !== void 0) setRerenderKey(next.rerenderKey);
    }, []);
    const onSelect = react_global_default.useCallback((id) => {
      setSelected(id);
      const url = new URL(window.location.href);
      const viewParam = url.searchParams.get("view");
      if (!viewParam || viewParam === ViewMode.CALC) {
        setCalcViewMode(ViewMode.CALC);
      }
      props.Action.select(id);
    }, [props.Action]);
    const openCalc = react_global_default.useCallback((id) => {
      onSelect(id);
    }, [onSelect]);
    const getCalcInfo = react_global_default.useCallback((selected_id2) => {
      var calcs2 = props.calcs || Calc.allByNewest();
      if (Array.isArray(calcs2)) {
        calcs2 = calcs2.filter((c) => c.id && !c.id.toString().startsWith("wiki-"));
      }
      var calc2 = selected_id2 ? Calc.get(selected_id2) : null;
      if (!calc2) {
        calc2 = calcs2.filter((c) => c.id == selected_id2)[0];
      }
      if (!calc2) {
        calc2 = calcs2[0];
      }
      return {
        calcs: calcs2,
        calc: calc2,
        selected_id: calc2 ? calc2.id : null
      };
    }, [props.calcs, calcsVersion]);
    const renderCalc = react_global_default.useCallback(() => {
      setRerenderKey((k) => k + 1);
    }, []);
    react_global_default.useImperativeHandle(ref, () => ({
      externalForceRender,
      setStateExt,
      openCalc,
      renderCalc
    }));
    react_global_default.useEffect(() => {
      props.Action.loadCurrencies(() => {
        setCurrenciesLoaded(true);
      });
    }, [props.Action]);
    useInstacalcEvent(window.InstacalcEvents?.CALC_CHANGED, onCalcDataChange);
    const handleCalcSwitch = react_global_default.useCallback((event) => {
      const { id, calcId } = event?.detail || {};
      const selectedId = id || calcId;
      Debug3.warn("ui", "handleCalcSwitch triggered. eventId:", selectedId);
      if (selectedId) {
        setSelected((prevSelected) => {
          Debug3.warn("ui", "handleCalcSwitch update. prev:", prevSelected, "next:", selectedId);
          if (prevSelected !== selectedId) {
            Debug3.warn("ui", "Switching view mode to calc because ID changed");
            const url = new URL(window.location.href);
            const viewParam = url.searchParams.get("view");
            if (!viewParam || viewParam === ViewMode.CALC) {
              setCalcViewMode((prev) => prev === ViewMode.CALC ? prev : ViewMode.CALC);
            }
          } else {
            Debug3.warn("ui", "ID matched, keeping view mode");
          }
          return selectedId;
        });
      }
    }, []);
    useInstacalcEvent(window.InstacalcEvents?.CALC_SWITCH, handleCalcSwitch);
    react_global_default.useEffect(() => {
      Debug3.warn("ui", "calcViewMode changed to:", calcViewMode);
    }, [calcViewMode]);
    react_global_default.useEffect(() => {
      var selected_id2 = selected || getCalcInfo().selected_id;
      setSelected(selected_id2);
      if (selected_id2 && props.Action?.select) {
        props.Action.select(selected_id2);
      }
    }, []);
    const prevInitialCalcIdRef = react_global_default.useRef(props.initialCalcId);
    react_global_default.useEffect(() => {
      if (props.initialCalcId && props.initialCalcId !== prevInitialCalcIdRef.current) {
        setSelected(props.initialCalcId);
        prevInitialCalcIdRef.current = props.initialCalcId;
      }
    }, [props.initialCalcId]);
    const calcHolderSelector = props.calcHolderId || "CalcHolder";
    const calcListSelector = props.calcListId || '[data-portal="CalcList"], CalcList, #CalcList';
    const calcToolbarSelector = props.calcToolbarId || "CalcToolbarHolder";
    const { calcs, calc, selected_id } = react_global_default.useMemo(() => {
      const info = getCalcInfo(selected);
      Debug3.log("ui", "getCalcInfo result - selected:", selected, "selected_id:", info.selected_id, "calc:", info.calc?.name, "calc.id:", info.calc?.id, "calcs count:", Object.keys(info.calcs || {}).length);
      return info;
    }, [getCalcInfo, selected]);
    const isUrlCalcView = import_CalcType7.default.isUrlDefined(calc?.type);
    const prevCalcsCountRef = react_global_default.useRef(calcs.length);
    react_global_default.useEffect(() => {
      if (prevCalcsCountRef.current !== calcs.length) {
        prevCalcsCountRef.current = calcs.length;
        window.InstacalcEvents.dispatch(window.InstacalcEvents.CALCS_UPDATED, {
          count: calcs.length
        });
      }
    }, [calcs.length]);
    return /* @__PURE__ */ (0, import_jsx_runtime94.jsx)(ToastProvider, { children: /* @__PURE__ */ (0, import_jsx_runtime94.jsx)(ModalProvider, { children: /* @__PURE__ */ (0, import_jsx_runtime94.jsxs)(ActionProvider, { action: props.Action, children: [
      /* @__PURE__ */ (0, import_jsx_runtime94.jsx)(
        EditorLayout_default,
        {
          calc,
          viewMode: calcViewMode,
          minimal: props.layout === "single",
          rerenderKey,
          currenciesLoaded,
          instaCalcProps,
          newCalcButtonProps,
          calcHolderSelector,
          calcToolbarSelector,
          selectedId: selected_id,
          calcToolbarProps,
          calcInfoProps,
          shareMenuProps,
          isUrlCalcView
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime94.jsx)(
        IslandPortals_default,
        {
          calcs,
          selected: selected_id,
          onSelect,
          rerenderKey,
          calcListSelector,
          calcListProps,
          userInfoProps,
          newCalcButtonProps
        }
      ),
      /* @__PURE__ */ (0, import_jsx_runtime94.jsx)(CalcModals_default, {}, `modals-${rerenderKey}`),
      /* @__PURE__ */ (0, import_jsx_runtime94.jsx)("div", { className: "render-from-app" })
    ] }) }) });
  });
  App.displayName = "App";
  var App_default = App;

  // public/src/react/components/calc/SharedInstaCalc.jsx
  init_react_global();
  init_Icon();
  var import_jsx_runtime95 = __toESM(require_jsx_runtime());
  var { useState: useState49, useEffect: useEffect47, useRef: useRef34 } = react_global_default;
  var sessionPromiseCache = /* @__PURE__ */ new Map();
  function SharedCalcInner({ embed_id, calcViewMode, setTitle, isMobile, footer }) {
    const { calc } = useCalc();
    const [titleSet, setTitleSet] = useState49(false);
    useEffect47(() => {
      if (setTitle && !titleSet && calc?.name) {
        document.title = calc.name + " | InstaCalc Online Calculator";
        setTitleSet(true);
      }
    }, [calc?.name, setTitle, titleSet]);
    return /* @__PURE__ */ (0, import_jsx_runtime95.jsxs)(import_jsx_runtime95.Fragment, { children: [
      /* @__PURE__ */ (0, import_jsx_runtime95.jsx)(CalcLoadingBoundary_default, { mode: "wrap", className: "", children: /* @__PURE__ */ (0, import_jsx_runtime95.jsx)(
        InstaCalc_default,
        {
          calcViewMode,
          footer,
          headerClassName: calcViewMode === "public" ? "mb-6" : ""
        }
      ) }),
      calcViewMode === "public" && /* @__PURE__ */ (0, import_jsx_runtime95.jsxs)(import_jsx_runtime95.Fragment, { children: [
        /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("div", { "data-role": "calctoolbarholder", className: "hidden lg:block", children: /* @__PURE__ */ (0, import_jsx_runtime95.jsx)(CalcToolbar_default, { calcViewMode, isMobile }) }),
        /* @__PURE__ */ (0, import_jsx_runtime95.jsx)(MultiPortal, { selector: '[data-slot="actions-mobile-shared"]', children: /* @__PURE__ */ (0, import_jsx_runtime95.jsx)(sharebar_default, { calc, viewMode: calcViewMode, className: "flex items-center gap-2" }) })
      ] })
    ] });
  }
  __name(SharedCalcInner, "SharedCalcInner");
  function SharedInstaCalc({ Action, embed_id, calcViewMode, setTitle, isMobile, initialDisplayMode }) {
    const [sessionCalcId, setSessionCalcId] = useState49(null);
    useEffect47(() => {
      if (!embed_id) return;
      let promise = sessionPromiseCache.get(embed_id);
      if (!promise) {
        DebugLogger.log("cache", "[SharedInstaCalc] CACHE MISS - Creating new session for:", embed_id);
        promise = Action.createSessionFromEmbed(embed_id);
        sessionPromiseCache.set(embed_id, promise);
      } else {
        DebugLogger.log("cache", "[SharedInstaCalc] CACHE HIT - Reusing session promise for:", embed_id);
      }
      let active = true;
      promise.then((id) => {
        if (active) {
          setSessionCalcId(id);
        }
      }).catch((err) => {
        console.error("Failed to load embed:", err);
        sessionPromiseCache.delete(embed_id);
      });
      return () => {
        active = false;
      };
    }, [embed_id, Action]);
    useEffect47(() => {
      Action.loadCurrencies();
    }, [Action]);
    if (!sessionCalcId) {
      return /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("div", { className: "progress", children: /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("div", { className: "indeterminate" }) });
    }
    const url = `https://instacalc.com/${embed_id}`;
    let footer = null;
    if (calcViewMode === "embed") {
      footer = /* @__PURE__ */ (0, import_jsx_runtime95.jsxs)("div", { className: "flex items-center justify-between embed-footer", children: [
        /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("div", { "data-role": "spacer" }),
        /* @__PURE__ */ (0, import_jsx_runtime95.jsxs)("a", { href: url, target: "_blank", rel: "noreferrer", className: "poweredby flex items-center gap-2 group transition-opacity opacity-70 hover:opacity-100", children: [
          /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("img", { src: "/img/icons/svg/instacalc.logo.svg", alt: "Instacalc", className: "inline-block w-4 h-4 ic-logo transition-all opacity-80 group-hover:opacity-100" }),
          /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("span", { className: "hidden tagline text-[10px] text-theme-text-muted", children: "powered by" }),
          /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("span", { className: "instacalc-title embed-title text-xs font-semibold tracking-wide ic-logo-text opacity-80 group-hover:opacity-100 group-hover:text-theme-text transition-colors", children: "instacalc" })
        ] }),
        /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("a", { href: url, className: "inline-block ml-1 embed-share-icon text-theme-text-muted hover:text-theme-text transition-colors", title: `Open ${url} in a new tab`, target: "_blank", rel: "noreferrer", children: Icon_default.share("w-4 h-4") })
      ] });
    }
    return /* @__PURE__ */ (0, import_jsx_runtime95.jsx)("sharedcalc", { className: calcViewMode, "data-guid": sessionCalcId, children: /* @__PURE__ */ (0, import_jsx_runtime95.jsx)(CalcProvider, { calcId: sessionCalcId, viewMode: calcViewMode, initialDisplayMode, children: /* @__PURE__ */ (0, import_jsx_runtime95.jsx)(
      SharedCalcInner,
      {
        embed_id,
        calcViewMode,
        setTitle,
        isMobile,
        footer
      }
    ) }) });
  }
  __name(SharedInstaCalc, "SharedInstaCalc");
  var SharedInstaCalc_default = SharedInstaCalc;

  // public/src/react/store/CalcStore.js
  var CalcStore = {
    get: /* @__PURE__ */ __name((id) => {
      if (typeof Calc === "undefined") return null;
      return Calc.get(id);
    }, "get"),
    set: /* @__PURE__ */ __name((id, calc) => {
      if (typeof Calc === "undefined") return;
      Calc.set(id, calc);
    }, "set"),
    list: /* @__PURE__ */ __name(() => {
      if (typeof Calc === "undefined") return [];
      return Calc.allByNewest();
    }, "list"),
    delete: /* @__PURE__ */ __name((id) => {
      if (typeof Calc === "undefined") return;
      const calc = Calc.get(id);
      if (calc) {
        calc.deleted = true;
        Calc.set(id, calc);
      }
    }, "delete"),
    create: /* @__PURE__ */ __name((options = {}) => {
      if (typeof Calc === "undefined") return null;
      const calc = Calc.createCalcObject(options);
      Calc.set(calc.id, calc);
      return calc;
    }, "create"),
    subscribe: /* @__PURE__ */ __name((callback) => {
      if (typeof Calc === "undefined") return;
      Calc.sub(callback);
    }, "subscribe"),
    update: /* @__PURE__ */ __name((id, props) => {
      if (typeof Calc === "undefined") return;
      Calc.merge(id, props);
    }, "update"),
    // ==================== SYNC METHODS (delegate to main CalcStore) ====================
    // These require window.calcStore to be initialized (guaranteed by bundle order).
    /**
     * User edited locally → save + sync to Firebase + dispatch event
     */
    applyLocalEdit: /* @__PURE__ */ __name((calc) => {
      if (!window.calcStore) throw new Error("CalcStore not initialized");
      return window.calcStore.applyLocalEdit(calc);
    }, "applyLocalEdit"),
    /**
     * Remote update arrived → save + dispatch event (no sync back)
     */
    applyRemoteUpdate: /* @__PURE__ */ __name((localCalc, remoteData) => {
      if (!window.calcStore) throw new Error("CalcStore not initialized");
      return window.calcStore.applyRemoteUpdate(localCalc, remoteData);
    }, "applyRemoteUpdate"),
    /**
     * Initial load from remote → save only (no sync, no event)
     */
    loadFromRemote: /* @__PURE__ */ __name((calc) => {
      if (!window.calcStore) throw new Error("CalcStore not initialized");
      return window.calcStore.loadFromRemote(calc);
    }, "loadFromRemote"),
    /**
     * Subscribe to real-time Firebase changes
     */
    subscribeToRemote: /* @__PURE__ */ __name((sourceType, identifier, localId) => {
      if (!window.calcStore) throw new Error("CalcStore not initialized");
      return window.calcStore.subscribeToRemote(sourceType, identifier, localId);
    }, "subscribeToRemote"),
    /**
     * Get session ID for echo prevention
     */
    get sessionId() {
      if (!window.calcStore) return null;
      return window.calcStore.sessionId;
    }
  };
  var CalcStore_default = CalcStore;

  // public/src/react/hooks/useDebounce.js
  init_react_global();
  var { useState: useState50, useEffect: useEffect48, useRef: useRef35, useCallback: useCallback30 } = react_global_default;
  function useDebounce(value, delay = 500) {
    const [debouncedValue, setDebouncedValue] = useState50(value);
    useEffect48(() => {
      const timeoutId = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        clearTimeout(timeoutId);
      };
    }, [value, delay]);
    return debouncedValue;
  }
  __name(useDebounce, "useDebounce");
  function useDebouncedCallback(callback, delay = 500) {
    const timeoutRef = useRef35(null);
    const callbackRef = useRef35(callback);
    useEffect48(() => {
      callbackRef.current = callback;
    }, [callback]);
    useEffect48(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);
    return useCallback30((...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }, [delay]);
  }
  __name(useDebouncedCallback, "useDebouncedCallback");

  // public/src/instacalc.v2.jsx
  var import_react6 = __toESM(require_react());
  if (typeof window !== "undefined") {
    window.React = import_react6.default;
    window.ReactDOM = react_dom_global_default;
  }
  var instacalc_v2_default = {
    App: App_default,
    Shared: SharedInstaCalc_default,
    // Keep "Shared" for backward compatibility with web.js
    SharedInstaCalc: SharedInstaCalc_default,
    InstaCalc: InstaCalc_default,
    // Direct access for Router.mount
    UserInfo: UserInfo_default,
    NewCalcButton: NewCalcButton_default,
    CalcEditor: InstaCalc_default,
    CalcStore: CalcStore_default,
    ShareMenu: ShareMenu_default,
    RemixButton
  };
  return __toCommonJS(instacalc_v2_exports);
})();
/*! Bundled license information:

react/cjs/react.production.min.js:
  (**
   * @license React
   * react.production.min.js
   *
   * Copyright (c) Facebook, Inc. and its affiliates.
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE file in the root directory of this source tree.
   *)

react/cjs/react-jsx-runtime.production.min.js:
  (**
   * @license React
   * react-jsx-runtime.production.min.js
   *
   * Copyright (c) Facebook, Inc. and its affiliates.
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE file in the root directory of this source tree.
   *)

scheduler/cjs/scheduler.production.min.js:
  (**
   * @license React
   * scheduler.production.min.js
   *
   * Copyright (c) Facebook, Inc. and its affiliates.
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE file in the root directory of this source tree.
   *)

react-dom/cjs/react-dom.production.min.js:
  (**
   * @license React
   * react-dom.production.min.js
   *
   * Copyright (c) Facebook, Inc. and its affiliates.
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE file in the root directory of this source tree.
   *)
*/
;
var __IC = (() => {
  var __create = Object.create;
  var __defProp = Object.defineProperty;
  var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
  var __getOwnPropNames = Object.getOwnPropertyNames;
  var __getProtoOf = Object.getPrototypeOf;
  var __hasOwnProp = Object.prototype.hasOwnProperty;
  var __require = /* @__PURE__ */ ((x) => typeof require !== "undefined" ? require : typeof Proxy !== "undefined" ? new Proxy(x, {
    get: (a, b) => (typeof require !== "undefined" ? require : a)[b]
  }) : x)(function(x) {
    if (typeof require !== "undefined") return require.apply(this, arguments);
    throw Error('Dynamic require of "' + x + '" is not supported');
  });
  var __esm = (fn, res) => function __init() {
    return fn && (res = (0, fn[__getOwnPropNames(fn)[0]])(fn = 0)), res;
  };
  var __commonJS = (cb, mod) => function __require2() {
    return mod || (0, cb[__getOwnPropNames(cb)[0]])((mod = { exports: {} }).exports, mod), mod.exports;
  };
  var __export = (target, all) => {
    for (var name in all)
      __defProp(target, name, { get: all[name], enumerable: true });
  };
  var __copyProps = (to, from, except, desc) => {
    if (from && typeof from === "object" || typeof from === "function") {
      for (let key of __getOwnPropNames(from))
        if (!__hasOwnProp.call(to, key) && key !== except)
          __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
    }
    return to;
  };
  var __toESM = (mod, isNodeMode, target) => (target = mod != null ? __create(__getProtoOf(mod)) : {}, __copyProps(
    // If the importer is in node compatibility mode or this is not an ESM
    // file that has been converted to a CommonJS file using a Babel-
    // compatible transform (i.e. "__esModule" has not been set), then set
    // "default" to the CommonJS "module.exports" for node compatibility.
    isNodeMode || !mod || !mod.__esModule ? __defProp(target, "default", { value: mod, enumerable: true }) : target,
    mod
  ));
  var __toCommonJS = (mod) => __copyProps(__defProp({}, "__esModule", { value: true }), mod);

  // public/src/utils/timestamps.js
  var timestamps_exports = {};
  __export(timestamps_exports, {
    compare: () => compare,
    default: () => timestamps_default,
    isNewer: () => isNewer,
    now: () => now,
    parse: () => parse,
    toISO: () => toISO
  });
  function now() {
    return Date.now();
  }
  function parse(ts) {
    if (ts == null) return 0;
    if (typeof ts === "number") return ts;
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === "string") {
      const parsed = Date.parse(ts);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }
  function compare(a, b) {
    return parse(a) - parse(b);
  }
  function isNewer(a, b) {
    return parse(a) > parse(b);
  }
  function toISO(ts) {
    const ms = parse(ts);
    return ms ? new Date(ms).toISOString() : "";
  }
  var timestamps_default;
  var init_timestamps = __esm({
    "public/src/utils/timestamps.js"() {
      timestamps_default = { now, parse, compare, isNewer, toISO };
      if (typeof window !== "undefined") {
        window.Timestamps = { now, parse, compare, isNewer, toISO };
      }
    }
  });

  // public/src/utils/tokenGenerator.js
  var require_tokenGenerator = __commonJS({
    "public/src/utils/tokenGenerator.js"(exports, module2) {
      function generateCollabToken(length = 12) {
        const array = new Uint8Array(Math.ceil(length * 0.75));
        crypto.getRandomValues(array);
        return btoa(String.fromCharCode(...array)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "").slice(0, length);
      }
      function generateSlug(title) {
        if (!title || typeof title !== "string") {
          return "untitled";
        }
        return title.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 50) || "untitled";
      }
      function isValidSlug(slug) {
        if (!slug || typeof slug !== "string") return false;
        return /^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$|^[a-z0-9]$/.test(slug);
      }
      async function checkSlugAvailable(slug) {
        if (!slug) return false;
        const baseUrl = typeof window !== "undefined" && window.FirebasePaths ? window.FirebasePaths.getWikiUrl(slug) : `https://instacalc.firebaseio.com/wiki/${slug}.json`;
        try {
          const response = await fetch(baseUrl);
          const data = await response.json();
          return data === null;
        } catch (err) {
          console.error("Error checking slug availability:", err);
          return false;
        }
      }
      async function generateUniqueSlug(baseSlug, maxAttempts = 10) {
        if (await checkSlugAvailable(baseSlug)) {
          return baseSlug;
        }
        for (let i = 2; i <= maxAttempts + 1; i++) {
          const candidate = `${baseSlug}-${i}`;
          if (candidate.length <= 50 && await checkSlugAvailable(candidate)) {
            return candidate;
          }
        }
        const randomSuffix = generateCollabToken(4).toLowerCase();
        const randomCandidate = `${baseSlug.slice(0, 44)}-${randomSuffix}`;
        if (await checkSlugAvailable(randomCandidate)) {
          return randomCandidate;
        }
        return null;
      }
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = {
          generateCollabToken,
          generateSlug,
          isValidSlug,
          checkSlugAvailable,
          generateUniqueSlug
        };
      }
      if (typeof window !== "undefined") {
        window.TokenGenerator = {
          generateCollabToken,
          generateSlug,
          isValidSlug,
          checkSlugAvailable,
          generateUniqueSlug
        };
      }
    }
  });

  // public/src/util.js
  var require_util = __commonJS({
    "public/src/util.js"() {
      window.Util = {};
      Util.cache = {};
      Util.timeSince = function(date) {
        var seconds = Math.floor((/* @__PURE__ */ new Date() - date) / 1e3);
        var interval = Math.floor(seconds / 31536e3);
        if (interval > 1) {
          return interval + " years";
        }
        interval = Math.floor(seconds / 2592e3);
        if (interval > 1) {
          return interval + " months";
        }
        interval = Math.floor(seconds / 86400);
        if (interval > 1) {
          return interval + " days";
        }
        interval = Math.floor(seconds / 3600);
        if (interval > 1) {
          return interval + " hours";
        }
        interval = Math.floor(seconds / 60);
        if (interval > 1) {
          return interval + " minutes";
        }
        return Math.floor(seconds) + " seconds";
      };
      Util.displayDate = function(date) {
        var cached = Util.cache[date];
        if (cached) {
          return cached;
        }
        if (typeof date === "undefined" || !date) {
          return null;
        }
        var dstring = new Date(date).toLocaleDateString([], {
          day: "numeric",
          month: "2-digit",
          year: "2-digit"
        });
        var diff = /* @__PURE__ */ new Date() - new Date(date);
        if (diff < 10 * 60 * 60 * 1e3) {
          dstring = new Date(date).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit"
          });
        }
        Util.cache[date] = dstring;
        return Util.cache[date];
      };
      Util.parseQuery = function(qstr) {
        var query = {};
        var a = qstr.substr(1).split("&");
        for (var i = 0; i < a.length; i++) {
          var b = a[i].split("=");
          query[decodeURIComponent(b[0])] = decodeURIComponent(b[1] || "");
        }
        return query;
      };
      Util.parseLegacyCalc = function(uri) {
        var query = Util.parseQuery(uri);
        try {
          var data = atob(query.c.replace(/\*/g, "+").replace(/-/g, "/"));
        } catch (e) {
          console.log("Error decoding: " + uri);
          return null;
        }
        var settings = query.s || "";
        var rows = data.split("|").map(function(text, i) {
          return { text, hidden: settings[i] == "h" };
        });
        return Calc.createCalcObject({ name: "InstaCalc", rows });
      };
      Util.guid = function() {
        if (typeof window !== "undefined" && window.CalcId?.generateUUID) {
          return window.CalcId.generateUUID();
        }
        function S4() {
          return ((1 + Math.random()) * 65536 | 0).toString(16).substring(1);
        }
        return (S4() + S4() + "-" + S4() + "-4" + S4().substr(0, 3) + "-" + S4() + "-" + S4() + S4() + S4()).toLowerCase();
      };
      Util.debounce = function(func, wait, immediate) {
        var timeout;
        return function() {
          var context = this;
          var args = arguments;
          var later = function() {
            timeout = null;
            if (!immediate) {
              func.apply(context, args);
            }
          };
          var callNow = immediate && !timeout;
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
          if (callNow) {
            func.apply(context, args);
          }
        };
      };
      Util.safeDecodeURI = function(str) {
        if (!str) return "";
        try {
          return decodeURIComponent(str);
        } catch (e) {
          const fixed = str.replace(/%(?![0-9a-fA-F]{2})/g, "%25");
          try {
            return decodeURIComponent(fixed);
          } catch (e2) {
            console.warn("[Util] Could not decode URI even after fix:", str);
            return str;
          }
        }
      };
      Util.encodeFriendly = function(str) {
        if (!str) return "";
        const escaped = str.replace(/_/g, "__");
        const encoded = encodeURI(escaped);
        return encoded.replace(/%20/g, "_").replace(/%7C/g, "|").replace(/%3D/g, "=").replace(/%2B/g, "+").replace(/%3B/g, ";").replace(/#/g, "%23").replace(/\?/g, "%3F");
      };
      Util.decodeFriendly = function(str) {
        if (!str) return "";
        const PLACEHOLDER = "%%US%%";
        let safeStr = str.replace(/__/g, PLACEHOLDER);
        safeStr = safeStr.replace(/_/g, " ");
        safeStr = safeStr.replace(new RegExp(PLACEHOLDER, "g"), "_");
        return Util.safeDecodeURI(safeStr);
      };
      Util.smartFetch = function(url, options) {
        let fetchUrl = url;
        if (url && !url.startsWith("/") && !url.match(/^https?:/)) {
          fetchUrl = "/" + url;
        }
        return fetch(fetchUrl, options).then(function(response) {
          if (!response.ok) {
            throw new Error("HTTP error! status: " + response.status);
          }
          return response.text();
        }).then(function(text) {
          if (text.includes("<!DOCTYPE html>") || text.includes("<title>Instacalc")) {
            console.error("[Util.smartFetch] Infinite loop detected! Server returned full page for resource: " + fetchUrl);
            throw new Error("Infinite loop detected: Server returned index.html for resource request");
          }
          return text;
        });
      };
    }
  });

  // public/src/utils/DebugToolsStub.js
  var require_DebugToolsStub = __commonJS({
    "public/src/utils/DebugToolsStub.js"() {
      (function() {
        "use strict";
        if (typeof window !== "undefined" && window.DebugTools?._fullLoaded) {
          return;
        }
        const MAX_PENDING = 100;
        const pending = {
          events: [],
          mutations: [],
          errors: [],
          network: [],
          keys: []
        };
        function pushPending(arr, item) {
          arr.push(item);
          if (arr.length > MAX_PENDING) arr.shift();
        }
        const stubs = {
          _isStub: true,
          EventLogger: {
            logEvent(eventName, payload) {
              pushPending(pending.events, { time: Date.now(), event: eventName, payload });
            },
            getRecentEvents(n = 50) {
              return pending.events.slice(-n);
            },
            clear() {
              pending.events.length = 0;
            }
          },
          MutationHistory: {
            recordMutation(type, before, after) {
              pushPending(pending.mutations, {
                time: Date.now(),
                type,
                calcId: after?.id,
                before: before?.id,
                // Just store IDs in stub mode
                afterRowCount: after?.rows?.length
              });
            },
            getRecent(n = 20) {
              return pending.mutations.slice(-n);
            },
            getHistoryLength() {
              return 0;
            },
            getStateAt() {
              return null;
            },
            clear() {
              pending.mutations.length = 0;
            }
          },
          ErrorCapture: {
            captureError(error, componentStack, source) {
              pushPending(pending.errors, {
                time: Date.now(),
                message: error?.message || String(error),
                stack: error?.stack?.slice(0, 500),
                source
              });
            },
            getRecent(n = 10) {
              return pending.errors.slice(-n);
            },
            getCount() {
              return pending.errors.length;
            },
            clear() {
              pending.errors.length = 0;
            }
          },
          NetworkLogger: {
            logRequest(type, path, duration, success) {
              pushPending(pending.network, { time: Date.now(), type, path, duration, success });
            },
            getRecent(n = 20) {
              return pending.network.slice(-n);
            },
            clear() {
              pending.network.length = 0;
            }
          },
          KeyLogger: {
            logKey(key, handler, action) {
              pushPending(pending.keys, { time: Date.now(), key, handler, action });
            },
            getRecent(n = 30) {
              return pending.keys.slice(-n);
            },
            clear() {
              pending.keys.length = 0;
            }
          },
          SlowRenderTracker: {
            recordRender() {
            },
            // No-op in stub - perf tracking needs full implementation
            getSlowRenders() {
              return [];
            },
            getSlowRenderCount() {
              return 0;
            },
            clear() {
            }
          },
          SWStatus: {
            async getStatus() {
              return { stub: true };
            }
          },
          RowDebugger: {
            getRowDebugInfo() {
              return null;
            }
          },
          DebugSnapshot: {
            capture() {
              return {
                stub: true,
                message: "Full DebugTools not loaded yet. Open debug panel to load.",
                pendingEvents: pending.events.length,
                pendingMutations: pending.mutations.length,
                pendingErrors: pending.errors.length
              };
            },
            copyToClipboard() {
              return 0;
            },
            exportTestCase() {
              return "// DebugTools not loaded";
            }
          },
          // Get pending data (used by full implementation when it loads)
          _getPending() {
            return pending;
          },
          clearAll() {
            for (const arr of Object.values(pending)) {
              arr.length = 0;
            }
          }
        };
        if (typeof window !== "undefined") {
          window.DebugTools = stubs;
          window._debugToolsPending = pending;
          window.addEventListener("error", (event) => {
            stubs.ErrorCapture.captureError(event.error || event.message, null, "window.onerror");
          });
          window.addEventListener("unhandledrejection", (event) => {
            stubs.ErrorCapture.captureError(event.reason, null, "unhandledrejection");
          });
        }
      })();
    }
  });

  // public/src/stores/FirebasePaths.js
  var require_FirebasePaths = __commonJS({
    "public/src/stores/FirebasePaths.js"(exports, module2) {
      var ARCHIVE_THRESHOLD = 5e4;
      var DEFAULT_BASE_URL = "https://instacalc.firebaseio.com";
      var getCalcType = () => {
        if (typeof window !== "undefined" && window.CalcStore?.CALC_TYPE) {
          return window.CalcStore.CALC_TYPE;
        }
        return { LIVE: "live", WIKI: "wiki", PRIVATE: "private" };
      };
      var FirebasePaths = {
        // Exported constant for documentation/testing
        ARCHIVE_THRESHOLD,
        // Mutable base URL for testing
        _baseUrl: DEFAULT_BASE_URL,
        /**
         * Get the Firebase path for a shared calc ID
         * IDs below threshold are in /archive/ (legacy), others in /shared/
         *
         * @param {string|number} id - The calc ID
         * @returns {string} - '/archive/' or '/shared/'
         * @throws {Error} - If ID is not numeric
         */
        getSharedPath(id) {
          const numId = parseInt(id, 10);
          if (isNaN(numId)) {
            throw new Error(`Invalid shared calc ID: ${id}. Must be numeric.`);
          }
          return numId < ARCHIVE_THRESHOLD ? "/archive/" : "/shared/";
        },
        /**
         * Get the full Firebase URL for a shared calc
         *
         * @param {string|number} id - The calc ID
         * @returns {string} - Full URL like 'https://instacalc.firebaseio.com/shared/12345.json'
         */
        getSharedUrl(id) {
          const path = this.getSharedPath(id);
          return `${this._baseUrl}${path}${id}.json`;
        },
        /**
         * Get the Firebase path for a user's calcs
         *
         * @param {string} uid - Firebase user ID
         * @returns {string} - Path like '/users/abc123/calcs/'
         */
        getUserCalcsPath(uid) {
          if (!uid) throw new Error("User ID is required");
          return `/users/${uid}/calcs/`;
        },
        /**
         * Get the Firebase path for publishing a calc
         *
         * @param {string|number} id - The calc ID to publish
         * @returns {string} - Path like '/shared/12345'
         */
        getPublishPath(id) {
          return `/shared/${id}`;
        },
        // ==================== Unified Remote Path ====================
        /**
         * Get the Firebase path for a remote calc based on type and remoteId
         * Single source of truth for all remote calc path resolution.
         *
         * @param {string} type - 'live', 'wiki', 'private', or 'collab'
         * @param {string} remoteId - The slug or token
         * @returns {string|null} - Path like '/wiki/live/slug' or null if invalid
         */
        getRemotePath(type, remoteId) {
          if (!remoteId) return null;
          const CALC_TYPE = getCalcType();
          switch (type) {
            case CALC_TYPE.LIVE:
              return this.getLivePath(remoteId);
            case CALC_TYPE.WIKI:
              return this.getWikiPath(remoteId);
            case CALC_TYPE.PRIVATE:
              return this.getCollabPath(remoteId);
            case "collab":
              return this.getCollabPath(remoteId);
            // CalcType.COLLAB alias
            default:
              return null;
          }
        },
        /**
         * Get the full Firebase URL for a remote calc
         *
         * @param {string} type - CalcStore.CALC_TYPE.LIVE, WIKI, PRIVATE, or 'collab'
         * @param {string} remoteId - The slug or token
         * @returns {string|null} - Full URL or null if invalid
         */
        getRemoteUrl(type, remoteId) {
          if (!remoteId) return null;
          const CALC_TYPE = getCalcType();
          switch (type) {
            case CALC_TYPE.LIVE:
              return this.getLiveUrl(remoteId);
            case CALC_TYPE.WIKI:
              return this.getWikiUrl(remoteId);
            case CALC_TYPE.PRIVATE:
              return this.getCollabUrl(remoteId);
            case "collab":
              return this.getCollabUrl(remoteId);
            // CalcType.COLLAB alias
            default:
              return null;
          }
        },
        // ==================== Wiki Paths ====================
        /**
         * Get the Firebase path for a wiki calc
         *
         * @param {string} slug - The wiki slug (e.g., 'tip-calculator')
         * @returns {string} - Path like '/wiki/tip-calculator'
         */
        getWikiPath(slug) {
          if (!slug) throw new Error("Wiki slug is required");
          return `/wiki/${slug}`;
        },
        /**
         * Get the full Firebase URL for a wiki calc
         *
         * @param {string} slug - The wiki slug
         * @returns {string} - Full URL like 'https://instacalc.firebaseio.com/wiki/tip-calculator.json'
         */
        getWikiUrl(slug) {
          return `${this._baseUrl}/wiki/${slug}.json`;
        },
        // ==================== Collab Paths ====================
        /**
         * Get the Firebase path for a collab (private collaborative) calc
         *
         * @param {string} token - The collab token (e.g., 'xK9mPqR2v4Yz')
         * @returns {string} - Path like '/collab/xK9mPqR2v4Yz'
         */
        getCollabPath(token) {
          if (!token) throw new Error("Collab token is required");
          return `/collab/${token}`;
        },
        /**
         * Get the full Firebase URL for a collab calc
         *
         * @param {string} token - The collab token
         * @returns {string} - Full URL like 'https://instacalc.firebaseio.com/collab/xK9mPqR2v4Yz.json'
         */
        getCollabUrl(token) {
          return `${this._baseUrl}/collab/${token}.json`;
        },
        // ==================== Live Wiki Paths ====================
        /**
         * Get the Firebase path for a live wiki calc
         *
         * @param {string} slug - The wiki slug (e.g., 'grocery-list')
         * @returns {string} - Path like '/wiki/live/grocery-list'
         */
        getLivePath(slug) {
          if (!slug) throw new Error("Live wiki slug is required");
          return `/wiki/live/${slug}`;
        },
        /**
         * Get the full Firebase URL for a live wiki calc
         *
         * @param {string} slug - The wiki slug
         * @returns {string} - Full URL like 'https://instacalc.firebaseio.com/wiki/live/grocery-list.json'
         */
        getLiveUrl(slug) {
          return `${this._baseUrl}/wiki/live/${slug}.json`;
        },
        // ==================== Loadout Paths ====================
        /**
         * Get path for loadouts collection under a calc
         * @param {string} calcId - Remote calc ID (numeric)
         * @returns {string} Firebase path
         */
        getLoadoutsPath(calcId) {
          return `/calcs/${calcId}/loadouts`;
        },
        /**
         * Get path for a specific loadout
         * @param {string} calcId - Remote calc ID
         * @param {string} loadoutId - Loadout ID
         * @returns {string} Firebase path
         */
        getLoadoutPath(calcId, loadoutId) {
          return `/calcs/${calcId}/loadouts/${loadoutId}`;
        },
        // ==================== Test Utilities ====================
        /**
         * Set custom base URL for testing
         * @param {string} url - The base URL to use
         */
        setBaseUrl(url) {
          this._baseUrl = url;
        },
        /**
         * Reset to production base URL
         */
        resetBaseUrl() {
          this._baseUrl = DEFAULT_BASE_URL;
        },
        /**
         * Get current base URL (for debugging/testing)
         * @returns {string}
         */
        getBaseUrl() {
          return this._baseUrl;
        }
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = FirebasePaths;
      }
      if (typeof window !== "undefined") {
        window.FirebasePaths = FirebasePaths;
      }
    }
  });

  // public/src/stores/CalcType.js
  var require_CalcType = __commonJS({
    "public/src/stores/CalcType.js"(exports, module2) {
      var CalcType = Object.freeze({
        // User's own calcs (localStorage + Firebase sync)
        USER: "user",
        // Remote collaborative calcs (session memory + Firebase sync)
        WIKI: "wiki",
        // instacalc.com/slug - curated gallery calc, API fetch
        LIVE: "live",
        // instacalc.com/slug - (deprecated, redirects to /slug)
        COLLAB: "collab",
        // instacalc.com/edit/TOKEN - private link
        // File-synced calcs (localStorage + file sync)
        FILE: "file",
        // Syncs to local .ic file via File System Access API
        // Ephemeral calcs (session memory only, no sync)
        URL_DEFINED: "url_defined",
        // Calc content defined by URL hash (#50+100)
        SHARED_VIEW: "shared_view",
        // Viewing someone's published calc (read-only session copy)
        /**
         * Derive CalcType from syncRef (syncRef is the source of truth)
         * @param {object} calc - Calc object with syncRef
         * @returns {string} - CalcType value
         */
        fromSyncRef(calc) {
          if (!calc) return CalcType.USER;
          if (!calc.syncRef) {
            if (calc.ephemeral || calc.isUrlCalc) return CalcType.URL_DEFINED;
            if (calc.isSharedView) return CalcType.SHARED_VIEW;
            return CalcType.USER;
          }
          if (calc.syncRef.type === "file") {
            return CalcType.FILE;
          }
          if (calc.syncRef.type === "firebase") {
            const path = calc.syncRef.path || "";
            if (path.startsWith("wiki/") && !path.startsWith("wiki/live/")) {
              return CalcType.WIKI;
            }
            if (path.startsWith("live/") || path.startsWith("wiki/live/")) {
              return CalcType.LIVE;
            }
            if (path.startsWith("collab/")) {
              return CalcType.COLLAB;
            }
          }
          return CalcType.USER;
        },
        /**
         * Get storage location for a calc type
         * @param {string} type - CalcType value
         * @returns {'localStorage' | 'session' | 'collab'}
         */
        getStorage(type) {
          switch (type) {
            case CalcType.USER:
            case CalcType.FILE:
              return "localStorage";
            case CalcType.COLLAB:
              return "collab";
            case CalcType.WIKI:
            case CalcType.LIVE:
            case CalcType.URL_DEFINED:
            case CalcType.SHARED_VIEW:
              return "session";
            default:
              return "session";
          }
        },
        /**
         * Check if type should sync to Firebase
         * @param {string} type - CalcType value
         * @returns {boolean}
         */
        shouldSyncToFirebase(type) {
          return [CalcType.USER, CalcType.WIKI, CalcType.LIVE, CalcType.COLLAB].includes(type);
        },
        /**
         * Check if calc syncs to Firebase (wiki, live, or collab)
         * @param {object} calc - Calc object with syncRef or legacy type
         * @returns {boolean}
         */
        isRemote(calc) {
          if (calc?.syncRef) {
            return calc.syncRef.type === "firebase";
          }
          if ([CalcType.WIKI, CalcType.LIVE, CalcType.COLLAB].includes(calc?.type)) {
            return true;
          }
          return false;
        },
        /**
         * Check if calc is a private collab (shared via /edit/TOKEN link)
         * @param {object|string} calcOrType - Calc object or type string
         * @returns {boolean}
         */
        isCollab(calcOrType) {
          if (calcOrType && typeof calcOrType === "object") {
            if (calcOrType.syncRef) {
              return calcOrType.syncRef.path?.startsWith("collab/") ?? false;
            }
            return calcOrType.type === CalcType.COLLAB;
          }
          return calcOrType === CalcType.COLLAB;
        },
        /**
         * Check if calc is a wiki (public, curated)
         * @param {object|string} calcOrType - Calc object or type string
         * @returns {boolean}
         */
        isWiki(calcOrType) {
          if (calcOrType && typeof calcOrType === "object") {
            if (calcOrType.syncRef) {
              const path = calcOrType.syncRef.path;
              if (!path) return false;
              return path.startsWith("wiki/") && !path.startsWith("wiki/live/");
            }
            return calcOrType.type === CalcType.WIKI;
          }
          return calcOrType === CalcType.WIKI;
        },
        /**
         * Check if calc is a live collab
         * @param {object|string} calcOrType - Calc object or type string
         * @returns {boolean}
         */
        isLive(calcOrType) {
          if (calcOrType && typeof calcOrType === "object") {
            if (calcOrType.syncRef) {
              const path = calcOrType.syncRef.path;
              if (!path) return false;
              return path.startsWith("live/") || path.startsWith("wiki/live/");
            }
            return calcOrType.type === CalcType.LIVE;
          }
          return calcOrType === CalcType.LIVE;
        },
        /**
         * Check if calc uses /!slug URL format (wiki or live)
         * @param {object} calc - Calc object with syncRef
         * @returns {boolean}
         */
        isWikiOrLive(calc) {
          return CalcType.isWiki(calc) || CalcType.isLive(calc);
        },
        /**
         * Check if calc is local-only (no sync)
         * @param {object} calc - Calc object with syncRef
         * @returns {boolean}
         */
        isLocal(calc) {
          return !calc?.syncRef;
        },
        /**
         * Check if calc syncs to an external location
         * @param {object} calc - Calc object with syncRef
         * @returns {boolean}
         */
        isSynced(calc) {
          return !!calc?.syncRef;
        },
        /**
         * Check if calc syncs to local file
         * @param {object} calc - Calc object with syncRef
         * @returns {boolean}
         */
        isFile(calc) {
          return calc?.syncRef?.type === "file";
        },
        /**
         * Get the URL path for a calc - SINGLE SOURCE OF TRUTH
         * @param {Object} calc - Calc object with type, remoteId, published, id
         * @returns {string|null} - URL path or null if no URL
         *
         * URL Design (see specs/arch/url-design.md):
         * - Local calcs: null (use #my/<id> hash via UrlManager.localCalcHash())
         * - Shared calcs: /s/<id>
         * - Wiki/live: /<slug>
         * - Collab: /edit/<token>
         */
        getUrlPath(calc) {
          if (!calc) return null;
          if (CalcType.isCollab(calc) && calc.remoteId) {
            return `/edit/${calc.remoteId}`;
          }
          if (CalcType.isWikiOrLive(calc) && calc.remoteId) {
            return `/${calc.remoteId}`;
          }
          if (CalcType.isUserOwned(calc.type)) {
            return null;
          }
          if (calc.published?.id) {
            return `/${calc.published.id}`;
          }
          if (calc.type === CalcType.SHARED_VIEW) {
            const id = calc.sourceId || calc.remoteId;
            if (id) return `/${id}`;
          }
          if (CalcType.isUrlDefined(calc.type)) {
            return null;
          }
          return null;
        },
        /**
         * Check if type is a URL-defined calc (content from URL hash)
         * @param {string} type - CalcType value
         * @returns {boolean}
         */
        isUrlDefined(type) {
          return type === CalcType.URL_DEFINED;
        },
        /**
         * Check if type is ephemeral (no sync, session only)
         * @param {string} type - CalcType value
         * @returns {boolean}
         */
        isEphemeral(type) {
          return CalcType.isUrlDefined(type) || type === CalcType.SHARED_VIEW;
        },
        /**
         * Check if this is the user's own calc (not shared/remote)
         * @param {string} type - CalcType value
         * @returns {boolean}
         */
        isUserOwned(type) {
          return type === CalcType.USER;
        },
        /**
         * Get display label for UI
         * @param {string} type - CalcType value
         * @returns {string}
         */
        getLabel(type) {
          const labels = {
            [CalcType.USER]: "My Calc",
            [CalcType.WIKI]: "Wiki",
            [CalcType.LIVE]: "Live",
            [CalcType.COLLAB]: "Private Link",
            [CalcType.FILE]: "File Synced",
            [CalcType.URL_DEFINED]: "Quick Calc",
            [CalcType.SHARED_VIEW]: "Shared Calc"
          };
          return labels[type] || "Calc";
        },
        /**
         * Get all valid CalcType values
         * @returns {string[]}
         */
        values() {
          return [
            CalcType.USER,
            CalcType.WIKI,
            CalcType.LIVE,
            CalcType.COLLAB,
            CalcType.FILE,
            CalcType.URL_DEFINED,
            CalcType.SHARED_VIEW
          ];
        },
        /**
         * Check if a value is a valid CalcType
         * @param {string} value
         * @returns {boolean}
         */
        isValid(value) {
          return CalcType.values().includes(value);
        },
        // ==========================================================================
        // SYNCREF EXTRACTORS
        // ==========================================================================
        /**
         * Get the wiki slug from a calc
         * @param {object} calc - Calc object with syncRef
         * @returns {string|null}
         */
        getSlug(calc) {
          if (!CalcType.isWiki(calc)) return null;
          return calc.syncRef.path.replace("wiki/", "");
        },
        /**
         * Get the live ID from a calc
         * @param {object} calc - Calc object with syncRef
         * @returns {string|null}
         */
        getLiveId(calc) {
          if (!CalcType.isLive(calc)) return null;
          const path = calc.syncRef.path;
          if (path.startsWith("wiki/live/")) {
            return path.replace("wiki/live/", "");
          }
          return path.replace("live/", "");
        },
        /**
         * Get the collab token from a calc
         * @param {object} calc - Calc object with syncRef
         * @returns {string|null}
         */
        getCollabToken(calc) {
          if (!CalcType.isCollab(calc)) return null;
          return calc.syncRef.path.replace("collab/", "");
        },
        /**
         * Get the filename from a file-synced calc
         * @param {object} calc - Calc object with syncRef
         * @returns {string|null}
         */
        getFilename(calc) {
          if (!CalcType.isFile(calc)) return null;
          return calc.syncRef.path;
        },
        // ==========================================================================
        // SYNCREF CREATORS
        // ==========================================================================
        /**
         * Create a syncRef for wiki
         * @param {string} slug - The wiki slug
         * @returns {object}
         */
        syncRefForWiki(slug) {
          return { type: "firebase", path: `wiki/${slug}` };
        },
        /**
         * Create a syncRef for live collab
         * @param {string} id - The live ID
         * @returns {object}
         */
        syncRefForLive(id) {
          return { type: "firebase", path: `live/${id}` };
        },
        /**
         * Create a syncRef for private collab
         * @param {string} token - The collab token
         * @returns {object}
         */
        syncRefForCollab(token) {
          return { type: "firebase", path: `collab/${token}` };
        },
        /**
         * Create a syncRef for local file
         * @param {string} filename - The filename
         * @returns {object}
         */
        syncRefForFile(filename) {
          return { type: "file", path: filename };
        }
      });
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = CalcType;
      }
      if (typeof window !== "undefined") {
        window.CalcType = CalcType;
      }
    }
  });

  // public/src/stores/ViewContext.js
  var require_ViewContext = __commonJS({
    "public/src/stores/ViewContext.js"(exports, module2) {
      var ViewContext = Object.freeze({
        // Layout structures (which UI components are rendered)
        HOME: "home",
        // Calc list sidebar (expanded) + editor
        SINGLE: "single",
        // Editor with sidebar (collapsed but available)
        EMBED: "embed",
        // Minimal editor only, no chrome
        GALLERY: "gallery",
        // Showcase grid layout, different sidebars
        /**
         * Get initial sidebar state for a context (only used on page load)
         * @param {string} ctx - ViewContext value
         * @returns {'expanded' | 'collapsed'}
         */
        getInitialSidebarState(ctx) {
          return ctx === ViewContext.HOME ? "expanded" : "collapsed";
        },
        /**
         * Derive ViewContext from current URL route
         * Uses UrlManager route detection methods
         * @returns {string} ViewContext value
         */
        fromUrl() {
          const UrlManager = typeof window !== "undefined" ? window.UrlManager : null;
          if (!UrlManager) throw new Error("UrlManager not loaded");
          if (UrlManager.isHomeRoute()) return ViewContext.HOME;
          if (UrlManager.isEmbedRoute()) return ViewContext.EMBED;
          if (UrlManager.isGalleryRoute()) return ViewContext.GALLERY;
          if (UrlManager.isPublishedRoute() || UrlManager.isWikiRoute() || UrlManager.isCollabRoute()) {
            return ViewContext.SINGLE;
          }
          return ViewContext.HOME;
        },
        /**
         * Get all valid ViewContext values
         * @returns {string[]}
         */
        values() {
          return [
            ViewContext.HOME,
            ViewContext.SINGLE,
            ViewContext.EMBED,
            ViewContext.GALLERY
          ];
        },
        /**
         * Check if a value is a valid ViewContext
         * @param {string} value
         * @returns {boolean}
         */
        isValid(value) {
          return ViewContext.values().includes(value);
        },
        /**
         * Check if current URL is a shared/single calc view (sidebar should be collapsed)
         * Consolidates Router.isSharedView() logic into ViewContext
         * @returns {boolean}
         */
        isSharedView() {
          const ctx = ViewContext.fromUrl();
          return ctx === ViewContext.SINGLE || ctx === ViewContext.EMBED;
        },
        /**
         * Check if current URL is home page
         * @returns {boolean}
         */
        isHome() {
          return ViewContext.fromUrl() === ViewContext.HOME;
        },
        /**
         * Check if current URL is a URL-defined (stateless) calc
         * Used for hash-based URL sync decisions
         * @returns {boolean}
         */
        isUrlCalc() {
          if (typeof window === "undefined") return false;
          const path = window.location.pathname;
          if (path === "/new" || path === "/new/") {
            return true;
          }
          if (window.UrlManager?.parseUrl) {
            const result = window.UrlManager.parseUrl(window.location.href);
            return !!result?.isStateless;
          }
          return false;
        }
      });
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = ViewContext;
      }
      if (typeof window !== "undefined") {
        window.ViewContext = ViewContext;
      }
    }
  });

  // public/src/stores/CalcId.js
  var require_CalcId = __commonJS({
    "public/src/stores/CalcId.js"(exports, module2) {
      var getCalcType = () => {
        if (typeof window !== "undefined" && window.CalcType) {
          return window.CalcType;
        }
        if (typeof __require !== "undefined") {
          try {
            return require_CalcType();
          } catch (e) {
            return null;
          }
        }
        return null;
      };
      var CalcId = {
        // ==========================================================================
        // CORE GENERATORS
        // ==========================================================================
        /**
         * Generate a new calc ID based on type
         * This is the ONLY function that should create new calc IDs
         *
         * @param {string} type - CalcType value (USER, WIKI, LIVE, COLLAB, etc.)
         * @param {object} options - Type-specific options
         * @param {string} options.slug - For WIKI/LIVE types
         * @param {string} options.token - For COLLAB type
         * @param {string} options.content - For URL_DEFINED type (hashed)
         * @param {string} options.filename - For FILE type (hashed)
         * @param {string} options.sourceId - For SHARED_VIEW type
         * @returns {string} - The generated ID
         */
        generate(type, options = {}) {
          const CalcType = getCalcType();
          if (!CalcType) {
            return CalcId.generateUUID();
          }
          switch (type) {
            case CalcType.USER:
            case "user":
              return CalcId.generateUUID();
            case CalcType.URL_DEFINED:
            case "url_defined":
              return CalcId.generateUrlId(options.content || "");
            case CalcType.FILE:
            case "file":
              return CalcId.generateFileId(options.filename || options.content || "");
            case CalcType.WIKI:
            case "wiki":
              if (!options.slug) throw new Error("CalcId.generate(WIKI) requires options.slug");
              return CalcId.generateWikiId(options.slug);
            case CalcType.LIVE:
            case "live":
              if (!options.slug) throw new Error("CalcId.generate(LIVE) requires options.slug");
              return CalcId.generateLiveId(options.slug);
            case CalcType.COLLAB:
            case "collab":
              if (!options.token) throw new Error("CalcId.generate(COLLAB) requires options.token");
              return CalcId.generateCollabId(options.token);
            case CalcType.SHARED_VIEW:
            case "shared_view":
              if (!options.sourceId) throw new Error("CalcId.generate(SHARED_VIEW) requires options.sourceId");
              return CalcId.generateSharedViewId(options.sourceId);
            default:
              console.warn(`CalcId.generate: Unknown type "${type}", using UUID`);
              return CalcId.generateUUID();
          }
        },
        // ==========================================================================
        // TYPE-SPECIFIC GENERATORS
        // ==========================================================================
        /**
         * Generate a UUID v4 (for USER calcs)
         * @returns {string}
         */
        generateUUID() {
          if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
          }
          const S4 = () => ((1 + Math.random()) * 65536 | 0).toString(16).substring(1);
          return `${S4()}${S4()}-${S4()}-4${S4().slice(0, 3)}-${S4()}-${S4()}${S4()}${S4()}`.toLowerCase();
        },
        /**
         * Generate a session ID (for Firebase sync tracking)
         * @returns {string}
         */
        generateSessionId() {
          if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
          }
          return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        },
        /**
         * Generate ID for URL-defined calc
         * @param {string} content - The URL content to hash
         * @returns {string}
         */
        generateUrlId(content) {
          if (!content) return "new";
          return `url-${CalcId._hash(content)}`;
        },
        /**
         * Generate ID for file-synced calc
         * @param {string} filename - The filename to hash
         * @returns {string}
         */
        generateFileId(filename) {
          return `file-${CalcId._hash(filename || "untitled")}`;
        },
        /**
         * Generate ID for wiki calc
         * @param {string} slug - The wiki slug
         * @returns {string}
         */
        generateWikiId(slug) {
          return `wiki-${slug}`;
        },
        /**
         * Generate ID for live calc
         * @param {string} slug - The live slug
         * @returns {string}
         */
        generateLiveId(slug) {
          return `live-${slug}`;
        },
        /**
         * Generate ID for collab calc
         * @param {string} token - The collab token
         * @returns {string}
         */
        generateCollabId(token) {
          return `collab-${token}`;
        },
        /**
         * Generate ID for shared view (viewing someone's published calc)
         * @param {string} sourceId - The source calc ID
         * @returns {string}
         */
        generateSharedViewId(sourceId) {
          return `shared_${sourceId}`;
        },
        /**
         * Generate ID for session copy (viewing published calc)
         * @param {string} sourceId - The source calc ID
         * @returns {string}
         */
        generateSessionCopyId(sourceId) {
          return `session_copy_${sourceId}`;
        },
        /**
         * Generate a blank embed ID
         * @returns {string}
         */
        generateBlankEmbedId() {
          return `blank-${CalcId.generateUUID()}`;
        },
        // ==========================================================================
        // COLLAB TOKEN GENERATION (cryptographically secure)
        // ==========================================================================
        /**
         * Generate a secure collab token
         * Uses crypto.getRandomValues for security
         *
         * @param {number} length - Desired token length (default 12)
         * @returns {string} - URL-safe random token
         */
        generateCollabToken(length = 12) {
          if (typeof crypto !== "undefined" && crypto.getRandomValues) {
            const array = new Uint8Array(Math.ceil(length * 0.75));
            crypto.getRandomValues(array);
            const bytes = Array.from(array);
            const base64 = typeof btoa !== "undefined" ? btoa(String.fromCharCode(...bytes)) : Buffer.from(bytes).toString("base64");
            return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "").slice(0, length);
          }
          const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
          let result = "";
          for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          return result;
        },
        // ==========================================================================
        // SLUG GENERATION (human-readable)
        // ==========================================================================
        /**
         * Generate a fun, memorable slug
         * Format: adjective-noun-number (e.g., 'swift-falcon-42')
         *
         * @returns {string}
         */
        generateFunSlug() {
          const adjectives = [
            "happy",
            "swift",
            "bright",
            "calm",
            "clever",
            "eager",
            "fair",
            "gentle",
            "jolly",
            "keen",
            "lively",
            "merry",
            "nice",
            "proud",
            "quick",
            "sharp",
            "smart",
            "sunny",
            "warm",
            "wise",
            "bold",
            "cool",
            "fresh",
            "grand"
          ];
          const nouns = [
            "panda",
            "falcon",
            "comet",
            "river",
            "tiger",
            "eagle",
            "spark",
            "wave",
            "storm",
            "flame",
            "frost",
            "stone",
            "brook",
            "maple",
            "cedar",
            "coral",
            "pearl",
            "ruby",
            "jade",
            "amber",
            "pixel",
            "quark",
            "orbit",
            "prism"
          ];
          const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
          const noun = nouns[Math.floor(Math.random() * nouns.length)];
          const num = Math.floor(Math.random() * 100);
          return `${adj}-${noun}-${num}`;
        },
        /**
         * Generate a URL-safe slug from a title
         *
         * @param {string} title - The title to slugify
         * @returns {string}
         */
        slugify(title) {
          if (!title || typeof title !== "string") {
            return "untitled";
          }
          return title.toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 50) || "untitled";
        },
        // ==========================================================================
        // VALIDATION
        // ==========================================================================
        /**
         * Check if an ID looks like a remote/external ID
         * Used by gatekeeper to prevent localStorage pollution
         *
         * @param {string} id - The ID to check
         * @returns {boolean}
         */
        isRemoteId(id) {
          if (!id || typeof id !== "string") return false;
          return CalcId.REMOTE_ID_PATTERNS.some((pattern) => pattern.test(id));
        },
        /**
         * Check if an ID is a valid UUID
         * @param {string} id
         * @returns {boolean}
         */
        isUUID(id) {
          if (!id || typeof id !== "string") return false;
          return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
        },
        /**
         * Check if a slug is valid
         * @param {string} slug
         * @returns {boolean}
         */
        isValidSlug(slug) {
          if (!slug || typeof slug !== "string") return false;
          return /^[a-z0-9][a-z0-9-]{0,48}[a-z0-9]$|^[a-z0-9]$/.test(slug);
        },
        /**
         * Extract the type prefix from an ID
         * @param {string} id
         * @returns {string|null} - The prefix (wiki, live, collab, url, file, embed, session_copy) or null
         */
        getPrefix(id) {
          if (!id || typeof id !== "string") return null;
          const prefixes = ["wiki-", "live-", "collab-", "url-", "file-", "shared_", "session_copy_", "blank-"];
          for (const prefix of prefixes) {
            if (id.startsWith(prefix)) {
              return prefix.replace(/[-_]$/, "");
            }
          }
          if (/^\d{4,}$/.test(id)) {
            return "numeric";
          }
          return null;
        },
        /**
         * Extract the identifier part from a prefixed ID
         * @param {string} id - e.g., 'wiki-tip-calculator'
         * @returns {string} - e.g., 'tip-calculator'
         */
        getIdentifier(id) {
          if (!id || typeof id !== "string") return id;
          const prefixes = ["wiki-", "live-", "collab-", "url-", "file-", "shared_", "session_copy_", "blank-"];
          for (const prefix of prefixes) {
            if (id.startsWith(prefix)) {
              return id.slice(prefix.length);
            }
          }
          return id;
        },
        // ==========================================================================
        // PATTERNS (for validation)
        // ==========================================================================
        /**
         * Patterns that identify remote/external IDs
         * Used to prevent localStorage pollution
         */
        REMOTE_ID_PATTERNS: [
          /^\d{4,}$/,
          // Pure numeric IDs (Firebase shared calcs)
          /^shared_/,
          // Shared view prefix
          /^embed_/,
          // Legacy embed prefix (backwards compat)
          /^live_/,
          // Live prefix (old format)
          /^live-/,
          // Live prefix (new format)
          /^wiki_/,
          // Wiki prefix (old format)
          /^wiki-/,
          // Wiki prefix (new format)
          /^collab_/,
          // Collab prefix (old format)
          /^collab-/,
          // Collab prefix (new format)
          /^url-/,
          // URL-defined prefix
          /^file-/,
          // File-synced prefix
          /^session_copy_/,
          // Session copy prefix
          /^blank-/
          // Blank embed prefix
        ],
        // ==========================================================================
        // INTERNAL HELPERS
        // ==========================================================================
        /**
         * Simple hash function (djb2)
         * @param {string} str
         * @returns {string}
         * @private
         */
        _hash(str) {
          let h = 5381;
          for (let i = 0; i < str.length; i++) {
            h = (h << 5) + h + str.charCodeAt(i);
          }
          return (h >>> 0).toString();
        }
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = CalcId;
      }
      if (typeof window !== "undefined") {
        window.CalcId = CalcId;
      }
    }
  });

  // public/src/utils/debug.js
  var require_debug = __commonJS({
    "public/src/utils/debug.js"(exports, module2) {
      var Debug9 = typeof window !== "undefined" && window.Debug || {
        // Fallback for SSR/tests - minimal stub
        log: () => {
        },
        warn: () => {
        },
        error: (...args) => console.error(...args),
        group: () => {
        },
        groupEnd: () => {
        },
        time: () => {
        },
        timeEnd: () => {
        },
        isEnabled: () => false,
        toggle: () => false,
        enableAll: () => {
        },
        disableAll: () => {
        },
        categories: []
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = Debug9;
      }
    }
  });

  // public/src/stores/CalcMigration.js
  var require_CalcMigration = __commonJS({
    "public/src/stores/CalcMigration.js"(exports, module2) {
      var CalcMigration = {
        // Bump this when schema changes
        VERSION: 2,
        /**
         * Migrate a single calc to include syncRef
         * @param {object} calc - The calc to migrate
         * @returns {object} - The migrated calc (new object, original unchanged)
         */
        migrate(calc) {
          if (!calc) return calc;
          if (Object.hasOwn(calc, "syncRef")) {
            return calc;
          }
          let syncRef = null;
          const CalcType = typeof window !== "undefined" && window.CalcType || typeof __require !== "undefined" && require_CalcType();
          if (calc.type === CalcType?.WIKI && calc.slug) {
            syncRef = { type: "firebase", path: `wiki/${calc.slug}` };
          } else if (calc.type === CalcType?.WIKI && calc.remoteId) {
            syncRef = { type: "firebase", path: `wiki/${calc.remoteId}` };
          } else if (calc.type === CalcType?.LIVE) {
            const liveId = calc.remoteId || calc.sourceId || calc.id;
            syncRef = { type: "firebase", path: `live/${liveId}` };
          } else if (calc.type === CalcType?.COLLAB) {
            const token = calc.remoteId || calc.id;
            syncRef = { type: "firebase", path: `collab/${token}` };
          }
          return {
            ...calc,
            syncRef
          };
        },
        /**
         * Migrate an array of calcs
         * @param {object[]} calcs - Array of calcs to migrate
         * @returns {object[]} - Array of migrated calcs
         */
        migrateAll(calcs) {
          if (!Array.isArray(calcs)) {
            if (calcs && typeof calcs === "object") {
              const result = {};
              for (const [id, calc] of Object.entries(calcs)) {
                result[id] = this.migrate(calc);
              }
              return result;
            }
            return calcs;
          }
          return calcs.map((c) => this.migrate(c));
        },
        /**
         * Check if a calc needs migration
         * @param {object} calc - The calc to check
         * @returns {boolean}
         */
        needsMigration(calc) {
          return calc && !Object.hasOwn(calc, "syncRef");
        }
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = CalcMigration;
      }
      if (typeof window !== "undefined") {
        window.CalcMigration = CalcMigration;
      }
    }
  });

  // public/src/stores/SyncRef.js
  var require_SyncRef = __commonJS({
    "public/src/stores/SyncRef.js"(exports, module2) {
      var SyncRef = {
        // ==========================================================================
        // CORE CHECKS
        // ==========================================================================
        /**
         * Check if calc is local-only (no sync)
         * @param {object} calc
         * @returns {boolean}
         */
        isLocal(calc) {
          return !calc?.syncRef;
        },
        /**
         * Check if calc syncs to an external location
         * @param {object} calc
         * @returns {boolean}
         */
        isSynced(calc) {
          return !!calc?.syncRef;
        },
        /**
         * Get the sync type (firebase, file, etc.)
         * @param {object} calc
         * @returns {string|null}
         */
        getSyncType(calc) {
          return calc?.syncRef?.type ?? null;
        },
        /**
         * Get the sync path
         * @param {object} calc
         * @returns {string|null}
         */
        getSyncPath(calc) {
          return calc?.syncRef?.path ?? null;
        },
        // ==========================================================================
        // SYNC TYPE CHECKS
        // ==========================================================================
        /**
         * Check if calc syncs to Firebase
         * @param {object} calc
         * @returns {boolean}
         */
        isFirebaseSync(calc) {
          return calc?.syncRef?.type === "firebase";
        },
        /**
         * Check if calc syncs to local file
         * @param {object} calc
         * @returns {boolean}
         */
        isFileSync(calc) {
          return calc?.syncRef?.type === "file";
        },
        // ==========================================================================
        // FIREBASE SUBTYPE CHECKS (derived from path)
        // ==========================================================================
        /**
         * Check if calc is a wiki (public, curated)
         * @param {object} calc
         * @returns {boolean}
         */
        isWiki(calc) {
          return calc?.syncRef?.path?.startsWith("wiki/") && !calc?.syncRef?.path?.startsWith("wiki/live/");
        },
        /**
         * Check if calc is a live collab
         * @param {object} calc
         * @returns {boolean}
         */
        isLive(calc) {
          return calc?.syncRef?.path?.startsWith("live/") || calc?.syncRef?.path?.startsWith("wiki/live/");
        },
        /**
         * Check if calc is a private collab (token-based)
         * @param {object} calc
         * @returns {boolean}
         */
        isCollab(calc) {
          return calc?.syncRef?.path?.startsWith("collab/");
        },
        /**
         * Check if calc uses /!slug URL format (wiki or live)
         * @param {object} calc
         * @returns {boolean}
         */
        isWikiOrLive(calc) {
          return SyncRef.isWiki(calc) || SyncRef.isLive(calc);
        },
        // ==========================================================================
        // CREATE SYNCREFS
        // ==========================================================================
        /**
         * Create a syncRef for wiki
         * @param {string} slug - The wiki slug
         * @returns {object}
         */
        forWiki(slug) {
          return { type: "firebase", path: `wiki/${slug}` };
        },
        /**
         * Create a syncRef for live collab
         * @param {string} id - The live ID
         * @returns {object}
         */
        forLive(id) {
          return { type: "firebase", path: `live/${id}` };
        },
        /**
         * Create a syncRef for private collab
         * @param {string} token - The collab token
         * @returns {object}
         */
        forCollab(token) {
          return { type: "firebase", path: `collab/${token}` };
        },
        /**
         * Create a syncRef for local file
         * @param {string} filename - The filename
         * @returns {object}
         */
        forFile(filename) {
          return { type: "file", path: filename };
        },
        // ==========================================================================
        // EXTRACT PATH COMPONENTS
        // ==========================================================================
        /**
         * Get the wiki slug from a calc
         * @param {object} calc
         * @returns {string|null}
         */
        getSlug(calc) {
          if (!SyncRef.isWiki(calc)) return null;
          return calc.syncRef.path.replace("wiki/", "");
        },
        /**
         * Get the live ID from a calc
         * @param {object} calc
         * @returns {string|null}
         */
        getLiveId(calc) {
          if (!SyncRef.isLive(calc)) return null;
          const path = calc.syncRef.path;
          if (path.startsWith("wiki/live/")) {
            return path.replace("wiki/live/", "");
          }
          return path.replace("live/", "");
        },
        /**
         * Get the collab token from a calc
         * @param {object} calc
         * @returns {string|null}
         */
        getCollabToken(calc) {
          if (!SyncRef.isCollab(calc)) return null;
          return calc.syncRef.path.replace("collab/", "");
        },
        /**
         * Get the filename from a file-synced calc
         * @param {object} calc
         * @returns {string|null}
         */
        getFilename(calc) {
          if (!SyncRef.isFileSync(calc)) return null;
          return calc.syncRef.path;
        },
        // ==========================================================================
        // FIREBASE PATH HELPERS
        // ==========================================================================
        /**
         * Get the Firebase path for a syncRef
         * Used for Firebase database operations
         * @param {object} syncRef
         * @returns {string|null}
         */
        getFirebasePath(syncRef) {
          if (!syncRef || syncRef.type !== "firebase") return null;
          const path = syncRef.path;
          if (path.startsWith("wiki/")) {
            return `/wiki/${path.replace("wiki/", "")}`;
          }
          if (path.startsWith("live/")) {
            return `/wiki/live/${path.replace("live/", "")}`;
          }
          if (path.startsWith("collab/")) {
            return `/collab/${path.replace("collab/", "")}`;
          }
          return null;
        },
        // ==========================================================================
        // UTILITY
        // ==========================================================================
        /**
         * Check if two syncRefs are equal (same sync target)
         * @param {object|null} a
         * @param {object|null} b
         * @returns {boolean}
         */
        equals(a, b) {
          if (!a && !b) return true;
          if (!a || !b) return false;
          return a.type === b.type && a.path === b.path;
        },
        /**
         * Create a copy of a syncRef
         * @param {object|null} syncRef
         * @returns {object|null}
         */
        clone(syncRef) {
          if (!syncRef) return null;
          return { type: syncRef.type, path: syncRef.path };
        }
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = SyncRef;
      }
      if (typeof window !== "undefined") {
        window.SyncRef = SyncRef;
      }
    }
  });

  // public/src/stores/PersistenceLayer.js
  var require_PersistenceLayer = __commonJS({
    "public/src/stores/PersistenceLayer.js"(exports, module2) {
      var CalcId = require_CalcId();
      var CalcType = require_CalcType();
      var CalcMigration = require_CalcMigration();
      var Debug9 = typeof window !== "undefined" && window.Debug || require_debug();
      var STORAGE_KEYS = {
        USER_CALCS: "store",
        // Primary user calc storage
        COLLAB_CALCS: "instacalc_collabs",
        // Collab calcs (shared but tracked)
        SETTINGS: "settings"
        // User preferences
      };
      var PersistenceLayer = class _PersistenceLayer {
        constructor(options = {}) {
          this._storage = options.localStorage ?? (typeof window !== "undefined" ? window.localStorage : null);
          this._pendingUserCalcs = null;
          this._userCalcsWriteTimeout = null;
          this._DEBOUNCE_MS = options.debounceMs ?? 5e3;
        }
        // ==========================================================================
        // USER CALCS (localStorage)
        // ==========================================================================
        /**
         * Load all user calcs from localStorage
         * Validates and cleans polluted data on load
         * @returns {Object} Map of id -> calc
         */
        loadUserCalcs() {
          if (!this._storage) return {};
          try {
            const stored = this._storage.getItem(STORAGE_KEYS.USER_CALCS);
            if (!stored) return {};
            const data = JSON.parse(stored);
            const loadedCalcs = data.calcs || {};
            const cleanedCalcs = {};
            let cleanedCount = 0;
            Object.keys(loadedCalcs).forEach((id) => {
              const calc = loadedCalcs[id];
              if (CalcId.isRemoteId(id)) {
                Debug9.warn("persistence", "Cleaning polluted calc (remote ID):", id);
                cleanedCount++;
                return;
              }
              if (calc.remoteId) {
                Debug9.warn("persistence", "Cleaning polluted calc (has remoteId):", id);
                cleanedCount++;
                return;
              }
              if (calc.type && !CalcType.isUserOwned(calc.type)) {
                Debug9.warn("persistence", "Cleaning polluted calc (non-USER type):", id, calc.type);
                cleanedCount++;
                return;
              }
              const cleaned = { ...calc };
              delete cleaned.isRemote;
              delete cleaned.sourceId;
              if (!cleaned.type) {
                cleaned.type = CalcType.USER;
              }
              cleanedCalcs[id] = CalcMigration.migrate(cleaned);
            });
            if (cleanedCount > 0) {
              Debug9.log("persistence", `Removed ${cleanedCount} polluted calc(s)`);
              this.saveUserCalcs(cleanedCalcs);
            }
            return cleanedCalcs;
          } catch (e) {
            Debug9.error("persistence", "Failed to load user calcs:", e);
            return {};
          }
        }
        /**
         * Save all user calcs to localStorage (DEBOUNCED)
         * Writes are batched - only persists after 500ms of no activity.
         * @param {Object} calcs - Map of id -> calc
         * @param {Object} options - { immediate: true } to bypass debounce
         */
        saveUserCalcs(calcs, options = {}) {
          if (!this._storage) return;
          this._pendingUserCalcs = calcs;
          if (options.immediate || this._DEBOUNCE_MS === 0) {
            this._flushUserCalcs();
            return;
          }
          if (this._userCalcsWriteTimeout) {
            clearTimeout(this._userCalcsWriteTimeout);
          }
          this._userCalcsWriteTimeout = setTimeout(() => {
            this._flushUserCalcs();
          }, this._DEBOUNCE_MS);
        }
        /**
         * Immediately write pending user calcs to localStorage
         * Called by debounce timer or manually for immediate writes
         *
         * Uses requestIdleCallback to avoid blocking typing/interactions
         * Falls back to setTimeout if idle callback not available
         * In test environments, writes synchronously for predictable tests
         */
        _flushUserCalcs() {
          if (!this._storage || this._pendingUserCalcs === null) return;
          const calcsToWrite = this._pendingUserCalcs;
          this._pendingUserCalcs = null;
          const doWrite = () => {
            try {
              const data = { calcs: calcsToWrite };
              Debug9.log("persistence", "Flushing user calcs:", Object.keys(calcsToWrite).length);
              this._storage.setItem(STORAGE_KEYS.USER_CALCS, JSON.stringify(data));
            } catch (e) {
              Debug9.error("persistence", "Failed to save user calcs:", e);
            }
          };
          if (this._DEBOUNCE_MS === 0) {
            doWrite();
          } else {
            if (typeof requestIdleCallback !== "undefined") {
              requestIdleCallback(doWrite, { timeout: 2e3 });
            } else {
              setTimeout(doWrite, 0);
            }
          }
          if (this._userCalcsWriteTimeout) {
            clearTimeout(this._userCalcsWriteTimeout);
            this._userCalcsWriteTimeout = null;
          }
        }
        // ==========================================================================
        // COLLAB CALCS (localStorage, separate key)
        // ==========================================================================
        /**
         * Load collab calcs from localStorage
         * @returns {Object} Map of id -> calc
         */
        loadCollabCalcs() {
          if (!this._storage) return {};
          try {
            const stored = this._storage.getItem(STORAGE_KEYS.COLLAB_CALCS);
            if (!stored) return {};
            const data = JSON.parse(stored);
            return data.calcs || {};
          } catch (e) {
            Debug9.error("persistence", "Failed to load collab calcs:", e);
            return {};
          }
        }
        /**
         * Save collab calcs to localStorage
         * @param {Object} calcs - Map of id -> calc
         */
        saveCollabCalcs(calcs) {
          if (!this._storage) return;
          const doWrite = () => {
            try {
              this._storage.setItem(STORAGE_KEYS.COLLAB_CALCS, JSON.stringify({ calcs }));
            } catch (e) {
              Debug9.error("persistence", "Failed to save collab calcs:", e);
            }
          };
          if (this._DEBOUNCE_MS === 0) {
            doWrite();
          } else {
            if (typeof requestIdleCallback !== "undefined") {
              requestIdleCallback(doWrite, { timeout: 2e3 });
            } else {
              setTimeout(doWrite, 0);
            }
          }
        }
        // ==========================================================================
        // SETTINGS (user preferences, selected calc, etc.)
        // ==========================================================================
        /**
         * Load settings from localStorage
         * @returns {Object} Settings object
         */
        loadSettings() {
          if (!this._storage) return {};
          try {
            const stored = this._storage.getItem(STORAGE_KEYS.SETTINGS);
            return stored ? JSON.parse(stored) : {};
          } catch (e) {
            Debug9.error("persistence", "Failed to load settings:", e);
            return {};
          }
        }
        /**
         * Save settings to localStorage
         * @param {Object} settings - Settings object
         */
        saveSettings(settings) {
          if (!this._storage) return;
          try {
            this._storage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
          } catch (e) {
            Debug9.error("persistence", "Failed to save settings:", e);
          }
        }
        /**
         * Get a single setting value
         * @param {string} key - Setting key
         * @returns {*} Setting value
         */
        getSetting(key) {
          const settings = this.loadSettings();
          return settings[key];
        }
        /**
         * Set a single setting value
         * @param {string} key - Setting key
         * @param {*} value - Setting value
         */
        setSetting(key, value) {
          const settings = this.loadSettings();
          settings[key] = value;
          this.saveSettings(settings);
        }
        /**
         * Get selected calc ID (for session restore)
         * @returns {string|null} Calc ID
         */
        getSelectedCalcId() {
          return this.getSetting("selected");
        }
        /**
         * Set selected calc ID (for session restore)
         * @param {string} calcId - Calc ID
         */
        setSelectedCalcId(calcId) {
          this.setSetting("selected", calcId);
        }
        // ==========================================================================
        // VALIDATION HELPERS
        // ==========================================================================
        /**
         * Check if a calc can be saved to user storage
         * @param {Object} calc - Calc to validate
         * @returns {Object} { valid: boolean, reason?: string }
         */
        validateForUserStorage(calc) {
          if (CalcId.isRemoteId(calc.id)) {
            return { valid: false, reason: "Remote ID pattern" };
          }
          if (calc.type && !CalcType.isUserOwned(calc.type)) {
            return { valid: false, reason: `Non-USER type: ${calc.type}` };
          }
          if (calc.remoteId) {
            return { valid: false, reason: "Has remoteId" };
          }
          return { valid: true };
        }
        // ==========================================================================
        // TEST UTILITIES
        // ==========================================================================
        /**
         * Create a mock persistence layer for testing
         */
        static createMock() {
          const mockStorage = {
            _data: {},
            getItem(k) {
              return this._data[k] || null;
            },
            setItem(k, v) {
              this._data[k] = v;
            },
            removeItem(k) {
              delete this._data[k];
            },
            clear() {
              this._data = {};
            }
          };
          return new _PersistenceLayer({
            localStorage: mockStorage,
            debounceMs: 0
            // Synchronous writes for tests
          });
        }
        /**
         * Clear all persisted data (for testing)
         */
        clear() {
          this._flushUserCalcs();
          if (!this._storage) return;
          this._storage.removeItem(STORAGE_KEYS.USER_CALCS);
          this._storage.removeItem(STORAGE_KEYS.COLLAB_CALCS);
          this._storage.removeItem(STORAGE_KEYS.SETTINGS);
        }
        /**
         * Force-flush all pending writes immediately.
         * Called on page unload to ensure no data loss.
         */
        flushAll() {
          this._flushUserCalcs();
        }
      };
      PersistenceLayer.STORAGE_KEYS = STORAGE_KEYS;
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = PersistenceLayer;
      }
      if (typeof window !== "undefined") {
        window.PersistenceLayer = PersistenceLayer;
      }
    }
  });

  // public/src/services/RemoteRepository.js
  var require_RemoteRepository = __commonJS({
    "public/src/services/RemoteRepository.js"(exports, module2) {
      var Debug9 = typeof window !== "undefined" && window.Debug || require_debug();
      function getFirebaseOptional() {
        if (typeof window !== "undefined" && window.firebaseOptional) {
          return window.firebaseOptional;
        }
        return null;
      }
      var RemoteRepository = class {
        constructor(firebaseInstance) {
          this._firebase = firebaseInstance;
        }
        get _db() {
          if (typeof this._firebase !== "undefined" && this._firebase?.database) {
            return this._firebase.database();
          }
          if (typeof window !== "undefined" && window.firebase?.database) {
            return window.firebase.database();
          }
          if (typeof firebase !== "undefined" && firebase?.database) {
            return firebase.database();
          }
          return null;
        }
        get _auth() {
          if (typeof this._firebase !== "undefined" && this._firebase?.auth) {
            return this._firebase.auth();
          }
          if (typeof window !== "undefined" && window.firebase?.auth) {
            return window.firebase.auth();
          }
          if (typeof firebase !== "undefined" && firebase?.auth) {
            return firebase.auth();
          }
          return null;
        }
        get available() {
          return !!this._db;
        }
        _getPaths() {
          if (typeof window !== "undefined" && window.FirebasePaths) {
            return window.FirebasePaths;
          }
          return {
            getRemoteUrl: (type, id) => `https://instacalc.firebaseio.com/shared/${id}.json`,
            getSharedUrl: (id) => `https://instacalc.firebaseio.com/shared/${id}.json`,
            getRemotePath: (type, id) => {
              if (type === "collab") return `/collab/${id}`;
              if (type === "live") return `/wiki/live/${id}`;
              if (type === "wiki") return `/wiki/${id}`;
              return `/shared/${id}`;
            },
            getPublishPath: (id) => `/shared/${id}`
          };
        }
        async fetch(type, identifier) {
          const paths = this._getPaths();
          const url = paths.getRemoteUrl(type, identifier) || paths.getSharedUrl(identifier);
          Debug9.log("store", "fetch:", url);
          const startTime = Date.now();
          try {
            const response = await fetch(url);
            const data = await response.json();
            if (typeof window !== "undefined" && window.DebugTools?.NetworkLogger?.logRequest) {
              window.DebugTools.NetworkLogger.logRequest("fetch", url, Date.now() - startTime, true);
            }
            return data;
          } catch (e) {
            if (typeof window !== "undefined" && window.DebugTools?.NetworkLogger?.logRequest) {
              window.DebugTools.NetworkLogger.logRequest("fetch", url, Date.now() - startTime, false, { error: e.message });
            }
            throw e;
          }
        }
        subscribe(type, identifier, callback) {
          const db = this._db;
          if (!db) {
            Debug9.warn("store", "Firebase not available for subscribe");
            return () => {
            };
          }
          const paths = this._getPaths();
          const path = paths.getRemotePath(type, identifier);
          if (!path) return () => {
          };
          const ref = db.ref(path);
          Debug9.log("store", "\u{1F442} Subscribing to:", path);
          const handler = (snapshot) => {
            const val = snapshot.val();
            if (val) callback(val);
          };
          ref.on("value", handler);
          return () => ref.off("value", handler);
        }
        /**
         * Write data to Firebase path with offline queue support
         * @private
         */
        async _writeToFirebase(path, data, operationName) {
          if (!path) return;
          Debug9.log("store", `\u{1F53C} ${operationName}:`, path);
          const startTime = Date.now();
          const fbOptional = getFirebaseOptional();
          if (fbOptional) {
            const success = await fbOptional.set(path, data);
            if (typeof window !== "undefined" && window.DebugTools?.NetworkLogger?.logRequest) {
              window.DebugTools.NetworkLogger.logRequest(operationName, path, Date.now() - startTime, success);
            }
            return success;
          }
          const db = this._db;
          if (!db) return;
          try {
            const result = await db.ref(path).set(data);
            if (typeof window !== "undefined" && window.DebugTools?.NetworkLogger?.logRequest) {
              window.DebugTools.NetworkLogger.logRequest(operationName, path, Date.now() - startTime, true);
            }
            return result;
          } catch (e) {
            if (typeof window !== "undefined" && window.DebugTools?.NetworkLogger?.logRequest) {
              window.DebugTools.NetworkLogger.logRequest(operationName, path, Date.now() - startTime, false, { error: e.message });
            }
            throw e;
          }
        }
        async push(type, identifier, data) {
          const path = this._getPaths().getRemotePath(type, identifier);
          return this._writeToFirebase(path, data, "push");
        }
        async syncUserCalc(uid, calcId, data) {
          return this._writeToFirebase(`/users/${uid}/calcs/${calcId}`, data, "syncUser");
        }
        async syncSharedCalc(publishedId, data) {
          const path = this._getPaths().getPublishPath(publishedId);
          return this._writeToFirebase(path, data, "syncShared");
        }
      };
      module2.exports = RemoteRepository;
    }
  });

  // public/src/stores/SelectionController.js
  var require_SelectionController = __commonJS({
    "public/src/stores/SelectionController.js"(exports, module2) {
      var Debug9 = typeof window !== "undefined" && window.Debug || {
        log: () => {
        },
        warn: () => {
        },
        error: (...args) => console.error(...args)
      };
      var SelectionController = class _SelectionController {
        constructor(options = {}) {
          this._selections = {};
          this._viewContexts = {};
          this._embedMode = false;
          this._subscribers = /* @__PURE__ */ new Set();
          this._persistence = options.persistence || null;
          this._defaultContainer = options.defaultContainer || "main";
          if (options.autoLoad !== false) {
            this._loadPersistedSelection();
          }
        }
        // ==========================================================================
        // SELECTION OPERATIONS
        // ==========================================================================
        /**
         * Select a calc in a container
         * @param {string} calcId - Calc ID to select
         * @param {string} containerId - Container ID (default: 'main')
         * @param {Object} options - { viewContext, skipEvent, skipPersist }
         * @returns {string|null} The selected calc ID, or null if blocked
         */
        select(calcId, containerId = this._defaultContainer, options = {}) {
          if (this._embedMode && containerId === this._defaultContainer) {
            Debug9.warn("selection", "\u{1F6AB} Selection blocked in embed mode");
            return null;
          }
          const oldId = this._selections[containerId];
          const oldViewContext = this._viewContexts[containerId];
          Debug9.log("selection", "\u{1F3AF} select:", {
            calcId: calcId?.slice(0, 8),
            containerId,
            oldId: oldId?.slice(0, 8),
            viewContext: options.viewContext
          });
          this._selections[containerId] = calcId;
          if (options.viewContext !== void 0) {
            this._viewContexts[containerId] = options.viewContext;
          }
          if (!options.skipPersist && containerId === this._defaultContainer) {
            this._persistSelection(calcId);
          }
          const viewContext = this._viewContexts[containerId];
          if (!options.skipEvent && (oldId !== calcId || oldViewContext !== viewContext)) {
            this._notifyChange(calcId, containerId, viewContext);
          }
          return calcId;
        }
        /**
         * Get selected calc ID for a container
         * @param {string} containerId - Container ID (default: 'main')
         * @returns {string|null} Calc ID or null
         */
        getSelectedId(containerId = this._defaultContainer) {
          return this._selections[containerId] || null;
        }
        /**
         * Get view context for a container
         * @param {string} containerId - Container ID (default: 'main')
         * @returns {string|null} View context or null
         */
        getViewContext(containerId = this._defaultContainer) {
          return this._viewContexts[containerId] || null;
        }
        /**
         * Set view context for a container
         * @param {string} viewContext - View context (HOME, SINGLE, EMBED, GALLERY)
         * @param {string} containerId - Container ID (default: 'main')
         */
        setViewContext(viewContext, containerId = this._defaultContainer) {
          this._viewContexts[containerId] = viewContext;
        }
        /**
         * Clear selection for a container
         * @param {string} containerId - Container ID (default: 'main')
         */
        clear(containerId = this._defaultContainer) {
          Debug9.log("selection", "\u{1F9F9} clear:", containerId);
          delete this._selections[containerId];
          delete this._viewContexts[containerId];
          if (containerId === this._defaultContainer) {
            this._persistSelection(null);
          }
          this._notifyChange(null, containerId, null);
        }
        // ==========================================================================
        // EMBED MODE
        // ==========================================================================
        /**
         * Enable embed mode (blocks selection changes in main container)
         */
        enableEmbedMode() {
          this._embedMode = true;
        }
        /**
         * Check if embed mode is enabled
         * @returns {boolean}
         */
        isEmbedMode() {
          return this._embedMode;
        }
        // ==========================================================================
        // SUBSCRIPTIONS
        // ==========================================================================
        /**
         * Subscribe to selection changes
         * @param {Function} callback - (calcId, containerId, viewContext) => void
         * @returns {Function} Cleanup function
         */
        subscribe(callback) {
          this._subscribers.add(callback);
          return () => this._subscribers.delete(callback);
        }
        // ==========================================================================
        // PRIVATE METHODS
        // ==========================================================================
        /**
         * Notify subscribers and dispatch event
         * @private
         */
        _notifyChange(calcId, containerId, viewContext) {
          Debug9.log("selection", "\u{1F4E2} _notifyChange:", {
            calcId: calcId?.slice(0, 8),
            containerId,
            viewContext,
            subscriberCount: this._subscribers.size
          });
          for (const callback of this._subscribers) {
            try {
              callback(calcId, containerId, viewContext);
            } catch (err) {
              Debug9.error("selection", "Subscriber error:", err);
            }
          }
          const InstacalcEvents = typeof window !== "undefined" ? window.InstacalcEvents : null;
          if (InstacalcEvents?.dispatch) {
            const ViewContext = typeof window !== "undefined" ? window.ViewContext : null;
            const shouldSyncUrl = viewContext !== ViewContext?.EMBED;
            const payload = containerId === this._defaultContainer ? { calcId, viewContext, shouldSyncUrl } : { calcId, containerId, viewContext, shouldSyncUrl };
            InstacalcEvents.dispatch(InstacalcEvents.CALC_SWITCH, payload);
          }
        }
        /**
         * Persist selection to localStorage
         * @private
         */
        _persistSelection(calcId) {
          const persistence = this._persistence || (typeof window !== "undefined" ? window.calcStore?._persistence : null);
          if (persistence?.setSelectedCalcId) {
            persistence.setSelectedCalcId(calcId);
          }
        }
        /**
         * Load persisted selection from localStorage
         * @private
         */
        _loadPersistedSelection() {
          const persistence = this._persistence || (typeof window !== "undefined" ? window.calcStore?._persistence : null);
          if (persistence?.getSelectedCalcId) {
            const calcId = persistence.getSelectedCalcId();
            if (calcId) {
              Debug9.log("selection", "\u{1F4E5} Loaded persisted selection:", calcId?.slice(0, 8));
              this._selections[this._defaultContainer] = calcId;
            }
          }
        }
        // ==========================================================================
        // TEST UTILITIES
        // ==========================================================================
        /**
         * Create a mock SelectionController for testing
         * @param {Object} options - Initial state
         * @returns {SelectionController}
         */
        static createMock(options = {}) {
          const controller = new _SelectionController({
            autoLoad: false,
            persistence: {
              getSelectedCalcId: () => options.initialSelection || null,
              setSelectedCalcId: () => {
              }
            }
          });
          if (options.initialSelection) {
            controller._selections.main = options.initialSelection;
          }
          return controller;
        }
        /**
         * Reset state (for testing)
         */
        reset() {
          this._selections = {};
          this._viewContexts = {};
          this._embedMode = false;
          this._subscribers.clear();
        }
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = SelectionController;
      }
      if (typeof window !== "undefined") {
        window.SelectionController = SelectionController;
        if (!window.selectionController) {
          window.selectionController = new SelectionController();
        }
      }
    }
  });

  // public/src/stores/CalcStore.js
  var require_CalcStore = __commonJS({
    "public/src/stores/CalcStore.js"(exports, module2) {
      var CalcType = require_CalcType();
      var CalcId = require_CalcId();
      var Debug9 = typeof window !== "undefined" && window.Debug || require_debug();
      var CalcMigration = require_CalcMigration();
      var SyncRef = require_SyncRef();
      var PersistenceLayer = require_PersistenceLayer();
      var RemoteRepository = require_RemoteRepository();
      var UPDATE_SOURCE = {
        LOCAL: "local",
        // User typed, clicked, or made a local change
        REMOTE: "remote",
        // Firebase/external update received
        MUTATION: "mutation"
        // Internal state sync (used by __fromMutation)
      };
      var REMOTE_ID_PATTERNS = CalcId.REMOTE_ID_PATTERNS;
      var getViewContext = () => {
        if (typeof window !== "undefined" && window.ViewContext) {
          return window.ViewContext;
        }
        if (typeof __require !== "undefined") {
          return require_ViewContext();
        }
        throw new Error("ViewContext not loaded");
      };
      var getCalcType = () => {
        if (typeof window !== "undefined" && window.CalcType) {
          return window.CalcType;
        }
        if (typeof __require !== "undefined") {
          return require_CalcType();
        }
        throw new Error("CalcType not loaded");
      };
      var makeRowId = () => {
        if (typeof window !== "undefined" && window.Util?.guid) {
          return window.Util.guid();
        }
        if (typeof global !== "undefined" && global.Util?.guid) {
          return global.Util.guid();
        }
        return `row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      };
      var CalcStore = class _CalcStore {
        /**
         * Create a new CalcStore instance
         * @param {Object} options - Configuration options
         * @param {Storage} [options.localStorage] - localStorage implementation
         * @param {PersistenceLayer} [options.persistence] - Persistence layer instance
         * @param {Object} [options.firebase] - Firebase configuration
         * @param {boolean} [options.autoLoad=true] - Load calcs from localStorage on init
         */
        constructor(options = {}) {
          this._localStorage = options.localStorage ?? (typeof window !== "undefined" ? window.localStorage : null);
          this._persistence = options.persistence ?? new PersistenceLayer({ localStorage: this._localStorage });
          this._remote = new RemoteRepository(options.firebase);
          this._userCalcs = {};
          this._collabCalcs = {};
          this._sessionCalcs = {};
          this._pendingFirebasePushes = /* @__PURE__ */ new Set();
          this._firebaseRetryTimer = null;
          this._callbacks = [];
          this._sessionId = CalcId.generateSessionId();
          this._pendingPushes = /* @__PURE__ */ new Map();
          this._lastPushedRows = /* @__PURE__ */ new Map();
          this._syncTimeouts = {};
          this._subscriptions = {};
          this._fetchCache = {};
          this._batchMode = false;
          this._batchedCalcs = /* @__PURE__ */ new Set();
          this._remoteSyncInProgress = false;
          this._pendingIDBWrites = /* @__PURE__ */ new Map();
          this._idbFlushScheduled = false;
          this._idbFlushTimer = null;
          this._saveTimestamps = [];
          this._replayCount = 0;
          this._features = {
            OFFLINE_PERSISTENCE: true,
            IDB_USER_CALCS: true,
            IDB_FLUSH_ON_IDLE: true
          };
          if (options.autoLoad !== false) {
            this._loadFromLocalStorage();
          }
          if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
            window.addEventListener("beforeunload", () => {
              this._persistence.flushAll();
              this._flushIDBQueue();
            });
          }
        }
        _firebaseAvailable() {
          return typeof firebase !== "undefined" && !!firebase?.database;
        }
        _queueFirebasePush(calcId) {
          if (!calcId) return;
          this._pendingFirebasePushes.add(calcId);
          this._scheduleFirebaseRetry();
        }
        _scheduleFirebaseRetry() {
          if (this._firebaseRetryTimer) return;
          this._firebaseRetryTimer = setTimeout(() => {
            this._firebaseRetryTimer = null;
            this._flushPendingFirebasePushes();
            if (this._pendingFirebasePushes.size > 0 && !this._firebaseAvailable()) {
              this._scheduleFirebaseRetry();
            }
          }, 250);
        }
        _flushPendingFirebasePushes() {
          if (!this._firebaseAvailable()) return;
          const pending = Array.from(this._pendingFirebasePushes);
          this._pendingFirebasePushes.clear();
          pending.forEach((calcId) => {
            const calc = this.get(calcId);
            if (calc) {
              this._pushToFirebase(calc);
            }
          });
        }
        // ==========================================================================
        // REMOTE SYNC STATUS - Track remote sync state for UI feedback
        // ==========================================================================
        /**
         * Set remote sync status
         * @param {string} status - 'loading', 'complete', or 'idle'
         */
        setRemoteSyncStatus(status) {
          const wasInProgress = this._remoteSyncInProgress;
          this._remoteSyncInProgress = status === "loading";
          if (typeof window !== "undefined" && window.InstacalcEvents?.dispatch) {
            window.InstacalcEvents.dispatch("ic:remote-sync-status", {
              status,
              inProgress: this._remoteSyncInProgress
            });
          }
          if (wasInProgress && !this._remoteSyncInProgress) {
            Debug9.log("store", "\u2705 Remote sync complete");
          } else if (!wasInProgress && this._remoteSyncInProgress) {
            Debug9.log("store", "\u{1F504} Remote sync started");
          }
        }
        /**
         * Check if remote sync is in progress
         * @returns {boolean}
         */
        isRemoteSyncInProgress() {
          return this._remoteSyncInProgress;
        }
        // ==========================================================================
        // BATCH MODE - Prevent notification storms during bulk operations
        // ==========================================================================
        /**
         * Start batch mode - notifications are suppressed until endBatch()
         * Use for bulk operations like Firebase sync to prevent notification storms
         */
        startBatch() {
          this._batchMode = true;
          this._batchedCalcs.clear();
          this._batchPendingSyncs = [];
        }
        /**
         * End batch mode - dispatches a single bulk notification for all batched changes
         */
        endBatch() {
          this._batchMode = false;
          if (this._batchedCalcs.size > 0) {
            const calcIds = Array.from(this._batchedCalcs);
            this._batchedCalcs.clear();
            if (typeof window !== "undefined" && window.DebugLogger?.isEnabled("store")) {
              window.DebugLogger.log("store", `BATCH_NOTIFY: ${calcIds.length} calcs changed`);
            }
            if (typeof window !== "undefined" && window.InstacalcEvents?.dispatch) {
              window.InstacalcEvents.dispatch(window.InstacalcEvents.CALCS_BULK_CHANGED, {
                calcIds,
                count: calcIds.length,
                source: UPDATE_SOURCE.REMOTE
              });
            }
          }
          if (this._batchPendingSyncs.length > 0) {
            const toSync = [...this._batchPendingSyncs];
            this._batchPendingSyncs = [];
            Debug9.log("store", `BATCH_SYNC: Flushing ${toSync.length} pending syncs`);
            toSync.forEach((calc) => this.syncUserCalc(calc));
          }
        }
        // ==========================================================================
        // CORE API
        // ==========================================================================
        /**
         * Get a calc by ID from any store (user, collab, or session)
         * @param {string} id - The calc ID
         * @returns {Object|null} The calc object or null if not found
         */
        get(id) {
          return this._userCalcs[id] || this._collabCalcs[id] || this._sessionCalcs[id] || null;
        }
        /**
         * Check if a calc exists in any store
         * @param {string} id - The calc ID
         * @returns {boolean} True if calc exists
         */
        has(id) {
          return this.get(id) !== null;
        }
        // ==========================================================================
        // REPOSITORY API - ensure() and applyEdit()
        // ==========================================================================
        /**
         * Get or fetch a calc. Single entry point for all remote data.
         * @param {string} type - 'embed', 'live', 'collab', 'wiki'
         * @param {string} identifier - numeric ID, slug, or token
         * @returns {Promise<string>} - the calc ID (ready to use)
         */
        async ensure(type, identifier) {
          const idPrefix = type === "embed" ? "shared" : type;
          const calcId = `${idPrefix}_${identifier}`;
          if (this._sessionCalcs[calcId] || this._userCalcs[calcId] || this._collabCalcs[calcId]) {
            return calcId;
          }
          if (this._sessionCalcs[identifier] || this._userCalcs[identifier] || this._collabCalcs[identifier]) {
            return identifier;
          }
          if (this._fetchCache[calcId]) {
            return this._fetchCache[calcId];
          }
          this._fetchCache[calcId] = this._fetchAndCreate(type, identifier, calcId);
          try {
            return await this._fetchCache[calcId];
          } finally {
            delete this._fetchCache[calcId];
          }
        }
        async _fetchAndCreate(type, identifier, calcId) {
          let data;
          try {
            data = await this._remote.fetch(type, identifier);
          } catch (e) {
            Debug9.log("store", "fetch failed:", e);
          }
          if (!data && type === "live") {
            Debug9.log("store", "Live wiki not found, creating:", identifier);
            const defaultCalc = this._createDefaultLiveCalc(identifier, calcId);
            this._sessionCalcs[calcId] = defaultCalc;
            return calcId;
          }
          if (!data) {
            throw new Error(`${type} calc "${identifier}" not found`);
          }
          const calcType = type === "embed" || type === "single" ? CalcType.SHARED_VIEW : type;
          const calc = {
            id: calcId,
            type: calcType,
            sourceId: identifier,
            remoteId: identifier,
            // Required for Firebase push - _getFirebasePathFromCalc checks this
            name: data.name || identifier,
            author: data.author,
            authorName: data.authorName,
            rows: typeof structuredClone !== "undefined" ? structuredClone(data.rows || []) : JSON.parse(JSON.stringify(data.rows || [])),
            created: data.created,
            updated: data.updated
          };
          const validated = this._validate(calc);
          if (CalcType.isCollab(validated)) {
            this._collabCalcs[calcId] = validated;
            this._persistCollabsToLocalStorage();
          } else {
            this._sessionCalcs[calcId] = validated;
          }
          Debug9.log("store", "ensure() created:", calcId);
          return calcId;
        }
        _createDefaultLiveCalc(slug, calcId) {
          const name = slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
          const now2 = Date.now();
          return this._validate({
            id: calcId,
            type: "live",
            sourceId: slug,
            remoteId: slug,
            // Required for Firebase push
            name,
            rows: [{ text: "" }, { text: "" }, { text: "" }, { text: "" }, { text: "" }],
            created: now2,
            updated: now2,
            notYetSavedToFirebase: true
            // Will be created on first edit
          });
        }
        /**
         * Apply a mutation to a calc by ID.
         * Gets current state, applies change immutably, saves, notifies.
         * @param {string} calcId - The calc ID
         * @param {object} mutation - { type: 'updateRow', rowIndex, text } etc.
         * @returns {object} - The updated calc
         */
        applyEdit(calcId, mutation) {
          const calc = this.get(calcId);
          if (!calc) {
            throw new Error(`applyEdit: calc "${calcId}" not found`);
          }
          let newCalc;
          switch (mutation.type) {
            case "updateRow":
              newCalc = {
                ...calc,
                rows: calc.rows.map(
                  (row, i) => i === mutation.rowIndex ? { ...row, text: mutation.text } : row
                ),
                updated: Date.now()
              };
              break;
            case "updateName":
              newCalc = {
                ...calc,
                name: mutation.name,
                updated: Date.now()
              };
              break;
            case "insertRow":
              newCalc = {
                ...calc,
                rows: [
                  ...calc.rows.slice(0, mutation.rowIndex),
                  { text: "", hidden: false },
                  ...calc.rows.slice(mutation.rowIndex)
                ],
                updated: Date.now()
              };
              break;
            case "deleteRow":
              newCalc = {
                ...calc,
                rows: calc.rows.filter((_, i) => i !== mutation.rowIndex),
                updated: Date.now()
              };
              break;
            case "toggleHidden":
              newCalc = {
                ...calc,
                rows: calc.rows.map(
                  (row, i) => i === mutation.rowIndex ? { ...row, hidden: !row.hidden } : row
                ),
                updated: Date.now()
              };
              break;
            default:
              throw new Error(`applyEdit: unknown mutation type "${mutation.type}"`);
          }
          return this.applyLocalEdit(newCalc);
        }
        // ==========================================================================
        // LEGACY API (kept for backward compatibility during migration)
        // ==========================================================================
        /**
         * Apply a local edit (user typed, changed title, etc.)
         * Routes to correct store based on where the calc already lives.
         */
        applyLocalEdit(calc) {
          const detail = { __fromMutation: true };
          if (this._userCalcs[calc.id]) {
            return this.saveUserCalc(calc, detail);
          } else if (this._sessionCalcs[calc.id]) {
            if (this._isRemoteCalc(calc)) {
              return this.saveRemoteCalc(calc, detail);
            }
            return this.saveSessionCalc(calc, detail);
          } else {
            return this.save(calc, detail);
          }
        }
        /**
         * Set a calc directly (legacy support / remote update)
         * Skips sync to avoid echo loops.
         */
        set(id, calc) {
          const CalcType2 = getCalcType();
          if (calc.id !== id) calc.id = id;
          if (this._userCalcs[id]) {
            return this.saveUserCalcLocal(calc);
          } else if (this._sessionCalcs[id]) {
            return this.saveRemoteCalcLocal(calc);
          }
          const validated = this._validate(calc);
          const storage = CalcType2.getStorage(validated.type);
          if (storage === "localStorage") {
            this._saveToUserStore(validated);
          } else {
            this._saveToSessionStore(validated);
          }
          this._notifyChanged(validated);
          return validated;
        }
        /**
         * Save a calc - routes to correct store based on CalcType
         *
         * ARCHITECTURE: Uses CalcType as single source of truth.
         * CalcType.getStorage(calc.type) determines localStorage vs session.
         *
         * Prefer explicit methods for new code:
         * - saveUserCalc() / saveUserCalcLocal()
         * - saveSessionCalc()
         * - saveRemoteCalc() / saveRemoteCalcLocal()
         */
        save(calc, detail = {}) {
          if (false) {
            Debug9.warn("store", "DEPRECATED: calcStore.save() called. Use explicit methods: saveUserCalc(), saveSessionCalc(), saveRemoteCalc()");
          }
          if (!calc || typeof calc !== "object" || !calc.id) {
            throw new Error("Invalid calc: must have id");
          }
          const CalcType2 = getCalcType();
          if (CalcType2.isUrlDefined(calc.type)) {
            Debug9.log("store", "URL_DEFINED calc routed to session:", calc.id);
            return this.saveSessionCalc(calc, detail);
          }
          if (this._userCalcs[calc.id]) {
            return this.saveUserCalc(calc, detail);
          }
          if (this._collabCalcs[calc.id]) {
            return this.saveCollabCalc(calc, detail);
          }
          if (this._sessionCalcs[calc.id]) {
            if (CalcType2.isRemote(calc)) {
              return this.saveRemoteCalc(calc, detail);
            }
            return this.saveSessionCalc(calc, detail);
          }
          const validated = this._validate({ ...calc });
          const storage = CalcType2.getStorage(validated.type);
          Debug9.log("store", "Routing new calc:", { id: validated.id, type: validated.type, storage });
          if (storage === "localStorage") {
            return this.saveUserCalc(validated, detail);
          } else if (storage === "collab") {
            return this.saveCollabCalc(validated, detail);
          } else {
            return this.saveSessionCalc(validated, detail);
          }
        }
        /**
         * Delete a calc from all stores (user, collab, session)
         * Also removes from IndexedDB if applicable
         * @param {string} id - The calc ID to delete
         */
        delete(id) {
          if (this._userCalcs[id]) {
            delete this._userCalcs[id];
            this._persistToLocalStorage();
            this._deleteFromIndexedDB(id);
          }
          if (this._collabCalcs[id]) {
            delete this._collabCalcs[id];
            this._persistCollabsToLocalStorage();
            this._deleteFromIndexedDB(id);
          }
          if (this._sessionCalcs[id]) {
            delete this._sessionCalcs[id];
          }
          this._notifyChanged(null);
        }
        // ==========================================================================
        // EXPLICIT SAVE METHODS (Context-Based Storage)
        // These are the preferred API - caller decides destination explicitly
        // ==========================================================================
        // ==========================================================================
        // USER CALC METHODS (localStorage + optional Firebase sync)
        // ==========================================================================
        /**
         * Save to user storage + sync to Firebase
         * Use for: user's own calcs when logged in
         */
        saveUserCalc(calc, detail = {}) {
          const validated = this._saveToUserStore(calc, { immediate: detail.immediate });
          if (!this._batchMode) {
            this.syncUserCalc(validated);
          } else {
            this._batchPendingSyncs.push(validated);
          }
          this._notifyChanged(validated, detail);
          return validated;
        }
        /**
         * Save to user storage WITHOUT sync
         * Use for: user calcs when not logged in, or initial load
         */
        saveUserCalcLocal(calc, detail = {}) {
          const existing = this._userCalcs[calc.id];
          if (existing) {
            const isEqual = this._calcContentEquals(existing, calc);
            Debug9.log("store:verbose", "saveUserCalcLocal:", calc.id.slice(0, 8), "existing?", !!existing, "equal?", isEqual);
            if (isEqual) {
              return existing;
            }
          } else {
            Debug9.log("store:verbose", "saveUserCalcLocal: NEW calc", calc.id.slice(0, 8));
          }
          const validated = this._saveToUserStore(calc, { immediate: detail.immediate });
          this._notifyChanged(validated, detail);
          return validated;
        }
        // ==========================================================================
        // SESSION CALC METHODS (memory only, never persisted)
        // ==========================================================================
        /**
         * Save to session storage (memory only)
         * Use for: URL calcs, snapshots, temporary calcs
         */
        saveSessionCalc(calc, detail = {}) {
          if (this._sessionCalcs[calc.id]) {
            Debug9.log("store", "saveSessionCalc: OVERWRITING existing session calc:", calc.id);
          } else {
            Debug9.log("store", "saveSessionCalc: Creating new session calc:", calc.id);
          }
          const validated = this._saveToSessionStore(calc);
          this._notifyChanged(validated, detail);
          return validated;
        }
        // ==========================================================================
        // EMBED CALC METHODS (ephemeral session storage, no global side effects)
        // ==========================================================================
        /**
         * Save calc from embed context - ALWAYS ephemeral, NEVER affects global state
         *
         * Embeds are sandboxed environments that should not:
         * - Change the main app's active calc
         * - Persist to localStorage
         * - Sync to Firebase
         * - Trigger CALC_SWITCH events
         *
         * This method ensures embeds remain isolated by:
         * 1. Storing only in _sessionCalcs (ephemeral)
         * 2. NOT persisting to localStorage
         * 3. NOT syncing to Firebase
         * 4. NOT changing activeCalcId
         * 5. Only dispatching CALC_MUTATION (not CALC_SWITCH)
         *
         * @param {Object} calc - The calc to save
         * @param {Object} detail - Optional detail for event dispatch
         * @returns {Object} The validated calc
         */
        saveEmbedCalc(calc, detail = {}) {
          Debug9.log("store", "saveEmbedCalc: Saving embed calc (ephemeral):", calc.id);
          const validated = this._saveToSessionStore(calc);
          this._notifyChanged(validated, { ...detail, source: UPDATE_SOURCE.LOCAL });
          return validated;
        }
        // ==========================================================================
        // REMOTE CALC METHODS (session storage + Firebase sync)
        // ==========================================================================
        /**
         * Save to session storage + push to remote Firebase
         * Use for: live wikis, collab calcs (user editing)
         */
        saveRemoteCalc(calc, detail = {}) {
          const validated = this._saveToSessionStore(calc);
          this._pushToFirebase(validated);
          this._notifyChanged(validated, detail);
          return validated;
        }
        /**
         * Save to session storage WITHOUT push
         * Use for: when caller already synced, or receiving remote updates
         */
        saveRemoteCalcLocal(calc, detail = {}) {
          const validated = this._saveToSessionStore(calc);
          this._notifyChanged(validated, detail);
          return validated;
        }
        // ==========================================================================
        // COLLAB CALC METHODS (localStorage + Firebase sync)
        // ==========================================================================
        /**
         * Save collab calc to localStorage + push to Firebase
         * Collab calcs persist locally so users can return to them,
         * but also sync to Firebase for real-time collaboration.
         */
        saveCollabCalc(calc, detail = {}) {
          const validated = this._saveToCollabStore(calc);
          this._pushToFirebase(validated);
          this._notifyChanged(validated, detail);
          return validated;
        }
        /**
         * Save collab calc to localStorage WITHOUT push
         * Use for: receiving remote updates (to avoid echo)
         */
        saveCollabCalcLocal(calc, detail = {}) {
          const existing = this._collabCalcs[calc.id];
          if (existing && this._calcContentEquals(existing, calc)) {
            Debug9.log("store", "saveCollabCalcLocal: content unchanged, skipping persist", calc.id);
            return existing;
          }
          const validated = this._saveToCollabStore(calc);
          this._notifyChanged(validated, detail);
          return validated;
        }
        _saveToCollabStore(calc) {
          const validated = this._validate({ ...calc });
          this._collabCalcs[validated.id] = validated;
          this._persistCollabsToLocalStorage();
          this._persistToIndexedDB(validated);
          return validated;
        }
        // ==========================================================================
        // INTERNAL STORE METHODS (no sync, no notify)
        // ==========================================================================
        _saveToUserStore(calc, options = {}) {
          const CalcType2 = getCalcType();
          const validated = this._validate({ ...calc });
          Debug9.log("store", "_saveToUserStore attempt:", { id: validated.id, name: validated.name, type: validated.type });
          if (validated.type && !CalcType2.isUserOwned(validated.type)) {
            Debug9.error("store", "REJECTED: Non-USER type detected - use appropriate save method", {
              id: validated.id,
              type: validated.type
            });
            return this._saveToSessionStore(validated);
          }
          if (REMOTE_ID_PATTERNS.some((p) => p.test(validated.id))) {
            Debug9.error("store", "REJECTED: Remote ID pattern detected - use import() to convert", {
              id: validated.id
            });
            return this._saveToSessionStore(validated);
          }
          if (validated.remoteId) {
            Debug9.error("store", "REJECTED: Calc has remoteId - must use import() to convert", {
              id: validated.id,
              remoteId: validated.remoteId
            });
            return this._saveToSessionStore(validated);
          }
          if (validated.isRemote) {
            Debug9.warn("store", "CLEANING: USER calc has isRemote flag, removing", { id: validated.id });
            delete validated.isRemote;
          }
          if (validated.sourceId) {
            Debug9.warn("store", "CLEANING: USER calc has sourceId, removing", { id: validated.id });
            delete validated.sourceId;
          }
          Debug9.log("store:verbose", "_saveToUserStore SUCCESS, persisting to localStorage");
          this._userCalcs[validated.id] = validated;
          this._persistToIndexedDB(validated);
          this._persistToLocalStorage({ immediate: options.immediate });
          return validated;
        }
        _saveToSessionStore(calc) {
          const validated = this._validate({ ...calc });
          this._sessionCalcs[validated.id] = validated;
          return validated;
        }
        /**
         * Get all user-owned calcs (excluding deleted)
         * @returns {Object[]} Array of user calc objects
         */
        allUser() {
          return Object.values(this._userCalcs).filter((c) => !c.deleted);
        }
        /**
         * Get all collab calcs the user is tracking (excluding deleted)
         * @returns {Object[]} Array of collab calc objects
         */
        allCollab() {
          return Object.values(this._collabCalcs).filter((c) => !c.deleted);
        }
        /**
         * Get all calcs that should appear in the sidebar
         * Includes user's own calcs + collab calcs they're tracking
         * @returns {Object[]} Array of calc objects for sidebar display
         */
        allForSidebar() {
          return [...this.allUser(), ...this.allCollab()];
        }
        /**
         * Get all calcs from all stores (user, collab, session)
         * Excludes deleted calcs
         * @returns {Object[]} Array of all calc objects
         */
        all() {
          return [...this.allUser(), ...this.allCollab(), ...Object.values(this._sessionCalcs).filter((c) => !c.deleted)];
        }
        // ==========================================================================
        // IMPORT (the ONLY way to convert session → user)
        // ==========================================================================
        /**
         * Import a session/remote calc as a user's own calc.
         * This is the ONLY legitimate way to convert a non-USER calc to USER.
         *
         * Creates a NEW calc with:
         * - New GUID (breaks link to remote source)
         * - USER type
         * - All remote indicators removed
         *
         * The new calc will pass the _saveToUserStore() gatekeeper.
         * @param {string} sourceId - ID of the calc to import
         * @returns {Object} The newly created user calc
         * @throws {Error} If source calc not found
         */
        import(sourceId) {
          const CalcType2 = getCalcType();
          const source = this.get(sourceId);
          if (!source) {
            throw new Error(`Cannot import: calc ${sourceId} not found`);
          }
          if (this._userCalcs[sourceId]) {
            return source;
          }
          const imported = {
            // Start with source data (for name, rows, settings, etc.)
            ...source,
            // NEW IDENTITY: Breaks link to remote source
            id: this._generateGuid(),
            type: CalcType2.USER,
            // Track import origin (safe metadata, not a remote indicator)
            importedFrom: sourceId,
            // Fresh timestamps
            created: window.Timestamps.parse(source.created) || window.Timestamps.now(),
            updated: window.Timestamps.now(),
            // Deep copy rows to avoid mutation
            rows: (source.rows || []).map((r) => ({ ...r }))
          };
          delete imported.remoteId;
          delete imported.sourceId;
          delete imported.isRemote;
          delete imported.published;
          delete imported.notYetSavedToFirebase;
          delete imported.liveSlug;
          Object.keys(imported).forEach((k) => imported[k] === void 0 && delete imported[k]);
          return this._saveToUserStore(imported);
        }
        /**
         * Check if a calculation is already saved in My Calcs.
         * @param {string|object} calcOrId - Calc ID or calc object
         * @returns {boolean}
         */
        isAlreadySaved(calcOrId) {
          const id = typeof calcOrId === "string" ? calcOrId : calcOrId?.id;
          const calc = typeof calcOrId === "object" ? calcOrId : this.get(id);
          if (!id) return false;
          if (this._userCalcs[id]) return true;
          const remoteId = calc?.published?.id || id;
          const allUser = this.allUser();
          return allUser.some(
            (c) => c.importedFrom === id || remoteId && c.importedFrom === remoteId || calc?.remoteId && c.importedFrom === calc.remoteId
          );
        }
        // ==========================================================================
        // SUBSCRIBE
        // ==========================================================================
        /**
         * Subscribe to calc changes
         * Callback receives (calc, detail) where detail contains source and change metadata
         * @param {Function} callback - Function called on calc changes: (calc, detail) => void
         * @returns {Function} Unsubscribe function - call to stop receiving updates
         */
        subscribe(callback) {
          this._callbacks.push(callback);
          return () => {
            const idx = this._callbacks.indexOf(callback);
            if (idx > -1) this._callbacks.splice(idx, 1);
          };
        }
        // ==========================================================================
        // REMOTE SYNC (Firebase)
        // ==========================================================================
        /**
         * ROW-LEVEL MERGE
         * Instead of replacing all rows, merge at row level:
         * - If User A edits row 1 and User B edits row 3, keep both
         * - Local edits (rows that differ from lastPushed) are preserved
         * - Remote edits are applied to rows we haven't touched
         */
        applyRemoteUpdate(localCalc, remoteData) {
          Debug9.log("store", "applyRemoteUpdate:", localCalc.id);
          const localRows = localCalc.rows || [];
          const remoteRows = remoteData.rows || [];
          const lastPushed = this._lastPushedRows.get(localCalc.id) || [];
          const maxLen = Math.max(localRows.length, remoteRows.length, lastPushed.length);
          const mergedRows = [];
          for (let i = 0; i < maxLen; i++) {
            const local = localRows[i];
            const remote = remoteRows[i];
            const pushed = lastPushed[i];
            const localText = local?.text ?? "";
            const remoteText = remote?.text ?? "";
            const pushedText = pushed?.text ?? "";
            const localEdited = localText !== pushedText;
            const remoteEdited = remoteText !== pushedText;
            if (localEdited && remoteEdited) {
              Debug9.log("store", `Row ${i} conflict: keeping remote`);
              mergedRows.push(remote || { text: "" });
            } else if (localEdited) {
              mergedRows.push(local || { text: "" });
            } else {
              mergedRows.push(remote || local || { text: "" });
            }
          }
          const updated = {
            ...localCalc,
            rows: mergedRows,
            updated: remoteData.updated
          };
          if (remoteData.name !== void 0) {
            updated.name = remoteData.name;
          }
          if (CalcType.isCollab(updated)) {
            return this.saveCollabCalcLocal(updated, { source: UPDATE_SOURCE.REMOTE });
          }
          return this.saveRemoteCalcLocal(updated, { source: UPDATE_SOURCE.REMOTE });
        }
        /**
         * Subscribe to real-time Firebase updates for a remote calc
         * Handles echo prevention and row-level merge for concurrent edits
         * @param {string} sourceType - Type of remote source ('wiki', 'live', 'collab')
         * @param {string} identifier - Remote identifier (slug, token, etc.)
         * @param {string} localId - Local calc ID to update when remote changes
         * @returns {Function} Unsubscribe function
         */
        subscribeToRemote(sourceType, identifier, localId) {
          if (!this._remote.available) {
            Debug9.warn("store", "Firebase not available");
            return () => {
            };
          }
          const handler = (remoteData) => {
            if (!this._lastPushedRows.has(localId)) {
              this._lastPushedRows.set(localId, JSON.parse(JSON.stringify(remoteData.rows || [])));
              Debug9.log("store", "\u{1F4E5} Initialized merge baseline from remote");
            }
            if (remoteData.lastEditBy === this._sessionId) return;
            if (this._pendingPushes.get(localId) === remoteData.updated) return;
            const localCalc = this.get(localId);
            const localUpdated = window.Timestamps.parse(localCalc?.updated);
            const remoteUpdated = window.Timestamps.parse(remoteData.updated);
            if (localCalc && remoteUpdated > localUpdated) {
              Debug9.log("store", "\u2728 Applying remote update");
              this.applyRemoteUpdate(localCalc, remoteData);
            }
          };
          const unsubscribe = this._remote.subscribe(sourceType, identifier, handler);
          this._subscriptions[localId] = unsubscribe;
          return unsubscribe;
        }
        /**
         * Unsubscribe from Firebase updates for a calc
         * @param {string} localId - The local calc ID
         */
        unsubscribeFromRemote(localId) {
          if (this._subscriptions[localId]) {
            this._subscriptions[localId]();
            delete this._subscriptions[localId];
          }
        }
        /**
         * Load a remote calc (initial load from Firebase)
         * Saves to session store WITHOUT notifying (silent initial load)
         */
        loadFromRemote(calc) {
          return this._saveToSessionStore(calc);
        }
        /**
         * Load a URL calc (from query params/hash)
         * Saves to session store WITHOUT notifying (silent initial load)
         */
        loadUrlCalc(calc) {
          return this._saveToSessionStore(calc);
        }
        // ==========================================================================
        // QUERY METHODS
        // ==========================================================================
        /**
         * Check if a calc is stored in session (ephemeral, not persisted)
         * @param {string} id - The calc ID
         * @returns {boolean} True if calc is in session storage
         */
        isSessionCalc(id) {
          const calc = this.get(id);
          return calc && this._isSessionCalc(calc);
        }
        /**
         * Check if a calc syncs to Firebase (wiki, live, or collab)
         * @param {string} id - The calc ID
         * @returns {boolean} True if calc syncs to remote
         */
        isRemoteCalc(id) {
          const calc = this.get(id);
          return calc && this._isRemoteCalc(calc);
        }
        /**
         * Get the CalcType for a calc by ID
         * @param {string} id - The calc ID
         * @returns {string|null} CalcType value or null if not found
         */
        getCalcType(id) {
          const calc = this.get(id);
          return calc ? calc.type || "local" : null;
        }
        // ==========================================================================
        // INTERNAL HELPERS
        // ==========================================================================
        /**
         * Check if calc should be stored in session (not localStorage)
         * @deprecated Use CalcType.getStorage(calc.type) instead
         */
        _isSessionCalc(calc) {
          const CalcType2 = getCalcType();
          return CalcType2.getStorage(calc.type) === "session";
        }
        /**
         * Check if calc is a remote collaborative calc (syncs to Firebase)
         * @deprecated Use CalcType.isRemote(calc) instead
         */
        _isRemoteCalc(calc) {
          const CalcType2 = getCalcType();
          return CalcType2.isRemote(calc);
        }
        _validate(calc) {
          const CalcType2 = getCalcType();
          if (!Array.isArray(calc.rows)) {
            calc.rows = Array(5).fill(null).map(() => ({ text: "" }));
          }
          calc.rows = calc.rows.map((row) => {
            const safeRow = row || { text: "" };
            if (!safeRow.id) {
              return { ...safeRow, id: makeRowId() };
            }
            return safeRow;
          });
          calc.updated = calc.updated ? window.Timestamps.parse(calc.updated) : window.Timestamps.now();
          if (calc.created != null) {
            calc.created = window.Timestamps.parse(calc.created);
          } else {
            calc.created = calc.updated;
          }
          if (calc.type && !CalcType2.isValid(calc.type)) {
            Debug9.warn("store", "Invalid calc type detected, clearing:", calc.type);
            calc.type = null;
          }
          if (!Object.hasOwn(calc, "syncRef")) {
            calc = CalcMigration.migrate(calc);
          }
          return calc;
        }
        _generateGuid() {
          return CalcId.generateUUID();
        }
        // ==========================================================================
        // LOCALSTORAGE
        // ==========================================================================
        _loadFromLocalStorage() {
          this._userCalcs = this._persistence.loadUserCalcs();
          this._collabCalcs = this._persistence.loadCollabCalcs();
          this._mergeFromIndexedDB().then(() => {
            this._populateIndexedDBFromLocalStorage();
          });
        }
        _persistToLocalStorage(options = {}) {
          this._persistence.saveUserCalcs(this._userCalcs, options);
        }
        _persistCollabsToLocalStorage() {
          this._persistence.saveCollabCalcs(this._collabCalcs);
        }
        // ==========================================================================
        // INDEXEDDB PERSISTENCE (Offline-First Layer)
        // ==========================================================================
        /**
         * Detect replay storms (>50 saves in 500ms)
         * @private
         */
        _detectReplayStorm() {
          if (typeof performance === "undefined" || !performance.now) {
            return false;
          }
          const now2 = performance.now();
          this._saveTimestamps.push(now2);
          const cutoff = now2 - 500;
          this._saveTimestamps = this._saveTimestamps.filter((t) => t > cutoff);
          if (this._saveTimestamps.length > 50) {
            this._replayCount++;
            const stack = new Error().stack;
            Debug9.warn("store", `\u26A0\uFE0F Replay storm detected! ${this._saveTimestamps.length} saves in 500ms (incident #${this._replayCount})`);
            Debug9.log("store:verbose", "Storm callsite:", stack);
            this._saveTimestamps = [];
            return true;
          }
          return false;
        }
        /**
         * Persist calc to IndexedDB via idle-flush queue
         * Coalesces multiple writes per calc to reduce main-thread overhead during typing
         * @private
         */
        _persistToIndexedDB(calc) {
          if (typeof window === "undefined" || !window.offlineStorage) return;
          if (!this._features.OFFLINE_PERSISTENCE) return;
          if (calc.type === "user" && !this._features.IDB_USER_CALCS) return;
          this._detectReplayStorm();
          if (!this._features.IDB_FLUSH_ON_IDLE) {
            window.offlineStorage.saveCalc(calc).catch((err) => {
              Debug9.warn("store", "IndexedDB save failed (localStorage OK):", err.message);
            });
            return;
          }
          this._pendingIDBWrites.set(calc.id, calc);
          if (!this._idbFlushScheduled) {
            this._idbFlushScheduled = true;
            if (typeof window.requestIdleCallback === "function") {
              window.requestIdleCallback(() => this._flushIDBQueue(), { timeout: 2e3 });
            } else {
              this._idbFlushTimer = setTimeout(() => this._flushIDBQueue(), 2e3);
            }
          }
        }
        /**
         * Flush pending IndexedDB writes
         * @private
         */
        _flushIDBQueue() {
          this._idbFlushScheduled = false;
          if (this._idbFlushTimer) {
            clearTimeout(this._idbFlushTimer);
            this._idbFlushTimer = null;
          }
          const toWrite = Array.from(this._pendingIDBWrites.values());
          this._pendingIDBWrites.clear();
          if (toWrite.length === 0) return;
          Debug9.log("offline", `\u{1F4BE} Flushing ${toWrite.length} calc(s) to IndexedDB`);
          toWrite.forEach((calc) => {
            window.offlineStorage.saveCalc(calc).catch((err) => {
              Debug9.warn("offline", "IDB flush failed:", calc.id.slice(0, 8), err.message);
            });
          });
        }
        /**
         * Merge IndexedDB data into memory (async)
         * IndexedDB may have edits from sessions where browser crashed before localStorage saved
         * @private
         */
        async _mergeFromIndexedDB() {
          if (typeof window === "undefined" || !window.offlineStorage) return;
          try {
            const idbCalcs = await window.offlineStorage.getAllCalcs();
            let mergeCount = 0;
            for (const [id, idbCalc] of Object.entries(idbCalcs)) {
              const localCalc = this._userCalcs[id] || this._collabCalcs[id];
              if (!localCalc) {
                Debug9.log("store", "Recovering calc from IndexedDB:", id);
                this._userCalcs[id] = idbCalc;
                mergeCount++;
              } else if ((idbCalc.updated || 0) > (localCalc.updated || 0)) {
                Debug9.log("store", "Merging newer IndexedDB version:", id);
                if (this._userCalcs[id]) {
                  this._userCalcs[id] = idbCalc;
                } else {
                  this._collabCalcs[id] = idbCalc;
                }
                mergeCount++;
              }
            }
            if (mergeCount > 0) {
              Debug9.log("store", `Merged ${mergeCount} calcs from IndexedDB`);
              this._persistToLocalStorage();
              this._persistCollabsToLocalStorage();
              this._notifyChanged(null);
            }
          } catch (err) {
            Debug9.warn("store", "IndexedDB merge failed:", err.message);
          }
        }
        /**
         * Delete calc from IndexedDB (non-blocking)
         * @private
         */
        _deleteFromIndexedDB(id) {
          if (typeof window === "undefined" || !window.offlineStorage) return;
          window.offlineStorage.deleteCalc(id).catch((err) => {
            Debug9.warn("store", "IndexedDB delete failed:", err.message);
          });
        }
        /**
         * Populate IndexedDB with all localStorage calcs (bidirectional sync)
         * Ensures IndexedDB has a complete backup of all user data.
         * Runs after _mergeFromIndexedDB() on app startup.
         * @private
         */
        async _populateIndexedDBFromLocalStorage() {
          if (typeof window === "undefined" || !window.offlineStorage) return;
          try {
            const idbCalcs = await window.offlineStorage.getAllCalcs();
            const calcsToSync = {};
            const allLocalCalcs = { ...this._userCalcs, ...this._collabCalcs };
            for (const [id, calc] of Object.entries(allLocalCalcs)) {
              const idbCalc = idbCalcs[id];
              if (!idbCalc || (calc.updated || 0) > (idbCalc.updated || 0)) {
                calcsToSync[id] = calc;
              }
            }
            const syncCount = Object.keys(calcsToSync).length;
            if (syncCount > 0) {
              Debug9.log("store", `Populating IndexedDB with ${syncCount} calcs from localStorage`);
              await window.offlineStorage.saveAllCalcs(calcsToSync);
            }
          } catch (err) {
            Debug9.warn("store", "IndexedDB population failed:", err.message);
          }
        }
        // ==========================================================================
        // NOTIFICATIONS
        // ==========================================================================
        notifyChanged(calc, detail = {}) {
          this._notifyChanged(calc, detail);
        }
        /**
         * Notify subscribers of calc changes
         * @param {Object} calc - The updated calc
         * @param {Object} detail - Change details
         * @param {string} detail.source - UPDATE_SOURCE.LOCAL for user edits, UPDATE_SOURCE.REMOTE for Firebase/external updates
         */
        _notifyChanged(calc, detail = {}) {
          if (this._batchMode) {
            this._batchedCalcs.add(calc?.id);
            return;
          }
          const enrichedDetail = { source: UPDATE_SOURCE.LOCAL, ...detail };
          if (typeof window !== "undefined" && window.DebugLogger?.isEnabled("store")) {
            const row0Text = calc?.rows?.[0]?.text || "";
            window.DebugLogger.log("store", `NOTIFY calcId=${calc?.id} type=${calc?.type} row0="${row0Text.slice(0, 30)}" subscribers=${this._callbacks.length}`);
          }
          this._callbacks.forEach((fn) => {
            try {
              fn(calc, enrichedDetail);
            } catch (e) {
              console.error("[CalcStore] Subscriber error:", e);
            }
          });
          const isTyping = detail?.immediate === true;
          if (!isTyping && typeof window !== "undefined" && window.InstacalcEvents?.dispatch) {
            window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_CHANGED, {
              calc,
              calcId: calc?.id,
              ...enrichedDetail
            });
          }
        }
        // ==========================================================================
        // FIREBASE SYNC
        // ==========================================================================
        _getFirebasePathFromCalc(calc) {
          const CalcType2 = getCalcType();
          if (!CalcType2.isRemote(calc) || !calc.remoteId) return null;
          return typeof window !== "undefined" && window.FirebasePaths ? window.FirebasePaths.getRemotePath(calc.type, calc.remoteId) : null;
        }
        /**
         * Sync user calc to their Firebase space: /users/{uid}/calcs/{id}
         * Called when saving user's personal calcs (not live/wiki calcs)
         * Supports both logged-in and anonymous users
         * Also subscribes to remote updates for cross-device sync
         */
        syncUserCalc(calc) {
          const CalcType2 = getCalcType();
          if (!this._remote.available) return;
          if (!CalcType2.isUserOwned(calc.type)) return;
          const fbUser = this._remote._auth?.currentUser;
          const actionUser = typeof window !== "undefined" && window.Action?.getCurrentUser?.();
          const uid = fbUser?.uid || actionUser?.uid;
          if (!uid) return;
          if (this._syncTimeouts[`user-${calc.id}`]) {
            clearTimeout(this._syncTimeouts[`user-${calc.id}`]);
          }
          this._syncTimeouts[`user-${calc.id}`] = setTimeout(() => {
            if (!fbUser?.isAnonymous && uid) {
              this._remote.syncUserCalc(uid, calc.id, calc).then(() => Debug9.log("store", "\u2705 User sync successful")).catch((err) => console.error("[CalcStore] \u274C User sync failed:", err.message || err));
            }
            if (calc.published?.id) {
              const sharedCalc = {
                ...calc,
                updated: window.Timestamps?.now() || Date.now(),
                uid
              };
              this._remote.syncSharedCalc(calc.published.id, sharedCalc).then(() => Debug9.log("store", "\u2705 Shared sync successful")).catch((err) => console.error("[CalcStore] \u274C Shared sync failed:", err.message || err));
            }
          }, 250);
        }
        _pushToFirebase(calc) {
          if (!this._firebaseAvailable()) {
            this._queueFirebasePush(calc?.id);
            return;
          }
          const path = this._getFirebasePathFromCalc(calc);
          if (!path) return;
          if (calc.notYetSavedToFirebase) {
            Debug9.log("store", "\u{1F3AF} First keystroke - creating in Firebase:", path);
            const timestamp = Date.now();
            this._pendingPushes.set(calc.id, timestamp);
            this._lastPushedRows.set(calc.id, JSON.parse(JSON.stringify(calc.rows || [])));
            const data = {
              name: calc.name,
              rows: calc.rows,
              updated: timestamp,
              created: calc.created || timestamp,
              lastEditBy: this._sessionId
            };
            firebase.database().ref(path).set(data).then(() => {
              Debug9.log("store", "\u2705 Created successfully, starting subscription");
              calc.notYetSavedToFirebase = false;
              this._saveToSessionStore(calc);
              this.subscribeToRemote(calc.type, calc.remoteId, calc.id);
            }).catch((err) => console.error("[CalcStore] \u274C Create failed:", err.message || err));
            return;
          }
          if (this._syncTimeouts[calc.id]) {
            clearTimeout(this._syncTimeouts[calc.id]);
          }
          this._syncTimeouts[calc.id] = setTimeout(() => {
            const timestamp = Date.now();
            this._pendingPushes.set(calc.id, timestamp);
            this._lastPushedRows.set(calc.id, JSON.parse(JSON.stringify(calc.rows || [])));
            const data = {
              name: calc.name,
              rows: calc.rows,
              updated: timestamp,
              lastEditBy: this._sessionId
            };
            if (calc.created != null) {
              data.created = calc.created;
            }
            Debug9.log("store", "\u{1F53C} Pushing to Firebase:", path);
            firebase.database().ref(path).set(data).then(() => Debug9.log("store", "\u2705 Push successful")).catch((err) => console.error("[CalcStore] \u274C Push failed:", err.message || err));
          }, 250);
        }
        // ==========================================================================
        // ACTIVE CALC (delegates to SelectionController)
        // ==========================================================================
        // NOTE: These methods delegate to SelectionController for selection state.
        // CalcStore remains the SSOT for calc DATA, SelectionController is SSOT for SELECTION.
        /**
         * Get SelectionController instance
         * @private
         */
        _getSelectionController() {
          return typeof window !== "undefined" ? window.selectionController : null;
        }
        /**
         * Get the currently active calc object
         * Delegates to SelectionController for selection state
         */
        getActiveCalc() {
          const controller = this._getSelectionController();
          const activeId = controller?.getSelectedId() || null;
          if (!activeId) return null;
          return this.get(activeId);
        }
        /**
         * Get the active calc ID
         * Delegates to SelectionController
         */
        getActiveCalcId() {
          const controller = this._getSelectionController();
          return controller?.getSelectedId() || null;
        }
        /**
         * Set the active calc (by calc object or ID)
         * @param {string|object} calcOrId - Calc object or ID
         * @param {string} viewContext - View context from ViewContext module
         * Delegates to SelectionController, which dispatches CALC_SWITCH event
         */
        setActiveCalc(calcOrId, viewContext = null) {
          const ViewContext = getViewContext();
          viewContext = viewContext || ViewContext.HOME;
          const calc = typeof calcOrId === "string" ? this.get(calcOrId) : calcOrId;
          if (!calc) {
            Debug9.warn("store", "setActiveCalc: calc not found", calcOrId);
            return null;
          }
          Debug9.log("store", "setActiveCalc:", calc.id, "viewContext:", viewContext);
          const controller = this._getSelectionController();
          if (controller) {
            const oldId = controller.getSelectedId();
            controller.select(calc.id, "main", { viewContext });
            if (oldId !== calc.id) {
              this._notifyChanged(calc);
            }
          }
          return calc;
        }
        /**
         * Get the current view context
         * Delegates to SelectionController
         */
        getActiveViewContext() {
          const controller = this._getSelectionController();
          return controller?.getViewContext() || getViewContext().HOME;
        }
        _rowsEqual(aRows, bRows) {
          if (aRows === bRows) return true;
          if (!Array.isArray(aRows) || !Array.isArray(bRows)) return false;
          if (aRows.length !== bRows.length) return false;
          const ignoredRowKeys = /* @__PURE__ */ new Set(["result", "error"]);
          for (let i = 0; i < aRows.length; i += 1) {
            const aRow = aRows[i];
            const bRow = bRows[i];
            if (aRow === bRow) continue;
            if (!aRow || !bRow) return false;
            const aKeys = Object.keys(aRow).filter((key) => !ignoredRowKeys.has(key)).sort();
            const bKeys = Object.keys(bRow).filter((key) => !ignoredRowKeys.has(key)).sort();
            if (aKeys.length !== bKeys.length) return false;
            for (let k = 0; k < aKeys.length; k += 1) {
              if (aKeys[k] !== bKeys[k]) return false;
              const key = aKeys[k];
              if (aRow[key] !== bRow[key]) return false;
            }
          }
          return true;
        }
        _calcContentEquals(a, b) {
          if (a === b) return true;
          if (!a || !b) return false;
          const ignoredCalcKeys = /* @__PURE__ */ new Set(["updated", "results", "history", "_historyManager"]);
          const aKeys = Object.keys(a).filter((key) => !ignoredCalcKeys.has(key)).sort();
          const bKeys = Object.keys(b).filter((key) => !ignoredCalcKeys.has(key)).sort();
          if (aKeys.length !== bKeys.length) return false;
          for (let i = 0; i < aKeys.length; i += 1) {
            const key = aKeys[i];
            if (key !== bKeys[i]) return false;
            if (key === "rows") {
              if (!this._rowsEqual(a.rows, b.rows)) return false;
              continue;
            }
            const aVal = a[key];
            const bVal = b[key];
            if (aVal === bVal) continue;
            if (aVal == null || bVal == null) return false;
            if (typeof aVal === "object" || typeof bVal === "object") {
              if (JSON.stringify(aVal) !== JSON.stringify(bVal)) return false;
              continue;
            }
            return false;
          }
          return true;
        }
        /**
         * Update the active calc data and notify
         * Use this after mutations (updateRowText, insertRow, etc.)
         * Delegates embed mode check to SelectionController
         */
        updateActiveCalc(calc) {
          if (!calc || !calc.id) return null;
          const controller = this._getSelectionController();
          if (controller?.isEmbedMode()) {
            Debug9.error("store", "updateActiveCalc blocked in embed mode - use saveEmbedCalc() instead", {
              calcId: calc.id,
              embedMode: true
            });
            return null;
          }
          const activeId = controller?.getSelectedId() || null;
          const existing = this.get(calc.id);
          if (activeId === calc.id && this._calcContentEquals(existing, calc)) {
            return existing || calc;
          }
          const idChanged = activeId !== calc.id;
          if (idChanged && controller) {
            controller.select(calc.id, "main", { skipEvent: true });
          }
          const saved = this.save(calc, { __fromMutation: true });
          if (idChanged && controller) {
            controller.select(saved.id, "main", { skipPersist: true });
          }
          return saved;
        }
        // ==========================================================================
        // ACCESSORS
        // ==========================================================================
        get sessionId() {
          return this._sessionId;
        }
        get activeCalcId() {
          const controller = this._getSelectionController();
          return controller?.getSelectedId() || null;
        }
        // ==========================================================================
        // TEST UTILITIES
        // ==========================================================================
        static createMock(options = {}) {
          const mockStorage = {
            _data: {},
            getItem(k) {
              return this._data[k] || null;
            },
            setItem(k, v) {
              this._data[k] = v;
            },
            removeItem(k) {
              delete this._data[k];
            },
            clear() {
              this._data = {};
            }
          };
          const mockPersistence = new PersistenceLayer({
            localStorage: mockStorage,
            debounceMs: 0
            // Immediate writes for tests
          });
          const SelectionController = typeof window !== "undefined" ? window.SelectionController : typeof __require !== "undefined" ? require_SelectionController() : null;
          if (SelectionController && typeof window !== "undefined") {
            const mockSelectionController = new SelectionController({
              autoLoad: false,
              persistence: mockPersistence
            });
            window.selectionController = mockSelectionController;
          }
          const store = new _CalcStore({
            localStorage: mockStorage,
            persistence: mockPersistence,
            autoLoad: false,
            ...options
          });
          store._localStorage = mockStorage;
          if (typeof jest !== "undefined" && jest.spyOn) {
            const methodsToSpy = ["save", "saveUserCalc", "saveUserCalcLocal", "delete"];
            methodsToSpy.forEach((method) => {
              if (typeof store[method] === "function") {
                jest.spyOn(store, method);
              }
            });
          }
          return store;
        }
        _reset() {
          this._userCalcs = {};
          this._sessionCalcs = {};
          this._callbacks = [];
          this._pendingPushes.clear();
          this._lastPushedRows.clear();
          Object.values(this._syncTimeouts).forEach((t) => clearTimeout(t));
          this._syncTimeouts = {};
          Object.values(this._subscriptions).forEach((u) => u());
          this._subscriptions = {};
          const controller = this._getSelectionController();
          if (controller?.reset) {
            controller.reset();
          }
        }
        _debug() {
          return { user: this._userCalcs, session: this._sessionCalcs };
        }
      };
      CalcStore.getCalcType = getCalcType;
      CalcStore.CALC_TYPE = {
        LIVE: "live",
        WIKI: "wiki",
        PRIVATE: "private"
        // Legacy alias for COLLAB
      };
      CalcStore.PERSIST = {
        USER: "user",
        SESSION: "session",
        SNIPPET: "snippet"
      };
      CalcStore.UPDATE_SOURCE = UPDATE_SOURCE;
      CalcStore.getViewContext = getViewContext;
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = CalcStore;
        module2.exports.UPDATE_SOURCE = UPDATE_SOURCE;
        module2.exports.getViewContext = getViewContext;
      }
      if (typeof window !== "undefined") {
        window.CalcStore = CalcStore;
        if (!window.calcStore) {
          window.calcStore = new CalcStore();
          window.dispatchEvent(new CustomEvent("ic:calc-store-ready"));
        }
      }
    }
  });

  // public/src/models/CalcModel.js
  var require_CalcModel = __commonJS({
    "public/src/models/CalcModel.js"(exports, module2) {
      var Debug9 = typeof window !== "undefined" && window.Debug || {
        log: () => {
        },
        warn: (...args) => console.warn("[CalcModel]", ...args),
        error: (...args) => console.error("[CalcModel]", ...args)
      };
      var CalcId = typeof window !== "undefined" && window.CalcId || require_CalcId();
      var CalcType = typeof window !== "undefined" && window.CalcType || require_CalcType();
      var Timestamps = typeof window !== "undefined" && window.Timestamps || (init_timestamps(), __toCommonJS(timestamps_exports));
      var makeRowId = function() {
        if (typeof window !== "undefined" && window.Util?.guid) {
          return window.Util.guid();
        }
        if (typeof global !== "undefined" && global.Util?.guid) {
          return global.Util.guid();
        }
        return `row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      };
      var normalizeRows = function(rows) {
        if (!Array.isArray(rows)) return [];
        return rows.map((row) => {
          const safeRow = row || { text: "", hidden: false };
          if (!safeRow.id) {
            return { ...safeRow, id: makeRowId() };
          }
          return safeRow;
        });
      };
      var EMPTY_ROW2 = function(n) {
        var rows = [];
        for (var i = 0; i < n; i++) {
          rows.push({ id: makeRowId(), text: "" });
        }
        return rows;
      };
      var DEFAULT_NAME = "Scratchpad";
      var DEMO_ROWS = [
        { text: "" },
        { text: "" },
        { text: "// Quickstart: trip planning" },
        { text: "budget = 1400" },
        { text: "cost = 500 + 300 + 250 // hotel, flight, food" },
        { text: "remaining = budget - cost" }
      ];
      var DEMO_ROWS_NEW = [
        { text: "" },
        { text: "" },
        { text: "// just type and answers appear" },
        { text: "50 + 300*2" },
        { text: "net income = $75k - 30%" },
        { text: "500 USD to EUR" },
        { text: "25 C to F" },
        { text: "50 miles @ 75 mph in minutes" },
        { text: "20 mbps in minutes until 5 gigabytes" }
      ];
      var CalcModel = {
        EMPTY_ROW: EMPTY_ROW2,
        DEFAULT_NAME,
        DEMO_ROWS,
        DEMO_ROWS_NEW,
        /**
         * Create a new calculator object with defaults
         * @param {Object} [obj] - Initial properties
         * @param {string} [obj.id] - Calc ID (auto-generated if not provided)
         * @param {string} [obj.name] - Calc name (defaults to "Scratchpad")
         * @param {Array} [obj.rows] - Row data (defaults to 5 empty rows)
         * @param {string} [obj.type] - CalcType value (defaults to USER)
         * @param {Object} [obj.syncRef] - Sync reference for remote calcs
         * @param {string} [obj.viewMode] - View mode ('text', 'calc', 'present')
         * @returns {Object} A new calc object with all required fields
         */
        create(obj) {
          const now2 = Timestamps.now();
          const type = obj?.type || CalcType.USER;
          const id = obj?.id || CalcId.generate(type, {
            content: obj?.content,
            slug: obj?.slug,
            token: obj?.token
          });
          Debug9.log("model", "\u{1F195} create:", { id: id?.slice(0, 8), type, name: obj?.name || DEFAULT_NAME, rows: obj?.rows?.length || 5 });
          const baseRows = Array.isArray(obj?.rows) ? obj.rows : EMPTY_ROW2(5);
          var calc = {
            id,
            name: obj?.name || DEFAULT_NAME,
            rows: normalizeRows(baseRows),
            updated: now2,
            created: now2,
            published: null,
            version: 2,
            viewMode: obj?.viewMode ?? null,
            // null = auto-detect based on content
            type,
            syncRef: obj?.syncRef || null
            // syncRef is the new sync abstraction
          };
          if (obj) {
            for (const key in obj) {
              if (!Object.hasOwn(calc, key)) {
                calc[key] = obj[key];
              }
            }
          }
          return calc;
        },
        /**
         * Validate and normalize a calc object
         * Ensures rows have IDs, type is set, and required fields exist
         * @param {Object} calc - Calc object to validate
         * @returns {Object|null} The validated calc or null if invalid
         */
        validate(calc) {
          if (!calc || typeof calc !== "object" || !calc.id) {
            Debug9.warn("Invalid calc:", calc);
            return null;
          }
          Debug9.log("model", "\u2705 validate:", { id: calc.id?.slice(0, 8), type: calc.type, rows: calc.rows?.length });
          if (!calc.type) {
            calc.type = CalcType.USER;
          }
          if (!Array.isArray(calc.rows) || calc.rows.length === 0) {
            console.warn("[CalcModel] Invalid or empty rows, creating defaults");
            calc.rows = EMPTY_ROW2(5);
          }
          calc.rows = normalizeRows(calc.rows);
          return calc;
        }
      };
      if (typeof window !== "undefined") {
        window.CalcModel = CalcModel;
        window.EMPTY_ROW = EMPTY_ROW2;
      }
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = CalcModel;
      }
    }
  });

  // public/src/actions/AuthService.js
  var require_AuthService = __commonJS({
    "public/src/actions/AuthService.js"() {
      (function() {
        var Action3 = window.Action || {};
        const authLog = (message, data = null) => {
          if (window.Debug) window.Debug.log("auth", message, data);
          if (window.AuthDebugLog) window.AuthDebugLog.log("auth", message, data);
        };
        window.user = null;
        Action3.getCurrentUserId = function() {
          return window.user?.uid || firebase.auth().currentUser.uid;
        };
        Action3.getCurrentUser = function() {
          return window.user;
        };
        Action3.setCurrentUser = function(user) {
          const previousUser = window.user;
          window.user = user;
          if (window.InstacalcEvents) {
            authLog("Dispatching USER_CHANGED event", {
              uid: user?.uid,
              email: user?.email,
              isAnonymous: user?.isAnonymous,
              previousUid: previousUser?.uid
            });
            window.InstacalcEvents.dispatch(window.InstacalcEvents.USER_CHANGED, {
              user,
              previousUser
            });
          }
        };
        Action3.updateUserData = function(uid, name, email) {
          firebase.database().ref("users/" + uid).update({
            username: name,
            email
          });
        };
        Action3.login = function() {
          localStorage["_lastlogin"] = Date.now();
          window.location = "/login.html";
        };
        Action3.logout = function() {
          if (Action3.getCurrentUser()) {
            firebase.auth().signOut().then(
              function() {
                Calc.clearLocalStore();
                localStorage.clear();
                if (window.offlineStorage?.clear) {
                  window.offlineStorage.clear().catch((err) => {
                    console.warn("[Logout] Failed to clear IndexedDB:", err);
                  });
                }
                if (Action3._remoteCalcsLoadedByUID) {
                  Action3._remoteCalcsLoadedByUID = {};
                }
                window.location = "/";
              },
              function(error) {
                console.error("Sign Out Error", error);
              }
            );
          }
        };
        Action3.isLoggedIn = function() {
          return window.user && window.user.uid;
        };
        Action3.isOwner = function(calc) {
          return window.user && window.user.uid == calc.uid;
        };
        Action3.__firebase_count = 0;
        Action3.loadFirebase = function() {
          authLog("loadFirebase called", { count: Action3.__firebase_count });
          Action3.__firebase_count++;
          if (typeof firebase === "undefined" || !firebase.auth || !firebase.database) {
            authLog("Firebase not ready, will retry in 250ms", null);
            setTimeout(Action3.loadFirebase, 250);
            return;
          }
          if (!firebase.apps || firebase.apps.length === 0) {
            authLog("Initializing Firebase app", null);
            if (window.FIREBASE_CONFIG) {
              firebase.initializeApp(window.FIREBASE_CONFIG);
              if (window.connectToEmulators) {
                window.connectToEmulators();
              }
              if (firebase.auth && firebase.auth.Auth?.Persistence?.LOCAL) {
                firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL).then(() => authLog("Auth persistence set to LOCAL")).catch((err) => authLog("Failed to set auth persistence", { error: err.message }));
              }
            } else {
              console.error("FIREBASE_CONFIG not found. Ensure firebase-config.js is loaded.");
            }
          } else {
            authLog("Firebase already initialized", null);
          }
          window.InitCoordinator?.signal("firebase");
        };
        Action3.checkUserLogin = function(callback) {
          const mockUser = new URLSearchParams(window.location.search).get("mockUser");
          if (mockUser && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
            const fakeUser = {
              uid: "mock_" + mockUser,
              displayName: mockUser,
              email: mockUser + "@mock.instacalc.com",
              alias: mockUser,
              isMockUser: true
            };
            window.user = fakeUser;
            window._mockUserActive = true;
            authLog("[MockAuth] Logged in as mock user", { mockUser });
            callback(fakeUser);
            return;
          }
          function afterLogin(user) {
            if (user?.isAnonymous) {
              authLog("afterLogin: Anonymous user, skipping", null);
              return;
            }
            if (!user || !user.uid) {
              console.error("invalid user: ", user);
              return;
            }
            authLog("afterLogin: Setting user", { uid: user.uid, email: user.email });
            Action3.setCurrentUser(user);
            Action3.loadRemoteCalcs(user.uid);
            Action3.updateUserData(user.uid, user.displayName, user.email);
            callback(user);
          }
          let authCheckResolve;
          window.authCheckComplete = new Promise((resolve) => {
            authCheckResolve = resolve;
          });
          const performAuthCheck = () => {
            authLog("performAuthCheck called", null);
            authLog("Calling getRedirectResult...", null);
            firebase.auth().getRedirectResult().then(function(result) {
              authLog("getRedirectResult returned", { hasUser: !!result?.user, uid: result?.user?.uid });
              const firebaseUser = firebase.auth().currentUser;
              if (result?.user) {
                afterLogin(result.user);
              } else if (!firebaseUser) {
                authLog("No Firebase currentUser, calling signInAnonymously...", null);
                firebase.auth().signInAnonymously();
                callback(null);
              } else {
                authLog("Firebase currentUser already exists", { uid: firebaseUser.uid, isAnonymous: firebaseUser.isAnonymous });
                callback(null);
              }
            }).catch((error) => {
              authLog("AUTH ERROR", { code: error?.code, message: error?.message });
              console.log("AUTH ERROR: ", error);
              if (authCheckResolve) {
                authCheckResolve();
              }
            });
            authLog("Setting up onAuthStateChanged listener", null);
            firebase.auth().onAuthStateChanged(function(user) {
              authLog("onAuthStateChanged fired", { hasUser: !!user, isAnonymous: user?.isAnonymous, uid: user?.uid });
              if (user && user.isAnonymous === false) {
                afterLogin(user);
              } else if (user && user.isAnonymous === true) {
                authLog("Setting anonymous user in window.user", { uid: user.uid });
                window.user = user;
              } else {
                if (Math.abs(localStorage["_lastlogin"] - Date.now()) < 30 * 1e3) {
                }
              }
              if (authCheckResolve) {
                authCheckResolve();
                authCheckResolve = null;
              }
            });
            setTimeout(() => {
              if (authCheckResolve) {
                authLog("Auth check timeout - proceeding with local loading", null);
                authCheckResolve();
                authCheckResolve = null;
              }
            }, 500);
          };
          Action3.loadFirebase();
          if (window.InitCoordinator) {
            window.InitCoordinator.whenReady("firebase", performAuthCheck);
          } else {
            const check = () => {
              if (typeof firebase !== "undefined" && firebase.apps?.length > 0) {
                performAuthCheck();
              } else {
                setTimeout(check, 250);
              }
            };
            check();
          }
        };
      })();
    }
  });

  // public/src/actions/HistoryService.js
  var require_HistoryService = __commonJS({
    "public/src/actions/HistoryService.js"() {
      (function() {
        var Action3 = window.Action || {};
        const historyManagers = /* @__PURE__ */ new Map();
        Action3.getHistoryManager = function(calc) {
          if (!calc || !calc.id) {
            console.warn("getHistoryManager called with invalid calc:", calc);
            return createMinimalManager();
          }
          const calcId = calc.id;
          if (!historyManagers.has(calcId)) {
            if (window.HistoryManager) {
              historyManagers.set(calcId, new window.HistoryManager(calcId));
            } else {
              console.warn("HistoryManager not found, using minimal fallback");
              historyManagers.set(calcId, createMinimalManager());
            }
          }
          const manager = historyManagers.get(calcId);
          manager.updateCalcReference(calc);
          return manager;
        };
        function createMinimalManager() {
          return {
            updateCalcReference: () => {
            },
            captureSnapshot: () => {
            },
            undo: () => false,
            redo: () => false,
            canUndo: () => false,
            canRedo: () => false,
            getHistory: () => [],
            getSnapshot: () => null,
            getDiffToCurrent: () => null,
            _displayIndexToActual: () => -1,
            restoreToDisplayIndex: () => false
          };
        }
        Action3.clearHistoryManager = function(calcId) {
          historyManagers.delete(calcId);
        };
        Action3._saveWithoutHistory = function(calc, detail = {}) {
          if (window.calcStore) {
            const isTyping = detail?.immediate === true;
            const updatedCalc = {
              ...calc,
              updated: window.Timestamps.now(),
              rows: isTyping ? calc.rows : (calc.rows || []).map((row) => ({ ...row })),
              results: null
            };
            window.calcStore.save(updatedCalc, detail);
          }
        };
        Action3.save = function(calc, options = {}) {
          const { snapshot, skipHistory, detail, immediate } = options;
          const manager = Action3.getHistoryManager(calc);
          if (!skipHistory && !immediate) {
            if (snapshot) {
              manager.captureSnapshot(snapshot, { force: true });
            } else {
              manager.captureSnapshot("edit");
            }
          }
          Action3._saveWithoutHistory(calc, { ...detail, immediate });
        };
        Action3.undo = function() {
          const calc = Action3.getCurrentCalc();
          if (!calc) return false;
          const manager = Action3.getHistoryManager(calc);
          if (manager.undo()) {
            if (Action3.setCurrentCalc) {
              Action3.setCurrentCalc(calc);
            }
            Action3._saveWithoutHistory(calc);
            const results = Action3.recalculate ? Action3.recalculate(calc) : null;
            if (results && calc?.id && window.resultsStore && Array.isArray(results)) {
              window.resultsStore.set(calc.id, results);
            }
            if (results && Action3.setResults) {
              Action3.setResults(calc, results);
            }
            return true;
          }
          return false;
        };
        Action3.redo = function() {
          const calc = Action3.getCurrentCalc();
          if (!calc) return false;
          const manager = Action3.getHistoryManager(calc);
          if (manager.redo()) {
            if (Action3.setCurrentCalc) {
              Action3.setCurrentCalc(calc);
            }
            Action3._saveWithoutHistory(calc);
            const results = Action3.recalculate ? Action3.recalculate(calc) : null;
            if (results && calc?.id && window.resultsStore && Array.isArray(results)) {
              window.resultsStore.set(calc.id, results);
            }
            if (results && Action3.setResults) {
              Action3.setResults(calc, results);
            }
            return true;
          }
          return false;
        };
        Action3.canUndo = function() {
          const calc = Action3.getCurrentCalc();
          if (!calc) return false;
          const manager = Action3.getHistoryManager(calc);
          return manager.canUndo();
        };
        Action3.canRedo = function() {
          const calc = Action3.getCurrentCalc();
          if (!calc) return false;
          const manager = Action3.getHistoryManager(calc);
          return manager.canRedo();
        };
        Action3.getHistory = function() {
          const calc = Action3.getCurrentCalc();
          if (!calc) return [];
          const manager = Action3.getHistoryManager(calc);
          return manager.getHistory();
        };
        Action3.getHistorySnapshot = function(index) {
          const calc = Action3.getCurrentCalc();
          if (!calc) return null;
          const manager = Action3.getHistoryManager(calc);
          return manager.getSnapshot(index);
        };
        Action3.getHistoryEntry = function(displayIndex) {
          const calc = Action3.getCurrentCalc();
          if (!calc) return null;
          if (!calc.history || !calc.history.stack) return null;
          const manager = Action3.getHistoryManager(calc);
          const actualIndex = manager._displayIndexToActual(displayIndex);
          if (actualIndex < 0 || actualIndex >= calc.history.stack.length) return null;
          return calc.history.stack[actualIndex];
        };
        Action3.jumpToHistory = function(displayIndex) {
          const calc = Action3.getCurrentCalc();
          if (!calc) return false;
          const manager = Action3.getHistoryManager(calc);
          if (manager.restoreToDisplayIndex(displayIndex)) {
            Action3._saveWithoutHistory(calc);
            const results = Action3.recalculate ? Action3.recalculate(calc) : null;
            if (results && calc?.id && window.resultsStore && Array.isArray(results)) {
              window.resultsStore.set(calc.id, results);
            }
            if (results && Action3.setResults) {
              Action3.setResults(calc, results);
            }
            return true;
          }
          return false;
        };
      })();
    }
  });

  // public/src/actions/PersistenceService.js
  var require_PersistenceService = __commonJS({
    "public/src/actions/PersistenceService.js"() {
      (function() {
        var Action3 = window.Action || {};
        Action3.setLocalOption = function(name, value) {
          let options;
          try {
            options = JSON.parse(window.localStorage.getItem("settings") || "{}");
          } catch (e) {
            options = {};
          }
          options[name] = value;
          window.localStorage.setItem("settings", JSON.stringify(options));
        };
        Action3.getLocalOption = function(name) {
          let options;
          try {
            options = JSON.parse(window.localStorage.getItem("settings") || "{}");
          } catch (e) {
            options = {};
          }
          return options[name];
        };
        Action3.select = function(calcOrId, viewContext) {
          if (!window.calcStore?.setActiveCalc) {
            console.warn("[Action.select] CalcStore not initialized");
            return;
          }
          const calcId = typeof calcOrId === "string" ? calcOrId : calcOrId?.id;
          if (window.DebugLogger) {
            window.DebugLogger.log("store", "Action.select:", calcId, "viewContext:", viewContext);
          }
          const ViewContext = window.ViewContext;
          if (!ViewContext) throw new Error("ViewContext not loaded");
          window.calcStore.setActiveCalc(calcOrId, viewContext || ViewContext.HOME);
        };
        Action3.redirectHomeAfterDelay = function() {
          setTimeout(function() {
            window.location.href = "/";
          }, 1500);
        };
        Action3.savePublic = function(oldCalc) {
          Action3.clone(oldCalc, "");
          Action3.redirectHomeAfterDelay();
        };
        Action3.saveEmbedCalc = function(id) {
          let calc = Action3.getEmbed(id);
          if (calc) {
            calc.parent = id;
            Action3.create(calc);
          }
        };
        Action3.get = function(id) {
          return Calc.get(id);
        };
        Action3.delete = function(calc) {
          console.log("deleting calc", calc.name);
          const selectedId = Action3.getLocalOption("selected");
          const isDeletingSelected = selectedId === calc.id;
          if (isDeletingSelected) {
            const allCalcs = Calc.allByNewest().filter((c) => !c.deleted);
            const currentIndex = allCalcs.findIndex((c) => c.id === calc.id);
            const remainingCalcs = allCalcs.filter((c) => c.id !== calc.id);
            if (remainingCalcs.length > 0) {
              let nextCalc;
              if (currentIndex >= 0 && currentIndex < remainingCalcs.length) {
                nextCalc = remainingCalcs[currentIndex];
              } else if (currentIndex > 0) {
                nextCalc = remainingCalcs[currentIndex - 1];
              } else {
                nextCalc = remainingCalcs[0];
              }
              if (nextCalc) {
                Action3.select(nextCalc.id);
              }
            }
          }
          calc.deleted = true;
          if (!window.calcStore) throw new Error("CalcStore not initialized");
          window.calcStore.save(calc);
        };
        Action3.merge = function(id, props) {
          let calc = Action3.get(id);
          calc.updated = window.Timestamps.now();
          for (let key in props) {
            calc[key] = props[key];
          }
          if (!window.calcStore) throw new Error("CalcStore not initialized");
          window.calcStore.save(calc);
        };
        Action3.publish = function(calc) {
          if (calc?.published?.origId && !calc?.published?.id) {
            calc.published.id = calc.published.origId;
            if (!window.calcStore) throw new Error("CalcStore not initialized");
            window.calcStore.save(calc);
          }
          Calc.publish(calc.id);
        };
        Action3.unpublish = function(calc) {
          if (!calc?.published?.id) {
            return;
          }
          calc.published.origId = calc.published.id;
          calc.published.id = null;
          Action3.save(calc);
          let blank = Calc.createCalcObject();
          blank.uid = firebase.auth().currentUser.uid;
          Calc.publishToSharedFirebase(calc.published.origId, blank);
        };
        Action3.getPublicURL = function(calc) {
          calc = calc || {};
          var base = "https://instacalc.com/";
          if (window.location.href.match("localhost")) {
            base = window.location.origin + "/";
          }
          if (!(calc.published && calc.published.id)) {
            return "";
          }
          return base + calc.published.id;
        };
        Action3.getEmbedURL = function(calc) {
          return Action3.getPublicURL(calc) + "/embed";
        };
        Action3.exportAllCalcs = function() {
          const data = JSON.stringify(Calc.all(), null, 2);
          const blob = new Blob([data], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "calcs.json";
          link.click();
        };
        Action3.exportCalc = function(calc) {
          const data = JSON.stringify(calc, null, 2);
          const blob = new Blob([data], { type: "application/json" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${calc.name}.json`;
          link.click();
        };
        Action3.importAllCalcs = function(file) {
          const reader = new FileReader();
          reader.onerror = () => {
            console.error("Failed to read file:", reader.error);
            alert("Failed to read file. Please try again.");
          };
          reader.onload = (event) => {
            let data;
            try {
              data = JSON.parse(event.target.result);
            } catch (e) {
              console.error("Invalid JSON:", e);
              alert("This file does not contain valid JSON data.");
              return;
            }
            let importCount = 0;
            if (Array.isArray(data)) {
              if (window.calcStore) {
                window.calcStore.startBatch();
              }
              try {
                data.forEach((calc) => {
                  Calc.set(calc.id, calc);
                });
                importCount = data.length;
                console.log("Imported", importCount, "calcs");
              } finally {
                if (window.calcStore) {
                  window.calcStore.endBatch();
                }
              }
            } else if (data.id) {
              Calc.set(data.id, data);
              importCount = 1;
              console.log("Imported 1 calc");
            } else {
              console.error("Invalid JSON data");
              alert("Invalid JSON data");
              return;
            }
            if (window.InstacalcEvents?.dispatch) {
              window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_CHANGED);
            }
            if (window.showToast) {
              const label = importCount === 1 ? "1 calc" : `${importCount} calcs`;
              window.showToast(`Imported ${label}`, "success");
            }
          };
          reader.readAsText(file);
        };
        Action3.openFileImportUX = function() {
          const fileInput = document.getElementById("calc-import-file");
          if (!fileInput) {
            console.error("Calc import file input not found");
            return;
          }
          fileInput.value = "";
          const handleChange = function(event) {
            const file = event.target.files[0];
            if (file) {
              Action3.importAllCalcs(file);
            }
            fileInput.removeEventListener("change", handleChange);
          };
          fileInput.addEventListener("change", handleChange);
          fileInput.click();
        };
        Action3._remoteCalcsLoadedByUID = {};
        Action3.loadRemoteCalcs = function(uid, options = {}) {
          const { force = false, _retryCount = 0 } = options;
          const MAX_RETRIES = 2;
          if (!force && Action3._remoteCalcsLoadedByUID[uid]) {
            if (window.Debug) window.Debug.log("store", "\u26A0\uFE0F loadRemoteCalcs already loaded for UID", uid.slice(0, 8), ", skipping");
            return;
          }
          const ref = firebase.database().ref(`/users/${uid}/calcs`);
          if (window.calcStore) {
            window.calcStore.setRemoteSyncStatus("loading");
          }
          ref.once("value", function(data) {
            const calcs = data.val();
            if (!calcs) {
              Action3._remoteCalcsLoadedByUID[uid] = true;
              if (window.calcStore) {
                window.calcStore.setRemoteSyncStatus("complete");
              }
              return;
            }
            const firebaseCalcIds = Object.keys(calcs);
            const firebaseCount = firebaseCalcIds.length;
            if (window.Debug) window.Debug.log("store", `\u{1F4E6} Loading ${firebaseCount} calcs from Firebase (one-time)`);
            if (window.calcStore) {
              window.calcStore.startBatch();
            }
            let loadedCount = 0;
            try {
              for (const id in calcs) {
                const calc = calcs[id];
                if (!calc.type) {
                  calc.type = "user";
                }
                Calc.setIfNewer(id, calc);
                loadedCount++;
              }
            } finally {
              if (window.calcStore) {
                window.calcStore.endBatch();
              }
            }
            const localCount = window.calcStore ? Object.keys(window.calcStore._userCalcs).length : 0;
            const missingCount = firebaseCount - localCount;
            if (missingCount > 10 && _retryCount < MAX_RETRIES) {
              console.warn(`[Sync] \u26A0\uFE0F Sync mismatch: Firebase has ${firebaseCount}, local has ${localCount}. Retrying... (${_retryCount + 1}/${MAX_RETRIES})`);
              if (window.Debug) window.Debug.log("store", `\u26A0\uFE0F Sync mismatch, retrying`, { firebaseCount, localCount, retry: _retryCount + 1 });
              setTimeout(() => {
                Action3.loadRemoteCalcs(uid, { force: true, _retryCount: _retryCount + 1 });
              }, 500);
              return;
            }
            if (missingCount > 0) {
              console.warn(`[Sync] Firebase: ${firebaseCount}, Local: ${localCount}, Delta: ${missingCount}`);
              if (window.Debug) window.Debug.log("store", `\u26A0\uFE0F Sync delta`, { firebaseCount, localCount, missingCount });
            }
            Action3._remoteCalcsLoadedByUID[uid] = true;
            if (window.calcStore) {
              window.calcStore.setRemoteSyncStatus("complete");
            }
            if (window.InstacalcEvents) {
              window.InstacalcEvents.dispatch("ic:remote-sync-complete", {
                count: firebaseCount,
                localCount,
                uid
              });
            }
            if (window.Debug) window.Debug.log("store", `\u2705 Sync complete: ${localCount} calcs in local storage`);
          });
          if (_retryCount === 0) {
            ref.on("child_changed", function(snapshot) {
              const calc = snapshot.val();
              if (!calc) return;
              if (!calc.type) calc.type = "user";
              const id = snapshot.key;
              if (window.Debug) window.Debug.log("store", "\u2728 child_changed:", id.slice(0, 8));
              Calc.setIfNewer(id, calc);
            });
            ref.on("child_added", function(snapshot) {
              const calc = snapshot.val();
              if (!calc) return;
              if (!calc.type) calc.type = "user";
              const id = snapshot.key;
              if (!Calc.get(id)) {
                if (window.Debug) window.Debug.log("store", "\u2728 child_added:", id.slice(0, 8));
                Calc.setIfNewer(id, calc);
              }
            });
          }
        };
        Action3.forceResync = function() {
          const user = Action3.getCurrentUser();
          if (!user?.uid) {
            console.warn("[Sync] Cannot resync - not logged in");
            return;
          }
          console.log("[Sync] Forcing resync...");
          Action3.loadRemoteCalcs(user.uid, { force: true });
        };
        Action3.getFolders = function() {
          return Action3.getLocalOption("folders") || [];
        };
        Action3.createFolder = function(name) {
          if (!name || !name.trim()) {
            return null;
          }
          const folders = Action3.getFolders();
          const folderName = name.trim();
          if (folders.some((f) => f.name === folderName)) {
            return null;
          }
          const newFolder = { name: folderName, open: true };
          const updatedFolders = [...folders, newFolder];
          Action3.setLocalOption("folders", updatedFolders);
          return newFolder;
        };
        Action3.renameFolder = function(oldName, newName) {
          if (!oldName || !newName || !newName.trim()) {
            return false;
          }
          const folders = Action3.getFolders();
          const newNameTrimmed = newName.trim();
          if (folders.some((f) => f.name === newNameTrimmed && f.name !== oldName)) {
            return false;
          }
          const folderExists = folders.some((folder) => folder.name === oldName);
          if (!folderExists) {
            return false;
          }
          const updatedFolders = folders.map(
            (folder) => folder.name === oldName ? { ...folder, name: newNameTrimmed } : folder
          );
          Action3.setLocalOption("folders", updatedFolders);
          Calc.all().forEach((calc) => {
            if (calc.folder === oldName) {
              Action3.updateFolder(calc, newNameTrimmed);
            }
          });
          return true;
        };
        Action3.deleteFolder = function(folderName) {
          if (!folderName) {
            return false;
          }
          const calcsInFolder = Calc.all().filter((calc) => calc.folder === folderName);
          if (calcsInFolder.length > 0) {
            return false;
          }
          const folders = Action3.getFolders();
          const updatedFolders = folders.filter((f) => f.name !== folderName);
          Action3.setLocalOption("folders", updatedFolders);
          return true;
        };
        Action3.toggleFolder = function(folderName) {
          if (!folderName) {
            return null;
          }
          const folders = Action3.getFolders();
          let newOpenState = null;
          const updatedFolders = folders.map((folder) => {
            if (folder.name === folderName) {
              newOpenState = !folder.open;
              return { ...folder, open: newOpenState };
            }
            return folder;
          });
          Action3.setLocalOption("folders", updatedFolders);
          return newOpenState;
        };
      })();
    }
  });

  // public/src/actions/ExternalDataService.js
  var require_ExternalDataService = __commonJS({
    "public/src/actions/ExternalDataService.js"() {
      (function() {
        var Action3 = typeof window !== "undefined" && window.Action || typeof self !== "undefined" && self.Action || typeof global !== "undefined" && global.Action || {};
        if (typeof window !== "undefined") {
          window.Action = Action3;
        } else if (typeof self !== "undefined") {
          self.Action = Action3;
        } else if (typeof global !== "undefined") {
          global.Action = Action3;
        }
        Action3.applyCacheSync = function(caches2) {
          if (!Action3.fn) return;
          if (!caches2) return;
          Action3.fn.stock_cache = caches2.stock_cache || {};
          Action3.fn.import_cache = caches2.import_cache || {};
          Action3.fn.import_calc_cache = caches2.import_calc_cache || {};
          if (caches2.currencies && Object.keys(caches2.currencies).length > 0) {
            if (typeof InstaCalc !== "undefined" && InstaCalc.Unit) {
              const prevCount = Object.keys(InstaCalc.Unit.currencies || {}).length;
              InstaCalc.Unit.currencies = caches2.currencies;
              InstaCalc.Unit.baseCurrencies = caches2.currencies;
              if (caches2.currencyRateTimestamp !== void 0) {
                InstaCalc.Unit.currencyRateTimestamp = caches2.currencyRateTimestamp;
              }
              const newCount = Object.keys(caches2.currencies).length;
              if (typeof InstaCalc.updateUnitTimeStamp === "function") {
                InstaCalc.updateUnitTimeStamp();
              }
              const isWorker2 = typeof WorkerGlobalScope !== "undefined" && self instanceof WorkerGlobalScope;
              if (isWorker2) {
                console.log(`[WORKER] \u{1F4B1} Currencies synced: ${prevCount} \u2192 ${newCount} (USD=${!!caches2.currencies.usd}, EUR=${!!caches2.currencies.eur})`);
              }
            }
          }
        };
        function notifyExternalDataLoaded() {
          if (typeof window === "undefined") {
            return;
          }
          let calc = null;
          if (Action3.getCurrentCalc) {
            try {
              calc = Action3.getCurrentCalc();
            } catch (e) {
            }
          }
          if (!calc && window.calcStore?.getActiveCalc) {
            calc = window.calcStore.getActiveCalc();
          }
          if (!calc) {
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("loader", "ExternalData: no active calc to notify");
            return;
          }
          const currencyCount = Object.keys(window.InstaCalc?.Unit?.currencies || {}).length;
          if (typeof window !== "undefined" && window.Debug) {
            window.Debug.log("loader", "ExternalData: syncing cache to worker for", calc.id);
            console.log(`\u{1F504} [ExternalDataService] notifyExternalDataLoaded called - ${currencyCount} currencies ready to sync`);
          }
          if (typeof window !== "undefined" && window.externalDataCache) {
            window.externalDataCache.syncToWorker();
          }
          if (typeof window !== "undefined" && window.IC?.WORKER_LOADED && Action3.async_recalculate) {
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("loader", "ExternalData: async recalc (worker) for", calc.id);
            Action3.async_recalculate(calc);
          } else {
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("loader", "ExternalData: sync recalc (main) for", calc.id);
            const results = Action3.recalculate(calc);
            if (calc.id && window.resultsStore) {
              window.resultsStore.set(calc.id, results);
            }
            if (window.InstacalcEvents?.dispatch) {
              window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_CHANGED, {
                calcId: calc.id,
                reason: "external-data-loaded"
              });
            }
          }
        }
        function useLocalEmulator() {
          if (typeof window === "undefined" || !window.location) {
            return false;
          }
          if (window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
            return false;
          }
          const params = new URLSearchParams(window.location.search);
          return params.has("local") && params.get("local") !== "false" && params.get("local") !== "0";
        }
        function getEmulatorFunctionUrl(functionName) {
          const currentPort = parseInt(window.location.port, 10) || 5002;
          const emulatorPort = currentPort - 1;
          const protocol = window.location.protocol;
          const hostname = window.location.hostname;
          return `${protocol}//${hostname}:${emulatorPort}/firebase-instacalc/us-central1/${functionName}`;
        }
        if (useLocalEmulator()) {
          Action3.CURRENCY_URL = getEmulatorFunctionUrl("currencies_v2");
        } else if (typeof location !== "undefined" && location.hostname !== "localhost" && window.location?.protocol !== "file:") {
          Action3.CURRENCY_URL = "/currencies.json";
        } else {
          Action3.CURRENCY_URL = "https://instacalc.com/currencies.json";
        }
        Action3.REQUEST_IN_FLIGHT = false;
        Action3.CURRENCY_FALLBACK_URL = "/data/currencies-fallback.json";
        function loadFallbackCurrencies(callback) {
          if (InstaCalc.Unit.currenciesLoaded()) return;
          fetch(Action3.CURRENCY_FALLBACK_URL).then((res) => {
            if (!res.ok) throw new Error("Fallback fetch failed");
            return res.json();
          }).then((data) => {
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("loader", "Currencies: loaded from static fallback");
            InstaCalc.loadCurrencies(data);
            if (callback) callback();
            notifyExternalDataLoaded();
          }).catch((err) => {
            console.error("Currencies: fallback load failed -", err);
          });
        }
        Action3.loadCurrencies = function(callback) {
          if ("caches" in window) {
            caches.match(Action3.CURRENCY_URL).then((response) => {
              if (response && !InstaCalc.Unit.currenciesLoaded()) {
                response.json().then((data) => {
                  const currencyCount = Object.keys(data || {}).length;
                  if (typeof window !== "undefined" && window.Debug) {
                    window.Debug.log("loader", "Currencies: instant load from cache");
                    console.log(`\u26A1 [ExternalDataService] Instant cache load: ${currencyCount} currencies - syncing to worker`);
                  }
                  InstaCalc.loadCurrencies(data);
                  if (callback) callback();
                  notifyExternalDataLoaded();
                });
              }
            });
          }
          if (Action3.REQUEST_IN_FLIGHT) {
            return;
          }
          Action3.REQUEST_IN_FLIGHT = true;
          fetch(Action3.CURRENCY_URL).then((res) => {
            if (!res.ok) {
              throw new Error("Network response was not ok");
            }
            const source = res.headers.get("x-cache") || (res.ok && !navigator.onLine ? "cache" : "network");
            return res.json().then((data) => ({ data, source }));
          }).then(({ data, source }) => {
            const currencyCount = Object.keys(data || {}).length;
            if (typeof window !== "undefined" && window.Debug) {
              window.Debug.log("loader", `Currencies: refresh from ${source}`);
              if (currencyCount > 0) {
                console.log(`\u2705 [ExternalDataService] Loaded ${currencyCount} currencies from ${source} - will sync to worker`);
              } else {
                console.error(`\u274C [ExternalDataService] LOADED 0 CURRENCIES from ${source}! Data:`, data);
              }
            }
            InstaCalc.loadCurrencies(data, true);
            if (callback) {
              callback();
            }
            notifyExternalDataLoaded();
          }).catch((error) => {
            console.error("Currencies: fetch error -", error);
            if (!InstaCalc.Unit.currenciesLoaded() && "caches" in window) {
              caches.match(Action3.CURRENCY_URL).then(function(response) {
                if (response) {
                  response.json().then(function(data) {
                    if (typeof window !== "undefined" && window.Debug) window.Debug.log("loader", "Currencies: fallback load from cache");
                    InstaCalc.loadCurrencies(data);
                    if (callback) callback();
                    notifyExternalDataLoaded();
                  });
                } else {
                  loadFallbackCurrencies(callback);
                }
              });
            } else if (!InstaCalc.Unit.currenciesLoaded()) {
              loadFallbackCurrencies(callback);
            }
          }).finally(() => {
            Action3.REQUEST_IN_FLIGHT = false;
          });
        };
        Action3.fn = Action3.fn || {};
        Action3.STOCK_URL = "https://instacalc.com/stock.json";
        if (location.hostname !== "localhost" && window.location?.protocol !== "file:") {
          Action3.STOCK_URL = "/stock.json";
        }
        Action3.fn.stock_cache = {};
        var isWorker = typeof WorkerGlobalScope !== "undefined" && self instanceof WorkerGlobalScope;
        function makeStockPrice(price, symbol) {
          if (typeof IC !== "undefined" && IC.makeICObject) {
            try {
              const result = IC.makeICObject(price, "USD");
              if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock:verbose", `makeStockPrice(${symbol}): created IC object`, { v: result.v, t: result.t });
              return result;
            } catch (e) {
              if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `makeStockPrice(${symbol}): IC.makeICObject failed, using raw number`);
            }
          }
          return price;
        }
        Action3.fn.stock = function(symbol) {
          let normalizedSymbol = symbol;
          if (symbol && symbol.toUpperCase().startsWith("STOCK_")) {
            normalizedSymbol = symbol.substring(6).replace(/_/g, ".");
          }
          const context = isWorker ? "WORKER" : "MAIN";
          const data = Action3.fn.stock_cache[normalizedSymbol];
          if (typeof window !== "undefined" && window.Debug) {
            const timestamp = (/* @__PURE__ */ new Date()).toISOString().split("T")[1].slice(0, 12);
            window.Debug.log("stock:verbose", `[${timestamp}] [${context}] Stock(${symbol}) cache:`, JSON.stringify(data));
          }
          if (data && data.loaded) {
            if (data.error) {
              if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `[${context}] Stock(${symbol}): \u274C cache HIT (error: ${data.error})`);
              return `Error: ${data.error}`;
            }
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `[${context}] Stock(${symbol}): \u2705 cache HIT, price=$${data.price}`);
            return makeStockPrice(data.price, symbol);
          }
          if (isWorker) {
            if (!data) {
              Action3.fn.stock_cache[normalizedSymbol] = { loaded: false, requestedAt: Date.now() };
              if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `[WORKER] Stock(${normalizedSymbol}): \u23F3 marking pending, signaling main thread`);
            }
            if (typeof Calc !== "undefined" && Calc.pub) {
              Calc.pub();
            }
            return void 0;
          }
          if (data && !data.loaded) {
            const elapsed = data.requestedAt ? Math.round((Date.now() - data.requestedAt) / 1e3) : "?";
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `[MAIN] Stock(${normalizedSymbol}): \u23F3 fetch in progress (${elapsed}s)`);
            return `Loading ${normalizedSymbol}...`;
          }
          const fetchUrl = Action3.STOCK_URL + "?symbol=" + normalizedSymbol;
          if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `[MAIN] Stock(${normalizedSymbol}): \u{1F680} initiating fetch from ${fetchUrl}`);
          Action3.fn.stock_cache[normalizedSymbol] = { loaded: false, requestedAt: Date.now() };
          fetch(fetchUrl).then((res) => {
            if (!res.ok) {
              throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }
            return res.json();
          }).then((data2) => {
            const price = data2.regularMarketPreviousClose;
            const elapsed = Action3.fn.stock_cache[normalizedSymbol]?.requestedAt ? Math.round(Date.now() - Action3.fn.stock_cache[normalizedSymbol].requestedAt) : "?";
            if (price === void 0 || price === null || isNaN(price)) {
              console.warn(`[Stock] ${normalizedSymbol}: API returned no price data`, data2);
            }
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `[MAIN] Stock(${normalizedSymbol}): \u2705 fetch SUCCESS in ${elapsed}ms, price=$${price}`);
            function getPreviousTradingDay(timestamp) {
              const date = new Date(timestamp);
              const dayOfWeek = date.getDay();
              const daysToSubtract = dayOfWeek === 1 ? 3 : dayOfWeek === 0 ? 2 : 1;
              date.setDate(date.getDate() - daysToSubtract);
              return date.getTime();
            }
            let tradingDate = null;
            if (data2.regularMarketTime) {
              tradingDate = getPreviousTradingDay(data2.regularMarketTime * 1e3);
            } else if (data2._created) {
              tradingDate = getPreviousTradingDay(new Date(data2._created).getTime());
            }
            const stockData = {
              loaded: true,
              price,
              tradingDate,
              // Date of the previous close price
              fetchedAt: Date.now()
              // When we fetched from our API
            };
            Action3.fn.stock_cache[normalizedSymbol] = stockData;
            if (typeof window !== "undefined" && window.externalDataCache) {
              window.externalDataCache.set("stock", normalizedSymbol, stockData);
              if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock:verbose", `[MAIN] Stock(${normalizedSymbol}): persisted to IndexedDB`);
            }
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `\u{1F504} Stock loaded: ${normalizedSymbol} = $${price}, triggering recalc`);
            notifyExternalDataLoaded();
          }).catch((error) => {
            console.error(`[Stock] ${normalizedSymbol}: Failed to load - ${error.message}`);
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("stock", `[MAIN] Stock(${normalizedSymbol}): \u274C fetch FAILED: ${error.message}`);
            const errorData = { loaded: true, error: error.message, fetchedAt: Date.now() };
            Action3.fn.stock_cache[normalizedSymbol] = errorData;
            notifyExternalDataLoaded();
          });
          return `Loading ${normalizedSymbol}...`;
        };
        if (typeof window !== "undefined") {
          window.getStockCache = function() {
            console.log("[Stock Cache]", JSON.stringify(Action3.fn.stock_cache, null, 2));
            return Action3.fn.stock_cache;
          };
        }
        Action3.fn.import_cache = {};
        Action3.fn.import = function(url) {
          const data = Action3.fn.import_cache[url];
          if (data) {
            return data.loaded ? data.json ?? data.text : void 0;
          }
          Action3.fn.import_cache[url] = { loaded: false };
          if (typeof window !== "undefined" && window.Debug) window.Debug.log("loader", "Importing url: " + url);
          fetch(url).then((res) => res.text()).then((text) => {
            const importData = {
              loaded: true,
              text,
              json: IC.ICParseRelaxedJSON(text),
              date: (/* @__PURE__ */ new Date()).toISOString()
            };
            Action3.fn.import_cache[url] = importData;
            if (typeof window !== "undefined" && window.externalDataCache) {
              window.externalDataCache.set("import", url, importData);
            }
            InstaCalc.updateUnitTimeStamp();
            notifyExternalDataLoaded();
          }).catch((error) => {
            console.error(`Error fetching ${url}:`, error);
          });
          return void 0;
        };
        Action3.fn.import_calc_cache = {};
        Action3.fn.import_calc_handle_result = function(id_lookup, calc) {
          const env = calc ? IC.api.recalculate(calc, true).env : "Calc not found";
          const key = id_lookup.toString();
          const calcData = {
            loaded: true,
            env,
            date: (/* @__PURE__ */ new Date()).toISOString()
          };
          Action3.fn.import_calc_cache[key] = calcData;
          if (typeof window !== "undefined" && window.externalDataCache) {
            window.externalDataCache.set("import_calc", key, calcData);
          }
          InstaCalc.updateUnitTimeStamp();
          notifyExternalDataLoaded();
        };
        Action3.fn.extractCalcIdFromString = function(str) {
          const match = str.toString().match(/^(?:https?:\/\/)?instacalc\.com\/(\d+)|^\d+$/);
          if (match) {
            return match[1] ? parseInt(match[1], 10) : parseInt(str, 10);
          }
          return null;
        };
        Action3.fn.expandGithubShorthand = function(path) {
          if (!path || typeof path !== "string") return path;
          const match = path.match(/^gh\/([^/]+)\/([^@/]+)(?:@([^/]+))?\/?(.+)$/);
          if (!match) return path;
          let [, user, repo, ref, filePath] = match;
          const blobMatch = filePath.match(/^blob\/([^/]+)\/(.+)$/);
          if (blobMatch) {
            ref = blobMatch[1];
            filePath = blobMatch[2];
          }
          ref = ref || "main";
          return `https://raw.githubusercontent.com/${user}/${repo}/${ref}/${filePath}`;
        };
        Action3.fn.isRemoteUrl = function(str) {
          if (!str || typeof str !== "string") return false;
          return str.startsWith("http://") || str.startsWith("https://") || str.startsWith("gh/");
        };
        Action3.fn.parseICFile = function(content, sourceName) {
          const lines = content.split("\n");
          const rows = lines.map((text) => ({ text }));
          while (rows.length < 7) {
            rows.push({ text: "" });
          }
          return {
            rows,
            name: sourceName || "Remote Calc"
          };
        };
        Action3.fn.buildEnvironmentFromCalc = function(calc) {
          if (!calc || !calc.rows) return {};
          const results = IC.api.recalculate(calc, true);
          return results.env || {};
        };
        Action3.fn.CORS_PROXY_URL = "https://instacalc-cors-proxy.kalid-azad.workers.dev";
        Action3.fn.fetchWithTimeout = async function(url, timeoutMs = 1e4) {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
          try {
            const response = await fetch(url, { signal: controller.signal });
            clearTimeout(timeoutId);
            return response;
          } catch (err) {
            clearTimeout(timeoutId);
            if (err.name === "AbortError") {
              throw new Error(`Request timeout after ${timeoutMs}ms`);
            }
            throw err;
          }
        };
        Action3.fn.isCorsEnabledHost = function(url) {
          try {
            const parsed = new URL(url);
            const host = parsed.hostname.toLowerCase();
            if (host === "raw.githubusercontent.com") return true;
            if (host === "gist.githubusercontent.com") return true;
            if (host.includes("gitlab.com") && parsed.pathname.includes("/raw/")) return true;
            if (host.endsWith(".jsdelivr.net")) return true;
            if (host.endsWith(".unpkg.com")) return true;
            if (host.endsWith(".cdnjs.cloudflare.com")) return true;
            return false;
          } catch (e) {
            return false;
          }
        };
        Action3.fn.fetchWithCorsFallback = async function(url, timeoutMs = 1e4) {
          if (Action3.fn.isCorsEnabledHost(url)) {
            return Action3.fn.fetchWithTimeout(url, timeoutMs);
          }
          try {
            const response = await Action3.fn.fetchWithTimeout(url, timeoutMs);
            return response;
          } catch (err) {
            const isCorsError = err.message.includes("CORS") || err.message.includes("cors") || err.message.includes("Failed to fetch") || err.message.includes("NetworkError") || err.name === "TypeError";
            if (!isCorsError) {
              throw err;
            }
            const proxyUrl = `${Action3.fn.CORS_PROXY_URL}/?url=${encodeURIComponent(url)}`;
            if (typeof window !== "undefined" && window.Debug) {
              window.Debug.log("loader", `CORS error for ${url}, retrying via proxy`);
            }
            try {
              const proxyResponse = await Action3.fn.fetchWithTimeout(proxyUrl, timeoutMs);
              if (!proxyResponse.ok) {
                const errorData = await proxyResponse.json().catch(() => ({}));
                throw new Error(errorData.error || `Proxy error: ${proxyResponse.status}`);
              }
              return proxyResponse;
            } catch (proxyErr) {
              throw new Error(`CORS blocked and proxy failed: ${proxyErr.message}`);
            }
          }
        };
        Action3.fn.fetchRemoteCalcWithDeps = async function(url, depth = 0, visiting = /* @__PURE__ */ new Set()) {
          if (visiting.has(url)) {
            throw new Error(`Circular dependency: ${url}`);
          }
          if (depth > 2) {
            throw new Error(`Remote import depth exceeded (max 2 levels): ${url}`);
          }
          visiting.add(url);
          const response = await Action3.fn.fetchWithCorsFallback(url, 1e4);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${url}: ${response.status}`);
          }
          const content = await response.text();
          const nestedUrls = Action3.fn.scanForCalcUrls(content);
          const nested = {};
          for (const nestedUrl of nestedUrls) {
            const expandedUrl = Action3.fn.expandGithubShorthand(nestedUrl);
            if (Action3.fn.isRemoteUrl(expandedUrl)) {
              try {
                nested[nestedUrl] = await Action3.fn.fetchRemoteCalcWithDeps(
                  expandedUrl,
                  depth + 1,
                  new Set(visiting)
                );
              } catch (e) {
                nested[nestedUrl] = { env: { __error: e.message } };
              }
            }
          }
          const sourceName = url.split("/").pop() || "Remote Calc";
          const calc = Action3.fn.parseICFile(content, sourceName);
          for (const [nestedUrl, nestedResult] of Object.entries(nested)) {
            if (nestedResult.env) {
              const normalizedNestedUrl = Action3.fn.expandGithubShorthand(nestedUrl);
              const cacheKey = `url:${normalizedNestedUrl}`;
              Action3.fn.import_calc_cache[cacheKey] = {
                loaded: true,
                env: nestedResult.env,
                date: (/* @__PURE__ */ new Date()).toISOString()
              };
            }
          }
          const env = Action3.fn.buildEnvironmentFromCalc(calc);
          visiting.delete(url);
          return { calc, env, nested, url };
        };
        Action3.fn.scanForCalcUrls = function(content) {
          const pattern = /calc\s*\(\s*["']([^"']+)["']\s*\)/g;
          const urls = [];
          let match;
          while ((match = pattern.exec(content)) !== null) {
            urls.push(match[1]);
          }
          return urls;
        };
        Action3.fn.import_calc_url = function(url) {
          const normalizedUrl = Action3.fn.expandGithubShorthand(url);
          const cacheKey = `url:${normalizedUrl}`;
          const cached = Action3.fn.import_calc_cache[cacheKey];
          if (cached) {
            if (cached.loaded) {
              return cached.env;
            }
            return void 0;
          }
          Action3.fn.import_calc_cache[cacheKey] = { loaded: false };
          const isWorker2 = typeof Calc !== "undefined" && typeof Calc.pub === "function";
          if (!isWorker2 && window.Debug) {
            window.Debug.log("loader", "Importing calc from URL: " + normalizedUrl);
          }
          Action3.fn.fetchRemoteCalcWithDeps(normalizedUrl, 0, /* @__PURE__ */ new Set()).then((result) => {
            const calcData = {
              loaded: true,
              env: result.env,
              calc: result.calc,
              nested: result.nested,
              date: (/* @__PURE__ */ new Date()).toISOString()
            };
            Action3.fn.import_calc_cache[cacheKey] = calcData;
            if (typeof window !== "undefined" && window.externalDataCache) {
              window.externalDataCache.set("import_calc", cacheKey, calcData);
            }
            if (typeof InstaCalc !== "undefined" && InstaCalc.updateUnitTimeStamp) {
              InstaCalc.updateUnitTimeStamp();
            }
            if (isWorker2) {
              Calc.pub();
            } else {
              notifyExternalDataLoaded();
            }
          }).catch((err) => {
            console.error("Failed to import remote calc:", err);
            const errorData = {
              loaded: true,
              env: { __error: err.message },
              date: (/* @__PURE__ */ new Date()).toISOString()
            };
            Action3.fn.import_calc_cache[cacheKey] = errorData;
            if (isWorker2) {
              Calc.pub();
            } else {
              notifyExternalDataLoaded();
            }
          });
          return void 0;
        };
        Action3.fn.import_calc = function(id) {
          if (typeof id === "string" && Action3.fn.isRemoteUrl(id)) {
            return Action3.fn.import_calc_url(id);
          }
          let possibleId = Action3.fn.extractCalcIdFromString(id);
          if (possibleId) {
            id = possibleId;
          }
          const data = Action3.fn.import_calc_cache[id?.toString()];
          if (data) {
            return data.loaded ? data.env : void 0;
          }
          if (typeof id === "number") {
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("loader", "Importing calc from embed id: " + id);
            Embed.loadEmbed(id, (calc) => {
              Action3.fn.import_calc_handle_result(id, calc);
            });
          }
          if (typeof id === "string") {
            if (typeof window !== "undefined" && window.Debug) window.Debug.log("loader", "Importing calc from string: " + id);
            if (typeof WorkerGlobalScope !== "undefined" && self instanceof WorkerGlobalScope) {
              self.postMessage({ type: "FETCH_CALC", id });
            } else {
              const calc = Calc.all().find((c) => c.name?.toLowerCase().trim() === id.toLowerCase().trim());
              if (calc) {
                Action3.fn.import_calc_handle_result(id, calc);
                return;
              }
            }
          }
        };
      })();
    }
  });

  // public/src/actions/PromoteService.js
  var require_PromoteService = __commonJS({
    "public/src/actions/PromoteService.js"() {
      (function() {
        var Action3 = window.Action || {};
        Action3.promoteCalc = function(calc) {
          if (!calc) return null;
          const CalcType = window.CalcType;
          if (!CalcType) throw new Error("CalcType not loaded");
          const clone = {
            ...calc,
            id: window.Util.guid(),
            type: CalcType.USER,
            syncRef: null
            // Local user calc, no external sync
          };
          delete clone.remoteId;
          if (window.calcStore) {
            window.calcStore.saveUserCalc(clone);
          } else {
            Calc.set(clone.id, clone);
            Calc.pub();
          }
          Action3.select(clone.id);
          return clone;
        };
        Action3.publishAs = function(mode) {
          const currentCalc = Action3.getCurrentCalc();
          if (!currentCalc) return;
          const CalcType = window.CalcType;
          if (!CalcType) throw new Error("CalcType not loaded");
          if (mode === "private") {
            const token = window.Util.guid() + window.Util.guid();
            const updatedCalc = {
              ...currentCalc,
              published: {
                id: token,
                type: "collab",
                token,
                updated: (/* @__PURE__ */ new Date()).toISOString()
              },
              type: CalcType.COLLAB,
              syncRef: CalcType.syncRefForCollab(token),
              remoteId: token
            };
            if (typeof firebase !== "undefined") {
              const collabPath = window.FirebasePaths ? window.FirebasePaths.getCollabPath(token) : `/collab/${token}`;
              firebase.database().ref(collabPath).set(updatedCalc);
            }
            if (window.calcStore) {
              window.calcStore.saveRemoteCalcLocal(updatedCalc);
            } else {
              Calc.set(updatedCalc.id, updatedCalc);
            }
            Action3.setCurrentCalc(updatedCalc);
            const prefix = window.CollabLinks?.PRIVATE_URL_PREFIX || "/edit/";
            window.history.pushState({}, "", prefix + token);
          } else if (mode === "wiki" || mode === "live") {
            let slug = prompt("Enter a name for this live wiki calc (e.g. 'physics-formulas'):");
            if (!slug) return;
            slug = slug.toLowerCase().replace(/[^a-z0-9-]/g, "-");
            const updatedCalc = {
              ...currentCalc,
              published: {
                id: slug,
                type: "live",
                liveSlug: slug,
                updated: (/* @__PURE__ */ new Date()).toISOString()
              },
              type: CalcType.LIVE,
              syncRef: CalcType.syncRefForLive(slug),
              remoteId: slug
            };
            if (typeof firebase !== "undefined") {
              const livePath = window.FirebasePaths ? window.FirebasePaths.getLivePath(slug) : `/wiki/live/${slug}`;
              firebase.database().ref(livePath).set({
                name: updatedCalc.name,
                rows: updatedCalc.rows,
                created: Date.now(),
                updated: Date.now()
              });
            }
            if (window.calcStore) {
              window.calcStore.saveRemoteCalcLocal(updatedCalc);
            } else {
              Calc.set(updatedCalc.id, updatedCalc);
            }
            Action3.setCurrentCalc(updatedCalc);
            const livePrefix = window.CollabLinks?.LIVE_URL_PREFIX || "/!";
            window.history.pushState({}, "", livePrefix + slug);
          }
        };
      })();
    }
  });

  // public/src/actions/CalcMutations.js
  var require_CalcMutations = __commonJS({
    "public/src/actions/CalcMutations.js"() {
      var Debug9 = typeof window !== "undefined" && window.Debug || {
        log: () => {
        },
        warn: () => {
        },
        error: (...args) => console.error(...args)
      };
      var DebugLogger2 = typeof window !== "undefined" && window.DebugLogger || {
        log: () => {
        },
        isEnabled: () => false
      };
      var safeClone3 = typeof window !== "undefined" && window.safeClone ? window.safeClone : function(obj) {
        try {
          return structuredClone(obj);
        } catch (e) {
          return JSON.parse(JSON.stringify(obj));
        }
      };
      (function() {
        var Action3 = window.Action || {};
        function resolveCalc(calcOrId) {
          if (typeof calcOrId === "string") {
            const calc = typeof Calc !== "undefined" && Calc.get ? Calc.get(calcOrId) : null;
            if (calc) return calc;
            if (typeof window !== "undefined" && window.calcStore?.get) {
              const storeCalc = window.calcStore.get(calcOrId);
              if (storeCalc) return storeCalc;
            }
            if (!calc) {
              console.warn("[Action] Could not find calc with ID:", calcOrId);
              return null;
            }
            return calc;
          }
          return calcOrId;
        }
        function makeBlankResult(status = "pending") {
          if (typeof window !== "undefined" && window.Result?.makeBlank) {
            return window.Result.makeBlank(status);
          }
          throw new Error("ResultUtils not loaded. window.Result.makeBlank is missing.");
        }
        function dispatchCalcMutation(calc, payload = {}) {
          DebugLogger2.log("perf", `[3/5] STORE_COMMIT type=${payload?.type || "unknown"} (React reactivity, no event)`);
        }
        Action3.addRows = function(calcOrId, n) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          Debug9.log("mutations", "\u2795 addRows:", { calcId: calc.id?.slice(0, 8), count: n });
          const newRows = [...calc.rows, ...EMPTY_ROW(n)];
          const newCalc = { ...calc, rows: newRows };
          if (calc.id && window.resultsStore) {
            const currentResults = window.resultsStore.get(calc.id);
            if (currentResults) {
              const baseResults = currentResults.length === 0 && calc.rows.length > 0 ? Array(calc.rows.length).fill(null).map(() => makeBlankResult()) : currentResults;
              const blankResults = Array(n).fill(null).map(() => makeBlankResult());
              const newResults = [...baseResults, ...blankResults];
              window.resultsStore.set(calc.id, newResults);
            }
          }
          Action3.commit(newCalc, {
            type: "addRows",
            deltaRows: n,
            selectionHint: newCalc.rows.length - 1
          });
          return newCalc;
        };
        Action3.insertRow = function(calcOrId, i, options = {}) {
          if (window.actionLoopGuard?.check()) {
            Debug9.error("mutations", "insertRow loop detected, aborting");
            return resolveCalc(calcOrId);
          }
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          const { focus = true } = options;
          Debug9.log("mutations", "\u2795 insertRow:", { calcId: calc.id?.slice(0, 8), index: i });
          const newRow = { ...EMPTY_ROW(1)[0], hidden: false };
          const newRows = [
            ...calc.rows.slice(0, i),
            newRow,
            ...calc.rows.slice(i)
          ];
          const newCalc = { ...calc, rows: newRows };
          if (calc.id && window.resultsStore) {
            const currentResults = window.resultsStore.get(calc.id);
            const newResults = [
              ...currentResults.slice(0, i),
              makeBlankResult(),
              ...currentResults.slice(i)
            ];
            window.resultsStore.set(calc.id, newResults);
          }
          Action3.updateRowReferences(newCalc, i, 1);
          Action3.commit(newCalc, {
            type: "insertRow",
            index: i,
            deltaRows: 1,
            selectionHint: i
          });
          if (focus && Action3.setSelectedRow) {
            Action3.setSelectedRow(i, { focus: true, calc: newCalc });
          }
          return newCalc;
        };
        Action3.deleteRow = function(calcOrId, i, options = {}) {
          if (window.actionLoopGuard?.check()) {
            Debug9.error("mutations", "deleteRow loop detected, aborting");
            return resolveCalc(calcOrId);
          }
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          Debug9.log("mutations", "\u2796 deleteRow:", { calcId: calc.id?.slice(0, 8), index: i, totalRows: calc.rows.length });
          const newRows = calc.rows.filter((_, idx) => idx !== i);
          const newCalc = { ...calc, rows: newRows };
          if (calc.id && window.resultsStore) {
            const currentResults = window.resultsStore.get(calc.id);
            const newResults = currentResults.filter((_, idx) => idx !== i);
            window.resultsStore.set(calc.id, newResults);
          }
          Action3.updateRowReferences(newCalc, i, -1);
          const defaultHint = Math.max(0, Math.min(i, newRows.length - 1));
          const selectionHint = typeof options.selectionHint === "number" ? options.selectionHint : defaultHint;
          Action3.commit(newCalc, {
            type: "deleteRow",
            index: i,
            deltaRows: -1,
            selectionHint
          });
          return newCalc;
        };
        Action3.insertRowWithTextCleanup = function(calcId, rowIndex, cleanedText, insertPosition) {
          const store = window.calcStore;
          let calc = store?._userCalcs?.[calcId] || store?._sessionCalcs?.[calcId] || store?._collabCalcs?.[calcId];
          if (!calc) {
            calc = resolveCalc(calcId);
          }
          if (!calc) {
            return Action3.insertRow(calcId, insertPosition);
          }
          Debug9.log("mutations", "\u2795\u{1F9F9} insertRowWithTextCleanup:", {
            calcId: calc.id?.slice(0, 8),
            rowIndex,
            insertPosition,
            cleanedText: cleanedText.slice(0, 20)
          });
          const newRow = { ...EMPTY_ROW(1)[0], hidden: false };
          const updatedRows = calc.rows.map(
            (row, i) => i === rowIndex ? { ...row, text: cleanedText } : row
          );
          const finalRows = [
            ...updatedRows.slice(0, insertPosition),
            newRow,
            ...updatedRows.slice(insertPosition)
          ];
          const newCalc = {
            ...calc,
            rows: finalRows,
            updated: window.Timestamps?.now() || Date.now(),
            results: null
          };
          Action3.updateRowReferences(newCalc, insertPosition, 1);
          if (window.resultsStore) {
            const currentResults = window.resultsStore.get(calcId) || [];
            const newResults = [
              ...currentResults.slice(0, insertPosition),
              makeBlankResult(),
              ...currentResults.slice(insertPosition)
            ];
            window.resultsStore.set(calcId, newResults);
          }
          Action3.commit(newCalc, {
            type: "insertRowWithTextCleanup",
            index: insertPosition,
            deltaRows: 1,
            selectionHint: insertPosition
          });
          return newCalc;
        };
        Action3.deleteRowWithTextCleanup = function(calcId, rowIndex, cleanedText) {
          const store = window.calcStore;
          let calc = store?._userCalcs?.[calcId] || store?._sessionCalcs?.[calcId] || store?._collabCalcs?.[calcId];
          if (!calc) {
            calc = resolveCalc(calcId);
          }
          if (!calc) {
            return Action3.deleteRow(calcId, rowIndex);
          }
          if (calc.rows.length <= 1) {
            return Action3.updateRowText(calcId, rowIndex, cleanedText);
          }
          Debug9.log("mutations", "\u2796\u{1F9F9} deleteRowWithTextCleanup:", {
            calcId: calc.id?.slice(0, 8),
            rowIndex,
            totalRows: calc.rows.length
          });
          const finalRows = calc.rows.filter((_, i) => i !== rowIndex);
          const newCalc = {
            ...calc,
            rows: finalRows,
            updated: window.Timestamps?.now() || Date.now(),
            results: null
          };
          Action3.updateRowReferences(newCalc, rowIndex, -1);
          if (window.resultsStore) {
            const currentResults = window.resultsStore.get(calcId) || [];
            window.resultsStore.set(calcId, currentResults.filter((_, i) => i !== rowIndex));
          }
          const newLen = finalRows.length;
          const selectionHint = newLen > 0 ? Math.min(rowIndex, newLen - 1) : 0;
          Action3.commit(newCalc, {
            type: "deleteRowWithTextCleanup",
            index: rowIndex,
            deltaRows: -1,
            selectionHint
          });
          return newCalc;
        };
        Action3.addText = function(calc, i, text) {
          const newRows = calc.rows.map(
            (row, idx) => idx === i ? { ...row, text: row.text + text } : row
          );
          const newCalc = { ...calc, rows: newRows };
          Action3.commit(newCalc, {
            type: "addText",
            index: i,
            selectionHint: i
          });
          return newCalc;
        };
        Action3.deleteText = function(calc, i, n) {
          n ||= 1;
          const text = calc.rows[i]?.text || "";
          const newRows = calc.rows.map(
            (row, idx) => idx === i ? { ...row, text: text.slice(0, -n) } : row
          );
          const newCalc = { ...calc, rows: newRows };
          Action3.commit(newCalc, {
            type: "deleteText",
            index: i,
            selectionHint: i
          });
          return newCalc;
        };
        Action3.updateRowText = function(calcOrId, row_id, text, options = {}) {
          if (window.actionLoopGuard?.check()) {
            Debug9.error("mutations", "updateRowText loop detected, aborting");
            return resolveCalc(calcOrId);
          }
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          const existingText = calc.rows?.[row_id]?.text;
          if (existingText === text) {
            return calc;
          }
          Debug9.log("mutations", "\u270F\uFE0F updateRowText:", {
            calcId: calc.id?.slice(0, 8),
            row: row_id,
            oldLen: existingText?.length || 0,
            newLen: text?.length || 0
          });
          const newRows = (calc.rows || []).map(
            (row, i) => i === row_id ? { ...row, text } : row
          );
          const newCalc = {
            ...calc,
            rows: newRows,
            results: null
            // force recompute for all rows (collapsed or not)
          };
          Action3.commit(newCalc, {
            type: "updateRowText",
            index: row_id,
            options
            // Pass options through (e.g. { immediate: true })
            // selectionHint intentionally omitted to avoid refocusing and selecting text on every keystroke
          });
          return newCalc;
        };
        let _pendingFlushCalcId = null;
        let _flushTimer = null;
        function _flushPendingChanges() {
          if (_flushTimer) {
            clearTimeout(_flushTimer);
            _flushTimer = null;
          }
          if (!_pendingFlushCalcId) return;
          const calcId = _pendingFlushCalcId;
          _pendingFlushCalcId = null;
          const calc = window.calcStore?.get(calcId);
          if (!calc) return;
          DebugLogger2.log("perf", "[FLUSH] Deferred save for history/persistence");
          Action3.save(calc, { snapshot: "edit" });
        }
        function _scheduleFlush(calcId) {
          _pendingFlushCalcId = calcId;
          if (_flushTimer) clearTimeout(_flushTimer);
          _flushTimer = setTimeout(_flushPendingChanges, 500);
        }
        Action3.updateRowTextFast = function(calcId, rowIndex, text) {
          const store = window.calcStore;
          let calc = store?._userCalcs?.[calcId] || store?._sessionCalcs?.[calcId] || store?._collabCalcs?.[calcId];
          if (!calc) {
            calc = resolveCalc(calcId);
          }
          if (!calc) {
            return Action3.updateRowText(calcId, rowIndex, text, { immediate: true });
          }
          const existingText = calc.rows?.[rowIndex]?.text;
          if (existingText === text) return calc;
          const newRows = calc.rows.slice();
          newRows[rowIndex] = { ...calc.rows[rowIndex], text };
          calc.rows = newRows;
          calc.updated = window.Timestamps?.now() || Date.now();
          calc.results = null;
          if (store?._callbacks) {
            store._callbacks.forEach((fn) => {
              try {
                fn(calc, { immediate: true, __fastPath: true });
              } catch (e) {
              }
            });
          }
          if (window.IC?.WORKER_LOADED && window.IC?.WORKER_RECALCULATE) {
            window.IC.WORKER_RECALCULATE(calc, (results) => {
              if (results && window.resultsStore) {
                window.resultsStore.set(calcId, results.map((r) => ({ ...r, status: "computed" })));
              }
            });
          } else if (window.IC?.api?.recalculate) {
            const results = window.IC.api.recalculate(calc);
            if (results && window.resultsStore) {
              window.resultsStore.set(calcId, results.map((r) => ({ ...r, status: "computed" })));
            }
          }
          _scheduleFlush(calcId);
          return calc;
        };
        Action3.flushPendingChanges = _flushPendingChanges;
        if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
          window.addEventListener("beforeunload", _flushPendingChanges);
        }
        Action3.commit = function(calc, mutation, detail = {}) {
          if (typeof window !== "undefined" && window.DebugLogger) {
            window.DebugLogger.log("store", `Commit: ${mutation?.type}`, { calcId: calc?.id, mutation, detail });
          }
          if (typeof window !== "undefined" && window.localStorage?.getItem("icDebugMutations") === "1") {
            console.log("[Action.commit]", {
              calcId: calc?.id,
              mutation,
              source: detail?.source
            });
          }
          if (typeof window !== "undefined" && window.DebugTools?.MutationHistory?.recordMutation) {
            let beforeCalc = null;
            if (calc?.id && window.calcStore?.get) {
              beforeCalc = window.calcStore.get(calc.id);
            }
            const mutationType = mutation?.type || "unknown";
            window.DebugTools.MutationHistory.recordMutation(mutationType, beforeCalc, calc);
          }
          const immediate = mutation?.options?.immediate;
          Action3.save(calc, { detail, immediate });
          DebugLogger2.log("perf", `[3/5] STORE_COMMIT type=${mutation?.type || "unknown"} calcId=${calc?.id?.slice(0, 8)}`);
          return calc;
        };
        Action3.updateName = function(calcOrId, name) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          const nextName = String(name || "").substr(0, 80);
          if (calc.name === nextName) {
            return calc;
          }
          Debug9.log("mutations", "\u{1F3F7}\uFE0F updateName:", { calcId: calc.id?.slice(0, 8), name: nextName });
          const newCalc = { ...calc, name: nextName };
          Action3.commit(newCalc, { type: "updateName" });
          return newCalc;
        };
        Action3.syncTextBlock = function(calcOrId, text, options = {}) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          const nextText = String(text ?? "");
          const lines = nextText.split("\n");
          const existingRows = calc.rows || [];
          const nextRows = lines.map((line, idx) => {
            const existing = existingRows[idx];
            if (!existing) {
              return { text: line, hidden: false };
            }
            if (existing.text === line) {
              return existing;
            }
            return { ...existing, text: line };
          });
          if (existingRows.length === nextRows.length) {
            let unchanged = true;
            for (let i = 0; i < existingRows.length; i += 1) {
              if (existingRows[i] !== nextRows[i]) {
                unchanged = false;
                break;
              }
            }
            if (unchanged) {
              return calc;
            }
          }
          const newCalc = { ...calc, rows: nextRows, results: null };
          if (calc.id && window.resultsStore) {
            const currentResults = window.resultsStore.get(calc.id) || [];
            let newResults;
            if (currentResults.length === nextRows.length) {
              newResults = currentResults;
            } else if (currentResults.length < nextRows.length) {
              newResults = [
                ...currentResults,
                ...Array(nextRows.length - currentResults.length).fill(null).map(() => makeBlankResult())
              ];
            } else {
              newResults = currentResults.slice(0, nextRows.length);
            }
            window.resultsStore.set(calc.id, newResults);
          }
          const source = options.source || "text-sync";
          Action3.commit(newCalc, { type: "syncTextBlock", rowCount: nextRows.length }, { source });
          return newCalc;
        };
        Action3.updateFolder = function(calcOrId, folder) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          const newCalc = { ...calc, folder: folder ? folder.trim() : "" };
          Action3.commit(newCalc, { type: "updateFolder" });
          return newCalc;
        };
        Action3.toggleRowVisibility = function(calcOrId, row_id) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          const newRows = calc.rows.map(
            (row, idx) => idx === row_id ? { ...row, hidden: !row.hidden } : row
          );
          const newCalc = { ...calc, rows: newRows };
          Action3.commit(newCalc, { type: "toggleRowVisibility", index: row_id });
          return newCalc;
        };
        Action3.clear = function(calc) {
          calc = calc || Action3.getCurrentCalc();
          const manager = Action3.getHistoryManager(calc);
          manager.captureSnapshot("clear", { force: true });
          calc.rows = calc.rows.map((row) => {
            row.text = "";
            row.hidden = false;
            return row;
          });
          manager.captureSnapshot("cleared", { force: true });
          Action3._saveWithoutHistory(calc);
          const results = Action3.recalculate(calc);
          if (calc?.id && typeof window !== "undefined" && window.resultsStore && Array.isArray(results)) {
            window.resultsStore.set(calc.id, results);
          }
          Action3.setResults(calc, results);
        };
        Action3.setRounding = function(calcOrId, digits) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          const newCalc = { ...calc, rounding: digits };
          Action3.commit(newCalc, { type: "setRounding" });
          const results = Action3.recalculate(newCalc);
          if (newCalc?.id && typeof window !== "undefined" && window.resultsStore && Array.isArray(results)) {
            window.resultsStore.clear(newCalc.id);
            window.resultsStore.set(newCalc.id, results);
          }
          if (results && Action3.setResults) {
            Action3.setResults(newCalc, results);
          }
          return newCalc;
        };
        Action3.setLocale = function(calcOrId, locale) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          let newLocale = calc.locale;
          try {
            let codes = Intl.getCanonicalLocales(locale);
            if (codes && codes.length) {
              newLocale = codes[0];
            }
          } catch (e) {
          }
          const newCalc = { ...calc, locale: newLocale };
          if (typeof window !== "undefined" && window.DebugLogger) {
            window.DebugLogger.log("store", `setLocale: ${newLocale}`, { calcId: calc.id, oldLocale: calc.locale });
          }
          Action3.commit(newCalc, { type: "setLocale" });
          const results = Action3.recalculate(newCalc);
          if (newCalc?.id && typeof window !== "undefined" && window.resultsStore && Array.isArray(results)) {
            window.resultsStore.clear(newCalc.id);
            window.resultsStore.set(newCalc.id, results);
          }
          if (results && Action3.setResults) {
            Action3.setResults(newCalc, results);
          }
          return newCalc;
        };
        Action3.create = function(fromCalc) {
          var i = Calc.all().filter((c) => {
            return c.name.match(Calc.DEFAULT_NAME);
          }).length;
          var default_name = i == 0 ? Calc.DEFAULT_NAME : Calc.DEFAULT_NAME + " " + (i + 1);
          fromCalc = fromCalc || {};
          var clone = safeClone3(fromCalc);
          delete clone.id;
          clone.name = clone.name || default_name;
          var calc = Calc.createCalcObject(clone);
          if (!window.calcStore) throw new Error("CalcStore not initialized");
          if (Action3.isLoggedIn()) {
            window.calcStore.saveUserCalc(calc);
          } else {
            window.calcStore.saveUserCalcLocal(calc);
          }
          return calc;
        };
        Action3.createAndOpen = function(options = {}) {
          const { folder = null, template = null } = options;
          const calc = Action3.create(template);
          if (folder) {
            Action3.updateFolder(calc, folder);
          }
          Action3.select(calc.id);
          return calc;
        };
        Action3.clone = function(calcOrId, suffix) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          var clone = safeClone3(calc);
          clone.name = calc.name + suffix;
          const CalcType = window.CalcType;
          if (CalcType && CalcType.isEphemeral(clone.type)) {
            delete clone.type;
          }
          delete clone.folder;
          var newCalc = Action3.create(clone);
          return newCalc;
        };
        Action3.cloneAndOpen = function(calc, options = {}) {
          const { suffix = " copy" } = options;
          const newCalc = Action3.clone(calc, suffix);
          Action3.select(newCalc.id);
          if (typeof window !== "undefined" && window.location.pathname !== "/") {
            window.location.href = "/";
          }
          return newCalc;
        };
        Action3.upgrade = function(calcOrId, version) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return;
          const newCalc = { ...calc, version: version || 2 };
          Action3.commit(newCalc, { type: "upgrade" });
          return newCalc;
        };
        Action3.sanitizeCalc = function(calc) {
          if (calc && calc.rows && Array.isArray(calc.rows)) {
            calc.rows = calc.rows.map((row) => {
              const safeRow = row || { text: "", hidden: false };
              if (!safeRow.id) {
                return { ...safeRow, id: EMPTY_ROW(1)[0].id };
              }
              return safeRow;
            });
          }
          return calc;
        };
        Action3.setCurrentCalc = function(calc) {
          if (window.actionLoopGuard?.check()) {
            console.error("[Action.setCurrentCalc] Loop detected, aborting");
            return;
          }
          const sanitized = Action3.sanitizeCalc(calc);
          if (!window.calcStore) throw new Error("CalcStore not initialized");
          window.calcStore.updateActiveCalc(sanitized);
        };
        Action3.getCurrentCalc = function() {
          if (!window.calcStore) throw new Error("CalcStore not initialized");
          const activeCalc = window.calcStore.getActiveCalc();
          return Action3.sanitizeCalc(activeCalc);
        };
        Action3.updateRowReferences = function(calc, n, change) {
          calc.rows.forEach((row) => {
            row.text = row.text.replace(/\b([:]?R)(\d+)\b/gi, (str, prefix, r) => {
              var rowRef = parseInt(r);
              var orig = prefix + rowRef;
              var changed = prefix + (rowRef + change);
              if (rowRef + change == 0) {
                return orig;
              }
              return rowRef > n ? changed : orig;
            });
          });
        };
        Action3.changeRowReferences = function(calc, searchIndex, replaceIndex) {
          calc.rows.forEach((row) => {
            row.text = row.text.replace(/\b([:]?R)(\d+)\b/gi, (str, prefix, r) => {
              var rowRef = parseInt(r);
              var orig = prefix + rowRef;
              var changed = `${prefix}${replaceIndex}`;
              if (rowRef == Number(searchIndex)) {
                return changed;
              }
              return orig;
            });
          });
        };
        Action3.moveRow = function(calc, from, to) {
          if (from === to || from < 0 || to < 0) return calc;
          if (!calc?.rows || from >= calc.rows.length) return calc;
          const newRows = [...calc.rows];
          const [movedRow] = newRows.splice(from, 1);
          newRows.splice(to, 0, movedRow);
          if (calc.id && window.resultsStore) {
            const results = window.resultsStore.get(calc.id);
            if (results && results.length > from) {
              const newResults = [...results];
              const [movedResult] = newResults.splice(from, 1);
              newResults.splice(to, 0, movedResult);
              window.resultsStore.set(calc.id, newResults);
            }
          }
          return { ...calc, rows: newRows };
        };
        Action3.dragMove = function(from, to) {
          if (from === null || to === null || from === to) return;
          from = Number(from);
          to = Number(to);
          let calc = Action3.getCurrentCalc();
          if (!calc?.rows) return;
          const maxIndex = calc.rows.length - 1;
          from = Math.max(0, Math.min(from, maxIndex));
          to = Math.max(0, Math.min(to, maxIndex));
          if (from === to) return;
          Action3.changeRowReferences(calc, from + 1, "99999");
          calc = Action3.moveRow(calc, from, to);
          Action3.changeRowReferences(calc, "99999", to + 1);
          Action3.commit(calc, { type: "dragMove", from, to });
          Action3.setSelectedRow(to, { calc });
        };
        Action3.pasteTextLines = function(calc, i, textLines) {
          if (window.actionLoopGuard?.check()) {
            console.error("[Action.pasteTextLines] Loop detected, aborting");
            return calc;
          }
          const rowCount = calc.rows.length;
          const newCount = i + textLines.length;
          let workingCalc = calc;
          if (newCount > rowCount) {
            workingCalc = Action3.addRows(calc, newCount - rowCount);
          }
          const newRows = workingCalc.rows.map((row, idx) => {
            const pasteIdx = idx - i;
            if (pasteIdx >= 0 && pasteIdx < textLines.length) {
              return { ...row, text: textLines[pasteIdx].trim() };
            }
            return row;
          });
          const newCalc = { ...workingCalc, rows: newRows };
          Action3.commit(newCalc, { type: "pasteTextLines", index: i });
          return newCalc;
        };
        Action3.shouldHideRows = function(calc) {
          let hiddenCount = calc.rows.filter((row) => row.hidden).length;
          let shouldHideRows = hiddenCount <= calc.rows.length / 2;
          return shouldHideRows;
        };
        Action3.presentCalc = function(calc) {
          let shouldHideRows = Action3.shouldHideRows(calc);
          const newRows = calc.rows.map((row) => ({ ...row, hidden: shouldHideRows }));
          const newCalc = { ...calc, rows: newRows };
          Action3.commit(newCalc, { type: "presentCalc" });
          return newCalc;
        };
        Action3.expandAllRows = function(calc) {
          const newRows = calc.rows.map((row) => ({ ...row, hidden: false }));
          const newCalc = { ...calc, rows: newRows };
          Action3.commit(newCalc, { type: "expandAllRows" });
          return newCalc;
        };
        Action3.collapseAllRows = function(calc) {
          const newRows = calc.rows.map((row) => ({ ...row, hidden: true }));
          const newCalc = { ...calc, rows: newRows };
          Action3.commit(newCalc, { type: "collapseAllRows" });
          return newCalc;
        };
        Action3.setCalcViewMode = function(calcOrId, mode) {
          const calc = resolveCalc(calcOrId);
          if (!calc) return null;
          const newCalc = { ...calc, viewMode: mode, updated: window.Timestamps.now() };
          Action3.commit(newCalc, { type: "setCalcViewMode", mode });
          return newCalc;
        };
        Action3.getCalcViewMode = function(calc) {
          if (!calc) return null;
          const viewMode = calc.viewMode;
          if (viewMode === "text" || viewMode === "calc" || viewMode === "present") {
            return viewMode;
          }
          return null;
        };
        Action3.legacyRecalculate = function(calc) {
          var inputs = calc.rows.map((row) => row?.text || "");
          var results = Action3.engine.instacalc_compute(inputs, {
            rounding: calc.rounding
          });
          return results.map((result, i) => {
            const meta = result?._meta || { v: 2 };
            const input = result.input || {
              original: inputs[i] || "",
              processed: result.processed || ""
            };
            return {
              ...result,
              _meta: meta,
              status: "computed",
              // Mark as computed for cache validity checks
              input
            };
          });
        };
        var processCache = {};
        Action3.scanForErrors = function(ret) {
          ret.forEach((result) => {
            const time = result?.debug?.timeMs;
            const processed = result?.input?.processed;
            if (time > 60 && processed && !processCache[processed]) {
              processCache[processed] = time;
              if (typeof Rollbar != "undefined") {
                Rollbar.info("slow parse", { time, processed });
              }
            }
          });
        };
        Action3.recalculate = function(calc) {
          if (Action3.isLegacyCalc(calc) || typeof IC == "undefined") {
            return Action3.legacyRecalculate(calc);
          }
          let ret = IC.api.recalculate(calc);
          ret = ret.map((r) => ({ ...r, status: "computed" }));
          window.setTimeout(() => Action3.scanForErrors(ret), 1e3);
          return ret;
        };
        Action3.async_recalculate = function(calc, callback) {
          const calcId = calc?.id;
          if (IC.WORKER_LOADED && !Action3.isLegacyCalc(calc)) {
            IC.WORKER_RECALCULATE(calc, (ret) => {
              ret = ret.map((r) => ({ ...r, status: "computed" }));
              if (calcId && window.resultsStore) {
                window.resultsStore.set(calcId, ret);
              }
              if (window.InstacalcEvents?.dispatch) {
                window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_CHANGED, {
                  calcId,
                  reason: "async-recalculate"
                });
              }
              if (callback) callback(ret);
            });
          } else {
            let ret = Action3.legacyRecalculate(calc);
            if (calcId && window.resultsStore) {
              window.resultsStore.set(calcId, ret);
            }
            if (callback) callback(ret);
          }
        };
        Action3.results = {};
        Action3.setResults = function(calc, results) {
          Action3.results[calc.id] = results;
        };
        Action3.getResults = function(calc) {
          return Action3.results[calc.id];
        };
        Action3.getTextView = function(calc, style = null) {
          const results = Action3.results[calc.id] || [];
          const inputs = results.map((result) => {
            let input = result?.input?.original || "";
            if (result?.comment?.original) {
              input = IC.parser.splitAtComment(input).before;
            }
            return input.trim();
          });
          const max = Math.max(...inputs.map((input) => input.length));
          const padToMax = (str) => str.padEnd(max + 5, " ");
          const formatFraction = (line) => line.replace(/FRACTION{(\d+)[,]\s*(\d+)}/g, "$1/$2");
          const lines = results.map((result, i) => {
            if (!inputs[i] && result?.comment?.original) {
              return result.comment.original;
            }
            const suffix = [result?.type?.units, result?.comment?.original].filter(Boolean).join(" ");
            const prefix = inputs[i] || (result?.comment?.original || "").trim();
            const equalsSign = inputs[i] && result?.output?.display ? " = " : "";
            const resultStr = result?.output?.display || "";
            let line = `${padToMax(prefix)}${equalsSign}${resultStr} ${suffix}`.trim();
            if (style === "inputs") {
              line = inputs[i];
            }
            if (style === "results") {
              line = resultStr;
            }
            if (style === "resultsAll") {
              line = `${resultStr} ${suffix}`.trim();
            }
            return formatFraction(line);
          });
          return lines.join("\n");
        };
        Action3.isLegacyCalc = function(calc) {
          if (!calc.published) {
            return false;
          }
          if (calc.version) {
            return false;
          }
          const BETA_EPOCH = Date.parse("2022-11-03T23:45:00.000Z");
          let date = calc.created || calc.created_at || calc.updated;
          if (date && Date.parse(date) < BETA_EPOCH) {
            return true;
          }
          return false;
        };
      })();
    }
  });

  // public/src/actions/UIService.js
  var require_UIService = __commonJS({
    "public/src/actions/UIService.js"() {
      var Debug9 = typeof window !== "undefined" && window.Debug || {
        log: () => {
        },
        warn: () => {
        },
        error: (...args) => console.error(...args)
      };
      (function() {
        var Action3 = window.Action || {};
        Action3.reactComponents = {};
        Action3.setReactComponent = function(calc, component) {
          Action3.reactComponents[calc.id] = component;
        };
        Action3.getReactComponent = function(calc) {
          return Action3.reactComponents[calc.id];
        };
        Action3._clampRowIndex = function(calc, i) {
          const c = calc || Action3.getCurrentCalc() || { rows: [] };
          const len = Array.isArray(c?.rows) ? c.rows.length : 0;
          let idx = Number.isInteger(i) ? i : 0;
          if (len === 0) return 0;
          if (idx < 0) return 0;
          if (idx >= len) return len - 1;
          return idx;
        };
        Action3.getUI = function(calc = null) {
          const c = calc || Action3.getCurrentCalc() || { rows: [] };
          return {
            hasUI: typeof window !== "undefined" && !!window.InstacalcEvents,
            getSelectedIndex: () => Action3._clampRowIndex(c, Action3.selectedRow),
            onSelectRow: (i) => {
              const idx = Action3._clampRowIndex(c, i);
              Action3.selectedRow = idx;
            },
            focusRow: (i) => {
              const idx = Action3._clampRowIndex(c, i);
              Debug9.log("ui", "\u{1F3AF} focusRow:", { index: idx, calcId: c.id?.slice(0, 8) });
              if (typeof window !== "undefined" && window.InstacalcEvents) {
                window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_ROW_FOCUS, {
                  rowIndex: idx,
                  calcId: c.id
                });
              }
            },
            smartRecalculate: () => {
              Debug9.log("ui", "\u{1F504} smartRecalculate:", { calcId: c.id?.slice(0, 8) });
              if (typeof window !== "undefined" && window.calcStore?.notifyChanged) {
                window.calcStore.notifyChanged(c, { reason: "smart-recalculate" });
              } else if (typeof window !== "undefined" && window.InstacalcEvents) {
                window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_CHANGED, {
                  calcId: c.id
                });
              }
            },
            updateRowVariableState: () => {
              if (typeof window !== "undefined" && window.InstacalcEvents) {
                window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_VARIABLE_STATE_UPDATE, {
                  calcId: c.id
                });
              }
            }
          };
        };
        Action3.getSelectedRowIndex = function(calc = null) {
          const ui = Action3.getUI(calc);
          return ui.getSelectedIndex();
        };
        Action3.setSelectedRow = function(i, options = {}) {
          const { focus = false, repeat = 1, calc = null } = options;
          const c = calc || Action3.getCurrentCalc();
          const idx = Action3._clampRowIndex(c, i);
          Debug9.log("ui", "\u{1F449} setSelectedRow:", { index: idx, focus, calcId: c?.id?.slice(0, 8) });
          Action3.selectedRow = idx;
          if (typeof window !== "undefined" && window.InstacalcEvents) {
            window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_SELECTION_REQUEST, {
              calcId: c?.id,
              rowIndex: idx,
              options
            });
          } else {
            const ui = Action3.getUI(c);
            for (let k = 0; k < Math.max(1, repeat); k++) {
              ui.onSelectRow(idx);
            }
            if (focus) {
              ui.focusRow(idx);
            }
          }
          return idx;
        };
        Action3.addTextToCurrentCalc = function(text) {
          var calc = Action3.getCurrentCalc();
          var row = Action3.getSelectedRowIndex(calc);
          var newCalc = Action3.addText(calc, row, text);
          Action3.setSelectedRow(row, { focus: true, calc: newCalc, selectionMode: "end" });
        };
        Action3.applyRowFormatStringToCurrentCalc = function(formatString) {
          var calc = Action3.getCurrentCalc();
          var row = Action3.getSelectedRowIndex(calc);
          formatString = " " + formatString.trim() + " ";
          let currentText = calc.rows[row].text.trimEnd();
          let newText = IC.api.appendBeforeComment(currentText, formatString);
          Action3.updateRowText(calc, row, newText);
          Action3.setSelectedRow(row, { focus: true, calc, selectionMode: "end" });
        };
        Action3.deleteTextFromCurrentCalc = function(n) {
          var calc = Action3.getCurrentCalc();
          var row = Action3.getSelectedRowIndex(calc);
          var newCalc = Action3.deleteText(calc, row, n || 1);
          Action3.setSelectedRow(row, { focus: true, calc: newCalc, selectionMode: "end" });
        };
        Action3.selectDefaultRow = function() {
          const calc = Action3.getCurrentCalc();
          let index = Action3.getSelectedRowIndex(calc);
          Action3.setSelectedRow(index, { repeat: 2, calc });
        };
        Action3.selectRelativeRow = function(n) {
          const calc = Action3.getCurrentCalc();
          let newRow = Action3.getSelectedRowIndex(calc) + n;
          if (newRow <= 0) newRow = 0;
          const max = Math.max(0, (calc?.rows?.length || 1) - 1);
          if (newRow > max) newRow = max;
          Action3.setSelectedRow(newRow, { repeat: 2, calc, focus: true });
        };
        Action3.selectRelativeRowSoft = function(n) {
          const calc = Action3.getCurrentCalc();
          let newRow = Action3.getSelectedRowIndex(calc) + n;
          if (newRow <= 0) newRow = 0;
          const max = Math.max(0, (calc?.rows?.length || 1) - 1);
          if (newRow > max) newRow = max;
          Action3.setSelectedRow(newRow, { repeat: 2, calc, focus: false });
        };
        Action3.hide = function() {
        };
        Action3.name = function(str, calc) {
          return str + calc.id;
        };
        Action3.getShortId = function(calc) {
          return calc.id.split("-")[0];
        };
      })();
    }
  });

  // public/src/action.js
  var require_action = __commonJS({
    "public/src/action.js"(exports, module2) {
      var Action3 = {};
      var Embed2 = Object.create(Action3);
      var Util2 = typeof window !== "undefined" && window.Util || typeof global !== "undefined" && global.Util;
      if (!Util2 && typeof window !== "undefined" && typeof __require === "function") {
        try {
          require_util();
        } catch (_e) {
        }
        Util2 = window.Util;
      }
      if (!Util2) {
        Util2 = {};
        if (typeof window !== "undefined") {
          window.Util = Util2;
        } else if (typeof global !== "undefined") {
          global.Util = Util2;
        }
      }
      if (!Util2.guid) {
        Util2.guid = function() {
          if (typeof window !== "undefined" && window.CalcId?.generateUUID) {
            return window.CalcId.generateUUID();
          }
          function S4() {
            return ((1 + Math.random()) * 65536 | 0).toString(16).substring(1);
          }
          return (S4() + S4() + "-" + S4() + "-4" + S4().substr(0, 3) + "-" + S4() + "-" + S4() + S4() + S4()).toLowerCase();
        };
      }
      if (!Util2.debounce) {
        Util2.debounce = function(func, wait, immediate) {
          var timeout;
          return function() {
            var context = this;
            var args = arguments;
            var later = function() {
              timeout = null;
              if (!immediate) {
                func.apply(context, args);
              }
            };
            var callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) {
              func.apply(context, args);
            }
          };
        };
      }
      if (typeof window !== "undefined") {
        window.Action = Action3;
        window.Embed = Embed2;
      } else if (typeof global !== "undefined") {
        global.Action = Action3;
        global.Embed = Embed2;
      }
      if (typeof window !== "undefined") {
        if (window.CalcStore && !window.calcStore) {
          window.calcStore = new window.CalcStore();
          window.dispatchEvent(new CustomEvent("ic:calc-store-ready"));
        }
      }
      Action3.saveEmbed = function(calc) {
        Calc2.set(calc.id, calc);
      };
      Action3.getEmbed = function(id) {
        return Calc2.get(id);
      };
      Action3.createEmbedCopy = function(id, newId) {
        const originalCalc = Calc2.get(id);
        if (!originalCalc) {
          console.log("[Action] createEmbedCopy: original calc not found:", id);
          return null;
        }
        const CalcType = window.CalcType;
        if (!CalcType) throw new Error("CalcType not loaded");
        const calcCopy = {
          ...originalCalc,
          id: newId,
          type: CalcType.SHARED_VIEW,
          // Single source of truth
          syncRef: null,
          // Shared views don't sync
          sourceId: id
          // Simple reference to original
        };
        delete calcCopy.source;
        if (window.calcStore) {
          return window.calcStore.saveSessionCalc(calcCopy);
        } else {
          Calc2.set(newId, calcCopy);
          return calcCopy;
        }
      };
      Action3.createSessionFromEmbed = function(id) {
        if (!id) return Promise.reject("Invalid ID");
        const newId = "session_copy_" + id;
        console.log("[Action] createSessionFromEmbed START. sourceId:", id, "newId:", newId);
        const finish = () => {
          if (Calc2.get(newId)) {
            console.log("[Action] createSessionFromEmbed: FINISH - Exists, returning:", newId);
            return newId;
          }
          const copy = Action3.createEmbedCopy(id, newId);
          console.log("[Action] createSessionFromEmbed: FINISH - Created new:", copy?.id);
          return copy ? newId : null;
        };
        if (Calc2.get(id)) {
          console.log("[Action] createSessionFromEmbed: Source exists in store.");
          if (Calc2.get(newId)) {
            console.log("[Action] createSessionFromEmbed: Target already exists (sync check).");
            return Promise.resolve(newId);
          }
          return Promise.resolve(finish());
        }
        console.log("[Action] createSessionFromEmbed: Fetching remote...");
        var url = window.FirebasePaths ? window.FirebasePaths.getSharedUrl(id) : `https://instacalc.firebaseio.com/shared/${id}.json`;
        return CachedJSON.fetch(url).then((calc) => {
          console.log("[Action] createSessionFromEmbed: Fetched.");
          const existing = Calc2.get(newId);
          if (existing) {
            console.log("[Action] Session created during fetch, reusing:", newId);
            return newId;
          }
          console.log("[Action] Session NOT found, creating copy:", newId);
          if (calc) {
            const updatedCalc = Calc2.validate({
              ...calc,
              id,
              published: { id }
            });
            Action3.saveEmbed(updatedCalc);
            return finish();
          }
          console.log("REMOTE NOT FOUND:" + id);
          return null;
        });
      };
      Action3.loadEmbed = function(id, fn, newId) {
        if (!id) {
          console.log("ERROR: invalid calc ID.");
          return () => {
          };
        }
        const setupSubscription = () => {
          return window.calcStore?.subscribe((updatedCalc, detail) => {
            if (updatedCalc?.id === newId) {
              fn(updatedCalc);
            }
          }) || (() => {
          });
        };
        const calcCopy = Action3.createEmbedCopy(id, newId);
        if (calcCopy) {
          fn(calcCopy);
          return setupSubscription();
        }
        var url = window.FirebasePaths ? window.FirebasePaths.getSharedUrl(id) : `https://instacalc.firebaseio.com/shared/${id}.json`;
        let isUnsubscribed = false;
        let innerUnsubscribe = () => {
          isUnsubscribed = true;
        };
        CachedJSON.fetch(url).then((calc) => {
          if (isUnsubscribed) return;
          if (calc) {
            const validatedCalc = Calc2.validate({
              ...calc,
              id,
              published: { id }
            });
            Action3.saveEmbed(validatedCalc);
            const copy = Action3.createEmbedCopy(id, newId);
            Action3.saveEmbed(copy);
            fn(copy);
            innerUnsubscribe = setupSubscription();
          } else {
            console.log("REMOTE NOT FOUND:" + id);
          }
        });
        return () => {
          innerUnsubscribe();
        };
      };
      Embed2.save = Action3.saveEmbed;
      Embed2.get = Action3.getEmbed;
      Embed2.createCopy = Action3.createEmbedCopy;
      var Calc2 = {};
      if (typeof window !== "undefined") {
        window.Calc = Calc2;
      } else if (typeof global !== "undefined") {
        global.Calc = Calc2;
      }
      var getCalcModel = () => {
        if (typeof window !== "undefined" && window.CalcModel) return window.CalcModel;
        if (typeof __require !== "undefined") return require_CalcModel();
        return { EMPTY_ROW: (n) => Array(n).fill({ text: "" }) };
      };
      var EMPTY_ROW2 = function(n) {
        return getCalcModel().EMPTY_ROW(n);
      };
      if (typeof window !== "undefined") {
        window.EMPTY_ROW = EMPTY_ROW2;
      } else if (typeof global !== "undefined") {
        global.EMPTY_ROW = EMPTY_ROW2;
      }
      Calc2.DEFAULT_NAME = "Scratchpad";
      var DEMO_ROWS = getCalcModel().DEMO_ROWS;
      var DEMO_ROWS_NEW = getCalcModel().DEMO_ROWS_NEW;
      Calc2.createCalcObject = function(obj) {
        return getCalcModel().create(obj);
      };
      Calc2.all = function() {
        if (!window.calcStore) throw new Error("CalcStore not initialized");
        var calcs = window.calcStore.allForSidebar();
        if (calcs.length === 0) {
          var new_calc = Calc2.createCalcObject({ rows: DEMO_ROWS });
          window.calcStore.saveUserCalcLocal(new_calc);
          return [new_calc];
        }
        return calcs;
      };
      Calc2.allByNewest = function() {
        return Calc2.all().sort(function(a, b) {
          return window.Timestamps.parse(b.updated || b.created) - window.Timestamps.parse(a.updated || a.created);
        });
      };
      Calc2.get = function(id) {
        if (!window.calcStore) throw new Error("CalcStore not initialized");
        return window.calcStore.get(id);
      };
      Calc2.set = function(id, calc) {
        if (!window.calcStore) throw new Error("CalcStore not initialized");
        return window.calcStore.set(id, calc);
      };
      Calc2.setIfNewer = function(id, calc) {
        let existing = Calc2.get(id);
        if (!existing) {
          return Calc2.set(id, calc);
        }
        if (window.calcStore && typeof window.calcStore._calcContentEquals === "function") {
          if (window.calcStore._calcContentEquals(existing, calc)) {
            return existing;
          }
        }
        let local_updated = window.Timestamps.parse(existing.updated);
        let remote_updated = window.Timestamps.parse(calc.updated);
        let editTime = 5 * 1e3;
        if (local_updated - remote_updated > editTime) {
        } else {
          Calc2.set(id, calc);
        }
      };
      Calc2.validate = function(calc) {
        return getCalcModel().validate(calc);
      };
      Calc2.merge = function(id, props) {
        var calc = Calc2.get(id);
        var updatedCalc = { ...calc, ...props };
        Calc2.set(id, updatedCalc);
      };
      Calc2.publishToSharedFirebase = function(id, calc) {
        if (typeof firebase !== "undefined" && firebase) {
          if (calc?.published?.disabled) {
            return;
          }
          var updates = {};
          const publishPath = window.FirebasePaths ? window.FirebasePaths.getPublishPath(id) : `/shared/${id}`;
          updates[publishPath] = calc;
          firebase.database().ref().update(updates);
        }
      };
      Calc2.publish = function(id) {
        var calc = Calc2.get(id);
        var published_date = window.Timestamps.now();
        var firebase_calc = {
          ...calc,
          updated: published_date,
          uid: firebase.auth().currentUser.uid
        };
        delete firebase_calc.folder;
        function handlePublish(published_id) {
          const updatedCalc = Calc2.get(id);
          updatedCalc.published = { id: published_id, updated: published_date };
          if (window.calcStore) {
            window.calcStore.saveUserCalc(updatedCalc);
          } else {
            Calc2.set(id, updatedCalc);
          }
          Calc2.publishToSharedFirebase(published_id, firebase_calc);
        }
        if (!calc?.published?.id && calc?.published?.origId) {
          return;
        }
        if (calc.published && calc.published.id) {
          handlePublish(calc.published.id);
        } else {
          var countRef = firebase.database().ref("/shared/_count");
          countRef.transaction(
            function(current_value) {
              return (current_value || 0) + 1;
            },
            function(err, committed, ss) {
              if (err) {
                console.warn("publish transaction failed", err);
              } else if (committed) {
                handlePublish(ss.val());
              }
            }
          );
        }
      };
      Calc2.publishAsWiki = async function(id, slug) {
        const CalcType = window.CalcType;
        if (!CalcType) throw new Error("CalcType not loaded");
        const calc = Calc2.get(id);
        if (!calc) throw new Error("Calc not found");
        if (!slug) throw new Error("Slug is required");
        const wikiCalc = {
          name: calc.name,
          rows: calc.rows,
          updated: window.Timestamps.now(),
          created: window.Timestamps.now()
        };
        if (typeof firebase !== "undefined" && firebase) {
          const path = window.FirebasePaths?.getRemotePath("wiki", slug);
          if (path) await firebase.database().ref(path).set(wikiCalc);
        }
        Calc2.merge(id, {
          type: CalcType.WIKI,
          syncRef: CalcType.syncRefForWiki(slug),
          remoteId: slug
        });
        return slug;
      };
      Calc2.publishAsCollab = async function(id) {
        const CalcType = window.CalcType;
        if (!CalcType) throw new Error("CalcType not loaded");
        const calc = Calc2.get(id);
        if (!calc) throw new Error("Calc not found");
        const token = window.TokenGenerator?.generateCollabToken(12) || generateFallbackToken(12);
        const collabCalc = {
          name: calc.name,
          rows: calc.rows,
          updated: window.Timestamps.now(),
          created: window.Timestamps.now()
        };
        if (typeof firebase !== "undefined" && firebase) {
          const path = window.FirebasePaths?.getRemotePath("private", token);
          if (path) await firebase.database().ref(path).set(collabCalc);
        }
        Calc2.merge(id, {
          type: CalcType.COLLAB,
          syncRef: CalcType.syncRefForCollab(token),
          remoteId: token
        });
        if (window.calcStore?.subscribeToRemote) {
          window.calcStore.subscribeToRemote("collab", token, id);
        }
        return token;
      };
      function generateFallbackToken(length) {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result = "";
        const array = new Uint8Array(length);
        crypto.getRandomValues(array);
        for (let i = 0; i < length; i++) {
          result += chars[array[i] % chars.length];
        }
        return result;
      }
      Calc2.unshare = async function(id) {
        const calc = Calc2.get(id);
        if (!calc) return;
        const calcType = calc.type || (calc.published?.id ? "shared" : "local");
        if (calcType === "local") return;
        if (calcType === "wiki" || calcType === "private") {
          Calc2.set(id, { ...calc, deleted: true });
        } else if (calc.published?.id) {
          const publishedId = calc.published.id;
          if (typeof firebase !== "undefined" && firebase) {
            try {
              const unpublishPath = window.FirebasePaths ? window.FirebasePaths.getPublishPath(publishedId) : `/shared/${publishedId}`;
              await firebase.database().ref(unpublishPath).remove();
            } catch (e) {
              console.warn("Failed to remove from Firebase:", e);
            }
          }
          Calc2.merge(id, {
            published: null,
            type: null
          });
        }
      };
      Calc2.createFromRemote = function(type, identifier, data) {
        const CalcType = window.CalcType;
        if (!CalcType) throw new Error("CalcType not loaded");
        let normalizedType = type;
        if (type === "private") normalizedType = CalcType.COLLAB;
        let syncRef = null;
        if (normalizedType === CalcType.WIKI || type === "wiki") {
          syncRef = CalcType.syncRefForWiki(identifier);
          normalizedType = CalcType.WIKI;
        } else if (normalizedType === CalcType.LIVE || type === "live") {
          syncRef = CalcType.syncRefForLive(identifier);
          normalizedType = CalcType.LIVE;
        } else if (normalizedType === CalcType.COLLAB || type === "collab" || type === "private") {
          syncRef = CalcType.syncRefForCollab(identifier);
          normalizedType = CalcType.COLLAB;
        }
        const isLive = normalizedType === CalcType.LIVE;
        const isWiki = normalizedType === CalcType.WIKI;
        const idPrefix = isLive ? "live" : isWiki ? "wiki" : "collab";
        const defaultName = isLive || isWiki ? identifier.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Private Calc";
        const calcObj = {
          id: `${idPrefix}-${identifier}`,
          name: data.name || defaultName,
          rows: data.rows || [],
          type: normalizedType,
          // CalcType is single source of truth
          syncRef,
          // syncRef is the new sync abstraction
          remoteId: identifier,
          updated: data.updated,
          created: data.created
        };
        return Calc2.validate(calcObj);
      };
      Calc2.createFromWiki = (slug, data) => Calc2.createFromRemote("wiki", slug, data);
      Calc2.createFromCollab = (token, data) => Calc2.createFromRemote("private", token, data);
      Calc2.createFromLive = (slug, data) => Calc2.createFromRemote("live", slug, data);
      Calc2.getUrl = function(calc, options = {}) {
        const origin = options.origin || (typeof window !== "undefined" ? window.location.origin : "https://instacalc.com");
        if (!calc) {
          return { path: "#", display: "#...", copyable: false };
        }
        const CalcType = window.CalcType;
        if (!CalcType) throw new Error("CalcType not loaded");
        if (CalcType.isUrlDefined(calc.type)) {
          const rows = calc.rows || [];
          const texts = rows.map((r) => r.text).filter((t) => t && t.trim());
          const compactTexts = texts.map((text) => text.replace(/\s*([=:])\s*/g, "$1"));
          const title = calc.name && calc.name !== "Quick Link Snippet" ? calc.name : null;
          const parts = title ? [title, ...compactTexts] : compactTexts;
          const content = parts.join("|");
          const path = `#${encodeURIComponent(content)}`;
          const truncated = content.length > 25 ? content.slice(0, 22) + "..." : content;
          return { path, display: `#${truncated}`, copyable: true };
        }
        const urlPath = CalcType.getUrlPath(calc);
        if (urlPath) {
          return { path: urlPath, display: `instacalc.com${urlPath}`, copyable: true, fullUrl: `${origin}${urlPath}` };
        }
        if (typeof window !== "undefined" && window.UrlManager) {
          const hashWithPrefix = window.UrlManager.localCalcHash(calc.id);
          return { path: hashWithPrefix, display: hashWithPrefix, copyable: false };
        }
        const prefix = "local:";
        const shortId = (calc.id || "").slice(0, 8);
        return { path: `#${prefix}${shortId}`, display: `#${prefix}${shortId}`, copyable: false };
      };
      Calc2.loadLocalStore = function() {
        var store;
        try {
          if (window.localStorage && localStorage.getItem("store")) {
            var json_string = localStorage.getItem("store");
            store = JSON.parse(json_string);
          }
        } catch (e) {
          console.log("Error decoding JSON");
        }
        window.store = store || {};
        if (!window.store.calcs) {
          var newCalc = Calc2.createCalcObject({ rows: DEMO_ROWS });
          window.store.calcs = {};
          window.store.calcs[newCalc.id] = newCalc;
        }
      };
      Calc2.clearLocalStore = function() {
        if (window.localStorage) {
          localStorage.setItem("store", "");
        }
      };
      Calc2._saveLocalStore = function() {
        if (!window.localStorage) {
          return;
        }
        if (localStorage) {
          const persistable = { ...window.store || {} };
          delete persistable.sessionCalcs;
          localStorage.setItem("store", JSON.stringify(persistable));
        }
      };
      Calc2.saveLocalStore = Util2.debounce(Calc2._saveLocalStore, 500);
      Calc2.loadLocalStore();
      var CachedJSON = {
        cache: /* @__PURE__ */ new Map(),
        fetch(url, options = {}) {
          const cacheKey = JSON.stringify({ url, options });
          if (!this.cache.has(cacheKey)) {
            this.cache.set(
              cacheKey,
              fetch(url, options).then((res) => res.json()).catch((error) => {
                this.cache.delete(cacheKey);
                throw error;
              })
            );
          }
          return this.cache.get(cacheKey);
        }
      };
      if (typeof InstaCalc === "function" && !Action3.engine) {
        Action3.engine = new InstaCalc();
      }
      if (typeof module2 !== "undefined" && module2.exports && typeof __filename !== "undefined" && __filename.includes("/src/")) {
        require_AuthService();
        require_HistoryService();
        require_PersistenceService();
        require_ExternalDataService();
        require_PromoteService();
        require_CalcMutations();
        require_UIService();
        if (!Action3.engine) {
          if (typeof InstaCalc === "function") {
            Action3.engine = new InstaCalc();
          } else {
            Action3.engine = {
              instacalc_compute: (inputs) => inputs.map((input) => {
                if (typeof window === "undefined" || !window.Result?.makeBlank) {
                  throw new Error("ResultUtils not loaded. window.Result.makeBlank is missing.");
                }
                const blank = window.Result.makeBlank("computed");
                blank.input = { original: input || "", processed: input || "" };
                blank.debug = { ...blank.debug, timeMs: 0 };
                return blank;
              })
            };
          }
        }
      }
    }
  });

  // public/src/actions/RegisteredShortcuts.js
  var require_RegisteredShortcuts = __commonJS({
    "public/src/actions/RegisteredShortcuts.js"(exports, module2) {
      (function() {
        "use strict";
        function initGlobalShortcuts() {
          document.addEventListener("keydown", (event) => {
            if (window.__instacalcDisplayMode && window.__instacalcDisplayMode !== "regular") {
              return;
            }
            if (event.key === "Escape") {
              if (window.DebugTools?.KeyLogger?.logKey) {
                window.DebugTools.KeyLogger.logKey("Escape", "RegisteredShortcuts", "closePanel", false);
              }
              const store = window.Alpine?.store?.("ui");
              if (store?.activePanel && store.rightRailPanels?.includes(store.activePanel)) {
                store.closePanel();
                event.preventDefault();
                return;
              }
              if (store?.mobiletab) {
                store.closeMobilePanel();
                event.preventDefault();
                return;
              }
              if (window.InstacalcEvents) {
                window.InstacalcEvents.dispatch("ESCAPE_PRESSED", {});
              }
            }
          });
        }
        function init() {
          initGlobalShortcuts();
          console.log("[RegisteredShortcuts] Global shortcuts initialized");
        }
        if (typeof window !== "undefined") {
          window.RegisteredShortcuts = { init, initGlobalShortcuts };
        }
        if (typeof module2 !== "undefined" && module2.exports) {
          module2.exports = { init, initGlobalShortcuts };
        }
      })();
    }
  });

  // public/src/utils/compression.js
  var require_compression = __commonJS({
    "public/src/utils/compression.js"(exports, module2) {
      var Compression = {
        /**
         * Compress string to Gzip + Base64 (URL-safe)
         */
        async compress(inputString) {
          if (typeof CompressionStream === "undefined") {
            return btoa(unescape(encodeURIComponent(inputString)));
          }
          const stream = new Blob([inputString]).stream().pipeThrough(new CompressionStream("gzip"));
          const buffer = await new Response(stream).arrayBuffer();
          const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
          return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        },
        /**
         * Decompress Base64 (URL-safe) + Gzip to string
         */
        async decompress(base64String) {
          if (typeof DecompressionStream === "undefined") {
            return decodeURIComponent(escape(atob(base64String)));
          }
          let base64 = base64String.replace(/-/g, "+").replace(/_/g, "/");
          while (base64.length % 4) base64 += "=";
          try {
            const binaryString = atob(base64);
            const bytes = Uint8Array.from(binaryString, (c) => c.charCodeAt(0));
            const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
            const buffer = await new Response(stream).arrayBuffer();
            return new TextDecoder().decode(buffer);
          } catch (e) {
            return decodeURIComponent(escape(atob(base64)));
          }
        }
      };
      if (typeof window !== "undefined") {
        window.Compression = Compression;
      }
      if (typeof module2 !== "undefined" && typeof module2.exports !== "undefined") {
        module2.exports = Compression;
      }
    }
  });

  // public/src/utils/UrlManager.js
  var require_UrlManager = __commonJS({
    "public/src/utils/UrlManager.js"(exports, module2) {
      var Debug9 = typeof window !== "undefined" && window.Debug || require_debug();
      var Compression = typeof window !== "undefined" && window.Compression || require_compression();
      var getViewContext = () => {
        if (typeof window !== "undefined" && window.ViewContext) {
          return window.ViewContext;
        }
        if (typeof __require !== "undefined") {
          return require_ViewContext();
        }
        throw new Error("ViewContext not loaded");
      };
      var UrlManager = {
        // ============================================================
        // Configuration
        // ============================================================
        // Feature flags
        FEATURES: {
          // Enable #my/<id> URL routing for local calcs (set to false to disable URL updates on calc selection)
          LOCAL_CALC_ROUTING: false
        },
        HASH: {
          LOCAL_CALC: {
            prefix: "my/",
            // New format: #my/<id>
            legacyPrefix: "local:",
            // Old format: #local:<id> (backward compat)
            idLength: 6
            // Generate 6 chars (16^6 = 16.7M unique IDs)
          },
          PARAMS: {
            DATA: "data",
            // Current: #data=<gzip-base64>
            GZIP: "gzip"
            // Legacy: #gzip=<gzip-base64>
          }
        },
        // Pattern: "my/" followed by any hex characters (new format)
        LOCAL_CALC_PATTERN: /^my\/[a-f0-9]+$/i,
        // Pattern: "local:" followed by any hex characters (legacy format, for backward compat)
        LEGACY_LOCAL_CALC_PATTERN: /^local:[a-f0-9]+$/i,
        // ============================================================
        // URL Parsing (migrated from UrlCalcModule)
        // ============================================================
        /**
         * Parses the URL and applies overrides to the base content.
         */
        parseUrl(urlString, baseContent = "") {
          let url;
          try {
            url = new URL(urlString || window.location.href);
          } catch (e) {
            url = new URL("https://instacalc.com");
          }
          const params = new URLSearchParams(url.search);
          let hash = url.hash.slice(1);
          let hashParams = new URLSearchParams();
          const qIdx = hash.indexOf("?");
          if (qIdx !== -1) {
            hashParams = new URLSearchParams(hash.slice(qIdx + 1));
            hash = hash.slice(0, qIdx);
          }
          let viewModeOverride = null;
          const hashDecodedFull = decodeURIComponent(hash);
          if (hashDecodedFull.endsWith("/embed")) {
            viewModeOverride = "embed";
            hash = hash.slice(0, -"/embed".length);
          }
          if (url.pathname.endsWith("/embed") || url.pathname.endsWith("/embed/")) {
            viewModeOverride = "embed";
          }
          const settings = {
            theme: hashParams.get("theme") || params.get("theme") || null,
            view: hashParams.get("view") || params.get("view") || viewModeOverride || null,
            input: hashParams.get("input") || params.get("input") || null,
            run: (hashParams.has("run") ? hashParams.get("run") : params.get("run")) !== "false"
          };
          Debug9.log("url", "[UrlManager.parseUrl] Parsed settings:", settings);
          let content = baseContent;
          let contentPromise = null;
          let isStateless = false;
          const externalUrl = this._extractExternalUrl(url.pathname);
          if (externalUrl) {
            return {
              content: baseContent,
              settings,
              isStateless: true,
              externalUrl,
              localFilePath: null
            };
          }
          const localFilePath = this._extractLocalFilePath(url.pathname);
          if (localFilePath) {
            return {
              content: baseContent,
              settings,
              isStateless: true,
              externalUrl: null,
              localFilePath
            };
          }
          const rawPath = decodeURIComponent(url.pathname.slice(1));
          const isReserved = rawPath.startsWith("wiki/") || rawPath.startsWith("embed") || rawPath.startsWith("@") || rawPath.startsWith("!") || rawPath === "";
          let pathContentString = null;
          if (!isReserved) {
            const isHexId = this.isLocalCalcHash("local:" + rawPath);
            const isNumericId = /^\d+$/.test(rawPath);
            const isId = isHexId || isNumericId;
            const hasStrongMath = /[+*=^]|\bto\b|\bin\b/.test(rawPath);
            const isExplicit = rawPath.startsWith("=");
            if (isExplicit || hasStrongMath && !isId) {
              pathContentString = isExplicit ? rawPath.slice(1) : rawPath;
              isStateless = true;
            }
          }
          let hashDecoded = window.Util?.safeDecodeURI ? window.Util.safeDecodeURI(hash) : decodeURIComponent(hash);
          if (hashDecoded) {
            if (hashDecoded.startsWith("http://") || hashDecoded.startsWith("https://")) {
              return {
                content: baseContent,
                settings,
                isStateless: true,
                externalUrl: hashDecoded,
                localFilePath: null
              };
            }
            if (hashDecoded.startsWith("/") && this._extractLocalFilePath(hashDecoded)) {
              return {
                content: baseContent,
                settings,
                isStateless: true,
                externalUrl: null,
                localFilePath: hashDecoded
              };
            }
            const dataPrefix = `${this.HASH.PARAMS.DATA}=`;
            const gzipPrefix = `${this.HASH.PARAMS.GZIP}=`;
            if (hashDecoded.startsWith(dataPrefix) || hashDecoded.startsWith(gzipPrefix)) {
              isStateless = true;
              const prefixLen = hashDecoded.startsWith(dataPrefix) ? dataPrefix.length : gzipPrefix.length;
              const b64Data = hashDecoded.slice(prefixLen);
              contentPromise = (async () => {
                try {
                  const jsonStr = await Compression.decompress(b64Data);
                  const data = JSON.parse(jsonStr);
                  if (data.rows && Array.isArray(data.rows)) {
                    const isNewFormat = data.v >= 1 && typeof data.rows[0] === "object";
                    if (isNewFormat) {
                      let innerContent = data.rows.map((r) => r.text).join("\n");
                      return {
                        content: innerContent,
                        settings: { ...settings, ...data.settings },
                        isStateless: true,
                        dataUrlName: data.name || null,
                        dataUrlDescription: data.description || null,
                        dataUrlRows: data.rows,
                        dataUrlVersion: data.v
                      };
                    } else {
                      return {
                        content: data.rows.join("\n"),
                        settings,
                        isStateless: true,
                        dataUrlName: data.name || null
                      };
                    }
                  }
                } catch (e) {
                  console.warn("[UrlManager] Failed to decode data URL:", e);
                }
                return null;
              })();
            }
          }
          const isLocalCalcId = this.isLocalCalcHash(hashDecoded);
          const isHashContent = !isStateless && hashDecoded && !isLocalCalcId && (hashDecoded.includes("=") || hashDecoded.includes("|") || hashDecoded.includes("+") || hashDecoded.match(/^\d/));
          let title = null;
          if (pathContentString) {
            const processed = this._processContentString(pathContentString);
            content = processed.content;
            title = processed.title;
          } else if (isHashContent) {
            const processed = this._processContentString(hashDecoded);
            content = processed.content;
            title = processed.title;
            isStateless = true;
          } else if (params.has("c")) {
            try {
              const rawC = params.get("c");
              const decodedC = decodeURIComponent(escape(window.atob(rawC)));
              const processed = this._processContentString(decodedC);
              content = processed.content;
              title = processed.title;
              isStateless = true;
            } catch (e) {
              console.warn("[UrlManager] Failed to parse legacy c= param", e);
            }
          } else if (params.has("d")) {
            const processed = this._processContentString(params.get("d"));
            content = processed.content;
            title = processed.title;
            isStateless = true;
          } else if (params.has("q")) {
            content = params.get("q");
            isStateless = true;
          } else if (urlString && urlString.endsWith("#")) {
            content = "";
            isStateless = true;
          }
          const paramCount = params.size !== void 0 ? params.size : Array.from(params.keys()).length;
          const hashParamCount = hashParams.size !== void 0 ? hashParams.size : Array.from(hashParams.keys()).length;
          const urlHasRowDelimiters = url.href.includes("|") || url.href.includes(";") || url.hash.includes("|") || url.hash.includes(";");
          const hasExplicitContent = !!content || isStateless;
          const hasOverrides = (paramCount > 0 || hashParamCount > 0) && (hasExplicitContent || urlHasRowDelimiters);
          if (content || hasOverrides) {
            const overrides = {};
            const keysToSkip = [
              // Legacy content params (data comes from these)
              "d",
              "q",
              "c",
              "s",
              // Version/cache-busting params
              "v",
              "ver",
              "version",
              "cache",
              "nocache",
              "_",
              "t",
              "ts",
              "timestamp",
              // Instacalc display params
              "theme",
              "view",
              "input",
              "run",
              "edit",
              "debug",
              "pwa",
              // Google Analytics / Ads
              "utm_source",
              "utm_medium",
              "utm_campaign",
              "utm_term",
              "utm_content",
              "utm_id",
              "gclid",
              "gclsrc",
              "dclid",
              "gbraid",
              "wbraid",
              // Facebook
              "fbclid",
              "fb_action_ids",
              "fb_action_types",
              "fb_source",
              // Microsoft / Bing
              "msclkid",
              // Mailchimp
              "mc_cid",
              "mc_eid",
              // Other common tracking
              "ref",
              "source",
              "referrer",
              "affiliate",
              "partner",
              "clickid",
              "campaign",
              "ad",
              "adgroup",
              "keyword",
              // Social / sharing
              "share",
              "from",
              "via",
              // PWA / app params
              "standalone",
              "display-mode",
              "utm_reader",
              // Analytics / measurement
              "_ga",
              "_gl",
              "_hsenc",
              "_hsmi",
              "hsa_acc",
              "hsa_cam",
              "hsa_grp",
              "hsa_ad",
              "hsa_src",
              "hsa_tgt",
              "hsa_kw",
              "hsa_mt",
              "hsa_net",
              "hsa_ver",
              // Other platforms
              "twclid",
              "ttclid",
              "li_fat_id",
              "igshid",
              "si",
              "feature"
            ];
            const mergeOverrides = (p) => {
              p.forEach((value, key) => {
                const lowerKey = key.toLowerCase();
                if (!keysToSkip.includes(lowerKey)) {
                  overrides[key] = value;
                }
              });
            };
            const contentFromQuery = params.has("d") || params.has("q");
            if (contentFromQuery) {
              mergeOverrides(params);
            }
            mergeOverrides(hashParams);
            if (!isStateless) {
              mergeOverrides(params);
            }
            if (content || Object.keys(overrides).length > 0) {
              content = this._applyOverrides(content || "", overrides);
            }
          }
          return {
            content,
            contentPromise,
            settings,
            isStateless,
            externalUrl: null,
            localFilePath: null,
            title,
            localCalcId: isLocalCalcId ? hashDecoded : null
          };
        },
        // ============================================================
        // URL Generation (migrated from UrlCalcModule)
        // ============================================================
        /**
         * Generate a data URL from calc object (inverse of parseUrl)
         */
        async toDataUrl(calc, options = {}) {
          const { baseUrl = "", settings = null, path = "/" } = options;
          if (!calc || !calc.rows) {
            return `${baseUrl}${path}`;
          }
          const data = {
            v: 1,
            name: calc.name || "Calculator",
            rows: calc.rows.map((r) => {
              const row = { text: r.text || "" };
              if (r.isHidden || r.hidden) row.hidden = true;
              return row;
            })
          };
          if (settings) {
            data.settings = settings;
          }
          const json = JSON.stringify(data);
          try {
            const compressed = await Compression.compress(json);
            let url = `${baseUrl}${path}#${this.HASH.PARAMS.DATA}=${compressed}`;
            if (settings) {
              const queryParams = new URLSearchParams();
              if (settings.view) queryParams.set("view", settings.view);
              if (settings.theme) queryParams.set("theme", settings.theme);
              if (settings.input) queryParams.set("input", settings.input);
              if (settings.run === false) queryParams.set("run", "false");
              const queryString = queryParams.toString();
              if (queryString) {
                url += "?" + queryString;
              }
            }
            return url;
          } catch (e) {
            return `${baseUrl}${path}`;
          }
        },
        /**
         * Generate a data URL from calc text directly
         */
        async textToDataUrl(calcText, options = {}) {
          const { baseUrl = "", path = "/", name = "Calculator", settings = null } = options;
          if (!calcText || typeof calcText !== "string") {
            return `${baseUrl}${path}`;
          }
          const rows = calcText.split("\n").map((text) => ({ text: text.trimEnd() }));
          const data = {
            v: 1,
            name,
            rows
          };
          if (settings) {
            data.settings = settings;
          }
          const json = JSON.stringify(data);
          try {
            const compressed = await Compression.compress(json);
            let url = `${baseUrl}${path}#${this.HASH.PARAMS.DATA}=${compressed}`;
            if (settings) {
              const queryParams = new URLSearchParams();
              if (settings.view) queryParams.set("view", settings.view);
              if (settings.theme) queryParams.set("theme", settings.theme);
              if (settings.input) queryParams.set("input", settings.input);
              if (settings.run === false) queryParams.set("run", "false");
              const queryString = queryParams.toString();
              if (queryString) {
                url += "?" + queryString;
              }
            }
            return url;
          } catch (e) {
            console.warn("[UrlManager] textToDataUrl compression failed:", e);
            return `${baseUrl}${path}`;
          }
        },
        toQuicklink(calc, options = {}) {
          const { baseUrl = "" } = options;
          if (!calc || !calc.rows) {
            return `${baseUrl}/embed`;
          }
          const parts = [];
          if (calc.name) {
            parts.push(calc.name);
          }
          calc.rows.forEach((r) => {
            if (!r.isHidden && !r.hidden && r.text) {
              parts.push(r.text);
            }
          });
          const hash = parts.join("|");
          return `${baseUrl}/embed#${window.Util?.encodeFriendly ? window.Util.encodeFriendly(hash) : encodeURIComponent(hash)}`;
        },
        // ============================================================
        // Parsing Helpers (migrated from UrlCalcModule)
        // ============================================================
        _looksLikeTitle(str) {
          if (!str || str.length < 2) return false;
          const trimmed = str.trim();
          if (!/^[a-zA-Z]/.test(trimmed)) return false;
          if (trimmed.includes("=") || trimmed.includes(":")) return false;
          if (/\d[x*+/-]\d/.test(trimmed)) return false;
          if (/^\d+[_\]?[a-z]+$/i.test(trimmed)) return false;
          return true;
        },
        _processContentString(raw) {
          if (!raw) return { content: "", title: null };
          if (window.Util?.decodeFriendly) {
            const decoded2 = window.Util.decodeFriendly(raw);
            const lines2 = decoded2.split(/[|;]/);
            let title2 = null;
            if (lines2.length > 1) {
              const firstLine = lines2[0].trim();
              if (this._looksLikeTitle(firstLine)) {
                title2 = firstLine;
                lines2.shift();
              }
            }
            const processedLines2 = lines2.map((line) => {
              line = line.replace(/ {2,}/g, " ");
              const assignmentMatch = line.match(/^(\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*([=:])\s*(.*)$/);
              if (assignmentMatch) {
                const [, leadingSpace, varName, op, value] = assignmentMatch;
                return `${leadingSpace}${varName} ${op} ${value.trim()}`;
              }
              return line;
            });
            return { content: processedLines2.join("\n"), title: title2 };
          }
          const decoded = decodeURIComponent(raw);
          const PLACEHOLDER = "%%US%% \u09A8\u09BF\u09B6\u09CD\u099A\u09BF\u09B9\u09CD\u09A8";
          const safeStr = decoded.replace(/__/g, PLACEHOLDER);
          const lines = safeStr.split(/[|;]/);
          let title = null;
          if (lines.length > 1) {
            const firstLine = lines[0].trim();
            if (this._looksLikeTitle(firstLine)) {
              title = firstLine.replace(/_/g, " ").replace(new RegExp(PLACEHOLDER, "g"), "_");
              lines.shift();
            }
          }
          const processedLines = lines.map((line) => {
            const assignmentMatch = line.match(/^(\s*)([a-zA-Z_][a-zA-Z0-9_]*)([=:])(.*)$/);
            if (assignmentMatch) {
              const [, leadingSpace, varName, op, value] = assignmentMatch;
              const cleanVar = varName.replace(new RegExp(PLACEHOLDER, "g"), "_");
              let cleanValue = value.trim().replace(/_/g, " ").replace(new RegExp(PLACEHOLDER, "g"), "_");
              cleanValue = cleanValue.replace(/ {2,}/g, " ");
              return `${leadingSpace}${cleanVar} ${op} ${cleanValue}`;
            } else {
              return line.replace(/_/g, " ").replace(new RegExp(PLACEHOLDER, "g"), "_").replace(/ {2,}/g, " ");
            }
          });
          return { content: processedLines.join("\n"), title };
        },
        _applyOverrides(content, overrides) {
          let lines = content.split("\n");
          const originalKeys = Object.keys(overrides);
          if (originalKeys.length === 0) return content;
          const assignmentRows = [];
          lines.forEach((line, index) => {
            const match = line.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(=|:)\s*(.*?)(\/\/.*)?$/);
            if (match) {
              assignmentRows.push({ index, varName: match[1] });
            }
          });
          const activeOverrides = {};
          const handledOriginalKeys = /* @__PURE__ */ new Set();
          originalKeys.forEach((key) => {
            const lowerKey = key.toLowerCase();
            const rMatch = lowerKey.match(/^r(\d+)$/);
            if (rMatch) {
              const rowNum = parseInt(rMatch[1]);
              if (rowNum > 0 && rowNum <= assignmentRows.length) {
                const targetVar = assignmentRows[rowNum - 1].varName;
                activeOverrides[targetVar.toLowerCase()] = overrides[key];
                handledOriginalKeys.add(key);
              }
            } else {
              activeOverrides[lowerKey] = overrides[key];
            }
          });
          const newLines = lines.map((line) => {
            const match = line.match(/^\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(=|:)\s*(.*?)(\/\/.*)?$/);
            if (match) {
              const varName = match[1];
              const separator = match[2];
              const comment = match[4] || "";
              const lowerVarName = varName.toLowerCase();
              if (Object.hasOwn(activeOverrides, lowerVarName)) {
                const newVal = activeOverrides[lowerVarName];
                originalKeys.forEach((k) => {
                  if (k.toLowerCase() === lowerVarName) handledOriginalKeys.add(k);
                });
                return `${varName} ${separator} ${newVal}${comment ? " " + comment : ""}`;
              }
            }
            return line;
          });
          const unusedKeys = originalKeys.filter((k) => !handledOriginalKeys.has(k));
          if (unusedKeys.length > 0) {
            const prependedLines = unusedKeys.map((k) => `${k} = ${overrides[k]}`);
            return [...prependedLines, ...newLines].join("\n");
          }
          return newLines.join("\n");
        },
        _extractExternalUrl(pathname) {
          if (!pathname) return null;
          const decoded = decodeURIComponent(pathname.slice(1));
          if (decoded.startsWith("http://") || decoded.startsWith("https://")) {
            try {
              new URL(decoded);
              return decoded;
            } catch (e) {
              return null;
            }
          }
          return null;
        },
        // Supported file extensions for remote/local calc loading
        _calcFileExtensions: [".ic", ".calc", ".txt", ".md"],
        _extractLocalFilePath(pathname) {
          if (!pathname) return null;
          const decoded = decodeURIComponent(pathname);
          const lowerPath = decoded.toLowerCase();
          if (this._calcFileExtensions.some((ext) => lowerPath.endsWith(ext))) {
            return decoded;
          }
          return null;
        },
        getFileType(url) {
          if (!url) return "unknown";
          try {
            const urlObj = new URL(url);
            const pathname = urlObj.pathname.toLowerCase();
            if (pathname.endsWith(".ic")) return "ic";
            if (pathname.endsWith(".calc")) return "calc";
            if (pathname.endsWith(".txt")) return "txt";
            if (pathname.endsWith(".md")) return "md";
            return "unknown";
          } catch (e) {
            return "unknown";
          }
        },
        _fetchStrategies: {
          direct: async (url) => {
            const response = await fetch(url, {
              method: "GET",
              headers: { "Accept": "text/plain, text/*, */*" }
            });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.text();
          },
          local: async (url, baseUrl) => {
            const targetUrl = new URL(url);
            const base = new URL(baseUrl || window.location.href);
            if (targetUrl.origin === base.origin) {
              return UrlManager._fetchStrategies.direct(url);
            }
            throw new Error("URL is not same-origin");
          },
          allorigins: async (url) => {
            const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
            const response = await fetch(proxyUrl);
            if (!response.ok) {
              throw new Error(`allorigins: HTTP ${response.status}`);
            }
            return response.text();
          },
          corsproxy: async (url) => {
            const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
            const response = await fetch(proxyUrl);
            if (!response.ok) {
              throw new Error(`corsproxy: HTTP ${response.status}`);
            }
            return response.text();
          },
          corssh: async (url) => {
            const proxyUrl = `https://cors.sh/${url}`;
            const response = await fetch(proxyUrl);
            if (!response.ok) {
              throw new Error(`cors.sh: HTTP ${response.status}`);
            }
            return response.text();
          }
        },
        _isLocalUrl(url) {
          if (typeof window === "undefined") return false;
          try {
            const targetUrl = new URL(url);
            const currentUrl = new URL(window.location.href);
            return targetUrl.origin === currentUrl.origin;
          } catch (e) {
            return false;
          }
        },
        _convertGitHubUrl(url) {
          try {
            const urlObj = new URL(url);
            if (urlObj.hostname === "github.com" && urlObj.pathname.includes("/blob/")) {
              const parts = urlObj.pathname.split("/");
              if (parts.length >= 5 && parts[3] === "blob") {
                const user = parts[1];
                const repo = parts[2];
                const branch = parts[4];
                const filePath = parts.slice(5).join("/");
                const rawUrl = `https://raw.githubusercontent.com/${user}/${repo}/${branch}/${filePath}`;
                console.log(`[UrlManager] Converted GitHub URL: ${url} \u2192 ${rawUrl}`);
                return rawUrl;
              }
            }
            return url;
          } catch (e) {
            return url;
          }
        },
        async fetchContent(url, options = {}) {
          url = this._convertGitHubUrl(url);
          const { strategy: forcedStrategy, timeout = 1e4 } = options;
          if (!url) {
            return { content: "", source: null, error: "No URL provided" };
          }
          try {
            new URL(url);
          } catch (e) {
            return { content: "", source: null, error: "Invalid URL format" };
          }
          let strategies = [];
          if (forcedStrategy) {
            strategies = [forcedStrategy];
          } else if (this._isLocalUrl(url)) {
            strategies = ["direct"];
          } else {
            strategies = ["direct", "allorigins", "corsproxy", "corssh"];
          }
          const fetchWithTimeout = async (fetchFn) => {
            return Promise.race([
              fetchFn(),
              new Promise(
                (_, reject) => setTimeout(() => reject(new Error("Request timeout")), timeout)
              )
            ]);
          };
          const errors = [];
          for (const strategyName of strategies) {
            const strategyFn = this._fetchStrategies[strategyName];
            if (!strategyFn) {
              errors.push(`Unknown strategy: ${strategyName}`);
              continue;
            }
            try {
              const content = await fetchWithTimeout(() => strategyFn(url, window?.location?.href));
              console.log(`[UrlManager] Fetched via ${strategyName}`);
              return {
                content,
                source: url,
                strategy: strategyName,
                error: null
              };
            } catch (e) {
              console.log(`[UrlManager] ${strategyName} failed: ${e.message}`);
              errors.push(`${strategyName}: ${e.message}`);
            }
          }
          return {
            content: "",
            source: url,
            error: `Failed to fetch: ${errors.join("; ")}`
          };
        },
        parseFileContent(content, fileType) {
          if (!content) return "";
          let processed = content.replace(/\\r\\n/g, "\\n").replace(/\\r/g, "\\n");
          processed = processed.split("\n").map((line) => line.trimEnd()).join("\n").trim();
          return processed;
        },
        // ============================================================
        // URL Route Detection - Which calc to load based on URL pattern
        // These detect URL ROUTES, not layouts or calc types
        // ============================================================
        /**
         * Check if URL is home page route
         */
        isHomeRoute() {
          const path = window.location.pathname;
          return path === "/" || path === "/index.html";
        },
        /**
         * Check if URL is published/shared calc route
         * New format: /s/<id> (shared calc)
         * Legacy format: /<id> (numeric ID, for backward compat)
         * Also matches slugs: /mortgage-calculator (must not be reserved)
         */
        isPublishedRoute() {
          const path = window.location.pathname;
          if (/^\/s\/[a-zA-Z0-9_\-.]+\/?$/.test(path)) return true;
          if (/^\/\d+\/?$/.test(path)) return true;
          const slugMatch = path.match(/^\/([a-z0-9-]+)\/?$/i);
          if (slugMatch) {
            const slug = slugMatch[1].toLowerCase();
            const CalcLoader = window.CalcLoader;
            if (!CalcLoader?.RESERVED_SLUGS) {
              throw new Error("CalcLoader.RESERVED_SLUGS not loaded - check script order");
            }
            return !CalcLoader.RESERVED_SLUGS.includes(slug);
          }
          return false;
        },
        /**
         * Check if URL is embed route
         */
        isEmbedRoute() {
          return window.location.pathname.includes("/embed");
        },
        /**
         * Check if URL is wiki/live route (/!slug)
         */
        isWikiRoute() {
          return /^\/![a-z0-9-]+$/i.test(window.location.pathname);
        },
        /**
         * Check if URL is collab route (/edit/TOKEN)
         */
        isCollabRoute() {
          return /^\/edit\/[a-zA-Z0-9_-]+$/.test(window.location.pathname);
        },
        /**
         * Check if URL is gallery route (/gallery)
         */
        isGalleryRoute() {
          return window.location.pathname === "/gallery" || window.location.pathname === "/gallery/";
        },
        // ============================================================
        // Layout Detection - What UI layout to render
        // Uses ViewContext for layout decisions
        // ============================================================
        /**
         * Get current ViewContext from URL
         * @returns {string} ViewContext constant
         */
        getLayout() {
          const ViewContext = getViewContext();
          if (this.isHomeRoute()) return ViewContext.HOME;
          if (this.isEmbedRoute()) return ViewContext.EMBED;
          if (this.isGalleryRoute()) return ViewContext.GALLERY;
          if (this.isPublishedRoute() || this.isWikiRoute() || this.isCollabRoute()) {
            return ViewContext.SINGLE;
          }
          return ViewContext.HOME;
        },
        // ============================================================
        // Hash Formatting
        // ============================================================
        /**
         * Create a hash for local calc navigation
         * @param {string} calcId - Full calc ID (must be UUID format, not prefixed like 'single_5')
         * @returns {string} Hash like "#my/a7ba9a" or '' if invalid
         */
        localCalcHash(calcId) {
          if (!calcId) return "";
          if (calcId.includes("_")) {
            Debug9.warn("url", "localCalcHash called with prefixed ID (not a local calc):", calcId);
            return "";
          }
          const prefix = this.HASH.LOCAL_CALC.prefix;
          const shortId = calcId.slice(0, this.HASH.LOCAL_CALC.idLength);
          return "#" + prefix + shortId;
        },
        /**
         * Check if hash is a local calc hash (supports both new #my/ and legacy #local: formats)
         * @param {string} hash - Hash to check (with or without #)
         * @returns {boolean}
         */
        isLocalCalcHash(hash) {
          if (!hash) return false;
          const h = hash.startsWith("#") ? hash.slice(1) : hash;
          return this.LOCAL_CALC_PATTERN.test(h) || this.LEGACY_LOCAL_CALC_PATTERN.test(h);
        },
        /**
         * Parse short ID from local calc hash (supports both new #my/ and legacy #local: formats)
         * @param {string} hash - Hash like "#my/a7ba9a" or "#local:a7ba9aaa"
         * @returns {string|null} Short ID like "a7ba9a"
         */
        parseLocalCalcHash(hash) {
          if (!this.isLocalCalcHash(hash)) return null;
          const h = hash.startsWith("#") ? hash.slice(1) : hash;
          if (h.startsWith(this.HASH.LOCAL_CALC.prefix)) {
            return h.slice(this.HASH.LOCAL_CALC.prefix.length);
          }
          if (h.startsWith(this.HASH.LOCAL_CALC.legacyPrefix)) {
            return h.slice(this.HASH.LOCAL_CALC.legacyPrefix.length);
          }
          return null;
        },
        /**
         * Find calc matching a short hash
         * @param {string} shortHash - 8-char hex ID
         * @param {Array} calcs - Array of calc objects
         * @returns {Object|null} Matching calc
         */
        findCalcByShortHash(shortHash, calcs) {
          if (!shortHash || !calcs) return null;
          return calcs.find((c) => c.id?.startsWith(shortHash)) || null;
        },
        /**
         * Check if current hash already matches a calc
         * @param {string} hash - Current hash
         * @param {string} calcId - Calc ID to check
         * @returns {boolean}
         */
        hashMatchesCalc(hash, calcId) {
          const shortHash = this.parseLocalCalcHash(hash);
          return shortHash && calcId?.startsWith(shortHash);
        },
        // ============================================================
        // Navigation Methods
        // ============================================================
        /**
         * Navigate to a local calc (pushState)
         * Updates URL without page reload, adds to browser history
         * @param {string} calcId - Calc ID to navigate to
         */
        navigateToLocalCalc(calcId) {
          if (!this.FEATURES.LOCAL_CALC_ROUTING) {
            Debug9.log("url", "navigateToLocalCalc: skipped (feature disabled)");
            return;
          }
          if (!calcId) return;
          const newHash = this.localCalcHash(calcId);
          if (!newHash) {
            Debug9.warn("url", "navigateToLocalCalc: localCalcHash returned empty for id:", calcId);
            return;
          }
          if (window.location.hash !== newHash) {
            window.history.pushState({}, "", "/" + newHash);
            Debug9.log("url", "navigateToLocalCalc:", newHash);
          }
        },
        /**
         * Sync quicklink hash (replaceState)
         * Updates URL without adding to browser history
         * Used for live-updating the URL as user types
         * @param {string} hash - Hash content (without #)
         */
        syncQuicklinkHash(hash) {
          if (!hash) return;
          const fullHash = hash.startsWith("#") ? hash : "#" + hash;
          window.history.replaceState(null, "", fullHash);
          Debug9.log("url", "syncQuicklinkHash:", fullHash);
        },
        /**
         * Redirect to home page (full page reload)
         */
        redirectHome() {
          Debug9.log("url", "redirectHome");
          window.location.href = "/";
        },
        /**
         * Redirect to embed view (full page reload)
         */
        redirectToEmbed() {
          const newUrl = "/embed" + window.location.search + window.location.hash;
          Debug9.log("url", "redirectToEmbed:", newUrl);
          window.location.href = newUrl;
        },
        /**
         * Remove trailing slash from URL (redirect)
         */
        removeTrailingSlash() {
          if (window.location.pathname.endsWith("/") && window.location.pathname !== "/") {
            const newPath = window.location.pathname.slice(0, -1);
            Debug9.log("url", "removeTrailingSlash:", newPath);
            window.location.href = newPath;
          }
        },
        /**
         * Clear the hash from URL
         */
        clearHash() {
          if (window.location.hash) {
            window.history.replaceState(null, "", window.location.pathname + window.location.search);
            Debug9.log("url", "clearHash");
          }
        },
        // ============================================================
        // Calc Change Logic
        // ============================================================
        /**
         * Determine if URL should update when calc changes
         * URL ALWAYS syncs except in embed view
         * @param {Object} calc - The new calc being selected
         * @returns {boolean}
         */
        shouldUpdateUrlOnCalcChange(calc) {
          if (!calc?.id) return false;
          const CalcType = window.CalcType;
          if (!CalcType) throw new Error("CalcType not loaded");
          if (CalcType.isUrlDefined(calc.type)) {
            return false;
          }
          const ViewContext = getViewContext();
          const layout = this.getLayout();
          return layout !== ViewContext.EMBED;
        },
        /**
         * Handle calc change - update URL if appropriate
         * Call this from CALC_CHANGED event handler
         * @param {Object} calc - The new calc being selected
         */
        handleCalcChange(calc) {
          if (!this.shouldUpdateUrlOnCalcChange(calc)) return;
          const CalcType = window.CalcType;
          if (!CalcType) throw new Error("CalcType not loaded");
          const urlPath = CalcType.getUrlPath(calc);
          if (urlPath) {
            if (window.location.pathname !== urlPath) {
              window.history.pushState({}, "", urlPath);
              Debug9.log("url", "navigateTo:", urlPath);
            }
            return;
          }
          this.navigateToLocalCalc(calc.id);
        },
        // ============================================================
        // Live URL Synchronization (migrated from url-state.js)
        // ============================================================
        _debounceTimer: null,
        _isHashMode: false,
        _originalUrlFormat: null,
        // 'gzip', 'data', or 'readable'
        /**
         * Detects hash, decodes, and returns the state object or null.
         */
        async getInitialState() {
          const hash = window.location.hash.slice(1);
          if (!hash) return null;
          const result = this.parseUrl(window.location.href);
          if (result.contentPromise) {
            const resolved = await result.contentPromise;
            if (resolved) {
              if (hash.startsWith("data=")) {
                this._originalUrlFormat = "data";
              } else if (hash.startsWith("gzip=") || hash.startsWith("v2:gzip:")) {
                this._originalUrlFormat = "gzip";
              }
              return {
                id: null,
                name: resolved.dataUrlName || "URL Calc",
                rows: (resolved.dataUrlRows || resolved.content.split("\n")).map((r) => typeof r === "string" ? { text: r } : r),
                version: resolved.dataUrlVersion || 2,
                settings: resolved.settings
              };
            }
          }
          if (result.isStateless && result.content) {
            this._originalUrlFormat = "readable";
            const rows = result.content.split("\n").map((text) => ({ text: text.trim() }));
            return {
              id: null,
              name: result.title || "URL Calc",
              rows,
              version: 2,
              settings: result.settings
            };
          }
          return null;
        },
        /**
         * Determines if state is "simple" enough for readable URL
         */
        isSimpleState(state) {
          if (state.folder === "Demo Calcs" || state.isDemo) return false;
          const CalcType = window.CalcType;
          if (CalcType && CalcType.isUrlDefined(state.type)) return true;
          const defaultNames = ["URL Calc", "Scratchpad", "Quick Link Snippet"];
          if (state.name && !defaultNames.includes(state.name)) return false;
          return true;
        },
        /**
         * Compresses state and updates the URL hash.
         */
        syncStateToUrl(state) {
          if (!state) return;
          if (!this._isHashMode) return;
          clearTimeout(this._debounceTimer);
          this._debounceTimer = setTimeout(() => {
            this._performSync(state);
          }, 250);
        },
        async _performSync(state) {
          try {
            let hash = "";
            if (this._originalUrlFormat === "gzip" || this._originalUrlFormat === "data") {
              const jsonPayload = {
                v: 2,
                name: state.name || "",
                rows: state.rows.map((r) => ({ text: r.text || "" })),
                settings: state.settings || {}
              };
              const jsonString = JSON.stringify(jsonPayload);
              try {
                const compressed = await Compression.compress(jsonString);
                hash = "data=" + compressed;
                const queryParams = new URLSearchParams();
                if (state.settings?.view) queryParams.set("view", state.settings.view);
                if (state.settings?.theme) queryParams.set("theme", state.settings.theme);
                if (queryParams.toString()) hash += "?" + queryParams.toString();
              } catch (err) {
                console.warn("[UrlManager] Compression failed", err);
                return;
              }
            } else {
              if (this.isSimpleState(state)) {
                const textRows = state.rows.map((r) => r.text?.trim() || "").filter((t) => t.length > 0);
                const title = state.name?.trim();
                const isDefaultTitle = !title || title === "Quick Link" || title === "Quick Link Snippet" || title === "Scratchpad";
                const parts = isDefaultTitle ? textRows : [title, ...textRows];
                const rawText = parts.join("|");
                if (rawText.length < 1e3) {
                  hash = window.Util?.encodeFriendly ? window.Util.encodeFriendly(rawText) : encodeURIComponent(rawText);
                }
              }
              if (!hash) {
                const payload = { v: 1, data: state };
                const jsonString = JSON.stringify(payload);
                try {
                  const compressed = await Compression.compress(jsonString);
                  hash = "v2:gzip:" + compressed;
                } catch (err) {
                  console.warn("[UrlManager] Compression failed", err);
                  return;
                }
              }
            }
            this.syncQuicklinkHash(hash);
          } catch (e) {
            console.error("[UrlManager] Sync failed:", e);
          }
        },
        enableHashMode() {
          this._isHashMode = true;
          const current = window.Action ? window.Action.getCurrentCalc() : null;
          if (current) this.syncStateToUrl(current);
        },
        async getShareLink(state) {
          let hash = "";
          if (this.isSimpleState(state)) {
            const textRows = state.rows.map((r) => r.text?.trim() || "").filter((t) => t.length > 0);
            const rawText = textRows.join("|");
            if (rawText.length < 2e3) {
              hash = window.Util?.encodeFriendly ? window.Util.encodeFriendly(rawText) : encodeURIComponent(rawText);
            }
          }
          if (!hash) {
            const payload = { v: 1, data: state };
            const jsonString = JSON.stringify(payload);
            try {
              const compressed = await Compression.compress(jsonString);
              hash = "v2:gzip:" + compressed;
            } catch (e) {
              return "";
            }
          }
          const url = new URL(window.location.href);
          url.hash = hash;
          return url.toString();
        },
        // ============================================================
        // Event Subscription
        // ============================================================
        /**
         * Initialize URL manager - subscribe to calc selection events
         * This is the SINGLE place where URL sync is handled
         */
        async init() {
          if (this._initialized) return;
          this._initialized = true;
          const urlParsed = this.parseUrl(window.location.href);
          const isRemoteFile = urlParsed?.externalUrl || urlParsed?.localFilePath;
          if (urlParsed?.isStateless && !isRemoteFile) {
            Debug9.log("url", "Enabling hash mode for stateless URL calc");
            this._isHashMode = true;
            const hash = window.location.hash;
            if (hash.startsWith("#data=")) {
              this._originalUrlFormat = "data";
            } else if (hash.startsWith("#v2:gzip:")) {
              this._originalUrlFormat = "gzip";
            } else {
              this._originalUrlFormat = "readable";
            }
          }
          if (!this._isHashMode) {
            const hashState = await this.getInitialState();
            if (hashState) {
              const existingCalc = window.calcStore?.getActiveCalc?.();
              const CalcType = window.CalcType;
              if (CalcType && CalcType.isUrlDefined(existingCalc?.type)) {
                Debug9.log("url", "Skipping - calc already loaded");
                this._isHashMode = true;
              } else {
                Debug9.log("url", "Loading compressed state from hash...");
                this._isHashMode = true;
                if (!hashState.id && window.Action) {
                  hashState.id = window.Util.guid();
                }
                if (window.Action) {
                  window.Action.setCurrentCalc(hashState);
                }
              }
            }
          }
          if (window.InstacalcEvents) {
            window.InstacalcEvents.on(window.InstacalcEvents.CALC_SWITCH, (e) => {
              const { calcId, shouldSyncUrl } = e.detail || {};
              if (shouldSyncUrl && calcId) {
                const calc = window.calcStore?.get(calcId);
                if (calc) {
                  this.handleCalcChange(calc);
                } else {
                  this.navigateToLocalCalc(calcId);
                }
              }
            });
            window.InstacalcEvents.on(window.InstacalcEvents.CALC_CHANGED, (e) => {
              const calc = e.detail?.calc;
              if (!calc) return;
              if (calc.sourceUrl && (calc.sourceUrl.startsWith("http://") || calc.sourceUrl.startsWith("https://"))) {
                return;
              }
              if (this._isHashMode) {
                const CalcType = window.CalcType;
                if (CalcType && CalcType.isUrlDefined(calc.type)) {
                  this.syncStateToUrl(calc);
                }
              }
            });
            Debug9.log("url", "UrlManager initialized");
          }
        }
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = UrlManager;
      }
      if (typeof window !== "undefined") {
        window.UrlManager = UrlManager;
        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", () => UrlManager.init());
        } else {
          UrlManager.init();
        }
      }
    }
  });

  // public/src/calc-loader.js
  var require_calc_loader = __commonJS({
    "public/src/calc-loader.js"(exports, module2) {
      var Debug9 = typeof window !== "undefined" && window.Debug || {
        log: () => {
        },
        warn: () => {
        },
        error: (...args) => console.error(...args)
      };
      var CalcLoader = {
        // ============================================================ 
        // Constants
        // ============================================================ 
        SOURCE: {
          LOCAL: "local",
          // User's local calcs (localStorage)
          FIREBASE: "firebase",
          // Remote calcs (single, live, wiki, collab)
          URL: "url",
          // Stateless content from URL hash/path
          API: "api",
          // Published calcs from gallery API
          FILE: "file",
          // External URL or local file
          USER_PROFILE: "user_profile",
          // /@username - User's profile page
          USER_CALC: "user_calc"
          // /@username/slug - User's public/private calc
        },
        // Route types for Firebase fetching
        // Note: LIVE, WIKI, COLLAB match CalcType values; PUBLISHED is unique to routes
        ROUTE_TYPE: {
          PUBLISHED: "published",
          // /s/<id> (new) or /<id> (legacy) - becomes SHARED_VIEW CalcType
          LIVE: "live",
          // /!slug - matches CalcType.LIVE
          WIKI: "wiki",
          // /wiki/slug - matches CalcType.WIKI
          COLLAB: "collab"
          // /edit/TOKEN - matches CalcType.COLLAB
        },
        // Note: Use ViewContext for layout constants (HOME, SINGLE, EMBED, GALLERY)
        // Reserved slugs that cannot be published calcs
        RESERVED_SLUGS: [
          "new",
          "new2",
          "embed",
          "shared",
          "dashboard",
          "chrome",
          "gallery",
          "api",
          "login",
          "logout",
          "signup",
          "about",
          "docs",
          "blog",
          "help",
          "settings",
          "account",
          "admin",
          "demo",
          "test",
          "static",
          "assets",
          "s"
          // Reserved for /s/<id> shared calc URLs (URL design spec)
        ],
        // Gallery categories
        GALLERY_CATEGORIES: [
          "finance",
          "health",
          "cooking",
          "math",
          "science",
          "conversion",
          "gaming"
        ],
        // Reserved hash prefixes - NEVER parse these as URL calc content
        // These are routing/system prefixes, not user content
        RESERVED_HASH_PREFIXES: [
          "my/",
          // Local calc reference (#my/xxxxxx) - new format (URL design spec)
          "local:",
          // Local calc reference (#local:xxxxxxxx) - legacy format
          "v2:"
          // Version 2 compressed format
        ],
        // ============================================================ 
        // Main Entry Point
        // ============================================================ 
        /**
         * Load a calc from URL. This is THE entry point for all calc loading.
         * @param {string} [url] - URL to parse (defaults to window.location.href)
         * @returns {Promise<string>} - The loaded calc ID
         */
        async loadCalc(url = window.location.href) {
          Debug9.log("loader", "\u{1F680} loadCalc START:", url);
          if (this._checkLegacyRedirect(url)) {
            return null;
          }
          if (window.Action?.loadCurrencies) {
            window.Action.loadCurrencies();
          }
          if (window.authCheckComplete) {
            Debug9.log("loader", "\u23F3 Waiting for auth check...");
            try {
              await Promise.race([
                window.authCheckComplete,
                new Promise((resolve) => setTimeout(resolve, 500))
                // 500ms timeout
              ]);
              Debug9.log("loader", "\u2705 Auth check complete");
            } catch (e) {
              Debug9.warn("loader", "Auth check failed, proceeding anyway:", e);
            }
          }
          try {
            const spec = await this.parseCalcSpec(url);
            Debug9.log("loader", "\u{1F4CB} parseCalcSpec result:", { source: spec.source, id: spec.id, slug: spec.slug, routeType: spec.routeType });
            Debug9.log("loader", "\u{1F50D} ensureCalc START for source:", spec.source);
            const calcId = await this.ensureCalc(spec);
            Debug9.log("loader", "\u2705 ensureCalc result:", calcId);
            if (!calcId && spec.source !== this.SOURCE.LOCAL && spec.source !== this.SOURCE.USER_PROFILE && spec.source !== this.SOURCE.USER_CALC && spec.source !== this.SOURCE.FILE) {
              this.showUrlParseError(url, spec);
              return null;
            }
            Debug9.log("loader", "\u{1F3A8} renderApp:", calcId, "layout:", spec.layout);
            this.renderApp(calcId, spec);
            Debug9.log("loader", "\u{1F3C1} loadCalc COMPLETE:", calcId);
            return calcId;
          } catch (error) {
            Debug9.error("loader", "loadCalc FAILED:", error);
            this.showLoadError(error, url);
            return null;
          }
        },
        /**
         * Show user-visible error for URL parsing failures
         */
        showUrlParseError(url, spec) {
          const calcwrap = document.querySelector("calcwrap");
          if (!calcwrap) return;
          const sourceLabel = spec.source || "unknown";
          const identifier = spec.id || spec.slug || spec.token || "N/A";
          calcwrap.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-theme-text-muted p-8">
        <div class="text-4xl mb-4">\u26A0\uFE0F</div>
        <h2 class="text-xl font-semibold text-theme-text mb-2">Could Not Load Calculator</h2>
        <p class="text-sm mb-2">Failed to load from <code class="bg-theme-bg-alt px-2 py-1 rounded">${sourceLabel}</code></p>
        <p class="text-xs text-theme-text-muted mb-4">Identifier: ${identifier}</p>
        <a href="/" class="px-4 py-2 bg-accent text-bg rounded-lg text-sm font-medium hover:brightness-110">Go Home</a>
      </div>
    `;
        },
        /**
         * Show user-visible error for general load failures
         */
        showLoadError(error, url) {
          const calcwrap = document.querySelector("calcwrap");
          if (!calcwrap) return;
          calcwrap.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-theme-text-muted p-8">
        <div class="text-4xl mb-4">\u274C</div>
        <h2 class="text-xl font-semibold text-theme-text mb-2">Something Went Wrong</h2>
        <p class="text-sm mb-2">${error.message || "Unknown error"}</p>
        <p class="text-xs text-theme-text-muted mb-4 max-w-md truncate">${url}</p>
        <a href="/" class="px-4 py-2 bg-accent text-bg rounded-lg text-sm font-medium hover:brightness-110">Go Home</a>
      </div>
    `;
        },
        /**
         * Check for legacy URL patterns and redirect to new format.
         * See specs/arch/url-design.md for URL design spec.
         *
         * Redirects:
         * - #local:<id> → #my/<id>
         * - /<numeric-id> → /s/<id>
         * - /<numeric-id>/embed → /s/<id>/embed
         *
         * @param {string} urlString - URL to check
         * @returns {boolean} - True if redirect was triggered
         */
        _checkLegacyRedirect(urlString) {
          return false;
        },
        // ============================================================
        // URL Parsing
        // ============================================================ 
        /**
         * Parse URL into a CalcSpec object.
         * Single source of truth for URL interpretation.
         *
         * @param {string} urlString - Full URL to parse
         * @returns {CalcSpec} - Parsed specification
         */
        async parseCalcSpec(urlString) {
          let url;
          try {
            url = new URL(urlString);
          } catch (e) {
            url = new URL("https://instacalc.com/");
          }
          const path = url.pathname || "/";
          const hash = url.hash.slice(1);
          const params = new URLSearchParams(url.search);
          const spec = {
            source: null,
            id: null,
            slug: null,
            token: null,
            content: null,
            externalUrl: null,
            localFilePath: null,
            routeType: null,
            layout: {
              sidebar: "open",
              embed: false,
              minimal: false
            },
            settings: {
              theme: params.get("theme") || null,
              radius: params.get("radius") || null,
              view: params.get("view") || null,
              decimals: params.get("decimals") ? parseInt(params.get("decimals")) : null
            },
            overrides: null,
            // Variable overrides from URL params (e.g., ?price=200)
            galleryCategory: null
          };
          if (path.includes("/embed")) {
            spec.layout.embed = true;
            spec.layout.sidebar = "collapsed";
            const newEmbedMatch = path.match(/^\/s\/([a-zA-Z0-9_\-.]+)\/embed\/?$/);
            if (newEmbedMatch) {
              spec.source = this.SOURCE.FIREBASE;
              spec.routeType = this.ROUTE_TYPE.PUBLISHED;
              spec.id = newEmbedMatch[1];
              return spec;
            }
            const embedIdMatch = path.match(/^\/(\d+)\/embed\/?$/);
            if (embedIdMatch) {
              spec.source = this.SOURCE.FIREBASE;
              spec.routeType = this.ROUTE_TYPE.PUBLISHED;
              spec.id = embedIdMatch[1];
              return spec;
            }
            if (hash) {
              const urlCalcData = await this._parseUrlCalcContent(hash, url);
              if (urlCalcData.content) {
                spec.source = this.SOURCE.URL;
                spec.content = urlCalcData.content;
                spec.settings = { ...spec.settings, ...urlCalcData.settings };
                return spec;
              }
            }
            spec.source = this.SOURCE.URL;
            spec.content = "";
            return spec;
          }
          const externalUrl = this._extractExternalUrl(path);
          if (externalUrl) {
            spec.source = this.SOURCE.FILE;
            spec.externalUrl = externalUrl;
            spec.overrides = this._extractOverrides(params, hash);
            spec.layout.sidebar = "collapsed";
            return spec;
          }
          const ghSpec = this._parseGithubShorthand(path);
          if (ghSpec) {
            spec.source = this.SOURCE.FILE;
            spec.externalUrl = ghSpec.url;
            spec.gitRef = ghSpec.ref;
            spec.overrides = this._extractOverrides(params, hash);
            spec.layout.sidebar = "collapsed";
            return spec;
          }
          const localFilePath = this._extractLocalFilePath(path);
          if (localFilePath) {
            spec.source = this.SOURCE.FILE;
            spec.localFilePath = localFilePath;
            spec.overrides = this._extractOverrides(params, hash);
            spec.layout.sidebar = "collapsed";
            return spec;
          }
          if (path === "/new" || path === "/new/") {
            spec.source = this.SOURCE.URL;
            spec.content = "";
            spec.layout.sidebar = "collapsed";
            return spec;
          }
          if (window.UrlManager) {
            let parseResult = window.UrlManager.parseUrl(urlString);
            if (parseResult.content) {
              spec.source = this.SOURCE.URL;
              spec.content = parseResult.content;
              spec.settings = { ...spec.settings, ...parseResult.settings };
              if (parseResult.localCalcId) {
                spec.source = this.SOURCE.LOCAL;
                spec.id = parseResult.localCalcId;
                spec.layout.sidebar = "open";
              } else {
                spec.id = "url-calc";
                spec.layout.sidebar = "collapsed";
              }
              return spec;
            }
            if (parseResult.contentPromise) {
              try {
                const resolved = await parseResult.contentPromise;
                if (resolved) {
                  spec.source = this.SOURCE.URL;
                  spec.content = resolved.content;
                  spec.settings = { ...spec.settings, ...resolved.settings };
                  spec.id = "url-calc";
                  spec.layout.sidebar = "collapsed";
                  return spec;
                }
              } catch (e) {
                console.warn("CalcLoader: contentPromise failed", e);
              }
            }
          }
          const UrlManager = window.UrlManager;
          if (!UrlManager) throw new Error("UrlManager not loaded");
          if (hash && UrlManager.isLocalCalcHash(hash)) {
            spec.source = this.SOURCE.LOCAL;
            spec.id = UrlManager.parseLocalCalcHash(hash);
            spec.layout.sidebar = "open";
            return spec;
          }
          if (path === "/" || path === "/index.html" || path.includes("index")) {
            if (hash && !hash.startsWith("local:")) {
              const urlCalcData = await this._parseUrlCalcContent(hash, url);
              if (urlCalcData.content) {
                spec.source = this.SOURCE.URL;
                spec.content = urlCalcData.content;
                spec.settings = { ...spec.settings, ...urlCalcData.settings };
                spec.layout.sidebar = "collapsed";
                return spec;
              }
            }
            spec.source = this.SOURCE.LOCAL;
            spec.layout.sidebar = "open";
            return spec;
          }
          if (path === "/gallery" || path.startsWith("/gallery/")) {
            spec.source = this.SOURCE.LOCAL;
            spec.layout.sidebar = "open";
            spec.layout.gallery = true;
            const categoryMatch = path.match(/^\/gallery\/([a-z0-9-]+)$/i);
            if (categoryMatch && this.GALLERY_CATEGORIES.includes(categoryMatch[1].toLowerCase())) {
              spec.galleryCategory = categoryMatch[1].toLowerCase();
            }
            return spec;
          }
          const liveMatch = path.match(/^\/!([a-z0-9-]+)$/i);
          if (liveMatch) {
            spec.source = this.SOURCE.FIREBASE;
            spec.routeType = this.ROUTE_TYPE.LIVE;
            spec.slug = liveMatch[1].toLowerCase();
            spec.layout.sidebar = "collapsed";
            Debug9.log("loader", "\u{1F517} Matched LIVE calc:", spec.slug);
            return spec;
          }
          const profileMatch = path.match(/^\/@([a-z0-9_-]+)$/i);
          if (profileMatch) {
            spec.source = this.SOURCE.USER_PROFILE;
            spec.username = profileMatch[1].toLowerCase();
            spec.layout.sidebar = "collapsed";
            return spec;
          }
          const privateCalcMatch = path.match(/^\/@([a-z0-9_-]+)\/!([a-z0-9_-]+)$/i);
          if (privateCalcMatch) {
            spec.source = this.SOURCE.USER_CALC;
            spec.username = privateCalcMatch[1].toLowerCase();
            spec.slug = privateCalcMatch[2].toLowerCase();
            spec.isPrivate = true;
            spec.layout.sidebar = "collapsed";
            return spec;
          }
          const userCalcMatch = path.match(/^\/@([a-z0-9_-]+)\/([a-z0-9_-]+)$/i);
          if (userCalcMatch) {
            spec.source = this.SOURCE.USER_CALC;
            spec.username = userCalcMatch[1].toLowerCase();
            spec.slug = userCalcMatch[2].toLowerCase();
            spec.isPrivate = false;
            spec.layout.sidebar = "collapsed";
            return spec;
          }
          const collabMatch = path.match(/^\/edit\/([a-zA-Z0-9_-]+)$/);
          if (collabMatch) {
            spec.source = this.SOURCE.FIREBASE;
            spec.routeType = this.ROUTE_TYPE.COLLAB;
            spec.token = collabMatch[1];
            spec.layout.sidebar = "collapsed";
            Debug9.log("loader", "\u{1F91D} Matched COLLAB calc:", spec.token);
            return spec;
          }
          const sharedMatch = path.match(/^\/s\/([a-zA-Z0-9_\-.]+)\/?$/);
          if (sharedMatch) {
            spec.source = this.SOURCE.FIREBASE;
            spec.routeType = this.ROUTE_TYPE.PUBLISHED;
            spec.id = sharedMatch[1];
            spec.overrides = this._extractOverrides(params, hash);
            spec.layout.sidebar = "collapsed";
            spec.layout.minimal = true;
            Debug9.log("loader", "\u{1F517} Matched SHARED calc (new format):", spec.id);
            return spec;
          }
          const singleMatch = path.match(/^\/(\d+)\/?$/);
          if (singleMatch) {
            spec.source = this.SOURCE.FIREBASE;
            spec.routeType = this.ROUTE_TYPE.PUBLISHED;
            spec.id = singleMatch[1];
            spec.overrides = this._extractOverrides(params, hash);
            spec.layout.sidebar = "collapsed";
            spec.layout.minimal = true;
            Debug9.log("loader", "\u{1F517} Matched SHARED calc (legacy format):", spec.id);
            return spec;
          }
          const slugMatch = path.match(/^\/([a-z0-9-]+)$/i);
          if (slugMatch) {
            const slug = slugMatch[1].toLowerCase();
            if (!this.RESERVED_SLUGS.includes(slug)) {
              spec.source = this.SOURCE.API;
              spec.slug = slug;
              spec.layout.sidebar = "collapsed";
              spec.layout.minimal = true;
              return spec;
            }
          }
          Debug9.warn("loader", "Unknown path:", path);
          spec.source = this.SOURCE.LOCAL;
          spec.layout.sidebar = "open";
          return spec;
        },
        /**
         * Parse hash content for URL calc
         */
        async _parseUrlCalcContent(hash, url) {
          for (const prefix of this.RESERVED_HASH_PREFIXES) {
            if (hash.startsWith(prefix)) {
              return { content: null, settings: {} };
            }
          }
          if (hash.startsWith("data=") || hash.startsWith("gzip=")) {
            try {
              if (window.UrlManager?.parseUrl) {
                const parseResult = window.UrlManager.parseUrl(url.href);
                if (parseResult.contentPromise) {
                  const resolved = await parseResult.contentPromise;
                  return {
                    content: resolved.content,
                    settings: resolved.settings || {}
                  };
                }
                if (parseResult.content) {
                  return {
                    content: parseResult.content,
                    settings: parseResult.settings || {}
                  };
                }
              }
            } catch (e) {
              Debug9.warn("loader", "Failed to decompress Gzip URL:", e);
            }
            return { content: null, settings: {} };
          }
          if (hash.includes("=") || hash.includes("|") || hash.includes("+")) {
            if (window.UrlManager) {
              const result = window.UrlManager.parseUrl(url.href);
              if (result.content) {
                return {
                  content: result.content,
                  settings: result.settings || {}
                };
              }
            }
            const decoded = window.Util?.decodeFriendly ? window.Util.decodeFriendly(hash) : decodeURIComponent(hash);
            const lines = decoded.split(/[|;]/);
            return {
              content: lines.join("\n"),
              settings: {}
            };
          }
          return { content: null, settings: {} };
        },
        /**
         * Extract external URL from path (/https://...)
         */
        _extractExternalUrl(path) {
          if (path.startsWith("/http://") || path.startsWith("/https://")) {
            return path.slice(1);
          }
          return null;
        },
        /**
         * Extract local file path (/demo/files/...)
         */
        _extractLocalFilePath(path) {
          if (path.match(/^\/demo\/.*\.(ic|txt|md)$/)) {
            return path;
          }
          return null;
        },
        /**
         * Parse GitHub shorthand URL (/gh/user/repo[@ref]/path/to/file)
         * Also handles GitHub web UI format with blob/ref in path
         * Examples:
         *   /gh/kazad/instacalc/demo/sample.ic -> main branch
         *   /gh/kazad/instacalc@v1.0.0/demo/sample.ic -> tagged version
         *   /gh/kazad/instacalc@abc123/demo/sample.ic -> specific commit
         *   /gh/kazad/instacalc/blob/main/demo/sample.ic -> strips blob/main
         */
        _parseGithubShorthand(path) {
          const match = path.match(/^\/gh\/([^/]+)\/([^@/]+)(?:@([^/]+))?\/?(.+)$/);
          if (!match) return null;
          let [, user, repo, ref, filePath] = match;
          const blobMatch = filePath.match(/^blob\/([^/]+)\/(.+)$/);
          if (blobMatch) {
            ref = blobMatch[1];
            filePath = blobMatch[2];
          }
          ref = ref || "main";
          const url = `https://raw.githubusercontent.com/${user}/${repo}/${ref}/${filePath}`;
          return { url, user, repo, ref, filePath };
        },
        /**
         * Extract variable overrides from URL params and hash
         * @param {URLSearchParams} params - Query params
         * @param {string} hash - URL hash (without #)
         * @returns {Object|null} - Key-value overrides or null if none
         */
        _extractOverrides(params, hash) {
          const overrides = {};
          const skipKeys = [
            // Settings
            "theme",
            "view",
            "radius",
            "decimals",
            "input",
            "run",
            "edit",
            "debug",
            "pwa",
            // Version/cache-busting
            "v",
            "ver",
            "version",
            "cache",
            "nocache",
            "_",
            "t",
            "ts",
            "timestamp",
            // Legacy content params
            "d",
            "q",
            "c",
            "s",
            // Analytics/tracking
            "utm_source",
            "utm_medium",
            "utm_campaign",
            "utm_term",
            "utm_content",
            "utm_id",
            "gclid",
            "gclsrc",
            "dclid",
            "gbraid",
            "wbraid",
            "fbclid",
            "msclkid",
            "mc_cid",
            "mc_eid",
            "ref",
            "source",
            "referrer",
            "affiliate",
            "partner"
          ];
          params.forEach((value, key) => {
            if (!skipKeys.includes(key.toLowerCase())) {
              overrides[key] = value;
            }
          });
          if (hash && !hash.startsWith("local:") && !hash.startsWith("data=") && !hash.startsWith("v2:")) {
            const hashParams = new URLSearchParams(hash.replace(/\|/g, "&"));
            hashParams.forEach((value, key) => {
              if (!skipKeys.includes(key.toLowerCase())) {
                const decodedValue = window.Util?.decodeFriendly ? window.Util.decodeFriendly(value) : value.replace(/_/g, " ");
                overrides[key] = decodedValue;
              }
            });
          }
          return Object.keys(overrides).length > 0 ? overrides : null;
        },
        // ============================================================
        // Calc Ensuring (Fetch/Create)
        // ============================================================ 
        /**
         * Ensure a calc exists and is ready to render.
         * @param {CalcSpec} spec - Parsed specification
         * @returns {Promise<string>} - The calc ID
         */
        async ensureCalc(spec) {
          Debug9.log("loader", "\u{1F527} ensureCalc switch:", spec.source, spec.routeType || "");
          switch (spec.source) {
            case this.SOURCE.LOCAL:
              return this._ensureLocalCalc(spec);
            case this.SOURCE.FIREBASE:
              return this._ensureFirebaseCalc(spec);
            case this.SOURCE.URL:
              return this._ensureUrlCalc(spec);
            case this.SOURCE.API:
              return this._ensureApiCalc(spec);
            case this.SOURCE.FILE:
              return this._ensureFileCalc(spec);
            case this.SOURCE.USER_PROFILE:
              return this._ensureUserProfile(spec);
            case this.SOURCE.USER_CALC:
              return this._ensureUserCalc(spec);
            default:
              Debug9.warn("loader", "Unknown source:", spec.source);
              return this._ensureLocalCalc(spec);
          }
        },
        /**
         * Ensure local calc (from localStorage)
         */
        _ensureLocalCalc(spec) {
          Debug9.log("loader", "\u{1F4C1} _ensureLocalCalc:", { specId: spec.id });
          if (spec.id && window.calcStore) {
            const calcs = window.calcStore.allUser();
            const matched = calcs.find((c) => c.id?.startsWith(spec.id));
            if (matched) {
              Debug9.log("loader", "\u{1F4C1} Found local calc by hash:", matched.id);
              return matched.id;
            }
          }
          const selected = window.Action?.getLocalOption?.("selected");
          Debug9.log("loader", "\u{1F4C1} Using selected calc:", selected || "(none)");
          return selected || null;
        },
        /**
         * Ensure Firebase calc (single, live, wiki, collab)
         */
        async _ensureFirebaseCalc(spec) {
          if (!window.calcStore) {
            Debug9.error("loader", "calcStore not available");
            return null;
          }
          const identifier = spec.id || spec.slug || spec.token;
          const type = this._mapRouteType(spec.routeType);
          Debug9.log("loader", "\u2601\uFE0F _ensureFirebaseCalc:", { type, identifier, routeType: spec.routeType });
          try {
            const calcId = await window.calcStore.ensure(type, identifier);
            Debug9.log("loader", "\u2601\uFE0F Firebase ensure result:", calcId);
            if (spec.overrides && Object.keys(spec.overrides).length > 0) {
              Debug9.log("loader", "\u{1F527} Applying URL overrides:", spec.overrides);
              this._applyOverridesToCalc(calcId, spec.overrides);
            }
            if (spec.routeType !== this.ROUTE_TYPE.PUBLISHED) {
              this._deferSubscription(type, identifier, calcId);
            }
            return calcId;
          } catch (err) {
            console.error("[CalcLoader] Failed to fetch Firebase calc:", err);
            return null;
          }
        },
        /**
         * Apply URL parameter overrides to a loaded calc
         * Modifies the calc in place in the store
         */
        _applyOverridesToCalc(calcId, overrides) {
          if (!calcId || !overrides || Object.keys(overrides).length === 0) return;
          const calc = window.calcStore?.get(calcId);
          if (!calc || !calc.rows) return;
          const content = calc.rows.map((r) => r.text || "").join("\n");
          const newContent = this._applyOverridesToContent(content, overrides);
          const newRows = newContent.split("\n").map((text, i) => {
            const existingRow = calc.rows[i] || {};
            return { ...existingRow, text };
          });
          const updatedCalc = { ...calc, rows: newRows };
          if (window.calcStore?.set) {
            window.calcStore.set(calcId, updatedCalc);
          }
          Debug9.log("loader", "\u2705 Applied overrides to calc:", calcId);
        },
        /**
         * Defer Firebase subscription until SDK is initialized
         * Firebase gets initialized in renderApp() via Action.loadFirebase()
         */
        _deferSubscription(type, identifier, calcId) {
          const trySubscribe = () => {
            try {
              if (typeof firebase !== "undefined" && firebase.apps?.length > 0) {
                window.calcStore.subscribeToRemote(type, identifier, calcId);
                return true;
              }
            } catch (e) {
            }
            return false;
          };
          if (trySubscribe()) return;
          let attempts = 0;
          const maxAttempts = 20;
          const interval = setInterval(() => {
            attempts++;
            if (trySubscribe() || attempts >= maxAttempts) {
              clearInterval(interval);
              if (attempts >= maxAttempts) {
                Debug9.warn("loader", "Firebase subscription timeout for:", calcId);
              }
            }
          }, 250);
        },
        /**
         * Map CalcLoader firebase type to CalcStore type
         */
        _mapRouteType(routeType) {
          const typeMap = {
            [this.ROUTE_TYPE.PUBLISHED]: "single",
            // Published calcs use 'single' fetch type
            [this.ROUTE_TYPE.LIVE]: "live",
            [this.ROUTE_TYPE.WIKI]: "wiki",
            [this.ROUTE_TYPE.COLLAB]: "collab"
          };
          return typeMap[routeType] || "single";
        },
        /**
         * Ensure URL calc (stateless content from URL)
         */
        _ensureUrlCalc(spec) {
          const content = spec.content || "";
          const rows = content.split("\n").map((text) => ({ text }));
          const MIN_ROWS = 7;
          while (rows.length < MIN_ROWS) {
            rows.push({ text: "" });
          }
          const id = content ? "url-" + this._hash(content) : "new";
          const CalcType = window.CalcType;
          const newCalc = window.Calc.createCalcObject({
            name: "Quick Link",
            rows
          });
          newCalc.id = id;
          newCalc.type = CalcType.URL_DEFINED;
          newCalc.syncRef = null;
          if (window.calcStore) {
            window.calcStore.loadUrlCalc(newCalc);
          } else if (window.Calc) {
            window.Calc.set(id, newCalc);
          }
          return id;
        },
        /**
         * Ensure API calc (published gallery calc)
         */
        async _ensureApiCalc(spec) {
          const fetcher = window._fetchFullCalcFromWiki || window.fetchFullCalcFromWiki;
          if (typeof fetcher !== "function") {
            console.error("[CalcLoader] Gallery API not available");
            return null;
          }
          try {
            const calc = await fetcher(spec.slug);
            if (!calc) {
              throw new Error("Calculator not found");
            }
            if (window.calcStore) {
              window.calcStore.loadFromRemote(calc);
            }
            return calc.id;
          } catch (err) {
            console.error("[CalcLoader] Failed to fetch published calc:", err);
            return null;
          }
        },
        /**
         * Ensure file calc (external URL or local file)
         */
        async _ensureFileCalc(spec) {
          const fetchUrl = spec.externalUrl || (spec.localFilePath ? window.location.origin + spec.localFilePath : null);
          if (!fetchUrl) {
            return this._ensureLocalCalc(spec);
          }
          try {
            const result = await window.UrlManager.fetchContent(fetchUrl);
            if (result.error) {
              console.error("[CalcLoader] File fetch error:", result.error);
              this._showRemoteFileError(result.error, fetchUrl);
              return null;
            }
            const fileType = window.UrlManager.getFileType(fetchUrl);
            const content = window.UrlManager.parseFileContent(result.content, fileType);
            const sourceName = fetchUrl.split("/").pop() || "Loaded File";
            return this._createFileCalcFromContent(content, sourceName, fetchUrl, spec.settings, spec.overrides);
          } catch (e) {
            console.error("[CalcLoader] File fetch exception:", e);
            this._showRemoteFileError(e.message, fetchUrl);
            return null;
          }
        },
        /**
         * Create calc from file content
         * @param {string} content - File content
         * @param {string} name - Calc name
         * @param {string} sourceUrl - Source URL
         * @param {Object} settings - Display settings
         * @param {Object|null} overrides - Variable overrides (e.g., { price: '200' })
         */
        _createFileCalcFromContent(content, name, sourceUrl, settings, overrides) {
          let finalContent = content;
          if (overrides && Object.keys(overrides).length > 0) {
            finalContent = this._applyOverridesToContent(content, overrides);
          }
          const rows = finalContent.split("\n").map((text) => ({ text }));
          const MIN_ROWS = 7;
          while (rows.length < MIN_ROWS) {
            rows.push({ text: "" });
          }
          const contentForHash = overrides ? content + JSON.stringify(overrides) : content;
          const id = "file-" + this._hash(contentForHash);
          const CalcType = window.CalcType;
          const newCalc = window.Calc.createCalcObject({
            name: name || "Loaded File",
            rows
          });
          newCalc.id = id;
          newCalc.type = CalcType.URL_DEFINED;
          newCalc.syncRef = null;
          newCalc.sourceUrl = sourceUrl;
          if (window.calcStore) {
            window.calcStore.loadUrlCalc(newCalc);
          }
          return id;
        },
        /**
         * Apply variable overrides to content
         * Finds lines like "var = value" and replaces value with override
         */
        _applyOverridesToContent(content, overrides) {
          if (window.UrlManager?._applyOverrides) {
            return window.UrlManager._applyOverrides(content, overrides);
          }
          const lines = content.split("\n");
          const overrideKeys = Object.keys(overrides);
          const lowerOverrides = {};
          overrideKeys.forEach((k) => {
            lowerOverrides[k.toLowerCase()] = overrides[k];
          });
          const newLines = lines.map((line) => {
            const match = line.match(/^(\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*(=|:)\s*(.*)$/);
            if (match) {
              const [, indent, varName, op, rest] = match;
              const lowerVar = varName.toLowerCase();
              if (Object.hasOwn(lowerOverrides, lowerVar)) {
                const newVal = lowerOverrides[lowerVar];
                let comment = "";
                const commentMatch = rest.match(/^(?:[^"'#]*|"[^"]*"|'[^']*')*?(\/\/.*)$/) || rest.match(/^(?:[^"'#]*|"[^"]*"|'[^']*')*(#.*)$/);
                if (commentMatch) {
                  comment = " " + commentMatch[1];
                }
                return `${indent}${varName} ${op} ${newVal}${comment}`;
              }
            }
            return line;
          });
          return newLines.join("\n");
        },
        /**
         * Escape HTML entities to prevent XSS
         */
        _escapeHtml(str) {
          if (!str) return "";
          return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
        },
        /**
         * Show error page for remote file fetch failure
         */
        _showRemoteFileError(errorMsg, sourceUrl) {
          const calcwrap = document.querySelector("calcwrap");
          if (!calcwrap) return;
          const is404 = errorMsg.includes("404");
          const isTimeout = errorMsg.includes("timeout");
          const isCors = errorMsg.includes("CORS") || errorMsg.includes("cors");
          let icon = "\u26A0\uFE0F";
          let title = "Could Not Load File";
          let suggestion = "";
          if (is404) {
            icon = "\u{1F50D}";
            title = "File Not Found";
            suggestion = "Check that the file path is correct and the file exists.";
          } else if (isTimeout) {
            icon = "\u23F1\uFE0F";
            title = "Request Timed Out";
            suggestion = "The server took too long to respond. Try again later.";
          } else if (isCors) {
            icon = "\u{1F512}";
            title = "Access Denied";
            suggestion = "The server does not allow cross-origin requests.";
          }
          let filename = "unknown";
          try {
            filename = sourceUrl.split("/").pop() || "unknown";
          } catch (e) {
          }
          const safeFilename = this._escapeHtml(filename);
          const safeSourceUrl = this._escapeHtml(sourceUrl);
          const safeErrorMsg = this._escapeHtml(errorMsg);
          calcwrap.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-theme-text-muted p-8">
        <div class="text-4xl mb-4">${icon}</div>
        <h2 class="text-xl font-semibold text-theme-text mb-2">${title}</h2>
        <p class="text-sm mb-2">Could not load <code class="bg-theme-bg-alt px-2 py-1 rounded">${safeFilename}</code></p>
        <p class="text-xs text-theme-text-muted mb-2 max-w-md truncate">${safeSourceUrl}</p>
        ${suggestion ? `<p class="text-xs text-theme-text-muted mb-4">${suggestion}</p>` : ""}
        <p class="text-xs text-red-500/70 mb-4">${safeErrorMsg}</p>
        <a href="/" class="px-4 py-2 bg-accent text-bg rounded-lg text-sm font-medium hover:brightness-110">Go Home</a>
      </div>
    `;
        },
        /**
         * Simple hash function (djb2)
         */
        _hash(str) {
          let h = 5381;
          for (let i = 0; i < str.length; i++) {
            h = (h << 5) + h + str.charCodeAt(i);
          }
          return (h >>> 0).toString();
        },
        /**
         * Ensure user profile page (/@username)
         * Returns null - profile pages don't load a calc, they show the user's calc list
         */
        async _ensureUserProfile(spec) {
          this._currentUserProfile = {
            username: spec.username
          };
          return null;
        },
        /**
         * Ensure user calc (/@username/slug or /@username/!slug)
         */
        async _ensureUserCalc(spec) {
          const { username, slug, isPrivate } = spec;
          Debug9.log("loader", `Loading user calc: @${username}/${isPrivate ? "!" : ""}${slug}`);
          if (isPrivate) {
            const currentUser = window.user || window.Action?.getUser?.();
            const currentAlias = currentUser?.alias || currentUser?.displayName?.toLowerCase();
            if (!currentUser || currentAlias !== username) {
              Debug9.warn("loader", "Unauthorized access to private calc");
              this._showPrivateCalcError(username, slug);
              return null;
            }
          }
          this._showUserCalcNotFound(username, slug, isPrivate);
          return null;
        },
        /**
         * Show error for unauthorized private calc access
         */
        _showPrivateCalcError(username, slug) {
          const calcwrap = document.querySelector("calcwrap");
          if (!calcwrap) return;
          calcwrap.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-theme-text-muted p-8">
        <div class="text-4xl mb-4">&#x1F512;</div>
        <h2 class="text-xl font-semibold text-theme-text mb-2">Private Calculator</h2>
        <p class="text-sm mb-2">This calculator is private to <code class="bg-theme-bg-alt px-2 py-1 rounded">@${username}</code></p>
        <p class="text-xs text-theme-text-muted mb-4">Log in as @${username} to view</p>
        <a href="/" class="px-4 py-2 bg-accent text-bg rounded-lg text-sm font-medium hover:brightness-110">Go Home</a>
      </div>
    `;
        },
        /**
         * Show error for user calc not found
         */
        _showUserCalcNotFound(username, slug, isPrivate) {
          const calcwrap = document.querySelector("calcwrap");
          if (!calcwrap) return;
          const calcPath = `@${username}/${isPrivate ? "!" : ""}${slug}`;
          calcwrap.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-theme-text-muted p-8">
        <div class="text-4xl mb-4">&#x1F50D;</div>
        <h2 class="text-xl font-semibold text-theme-text mb-2">Calculator Not Found</h2>
        <p class="text-sm mb-2">Could not find <code class="bg-theme-bg-alt px-2 py-1 rounded">${calcPath}</code></p>
        <div class="flex gap-2 mt-4">
          <a href="/@${username}" class="px-4 py-2 bg-theme-bg-alt text-theme-text rounded-lg text-sm font-medium hover:brightness-110">View @${username}'s Calcs</a>
          <a href="/" class="px-4 py-2 bg-accent text-bg rounded-lg text-sm font-medium hover:brightness-110">Go Home</a>
        </div>
      </div>
    `;
        },
        // ============================================================
        // View Context Mapping
        // ============================================================
        /**
         * Get ViewContext from spec for layout decisions
         * ViewContext is about LAYOUT only - not calc type or URL sync
         * @param {CalcSpec} spec - Parsed specification
         * @returns {string} ViewContext constant
         */
        _getViewContextFromSpec(spec) {
          const ViewContext = window.ViewContext;
          if (!ViewContext) throw new Error("ViewContext not loaded");
          if (spec.layout.embed) return ViewContext.EMBED;
          if (spec.layout.gallery) return ViewContext.GALLERY;
          if (spec.routeType === this.ROUTE_TYPE.COLLAB || spec.routeType === this.ROUTE_TYPE.LIVE || spec.routeType === this.ROUTE_TYPE.PUBLISHED) {
            return ViewContext.SINGLE;
          }
          return ViewContext.HOME;
        },
        // ============================================================ 
        // Rendering
        // ============================================================ 
        /**
         * Render the app with the loaded calc.
         * @param {string} calcId - The calc ID to render
         * @param {CalcSpec} spec - Parsed specification
         */
        renderApp(calcId, spec) {
          if (!spec.layout.embed) {
            window.Router?.setupUserLogin?.();
          }
          if (!spec.layout.embed && window.Action?.loadFirebase) {
            window.Action.loadFirebase();
          }
          const viewContext = spec.layout.embed ? "embed" : spec.layout.minimal ? "single" : "home";
          document.body.setAttribute("data-view-context", viewContext);
          if (spec.settings?.theme) {
            const themeValue = spec.settings.theme;
            if (themeValue.includes("|") && typeof window.ThemeSystem?.importConfig === "function") {
              window.ThemeSystem.importConfig(themeValue);
            } else {
              const themeId = themeValue.startsWith("theme-") ? themeValue : `theme-${themeValue}`;
              document.body.setAttribute("data-theme", themeId);
              document.body.classList.add(themeId);
              if (window.ThemeSystem?.setTheme) {
                window.ThemeSystem.setTheme(themeId);
              } else if (typeof window.applyTheme_v2 === "function") {
                window.applyTheme_v2(themeId);
              }
            }
          }
          if (spec.settings?.radius) {
            const radius = spec.settings.radius;
            document.body.setAttribute("data-radius", radius);
            document.body.classList.add(`radius-${radius}`);
            document.querySelectorAll("calc[data-calc-wrapper], [data-calc-wrapper], #live-calc").forEach((el) => {
              el.setAttribute("data-radius", radius);
              el.classList.add(`radius-${radius}`);
            });
          }
          if (spec.settings?.view && window.Action?.setDisplayMode) {
            const displayModeMap = {
              "calc": 0,
              // DisplayMode.CALC
              "text": 1,
              // DisplayMode.TEXT
              "present": 2
              // DisplayMode.PRESENT
            };
            const mode = displayModeMap[spec.settings.view];
            if (mode !== void 0) {
              window.Action.setDisplayMode(mode);
            }
          }
          if (spec.layout.embed) {
            this._renderEmbed(calcId, spec);
            return;
          }
          if (spec.layout.gallery) {
            this._renderGallery(spec);
            return;
          }
          if (spec.source === this.SOURCE.USER_PROFILE) {
            this._renderUserProfile(spec);
            return;
          }
          if (spec.source === this.SOURCE.USER_CALC && !calcId) {
            return;
          }
          if (spec.source === this.SOURCE.FILE && !calcId) {
            return;
          }
          if (calcId && window.Action) {
            const viewContext2 = this._getViewContextFromSpec(spec);
            if (window.Action.select) {
              window.Action.select(calcId, viewContext2);
            } else if (window.Action.setLocalOption) {
              window.Action.setLocalOption("selected", calcId);
            }
          }
          const layout = spec.layout.minimal ? "single" : "home";
          this._renderHome(layout, calcId);
          if (spec.layout.sidebar === "collapsed") {
            setTimeout(() => {
              window.InstacalcEvents?.dispatch(window.InstacalcEvents.SIDEBAR_COLLAPSE);
            }, 100);
          }
        },
        /**
         * Render home layout
         */
        _renderHome(layout, calcId) {
          if (typeof __ICREACT === "undefined" || !__ICREACT?.ActionProvider) {
            if (typeof __JEST__ === "undefined") {
              Debug9.warn("loader", "React bundle not loaded, skipping render");
            }
            return;
          }
          const uiOverrides = window.InstacalcConfig?.uiOverrides || {};
          const appComponent = React.createElement(
            __ICREACT.ActionProvider,
            { action: window.Action },
            React.createElement(__ICREACT.default.App, {
              Action: window.Action,
              layout: layout || "home",
              initialCalcId: calcId,
              uiOverrides,
              ref: (component) => {
                window.App = component;
              }
            })
          );
          const el = document.querySelector("App");
          if (!el) {
            if (typeof __JEST__ === "undefined") {
              Debug9.warn("loader", "No <App> element found");
            }
            return;
          }
          if (!el.__ic_root) {
            el.__ic_root = ReactDOM.createRoot(el);
          }
          el.__ic_root.render(appComponent);
          window.__IC_MAIN_APP_MOUNTED = true;
        },
        /**
         * Render embed layout
         */
        _renderEmbed(calcId, spec) {
          const el = document.querySelector("instacalc[data-load-url]");
          if (!el) {
            Debug9.warn("loader", "No embed element found");
            return;
          }
          const rawId = spec.id || spec.slug || spec.token;
          el.setAttribute("embed-id", rawId || `blank-${window.Util?.guid?.() || Date.now()}`);
          el.classList.remove("loaded");
          window.Action?.scanForEmbed?.();
        },
        /**
         * Render gallery layout
         */
        _renderGallery(spec) {
          if (window.GalleryModule?.init) {
            window.GalleryModule.init();
          }
          this._renderHome("home", null);
          try {
            const galleryView = window.Router?.VIEW?.GALLERY ?? 6;
            window.Alpine?.store?.("view", galleryView);
          } catch (_e) {
          }
        },
        /**
         * Render user profile page (/@username)
         */
        _renderUserProfile(spec) {
          const username = spec.username;
          const calcwrap = document.querySelector("calcwrap");
          if (!calcwrap) {
            Debug9.warn("loader", "No calcwrap element found for user profile");
            return;
          }
          calcwrap.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full text-theme-text-muted p-8">
        <div class="text-6xl mb-4">&#x1F464;</div>
        <h2 class="text-2xl font-semibold text-theme-text mb-2">@${username}</h2>
        <p class="text-sm mb-6">User profile coming soon!</p>
        <div class="text-xs text-theme-text-muted mb-4 text-center max-w-md">
          <p class="mb-2">This page will show all public calculators shared by @${username}.</p>
          <p>Users will be able to customize their profile and share their calcs with a clean URL like:</p>
          <code class="block mt-2 bg-theme-bg-alt px-3 py-1 rounded">instacalc.com/@${username}/my-calc</code>
        </div>
        <a href="/" class="px-4 py-2 bg-accent text-bg rounded-lg text-sm font-medium hover:brightness-110">Go Home</a>
      </div>
    `;
        },
        /**
         * Show error page for calc not found
         */
        showCalcNotFound(type, identifier) {
          const calcwrap = document.querySelector("calcwrap");
          if (calcwrap) {
            const typeLabels = {
              single: "Shared",
              wiki: "Wiki",
              collab: "Private",
              live: "Live Wiki",
              published: "Published"
            };
            const typeLabel = typeLabels[type] || "Calculator";
            calcwrap.innerHTML = `
        <div class="flex flex-col items-center justify-center h-full text-theme-text-muted p-8">
          <div class="text-4xl mb-4">&#x1F615;</div>
          <h2 class="text-xl font-semibold text-theme-text mb-2">${typeLabel} Calculator Not Found</h2>
          <p class="text-sm">Could not load <code class="bg-theme-bg-secondary px-2 py-1 rounded">${identifier}</code></p>
          <a href="/" class="mt-4 px-4 py-2 bg-theme-accent text-theme-accent-text rounded-lg text-sm font-medium hover:brightness-110">Go Home</a>
        </div>
      `;
          }
        }
      };
      if (typeof window !== "undefined") {
        window.CalcLoader = CalcLoader;
      }
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = CalcLoader;
      }
    }
  });

  // public/src/plugins/labs/FeatureFlagRegistry.js
  var STORAGE_KEY, FeatureFlagRegistry, featureFlags;
  var init_FeatureFlagRegistry = __esm({
    "public/src/plugins/labs/FeatureFlagRegistry.js"() {
      STORAGE_KEY = "instacalc_labs_flags";
      FeatureFlagRegistry = class {
        constructor() {
          this._flags = /* @__PURE__ */ new Map();
          this._overrides = this._loadOverrides();
          this._listeners = /* @__PURE__ */ new Set();
        }
        /**
         * Register a feature flag
         * @param {Object} flag - Flag configuration
         * @param {string} flag.id - Unique flag identifier
         * @param {string} flag.label - Human-readable name
         * @param {string} flag.description - What this flag does
         * @param {boolean} flag.defaultValue - Default state
         * @param {string} [flag.category] - Group (e.g., 'experimental', 'beta')
         * @param {number} [flag.order] - Sort order in UI
         */
        register(flag) {
          if (!flag.id) {
            console.warn("[FeatureFlags] Flag missing id:", flag);
            return;
          }
          this._flags.set(flag.id, {
            id: flag.id,
            label: flag.label || flag.id,
            description: flag.description || "",
            defaultValue: Boolean(flag.defaultValue),
            category: flag.category || "general",
            order: flag.order ?? 50
          });
        }
        /**
         * Check if a feature is enabled
         * User override > IC_CONFIG > registered default > false
         */
        isEnabled(flagId) {
          if (Object.hasOwn(this._overrides, flagId)) {
            return Boolean(this._overrides[flagId]);
          }
          if (typeof window !== "undefined" && window.IC_CONFIG?.features) {
            if (Object.hasOwn(window.IC_CONFIG.features, flagId)) {
              return Boolean(window.IC_CONFIG.features[flagId]);
            }
          }
          const flag = this._flags.get(flagId);
          if (flag) {
            return flag.defaultValue;
          }
          return false;
        }
        /**
         * Set a user override for a flag
         */
        setOverride(flagId, enabled) {
          this._overrides[flagId] = Boolean(enabled);
          this._saveOverrides();
          this._notifyListeners(flagId, enabled);
        }
        /**
         * Clear user override (revert to default)
         */
        clearOverride(flagId) {
          delete this._overrides[flagId];
          this._saveOverrides();
          this._notifyListeners(flagId, this.isEnabled(flagId));
        }
        /**
         * Check if a flag has a user override
         */
        hasOverride(flagId) {
          return Object.hasOwn(this._overrides, flagId);
        }
        /**
         * Get all registered flags
         */
        getAll() {
          return Array.from(this._flags.values());
        }
        /**
         * Get flags sorted by order, then by label
         */
        getAllSorted() {
          return this.getAll().sort((a, b) => {
            if (a.order !== b.order) return a.order - b.order;
            return a.label.localeCompare(b.label);
          });
        }
        /**
         * Get flags grouped by category
         */
        getByCategory() {
          const groups = {};
          for (const flag of this.getAllSorted()) {
            if (!groups[flag.category]) {
              groups[flag.category] = [];
            }
            groups[flag.category].push(flag);
          }
          return groups;
        }
        /**
         * Subscribe to flag changes
         */
        subscribe(callback) {
          this._listeners.add(callback);
          return () => this._listeners.delete(callback);
        }
        _notifyListeners(flagId, enabled) {
          for (const listener of this._listeners) {
            try {
              listener(flagId, enabled);
            } catch (e) {
              console.error("[FeatureFlags] Listener error:", e);
            }
          }
        }
        _loadOverrides() {
          if (typeof window === "undefined") return {};
          try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : {};
          } catch {
            return {};
          }
        }
        _saveOverrides() {
          if (typeof window === "undefined") return;
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this._overrides));
          } catch (e) {
            console.error("[FeatureFlags] Failed to save:", e);
          }
        }
      };
      featureFlags = new FeatureFlagRegistry();
      if (typeof window !== "undefined") {
        window.__IC_FEATURE_FLAGS__ = featureFlags;
      }
    }
  });

  // public/src/plugins/labs/panel.js
  var LabsPanel;
  var init_panel = __esm({
    "public/src/plugins/labs/panel.js"() {
      LabsPanel = {
        id: "labs",
        label: "Labs",
        icon: "flask",
        // or 'beaker' - will need icon support
        slot: "right-sidebar",
        showInDock: false,
        order: 45,
        // After theme, before gallery
        templateUrl: "templates/labs-panel.html",
        innerClass: "h-full"
      };
    }
  });

  // node_modules/react/cjs/react.production.min.js
  var require_react_production_min = __commonJS({
    "node_modules/react/cjs/react.production.min.js"(exports) {
      "use strict";
      var l = Symbol.for("react.element");
      var n = Symbol.for("react.portal");
      var p = Symbol.for("react.fragment");
      var q = Symbol.for("react.strict_mode");
      var r = Symbol.for("react.profiler");
      var t = Symbol.for("react.provider");
      var u = Symbol.for("react.context");
      var v = Symbol.for("react.forward_ref");
      var w = Symbol.for("react.suspense");
      var x = Symbol.for("react.memo");
      var y = Symbol.for("react.lazy");
      var z = Symbol.iterator;
      function A(a) {
        if (null === a || "object" !== typeof a) return null;
        a = z && a[z] || a["@@iterator"];
        return "function" === typeof a ? a : null;
      }
      var B = { isMounted: function() {
        return false;
      }, enqueueForceUpdate: function() {
      }, enqueueReplaceState: function() {
      }, enqueueSetState: function() {
      } };
      var C = Object.assign;
      var D = {};
      function E(a, b, e) {
        this.props = a;
        this.context = b;
        this.refs = D;
        this.updater = e || B;
      }
      E.prototype.isReactComponent = {};
      E.prototype.setState = function(a, b) {
        if ("object" !== typeof a && "function" !== typeof a && null != a) throw Error("setState(...): takes an object of state variables to update or a function which returns an object of state variables.");
        this.updater.enqueueSetState(this, a, b, "setState");
      };
      E.prototype.forceUpdate = function(a) {
        this.updater.enqueueForceUpdate(this, a, "forceUpdate");
      };
      function F() {
      }
      F.prototype = E.prototype;
      function G(a, b, e) {
        this.props = a;
        this.context = b;
        this.refs = D;
        this.updater = e || B;
      }
      var H = G.prototype = new F();
      H.constructor = G;
      C(H, E.prototype);
      H.isPureReactComponent = true;
      var I = Array.isArray;
      var J = Object.prototype.hasOwnProperty;
      var K = { current: null };
      var L = { key: true, ref: true, __self: true, __source: true };
      function M(a, b, e) {
        var d, c = {}, k = null, h = null;
        if (null != b) for (d in void 0 !== b.ref && (h = b.ref), void 0 !== b.key && (k = "" + b.key), b) J.call(b, d) && !L.hasOwnProperty(d) && (c[d] = b[d]);
        var g = arguments.length - 2;
        if (1 === g) c.children = e;
        else if (1 < g) {
          for (var f = Array(g), m = 0; m < g; m++) f[m] = arguments[m + 2];
          c.children = f;
        }
        if (a && a.defaultProps) for (d in g = a.defaultProps, g) void 0 === c[d] && (c[d] = g[d]);
        return { $$typeof: l, type: a, key: k, ref: h, props: c, _owner: K.current };
      }
      function N(a, b) {
        return { $$typeof: l, type: a.type, key: b, ref: a.ref, props: a.props, _owner: a._owner };
      }
      function O(a) {
        return "object" === typeof a && null !== a && a.$$typeof === l;
      }
      function escape2(a) {
        var b = { "=": "=0", ":": "=2" };
        return "$" + a.replace(/[=:]/g, function(a2) {
          return b[a2];
        });
      }
      var P = /\/+/g;
      function Q(a, b) {
        return "object" === typeof a && null !== a && null != a.key ? escape2("" + a.key) : b.toString(36);
      }
      function R(a, b, e, d, c) {
        var k = typeof a;
        if ("undefined" === k || "boolean" === k) a = null;
        var h = false;
        if (null === a) h = true;
        else switch (k) {
          case "string":
          case "number":
            h = true;
            break;
          case "object":
            switch (a.$$typeof) {
              case l:
              case n:
                h = true;
            }
        }
        if (h) return h = a, c = c(h), a = "" === d ? "." + Q(h, 0) : d, I(c) ? (e = "", null != a && (e = a.replace(P, "$&/") + "/"), R(c, b, e, "", function(a2) {
          return a2;
        })) : null != c && (O(c) && (c = N(c, e + (!c.key || h && h.key === c.key ? "" : ("" + c.key).replace(P, "$&/") + "/") + a)), b.push(c)), 1;
        h = 0;
        d = "" === d ? "." : d + ":";
        if (I(a)) for (var g = 0; g < a.length; g++) {
          k = a[g];
          var f = d + Q(k, g);
          h += R(k, b, e, f, c);
        }
        else if (f = A(a), "function" === typeof f) for (a = f.call(a), g = 0; !(k = a.next()).done; ) k = k.value, f = d + Q(k, g++), h += R(k, b, e, f, c);
        else if ("object" === k) throw b = String(a), Error("Objects are not valid as a React child (found: " + ("[object Object]" === b ? "object with keys {" + Object.keys(a).join(", ") + "}" : b) + "). If you meant to render a collection of children, use an array instead.");
        return h;
      }
      function S(a, b, e) {
        if (null == a) return a;
        var d = [], c = 0;
        R(a, d, "", "", function(a2) {
          return b.call(e, a2, c++);
        });
        return d;
      }
      function T(a) {
        if (-1 === a._status) {
          var b = a._result;
          b = b();
          b.then(function(b2) {
            if (0 === a._status || -1 === a._status) a._status = 1, a._result = b2;
          }, function(b2) {
            if (0 === a._status || -1 === a._status) a._status = 2, a._result = b2;
          });
          -1 === a._status && (a._status = 0, a._result = b);
        }
        if (1 === a._status) return a._result.default;
        throw a._result;
      }
      var U = { current: null };
      var V = { transition: null };
      var W = { ReactCurrentDispatcher: U, ReactCurrentBatchConfig: V, ReactCurrentOwner: K };
      function X() {
        throw Error("act(...) is not supported in production builds of React.");
      }
      exports.Children = { map: S, forEach: function(a, b, e) {
        S(a, function() {
          b.apply(this, arguments);
        }, e);
      }, count: function(a) {
        var b = 0;
        S(a, function() {
          b++;
        });
        return b;
      }, toArray: function(a) {
        return S(a, function(a2) {
          return a2;
        }) || [];
      }, only: function(a) {
        if (!O(a)) throw Error("React.Children.only expected to receive a single React element child.");
        return a;
      } };
      exports.Component = E;
      exports.Fragment = p;
      exports.Profiler = r;
      exports.PureComponent = G;
      exports.StrictMode = q;
      exports.Suspense = w;
      exports.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED = W;
      exports.act = X;
      exports.cloneElement = function(a, b, e) {
        if (null === a || void 0 === a) throw Error("React.cloneElement(...): The argument must be a React element, but you passed " + a + ".");
        var d = C({}, a.props), c = a.key, k = a.ref, h = a._owner;
        if (null != b) {
          void 0 !== b.ref && (k = b.ref, h = K.current);
          void 0 !== b.key && (c = "" + b.key);
          if (a.type && a.type.defaultProps) var g = a.type.defaultProps;
          for (f in b) J.call(b, f) && !L.hasOwnProperty(f) && (d[f] = void 0 === b[f] && void 0 !== g ? g[f] : b[f]);
        }
        var f = arguments.length - 2;
        if (1 === f) d.children = e;
        else if (1 < f) {
          g = Array(f);
          for (var m = 0; m < f; m++) g[m] = arguments[m + 2];
          d.children = g;
        }
        return { $$typeof: l, type: a.type, key: c, ref: k, props: d, _owner: h };
      };
      exports.createContext = function(a) {
        a = { $$typeof: u, _currentValue: a, _currentValue2: a, _threadCount: 0, Provider: null, Consumer: null, _defaultValue: null, _globalName: null };
        a.Provider = { $$typeof: t, _context: a };
        return a.Consumer = a;
      };
      exports.createElement = M;
      exports.createFactory = function(a) {
        var b = M.bind(null, a);
        b.type = a;
        return b;
      };
      exports.createRef = function() {
        return { current: null };
      };
      exports.forwardRef = function(a) {
        return { $$typeof: v, render: a };
      };
      exports.isValidElement = O;
      exports.lazy = function(a) {
        return { $$typeof: y, _payload: { _status: -1, _result: a }, _init: T };
      };
      exports.memo = function(a, b) {
        return { $$typeof: x, type: a, compare: void 0 === b ? null : b };
      };
      exports.startTransition = function(a) {
        var b = V.transition;
        V.transition = {};
        try {
          a();
        } finally {
          V.transition = b;
        }
      };
      exports.unstable_act = X;
      exports.useCallback = function(a, b) {
        return U.current.useCallback(a, b);
      };
      exports.useContext = function(a) {
        return U.current.useContext(a);
      };
      exports.useDebugValue = function() {
      };
      exports.useDeferredValue = function(a) {
        return U.current.useDeferredValue(a);
      };
      exports.useEffect = function(a, b) {
        return U.current.useEffect(a, b);
      };
      exports.useId = function() {
        return U.current.useId();
      };
      exports.useImperativeHandle = function(a, b, e) {
        return U.current.useImperativeHandle(a, b, e);
      };
      exports.useInsertionEffect = function(a, b) {
        return U.current.useInsertionEffect(a, b);
      };
      exports.useLayoutEffect = function(a, b) {
        return U.current.useLayoutEffect(a, b);
      };
      exports.useMemo = function(a, b) {
        return U.current.useMemo(a, b);
      };
      exports.useReducer = function(a, b, e) {
        return U.current.useReducer(a, b, e);
      };
      exports.useRef = function(a) {
        return U.current.useRef(a);
      };
      exports.useState = function(a) {
        return U.current.useState(a);
      };
      exports.useSyncExternalStore = function(a, b, e) {
        return U.current.useSyncExternalStore(a, b, e);
      };
      exports.useTransition = function() {
        return U.current.useTransition();
      };
      exports.version = "18.3.1";
    }
  });

  // node_modules/react/index.js
  var require_react = __commonJS({
    "node_modules/react/index.js"(exports, module2) {
      "use strict";
      if (true) {
        module2.exports = require_react_production_min();
      } else {
        module2.exports = null;
      }
    }
  });

  // public/src/react/react-global.js
  var import_react, useState, useEffect, useContext, useReducer, useCallback, useMemo, useRef, useImperativeHandle, useLayoutEffect, useDebugValue, useSyncExternalStore, createContext, react_global_default;
  var init_react_global = __esm({
    "public/src/react/react-global.js"() {
      import_react = __toESM(require_react());
      ({
        useState,
        useEffect,
        useContext,
        useReducer,
        useCallback,
        useMemo,
        useRef,
        useImperativeHandle,
        useLayoutEffect,
        useDebugValue,
        useSyncExternalStore,
        createContext
      } = import_react.default);
      react_global_default = import_react.default;
    }
  });

  // node_modules/scheduler/cjs/scheduler.production.min.js
  var require_scheduler_production_min = __commonJS({
    "node_modules/scheduler/cjs/scheduler.production.min.js"(exports) {
      "use strict";
      function f(a, b) {
        var c = a.length;
        a.push(b);
        a: for (; 0 < c; ) {
          var d = c - 1 >>> 1, e = a[d];
          if (0 < g(e, b)) a[d] = b, a[c] = e, c = d;
          else break a;
        }
      }
      function h(a) {
        return 0 === a.length ? null : a[0];
      }
      function k(a) {
        if (0 === a.length) return null;
        var b = a[0], c = a.pop();
        if (c !== b) {
          a[0] = c;
          a: for (var d = 0, e = a.length, w = e >>> 1; d < w; ) {
            var m = 2 * (d + 1) - 1, C = a[m], n = m + 1, x = a[n];
            if (0 > g(C, c)) n < e && 0 > g(x, C) ? (a[d] = x, a[n] = c, d = n) : (a[d] = C, a[m] = c, d = m);
            else if (n < e && 0 > g(x, c)) a[d] = x, a[n] = c, d = n;
            else break a;
          }
        }
        return b;
      }
      function g(a, b) {
        var c = a.sortIndex - b.sortIndex;
        return 0 !== c ? c : a.id - b.id;
      }
      if ("object" === typeof performance && "function" === typeof performance.now) {
        l = performance;
        exports.unstable_now = function() {
          return l.now();
        };
      } else {
        p = Date, q = p.now();
        exports.unstable_now = function() {
          return p.now() - q;
        };
      }
      var l;
      var p;
      var q;
      var r = [];
      var t = [];
      var u = 1;
      var v = null;
      var y = 3;
      var z = false;
      var A = false;
      var B = false;
      var D = "function" === typeof setTimeout ? setTimeout : null;
      var E = "function" === typeof clearTimeout ? clearTimeout : null;
      var F = "undefined" !== typeof setImmediate ? setImmediate : null;
      "undefined" !== typeof navigator && void 0 !== navigator.scheduling && void 0 !== navigator.scheduling.isInputPending && navigator.scheduling.isInputPending.bind(navigator.scheduling);
      function G(a) {
        for (var b = h(t); null !== b; ) {
          if (null === b.callback) k(t);
          else if (b.startTime <= a) k(t), b.sortIndex = b.expirationTime, f(r, b);
          else break;
          b = h(t);
        }
      }
      function H(a) {
        B = false;
        G(a);
        if (!A) if (null !== h(r)) A = true, I(J);
        else {
          var b = h(t);
          null !== b && K(H, b.startTime - a);
        }
      }
      function J(a, b) {
        A = false;
        B && (B = false, E(L), L = -1);
        z = true;
        var c = y;
        try {
          G(b);
          for (v = h(r); null !== v && (!(v.expirationTime > b) || a && !M()); ) {
            var d = v.callback;
            if ("function" === typeof d) {
              v.callback = null;
              y = v.priorityLevel;
              var e = d(v.expirationTime <= b);
              b = exports.unstable_now();
              "function" === typeof e ? v.callback = e : v === h(r) && k(r);
              G(b);
            } else k(r);
            v = h(r);
          }
          if (null !== v) var w = true;
          else {
            var m = h(t);
            null !== m && K(H, m.startTime - b);
            w = false;
          }
          return w;
        } finally {
          v = null, y = c, z = false;
        }
      }
      var N = false;
      var O = null;
      var L = -1;
      var P = 5;
      var Q = -1;
      function M() {
        return exports.unstable_now() - Q < P ? false : true;
      }
      function R() {
        if (null !== O) {
          var a = exports.unstable_now();
          Q = a;
          var b = true;
          try {
            b = O(true, a);
          } finally {
            b ? S() : (N = false, O = null);
          }
        } else N = false;
      }
      var S;
      if ("function" === typeof F) S = function() {
        F(R);
      };
      else if ("undefined" !== typeof MessageChannel) {
        T = new MessageChannel(), U = T.port2;
        T.port1.onmessage = R;
        S = function() {
          U.postMessage(null);
        };
      } else S = function() {
        D(R, 0);
      };
      var T;
      var U;
      function I(a) {
        O = a;
        N || (N = true, S());
      }
      function K(a, b) {
        L = D(function() {
          a(exports.unstable_now());
        }, b);
      }
      exports.unstable_IdlePriority = 5;
      exports.unstable_ImmediatePriority = 1;
      exports.unstable_LowPriority = 4;
      exports.unstable_NormalPriority = 3;
      exports.unstable_Profiling = null;
      exports.unstable_UserBlockingPriority = 2;
      exports.unstable_cancelCallback = function(a) {
        a.callback = null;
      };
      exports.unstable_continueExecution = function() {
        A || z || (A = true, I(J));
      };
      exports.unstable_forceFrameRate = function(a) {
        0 > a || 125 < a ? console.error("forceFrameRate takes a positive int between 0 and 125, forcing frame rates higher than 125 fps is not supported") : P = 0 < a ? Math.floor(1e3 / a) : 5;
      };
      exports.unstable_getCurrentPriorityLevel = function() {
        return y;
      };
      exports.unstable_getFirstCallbackNode = function() {
        return h(r);
      };
      exports.unstable_next = function(a) {
        switch (y) {
          case 1:
          case 2:
          case 3:
            var b = 3;
            break;
          default:
            b = y;
        }
        var c = y;
        y = b;
        try {
          return a();
        } finally {
          y = c;
        }
      };
      exports.unstable_pauseExecution = function() {
      };
      exports.unstable_requestPaint = function() {
      };
      exports.unstable_runWithPriority = function(a, b) {
        switch (a) {
          case 1:
          case 2:
          case 3:
          case 4:
          case 5:
            break;
          default:
            a = 3;
        }
        var c = y;
        y = a;
        try {
          return b();
        } finally {
          y = c;
        }
      };
      exports.unstable_scheduleCallback = function(a, b, c) {
        var d = exports.unstable_now();
        "object" === typeof c && null !== c ? (c = c.delay, c = "number" === typeof c && 0 < c ? d + c : d) : c = d;
        switch (a) {
          case 1:
            var e = -1;
            break;
          case 2:
            e = 250;
            break;
          case 5:
            e = 1073741823;
            break;
          case 4:
            e = 1e4;
            break;
          default:
            e = 5e3;
        }
        e = c + e;
        a = { id: u++, callback: b, priorityLevel: a, startTime: c, expirationTime: e, sortIndex: -1 };
        c > d ? (a.sortIndex = c, f(t, a), null === h(r) && a === h(t) && (B ? (E(L), L = -1) : B = true, K(H, c - d))) : (a.sortIndex = e, f(r, a), A || z || (A = true, I(J)));
        return a;
      };
      exports.unstable_shouldYield = M;
      exports.unstable_wrapCallback = function(a) {
        var b = y;
        return function() {
          var c = y;
          y = b;
          try {
            return a.apply(this, arguments);
          } finally {
            y = c;
          }
        };
      };
    }
  });

  // node_modules/scheduler/index.js
  var require_scheduler = __commonJS({
    "node_modules/scheduler/index.js"(exports, module2) {
      "use strict";
      if (true) {
        module2.exports = require_scheduler_production_min();
      } else {
        module2.exports = null;
      }
    }
  });

  // node_modules/react-dom/cjs/react-dom.production.min.js
  var require_react_dom_production_min = __commonJS({
    "node_modules/react-dom/cjs/react-dom.production.min.js"(exports) {
      "use strict";
      var aa = require_react();
      var ca = require_scheduler();
      function p(a) {
        for (var b = "https://reactjs.org/docs/error-decoder.html?invariant=" + a, c = 1; c < arguments.length; c++) b += "&args[]=" + encodeURIComponent(arguments[c]);
        return "Minified React error #" + a + "; visit " + b + " for the full message or use the non-minified dev environment for full errors and additional helpful warnings.";
      }
      var da = /* @__PURE__ */ new Set();
      var ea = {};
      function fa(a, b) {
        ha(a, b);
        ha(a + "Capture", b);
      }
      function ha(a, b) {
        ea[a] = b;
        for (a = 0; a < b.length; a++) da.add(b[a]);
      }
      var ia = !("undefined" === typeof window || "undefined" === typeof window.document || "undefined" === typeof window.document.createElement);
      var ja = Object.prototype.hasOwnProperty;
      var ka = /^[:A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD][:A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\-.0-9\u00B7\u0300-\u036F\u203F-\u2040]*$/;
      var la = {};
      var ma = {};
      function oa(a) {
        if (ja.call(ma, a)) return true;
        if (ja.call(la, a)) return false;
        if (ka.test(a)) return ma[a] = true;
        la[a] = true;
        return false;
      }
      function pa(a, b, c, d) {
        if (null !== c && 0 === c.type) return false;
        switch (typeof b) {
          case "function":
          case "symbol":
            return true;
          case "boolean":
            if (d) return false;
            if (null !== c) return !c.acceptsBooleans;
            a = a.toLowerCase().slice(0, 5);
            return "data-" !== a && "aria-" !== a;
          default:
            return false;
        }
      }
      function qa(a, b, c, d) {
        if (null === b || "undefined" === typeof b || pa(a, b, c, d)) return true;
        if (d) return false;
        if (null !== c) switch (c.type) {
          case 3:
            return !b;
          case 4:
            return false === b;
          case 5:
            return isNaN(b);
          case 6:
            return isNaN(b) || 1 > b;
        }
        return false;
      }
      function v(a, b, c, d, e, f, g) {
        this.acceptsBooleans = 2 === b || 3 === b || 4 === b;
        this.attributeName = d;
        this.attributeNamespace = e;
        this.mustUseProperty = c;
        this.propertyName = a;
        this.type = b;
        this.sanitizeURL = f;
        this.removeEmptyString = g;
      }
      var z = {};
      "children dangerouslySetInnerHTML defaultValue defaultChecked innerHTML suppressContentEditableWarning suppressHydrationWarning style".split(" ").forEach(function(a) {
        z[a] = new v(a, 0, false, a, null, false, false);
      });
      [["acceptCharset", "accept-charset"], ["className", "class"], ["htmlFor", "for"], ["httpEquiv", "http-equiv"]].forEach(function(a) {
        var b = a[0];
        z[b] = new v(b, 1, false, a[1], null, false, false);
      });
      ["contentEditable", "draggable", "spellCheck", "value"].forEach(function(a) {
        z[a] = new v(a, 2, false, a.toLowerCase(), null, false, false);
      });
      ["autoReverse", "externalResourcesRequired", "focusable", "preserveAlpha"].forEach(function(a) {
        z[a] = new v(a, 2, false, a, null, false, false);
      });
      "allowFullScreen async autoFocus autoPlay controls default defer disabled disablePictureInPicture disableRemotePlayback formNoValidate hidden loop noModule noValidate open playsInline readOnly required reversed scoped seamless itemScope".split(" ").forEach(function(a) {
        z[a] = new v(a, 3, false, a.toLowerCase(), null, false, false);
      });
      ["checked", "multiple", "muted", "selected"].forEach(function(a) {
        z[a] = new v(a, 3, true, a, null, false, false);
      });
      ["capture", "download"].forEach(function(a) {
        z[a] = new v(a, 4, false, a, null, false, false);
      });
      ["cols", "rows", "size", "span"].forEach(function(a) {
        z[a] = new v(a, 6, false, a, null, false, false);
      });
      ["rowSpan", "start"].forEach(function(a) {
        z[a] = new v(a, 5, false, a.toLowerCase(), null, false, false);
      });
      var ra = /[\-:]([a-z])/g;
      function sa(a) {
        return a[1].toUpperCase();
      }
      "accent-height alignment-baseline arabic-form baseline-shift cap-height clip-path clip-rule color-interpolation color-interpolation-filters color-profile color-rendering dominant-baseline enable-background fill-opacity fill-rule flood-color flood-opacity font-family font-size font-size-adjust font-stretch font-style font-variant font-weight glyph-name glyph-orientation-horizontal glyph-orientation-vertical horiz-adv-x horiz-origin-x image-rendering letter-spacing lighting-color marker-end marker-mid marker-start overline-position overline-thickness paint-order panose-1 pointer-events rendering-intent shape-rendering stop-color stop-opacity strikethrough-position strikethrough-thickness stroke-dasharray stroke-dashoffset stroke-linecap stroke-linejoin stroke-miterlimit stroke-opacity stroke-width text-anchor text-decoration text-rendering underline-position underline-thickness unicode-bidi unicode-range units-per-em v-alphabetic v-hanging v-ideographic v-mathematical vector-effect vert-adv-y vert-origin-x vert-origin-y word-spacing writing-mode xmlns:xlink x-height".split(" ").forEach(function(a) {
        var b = a.replace(
          ra,
          sa
        );
        z[b] = new v(b, 1, false, a, null, false, false);
      });
      "xlink:actuate xlink:arcrole xlink:role xlink:show xlink:title xlink:type".split(" ").forEach(function(a) {
        var b = a.replace(ra, sa);
        z[b] = new v(b, 1, false, a, "http://www.w3.org/1999/xlink", false, false);
      });
      ["xml:base", "xml:lang", "xml:space"].forEach(function(a) {
        var b = a.replace(ra, sa);
        z[b] = new v(b, 1, false, a, "http://www.w3.org/XML/1998/namespace", false, false);
      });
      ["tabIndex", "crossOrigin"].forEach(function(a) {
        z[a] = new v(a, 1, false, a.toLowerCase(), null, false, false);
      });
      z.xlinkHref = new v("xlinkHref", 1, false, "xlink:href", "http://www.w3.org/1999/xlink", true, false);
      ["src", "href", "action", "formAction"].forEach(function(a) {
        z[a] = new v(a, 1, false, a.toLowerCase(), null, true, true);
      });
      function ta(a, b, c, d) {
        var e = z.hasOwnProperty(b) ? z[b] : null;
        if (null !== e ? 0 !== e.type : d || !(2 < b.length) || "o" !== b[0] && "O" !== b[0] || "n" !== b[1] && "N" !== b[1]) qa(b, c, e, d) && (c = null), d || null === e ? oa(b) && (null === c ? a.removeAttribute(b) : a.setAttribute(b, "" + c)) : e.mustUseProperty ? a[e.propertyName] = null === c ? 3 === e.type ? false : "" : c : (b = e.attributeName, d = e.attributeNamespace, null === c ? a.removeAttribute(b) : (e = e.type, c = 3 === e || 4 === e && true === c ? "" : "" + c, d ? a.setAttributeNS(d, b, c) : a.setAttribute(b, c)));
      }
      var ua = aa.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
      var va = Symbol.for("react.element");
      var wa = Symbol.for("react.portal");
      var ya = Symbol.for("react.fragment");
      var za = Symbol.for("react.strict_mode");
      var Aa = Symbol.for("react.profiler");
      var Ba = Symbol.for("react.provider");
      var Ca = Symbol.for("react.context");
      var Da = Symbol.for("react.forward_ref");
      var Ea = Symbol.for("react.suspense");
      var Fa = Symbol.for("react.suspense_list");
      var Ga = Symbol.for("react.memo");
      var Ha = Symbol.for("react.lazy");
      Symbol.for("react.scope");
      Symbol.for("react.debug_trace_mode");
      var Ia = Symbol.for("react.offscreen");
      Symbol.for("react.legacy_hidden");
      Symbol.for("react.cache");
      Symbol.for("react.tracing_marker");
      var Ja = Symbol.iterator;
      function Ka(a) {
        if (null === a || "object" !== typeof a) return null;
        a = Ja && a[Ja] || a["@@iterator"];
        return "function" === typeof a ? a : null;
      }
      var A = Object.assign;
      var La;
      function Ma(a) {
        if (void 0 === La) try {
          throw Error();
        } catch (c) {
          var b = c.stack.trim().match(/\n( *(at )?)/);
          La = b && b[1] || "";
        }
        return "\n" + La + a;
      }
      var Na = false;
      function Oa(a, b) {
        if (!a || Na) return "";
        Na = true;
        var c = Error.prepareStackTrace;
        Error.prepareStackTrace = void 0;
        try {
          if (b) if (b = function() {
            throw Error();
          }, Object.defineProperty(b.prototype, "props", { set: function() {
            throw Error();
          } }), "object" === typeof Reflect && Reflect.construct) {
            try {
              Reflect.construct(b, []);
            } catch (l) {
              var d = l;
            }
            Reflect.construct(a, [], b);
          } else {
            try {
              b.call();
            } catch (l) {
              d = l;
            }
            a.call(b.prototype);
          }
          else {
            try {
              throw Error();
            } catch (l) {
              d = l;
            }
            a();
          }
        } catch (l) {
          if (l && d && "string" === typeof l.stack) {
            for (var e = l.stack.split("\n"), f = d.stack.split("\n"), g = e.length - 1, h = f.length - 1; 1 <= g && 0 <= h && e[g] !== f[h]; ) h--;
            for (; 1 <= g && 0 <= h; g--, h--) if (e[g] !== f[h]) {
              if (1 !== g || 1 !== h) {
                do
                  if (g--, h--, 0 > h || e[g] !== f[h]) {
                    var k = "\n" + e[g].replace(" at new ", " at ");
                    a.displayName && k.includes("<anonymous>") && (k = k.replace("<anonymous>", a.displayName));
                    return k;
                  }
                while (1 <= g && 0 <= h);
              }
              break;
            }
          }
        } finally {
          Na = false, Error.prepareStackTrace = c;
        }
        return (a = a ? a.displayName || a.name : "") ? Ma(a) : "";
      }
      function Pa(a) {
        switch (a.tag) {
          case 5:
            return Ma(a.type);
          case 16:
            return Ma("Lazy");
          case 13:
            return Ma("Suspense");
          case 19:
            return Ma("SuspenseList");
          case 0:
          case 2:
          case 15:
            return a = Oa(a.type, false), a;
          case 11:
            return a = Oa(a.type.render, false), a;
          case 1:
            return a = Oa(a.type, true), a;
          default:
            return "";
        }
      }
      function Qa(a) {
        if (null == a) return null;
        if ("function" === typeof a) return a.displayName || a.name || null;
        if ("string" === typeof a) return a;
        switch (a) {
          case ya:
            return "Fragment";
          case wa:
            return "Portal";
          case Aa:
            return "Profiler";
          case za:
            return "StrictMode";
          case Ea:
            return "Suspense";
          case Fa:
            return "SuspenseList";
        }
        if ("object" === typeof a) switch (a.$$typeof) {
          case Ca:
            return (a.displayName || "Context") + ".Consumer";
          case Ba:
            return (a._context.displayName || "Context") + ".Provider";
          case Da:
            var b = a.render;
            a = a.displayName;
            a || (a = b.displayName || b.name || "", a = "" !== a ? "ForwardRef(" + a + ")" : "ForwardRef");
            return a;
          case Ga:
            return b = a.displayName || null, null !== b ? b : Qa(a.type) || "Memo";
          case Ha:
            b = a._payload;
            a = a._init;
            try {
              return Qa(a(b));
            } catch (c) {
            }
        }
        return null;
      }
      function Ra(a) {
        var b = a.type;
        switch (a.tag) {
          case 24:
            return "Cache";
          case 9:
            return (b.displayName || "Context") + ".Consumer";
          case 10:
            return (b._context.displayName || "Context") + ".Provider";
          case 18:
            return "DehydratedFragment";
          case 11:
            return a = b.render, a = a.displayName || a.name || "", b.displayName || ("" !== a ? "ForwardRef(" + a + ")" : "ForwardRef");
          case 7:
            return "Fragment";
          case 5:
            return b;
          case 4:
            return "Portal";
          case 3:
            return "Root";
          case 6:
            return "Text";
          case 16:
            return Qa(b);
          case 8:
            return b === za ? "StrictMode" : "Mode";
          case 22:
            return "Offscreen";
          case 12:
            return "Profiler";
          case 21:
            return "Scope";
          case 13:
            return "Suspense";
          case 19:
            return "SuspenseList";
          case 25:
            return "TracingMarker";
          case 1:
          case 0:
          case 17:
          case 2:
          case 14:
          case 15:
            if ("function" === typeof b) return b.displayName || b.name || null;
            if ("string" === typeof b) return b;
        }
        return null;
      }
      function Sa(a) {
        switch (typeof a) {
          case "boolean":
          case "number":
          case "string":
          case "undefined":
            return a;
          case "object":
            return a;
          default:
            return "";
        }
      }
      function Ta(a) {
        var b = a.type;
        return (a = a.nodeName) && "input" === a.toLowerCase() && ("checkbox" === b || "radio" === b);
      }
      function Ua(a) {
        var b = Ta(a) ? "checked" : "value", c = Object.getOwnPropertyDescriptor(a.constructor.prototype, b), d = "" + a[b];
        if (!a.hasOwnProperty(b) && "undefined" !== typeof c && "function" === typeof c.get && "function" === typeof c.set) {
          var e = c.get, f = c.set;
          Object.defineProperty(a, b, { configurable: true, get: function() {
            return e.call(this);
          }, set: function(a2) {
            d = "" + a2;
            f.call(this, a2);
          } });
          Object.defineProperty(a, b, { enumerable: c.enumerable });
          return { getValue: function() {
            return d;
          }, setValue: function(a2) {
            d = "" + a2;
          }, stopTracking: function() {
            a._valueTracker = null;
            delete a[b];
          } };
        }
      }
      function Va(a) {
        a._valueTracker || (a._valueTracker = Ua(a));
      }
      function Wa(a) {
        if (!a) return false;
        var b = a._valueTracker;
        if (!b) return true;
        var c = b.getValue();
        var d = "";
        a && (d = Ta(a) ? a.checked ? "true" : "false" : a.value);
        a = d;
        return a !== c ? (b.setValue(a), true) : false;
      }
      function Xa(a) {
        a = a || ("undefined" !== typeof document ? document : void 0);
        if ("undefined" === typeof a) return null;
        try {
          return a.activeElement || a.body;
        } catch (b) {
          return a.body;
        }
      }
      function Ya(a, b) {
        var c = b.checked;
        return A({}, b, { defaultChecked: void 0, defaultValue: void 0, value: void 0, checked: null != c ? c : a._wrapperState.initialChecked });
      }
      function Za(a, b) {
        var c = null == b.defaultValue ? "" : b.defaultValue, d = null != b.checked ? b.checked : b.defaultChecked;
        c = Sa(null != b.value ? b.value : c);
        a._wrapperState = { initialChecked: d, initialValue: c, controlled: "checkbox" === b.type || "radio" === b.type ? null != b.checked : null != b.value };
      }
      function ab(a, b) {
        b = b.checked;
        null != b && ta(a, "checked", b, false);
      }
      function bb(a, b) {
        ab(a, b);
        var c = Sa(b.value), d = b.type;
        if (null != c) if ("number" === d) {
          if (0 === c && "" === a.value || a.value != c) a.value = "" + c;
        } else a.value !== "" + c && (a.value = "" + c);
        else if ("submit" === d || "reset" === d) {
          a.removeAttribute("value");
          return;
        }
        b.hasOwnProperty("value") ? cb(a, b.type, c) : b.hasOwnProperty("defaultValue") && cb(a, b.type, Sa(b.defaultValue));
        null == b.checked && null != b.defaultChecked && (a.defaultChecked = !!b.defaultChecked);
      }
      function db(a, b, c) {
        if (b.hasOwnProperty("value") || b.hasOwnProperty("defaultValue")) {
          var d = b.type;
          if (!("submit" !== d && "reset" !== d || void 0 !== b.value && null !== b.value)) return;
          b = "" + a._wrapperState.initialValue;
          c || b === a.value || (a.value = b);
          a.defaultValue = b;
        }
        c = a.name;
        "" !== c && (a.name = "");
        a.defaultChecked = !!a._wrapperState.initialChecked;
        "" !== c && (a.name = c);
      }
      function cb(a, b, c) {
        if ("number" !== b || Xa(a.ownerDocument) !== a) null == c ? a.defaultValue = "" + a._wrapperState.initialValue : a.defaultValue !== "" + c && (a.defaultValue = "" + c);
      }
      var eb = Array.isArray;
      function fb(a, b, c, d) {
        a = a.options;
        if (b) {
          b = {};
          for (var e = 0; e < c.length; e++) b["$" + c[e]] = true;
          for (c = 0; c < a.length; c++) e = b.hasOwnProperty("$" + a[c].value), a[c].selected !== e && (a[c].selected = e), e && d && (a[c].defaultSelected = true);
        } else {
          c = "" + Sa(c);
          b = null;
          for (e = 0; e < a.length; e++) {
            if (a[e].value === c) {
              a[e].selected = true;
              d && (a[e].defaultSelected = true);
              return;
            }
            null !== b || a[e].disabled || (b = a[e]);
          }
          null !== b && (b.selected = true);
        }
      }
      function gb(a, b) {
        if (null != b.dangerouslySetInnerHTML) throw Error(p(91));
        return A({}, b, { value: void 0, defaultValue: void 0, children: "" + a._wrapperState.initialValue });
      }
      function hb(a, b) {
        var c = b.value;
        if (null == c) {
          c = b.children;
          b = b.defaultValue;
          if (null != c) {
            if (null != b) throw Error(p(92));
            if (eb(c)) {
              if (1 < c.length) throw Error(p(93));
              c = c[0];
            }
            b = c;
          }
          null == b && (b = "");
          c = b;
        }
        a._wrapperState = { initialValue: Sa(c) };
      }
      function ib(a, b) {
        var c = Sa(b.value), d = Sa(b.defaultValue);
        null != c && (c = "" + c, c !== a.value && (a.value = c), null == b.defaultValue && a.defaultValue !== c && (a.defaultValue = c));
        null != d && (a.defaultValue = "" + d);
      }
      function jb(a) {
        var b = a.textContent;
        b === a._wrapperState.initialValue && "" !== b && null !== b && (a.value = b);
      }
      function kb(a) {
        switch (a) {
          case "svg":
            return "http://www.w3.org/2000/svg";
          case "math":
            return "http://www.w3.org/1998/Math/MathML";
          default:
            return "http://www.w3.org/1999/xhtml";
        }
      }
      function lb(a, b) {
        return null == a || "http://www.w3.org/1999/xhtml" === a ? kb(b) : "http://www.w3.org/2000/svg" === a && "foreignObject" === b ? "http://www.w3.org/1999/xhtml" : a;
      }
      var mb;
      var nb = (function(a) {
        return "undefined" !== typeof MSApp && MSApp.execUnsafeLocalFunction ? function(b, c, d, e) {
          MSApp.execUnsafeLocalFunction(function() {
            return a(b, c, d, e);
          });
        } : a;
      })(function(a, b) {
        if ("http://www.w3.org/2000/svg" !== a.namespaceURI || "innerHTML" in a) a.innerHTML = b;
        else {
          mb = mb || document.createElement("div");
          mb.innerHTML = "<svg>" + b.valueOf().toString() + "</svg>";
          for (b = mb.firstChild; a.firstChild; ) a.removeChild(a.firstChild);
          for (; b.firstChild; ) a.appendChild(b.firstChild);
        }
      });
      function ob(a, b) {
        if (b) {
          var c = a.firstChild;
          if (c && c === a.lastChild && 3 === c.nodeType) {
            c.nodeValue = b;
            return;
          }
        }
        a.textContent = b;
      }
      var pb = {
        animationIterationCount: true,
        aspectRatio: true,
        borderImageOutset: true,
        borderImageSlice: true,
        borderImageWidth: true,
        boxFlex: true,
        boxFlexGroup: true,
        boxOrdinalGroup: true,
        columnCount: true,
        columns: true,
        flex: true,
        flexGrow: true,
        flexPositive: true,
        flexShrink: true,
        flexNegative: true,
        flexOrder: true,
        gridArea: true,
        gridRow: true,
        gridRowEnd: true,
        gridRowSpan: true,
        gridRowStart: true,
        gridColumn: true,
        gridColumnEnd: true,
        gridColumnSpan: true,
        gridColumnStart: true,
        fontWeight: true,
        lineClamp: true,
        lineHeight: true,
        opacity: true,
        order: true,
        orphans: true,
        tabSize: true,
        widows: true,
        zIndex: true,
        zoom: true,
        fillOpacity: true,
        floodOpacity: true,
        stopOpacity: true,
        strokeDasharray: true,
        strokeDashoffset: true,
        strokeMiterlimit: true,
        strokeOpacity: true,
        strokeWidth: true
      };
      var qb = ["Webkit", "ms", "Moz", "O"];
      Object.keys(pb).forEach(function(a) {
        qb.forEach(function(b) {
          b = b + a.charAt(0).toUpperCase() + a.substring(1);
          pb[b] = pb[a];
        });
      });
      function rb(a, b, c) {
        return null == b || "boolean" === typeof b || "" === b ? "" : c || "number" !== typeof b || 0 === b || pb.hasOwnProperty(a) && pb[a] ? ("" + b).trim() : b + "px";
      }
      function sb(a, b) {
        a = a.style;
        for (var c in b) if (b.hasOwnProperty(c)) {
          var d = 0 === c.indexOf("--"), e = rb(c, b[c], d);
          "float" === c && (c = "cssFloat");
          d ? a.setProperty(c, e) : a[c] = e;
        }
      }
      var tb = A({ menuitem: true }, { area: true, base: true, br: true, col: true, embed: true, hr: true, img: true, input: true, keygen: true, link: true, meta: true, param: true, source: true, track: true, wbr: true });
      function ub(a, b) {
        if (b) {
          if (tb[a] && (null != b.children || null != b.dangerouslySetInnerHTML)) throw Error(p(137, a));
          if (null != b.dangerouslySetInnerHTML) {
            if (null != b.children) throw Error(p(60));
            if ("object" !== typeof b.dangerouslySetInnerHTML || !("__html" in b.dangerouslySetInnerHTML)) throw Error(p(61));
          }
          if (null != b.style && "object" !== typeof b.style) throw Error(p(62));
        }
      }
      function vb(a, b) {
        if (-1 === a.indexOf("-")) return "string" === typeof b.is;
        switch (a) {
          case "annotation-xml":
          case "color-profile":
          case "font-face":
          case "font-face-src":
          case "font-face-uri":
          case "font-face-format":
          case "font-face-name":
          case "missing-glyph":
            return false;
          default:
            return true;
        }
      }
      var wb = null;
      function xb(a) {
        a = a.target || a.srcElement || window;
        a.correspondingUseElement && (a = a.correspondingUseElement);
        return 3 === a.nodeType ? a.parentNode : a;
      }
      var yb = null;
      var zb = null;
      var Ab = null;
      function Bb(a) {
        if (a = Cb(a)) {
          if ("function" !== typeof yb) throw Error(p(280));
          var b = a.stateNode;
          b && (b = Db(b), yb(a.stateNode, a.type, b));
        }
      }
      function Eb(a) {
        zb ? Ab ? Ab.push(a) : Ab = [a] : zb = a;
      }
      function Fb() {
        if (zb) {
          var a = zb, b = Ab;
          Ab = zb = null;
          Bb(a);
          if (b) for (a = 0; a < b.length; a++) Bb(b[a]);
        }
      }
      function Gb(a, b) {
        return a(b);
      }
      function Hb() {
      }
      var Ib = false;
      function Jb(a, b, c) {
        if (Ib) return a(b, c);
        Ib = true;
        try {
          return Gb(a, b, c);
        } finally {
          if (Ib = false, null !== zb || null !== Ab) Hb(), Fb();
        }
      }
      function Kb(a, b) {
        var c = a.stateNode;
        if (null === c) return null;
        var d = Db(c);
        if (null === d) return null;
        c = d[b];
        a: switch (b) {
          case "onClick":
          case "onClickCapture":
          case "onDoubleClick":
          case "onDoubleClickCapture":
          case "onMouseDown":
          case "onMouseDownCapture":
          case "onMouseMove":
          case "onMouseMoveCapture":
          case "onMouseUp":
          case "onMouseUpCapture":
          case "onMouseEnter":
            (d = !d.disabled) || (a = a.type, d = !("button" === a || "input" === a || "select" === a || "textarea" === a));
            a = !d;
            break a;
          default:
            a = false;
        }
        if (a) return null;
        if (c && "function" !== typeof c) throw Error(p(231, b, typeof c));
        return c;
      }
      var Lb = false;
      if (ia) try {
        Mb = {};
        Object.defineProperty(Mb, "passive", { get: function() {
          Lb = true;
        } });
        window.addEventListener("test", Mb, Mb);
        window.removeEventListener("test", Mb, Mb);
      } catch (a) {
        Lb = false;
      }
      var Mb;
      function Nb(a, b, c, d, e, f, g, h, k) {
        var l = Array.prototype.slice.call(arguments, 3);
        try {
          b.apply(c, l);
        } catch (m) {
          this.onError(m);
        }
      }
      var Ob = false;
      var Pb = null;
      var Qb = false;
      var Rb = null;
      var Sb = { onError: function(a) {
        Ob = true;
        Pb = a;
      } };
      function Tb(a, b, c, d, e, f, g, h, k) {
        Ob = false;
        Pb = null;
        Nb.apply(Sb, arguments);
      }
      function Ub(a, b, c, d, e, f, g, h, k) {
        Tb.apply(this, arguments);
        if (Ob) {
          if (Ob) {
            var l = Pb;
            Ob = false;
            Pb = null;
          } else throw Error(p(198));
          Qb || (Qb = true, Rb = l);
        }
      }
      function Vb(a) {
        var b = a, c = a;
        if (a.alternate) for (; b.return; ) b = b.return;
        else {
          a = b;
          do
            b = a, 0 !== (b.flags & 4098) && (c = b.return), a = b.return;
          while (a);
        }
        return 3 === b.tag ? c : null;
      }
      function Wb(a) {
        if (13 === a.tag) {
          var b = a.memoizedState;
          null === b && (a = a.alternate, null !== a && (b = a.memoizedState));
          if (null !== b) return b.dehydrated;
        }
        return null;
      }
      function Xb(a) {
        if (Vb(a) !== a) throw Error(p(188));
      }
      function Yb(a) {
        var b = a.alternate;
        if (!b) {
          b = Vb(a);
          if (null === b) throw Error(p(188));
          return b !== a ? null : a;
        }
        for (var c = a, d = b; ; ) {
          var e = c.return;
          if (null === e) break;
          var f = e.alternate;
          if (null === f) {
            d = e.return;
            if (null !== d) {
              c = d;
              continue;
            }
            break;
          }
          if (e.child === f.child) {
            for (f = e.child; f; ) {
              if (f === c) return Xb(e), a;
              if (f === d) return Xb(e), b;
              f = f.sibling;
            }
            throw Error(p(188));
          }
          if (c.return !== d.return) c = e, d = f;
          else {
            for (var g = false, h = e.child; h; ) {
              if (h === c) {
                g = true;
                c = e;
                d = f;
                break;
              }
              if (h === d) {
                g = true;
                d = e;
                c = f;
                break;
              }
              h = h.sibling;
            }
            if (!g) {
              for (h = f.child; h; ) {
                if (h === c) {
                  g = true;
                  c = f;
                  d = e;
                  break;
                }
                if (h === d) {
                  g = true;
                  d = f;
                  c = e;
                  break;
                }
                h = h.sibling;
              }
              if (!g) throw Error(p(189));
            }
          }
          if (c.alternate !== d) throw Error(p(190));
        }
        if (3 !== c.tag) throw Error(p(188));
        return c.stateNode.current === c ? a : b;
      }
      function Zb(a) {
        a = Yb(a);
        return null !== a ? $b(a) : null;
      }
      function $b(a) {
        if (5 === a.tag || 6 === a.tag) return a;
        for (a = a.child; null !== a; ) {
          var b = $b(a);
          if (null !== b) return b;
          a = a.sibling;
        }
        return null;
      }
      var ac = ca.unstable_scheduleCallback;
      var bc = ca.unstable_cancelCallback;
      var cc = ca.unstable_shouldYield;
      var dc = ca.unstable_requestPaint;
      var B = ca.unstable_now;
      var ec = ca.unstable_getCurrentPriorityLevel;
      var fc = ca.unstable_ImmediatePriority;
      var gc = ca.unstable_UserBlockingPriority;
      var hc = ca.unstable_NormalPriority;
      var ic = ca.unstable_LowPriority;
      var jc = ca.unstable_IdlePriority;
      var kc = null;
      var lc = null;
      function mc(a) {
        if (lc && "function" === typeof lc.onCommitFiberRoot) try {
          lc.onCommitFiberRoot(kc, a, void 0, 128 === (a.current.flags & 128));
        } catch (b) {
        }
      }
      var oc = Math.clz32 ? Math.clz32 : nc;
      var pc = Math.log;
      var qc = Math.LN2;
      function nc(a) {
        a >>>= 0;
        return 0 === a ? 32 : 31 - (pc(a) / qc | 0) | 0;
      }
      var rc = 64;
      var sc = 4194304;
      function tc(a) {
        switch (a & -a) {
          case 1:
            return 1;
          case 2:
            return 2;
          case 4:
            return 4;
          case 8:
            return 8;
          case 16:
            return 16;
          case 32:
            return 32;
          case 64:
          case 128:
          case 256:
          case 512:
          case 1024:
          case 2048:
          case 4096:
          case 8192:
          case 16384:
          case 32768:
          case 65536:
          case 131072:
          case 262144:
          case 524288:
          case 1048576:
          case 2097152:
            return a & 4194240;
          case 4194304:
          case 8388608:
          case 16777216:
          case 33554432:
          case 67108864:
            return a & 130023424;
          case 134217728:
            return 134217728;
          case 268435456:
            return 268435456;
          case 536870912:
            return 536870912;
          case 1073741824:
            return 1073741824;
          default:
            return a;
        }
      }
      function uc(a, b) {
        var c = a.pendingLanes;
        if (0 === c) return 0;
        var d = 0, e = a.suspendedLanes, f = a.pingedLanes, g = c & 268435455;
        if (0 !== g) {
          var h = g & ~e;
          0 !== h ? d = tc(h) : (f &= g, 0 !== f && (d = tc(f)));
        } else g = c & ~e, 0 !== g ? d = tc(g) : 0 !== f && (d = tc(f));
        if (0 === d) return 0;
        if (0 !== b && b !== d && 0 === (b & e) && (e = d & -d, f = b & -b, e >= f || 16 === e && 0 !== (f & 4194240))) return b;
        0 !== (d & 4) && (d |= c & 16);
        b = a.entangledLanes;
        if (0 !== b) for (a = a.entanglements, b &= d; 0 < b; ) c = 31 - oc(b), e = 1 << c, d |= a[c], b &= ~e;
        return d;
      }
      function vc(a, b) {
        switch (a) {
          case 1:
          case 2:
          case 4:
            return b + 250;
          case 8:
          case 16:
          case 32:
          case 64:
          case 128:
          case 256:
          case 512:
          case 1024:
          case 2048:
          case 4096:
          case 8192:
          case 16384:
          case 32768:
          case 65536:
          case 131072:
          case 262144:
          case 524288:
          case 1048576:
          case 2097152:
            return b + 5e3;
          case 4194304:
          case 8388608:
          case 16777216:
          case 33554432:
          case 67108864:
            return -1;
          case 134217728:
          case 268435456:
          case 536870912:
          case 1073741824:
            return -1;
          default:
            return -1;
        }
      }
      function wc(a, b) {
        for (var c = a.suspendedLanes, d = a.pingedLanes, e = a.expirationTimes, f = a.pendingLanes; 0 < f; ) {
          var g = 31 - oc(f), h = 1 << g, k = e[g];
          if (-1 === k) {
            if (0 === (h & c) || 0 !== (h & d)) e[g] = vc(h, b);
          } else k <= b && (a.expiredLanes |= h);
          f &= ~h;
        }
      }
      function xc(a) {
        a = a.pendingLanes & -1073741825;
        return 0 !== a ? a : a & 1073741824 ? 1073741824 : 0;
      }
      function yc() {
        var a = rc;
        rc <<= 1;
        0 === (rc & 4194240) && (rc = 64);
        return a;
      }
      function zc(a) {
        for (var b = [], c = 0; 31 > c; c++) b.push(a);
        return b;
      }
      function Ac(a, b, c) {
        a.pendingLanes |= b;
        536870912 !== b && (a.suspendedLanes = 0, a.pingedLanes = 0);
        a = a.eventTimes;
        b = 31 - oc(b);
        a[b] = c;
      }
      function Bc(a, b) {
        var c = a.pendingLanes & ~b;
        a.pendingLanes = b;
        a.suspendedLanes = 0;
        a.pingedLanes = 0;
        a.expiredLanes &= b;
        a.mutableReadLanes &= b;
        a.entangledLanes &= b;
        b = a.entanglements;
        var d = a.eventTimes;
        for (a = a.expirationTimes; 0 < c; ) {
          var e = 31 - oc(c), f = 1 << e;
          b[e] = 0;
          d[e] = -1;
          a[e] = -1;
          c &= ~f;
        }
      }
      function Cc(a, b) {
        var c = a.entangledLanes |= b;
        for (a = a.entanglements; c; ) {
          var d = 31 - oc(c), e = 1 << d;
          e & b | a[d] & b && (a[d] |= b);
          c &= ~e;
        }
      }
      var C = 0;
      function Dc(a) {
        a &= -a;
        return 1 < a ? 4 < a ? 0 !== (a & 268435455) ? 16 : 536870912 : 4 : 1;
      }
      var Ec;
      var Fc;
      var Gc;
      var Hc;
      var Ic;
      var Jc = false;
      var Kc = [];
      var Lc = null;
      var Mc = null;
      var Nc = null;
      var Oc = /* @__PURE__ */ new Map();
      var Pc = /* @__PURE__ */ new Map();
      var Qc = [];
      var Rc = "mousedown mouseup touchcancel touchend touchstart auxclick dblclick pointercancel pointerdown pointerup dragend dragstart drop compositionend compositionstart keydown keypress keyup input textInput copy cut paste click change contextmenu reset submit".split(" ");
      function Sc(a, b) {
        switch (a) {
          case "focusin":
          case "focusout":
            Lc = null;
            break;
          case "dragenter":
          case "dragleave":
            Mc = null;
            break;
          case "mouseover":
          case "mouseout":
            Nc = null;
            break;
          case "pointerover":
          case "pointerout":
            Oc.delete(b.pointerId);
            break;
          case "gotpointercapture":
          case "lostpointercapture":
            Pc.delete(b.pointerId);
        }
      }
      function Tc(a, b, c, d, e, f) {
        if (null === a || a.nativeEvent !== f) return a = { blockedOn: b, domEventName: c, eventSystemFlags: d, nativeEvent: f, targetContainers: [e] }, null !== b && (b = Cb(b), null !== b && Fc(b)), a;
        a.eventSystemFlags |= d;
        b = a.targetContainers;
        null !== e && -1 === b.indexOf(e) && b.push(e);
        return a;
      }
      function Uc(a, b, c, d, e) {
        switch (b) {
          case "focusin":
            return Lc = Tc(Lc, a, b, c, d, e), true;
          case "dragenter":
            return Mc = Tc(Mc, a, b, c, d, e), true;
          case "mouseover":
            return Nc = Tc(Nc, a, b, c, d, e), true;
          case "pointerover":
            var f = e.pointerId;
            Oc.set(f, Tc(Oc.get(f) || null, a, b, c, d, e));
            return true;
          case "gotpointercapture":
            return f = e.pointerId, Pc.set(f, Tc(Pc.get(f) || null, a, b, c, d, e)), true;
        }
        return false;
      }
      function Vc(a) {
        var b = Wc(a.target);
        if (null !== b) {
          var c = Vb(b);
          if (null !== c) {
            if (b = c.tag, 13 === b) {
              if (b = Wb(c), null !== b) {
                a.blockedOn = b;
                Ic(a.priority, function() {
                  Gc(c);
                });
                return;
              }
            } else if (3 === b && c.stateNode.current.memoizedState.isDehydrated) {
              a.blockedOn = 3 === c.tag ? c.stateNode.containerInfo : null;
              return;
            }
          }
        }
        a.blockedOn = null;
      }
      function Xc(a) {
        if (null !== a.blockedOn) return false;
        for (var b = a.targetContainers; 0 < b.length; ) {
          var c = Yc(a.domEventName, a.eventSystemFlags, b[0], a.nativeEvent);
          if (null === c) {
            c = a.nativeEvent;
            var d = new c.constructor(c.type, c);
            wb = d;
            c.target.dispatchEvent(d);
            wb = null;
          } else return b = Cb(c), null !== b && Fc(b), a.blockedOn = c, false;
          b.shift();
        }
        return true;
      }
      function Zc(a, b, c) {
        Xc(a) && c.delete(b);
      }
      function $c() {
        Jc = false;
        null !== Lc && Xc(Lc) && (Lc = null);
        null !== Mc && Xc(Mc) && (Mc = null);
        null !== Nc && Xc(Nc) && (Nc = null);
        Oc.forEach(Zc);
        Pc.forEach(Zc);
      }
      function ad(a, b) {
        a.blockedOn === b && (a.blockedOn = null, Jc || (Jc = true, ca.unstable_scheduleCallback(ca.unstable_NormalPriority, $c)));
      }
      function bd(a) {
        function b(b2) {
          return ad(b2, a);
        }
        if (0 < Kc.length) {
          ad(Kc[0], a);
          for (var c = 1; c < Kc.length; c++) {
            var d = Kc[c];
            d.blockedOn === a && (d.blockedOn = null);
          }
        }
        null !== Lc && ad(Lc, a);
        null !== Mc && ad(Mc, a);
        null !== Nc && ad(Nc, a);
        Oc.forEach(b);
        Pc.forEach(b);
        for (c = 0; c < Qc.length; c++) d = Qc[c], d.blockedOn === a && (d.blockedOn = null);
        for (; 0 < Qc.length && (c = Qc[0], null === c.blockedOn); ) Vc(c), null === c.blockedOn && Qc.shift();
      }
      var cd = ua.ReactCurrentBatchConfig;
      var dd = true;
      function ed(a, b, c, d) {
        var e = C, f = cd.transition;
        cd.transition = null;
        try {
          C = 1, fd(a, b, c, d);
        } finally {
          C = e, cd.transition = f;
        }
      }
      function gd(a, b, c, d) {
        var e = C, f = cd.transition;
        cd.transition = null;
        try {
          C = 4, fd(a, b, c, d);
        } finally {
          C = e, cd.transition = f;
        }
      }
      function fd(a, b, c, d) {
        if (dd) {
          var e = Yc(a, b, c, d);
          if (null === e) hd(a, b, d, id, c), Sc(a, d);
          else if (Uc(e, a, b, c, d)) d.stopPropagation();
          else if (Sc(a, d), b & 4 && -1 < Rc.indexOf(a)) {
            for (; null !== e; ) {
              var f = Cb(e);
              null !== f && Ec(f);
              f = Yc(a, b, c, d);
              null === f && hd(a, b, d, id, c);
              if (f === e) break;
              e = f;
            }
            null !== e && d.stopPropagation();
          } else hd(a, b, d, null, c);
        }
      }
      var id = null;
      function Yc(a, b, c, d) {
        id = null;
        a = xb(d);
        a = Wc(a);
        if (null !== a) if (b = Vb(a), null === b) a = null;
        else if (c = b.tag, 13 === c) {
          a = Wb(b);
          if (null !== a) return a;
          a = null;
        } else if (3 === c) {
          if (b.stateNode.current.memoizedState.isDehydrated) return 3 === b.tag ? b.stateNode.containerInfo : null;
          a = null;
        } else b !== a && (a = null);
        id = a;
        return null;
      }
      function jd(a) {
        switch (a) {
          case "cancel":
          case "click":
          case "close":
          case "contextmenu":
          case "copy":
          case "cut":
          case "auxclick":
          case "dblclick":
          case "dragend":
          case "dragstart":
          case "drop":
          case "focusin":
          case "focusout":
          case "input":
          case "invalid":
          case "keydown":
          case "keypress":
          case "keyup":
          case "mousedown":
          case "mouseup":
          case "paste":
          case "pause":
          case "play":
          case "pointercancel":
          case "pointerdown":
          case "pointerup":
          case "ratechange":
          case "reset":
          case "resize":
          case "seeked":
          case "submit":
          case "touchcancel":
          case "touchend":
          case "touchstart":
          case "volumechange":
          case "change":
          case "selectionchange":
          case "textInput":
          case "compositionstart":
          case "compositionend":
          case "compositionupdate":
          case "beforeblur":
          case "afterblur":
          case "beforeinput":
          case "blur":
          case "fullscreenchange":
          case "focus":
          case "hashchange":
          case "popstate":
          case "select":
          case "selectstart":
            return 1;
          case "drag":
          case "dragenter":
          case "dragexit":
          case "dragleave":
          case "dragover":
          case "mousemove":
          case "mouseout":
          case "mouseover":
          case "pointermove":
          case "pointerout":
          case "pointerover":
          case "scroll":
          case "toggle":
          case "touchmove":
          case "wheel":
          case "mouseenter":
          case "mouseleave":
          case "pointerenter":
          case "pointerleave":
            return 4;
          case "message":
            switch (ec()) {
              case fc:
                return 1;
              case gc:
                return 4;
              case hc:
              case ic:
                return 16;
              case jc:
                return 536870912;
              default:
                return 16;
            }
          default:
            return 16;
        }
      }
      var kd = null;
      var ld = null;
      var md = null;
      function nd() {
        if (md) return md;
        var a, b = ld, c = b.length, d, e = "value" in kd ? kd.value : kd.textContent, f = e.length;
        for (a = 0; a < c && b[a] === e[a]; a++) ;
        var g = c - a;
        for (d = 1; d <= g && b[c - d] === e[f - d]; d++) ;
        return md = e.slice(a, 1 < d ? 1 - d : void 0);
      }
      function od(a) {
        var b = a.keyCode;
        "charCode" in a ? (a = a.charCode, 0 === a && 13 === b && (a = 13)) : a = b;
        10 === a && (a = 13);
        return 32 <= a || 13 === a ? a : 0;
      }
      function pd() {
        return true;
      }
      function qd() {
        return false;
      }
      function rd(a) {
        function b(b2, d, e, f, g) {
          this._reactName = b2;
          this._targetInst = e;
          this.type = d;
          this.nativeEvent = f;
          this.target = g;
          this.currentTarget = null;
          for (var c in a) a.hasOwnProperty(c) && (b2 = a[c], this[c] = b2 ? b2(f) : f[c]);
          this.isDefaultPrevented = (null != f.defaultPrevented ? f.defaultPrevented : false === f.returnValue) ? pd : qd;
          this.isPropagationStopped = qd;
          return this;
        }
        A(b.prototype, { preventDefault: function() {
          this.defaultPrevented = true;
          var a2 = this.nativeEvent;
          a2 && (a2.preventDefault ? a2.preventDefault() : "unknown" !== typeof a2.returnValue && (a2.returnValue = false), this.isDefaultPrevented = pd);
        }, stopPropagation: function() {
          var a2 = this.nativeEvent;
          a2 && (a2.stopPropagation ? a2.stopPropagation() : "unknown" !== typeof a2.cancelBubble && (a2.cancelBubble = true), this.isPropagationStopped = pd);
        }, persist: function() {
        }, isPersistent: pd });
        return b;
      }
      var sd = { eventPhase: 0, bubbles: 0, cancelable: 0, timeStamp: function(a) {
        return a.timeStamp || Date.now();
      }, defaultPrevented: 0, isTrusted: 0 };
      var td = rd(sd);
      var ud = A({}, sd, { view: 0, detail: 0 });
      var vd = rd(ud);
      var wd;
      var xd;
      var yd;
      var Ad = A({}, ud, { screenX: 0, screenY: 0, clientX: 0, clientY: 0, pageX: 0, pageY: 0, ctrlKey: 0, shiftKey: 0, altKey: 0, metaKey: 0, getModifierState: zd, button: 0, buttons: 0, relatedTarget: function(a) {
        return void 0 === a.relatedTarget ? a.fromElement === a.srcElement ? a.toElement : a.fromElement : a.relatedTarget;
      }, movementX: function(a) {
        if ("movementX" in a) return a.movementX;
        a !== yd && (yd && "mousemove" === a.type ? (wd = a.screenX - yd.screenX, xd = a.screenY - yd.screenY) : xd = wd = 0, yd = a);
        return wd;
      }, movementY: function(a) {
        return "movementY" in a ? a.movementY : xd;
      } });
      var Bd = rd(Ad);
      var Cd = A({}, Ad, { dataTransfer: 0 });
      var Dd = rd(Cd);
      var Ed = A({}, ud, { relatedTarget: 0 });
      var Fd = rd(Ed);
      var Gd = A({}, sd, { animationName: 0, elapsedTime: 0, pseudoElement: 0 });
      var Hd = rd(Gd);
      var Id = A({}, sd, { clipboardData: function(a) {
        return "clipboardData" in a ? a.clipboardData : window.clipboardData;
      } });
      var Jd = rd(Id);
      var Kd = A({}, sd, { data: 0 });
      var Ld = rd(Kd);
      var Md = {
        Esc: "Escape",
        Spacebar: " ",
        Left: "ArrowLeft",
        Up: "ArrowUp",
        Right: "ArrowRight",
        Down: "ArrowDown",
        Del: "Delete",
        Win: "OS",
        Menu: "ContextMenu",
        Apps: "ContextMenu",
        Scroll: "ScrollLock",
        MozPrintableKey: "Unidentified"
      };
      var Nd = {
        8: "Backspace",
        9: "Tab",
        12: "Clear",
        13: "Enter",
        16: "Shift",
        17: "Control",
        18: "Alt",
        19: "Pause",
        20: "CapsLock",
        27: "Escape",
        32: " ",
        33: "PageUp",
        34: "PageDown",
        35: "End",
        36: "Home",
        37: "ArrowLeft",
        38: "ArrowUp",
        39: "ArrowRight",
        40: "ArrowDown",
        45: "Insert",
        46: "Delete",
        112: "F1",
        113: "F2",
        114: "F3",
        115: "F4",
        116: "F5",
        117: "F6",
        118: "F7",
        119: "F8",
        120: "F9",
        121: "F10",
        122: "F11",
        123: "F12",
        144: "NumLock",
        145: "ScrollLock",
        224: "Meta"
      };
      var Od = { Alt: "altKey", Control: "ctrlKey", Meta: "metaKey", Shift: "shiftKey" };
      function Pd(a) {
        var b = this.nativeEvent;
        return b.getModifierState ? b.getModifierState(a) : (a = Od[a]) ? !!b[a] : false;
      }
      function zd() {
        return Pd;
      }
      var Qd = A({}, ud, { key: function(a) {
        if (a.key) {
          var b = Md[a.key] || a.key;
          if ("Unidentified" !== b) return b;
        }
        return "keypress" === a.type ? (a = od(a), 13 === a ? "Enter" : String.fromCharCode(a)) : "keydown" === a.type || "keyup" === a.type ? Nd[a.keyCode] || "Unidentified" : "";
      }, code: 0, location: 0, ctrlKey: 0, shiftKey: 0, altKey: 0, metaKey: 0, repeat: 0, locale: 0, getModifierState: zd, charCode: function(a) {
        return "keypress" === a.type ? od(a) : 0;
      }, keyCode: function(a) {
        return "keydown" === a.type || "keyup" === a.type ? a.keyCode : 0;
      }, which: function(a) {
        return "keypress" === a.type ? od(a) : "keydown" === a.type || "keyup" === a.type ? a.keyCode : 0;
      } });
      var Rd = rd(Qd);
      var Sd = A({}, Ad, { pointerId: 0, width: 0, height: 0, pressure: 0, tangentialPressure: 0, tiltX: 0, tiltY: 0, twist: 0, pointerType: 0, isPrimary: 0 });
      var Td = rd(Sd);
      var Ud = A({}, ud, { touches: 0, targetTouches: 0, changedTouches: 0, altKey: 0, metaKey: 0, ctrlKey: 0, shiftKey: 0, getModifierState: zd });
      var Vd = rd(Ud);
      var Wd = A({}, sd, { propertyName: 0, elapsedTime: 0, pseudoElement: 0 });
      var Xd = rd(Wd);
      var Yd = A({}, Ad, {
        deltaX: function(a) {
          return "deltaX" in a ? a.deltaX : "wheelDeltaX" in a ? -a.wheelDeltaX : 0;
        },
        deltaY: function(a) {
          return "deltaY" in a ? a.deltaY : "wheelDeltaY" in a ? -a.wheelDeltaY : "wheelDelta" in a ? -a.wheelDelta : 0;
        },
        deltaZ: 0,
        deltaMode: 0
      });
      var Zd = rd(Yd);
      var $d = [9, 13, 27, 32];
      var ae = ia && "CompositionEvent" in window;
      var be = null;
      ia && "documentMode" in document && (be = document.documentMode);
      var ce = ia && "TextEvent" in window && !be;
      var de = ia && (!ae || be && 8 < be && 11 >= be);
      var ee = String.fromCharCode(32);
      var fe = false;
      function ge(a, b) {
        switch (a) {
          case "keyup":
            return -1 !== $d.indexOf(b.keyCode);
          case "keydown":
            return 229 !== b.keyCode;
          case "keypress":
          case "mousedown":
          case "focusout":
            return true;
          default:
            return false;
        }
      }
      function he(a) {
        a = a.detail;
        return "object" === typeof a && "data" in a ? a.data : null;
      }
      var ie = false;
      function je(a, b) {
        switch (a) {
          case "compositionend":
            return he(b);
          case "keypress":
            if (32 !== b.which) return null;
            fe = true;
            return ee;
          case "textInput":
            return a = b.data, a === ee && fe ? null : a;
          default:
            return null;
        }
      }
      function ke(a, b) {
        if (ie) return "compositionend" === a || !ae && ge(a, b) ? (a = nd(), md = ld = kd = null, ie = false, a) : null;
        switch (a) {
          case "paste":
            return null;
          case "keypress":
            if (!(b.ctrlKey || b.altKey || b.metaKey) || b.ctrlKey && b.altKey) {
              if (b.char && 1 < b.char.length) return b.char;
              if (b.which) return String.fromCharCode(b.which);
            }
            return null;
          case "compositionend":
            return de && "ko" !== b.locale ? null : b.data;
          default:
            return null;
        }
      }
      var le = { color: true, date: true, datetime: true, "datetime-local": true, email: true, month: true, number: true, password: true, range: true, search: true, tel: true, text: true, time: true, url: true, week: true };
      function me(a) {
        var b = a && a.nodeName && a.nodeName.toLowerCase();
        return "input" === b ? !!le[a.type] : "textarea" === b ? true : false;
      }
      function ne(a, b, c, d) {
        Eb(d);
        b = oe(b, "onChange");
        0 < b.length && (c = new td("onChange", "change", null, c, d), a.push({ event: c, listeners: b }));
      }
      var pe = null;
      var qe = null;
      function re(a) {
        se(a, 0);
      }
      function te(a) {
        var b = ue(a);
        if (Wa(b)) return a;
      }
      function ve(a, b) {
        if ("change" === a) return b;
      }
      var we = false;
      if (ia) {
        if (ia) {
          ye = "oninput" in document;
          if (!ye) {
            ze = document.createElement("div");
            ze.setAttribute("oninput", "return;");
            ye = "function" === typeof ze.oninput;
          }
          xe = ye;
        } else xe = false;
        we = xe && (!document.documentMode || 9 < document.documentMode);
      }
      var xe;
      var ye;
      var ze;
      function Ae() {
        pe && (pe.detachEvent("onpropertychange", Be), qe = pe = null);
      }
      function Be(a) {
        if ("value" === a.propertyName && te(qe)) {
          var b = [];
          ne(b, qe, a, xb(a));
          Jb(re, b);
        }
      }
      function Ce(a, b, c) {
        "focusin" === a ? (Ae(), pe = b, qe = c, pe.attachEvent("onpropertychange", Be)) : "focusout" === a && Ae();
      }
      function De(a) {
        if ("selectionchange" === a || "keyup" === a || "keydown" === a) return te(qe);
      }
      function Ee(a, b) {
        if ("click" === a) return te(b);
      }
      function Fe(a, b) {
        if ("input" === a || "change" === a) return te(b);
      }
      function Ge(a, b) {
        return a === b && (0 !== a || 1 / a === 1 / b) || a !== a && b !== b;
      }
      var He = "function" === typeof Object.is ? Object.is : Ge;
      function Ie(a, b) {
        if (He(a, b)) return true;
        if ("object" !== typeof a || null === a || "object" !== typeof b || null === b) return false;
        var c = Object.keys(a), d = Object.keys(b);
        if (c.length !== d.length) return false;
        for (d = 0; d < c.length; d++) {
          var e = c[d];
          if (!ja.call(b, e) || !He(a[e], b[e])) return false;
        }
        return true;
      }
      function Je(a) {
        for (; a && a.firstChild; ) a = a.firstChild;
        return a;
      }
      function Ke(a, b) {
        var c = Je(a);
        a = 0;
        for (var d; c; ) {
          if (3 === c.nodeType) {
            d = a + c.textContent.length;
            if (a <= b && d >= b) return { node: c, offset: b - a };
            a = d;
          }
          a: {
            for (; c; ) {
              if (c.nextSibling) {
                c = c.nextSibling;
                break a;
              }
              c = c.parentNode;
            }
            c = void 0;
          }
          c = Je(c);
        }
      }
      function Le(a, b) {
        return a && b ? a === b ? true : a && 3 === a.nodeType ? false : b && 3 === b.nodeType ? Le(a, b.parentNode) : "contains" in a ? a.contains(b) : a.compareDocumentPosition ? !!(a.compareDocumentPosition(b) & 16) : false : false;
      }
      function Me() {
        for (var a = window, b = Xa(); b instanceof a.HTMLIFrameElement; ) {
          try {
            var c = "string" === typeof b.contentWindow.location.href;
          } catch (d) {
            c = false;
          }
          if (c) a = b.contentWindow;
          else break;
          b = Xa(a.document);
        }
        return b;
      }
      function Ne(a) {
        var b = a && a.nodeName && a.nodeName.toLowerCase();
        return b && ("input" === b && ("text" === a.type || "search" === a.type || "tel" === a.type || "url" === a.type || "password" === a.type) || "textarea" === b || "true" === a.contentEditable);
      }
      function Oe(a) {
        var b = Me(), c = a.focusedElem, d = a.selectionRange;
        if (b !== c && c && c.ownerDocument && Le(c.ownerDocument.documentElement, c)) {
          if (null !== d && Ne(c)) {
            if (b = d.start, a = d.end, void 0 === a && (a = b), "selectionStart" in c) c.selectionStart = b, c.selectionEnd = Math.min(a, c.value.length);
            else if (a = (b = c.ownerDocument || document) && b.defaultView || window, a.getSelection) {
              a = a.getSelection();
              var e = c.textContent.length, f = Math.min(d.start, e);
              d = void 0 === d.end ? f : Math.min(d.end, e);
              !a.extend && f > d && (e = d, d = f, f = e);
              e = Ke(c, f);
              var g = Ke(
                c,
                d
              );
              e && g && (1 !== a.rangeCount || a.anchorNode !== e.node || a.anchorOffset !== e.offset || a.focusNode !== g.node || a.focusOffset !== g.offset) && (b = b.createRange(), b.setStart(e.node, e.offset), a.removeAllRanges(), f > d ? (a.addRange(b), a.extend(g.node, g.offset)) : (b.setEnd(g.node, g.offset), a.addRange(b)));
            }
          }
          b = [];
          for (a = c; a = a.parentNode; ) 1 === a.nodeType && b.push({ element: a, left: a.scrollLeft, top: a.scrollTop });
          "function" === typeof c.focus && c.focus();
          for (c = 0; c < b.length; c++) a = b[c], a.element.scrollLeft = a.left, a.element.scrollTop = a.top;
        }
      }
      var Pe = ia && "documentMode" in document && 11 >= document.documentMode;
      var Qe = null;
      var Re = null;
      var Se = null;
      var Te = false;
      function Ue(a, b, c) {
        var d = c.window === c ? c.document : 9 === c.nodeType ? c : c.ownerDocument;
        Te || null == Qe || Qe !== Xa(d) || (d = Qe, "selectionStart" in d && Ne(d) ? d = { start: d.selectionStart, end: d.selectionEnd } : (d = (d.ownerDocument && d.ownerDocument.defaultView || window).getSelection(), d = { anchorNode: d.anchorNode, anchorOffset: d.anchorOffset, focusNode: d.focusNode, focusOffset: d.focusOffset }), Se && Ie(Se, d) || (Se = d, d = oe(Re, "onSelect"), 0 < d.length && (b = new td("onSelect", "select", null, b, c), a.push({ event: b, listeners: d }), b.target = Qe)));
      }
      function Ve(a, b) {
        var c = {};
        c[a.toLowerCase()] = b.toLowerCase();
        c["Webkit" + a] = "webkit" + b;
        c["Moz" + a] = "moz" + b;
        return c;
      }
      var We = { animationend: Ve("Animation", "AnimationEnd"), animationiteration: Ve("Animation", "AnimationIteration"), animationstart: Ve("Animation", "AnimationStart"), transitionend: Ve("Transition", "TransitionEnd") };
      var Xe = {};
      var Ye = {};
      ia && (Ye = document.createElement("div").style, "AnimationEvent" in window || (delete We.animationend.animation, delete We.animationiteration.animation, delete We.animationstart.animation), "TransitionEvent" in window || delete We.transitionend.transition);
      function Ze(a) {
        if (Xe[a]) return Xe[a];
        if (!We[a]) return a;
        var b = We[a], c;
        for (c in b) if (b.hasOwnProperty(c) && c in Ye) return Xe[a] = b[c];
        return a;
      }
      var $e = Ze("animationend");
      var af = Ze("animationiteration");
      var bf = Ze("animationstart");
      var cf = Ze("transitionend");
      var df = /* @__PURE__ */ new Map();
      var ef = "abort auxClick cancel canPlay canPlayThrough click close contextMenu copy cut drag dragEnd dragEnter dragExit dragLeave dragOver dragStart drop durationChange emptied encrypted ended error gotPointerCapture input invalid keyDown keyPress keyUp load loadedData loadedMetadata loadStart lostPointerCapture mouseDown mouseMove mouseOut mouseOver mouseUp paste pause play playing pointerCancel pointerDown pointerMove pointerOut pointerOver pointerUp progress rateChange reset resize seeked seeking stalled submit suspend timeUpdate touchCancel touchEnd touchStart volumeChange scroll toggle touchMove waiting wheel".split(" ");
      function ff(a, b) {
        df.set(a, b);
        fa(b, [a]);
      }
      for (gf = 0; gf < ef.length; gf++) {
        hf = ef[gf], jf = hf.toLowerCase(), kf = hf[0].toUpperCase() + hf.slice(1);
        ff(jf, "on" + kf);
      }
      var hf;
      var jf;
      var kf;
      var gf;
      ff($e, "onAnimationEnd");
      ff(af, "onAnimationIteration");
      ff(bf, "onAnimationStart");
      ff("dblclick", "onDoubleClick");
      ff("focusin", "onFocus");
      ff("focusout", "onBlur");
      ff(cf, "onTransitionEnd");
      ha("onMouseEnter", ["mouseout", "mouseover"]);
      ha("onMouseLeave", ["mouseout", "mouseover"]);
      ha("onPointerEnter", ["pointerout", "pointerover"]);
      ha("onPointerLeave", ["pointerout", "pointerover"]);
      fa("onChange", "change click focusin focusout input keydown keyup selectionchange".split(" "));
      fa("onSelect", "focusout contextmenu dragend focusin keydown keyup mousedown mouseup selectionchange".split(" "));
      fa("onBeforeInput", ["compositionend", "keypress", "textInput", "paste"]);
      fa("onCompositionEnd", "compositionend focusout keydown keypress keyup mousedown".split(" "));
      fa("onCompositionStart", "compositionstart focusout keydown keypress keyup mousedown".split(" "));
      fa("onCompositionUpdate", "compositionupdate focusout keydown keypress keyup mousedown".split(" "));
      var lf = "abort canplay canplaythrough durationchange emptied encrypted ended error loadeddata loadedmetadata loadstart pause play playing progress ratechange resize seeked seeking stalled suspend timeupdate volumechange waiting".split(" ");
      var mf = new Set("cancel close invalid load scroll toggle".split(" ").concat(lf));
      function nf(a, b, c) {
        var d = a.type || "unknown-event";
        a.currentTarget = c;
        Ub(d, b, void 0, a);
        a.currentTarget = null;
      }
      function se(a, b) {
        b = 0 !== (b & 4);
        for (var c = 0; c < a.length; c++) {
          var d = a[c], e = d.event;
          d = d.listeners;
          a: {
            var f = void 0;
            if (b) for (var g = d.length - 1; 0 <= g; g--) {
              var h = d[g], k = h.instance, l = h.currentTarget;
              h = h.listener;
              if (k !== f && e.isPropagationStopped()) break a;
              nf(e, h, l);
              f = k;
            }
            else for (g = 0; g < d.length; g++) {
              h = d[g];
              k = h.instance;
              l = h.currentTarget;
              h = h.listener;
              if (k !== f && e.isPropagationStopped()) break a;
              nf(e, h, l);
              f = k;
            }
          }
        }
        if (Qb) throw a = Rb, Qb = false, Rb = null, a;
      }
      function D(a, b) {
        var c = b[of];
        void 0 === c && (c = b[of] = /* @__PURE__ */ new Set());
        var d = a + "__bubble";
        c.has(d) || (pf(b, a, 2, false), c.add(d));
      }
      function qf(a, b, c) {
        var d = 0;
        b && (d |= 4);
        pf(c, a, d, b);
      }
      var rf = "_reactListening" + Math.random().toString(36).slice(2);
      function sf(a) {
        if (!a[rf]) {
          a[rf] = true;
          da.forEach(function(b2) {
            "selectionchange" !== b2 && (mf.has(b2) || qf(b2, false, a), qf(b2, true, a));
          });
          var b = 9 === a.nodeType ? a : a.ownerDocument;
          null === b || b[rf] || (b[rf] = true, qf("selectionchange", false, b));
        }
      }
      function pf(a, b, c, d) {
        switch (jd(b)) {
          case 1:
            var e = ed;
            break;
          case 4:
            e = gd;
            break;
          default:
            e = fd;
        }
        c = e.bind(null, b, c, a);
        e = void 0;
        !Lb || "touchstart" !== b && "touchmove" !== b && "wheel" !== b || (e = true);
        d ? void 0 !== e ? a.addEventListener(b, c, { capture: true, passive: e }) : a.addEventListener(b, c, true) : void 0 !== e ? a.addEventListener(b, c, { passive: e }) : a.addEventListener(b, c, false);
      }
      function hd(a, b, c, d, e) {
        var f = d;
        if (0 === (b & 1) && 0 === (b & 2) && null !== d) a: for (; ; ) {
          if (null === d) return;
          var g = d.tag;
          if (3 === g || 4 === g) {
            var h = d.stateNode.containerInfo;
            if (h === e || 8 === h.nodeType && h.parentNode === e) break;
            if (4 === g) for (g = d.return; null !== g; ) {
              var k = g.tag;
              if (3 === k || 4 === k) {
                if (k = g.stateNode.containerInfo, k === e || 8 === k.nodeType && k.parentNode === e) return;
              }
              g = g.return;
            }
            for (; null !== h; ) {
              g = Wc(h);
              if (null === g) return;
              k = g.tag;
              if (5 === k || 6 === k) {
                d = f = g;
                continue a;
              }
              h = h.parentNode;
            }
          }
          d = d.return;
        }
        Jb(function() {
          var d2 = f, e2 = xb(c), g2 = [];
          a: {
            var h2 = df.get(a);
            if (void 0 !== h2) {
              var k2 = td, n = a;
              switch (a) {
                case "keypress":
                  if (0 === od(c)) break a;
                case "keydown":
                case "keyup":
                  k2 = Rd;
                  break;
                case "focusin":
                  n = "focus";
                  k2 = Fd;
                  break;
                case "focusout":
                  n = "blur";
                  k2 = Fd;
                  break;
                case "beforeblur":
                case "afterblur":
                  k2 = Fd;
                  break;
                case "click":
                  if (2 === c.button) break a;
                case "auxclick":
                case "dblclick":
                case "mousedown":
                case "mousemove":
                case "mouseup":
                case "mouseout":
                case "mouseover":
                case "contextmenu":
                  k2 = Bd;
                  break;
                case "drag":
                case "dragend":
                case "dragenter":
                case "dragexit":
                case "dragleave":
                case "dragover":
                case "dragstart":
                case "drop":
                  k2 = Dd;
                  break;
                case "touchcancel":
                case "touchend":
                case "touchmove":
                case "touchstart":
                  k2 = Vd;
                  break;
                case $e:
                case af:
                case bf:
                  k2 = Hd;
                  break;
                case cf:
                  k2 = Xd;
                  break;
                case "scroll":
                  k2 = vd;
                  break;
                case "wheel":
                  k2 = Zd;
                  break;
                case "copy":
                case "cut":
                case "paste":
                  k2 = Jd;
                  break;
                case "gotpointercapture":
                case "lostpointercapture":
                case "pointercancel":
                case "pointerdown":
                case "pointermove":
                case "pointerout":
                case "pointerover":
                case "pointerup":
                  k2 = Td;
              }
              var t = 0 !== (b & 4), J = !t && "scroll" === a, x = t ? null !== h2 ? h2 + "Capture" : null : h2;
              t = [];
              for (var w = d2, u; null !== w; ) {
                u = w;
                var F = u.stateNode;
                5 === u.tag && null !== F && (u = F, null !== x && (F = Kb(w, x), null != F && t.push(tf(w, F, u))));
                if (J) break;
                w = w.return;
              }
              0 < t.length && (h2 = new k2(h2, n, null, c, e2), g2.push({ event: h2, listeners: t }));
            }
          }
          if (0 === (b & 7)) {
            a: {
              h2 = "mouseover" === a || "pointerover" === a;
              k2 = "mouseout" === a || "pointerout" === a;
              if (h2 && c !== wb && (n = c.relatedTarget || c.fromElement) && (Wc(n) || n[uf])) break a;
              if (k2 || h2) {
                h2 = e2.window === e2 ? e2 : (h2 = e2.ownerDocument) ? h2.defaultView || h2.parentWindow : window;
                if (k2) {
                  if (n = c.relatedTarget || c.toElement, k2 = d2, n = n ? Wc(n) : null, null !== n && (J = Vb(n), n !== J || 5 !== n.tag && 6 !== n.tag)) n = null;
                } else k2 = null, n = d2;
                if (k2 !== n) {
                  t = Bd;
                  F = "onMouseLeave";
                  x = "onMouseEnter";
                  w = "mouse";
                  if ("pointerout" === a || "pointerover" === a) t = Td, F = "onPointerLeave", x = "onPointerEnter", w = "pointer";
                  J = null == k2 ? h2 : ue(k2);
                  u = null == n ? h2 : ue(n);
                  h2 = new t(F, w + "leave", k2, c, e2);
                  h2.target = J;
                  h2.relatedTarget = u;
                  F = null;
                  Wc(e2) === d2 && (t = new t(x, w + "enter", n, c, e2), t.target = u, t.relatedTarget = J, F = t);
                  J = F;
                  if (k2 && n) b: {
                    t = k2;
                    x = n;
                    w = 0;
                    for (u = t; u; u = vf(u)) w++;
                    u = 0;
                    for (F = x; F; F = vf(F)) u++;
                    for (; 0 < w - u; ) t = vf(t), w--;
                    for (; 0 < u - w; ) x = vf(x), u--;
                    for (; w--; ) {
                      if (t === x || null !== x && t === x.alternate) break b;
                      t = vf(t);
                      x = vf(x);
                    }
                    t = null;
                  }
                  else t = null;
                  null !== k2 && wf(g2, h2, k2, t, false);
                  null !== n && null !== J && wf(g2, J, n, t, true);
                }
              }
            }
            a: {
              h2 = d2 ? ue(d2) : window;
              k2 = h2.nodeName && h2.nodeName.toLowerCase();
              if ("select" === k2 || "input" === k2 && "file" === h2.type) var na = ve;
              else if (me(h2)) if (we) na = Fe;
              else {
                na = De;
                var xa = Ce;
              }
              else (k2 = h2.nodeName) && "input" === k2.toLowerCase() && ("checkbox" === h2.type || "radio" === h2.type) && (na = Ee);
              if (na && (na = na(a, d2))) {
                ne(g2, na, c, e2);
                break a;
              }
              xa && xa(a, h2, d2);
              "focusout" === a && (xa = h2._wrapperState) && xa.controlled && "number" === h2.type && cb(h2, "number", h2.value);
            }
            xa = d2 ? ue(d2) : window;
            switch (a) {
              case "focusin":
                if (me(xa) || "true" === xa.contentEditable) Qe = xa, Re = d2, Se = null;
                break;
              case "focusout":
                Se = Re = Qe = null;
                break;
              case "mousedown":
                Te = true;
                break;
              case "contextmenu":
              case "mouseup":
              case "dragend":
                Te = false;
                Ue(g2, c, e2);
                break;
              case "selectionchange":
                if (Pe) break;
              case "keydown":
              case "keyup":
                Ue(g2, c, e2);
            }
            var $a;
            if (ae) b: {
              switch (a) {
                case "compositionstart":
                  var ba = "onCompositionStart";
                  break b;
                case "compositionend":
                  ba = "onCompositionEnd";
                  break b;
                case "compositionupdate":
                  ba = "onCompositionUpdate";
                  break b;
              }
              ba = void 0;
            }
            else ie ? ge(a, c) && (ba = "onCompositionEnd") : "keydown" === a && 229 === c.keyCode && (ba = "onCompositionStart");
            ba && (de && "ko" !== c.locale && (ie || "onCompositionStart" !== ba ? "onCompositionEnd" === ba && ie && ($a = nd()) : (kd = e2, ld = "value" in kd ? kd.value : kd.textContent, ie = true)), xa = oe(d2, ba), 0 < xa.length && (ba = new Ld(ba, a, null, c, e2), g2.push({ event: ba, listeners: xa }), $a ? ba.data = $a : ($a = he(c), null !== $a && (ba.data = $a))));
            if ($a = ce ? je(a, c) : ke(a, c)) d2 = oe(d2, "onBeforeInput"), 0 < d2.length && (e2 = new Ld("onBeforeInput", "beforeinput", null, c, e2), g2.push({ event: e2, listeners: d2 }), e2.data = $a);
          }
          se(g2, b);
        });
      }
      function tf(a, b, c) {
        return { instance: a, listener: b, currentTarget: c };
      }
      function oe(a, b) {
        for (var c = b + "Capture", d = []; null !== a; ) {
          var e = a, f = e.stateNode;
          5 === e.tag && null !== f && (e = f, f = Kb(a, c), null != f && d.unshift(tf(a, f, e)), f = Kb(a, b), null != f && d.push(tf(a, f, e)));
          a = a.return;
        }
        return d;
      }
      function vf(a) {
        if (null === a) return null;
        do
          a = a.return;
        while (a && 5 !== a.tag);
        return a ? a : null;
      }
      function wf(a, b, c, d, e) {
        for (var f = b._reactName, g = []; null !== c && c !== d; ) {
          var h = c, k = h.alternate, l = h.stateNode;
          if (null !== k && k === d) break;
          5 === h.tag && null !== l && (h = l, e ? (k = Kb(c, f), null != k && g.unshift(tf(c, k, h))) : e || (k = Kb(c, f), null != k && g.push(tf(c, k, h))));
          c = c.return;
        }
        0 !== g.length && a.push({ event: b, listeners: g });
      }
      var xf = /\r\n?/g;
      var yf = /\u0000|\uFFFD/g;
      function zf(a) {
        return ("string" === typeof a ? a : "" + a).replace(xf, "\n").replace(yf, "");
      }
      function Af(a, b, c) {
        b = zf(b);
        if (zf(a) !== b && c) throw Error(p(425));
      }
      function Bf() {
      }
      var Cf = null;
      var Df = null;
      function Ef(a, b) {
        return "textarea" === a || "noscript" === a || "string" === typeof b.children || "number" === typeof b.children || "object" === typeof b.dangerouslySetInnerHTML && null !== b.dangerouslySetInnerHTML && null != b.dangerouslySetInnerHTML.__html;
      }
      var Ff = "function" === typeof setTimeout ? setTimeout : void 0;
      var Gf = "function" === typeof clearTimeout ? clearTimeout : void 0;
      var Hf = "function" === typeof Promise ? Promise : void 0;
      var Jf = "function" === typeof queueMicrotask ? queueMicrotask : "undefined" !== typeof Hf ? function(a) {
        return Hf.resolve(null).then(a).catch(If);
      } : Ff;
      function If(a) {
        setTimeout(function() {
          throw a;
        });
      }
      function Kf(a, b) {
        var c = b, d = 0;
        do {
          var e = c.nextSibling;
          a.removeChild(c);
          if (e && 8 === e.nodeType) if (c = e.data, "/$" === c) {
            if (0 === d) {
              a.removeChild(e);
              bd(b);
              return;
            }
            d--;
          } else "$" !== c && "$?" !== c && "$!" !== c || d++;
          c = e;
        } while (c);
        bd(b);
      }
      function Lf(a) {
        for (; null != a; a = a.nextSibling) {
          var b = a.nodeType;
          if (1 === b || 3 === b) break;
          if (8 === b) {
            b = a.data;
            if ("$" === b || "$!" === b || "$?" === b) break;
            if ("/$" === b) return null;
          }
        }
        return a;
      }
      function Mf(a) {
        a = a.previousSibling;
        for (var b = 0; a; ) {
          if (8 === a.nodeType) {
            var c = a.data;
            if ("$" === c || "$!" === c || "$?" === c) {
              if (0 === b) return a;
              b--;
            } else "/$" === c && b++;
          }
          a = a.previousSibling;
        }
        return null;
      }
      var Nf = Math.random().toString(36).slice(2);
      var Of = "__reactFiber$" + Nf;
      var Pf = "__reactProps$" + Nf;
      var uf = "__reactContainer$" + Nf;
      var of = "__reactEvents$" + Nf;
      var Qf = "__reactListeners$" + Nf;
      var Rf = "__reactHandles$" + Nf;
      function Wc(a) {
        var b = a[Of];
        if (b) return b;
        for (var c = a.parentNode; c; ) {
          if (b = c[uf] || c[Of]) {
            c = b.alternate;
            if (null !== b.child || null !== c && null !== c.child) for (a = Mf(a); null !== a; ) {
              if (c = a[Of]) return c;
              a = Mf(a);
            }
            return b;
          }
          a = c;
          c = a.parentNode;
        }
        return null;
      }
      function Cb(a) {
        a = a[Of] || a[uf];
        return !a || 5 !== a.tag && 6 !== a.tag && 13 !== a.tag && 3 !== a.tag ? null : a;
      }
      function ue(a) {
        if (5 === a.tag || 6 === a.tag) return a.stateNode;
        throw Error(p(33));
      }
      function Db(a) {
        return a[Pf] || null;
      }
      var Sf = [];
      var Tf = -1;
      function Uf(a) {
        return { current: a };
      }
      function E(a) {
        0 > Tf || (a.current = Sf[Tf], Sf[Tf] = null, Tf--);
      }
      function G(a, b) {
        Tf++;
        Sf[Tf] = a.current;
        a.current = b;
      }
      var Vf = {};
      var H = Uf(Vf);
      var Wf = Uf(false);
      var Xf = Vf;
      function Yf(a, b) {
        var c = a.type.contextTypes;
        if (!c) return Vf;
        var d = a.stateNode;
        if (d && d.__reactInternalMemoizedUnmaskedChildContext === b) return d.__reactInternalMemoizedMaskedChildContext;
        var e = {}, f;
        for (f in c) e[f] = b[f];
        d && (a = a.stateNode, a.__reactInternalMemoizedUnmaskedChildContext = b, a.__reactInternalMemoizedMaskedChildContext = e);
        return e;
      }
      function Zf(a) {
        a = a.childContextTypes;
        return null !== a && void 0 !== a;
      }
      function $f() {
        E(Wf);
        E(H);
      }
      function ag(a, b, c) {
        if (H.current !== Vf) throw Error(p(168));
        G(H, b);
        G(Wf, c);
      }
      function bg(a, b, c) {
        var d = a.stateNode;
        b = b.childContextTypes;
        if ("function" !== typeof d.getChildContext) return c;
        d = d.getChildContext();
        for (var e in d) if (!(e in b)) throw Error(p(108, Ra(a) || "Unknown", e));
        return A({}, c, d);
      }
      function cg(a) {
        a = (a = a.stateNode) && a.__reactInternalMemoizedMergedChildContext || Vf;
        Xf = H.current;
        G(H, a);
        G(Wf, Wf.current);
        return true;
      }
      function dg(a, b, c) {
        var d = a.stateNode;
        if (!d) throw Error(p(169));
        c ? (a = bg(a, b, Xf), d.__reactInternalMemoizedMergedChildContext = a, E(Wf), E(H), G(H, a)) : E(Wf);
        G(Wf, c);
      }
      var eg = null;
      var fg = false;
      var gg = false;
      function hg(a) {
        null === eg ? eg = [a] : eg.push(a);
      }
      function ig(a) {
        fg = true;
        hg(a);
      }
      function jg() {
        if (!gg && null !== eg) {
          gg = true;
          var a = 0, b = C;
          try {
            var c = eg;
            for (C = 1; a < c.length; a++) {
              var d = c[a];
              do
                d = d(true);
              while (null !== d);
            }
            eg = null;
            fg = false;
          } catch (e) {
            throw null !== eg && (eg = eg.slice(a + 1)), ac(fc, jg), e;
          } finally {
            C = b, gg = false;
          }
        }
        return null;
      }
      var kg = [];
      var lg = 0;
      var mg = null;
      var ng = 0;
      var og = [];
      var pg = 0;
      var qg = null;
      var rg = 1;
      var sg = "";
      function tg(a, b) {
        kg[lg++] = ng;
        kg[lg++] = mg;
        mg = a;
        ng = b;
      }
      function ug(a, b, c) {
        og[pg++] = rg;
        og[pg++] = sg;
        og[pg++] = qg;
        qg = a;
        var d = rg;
        a = sg;
        var e = 32 - oc(d) - 1;
        d &= ~(1 << e);
        c += 1;
        var f = 32 - oc(b) + e;
        if (30 < f) {
          var g = e - e % 5;
          f = (d & (1 << g) - 1).toString(32);
          d >>= g;
          e -= g;
          rg = 1 << 32 - oc(b) + e | c << e | d;
          sg = f + a;
        } else rg = 1 << f | c << e | d, sg = a;
      }
      function vg(a) {
        null !== a.return && (tg(a, 1), ug(a, 1, 0));
      }
      function wg(a) {
        for (; a === mg; ) mg = kg[--lg], kg[lg] = null, ng = kg[--lg], kg[lg] = null;
        for (; a === qg; ) qg = og[--pg], og[pg] = null, sg = og[--pg], og[pg] = null, rg = og[--pg], og[pg] = null;
      }
      var xg = null;
      var yg = null;
      var I = false;
      var zg = null;
      function Ag(a, b) {
        var c = Bg(5, null, null, 0);
        c.elementType = "DELETED";
        c.stateNode = b;
        c.return = a;
        b = a.deletions;
        null === b ? (a.deletions = [c], a.flags |= 16) : b.push(c);
      }
      function Cg(a, b) {
        switch (a.tag) {
          case 5:
            var c = a.type;
            b = 1 !== b.nodeType || c.toLowerCase() !== b.nodeName.toLowerCase() ? null : b;
            return null !== b ? (a.stateNode = b, xg = a, yg = Lf(b.firstChild), true) : false;
          case 6:
            return b = "" === a.pendingProps || 3 !== b.nodeType ? null : b, null !== b ? (a.stateNode = b, xg = a, yg = null, true) : false;
          case 13:
            return b = 8 !== b.nodeType ? null : b, null !== b ? (c = null !== qg ? { id: rg, overflow: sg } : null, a.memoizedState = { dehydrated: b, treeContext: c, retryLane: 1073741824 }, c = Bg(18, null, null, 0), c.stateNode = b, c.return = a, a.child = c, xg = a, yg = null, true) : false;
          default:
            return false;
        }
      }
      function Dg(a) {
        return 0 !== (a.mode & 1) && 0 === (a.flags & 128);
      }
      function Eg(a) {
        if (I) {
          var b = yg;
          if (b) {
            var c = b;
            if (!Cg(a, b)) {
              if (Dg(a)) throw Error(p(418));
              b = Lf(c.nextSibling);
              var d = xg;
              b && Cg(a, b) ? Ag(d, c) : (a.flags = a.flags & -4097 | 2, I = false, xg = a);
            }
          } else {
            if (Dg(a)) throw Error(p(418));
            a.flags = a.flags & -4097 | 2;
            I = false;
            xg = a;
          }
        }
      }
      function Fg(a) {
        for (a = a.return; null !== a && 5 !== a.tag && 3 !== a.tag && 13 !== a.tag; ) a = a.return;
        xg = a;
      }
      function Gg(a) {
        if (a !== xg) return false;
        if (!I) return Fg(a), I = true, false;
        var b;
        (b = 3 !== a.tag) && !(b = 5 !== a.tag) && (b = a.type, b = "head" !== b && "body" !== b && !Ef(a.type, a.memoizedProps));
        if (b && (b = yg)) {
          if (Dg(a)) throw Hg(), Error(p(418));
          for (; b; ) Ag(a, b), b = Lf(b.nextSibling);
        }
        Fg(a);
        if (13 === a.tag) {
          a = a.memoizedState;
          a = null !== a ? a.dehydrated : null;
          if (!a) throw Error(p(317));
          a: {
            a = a.nextSibling;
            for (b = 0; a; ) {
              if (8 === a.nodeType) {
                var c = a.data;
                if ("/$" === c) {
                  if (0 === b) {
                    yg = Lf(a.nextSibling);
                    break a;
                  }
                  b--;
                } else "$" !== c && "$!" !== c && "$?" !== c || b++;
              }
              a = a.nextSibling;
            }
            yg = null;
          }
        } else yg = xg ? Lf(a.stateNode.nextSibling) : null;
        return true;
      }
      function Hg() {
        for (var a = yg; a; ) a = Lf(a.nextSibling);
      }
      function Ig() {
        yg = xg = null;
        I = false;
      }
      function Jg(a) {
        null === zg ? zg = [a] : zg.push(a);
      }
      var Kg = ua.ReactCurrentBatchConfig;
      function Lg(a, b, c) {
        a = c.ref;
        if (null !== a && "function" !== typeof a && "object" !== typeof a) {
          if (c._owner) {
            c = c._owner;
            if (c) {
              if (1 !== c.tag) throw Error(p(309));
              var d = c.stateNode;
            }
            if (!d) throw Error(p(147, a));
            var e = d, f = "" + a;
            if (null !== b && null !== b.ref && "function" === typeof b.ref && b.ref._stringRef === f) return b.ref;
            b = function(a2) {
              var b2 = e.refs;
              null === a2 ? delete b2[f] : b2[f] = a2;
            };
            b._stringRef = f;
            return b;
          }
          if ("string" !== typeof a) throw Error(p(284));
          if (!c._owner) throw Error(p(290, a));
        }
        return a;
      }
      function Mg(a, b) {
        a = Object.prototype.toString.call(b);
        throw Error(p(31, "[object Object]" === a ? "object with keys {" + Object.keys(b).join(", ") + "}" : a));
      }
      function Ng(a) {
        var b = a._init;
        return b(a._payload);
      }
      function Og(a) {
        function b(b2, c2) {
          if (a) {
            var d2 = b2.deletions;
            null === d2 ? (b2.deletions = [c2], b2.flags |= 16) : d2.push(c2);
          }
        }
        function c(c2, d2) {
          if (!a) return null;
          for (; null !== d2; ) b(c2, d2), d2 = d2.sibling;
          return null;
        }
        function d(a2, b2) {
          for (a2 = /* @__PURE__ */ new Map(); null !== b2; ) null !== b2.key ? a2.set(b2.key, b2) : a2.set(b2.index, b2), b2 = b2.sibling;
          return a2;
        }
        function e(a2, b2) {
          a2 = Pg(a2, b2);
          a2.index = 0;
          a2.sibling = null;
          return a2;
        }
        function f(b2, c2, d2) {
          b2.index = d2;
          if (!a) return b2.flags |= 1048576, c2;
          d2 = b2.alternate;
          if (null !== d2) return d2 = d2.index, d2 < c2 ? (b2.flags |= 2, c2) : d2;
          b2.flags |= 2;
          return c2;
        }
        function g(b2) {
          a && null === b2.alternate && (b2.flags |= 2);
          return b2;
        }
        function h(a2, b2, c2, d2) {
          if (null === b2 || 6 !== b2.tag) return b2 = Qg(c2, a2.mode, d2), b2.return = a2, b2;
          b2 = e(b2, c2);
          b2.return = a2;
          return b2;
        }
        function k(a2, b2, c2, d2) {
          var f2 = c2.type;
          if (f2 === ya) return m(a2, b2, c2.props.children, d2, c2.key);
          if (null !== b2 && (b2.elementType === f2 || "object" === typeof f2 && null !== f2 && f2.$$typeof === Ha && Ng(f2) === b2.type)) return d2 = e(b2, c2.props), d2.ref = Lg(a2, b2, c2), d2.return = a2, d2;
          d2 = Rg(c2.type, c2.key, c2.props, null, a2.mode, d2);
          d2.ref = Lg(a2, b2, c2);
          d2.return = a2;
          return d2;
        }
        function l(a2, b2, c2, d2) {
          if (null === b2 || 4 !== b2.tag || b2.stateNode.containerInfo !== c2.containerInfo || b2.stateNode.implementation !== c2.implementation) return b2 = Sg(c2, a2.mode, d2), b2.return = a2, b2;
          b2 = e(b2, c2.children || []);
          b2.return = a2;
          return b2;
        }
        function m(a2, b2, c2, d2, f2) {
          if (null === b2 || 7 !== b2.tag) return b2 = Tg(c2, a2.mode, d2, f2), b2.return = a2, b2;
          b2 = e(b2, c2);
          b2.return = a2;
          return b2;
        }
        function q(a2, b2, c2) {
          if ("string" === typeof b2 && "" !== b2 || "number" === typeof b2) return b2 = Qg("" + b2, a2.mode, c2), b2.return = a2, b2;
          if ("object" === typeof b2 && null !== b2) {
            switch (b2.$$typeof) {
              case va:
                return c2 = Rg(b2.type, b2.key, b2.props, null, a2.mode, c2), c2.ref = Lg(a2, null, b2), c2.return = a2, c2;
              case wa:
                return b2 = Sg(b2, a2.mode, c2), b2.return = a2, b2;
              case Ha:
                var d2 = b2._init;
                return q(a2, d2(b2._payload), c2);
            }
            if (eb(b2) || Ka(b2)) return b2 = Tg(b2, a2.mode, c2, null), b2.return = a2, b2;
            Mg(a2, b2);
          }
          return null;
        }
        function r(a2, b2, c2, d2) {
          var e2 = null !== b2 ? b2.key : null;
          if ("string" === typeof c2 && "" !== c2 || "number" === typeof c2) return null !== e2 ? null : h(a2, b2, "" + c2, d2);
          if ("object" === typeof c2 && null !== c2) {
            switch (c2.$$typeof) {
              case va:
                return c2.key === e2 ? k(a2, b2, c2, d2) : null;
              case wa:
                return c2.key === e2 ? l(a2, b2, c2, d2) : null;
              case Ha:
                return e2 = c2._init, r(
                  a2,
                  b2,
                  e2(c2._payload),
                  d2
                );
            }
            if (eb(c2) || Ka(c2)) return null !== e2 ? null : m(a2, b2, c2, d2, null);
            Mg(a2, c2);
          }
          return null;
        }
        function y(a2, b2, c2, d2, e2) {
          if ("string" === typeof d2 && "" !== d2 || "number" === typeof d2) return a2 = a2.get(c2) || null, h(b2, a2, "" + d2, e2);
          if ("object" === typeof d2 && null !== d2) {
            switch (d2.$$typeof) {
              case va:
                return a2 = a2.get(null === d2.key ? c2 : d2.key) || null, k(b2, a2, d2, e2);
              case wa:
                return a2 = a2.get(null === d2.key ? c2 : d2.key) || null, l(b2, a2, d2, e2);
              case Ha:
                var f2 = d2._init;
                return y(a2, b2, c2, f2(d2._payload), e2);
            }
            if (eb(d2) || Ka(d2)) return a2 = a2.get(c2) || null, m(b2, a2, d2, e2, null);
            Mg(b2, d2);
          }
          return null;
        }
        function n(e2, g2, h2, k2) {
          for (var l2 = null, m2 = null, u = g2, w = g2 = 0, x = null; null !== u && w < h2.length; w++) {
            u.index > w ? (x = u, u = null) : x = u.sibling;
            var n2 = r(e2, u, h2[w], k2);
            if (null === n2) {
              null === u && (u = x);
              break;
            }
            a && u && null === n2.alternate && b(e2, u);
            g2 = f(n2, g2, w);
            null === m2 ? l2 = n2 : m2.sibling = n2;
            m2 = n2;
            u = x;
          }
          if (w === h2.length) return c(e2, u), I && tg(e2, w), l2;
          if (null === u) {
            for (; w < h2.length; w++) u = q(e2, h2[w], k2), null !== u && (g2 = f(u, g2, w), null === m2 ? l2 = u : m2.sibling = u, m2 = u);
            I && tg(e2, w);
            return l2;
          }
          for (u = d(e2, u); w < h2.length; w++) x = y(u, e2, w, h2[w], k2), null !== x && (a && null !== x.alternate && u.delete(null === x.key ? w : x.key), g2 = f(x, g2, w), null === m2 ? l2 = x : m2.sibling = x, m2 = x);
          a && u.forEach(function(a2) {
            return b(e2, a2);
          });
          I && tg(e2, w);
          return l2;
        }
        function t(e2, g2, h2, k2) {
          var l2 = Ka(h2);
          if ("function" !== typeof l2) throw Error(p(150));
          h2 = l2.call(h2);
          if (null == h2) throw Error(p(151));
          for (var u = l2 = null, m2 = g2, w = g2 = 0, x = null, n2 = h2.next(); null !== m2 && !n2.done; w++, n2 = h2.next()) {
            m2.index > w ? (x = m2, m2 = null) : x = m2.sibling;
            var t2 = r(e2, m2, n2.value, k2);
            if (null === t2) {
              null === m2 && (m2 = x);
              break;
            }
            a && m2 && null === t2.alternate && b(e2, m2);
            g2 = f(t2, g2, w);
            null === u ? l2 = t2 : u.sibling = t2;
            u = t2;
            m2 = x;
          }
          if (n2.done) return c(
            e2,
            m2
          ), I && tg(e2, w), l2;
          if (null === m2) {
            for (; !n2.done; w++, n2 = h2.next()) n2 = q(e2, n2.value, k2), null !== n2 && (g2 = f(n2, g2, w), null === u ? l2 = n2 : u.sibling = n2, u = n2);
            I && tg(e2, w);
            return l2;
          }
          for (m2 = d(e2, m2); !n2.done; w++, n2 = h2.next()) n2 = y(m2, e2, w, n2.value, k2), null !== n2 && (a && null !== n2.alternate && m2.delete(null === n2.key ? w : n2.key), g2 = f(n2, g2, w), null === u ? l2 = n2 : u.sibling = n2, u = n2);
          a && m2.forEach(function(a2) {
            return b(e2, a2);
          });
          I && tg(e2, w);
          return l2;
        }
        function J(a2, d2, f2, h2) {
          "object" === typeof f2 && null !== f2 && f2.type === ya && null === f2.key && (f2 = f2.props.children);
          if ("object" === typeof f2 && null !== f2) {
            switch (f2.$$typeof) {
              case va:
                a: {
                  for (var k2 = f2.key, l2 = d2; null !== l2; ) {
                    if (l2.key === k2) {
                      k2 = f2.type;
                      if (k2 === ya) {
                        if (7 === l2.tag) {
                          c(a2, l2.sibling);
                          d2 = e(l2, f2.props.children);
                          d2.return = a2;
                          a2 = d2;
                          break a;
                        }
                      } else if (l2.elementType === k2 || "object" === typeof k2 && null !== k2 && k2.$$typeof === Ha && Ng(k2) === l2.type) {
                        c(a2, l2.sibling);
                        d2 = e(l2, f2.props);
                        d2.ref = Lg(a2, l2, f2);
                        d2.return = a2;
                        a2 = d2;
                        break a;
                      }
                      c(a2, l2);
                      break;
                    } else b(a2, l2);
                    l2 = l2.sibling;
                  }
                  f2.type === ya ? (d2 = Tg(f2.props.children, a2.mode, h2, f2.key), d2.return = a2, a2 = d2) : (h2 = Rg(f2.type, f2.key, f2.props, null, a2.mode, h2), h2.ref = Lg(a2, d2, f2), h2.return = a2, a2 = h2);
                }
                return g(a2);
              case wa:
                a: {
                  for (l2 = f2.key; null !== d2; ) {
                    if (d2.key === l2) if (4 === d2.tag && d2.stateNode.containerInfo === f2.containerInfo && d2.stateNode.implementation === f2.implementation) {
                      c(a2, d2.sibling);
                      d2 = e(d2, f2.children || []);
                      d2.return = a2;
                      a2 = d2;
                      break a;
                    } else {
                      c(a2, d2);
                      break;
                    }
                    else b(a2, d2);
                    d2 = d2.sibling;
                  }
                  d2 = Sg(f2, a2.mode, h2);
                  d2.return = a2;
                  a2 = d2;
                }
                return g(a2);
              case Ha:
                return l2 = f2._init, J(a2, d2, l2(f2._payload), h2);
            }
            if (eb(f2)) return n(a2, d2, f2, h2);
            if (Ka(f2)) return t(a2, d2, f2, h2);
            Mg(a2, f2);
          }
          return "string" === typeof f2 && "" !== f2 || "number" === typeof f2 ? (f2 = "" + f2, null !== d2 && 6 === d2.tag ? (c(a2, d2.sibling), d2 = e(d2, f2), d2.return = a2, a2 = d2) : (c(a2, d2), d2 = Qg(f2, a2.mode, h2), d2.return = a2, a2 = d2), g(a2)) : c(a2, d2);
        }
        return J;
      }
      var Ug = Og(true);
      var Vg = Og(false);
      var Wg = Uf(null);
      var Xg = null;
      var Yg = null;
      var Zg = null;
      function $g() {
        Zg = Yg = Xg = null;
      }
      function ah(a) {
        var b = Wg.current;
        E(Wg);
        a._currentValue = b;
      }
      function bh(a, b, c) {
        for (; null !== a; ) {
          var d = a.alternate;
          (a.childLanes & b) !== b ? (a.childLanes |= b, null !== d && (d.childLanes |= b)) : null !== d && (d.childLanes & b) !== b && (d.childLanes |= b);
          if (a === c) break;
          a = a.return;
        }
      }
      function ch(a, b) {
        Xg = a;
        Zg = Yg = null;
        a = a.dependencies;
        null !== a && null !== a.firstContext && (0 !== (a.lanes & b) && (dh = true), a.firstContext = null);
      }
      function eh(a) {
        var b = a._currentValue;
        if (Zg !== a) if (a = { context: a, memoizedValue: b, next: null }, null === Yg) {
          if (null === Xg) throw Error(p(308));
          Yg = a;
          Xg.dependencies = { lanes: 0, firstContext: a };
        } else Yg = Yg.next = a;
        return b;
      }
      var fh = null;
      function gh(a) {
        null === fh ? fh = [a] : fh.push(a);
      }
      function hh(a, b, c, d) {
        var e = b.interleaved;
        null === e ? (c.next = c, gh(b)) : (c.next = e.next, e.next = c);
        b.interleaved = c;
        return ih(a, d);
      }
      function ih(a, b) {
        a.lanes |= b;
        var c = a.alternate;
        null !== c && (c.lanes |= b);
        c = a;
        for (a = a.return; null !== a; ) a.childLanes |= b, c = a.alternate, null !== c && (c.childLanes |= b), c = a, a = a.return;
        return 3 === c.tag ? c.stateNode : null;
      }
      var jh = false;
      function kh(a) {
        a.updateQueue = { baseState: a.memoizedState, firstBaseUpdate: null, lastBaseUpdate: null, shared: { pending: null, interleaved: null, lanes: 0 }, effects: null };
      }
      function lh(a, b) {
        a = a.updateQueue;
        b.updateQueue === a && (b.updateQueue = { baseState: a.baseState, firstBaseUpdate: a.firstBaseUpdate, lastBaseUpdate: a.lastBaseUpdate, shared: a.shared, effects: a.effects });
      }
      function mh(a, b) {
        return { eventTime: a, lane: b, tag: 0, payload: null, callback: null, next: null };
      }
      function nh(a, b, c) {
        var d = a.updateQueue;
        if (null === d) return null;
        d = d.shared;
        if (0 !== (K & 2)) {
          var e = d.pending;
          null === e ? b.next = b : (b.next = e.next, e.next = b);
          d.pending = b;
          return ih(a, c);
        }
        e = d.interleaved;
        null === e ? (b.next = b, gh(d)) : (b.next = e.next, e.next = b);
        d.interleaved = b;
        return ih(a, c);
      }
      function oh(a, b, c) {
        b = b.updateQueue;
        if (null !== b && (b = b.shared, 0 !== (c & 4194240))) {
          var d = b.lanes;
          d &= a.pendingLanes;
          c |= d;
          b.lanes = c;
          Cc(a, c);
        }
      }
      function ph(a, b) {
        var c = a.updateQueue, d = a.alternate;
        if (null !== d && (d = d.updateQueue, c === d)) {
          var e = null, f = null;
          c = c.firstBaseUpdate;
          if (null !== c) {
            do {
              var g = { eventTime: c.eventTime, lane: c.lane, tag: c.tag, payload: c.payload, callback: c.callback, next: null };
              null === f ? e = f = g : f = f.next = g;
              c = c.next;
            } while (null !== c);
            null === f ? e = f = b : f = f.next = b;
          } else e = f = b;
          c = { baseState: d.baseState, firstBaseUpdate: e, lastBaseUpdate: f, shared: d.shared, effects: d.effects };
          a.updateQueue = c;
          return;
        }
        a = c.lastBaseUpdate;
        null === a ? c.firstBaseUpdate = b : a.next = b;
        c.lastBaseUpdate = b;
      }
      function qh(a, b, c, d) {
        var e = a.updateQueue;
        jh = false;
        var f = e.firstBaseUpdate, g = e.lastBaseUpdate, h = e.shared.pending;
        if (null !== h) {
          e.shared.pending = null;
          var k = h, l = k.next;
          k.next = null;
          null === g ? f = l : g.next = l;
          g = k;
          var m = a.alternate;
          null !== m && (m = m.updateQueue, h = m.lastBaseUpdate, h !== g && (null === h ? m.firstBaseUpdate = l : h.next = l, m.lastBaseUpdate = k));
        }
        if (null !== f) {
          var q = e.baseState;
          g = 0;
          m = l = k = null;
          h = f;
          do {
            var r = h.lane, y = h.eventTime;
            if ((d & r) === r) {
              null !== m && (m = m.next = {
                eventTime: y,
                lane: 0,
                tag: h.tag,
                payload: h.payload,
                callback: h.callback,
                next: null
              });
              a: {
                var n = a, t = h;
                r = b;
                y = c;
                switch (t.tag) {
                  case 1:
                    n = t.payload;
                    if ("function" === typeof n) {
                      q = n.call(y, q, r);
                      break a;
                    }
                    q = n;
                    break a;
                  case 3:
                    n.flags = n.flags & -65537 | 128;
                  case 0:
                    n = t.payload;
                    r = "function" === typeof n ? n.call(y, q, r) : n;
                    if (null === r || void 0 === r) break a;
                    q = A({}, q, r);
                    break a;
                  case 2:
                    jh = true;
                }
              }
              null !== h.callback && 0 !== h.lane && (a.flags |= 64, r = e.effects, null === r ? e.effects = [h] : r.push(h));
            } else y = { eventTime: y, lane: r, tag: h.tag, payload: h.payload, callback: h.callback, next: null }, null === m ? (l = m = y, k = q) : m = m.next = y, g |= r;
            h = h.next;
            if (null === h) if (h = e.shared.pending, null === h) break;
            else r = h, h = r.next, r.next = null, e.lastBaseUpdate = r, e.shared.pending = null;
          } while (1);
          null === m && (k = q);
          e.baseState = k;
          e.firstBaseUpdate = l;
          e.lastBaseUpdate = m;
          b = e.shared.interleaved;
          if (null !== b) {
            e = b;
            do
              g |= e.lane, e = e.next;
            while (e !== b);
          } else null === f && (e.shared.lanes = 0);
          rh |= g;
          a.lanes = g;
          a.memoizedState = q;
        }
      }
      function sh(a, b, c) {
        a = b.effects;
        b.effects = null;
        if (null !== a) for (b = 0; b < a.length; b++) {
          var d = a[b], e = d.callback;
          if (null !== e) {
            d.callback = null;
            d = c;
            if ("function" !== typeof e) throw Error(p(191, e));
            e.call(d);
          }
        }
      }
      var th = {};
      var uh = Uf(th);
      var vh = Uf(th);
      var wh = Uf(th);
      function xh(a) {
        if (a === th) throw Error(p(174));
        return a;
      }
      function yh(a, b) {
        G(wh, b);
        G(vh, a);
        G(uh, th);
        a = b.nodeType;
        switch (a) {
          case 9:
          case 11:
            b = (b = b.documentElement) ? b.namespaceURI : lb(null, "");
            break;
          default:
            a = 8 === a ? b.parentNode : b, b = a.namespaceURI || null, a = a.tagName, b = lb(b, a);
        }
        E(uh);
        G(uh, b);
      }
      function zh() {
        E(uh);
        E(vh);
        E(wh);
      }
      function Ah(a) {
        xh(wh.current);
        var b = xh(uh.current);
        var c = lb(b, a.type);
        b !== c && (G(vh, a), G(uh, c));
      }
      function Bh(a) {
        vh.current === a && (E(uh), E(vh));
      }
      var L = Uf(0);
      function Ch(a) {
        for (var b = a; null !== b; ) {
          if (13 === b.tag) {
            var c = b.memoizedState;
            if (null !== c && (c = c.dehydrated, null === c || "$?" === c.data || "$!" === c.data)) return b;
          } else if (19 === b.tag && void 0 !== b.memoizedProps.revealOrder) {
            if (0 !== (b.flags & 128)) return b;
          } else if (null !== b.child) {
            b.child.return = b;
            b = b.child;
            continue;
          }
          if (b === a) break;
          for (; null === b.sibling; ) {
            if (null === b.return || b.return === a) return null;
            b = b.return;
          }
          b.sibling.return = b.return;
          b = b.sibling;
        }
        return null;
      }
      var Dh = [];
      function Eh() {
        for (var a = 0; a < Dh.length; a++) Dh[a]._workInProgressVersionPrimary = null;
        Dh.length = 0;
      }
      var Fh = ua.ReactCurrentDispatcher;
      var Gh = ua.ReactCurrentBatchConfig;
      var Hh = 0;
      var M = null;
      var N = null;
      var O = null;
      var Ih = false;
      var Jh = false;
      var Kh = 0;
      var Lh = 0;
      function P() {
        throw Error(p(321));
      }
      function Mh(a, b) {
        if (null === b) return false;
        for (var c = 0; c < b.length && c < a.length; c++) if (!He(a[c], b[c])) return false;
        return true;
      }
      function Nh(a, b, c, d, e, f) {
        Hh = f;
        M = b;
        b.memoizedState = null;
        b.updateQueue = null;
        b.lanes = 0;
        Fh.current = null === a || null === a.memoizedState ? Oh : Ph;
        a = c(d, e);
        if (Jh) {
          f = 0;
          do {
            Jh = false;
            Kh = 0;
            if (25 <= f) throw Error(p(301));
            f += 1;
            O = N = null;
            b.updateQueue = null;
            Fh.current = Qh;
            a = c(d, e);
          } while (Jh);
        }
        Fh.current = Rh;
        b = null !== N && null !== N.next;
        Hh = 0;
        O = N = M = null;
        Ih = false;
        if (b) throw Error(p(300));
        return a;
      }
      function Sh() {
        var a = 0 !== Kh;
        Kh = 0;
        return a;
      }
      function Th() {
        var a = { memoizedState: null, baseState: null, baseQueue: null, queue: null, next: null };
        null === O ? M.memoizedState = O = a : O = O.next = a;
        return O;
      }
      function Uh() {
        if (null === N) {
          var a = M.alternate;
          a = null !== a ? a.memoizedState : null;
        } else a = N.next;
        var b = null === O ? M.memoizedState : O.next;
        if (null !== b) O = b, N = a;
        else {
          if (null === a) throw Error(p(310));
          N = a;
          a = { memoizedState: N.memoizedState, baseState: N.baseState, baseQueue: N.baseQueue, queue: N.queue, next: null };
          null === O ? M.memoizedState = O = a : O = O.next = a;
        }
        return O;
      }
      function Vh(a, b) {
        return "function" === typeof b ? b(a) : b;
      }
      function Wh(a) {
        var b = Uh(), c = b.queue;
        if (null === c) throw Error(p(311));
        c.lastRenderedReducer = a;
        var d = N, e = d.baseQueue, f = c.pending;
        if (null !== f) {
          if (null !== e) {
            var g = e.next;
            e.next = f.next;
            f.next = g;
          }
          d.baseQueue = e = f;
          c.pending = null;
        }
        if (null !== e) {
          f = e.next;
          d = d.baseState;
          var h = g = null, k = null, l = f;
          do {
            var m = l.lane;
            if ((Hh & m) === m) null !== k && (k = k.next = { lane: 0, action: l.action, hasEagerState: l.hasEagerState, eagerState: l.eagerState, next: null }), d = l.hasEagerState ? l.eagerState : a(d, l.action);
            else {
              var q = {
                lane: m,
                action: l.action,
                hasEagerState: l.hasEagerState,
                eagerState: l.eagerState,
                next: null
              };
              null === k ? (h = k = q, g = d) : k = k.next = q;
              M.lanes |= m;
              rh |= m;
            }
            l = l.next;
          } while (null !== l && l !== f);
          null === k ? g = d : k.next = h;
          He(d, b.memoizedState) || (dh = true);
          b.memoizedState = d;
          b.baseState = g;
          b.baseQueue = k;
          c.lastRenderedState = d;
        }
        a = c.interleaved;
        if (null !== a) {
          e = a;
          do
            f = e.lane, M.lanes |= f, rh |= f, e = e.next;
          while (e !== a);
        } else null === e && (c.lanes = 0);
        return [b.memoizedState, c.dispatch];
      }
      function Xh(a) {
        var b = Uh(), c = b.queue;
        if (null === c) throw Error(p(311));
        c.lastRenderedReducer = a;
        var d = c.dispatch, e = c.pending, f = b.memoizedState;
        if (null !== e) {
          c.pending = null;
          var g = e = e.next;
          do
            f = a(f, g.action), g = g.next;
          while (g !== e);
          He(f, b.memoizedState) || (dh = true);
          b.memoizedState = f;
          null === b.baseQueue && (b.baseState = f);
          c.lastRenderedState = f;
        }
        return [f, d];
      }
      function Yh() {
      }
      function Zh(a, b) {
        var c = M, d = Uh(), e = b(), f = !He(d.memoizedState, e);
        f && (d.memoizedState = e, dh = true);
        d = d.queue;
        $h(ai.bind(null, c, d, a), [a]);
        if (d.getSnapshot !== b || f || null !== O && O.memoizedState.tag & 1) {
          c.flags |= 2048;
          bi(9, ci.bind(null, c, d, e, b), void 0, null);
          if (null === Q) throw Error(p(349));
          0 !== (Hh & 30) || di(c, b, e);
        }
        return e;
      }
      function di(a, b, c) {
        a.flags |= 16384;
        a = { getSnapshot: b, value: c };
        b = M.updateQueue;
        null === b ? (b = { lastEffect: null, stores: null }, M.updateQueue = b, b.stores = [a]) : (c = b.stores, null === c ? b.stores = [a] : c.push(a));
      }
      function ci(a, b, c, d) {
        b.value = c;
        b.getSnapshot = d;
        ei(b) && fi(a);
      }
      function ai(a, b, c) {
        return c(function() {
          ei(b) && fi(a);
        });
      }
      function ei(a) {
        var b = a.getSnapshot;
        a = a.value;
        try {
          var c = b();
          return !He(a, c);
        } catch (d) {
          return true;
        }
      }
      function fi(a) {
        var b = ih(a, 1);
        null !== b && gi(b, a, 1, -1);
      }
      function hi(a) {
        var b = Th();
        "function" === typeof a && (a = a());
        b.memoizedState = b.baseState = a;
        a = { pending: null, interleaved: null, lanes: 0, dispatch: null, lastRenderedReducer: Vh, lastRenderedState: a };
        b.queue = a;
        a = a.dispatch = ii.bind(null, M, a);
        return [b.memoizedState, a];
      }
      function bi(a, b, c, d) {
        a = { tag: a, create: b, destroy: c, deps: d, next: null };
        b = M.updateQueue;
        null === b ? (b = { lastEffect: null, stores: null }, M.updateQueue = b, b.lastEffect = a.next = a) : (c = b.lastEffect, null === c ? b.lastEffect = a.next = a : (d = c.next, c.next = a, a.next = d, b.lastEffect = a));
        return a;
      }
      function ji() {
        return Uh().memoizedState;
      }
      function ki(a, b, c, d) {
        var e = Th();
        M.flags |= a;
        e.memoizedState = bi(1 | b, c, void 0, void 0 === d ? null : d);
      }
      function li(a, b, c, d) {
        var e = Uh();
        d = void 0 === d ? null : d;
        var f = void 0;
        if (null !== N) {
          var g = N.memoizedState;
          f = g.destroy;
          if (null !== d && Mh(d, g.deps)) {
            e.memoizedState = bi(b, c, f, d);
            return;
          }
        }
        M.flags |= a;
        e.memoizedState = bi(1 | b, c, f, d);
      }
      function mi(a, b) {
        return ki(8390656, 8, a, b);
      }
      function $h(a, b) {
        return li(2048, 8, a, b);
      }
      function ni(a, b) {
        return li(4, 2, a, b);
      }
      function oi(a, b) {
        return li(4, 4, a, b);
      }
      function pi(a, b) {
        if ("function" === typeof b) return a = a(), b(a), function() {
          b(null);
        };
        if (null !== b && void 0 !== b) return a = a(), b.current = a, function() {
          b.current = null;
        };
      }
      function qi(a, b, c) {
        c = null !== c && void 0 !== c ? c.concat([a]) : null;
        return li(4, 4, pi.bind(null, b, a), c);
      }
      function ri() {
      }
      function si(a, b) {
        var c = Uh();
        b = void 0 === b ? null : b;
        var d = c.memoizedState;
        if (null !== d && null !== b && Mh(b, d[1])) return d[0];
        c.memoizedState = [a, b];
        return a;
      }
      function ti(a, b) {
        var c = Uh();
        b = void 0 === b ? null : b;
        var d = c.memoizedState;
        if (null !== d && null !== b && Mh(b, d[1])) return d[0];
        a = a();
        c.memoizedState = [a, b];
        return a;
      }
      function ui(a, b, c) {
        if (0 === (Hh & 21)) return a.baseState && (a.baseState = false, dh = true), a.memoizedState = c;
        He(c, b) || (c = yc(), M.lanes |= c, rh |= c, a.baseState = true);
        return b;
      }
      function vi(a, b) {
        var c = C;
        C = 0 !== c && 4 > c ? c : 4;
        a(true);
        var d = Gh.transition;
        Gh.transition = {};
        try {
          a(false), b();
        } finally {
          C = c, Gh.transition = d;
        }
      }
      function wi() {
        return Uh().memoizedState;
      }
      function xi(a, b, c) {
        var d = yi(a);
        c = { lane: d, action: c, hasEagerState: false, eagerState: null, next: null };
        if (zi(a)) Ai(b, c);
        else if (c = hh(a, b, c, d), null !== c) {
          var e = R();
          gi(c, a, d, e);
          Bi(c, b, d);
        }
      }
      function ii(a, b, c) {
        var d = yi(a), e = { lane: d, action: c, hasEagerState: false, eagerState: null, next: null };
        if (zi(a)) Ai(b, e);
        else {
          var f = a.alternate;
          if (0 === a.lanes && (null === f || 0 === f.lanes) && (f = b.lastRenderedReducer, null !== f)) try {
            var g = b.lastRenderedState, h = f(g, c);
            e.hasEagerState = true;
            e.eagerState = h;
            if (He(h, g)) {
              var k = b.interleaved;
              null === k ? (e.next = e, gh(b)) : (e.next = k.next, k.next = e);
              b.interleaved = e;
              return;
            }
          } catch (l) {
          } finally {
          }
          c = hh(a, b, e, d);
          null !== c && (e = R(), gi(c, a, d, e), Bi(c, b, d));
        }
      }
      function zi(a) {
        var b = a.alternate;
        return a === M || null !== b && b === M;
      }
      function Ai(a, b) {
        Jh = Ih = true;
        var c = a.pending;
        null === c ? b.next = b : (b.next = c.next, c.next = b);
        a.pending = b;
      }
      function Bi(a, b, c) {
        if (0 !== (c & 4194240)) {
          var d = b.lanes;
          d &= a.pendingLanes;
          c |= d;
          b.lanes = c;
          Cc(a, c);
        }
      }
      var Rh = { readContext: eh, useCallback: P, useContext: P, useEffect: P, useImperativeHandle: P, useInsertionEffect: P, useLayoutEffect: P, useMemo: P, useReducer: P, useRef: P, useState: P, useDebugValue: P, useDeferredValue: P, useTransition: P, useMutableSource: P, useSyncExternalStore: P, useId: P, unstable_isNewReconciler: false };
      var Oh = { readContext: eh, useCallback: function(a, b) {
        Th().memoizedState = [a, void 0 === b ? null : b];
        return a;
      }, useContext: eh, useEffect: mi, useImperativeHandle: function(a, b, c) {
        c = null !== c && void 0 !== c ? c.concat([a]) : null;
        return ki(
          4194308,
          4,
          pi.bind(null, b, a),
          c
        );
      }, useLayoutEffect: function(a, b) {
        return ki(4194308, 4, a, b);
      }, useInsertionEffect: function(a, b) {
        return ki(4, 2, a, b);
      }, useMemo: function(a, b) {
        var c = Th();
        b = void 0 === b ? null : b;
        a = a();
        c.memoizedState = [a, b];
        return a;
      }, useReducer: function(a, b, c) {
        var d = Th();
        b = void 0 !== c ? c(b) : b;
        d.memoizedState = d.baseState = b;
        a = { pending: null, interleaved: null, lanes: 0, dispatch: null, lastRenderedReducer: a, lastRenderedState: b };
        d.queue = a;
        a = a.dispatch = xi.bind(null, M, a);
        return [d.memoizedState, a];
      }, useRef: function(a) {
        var b = Th();
        a = { current: a };
        return b.memoizedState = a;
      }, useState: hi, useDebugValue: ri, useDeferredValue: function(a) {
        return Th().memoizedState = a;
      }, useTransition: function() {
        var a = hi(false), b = a[0];
        a = vi.bind(null, a[1]);
        Th().memoizedState = a;
        return [b, a];
      }, useMutableSource: function() {
      }, useSyncExternalStore: function(a, b, c) {
        var d = M, e = Th();
        if (I) {
          if (void 0 === c) throw Error(p(407));
          c = c();
        } else {
          c = b();
          if (null === Q) throw Error(p(349));
          0 !== (Hh & 30) || di(d, b, c);
        }
        e.memoizedState = c;
        var f = { value: c, getSnapshot: b };
        e.queue = f;
        mi(ai.bind(
          null,
          d,
          f,
          a
        ), [a]);
        d.flags |= 2048;
        bi(9, ci.bind(null, d, f, c, b), void 0, null);
        return c;
      }, useId: function() {
        var a = Th(), b = Q.identifierPrefix;
        if (I) {
          var c = sg;
          var d = rg;
          c = (d & ~(1 << 32 - oc(d) - 1)).toString(32) + c;
          b = ":" + b + "R" + c;
          c = Kh++;
          0 < c && (b += "H" + c.toString(32));
          b += ":";
        } else c = Lh++, b = ":" + b + "r" + c.toString(32) + ":";
        return a.memoizedState = b;
      }, unstable_isNewReconciler: false };
      var Ph = {
        readContext: eh,
        useCallback: si,
        useContext: eh,
        useEffect: $h,
        useImperativeHandle: qi,
        useInsertionEffect: ni,
        useLayoutEffect: oi,
        useMemo: ti,
        useReducer: Wh,
        useRef: ji,
        useState: function() {
          return Wh(Vh);
        },
        useDebugValue: ri,
        useDeferredValue: function(a) {
          var b = Uh();
          return ui(b, N.memoizedState, a);
        },
        useTransition: function() {
          var a = Wh(Vh)[0], b = Uh().memoizedState;
          return [a, b];
        },
        useMutableSource: Yh,
        useSyncExternalStore: Zh,
        useId: wi,
        unstable_isNewReconciler: false
      };
      var Qh = { readContext: eh, useCallback: si, useContext: eh, useEffect: $h, useImperativeHandle: qi, useInsertionEffect: ni, useLayoutEffect: oi, useMemo: ti, useReducer: Xh, useRef: ji, useState: function() {
        return Xh(Vh);
      }, useDebugValue: ri, useDeferredValue: function(a) {
        var b = Uh();
        return null === N ? b.memoizedState = a : ui(b, N.memoizedState, a);
      }, useTransition: function() {
        var a = Xh(Vh)[0], b = Uh().memoizedState;
        return [a, b];
      }, useMutableSource: Yh, useSyncExternalStore: Zh, useId: wi, unstable_isNewReconciler: false };
      function Ci(a, b) {
        if (a && a.defaultProps) {
          b = A({}, b);
          a = a.defaultProps;
          for (var c in a) void 0 === b[c] && (b[c] = a[c]);
          return b;
        }
        return b;
      }
      function Di(a, b, c, d) {
        b = a.memoizedState;
        c = c(d, b);
        c = null === c || void 0 === c ? b : A({}, b, c);
        a.memoizedState = c;
        0 === a.lanes && (a.updateQueue.baseState = c);
      }
      var Ei = { isMounted: function(a) {
        return (a = a._reactInternals) ? Vb(a) === a : false;
      }, enqueueSetState: function(a, b, c) {
        a = a._reactInternals;
        var d = R(), e = yi(a), f = mh(d, e);
        f.payload = b;
        void 0 !== c && null !== c && (f.callback = c);
        b = nh(a, f, e);
        null !== b && (gi(b, a, e, d), oh(b, a, e));
      }, enqueueReplaceState: function(a, b, c) {
        a = a._reactInternals;
        var d = R(), e = yi(a), f = mh(d, e);
        f.tag = 1;
        f.payload = b;
        void 0 !== c && null !== c && (f.callback = c);
        b = nh(a, f, e);
        null !== b && (gi(b, a, e, d), oh(b, a, e));
      }, enqueueForceUpdate: function(a, b) {
        a = a._reactInternals;
        var c = R(), d = yi(a), e = mh(c, d);
        e.tag = 2;
        void 0 !== b && null !== b && (e.callback = b);
        b = nh(a, e, d);
        null !== b && (gi(b, a, d, c), oh(b, a, d));
      } };
      function Fi(a, b, c, d, e, f, g) {
        a = a.stateNode;
        return "function" === typeof a.shouldComponentUpdate ? a.shouldComponentUpdate(d, f, g) : b.prototype && b.prototype.isPureReactComponent ? !Ie(c, d) || !Ie(e, f) : true;
      }
      function Gi(a, b, c) {
        var d = false, e = Vf;
        var f = b.contextType;
        "object" === typeof f && null !== f ? f = eh(f) : (e = Zf(b) ? Xf : H.current, d = b.contextTypes, f = (d = null !== d && void 0 !== d) ? Yf(a, e) : Vf);
        b = new b(c, f);
        a.memoizedState = null !== b.state && void 0 !== b.state ? b.state : null;
        b.updater = Ei;
        a.stateNode = b;
        b._reactInternals = a;
        d && (a = a.stateNode, a.__reactInternalMemoizedUnmaskedChildContext = e, a.__reactInternalMemoizedMaskedChildContext = f);
        return b;
      }
      function Hi(a, b, c, d) {
        a = b.state;
        "function" === typeof b.componentWillReceiveProps && b.componentWillReceiveProps(c, d);
        "function" === typeof b.UNSAFE_componentWillReceiveProps && b.UNSAFE_componentWillReceiveProps(c, d);
        b.state !== a && Ei.enqueueReplaceState(b, b.state, null);
      }
      function Ii(a, b, c, d) {
        var e = a.stateNode;
        e.props = c;
        e.state = a.memoizedState;
        e.refs = {};
        kh(a);
        var f = b.contextType;
        "object" === typeof f && null !== f ? e.context = eh(f) : (f = Zf(b) ? Xf : H.current, e.context = Yf(a, f));
        e.state = a.memoizedState;
        f = b.getDerivedStateFromProps;
        "function" === typeof f && (Di(a, b, f, c), e.state = a.memoizedState);
        "function" === typeof b.getDerivedStateFromProps || "function" === typeof e.getSnapshotBeforeUpdate || "function" !== typeof e.UNSAFE_componentWillMount && "function" !== typeof e.componentWillMount || (b = e.state, "function" === typeof e.componentWillMount && e.componentWillMount(), "function" === typeof e.UNSAFE_componentWillMount && e.UNSAFE_componentWillMount(), b !== e.state && Ei.enqueueReplaceState(e, e.state, null), qh(a, c, e, d), e.state = a.memoizedState);
        "function" === typeof e.componentDidMount && (a.flags |= 4194308);
      }
      function Ji(a, b) {
        try {
          var c = "", d = b;
          do
            c += Pa(d), d = d.return;
          while (d);
          var e = c;
        } catch (f) {
          e = "\nError generating stack: " + f.message + "\n" + f.stack;
        }
        return { value: a, source: b, stack: e, digest: null };
      }
      function Ki(a, b, c) {
        return { value: a, source: null, stack: null != c ? c : null, digest: null != b ? b : null };
      }
      function Li(a, b) {
        try {
          console.error(b.value);
        } catch (c) {
          setTimeout(function() {
            throw c;
          });
        }
      }
      var Mi = "function" === typeof WeakMap ? WeakMap : Map;
      function Ni(a, b, c) {
        c = mh(-1, c);
        c.tag = 3;
        c.payload = { element: null };
        var d = b.value;
        c.callback = function() {
          Oi || (Oi = true, Pi = d);
          Li(a, b);
        };
        return c;
      }
      function Qi(a, b, c) {
        c = mh(-1, c);
        c.tag = 3;
        var d = a.type.getDerivedStateFromError;
        if ("function" === typeof d) {
          var e = b.value;
          c.payload = function() {
            return d(e);
          };
          c.callback = function() {
            Li(a, b);
          };
        }
        var f = a.stateNode;
        null !== f && "function" === typeof f.componentDidCatch && (c.callback = function() {
          Li(a, b);
          "function" !== typeof d && (null === Ri ? Ri = /* @__PURE__ */ new Set([this]) : Ri.add(this));
          var c2 = b.stack;
          this.componentDidCatch(b.value, { componentStack: null !== c2 ? c2 : "" });
        });
        return c;
      }
      function Si(a, b, c) {
        var d = a.pingCache;
        if (null === d) {
          d = a.pingCache = new Mi();
          var e = /* @__PURE__ */ new Set();
          d.set(b, e);
        } else e = d.get(b), void 0 === e && (e = /* @__PURE__ */ new Set(), d.set(b, e));
        e.has(c) || (e.add(c), a = Ti.bind(null, a, b, c), b.then(a, a));
      }
      function Ui(a) {
        do {
          var b;
          if (b = 13 === a.tag) b = a.memoizedState, b = null !== b ? null !== b.dehydrated ? true : false : true;
          if (b) return a;
          a = a.return;
        } while (null !== a);
        return null;
      }
      function Vi(a, b, c, d, e) {
        if (0 === (a.mode & 1)) return a === b ? a.flags |= 65536 : (a.flags |= 128, c.flags |= 131072, c.flags &= -52805, 1 === c.tag && (null === c.alternate ? c.tag = 17 : (b = mh(-1, 1), b.tag = 2, nh(c, b, 1))), c.lanes |= 1), a;
        a.flags |= 65536;
        a.lanes = e;
        return a;
      }
      var Wi = ua.ReactCurrentOwner;
      var dh = false;
      function Xi(a, b, c, d) {
        b.child = null === a ? Vg(b, null, c, d) : Ug(b, a.child, c, d);
      }
      function Yi(a, b, c, d, e) {
        c = c.render;
        var f = b.ref;
        ch(b, e);
        d = Nh(a, b, c, d, f, e);
        c = Sh();
        if (null !== a && !dh) return b.updateQueue = a.updateQueue, b.flags &= -2053, a.lanes &= ~e, Zi(a, b, e);
        I && c && vg(b);
        b.flags |= 1;
        Xi(a, b, d, e);
        return b.child;
      }
      function $i(a, b, c, d, e) {
        if (null === a) {
          var f = c.type;
          if ("function" === typeof f && !aj(f) && void 0 === f.defaultProps && null === c.compare && void 0 === c.defaultProps) return b.tag = 15, b.type = f, bj(a, b, f, d, e);
          a = Rg(c.type, null, d, b, b.mode, e);
          a.ref = b.ref;
          a.return = b;
          return b.child = a;
        }
        f = a.child;
        if (0 === (a.lanes & e)) {
          var g = f.memoizedProps;
          c = c.compare;
          c = null !== c ? c : Ie;
          if (c(g, d) && a.ref === b.ref) return Zi(a, b, e);
        }
        b.flags |= 1;
        a = Pg(f, d);
        a.ref = b.ref;
        a.return = b;
        return b.child = a;
      }
      function bj(a, b, c, d, e) {
        if (null !== a) {
          var f = a.memoizedProps;
          if (Ie(f, d) && a.ref === b.ref) if (dh = false, b.pendingProps = d = f, 0 !== (a.lanes & e)) 0 !== (a.flags & 131072) && (dh = true);
          else return b.lanes = a.lanes, Zi(a, b, e);
        }
        return cj(a, b, c, d, e);
      }
      function dj(a, b, c) {
        var d = b.pendingProps, e = d.children, f = null !== a ? a.memoizedState : null;
        if ("hidden" === d.mode) if (0 === (b.mode & 1)) b.memoizedState = { baseLanes: 0, cachePool: null, transitions: null }, G(ej, fj), fj |= c;
        else {
          if (0 === (c & 1073741824)) return a = null !== f ? f.baseLanes | c : c, b.lanes = b.childLanes = 1073741824, b.memoizedState = { baseLanes: a, cachePool: null, transitions: null }, b.updateQueue = null, G(ej, fj), fj |= a, null;
          b.memoizedState = { baseLanes: 0, cachePool: null, transitions: null };
          d = null !== f ? f.baseLanes : c;
          G(ej, fj);
          fj |= d;
        }
        else null !== f ? (d = f.baseLanes | c, b.memoizedState = null) : d = c, G(ej, fj), fj |= d;
        Xi(a, b, e, c);
        return b.child;
      }
      function gj(a, b) {
        var c = b.ref;
        if (null === a && null !== c || null !== a && a.ref !== c) b.flags |= 512, b.flags |= 2097152;
      }
      function cj(a, b, c, d, e) {
        var f = Zf(c) ? Xf : H.current;
        f = Yf(b, f);
        ch(b, e);
        c = Nh(a, b, c, d, f, e);
        d = Sh();
        if (null !== a && !dh) return b.updateQueue = a.updateQueue, b.flags &= -2053, a.lanes &= ~e, Zi(a, b, e);
        I && d && vg(b);
        b.flags |= 1;
        Xi(a, b, c, e);
        return b.child;
      }
      function hj(a, b, c, d, e) {
        if (Zf(c)) {
          var f = true;
          cg(b);
        } else f = false;
        ch(b, e);
        if (null === b.stateNode) ij(a, b), Gi(b, c, d), Ii(b, c, d, e), d = true;
        else if (null === a) {
          var g = b.stateNode, h = b.memoizedProps;
          g.props = h;
          var k = g.context, l = c.contextType;
          "object" === typeof l && null !== l ? l = eh(l) : (l = Zf(c) ? Xf : H.current, l = Yf(b, l));
          var m = c.getDerivedStateFromProps, q = "function" === typeof m || "function" === typeof g.getSnapshotBeforeUpdate;
          q || "function" !== typeof g.UNSAFE_componentWillReceiveProps && "function" !== typeof g.componentWillReceiveProps || (h !== d || k !== l) && Hi(b, g, d, l);
          jh = false;
          var r = b.memoizedState;
          g.state = r;
          qh(b, d, g, e);
          k = b.memoizedState;
          h !== d || r !== k || Wf.current || jh ? ("function" === typeof m && (Di(b, c, m, d), k = b.memoizedState), (h = jh || Fi(b, c, h, d, r, k, l)) ? (q || "function" !== typeof g.UNSAFE_componentWillMount && "function" !== typeof g.componentWillMount || ("function" === typeof g.componentWillMount && g.componentWillMount(), "function" === typeof g.UNSAFE_componentWillMount && g.UNSAFE_componentWillMount()), "function" === typeof g.componentDidMount && (b.flags |= 4194308)) : ("function" === typeof g.componentDidMount && (b.flags |= 4194308), b.memoizedProps = d, b.memoizedState = k), g.props = d, g.state = k, g.context = l, d = h) : ("function" === typeof g.componentDidMount && (b.flags |= 4194308), d = false);
        } else {
          g = b.stateNode;
          lh(a, b);
          h = b.memoizedProps;
          l = b.type === b.elementType ? h : Ci(b.type, h);
          g.props = l;
          q = b.pendingProps;
          r = g.context;
          k = c.contextType;
          "object" === typeof k && null !== k ? k = eh(k) : (k = Zf(c) ? Xf : H.current, k = Yf(b, k));
          var y = c.getDerivedStateFromProps;
          (m = "function" === typeof y || "function" === typeof g.getSnapshotBeforeUpdate) || "function" !== typeof g.UNSAFE_componentWillReceiveProps && "function" !== typeof g.componentWillReceiveProps || (h !== q || r !== k) && Hi(b, g, d, k);
          jh = false;
          r = b.memoizedState;
          g.state = r;
          qh(b, d, g, e);
          var n = b.memoizedState;
          h !== q || r !== n || Wf.current || jh ? ("function" === typeof y && (Di(b, c, y, d), n = b.memoizedState), (l = jh || Fi(b, c, l, d, r, n, k) || false) ? (m || "function" !== typeof g.UNSAFE_componentWillUpdate && "function" !== typeof g.componentWillUpdate || ("function" === typeof g.componentWillUpdate && g.componentWillUpdate(d, n, k), "function" === typeof g.UNSAFE_componentWillUpdate && g.UNSAFE_componentWillUpdate(d, n, k)), "function" === typeof g.componentDidUpdate && (b.flags |= 4), "function" === typeof g.getSnapshotBeforeUpdate && (b.flags |= 1024)) : ("function" !== typeof g.componentDidUpdate || h === a.memoizedProps && r === a.memoizedState || (b.flags |= 4), "function" !== typeof g.getSnapshotBeforeUpdate || h === a.memoizedProps && r === a.memoizedState || (b.flags |= 1024), b.memoizedProps = d, b.memoizedState = n), g.props = d, g.state = n, g.context = k, d = l) : ("function" !== typeof g.componentDidUpdate || h === a.memoizedProps && r === a.memoizedState || (b.flags |= 4), "function" !== typeof g.getSnapshotBeforeUpdate || h === a.memoizedProps && r === a.memoizedState || (b.flags |= 1024), d = false);
        }
        return jj(a, b, c, d, f, e);
      }
      function jj(a, b, c, d, e, f) {
        gj(a, b);
        var g = 0 !== (b.flags & 128);
        if (!d && !g) return e && dg(b, c, false), Zi(a, b, f);
        d = b.stateNode;
        Wi.current = b;
        var h = g && "function" !== typeof c.getDerivedStateFromError ? null : d.render();
        b.flags |= 1;
        null !== a && g ? (b.child = Ug(b, a.child, null, f), b.child = Ug(b, null, h, f)) : Xi(a, b, h, f);
        b.memoizedState = d.state;
        e && dg(b, c, true);
        return b.child;
      }
      function kj(a) {
        var b = a.stateNode;
        b.pendingContext ? ag(a, b.pendingContext, b.pendingContext !== b.context) : b.context && ag(a, b.context, false);
        yh(a, b.containerInfo);
      }
      function lj(a, b, c, d, e) {
        Ig();
        Jg(e);
        b.flags |= 256;
        Xi(a, b, c, d);
        return b.child;
      }
      var mj = { dehydrated: null, treeContext: null, retryLane: 0 };
      function nj(a) {
        return { baseLanes: a, cachePool: null, transitions: null };
      }
      function oj(a, b, c) {
        var d = b.pendingProps, e = L.current, f = false, g = 0 !== (b.flags & 128), h;
        (h = g) || (h = null !== a && null === a.memoizedState ? false : 0 !== (e & 2));
        if (h) f = true, b.flags &= -129;
        else if (null === a || null !== a.memoizedState) e |= 1;
        G(L, e & 1);
        if (null === a) {
          Eg(b);
          a = b.memoizedState;
          if (null !== a && (a = a.dehydrated, null !== a)) return 0 === (b.mode & 1) ? b.lanes = 1 : "$!" === a.data ? b.lanes = 8 : b.lanes = 1073741824, null;
          g = d.children;
          a = d.fallback;
          return f ? (d = b.mode, f = b.child, g = { mode: "hidden", children: g }, 0 === (d & 1) && null !== f ? (f.childLanes = 0, f.pendingProps = g) : f = pj(g, d, 0, null), a = Tg(a, d, c, null), f.return = b, a.return = b, f.sibling = a, b.child = f, b.child.memoizedState = nj(c), b.memoizedState = mj, a) : qj(b, g);
        }
        e = a.memoizedState;
        if (null !== e && (h = e.dehydrated, null !== h)) return rj(a, b, g, d, h, e, c);
        if (f) {
          f = d.fallback;
          g = b.mode;
          e = a.child;
          h = e.sibling;
          var k = { mode: "hidden", children: d.children };
          0 === (g & 1) && b.child !== e ? (d = b.child, d.childLanes = 0, d.pendingProps = k, b.deletions = null) : (d = Pg(e, k), d.subtreeFlags = e.subtreeFlags & 14680064);
          null !== h ? f = Pg(h, f) : (f = Tg(f, g, c, null), f.flags |= 2);
          f.return = b;
          d.return = b;
          d.sibling = f;
          b.child = d;
          d = f;
          f = b.child;
          g = a.child.memoizedState;
          g = null === g ? nj(c) : { baseLanes: g.baseLanes | c, cachePool: null, transitions: g.transitions };
          f.memoizedState = g;
          f.childLanes = a.childLanes & ~c;
          b.memoizedState = mj;
          return d;
        }
        f = a.child;
        a = f.sibling;
        d = Pg(f, { mode: "visible", children: d.children });
        0 === (b.mode & 1) && (d.lanes = c);
        d.return = b;
        d.sibling = null;
        null !== a && (c = b.deletions, null === c ? (b.deletions = [a], b.flags |= 16) : c.push(a));
        b.child = d;
        b.memoizedState = null;
        return d;
      }
      function qj(a, b) {
        b = pj({ mode: "visible", children: b }, a.mode, 0, null);
        b.return = a;
        return a.child = b;
      }
      function sj(a, b, c, d) {
        null !== d && Jg(d);
        Ug(b, a.child, null, c);
        a = qj(b, b.pendingProps.children);
        a.flags |= 2;
        b.memoizedState = null;
        return a;
      }
      function rj(a, b, c, d, e, f, g) {
        if (c) {
          if (b.flags & 256) return b.flags &= -257, d = Ki(Error(p(422))), sj(a, b, g, d);
          if (null !== b.memoizedState) return b.child = a.child, b.flags |= 128, null;
          f = d.fallback;
          e = b.mode;
          d = pj({ mode: "visible", children: d.children }, e, 0, null);
          f = Tg(f, e, g, null);
          f.flags |= 2;
          d.return = b;
          f.return = b;
          d.sibling = f;
          b.child = d;
          0 !== (b.mode & 1) && Ug(b, a.child, null, g);
          b.child.memoizedState = nj(g);
          b.memoizedState = mj;
          return f;
        }
        if (0 === (b.mode & 1)) return sj(a, b, g, null);
        if ("$!" === e.data) {
          d = e.nextSibling && e.nextSibling.dataset;
          if (d) var h = d.dgst;
          d = h;
          f = Error(p(419));
          d = Ki(f, d, void 0);
          return sj(a, b, g, d);
        }
        h = 0 !== (g & a.childLanes);
        if (dh || h) {
          d = Q;
          if (null !== d) {
            switch (g & -g) {
              case 4:
                e = 2;
                break;
              case 16:
                e = 8;
                break;
              case 64:
              case 128:
              case 256:
              case 512:
              case 1024:
              case 2048:
              case 4096:
              case 8192:
              case 16384:
              case 32768:
              case 65536:
              case 131072:
              case 262144:
              case 524288:
              case 1048576:
              case 2097152:
              case 4194304:
              case 8388608:
              case 16777216:
              case 33554432:
              case 67108864:
                e = 32;
                break;
              case 536870912:
                e = 268435456;
                break;
              default:
                e = 0;
            }
            e = 0 !== (e & (d.suspendedLanes | g)) ? 0 : e;
            0 !== e && e !== f.retryLane && (f.retryLane = e, ih(a, e), gi(d, a, e, -1));
          }
          tj();
          d = Ki(Error(p(421)));
          return sj(a, b, g, d);
        }
        if ("$?" === e.data) return b.flags |= 128, b.child = a.child, b = uj.bind(null, a), e._reactRetry = b, null;
        a = f.treeContext;
        yg = Lf(e.nextSibling);
        xg = b;
        I = true;
        zg = null;
        null !== a && (og[pg++] = rg, og[pg++] = sg, og[pg++] = qg, rg = a.id, sg = a.overflow, qg = b);
        b = qj(b, d.children);
        b.flags |= 4096;
        return b;
      }
      function vj(a, b, c) {
        a.lanes |= b;
        var d = a.alternate;
        null !== d && (d.lanes |= b);
        bh(a.return, b, c);
      }
      function wj(a, b, c, d, e) {
        var f = a.memoizedState;
        null === f ? a.memoizedState = { isBackwards: b, rendering: null, renderingStartTime: 0, last: d, tail: c, tailMode: e } : (f.isBackwards = b, f.rendering = null, f.renderingStartTime = 0, f.last = d, f.tail = c, f.tailMode = e);
      }
      function xj(a, b, c) {
        var d = b.pendingProps, e = d.revealOrder, f = d.tail;
        Xi(a, b, d.children, c);
        d = L.current;
        if (0 !== (d & 2)) d = d & 1 | 2, b.flags |= 128;
        else {
          if (null !== a && 0 !== (a.flags & 128)) a: for (a = b.child; null !== a; ) {
            if (13 === a.tag) null !== a.memoizedState && vj(a, c, b);
            else if (19 === a.tag) vj(a, c, b);
            else if (null !== a.child) {
              a.child.return = a;
              a = a.child;
              continue;
            }
            if (a === b) break a;
            for (; null === a.sibling; ) {
              if (null === a.return || a.return === b) break a;
              a = a.return;
            }
            a.sibling.return = a.return;
            a = a.sibling;
          }
          d &= 1;
        }
        G(L, d);
        if (0 === (b.mode & 1)) b.memoizedState = null;
        else switch (e) {
          case "forwards":
            c = b.child;
            for (e = null; null !== c; ) a = c.alternate, null !== a && null === Ch(a) && (e = c), c = c.sibling;
            c = e;
            null === c ? (e = b.child, b.child = null) : (e = c.sibling, c.sibling = null);
            wj(b, false, e, c, f);
            break;
          case "backwards":
            c = null;
            e = b.child;
            for (b.child = null; null !== e; ) {
              a = e.alternate;
              if (null !== a && null === Ch(a)) {
                b.child = e;
                break;
              }
              a = e.sibling;
              e.sibling = c;
              c = e;
              e = a;
            }
            wj(b, true, c, null, f);
            break;
          case "together":
            wj(b, false, null, null, void 0);
            break;
          default:
            b.memoizedState = null;
        }
        return b.child;
      }
      function ij(a, b) {
        0 === (b.mode & 1) && null !== a && (a.alternate = null, b.alternate = null, b.flags |= 2);
      }
      function Zi(a, b, c) {
        null !== a && (b.dependencies = a.dependencies);
        rh |= b.lanes;
        if (0 === (c & b.childLanes)) return null;
        if (null !== a && b.child !== a.child) throw Error(p(153));
        if (null !== b.child) {
          a = b.child;
          c = Pg(a, a.pendingProps);
          b.child = c;
          for (c.return = b; null !== a.sibling; ) a = a.sibling, c = c.sibling = Pg(a, a.pendingProps), c.return = b;
          c.sibling = null;
        }
        return b.child;
      }
      function yj(a, b, c) {
        switch (b.tag) {
          case 3:
            kj(b);
            Ig();
            break;
          case 5:
            Ah(b);
            break;
          case 1:
            Zf(b.type) && cg(b);
            break;
          case 4:
            yh(b, b.stateNode.containerInfo);
            break;
          case 10:
            var d = b.type._context, e = b.memoizedProps.value;
            G(Wg, d._currentValue);
            d._currentValue = e;
            break;
          case 13:
            d = b.memoizedState;
            if (null !== d) {
              if (null !== d.dehydrated) return G(L, L.current & 1), b.flags |= 128, null;
              if (0 !== (c & b.child.childLanes)) return oj(a, b, c);
              G(L, L.current & 1);
              a = Zi(a, b, c);
              return null !== a ? a.sibling : null;
            }
            G(L, L.current & 1);
            break;
          case 19:
            d = 0 !== (c & b.childLanes);
            if (0 !== (a.flags & 128)) {
              if (d) return xj(a, b, c);
              b.flags |= 128;
            }
            e = b.memoizedState;
            null !== e && (e.rendering = null, e.tail = null, e.lastEffect = null);
            G(L, L.current);
            if (d) break;
            else return null;
          case 22:
          case 23:
            return b.lanes = 0, dj(a, b, c);
        }
        return Zi(a, b, c);
      }
      var zj;
      var Aj;
      var Bj;
      var Cj;
      zj = function(a, b) {
        for (var c = b.child; null !== c; ) {
          if (5 === c.tag || 6 === c.tag) a.appendChild(c.stateNode);
          else if (4 !== c.tag && null !== c.child) {
            c.child.return = c;
            c = c.child;
            continue;
          }
          if (c === b) break;
          for (; null === c.sibling; ) {
            if (null === c.return || c.return === b) return;
            c = c.return;
          }
          c.sibling.return = c.return;
          c = c.sibling;
        }
      };
      Aj = function() {
      };
      Bj = function(a, b, c, d) {
        var e = a.memoizedProps;
        if (e !== d) {
          a = b.stateNode;
          xh(uh.current);
          var f = null;
          switch (c) {
            case "input":
              e = Ya(a, e);
              d = Ya(a, d);
              f = [];
              break;
            case "select":
              e = A({}, e, { value: void 0 });
              d = A({}, d, { value: void 0 });
              f = [];
              break;
            case "textarea":
              e = gb(a, e);
              d = gb(a, d);
              f = [];
              break;
            default:
              "function" !== typeof e.onClick && "function" === typeof d.onClick && (a.onclick = Bf);
          }
          ub(c, d);
          var g;
          c = null;
          for (l in e) if (!d.hasOwnProperty(l) && e.hasOwnProperty(l) && null != e[l]) if ("style" === l) {
            var h = e[l];
            for (g in h) h.hasOwnProperty(g) && (c || (c = {}), c[g] = "");
          } else "dangerouslySetInnerHTML" !== l && "children" !== l && "suppressContentEditableWarning" !== l && "suppressHydrationWarning" !== l && "autoFocus" !== l && (ea.hasOwnProperty(l) ? f || (f = []) : (f = f || []).push(l, null));
          for (l in d) {
            var k = d[l];
            h = null != e ? e[l] : void 0;
            if (d.hasOwnProperty(l) && k !== h && (null != k || null != h)) if ("style" === l) if (h) {
              for (g in h) !h.hasOwnProperty(g) || k && k.hasOwnProperty(g) || (c || (c = {}), c[g] = "");
              for (g in k) k.hasOwnProperty(g) && h[g] !== k[g] && (c || (c = {}), c[g] = k[g]);
            } else c || (f || (f = []), f.push(
              l,
              c
            )), c = k;
            else "dangerouslySetInnerHTML" === l ? (k = k ? k.__html : void 0, h = h ? h.__html : void 0, null != k && h !== k && (f = f || []).push(l, k)) : "children" === l ? "string" !== typeof k && "number" !== typeof k || (f = f || []).push(l, "" + k) : "suppressContentEditableWarning" !== l && "suppressHydrationWarning" !== l && (ea.hasOwnProperty(l) ? (null != k && "onScroll" === l && D("scroll", a), f || h === k || (f = [])) : (f = f || []).push(l, k));
          }
          c && (f = f || []).push("style", c);
          var l = f;
          if (b.updateQueue = l) b.flags |= 4;
        }
      };
      Cj = function(a, b, c, d) {
        c !== d && (b.flags |= 4);
      };
      function Dj(a, b) {
        if (!I) switch (a.tailMode) {
          case "hidden":
            b = a.tail;
            for (var c = null; null !== b; ) null !== b.alternate && (c = b), b = b.sibling;
            null === c ? a.tail = null : c.sibling = null;
            break;
          case "collapsed":
            c = a.tail;
            for (var d = null; null !== c; ) null !== c.alternate && (d = c), c = c.sibling;
            null === d ? b || null === a.tail ? a.tail = null : a.tail.sibling = null : d.sibling = null;
        }
      }
      function S(a) {
        var b = null !== a.alternate && a.alternate.child === a.child, c = 0, d = 0;
        if (b) for (var e = a.child; null !== e; ) c |= e.lanes | e.childLanes, d |= e.subtreeFlags & 14680064, d |= e.flags & 14680064, e.return = a, e = e.sibling;
        else for (e = a.child; null !== e; ) c |= e.lanes | e.childLanes, d |= e.subtreeFlags, d |= e.flags, e.return = a, e = e.sibling;
        a.subtreeFlags |= d;
        a.childLanes = c;
        return b;
      }
      function Ej(a, b, c) {
        var d = b.pendingProps;
        wg(b);
        switch (b.tag) {
          case 2:
          case 16:
          case 15:
          case 0:
          case 11:
          case 7:
          case 8:
          case 12:
          case 9:
          case 14:
            return S(b), null;
          case 1:
            return Zf(b.type) && $f(), S(b), null;
          case 3:
            d = b.stateNode;
            zh();
            E(Wf);
            E(H);
            Eh();
            d.pendingContext && (d.context = d.pendingContext, d.pendingContext = null);
            if (null === a || null === a.child) Gg(b) ? b.flags |= 4 : null === a || a.memoizedState.isDehydrated && 0 === (b.flags & 256) || (b.flags |= 1024, null !== zg && (Fj(zg), zg = null));
            Aj(a, b);
            S(b);
            return null;
          case 5:
            Bh(b);
            var e = xh(wh.current);
            c = b.type;
            if (null !== a && null != b.stateNode) Bj(a, b, c, d, e), a.ref !== b.ref && (b.flags |= 512, b.flags |= 2097152);
            else {
              if (!d) {
                if (null === b.stateNode) throw Error(p(166));
                S(b);
                return null;
              }
              a = xh(uh.current);
              if (Gg(b)) {
                d = b.stateNode;
                c = b.type;
                var f = b.memoizedProps;
                d[Of] = b;
                d[Pf] = f;
                a = 0 !== (b.mode & 1);
                switch (c) {
                  case "dialog":
                    D("cancel", d);
                    D("close", d);
                    break;
                  case "iframe":
                  case "object":
                  case "embed":
                    D("load", d);
                    break;
                  case "video":
                  case "audio":
                    for (e = 0; e < lf.length; e++) D(lf[e], d);
                    break;
                  case "source":
                    D("error", d);
                    break;
                  case "img":
                  case "image":
                  case "link":
                    D(
                      "error",
                      d
                    );
                    D("load", d);
                    break;
                  case "details":
                    D("toggle", d);
                    break;
                  case "input":
                    Za(d, f);
                    D("invalid", d);
                    break;
                  case "select":
                    d._wrapperState = { wasMultiple: !!f.multiple };
                    D("invalid", d);
                    break;
                  case "textarea":
                    hb(d, f), D("invalid", d);
                }
                ub(c, f);
                e = null;
                for (var g in f) if (f.hasOwnProperty(g)) {
                  var h = f[g];
                  "children" === g ? "string" === typeof h ? d.textContent !== h && (true !== f.suppressHydrationWarning && Af(d.textContent, h, a), e = ["children", h]) : "number" === typeof h && d.textContent !== "" + h && (true !== f.suppressHydrationWarning && Af(
                    d.textContent,
                    h,
                    a
                  ), e = ["children", "" + h]) : ea.hasOwnProperty(g) && null != h && "onScroll" === g && D("scroll", d);
                }
                switch (c) {
                  case "input":
                    Va(d);
                    db(d, f, true);
                    break;
                  case "textarea":
                    Va(d);
                    jb(d);
                    break;
                  case "select":
                  case "option":
                    break;
                  default:
                    "function" === typeof f.onClick && (d.onclick = Bf);
                }
                d = e;
                b.updateQueue = d;
                null !== d && (b.flags |= 4);
              } else {
                g = 9 === e.nodeType ? e : e.ownerDocument;
                "http://www.w3.org/1999/xhtml" === a && (a = kb(c));
                "http://www.w3.org/1999/xhtml" === a ? "script" === c ? (a = g.createElement("div"), a.innerHTML = "<script><\/script>", a = a.removeChild(a.firstChild)) : "string" === typeof d.is ? a = g.createElement(c, { is: d.is }) : (a = g.createElement(c), "select" === c && (g = a, d.multiple ? g.multiple = true : d.size && (g.size = d.size))) : a = g.createElementNS(a, c);
                a[Of] = b;
                a[Pf] = d;
                zj(a, b, false, false);
                b.stateNode = a;
                a: {
                  g = vb(c, d);
                  switch (c) {
                    case "dialog":
                      D("cancel", a);
                      D("close", a);
                      e = d;
                      break;
                    case "iframe":
                    case "object":
                    case "embed":
                      D("load", a);
                      e = d;
                      break;
                    case "video":
                    case "audio":
                      for (e = 0; e < lf.length; e++) D(lf[e], a);
                      e = d;
                      break;
                    case "source":
                      D("error", a);
                      e = d;
                      break;
                    case "img":
                    case "image":
                    case "link":
                      D(
                        "error",
                        a
                      );
                      D("load", a);
                      e = d;
                      break;
                    case "details":
                      D("toggle", a);
                      e = d;
                      break;
                    case "input":
                      Za(a, d);
                      e = Ya(a, d);
                      D("invalid", a);
                      break;
                    case "option":
                      e = d;
                      break;
                    case "select":
                      a._wrapperState = { wasMultiple: !!d.multiple };
                      e = A({}, d, { value: void 0 });
                      D("invalid", a);
                      break;
                    case "textarea":
                      hb(a, d);
                      e = gb(a, d);
                      D("invalid", a);
                      break;
                    default:
                      e = d;
                  }
                  ub(c, e);
                  h = e;
                  for (f in h) if (h.hasOwnProperty(f)) {
                    var k = h[f];
                    "style" === f ? sb(a, k) : "dangerouslySetInnerHTML" === f ? (k = k ? k.__html : void 0, null != k && nb(a, k)) : "children" === f ? "string" === typeof k ? ("textarea" !== c || "" !== k) && ob(a, k) : "number" === typeof k && ob(a, "" + k) : "suppressContentEditableWarning" !== f && "suppressHydrationWarning" !== f && "autoFocus" !== f && (ea.hasOwnProperty(f) ? null != k && "onScroll" === f && D("scroll", a) : null != k && ta(a, f, k, g));
                  }
                  switch (c) {
                    case "input":
                      Va(a);
                      db(a, d, false);
                      break;
                    case "textarea":
                      Va(a);
                      jb(a);
                      break;
                    case "option":
                      null != d.value && a.setAttribute("value", "" + Sa(d.value));
                      break;
                    case "select":
                      a.multiple = !!d.multiple;
                      f = d.value;
                      null != f ? fb(a, !!d.multiple, f, false) : null != d.defaultValue && fb(
                        a,
                        !!d.multiple,
                        d.defaultValue,
                        true
                      );
                      break;
                    default:
                      "function" === typeof e.onClick && (a.onclick = Bf);
                  }
                  switch (c) {
                    case "button":
                    case "input":
                    case "select":
                    case "textarea":
                      d = !!d.autoFocus;
                      break a;
                    case "img":
                      d = true;
                      break a;
                    default:
                      d = false;
                  }
                }
                d && (b.flags |= 4);
              }
              null !== b.ref && (b.flags |= 512, b.flags |= 2097152);
            }
            S(b);
            return null;
          case 6:
            if (a && null != b.stateNode) Cj(a, b, a.memoizedProps, d);
            else {
              if ("string" !== typeof d && null === b.stateNode) throw Error(p(166));
              c = xh(wh.current);
              xh(uh.current);
              if (Gg(b)) {
                d = b.stateNode;
                c = b.memoizedProps;
                d[Of] = b;
                if (f = d.nodeValue !== c) {
                  if (a = xg, null !== a) switch (a.tag) {
                    case 3:
                      Af(d.nodeValue, c, 0 !== (a.mode & 1));
                      break;
                    case 5:
                      true !== a.memoizedProps.suppressHydrationWarning && Af(d.nodeValue, c, 0 !== (a.mode & 1));
                  }
                }
                f && (b.flags |= 4);
              } else d = (9 === c.nodeType ? c : c.ownerDocument).createTextNode(d), d[Of] = b, b.stateNode = d;
            }
            S(b);
            return null;
          case 13:
            E(L);
            d = b.memoizedState;
            if (null === a || null !== a.memoizedState && null !== a.memoizedState.dehydrated) {
              if (I && null !== yg && 0 !== (b.mode & 1) && 0 === (b.flags & 128)) Hg(), Ig(), b.flags |= 98560, f = false;
              else if (f = Gg(b), null !== d && null !== d.dehydrated) {
                if (null === a) {
                  if (!f) throw Error(p(318));
                  f = b.memoizedState;
                  f = null !== f ? f.dehydrated : null;
                  if (!f) throw Error(p(317));
                  f[Of] = b;
                } else Ig(), 0 === (b.flags & 128) && (b.memoizedState = null), b.flags |= 4;
                S(b);
                f = false;
              } else null !== zg && (Fj(zg), zg = null), f = true;
              if (!f) return b.flags & 65536 ? b : null;
            }
            if (0 !== (b.flags & 128)) return b.lanes = c, b;
            d = null !== d;
            d !== (null !== a && null !== a.memoizedState) && d && (b.child.flags |= 8192, 0 !== (b.mode & 1) && (null === a || 0 !== (L.current & 1) ? 0 === T && (T = 3) : tj()));
            null !== b.updateQueue && (b.flags |= 4);
            S(b);
            return null;
          case 4:
            return zh(), Aj(a, b), null === a && sf(b.stateNode.containerInfo), S(b), null;
          case 10:
            return ah(b.type._context), S(b), null;
          case 17:
            return Zf(b.type) && $f(), S(b), null;
          case 19:
            E(L);
            f = b.memoizedState;
            if (null === f) return S(b), null;
            d = 0 !== (b.flags & 128);
            g = f.rendering;
            if (null === g) if (d) Dj(f, false);
            else {
              if (0 !== T || null !== a && 0 !== (a.flags & 128)) for (a = b.child; null !== a; ) {
                g = Ch(a);
                if (null !== g) {
                  b.flags |= 128;
                  Dj(f, false);
                  d = g.updateQueue;
                  null !== d && (b.updateQueue = d, b.flags |= 4);
                  b.subtreeFlags = 0;
                  d = c;
                  for (c = b.child; null !== c; ) f = c, a = d, f.flags &= 14680066, g = f.alternate, null === g ? (f.childLanes = 0, f.lanes = a, f.child = null, f.subtreeFlags = 0, f.memoizedProps = null, f.memoizedState = null, f.updateQueue = null, f.dependencies = null, f.stateNode = null) : (f.childLanes = g.childLanes, f.lanes = g.lanes, f.child = g.child, f.subtreeFlags = 0, f.deletions = null, f.memoizedProps = g.memoizedProps, f.memoizedState = g.memoizedState, f.updateQueue = g.updateQueue, f.type = g.type, a = g.dependencies, f.dependencies = null === a ? null : { lanes: a.lanes, firstContext: a.firstContext }), c = c.sibling;
                  G(L, L.current & 1 | 2);
                  return b.child;
                }
                a = a.sibling;
              }
              null !== f.tail && B() > Gj && (b.flags |= 128, d = true, Dj(f, false), b.lanes = 4194304);
            }
            else {
              if (!d) if (a = Ch(g), null !== a) {
                if (b.flags |= 128, d = true, c = a.updateQueue, null !== c && (b.updateQueue = c, b.flags |= 4), Dj(f, true), null === f.tail && "hidden" === f.tailMode && !g.alternate && !I) return S(b), null;
              } else 2 * B() - f.renderingStartTime > Gj && 1073741824 !== c && (b.flags |= 128, d = true, Dj(f, false), b.lanes = 4194304);
              f.isBackwards ? (g.sibling = b.child, b.child = g) : (c = f.last, null !== c ? c.sibling = g : b.child = g, f.last = g);
            }
            if (null !== f.tail) return b = f.tail, f.rendering = b, f.tail = b.sibling, f.renderingStartTime = B(), b.sibling = null, c = L.current, G(L, d ? c & 1 | 2 : c & 1), b;
            S(b);
            return null;
          case 22:
          case 23:
            return Hj(), d = null !== b.memoizedState, null !== a && null !== a.memoizedState !== d && (b.flags |= 8192), d && 0 !== (b.mode & 1) ? 0 !== (fj & 1073741824) && (S(b), b.subtreeFlags & 6 && (b.flags |= 8192)) : S(b), null;
          case 24:
            return null;
          case 25:
            return null;
        }
        throw Error(p(156, b.tag));
      }
      function Ij(a, b) {
        wg(b);
        switch (b.tag) {
          case 1:
            return Zf(b.type) && $f(), a = b.flags, a & 65536 ? (b.flags = a & -65537 | 128, b) : null;
          case 3:
            return zh(), E(Wf), E(H), Eh(), a = b.flags, 0 !== (a & 65536) && 0 === (a & 128) ? (b.flags = a & -65537 | 128, b) : null;
          case 5:
            return Bh(b), null;
          case 13:
            E(L);
            a = b.memoizedState;
            if (null !== a && null !== a.dehydrated) {
              if (null === b.alternate) throw Error(p(340));
              Ig();
            }
            a = b.flags;
            return a & 65536 ? (b.flags = a & -65537 | 128, b) : null;
          case 19:
            return E(L), null;
          case 4:
            return zh(), null;
          case 10:
            return ah(b.type._context), null;
          case 22:
          case 23:
            return Hj(), null;
          case 24:
            return null;
          default:
            return null;
        }
      }
      var Jj = false;
      var U = false;
      var Kj = "function" === typeof WeakSet ? WeakSet : Set;
      var V = null;
      function Lj(a, b) {
        var c = a.ref;
        if (null !== c) if ("function" === typeof c) try {
          c(null);
        } catch (d) {
          W(a, b, d);
        }
        else c.current = null;
      }
      function Mj(a, b, c) {
        try {
          c();
        } catch (d) {
          W(a, b, d);
        }
      }
      var Nj = false;
      function Oj(a, b) {
        Cf = dd;
        a = Me();
        if (Ne(a)) {
          if ("selectionStart" in a) var c = { start: a.selectionStart, end: a.selectionEnd };
          else a: {
            c = (c = a.ownerDocument) && c.defaultView || window;
            var d = c.getSelection && c.getSelection();
            if (d && 0 !== d.rangeCount) {
              c = d.anchorNode;
              var e = d.anchorOffset, f = d.focusNode;
              d = d.focusOffset;
              try {
                c.nodeType, f.nodeType;
              } catch (F) {
                c = null;
                break a;
              }
              var g = 0, h = -1, k = -1, l = 0, m = 0, q = a, r = null;
              b: for (; ; ) {
                for (var y; ; ) {
                  q !== c || 0 !== e && 3 !== q.nodeType || (h = g + e);
                  q !== f || 0 !== d && 3 !== q.nodeType || (k = g + d);
                  3 === q.nodeType && (g += q.nodeValue.length);
                  if (null === (y = q.firstChild)) break;
                  r = q;
                  q = y;
                }
                for (; ; ) {
                  if (q === a) break b;
                  r === c && ++l === e && (h = g);
                  r === f && ++m === d && (k = g);
                  if (null !== (y = q.nextSibling)) break;
                  q = r;
                  r = q.parentNode;
                }
                q = y;
              }
              c = -1 === h || -1 === k ? null : { start: h, end: k };
            } else c = null;
          }
          c = c || { start: 0, end: 0 };
        } else c = null;
        Df = { focusedElem: a, selectionRange: c };
        dd = false;
        for (V = b; null !== V; ) if (b = V, a = b.child, 0 !== (b.subtreeFlags & 1028) && null !== a) a.return = b, V = a;
        else for (; null !== V; ) {
          b = V;
          try {
            var n = b.alternate;
            if (0 !== (b.flags & 1024)) switch (b.tag) {
              case 0:
              case 11:
              case 15:
                break;
              case 1:
                if (null !== n) {
                  var t = n.memoizedProps, J = n.memoizedState, x = b.stateNode, w = x.getSnapshotBeforeUpdate(b.elementType === b.type ? t : Ci(b.type, t), J);
                  x.__reactInternalSnapshotBeforeUpdate = w;
                }
                break;
              case 3:
                var u = b.stateNode.containerInfo;
                1 === u.nodeType ? u.textContent = "" : 9 === u.nodeType && u.documentElement && u.removeChild(u.documentElement);
                break;
              case 5:
              case 6:
              case 4:
              case 17:
                break;
              default:
                throw Error(p(163));
            }
          } catch (F) {
            W(b, b.return, F);
          }
          a = b.sibling;
          if (null !== a) {
            a.return = b.return;
            V = a;
            break;
          }
          V = b.return;
        }
        n = Nj;
        Nj = false;
        return n;
      }
      function Pj(a, b, c) {
        var d = b.updateQueue;
        d = null !== d ? d.lastEffect : null;
        if (null !== d) {
          var e = d = d.next;
          do {
            if ((e.tag & a) === a) {
              var f = e.destroy;
              e.destroy = void 0;
              void 0 !== f && Mj(b, c, f);
            }
            e = e.next;
          } while (e !== d);
        }
      }
      function Qj(a, b) {
        b = b.updateQueue;
        b = null !== b ? b.lastEffect : null;
        if (null !== b) {
          var c = b = b.next;
          do {
            if ((c.tag & a) === a) {
              var d = c.create;
              c.destroy = d();
            }
            c = c.next;
          } while (c !== b);
        }
      }
      function Rj(a) {
        var b = a.ref;
        if (null !== b) {
          var c = a.stateNode;
          switch (a.tag) {
            case 5:
              a = c;
              break;
            default:
              a = c;
          }
          "function" === typeof b ? b(a) : b.current = a;
        }
      }
      function Sj(a) {
        var b = a.alternate;
        null !== b && (a.alternate = null, Sj(b));
        a.child = null;
        a.deletions = null;
        a.sibling = null;
        5 === a.tag && (b = a.stateNode, null !== b && (delete b[Of], delete b[Pf], delete b[of], delete b[Qf], delete b[Rf]));
        a.stateNode = null;
        a.return = null;
        a.dependencies = null;
        a.memoizedProps = null;
        a.memoizedState = null;
        a.pendingProps = null;
        a.stateNode = null;
        a.updateQueue = null;
      }
      function Tj(a) {
        return 5 === a.tag || 3 === a.tag || 4 === a.tag;
      }
      function Uj(a) {
        a: for (; ; ) {
          for (; null === a.sibling; ) {
            if (null === a.return || Tj(a.return)) return null;
            a = a.return;
          }
          a.sibling.return = a.return;
          for (a = a.sibling; 5 !== a.tag && 6 !== a.tag && 18 !== a.tag; ) {
            if (a.flags & 2) continue a;
            if (null === a.child || 4 === a.tag) continue a;
            else a.child.return = a, a = a.child;
          }
          if (!(a.flags & 2)) return a.stateNode;
        }
      }
      function Vj(a, b, c) {
        var d = a.tag;
        if (5 === d || 6 === d) a = a.stateNode, b ? 8 === c.nodeType ? c.parentNode.insertBefore(a, b) : c.insertBefore(a, b) : (8 === c.nodeType ? (b = c.parentNode, b.insertBefore(a, c)) : (b = c, b.appendChild(a)), c = c._reactRootContainer, null !== c && void 0 !== c || null !== b.onclick || (b.onclick = Bf));
        else if (4 !== d && (a = a.child, null !== a)) for (Vj(a, b, c), a = a.sibling; null !== a; ) Vj(a, b, c), a = a.sibling;
      }
      function Wj(a, b, c) {
        var d = a.tag;
        if (5 === d || 6 === d) a = a.stateNode, b ? c.insertBefore(a, b) : c.appendChild(a);
        else if (4 !== d && (a = a.child, null !== a)) for (Wj(a, b, c), a = a.sibling; null !== a; ) Wj(a, b, c), a = a.sibling;
      }
      var X = null;
      var Xj = false;
      function Yj(a, b, c) {
        for (c = c.child; null !== c; ) Zj(a, b, c), c = c.sibling;
      }
      function Zj(a, b, c) {
        if (lc && "function" === typeof lc.onCommitFiberUnmount) try {
          lc.onCommitFiberUnmount(kc, c);
        } catch (h) {
        }
        switch (c.tag) {
          case 5:
            U || Lj(c, b);
          case 6:
            var d = X, e = Xj;
            X = null;
            Yj(a, b, c);
            X = d;
            Xj = e;
            null !== X && (Xj ? (a = X, c = c.stateNode, 8 === a.nodeType ? a.parentNode.removeChild(c) : a.removeChild(c)) : X.removeChild(c.stateNode));
            break;
          case 18:
            null !== X && (Xj ? (a = X, c = c.stateNode, 8 === a.nodeType ? Kf(a.parentNode, c) : 1 === a.nodeType && Kf(a, c), bd(a)) : Kf(X, c.stateNode));
            break;
          case 4:
            d = X;
            e = Xj;
            X = c.stateNode.containerInfo;
            Xj = true;
            Yj(a, b, c);
            X = d;
            Xj = e;
            break;
          case 0:
          case 11:
          case 14:
          case 15:
            if (!U && (d = c.updateQueue, null !== d && (d = d.lastEffect, null !== d))) {
              e = d = d.next;
              do {
                var f = e, g = f.destroy;
                f = f.tag;
                void 0 !== g && (0 !== (f & 2) ? Mj(c, b, g) : 0 !== (f & 4) && Mj(c, b, g));
                e = e.next;
              } while (e !== d);
            }
            Yj(a, b, c);
            break;
          case 1:
            if (!U && (Lj(c, b), d = c.stateNode, "function" === typeof d.componentWillUnmount)) try {
              d.props = c.memoizedProps, d.state = c.memoizedState, d.componentWillUnmount();
            } catch (h) {
              W(c, b, h);
            }
            Yj(a, b, c);
            break;
          case 21:
            Yj(a, b, c);
            break;
          case 22:
            c.mode & 1 ? (U = (d = U) || null !== c.memoizedState, Yj(a, b, c), U = d) : Yj(a, b, c);
            break;
          default:
            Yj(a, b, c);
        }
      }
      function ak(a) {
        var b = a.updateQueue;
        if (null !== b) {
          a.updateQueue = null;
          var c = a.stateNode;
          null === c && (c = a.stateNode = new Kj());
          b.forEach(function(b2) {
            var d = bk.bind(null, a, b2);
            c.has(b2) || (c.add(b2), b2.then(d, d));
          });
        }
      }
      function ck(a, b) {
        var c = b.deletions;
        if (null !== c) for (var d = 0; d < c.length; d++) {
          var e = c[d];
          try {
            var f = a, g = b, h = g;
            a: for (; null !== h; ) {
              switch (h.tag) {
                case 5:
                  X = h.stateNode;
                  Xj = false;
                  break a;
                case 3:
                  X = h.stateNode.containerInfo;
                  Xj = true;
                  break a;
                case 4:
                  X = h.stateNode.containerInfo;
                  Xj = true;
                  break a;
              }
              h = h.return;
            }
            if (null === X) throw Error(p(160));
            Zj(f, g, e);
            X = null;
            Xj = false;
            var k = e.alternate;
            null !== k && (k.return = null);
            e.return = null;
          } catch (l) {
            W(e, b, l);
          }
        }
        if (b.subtreeFlags & 12854) for (b = b.child; null !== b; ) dk(b, a), b = b.sibling;
      }
      function dk(a, b) {
        var c = a.alternate, d = a.flags;
        switch (a.tag) {
          case 0:
          case 11:
          case 14:
          case 15:
            ck(b, a);
            ek(a);
            if (d & 4) {
              try {
                Pj(3, a, a.return), Qj(3, a);
              } catch (t) {
                W(a, a.return, t);
              }
              try {
                Pj(5, a, a.return);
              } catch (t) {
                W(a, a.return, t);
              }
            }
            break;
          case 1:
            ck(b, a);
            ek(a);
            d & 512 && null !== c && Lj(c, c.return);
            break;
          case 5:
            ck(b, a);
            ek(a);
            d & 512 && null !== c && Lj(c, c.return);
            if (a.flags & 32) {
              var e = a.stateNode;
              try {
                ob(e, "");
              } catch (t) {
                W(a, a.return, t);
              }
            }
            if (d & 4 && (e = a.stateNode, null != e)) {
              var f = a.memoizedProps, g = null !== c ? c.memoizedProps : f, h = a.type, k = a.updateQueue;
              a.updateQueue = null;
              if (null !== k) try {
                "input" === h && "radio" === f.type && null != f.name && ab(e, f);
                vb(h, g);
                var l = vb(h, f);
                for (g = 0; g < k.length; g += 2) {
                  var m = k[g], q = k[g + 1];
                  "style" === m ? sb(e, q) : "dangerouslySetInnerHTML" === m ? nb(e, q) : "children" === m ? ob(e, q) : ta(e, m, q, l);
                }
                switch (h) {
                  case "input":
                    bb(e, f);
                    break;
                  case "textarea":
                    ib(e, f);
                    break;
                  case "select":
                    var r = e._wrapperState.wasMultiple;
                    e._wrapperState.wasMultiple = !!f.multiple;
                    var y = f.value;
                    null != y ? fb(e, !!f.multiple, y, false) : r !== !!f.multiple && (null != f.defaultValue ? fb(
                      e,
                      !!f.multiple,
                      f.defaultValue,
                      true
                    ) : fb(e, !!f.multiple, f.multiple ? [] : "", false));
                }
                e[Pf] = f;
              } catch (t) {
                W(a, a.return, t);
              }
            }
            break;
          case 6:
            ck(b, a);
            ek(a);
            if (d & 4) {
              if (null === a.stateNode) throw Error(p(162));
              e = a.stateNode;
              f = a.memoizedProps;
              try {
                e.nodeValue = f;
              } catch (t) {
                W(a, a.return, t);
              }
            }
            break;
          case 3:
            ck(b, a);
            ek(a);
            if (d & 4 && null !== c && c.memoizedState.isDehydrated) try {
              bd(b.containerInfo);
            } catch (t) {
              W(a, a.return, t);
            }
            break;
          case 4:
            ck(b, a);
            ek(a);
            break;
          case 13:
            ck(b, a);
            ek(a);
            e = a.child;
            e.flags & 8192 && (f = null !== e.memoizedState, e.stateNode.isHidden = f, !f || null !== e.alternate && null !== e.alternate.memoizedState || (fk = B()));
            d & 4 && ak(a);
            break;
          case 22:
            m = null !== c && null !== c.memoizedState;
            a.mode & 1 ? (U = (l = U) || m, ck(b, a), U = l) : ck(b, a);
            ek(a);
            if (d & 8192) {
              l = null !== a.memoizedState;
              if ((a.stateNode.isHidden = l) && !m && 0 !== (a.mode & 1)) for (V = a, m = a.child; null !== m; ) {
                for (q = V = m; null !== V; ) {
                  r = V;
                  y = r.child;
                  switch (r.tag) {
                    case 0:
                    case 11:
                    case 14:
                    case 15:
                      Pj(4, r, r.return);
                      break;
                    case 1:
                      Lj(r, r.return);
                      var n = r.stateNode;
                      if ("function" === typeof n.componentWillUnmount) {
                        d = r;
                        c = r.return;
                        try {
                          b = d, n.props = b.memoizedProps, n.state = b.memoizedState, n.componentWillUnmount();
                        } catch (t) {
                          W(d, c, t);
                        }
                      }
                      break;
                    case 5:
                      Lj(r, r.return);
                      break;
                    case 22:
                      if (null !== r.memoizedState) {
                        gk(q);
                        continue;
                      }
                  }
                  null !== y ? (y.return = r, V = y) : gk(q);
                }
                m = m.sibling;
              }
              a: for (m = null, q = a; ; ) {
                if (5 === q.tag) {
                  if (null === m) {
                    m = q;
                    try {
                      e = q.stateNode, l ? (f = e.style, "function" === typeof f.setProperty ? f.setProperty("display", "none", "important") : f.display = "none") : (h = q.stateNode, k = q.memoizedProps.style, g = void 0 !== k && null !== k && k.hasOwnProperty("display") ? k.display : null, h.style.display = rb("display", g));
                    } catch (t) {
                      W(a, a.return, t);
                    }
                  }
                } else if (6 === q.tag) {
                  if (null === m) try {
                    q.stateNode.nodeValue = l ? "" : q.memoizedProps;
                  } catch (t) {
                    W(a, a.return, t);
                  }
                } else if ((22 !== q.tag && 23 !== q.tag || null === q.memoizedState || q === a) && null !== q.child) {
                  q.child.return = q;
                  q = q.child;
                  continue;
                }
                if (q === a) break a;
                for (; null === q.sibling; ) {
                  if (null === q.return || q.return === a) break a;
                  m === q && (m = null);
                  q = q.return;
                }
                m === q && (m = null);
                q.sibling.return = q.return;
                q = q.sibling;
              }
            }
            break;
          case 19:
            ck(b, a);
            ek(a);
            d & 4 && ak(a);
            break;
          case 21:
            break;
          default:
            ck(
              b,
              a
            ), ek(a);
        }
      }
      function ek(a) {
        var b = a.flags;
        if (b & 2) {
          try {
            a: {
              for (var c = a.return; null !== c; ) {
                if (Tj(c)) {
                  var d = c;
                  break a;
                }
                c = c.return;
              }
              throw Error(p(160));
            }
            switch (d.tag) {
              case 5:
                var e = d.stateNode;
                d.flags & 32 && (ob(e, ""), d.flags &= -33);
                var f = Uj(a);
                Wj(a, f, e);
                break;
              case 3:
              case 4:
                var g = d.stateNode.containerInfo, h = Uj(a);
                Vj(a, h, g);
                break;
              default:
                throw Error(p(161));
            }
          } catch (k) {
            W(a, a.return, k);
          }
          a.flags &= -3;
        }
        b & 4096 && (a.flags &= -4097);
      }
      function hk(a, b, c) {
        V = a;
        ik(a, b, c);
      }
      function ik(a, b, c) {
        for (var d = 0 !== (a.mode & 1); null !== V; ) {
          var e = V, f = e.child;
          if (22 === e.tag && d) {
            var g = null !== e.memoizedState || Jj;
            if (!g) {
              var h = e.alternate, k = null !== h && null !== h.memoizedState || U;
              h = Jj;
              var l = U;
              Jj = g;
              if ((U = k) && !l) for (V = e; null !== V; ) g = V, k = g.child, 22 === g.tag && null !== g.memoizedState ? jk(e) : null !== k ? (k.return = g, V = k) : jk(e);
              for (; null !== f; ) V = f, ik(f, b, c), f = f.sibling;
              V = e;
              Jj = h;
              U = l;
            }
            kk(a, b, c);
          } else 0 !== (e.subtreeFlags & 8772) && null !== f ? (f.return = e, V = f) : kk(a, b, c);
        }
      }
      function kk(a) {
        for (; null !== V; ) {
          var b = V;
          if (0 !== (b.flags & 8772)) {
            var c = b.alternate;
            try {
              if (0 !== (b.flags & 8772)) switch (b.tag) {
                case 0:
                case 11:
                case 15:
                  U || Qj(5, b);
                  break;
                case 1:
                  var d = b.stateNode;
                  if (b.flags & 4 && !U) if (null === c) d.componentDidMount();
                  else {
                    var e = b.elementType === b.type ? c.memoizedProps : Ci(b.type, c.memoizedProps);
                    d.componentDidUpdate(e, c.memoizedState, d.__reactInternalSnapshotBeforeUpdate);
                  }
                  var f = b.updateQueue;
                  null !== f && sh(b, f, d);
                  break;
                case 3:
                  var g = b.updateQueue;
                  if (null !== g) {
                    c = null;
                    if (null !== b.child) switch (b.child.tag) {
                      case 5:
                        c = b.child.stateNode;
                        break;
                      case 1:
                        c = b.child.stateNode;
                    }
                    sh(b, g, c);
                  }
                  break;
                case 5:
                  var h = b.stateNode;
                  if (null === c && b.flags & 4) {
                    c = h;
                    var k = b.memoizedProps;
                    switch (b.type) {
                      case "button":
                      case "input":
                      case "select":
                      case "textarea":
                        k.autoFocus && c.focus();
                        break;
                      case "img":
                        k.src && (c.src = k.src);
                    }
                  }
                  break;
                case 6:
                  break;
                case 4:
                  break;
                case 12:
                  break;
                case 13:
                  if (null === b.memoizedState) {
                    var l = b.alternate;
                    if (null !== l) {
                      var m = l.memoizedState;
                      if (null !== m) {
                        var q = m.dehydrated;
                        null !== q && bd(q);
                      }
                    }
                  }
                  break;
                case 19:
                case 17:
                case 21:
                case 22:
                case 23:
                case 25:
                  break;
                default:
                  throw Error(p(163));
              }
              U || b.flags & 512 && Rj(b);
            } catch (r) {
              W(b, b.return, r);
            }
          }
          if (b === a) {
            V = null;
            break;
          }
          c = b.sibling;
          if (null !== c) {
            c.return = b.return;
            V = c;
            break;
          }
          V = b.return;
        }
      }
      function gk(a) {
        for (; null !== V; ) {
          var b = V;
          if (b === a) {
            V = null;
            break;
          }
          var c = b.sibling;
          if (null !== c) {
            c.return = b.return;
            V = c;
            break;
          }
          V = b.return;
        }
      }
      function jk(a) {
        for (; null !== V; ) {
          var b = V;
          try {
            switch (b.tag) {
              case 0:
              case 11:
              case 15:
                var c = b.return;
                try {
                  Qj(4, b);
                } catch (k) {
                  W(b, c, k);
                }
                break;
              case 1:
                var d = b.stateNode;
                if ("function" === typeof d.componentDidMount) {
                  var e = b.return;
                  try {
                    d.componentDidMount();
                  } catch (k) {
                    W(b, e, k);
                  }
                }
                var f = b.return;
                try {
                  Rj(b);
                } catch (k) {
                  W(b, f, k);
                }
                break;
              case 5:
                var g = b.return;
                try {
                  Rj(b);
                } catch (k) {
                  W(b, g, k);
                }
            }
          } catch (k) {
            W(b, b.return, k);
          }
          if (b === a) {
            V = null;
            break;
          }
          var h = b.sibling;
          if (null !== h) {
            h.return = b.return;
            V = h;
            break;
          }
          V = b.return;
        }
      }
      var lk = Math.ceil;
      var mk = ua.ReactCurrentDispatcher;
      var nk = ua.ReactCurrentOwner;
      var ok = ua.ReactCurrentBatchConfig;
      var K = 0;
      var Q = null;
      var Y = null;
      var Z = 0;
      var fj = 0;
      var ej = Uf(0);
      var T = 0;
      var pk = null;
      var rh = 0;
      var qk = 0;
      var rk = 0;
      var sk = null;
      var tk = null;
      var fk = 0;
      var Gj = Infinity;
      var uk = null;
      var Oi = false;
      var Pi = null;
      var Ri = null;
      var vk = false;
      var wk = null;
      var xk = 0;
      var yk = 0;
      var zk = null;
      var Ak = -1;
      var Bk = 0;
      function R() {
        return 0 !== (K & 6) ? B() : -1 !== Ak ? Ak : Ak = B();
      }
      function yi(a) {
        if (0 === (a.mode & 1)) return 1;
        if (0 !== (K & 2) && 0 !== Z) return Z & -Z;
        if (null !== Kg.transition) return 0 === Bk && (Bk = yc()), Bk;
        a = C;
        if (0 !== a) return a;
        a = window.event;
        a = void 0 === a ? 16 : jd(a.type);
        return a;
      }
      function gi(a, b, c, d) {
        if (50 < yk) throw yk = 0, zk = null, Error(p(185));
        Ac(a, c, d);
        if (0 === (K & 2) || a !== Q) a === Q && (0 === (K & 2) && (qk |= c), 4 === T && Ck(a, Z)), Dk(a, d), 1 === c && 0 === K && 0 === (b.mode & 1) && (Gj = B() + 500, fg && jg());
      }
      function Dk(a, b) {
        var c = a.callbackNode;
        wc(a, b);
        var d = uc(a, a === Q ? Z : 0);
        if (0 === d) null !== c && bc(c), a.callbackNode = null, a.callbackPriority = 0;
        else if (b = d & -d, a.callbackPriority !== b) {
          null != c && bc(c);
          if (1 === b) 0 === a.tag ? ig(Ek.bind(null, a)) : hg(Ek.bind(null, a)), Jf(function() {
            0 === (K & 6) && jg();
          }), c = null;
          else {
            switch (Dc(d)) {
              case 1:
                c = fc;
                break;
              case 4:
                c = gc;
                break;
              case 16:
                c = hc;
                break;
              case 536870912:
                c = jc;
                break;
              default:
                c = hc;
            }
            c = Fk(c, Gk.bind(null, a));
          }
          a.callbackPriority = b;
          a.callbackNode = c;
        }
      }
      function Gk(a, b) {
        Ak = -1;
        Bk = 0;
        if (0 !== (K & 6)) throw Error(p(327));
        var c = a.callbackNode;
        if (Hk() && a.callbackNode !== c) return null;
        var d = uc(a, a === Q ? Z : 0);
        if (0 === d) return null;
        if (0 !== (d & 30) || 0 !== (d & a.expiredLanes) || b) b = Ik(a, d);
        else {
          b = d;
          var e = K;
          K |= 2;
          var f = Jk();
          if (Q !== a || Z !== b) uk = null, Gj = B() + 500, Kk(a, b);
          do
            try {
              Lk();
              break;
            } catch (h) {
              Mk(a, h);
            }
          while (1);
          $g();
          mk.current = f;
          K = e;
          null !== Y ? b = 0 : (Q = null, Z = 0, b = T);
        }
        if (0 !== b) {
          2 === b && (e = xc(a), 0 !== e && (d = e, b = Nk(a, e)));
          if (1 === b) throw c = pk, Kk(a, 0), Ck(a, d), Dk(a, B()), c;
          if (6 === b) Ck(a, d);
          else {
            e = a.current.alternate;
            if (0 === (d & 30) && !Ok(e) && (b = Ik(a, d), 2 === b && (f = xc(a), 0 !== f && (d = f, b = Nk(a, f))), 1 === b)) throw c = pk, Kk(a, 0), Ck(a, d), Dk(a, B()), c;
            a.finishedWork = e;
            a.finishedLanes = d;
            switch (b) {
              case 0:
              case 1:
                throw Error(p(345));
              case 2:
                Pk(a, tk, uk);
                break;
              case 3:
                Ck(a, d);
                if ((d & 130023424) === d && (b = fk + 500 - B(), 10 < b)) {
                  if (0 !== uc(a, 0)) break;
                  e = a.suspendedLanes;
                  if ((e & d) !== d) {
                    R();
                    a.pingedLanes |= a.suspendedLanes & e;
                    break;
                  }
                  a.timeoutHandle = Ff(Pk.bind(null, a, tk, uk), b);
                  break;
                }
                Pk(a, tk, uk);
                break;
              case 4:
                Ck(a, d);
                if ((d & 4194240) === d) break;
                b = a.eventTimes;
                for (e = -1; 0 < d; ) {
                  var g = 31 - oc(d);
                  f = 1 << g;
                  g = b[g];
                  g > e && (e = g);
                  d &= ~f;
                }
                d = e;
                d = B() - d;
                d = (120 > d ? 120 : 480 > d ? 480 : 1080 > d ? 1080 : 1920 > d ? 1920 : 3e3 > d ? 3e3 : 4320 > d ? 4320 : 1960 * lk(d / 1960)) - d;
                if (10 < d) {
                  a.timeoutHandle = Ff(Pk.bind(null, a, tk, uk), d);
                  break;
                }
                Pk(a, tk, uk);
                break;
              case 5:
                Pk(a, tk, uk);
                break;
              default:
                throw Error(p(329));
            }
          }
        }
        Dk(a, B());
        return a.callbackNode === c ? Gk.bind(null, a) : null;
      }
      function Nk(a, b) {
        var c = sk;
        a.current.memoizedState.isDehydrated && (Kk(a, b).flags |= 256);
        a = Ik(a, b);
        2 !== a && (b = tk, tk = c, null !== b && Fj(b));
        return a;
      }
      function Fj(a) {
        null === tk ? tk = a : tk.push.apply(tk, a);
      }
      function Ok(a) {
        for (var b = a; ; ) {
          if (b.flags & 16384) {
            var c = b.updateQueue;
            if (null !== c && (c = c.stores, null !== c)) for (var d = 0; d < c.length; d++) {
              var e = c[d], f = e.getSnapshot;
              e = e.value;
              try {
                if (!He(f(), e)) return false;
              } catch (g) {
                return false;
              }
            }
          }
          c = b.child;
          if (b.subtreeFlags & 16384 && null !== c) c.return = b, b = c;
          else {
            if (b === a) break;
            for (; null === b.sibling; ) {
              if (null === b.return || b.return === a) return true;
              b = b.return;
            }
            b.sibling.return = b.return;
            b = b.sibling;
          }
        }
        return true;
      }
      function Ck(a, b) {
        b &= ~rk;
        b &= ~qk;
        a.suspendedLanes |= b;
        a.pingedLanes &= ~b;
        for (a = a.expirationTimes; 0 < b; ) {
          var c = 31 - oc(b), d = 1 << c;
          a[c] = -1;
          b &= ~d;
        }
      }
      function Ek(a) {
        if (0 !== (K & 6)) throw Error(p(327));
        Hk();
        var b = uc(a, 0);
        if (0 === (b & 1)) return Dk(a, B()), null;
        var c = Ik(a, b);
        if (0 !== a.tag && 2 === c) {
          var d = xc(a);
          0 !== d && (b = d, c = Nk(a, d));
        }
        if (1 === c) throw c = pk, Kk(a, 0), Ck(a, b), Dk(a, B()), c;
        if (6 === c) throw Error(p(345));
        a.finishedWork = a.current.alternate;
        a.finishedLanes = b;
        Pk(a, tk, uk);
        Dk(a, B());
        return null;
      }
      function Qk(a, b) {
        var c = K;
        K |= 1;
        try {
          return a(b);
        } finally {
          K = c, 0 === K && (Gj = B() + 500, fg && jg());
        }
      }
      function Rk(a) {
        null !== wk && 0 === wk.tag && 0 === (K & 6) && Hk();
        var b = K;
        K |= 1;
        var c = ok.transition, d = C;
        try {
          if (ok.transition = null, C = 1, a) return a();
        } finally {
          C = d, ok.transition = c, K = b, 0 === (K & 6) && jg();
        }
      }
      function Hj() {
        fj = ej.current;
        E(ej);
      }
      function Kk(a, b) {
        a.finishedWork = null;
        a.finishedLanes = 0;
        var c = a.timeoutHandle;
        -1 !== c && (a.timeoutHandle = -1, Gf(c));
        if (null !== Y) for (c = Y.return; null !== c; ) {
          var d = c;
          wg(d);
          switch (d.tag) {
            case 1:
              d = d.type.childContextTypes;
              null !== d && void 0 !== d && $f();
              break;
            case 3:
              zh();
              E(Wf);
              E(H);
              Eh();
              break;
            case 5:
              Bh(d);
              break;
            case 4:
              zh();
              break;
            case 13:
              E(L);
              break;
            case 19:
              E(L);
              break;
            case 10:
              ah(d.type._context);
              break;
            case 22:
            case 23:
              Hj();
          }
          c = c.return;
        }
        Q = a;
        Y = a = Pg(a.current, null);
        Z = fj = b;
        T = 0;
        pk = null;
        rk = qk = rh = 0;
        tk = sk = null;
        if (null !== fh) {
          for (b = 0; b < fh.length; b++) if (c = fh[b], d = c.interleaved, null !== d) {
            c.interleaved = null;
            var e = d.next, f = c.pending;
            if (null !== f) {
              var g = f.next;
              f.next = e;
              d.next = g;
            }
            c.pending = d;
          }
          fh = null;
        }
        return a;
      }
      function Mk(a, b) {
        do {
          var c = Y;
          try {
            $g();
            Fh.current = Rh;
            if (Ih) {
              for (var d = M.memoizedState; null !== d; ) {
                var e = d.queue;
                null !== e && (e.pending = null);
                d = d.next;
              }
              Ih = false;
            }
            Hh = 0;
            O = N = M = null;
            Jh = false;
            Kh = 0;
            nk.current = null;
            if (null === c || null === c.return) {
              T = 1;
              pk = b;
              Y = null;
              break;
            }
            a: {
              var f = a, g = c.return, h = c, k = b;
              b = Z;
              h.flags |= 32768;
              if (null !== k && "object" === typeof k && "function" === typeof k.then) {
                var l = k, m = h, q = m.tag;
                if (0 === (m.mode & 1) && (0 === q || 11 === q || 15 === q)) {
                  var r = m.alternate;
                  r ? (m.updateQueue = r.updateQueue, m.memoizedState = r.memoizedState, m.lanes = r.lanes) : (m.updateQueue = null, m.memoizedState = null);
                }
                var y = Ui(g);
                if (null !== y) {
                  y.flags &= -257;
                  Vi(y, g, h, f, b);
                  y.mode & 1 && Si(f, l, b);
                  b = y;
                  k = l;
                  var n = b.updateQueue;
                  if (null === n) {
                    var t = /* @__PURE__ */ new Set();
                    t.add(k);
                    b.updateQueue = t;
                  } else n.add(k);
                  break a;
                } else {
                  if (0 === (b & 1)) {
                    Si(f, l, b);
                    tj();
                    break a;
                  }
                  k = Error(p(426));
                }
              } else if (I && h.mode & 1) {
                var J = Ui(g);
                if (null !== J) {
                  0 === (J.flags & 65536) && (J.flags |= 256);
                  Vi(J, g, h, f, b);
                  Jg(Ji(k, h));
                  break a;
                }
              }
              f = k = Ji(k, h);
              4 !== T && (T = 2);
              null === sk ? sk = [f] : sk.push(f);
              f = g;
              do {
                switch (f.tag) {
                  case 3:
                    f.flags |= 65536;
                    b &= -b;
                    f.lanes |= b;
                    var x = Ni(f, k, b);
                    ph(f, x);
                    break a;
                  case 1:
                    h = k;
                    var w = f.type, u = f.stateNode;
                    if (0 === (f.flags & 128) && ("function" === typeof w.getDerivedStateFromError || null !== u && "function" === typeof u.componentDidCatch && (null === Ri || !Ri.has(u)))) {
                      f.flags |= 65536;
                      b &= -b;
                      f.lanes |= b;
                      var F = Qi(f, h, b);
                      ph(f, F);
                      break a;
                    }
                }
                f = f.return;
              } while (null !== f);
            }
            Sk(c);
          } catch (na) {
            b = na;
            Y === c && null !== c && (Y = c = c.return);
            continue;
          }
          break;
        } while (1);
      }
      function Jk() {
        var a = mk.current;
        mk.current = Rh;
        return null === a ? Rh : a;
      }
      function tj() {
        if (0 === T || 3 === T || 2 === T) T = 4;
        null === Q || 0 === (rh & 268435455) && 0 === (qk & 268435455) || Ck(Q, Z);
      }
      function Ik(a, b) {
        var c = K;
        K |= 2;
        var d = Jk();
        if (Q !== a || Z !== b) uk = null, Kk(a, b);
        do
          try {
            Tk();
            break;
          } catch (e) {
            Mk(a, e);
          }
        while (1);
        $g();
        K = c;
        mk.current = d;
        if (null !== Y) throw Error(p(261));
        Q = null;
        Z = 0;
        return T;
      }
      function Tk() {
        for (; null !== Y; ) Uk(Y);
      }
      function Lk() {
        for (; null !== Y && !cc(); ) Uk(Y);
      }
      function Uk(a) {
        var b = Vk(a.alternate, a, fj);
        a.memoizedProps = a.pendingProps;
        null === b ? Sk(a) : Y = b;
        nk.current = null;
      }
      function Sk(a) {
        var b = a;
        do {
          var c = b.alternate;
          a = b.return;
          if (0 === (b.flags & 32768)) {
            if (c = Ej(c, b, fj), null !== c) {
              Y = c;
              return;
            }
          } else {
            c = Ij(c, b);
            if (null !== c) {
              c.flags &= 32767;
              Y = c;
              return;
            }
            if (null !== a) a.flags |= 32768, a.subtreeFlags = 0, a.deletions = null;
            else {
              T = 6;
              Y = null;
              return;
            }
          }
          b = b.sibling;
          if (null !== b) {
            Y = b;
            return;
          }
          Y = b = a;
        } while (null !== b);
        0 === T && (T = 5);
      }
      function Pk(a, b, c) {
        var d = C, e = ok.transition;
        try {
          ok.transition = null, C = 1, Wk(a, b, c, d);
        } finally {
          ok.transition = e, C = d;
        }
        return null;
      }
      function Wk(a, b, c, d) {
        do
          Hk();
        while (null !== wk);
        if (0 !== (K & 6)) throw Error(p(327));
        c = a.finishedWork;
        var e = a.finishedLanes;
        if (null === c) return null;
        a.finishedWork = null;
        a.finishedLanes = 0;
        if (c === a.current) throw Error(p(177));
        a.callbackNode = null;
        a.callbackPriority = 0;
        var f = c.lanes | c.childLanes;
        Bc(a, f);
        a === Q && (Y = Q = null, Z = 0);
        0 === (c.subtreeFlags & 2064) && 0 === (c.flags & 2064) || vk || (vk = true, Fk(hc, function() {
          Hk();
          return null;
        }));
        f = 0 !== (c.flags & 15990);
        if (0 !== (c.subtreeFlags & 15990) || f) {
          f = ok.transition;
          ok.transition = null;
          var g = C;
          C = 1;
          var h = K;
          K |= 4;
          nk.current = null;
          Oj(a, c);
          dk(c, a);
          Oe(Df);
          dd = !!Cf;
          Df = Cf = null;
          a.current = c;
          hk(c, a, e);
          dc();
          K = h;
          C = g;
          ok.transition = f;
        } else a.current = c;
        vk && (vk = false, wk = a, xk = e);
        f = a.pendingLanes;
        0 === f && (Ri = null);
        mc(c.stateNode, d);
        Dk(a, B());
        if (null !== b) for (d = a.onRecoverableError, c = 0; c < b.length; c++) e = b[c], d(e.value, { componentStack: e.stack, digest: e.digest });
        if (Oi) throw Oi = false, a = Pi, Pi = null, a;
        0 !== (xk & 1) && 0 !== a.tag && Hk();
        f = a.pendingLanes;
        0 !== (f & 1) ? a === zk ? yk++ : (yk = 0, zk = a) : yk = 0;
        jg();
        return null;
      }
      function Hk() {
        if (null !== wk) {
          var a = Dc(xk), b = ok.transition, c = C;
          try {
            ok.transition = null;
            C = 16 > a ? 16 : a;
            if (null === wk) var d = false;
            else {
              a = wk;
              wk = null;
              xk = 0;
              if (0 !== (K & 6)) throw Error(p(331));
              var e = K;
              K |= 4;
              for (V = a.current; null !== V; ) {
                var f = V, g = f.child;
                if (0 !== (V.flags & 16)) {
                  var h = f.deletions;
                  if (null !== h) {
                    for (var k = 0; k < h.length; k++) {
                      var l = h[k];
                      for (V = l; null !== V; ) {
                        var m = V;
                        switch (m.tag) {
                          case 0:
                          case 11:
                          case 15:
                            Pj(8, m, f);
                        }
                        var q = m.child;
                        if (null !== q) q.return = m, V = q;
                        else for (; null !== V; ) {
                          m = V;
                          var r = m.sibling, y = m.return;
                          Sj(m);
                          if (m === l) {
                            V = null;
                            break;
                          }
                          if (null !== r) {
                            r.return = y;
                            V = r;
                            break;
                          }
                          V = y;
                        }
                      }
                    }
                    var n = f.alternate;
                    if (null !== n) {
                      var t = n.child;
                      if (null !== t) {
                        n.child = null;
                        do {
                          var J = t.sibling;
                          t.sibling = null;
                          t = J;
                        } while (null !== t);
                      }
                    }
                    V = f;
                  }
                }
                if (0 !== (f.subtreeFlags & 2064) && null !== g) g.return = f, V = g;
                else b: for (; null !== V; ) {
                  f = V;
                  if (0 !== (f.flags & 2048)) switch (f.tag) {
                    case 0:
                    case 11:
                    case 15:
                      Pj(9, f, f.return);
                  }
                  var x = f.sibling;
                  if (null !== x) {
                    x.return = f.return;
                    V = x;
                    break b;
                  }
                  V = f.return;
                }
              }
              var w = a.current;
              for (V = w; null !== V; ) {
                g = V;
                var u = g.child;
                if (0 !== (g.subtreeFlags & 2064) && null !== u) u.return = g, V = u;
                else b: for (g = w; null !== V; ) {
                  h = V;
                  if (0 !== (h.flags & 2048)) try {
                    switch (h.tag) {
                      case 0:
                      case 11:
                      case 15:
                        Qj(9, h);
                    }
                  } catch (na) {
                    W(h, h.return, na);
                  }
                  if (h === g) {
                    V = null;
                    break b;
                  }
                  var F = h.sibling;
                  if (null !== F) {
                    F.return = h.return;
                    V = F;
                    break b;
                  }
                  V = h.return;
                }
              }
              K = e;
              jg();
              if (lc && "function" === typeof lc.onPostCommitFiberRoot) try {
                lc.onPostCommitFiberRoot(kc, a);
              } catch (na) {
              }
              d = true;
            }
            return d;
          } finally {
            C = c, ok.transition = b;
          }
        }
        return false;
      }
      function Xk(a, b, c) {
        b = Ji(c, b);
        b = Ni(a, b, 1);
        a = nh(a, b, 1);
        b = R();
        null !== a && (Ac(a, 1, b), Dk(a, b));
      }
      function W(a, b, c) {
        if (3 === a.tag) Xk(a, a, c);
        else for (; null !== b; ) {
          if (3 === b.tag) {
            Xk(b, a, c);
            break;
          } else if (1 === b.tag) {
            var d = b.stateNode;
            if ("function" === typeof b.type.getDerivedStateFromError || "function" === typeof d.componentDidCatch && (null === Ri || !Ri.has(d))) {
              a = Ji(c, a);
              a = Qi(b, a, 1);
              b = nh(b, a, 1);
              a = R();
              null !== b && (Ac(b, 1, a), Dk(b, a));
              break;
            }
          }
          b = b.return;
        }
      }
      function Ti(a, b, c) {
        var d = a.pingCache;
        null !== d && d.delete(b);
        b = R();
        a.pingedLanes |= a.suspendedLanes & c;
        Q === a && (Z & c) === c && (4 === T || 3 === T && (Z & 130023424) === Z && 500 > B() - fk ? Kk(a, 0) : rk |= c);
        Dk(a, b);
      }
      function Yk(a, b) {
        0 === b && (0 === (a.mode & 1) ? b = 1 : (b = sc, sc <<= 1, 0 === (sc & 130023424) && (sc = 4194304)));
        var c = R();
        a = ih(a, b);
        null !== a && (Ac(a, b, c), Dk(a, c));
      }
      function uj(a) {
        var b = a.memoizedState, c = 0;
        null !== b && (c = b.retryLane);
        Yk(a, c);
      }
      function bk(a, b) {
        var c = 0;
        switch (a.tag) {
          case 13:
            var d = a.stateNode;
            var e = a.memoizedState;
            null !== e && (c = e.retryLane);
            break;
          case 19:
            d = a.stateNode;
            break;
          default:
            throw Error(p(314));
        }
        null !== d && d.delete(b);
        Yk(a, c);
      }
      var Vk;
      Vk = function(a, b, c) {
        if (null !== a) if (a.memoizedProps !== b.pendingProps || Wf.current) dh = true;
        else {
          if (0 === (a.lanes & c) && 0 === (b.flags & 128)) return dh = false, yj(a, b, c);
          dh = 0 !== (a.flags & 131072) ? true : false;
        }
        else dh = false, I && 0 !== (b.flags & 1048576) && ug(b, ng, b.index);
        b.lanes = 0;
        switch (b.tag) {
          case 2:
            var d = b.type;
            ij(a, b);
            a = b.pendingProps;
            var e = Yf(b, H.current);
            ch(b, c);
            e = Nh(null, b, d, a, e, c);
            var f = Sh();
            b.flags |= 1;
            "object" === typeof e && null !== e && "function" === typeof e.render && void 0 === e.$$typeof ? (b.tag = 1, b.memoizedState = null, b.updateQueue = null, Zf(d) ? (f = true, cg(b)) : f = false, b.memoizedState = null !== e.state && void 0 !== e.state ? e.state : null, kh(b), e.updater = Ei, b.stateNode = e, e._reactInternals = b, Ii(b, d, a, c), b = jj(null, b, d, true, f, c)) : (b.tag = 0, I && f && vg(b), Xi(null, b, e, c), b = b.child);
            return b;
          case 16:
            d = b.elementType;
            a: {
              ij(a, b);
              a = b.pendingProps;
              e = d._init;
              d = e(d._payload);
              b.type = d;
              e = b.tag = Zk(d);
              a = Ci(d, a);
              switch (e) {
                case 0:
                  b = cj(null, b, d, a, c);
                  break a;
                case 1:
                  b = hj(null, b, d, a, c);
                  break a;
                case 11:
                  b = Yi(null, b, d, a, c);
                  break a;
                case 14:
                  b = $i(null, b, d, Ci(d.type, a), c);
                  break a;
              }
              throw Error(p(
                306,
                d,
                ""
              ));
            }
            return b;
          case 0:
            return d = b.type, e = b.pendingProps, e = b.elementType === d ? e : Ci(d, e), cj(a, b, d, e, c);
          case 1:
            return d = b.type, e = b.pendingProps, e = b.elementType === d ? e : Ci(d, e), hj(a, b, d, e, c);
          case 3:
            a: {
              kj(b);
              if (null === a) throw Error(p(387));
              d = b.pendingProps;
              f = b.memoizedState;
              e = f.element;
              lh(a, b);
              qh(b, d, null, c);
              var g = b.memoizedState;
              d = g.element;
              if (f.isDehydrated) if (f = { element: d, isDehydrated: false, cache: g.cache, pendingSuspenseBoundaries: g.pendingSuspenseBoundaries, transitions: g.transitions }, b.updateQueue.baseState = f, b.memoizedState = f, b.flags & 256) {
                e = Ji(Error(p(423)), b);
                b = lj(a, b, d, c, e);
                break a;
              } else if (d !== e) {
                e = Ji(Error(p(424)), b);
                b = lj(a, b, d, c, e);
                break a;
              } else for (yg = Lf(b.stateNode.containerInfo.firstChild), xg = b, I = true, zg = null, c = Vg(b, null, d, c), b.child = c; c; ) c.flags = c.flags & -3 | 4096, c = c.sibling;
              else {
                Ig();
                if (d === e) {
                  b = Zi(a, b, c);
                  break a;
                }
                Xi(a, b, d, c);
              }
              b = b.child;
            }
            return b;
          case 5:
            return Ah(b), null === a && Eg(b), d = b.type, e = b.pendingProps, f = null !== a ? a.memoizedProps : null, g = e.children, Ef(d, e) ? g = null : null !== f && Ef(d, f) && (b.flags |= 32), gj(a, b), Xi(a, b, g, c), b.child;
          case 6:
            return null === a && Eg(b), null;
          case 13:
            return oj(a, b, c);
          case 4:
            return yh(b, b.stateNode.containerInfo), d = b.pendingProps, null === a ? b.child = Ug(b, null, d, c) : Xi(a, b, d, c), b.child;
          case 11:
            return d = b.type, e = b.pendingProps, e = b.elementType === d ? e : Ci(d, e), Yi(a, b, d, e, c);
          case 7:
            return Xi(a, b, b.pendingProps, c), b.child;
          case 8:
            return Xi(a, b, b.pendingProps.children, c), b.child;
          case 12:
            return Xi(a, b, b.pendingProps.children, c), b.child;
          case 10:
            a: {
              d = b.type._context;
              e = b.pendingProps;
              f = b.memoizedProps;
              g = e.value;
              G(Wg, d._currentValue);
              d._currentValue = g;
              if (null !== f) if (He(f.value, g)) {
                if (f.children === e.children && !Wf.current) {
                  b = Zi(a, b, c);
                  break a;
                }
              } else for (f = b.child, null !== f && (f.return = b); null !== f; ) {
                var h = f.dependencies;
                if (null !== h) {
                  g = f.child;
                  for (var k = h.firstContext; null !== k; ) {
                    if (k.context === d) {
                      if (1 === f.tag) {
                        k = mh(-1, c & -c);
                        k.tag = 2;
                        var l = f.updateQueue;
                        if (null !== l) {
                          l = l.shared;
                          var m = l.pending;
                          null === m ? k.next = k : (k.next = m.next, m.next = k);
                          l.pending = k;
                        }
                      }
                      f.lanes |= c;
                      k = f.alternate;
                      null !== k && (k.lanes |= c);
                      bh(
                        f.return,
                        c,
                        b
                      );
                      h.lanes |= c;
                      break;
                    }
                    k = k.next;
                  }
                } else if (10 === f.tag) g = f.type === b.type ? null : f.child;
                else if (18 === f.tag) {
                  g = f.return;
                  if (null === g) throw Error(p(341));
                  g.lanes |= c;
                  h = g.alternate;
                  null !== h && (h.lanes |= c);
                  bh(g, c, b);
                  g = f.sibling;
                } else g = f.child;
                if (null !== g) g.return = f;
                else for (g = f; null !== g; ) {
                  if (g === b) {
                    g = null;
                    break;
                  }
                  f = g.sibling;
                  if (null !== f) {
                    f.return = g.return;
                    g = f;
                    break;
                  }
                  g = g.return;
                }
                f = g;
              }
              Xi(a, b, e.children, c);
              b = b.child;
            }
            return b;
          case 9:
            return e = b.type, d = b.pendingProps.children, ch(b, c), e = eh(e), d = d(e), b.flags |= 1, Xi(a, b, d, c), b.child;
          case 14:
            return d = b.type, e = Ci(d, b.pendingProps), e = Ci(d.type, e), $i(a, b, d, e, c);
          case 15:
            return bj(a, b, b.type, b.pendingProps, c);
          case 17:
            return d = b.type, e = b.pendingProps, e = b.elementType === d ? e : Ci(d, e), ij(a, b), b.tag = 1, Zf(d) ? (a = true, cg(b)) : a = false, ch(b, c), Gi(b, d, e), Ii(b, d, e, c), jj(null, b, d, true, a, c);
          case 19:
            return xj(a, b, c);
          case 22:
            return dj(a, b, c);
        }
        throw Error(p(156, b.tag));
      };
      function Fk(a, b) {
        return ac(a, b);
      }
      function $k(a, b, c, d) {
        this.tag = a;
        this.key = c;
        this.sibling = this.child = this.return = this.stateNode = this.type = this.elementType = null;
        this.index = 0;
        this.ref = null;
        this.pendingProps = b;
        this.dependencies = this.memoizedState = this.updateQueue = this.memoizedProps = null;
        this.mode = d;
        this.subtreeFlags = this.flags = 0;
        this.deletions = null;
        this.childLanes = this.lanes = 0;
        this.alternate = null;
      }
      function Bg(a, b, c, d) {
        return new $k(a, b, c, d);
      }
      function aj(a) {
        a = a.prototype;
        return !(!a || !a.isReactComponent);
      }
      function Zk(a) {
        if ("function" === typeof a) return aj(a) ? 1 : 0;
        if (void 0 !== a && null !== a) {
          a = a.$$typeof;
          if (a === Da) return 11;
          if (a === Ga) return 14;
        }
        return 2;
      }
      function Pg(a, b) {
        var c = a.alternate;
        null === c ? (c = Bg(a.tag, b, a.key, a.mode), c.elementType = a.elementType, c.type = a.type, c.stateNode = a.stateNode, c.alternate = a, a.alternate = c) : (c.pendingProps = b, c.type = a.type, c.flags = 0, c.subtreeFlags = 0, c.deletions = null);
        c.flags = a.flags & 14680064;
        c.childLanes = a.childLanes;
        c.lanes = a.lanes;
        c.child = a.child;
        c.memoizedProps = a.memoizedProps;
        c.memoizedState = a.memoizedState;
        c.updateQueue = a.updateQueue;
        b = a.dependencies;
        c.dependencies = null === b ? null : { lanes: b.lanes, firstContext: b.firstContext };
        c.sibling = a.sibling;
        c.index = a.index;
        c.ref = a.ref;
        return c;
      }
      function Rg(a, b, c, d, e, f) {
        var g = 2;
        d = a;
        if ("function" === typeof a) aj(a) && (g = 1);
        else if ("string" === typeof a) g = 5;
        else a: switch (a) {
          case ya:
            return Tg(c.children, e, f, b);
          case za:
            g = 8;
            e |= 8;
            break;
          case Aa:
            return a = Bg(12, c, b, e | 2), a.elementType = Aa, a.lanes = f, a;
          case Ea:
            return a = Bg(13, c, b, e), a.elementType = Ea, a.lanes = f, a;
          case Fa:
            return a = Bg(19, c, b, e), a.elementType = Fa, a.lanes = f, a;
          case Ia:
            return pj(c, e, f, b);
          default:
            if ("object" === typeof a && null !== a) switch (a.$$typeof) {
              case Ba:
                g = 10;
                break a;
              case Ca:
                g = 9;
                break a;
              case Da:
                g = 11;
                break a;
              case Ga:
                g = 14;
                break a;
              case Ha:
                g = 16;
                d = null;
                break a;
            }
            throw Error(p(130, null == a ? a : typeof a, ""));
        }
        b = Bg(g, c, b, e);
        b.elementType = a;
        b.type = d;
        b.lanes = f;
        return b;
      }
      function Tg(a, b, c, d) {
        a = Bg(7, a, d, b);
        a.lanes = c;
        return a;
      }
      function pj(a, b, c, d) {
        a = Bg(22, a, d, b);
        a.elementType = Ia;
        a.lanes = c;
        a.stateNode = { isHidden: false };
        return a;
      }
      function Qg(a, b, c) {
        a = Bg(6, a, null, b);
        a.lanes = c;
        return a;
      }
      function Sg(a, b, c) {
        b = Bg(4, null !== a.children ? a.children : [], a.key, b);
        b.lanes = c;
        b.stateNode = { containerInfo: a.containerInfo, pendingChildren: null, implementation: a.implementation };
        return b;
      }
      function al(a, b, c, d, e) {
        this.tag = b;
        this.containerInfo = a;
        this.finishedWork = this.pingCache = this.current = this.pendingChildren = null;
        this.timeoutHandle = -1;
        this.callbackNode = this.pendingContext = this.context = null;
        this.callbackPriority = 0;
        this.eventTimes = zc(0);
        this.expirationTimes = zc(-1);
        this.entangledLanes = this.finishedLanes = this.mutableReadLanes = this.expiredLanes = this.pingedLanes = this.suspendedLanes = this.pendingLanes = 0;
        this.entanglements = zc(0);
        this.identifierPrefix = d;
        this.onRecoverableError = e;
        this.mutableSourceEagerHydrationData = null;
      }
      function bl(a, b, c, d, e, f, g, h, k) {
        a = new al(a, b, c, h, k);
        1 === b ? (b = 1, true === f && (b |= 8)) : b = 0;
        f = Bg(3, null, null, b);
        a.current = f;
        f.stateNode = a;
        f.memoizedState = { element: d, isDehydrated: c, cache: null, transitions: null, pendingSuspenseBoundaries: null };
        kh(f);
        return a;
      }
      function cl(a, b, c) {
        var d = 3 < arguments.length && void 0 !== arguments[3] ? arguments[3] : null;
        return { $$typeof: wa, key: null == d ? null : "" + d, children: a, containerInfo: b, implementation: c };
      }
      function dl(a) {
        if (!a) return Vf;
        a = a._reactInternals;
        a: {
          if (Vb(a) !== a || 1 !== a.tag) throw Error(p(170));
          var b = a;
          do {
            switch (b.tag) {
              case 3:
                b = b.stateNode.context;
                break a;
              case 1:
                if (Zf(b.type)) {
                  b = b.stateNode.__reactInternalMemoizedMergedChildContext;
                  break a;
                }
            }
            b = b.return;
          } while (null !== b);
          throw Error(p(171));
        }
        if (1 === a.tag) {
          var c = a.type;
          if (Zf(c)) return bg(a, c, b);
        }
        return b;
      }
      function el(a, b, c, d, e, f, g, h, k) {
        a = bl(c, d, true, a, e, f, g, h, k);
        a.context = dl(null);
        c = a.current;
        d = R();
        e = yi(c);
        f = mh(d, e);
        f.callback = void 0 !== b && null !== b ? b : null;
        nh(c, f, e);
        a.current.lanes = e;
        Ac(a, e, d);
        Dk(a, d);
        return a;
      }
      function fl(a, b, c, d) {
        var e = b.current, f = R(), g = yi(e);
        c = dl(c);
        null === b.context ? b.context = c : b.pendingContext = c;
        b = mh(f, g);
        b.payload = { element: a };
        d = void 0 === d ? null : d;
        null !== d && (b.callback = d);
        a = nh(e, b, g);
        null !== a && (gi(a, e, g, f), oh(a, e, g));
        return g;
      }
      function gl(a) {
        a = a.current;
        if (!a.child) return null;
        switch (a.child.tag) {
          case 5:
            return a.child.stateNode;
          default:
            return a.child.stateNode;
        }
      }
      function hl(a, b) {
        a = a.memoizedState;
        if (null !== a && null !== a.dehydrated) {
          var c = a.retryLane;
          a.retryLane = 0 !== c && c < b ? c : b;
        }
      }
      function il(a, b) {
        hl(a, b);
        (a = a.alternate) && hl(a, b);
      }
      function jl() {
        return null;
      }
      var kl = "function" === typeof reportError ? reportError : function(a) {
        console.error(a);
      };
      function ll(a) {
        this._internalRoot = a;
      }
      ml.prototype.render = ll.prototype.render = function(a) {
        var b = this._internalRoot;
        if (null === b) throw Error(p(409));
        fl(a, b, null, null);
      };
      ml.prototype.unmount = ll.prototype.unmount = function() {
        var a = this._internalRoot;
        if (null !== a) {
          this._internalRoot = null;
          var b = a.containerInfo;
          Rk(function() {
            fl(null, a, null, null);
          });
          b[uf] = null;
        }
      };
      function ml(a) {
        this._internalRoot = a;
      }
      ml.prototype.unstable_scheduleHydration = function(a) {
        if (a) {
          var b = Hc();
          a = { blockedOn: null, target: a, priority: b };
          for (var c = 0; c < Qc.length && 0 !== b && b < Qc[c].priority; c++) ;
          Qc.splice(c, 0, a);
          0 === c && Vc(a);
        }
      };
      function nl(a) {
        return !(!a || 1 !== a.nodeType && 9 !== a.nodeType && 11 !== a.nodeType);
      }
      function ol(a) {
        return !(!a || 1 !== a.nodeType && 9 !== a.nodeType && 11 !== a.nodeType && (8 !== a.nodeType || " react-mount-point-unstable " !== a.nodeValue));
      }
      function pl() {
      }
      function ql(a, b, c, d, e) {
        if (e) {
          if ("function" === typeof d) {
            var f = d;
            d = function() {
              var a2 = gl(g);
              f.call(a2);
            };
          }
          var g = el(b, d, a, 0, null, false, false, "", pl);
          a._reactRootContainer = g;
          a[uf] = g.current;
          sf(8 === a.nodeType ? a.parentNode : a);
          Rk();
          return g;
        }
        for (; e = a.lastChild; ) a.removeChild(e);
        if ("function" === typeof d) {
          var h = d;
          d = function() {
            var a2 = gl(k);
            h.call(a2);
          };
        }
        var k = bl(a, 0, false, null, null, false, false, "", pl);
        a._reactRootContainer = k;
        a[uf] = k.current;
        sf(8 === a.nodeType ? a.parentNode : a);
        Rk(function() {
          fl(b, k, c, d);
        });
        return k;
      }
      function rl(a, b, c, d, e) {
        var f = c._reactRootContainer;
        if (f) {
          var g = f;
          if ("function" === typeof e) {
            var h = e;
            e = function() {
              var a2 = gl(g);
              h.call(a2);
            };
          }
          fl(b, g, a, e);
        } else g = ql(c, b, a, e, d);
        return gl(g);
      }
      Ec = function(a) {
        switch (a.tag) {
          case 3:
            var b = a.stateNode;
            if (b.current.memoizedState.isDehydrated) {
              var c = tc(b.pendingLanes);
              0 !== c && (Cc(b, c | 1), Dk(b, B()), 0 === (K & 6) && (Gj = B() + 500, jg()));
            }
            break;
          case 13:
            Rk(function() {
              var b2 = ih(a, 1);
              if (null !== b2) {
                var c2 = R();
                gi(b2, a, 1, c2);
              }
            }), il(a, 1);
        }
      };
      Fc = function(a) {
        if (13 === a.tag) {
          var b = ih(a, 134217728);
          if (null !== b) {
            var c = R();
            gi(b, a, 134217728, c);
          }
          il(a, 134217728);
        }
      };
      Gc = function(a) {
        if (13 === a.tag) {
          var b = yi(a), c = ih(a, b);
          if (null !== c) {
            var d = R();
            gi(c, a, b, d);
          }
          il(a, b);
        }
      };
      Hc = function() {
        return C;
      };
      Ic = function(a, b) {
        var c = C;
        try {
          return C = a, b();
        } finally {
          C = c;
        }
      };
      yb = function(a, b, c) {
        switch (b) {
          case "input":
            bb(a, c);
            b = c.name;
            if ("radio" === c.type && null != b) {
              for (c = a; c.parentNode; ) c = c.parentNode;
              c = c.querySelectorAll("input[name=" + JSON.stringify("" + b) + '][type="radio"]');
              for (b = 0; b < c.length; b++) {
                var d = c[b];
                if (d !== a && d.form === a.form) {
                  var e = Db(d);
                  if (!e) throw Error(p(90));
                  Wa(d);
                  bb(d, e);
                }
              }
            }
            break;
          case "textarea":
            ib(a, c);
            break;
          case "select":
            b = c.value, null != b && fb(a, !!c.multiple, b, false);
        }
      };
      Gb = Qk;
      Hb = Rk;
      var sl = { usingClientEntryPoint: false, Events: [Cb, ue, Db, Eb, Fb, Qk] };
      var tl = { findFiberByHostInstance: Wc, bundleType: 0, version: "18.3.1", rendererPackageName: "react-dom" };
      var ul = { bundleType: tl.bundleType, version: tl.version, rendererPackageName: tl.rendererPackageName, rendererConfig: tl.rendererConfig, overrideHookState: null, overrideHookStateDeletePath: null, overrideHookStateRenamePath: null, overrideProps: null, overridePropsDeletePath: null, overridePropsRenamePath: null, setErrorHandler: null, setSuspenseHandler: null, scheduleUpdate: null, currentDispatcherRef: ua.ReactCurrentDispatcher, findHostInstanceByFiber: function(a) {
        a = Zb(a);
        return null === a ? null : a.stateNode;
      }, findFiberByHostInstance: tl.findFiberByHostInstance || jl, findHostInstancesForRefresh: null, scheduleRefresh: null, scheduleRoot: null, setRefreshHandler: null, getCurrentFiber: null, reconcilerVersion: "18.3.1-next-f1338f8080-20240426" };
      if ("undefined" !== typeof __REACT_DEVTOOLS_GLOBAL_HOOK__) {
        vl = __REACT_DEVTOOLS_GLOBAL_HOOK__;
        if (!vl.isDisabled && vl.supportsFiber) try {
          kc = vl.inject(ul), lc = vl;
        } catch (a) {
        }
      }
      var vl;
      exports.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED = sl;
      exports.createPortal = function(a, b) {
        var c = 2 < arguments.length && void 0 !== arguments[2] ? arguments[2] : null;
        if (!nl(b)) throw Error(p(200));
        return cl(a, b, null, c);
      };
      exports.createRoot = function(a, b) {
        if (!nl(a)) throw Error(p(299));
        var c = false, d = "", e = kl;
        null !== b && void 0 !== b && (true === b.unstable_strictMode && (c = true), void 0 !== b.identifierPrefix && (d = b.identifierPrefix), void 0 !== b.onRecoverableError && (e = b.onRecoverableError));
        b = bl(a, 1, false, null, null, c, false, d, e);
        a[uf] = b.current;
        sf(8 === a.nodeType ? a.parentNode : a);
        return new ll(b);
      };
      exports.findDOMNode = function(a) {
        if (null == a) return null;
        if (1 === a.nodeType) return a;
        var b = a._reactInternals;
        if (void 0 === b) {
          if ("function" === typeof a.render) throw Error(p(188));
          a = Object.keys(a).join(",");
          throw Error(p(268, a));
        }
        a = Zb(b);
        a = null === a ? null : a.stateNode;
        return a;
      };
      exports.flushSync = function(a) {
        return Rk(a);
      };
      exports.hydrate = function(a, b, c) {
        if (!ol(b)) throw Error(p(200));
        return rl(null, a, b, true, c);
      };
      exports.hydrateRoot = function(a, b, c) {
        if (!nl(a)) throw Error(p(405));
        var d = null != c && c.hydratedSources || null, e = false, f = "", g = kl;
        null !== c && void 0 !== c && (true === c.unstable_strictMode && (e = true), void 0 !== c.identifierPrefix && (f = c.identifierPrefix), void 0 !== c.onRecoverableError && (g = c.onRecoverableError));
        b = el(b, null, a, 1, null != c ? c : null, e, false, f, g);
        a[uf] = b.current;
        sf(a);
        if (d) for (a = 0; a < d.length; a++) c = d[a], e = c._getVersion, e = e(c._source), null == b.mutableSourceEagerHydrationData ? b.mutableSourceEagerHydrationData = [c, e] : b.mutableSourceEagerHydrationData.push(
          c,
          e
        );
        return new ml(b);
      };
      exports.render = function(a, b, c) {
        if (!ol(b)) throw Error(p(200));
        return rl(null, a, b, false, c);
      };
      exports.unmountComponentAtNode = function(a) {
        if (!ol(a)) throw Error(p(40));
        return a._reactRootContainer ? (Rk(function() {
          rl(null, null, a, false, function() {
            a._reactRootContainer = null;
            a[uf] = null;
          });
        }), true) : false;
      };
      exports.unstable_batchedUpdates = Qk;
      exports.unstable_renderSubtreeIntoContainer = function(a, b, c, d) {
        if (!ol(c)) throw Error(p(200));
        if (null == a || void 0 === a._reactInternals) throw Error(p(38));
        return rl(a, b, c, false, d);
      };
      exports.version = "18.3.1-next-f1338f8080-20240426";
    }
  });

  // node_modules/react-dom/index.js
  var require_react_dom = __commonJS({
    "node_modules/react-dom/index.js"(exports, module2) {
      "use strict";
      function checkDCE() {
        if (typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ === "undefined" || typeof __REACT_DEVTOOLS_GLOBAL_HOOK__.checkDCE !== "function") {
          return;
        }
        if (false) {
          throw new Error("^_^");
        }
        try {
          __REACT_DEVTOOLS_GLOBAL_HOOK__.checkDCE(checkDCE);
        } catch (err) {
          console.error(err);
        }
      }
      if (true) {
        checkDCE();
        module2.exports = require_react_dom_production_min();
      } else {
        module2.exports = null;
      }
    }
  });

  // node_modules/react-dom/client.js
  var require_client = __commonJS({
    "node_modules/react-dom/client.js"(exports) {
      "use strict";
      var m = require_react_dom();
      if (true) {
        exports.createRoot = m.createRoot;
        exports.hydrateRoot = m.hydrateRoot;
      } else {
        i = m.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;
        exports.createRoot = function(c, o) {
          i.usingClientEntryPoint = true;
          try {
            return m.createRoot(c, o);
          } finally {
            i.usingClientEntryPoint = false;
          }
        };
        exports.hydrateRoot = function(c, h, o) {
          i.usingClientEntryPoint = true;
          try {
            return m.hydrateRoot(c, h, o);
          } finally {
            i.usingClientEntryPoint = false;
          }
        };
      }
      var i;
    }
  });

  // public/src/react/react-dom-global.js
  var ReactDOMClient, ReactDOM2, Combined, react_dom_global_default;
  var init_react_dom_global = __esm({
    "public/src/react/react-dom-global.js"() {
      ReactDOMClient = __toESM(require_client());
      ReactDOM2 = __toESM(require_react_dom());
      Combined = {
        ...ReactDOM2,
        ...ReactDOMClient
      };
      react_dom_global_default = Combined;
    }
  });

  // public/src/react/lib.jsx
  function perfStart(label) {
    if (!globalScope.IC_DEBUG_NAMESPACES?.includes("perf") && !globalScope.IC_DEBUG_NAMESPACES?.includes("all")) {
      return;
    }
    perfTimers[label] = performance.now();
  }
  function perfEnd(label, extraInfo = "") {
    if (!globalScope.IC_DEBUG_NAMESPACES?.includes("perf") && !globalScope.IC_DEBUG_NAMESPACES?.includes("all")) {
      return;
    }
    const start = perfTimers[label];
    if (start) {
      const duration = (performance.now() - start).toFixed(2);
      console.log(`[perf] ${label}: ${duration}ms${extraInfo ? ` (${extraInfo})` : ""}`);
      delete perfTimers[label];
    }
  }
  function perfCount(label) {
    if (!globalScope.IC_DEBUG_NAMESPACES?.includes("perf") && !globalScope.IC_DEBUG_NAMESPACES?.includes("all")) {
      return;
    }
    perfCounts[label] = (perfCounts[label] || 0) + 1;
    console.log(`[perf] ${label} count: ${perfCounts[label]}`);
  }
  function perfReset() {
    Object.keys(perfCounts).forEach((k) => delete perfCounts[k]);
    Object.keys(perfTimers).forEach((k) => delete perfTimers[k]);
  }
  function createLoopGuard(label, options = {}) {
    const { maxCalls = 100, windowMs = 1e3, onDetected = null, showDevWarning = true } = options;
    let callCount = 0;
    let windowStart = Date.now();
    let hasWarned = false;
    return {
      /**
       * Check if we're in an infinite loop. Call at start of function/render.
       * @returns {boolean} true if loop detected (caller should abort)
       */
      check() {
        const now2 = Date.now();
        if (now2 - windowStart > windowMs) {
          callCount = 0;
          windowStart = now2;
          hasWarned = false;
        }
        callCount++;
        if (callCount > maxCalls) {
          if (!hasWarned) {
            hasWarned = true;
            const msg = `[LoopGuard] Infinite loop detected in "${label}": ${callCount} calls in ${windowMs}ms. Aborting to prevent crash.`;
            console.error(msg);
            if (showDevWarning && typeof document !== "undefined" && !document.querySelector(".ic-loop-warning")) {
              try {
                const banner = document.createElement("div");
                banner.className = "ic-loop-warning";
                banner.style.cssText = "position:fixed;top:0;left:0;right:0;background:#dc2626;color:white;padding:12px;z-index:99999;text-align:center;font-family:system-ui;font-size:14px;";
                banner.innerHTML = `<strong>Infinite loop detected in "${label}"</strong> - Check console for details. <button onclick="this.parentElement.remove()" style="margin-left:12px;padding:4px 8px;cursor:pointer;">Dismiss</button>`;
                document.body.appendChild(banner);
              } catch (e) {
              }
            }
            if (onDetected) {
              onDetected(label, callCount);
            }
          }
          return true;
        }
        return false;
      },
      /** Reset the guard (useful after intentional batch operations) */
      reset() {
        callCount = 0;
        windowStart = Date.now();
        hasWarned = false;
      },
      /** Get current stats for debugging */
      getStats() {
        return { label, callCount, windowStart, maxCalls, windowMs };
      }
    };
  }
  function boundedWhile(condition, body, maxIterations = 1e3, label = "unknown") {
    let i = 0;
    while (condition() && i < maxIterations) {
      body();
      i++;
    }
    if (i >= maxIterations) {
      console.error(`[boundedWhile] "${label}" hit max iterations (${maxIterations}). Possible infinite loop.`);
    }
    return i;
  }
  var globalScope, LIB, extractImaginaryParts, RESULT_TYPE_DETECTORS, perfTimers, perfCounts, actionLoopGuard;
  var init_lib = __esm({
    "public/src/react/lib.jsx"() {
      init_react_global();
      init_react_dom_global();
      globalScope = typeof window !== "undefined" ? window : globalThis;
      LIB = globalScope.LIB || {};
      try {
        if (typeof window !== "undefined" && typeof URLSearchParams !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          const debugParam = params.get("debug");
          if (debugParam) {
            globalScope.IC_DEBUG_NAMESPACES = debugParam.split(",").map((s) => s.trim().toLowerCase());
            globalScope.IC_UI_DEBUG = globalScope.IC_DEBUG_NAMESPACES.includes("all") || globalScope.IC_DEBUG_NAMESPACES.includes("ui");
          }
        }
        globalScope.IC_UI_DEBUG = globalScope.IC_UI_DEBUG || false;
        globalScope.IC_DEBUG_NAMESPACES = globalScope.IC_DEBUG_NAMESPACES || [];
      } catch (e) {
      }
      LIB.tryToRender = function tryToRender(reactElement, querySelector) {
        const el = typeof document !== "undefined" ? document.querySelector(querySelector) : null;
        if (el) {
          try {
            if (!el.__ic_root) {
              el.__ic_root = react_dom_global_default.createRoot(el);
            }
            el.__ic_root.render(reactElement);
          } catch (e) {
            console.error("LIB.tryToRender failed:", e);
            throw e;
          }
        }
      };
      extractImaginaryParts = (formattedStr) => {
        if (typeof formattedStr !== "string") return null;
        const parts = [];
        let lastIndex = 0;
        const tokenRegex = /[-+]?(?:\d*\.?\d+|π)?i/g;
        let match;
        while ((match = tokenRegex.exec(formattedStr)) !== null) {
          const start = match.index;
          const end = start + match[0].length;
          const prev = formattedStr[start - 1];
          const next = formattedStr[end];
          if (prev && /[a-zA-Z]/.test(prev) || next && /[a-zA-Z]/.test(next)) {
            continue;
          }
          if (start > lastIndex) {
            parts.push(formattedStr.slice(lastIndex, start));
          }
          parts.push({ imag: match[0] });
          lastIndex = end;
        }
        if (!parts.length) return null;
        if (lastIndex < formattedStr.length) {
          parts.push(formattedStr.slice(lastIndex));
        }
        return parts;
      };
      RESULT_TYPE_DETECTORS = [
        // Complex numbers (e.g., 3 + 2i, πi)
        {
          type: "complex",
          detect: (rawResult, formattedStr) => {
            const Complex = globalScope.LIB?.Complex || globalScope.IC?.LIB?.Complex;
            if (Complex?.isComplex?.(rawResult)) return true;
            return !!extractImaginaryParts(formattedStr);
          },
          render: (formattedStr) => {
            const parts = extractImaginaryParts(formattedStr);
            if (!parts) return null;
            return /* @__PURE__ */ react_global_default.createElement("span", null, parts.map((part, idx) => {
              if (typeof part === "object" && part?.imag) {
                const coef = part.imag.slice(0, -1);
                return /* @__PURE__ */ react_global_default.createElement("span", { key: idx }, coef, /* @__PURE__ */ react_global_default.createElement("span", { className: "imaginary-i" }, "i"));
              }
              return part;
            }));
          }
        },
        // Fractions (detected from formatted string pattern)
        {
          type: "fraction",
          detect: (rawResult, formattedStr) => {
            return typeof formattedStr === "string" && /FRACTION{\d+,\s*\d+}/.test(formattedStr);
          },
          render: (formattedStr) => {
            const fracRegex = /FRACTION{(\d+)[,]\s*(\d+)}/;
            const match = formattedStr.match(fracRegex);
            if (!match) return null;
            const [, numerator, denominator] = match;
            const parts = formattedStr.split(fracRegex);
            return /* @__PURE__ */ react_global_default.createElement("span", null, (parts[0] || "").trim(), /* @__PURE__ */ react_global_default.createElement("frac", null, /* @__PURE__ */ react_global_default.createElement("span", { className: "fracspace" }, " "), /* @__PURE__ */ react_global_default.createElement("sup", null, numerator), "\u2044", /* @__PURE__ */ react_global_default.createElement("sub", null, denominator)), (parts[parts.length - 1] || "").trim());
          }
        }
        // Future: Add more type detectors here
        // { type: 'unit', detect: ..., render: ... },
        // { type: 'currency', detect: ..., render: ... },
        // { type: 'date', detect: ..., render: ... },
      ];
      LIB.getResultDisplayInfo = (rawResult, formattedResult) => {
        for (const detector of RESULT_TYPE_DETECTORS) {
          if (detector.detect(rawResult, formattedResult)) {
            return {
              type: detector.type,
              render: detector.render,
              meta: detector.meta
            };
          }
        }
        return null;
      };
      LIB.prettyPrintResultHTML = (formattedResult, rawResult = null) => {
        let resultDisplay = formattedResult;
        if (resultDisplay && typeof resultDisplay === "object") {
          try {
            resultDisplay = JSON.stringify(resultDisplay);
          } catch (e) {
            resultDisplay = String(resultDisplay);
          }
        }
        if (typeof resultDisplay === "string") {
          const displayInfo = LIB.getResultDisplayInfo(rawResult, resultDisplay);
          if (displayInfo?.render) {
            const rendered = displayInfo.render(resultDisplay);
            if (rendered) {
              resultDisplay = rendered;
            }
          }
        }
        return resultDisplay;
      };
      LIB.prettyPrintFunction = (funcObj) => {
        return `function(${funcObj.params.join(", ")})`;
      };
      LIB.prettyPrintObject = (obj, indent = 2) => {
        let result = "";
        const addIndent = (level) => " ".repeat(level * indent);
        const isReactEl = (v) => v && typeof v === "object" && (v.$$typeof || v.props);
        const formatObject = (o, level = 0) => {
          if (!o || typeof o !== "object") {
            result += `${addIndent(level)}${String(o)}
`;
            return;
          }
          if (isReactEl(o)) {
            result += `${addIndent(level)}[react-element]
`;
            return;
          }
          let entries = [];
          try {
            entries = Object.entries(o);
          } catch (e) {
            entries = [];
          }
          for (const [key, value] of entries) {
            if (typeof value === "object" && value !== null) {
              if (isReactEl(value)) {
                result += `${addIndent(level)}${key}: [react-element]
`;
                continue;
              }
              const formatted = IC.api.formatValue(value);
              if (formatted !== value && typeof formatted !== "object") {
                result += `${addIndent(level)}${key}: ${formatted}
`;
              } else {
                result += `${addIndent(level)}${key}:
`;
                formatObject(value, level + 1);
              }
            } else {
              result += `${addIndent(level)}${key}: ${value}
`;
            }
          }
        };
        formatObject(obj);
        return result;
      };
      LIB.prettyPrintArray = (arr, indent = 2, level = 0) => {
        let result = "";
        const addIndent = (lvl) => " ".repeat(lvl * indent);
        const isReactEl = (v) => v && typeof v === "object" && (v.$$typeof || v.props);
        const formatArray = (a, currentLevel = 0) => {
          (a || []).forEach((item, index) => {
            if (typeof item === "object" && item !== null) {
              if (isReactEl(item)) {
                result += `${addIndent(currentLevel)}[${index}]: [react-element]
`;
                return;
              }
              const formatted = IC.api.formatValue(item);
              if (formatted !== item && typeof formatted !== "object") {
                result += `${addIndent(currentLevel)}[${index}]: ${formatted}
`;
              } else if (Array.isArray(item)) {
                result += `${addIndent(currentLevel)}[${index}]:
`;
                formatArray(item, currentLevel + 1);
              } else {
                result += `${addIndent(currentLevel)}[${index}]:
`;
                result += LIB.prettyPrintObject(item, indent, currentLevel + 1);
              }
            } else {
              result += `${addIndent(currentLevel)}[${index}]: ${item}
`;
            }
          });
        };
        formatArray(arr, level);
        return result;
      };
      LIB.setVariablesInCalc = function setVariablesInCalc(Action3, calc, vars, results) {
        const solution = vars;
        if (!solution || !calc) return;
        for (const varName in solution) {
          const solvedValue = solution[varName];
          let newText = null;
          let rowIndex = 0;
          if (/^r\d+$/i.test(varName)) {
            const varRowIndex = parseInt(varName.slice(1), 10) - 1;
            const currentRowText = calc.rows[varRowIndex].text;
            const newMainText = IC.api.formatValue(solvedValue);
            newText = IC.api.replaceBeforeComment(currentRowText, newMainText);
            Action3.updateRowText(calc, varRowIndex, newText);
          } else {
            rowIndex = IC.api.findParameterRow(calc, varName);
            if (rowIndex !== null) {
              const row = calc.rows[rowIndex];
              newText = IC.api.buildParameterText(row.text, IC.api.formatValue(solvedValue));
            }
            if (newText) {
              Action3.updateRowText(calc, rowIndex, newText);
            }
          }
        }
      };
      perfTimers = {};
      perfCounts = {};
      globalScope.LIB = LIB;
      globalScope.perfStart = perfStart;
      globalScope.perfEnd = perfEnd;
      globalScope.perfCount = perfCount;
      globalScope.perfReset = perfReset;
      actionLoopGuard = createLoopGuard("Action.mutations", {
        maxCalls: 200,
        windowMs: 1e3
      });
      globalScope.actionLoopGuard = actionLoopGuard;
      globalScope.createLoopGuard = createLoopGuard;
      globalScope.boundedWhile = boundedWhile;
    }
  });

  // public/src/plugins/labs/components/LabsPanel.jsx
  function LabsPanel2() {
    const [flags, setFlags] = (0, import_react2.useState)([]);
    const [, forceUpdate] = (0, import_react2.useState)(0);
    const registry = typeof window !== "undefined" ? window.__IC_FEATURE_FLAGS__ : null;
    (0, import_react2.useEffect)(() => {
      if (!registry) return;
      setFlags(registry.getAllSorted());
      const unsubscribe = registry.subscribe(() => {
        forceUpdate((n) => n + 1);
      });
      return unsubscribe;
    }, [registry]);
    const handleToggle = (0, import_react2.useCallback)((flagId) => {
      if (!registry) return;
      const currentValue = registry.isEnabled(flagId);
      registry.setOverride(flagId, !currentValue);
    }, [registry]);
    const handleReset = (0, import_react2.useCallback)((flagId) => {
      if (!registry) return;
      registry.clearOverride(flagId);
    }, [registry]);
    const handleResetAll = (0, import_react2.useCallback)(() => {
      if (!registry) return;
      for (const flag of flags) {
        registry.clearOverride(flag.id);
      }
      forceUpdate((n) => n + 1);
    }, [registry, flags]);
    if (!registry) {
      return /* @__PURE__ */ import_react2.default.createElement("div", { className: "p-4 text-center text-[var(--text-dim)]" }, "Feature flag system not available.");
    }
    const groupedFlags = registry.getByCategory();
    const categoryOrder = ["beta", "experimental", "general"];
    const sortedCategories = Object.keys(groupedFlags).sort((a, b) => {
      const aIdx = categoryOrder.indexOf(a);
      const bIdx = categoryOrder.indexOf(b);
      if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
    const categoryLabels = {
      beta: "Beta Features",
      experimental: "Experimental",
      general: "General"
    };
    return /* @__PURE__ */ import_react2.default.createElement("div", { className: "flex flex-col h-full" }, /* @__PURE__ */ import_react2.default.createElement("div", { className: "p-4 border-b border-[var(--border-color)]" }, /* @__PURE__ */ import_react2.default.createElement("h2", { className: "text-lg font-semibold text-[var(--text)]" }, "Labs"), /* @__PURE__ */ import_react2.default.createElement("p", { className: "text-sm text-[var(--text-dim)] mt-1" }, "Enable experimental features. Changes take effect immediately.")), /* @__PURE__ */ import_react2.default.createElement("div", { className: "flex-1 overflow-y-auto p-4" }, flags.length === 0 ? /* @__PURE__ */ import_react2.default.createElement("div", { className: "text-center text-[var(--text-dim)] py-8" }, "No feature flags registered.") : /* @__PURE__ */ import_react2.default.createElement("div", { className: "space-y-6" }, sortedCategories.map((category) => /* @__PURE__ */ import_react2.default.createElement("div", { key: category }, /* @__PURE__ */ import_react2.default.createElement("h3", { className: "text-xs font-semibold uppercase text-[var(--text-dim)] mb-3" }, categoryLabels[category] || category), /* @__PURE__ */ import_react2.default.createElement("div", { className: "space-y-3" }, groupedFlags[category].map((flag) => /* @__PURE__ */ import_react2.default.createElement(
      FlagToggle,
      {
        key: flag.id,
        flag,
        enabled: registry.isEnabled(flag.id),
        hasOverride: registry.hasOverride(flag.id),
        onToggle: handleToggle,
        onReset: handleReset
      }
    ))))))), flags.length > 0 && /* @__PURE__ */ import_react2.default.createElement("div", { className: "p-4 border-t border-[var(--border-color)]" }, /* @__PURE__ */ import_react2.default.createElement(
      "button",
      {
        onClick: handleResetAll,
        className: "w-full px-4 py-2 text-sm text-[var(--text-dim)] hover:text-[var(--text)] border border-[var(--border-color)] rounded-lg transition-colors"
      },
      "Reset All to Defaults"
    ), /* @__PURE__ */ import_react2.default.createElement("p", { className: "text-xs text-[var(--text-dim)] text-center mt-2" }, "Some changes may require a page refresh.")));
  }
  function FlagToggle({ flag, enabled, hasOverride, onToggle, onReset }) {
    return /* @__PURE__ */ import_react2.default.createElement("div", { className: "flex items-start justify-between p-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-alt)]" }, /* @__PURE__ */ import_react2.default.createElement("div", { className: "flex-1 min-w-0 pr-3" }, /* @__PURE__ */ import_react2.default.createElement("div", { className: "flex items-center gap-2" }, /* @__PURE__ */ import_react2.default.createElement("span", { className: "font-medium text-[var(--text)]" }, flag.label), hasOverride && /* @__PURE__ */ import_react2.default.createElement(
      "button",
      {
        onClick: () => onReset(flag.id),
        className: "text-xs text-[var(--accent)] hover:underline",
        title: "Reset to default"
      },
      "(reset)"
    )), flag.description && /* @__PURE__ */ import_react2.default.createElement("p", { className: "text-sm text-[var(--text-dim)] mt-1" }, flag.description)), /* @__PURE__ */ import_react2.default.createElement(
      "button",
      {
        onClick: () => onToggle(flag.id),
        className: `relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${enabled ? "bg-[var(--accent-primary)]" : "bg-[var(--border-color)]"}`,
        role: "switch",
        "aria-checked": enabled
      },
      /* @__PURE__ */ import_react2.default.createElement(
        "span",
        {
          className: `pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? "translate-x-5" : "translate-x-0"}`
        }
      )
    ));
  }
  var import_react2;
  var init_LabsPanel = __esm({
    "public/src/plugins/labs/components/LabsPanel.jsx"() {
      import_react2 = __toESM(require_react());
    }
  });

  // public/src/plugins/labs/LabsPanelMount.jsx
  function mountLabsPanel() {
    try {
      LIB.tryToRender(/* @__PURE__ */ react_global_default.createElement(LabsPanel2, null), "#labs-panel-root");
      return true;
    } catch (e) {
      console.warn("[labs] Failed to mount panel:", e);
      return false;
    }
  }
  function initLabsPanelMount() {
    if (typeof window === "undefined") return;
    const handlePanelOpen = (e) => {
      if (e.detail?.panel === "labs") {
        requestAnimationFrame(() => {
          mountLabsPanel();
        });
      }
    };
    window.InstacalcEvents?.on?.(
      window.InstacalcEvents?.PANEL_OPEN || "ic:panel-open",
      handlePanelOpen
    );
    if (!mountLabsPanel()) {
      const observer = new MutationObserver(() => {
        if (mountLabsPanel()) {
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 1e4);
    }
  }
  var init_LabsPanelMount = __esm({
    "public/src/plugins/labs/LabsPanelMount.jsx"() {
      init_react_global();
      init_lib();
      init_LabsPanel();
    }
  });

  // public/src/plugins/labs/index.js
  var labs_exports = {};
  __export(labs_exports, {
    LabsPanel: () => LabsPanel,
    default: () => LabsPanel,
    featureFlags: () => featureFlags
  });
  function registerBuiltInFlags() {
    featureFlags.register({
      id: "presentModeBeta",
      label: "Present Mode",
      description: "Enhanced presentation view with layouts and styling options.",
      defaultValue: true,
      category: "beta",
      order: 10
    });
    featureFlags.register({
      id: "goalseek",
      label: "Goal Seek",
      description: "Find input values that produce a target result.",
      defaultValue: false,
      category: "beta",
      order: 20
    });
    featureFlags.register({
      id: "community",
      label: "Community Loadouts",
      description: "Share and discover parameter configurations from other users.",
      defaultValue: false,
      category: "experimental",
      order: 10
    });
    featureFlags.register({
      id: "gallery",
      label: "Public Gallery",
      description: "Browse and discover public calculators.",
      defaultValue: false,
      category: "experimental",
      order: 20
    });
    featureFlags.register({
      id: "privateShareLink",
      label: "Private Share Links",
      description: "Create secret links for sharing calcs with collaborators.",
      defaultValue: false,
      category: "experimental",
      order: 30
    });
    featureFlags.register({
      id: "tour",
      label: "Welcome Tour",
      description: "Show the onboarding tour for new users.",
      defaultValue: true,
      category: "general",
      order: 10
    });
    featureFlags.register({
      id: "showPlaceholder",
      label: "Input Placeholders",
      description: "Show example text in empty input fields.",
      defaultValue: false,
      category: "general",
      order: 20
    });
    featureFlags.register({
      id: "morphingShareBar",
      label: "Morphing Share Bar",
      description: "Animated share button transitions.",
      defaultValue: false,
      category: "general",
      order: 30
    });
  }
  function initLabsPlugin() {
    const plugins2 = getPlugins();
    if (!plugins2) {
      console.warn("[labs] Plugin registry not available");
      return;
    }
    registerBuiltInFlags();
    plugins2.panels.register(LabsPanel);
    initLabsPanelMount();
    plugins2.featureFlags = featureFlags;
    console.log("[labs] Plugin initialized");
  }
  var getPlugins;
  var init_labs = __esm({
    "public/src/plugins/labs/index.js"() {
      init_FeatureFlagRegistry();
      init_panel();
      init_LabsPanelMount();
      init_FeatureFlagRegistry();
      init_panel();
      init_panel();
      getPlugins = () => {
        if (typeof window !== "undefined" && window.__IC_PLUGINS__) {
          return window.__IC_PLUGINS__;
        }
        return null;
      };
      if (typeof window !== "undefined") {
        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", initLabsPlugin);
        } else {
          initLabsPlugin();
        }
      }
    }
  });

  // public/src/plugins/calc-info/services/LoadoutStore.js
  var require_LoadoutStore = __commonJS({
    "public/src/plugins/calc-info/services/LoadoutStore.js"(exports, module2) {
      var FirebasePaths = typeof window !== "undefined" && window.FirebasePaths || require_FirebasePaths();
      var LoadoutStore = {
        _listeners: /* @__PURE__ */ new Map(),
        /**
         * Sort loadouts: pinned first, then by votes descending
         * @param {Array} loadouts - Loadouts to sort
         * @returns {Array} Sorted loadouts
         */
        _sortLoadouts(loadouts) {
          return loadouts.sort((a, b) => {
            if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
            return (b.votes || 0) - (a.votes || 0);
          });
        },
        /**
         * Create a new loadout
         * @param {Object} data - Loadout data
         * @returns {Promise<Object>} Created loadout with ID
         */
        async create(data) {
          const { calcId, params, name = "", authorName = "" } = data;
          if (!calcId || !params) {
            throw new Error("calcId and params are required");
          }
          const path = FirebasePaths.getLoadoutsPath(calcId);
          const ref = firebase.database().ref(path);
          const newRef = ref.push();
          const loadout = {
            id: newRef.key,
            calcId,
            params,
            name,
            authorName,
            votes: 0,
            pinned: false,
            status: "approved",
            createdAt: Date.now()
          };
          await newRef.set(loadout);
          return loadout;
        },
        /**
         * Get all loadouts for a calc
         * @param {string} calcId - Calc ID
         * @returns {Promise<Array>} Loadouts sorted by pinned then votes
         */
        async getForCalc(calcId) {
          const path = FirebasePaths.getLoadoutsPath(calcId);
          const snapshot = await firebase.database().ref(path).once("value");
          const data = snapshot.val();
          if (!data) return [];
          const loadouts = Object.values(data);
          return this._sortLoadouts(loadouts);
        },
        // Track votes in localStorage to prevent repeat votes
        _hasVoted(calcId, loadoutId) {
          if (typeof localStorage === "undefined") return false;
          const key = `voted:${calcId}:${loadoutId}`;
          return localStorage.getItem(key) === "true";
        },
        _markVoted(calcId, loadoutId) {
          if (typeof localStorage === "undefined") return;
          const key = `voted:${calcId}:${loadoutId}`;
          localStorage.setItem(key, "true");
        },
        /**
         * Upvote a loadout
         * @param {string} calcId - Calc ID
         * @param {string} loadoutId - Loadout ID
         * @returns {Promise<number>} New vote count
         */
        async upvote(calcId, loadoutId) {
          if (this._hasVoted(calcId, loadoutId)) {
            throw new Error("Already voted");
          }
          const path = FirebasePaths.getLoadoutPath(calcId, loadoutId);
          const ref = firebase.database().ref(path);
          const result = await ref.transaction((current) => {
            if (current) {
              current.votes = (current.votes || 0) + 1;
            }
            return current;
          });
          if (result.committed) {
            this._markVoted(calcId, loadoutId);
          }
          return result.snapshot.val()?.votes || 0;
        },
        /**
         * Subscribe to loadout changes for a calc
         * @param {string} calcId - Calc ID
         * @param {Function} callback - Called with loadouts array on change
         * @returns {Function} Unsubscribe function
         */
        subscribe(calcId, callback) {
          const path = FirebasePaths.getLoadoutsPath(calcId);
          const ref = firebase.database().ref(path);
          const handler = (snapshot) => {
            const data = snapshot.val();
            const loadouts = data ? Object.values(data) : [];
            const sorted = this._sortLoadouts(loadouts);
            callback(sorted);
          };
          ref.on("value", handler);
          this._listeners.set(calcId, { ref, handler });
          return () => {
            ref.off("value", handler);
            this._listeners.delete(calcId);
          };
        },
        /**
         * Pin/unpin a loadout (author only)
         * @param {string} calcId - Calc ID
         * @param {string} loadoutId - Loadout ID
         * @param {boolean} pinned - Pin state
         */
        async setPinned(calcId, loadoutId, pinned) {
          const path = `${FirebasePaths.getLoadoutPath(calcId, loadoutId)}/pinned`;
          await firebase.database().ref(path).set(pinned);
        },
        /**
         * Delete a loadout (author/moderator only)
         * @param {string} calcId - Calc ID
         * @param {string} loadoutId - Loadout ID
         */
        async delete(calcId, loadoutId) {
          const path = FirebasePaths.getLoadoutPath(calcId, loadoutId);
          await firebase.database().ref(path).remove();
        }
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = LoadoutStore;
      }
      if (typeof window !== "undefined") {
        window.LoadoutStore = LoadoutStore;
      }
    }
  });

  // public/src/plugins/calc-info/services/loadoutParser.js
  var require_loadoutParser = __commonJS({
    "public/src/plugins/calc-info/services/loadoutParser.js"(exports, module2) {
      var LoadoutParser = {
        /**
         * Parse a loadout string into params object
         * @param {string} str - Loadout string like "armor=500|health=1000"
         * @returns {Object} Params object { armor: '500', health: '1000' }
         */
        parse(str) {
          if (!str) return {};
          const cleaned = str.startsWith("#") ? str.slice(1) : str;
          if (!cleaned) return {};
          const params = {};
          const pairs = cleaned.split("|");
          for (const pair of pairs) {
            const eqIndex = pair.indexOf("=");
            if (eqIndex === -1) continue;
            const key = pair.slice(0, eqIndex).trim();
            const value = pair.slice(eqIndex + 1).trim();
            if (key) {
              params[key] = value;
            }
          }
          return params;
        },
        /**
         * Convert params object to loadout string
         * @param {Object} params - Params object
         * @returns {string} Loadout string
         */
        stringify(params) {
          if (!params || Object.keys(params).length === 0) return "";
          return Object.entries(params).map(([key, value]) => `${key}=${value}`).join("|");
        },
        /**
         * Extract parameter assignments from a calc
         * Only extracts simple assignments (x = 5), not formulas (x = a + b)
         * @param {Object} calc - Calc object with rows
         * @returns {Object} Params object
         */
        extractParamsFromCalc(calc) {
          if (!calc?.rows) return {};
          const params = {};
          for (const row of calc.rows) {
            if (!row.text) continue;
            let param = null;
            if (typeof IC !== "undefined" && IC.parser?.getRowParameterFromString) {
              param = IC.parser.getRowParameterFromString(row.text);
            } else {
              const match = row.text.match(/^(\w+)\s*=\s*([^+\-*/%()]+)$/);
              if (match) {
                param = { name: match[1], value: match[2].trim() };
              }
            }
            if (param?.name && param?.value !== void 0) {
              params[param.name] = String(param.value);
            }
          }
          return params;
        },
        /**
         * Apply loadout params to a calc
         * Updates rows with matching variable names
         * @param {Object} calc - Calc object
         * @param {Object} params - Params to apply
         * @returns {Object} New calc object with updated rows
         */
        applyToCalc(calc, params) {
          if (!calc?.rows || !params) return calc;
          const newRows = calc.rows.map((row) => {
            if (!row.text) return row;
            const match = row.text.match(/^(\w+)\s*=\s*.+$/);
            if (match) {
              const varName = match[1];
              if (varName in params) {
                return { ...row, text: `${varName} = ${params[varName]}` };
              }
            }
            return row;
          });
          return { ...calc, rows: newRows };
        }
      };
      if (typeof module2 !== "undefined" && module2.exports) {
        module2.exports = LoadoutParser;
      }
      if (typeof window !== "undefined") {
        window.LoadoutParser = LoadoutParser;
      }
    }
  });

  // public/src/plugins/calc-info/panel.js
  var CalcInfoPanelConfig;
  var init_panel2 = __esm({
    "public/src/plugins/calc-info/panel.js"() {
      CalcInfoPanelConfig = {
        id: "calc-info",
        label: "Info",
        icon: "rectangleStack",
        slot: "right-sidebar",
        showInDock: true,
        order: 45,
        templateUrl: "templates/calc-info-panel.html",
        innerClass: "h-full"
        // Note: enabled check happens at plugin load time, not here
      };
    }
  });

  // public/src/plugins/calc-info/actions.js
  function applyLoadout(calcOrId, loadoutParams) {
    const Action3 = window.Action;
    if (!Action3) {
      console.warn("[calc-info:applyLoadout] Action not available");
      return null;
    }
    let calc;
    if (typeof calcOrId === "string") {
      calc = Action3.getCurrentCalc?.();
      if (calc?.id !== calcOrId && calc?.remoteId !== calcOrId) {
        calc = null;
      }
    } else {
      calc = calcOrId;
    }
    if (!calc) {
      console.warn("[calc-info:applyLoadout] Calc not found:", calcOrId);
      return null;
    }
    const LoadoutParser = window.LoadoutParser;
    if (!LoadoutParser) {
      console.warn("[calc-info:applyLoadout] LoadoutParser not loaded");
      return calc;
    }
    const params = LoadoutParser.parse(loadoutParams);
    const newCalc = LoadoutParser.applyToCalc(calc, params);
    return Action3.commit(newCalc, {
      type: "applyLoadout",
      params: loadoutParams
    });
  }
  var init_actions = __esm({
    "public/src/plugins/calc-info/actions.js"() {
    }
  });

  // public/src/plugins/calc-info/hooks/useCalcInfoCounts.js
  function useCalcInfoCounts(calcId) {
    const [loadoutCount, setLoadoutCount] = (0, import_react3.useState)(0);
    const [commentCount, setCommentCount] = (0, import_react3.useState)(0);
    const [loading, setLoading] = (0, import_react3.useState)(true);
    (0, import_react3.useEffect)(() => {
      if (!calcId) {
        setLoadoutCount(0);
        setCommentCount(0);
        setLoading(false);
        return;
      }
      setLoading(true);
      const unsubscribe = window.LoadoutStore?.subscribe(calcId, (loadouts) => {
        setLoadoutCount(loadouts.length);
        setLoading(false);
      });
      return unsubscribe;
    }, [calcId]);
    return { loadoutCount, commentCount, loading };
  }
  var import_react3;
  var init_useCalcInfoCounts = __esm({
    "public/src/plugins/calc-info/hooks/useCalcInfoCounts.js"() {
      import_react3 = __toESM(require_react());
    }
  });

  // public/src/plugins/calc-info/CalcInfoBadge.jsx
  function CalcInfoBadge({ calc }) {
    const remoteId = calc?.remoteId;
    const { loadoutCount } = useCalcInfoCounts(remoteId);
    if (!remoteId) return null;
    function handleClick() {
      window.InstacalcEvents?.dispatch(
        window.InstacalcEvents?.PANEL_OPEN || "ic:panel-open",
        { panel: "calc-info" }
      );
    }
    return /* @__PURE__ */ react_global_default.createElement(
      "button",
      {
        onClick: handleClick,
        className: "flex items-center gap-1 px-2 py-1 text-sm text-[var(--text-dim)] hover:text-[var(--text)] transition-colors",
        title: "Calc info"
      },
      /* @__PURE__ */ react_global_default.createElement("span", { className: "text-xs" }, "[i]"),
      loadoutCount > 0 && /* @__PURE__ */ react_global_default.createElement("span", { className: "text-xs bg-[var(--accent-primary)] text-white px-1.5 rounded-full" }, loadoutCount)
    );
  }
  var init_CalcInfoBadge = __esm({
    "public/src/plugins/calc-info/CalcInfoBadge.jsx"() {
      init_react_global();
      init_useCalcInfoCounts();
    }
  });

  // public/src/plugins/calc-info/hooks/useLoadouts.js
  function useLoadouts(calcId) {
    const [loadouts, setLoadouts] = (0, import_react4.useState)([]);
    const [loading, setLoading] = (0, import_react4.useState)(true);
    const [error, setError] = (0, import_react4.useState)(null);
    (0, import_react4.useEffect)(() => {
      if (!calcId) {
        setLoadouts([]);
        setLoading(false);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      const unsubscribe = window.LoadoutStore?.subscribe(calcId, (newLoadouts) => {
        setLoadouts(newLoadouts);
        setLoading(false);
      });
      return unsubscribe;
    }, [calcId]);
    const createLoadout = (0, import_react4.useCallback)(async (data) => {
      try {
        return await window.LoadoutStore?.create({ calcId, ...data });
      } catch (err) {
        setError(err);
        throw err;
      }
    }, [calcId]);
    const upvote = (0, import_react4.useCallback)(async (loadoutId) => {
      try {
        return await window.LoadoutStore?.upvote(calcId, loadoutId);
      } catch (err) {
        setError(err);
        throw err;
      }
    }, [calcId]);
    const applyLoadout2 = (0, import_react4.useCallback)((params) => {
      window.Action?.applyLoadout(calcId, params);
    }, [calcId]);
    return {
      loadouts,
      loading,
      error,
      createLoadout,
      upvote,
      applyLoadout: applyLoadout2,
      pinnedCount: loadouts.filter((l) => l.pinned).length,
      totalCount: loadouts.length
    };
  }
  var import_react4;
  var init_useLoadouts = __esm({
    "public/src/plugins/calc-info/hooks/useLoadouts.js"() {
      import_react4 = __toESM(require_react());
    }
  });

  // public/src/plugins/calc-info/components/LoadoutCard.jsx
  function LoadoutCard({ loadout, onLoad, onUpvote }) {
    const { name, authorName, votes, pinned, params } = loadout;
    function handleLoad() {
      onLoad(params);
    }
    function handleUpvote(e) {
      e.stopPropagation();
      onUpvote(loadout.id);
    }
    return /* @__PURE__ */ import_react5.default.createElement(
      "div",
      {
        className: "flex items-center justify-between p-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-alt)] hover:bg-[var(--bg)] transition-colors"
      },
      /* @__PURE__ */ import_react5.default.createElement("div", { className: "flex-1 min-w-0" }, /* @__PURE__ */ import_react5.default.createElement("div", { className: "flex items-center gap-2" }, pinned && /* @__PURE__ */ import_react5.default.createElement("span", { className: "text-xs text-[var(--accent)]", title: "Pinned by author" }, "[pin]"), /* @__PURE__ */ import_react5.default.createElement("span", { className: "font-medium text-[var(--text)] truncate" }, name || "Unnamed Loadout")), /* @__PURE__ */ import_react5.default.createElement("div", { className: "text-xs text-[var(--text-dim)] mt-0.5" }, "by ", authorName || "anonymous")),
      /* @__PURE__ */ import_react5.default.createElement("div", { className: "flex items-center gap-2 ml-3" }, /* @__PURE__ */ import_react5.default.createElement(
        "button",
        {
          onClick: handleUpvote,
          className: "flex items-center gap-1 px-2 py-1 text-sm text-[var(--text-dim)] hover:text-[var(--accent)] transition-colors",
          title: "Upvote"
        },
        /* @__PURE__ */ import_react5.default.createElement("span", null, "+"),
        /* @__PURE__ */ import_react5.default.createElement("span", null, votes || 0)
      ), /* @__PURE__ */ import_react5.default.createElement(
        "button",
        {
          onClick: handleLoad,
          className: "px-3 py-1 text-sm font-medium bg-[var(--accent-primary)] text-white rounded hover:opacity-90 transition-opacity"
        },
        "Load"
      ))
    );
  }
  var import_react5;
  var init_LoadoutCard = __esm({
    "public/src/plugins/calc-info/components/LoadoutCard.jsx"() {
      import_react5 = __toESM(require_react());
    }
  });

  // public/src/plugins/calc-info/components/LoadoutsTab.jsx
  function LoadoutsTab({ calcId }) {
    const [showSaveModal, setShowSaveModal] = (0, import_react6.useState)(false);
    const {
      loadouts,
      loading,
      createLoadout,
      upvote,
      applyLoadout: applyLoadout2
    } = useLoadouts(calcId);
    const pinnedLoadouts = loadouts.filter((l) => l.pinned);
    const communityLoadouts = loadouts.filter((l) => !l.pinned);
    const handleSaveLoadout = (0, import_react6.useCallback)(async (name, authorName) => {
      const calc = window.Action?.getCurrentCalc();
      if (!calc) return;
      const params = window.LoadoutParser?.extractParamsFromCalc(calc);
      const paramsString = window.LoadoutParser?.stringify(params);
      if (!paramsString) {
        alert("No parameters to save. Add some variable assignments first.");
        return;
      }
      await createLoadout({ params: paramsString, name, authorName });
      setShowSaveModal(false);
    }, [createLoadout]);
    if (loading) {
      return /* @__PURE__ */ import_react6.default.createElement("div", { className: "text-center text-[var(--text-dim)] py-8" }, "Loading...");
    }
    return /* @__PURE__ */ import_react6.default.createElement("div", { className: "flex flex-col h-full" }, /* @__PURE__ */ import_react6.default.createElement("div", { className: "flex-1 overflow-y-auto" }, /* @__PURE__ */ import_react6.default.createElement("div", { className: "space-y-4" }, pinnedLoadouts.length > 0 && /* @__PURE__ */ import_react6.default.createElement("div", null, /* @__PURE__ */ import_react6.default.createElement("h3", { className: "text-xs font-semibold uppercase text-[var(--text-dim)] mb-2" }, "Pinned"), /* @__PURE__ */ import_react6.default.createElement("div", { className: "space-y-2" }, pinnedLoadouts.map((loadout) => /* @__PURE__ */ import_react6.default.createElement(
      LoadoutCard,
      {
        key: loadout.id,
        loadout,
        onLoad: applyLoadout2,
        onUpvote: upvote
      }
    )))), communityLoadouts.length > 0 && /* @__PURE__ */ import_react6.default.createElement("div", null, /* @__PURE__ */ import_react6.default.createElement("h3", { className: "text-xs font-semibold uppercase text-[var(--text-dim)] mb-2" }, "Community"), /* @__PURE__ */ import_react6.default.createElement("div", { className: "space-y-2" }, communityLoadouts.map((loadout) => /* @__PURE__ */ import_react6.default.createElement(
      LoadoutCard,
      {
        key: loadout.id,
        loadout,
        onLoad: applyLoadout2,
        onUpvote: upvote
      }
    )))), loadouts.length === 0 && /* @__PURE__ */ import_react6.default.createElement("div", { className: "text-center text-[var(--text-dim)] py-8" }, "No loadouts yet. Be the first to save one!"))), /* @__PURE__ */ import_react6.default.createElement("div", { className: "pt-4 border-t border-[var(--border-color)] mt-4" }, /* @__PURE__ */ import_react6.default.createElement(
      "button",
      {
        onClick: () => setShowSaveModal(true),
        className: "w-full px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
      },
      "+ Save Current as Loadout"
    )), showSaveModal && /* @__PURE__ */ import_react6.default.createElement(
      SaveLoadoutModal,
      {
        onSave: handleSaveLoadout,
        onClose: () => setShowSaveModal(false)
      }
    ));
  }
  function SaveLoadoutModal({ onSave, onClose }) {
    const [name, setName] = (0, import_react6.useState)("");
    const [authorName, setAuthorName] = (0, import_react6.useState)("");
    function handleSubmit(e) {
      e.preventDefault();
      onSave(name, authorName);
    }
    return /* @__PURE__ */ import_react6.default.createElement("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50" }, /* @__PURE__ */ import_react6.default.createElement("div", { className: "bg-[var(--bg)] rounded-lg p-6 w-full max-w-sm mx-4 shadow-xl" }, /* @__PURE__ */ import_react6.default.createElement("h2", { className: "text-lg font-semibold text-[var(--text)] mb-4" }, "Save Loadout"), /* @__PURE__ */ import_react6.default.createElement("form", { onSubmit: handleSubmit, className: "space-y-4" }, /* @__PURE__ */ import_react6.default.createElement("div", null, /* @__PURE__ */ import_react6.default.createElement("label", { className: "block text-sm text-[var(--text-dim)] mb-1" }, "Loadout Name (optional)"), /* @__PURE__ */ import_react6.default.createElement(
      "input",
      {
        type: "text",
        value: name,
        onChange: (e) => setName(e.target.value),
        placeholder: "e.g., Rhino Tank Build",
        className: "w-full px-3 py-2 bg-[var(--bg-alt)] border border-[var(--border-color)] rounded text-[var(--text)]"
      }
    )), /* @__PURE__ */ import_react6.default.createElement("div", null, /* @__PURE__ */ import_react6.default.createElement("label", { className: "block text-sm text-[var(--text-dim)] mb-1" }, "Your Name (optional)"), /* @__PURE__ */ import_react6.default.createElement(
      "input",
      {
        type: "text",
        value: authorName,
        onChange: (e) => setAuthorName(e.target.value),
        placeholder: "Anonymous if blank",
        className: "w-full px-3 py-2 bg-[var(--bg-alt)] border border-[var(--border-color)] rounded text-[var(--text)]"
      }
    )), /* @__PURE__ */ import_react6.default.createElement("div", { className: "flex gap-3 pt-2" }, /* @__PURE__ */ import_react6.default.createElement(
      "button",
      {
        type: "button",
        onClick: onClose,
        className: "flex-1 px-4 py-2 border border-[var(--border-color)] rounded text-[var(--text-dim)] hover:text-[var(--text)]"
      },
      "Cancel"
    ), /* @__PURE__ */ import_react6.default.createElement(
      "button",
      {
        type: "submit",
        className: "flex-1 px-4 py-2 bg-[var(--accent-primary)] text-white rounded font-medium hover:opacity-90"
      },
      "Save"
    )))));
  }
  var import_react6;
  var init_LoadoutsTab = __esm({
    "public/src/plugins/calc-info/components/LoadoutsTab.jsx"() {
      import_react6 = __toESM(require_react());
      init_useLoadouts();
      init_LoadoutCard();
    }
  });

  // public/src/plugins/calc-info/components/CalcInfoPanel.jsx
  function CalcInfoPanel({ calcId, remoteId }) {
    const [activeTab, setActiveTab] = (0, import_react7.useState)("loadouts");
    const effectiveId = remoteId || calcId;
    if (!effectiveId) {
      return /* @__PURE__ */ import_react7.default.createElement("div", { className: "p-4 text-center text-[var(--text-dim)]" }, "Publish this calc to enable community features.");
    }
    const tabs = [
      { id: "loadouts", label: "Loadouts" },
      { id: "discussion", label: "Discussion" }
      // Future tabs:
      // { id: 'stats', label: 'Stats' },
      // { id: 'history', label: 'History' },
    ];
    return /* @__PURE__ */ import_react7.default.createElement("div", { className: "flex flex-col h-full" }, /* @__PURE__ */ import_react7.default.createElement("div", { className: "flex border-b border-[var(--border-color)]" }, tabs.map((tab) => /* @__PURE__ */ import_react7.default.createElement(
      "button",
      {
        key: tab.id,
        onClick: () => setActiveTab(tab.id),
        className: `flex-1 px-4 py-2 text-sm font-medium transition-colors ${activeTab === tab.id ? "text-[var(--accent)] border-b-2 border-[var(--accent)]" : "text-[var(--text-dim)] hover:text-[var(--text)]"}`
      },
      tab.label
    ))), /* @__PURE__ */ import_react7.default.createElement("div", { className: "flex-1 overflow-y-auto p-4" }, activeTab === "loadouts" && /* @__PURE__ */ import_react7.default.createElement(LoadoutsTab, { calcId: effectiveId }), activeTab === "discussion" && /* @__PURE__ */ import_react7.default.createElement("div", { className: "text-center text-[var(--text-dim)] py-8" }, "GitHub Discussion integration coming in Phase 2.")));
  }
  var import_react7;
  var init_CalcInfoPanel = __esm({
    "public/src/plugins/calc-info/components/CalcInfoPanel.jsx"() {
      import_react7 = __toESM(require_react());
      init_LoadoutsTab();
    }
  });

  // public/src/plugins/calc-info/CalcInfoPanelMount.jsx
  function mountCalcInfoPanel() {
    const root = document.getElementById("calc-info-panel-root");
    if (!root) {
      return false;
    }
    if (!reactRoot) {
      reactRoot = react_dom_global_default.createRoot(root);
    }
    const calc = window.Action?.getCurrentCalc?.();
    const calcId = calc?.id;
    const remoteId = calc?.remoteId;
    reactRoot.render(
      /* @__PURE__ */ react_global_default.createElement(
        CalcInfoPanel,
        {
          calcId,
          remoteId
        }
      )
    );
    return true;
  }
  function updateCalcInfoPanel() {
    if (!reactRoot) return;
    mountCalcInfoPanel();
  }
  function initCalcInfoPanelMount() {
    if (typeof window === "undefined") return;
    const handlePanelOpen = (e) => {
      if (e.detail?.panel === "calc-info") {
        requestAnimationFrame(() => {
          mountCalcInfoPanel();
        });
      }
    };
    window.InstacalcEvents?.on?.(
      window.InstacalcEvents?.PANEL_OPEN || "ic:panel-open",
      handlePanelOpen
    );
    if (!mountCalcInfoPanel()) {
      const observer = new MutationObserver(() => {
        if (mountCalcInfoPanel()) {
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer.disconnect(), 1e4);
    }
    window.InstacalcEvents?.on?.(
      window.InstacalcEvents?.CALC_SWITCH || "ic:calc-switch",
      updateCalcInfoPanel
    );
  }
  var reactRoot;
  var init_CalcInfoPanelMount = __esm({
    "public/src/plugins/calc-info/CalcInfoPanelMount.jsx"() {
      init_react_global();
      init_react_dom_global();
      init_CalcInfoPanel();
      reactRoot = null;
    }
  });

  // public/src/plugins/calc-info/index.js
  var calc_info_exports = {};
  __export(calc_info_exports, {
    CalcInfoBadge: () => CalcInfoBadge,
    CalcInfoPanelConfig: () => CalcInfoPanelConfig,
    LoadoutParser: () => import_loadoutParser2.default,
    LoadoutStore: () => import_LoadoutStore2.default,
    applyLoadout: () => applyLoadout,
    useCalcInfoCounts: () => useCalcInfoCounts,
    useLoadouts: () => useLoadouts
  });
  function initCalcInfoPlugin() {
    const plugins2 = getPlugins2();
    if (!plugins2) {
      console.warn("[calc-info] Plugin registry not available");
      return;
    }
    plugins2.panels.register(CalcInfoPanelConfig);
    plugins2.toolbarSlots.register({
      id: "calc-info-badge",
      component: CalcInfoBadge,
      position: "right",
      // Render on right side of toolbar
      order: 50
    });
    plugins2.registerAction("applyLoadout", applyLoadout);
    initCalcInfoPanelMount();
    console.log("[calc-info] Plugin initialized");
  }
  var import_LoadoutStore, import_loadoutParser, import_LoadoutStore2, import_loadoutParser2, getPlugins2;
  var init_calc_info = __esm({
    "public/src/plugins/calc-info/index.js"() {
      import_LoadoutStore = __toESM(require_LoadoutStore());
      import_loadoutParser = __toESM(require_loadoutParser());
      init_panel2();
      init_actions();
      init_CalcInfoBadge();
      init_CalcInfoPanelMount();
      init_panel2();
      init_actions();
      init_CalcInfoBadge();
      import_LoadoutStore2 = __toESM(require_LoadoutStore());
      import_loadoutParser2 = __toESM(require_loadoutParser());
      init_useLoadouts();
      init_useCalcInfoCounts();
      getPlugins2 = () => {
        if (typeof window !== "undefined" && window.__IC_PLUGINS__) {
          return window.__IC_PLUGINS__;
        }
        return null;
      };
      if (typeof window !== "undefined") {
        if (document.readyState === "loading") {
          document.addEventListener("DOMContentLoaded", initCalcInfoPlugin);
        } else {
          initCalcInfoPlugin();
        }
      }
    }
  });

  // public/src/firebase-config.js
  (function() {
    window.FIREBASE_CONFIG = {
      apiKey: "AIzaSyDB0PiisLG31Lvn-KGADptCUpUiOcliWb8",
      authDomain: "instacalc.com",
      databaseURL: "https://instacalc.firebaseio.com",
      projectId: "firebase-instacalc",
      storageBucket: "firebase-instacalc.appspot.com",
      messagingSenderId: "297159307988"
    };
    window.getCloudFunctionUrl = function(functionName, region) {
      region = region || "us-central1";
      return `https://${region}-${window.FIREBASE_CONFIG.projectId}.cloudfunctions.net/${functionName}`;
    };
    window.GOOGLE_CLIENT_ID = "297159307988-ufriq1likvgqranbqgf9krkm1g7rjn85.apps.googleusercontent.com";
    window.FIREBASE_CONFIG_WIKI = {
      apiKey: "AIzaSyAXbf-7j1F9VSoXs9EiKVwlzmDG99Qpf3M",
      authDomain: "instacalc-2019.firebaseapp.com",
      databaseURL: "https://instacalc-2019-default-rtdb.firebaseio.com",
      projectId: "instacalc-2019",
      storageBucket: "instacalc-2019.appspot.com",
      messagingSenderId: "1085046399816",
      appId: "1:1085046399816:web:b60e54b63b5ca1f45a5c83"
    };
    const MAX_AUTH_LOGS = 100;
    window.AuthDebugLog = {
      entries: [],
      log(type, message, data = null) {
        const entry = {
          time: (/* @__PURE__ */ new Date()).toISOString(),
          timestamp: Date.now(),
          type,
          message,
          data
        };
        this.entries.push(entry);
        if (this.entries.length > MAX_AUTH_LOGS) this.entries.shift();
        if (window.location.search.includes("debug=auth") || window.location.search.includes("debug=all")) {
          console.log(`[AuthDebug] ${type}: ${message}`, data || "");
        }
      },
      getRecent(n = 50) {
        return this.entries.slice(-n);
      },
      clear() {
        this.entries = [];
      },
      // Format for copy/paste
      toText() {
        return this.entries.map((e) => {
          const dataStr = e.data ? ` | ${JSON.stringify(e.data)}` : "";
          return `[${e.time}] ${e.type}: ${e.message}${dataStr}`;
        }).join("\n");
      }
    };
    window.isLocalDev = function() {
      const host = window.location.hostname;
      return host === "localhost" || host === "127.0.0.1";
    };
    window.EMULATOR_PORTS = (function() {
      const params = new URLSearchParams(window.location.search);
      const hostingPort = parseInt(window.location.port || "80", 10);
      const baseHosting = 5002;
      const autoOffset = hostingPort >= baseHosting ? hostingPort - baseHosting : 0;
      const baseAuth = 9099;
      const baseDb = 9e3;
      const baseFunctions = 5001;
      const ports = {
        auth: parseInt(params.get("authPort") || String(baseAuth + autoOffset), 10),
        database: parseInt(params.get("dbPort") || String(baseDb + autoOffset), 10),
        functions: parseInt(params.get("functionsPort") || String(baseFunctions + autoOffset), 10),
        offset: autoOffset,
        hostingPort
      };
      AuthDebugLog.log("ports", "Emulator ports auto-detected", {
        hostingPort,
        offset: autoOffset,
        auth: ports.auth,
        database: ports.database,
        urlOverrides: {
          authPort: params.get("authPort"),
          dbPort: params.get("dbPort")
        }
      });
      return ports;
    })();
    window.shouldUseEmulators = function() {
      if (!window.isLocalDev()) return false;
      const params = new URLSearchParams(window.location.search);
      return params.has("emulator") || params.get("emulator") === "true";
    };
    window.getEmulatorsToUse = function() {
      const params = new URLSearchParams(window.location.search);
      const val = params.get("emulator");
      if (!val || val === "true") {
        return ["auth", "database"];
      }
      return val.split(",").map((s) => s.trim());
    };
    window.connectToEmulators = function() {
      if (!window.shouldUseEmulators()) {
        AuthDebugLog.log("emulator", "Emulators disabled (not localhost or no ?emulator param)");
        return false;
      }
      const emulatorsToUse = window.getEmulatorsToUse();
      AuthDebugLog.log("emulator", "Connecting to emulators", { emulators: emulatorsToUse });
      try {
        if (emulatorsToUse.includes("auth") && firebase.auth) {
          const authPort = window.EMULATOR_PORTS.auth;
          firebase.auth().useEmulator(`http://localhost:${authPort}`);
          AuthDebugLog.log("emulator", `Auth emulator connected on port ${authPort}`);
          const removeEmulatorWarning = () => {
            const warning = document.querySelector(".firebase-emulator-warning");
            if (warning) {
              warning.remove();
              AuthDebugLog.log("emulator", "Removed Firebase emulator warning banner");
              return true;
            }
            return false;
          };
          if (!removeEmulatorWarning()) {
            const observer = new MutationObserver((mutations, obs) => {
              if (removeEmulatorWarning()) {
                obs.disconnect();
              }
            });
            observer.observe(document.body, { childList: true, subtree: true });
            setTimeout(() => observer.disconnect(), 5e3);
          }
        }
        if (emulatorsToUse.includes("database") && firebase.database) {
          const dbPort = window.EMULATOR_PORTS.database;
          firebase.database().useEmulator("localhost", dbPort);
          AuthDebugLog.log("emulator", `Database emulator connected on port ${dbPort}`);
        }
        return true;
      } catch (err) {
        AuthDebugLog.log("emulator-error", "Failed to connect to emulators", { error: err.message });
        console.error("Emulator connection failed:", err);
        return false;
      }
    };
    window.AuthDebug = {
      // Get current auth status summary
      status() {
        const user = typeof firebase !== "undefined" && firebase.auth ? firebase.auth().currentUser : null;
        const status = {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          isLocalDev: window.isLocalDev(),
          emulatorMode: window.shouldUseEmulators?.() || false,
          ports: window.EMULATOR_PORTS,
          firebaseInitialized: typeof firebase !== "undefined" && firebase.apps?.length > 0,
          user: user ? {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            isAnonymous: user.isAnonymous,
            providerId: user.providerData?.[0]?.providerId
          } : null
        };
        console.table({
          "Local Dev": status.isLocalDev,
          "Emulator Mode": status.emulatorMode,
          "Auth Port": status.ports?.auth,
          "DB Port": status.ports?.database,
          "Offset": status.ports?.offset,
          "Firebase Init": status.firebaseInitialized,
          "User": status.user?.email || (status.user?.isAnonymous ? "anonymous" : "none")
        });
        return status;
      },
      // Get recent auth log entries
      log(n = 20) {
        const entries = AuthDebugLog.getRecent(n);
        entries.forEach((e) => {
          const time = new Date(e.timestamp).toLocaleTimeString();
          console.log(`[${time}] ${e.type}: ${e.message}`, e.data || "");
        });
        return entries;
      },
      // Copy full debug info to clipboard
      copy() {
        const info = {
          ...this.status(),
          authLog: AuthDebugLog.getRecent(50)
        };
        const text = JSON.stringify(info, null, 2);
        if (navigator.clipboard) {
          navigator.clipboard.writeText(text).then(() => console.log("Debug info copied!"));
        }
        return info;
      },
      // Test emulator connectivity
      async testEmulator() {
        const ports = window.EMULATOR_PORTS;
        console.log("Testing emulator connectivity...");
        try {
          const authRes = await fetch(`http://localhost:${ports.auth}/`);
          const authData = await authRes.json();
          console.log(`\u2713 Auth emulator (port ${ports.auth}):`, authData.authEmulator?.ready ? "ready" : "not ready");
        } catch (e) {
          console.log(`\u2717 Auth emulator (port ${ports.auth}): not reachable - ${e.message}`);
        }
        try {
          const dbRes = await fetch(`http://localhost:${ports.database}/.json`);
          console.log(`\u2713 Database emulator (port ${ports.database}): reachable`);
        } catch (e) {
          console.log(`\u2717 Database emulator (port ${ports.database}): not reachable - ${e.message}`);
        }
      }
    };
    AuthDebugLog.log("init", "Firebase config loaded", {
      isLocalDev: window.isLocalDev(),
      hasEmulatorParam: new URLSearchParams(window.location.search).has("emulator")
    });
  })();

  // public/src/main.js
  init_timestamps();
  var import_tokenGenerator = __toESM(require_tokenGenerator());

  // public/src/utils/helpers.js
  var urlCache = /* @__PURE__ */ new Map();
  function safeClone(obj) {
    try {
      return structuredClone(obj);
    } catch (e) {
      return JSON.parse(JSON.stringify(obj));
    }
  }
  if (typeof window !== "undefined") {
    window.safeClone = safeClone;
  }
  function loadHTML() {
    const getCacheBuster = () => {
      const script = document.querySelector('script[src*="?v="]');
      if (script) {
        const match = script.src.match(/\?v=([a-zA-Z0-9_-]+)/);
        if (match) return match[1];
      }
      return null;
    };
    const cacheBuster = getCacheBuster();
    document.querySelectorAll("[data-load]:not(.loaded)").forEach((el) => {
      el.classList.add("loaded");
      let url = el.getAttribute("data-load");
      if (cacheBuster && !url.includes("?")) {
        url = `${url}?v=${cacheBuster}`;
      }
      if (!urlCache.has(url)) {
        urlCache.set(url, Util.smartFetch(url));
      }
      urlCache.get(url).then((text) => {
        el.innerHTML = text;
        if (window.Alpine && window.Alpine.initTree && !window.Alpine._startedViaObserver) {
          setTimeout(() => {
            const xDataEl = el.querySelector("[x-data]");
            if (xDataEl && !xDataEl._x_dataStack) {
              window.Alpine.initTree(el);
            }
          }, 0);
        }
        try {
          el.querySelectorAll('CalcList,[data-portal="CalcList"],#CalcList').forEach((node) => {
            node.setAttribute("data-ic-portal-owner", "CalcList");
          });
        } catch (_e) {
        }
        if (text.includes("data-load")) {
          setTimeout(() => loadHTML(), 10);
        }
        if (typeof Action !== "undefined" && Action.scanForEmbed) {
          Action.scanForEmbed();
        }
        scanForConversionUI();
      }).catch((err) => {
        console.error(`[loadHTML] Failed to load ${url}:`, err);
        el.innerHTML = "<!-- Load Failed -->";
        el.classList.add("load-failed");
      });
    });
    if (typeof Action !== "undefined" && Action.scanForEmbed) {
      Action.scanForEmbed();
    }
    scanForConversionUI();
  }
  function processPress(event) {
    const el = event.target;
    const isButton = el.tagName === "BUTTON" || el.closest("button");
    const link = el.closest(".gallery a");
    if (link) {
      event.preventDefault();
      const preview = link.parentElement.parentElement.querySelector(".gallery-preview") || link.parentElement.parentElement.parentElement.querySelector(".gallery-preview");
      const embedId = link.getAttribute("href").split("/").reverse()[0];
      preview.classList.remove("loaded");
      preview.setAttribute("embed-id", embedId);
      Action.scanForEmbed();
      return;
    }
    const convertButtonPad = el.closest(".convert buttonpad");
    if (convertButtonPad && isButton) {
      const selects = convertButtonPad.querySelectorAll("select");
      const str = `${selects[0].value} ${selects[1].value} in ${selects[2].value}`;
      Action.addTextToCurrentCalc(str.trim());
      return;
    }
    const buttonPad = el.closest("buttonpad");
    if (buttonPad && isButton) {
      const button = el.closest("button");
      const cmd = button.getAttribute("data-cmd") || button.textContent;
      switch (cmd) {
        case "BACKSPACE":
          Action.deleteTextFromCurrentCalc(1);
          break;
        case "DOWNARROW":
          Action.selectRelativeRowSoft(1);
          break;
        case "UPARROW":
          Action.selectRelativeRowSoft(-1);
          break;
        default:
          Action.addTextToCurrentCalc(cmd.trim());
      }
    }
  }
  function scanForConversionUI() {
    document.querySelectorAll(".conversion-ui:not(.loaded)").forEach((container) => {
      container.classList.add("loaded");
      createConversionUI(config, container);
    });
  }
  if (typeof window !== "undefined") {
    window.loadHTML = loadHTML;
  }
  document.addEventListener("DOMContentLoaded", function() {
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
    const eventType = isTouchDevice ? "touchstart" : "click";
    document.addEventListener(eventType, processPress);
    loadHTML();
  });
  var config = {
    categories: [
      "money",
      "length",
      "area",
      "volume",
      "mass",
      "time",
      "temp",
      "energy",
      "power",
      "data"
    ],
    categoriesAdvanced: [
      "speed",
      "angle",
      "pressure",
      "freq",
      "force",
      "current",
      "voltage",
      "charge",
      "cap",
      "res"
      /*'lum intensity', 'illuminance', */
      /*'radioactivity',  'absorbed dose', 'data rate' */
    ],
    defaultCategory: "length",
    moneyflags: {
      "USD": "\u{1F1FA}\u{1F1F8}",
      // United States
      "EUR": "\u{1F1EA}\u{1F1FA}",
      // European Union
      "CNY": "\u{1F1E8}\u{1F1F3}",
      // China
      "JPY": "\u{1F1EF}\u{1F1F5}",
      // Japan
      "INR": "\u{1F1EE}\u{1F1F3}",
      // India
      "MXN": "\u{1F1F2}\u{1F1FD}",
      // Mexico
      "BRL": "\u{1F1E7}\u{1F1F7}",
      // Brazil
      "KRW": "\u{1F1F0}\u{1F1F7}",
      // South Korea
      "GBP": "\u{1F1EC}\u{1F1E7}",
      // United Kingdom
      "AUD": "\u{1F1E6}\u{1F1FA}",
      // Australia
      "CAD": "\u{1F1E8}\u{1F1E6}",
      // Canada
      "HKD": "\u{1F1ED}\u{1F1F0}",
      // Hong Kong
      "BTC": "\u20BF",
      // Bitcoin
      "AED": "\u{1F1E6}\u{1F1EA}",
      // United Arab Emirates
      "AFN": "\u{1F1E6}\u{1F1EB}",
      // Afghanistan
      "ALL": "\u{1F1E6}\u{1F1F1}",
      // Albania
      "AMD": "\u{1F1E6}\u{1F1F2}",
      // Armenia
      "ANG": "\u{1F1E8}\u{1F1FC}",
      // Curaçao
      "AOA": "\u{1F1E6}\u{1F1F4}",
      // Angola
      "ARS": "\u{1F1E6}\u{1F1F7}",
      // Argentina
      "AWG": "\u{1F1E6}\u{1F1FC}",
      // Aruba
      "AZN": "\u{1F1E6}\u{1F1FF}",
      // Azerbaijan
      "BAM": "\u{1F1E7}\u{1F1E6}",
      // Bosnia and Herzegovina
      "BBD": "\u{1F1E7}\u{1F1E7}",
      // Barbados
      "BDT": "\u{1F1E7}\u{1F1E9}",
      // Bangladesh
      "BGN": "\u{1F1E7}\u{1F1EC}",
      // Bulgaria
      "BHD": "\u{1F1E7}\u{1F1ED}",
      // Bahrain
      "BIF": "\u{1F1E7}\u{1F1EE}",
      // Burundi
      "BMD": "\u{1F1E7}\u{1F1F2}",
      // Bermuda
      "BND": "\u{1F1E7}\u{1F1F3}",
      // Brunei
      "BOB": "\u{1F1E7}\u{1F1F4}",
      // Bolivia
      "BSD": "\u{1F1E7}\u{1F1F8}",
      // Bahamas
      "BTN": "\u{1F1E7}\u{1F1F9}",
      // Bhutan
      "BWP": "\u{1F1E7}\u{1F1FC}",
      // Botswana
      "BYN": "\u{1F1E7}\u{1F1FE}",
      // Belarus
      "BZD": "\u{1F1E7}\u{1F1FF}",
      // Belize
      "CDF": "\u{1F1E8}\u{1F1E9}",
      // Democratic Republic of Congo
      "CHF": "\u{1F1E8}\u{1F1ED}",
      // Switzerland
      "CLP": "\u{1F1E8}\u{1F1F1}",
      // Chile
      "COP": "\u{1F1E8}\u{1F1F4}",
      // Colombia
      "CRC": "\u{1F1E8}\u{1F1F7}",
      // Costa Rica
      "CUC": "\u{1F1E8}\u{1F1FA}",
      // Cuba
      "CVE": "\u{1F1E8}\u{1F1FB}",
      // Cape Verde
      "CZK": "\u{1F1E8}\u{1F1FF}",
      // Czech Republic
      "DJF": "\u{1F1E9}\u{1F1EF}",
      // Djibouti
      "DKK": "\u{1F1E9}\u{1F1F0}",
      // Denmark
      "DOP": "\u{1F1E9}\u{1F1F4}",
      // Dominican Republic
      "DZD": "\u{1F1E9}\u{1F1FF}",
      // Algeria
      "EGP": "\u{1F1EA}\u{1F1EC}",
      // Egypt
      "ERN": "\u{1F1EA}\u{1F1F7}",
      // Eritrea
      "ETB": "\u{1F1EA}\u{1F1F9}",
      // Ethiopia
      "FJD": "\u{1F1EB}\u{1F1EF}",
      // Fiji
      "FKP": "\u{1F1EB}\u{1F1F0}",
      // Falkland Islands
      "GEL": "\u{1F1EC}\u{1F1EA}",
      // Georgia
      "GGP": "\u{1F1EC}\u{1F1EC}",
      // Guernsey
      "GHS": "\u{1F1EC}\u{1F1ED}",
      // Ghana
      "GIP": "\u{1F1EC}\u{1F1EE}",
      // Gibraltar
      "GMD": "\u{1F1EC}\u{1F1F2}",
      // Gambia
      "GNF": "\u{1F1EC}\u{1F1F3}",
      // Guinea
      "GTQ": "\u{1F1EC}\u{1F1F9}",
      // Guatemala
      "GYD": "\u{1F1EC}\u{1F1FE}",
      // Guyana
      "HNL": "\u{1F1ED}\u{1F1F3}",
      // Honduras
      "HRK": "\u{1F1ED}\u{1F1F7}",
      // Croatia
      "HTG": "\u{1F1ED}\u{1F1F9}",
      // Haiti
      "HUF": "\u{1F1ED}\u{1F1FA}",
      // Hungary
      "IDR": "\u{1F1EE}\u{1F1E9}",
      // Indonesia
      "ILS": "\u{1F1EE}\u{1F1F1}",
      // Israel
      "IMP": "\u{1F1EE}\u{1F1F2}",
      // Isle of Man
      "IQD": "\u{1F1EE}\u{1F1F6}",
      // Iraq
      "IRR": "\u{1F1EE}\u{1F1F7}",
      // Iran
      "ISK": "\u{1F1EE}\u{1F1F8}",
      // Iceland
      "JEP": "\u{1F1EF}\u{1F1EA}",
      // Jersey
      "JMD": "\u{1F1EF}\u{1F1F2}",
      // Jamaica
      "JOD": "\u{1F1EF}\u{1F1F4}",
      // Jordan
      "KES": "\u{1F1F0}\u{1F1EA}",
      // Kenya
      "KHR": "\u{1F1F0}\u{1F1ED}",
      // Cambodia
      "KMF": "\u{1F1F0}\u{1F1F2}",
      // Comoros
      "KPW": "\u{1F1F0}\u{1F1F5}",
      // North Korea
      "KWD": "\u{1F1F0}\u{1F1FC}",
      // Kuwait
      "KYD": "\u{1F1F0}\u{1F1FE}",
      // Cayman Islands
      "KZT": "\u{1F1F0}\u{1F1FF}",
      // Kazakhstan
      "LAK": "\u{1F1F1}\u{1F1E6}",
      // Laos
      "LBP": "\u{1F1F1}\u{1F1E7}",
      // Lebanon
      "LEV": "\u{1F1E7}\u{1F1EC}",
      // Bulgaria (LEV is BGN)
      "LKR": "\u{1F1F1}\u{1F1F0}",
      // Sri Lanka
      "LRD": "\u{1F1F1}\u{1F1F7}",
      // Liberia
      "LSL": "\u{1F1F1}\u{1F1F8}",
      // Lesotho
      "LYD": "\u{1F1F1}\u{1F1FE}",
      // Libya
      "MAD": "\u{1F1F2}\u{1F1E6}",
      // Morocco
      "MDL": "\u{1F1F2}\u{1F1E9}",
      // Moldova
      "MGA": "\u{1F1F2}\u{1F1EC}",
      // Madagascar
      "MKD": "\u{1F1F2}\u{1F1F0}",
      // North Macedonia
      "MMK": "\u{1F1F2}\u{1F1F2}",
      // Myanmar
      "MNT": "\u{1F1F2}\u{1F1F3}",
      // Mongolia
      "MOP": "\u{1F1F2}\u{1F1F4}",
      // Macau
      "MRU": "\u{1F1F2}\u{1F1F7}",
      // Mauritania
      "MUR": "\u{1F1F2}\u{1F1FA}",
      // Mauritius
      "MVR": "\u{1F1F2}\u{1F1FB}",
      // Maldives
      "MWK": "\u{1F1F2}\u{1F1FC}",
      // Malawi
      "MYR": "\u{1F1F2}\u{1F1FE}",
      // Malaysia
      "MZN": "\u{1F1F2}\u{1F1FF}",
      // Mozambique
      "NAD": "\u{1F1F3}\u{1F1E6}",
      // Namibia
      "NGN": "\u{1F1F3}\u{1F1EC}",
      // Nigeria
      "NIO": "\u{1F1F3}\u{1F1EE}",
      // Nicaragua
      "NOK": "\u{1F1F3}\u{1F1F4}",
      // Norway
      "NPR": "\u{1F1F3}\u{1F1F5}",
      // Nepal
      "NZD": "\u{1F1F3}\u{1F1FF}",
      // New Zealand
      "OMR": "\u{1F1F4}\u{1F1F2}",
      // Oman
      "PAB": "\u{1F1F5}\u{1F1E6}",
      // Panama
      "PEN": "\u{1F1F5}\u{1F1EA}",
      // Peru
      "PGK": "\u{1F1F5}\u{1F1EC}",
      // Papua New Guinea
      "PHP": "\u{1F1F5}\u{1F1ED}",
      // Philippines
      "PKR": "\u{1F1F5}\u{1F1F0}",
      // Pakistan
      "PLN": "\u{1F1F5}\u{1F1F1}",
      // Poland
      "PYG": "\u{1F1F5}\u{1F1FE}",
      // Paraguay
      "QAR": "\u{1F1F6}\u{1F1E6}",
      // Qatar
      "RMB": "\u{1F1E8}\u{1F1F3}",
      // China (RMB is CNY)
      "RON": "\u{1F1F7}\u{1F1F4}",
      // Romania
      "RSD": "\u{1F1F7}\u{1F1F8}",
      // Serbia
      "RUB": "\u{1F1F7}\u{1F1FA}",
      // Russia
      "RWF": "\u{1F1F7}\u{1F1FC}",
      // Rwanda
      "SAR": "\u{1F1F8}\u{1F1E6}",
      // Saudi Arabia
      "SBD": "\u{1F1F8}\u{1F1E7}",
      // Solomon Islands
      "SCR": "\u{1F1F8}\u{1F1E8}",
      // Seychelles
      "SDG": "\u{1F1F8}\u{1F1E9}",
      // Sudan
      "SEK": "\u{1F1F8}\u{1F1EA}",
      // Sweden
      "SGD": "\u{1F1F8}\u{1F1EC}",
      // Singapore
      "SHP": "\u{1F1F8}\u{1F1ED}",
      // Saint Helena
      "SLL": "\u{1F1F8}\u{1F1F1}",
      // Sierra Leone
      "SOS": "\u{1F1F8}\u{1F1F4}",
      // Somalia
      "SRD": "\u{1F1F8}\u{1F1F7}",
      // Suriname
      "SSP": "\u{1F1F8}\u{1F1F8}",
      // South Sudan
      "STD": "\u{1F1F8}\u{1F1F9}",
      // São Tomé and Príncipe
      "STN": "\u{1F1F8}\u{1F1F9}",
      // São Tomé and Príncipe
      "SVC": "\u{1F1F8}\u{1F1FB}",
      // El Salvador
      "SYP": "\u{1F1F8}\u{1F1FE}",
      // Syria
      "SZL": "\u{1F1F8}\u{1F1FF}",
      // Eswatini
      "THB": "\u{1F1F9}\u{1F1ED}",
      // Thailand
      "TJS": "\u{1F1F9}\u{1F1EF}",
      // Tajikistan
      "TMT": "\u{1F1F9}\u{1F1F2}",
      // Turkmenistan
      "TND": "\u{1F1F9}\u{1F1F3}",
      // Tunisia
      "TOP": "\u{1F1F9}\u{1F1F4}",
      // Tonga
      "TRY": "\u{1F1F9}\u{1F1F7}",
      // Turkey
      "TTD": "\u{1F1F9}\u{1F1F9}",
      // Trinidad and Tobago
      "TWD": "\u{1F1F9}\u{1F1FC}",
      // Taiwan
      "TZS": "\u{1F1F9}\u{1F1FF}",
      // Tanzania
      "UAH": "\u{1F1FA}\u{1F1E6}",
      // Ukraine
      "UGX": "\u{1F1FA}\u{1F1EC}",
      // Uganda
      "UYU": "\u{1F1FA}\u{1F1FE}",
      // Uruguay
      "UZS": "\u{1F1FA}\u{1F1FF}",
      // Uzbekistan
      "VES": "\u{1F1FB}\u{1F1EA}",
      // Venezuela
      "VND": "\u{1F1FB}\u{1F1F3}",
      // Vietnam
      "VUV": "\u{1F1FB}\u{1F1FA}",
      // Vanuatu
      "WON": "\u{1F1F0}\u{1F1F7}",
      // South Korea (WON is KRW)
      "WST": "\u{1F1FC}\u{1F1F8}",
      // Samoa
      "XAF": "\u{1F3F3}\uFE0F",
      // Central African CFA franc
      "XAG": "\u{1F948}",
      // Silver
      "XAU": "\u{1F947}",
      // Gold
      "XCD": "\u{1F3F3}\uFE0F",
      // East Caribbean dollar
      "XDR": "\u{1F3E6}",
      // Special Drawing Rights
      "XOF": "\u{1F3F3}\uFE0F",
      // West African CFA franc
      "XPD": "\u26AA",
      // Palladium
      "XPF": "\u{1F3F3}\uFE0F",
      // CFP franc
      "XPT": "\u26AA",
      // Platinum
      "YEN": "\u{1F1EF}\u{1F1F5}",
      // Japan (YEN is JPY)
      "YER": "\u{1F1FE}\u{1F1EA}",
      // Yemen
      "ZAR": "\u{1F1FF}\u{1F1E6}",
      // South Africa
      "ZMW": "\u{1F1FF}\u{1F1F2}",
      // Zambia
      "ZWL": "\u{1F1FF}\u{1F1FC}"
      // Zimbabwe
    },
    units: {
      money: ["USD", "EUR", "CNY", "JPY", "INR", "MXN", "BRL", "KRW", "GBP", "AUD", "CAD", "HKD", "BTC", "---", "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN", "BAM", "BBD", "BDT", "BGN", "BHD", "BIF", "BMD", "BND", "BOB", "BRL", "BSD", "BTC", "BTN", "BWP", "BYN", "BZD", "CAD", "CDF", "CHF", "CLF", "CLP", "CNH", "CNY", "COP", "CRC", "CUC", "CVE", "CZK", "DJF", "DKK", "DOP", "DZD", "EGP", "ERN", "ETB", "EUR", "FJD", "FKP", "GBP", "GEL", "GGP", "GHS", "GIP", "GMD", "GNF", "GTQ", "GYD", "HKD", "HNL", "HRK", "HTG", "HUF", "IDR", "ILS", "IMP", "INR", "IQD", "IRR", "ISK", "JEP", "JMD", "JOD", "JPY", "KES", "KHR", "KMF", "KPW", "KRW", "KWD", "KYD", "KZT", "LAK", "LBP", "LEV", "LKR", "LRD", "LSL", "LYD", "MAD", "MDL", "MGA", "MKD", "MMK", "MNT", "MOP", "MRU", "MUR", "MVR", "MWK", "MXN", "MYR", "MZN", "NAD", "NGN", "NIO", "NOK", "NPR", "NZD", "OMR", "PAB", "PEN", "PGK", "PHP", "PKR", "PLN", "PYG", "QAR", "RMB", "RON", "RSD", "RUB", "RWF", "SAR", "SBD", "SCR", "SDG", "SEK", "SGD", "SHP", "SLL", "SOS", "SRD", "SSP", "STD", "STN", "SVC", "SYP", "SZL", "THB", "TJS", "TMT", "TND", "TOP", "TRY", "TTD", "TWD", "TZS", "UAH", "UGX", "USD", "UYU", "UZS", "VES", "VND", "VUV", "WON", "WST", "XAF", "XAG", "XAU", "XCD", "XDR", "XOF", "XPD", "XPF", "XPT", "YEN", "YER", "ZAR", "ZMW", "ZWL"],
      length: ["meter", "kilometer", "centimeter", "millimeter", "micrometer", "nanometer", "inch", "foot", "yard", "mile", "lightyear", "astronomical unit", "parsec"],
      area: ["square meter", "square kilometer", "square centimeter", "square millimeter", "square foot", "square inch", "square yard", "square mile", "acre", "hectare"],
      volume: ["cubic meter", "liter", "milliliter", "cubic centimeter", "cubic foot", "cubic inch", "gallon", "quart", "pint", "cup", "fluid ounce", "tablespoon", "teaspoon"],
      mass: ["kilogram", "gram", "milligram", "microgram", "metric ton", "pound", "ounce", "stone", "short ton", "long ton"],
      time: ["second", "millisecond", "microsecond", "nanosecond", "minute", "hour", "day", "week", "month", "year", "decade", "century"],
      temp: ["celsius", "fahrenheit", "kelvin"],
      energy: ["joule", "kilojoule", "calorie", "kilocalorie", "watt hour", "kilowatt hour", "electronvolt", "british thermal unit"],
      power: ["watt", "kilowatt", "megawatt", "horsepower"],
      pressure: ["pascal", "kilopascal", "bar", "atmosphere", "torr", "psi"],
      speed: ["meter per second", "kilometer per hour", "mile per hour", "foot per second", "knot"],
      data: ["bit", "byte", "kilobyte", "megabyte", "gigabyte", "terabyte", "petabyte"],
      "data rate": ["bit per second", "byte per second", "kilobit per second", "megabit per second", "gigabit per second"],
      angle: ["degree", "radian", "gradian", "arcminute", "arcsecond"],
      freq: ["hertz", "kilohertz", "megahertz", "gigahertz"],
      force: ["newton", "kilonewton", "pound force", "kilogram force"],
      "current": ["ampere", "milliampere", "microampere"],
      "voltage": ["volt", "kilovolt", "millivolt"],
      "charge": ["coulomb", "ampere hour", "milliampere hour"],
      "cap": ["farad", "microfarad", "nanofarad", "picofarad"],
      "res": ["ohm", "kiloohm", "megaohm"],
      "lum intensity": ["candela", "lumen"],
      illuminance: ["lux", "foot candle"],
      radioactivity: ["becquerel", "curie"],
      "absorbed dose": ["gray", "rad", "sievert"]
    },
    defaultUnits: {
      money: ["USD", "EUR"],
      length: ["meter", "foot"],
      area: ["square meter", "square foot"],
      volume: ["liter", "gallon"],
      mass: ["kilogram", "pound"],
      time: ["second", "minute"],
      temp: ["celsius", "fahrenheit"],
      energy: ["joule", "calorie"],
      power: ["watt", "horsepower"],
      pressure: ["pascal", "psi"],
      speed: ["meter per second", "mile per hour"],
      data: ["byte", "megabyte"],
      "data rate": ["bit per second", "megabit per second"],
      angle: ["degree", "radian"],
      freq: ["hertz", "kilohertz"],
      force: ["newton", "pound force"],
      "current": ["ampere", "milliampere"],
      "voltage": ["volt", "kilovolt"],
      "charge": ["coulomb", "ampere hour"],
      "cap": ["farad", "microfarad"],
      "res": ["ohm", "kiloohm"],
      "lum intensity": ["candela", "lumen"],
      illuminance: ["lux", "foot-candle"],
      radioactivity: ["becquerel", "curie"],
      "absorbed dose": ["gray", "sievert"]
    }
  };
  function createConversionUI(config2, container) {
    let currentCategory = config2.defaultCategory;
    let showMore = true;
    container.innerHTML = `
        <div class="flex items-center justify-between mx-4 mt-4 mb-2">
            <input class="w-2/5 p-2 text-center border rounded input1 border-color-soft text-color-default" value="1">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="relative w-6 h-6 cursor-pointer top-6 swap-button">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
            <input class="w-2/5 p-2 text-center border rounded input2 border-color-soft text-color-default">
        </div>
        <div class="flex items-center justify-between mx-4 mb-4">
            <select class="w-2/5 p-2 text-center border rounded unit1 border-color-soft text-color-default bg-secondary"></select>
            <select class="w-2/5 p-2 text-center border rounded unit2 border-color-soft text-color-default bg-secondary"></select>
        </div>
        <div class="grid grid-flow-row grid-cols-4 gap-1 p-1 overflow-x-scroll no-scrollbar rounded-md category-buttons"></div>
    `;
    const categoryButtons = container.querySelector(".category-buttons");
    const [input1, input2] = container.querySelectorAll("input");
    const [unit1, unit2] = container.querySelectorAll("select");
    const swapButton = container.querySelector(".swap-button");
    function renderCategories() {
      const regularCategories = config2.categories.map(createCategoryButton);
      const moreButton = createMoreButton();
      const advancedCategories = showMore ? config2.categoriesAdvanced.map(createCategoryButton) : [];
      categoryButtons.innerHTML = "";
      categoryButtons.append(...regularCategories, ...advancedCategories);
    }
    function createCategoryButton(category) {
      const button = document.createElement("button");
      button.className = `p-2 h-10 lg:h-10 rounded bg-secondary capitalize text-xs font-medium${category === currentCategory ? " active" : ""}`;
      button.dataset.category = category;
      if (typeof categorySVG !== "undefined" && categorySVG[category]) {
        button.innerHTML = `
                        <span class="flex items-center justify-center gap-2">
                            ${categorySVG[category]}
                            <span class="capitalize">${category}</span>
                        </span>
                    `;
      } else {
        button.textContent = category;
      }
      return button;
    }
    const categorySVG = {
      "money": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
</svg>`,
      "length": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
  stroke-width="1.5" stroke="currentColor" class="size-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M5 9v6m14-6v6" />
  </svg>`,
      "area": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
  stroke-width="1.5" stroke="currentColor" class="size-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M3 4.5h18v15H3v-15Z" />
  </svg>`,
      "volume": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
</svg>`,
      "mass": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0 0 12 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 0 1-2.031.352 5.988 5.988 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971Zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0 2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 0 1-2.031.352 5.989 5.989 0 0 1-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971Z" />
</svg>`,
      "time": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
</svg>`,
      "temp": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
  stroke-width="1.5" stroke="currentColor" class="size-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v10.5m0 0a3.75 3.75 0 1 0
   0 7.5 3.75 3.75 0 0 0 0-7.5ZM10.5 3h3v10.5a5.25 5.25 0 1 1-3 0V3Z" />
  </svg>`,
      "energy": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M21 10.5h.375c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125H21M4.5 10.5H18V15H4.5v-4.5ZM3.75 18h15A2.25 2.25 0 0 0 21 15.75v-6a2.25 2.25 0 0 0-2.25-2.25h-15A2.25 2.25 0 0 0 1.5 9.75v6A2.25 2.25 0 0 0 3.75 18Z" />
</svg>`,
      "power": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
</svg>`,
      "data": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
</svg>`,
      "speed": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
</svg>`,
      "angle": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
  stroke-width="1.5" stroke="currentColor" class="size-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M3 20h18M13 4L3 20h10" />
  </svg>`,
      "pressure": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="2 2 20 20"
  stroke-width="1.5" stroke="currentColor" class="size-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0
  5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
  </svg>`,
      "freq": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
  stroke-width="1.5" stroke="currentColor" class="size-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M3 12h4.5l3-9 3 18 3-9H21" />
  </svg>`,
      "force": `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
  stroke-width="1.5" stroke="currentColor" class="size-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25
   0v11.25" />
  </svg>`,
      "current": `<span class="inline-flex items-center justify-center w-4 h-4 font-serif text-base italic">I</span>`,
      "voltage": `<span class="inline-flex items-center justify-center w-4 h-4 font-serif text-base italic">V</span>`,
      "charge": `<span class="inline-flex items-center justify-center w-4 h-4 font-serif text-base italic">Q</span>`,
      "cap": `<span class="inline-flex items-center justify-center w-4 h-4 font-serif text-base italic">C</span>`,
      "res": `<span class="inline-flex items-center justify-center w-4 h-4 text-base">\u03A9</span>`
    };
    function createMoreButton() {
      const button = document.createElement("button");
      button.className = "flex items-center justify-center p-2 m-1 text-xs font-medium capitalize rounded bg-secondary more-button";
      button.dataset.category = "more";
      button.title = showMore ? "Show less categories" : "Show more categories";
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 ${showMore ? "rotate-180" : ""}">
            <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 5.25 7.5 7.5 7.5-7.5m-15 6 7.5 7.5 7.5-7.5"></path>
        </svg>
        `;
      button.innerHTML = svg;
      return button;
    }
    function updateUnits(category) {
      const options = config2.units[category].map((unit) => {
        if (category === "money" && config2.moneyflags[unit]) {
          return `<option value="${config2.moneyflags[unit]} ${unit}">${config2.moneyflags[unit]} ${unit}</option>`;
        }
        return `<option value="${unit}">${unit}</option>`;
      }).join("");
      [unit1, unit2].forEach((select, index) => {
        select.innerHTML = options;
        const defaultUnit = config2.defaultUnits[category][index] || config2.units[category][index];
        if (category === "money" && config2.moneyflags[defaultUnit]) {
          select.value = `${config2.moneyflags[defaultUnit]} ${defaultUnit}`;
        } else {
          select.value = defaultUnit;
        }
      });
    }
    function convert(e) {
      const isInput2Source = e && e.target === input2;
      const sourceInput = isInput2Source ? input2 : input1;
      const sourceUnit = isInput2Source ? unit2 : unit1;
      const targetInput = isInput2Source ? input1 : input2;
      const targetUnit = isInput2Source ? unit1 : unit2;
      const quantity = sourceInput.value;
      const fromUnit = sourceUnit.value.replace(/^[^\s]+\s/, "").trim() || sourceUnit.value;
      const toUnit = targetUnit.value.replace(/^[^\s]+\s/, "").trim() || targetUnit.value;
      if (!quantity.length) {
        return;
      }
      try {
        let result = IC.parser.quickEval(`${quantity} ${fromUnit} to ${toUnit}`);
        if (currentCategory === "money" && !isNaN(result)) {
          const maxDecimals = result < 0.01 ? 6 : 2;
          const formatter = new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: maxDecimals
          });
          result = formatter.format(result);
        }
        targetInput.value = result;
      } catch (error) {
        console.error(`Conversion error: ${error.message}`);
        targetInput.value = "";
      }
    }
    categoryButtons.addEventListener("click", (e) => {
      const button = e.target.closest("button");
      if (!button) return;
      const category = button.dataset.category;
      if (category === "more") {
        showMore = !showMore;
        renderCategories();
      } else if (category) {
        currentCategory = category;
        updateUnits(currentCategory);
        convert();
        renderCategories();
      }
    });
    [input1, input2, unit1, unit2].forEach((el) => el.addEventListener("input", convert));
    swapButton.addEventListener("click", () => {
      [unit1.value, unit2.value] = [unit2.value, unit1.value];
      convert();
    });
    renderCategories();
    updateUnits(currentCategory);
    convert();
  }

  // public/src/main.js
  var import_util = __toESM(require_util());

  // public/ui/theme/theme.js
  var DEFAULT_RESULT_COLOR = "#C95A6A";
  var DEFAULT_COMMENT_COLOR = "hsla(200, 65%, 35%, 0.9)";
  var DEFAULT_ROW_TYPE_COLOR = "#e57373";
  var DEFAULT_CUSTOM_TYPE_COLOR = "#AA4499";
  var baseLightTheme = {
    bg: "#f7f8fa",
    bgAlt: "#eef1f5",
    text: "#444",
    textDim: "#7a7a7a",
    accent: "#3a7ad9",
    accentBright: "#6ea8ef",
    borderColor: "rgba(0, 0, 0, 0.1)",
    calcResultColor: DEFAULT_RESULT_COLOR,
    calcCommentColor: DEFAULT_COMMENT_COLOR,
    calcRowTypeColor: DEFAULT_ROW_TYPE_COLOR,
    calcRowTypeCustomColor: DEFAULT_CUSTOM_TYPE_COLOR
  };
  var baseDarkTheme = {
    bg: "#1e2336",
    bgAlt: "#23283d",
    text: "#b8bfe8",
    textDim: "#6a7193",
    accent: "#c55970",
    accentBright: "#ee6e73",
    borderColor: "rgba(255, 255, 255, 0.1)",
    calcResultColor: "#c55970",
    calcCommentColor: "#7aa2f7",
    calcRowTypeColor: "#ff9e64",
    calcRowTypeCustomColor: "#bb9af7"
  };
  function hexToFilter(hex) {
    if (!hex) return "none";
    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex.split("").map((c) => c + c).join("");
    }
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }
    const hueRotate = Math.round(h * 360);
    const saturate = Math.round(s * 100 * 2);
    const brightness = Math.round((l + 0.5) * 100);
    const invert = l > 0.5 ? Math.round((l - 0.3) * 100) : Math.round(l * 100 + 30);
    const sepia = Math.round(s * 100);
    return `brightness(0) saturate(100%) invert(${invert}%) sepia(${sepia}%) saturate(${saturate}%) hue-rotate(${hueRotate}deg) brightness(${brightness}%) contrast(90%)`;
  }
  function generatePaletteFromTheme(theme) {
    const result = theme.calcResultColor || baseLightTheme.calcResultColor;
    const comment = theme.calcCommentColor || baseLightTheme.calcCommentColor;
    const rowType = theme.calcRowTypeColor || baseLightTheme.calcRowTypeColor;
    const rowTypeCustom = theme.calcRowTypeCustomColor || baseLightTheme.calcRowTypeCustomColor;
    const colors = [result, comment, rowType, rowTypeCustom];
    const names = ["Result", "Comment", "Type", "Custom"];
    const defaultIndices = { result: 0, comment: 1, rowType: 2, rowTypeCustom: 3 };
    return { colors, names, defaultIndices };
  }
  function buildThemeObject(themeId, minimal) {
    const baseTheme = minimal.mode === "dark" ? baseDarkTheme : baseLightTheme;
    const resolved = { ...baseTheme };
    if (minimal.displayName) resolved.displayName = minimal.displayName;
    if (minimal.category) resolved.category = minimal.category;
    if (minimal.description) resolved.description = minimal.description;
    if (minimal.surfaces) {
      if (minimal.surfaces.bg) resolved.bg = minimal.surfaces.bg;
      if (minimal.surfaces.bgAlt) resolved.bgAlt = minimal.surfaces.bgAlt;
      if (minimal.surfaces.text) resolved.text = minimal.surfaces.text;
      if (minimal.surfaces.textDim) resolved.textDim = minimal.surfaces.textDim;
    }
    if (minimal.accents) {
      if (minimal.accents.accent) resolved.accent = minimal.accents.accent;
      if (minimal.accents.accentBright) resolved.accentBright = minimal.accents.accentBright;
    }
    if (!minimal.mainBg) {
      const bg = minimal.surfaces?.bg || resolved.bg;
      const bgAlt = minimal.surfaces?.bgAlt || resolved.bgAlt;
      resolved.mainBg = `color-mix(in srgb, ${bg} 60%, ${bgAlt} 40%)`;
    } else {
      resolved.mainBg = minimal.mainBg;
    }
    resolved.background = minimal.background || minimal.surfaces?.bg || resolved.bg;
    if (minimal.fontFamily) {
      resolved.fontFamily = minimal.fontFamily;
    }
    let palette = null;
    let paletteKey = minimal.paletteKey;
    if (paletteKey && window.OMARCHY_PALETTES?.[paletteKey]) {
      palette = window.OMARCHY_PALETTES[paletteKey];
    } else if (paletteKey && paletteKey.startsWith("auto-")) {
      palette = generatePaletteFromTheme(minimal);
    } else if (minimal.palette) {
      palette = minimal.palette;
    }
    if (palette) {
      resolved.colors = palette.colors;
      resolved.names = palette.names;
      resolved.defaultIndices = palette.defaultIndices;
      const indices = { ...palette.defaultIndices, ...minimal.paletteOverrides || {} };
      resolved.calcResultColor = palette.colors[indices.result];
      resolved.calcCommentColor = palette.colors[indices.comment];
      resolved.calcRowTypeColor = palette.colors[indices.rowType];
      resolved.calcRowTypeCustomColor = palette.colors[indices.rowTypeCustom];
      resolved.calcResultColorIndex = indices.result;
      resolved.calcCommentColorIndex = indices.comment;
      resolved.calcRowTypeColorIndex = indices.rowType;
      resolved.calcRowTypeCustomColorIndex = indices.rowTypeCustom;
    } else if (minimal.calcColors) {
      if (minimal.calcColors.result) resolved.calcResultColor = minimal.calcColors.result;
      if (minimal.calcColors.comment) resolved.calcCommentColor = minimal.calcColors.comment;
      if (minimal.calcColors.rowType) resolved.calcRowTypeColor = minimal.calcColors.rowType;
      if (minimal.calcColors.rowTypeCustom) resolved.calcRowTypeCustomColor = minimal.calcColors.rowTypeCustom;
    }
    if (minimal.overrides) {
      Object.assign(resolved, minimal.overrides);
    }
    if (minimal.radii) {
      resolved.radii = minimal.radii;
    }
    if (minimal.features) {
      resolved.features = minimal.features;
    }
    if (minimal.logoFilter) {
      resolved.logoFilter = minimal.logoFilter;
    } else {
      resolved.logoFilter = hexToFilter(resolved.accent);
    }
    Object.keys(minimal).forEach((key) => {
      if (resolved[key] === void 0) {
        resolved[key] = minimal[key];
      }
    });
    return resolved;
  }
  window.THEME_DEFAULTS = Object.freeze({
    themeId: "theme-nordic",
    bgStyle: "gradient",
    cardStyles: Object.freeze({
      header: "flat",
      sidebar: "flat",
      calc: "elevated"
    }),
    fontStyle: "default",
    customFontName: "",
    sizeStyle: "spacious",
    showBottomNav: true,
    fontLocked: false,
    gradientIntensity: 0.5,
    options: Object.freeze({
      backgroundStyles: ["gradient", "solid", "grid", "waves"],
      cardStyleOptions: ["elevated", "sunken", "flat", "glass"],
      fontStyles: ["default", "clean", "mono", "serif", "hand", "custom"],
      sizeStyles: ["spacious", "compact"],
      bottomNavOptions: ["header", "floating"]
    }),
    getDefaultCardStyle(type) {
      return this.cardStyles[type] || "flat";
    },
    getDefaultPalettePreset(themeId) {
      return themeId?.startsWith("theme-omarchy-") ? "vscode-style" : "default";
    }
  });
  window.ThemeSystem = {
    defaults: window.THEME_DEFAULTS,
    state: {
      themeId: window.THEME_DEFAULTS.themeId,
      bgStyle: window.THEME_DEFAULTS.bgStyle,
      cardStyles: { ...window.THEME_DEFAULTS.cardStyles },
      fontStyle: window.THEME_DEFAULTS.fontStyle,
      customFontName: window.THEME_DEFAULTS.customFontName,
      sizeStyle: window.THEME_DEFAULTS.sizeStyle,
      showBottomNav: window.THEME_DEFAULTS.showBottomNav,
      fontLocked: window.THEME_DEFAULTS.fontLocked,
      palettePresets: {},
      gradientIntensity: null,
      waveStyle: 1,
      gridSize: 50,
      dockStyle: "default",
      dockPosition: "right",
      customColors: null
    },
    init() {
      if (window.EMBED_DEFAULT_MODE) {
        return;
      }
      if (window.EMBED_FALLBACK_TO_WHITE) {
        const urlTheme = this.getUrlThemeParam();
        if (!urlTheme) {
          return;
        }
      }
      if (window.IGNORE_LOCAL_THEME) {
        this.state.themeId = "theme-daylight";
        this.state.bgStyle = "solid";
      } else {
        const savedState = this.loadState();
        if (savedState) {
          this.state = { ...this.state, ...savedState };
          if (!this.state.gridSize || this.state.gridSize < 20) {
            this.state.gridSize = 50;
          }
        } else {
          const oldThemeId = localStorage.getItem("aceternity-theme");
          if (oldThemeId) {
            this.state.themeId = oldThemeId;
            this.saveState();
          }
        }
      }
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const themeParam = urlParams.get("theme");
        if (themeParam && themeParam.includes("|")) {
          const parsed = this.parseFromUrl(themeParam);
          if (parsed) {
            Object.assign(this.state, parsed);
            if (parsed.palette && parsed.themeId) {
              this.state.palettePresets[parsed.themeId] = parsed.palette;
            }
          }
        } else if (themeParam) {
          const resolvedTheme = this.resolveThemeId(themeParam);
          if (resolvedTheme) {
            this.state.themeId = resolvedTheme;
          }
        }
        if (!themeParam?.includes("bg=")) {
          const themeConfig = window.THEME_CONFIG_V2?.[this.state.themeId];
          if (themeConfig?.defaultBgStyle) {
            this.state.bgStyle = themeConfig.defaultBgStyle;
          }
        }
      } catch (e) {
        console.warn("[theme.js] Error parsing URL theme parameters:", e);
      }
      this.applyAll();
    },
    getUrlThemeParam() {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const themeParam = urlParams.get("theme");
        if (!themeParam) return null;
        const prefixes = ["", "theme-", "theme-omarchy-"];
        for (const prefix of prefixes) {
          const candidate = themeParam.startsWith("theme-") ? themeParam : `${prefix}${themeParam}`;
          if (window.THEME_CONFIG_V2?.[candidate]) {
            return candidate;
          }
        }
        console.warn(`[ThemeSystem] Unknown theme from URL: ${themeParam}`);
        return null;
      } catch (e) {
        console.warn("[ThemeSystem] Error parsing URL theme param:", e);
        return null;
      }
    },
    loadState() {
      try {
        if (window.SettingsManager) {
          const theme = window.SettingsManager.get("theme");
          return theme || null;
        }
        const json = localStorage.getItem("instacalc_theme_settings");
        return json ? JSON.parse(json) : null;
      } catch (e) {
        console.warn("[ThemeSystem] Failed to load state:", e);
        return null;
      }
    },
    saveState() {
      try {
        if (window.SettingsManager) {
          const stateProps = [
            "themeId",
            "bgStyle",
            "cardStyles",
            "fontStyle",
            "customFontName",
            "sizeStyle",
            "showBottomNav",
            "fontLocked",
            "palettePresets",
            "gradientIntensity",
            "waveStyle",
            "gridSize",
            "dockStyle",
            "dockPosition",
            "customColors"
          ];
          stateProps.forEach((prop) => {
            if (this.state[prop] !== void 0) {
              window.SettingsManager.set(`theme.${prop}`, this.state[prop]);
            }
          });
        } else {
          localStorage.setItem("instacalc_theme_settings", JSON.stringify(this.state));
          localStorage.setItem("theme_v2", this.state.themeId);
        }
      } catch (e) {
        console.warn("[ThemeSystem] Failed to save state:", e);
      }
    },
    applyAll() {
      this.applyTheme(this.state.themeId);
      this.applyBackgroundStyle(this.state.bgStyle);
      this.applyCardStyles(this.state.cardStyles);
      this.applyFontStyle(this.state.fontStyle);
      this.applySizeStyle(this.state.sizeStyle);
      this.applyBottomNavVisibility(this.state.showBottomNav);
      if (this.state.gridSize) {
        document.documentElement.style.setProperty("--grid-size", `${this.state.gridSize}px`);
      }
      this.applyDockStyle(this.state.dockStyle);
      this.applyDockPosition(this.state.dockPosition);
      const preset = this.state.palettePresets[this.state.themeId];
      if (preset) {
        this.applyPalettePreset(preset);
      }
    },
    setTheme(themeId) {
      this.state.themeId = themeId;
      this.applyTheme(themeId);
      if (!this.state.fontLocked) {
        this.state.fontStyle = "default";
        this.applyFontStyle("default");
      }
      const themeConfig = window.THEME_CONFIG_V2?.[themeId];
      const newBgStyle = themeConfig?.defaultBgStyle || window.THEME_DEFAULTS.bgStyle;
      this.state.bgStyle = newBgStyle;
      this.applyBackgroundStyle(newBgStyle);
      const defaultCalcStyle = themeConfig?.overrides?.defaultCalcStyle || "elevated";
      this.state.cardStyles.calc = defaultCalcStyle;
      this.applyCardStyles(this.state.cardStyles);
      this.saveState();
      this.notifyChange();
    },
    applyTheme(themeId) {
      window.applyTheme_v2(themeId);
      const root = document.documentElement;
      root.style.setProperty("--status-public-fg", "var(--accent)");
      root.style.setProperty("--status-public-bg", "var(--accent)");
      root.style.setProperty("--status-private-fg", "var(--text-dim)");
      root.style.setProperty("--status-private-bg", "var(--text-dim)");
    },
    setBackgroundStyle(style) {
      this.state.bgStyle = style;
      this.applyBackgroundStyle(style);
      this.saveState();
      this.notifyChange();
    },
    applyBackgroundStyle(style) {
      const root = document.documentElement;
      root.setAttribute("data-bg-style", style || "gradient");
      if (style === "waves" && window.bgPlay) {
        this.applyWaveStyle(this.state.waveStyle);
      } else if (style === "grid" && window.bgPlay) {
        window.bgPlay.grid(this.state.gridSize || 50);
      } else if (window.bgPlay && style !== "waves" && style !== "grid") {
        window.bgPlay.reset();
      }
    },
    setWaveStyle(index) {
      this.state.waveStyle = index;
      if (this.state.bgStyle === "waves") {
        this.applyWaveStyle(index);
      }
      this.saveState();
      this.notifyChange();
    },
    applyWaveStyle(index) {
      if (!window.bgPlay) return;
      window.bgPlay.wave(index);
    },
    getWaveStyle() {
      return this.state.waveStyle ?? 2;
    },
    setCardStyle(type, style) {
      this.state.cardStyles[type] = style;
      this.applyCardStyles(this.state.cardStyles);
      this.saveState();
      this.notifyChange();
    },
    applyCardStyles(styles) {
      const root = document.documentElement;
      root.setAttribute("data-sidebar-style", styles.sidebar || "flat");
      root.setAttribute("data-calc-style", styles.calc || "elevated");
      root.setAttribute("data-header-style", styles.header || "flat");
    },
    setFontStyle(style) {
      this.state.fontStyle = style;
      this.applyFontStyle(style);
      this.saveState();
      this.notifyChange();
    },
    setCustomFont(fontName, fontFamily) {
      this.state.fontStyle = "custom";
      this.state.customFontName = fontName;
      const fullFontFamily = fontFamily || `'${fontName}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
      const root = document.documentElement;
      root.style.setProperty("--custom-font-family", fullFontFamily);
      root.style.setProperty("--theme-font-family", fullFontFamily);
      document.querySelectorAll("calc, [data-calc-wrapper]").forEach((el) => {
        el.setAttribute("data-font-style", "custom");
        el.style.setProperty("--calc-font-family", fullFontFamily);
      });
      this.saveState();
      this.notifyChange();
      window.dispatchEvent(new CustomEvent("custom-font-applied", {
        detail: { fontName }
      }));
    },
    applyFontStyle(fontId) {
      const body = document.body;
      body.classList.remove("font-hand");
      if (fontId === "hand") {
        body.classList.add("font-hand");
      }
      if (fontId === "custom" && this.state.customFontName) {
        const fontName = this.state.customFontName;
        const fullFontFamily = `'${fontName}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
        const root2 = document.documentElement;
        root2.style.setProperty("--custom-font-family", fullFontFamily);
        root2.style.setProperty("--theme-font-family", fullFontFamily);
        document.querySelectorAll("calc, [data-calc-wrapper]").forEach((el) => {
          el.setAttribute("data-font-style", "custom");
          el.style.setProperty("--calc-font-family", fullFontFamily);
        });
        return;
      }
      const root = document.documentElement;
      root.style.removeProperty("--custom-font-family");
      root.style.removeProperty("--theme-font-family");
      document.querySelectorAll("calc, [data-calc-wrapper]").forEach((el) => {
        el.style.removeProperty("--calc-font-family");
        if (fontId && fontId !== "default") {
          el.setAttribute("data-font-style", fontId);
        } else {
          el.removeAttribute("data-font-style");
        }
      });
    },
    setSizeStyle(size) {
      this.state.sizeStyle = size;
      this.applySizeStyle(size);
      this.saveState();
      this.notifyChange();
    },
    applySizeStyle(size) {
      document.querySelectorAll("calc, .icreact, CalcHolder").forEach((el) => {
        if (size === "compact") el.classList.add("size-compact");
        else el.classList.remove("size-compact");
      });
    },
    setBottomNav(show) {
      this.state.showBottomNav = show;
      this.applyBottomNavVisibility(show);
      this.saveState();
      this.notifyChange();
    },
    applyBottomNavVisibility(show) {
      if (show) document.body.classList.add("bottom-nav-enabled");
      else document.body.classList.remove("bottom-nav-enabled");
      window.dispatchEvent(new CustomEvent("bottom-nav-changed", { detail: { showBottomNav: show } }));
    },
    notifyChange() {
      window.dispatchEvent(new CustomEvent("theme-state-changed", { detail: this.state }));
    },
    setVars(vars) {
      const root = document.documentElement;
      Object.entries(vars).forEach(([key, val]) => root.style.setProperty(key, val));
    },
    setGradientIntensity(value) {
      this.state.gradientIntensity = value;
      this.applyGradientIntensity(value);
      this.saveState();
      this.notifyChange();
      window.dispatchEvent(new CustomEvent("gradient-intensity-changed", { detail: { intensity: value } }));
    },
    applyGradientIntensity(value) {
      const themeConfig = window.THEME_CONFIG_V2?.[this.state.themeId] || {};
      const defaultIntensity = this.defaults.gradientIntensity;
      const intensity = value ?? themeConfig.gradientIntensity ?? defaultIntensity;
      const root = document.documentElement;
      root.style.setProperty("--gradient-intensity", intensity);
      root.style.setProperty("--orb-opacity", intensity);
      const dockMixPercent = intensity > 0 ? Math.round(intensity / defaultIntensity * 15) : 0;
      root.style.setProperty("--dock-gradient-mix", `${dockMixPercent}%`);
    },
    resetGradientIntensity() {
      this.state.gradientIntensity = null;
      this.applyGradientIntensity(null);
      this.saveState();
      this.notifyChange();
      window.dispatchEvent(new CustomEvent("gradient-intensity-changed", { detail: { intensity: null } }));
    },
    getGradientIntensity() {
      const themeConfig = window.THEME_CONFIG_V2?.[this.state.themeId] || {};
      return this.state.gradientIntensity ?? themeConfig.gradientIntensity ?? this.defaults.gradientIntensity;
    },
    setGridSize(size) {
      this.state.gridSize = size;
      document.documentElement.style.setProperty("--grid-size", `${size}px`);
      if (this.state.bgStyle === "grid" && window.bgPlay) {
        window.bgPlay.grid(size);
      }
      this.saveState();
      this.notifyChange();
    },
    getGridSize() {
      return this.state.gridSize || 50;
    },
    setDockStyle(style) {
      this.state.dockStyle = style;
      this.applyDockStyle(style);
      this.saveState();
      this.notifyChange();
    },
    getDockStyle() {
      return this.state.dockStyle || "default";
    },
    applyDockStyle(style) {
      const dock = document.querySelector("[data-panel-icons-orientation]");
      if (dock) {
        if (style === "minimal") {
          dock.setAttribute("data-dock-style", "minimal");
        } else {
          dock.removeAttribute("data-dock-style");
        }
      }
    },
    setDockPosition(position) {
      this.state.dockPosition = position;
      this.applyDockPosition(position);
      this.saveState();
      this.notifyChange();
    },
    getDockPosition() {
      return this.state.dockPosition || "right";
    },
    applyDockPosition(position) {
      const positionMap = {
        "left": "bottom-left",
        "center": "bottom-center",
        "right": "bottom-right"
      };
      const fullPosition = positionMap[position] || "bottom-center";
      if (window.Alpine && window.Alpine.store("ui")) {
        const uiStore = window.Alpine.store("ui");
        if (uiStore.panelPosition !== void 0) {
          uiStore.panelPosition = fullPosition;
        }
      }
      window.dispatchEvent(new CustomEvent("dock-position-changed", {
        detail: { position, fullPosition }
      }));
    },
    applyPalettePreset(presetKey) {
      const themeId = this.state.themeId;
      if (!themeId || !window.DesignLabPalette) return;
      let indices = window.DesignLabPalette.getPresetIndices(themeId, presetKey);
      if (indices) {
        this.state.palettePresets[themeId] = presetKey;
        window.DesignLabPalette.applyIndices(themeId, indices);
        this.applyTheme(themeId);
        this.saveState();
        this.notifyChange();
      } else {
        console.warn(`[ThemeSystem] Preset '${presetKey}' not compatible with theme '${themeId}'`);
      }
    },
    applyCustomColors(colors) {
      if (!colors) return;
      const root = document.documentElement;
      const cssVarMap = {
        bg: "--bg",
        bgAlt: "--bg-alt",
        text: "--text",
        textDim: "--text-dim",
        accent: "--accent",
        border: "--border-color",
        result: "--calc-result-color",
        comment: "--calc-comment-color",
        rowType: "--calc-row-type-color",
        rowTypeCustom: "--calc-row-type-custom-color"
      };
      Object.entries(colors).forEach(([key, value]) => {
        const cssVar = cssVarMap[key];
        if (cssVar && value) {
          root.style.setProperty(cssVar, value);
        }
      });
      if (colors.bg) {
        root.style.setProperty("--main-bg", colors.bg);
      }
    },
    serializeToUrl(includeDefaults = false) {
      const parts = [];
      const defaults = window.THEME_DEFAULTS;
      const shortId = this.state.themeId.replace(/^theme-omarchy-/, "").replace(/^theme-/, "");
      parts.push(shortId);
      if (includeDefaults || this.state.bgStyle !== defaults.bgStyle) {
        parts.push(`bg=${this.state.bgStyle}`);
      }
      if (includeDefaults || this.state.fontStyle !== defaults.fontStyle) {
        parts.push(`font=${this.state.fontStyle}`);
      }
      if (includeDefaults || this.state.sizeStyle !== defaults.sizeStyle) {
        parts.push(`size=${this.state.sizeStyle}`);
      }
      const cardsDiffer = Object.entries(this.state.cardStyles).some(
        ([type, style]) => style !== defaults.cardStyles[type]
      );
      if (includeDefaults || cardsDiffer) {
        const { sidebar, calc, header } = this.state.cardStyles;
        parts.push(`cards=sidebar:${sidebar},calc:${calc},header:${header}`);
      }
      if (this.state.gradientIntensity !== null) {
        parts.push(`gradient=${this.state.gradientIntensity}`);
      }
      if (this.state.gridSize !== 50) {
        parts.push(`grid=${this.state.gridSize}`);
      }
      if (this.state.waveStyle !== 1) {
        parts.push(`wave=${this.state.waveStyle}`);
      }
      const palette = this.state.palettePresets[this.state.themeId];
      if (palette && palette !== "default") {
        parts.push(`palette=${palette}`);
      }
      if (this.state.themeId === "theme-custom" && this.state.customColors) {
        const c = this.state.customColors;
        const strip = (hex) => (hex || "").replace("#", "");
        const colorPairs = [];
        if (c.bg) colorPairs.push(`bg:${strip(c.bg)}`);
        if (c.bgAlt) colorPairs.push(`bgalt:${strip(c.bgAlt)}`);
        if (c.text) colorPairs.push(`text:${strip(c.text)}`);
        if (c.textDim) colorPairs.push(`dim:${strip(c.textDim)}`);
        if (c.accent) colorPairs.push(`accent:${strip(c.accent)}`);
        if (c.border) colorPairs.push(`border:${strip(c.border)}`);
        if (c.result) colorPairs.push(`result:${strip(c.result)}`);
        if (c.comment) colorPairs.push(`comment:${strip(c.comment)}`);
        if (c.rowType) colorPairs.push(`type:${strip(c.rowType)}`);
        if (c.rowTypeCustom) colorPairs.push(`typecustom:${strip(c.rowTypeCustom)}`);
        if (colorPairs.length > 0) {
          parts.push(`colors=${colorPairs.join(",")}`);
        }
      }
      return parts.join("|");
    },
    parseFromUrl(str) {
      if (!str) return null;
      const parts = str.split("|");
      if (parts.length === 0) return null;
      const config2 = {};
      const rawTheme = parts[0];
      config2.themeId = this.resolveThemeId(rawTheme);
      for (let i = 1; i < parts.length; i++) {
        const [key, value] = parts[i].split("=");
        if (!key || value === void 0) continue;
        switch (key) {
          case "bg":
            if (["gradient", "solid", "grid", "waves"].includes(value)) {
              config2.bgStyle = value;
            }
            break;
          case "font":
            if (["default", "clean", "mono", "serif", "hand"].includes(value)) {
              config2.fontStyle = value;
            }
            break;
          case "size":
            if (["spacious", "compact"].includes(value)) {
              config2.sizeStyle = value;
            }
            break;
          case "cards": {
            const validStyles = ["elevated", "sunken", "flat", "glass"];
            const cardStyles = {};
            const pairs = value.split(",");
            for (const pair of pairs) {
              const [cardType, style] = pair.split(":");
              if (cardType && style && validStyles.includes(style)) {
                if (["sidebar", "calc", "header"].includes(cardType)) {
                  cardStyles[cardType] = style;
                }
              }
            }
            if (Object.keys(cardStyles).length > 0) {
              config2.cardStyles = {
                sidebar: cardStyles.sidebar || "flat",
                calc: cardStyles.calc || "elevated",
                header: cardStyles.header || "flat",
                ...cardStyles
              };
            }
            break;
          }
          case "gradient": {
            const g = parseFloat(value);
            if (!isNaN(g) && g >= 0 && g <= 1) {
              config2.gradientIntensity = g;
            }
            break;
          }
          case "grid": {
            const gridVal = parseInt(value, 10);
            if (!isNaN(gridVal) && gridVal >= 20 && gridVal <= 100) {
              config2.gridSize = gridVal;
            }
            break;
          }
          case "wave": {
            const w = parseInt(value, 10);
            if (!isNaN(w) && w >= 0 && w <= 4) {
              config2.waveStyle = w;
            }
            break;
          }
          case "palette":
            config2.palette = value;
            break;
          case "colors": {
            const colorMap = {
              "bg": "bg",
              "bgalt": "bgAlt",
              "text": "text",
              "dim": "textDim",
              "accent": "accent",
              "border": "border",
              "result": "result",
              "comment": "comment",
              "type": "rowType",
              "typecustom": "rowTypeCustom"
            };
            const pairs = value.split(",");
            for (const pair of pairs) {
              const [colorKey, colorVal] = pair.split(":");
              if (colorKey && colorVal && /^[0-9a-fA-F]{6}$/.test(colorVal)) {
                if (!config2.customColors) config2.customColors = {};
                const mappedKey = colorMap[colorKey];
                if (mappedKey) {
                  config2.customColors[mappedKey] = `#${colorVal}`;
                }
              }
            }
            break;
          }
        }
      }
      return config2;
    },
    resolveThemeId(input) {
      if (!input) return window.THEME_DEFAULTS.themeId;
      if (window.THEME_CONFIG_V2?.[input]) return input;
      const withTheme = `theme-${input}`;
      if (window.THEME_CONFIG_V2?.[withTheme]) return withTheme;
      const withOmarchy = `theme-omarchy-${input}`;
      if (window.THEME_CONFIG_V2?.[withOmarchy]) return withOmarchy;
      return window.THEME_DEFAULTS.themeId;
    },
    exportConfig() {
      return this.serializeToUrl(true);
    },
    importConfig(str) {
      const parsed = this.parseFromUrl(str);
      if (!parsed || !parsed.themeId) return false;
      this.state.themeId = parsed.themeId;
      if (parsed.bgStyle) this.state.bgStyle = parsed.bgStyle;
      if (parsed.fontStyle) this.state.fontStyle = parsed.fontStyle;
      if (parsed.sizeStyle) this.state.sizeStyle = parsed.sizeStyle;
      if (parsed.cardStyles) this.state.cardStyles = { ...parsed.cardStyles };
      if (parsed.gradientIntensity !== void 0) this.state.gradientIntensity = parsed.gradientIntensity;
      if (parsed.gridSize) this.state.gridSize = parsed.gridSize;
      if (parsed.waveStyle !== void 0) this.state.waveStyle = parsed.waveStyle;
      if (parsed.palette) {
        this.state.palettePresets[parsed.themeId] = parsed.palette;
      }
      if (parsed.customColors) {
        this.state.customColors = parsed.customColors;
        this.state.themeId = "theme-custom";
      }
      this.applyAll();
      if (parsed.customColors) {
        this.applyCustomColors(parsed.customColors);
      }
      this.saveState();
      this.notifyChange();
      return true;
    },
    PRESETS_STORAGE_KEY: "instacalc_theme_presets",
    loadPresets() {
      try {
        const json = localStorage.getItem(this.PRESETS_STORAGE_KEY);
        if (!json) return [];
        const data = JSON.parse(json);
        return Array.isArray(data.presets) ? data.presets : [];
      } catch (e) {
        console.warn("[ThemeSystem] Failed to load presets:", e);
        return [];
      }
    },
    savePresets(presets) {
      try {
        localStorage.setItem(this.PRESETS_STORAGE_KEY, JSON.stringify({ presets }));
      } catch (e) {
        console.warn("[ThemeSystem] Failed to save presets:", e);
      }
    },
    createPreset(name) {
      const presets = this.loadPresets();
      const newPreset = {
        id: crypto.randomUUID ? crypto.randomUUID() : `preset-${Date.now()}`,
        name: name.trim() || `Preset ${presets.length + 1}`,
        createdAt: Date.now(),
        config: {
          themeId: this.state.themeId,
          bgStyle: this.state.bgStyle,
          cardStyles: { ...this.state.cardStyles },
          fontStyle: this.state.fontStyle,
          sizeStyle: this.state.sizeStyle,
          gradientIntensity: this.state.gradientIntensity,
          waveStyle: this.state.waveStyle,
          gridSize: this.state.gridSize,
          palette: this.state.palettePresets[this.state.themeId] || null
        }
      };
      presets.push(newPreset);
      this.savePresets(presets);
      return newPreset;
    },
    applyPreset(presetId) {
      const presets = this.loadPresets();
      const preset = presets.find((p) => p.id === presetId);
      if (!preset) return false;
      const config2 = preset.config;
      this.state.themeId = config2.themeId;
      this.state.bgStyle = config2.bgStyle;
      this.state.cardStyles = { ...config2.cardStyles };
      this.state.fontStyle = config2.fontStyle;
      this.state.sizeStyle = config2.sizeStyle;
      this.state.gradientIntensity = config2.gradientIntensity;
      this.state.waveStyle = config2.waveStyle;
      this.state.gridSize = config2.gridSize;
      if (config2.palette) {
        this.state.palettePresets[config2.themeId] = config2.palette;
      }
      this.applyAll();
      this.saveState();
      this.notifyChange();
      return true;
    },
    deletePreset(presetId) {
      const presets = this.loadPresets();
      const filtered = presets.filter((p) => p.id !== presetId);
      this.savePresets(filtered);
      return true;
    },
    renamePreset(presetId, newName) {
      const presets = this.loadPresets();
      const preset = presets.find((p) => p.id === presetId);
      if (!preset) return false;
      preset.name = newName.trim();
      this.savePresets(presets);
      return true;
    },
    gradientLooksSolid() {
      const intensity = this.getGradientIntensity();
      return intensity === 0;
    }
  };
  var BASE_THEMES = {
    "theme-daylight": {
      displayName: "Daylight",
      category: "defaults",
      description: "Classic Instacalc light theme with softer colors",
      mode: "light",
      surfaces: {
        bg: "#fff",
        bgAlt: "#eef1f5",
        text: "#444",
        textDim: "#7a7a7a"
      },
      accents: {
        accent: "#3a7ad9",
        accentBright: "#6ea8ef"
      },
      mainBg: "color-mix(in srgb, #fff 45%, #eef1f5 55%)",
      fontFamily: "'Fira Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      logoFilter: "brightness(0) saturate(100%) invert(45%) sepia(22%) saturate(1046%) hue-rotate(163deg) brightness(91%) contrast(88%)",
      paletteKey: "theme-daylight",
      overrides: {
        calcRowNumber: "#555",
        calcRowSelectedInputBgColor: "rgba(255, 249, 190, 0.45)",
        calcCommentHoverBgColor: "#eaecef",
        calcRowLogColor: "#555",
        calcRowLogBgColor: "#f1f3f6",
        calcRowLogBorderColor: "#dcdcdc",
        linkHoverColor: "#d64265",
        tabActiveColor: "#4f82d4",
        navLinkHoverColor: "#444",
        navLinkBgColor: "#dfe5f2",
        navLinkActiveColor: "#3a7ad9",
        navLinkActiveBgColor: "#ffffffcc",
        secondaryBgColor: "#eef1f5",
        highlightBgColor: "#4cae50",
        highlightTextColor: "#ffffff",
        buttonBgColorHover: "#f3f4f6",
        buttonBgColorCreate: "#e29c6c"
      }
    },
    "theme-warm": {
      displayName: "Warm",
      category: "defaults",
      description: "Cozy warm taupe with terracotta accents.",
      mode: "light",
      gradientIntensity: 0,
      defaultBgStyle: "solid",
      surfaces: {
        bg: "#e5ddd5",
        bgAlt: "#f0ebe5",
        text: "#3d3027",
        textDim: "#8a7d72"
      },
      accents: {
        accent: "#c96a4a",
        accentBright: "#d4806a"
      },
      mainBg: "#e5ddd5",
      background: "#e5ddd5",
      fontFamily: "'Fira Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      logoFilter: "brightness(0) saturate(100%) invert(20%) sepia(10%) saturate(500%) hue-rotate(350deg)",
      paletteKey: "auto-warm",
      calcColors: {
        result: "#c96a4a",
        comment: "#8a7d72",
        rowType: "#b85c3c",
        rowTypeCustom: "#9a6b4a"
      },
      overrides: {
        calcRowNumber: "#a09080",
        calcRowSelectedInputBgColor: "#f8f3ed",
        calcCommentHoverBgColor: "#f5ebe3",
        calcRowLogColor: "#3d3027",
        calcRowLogBgColor: "#f5ebe3",
        calcRowLogBorderColor: "rgba(160, 144, 128, 0.3)",
        linkHoverColor: "#c96a4a",
        tabActiveColor: "#c96a4a",
        navLinkHoverColor: "#3d3027",
        navLinkBgColor: "#f5ebe3",
        navLinkActiveColor: "#c96a4a",
        navLinkActiveBgColor: "#fffaf5",
        borderColor: "rgba(160, 144, 128, 0.25)",
        secondaryBgColor: "#fffaf5",
        highlightBgColor: "#c96a4a",
        highlightTextColor: "#ffffff",
        buttonBgColorHover: "#f5ebe3",
        buttonBgColorCreate: "#c96a4a"
      }
    },
    "theme-night": {
      displayName: "Night",
      category: "defaults",
      description: "Dark slate canvas with rose accents and clarity.",
      mode: "dark",
      surfaces: {
        bg: "#1e2336",
        bgAlt: "#23283d",
        text: "#b8bfe8",
        textDim: "#6a7193"
      },
      accents: {
        accent: "#c55970",
        accentBright: "#ee6e73"
      },
      fontFamily: "'Fira Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      background: "linear-gradient(135deg, #1e2336 0%, #23283d 100%)",
      logoFilter: "brightness(0) saturate(100%) invert(67%) sepia(27%) saturate(1200%) hue-rotate(196deg) brightness(98%) contrast(93%)",
      paletteKey: "theme-night"
    },
    "theme-morning": {
      displayName: "Morning",
      category: "defaults",
      description: "Soft sunrise glow with warm pink and peach tones.",
      mode: "light",
      gradientIntensity: 0,
      defaultBgStyle: "solid",
      surfaces: {
        bg: "#f5e6e8",
        bgAlt: "#faf2f3",
        text: "#4a3540",
        textDim: "#9a7d87"
      },
      accents: {
        accent: "#d4707a",
        accentBright: "#e8949b"
      },
      mainBg: "#f5e6e8",
      background: "#f5e6e8",
      calcBgColor: "#faf2f3",
      fontFamily: "'Fira Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      logoFilter: "brightness(0) saturate(100%) invert(50%) sepia(20%) saturate(800%) hue-rotate(310deg) brightness(95%) contrast(90%)",
      paletteKey: "auto-morning",
      calcColors: {
        result: "#d4707a",
        comment: "#9a7d87",
        rowType: "#c45a65",
        rowTypeCustom: "#b07080"
      },
      overrides: {
        calcRowNumber: "#b09098",
        calcRowSelectedInputBgColor: "#fff5f6",
        calcCommentHoverBgColor: "#fcedef",
        calcRowLogColor: "#4a3540",
        calcRowLogBgColor: "#fcedef",
        calcRowLogBorderColor: "rgba(180, 144, 152, 0.3)",
        linkHoverColor: "#d4707a",
        tabActiveColor: "#d4707a",
        navLinkHoverColor: "#4a3540",
        navLinkBgColor: "#fcedef",
        navLinkActiveColor: "#d4707a",
        navLinkActiveBgColor: "#fff8f8",
        borderColor: "rgba(180, 144, 152, 0.25)",
        secondaryBgColor: "#fff8f8",
        highlightBgColor: "#d4707a",
        highlightTextColor: "#ffffff",
        buttonBgColorHover: "#fcedef",
        buttonBgColorCreate: "#d4707a"
      }
    },
    "theme-1984": {
      displayName: "1984",
      category: "defaults",
      description: "Retro terminal noir with amber headline strokes.",
      mode: "dark",
      gradientIntensity: 0,
      defaultBgStyle: "solid",
      surfaces: {
        bg: "#000000",
        bgAlt: "#1e1e1e",
        text: "#72f3bb",
        textDim: "#666666"
      },
      accents: {
        accent: "#e9b97b",
        accentBright: "#e97bb2"
      },
      fontFamily: "'JetBrains Mono', 'Menlo', 'Consolas', monospace",
      background: "#000000",
      logoFilter: "brightness(0) saturate(100%) invert(85%) sepia(37%) saturate(483%) hue-rotate(100deg) brightness(113%) contrast(105%)",
      paletteKey: "theme-1984",
      radii: {
        control: "0px",
        base: "0px",
        card: "0px",
        surface: "0px",
        sidebar: "0px"
      },
      features: {
        roundedControls: true,
        roundedUtilities: true
      },
      overrides: {
        calcBgColor: "#111010",
        secondaryBgColor: "#000000",
        calcRowNumber: "#666666"
      }
    },
    "theme-nordic": {
      displayName: "Nordic",
      category: "defaults",
      description: "Clean Nordic design with frost-inspired colors",
      mode: "light",
      surfaces: {
        bg: "#eceff4",
        bgAlt: "#e5e9f0",
        text: "#2e3440",
        textDim: "#4c566a"
      },
      accents: {
        accent: "#5e81ac",
        accentBright: "#81a1c1"
      },
      fontFamily: "'Fira Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      gradientIntensity: 0.5,
      background: "linear-gradient(180deg, #eceff4 0%, #e5e9f0 100%)",
      logoFilter: "brightness(0) saturate(100%) invert(34%) sepia(13%) saturate(1094%) hue-rotate(174deg) brightness(97%) contrast(85%)",
      paletteKey: "theme-nordic"
    },
    "theme-retro": {
      displayName: "Retro",
      category: "defaults",
      description: "Vintage vibe with warm pink tones and serif fonts",
      mode: "dark",
      surfaces: {
        bg: "#2d1b3d",
        bgAlt: "#3d2b4d",
        text: "#ffc0cb",
        textDim: "#dda0bb"
      },
      accents: {
        accent: "#ff6b9d",
        accentBright: "#ffb6c1"
      },
      fontFamily: "'Crimson Pro', Georgia, 'Times New Roman', serif",
      background: "linear-gradient(135deg, #2d1b3d 0%, #3d2b4d 50%, #4d3b5d 100%)",
      logoFilter: "brightness(0) saturate(100%) invert(72%) sepia(40%) saturate(545%) hue-rotate(296deg) brightness(102%) contrast(103%)",
      paletteKey: "theme-retro"
    },
    "theme-grunge": {
      displayName: "Grunge",
      category: "defaults",
      description: "Dark and gritty with muted tones and a rugged feel",
      mode: "dark",
      surfaces: {
        bg: "#2b2b2b",
        bgAlt: "#3a3a3a",
        text: "#e0e0e0",
        textDim: "#999999"
      },
      accents: {
        accent: "#d4a259",
        accentBright: "#e5b673"
      },
      fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      background: "#2b2b2b",
      logoFilter: "brightness(0) saturate(100%) invert(74%) sepia(29%) saturate(452%) hue-rotate(359deg) brightness(89%) contrast(84%)",
      paletteKey: "theme-grunge"
    },
    "theme-brutalist": {
      displayName: "Brutalist",
      category: "defaults",
      description: "Raw, high-contrast, hard-edged aesthetic.",
      mode: "light",
      surfaces: {
        bg: "#f3f4f6",
        bgAlt: "#ececec",
        text: "#000000",
        textDim: "#555555"
      },
      accents: {
        accent: "#000000",
        accentBright: "#ffee00"
      },
      fontFamily: "'JetBrains Mono', 'Menlo', monospace",
      background: "#f3f4f6",
      logoFilter: "grayscale(100%) contrast(150%)",
      paletteKey: "theme-brutalist",
      radii: {
        control: "0px",
        base: "0px",
        card: "0px",
        surface: "0px",
        sidebar: "0px"
      },
      overrides: {
        borderColor: "#000000",
        secondaryBgColor: "#ffffff",
        buttonBgColorCreate: "#fde047",
        buttonTextColorCreate: "#000000",
        defaultCalcStyle: "flat"
      }
    },
    "theme-cute": {
      displayName: "Cute",
      category: "defaults",
      description: "Soft and pastel with a playful and friendly vibe",
      mode: "light",
      surfaces: {
        bg: "#f5f7fa",
        bgAlt: "#ffffff",
        text: "#2d3748",
        textDim: "#718096"
      },
      accents: {
        accent: "#7ab2dd",
        accentBright: "#90c8ef"
      },
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
      logoFilter: "brightness(0) saturate(100%) invert(60%) sepia(20%) saturate(1094%) hue-rotate(174deg) brightness(97%) contrast(85%)",
      paletteKey: "theme-cute",
      radii: {
        control: "12px",
        base: "16px",
        card: "20px",
        surface: "24px",
        sidebar: "20px"
      }
    },
    "theme-omarchy-tokyo": {
      displayName: "Tokyo Night",
      category: "omarchy",
      description: "Deep blue night with vibrant syntax highlighting",
      mode: "dark",
      surfaces: {
        bg: "#1a1b26",
        bgAlt: "#24283b",
        text: "#c0caf5",
        textDim: "#565f89"
      },
      accents: {
        accent: "#7aa2f7",
        accentBright: "#7dcfff"
      },
      paletteKey: "theme-omarchy-tokyo",
      logoFilter: "brightness(0) saturate(100%) invert(67%) sepia(27%) saturate(1200%) hue-rotate(196deg) brightness(98%) contrast(93%)"
    },
    "theme-omarchy-catppuccin": {
      displayName: "Catppuccin Mocha",
      category: "omarchy",
      description: "Soothing pastel theme for the high-spirited",
      mode: "dark",
      surfaces: {
        bg: "#1e1e2e",
        bgAlt: "#313244",
        text: "#cdd6f4",
        textDim: "#7f849c"
      },
      accents: {
        accent: "#89b4fa",
        accentBright: "#94e2d5"
      },
      paletteKey: "theme-omarchy-catppuccin",
      logoFilter: "brightness(0) saturate(100%) invert(75%) sepia(25%) saturate(1500%) hue-rotate(190deg) brightness(100%) contrast(95%)"
    },
    "theme-omarchy-gruvbox": {
      displayName: "Gruvbox",
      category: "omarchy",
      description: "Retro groove with warm, earthy tones",
      mode: "dark",
      surfaces: {
        bg: "#282828",
        bgAlt: "#3c3836",
        text: "#ebdbb2",
        textDim: "#928374"
      },
      accents: {
        accent: "#d8a657",
        accentBright: "#a9b665"
      },
      paletteKey: "theme-omarchy-gruvbox",
      logoFilter: "brightness(0) saturate(100%) invert(85%) sepia(20%) saturate(800%) hue-rotate(20deg) brightness(105%) contrast(90%)"
    },
    "theme-omarchy-rose-pine": {
      displayName: "Ros\xE9 Pine",
      category: "omarchy",
      description: "All natural pine, faux fur and a bit of soho vibes",
      mode: "dark",
      surfaces: {
        bg: "#191724",
        bgAlt: "#1f1d2e",
        text: "#e0def4",
        textDim: "#6e6a86"
      },
      accents: {
        accent: "#ea9d34",
        accentBright: "#d7827e"
      },
      paletteKey: "theme-omarchy-rose-pine",
      logoFilter: "brightness(0) saturate(100%) invert(90%) sepia(15%) saturate(600%) hue-rotate(250deg) brightness(105%) contrast(92%)"
    },
    "theme-omarchy-nord": {
      displayName: "Nord",
      category: "omarchy",
      description: "Arctic, north-bluish color palette",
      mode: "dark",
      surfaces: {
        bg: "#2e3440",
        bgAlt: "#3b4252",
        text: "#d8dee9",
        textDim: "#4c566a"
      },
      accents: {
        accent: "#88c0d0",
        accentBright: "#81a1c1"
      },
      paletteKey: "theme-omarchy-nord",
      logoFilter: "brightness(0) saturate(100%) invert(80%) sepia(20%) saturate(1000%) hue-rotate(160deg) brightness(95%) contrast(90%)"
    },
    "theme-omarchy-latte": {
      displayName: "Catppuccin Latte",
      category: "omarchy",
      description: "Light and creamy catppuccin variant",
      mode: "light",
      surfaces: {
        bg: "#eff1f5",
        bgAlt: "#e6e9ef",
        text: "#4c4f69",
        textDim: "#6c6f85"
      },
      accents: {
        accent: "#1e66f5",
        accentBright: "#179299"
      },
      paletteKey: "theme-omarchy-latte",
      logoFilter: "brightness(0) saturate(100%) invert(30%) sepia(50%) saturate(2000%) hue-rotate(210deg) brightness(95%) contrast(100%)"
    },
    "theme-omarchy-everforest": {
      displayName: "Everforest",
      category: "omarchy",
      description: "Comfortable green forest theme for your eyes",
      mode: "dark",
      surfaces: {
        bg: "#2b3339",
        bgAlt: "#323c41",
        text: "#d3c6aa",
        textDim: "#859289"
      },
      accents: {
        accent: "#a7c080",
        accentBright: "#83c092"
      },
      paletteKey: "theme-omarchy-everforest",
      logoFilter: "brightness(0) saturate(100%) invert(70%) sepia(25%) saturate(800%) hue-rotate(60deg) brightness(100%) contrast(90%)"
    },
    "theme-omarchy-flexoki": {
      displayName: "Flexoki",
      category: "omarchy",
      description: "Inky theme with organic warmth",
      mode: "dark",
      surfaces: {
        bg: "#100f0f",
        bgAlt: "#1c1b1a",
        text: "#cecdc3",
        textDim: "#6f6e69"
      },
      accents: {
        accent: "#205ea6",
        accentBright: "#3aa99f"
      },
      paletteKey: "theme-omarchy-flexoki",
      logoFilter: "brightness(0) saturate(100%) invert(80%) sepia(10%) saturate(500%) hue-rotate(35deg) brightness(95%) contrast(85%)"
    },
    "theme-omarchy-kanagawa": {
      displayName: "Kanagawa",
      category: "omarchy",
      description: "Inspired by the Great Wave off Kanagawa",
      mode: "dark",
      surfaces: {
        bg: "#1f1f28",
        bgAlt: "#2a2a37",
        text: "#ddd8bb",
        textDim: "#727169"
      },
      accents: {
        accent: "#7e9cd8",
        accentBright: "#7fb4ca"
      },
      paletteKey: "theme-omarchy-kanagawa",
      logoFilter: "brightness(0) saturate(100%) invert(75%) sepia(20%) saturate(900%) hue-rotate(200deg) brightness(100%) contrast(92%)"
    },
    "theme-omarchy-matte": {
      displayName: "Matte",
      category: "omarchy",
      description: "Soft matte colors with subtle contrast",
      mode: "dark",
      surfaces: {
        bg: "#212121",
        bgAlt: "#2c2c2c",
        text: "#eeeeee",
        textDim: "#7e7e7e"
      },
      accents: {
        accent: "#42a5f5",
        accentBright: "#66bb6a"
      },
      paletteKey: "theme-omarchy-matte",
      logoFilter: "brightness(0) saturate(100%) invert(65%) sepia(30%) saturate(1200%) hue-rotate(180deg) brightness(98%) contrast(95%)"
    },
    "theme-omarchy-osaka": {
      displayName: "Osaka",
      category: "omarchy",
      description: "Calm and balanced Japanese-inspired theme",
      mode: "dark",
      surfaces: {
        bg: "#1e1e2e",
        bgAlt: "#262637",
        text: "#d4d5c9",
        textDim: "#53685b"
      },
      accents: {
        accent: "#63b07a",
        accentBright: "#549e6a"
      },
      paletteKey: "theme-omarchy-osaka",
      logoFilter: "brightness(0) saturate(100%) invert(75%) sepia(20%) saturate(700%) hue-rotate(80deg) brightness(100%) contrast(90%)"
    },
    "theme-omarchy-ristretto": {
      displayName: "Ristretto",
      category: "omarchy",
      description: "Warm, coffee-inspired tones with a retro feel",
      mode: "dark",
      surfaces: {
        bg: "#2c2423",
        bgAlt: "#352f2e",
        text: "#f0e5de",
        textDim: "#948a8b"
      },
      accents: {
        accent: "#fd6883",
        accentBright: "#f59762"
      },
      paletteKey: "theme-omarchy-ristretto",
      logoFilter: "brightness(0) saturate(100%) invert(85%) sepia(25%) saturate(1000%) hue-rotate(320deg) brightness(105%) contrast(95%)"
    }
  };
  window.testThemeResolver = function() {
    console.log("Testing Theme Resolver...\n");
    const testThemes = ["theme-daylight", "theme-night", "theme-morning", "theme-1984"];
    testThemes.forEach((themeId) => {
      console.log(`
=== Testing ${themeId} ===`);
      const minimal = BASE_THEMES[themeId];
      const resolved = buildThemeObject(themeId, minimal);
      console.log("Minimal input:", minimal);
      console.log("Resolved output:", resolved);
      console.log("\nKey properties:");
      console.log("  bg:", resolved.bg);
      console.log("  text:", resolved.text);
      console.log("  accent:", resolved.accent);
      console.log("  mainBg:", resolved.mainBg);
      console.log("  calcResultColor:", resolved.calcResultColor);
      console.log("  calcCommentColor:", resolved.calcCommentColor);
      console.log("  calcRowTypeColor:", resolved.calcRowTypeColor);
      console.log("  calcRowTypeCustomColor:", resolved.calcRowTypeCustomColor);
      if (resolved.colors) {
        console.log("  Palette colors:", resolved.colors);
        console.log("  Default indices:", resolved.defaultIndices);
      }
    });
    console.log("\n\u2713 Test complete - check output above");
  };
  window.OMARCHY_PALETTES = {
    "theme-daylight": {
      colors: [DEFAULT_RESULT_COLOR, DEFAULT_COMMENT_COLOR, DEFAULT_ROW_TYPE_COLOR, DEFAULT_CUSTOM_TYPE_COLOR, "#3a7ad9", "#4cae50", "#d66b74", "#7a7a7a"],
      names: ["Result-Red", "Comment-Blue", "Type-Coral", "Custom-Purple", "Blue", "Green", "Pink", "Grey"],
      defaultIndices: { result: 0, comment: 1, rowType: 2, rowTypeCustom: 3 }
    },
    "theme-night": {
      colors: ["#c55970", "#7aa2f7", "#ff9e64", "#bb9af7", "#ee6e73", "#9d7ea6", "#6a7193", "#b8bfe8"],
      names: ["Result-Rose", "Comment-Blue", "Type-Orange", "Custom-Purple", "Bright-Rose", "Mauve", "Slate", "Light-Blue"],
      defaultIndices: { result: 0, comment: 1, rowType: 2, rowTypeCustom: 3 }
    },
    "theme-morning": {
      colors: [DEFAULT_RESULT_COLOR, DEFAULT_COMMENT_COLOR, DEFAULT_ROW_TYPE_COLOR, DEFAULT_CUSTOM_TYPE_COLOR, "#cb678f", "#d97ba6", "#598cb1", "#9a8aaa"],
      names: ["Result-Red", "Comment-Blue", "Type-Coral", "Custom-Purple", "Pink", "Light-Pink", "Blue", "Mauve"],
      defaultIndices: { result: 0, comment: 1, rowType: 2, rowTypeCustom: 3 }
    },
    "theme-1984": {
      colors: ["#c55970", "#7aa2f7", "#ff9e64", "#bb9af7", "#e9b97b", "#e97bb2", "#72f3bb", "#666666"],
      names: ["Result-Rose", "Comment-Blue", "Type-Orange", "Custom-Purple", "Amber", "Pink", "Mint", "Grey"],
      defaultIndices: { result: 0, comment: 1, rowType: 2, rowTypeCustom: 3 }
    },
    "theme-nordic": {
      colors: [DEFAULT_RESULT_COLOR, "#5e81ac", "#81a1c1", "#b48ead", "#88c0d0", "#a3be8c", "#bf616a", "#4c566a"],
      names: ["Result-Red", "Comment-Frost", "Type-Light-Frost", "Custom-Purple", "Cyan", "Green", "Red", "Polar"],
      defaultIndices: { result: 0, comment: 1, rowType: 2, rowTypeCustom: 3 }
    },
    "theme-retro": {
      colors: ["#ff6b9d", "#dda0bb", "#ffb6c1", "#bb9af7", "#ff9e64", "#ffc0cb", "#e97bb2", "#8a6d82"],
      names: ["Result-Pink", "Comment-Mauve", "Type-Light-Pink", "Custom-Purple", "Orange", "Blush", "Hot-Pink", "Grey"],
      defaultIndices: { result: 0, comment: 1, rowType: 2, rowTypeCustom: 3 }
    },
    "theme-brutalist": {
      colors: ["#000000", "#000000", "#dc2626", "#7c3aed", "#2563eb", "#16a34a", "#fde047", "#666666"],
      names: ["Black", "Black", "Red", "Purple", "Blue", "Green", "Yellow", "Grey"],
      defaultIndices: { result: 2, comment: 7, rowType: 3, rowTypeCustom: 4 }
    },
    "theme-omarchy-tokyo": {
      colors: ["#7aa2f7", "#bb9af7", "#ff9e64", "#f7768e", "#7dcfff", "#9ece6a", "#e06c75", "#565f89"],
      names: ["Blue", "Purple", "Orange", "Red", "Cyan", "Green", "Rose", "Grey"],
      defaultIndices: { result: 2, comment: 7, rowType: 5, rowTypeCustom: 4 }
    },
    "theme-omarchy-catppuccin": {
      colors: ["#89b4fa", "#f5c2e7", "#fab387", "#f38ba8", "#94e2d5", "#a6e3a1", "#eba0ac", "#7f849c"],
      names: ["Blue", "Pink", "Peach", "Red", "Teal", "Green", "Maroon", "Grey"],
      defaultIndices: { result: 2, comment: 7, rowType: 5, rowTypeCustom: 4 }
    },
    "theme-omarchy-gruvbox": {
      colors: ["#ea6962", "#d8a657", "#a9b665", "#89b482", "#7e8c9a", "#d65d0e", "#8f3f2f", "#928374"],
      names: ["Red", "Yellow", "Green", "Aqua", "Blue", "Orange", "Purple", "Grey"],
      defaultIndices: { result: 5, comment: 7, rowType: 2, rowTypeCustom: 3 }
    },
    "theme-omarchy-rose-pine": {
      colors: ["#d7827e", "#ea9d34", "#286983", "#d192b0", "#56949f", "#ebbcba", "#eb6f92", "#9893a5"],
      names: ["Rose", "Gold", "Pine", "Iris", "Foam", "Dust", "Love", "Muted"],
      defaultIndices: { result: 1, comment: 7, rowType: 4, rowTypeCustom: 2 }
    },
    "theme-omarchy-nord": {
      colors: ["#81a1c1", "#88c0d0", "#a3be8c", "#b48ead", "#5e81ac", "#bf616a", "#d08770", "#4c566a"],
      names: ["Frost-1", "Frost-2", "Aurora-Green", "Aurora-Purple", "Frost-Deep", "Frost-Red", "Aurora-Orange", "Polar"],
      defaultIndices: { result: 3, comment: 7, rowType: 2, rowTypeCustom: 0 }
    },
    "theme-omarchy-latte": {
      colors: ["#1e66f5", "#179299", "#40a02b", "#d20f39", "#8839ef", "#dd7878", "#fe640e", "#6c6f85"],
      names: ["Blue", "Teal", "Green", "Red", "Mauve", "Maroon", "Orange", "Subtext"],
      defaultIndices: { result: 6, comment: 7, rowType: 2, rowTypeCustom: 1 }
    },
    "theme-omarchy-everforest": {
      colors: ["#e67e80", "#e69875", "#dbbc7f", "#a7c080", "#83c092", "#7fbbb3", "#d699b6", "#859289"],
      names: ["Red", "Orange", "Yellow", "Green", "Aqua", "Blue", "Purple", "Grey"],
      defaultIndices: { result: 1, comment: 7, rowType: 3, rowTypeCustom: 4 }
    },
    "theme-omarchy-flexoki": {
      colors: ["#205ea6", "#3aa99f", "#886053", "#d76e6e", "#8b7100", "#c26d3f", "#b4637a", "#6f6e69"],
      names: ["Blue", "Teal", "Brown", "Red", "Yellow", "Orange", "Purple", "Grey"],
      defaultIndices: { result: 5, comment: 7, rowType: 1, rowTypeCustom: 0 }
    },
    "theme-omarchy-kanagawa": {
      colors: ["#7e9cd8", "#7fb4ca", "#98bb6c", "#d27e99", "#b99a7c", "#dca561", "#c34043", "#727169"],
      names: ["Blue", "Cyan", "Green", "Pink", "Brown", "Orange", "Red", "Grey"],
      defaultIndices: { result: 5, comment: 7, rowType: 2, rowTypeCustom: 1 }
    },
    "theme-omarchy-matte": {
      colors: ["#d35f5f", "#ffc107", "#66bb6a", "#42a5f5", "#ab47bc", "#ec407a", "#ff7043", "#7e7e7e"],
      names: ["Red", "Amber", "Green", "Blue", "Purple", "Pink", "Orange", "Grey"],
      defaultIndices: { result: 6, comment: 7, rowType: 2, rowTypeCustom: 3 }
    },
    "theme-omarchy-osaka": {
      colors: ["#549e6a", "#63b07a", "#d4b896", "#8c3b3d", "#7a6a74", "#dcb559", "#6b5b95", "#53685b"],
      names: ["Green", "Light-Green", "Beige", "Red", "Purple", "Yellow", "Darker-Purple", "Grey"],
      defaultIndices: { result: 5, comment: 7, rowType: 0, rowTypeCustom: 6 }
    },
    "theme-omarchy-ristretto": {
      colors: ["#fd6883", "#ff8297", "#fac0ca", "#ab7aa6", "#c0a5a5", "#f59762", "#e2876d", "#948a8b"],
      names: ["Pink", "Light-Pink", "Rose", "Purple", "Mauve", "Orange", "Terracotta", "Grey"],
      defaultIndices: { result: 5, comment: 7, rowType: 4, rowTypeCustom: 3 }
    }
  };
  var OMARCHY_COLOR_KEYS = {
    result: "calcResultColor",
    comment: "calcCommentColor",
    rowType: "calcRowTypeColor",
    rowTypeCustom: "calcRowTypeCustomColor"
  };
  function getOmarchyPalette(themeName) {
    return window.OMARCHY_PALETTES?.[themeName] || null;
  }
  function normalizeHexColor(color) {
    if (!color) return null;
    let hex = color.trim().toLowerCase();
    if (hex.startsWith("#")) hex = hex.slice(1);
    if (hex.length === 3) {
      hex = hex.split("").map((ch) => ch + ch).join("");
    }
    return /^[0-9a-f]{6}$/.test(hex) ? hex : null;
  }
  function hexToRgb(color) {
    const hex = normalizeHexColor(color);
    if (!hex) return null;
    const value = parseInt(hex, 16);
    return {
      r: value >> 16 & 255,
      g: value >> 8 & 255,
      b: value & 255
    };
  }
  function rgbToHsl({ r, g, b }) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case rn:
          h = (gn - bn) / d + (gn < bn ? 6 : 0);
          break;
        case gn:
          h = (bn - rn) / d + 2;
          break;
        case bn:
          h = (rn - gn) / d + 4;
          break;
      }
      h /= 6;
    }
    return { h: h * 360, s, l };
  }
  function getColorKeywordSet(color, name = "") {
    const keywords = /* @__PURE__ */ new Set();
    if (name) {
      name.toLowerCase().split(/[\s-_/]+/).forEach((token) => {
        if (token) keywords.add(token);
      });
    }
    const rgb = hexToRgb(color);
    if (!rgb) return keywords;
    const { h, s, l } = rgbToHsl(rgb);
    if (s < 0.1) {
      if (l >= 0.88) {
        keywords.add("white");
        keywords.add("light");
      } else if (l <= 0.14) {
        keywords.add("black");
        keywords.add("dark");
      } else {
        keywords.add("grey");
        keywords.add("gray");
        keywords.add("neutral");
        keywords.add("stone");
        keywords.add("muted");
      }
      return keywords;
    }
    let base = "red";
    if (h >= 8 && h < 30) base = "orange";
    else if (h >= 30 && h < 50) base = "amber";
    else if (h >= 50 && h < 70) base = "yellow";
    else if (h >= 70 && h < 95) base = "lime";
    else if (h >= 95 && h < 150) base = "green";
    else if (h >= 150 && h < 180) base = "teal";
    else if (h >= 180 && h < 205) base = "cyan";
    else if (h >= 205 && h < 255) base = "blue";
    else if (h >= 255 && h < 295) base = "purple";
    else if (h >= 295 && h < 330) base = "magenta";
    else if (h >= 330 && h < 345) base = "rose";
    else if (h >= 345 || h < 8) base = "red";
    const synonymMap = {
      red: ["red", "crimson", "rose", "scarlet"],
      orange: ["orange", "peach", "sunset", "coral"],
      amber: ["amber", "gold", "sun", "warm"],
      yellow: ["yellow", "golden", "sunny"],
      lime: ["lime", "chartreuse", "neon"],
      green: ["green", "forest", "emerald", "moss"],
      teal: ["teal", "aqua", "sea"],
      cyan: ["cyan", "aqua", "turquoise"],
      blue: ["blue", "navy", "azure", "frost"],
      purple: ["purple", "violet", "mauve", "iris"],
      magenta: ["magenta", "fuchsia", "pink"],
      rose: ["rose", "pink", "blush"]
    };
    const synonyms = synonymMap[base] || [base];
    synonyms.forEach((word) => keywords.add(word));
    if (l > 0.75) keywords.add("light");
    if (l < 0.25) keywords.add("dark");
    if (s > 0.65 && l >= 0.35 && l <= 0.65) keywords.add("vivid");
    if (["orange", "amber", "yellow", "rose", "magenta", "red"].includes(base)) keywords.add("warm");
    if (["teal", "cyan", "blue", "purple"].includes(base)) keywords.add("cool");
    return keywords;
  }
  function sanitizePaletteIndex(value, fallback, size) {
    return Number.isInteger(value) && value >= 0 && value < size ? value : fallback;
  }
  function sanitizePaletteIndices(themeName, rawIndices = {}) {
    const palette = getOmarchyPalette(themeName);
    if (!palette) return null;
    const { colors = [], defaultIndices = {} } = palette;
    const size = colors.length;
    const safeIndices = {};
    Object.keys(OMARCHY_COLOR_KEYS).forEach((key) => {
      const fallback = sanitizePaletteIndex(defaultIndices?.[key], 0, size);
      safeIndices[key] = sanitizePaletteIndex(rawIndices?.[key], fallback, size);
    });
    return safeIndices;
  }
  var OMARCHY_PRESET_KEYWORD_GROUPS = {
    warm: ["orange", "amber", "gold", "peach", "yellow", "sun", "maroon", "terracotta", "aurora-orange", "sunset", "beige"],
    warmStrong: ["red", "rose", "love", "magenta", "pink", "scarlet"],
    coolGreen: ["green", "light-green", "aurora-green", "pine", "moss"],
    aqua: ["aqua", "cyan", "teal", "foam", "turquoise", "frost-2", "blue"],
    deepBlue: ["blue", "navy", "pine", "frost", "cyan"],
    purple: ["purple", "mauve", "iris", "dust", "love", "maroon"],
    neutral: ["grey", "gray", "muted", "polar", "subtext", "stone", "smoke"]
  };
  function findPaletteIndexByKeywords(palette, keywords = []) {
    if (!palette || !Array.isArray(palette.colors)) return -1;
    const lowered = Array.isArray(palette.names) ? palette.names.map((name) => (name || "").toLowerCase()) : [];
    const descriptorSets = palette.colors.map((color, idx) => {
      const set = getColorKeywordSet(color, lowered[idx]);
      if (lowered[idx]) {
        lowered[idx].split(/[\s-_/]+/).forEach((token) => {
          if (token) set.add(token);
        });
      }
      return set;
    });
    for (const keyword of keywords) {
      const target = keyword.toLowerCase();
      for (let idx = 0; idx < descriptorSets.length; idx++) {
        if (descriptorSets[idx].has(target)) {
          return idx;
        }
      }
    }
    return -1;
  }
  function clampPaletteIndex(index, palette) {
    if (!palette || !Array.isArray(palette.colors)) return 0;
    const max = palette.colors.length - 1;
    if (!Number.isInteger(index)) return 0;
    return Math.min(Math.max(index, 0), max);
  }
  function buildPresetIndices(themeName, presetConfig) {
    const palette = getOmarchyPalette(themeName);
    if (!palette) return null;
    const { selectors = {}, perTheme = {} } = presetConfig || {};
    const themeOverrides = perTheme?.[themeName] || {};
    const result = {};
    Object.keys(OMARCHY_COLOR_KEYS).forEach((role) => {
      if (Number.isInteger(themeOverrides[role])) {
        result[role] = clampPaletteIndex(themeOverrides[role], palette);
        return;
      }
      const selector = selectors[role] || {};
      let index = -1;
      if (selector.keywords?.length) {
        index = findPaletteIndexByKeywords(palette, selector.keywords);
      }
      if (index === -1 && Number.isInteger(selector.fallbackIndex)) {
        index = selector.fallbackIndex;
      }
      if (index === -1 && palette.defaultIndices) {
        index = palette.defaultIndices[role];
      }
      result[role] = clampPaletteIndex(index, palette);
    });
    return result;
  }
  var OMARCHY_PALETTE_PRESET_CONFIGS = {
    default: {
      label: "Theme default",
      description: "Use the Omarchy theme's built-in calc colors.",
      getIndices: (themeName) => {
        const palette = getOmarchyPalette(themeName);
        if (!palette) return null;
        return { ...palette.defaultIndices };
      }
    },
    "vscode-style": {
      label: "VS Code style",
      description: "Warm results, green types, aqua custom labels, soft grey comments.",
      selectors: {
        result: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.warm, ...OMARCHY_PRESET_KEYWORD_GROUPS.warmStrong], fallbackIndex: 2 },
        rowType: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.coolGreen, "aurora-purple"], fallbackIndex: 3 },
        rowTypeCustom: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.aqua, "foam", "dust"], fallbackIndex: 4 },
        comment: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.neutral], fallbackIndex: 7 }
      }
    },
    "vim-style": {
      label: "Vim style",
      description: "Red-toned numbers, cyan types, purple custom labels, muted comments.",
      selectors: {
        result: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.warmStrong, ...OMARCHY_PRESET_KEYWORD_GROUPS.warm], fallbackIndex: 6 },
        rowType: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.aqua, ...OMARCHY_PRESET_KEYWORD_GROUPS.deepBlue], fallbackIndex: 4 },
        rowTypeCustom: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.purple, "magenta"], fallbackIndex: 5 },
        comment: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.neutral], fallbackIndex: 7 }
      }
    },
    "btop-style": {
      label: "btop dashboard",
      description: "Amber numbers, teal types, and orange custom labels inspired by btop's panels.",
      selectors: {
        result: { keywords: ["amber", "gold", "yellow", "sun", "peach", "orange"], fallbackIndex: 2 },
        rowType: { keywords: ["teal", "aqua", "cyan", "green", "light-green"], fallbackIndex: 4 },
        rowTypeCustom: { keywords: ["orange", "peach", "maroon", "rose", "pink"], fallbackIndex: 6 },
        comment: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.neutral], fallbackIndex: 7 }
      }
    },
    "terminal-style": {
      label: "Terminal green",
      description: "Command-line inspired: green results, cyan types, amber custom labels, subdued comments.",
      selectors: {
        result: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.coolGreen, "terminal"], fallbackIndex: 3 },
        rowType: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.aqua, "cyan"], fallbackIndex: 4 },
        rowTypeCustom: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.warm, "amber"], fallbackIndex: 2 },
        comment: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.neutral], fallbackIndex: 7 }
      }
    },
    "pastel-notebook": {
      label: "Pastel notebook",
      description: "Soft study vibe: peach results, mauve types, teal custom labels, gentle notes.",
      selectors: {
        result: { keywords: ["peach", "dust", "mauve", "pink"], fallbackIndex: 2 },
        rowType: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.purple, "iris"], fallbackIndex: 4 },
        rowTypeCustom: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.aqua, "foam"], fallbackIndex: 5 },
        comment: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.neutral], fallbackIndex: 7 }
      }
    },
    "contrast-pop": {
      label: "Contrast pop",
      description: "High-contrast dashboard: vivid red results, deep blue types, golden custom labels, cool comments.",
      selectors: {
        result: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.warmStrong, "red"], fallbackIndex: 0 },
        rowType: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.deepBlue, "navy"], fallbackIndex: 0 },
        rowTypeCustom: { keywords: ["gold", "amber", "yellow", "sun"], fallbackIndex: 2 },
        comment: { keywords: [...OMARCHY_PRESET_KEYWORD_GROUPS.neutral], fallbackIndex: 7 }
      }
    }
  };
  var OMARCHY_PALETTE_PRESETS = {};
  Object.entries(OMARCHY_PALETTE_PRESET_CONFIGS).forEach(([key, config2]) => {
    const preset = {
      key,
      label: config2.label,
      description: config2.description || "",
      getIndices(themeName) {
        if (typeof config2.getIndices === "function") {
          const indices2 = config2.getIndices(themeName);
          return indices2 ? sanitizePaletteIndices(themeName, indices2) : null;
        }
        const indices = buildPresetIndices(themeName, config2);
        return indices ? sanitizePaletteIndices(themeName, indices) : null;
      }
    };
    OMARCHY_PALETTE_PRESETS[key] = preset;
  });
  window.OMARCHY_PALETTE_PRESETS = OMARCHY_PALETTE_PRESETS;
  function applyOmarchyPaletteIndices(themeName, rawIndices = {}) {
    const palette = getOmarchyPalette(themeName);
    const theme = THEME_CONFIG_V2?.[themeName];
    if (!palette || !theme) return null;
    const indices = sanitizePaletteIndices(themeName, rawIndices) || {};
    Object.entries(OMARCHY_COLOR_KEYS).forEach(([key, themeProp]) => {
      const index = indices[key];
      const color = palette.colors?.[index] ?? palette.colors?.[palette.defaultIndices?.[key]];
      if (color) {
        theme[themeProp] = color;
      }
      const indexProp = `${themeProp}Index`;
      theme[indexProp] = index;
    });
    theme.paletteIndices = indices;
    return indices;
  }
  window.DesignLabPalette = {
    getPalette: getOmarchyPalette,
    sanitizeIndices: sanitizePaletteIndices,
    applyIndices: applyOmarchyPaletteIndices,
    defaultIndices(themeName) {
      const palette = getOmarchyPalette(themeName);
      if (!palette) return null;
      return sanitizePaletteIndices(themeName, palette.defaultIndices);
    },
    listPresets() {
      return Object.values(window.OMARCHY_PALETTE_PRESETS || {}).map((preset) => ({
        key: preset.key,
        label: preset.label,
        description: preset.description
      }));
    },
    getPresetIndices(themeName, presetKey) {
      const preset = window.OMARCHY_PALETTE_PRESETS?.[presetKey];
      if (!preset) return null;
      return preset.getIndices(themeName);
    },
    getPresetMetadata(presetKey) {
      const preset = window.OMARCHY_PALETTE_PRESETS?.[presetKey];
      if (!preset) return null;
      return { key: preset.key, label: preset.label, description: preset.description };
    },
    detectPreset(themeName, indices) {
      const sanitized = sanitizePaletteIndices(themeName, indices);
      if (!sanitized) return null;
      const presets = window.OMARCHY_PALETTE_PRESETS || {};
      for (const key of Object.keys(presets)) {
        const presetIndices = presets[key].getIndices(themeName);
        if (!presetIndices) continue;
        const matches = Object.keys(OMARCHY_COLOR_KEYS).every((role) => presetIndices[role] === sanitized[role]);
        if (matches) {
          return key;
        }
      }
      return null;
    }
  };
  var RESOLVED_THEMES = {};
  try {
    Object.keys(BASE_THEMES).forEach((themeId) => {
      try {
        RESOLVED_THEMES[themeId] = buildThemeObject(themeId, BASE_THEMES[themeId]);
      } catch (err) {
        console.error(`Error resolving theme ${themeId}:`, err);
        RESOLVED_THEMES[themeId] = BASE_THEMES[themeId];
      }
    });
  } catch (err) {
    console.error("Error building RESOLVED_THEMES:", err);
  }
  window.RESOLVED_THEMES = RESOLVED_THEMES;
  window.BASE_THEMES = BASE_THEMES;
  window.THEME_CONFIG_V2 = RESOLVED_THEMES;
  var DESIGN_LAB_FALLBACK_COLORS = [
    "#FF6B6B",
    "#FFA94D",
    "#FFD43B",
    "#69DB7C",
    "#1EC6FF",
    "#4263EB",
    "#9775FA",
    "#F06595",
    "#868E96",
    "#343A40"
  ];
  function buildPaletteFromTheme(themeName, themeConfig) {
    if (!themeConfig) return null;
    const paletteColors = [];
    const paletteNames = [];
    const defaultIndices = {};
    const pushHexColor = (label, color) => {
      const normalized = normalizeHexColor(color);
      if (!normalized) return -1;
      const hexValue = `#${normalized}`;
      const existingIndex = paletteColors.findIndex((existing) => existing.toLowerCase() === hexValue.toLowerCase());
      if (existingIndex !== -1) return existingIndex;
      paletteColors.push(hexValue);
      const descriptors = Array.from(getColorKeywordSet(hexValue, label));
      const descriptor = descriptors.find((word) => !["warm", "cool", "light", "dark", "neutral", "stone", "muted", "vivid"].includes(word)) || descriptors[0] || label;
      const base = label.toLowerCase();
      const nameParts = descriptor && descriptor !== base ? [base, descriptor] : [base];
      paletteNames.push(nameParts.join("-"));
      return paletteColors.length - 1;
    };
    const coreSwatches = [
      { label: "result", role: "result", color: themeConfig.calcResultColor },
      { label: "comment", role: "comment", color: themeConfig.calcCommentColor },
      { label: "row-type", role: "rowType", color: themeConfig.calcRowTypeColor },
      { label: "row-type-custom", role: "rowTypeCustom", color: themeConfig.calcRowTypeCustomColor }
    ];
    coreSwatches.forEach((item) => {
      const index = pushHexColor(item.label, item.color);
      if (index !== -1 && item.role) {
        defaultIndices[item.role] = index;
      }
    });
    const supplementalSwatches = [
      { label: "accent", color: themeConfig.accent },
      { label: "accent-bright", color: themeConfig.accentBright },
      { label: "text", color: themeConfig.text },
      { label: "text-dim", color: themeConfig.textDim },
      { label: "background", color: themeConfig.bg },
      { label: "background-alt", color: themeConfig.bgAlt },
      { label: "highlight", color: themeConfig.highlightBgColor },
      { label: "border", color: themeConfig.borderColor },
      { label: "log", color: themeConfig.calcRowLogColor }
    ];
    supplementalSwatches.forEach((item) => pushHexColor(item.label, item.color));
    DESIGN_LAB_FALLBACK_COLORS.forEach((color, idx) => {
      if (paletteColors.length >= 12) return;
      pushHexColor(`fallback-${idx + 1}`, color);
    });
    if (paletteColors.length === 0) return null;
    ["result", "comment", "rowType", "rowTypeCustom"].forEach((role) => {
      if (!Number.isInteger(defaultIndices[role])) {
        defaultIndices[role] = 0;
      }
    });
    return {
      colors: paletteColors,
      names: paletteNames,
      defaultIndices
    };
  }
  (function initializeSemanticPalettes() {
    window.OMARCHY_PALETTES = window.OMARCHY_PALETTES || {};
    Object.entries(THEME_CONFIG_V2).forEach(([themeName, themeConfig]) => {
      const existingPalette = window.OMARCHY_PALETTES[themeName];
      if (existingPalette) {
        themeConfig.calcResultColorIndex = themeConfig.calcResultColorIndex ?? existingPalette.defaultIndices?.result ?? 0;
        themeConfig.calcCommentColorIndex = themeConfig.calcCommentColorIndex ?? existingPalette.defaultIndices?.comment ?? 0;
        themeConfig.calcRowTypeColorIndex = themeConfig.calcRowTypeColorIndex ?? existingPalette.defaultIndices?.rowType ?? 0;
        themeConfig.calcRowTypeCustomColorIndex = themeConfig.calcRowTypeCustomColorIndex ?? existingPalette.defaultIndices?.rowTypeCustom ?? 0;
        themeConfig.paletteIndices = themeConfig.paletteIndices || { ...existingPalette.defaultIndices };
        return;
      }
      const palette = buildPaletteFromTheme(themeName, themeConfig);
      if (!palette) return;
      window.OMARCHY_PALETTES[themeName] = palette;
      themeConfig.calcResultColorIndex = palette.defaultIndices.result ?? 0;
      themeConfig.calcCommentColorIndex = palette.defaultIndices.comment ?? 0;
      themeConfig.calcRowTypeColorIndex = palette.defaultIndices.rowType ?? 0;
      themeConfig.calcRowTypeCustomColorIndex = palette.defaultIndices.rowTypeCustom ?? 0;
      themeConfig.paletteIndices = { ...palette.defaultIndices };
    });
  })();
  if (window.DesignLabPalette?.applyIndices) {
    Object.keys(window.OMARCHY_PALETTES || {}).forEach((themeName) => {
      const theme = THEME_CONFIG_V2[themeName];
      if (!theme) return;
      const indices = {
        result: theme.calcResultColorIndex,
        comment: theme.calcCommentColorIndex,
        rowType: theme.calcRowTypeColorIndex,
        rowTypeCustom: theme.calcRowTypeCustomColorIndex
      };
      window.DesignLabPalette.applyIndices(themeName, indices);
    });
  }
  var loadedFonts = /* @__PURE__ */ new Set();
  var FONT_MAP = {
    "Fira Sans": "family=Fira+Sans:wght@400;500;600;700",
    "IBM Plex Mono": "family=IBM+Plex+Mono:wght@400;600",
    "IBM Plex Sans": "family=IBM+Plex+Sans:wght@400;600",
    "Archivo Black": "family=Archivo+Black",
    "Playfair Display": "family=Playfair+Display:ital,wght@0,600;1,400",
    "Outfit": "family=Outfit:wght@400;600",
    "Fraunces": "family=Fraunces:opsz,wght@9..144,600",
    "JetBrains Mono": "family=JetBrains+Mono:wght@400;700",
    "Courier Prime": "family=Courier+Prime:wght@400;700",
    "Bungee": "family=Bungee",
    "Zilla Slab": "family=Zilla+Slab:wght@400;700",
    "Bebas Neue": "family=Bebas+Neue",
    "Fugaz One": "family=Fugaz+One",
    "VT323": "family=VT323",
    "Crimson Pro": "family=Crimson+Pro:wght@400;600",
    "Gruppo": "family=Gruppo",
    "Lora": "family=Lora:wght@400;600",
    "Syne": "family=Syne:wght@400;700"
  };
  function loadFontForTheme(themeId) {
    const config2 = window.THEME_CONFIG_V2?.[themeId];
    if (!config2?.fontFamily) return;
    const fontMatch = config2.fontFamily.match(/['"]([^'"]+)['"]/);
    if (!fontMatch) return;
    const fontName = fontMatch[1];
    if (loadedFonts.has(fontName) || !FONT_MAP[fontName]) {
      return;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${FONT_MAP[fontName]}&display=swap`;
    document.head.appendChild(link);
    loadedFonts.add(fontName);
  }
  function colorToRgb(color) {
    if (color.startsWith("#")) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `${r} ${g} ${b}`;
    }
    const rgbMatch = color.match(/rgba?\((\d+),?\s*(\d+),?\s*(\d+)/);
    if (rgbMatch) {
      return `${rgbMatch[1]} ${rgbMatch[2]} ${rgbMatch[3]}`;
    }
    const hslMatch = color.match(/hsla?\((\d+),?\s*(\d+)%?,?\s*(\d+)%?/);
    if (hslMatch) {
      const h = parseInt(hslMatch[1]);
      const s = parseInt(hslMatch[2]) / 100;
      const l = parseInt(hslMatch[3]) / 100;
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs(h / 60 % 2 - 1));
      const m = l - c / 2;
      let r, g, b;
      if (h < 60) [r, g, b] = [c, x, 0];
      else if (h < 120) [r, g, b] = [x, c, 0];
      else if (h < 180) [r, g, b] = [0, c, x];
      else if (h < 240) [r, g, b] = [0, x, c];
      else if (h < 300) [r, g, b] = [x, 0, c];
      else [r, g, b] = [c, 0, x];
      return `${Math.round((r + m) * 255)} ${Math.round((g + m) * 255)} ${Math.round((b + m) * 255)}`;
    }
    return "0 0 0";
  }
  function applyTheme(themeName, themeConfig = null) {
    if (!themeConfig) {
      themeConfig = window.THEME_CONFIG_V2?.[themeName];
      if (!themeConfig) {
        const resolvedId = resolveThemeId(themeName);
        if (resolvedId && window.THEME_CONFIG_V2?.[resolvedId]) {
          themeName = resolvedId;
          themeConfig = window.THEME_CONFIG_V2[resolvedId];
        }
      }
      if (!themeConfig) {
        console.warn(`Theme "${themeName}" not found. Falling back to daylight.`);
        return applyTheme("theme-daylight", window.THEME_CONFIG_V2?.["theme-daylight"]);
      }
    }
    const root = document.documentElement;
    document.body.setAttribute("data-theme", themeName);
    root.style.setProperty("--bg", themeConfig.bg);
    root.style.setProperty("--bg-alt", themeConfig.bgAlt);
    root.style.setProperty("--text", themeConfig.text);
    root.style.setProperty("--text-dim", themeConfig.textDim);
    root.style.setProperty("--accent", themeConfig.accent);
    root.style.setProperty("--accent-bright", themeConfig.accentBright);
    root.style.setProperty("--border-color", themeConfig.borderColor);
    root.style.setProperty("--calc-result-color", themeConfig.calcResultColor);
    root.style.setProperty("--calc-comment-color", themeConfig.calcCommentColor);
    root.style.setProperty("--calc-row-type-color", themeConfig.calcRowTypeColor);
    root.style.setProperty("--calc-row-type-custom-color", themeConfig.calcRowTypeCustomColor);
    root.style.setProperty("--bg-rgb", colorToRgb(themeConfig.bg));
    root.style.setProperty("--bg-alt-rgb", colorToRgb(themeConfig.bgAlt));
    root.style.setProperty("--text-rgb", colorToRgb(themeConfig.text));
    root.style.setProperty("--text-dim-rgb", colorToRgb(themeConfig.textDim));
    root.style.setProperty("--accent-rgb", colorToRgb(themeConfig.accent));
    root.style.setProperty("--accent-bright-rgb", colorToRgb(themeConfig.accentBright));
    root.style.setProperty("--border-color-rgb", colorToRgb(themeConfig.borderColor));
    root.style.setProperty("--calc-result-color-rgb", colorToRgb(themeConfig.calcResultColor));
    root.style.setProperty("--calc-row-type-color-rgb", colorToRgb(themeConfig.calcRowTypeColor));
    root.style.setProperty("--calc-row-type-custom-color-rgb", colorToRgb(themeConfig.calcRowTypeCustomColor));
    if (themeConfig.logoFilter) {
      root.style.setProperty("--theme-logo-filter", themeConfig.logoFilter);
    } else {
      root.style.removeProperty("--theme-logo-filter");
    }
    const radiusKeys = ["--radius-control", "--radius-base", "--radius-card", "--radius-surface", "--radius-sidebar"];
    if (themeConfig.radii) {
      const radiusMap = {
        "--radius-control": themeConfig.radii.control,
        "--radius-base": themeConfig.radii.base,
        "--radius-card": themeConfig.radii.card,
        "--radius-surface": themeConfig.radii.surface,
        "--radius-sidebar": themeConfig.radii.sidebar
      };
      Object.entries(radiusMap).forEach(([key, value]) => {
        if (value) root.style.setProperty(key, value);
        else root.style.removeProperty(key);
      });
    } else {
      radiusKeys.forEach((key) => root.style.removeProperty(key));
    }
    const fontVars = {
      "--font-family-body": themeConfig.fontFamilyBody,
      "--font-family-mono": themeConfig.fontFamilyMono,
      "--theme-font-family": themeConfig.fontFamily
    };
    Object.entries(fontVars).forEach(([cssVar, value]) => {
      if (value) root.style.setProperty(cssVar, value);
      else root.style.removeProperty(cssVar);
    });
    const typographyVars = ["--theme-h2-font-size", "--theme-h2-font-weight", "--theme-h2-margin-bottom"];
    if (themeConfig.typography?.h2) {
      const h2 = themeConfig.typography.h2;
      const h2Map = {
        "--theme-h2-font-size": h2.size,
        "--theme-h2-font-weight": h2.weight,
        "--theme-h2-margin-bottom": h2.marginBottom
      };
      Object.entries(h2Map).forEach(([cssVar, value]) => {
        if (value) root.style.setProperty(cssVar, value);
        else root.style.removeProperty(cssVar);
      });
    } else {
      typographyVars.forEach((v) => root.style.removeProperty(v));
    }
    if (themeConfig.mainBg) {
      root.style.setProperty("--main-bg", themeConfig.mainBg);
    } else {
      root.style.removeProperty("--main-bg");
    }
    const themeBgImage = themeConfig.background || "none";
    root.style.setProperty("--theme-bg-image", themeBgImage);
    root.style.setProperty("--bg-image", themeBgImage);
    if (themeConfig.className) {
      document.body.className = themeConfig.className;
    } else {
    }
    if (themeConfig.mode === "dark") {
      document.body.classList.add("theme-dark");
      document.body.classList.remove("theme-light");
    } else {
      document.body.classList.add("theme-light");
      document.body.classList.remove("theme-dark");
    }
    document.body.classList.forEach((cls) => {
      if (cls.startsWith("theme-") && cls !== "theme-dark" && cls !== "theme-light") {
        document.body.classList.remove(cls);
      }
    });
    document.body.classList.add(themeName);
    loadFontForTheme(themeName);
    const VARIABLE_MAP = {
      calcBgColor: "--calc-bg-color",
      calcRowNumber: "--calc-row-number",
      calcRowSelectedInputBgColor: "--calc-row-selected-input-bg-color",
      calcCommentHoverBgColor: "--calc-comment-hover-bg-color",
      calcRowLogColor: "--calc-row-log-color",
      calcRowLogBgColor: "--calc-row-log-bg-color",
      calcRowLogBorderColor: "--calc-row-log-border-color",
      linkHoverColor: "--link-hover-color",
      tabActiveColor: "--tab-active-color",
      navLinkHoverColor: "--nav-link-hover-color",
      navLinkBgColor: "--nav-link-bg-color",
      navLinkActiveColor: "--nav-link-active-color",
      navLinkActiveBgColor: "--nav-link-active-bg-color",
      secondaryBgColor: "--secondary-bg-color",
      highlightBgColor: "--highlight-bg-color",
      highlightTextColor: "--highlight-text-color",
      buttonBgColorHover: "--button-bg-color-hover",
      buttonBgColorCreate: "--button-bg-color-create",
      buttonTextColorCreate: "--button-text-color-create",
      themeLogoScale: "--theme-logo-scale",
      themeHeaderBg: "--theme-header-bg",
      themeAsideButtonFontSize: "--theme-aside-button-font-size",
      themeAsideButtonPadding: "--theme-aside-button-padding",
      themeMainInnerMaxWidth: "--theme-main-inner-max-width",
      text: "--theme-text"
    };
    Object.entries(VARIABLE_MAP).forEach(([key, cssVar]) => {
      if (themeConfig[key] !== void 0) {
        root.style.setProperty(cssVar, themeConfig[key]);
      } else {
        root.style.removeProperty(cssVar);
      }
    });
    const defaultIntensity = window.THEME_DEFAULTS.gradientIntensity;
    const userIntensity = window.ThemeSystem?.state?.gradientIntensity;
    const intensity = userIntensity ?? themeConfig.gradientIntensity ?? defaultIntensity;
    root.style.setProperty("--gradient-intensity", intensity);
    root.style.setProperty("--orb-opacity", intensity);
    const dockMixPercent = intensity > 0 ? Math.round(intensity / defaultIntensity * 15) : 0;
    root.style.setProperty("--dock-gradient-mix", `${dockMixPercent}%`);
    root.style.setProperty("--new-layout-card-bg", "var(--calc-bg-color)");
    root.style.setProperty("--new-layout-text", "var(--text)");
    root.style.setProperty("--new-layout-border", "var(--border-color)");
    root.style.setProperty("--new-layout-accent", "var(--accent)");
    root.style.setProperty("--new-layout-text-muted", "var(--text-dim)");
    const hasCustomControlRadius = themeConfig.radii?.control && themeConfig.radii.control !== "0px" && themeConfig.radii.control !== "0";
    if (themeConfig.features?.roundedControls || hasCustomControlRadius) {
      document.body.setAttribute("data-theme-rounded-controls", "true");
    } else {
      document.body.removeAttribute("data-theme-rounded-controls");
    }
    if (themeConfig.features?.roundedUtilities) {
      document.body.setAttribute("data-theme-rounded-utilities", "true");
    } else {
      document.body.removeAttribute("data-theme-rounded-utilities");
    }
  }
  window.resolveTheme = applyTheme;
  window.applyTheme_v2 = applyTheme;
  window.loadFontForTheme = loadFontForTheme;
  function resolveThemeId(inputThemeId) {
    if (!inputThemeId) return null;
    if (window.THEME_CONFIG_V2?.[inputThemeId]) return inputThemeId;
    const withTheme = inputThemeId.startsWith("theme-") ? inputThemeId : `theme-${inputThemeId}`;
    if (window.THEME_CONFIG_V2?.[withTheme]) return withTheme;
    const cleanId = inputThemeId.replace(/^theme-/, "");
    const withOmarchy = `theme-omarchy-${cleanId}`;
    if (window.THEME_CONFIG_V2?.[withOmarchy]) return withOmarchy;
    return null;
  }
  document.addEventListener("DOMContentLoaded", () => {
    window.ThemeSystem.init();
  });

  // public/src/utils/PerformanceMetrics.js
  var PerformanceMetrics = class {
    constructor() {
      this.samples = {
        commit: [],
        paint: []
      };
      this.breakdownSamples = [];
      this.maxSamples = 100;
      this.enabled = false;
    }
    /**
     * Record a breakdown measurement
     * @param {Object} breakdown - { react_sync, zero_lag_raf, engine, sync_total, vsync_wait, async_total }
     */
    recordBreakdown(breakdown) {
      if (!this.enabled) return;
      this.breakdownSamples.push({
        time: Date.now(),
        ...breakdown
      });
      if (this.breakdownSamples.length > this.maxSamples) {
        this.breakdownSamples.shift();
      }
    }
    /**
     * Get breakdown statistics
     * @returns {Object} p50 values for each breakdown category
     */
    getBreakdownStats() {
      if (this.breakdownSamples.length === 0) {
        return { react_sync: 0, zero_lag_raf: 0, engine: 0, sync_total: 0, vsync_wait: 0, async_total: 0, count: 0 };
      }
      const keys = ["react_sync", "zero_lag_raf", "engine", "sync_total", "vsync_wait", "async_total"];
      const stats = { count: this.breakdownSamples.length };
      keys.forEach((key) => {
        const values = this.breakdownSamples.map((s) => s[key] || 0).sort((a, b) => a - b);
        stats[key] = values[Math.floor(values.length * 0.5)] || 0;
      });
      return stats;
    }
    /**
     * Enable metrics tracking (typically via ?debug)
     */
    enable() {
      this.enabled = true;
    }
    /**
     * Disable metrics tracking
     */
    disable() {
      this.enabled = false;
    }
    /**
     * Start tracking a keypress
     * @returns {number} Start timestamp
     */
    startKeypress() {
      if (!this.enabled) return null;
      return performance.now();
    }
    /**
     * Record a phase measurement
     * @param {string} phase - 'commit' or 'paint'
     * @param {number} startTime - Timestamp from startKeypress()
     */
    recordPhase(phase, startTime) {
      if (!this.enabled || startTime === null) return;
      const duration = performance.now() - startTime;
      if (!this.samples[phase]) {
        console.warn("[PerformanceMetrics] Unknown phase:", phase);
        return;
      }
      this.samples[phase].push(duration);
      if (this.samples[phase].length > this.maxSamples) {
        this.samples[phase].shift();
      }
    }
    /**
     * Get statistics for a phase
     * @param {string} phase - 'commit' or 'paint'
     * @returns {{p50: number, p95: number, count: number}}
     */
    getStats(phase) {
      const samples = this.samples[phase] || [];
      if (samples.length === 0) {
        return { p50: 0, p95: 0, count: 0 };
      }
      const sorted = [...samples].sort((a, b) => a - b);
      return {
        p50: sorted[Math.floor(sorted.length * 0.5)] || 0,
        p95: sorted[Math.floor(sorted.length * 0.95)] || 0,
        count: sorted.length
      };
    }
    /**
     * Get all statistics
     * @returns {Object} Stats for all phases
     */
    getAllStats() {
      return {
        commit: this.getStats("commit"),
        paint: this.getStats("paint"),
        breakdown: this.getBreakdownStats()
      };
    }
    /**
     * Format stats for display in ?debug
     * @returns {string}
     */
    format() {
      const stats = this.getAllStats();
      const lines = [];
      lines.push("\u{1F4CA} Keypress Latency (ms):");
      lines.push(`  Commit: p50=${stats.commit.p50.toFixed(1)} p95=${stats.commit.p95.toFixed(1)} (n=${stats.commit.count})`);
      lines.push(`  Paint:  p50=${stats.paint.p50.toFixed(1)} p95=${stats.paint.p95.toFixed(1)} (n=${stats.paint.count})`);
      return lines.join("\n");
    }
    /**
     * Reset all samples
     */
    reset() {
      this.samples = {
        commit: [],
        paint: []
      };
      this.breakdownSamples = [];
    }
  };
  var perfMetrics = new PerformanceMetrics();
  if (typeof window !== "undefined") {
    window.perfMetrics = perfMetrics;
  }

  // public/src/utils/DebugLogger.js
  var MAX_LOG_BUFFER = 1e4;
  var DEFAULT_CATEGORIES = {
    // From DebugLogger (React/component debugging)
    render: false,
    input: false,
    focus: false,
    cache: false,
    events: false,
    // From Debug (system-level debugging)
    store: false,
    // CalcStore operations
    storage: false,
    // IndexedDB/localStorage
    router: false,
    // URL routing and navigation
    loader: false,
    // CalcLoader operations (currencies, imports)
    stock: false,
    // Stock loading, caching, and unit registration
    parser: false,
    // Parser preprocessing, evaluation paths, symbol resolution
    url: false,
    // UrlManager, url-state
    ui: false,
    // React UI components
    auth: false,
    // Firebase auth operations
    plugins: false,
    // Plugin Registry operations
    perf: false,
    // Performance timing (controlled by perf stats toggle, excluded from ?debug=all)
    // Verbose categories (extra noisy, off by default)
    "store:verbose": false,
    // Per-calc save confirmations
    "offline:verbose": false,
    // Individual IndexedDB writes
    "portal:verbose": false,
    // MultiPortal updates
    "stock:verbose": false,
    // Extra stock details (cache contents, etc.)
    "parser:verbose": false
    // Extra parser details (all transformations)
  };
  var cachedSettings = null;
  var logBuffer = [];
  var originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error
  };
  function interceptConsole() {
    ["log", "warn", "error"].forEach((method) => {
      console[method] = function(...args) {
        const message = args.map(
          (a) => typeof a === "object" ? JSON.stringify(a) : String(a)
        ).join(" ");
        logBuffer.push({ time: Date.now(), type: method, message });
        if (logBuffer.length > MAX_LOG_BUFFER) {
          logBuffer.shift();
        }
        originalConsole[method].apply(console, args);
      };
    });
  }
  function getSettings() {
    if (cachedSettings) return cachedSettings;
    cachedSettings = { ...DEFAULT_CATEGORIES };
    return cachedSettings;
  }
  function saveSettings(settings) {
    cachedSettings = settings;
  }
  var DebugLogger = {
    /** All available log categories */
    categories: Object.keys(DEFAULT_CATEGORIES),
    /**
     * Check if a category is enabled
     * @param {string} category 
     * @returns {boolean}
     */
    isEnabled(category) {
      return getSettings()[category] ?? false;
    },
    /**
     * Log a message if the category is enabled
     * @param {string} category
     * @param  {...any} args
     */
    log(category, ...args) {
      if (!this.isEnabled(category)) return;
      const message = args.map(
        (a) => typeof a === "object" ? JSON.stringify(a) : String(a)
      ).join(" ");
      logBuffer.push({ time: Date.now(), category, message });
      if (logBuffer.length > MAX_LOG_BUFFER) {
        logBuffer.shift();
      }
      if (typeof window !== "undefined" && originalConsole.log) {
        originalConsole.log(`[${category}]`, ...args);
      } else {
        console.log(`[${category}]`, ...args);
      }
    },
    /**
     * Log a warning if the category is enabled
     * @param {string} category
     * @param  {...any} args
     */
    warn(category, ...args) {
      if (!this.isEnabled(category)) return;
      console.warn(`[${category}]`, ...args);
    },
    /**
     * Log an error - ALWAYS shows regardless of category setting
     * @param {string} category
     * @param  {...any} args
     */
    error(category, ...args) {
      console.error(`[${category}]`, ...args);
      const message = args.map(
        (a) => typeof a === "object" ? JSON.stringify(a) : String(a)
      ).join(" ");
      logBuffer.push({ time: Date.now(), category, type: "error", message });
      if (logBuffer.length > MAX_LOG_BUFFER) {
        logBuffer.shift();
      }
    },
    /**
     * Group related logs together
     * @param {string} category
     * @param {string} label
     */
    group(category, label) {
      if (this.isEnabled(category)) {
        console.group(`[${category}] ${label}`);
      }
    },
    /**
     * End a log group
     * @param {string} category
     */
    groupEnd(category) {
      if (this.isEnabled(category)) {
        console.groupEnd();
      }
    },
    /**
     * Start a timer
     * @param {string} category
     * @param {string} label
     */
    time(category, label) {
      if (this.isEnabled(category) || this.isEnabled("perf")) {
        console.time(`[${category}] ${label}`);
      }
    },
    /**
     * End a timer and log the result
     * @param {string} category
     * @param {string} label
     */
    timeEnd(category, label) {
      if (this.isEnabled(category) || this.isEnabled("perf")) {
        console.timeEnd(`[${category}] ${label}`);
      }
    },
    /**
     * Toggle a category on/off
     * @param {string} category 
     * @param {boolean} [enabled] - If not provided, toggles current value
     * @returns {boolean} New state
     */
    toggle(category, enabled) {
      const settings = getSettings();
      const newValue = enabled ?? !settings[category];
      settings[category] = newValue;
      saveSettings(settings);
      return newValue;
    },
    /**
     * Enable all categories
     */
    enableAll() {
      const settings = getSettings();
      for (const cat of this.categories) {
        settings[cat] = true;
      }
      saveSettings(settings);
    },
    /**
     * Disable all categories
     */
    disableAll() {
      const settings = getSettings();
      for (const cat of this.categories) {
        settings[cat] = false;
      }
      saveSettings(settings);
    },
    /**
     * Get all current settings
     * @returns {Record<string, boolean>}
     */
    getSettings() {
      return { ...getSettings() };
    },
    /**
     * Check if any category is enabled
     * @returns {boolean}
     */
    hasAnyEnabled() {
      const settings = getSettings();
      return Object.values(settings).some((v) => v);
    },
    /**
     * Get the current log buffer
     * @returns {Array<{time: number, category: string, message: string}>}
     */
    getLogBuffer() {
      return logBuffer;
    },
    /**
     * Clear the log buffer
     */
    clearLogBuffer() {
      logBuffer = [];
    },
    /**
     * Gets counts for all categories
     * @returns {Object} { category: count }
     */
    getCategoryCounts() {
      const counts = {};
      this.categories.forEach((cat) => counts[cat] = 0);
      const allCats = new Set(this.categories);
      logBuffer.forEach((entry) => {
        if (entry.category) {
          counts[entry.category] = (counts[entry.category] || 0) + 1;
        } else {
          const match = entry.message && entry.message.match(/^\[([^\]]+)\]/);
          if (match) {
            const potentialCat = match[1].toLowerCase();
            if (allCats.has(potentialCat)) {
              counts[potentialCat] = (counts[potentialCat] || 0) + 1;
            }
          }
        }
      });
      return counts;
    },
    /**
     * Filters log buffer and returns formatted string
     * @param {Record<string, boolean>} [enabledCategories] - Optional filter: only include logs from enabled categories
     * @returns {string}
     */
    getLogText(enabledCategories) {
      let filtered = logBuffer;
      if (enabledCategories) {
        const enabledSet = new Set(
          Object.entries(enabledCategories).filter(([_, enabled]) => enabled).map(([cat]) => cat)
        );
        const allCats = new Set(this.categories);
        filtered = logBuffer.filter((entry) => {
          if (entry.category) {
            return enabledSet.has(entry.category);
          }
          const match = entry.message && entry.message.match(/^\[([^\]]+)\]/);
          if (match) {
            const potentialCat = match[1].toLowerCase();
            if (allCats.has(potentialCat)) {
              return enabledSet.has(potentialCat);
            }
          }
          if (enabledSet.size === 0) return true;
          if (entry.type === "error" || entry.type === "warn") return true;
          return false;
        });
      }
      return filtered.map((entry) => {
        const d = new Date(entry.time);
        const time = d.toLocaleTimeString("en-US", { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
        const tag = entry.category || (entry.type !== "log" ? entry.type : "");
        return tag ? `${time} [${tag}] ${entry.message}` : `${time} ${entry.message}`;
      }).join("\n");
    }
  };
  function logBuildAge() {
    const script = document.querySelector('script[src*="instacalc-core.min.js?v="]');
    if (script) {
      const match = script.src.match(/\?v=(\d+)/);
      if (match) {
        const buildTime = parseInt(match[1]) * 1e3;
        const now2 = Date.now();
        const diff = now2 - buildTime;
        const seconds = Math.floor(diff / 1e3);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        let age;
        if (hours > 0) {
          age = `${hours}h ${minutes % 60}m ago`;
        } else if (minutes > 0) {
          age = `${minutes}m ${seconds % 60}s ago`;
        } else {
          age = `${seconds}s ago`;
        }
        const buildDate = new Date(buildTime).toLocaleTimeString("en-US", { hour12: false });
        DebugLogger.log("Build", `Age: ${age} (built at ${buildDate})`);
      }
    }
  }
  if (typeof window !== "undefined") {
    window.DebugLogger = DebugLogger;
    window.Debug = DebugLogger;
    try {
      const url = new URL(window.location.href);
      const debugParam = url.searchParams.get("debug");
      if (debugParam !== null) {
        interceptConsole();
        if (debugParam === "" || debugParam === "all") {
          const settings = getSettings();
          for (const cat of DebugLogger.categories) {
            if (cat !== "perf") {
              settings[cat] = true;
            }
          }
          saveSettings(settings);
          const enabledCats = Object.entries(settings).filter(([k, v]) => v).map(([k]) => k);
          console.log(`[Debug] Enabled ${enabledCats.length} categories:`, enabledCats.includes("stock") ? "\u2705 stock included" : "\u274C stock MISSING");
          console.log("[Debug] All categories:", DebugLogger.categories);
        } else if (debugParam === "none") {
          DebugLogger.disableAll();
        } else {
          const categories = debugParam.split(",").map((s) => s.trim().toLowerCase());
          categories.forEach((cat) => {
            if (DebugLogger.categories.includes(cat)) {
              DebugLogger.toggle(cat, true);
            }
          });
          console.log(`[Debug] Enabled categories: ${categories.join(", ")}`);
        }
        window.IC_DEBUG_NAMESPACES = DebugLogger.categories.filter((c) => DebugLogger.isEnabled(c));
        window.IC_UI_DEBUG = DebugLogger.isEnabled("ui");
        if (window.perfMetrics) {
          window.perfMetrics.enable();
          console.log("[Debug] Performance metrics enabled");
        }
      }
    } catch (e) {
    }
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", logBuildAge);
    } else {
      logBuildAge();
    }
    Promise.resolve().then(() => __toESM(require_DebugToolsStub())).catch(() => {
    });
    window.perfStats = function() {
      if (!window.perfMetrics) {
        console.log("Performance metrics not available. Enable with ?debug");
        return;
      }
      if (!window.perfMetrics.enabled) {
        console.log("Performance metrics disabled. Enable with ?debug");
        return;
      }
      console.log(window.perfMetrics.format());
    };
  }

  // public/src/history.js
  var Debug = typeof window !== "undefined" && window.Debug || {
    log: () => {
    },
    warn: () => {
    },
    error: (...args) => console.error(...args)
  };
  var safeClone2 = typeof window !== "undefined" && window.safeClone ? window.safeClone : function(obj) {
    try {
      return structuredClone(obj);
    } catch (e) {
      return JSON.parse(JSON.stringify(obj));
    }
  };
  var JSONPatch = {
    // Generate patches between two objects
    compare(oldObj, newObj) {
      const patches = [];
      this._diff(oldObj, newObj, "", patches);
      return patches;
    },
    _diff(oldObj, newObj, path, patches) {
      if (this._deepEqual(oldObj, newObj)) return;
      if (Array.isArray(newObj) && Array.isArray(oldObj)) {
        const maxLen = Math.max(oldObj.length, newObj.length);
        for (let i = 0; i < maxLen; i++) {
          if (i >= newObj.length) {
            patches.push({ op: "remove", path: `${path}/${i}` });
          } else if (i >= oldObj.length) {
            patches.push({ op: "add", path: `${path}/${i}`, value: newObj[i] });
          } else if (!this._deepEqual(oldObj[i], newObj[i])) {
            if (typeof newObj[i] === "object" && newObj[i] !== null && !Array.isArray(newObj[i])) {
              this._diff(oldObj[i], newObj[i], `${path}/${i}`, patches);
            } else {
              patches.push({ op: "replace", path: `${path}/${i}`, value: newObj[i] });
            }
          }
        }
      } else if (typeof newObj === "object" && newObj !== null && typeof oldObj === "object" && oldObj !== null) {
        const allKeys = /* @__PURE__ */ new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);
        for (const key of allKeys) {
          const newPath = path ? `${path}/${key}` : `/${key}`;
          if (!(key in newObj)) {
            patches.push({ op: "remove", path: newPath });
          } else if (!(key in oldObj)) {
            patches.push({ op: "add", path: newPath, value: newObj[key] });
          } else if (!this._deepEqual(oldObj[key], newObj[key])) {
            if (typeof newObj[key] === "object" && newObj[key] !== null) {
              this._diff(oldObj[key], newObj[key], newPath, patches);
            } else {
              patches.push({ op: "replace", path: newPath, value: newObj[key] });
            }
          }
        }
      } else {
        patches.push({ op: "replace", path: path || "/", value: newObj });
      }
    },
    // Apply patches to an object
    applyPatch(obj, patches) {
      const result = safeClone2(obj);
      for (const patch of patches) {
        this._applyOperation(result, patch);
      }
      return result;
    },
    _applyOperation(obj, op) {
      const path = op.path.split("/").filter((p) => p !== "");
      if (op.op === "replace" || op.op === "add") {
        this._setValue(obj, path, op.value);
      } else if (op.op === "remove") {
        this._removeValue(obj, path);
      }
    },
    _setValue(obj, path, value) {
      if (path.length === 0) return obj;
      let current = obj;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (Array.isArray(current) && !isNaN(key)) {
          const index = Number(key);
          while (current.length <= index) {
            current.push(null);
          }
        }
        if (!(key in current) || current[key] === null) {
          current[key] = isNaN(path[i + 1]) ? {} : [];
        }
        current = current[key];
      }
      const lastKey = path[path.length - 1];
      if (Array.isArray(current) && !isNaN(lastKey)) {
        const index = Number(lastKey);
        while (current.length <= index) {
          current.push(null);
        }
      }
      current[lastKey] = value;
    },
    _removeValue(obj, path) {
      if (path.length === 0) return;
      let current = obj;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
        if (!current) return;
      }
      const lastKey = path[path.length - 1];
      if (Array.isArray(current)) {
        current.splice(Number(lastKey), 1);
      } else {
        delete current[lastKey];
      }
    },
    _deepEqual(a, b) {
      return JSON.stringify(a) === JSON.stringify(b);
    }
  };
  var HistoryManager = class {
    /**
     * @param {string} calcId - The calc ID (not the calc object!)
     * HistoryService manages the mapping from calc ID to manager.
     */
    constructor(calcId) {
      this.calcId = calcId;
      this.calc = null;
      this.maxSize = 50;
      this.excludedFields = ["id", "created", "updated", "published", "uid", "history", "_historyManager"];
      this.debounceMs = 2e3;
    }
    /**
     * Update the calc reference. Called by HistoryService.getHistoryManager()
     * because immutable updates create new calc objects on every mutation.
     * Mutates calc to add history if missing (required for captureSnapshot()).
     */
    updateCalcReference(calc) {
      this.calc = calc;
      if (calc && !calc.history) {
        calc.history = {
          stack: [],
          currentIndex: -1
        };
      }
    }
    // Capture current state as snapshot
    captureSnapshot(operation, options = {}) {
      if (!this.calc) return;
      const { force = false } = options;
      Debug.log("history", "\u{1F4F8} captureSnapshot:", operation, "calcId:", this.calcId);
      const currentSnapshot = this._createSnapshot();
      const previousSnapshot = this._getPreviousSnapshot();
      const patch = this._computePatch(previousSnapshot, currentSnapshot);
      if (patch.type === "patch" && patch.patches.length === 0) {
        Debug.log("history", "\u{1F4F8} No changes, skipping snapshot");
        return;
      }
      const now2 = /* @__PURE__ */ new Date();
      const currentTime = now2.toISOString();
      const lastEntry = this.calc.history.stack[this.calc.history.currentIndex];
      const shouldReplaceLastEntry = !force && lastEntry && this.calc.history.currentIndex === this.calc.history.stack.length - 1 && now2 - new Date(lastEntry.timestamp) < this.debounceMs;
      const entry = {
        timestamp: currentTime,
        operation,
        patch
      };
      if (shouldReplaceLastEntry) {
        Debug.log("history", "\u{1F4F8} Replacing last entry (debounce)", "patches:", patch.patches?.length || "full");
        this.calc.history.stack[this.calc.history.currentIndex] = entry;
      } else {
        if (this.calc.history.currentIndex < this.calc.history.stack.length - 1) {
          Debug.log("history", "\u{1F4F8} Truncating forward history from:", this.calc.history.currentIndex);
          this.calc.history.stack = this.calc.history.stack.slice(0, this.calc.history.currentIndex + 1);
        }
        this.calc.history.stack.push(entry);
        this.calc.history.currentIndex++;
        Debug.log("history", "\u{1F4F8} New entry added, index:", this.calc.history.currentIndex, "patches:", patch.patches?.length || "full");
        if (this.calc.history.stack.length > this.maxSize) {
          this.calc.history.stack.shift();
          this.calc.history.currentIndex--;
        }
      }
    }
    // Create snapshot of current state (all fields except excluded)
    _createSnapshot() {
      if (!this.calc) return {};
      const snapshot = {};
      for (const key in this.calc) {
        if (!this.excludedFields.includes(key)) {
          snapshot[key] = safeClone2(this.calc[key]);
        }
      }
      return snapshot;
    }
    _getPreviousSnapshot() {
      if (!this.calc || !this.calc.history) return null;
      const idx = this.calc.history.currentIndex;
      if (idx < 0) return null;
      return this._reconstructSnapshot(idx);
    }
    // Compute JSON Patch between two snapshots
    _computePatch(oldSnapshot, newSnapshot) {
      if (!oldSnapshot) {
        return { type: "full", data: newSnapshot };
      }
      const patches = JSONPatch.compare(oldSnapshot, newSnapshot);
      return { type: "patch", patches };
    }
    // Reconstruct snapshot at given index by applying patches
    _reconstructSnapshot(index) {
      if (!this.calc || !this.calc.history) return {};
      let state = {};
      for (let i = 0; i <= index; i++) {
        const entry = this.calc.history.stack[i];
        if (entry && entry.patch) {
          state = this._applyPatch(state, entry.patch);
        }
      }
      return state;
    }
    // Apply a patch to a state
    _applyPatch(state, patch) {
      if (patch.type === "full") {
        return safeClone2(patch.data);
      }
      return JSONPatch.applyPatch(state, patch.patches);
    }
    // Undo to previous state
    undo() {
      if (!this.calc || !this.calc.history) {
        Debug.log("history", "\u21A9\uFE0F undo: no history");
        return false;
      }
      if (this.calc.history.currentIndex <= 0) {
        Debug.log("history", "\u21A9\uFE0F undo: at start, cannot undo");
        return false;
      }
      this.calc.history.currentIndex--;
      Debug.log("history", "\u21A9\uFE0F undo: restoring index", this.calc.history.currentIndex);
      this._restoreSnapshot(this.calc.history.currentIndex);
      return true;
    }
    // Redo to next state
    redo() {
      if (!this.calc || !this.calc.history) {
        Debug.log("history", "\u21AA\uFE0F redo: no history");
        return false;
      }
      if (this.calc.history.currentIndex >= this.calc.history.stack.length - 1) {
        Debug.log("history", "\u21AA\uFE0F redo: at end, cannot redo");
        return false;
      }
      this.calc.history.currentIndex++;
      Debug.log("history", "\u21AA\uFE0F redo: restoring index", this.calc.history.currentIndex);
      this._restoreSnapshot(this.calc.history.currentIndex);
      return true;
    }
    // Restore calc to snapshot at index
    _restoreSnapshot(index) {
      Debug.log("history", "\u{1F504} _restoreSnapshot:", index);
      const snapshot = this._reconstructSnapshot(index);
      for (const key in snapshot) {
        if (!this.excludedFields.includes(key)) {
          let value = safeClone2(snapshot[key]);
          if (key === "rows" && Array.isArray(value)) {
            value = value.map((row) => row || { text: "", hidden: false });
          }
          this.calc[key] = value;
        }
      }
    }
    // Jump to specific history index
    jumpTo(index) {
      if (!this.calc || !this.calc.history) return false;
      if (index < 0 || index >= this.calc.history.stack.length) {
        return false;
      }
      this.calc.history.currentIndex = index;
      this._restoreSnapshot(index);
      return true;
    }
    // Get current history size in bytes
    getHistorySize() {
      if (!this.calc || !this.calc.history) return 0;
      return JSON.stringify(this.calc.history).length;
    }
    // Get compression ratio (compared to full snapshots)
    getCompressionRatio() {
      if (!this.calc || !this.calc.history) return 0;
      const patchSize = this.getHistorySize();
      const fullSnapshotsSize = this.calc.history.stack.length * JSON.stringify(this._createSnapshot()).length;
      return fullSnapshotsSize > 0 ? 1 - patchSize / fullSnapshotsSize : 0;
    }
    // Get history entries for display (reverse chronological - newest first)
    getHistory() {
      if (!this.calc || !this.calc.history) return [];
      return this.calc.history.stack.map((entry, index) => ({
        index,
        timestamp: entry.timestamp,
        operation: entry.operation
      })).reverse();
    }
    // Convert display index (reverse chronological) to actual stack index
    _displayIndexToActual(displayIndex) {
      if (!this.calc || !this.calc.history) return -1;
      return this.calc.history.stack.length - 1 - displayIndex;
    }
    // Get snapshot at display index (handles reverse chronological order)
    getSnapshot(displayIndex) {
      if (!this.calc || !this.calc.history) return null;
      const actualIndex = this._displayIndexToActual(displayIndex);
      if (actualIndex < 0 || actualIndex >= this.calc.history.stack.length) {
        return null;
      }
      return this._reconstructSnapshot(actualIndex);
    }
    // Can undo?
    canUndo() {
      if (!this.calc || !this.calc.history) return false;
      return this.calc.history.currentIndex > 0;
    }
    // Can redo?
    canRedo() {
      if (!this.calc || !this.calc.history) return false;
      return this.calc.history.currentIndex < this.calc.history.stack.length - 1;
    }
    // Get diff from history snapshot to current live state (handles reverse chronological order)
    getDiffToCurrent(displayIndex) {
      if (!this.calc || !this.calc.history) return null;
      const actualIndex = this._displayIndexToActual(displayIndex);
      if (actualIndex < 0 || actualIndex >= this.calc.history.stack.length) {
        return null;
      }
      const historySnapshot = this._reconstructSnapshot(actualIndex);
      const currentSnapshot = this._createSnapshot();
      const patch = this._computePatch(historySnapshot, currentSnapshot);
      return {
        timestamp: (/* @__PURE__ */ new Date()).toISOString(),
        operation: "diff_to_current",
        patch
      };
    }
    // Restore to a specific display index (handles reverse chronological order)
    restoreToDisplayIndex(displayIndex) {
      if (!this.calc || !this.calc.history) return false;
      const actualIndex = this._displayIndexToActual(displayIndex);
      return this.jumpTo(actualIndex);
    }
  };
  window.HistoryManager = HistoryManager;

  // public/src/main.js
  var import_FirebasePaths = __toESM(require_FirebasePaths());
  var import_CalcType = __toESM(require_CalcType());
  var import_ViewContext = __toESM(require_ViewContext());

  // public/src/stores/OfflineStorage.js
  var Debug2 = typeof window !== "undefined" && window.Debug || { log: () => {
  }, warn: () => {
  }, error: console.error };
  function promisifyRequest(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  var DB_NAME = "instacalc";
  var DB_VERSION = 1;
  var STORES = {
    CALCS: "calcs",
    // All calculator data
    SYNC_QUEUE: "syncQueue",
    // Pending sync operations
    METADATA: "metadata"
    // Last sync time, schema version, etc.
  };
  function openDB() {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === "undefined") {
        reject(new Error("IndexedDB not available"));
        return;
      }
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => {
        Debug2.error("offline", "Failed to open IndexedDB:", request.error);
        reject(request.error);
      };
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORES.CALCS)) {
          const calcsStore = db.createObjectStore(STORES.CALCS, { keyPath: "id" });
          calcsStore.createIndex("type", "type", { unique: false });
          calcsStore.createIndex("folder", "folder", { unique: false });
          calcsStore.createIndex("modified", "modified", { unique: false });
          calcsStore.createIndex("syncStatus", "syncStatus", { unique: false });
        }
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: "id", autoIncrement: true });
          syncStore.createIndex("calcId", "calcId", { unique: false });
          syncStore.createIndex("timestamp", "timestamp", { unique: false });
        }
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          db.createObjectStore(STORES.METADATA, { keyPath: "key" });
        }
        Debug2.log("offline", "IndexedDB schema created/upgraded to version", DB_VERSION);
      };
    });
  }
  var OfflineStorage = class {
    constructor() {
      this._db = null;
      this._dbPromise = null;
    }
    /**
     * Get database connection (lazy initialization)
     * @returns {Promise<IDBDatabase>}
     */
    async getDB() {
      if (this._db) return this._db;
      if (!this._dbPromise) {
        this._dbPromise = openDB().then((db) => {
          this._db = db;
          return db;
        }).catch((err) => {
          this._dbPromise = null;
          throw err;
        });
      }
      return this._dbPromise;
    }
    // ==========================================================================
    // CALC OPERATIONS
    // ==========================================================================
    /**
     * Save a calc to IndexedDB
     * @param {Object} calc - Calc object with id
     * @returns {Promise<void>}
     */
    async saveCalc(calc) {
      if (!calc || !calc.id) {
        Debug2.warn("offline", "Cannot save calc without id");
        return;
      }
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.CALCS, "readwrite").objectStore(STORES.CALCS);
        const calcToSave = {
          ...calc,
          updated: calc.updated || Date.now()
        };
        await promisifyRequest(store.put(calcToSave));
        Debug2.log("offline:verbose", "Saved calc to IndexedDB:", calc.id);
      } catch (err) {
        Debug2.error("offline", "Failed to save calc:", err);
        throw err;
      }
    }
    /**
     * Get a calc by ID
     * @param {string} id - Calc ID
     * @returns {Promise<Object|null>}
     */
    async getCalc(id) {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.CALCS, "readonly").objectStore(STORES.CALCS);
        const result = await promisifyRequest(store.get(id));
        return result || null;
      } catch (err) {
        Debug2.error("offline", "Failed to get calc:", err);
        return null;
      }
    }
    /**
     * Get all calcs
     * @returns {Promise<Object>} Map of id -> calc
     */
    async getAllCalcs() {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.CALCS, "readonly").objectStore(STORES.CALCS);
        const results = await promisifyRequest(store.getAll());
        const calcs = {};
        (results || []).forEach((calc) => {
          calcs[calc.id] = calc;
        });
        return calcs;
      } catch (err) {
        Debug2.error("offline", "Failed to get all calcs:", err);
        return {};
      }
    }
    /**
     * Delete a calc
     * @param {string} id - Calc ID
     * @returns {Promise<void>}
     */
    async deleteCalc(id) {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.CALCS, "readwrite").objectStore(STORES.CALCS);
        await promisifyRequest(store.delete(id));
        Debug2.log("offline", "Deleted calc from IndexedDB:", id);
      } catch (err) {
        Debug2.error("offline", "Failed to delete calc:", err);
        throw err;
      }
    }
    /**
     * Save multiple calcs at once (bulk operation)
     * @param {Object} calcs - Map of id -> calc
     * @returns {Promise<void>}
     */
    async saveAllCalcs(calcs) {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.CALCS, "readwrite").objectStore(STORES.CALCS);
        const promises = Object.values(calcs).map((calc) => {
          const calcToSave = {
            ...calc,
            updated: calc.updated || Date.now()
          };
          return promisifyRequest(store.put(calcToSave));
        });
        await Promise.all(promises);
        Debug2.log("offline", "Bulk saved", Object.keys(calcs).length, "calcs to IndexedDB");
      } catch (err) {
        Debug2.error("offline", "Failed to bulk save calcs:", err);
        throw err;
      }
    }
    // ==========================================================================
    // SYNC QUEUE OPERATIONS
    // ==========================================================================
    /**
     * Add an operation to the sync queue
     * @param {string} operation - 'create', 'update', 'delete'
     * @param {string} calcId - Calc ID
     * @returns {Promise<void>}
     */
    async queueSync(operation, calcId) {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.SYNC_QUEUE, "readwrite").objectStore(STORES.SYNC_QUEUE);
        await promisifyRequest(store.add({ operation, calcId, timestamp: Date.now() }));
        Debug2.log("offline", "Queued sync operation:", operation, calcId);
      } catch (err) {
        Debug2.error("offline", "Failed to queue sync:", err);
      }
    }
    /**
     * Get all pending sync operations
     * @returns {Promise<Array>}
     */
    async getPendingSyncs() {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.SYNC_QUEUE, "readonly").objectStore(STORES.SYNC_QUEUE);
        return await promisifyRequest(store.getAll()) || [];
      } catch (err) {
        Debug2.error("offline", "Failed to get pending syncs:", err);
        return [];
      }
    }
    /**
     * Remove a sync operation from the queue
     * @param {number} id - Sync queue entry ID
     * @returns {Promise<void>}
     */
    async removeSyncEntry(id) {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.SYNC_QUEUE, "readwrite").objectStore(STORES.SYNC_QUEUE);
        await promisifyRequest(store.delete(id));
      } catch (err) {
        Debug2.error("offline", "Failed to remove sync entry:", err);
      }
    }
    /**
     * Clear the entire sync queue
     * @returns {Promise<void>}
     */
    async clearSyncQueue() {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.SYNC_QUEUE, "readwrite").objectStore(STORES.SYNC_QUEUE);
        await promisifyRequest(store.clear());
        Debug2.log("offline", "Cleared sync queue");
      } catch (err) {
        Debug2.error("offline", "Failed to clear sync queue:", err);
      }
    }
    // ==========================================================================
    // METADATA OPERATIONS
    // ==========================================================================
    /**
     * Get metadata value
     * @param {string} key
     * @returns {Promise<any>}
     */
    async getMeta(key) {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.METADATA, "readonly").objectStore(STORES.METADATA);
        const result = await promisifyRequest(store.get(key));
        return result?.value;
      } catch (err) {
        Debug2.error("offline", "Failed to get metadata:", err);
        return null;
      }
    }
    /**
     * Set metadata value
     * @param {string} key
     * @param {any} value
     * @returns {Promise<void>}
     */
    async setMeta(key, value) {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.METADATA, "readwrite").objectStore(STORES.METADATA);
        await promisifyRequest(store.put({ key, value }));
      } catch (err) {
        Debug2.error("offline", "Failed to set metadata:", err);
      }
    }
    // ==========================================================================
    // UTILITY METHODS
    // ==========================================================================
    /**
     * Check if IndexedDB is available
     * @returns {boolean}
     */
    static isAvailable() {
      return typeof indexedDB !== "undefined";
    }
    /**
     * Clear all data (for testing/reset)
     * @returns {Promise<void>}
     */
    async clear() {
      try {
        const db = await this.getDB();
        const storeNames = [STORES.CALCS, STORES.SYNC_QUEUE, STORES.METADATA];
        for (const storeName of storeNames) {
          const store = db.transaction(storeName, "readwrite").objectStore(storeName);
          await promisifyRequest(store.clear());
        }
        Debug2.log("offline", "Cleared all IndexedDB data");
      } catch (err) {
        Debug2.error("offline", "Failed to clear IndexedDB:", err);
      }
    }
    /**
     * Get count of pending sync operations
     * @returns {Promise<number>}
     */
    async getPendingSyncCount() {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORES.SYNC_QUEUE, "readonly").objectStore(STORES.SYNC_QUEUE);
        return await promisifyRequest(store.count());
      } catch (err) {
        return 0;
      }
    }
  };
  var offlineStorage = new OfflineStorage();
  if (typeof window !== "undefined") {
    window.OfflineStorage = OfflineStorage;
    window.offlineStorage = offlineStorage;
  }

  // public/src/stores/FirebaseOptional.js
  var Debug3 = typeof window !== "undefined" && window.Debug || { log: () => {
  }, warn: () => {
  }, error: console.error };
  function showToast(message, type = "default") {
    if (typeof window !== "undefined" && window.showToast) {
      window.showToast(message, type);
    }
  }
  function getOfflineStorage() {
    return typeof window !== "undefined" ? window.offlineStorage : null;
  }
  function isFirebaseAvailable() {
    return typeof firebase !== "undefined" && firebase.apps && firebase.apps.length > 0;
  }
  function isOnline() {
    return navigator.onLine;
  }
  function getDatabase() {
    if (!isFirebaseAvailable()) {
      return null;
    }
    try {
      return firebase.database();
    } catch (e) {
      Debug3.warn("firebase", "Database not available:", e.message);
      return null;
    }
  }
  var FirebaseOptional = class {
    constructor() {
      this._connectionListeners = [];
      this._connected = false;
      this._initialized = false;
    }
    /**
     * Initialize Firebase connection monitoring
     */
    init() {
      if (this._initialized) return;
      this._initialized = true;
      const db = getDatabase();
      if (!db) {
        Debug3.log("firebase", "Firebase not available - running in offline mode");
        return;
      }
      try {
        const connectedRef = db.ref(".info/connected");
        connectedRef.on("value", (snap) => {
          this._connected = snap.val() === true;
          Debug3.log("firebase", "Connection state:", this._connected ? "connected" : "disconnected");
          this._notifyConnectionListeners();
        });
      } catch (e) {
        Debug3.warn("firebase", "Could not monitor connection:", e.message);
      }
    }
    /**
     * Check if Firebase is available and connected
     * @returns {boolean}
     */
    isAvailable() {
      return isFirebaseAvailable() && this._connected;
    }
    /**
     * Check if Firebase SDK is loaded (may not be connected)
     * @returns {boolean}
     */
    isLoaded() {
      return isFirebaseAvailable();
    }
    /**
     * Add a connection state listener
     * @param {Function} callback - Called with (connected: boolean)
     */
    onConnectionChange(callback) {
      this._connectionListeners.push(callback);
      callback(this._connected);
    }
    /**
     * Remove a connection state listener
     * @param {Function} callback
     */
    offConnectionChange(callback) {
      this._connectionListeners = this._connectionListeners.filter((cb) => cb !== callback);
    }
    _notifyConnectionListeners() {
      this._connectionListeners.forEach((cb) => {
        try {
          cb(this._connected);
        } catch (e) {
          Debug3.error("firebase", "Connection listener error:", e);
        }
      });
    }
    // ==========================================================================
    // READ OPERATIONS (return null if unavailable)
    // ==========================================================================
    /**
     * Get data from Firebase path
     * @param {string} path - Firebase path
     * @returns {Promise<any|null>} Data or null if unavailable
     */
    async get(path) {
      const db = getDatabase();
      if (!db || !isOnline()) {
        Debug3.log("firebase", "get() skipped - offline or unavailable");
        return null;
      }
      try {
        const snapshot = await db.ref(path).once("value");
        return snapshot.val();
      } catch (e) {
        Debug3.warn("firebase", "get() failed:", path, e.message);
        return null;
      }
    }
    /**
     * Subscribe to data changes
     * @param {string} path - Firebase path
     * @param {Function} callback - Called with (value, error)
     * @returns {Function} Unsubscribe function
     */
    subscribe(path, callback) {
      const db = getDatabase();
      if (!db) {
        Debug3.log("firebase", "subscribe() skipped - unavailable");
        setTimeout(() => callback(null, null), 0);
        return () => {
        };
      }
      const ref = db.ref(path);
      const handler = (snapshot) => {
        callback(snapshot.val(), null);
      };
      const errorHandler = (error) => {
        Debug3.warn("firebase", "subscribe() error:", path, error.message);
        callback(null, error);
      };
      ref.on("value", handler, errorHandler);
      return () => {
        ref.off("value", handler);
      };
    }
    // ==========================================================================
    // WRITE OPERATIONS (queue if unavailable)
    // ==========================================================================
    /**
     * Set data at Firebase path
     * @param {string} path - Firebase path
     * @param {any} value - Data to set
     * @returns {Promise<boolean>} True if successful, false if queued/failed
     */
    async set(path, value) {
      const db = getDatabase();
      if (!db || !isOnline()) {
        Debug3.log("firebase", "set() queued - offline or unavailable");
        await this._queueOperation("set", path, value);
        return false;
      }
      try {
        await db.ref(path).set(value);
        Debug3.log("firebase", "set() success:", path);
        return true;
      } catch (e) {
        Debug3.warn("firebase", "set() failed, queuing:", path, e.message);
        await this._queueOperation("set", path, value);
        return false;
      }
    }
    /**
     * Update data at Firebase path
     * @param {string} path - Firebase path
     * @param {object} updates - Updates to apply
     * @returns {Promise<boolean>} True if successful, false if queued/failed
     */
    async update(path, updates) {
      const db = getDatabase();
      if (!db || !isOnline()) {
        Debug3.log("firebase", "update() queued - offline or unavailable");
        await this._queueOperation("update", path, updates);
        return false;
      }
      try {
        await db.ref(path).update(updates);
        Debug3.log("firebase", "update() success:", path);
        return true;
      } catch (e) {
        Debug3.warn("firebase", "update() failed, queuing:", path, e.message);
        await this._queueOperation("update", path, updates);
        return false;
      }
    }
    /**
     * Remove data at Firebase path
     * @param {string} path - Firebase path
     * @returns {Promise<boolean>} True if successful, false if queued/failed
     */
    async remove(path) {
      const db = getDatabase();
      if (!db || !isOnline()) {
        Debug3.log("firebase", "remove() queued - offline or unavailable");
        await this._queueOperation("remove", path, null);
        return false;
      }
      try {
        await db.ref(path).remove();
        Debug3.log("firebase", "remove() success:", path);
        return true;
      } catch (e) {
        Debug3.warn("firebase", "remove() failed, queuing:", path, e.message);
        await this._queueOperation("remove", path, null);
        return false;
      }
    }
    // ==========================================================================
    // SYNC QUEUE
    // ==========================================================================
    /**
     * Queue an operation for later sync
     * @private
     */
    async _queueOperation(operation, path, value) {
      const storage = getOfflineStorage();
      if (!storage) return;
      try {
        await storage.queueSync(operation, path);
        await storage.setMeta(`pending_${path}`, {
          operation,
          path,
          value,
          timestamp: Date.now()
        });
        const count = await storage.getPendingSyncCount();
        if (count === 1) {
          showToast("Offline - changes saved locally", "info");
        }
      } catch (e) {
        Debug3.error("firebase", "Failed to queue operation:", e);
      }
    }
    /**
     * Process queued operations (call when online)
     * @returns {Promise<number>} Number of operations processed
     */
    async processSyncQueue() {
      if (!isOnline() || !isFirebaseAvailable()) {
        return 0;
      }
      const storage = getOfflineStorage();
      if (!storage) return 0;
      const pending = await storage.getPendingSyncs();
      if (pending.length === 0) return 0;
      Debug3.log("firebase", `Syncing ${pending.length} queued changes...`);
      let processed = 0;
      for (const entry of pending) {
        try {
          const meta = await storage.getMeta(`pending_${entry.calcId}`);
          if (meta) {
            const { operation, path, value } = meta;
            const db = getDatabase();
            if (db) {
              switch (operation) {
                case "set":
                  await db.ref(path).set(value);
                  break;
                case "update":
                  await db.ref(path).update(value);
                  break;
                case "remove":
                  await db.ref(path).remove();
                  break;
              }
            }
            await storage.setMeta(`pending_${entry.calcId}`, null);
          }
          await storage.removeSyncEntry(entry.id);
          processed++;
        } catch (e) {
          Debug3.warn("firebase", "Failed to process queued operation:", e);
        }
      }
      if (processed > 0) {
        Debug3.log("firebase", "Processed", processed, "queued operations");
        showToast(`Synced ${processed} change${processed > 1 ? "s" : ""}`, "success");
      }
      return processed;
    }
  };
  var firebaseOptional = new FirebaseOptional();
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      Debug3.log("firebase", "Online - processing sync queue");
      firebaseOptional.processSyncQueue();
    });
    window.addEventListener("load", () => {
      setTimeout(() => {
        firebaseOptional.init();
      }, 100);
    });
  }
  if (typeof window !== "undefined") {
    window.FirebaseOptional = FirebaseOptional;
    window.firebaseOptional = firebaseOptional;
    window.isFirebaseAvailable = isFirebaseAvailable;
  }

  // public/src/main.js
  var import_CalcStore = __toESM(require_CalcStore());
  var import_SelectionController = __toESM(require_SelectionController());

  // public/src/stores/ResultsStore.js
  var EMPTY_ARRAY = Object.freeze([]);
  var ResultsStore = class {
    constructor() {
      this._results = {};
      this._meta = {};
      this._listeners = /* @__PURE__ */ new Set();
      this._enableValidation = typeof process !== "undefined" && false;
    }
    /**
     * Quick check if results are equivalent (same length + same displayed values).
     * Avoids expensive deep comparison by only checking key display properties.
     * @param {any[]} oldResults
     * @param {any[]} newResults
     * @returns {boolean}
     */
    _resultsEqual(oldResults, newResults, oldMeta, newMeta) {
      if (!oldResults || !newResults) return false;
      if (oldMeta?.locale !== newMeta?.locale) return false;
      if (oldMeta?.rounding !== newMeta?.rounding) return false;
      if (oldResults.length !== newResults.length) return false;
      for (let i = 0; i < oldResults.length; i++) {
        const oldR = oldResults[i];
        const newR = newResults[i];
        if (oldR?.final_result !== newR?.final_result) return false;
        if (oldR?.eval_result !== newR?.eval_result) return false;
      }
      return true;
    }
    /**
     * Set results for a specific calc.
     * Includes validation guards to detect synchronization bugs.
     * Optimized: skips notification if results haven't changed.
     * @param {string} calcId
     * @param {any[]} results
     */
    set(calcId, results) {
      if (!calcId) {
        console.warn("[ResultsStore] Attempted to set results with no calcId");
        return;
      }
      if (!Array.isArray(results)) {
        console.error("[ResultsStore] Results must be an array", {
          calcId,
          resultsType: typeof results,
          results
        });
        return;
      }
      if (this._enableValidation && typeof window !== "undefined" && window.calcStore) {
        const calc = window.calcStore.get(calcId);
        if (calc && calc.rows && calc.rows.length !== results.length) {
          console.warn("[ResultsStore] \u26A0\uFE0F Row count mismatch detected", {
            calcId: calcId.slice(0, 8),
            calcName: calc.name,
            calcRows: calc.rows.length,
            resultsCount: results.length,
            diff: calc.rows.length - results.length,
            stack: new Error().stack?.split("\n").slice(2, 5).join("\n")
          });
        }
      }
      const oldResults = this._results[calcId];
      const oldMeta = this._meta[calcId];
      const newMeta = typeof window !== "undefined" && window.calcStore?.get ? {
        locale: window.calcStore.get(calcId)?.locale,
        rounding: window.calcStore.get(calcId)?.rounding
      } : void 0;
      if (this._resultsEqual(oldResults, results, oldMeta, newMeta)) {
        if (typeof window !== "undefined" && window.Debug) {
          window.Debug.log("store:verbose", "[ResultsStore] SKIP (results unchanged)", calcId?.slice(0, 8));
        }
        return;
      }
      this._results[calcId] = results;
      if (newMeta) {
        this._meta[calcId] = newMeta;
      }
      this._listeners.forEach((fn) => fn(calcId));
      if (typeof window !== "undefined" && window.Debug) {
        window.Debug.log("store:verbose", "[ResultsStore] SET + NOTIFY", calcId?.slice(0, 8), `(${this._listeners.size} listeners)`);
      }
    }
    /**
     * Get results for a specific calc.
     * @param {string} calcId
     * @returns {any[]}
     */
    get(calcId) {
      return this._results[calcId] || EMPTY_ARRAY;
    }
    /**
     * Subscribe to result changes.
     * Callback receives the calcId that was updated.
     * @param {(calcId: string) => void} callback
     * @returns {() => void} Unsubscribe function
     */
    subscribe(callback) {
      this._listeners.add(callback);
      return () => this._listeners.delete(callback);
    }
    /**
     * Clear results for a specific calc.
     * @param {string} calcId
     */
    clear(calcId) {
      delete this._results[calcId];
      this._listeners.forEach((fn) => fn(calcId));
    }
    /**
     * Clear all results.
     */
    clearAll() {
      const calcIds = Object.keys(this._results);
      this._results = {};
      calcIds.forEach((calcId) => {
        this._listeners.forEach((fn) => fn(calcId));
      });
    }
  };
  var resultsStore = new ResultsStore();
  if (typeof window !== "undefined") {
    window.resultsStore = resultsStore;
  }

  // public/src/main.js
  var import_CalcModel = __toESM(require_CalcModel());
  var import_action = __toESM(require_action());
  var import_AuthService = __toESM(require_AuthService());
  var import_CalcMutations = __toESM(require_CalcMutations());
  var import_ExternalDataService = __toESM(require_ExternalDataService());
  var import_HistoryService = __toESM(require_HistoryService());
  var import_PersistenceService = __toESM(require_PersistenceService());
  var import_PromoteService = __toESM(require_PromoteService());
  var import_UIService = __toESM(require_UIService());
  var import_RegisteredShortcuts = __toESM(require_RegisteredShortcuts());

  // public/src/stores/ExternalDataCache.js
  function getDebug() {
    return typeof window !== "undefined" && window.Debug || { log: () => {
    }, warn: () => {
    }, error: console.error };
  }
  var Debug4 = {
    log: (...args) => getDebug().log(...args),
    warn: (...args) => getDebug().warn(...args),
    error: (...args) => getDebug().error(...args)
  };
  var DB_NAME2 = "instacalc-cache";
  var DB_VERSION2 = 1;
  var STORE_NAME = "entries";
  function promisifyRequest2(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  function openCacheDB() {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === "undefined") {
        reject(new Error("IndexedDB not available"));
        return;
      }
      const request = indexedDB.open(DB_NAME2, DB_VERSION2);
      request.onerror = () => {
        Debug4.error("loader", "ExternalDataCache: Failed to open IndexedDB:", request.error);
        reject(request.error);
      };
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
          store.createIndex("type", "type", { unique: false });
          store.createIndex("updated", "updated", { unique: false });
        }
        Debug4.log("loader", "ExternalDataCache: IndexedDB schema created v" + DB_VERSION2);
      };
    });
  }
  var ExternalDataCache = class {
    constructor() {
      this._db = null;
      this._dbPromise = null;
      this._initialized = false;
      this._syncDebounceTimer = null;
      this.caches = {
        stock: {},
        import: {},
        import_calc: {}
      };
      this._syncVersion = 0;
      this._lastAckedVersion = 0;
      this._pendingSyncVersion = 0;
      this._dirty = true;
      this._syncHistory = [];
      this._lastSync = null;
    }
    /**
     * Get database connection (lazy initialization)
     */
    async getDB() {
      if (this._db) return this._db;
      if (!this._dbPromise) {
        this._dbPromise = openCacheDB().then((db) => {
          this._db = db;
          return db;
        }).catch((err) => {
          this._dbPromise = null;
          throw err;
        });
      }
      return this._dbPromise;
    }
    /**
     * Check if cache is initialized (public accessor for _initialized)
     */
    isInitialized() {
      return this._initialized;
    }
    /**
     * Initialize - load from IndexedDB and wire up
     */
    async init() {
      console.log(
        "[STOCK-DIRECT] ExternalDataCache.init() called, Debug.log stock enabled?",
        typeof window !== "undefined" && window.Debug?.isEnabled?.("stock")
      );
      Debug4.log("stock", "\u{1F4C2} ExternalDataCache initializing...");
      try {
        await this._hydrate();
        this._initialized = true;
        if (window.Action?.fn) {
          window.Action.fn.stock_cache = this.caches.stock;
          window.Action.fn.import_cache = this.caches.import;
          window.Action.fn.import_calc_cache = this.caches.import_calc;
        }
        const stockKeys = Object.keys(this.caches.stock);
        Debug4.log("stock", `\u2705 ExternalDataCache initialized: ${stockKeys.length} stocks [${stockKeys.join(",")}]`);
        if (stockKeys.length > 0) {
          const prices = stockKeys.slice(0, 5).map((k) => `${k}=$${this.caches.stock[k]?.price}`).join(", ");
          Debug4.log("stock", `\u{1F4B0} Stock prices: ${prices}${stockKeys.length > 5 ? "..." : ""}`);
        }
        Debug4.log("loader", "ExternalDataCache: initialized", {
          stock: stockKeys.length,
          import: Object.keys(this.caches.import).length,
          import_calc: Object.keys(this.caches.import_calc).length
        });
        this.syncToWorker();
      } catch (err) {
        Debug4.error("stock", "\u274C ExternalDataCache init failed:", err);
        Debug4.error("loader", "ExternalDataCache: init failed:", err);
        this._initialized = true;
      }
    }
    /**
     * Get a value from cache (sync, from memory)
     */
    get(type, key) {
      return this.caches[type]?.[key];
    }
    /**
     * Set a value in cache (persists to IndexedDB, syncs to worker)
     */
    async set(type, key, value) {
      if (!this.caches[type]) {
        this.caches[type] = {};
      }
      this.caches[type][key] = value;
      this._dirty = true;
      if (type === "stock") {
        Debug4.log("stock", `\u{1F4E5} Stock cached: ${key} = $${value?.price} (loaded=${value?.loaded})`);
      }
      this._persistEntry(type, key, value).catch((err) => {
        Debug4.warn("loader", "ExternalDataCache: persist failed:", err);
      });
      this._debouncedSync();
    }
    /**
     * Remove a value from cache
     */
    async remove(type, key) {
      if (this.caches[type]) {
        delete this.caches[type][key];
        this._dirty = true;
      }
      try {
        const db = await this.getDB();
        const store = db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME);
        await promisifyRequest2(store.delete(`${type}:${key}`));
      } catch (err) {
        Debug4.warn("loader", "ExternalDataCache: remove failed:", err);
      }
      this._debouncedSync();
    }
    /**
     * Clear all entries of a specific type
     */
    async clearType(type) {
      this.caches[type] = {};
      this._dirty = true;
      try {
        const db = await this.getDB();
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const index = store.index("type");
        const request = index.openCursor(IDBKeyRange.only(type));
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            store.delete(cursor.primaryKey);
            cursor.continue();
          }
        };
        await new Promise((resolve, reject) => {
          tx.oncomplete = resolve;
          tx.onerror = () => reject(tx.error);
        });
      } catch (err) {
        Debug4.warn("loader", "ExternalDataCache: clearType failed:", err);
      }
      this._debouncedSync();
    }
    /**
     * Sync all caches to worker (unified method)
     *
     * HYBRID ARCHITECTURE:
     * - Sends lightweight CACHE_UPDATED notification (no data)
     * - Worker reads from IndexedDB directly
     * - Falls back to full SYNC_CACHES for backward compatibility
     *
     * Replaces IC.WORKER_SYNC_STOCK_CACHE()
     * Includes version number to prevent out-of-order sync issues
     * Optimized: skips sync if caches haven't changed (dirty flag)
     */
    syncToWorker(force = false) {
      if (!window.IC?.WORKER_LOADED || !window.IC?.WORKER) {
        return;
      }
      if (!force && !this._dirty) {
        Debug4.log("loader:verbose", "\u{1F4E4} SYNC_CACHES SKIP (not dirty)");
        return;
      }
      this._syncVersion++;
      const version = this._syncVersion;
      this._pendingSyncVersion = version;
      const currencies = typeof window !== "undefined" && window.InstaCalc?.Unit?.baseCurrencies || {};
      const currencyRateTimestamp = typeof window !== "undefined" && window.InstaCalc?.Unit?.currencyRateTimestamp || null;
      const currencyKeys = Object.keys(currencies);
      const syncInfo = {
        version,
        time: Date.now(),
        stock: Object.keys(this.caches.stock),
        import: Object.keys(this.caches.import),
        import_calc: Object.keys(this.caches.import_calc),
        currencies: currencyKeys.length
      };
      this._lastSync = syncInfo;
      this._syncHistory.push(syncInfo);
      if (this._syncHistory.length > 20) this._syncHistory.shift();
      if (currencyKeys.length === 0) {
        Debug4.log("loader", "\u23F3 Syncing to worker before currencies loaded - will re-sync when network fetch completes");
      }
      Debug4.log("loader", `\u{1F4E4} CACHE_UPDATED v${version} \u2192`, syncInfo);
      window.IC.WORKER.postMessage({
        type: "CACHE_UPDATED",
        version
      });
      const stockCount = Object.keys(this.caches.stock).length;
      Debug4.log("loader", `\u{1F4E4} SYNC_CACHES v${version} \u2192 stocks=${stockCount}, currencies=${currencyKeys.length}`);
      window.IC.WORKER.postMessage({
        type: "SYNC_CACHES",
        version,
        caches: {
          stock_cache: this.caches.stock,
          import_cache: this.caches.import,
          import_calc_cache: this.caches.import_calc,
          // Include currencies in the same sync mechanism as stocks
          currencies,
          // Include rate timestamp for tooltip display in worker
          currencyRateTimestamp
        },
        ticker_list: this._tickerSet ? Array.from(this._tickerSet) : null
      });
    }
    /**
     * Handle SYNC_ACK from worker - clears dirty flag when sync is confirmed
     * This prevents race conditions when multiple stocks load rapidly
     * @param {number} version - The version being acknowledged
     */
    handleSyncAck(version) {
      Debug4.log("loader", `\u{1F4EC} SYNC_ACK v${version} received (pending=${this._pendingSyncVersion})`);
      if (version > this._lastAckedVersion) {
        this._lastAckedVersion = version;
      }
      if (version >= this._pendingSyncVersion) {
        this._dirty = false;
        Debug4.log("loader", `\u2705 SYNC_ACK v${version} - dirty flag cleared`);
      } else {
        Debug4.log("loader", `\u23F3 SYNC_ACK v${version} - newer sync pending (v${this._pendingSyncVersion})`);
      }
    }
    /**
     * Handle REQUEST_FULL_SYNC from worker - sends full cache when worker's IndexedDB failed
     */
    handleFullSyncRequest() {
      Debug4.log("loader", "\u{1F4E8} Worker requested full sync (IndexedDB failed)");
      this._dirty = true;
      this.syncToWorker(true);
    }
    /**
     * Debounced sync - batches rapid changes
     */
    _debouncedSync() {
      if (this._syncDebounceTimer) {
        clearTimeout(this._syncDebounceTimer);
      }
      this._syncDebounceTimer = setTimeout(() => {
        this.syncToWorker();
        this._syncDebounceTimer = null;
      }, 50);
    }
    /**
     * Load all cached data from IndexedDB
     */
    async _hydrate() {
      try {
        const db = await this.getDB();
        const store = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME);
        const entries = await promisifyRequest2(store.getAll());
        let loaded = { stock: 0, import: 0, import_calc: 0 };
        for (const entry of entries) {
          const { type, key, value, updated } = entry;
          const maxAge = type === "stock" ? 24 * 60 * 60 * 1e3 : Infinity;
          const age = Date.now() - (updated || 0);
          if (age > maxAge) {
            if (value && typeof value === "object") {
              value._stale = true;
              value._age = age;
            }
          }
          if (!this.caches[type]) {
            this.caches[type] = {};
          }
          this.caches[type][key] = value;
          loaded[type]++;
        }
        Debug4.log("loader", "ExternalDataCache: hydrated from IndexedDB", loaded);
      } catch (err) {
        Debug4.warn("loader", "ExternalDataCache: hydration failed:", err);
      }
    }
    /**
     * Persist a single entry to IndexedDB
     */
    async _persistEntry(type, key, value) {
      if (!this._initialized) return;
      const db = await this.getDB();
      const store = db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME);
      const entry = {
        id: `${type}:${key}`,
        type,
        key,
        value,
        updated: Date.now()
      };
      await promisifyRequest2(store.put(entry));
      Debug4.log("loader", `ExternalDataCache: persisted ${type}:${key}`);
    }
    /**
     * Get debug info for the debug panel
     */
    getDebugInfo() {
      return {
        initialized: this._initialized,
        dirty: this._dirty,
        syncVersion: this._syncVersion,
        lastAckedVersion: this._lastAckedVersion,
        lastSync: this._lastSync,
        syncHistory: this._syncHistory,
        caches: {
          stock: Object.keys(this.caches.stock),
          stockDetails: this.caches.stock,
          import: Object.keys(this.caches.import),
          import_calc: Object.keys(this.caches.import_calc)
        }
      };
    }
    // === Ticker List Support ===
    // In-memory Set of known tickers (lazy-loaded)
    _tickerSet = null;
    _tickerLoadPromise = null;
    /**
     * Load ticker list from /data/tickers.json (lazy, cached)
     * @returns {Promise<Set<string>>}
     */
    async loadTickerList() {
      if (this._tickerSet) return this._tickerSet;
      if (this._tickerLoadPromise) return this._tickerLoadPromise;
      this._tickerLoadPromise = (async () => {
        try {
          const data = await this.fetchAsset("tickers", "list", "/data/tickers.json", {
            transform: (json) => [...json.companies || [], ...json.etfs || [], ...json.crypto || []]
          });
          this._tickerSet = new Set(data.map((t) => t.toUpperCase()));
          Debug4.log("loader", `ExternalDataCache: loaded ${this._tickerSet.size} tickers`);
          return this._tickerSet;
        } catch (err) {
          console.warn("[Stock] Ticker list failed to load - stock detection may be limited:", err.message);
          Debug4.warn("loader", "ExternalDataCache: ticker list load failed:", err);
          this._tickerSet = /* @__PURE__ */ new Set();
          return this._tickerSet;
        } finally {
          this._tickerLoadPromise = null;
        }
      })();
      return this._tickerLoadPromise;
    }
    /**
     * Check if a symbol is a known stock ticker (sync check)
     *
     * DESIGN:
     * - Explicit $TICKER syntax → validates against full ticker list (~10,000 tickers)
     * - Bare symbol (no $) → only auto-detects FAMOUS_STOCK_TICKERS (~100 tickers)
     *
     * This prevents "DDD" from triggering stock lookup (vim delete shortcut),
     * while still allowing "$DDD" to explicitly look up 3D Systems stock.
     *
     * CRITICAL: Fiat currencies ALWAYS take precedence over stocks.
     *
     * @param {string} symbol
     * @returns {boolean}
     */
    isPossibleStock(symbol) {
      if (!symbol || symbol.length < 1) {
        Debug4.log("stock:verbose", `isPossibleStock("${symbol}"): \u274C empty/null symbol`);
        return false;
      }
      let upperSymbol = symbol.toUpperCase();
      let isExplicit = false;
      if (upperSymbol.startsWith("STOCK_")) {
        upperSymbol = upperSymbol.substring(6).replace(/_/g, ".");
        isExplicit = true;
        Debug4.log("stock:verbose", `isPossibleStock: explicit $TICKER \u2192 "${upperSymbol}"`);
      }
      const keywords = typeof IC_KEYWORDS !== "undefined" ? IC_KEYWORDS : typeof window !== "undefined" ? window.IC_KEYWORDS : null;
      const isFiat = keywords && keywords.isFiatCurrency ? keywords.isFiatCurrency(upperSymbol) : false;
      if (isFiat) {
        Debug4.log("stock:verbose", `isPossibleStock("${upperSymbol}"): \u274C fiat currency (protected)`);
        return false;
      }
      if (isExplicit) {
        if (this._tickerSet) {
          const found = this._tickerSet.has(upperSymbol);
          Debug4.log("stock:verbose", `isPossibleStock("${upperSymbol}"): ${found ? "\u2705" : "\u274C"} explicit (ticker list)`);
          return found;
        }
        const validFormat = /^[A-Z]{1,5}(\.[A-Z])?$/.test(upperSymbol);
        Debug4.log("stock:verbose", `isPossibleStock("${upperSymbol}"): ${validFormat ? "\u2705" : "\u274C"} explicit (format check, list not loaded)`);
        return validFormat;
      }
      const isFamous = keywords?.isFamousTicker?.(upperSymbol);
      if (isFamous) {
        Debug4.log("stock", `isPossibleStock("${upperSymbol}"): \u2705 famous ticker (auto-detect)`);
        this.loadTickerList().catch(() => {
        });
        return true;
      }
      Debug4.log("stock:verbose", `isPossibleStock("${upperSymbol}"): \u274C not famous (use $${upperSymbol} for explicit lookup)`);
      return false;
    }
    /**
     * Check if a symbol is in the loaded ticker list (authoritative check).
     * Unlike isPossibleStock, this does NOT check envIsDefinedSearchAll,
     * so it returns true for stocks even after they're registered as currency units.
     * @param {string} symbol
     * @returns {boolean}
     */
    isKnownTicker(symbol) {
      if (!symbol || !this._tickerSet) {
        return false;
      }
      let upperSymbol = symbol.toUpperCase();
      if (upperSymbol.startsWith("STOCK_")) {
        upperSymbol = upperSymbol.substring(6).replace(/_/g, ".");
      }
      return this._tickerSet.has(upperSymbol);
    }
    /**
     * Fetch a JSON asset from URL with caching
     * @param {string} type - Cache type (e.g., 'tickers')
     * @param {string} key - Cache key (e.g., 'list')
     * @param {string} url - URL to fetch from
     * @param {object} options - { transform?: (data) => any, forceRefresh?: boolean }
     * @returns {Promise<any>} The cached or fetched data
     */
    async fetchAsset(type, key, url, options = {}) {
      if (!options.forceRefresh) {
        const cached = this.get(type, key);
        if (cached) {
          Debug4.log("loader", `ExternalDataCache: ${type}:${key} loaded from cache`);
          return cached;
        }
      }
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${url}: ${response.status}`);
        }
        let data = await response.json();
        if (options.transform) {
          data = options.transform(data);
        }
        await this.set(type, key, data);
        Debug4.log("loader", `ExternalDataCache: ${type}:${key} fetched from ${url}`);
        return data;
      } catch (err) {
        Debug4.error("loader", `ExternalDataCache: fetchAsset failed for ${url}:`, err);
        throw err;
      }
    }
  };
  var externalDataCache = new ExternalDataCache();
  if (typeof window !== "undefined") {
    window.externalDataCache = externalDataCache;
  }
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { ExternalDataCache, externalDataCache };
  }

  // public/src/main.js
  var import_UrlManager = __toESM(require_UrlManager());
  var import_calc_loader = __toESM(require_calc_loader());

  // public/src/router.js
  var Debug5 = typeof window !== "undefined" && window.Debug || {
    log: () => {
    },
    warn: () => {
    },
    error: (...args) => console.error(...args)
  };
  var Router2 = class {
    // View type constants - delegates to ViewContext
    // String-based for simplicity, matches ViewContext values
    static VIEW = {
      HOME: "home",
      SINGLE: "single",
      EMBED: "embed",
      GALLERY: "gallery",
      URL_CALC: "url_calc"
      // Stateless URL-defined calc
    };
    /**
     * Check if current URL is home page
     * Delegates to ViewContext.isHome()
     */
    static isHome() {
      if (!window.ViewContext) {
        throw new Error("ViewContext not loaded - check script order");
      }
      return window.ViewContext.isHome();
    }
    /**
     * Returns true for shared/single calc views (sidebar should be collapsed)
     * Delegates to ViewContext.isSharedView()
     */
    static isSharedView() {
      if (!window.ViewContext) {
        throw new Error("ViewContext not loaded - check script order");
      }
      return window.ViewContext.isSharedView();
    }
    static goHome() {
      window.location = "/";
    }
    /**
     * Get current view context as string
     * Returns ViewContext value or URL_CALC for stateless calcs
     */
    static getLayoutType() {
      if (!window.ViewContext) {
        throw new Error("ViewContext not loaded - check script order");
      }
      if (window.ViewContext.isUrlCalc()) {
        return this.VIEW.URL_CALC;
      }
      return window.ViewContext.fromUrl();
    }
    static getCalcIdFromURL() {
      const match = window.location.href.match(/[/]([0-9]+)/);
      return match ? match[1] : null;
    }
    static setupUserLogin() {
      try {
        Debug5.log("router", "setupUserLogin called");
        const StoreClass = window.Store;
        if (typeof StoreClass === "undefined") {
          Debug5.warn("router", "Store not yet available, deferring setupUserLogin");
          return;
        }
        window.UserStore = new StoreClass();
        window.UserStore.sub((user) => {
          if (user?.uid) {
            document.querySelectorAll(".if-logged-in").forEach((el) => el.style.display = "");
            document.querySelectorAll(".if-logged-out").forEach((el) => el.style.display = "none");
          } else {
            document.querySelectorAll(".if-logged-out").forEach((el) => el.style.display = "");
            document.querySelectorAll(".if-logged-in").forEach((el) => el.style.display = "none");
          }
        });
        if (window.Action && window.Action.checkUserLogin) {
          window.Action.checkUserLogin((user) => window.UserStore.pub(user));
        }
      } catch (e) {
        console.error("[Router.setupUserLogin] Error:", e);
      }
    }
    /**
     * Main routing entry point.
     * Delegates to CalcLoader for all calc loading.
     */
    static route() {
      Debug5.log("router", "route START:", window.location.href);
      if (window.location.pathname?.startsWith("/demo/")) {
        if (document.querySelector("App")) {
          Debug5.log("router", "Demo page with App component");
          window.CalcLoader?.loadCalc();
        }
        return;
      }
      if (window.location.pathname?.match("/shared")) {
        this._loadSharedGallery();
        return;
      }
      if (window.CalcLoader?.loadCalc) {
        window.CalcLoader.loadCalc().catch((err) => {
          console.error("[Router.route] CalcLoader error:", err);
        });
      } else {
        console.error("[Router.route] CalcLoader not available");
      }
    }
    /**
     * Load the legacy shared gallery page (special case, not a calc view)
     */
    static _loadSharedGallery() {
      const el = document.querySelector("SharedGallery");
      if (!el) return;
      fetch("https://gist.githubusercontent.com/kazad/615e5b87d158d5bf98508b335973ff3c/raw/instacalc.gallery.html").then((response) => response.text()).then((html) => {
        el.innerHTML = html;
        const wrap = document.getElementById("shared-gallery-wrap");
        if (wrap) wrap.classList.remove("hidden");
      }).catch((err) => {
        Debug5.warn("router", "Error loading Gallery HTML", err);
      });
    }
  };
  if (typeof window !== "undefined") {
    window.Router = Router2;
  }

  // public/src/web.js
  var Debug6 = typeof window !== "undefined" && window.Debug || {
    log: () => {
    },
    warn: () => {
    },
    error: (...args) => console.error(...args)
  };
  var Action2 = window.Action = window.Action || {};
  Action2.renderCalcInto = function(container, calc, options = {}) {
    const action = options.action || window.Embed;
    const calcViewMode = options.calcViewMode || "embed";
    if (action && action.save) {
      action.save(calc);
    }
    if (!container.__ic_root) {
      container.__ic_root = ReactDOM.createRoot(container);
    }
    container.__ic_root.render(
      React.createElement(
        __ICREACT.ActionProvider,
        { action },
        React.createElement(__ICREACT.default.Shared, {
          Action: action,
          calcViewMode,
          embed_id: calc.id,
          key: calc.id
          // Force re-render on calc change
        })
      )
    );
    let footerRoot = null;
    if (options.footerEl) {
      const allExports = __ICREACT.default;
      const ShareMenu = allExports.ShareMenu;
      const RemixButton = allExports.RemixButton;
      const CalcProvider = __ICREACT.CalcProvider;
      if (ShareMenu && RemixButton && CalcProvider) {
        if (!options.footerEl.__ic_root) {
          options.footerEl.__ic_root = ReactDOM.createRoot(options.footerEl);
        }
        footerRoot = options.footerEl.__ic_root;
        footerRoot.render(
          React.createElement(
            __ICREACT.ActionProvider,
            { action },
            React.createElement(
              CalcProvider,
              { calc, viewMode: calcViewMode },
              React.createElement(
                "div",
                { className: "flex items-center gap-2" },
                React.createElement(ShareMenu, { variant: "icon" }),
                React.createElement(RemixButton, { variant: "icon" })
              )
            )
          )
        );
      }
    }
    if (options.onRender) {
      options.onRender(container);
    }
    return {
      root: container.__ic_root,
      footerRoot,
      unmount: () => {
        if (container.__ic_root) {
          container.__ic_root.unmount();
          container.__ic_root = null;
        }
        if (footerRoot) {
          footerRoot.unmount();
          if (options.footerEl) options.footerEl.__ic_root = null;
        }
      }
    };
  };
  Action2.scanForEmbed = function(fn, retries = 2) {
    document.querySelectorAll("instacalc:not(.loaded)").forEach(async (el) => {
      const embed_id = el.getAttribute("embed-id");
      if (!embed_id) return;
      try {
        const calcId = await window.calcStore.ensure("embed", embed_id);
        if (!el.__ic_root) {
          el.__ic_root = ReactDOM.createRoot(el);
        }
        el.__ic_root.render(
          React.createElement(
            __ICREACT.ActionProvider,
            { action: window.Embed },
            React.createElement(
              __ICREACT.CalcProvider,
              { calcId, viewMode: "embed" },
              React.createElement(
                __ICREACT.CalcLoadingBoundary,
                { mode: "wrap", className: "notooltip" },
                React.createElement(__ICREACT.default.InstaCalc, {
                  calcViewMode: "embed",
                  embedId: embed_id
                  // InstaCalc renders its own footer when embedId is provided
                })
              )
            )
          )
        );
        setTimeout(() => {
          const rowsElement = el.querySelector("rows");
          if (rowsElement && rowsElement.children.length > 0) {
            el.classList.add("loaded");
            el.classList.remove("load-failed");
            fn && fn(embed_id);
          } else {
            el.classList.add("load-failed");
            if (retries > 0) {
              setTimeout(() => {
                Action2.scanForEmbed(fn, retries - 1);
              }, 2e3);
            }
          }
        }, 100);
      } catch (err) {
        console.error("[scanForEmbed] Failed to load:", embed_id, err);
        el.classList.add("load-failed");
      }
    });
  };
  var Store = class {
    constructor(data) {
      this.callbacks = [];
      this.data = data || null;
    }
    pub(data) {
      this.data = data;
      this.callbacks.forEach((fn) => fn(data));
    }
    sub(fn) {
      this.callbacks.push(fn);
      if (this.data) {
        fn(this.data);
      }
    }
    data() {
      return this.data;
    }
  };
  if (typeof window !== "undefined") {
    window.Store = Store;
  }
  function initAlpineViewStore() {
    if (typeof Alpine !== "undefined" && Alpine.store) {
      Alpine.store("view", Router.getLayoutType());
    }
  }
  function initSidebarState() {
  }
  initAlpineViewStore();
  initSidebarState();
  document.addEventListener("alpine:init", initAlpineViewStore);
  function handleUrlCalcHashChange() {
    try {
      const viewType = Router.getLayoutType();
      if (viewType === Router.VIEW.URL_CALC || viewType === Router.VIEW.EMBED) {
        Router.route();
      }
    } catch (e) {
      Debug6.warn("router", "Hash change handling failed", e);
    }
  }
  window.addEventListener("hashchange", handleUrlCalcHashChange);
  window.addEventListener("popstate", function(event) {
    try {
      const hash = window.location.hash;
      const isHomePage = window.location.pathname === "/" || window.location.pathname === "/index.html";
      const UrlManager = window.UrlManager;
      if (!UrlManager) throw new Error("UrlManager not loaded");
      if (isHomePage && UrlManager.isLocalCalcHash(hash)) {
        const shortHash = UrlManager.parseLocalCalcHash(hash);
        if (shortHash && window.Action?.select && window.Calc?.all) {
          const matchingCalc = UrlManager.findCalcByShortHash(shortHash, window.Calc.all());
          if (matchingCalc) {
            window.Action.select(matchingCalc.id);
          }
        }
      }
    } catch (e) {
      Debug6.warn("router", "Popstate handling failed", e);
    }
  });
  function bootstrap() {
    if (window.RegisteredShortcuts?.init) {
      window.RegisteredShortcuts.init();
    }
    const doRoute = () => {
      Debug6.log("router", "Routing...");
      Router.route();
    };
    if (window.InitCoordinator?.whenReady) {
      window.InitCoordinator.whenReady("calcStore", doRoute);
      return;
    }
    if (window.calcStore) {
      doRoute();
    } else {
      Debug6.log("router", "Waiting for ic:calc-store-ready event");
      window.addEventListener("ic:calc-store-ready", doRoute, { once: true });
    }
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }

  // public/src/loadworker.js
  if (!IC) {
    throw "IC object not found";
  }
  IC.WORKER = null;
  IC.WORKER_LOADED = false;
  IC.WORKER_LOAD_ERROR = null;
  var WORKER_LOAD_TIMEOUT_MS = 5e3;
  if (typeof Worker !== "undefined") {
    let nonce = document.lastModified;
    try {
      IC.WORKER = new Worker("/dist/worker.js?" + nonce);
    } catch (e) {
      IC.WORKER_LOAD_ERROR = e;
      console.error("[Worker] Failed to create worker:", e.message);
    }
    if (IC.WORKER) {
      let calcMightHaveTickers = function(calc) {
        if (!calc?.rows) return false;
        const tickerPattern = /\$[A-Z]{1,5}\b/;
        return calc.rows.some((row) => {
          const text = row.text || "";
          return tickerPattern.test(text);
        });
      };
      calcMightHaveTickers2 = calcMightHaveTickers;
      const loadTimeout = setTimeout(() => {
        if (!IC.WORKER_LOADED) {
          console.warn("[Worker] \u26A0\uFE0F Worker not loaded after " + WORKER_LOAD_TIMEOUT_MS / 1e3 + "s. Calculations will run on main thread (slower).");
          IC.WORKER_LOAD_ERROR = new Error("Worker load timeout");
        }
      }, WORKER_LOAD_TIMEOUT_MS);
      IC.WORKER.onerror = function(event) {
        clearTimeout(loadTimeout);
        IC.WORKER_LOAD_ERROR = event;
        console.error("[Worker] \u274C Worker error:", event.message, "at", event.filename + ":" + event.lineno);
      };
      IC.WORKER.postMessage({
        type: "LOAD",
        nonce,
        debug: window.Debug && window.Debug.isEnabled("loader")
      });
      BG_REQUEST = {};
      IC.WORKER.onmessage = function(event) {
        if (event.data.type == "LOADED") {
          clearTimeout(loadTimeout);
          if (window.Debug) window.Debug.log("loader", "\u26A1\u26A1\u26A1 background worker loaded");
          IC.WORKER_LOADED = true;
          if (window.externalDataCache?.isInitialized?.()) {
            const currCount = Object.keys(window.InstaCalc?.Unit?.currencies || {}).length;
            if (window.Debug) window.Debug.log("loader", `\u{1F4E4} SYNC_CACHES (worker ready) - syncing stocks + ${currCount} currencies`);
            window.externalDataCache.syncToWorker(true);
          } else if (IC.WORKER_SYNC_STOCK_CACHE) {
            IC.WORKER_SYNC_STOCK_CACHE();
          }
        }
        if (event.data.type == "RESULT") {
          let fn = BG_REQUEST[event.data.id];
          if (fn) {
            if (window.Debug) window.Debug.log("loader", "\u{1F4E5} RESULT \u2190 worker for", event.data.id, "(" + (event.data.time || "?") + "ms)");
            if (window.Debug?.isEnabled?.("stock")) {
              const results = event.data.result;
              results?.forEach((r, i) => {
                const text = r.input?.original || r.text || "";
                if (text.match(/\b[A-Z]{2,5}\b/)) {
                  window.Debug.log("stock", `\u{1F4CA} RESULT[${i}] "${text}" \u2192 ${r.output?.value} ${r.type?.units || r.output?.units || "(no units)"}`);
                }
              });
            }
            fn(event.data.result);
          } else {
            if (window.Debug) window.Debug.log("loader", "\u23ED\uFE0F RESULT ignored for", event.data.id, "(callback canceled by PUB)");
          }
        }
        if (event.data.type == "PUB") {
          const calc = window.calcStore?.getActiveCalc?.();
          if (calc) {
            if (window.Debug) window.Debug.log("loader", "\u{1F514} PUB \u2190 worker needs external data for calc", calc.id);
            if (BG_REQUEST[calc.id]) {
              if (window.Debug) window.Debug.log("loader", "\u{1F6AB} Canceling stale worker callback for", calc.id);
              delete BG_REQUEST[calc.id];
            }
            if (window.Debug) window.Debug.log("loader", "\u{1F504} Main thread sync recalc to trigger fetch");
            const results = Action.recalculate(calc);
            if (calc.id && window.resultsStore) {
              window.resultsStore.set(calc.id, results);
            }
            if (window.InstacalcEvents?.dispatch) {
              window.InstacalcEvents.dispatch(window.InstacalcEvents.CALC_CHANGED, {
                calcId: calc.id,
                reason: "external-data-sync"
              });
            }
          } else {
            if (window.Debug) window.Debug.log("loader", "\u26A0\uFE0F PUB received but no active calc");
          }
        }
        if (event.data.type == "FETCH_CALC") {
          const id = event.data.id;
          const calc = Calc.all().find((c) => c.name?.toLowerCase().trim() === id.toLowerCase().trim());
          IC.WORKER.postMessage({ type: "FETCH_CALC_RESULT", calc, id_lookup: id });
        }
        if (event.data.type == "SYNC_ACK") {
          if (window.externalDataCache?.handleSyncAck) {
            window.externalDataCache.handleSyncAck(event.data.version);
          } else if (window.externalDataCache) {
            window.externalDataCache._lastAckedVersion = event.data.version;
            if (window.Debug) window.Debug.log("loader", `\u{1F4E5} SYNC_ACK v${event.data.version} from worker`);
          }
        }
        if (event.data.type == "REQUEST_FULL_SYNC") {
          const reason = event.data.reason || "unknown";
          const retry = event.data.retry || 0;
          if (window.Debug) window.Debug.log("loader", `\u{1F4E8} Worker requested full sync (reason: ${reason}, retry: ${retry})`);
          if (window.externalDataCache?.handleFullSyncRequest) {
            window.externalDataCache.handleFullSyncRequest();
          } else {
            if (window.Debug) window.Debug.log("loader", "\u{1F4E8} Using legacy sync handler");
            if (IC.WORKER_SYNC_STOCK_CACHE) {
              IC.WORKER_SYNC_STOCK_CACHE();
            }
          }
        }
        if (event.data.type == "WORKER_HEALTH") {
          const status = event.data.status;
          const details = event.data.details || {};
          if (status === "healthy") {
            if (window.Debug) window.Debug.log("loader", `\u2705 Worker health: HEALTHY (${details.currencyCount} currencies, ${details.stockCount} stocks)`);
          } else {
            console.warn(`[Worker] \u26A0\uFE0F Worker health: UNHEALTHY`, details);
            const currCount = Object.keys(window.InstaCalc?.Unit?.currencies || {}).length;
            if (currCount > 0 && details.currencyCount === 0) {
              console.log("[Worker] \u{1F504} Main thread has currencies but worker does not - forcing sync");
              if (window.externalDataCache?.syncToWorker) {
                window.externalDataCache.syncToWorker(true);
              } else if (IC.WORKER_SYNC_STOCK_CACHE) {
                IC.WORKER_SYNC_STOCK_CACHE();
              }
            }
          }
          IC.WORKER_HEALTH = details;
        }
        if (event.data.type == "DEBUG_STATE_RESPONSE") {
          const state = event.data.state;
          console.log("\n[WORKER STATE]");
          console.log("\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500");
          console.log(`\u{1F4CB} Ticker List: ${state.tickerList.ready ? "\u2705 ready" : "\u23F3 loading"} (${state.tickerList.count} tickers)`);
          if (state.tickerList.sample?.length > 0) {
            console.log(`   Sample: ${state.tickerList.sample.join(", ")}...`);
          }
          console.log(`\u{1F4B0} Stock Cache: ${state.stockCache.count} stocks`);
          if (state.stockCache.stocks?.length > 0) {
            state.stockCache.stocks.forEach((s) => {
              const status = s.error ? `\u274C ${s.error}` : `$${s.price}`;
              console.log(`   ${s.symbol}: ${status}`);
            });
          }
          if (state.currencies) {
            console.log(`\u{1F4B1} Currencies: ${state.currencies.loaded ? "\u2705 loaded" : "\u274C NOT LOADED"} (${state.currencies.count} currencies)`);
            console.log(`   USD: ${state.currencies.hasUSD ? "\u2705" : "\u274C"}, EUR: ${state.currencies.hasEUR ? "\u2705" : "\u274C"}`);
            console.log(`   Unit Timestamp: ${state.currencies.unitTimestamp || "none"}`);
            if (state.currencies.sample?.length > 0) {
              console.log(`   Sample: ${state.currencies.sample.join(", ")}...`);
            }
            if (!state.currencies.loaded || state.currencies.count === 0) {
              console.warn("   \u26A0\uFE0F WARNING: Worker has no currencies - conversions will fail!");
            }
          }
          console.log(`\u{1F527} Cache Mode: ${state.cacheMode.hybrid ? "IndexedDB (hybrid)" : "Message sync"}`);
          console.log(`   Last Sync Version: ${state.cacheMode.lastSyncVersion}`);
        }
        if (event.data.type == "TICKER_LIST_READY") {
          const count = event.data.count || 0;
          const error = event.data.error;
          if (error) {
            if (window.Debug) window.Debug.log("stock", `\u{1F4CB} Ticker list failed (non-critical): ${error}`);
          } else {
            if (window.Debug) window.Debug.log("stock", `\u{1F4CB} Ticker list ready: ${count} tickers`);
          }
          if (count > 0) {
            const calc = window.calcStore?.getActiveCalc?.();
            if (calc && calcMightHaveTickers(calc)) {
              if (window.Debug) window.Debug.log("stock", "\u{1F504} Auto-recalc triggered (ticker list now available)");
              setTimeout(() => {
                if (window.Action?.recalculateAndStore) {
                  window.Action.recalculateAndStore(calc.id, "ticker-list-ready");
                }
              }, 50);
            }
          }
        }
      };
      IC.WORKER_RECALCULATE = (calc, fn) => {
        if (IC.WORKER_LOADED) {
          BG_REQUEST[calc.id] = fn;
          const cleanCalc = JSON.parse(JSON.stringify(calc));
          IC.WORKER.postMessage({ calc: cleanCalc, type: "RECALCULATE", id: calc.id });
        } else {
          console.error("[Worker] Worker not loaded - falling back to main thread");
        }
      };
      IC.WORKER_SYNC_STOCK_CACHE = () => {
        if (window.externalDataCache?.isInitialized?.()) {
          window.externalDataCache.syncToWorker();
          return;
        }
        if (IC.WORKER_LOADED && window.Action?.fn) {
          const caches2 = {
            stock_cache: window.Action.fn.stock_cache || {},
            import_cache: window.Action.fn.import_cache || {},
            import_calc_cache: window.Action.fn.import_calc_cache || {}
          };
          if (window.Debug) {
            window.Debug.log("loader", "\u{1F4E4} SYNC_CACHES (legacy) \u2192", Object.keys(caches2.stock_cache));
          }
          IC.WORKER.postMessage({
            type: "SYNC_CACHES",
            caches: caches2
          });
        }
      };
      IC.debugStock = (ticker) => {
        console.log("\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550");
        console.log("\u{1F4CA} INSTACALC STOCK DEBUG");
        console.log("\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550");
        const extCache = window.externalDataCache;
        const tickerSet = extCache?._tickerSet || window.knownTickerSet;
        const stockCache = window.Action?.fn?.stock_cache || {};
        const stockKeys = Object.keys(stockCache);
        console.log("\n[MAIN THREAD]");
        console.log("\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500");
        console.log(`\u{1F4CB} Ticker List: ${tickerSet ? `\u2705 ${tickerSet.size} tickers` : "\u274C not loaded"}`);
        console.log(`\u{1F4B0} Stock Cache: ${stockKeys.length} stocks`);
        if (stockKeys.length > 0) {
          stockKeys.forEach((k) => {
            const s = stockCache[k];
            const status = s?.error ? `\u274C ${s.error}` : `$${s?.price}`;
            console.log(`   ${k}: ${status} ${s?.loaded ? "(loaded)" : "(pending)"}`);
          });
        }
        console.log(`\u{1F527} ExternalDataCache: ${extCache?.isInitialized?.() ? "\u2705 initialized" : "\u23F3 initializing"}`);
        if (ticker) {
          const upper = ticker.toUpperCase();
          console.log(`
[TICKER TRACE: ${upper}]`);
          console.log("\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500");
          console.log(`   In tickerSet: ${tickerSet?.has?.(upper) ? "\u2705 yes" : "\u274C no"}`);
          console.log(`   In stockCache: ${stockCache[upper] ? `\u2705 $${stockCache[upper]?.price}` : "\u274C no"}`);
          console.log(`   isPossibleStock: ${extCache?.isPossibleStock?.(upper) ? "\u2705 yes" : "\u274C no"}`);
          console.log(`   isKnownTicker: ${extCache?.isKnownTicker?.(upper) ? "\u2705 yes" : "\u274C no"}`);
          const wouldConvert = tickerSet?.has?.(upper) || stockCache[upper];
          console.log(`   $${upper} would become: ${wouldConvert ? `STOCK_${upper}` : `$${upper} (unchanged)`}`);
        }
        if (IC.WORKER && IC.WORKER_LOADED) {
          console.log("\n[WORKER] Requesting state...");
          IC.WORKER.postMessage({ type: "DEBUG_STATE" });
        } else {
          console.log("\n[WORKER] Not loaded");
        }
        console.log("\n\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550");
        console.log("\u{1F4A1} Tips:");
        console.log("   - Enable debug: add ?debug=stock to URL");
        console.log('   - Trace ticker: IC.debugStock("AAPL")');
        console.log("   - Full state: IC.getWorkerDebugInfo()");
        console.log("\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550");
        return { tickerSetSize: tickerSet?.size, stockCacheSize: stockKeys.length };
      };
      IC.debugWorkerHealth = () => {
        console.log("\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550");
        console.log("\u{1F3E5} WORKER HEALTH CHECK");
        console.log("\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550");
        const currencies = window.InstaCalc?.Unit?.currencies || {};
        const currencyCount = Object.keys(currencies).length;
        const hasUSD = !!currencies.usd;
        const hasEUR = !!currencies.eur;
        console.log("\n[MAIN THREAD]");
        console.log("\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500");
        console.log(`\u{1F4B1} Currencies: ${currencyCount} loaded`);
        console.log(`   USD: ${hasUSD ? "\u2705" : "\u274C"}, EUR: ${hasEUR ? "\u2705" : "\u274C"}`);
        console.log(`   Health: ${currencyCount > 0 && hasUSD && hasEUR ? "\u2705 HEALTHY" : "\u26A0\uFE0F UNHEALTHY"}`);
        if (IC.WORKER_HEALTH) {
          console.log("\n[WORKER (last known)]");
          console.log("\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500");
          console.log(`\u{1F4B1} Currencies: ${IC.WORKER_HEALTH.currencyCount || 0} loaded`);
          console.log(`   USD: ${IC.WORKER_HEALTH.hasUSD ? "\u2705" : "\u274C"}, EUR: ${IC.WORKER_HEALTH.hasEUR ? "\u2705" : "\u274C"}`);
          console.log(`   Synced: ${IC.WORKER_HEALTH.currenciesSynced ? "\u2705" : "\u274C"}`);
          console.log(`   Fallback: ${IC.WORKER_HEALTH.fallbackUsed ? "\u26A0\uFE0F YES" : "\u2705 NO"}`);
          console.log(`   Retries: ${IC.WORKER_HEALTH.syncRetries || 0}`);
          console.log(`   Health: ${IC.WORKER_HEALTH.isHealthy ? "\u2705 HEALTHY" : "\u26A0\uFE0F UNHEALTHY"}`);
          if (IC.WORKER_HEALTH.errors?.length > 0) {
            console.log(`   Errors: ${IC.WORKER_HEALTH.errors.join(", ")}`);
          }
        }
        if (IC.WORKER && IC.WORKER_LOADED) {
          console.log("\n[WORKER] Requesting fresh health status...");
          IC.WORKER.postMessage({ type: "DEBUG_HEALTH" });
        } else {
          console.log("\n[WORKER] Not loaded");
        }
        console.log("\n\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550");
        return {
          mainThread: { currencyCount, hasUSD, hasEUR },
          worker: IC.WORKER_HEALTH || null
        };
      };
      IC.getWorkerDebugInfo = () => {
        const cacheDebug = window.externalDataCache?.getDebugInfo?.() || null;
        return {
          workerLoaded: IC.WORKER_LOADED,
          workerLoadError: IC.WORKER_LOAD_ERROR?.message || null,
          syncVersion: cacheDebug?.syncVersion || 0,
          lastAckedVersion: cacheDebug?.lastAckedVersion || 0,
          lastCacheSync: cacheDebug?.lastSync || null,
          cacheSyncHistory: cacheDebug?.syncHistory || [],
          pendingRequests: Object.keys(BG_REQUEST),
          mainThreadCaches: cacheDebug?.caches || {
            stock: Object.keys(window.Action?.fn?.stock_cache || {}),
            stockDetails: window.Action?.fn?.stock_cache || {},
            import: Object.keys(window.Action?.fn?.import_cache || {}),
            import_calc: Object.keys(window.Action?.fn?.import_calc_cache || {})
          },
          currencies: {
            count: Object.keys(window.InstaCalc?.Unit?.currencies || {}).length,
            loaded: window.InstaCalc?.Unit?.currenciesLoaded?.(),
            codes: Object.keys(window.InstaCalc?.Unit?.currencies || {}).slice(0, 10).concat(Object.keys(window.InstaCalc?.Unit?.currencies || {}).length > 10 ? ["..."] : [])
          },
          cacheInitialized: cacheDebug?.initialized || false
        };
      };
    }
  } else {
    console.warn("[Worker] \u26A0\uFE0F Web Workers not supported in this browser. Calculations will run on main thread.");
  }
  var BG_REQUEST;
  var calcMightHaveTickers2;

  // public/src/utils/search.js
  async function decompressGzip(gzippedData) {
    const decompressedStream = new Response(gzippedData).body.pipeThrough(new DecompressionStream("gzip"));
    return await new Response(decompressedStream).arrayBuffer();
  }
  function arrayBufferToString(buffer) {
    return new TextDecoder().decode(buffer);
  }
  var miniSearch = null;
  var loadAttempts = 0;
  var searchIndexStarted = false;
  var MAX_LOAD_ATTEMPTS = 50;
  function loadRemoteCalcIndex() {
    if (searchIndexStarted) return;
    searchIndexStarted = true;
    _doLoadRemoteCalcIndex();
  }
  function _doLoadRemoteCalcIndex() {
    if (typeof MiniSearch === "undefined") {
      loadAttempts++;
      if (loadAttempts >= MAX_LOAD_ATTEMPTS) {
        console.error("MiniSearch failed to load after 5 seconds. Search functionality unavailable.");
        return;
      }
      setTimeout(_doLoadRemoteCalcIndex, 100);
      return;
    }
    const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const indexUrl = "https://instacalc.com/index.json.gz?dl=1";
    fetch(indexUrl, {
      method: "GET",
      headers: {
        "Accept": "application/gzip"
      }
    }).then((response) => response.arrayBuffer()).then(async (gzippedData) => {
      const decompressedBuffer = await decompressGzip(gzippedData);
      const jsonString = arrayBufferToString(decompressedBuffer);
      const indexData = JSON.parse(jsonString);
      miniSearch = MiniSearch.loadJS(indexData.index, {
        fields: ["name", "rows"],
        // Include fields required for MiniSearch
        storeFields: ["name", "updated"]
      });
    }).catch((error) => console.error("Error:", error));
  }
  function calcSearch(query) {
    if (miniSearch) {
      const results = miniSearch.search(query, {
        fuzzy: 0.2,
        prefix: true,
        boost: { name: 4 }
      });
      return results.slice(0, 30);
    }
  }
  window.searchUI = function searchUI() {
    return {
      searchQuery: "",
      searchResults: [],
      groupedResults: [],
      showResults: false,
      selectedResult: null,
      embedId: null,
      saveStatus: "Save to my calcs",
      docResults: [],
      // Platform detection for keyboard shortcuts
      isMac: navigator.platform.toUpperCase().indexOf("MAC") >= 0 || navigator.platform.toUpperCase().indexOf("IPHONE") >= 0 || navigator.platform.toUpperCase().indexOf("IPAD") >= 0,
      // Computed property for keyboard shortcut display
      get keyboardShortcut() {
        return this.isMac ? "\u2318K" : "Ctrl+K";
      },
      init() {
        try {
          this.localIndex = this.buildLocalSearchIndex();
        } catch (error) {
          console.error("Error building local search index:", error);
        }
      },
      waiting: false,
      debounceTimeout: null,
      async search() {
        clearTimeout(this.debounceTimeout);
        this.waiting = true;
        this.debounceTimeout = setTimeout(async () => {
          if (this.searchQuery.length > 1) {
            try {
              this.searchResults = calcSearch(this.searchQuery);
              this.searchResults.sort((a, b) => {
                if (a.name.toLowerCase().includes("instacalc") && !b.name.toLowerCase().includes("instacalc")) {
                  return -1;
                } else if (!a.name.toLowerCase().includes("instacalc") && b.name.toLowerCase().includes("instacalc")) {
                  return 1;
                } else {
                  return 0;
                }
              });
              this.groupResults();
              if (this.searchResults.length > 0) {
                this.selectResult(this.searchResults[0]);
              }
            } catch (error) {
              console.error("Error fetching search results:", error);
              this.searchResults = [];
              this.groupedResults = [];
            }
          } else {
            this.searchResults = [];
            this.groupedResults = [];
          }
          setTimeout(() => {
            this.waiting = false;
          }, 100);
        }, 100);
        this.docSearch(this.searchQuery);
      },
      buildLocalSearchIndex() {
        const index = [];
        const unitGroups = /* @__PURE__ */ new Map();
        function formatSIUnitDefinition(siDefinition) {
          if (!siDefinition) return "";
          const unitParts = [];
          for (const [unit, power] of Object.entries(siDefinition)) {
            if (power === 1) {
              unitParts.push(unit);
            } else if (power !== 0) {
              unitParts.push(`${unit}^${power}`);
            }
          }
          return unitParts.join(" ");
        }
        for (const unitKey in InstaCalc.Unit) {
          const unitObject = InstaCalc.Unit[unitKey];
          if (typeof unitObject === "object" && unitObject !== null) {
            const baseUnit = unitObject.base || 1;
            const unitName = unitObject.name || unitKey;
            const siDefinition = unitObject.SI ? formatSIUnitDefinition(unitObject.SI) : "";
            for (const alias in unitObject) {
              const conversionFactor = Number(unitObject[alias]);
              if (alias != "base" && alias != "SI" && !isNaN(conversionFactor)) {
                const groupKey = `${unitName}-${conversionFactor}`;
                const absConversionFactor = Math.abs(conversionFactor);
                const shouldUseScientific = absConversionFactor > 1e3 || absConversionFactor < 1e-3;
                let conversionFactorStr = shouldUseScientific ? conversionFactor.toExponential() : conversionFactor.toString();
                conversionFactorStr = conversionFactorStr.replace(/\.0+e/, "e");
                if (!unitGroups.has(groupKey)) {
                  unitGroups.set(groupKey, {
                    type: "unit",
                    name: alias,
                    aliases: [],
                    description: siDefinition ? `${unitName}: ${conversionFactorStr} * (${siDefinition})` : `${unitName}: ${conversionFactorStr} * base unit`,
                    category: "Units",
                    baseUnit,
                    conversionFactor,
                    siDefinition
                  });
                }
                unitGroups.get(groupKey).aliases.push(alias);
              }
            }
            if (unitObject.SI) {
              index.push({
                type: "unit_si",
                name: unitName,
                description: `SI definition: ${siDefinition}`,
                category: "SI Units",
                siDefinition
              });
            }
          }
        }
        for (const group of unitGroups.values()) {
          index.push(group);
        }
        for (const func in CONFIG.FUNCTION_USAGE_MSG) {
          index.push({
            type: "function",
            name: func,
            description: CONFIG.FUNCTION_USAGE_MSG[func],
            category: "Functions"
          });
        }
        for (const variable in CONFIG.PREDEFINED) {
          const isFunction = typeof CONFIG.PREDEFINED[variable] === "function";
          let value = null;
          if (isFunction) {
            value = CONFIG.PREDEFINED[variable]();
          }
          index.push({
            type: "variable",
            name: variable,
            description: isFunction ? IC.ICObjectPrettyPrint(value) : JSON.stringify(CONFIG.PREDEFINED[variable]),
            category: "Predefined Variables"
          });
        }
        for (const [alias, data] of Object.entries(CONFIG.FLEX_UNIT_ALIAS)) {
          index.push({
            type: "unit",
            name: alias,
            description: `${data.scale} ${data.type} `,
            category: "Units",
            scale: data.scale,
            baseType: data.type
          });
        }
        return index;
      },
      async docSearch(query) {
        const RESULT_LIMIT = 30;
        query = query.toLowerCase().trim();
        let index = this.localIndex;
        let exactMatch = null;
        let exactMatchInDescription = [];
        let startsWithMatches = [];
        let containsMatches = [];
        let otherMatches = [];
        try {
          for (let item of index) {
            const nameLower = item.name.toLowerCase();
            const descriptionLower = item?.description?.toLowerCase();
            let isStartsWithMatch = false;
            let isContainsMatch = false;
            if (nameLower === query) {
              exactMatch = item;
              continue;
            }
            if (nameLower.startsWith(query)) {
              isStartsWithMatch = true;
            } else if (nameLower.includes(query)) {
              isContainsMatch = true;
            }
            if (item.aliases) {
              for (let alias of item.aliases) {
                const aliasLower = alias.toLowerCase();
                if (aliasLower === query) {
                  exactMatch = item;
                  break;
                } else if (aliasLower.startsWith(query)) {
                  isStartsWithMatch = true;
                  break;
                } else if (aliasLower.includes(query)) {
                  isContainsMatch = true;
                }
              }
            }
            if (descriptionLower) {
              const descriptionWords = descriptionLower.split(" ");
              if (descriptionWords.includes(query)) {
                exactMatchInDescription.push(item);
                continue;
              }
            }
            if (isStartsWithMatch) {
              startsWithMatches.push(item);
            } else if (isContainsMatch) {
              containsMatches.push(item);
            } else if (descriptionLower?.includes(query)) {
              otherMatches.push(item);
            }
          }
        } catch (error) {
          console.error("Error searching docs:", error);
        }
        let results = [];
        if (exactMatch) {
          results.push(exactMatch);
        }
        results = results.concat(exactMatchInDescription, startsWithMatches, containsMatches, otherMatches);
        this.docResults = results.slice(0, RESULT_LIMIT);
      },
      formatDocResult(result) {
        let formatted = result.name;
        if (result.description) {
          formatted += ` - ${result.description}`;
        }
        return formatted;
      },
      groupResults() {
        const groups = {};
        this.searchResults.forEach((result) => {
          if (!groups[result.name]) {
            groups[result.name] = {
              name: result.name,
              items: [],
              expanded: false
            };
          }
          groups[result.name].items.push(result);
        });
        this.groupedResults = Object.values(groups).map((group) => {
          group.items.sort((a, b) => new Date(b.updated) - new Date(a.updated));
          return group;
        });
        if (this.groupedResults.length > 0) {
          this.groupedResults[0].expanded = true;
        }
      },
      toggleGroup(index) {
        this.groupedResults[index].expanded = !this.groupedResults[index].expanded;
        if (this.groupedResults[index].expanded) {
          this.selectResult(this.groupedResults[index].items[0]);
        }
      },
      clearSearch() {
        this.searchQuery = "";
        this.searchResults = [];
        this.groupedResults = [];
        this.showResults = false;
        this.selectedResult = null;
        this.embedId = null;
      },
      selectResult(result) {
        this.selectedResult = result;
        this.previewItem(result);
      },
      previewItem(result) {
        this.embedId = result?.id;
        const preview = this.$el.closest(".search")?.querySelector(".search-preview");
        if (preview) {
          preview.classList.remove("loaded");
          preview.classList.remove("load-failed");
          preview.setAttribute("embed-id", this.embedId);
          Action.scanForEmbed();
        } else {
          console.error("Preview element not found");
        }
      },
      formatDate(dateString) {
        if (!dateString) {
          return "";
        }
        const date = new Date(dateString);
        const now2 = /* @__PURE__ */ new Date();
        const options = { month: "numeric", day: "numeric", year: "2-digit" };
        return date.toLocaleDateString("en-US", options);
      },
      saveCalc() {
        Action.saveEmbedCalc(this.embedId);
        this.saveStatus = "Saved!";
        setTimeout(() => {
          this.saveStatus = "Save to my calcs";
        }, 2e3);
      }
    };
  };
  var globalSearchInput;
  if (window.IC_CONFIG?.features?.search) {
    document.addEventListener("keydown", function(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        loadRemoteCalcIndex();
        if (!globalSearchInput) {
          globalSearchInput = document.querySelector(".search input.globalsearch");
        }
        if (globalSearchInput) {
          event.preventDefault();
          const container = document.querySelector(".search-container");
          if (container) {
            container.style.display = "block";
            container.classList.add("fixed", "inset-0", "z-[100]", "flex", "items-start", "justify-center", "pt-20", "bg-black/20", "backdrop-blur-sm");
          }
          globalSearchInput.style.display = "block";
          globalSearchInput.focus();
          globalSearchInput.select();
        }
      }
      if (event.key === "Escape") {
        const container = document.querySelector(".search-container");
        if (container && container.style.display !== "none") {
          container.style.display = "none";
        }
      }
    });
  }

  // public/src/utils/chat.js
  function markdownToHtml(markdownText) {
    markdownText = markdownText.replace(/^# (.+)$/gm, "<h1>$1</h1>");
    markdownText = markdownText.replace(/^## (.+)$/gm, "<h2>$1</h2>");
    markdownText = markdownText.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    markdownText = markdownText.replace(/\*(.+?)\*/g, "<em>$1</em>");
    markdownText = markdownText.replace(/^\* (.+)$/gm, "<li>$1</li>");
    markdownText = markdownText.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");
    return markdownText;
  }
  var chatInstanceCounter = 0;
  function chatInterface() {
    return {
      // Unique ID per instance (for x-for :key attributes)
      instanceId: "chat" + ++chatInstanceCounter,
      // Visibility flag expected by templates (e.g., x-show="calcy")
      calcy: 1,
      open: false,
      info: 1,
      messages: [],
      newMessage: "",
      analysisMessage: "",
      waiting: 0,
      error: "",
      clearConversation() {
        this.error = "";
        this.waiting = 0;
        this.messages = [];
      },
      autoResize(textarea) {
        textarea.style.height = "auto";
        textarea.style.height = textarea.scrollHeight + "px";
      },
      analyzeCalc() {
        let text = Action.getTextView(Action.getCurrentCalc(), "inputs");
        this.newMessage = "Analyze this calc and give suggestions, enhancements, and improvements. A new, improved calculator must appear inside triple backticks (```). Be friendly yet succinct in your analysis. Here is the calc to analyze:  --- \n\n " + text + " ---";
        this.analysisMessage = this.newMessage;
        this.sendMessage();
      },
      render(message) {
        if (message?.parts[0]?.text?.trim() == this.analysisMessage?.trim()) {
          return "Calcy, enhance!";
        }
        let content = message.parts.map((part) => part.text).join("");
        content = content.replace("<instacalc>", "<pre>");
        content = content.replace("</instacalc>", "</pre>");
        content = content.replace("<start>", "<pre>");
        content = content.replace("<end>", "</pre>");
        content = content.replace(/```([\s\S]*?)```/g, function(match, p1) {
          return `<pre>${p1.trim()}</pre>`;
        });
        content = content.replace(/<pre>([\s\S]*?)<\/pre>/g, function(match, p1) {
          return match;
        });
        content = this.addButtonsToPreBlocks(content);
        content = markdownToHtml(content);
        return content;
      },
      addButtonsToPreBlocks(text = "") {
        let parser = new DOMParser();
        let doc = parser.parseFromString(text, "text/html");
        let preBlocks = doc.querySelectorAll("pre");
        preBlocks.forEach((pre, index) => {
          const allRows = pre.textContent.split("\n").map((text2) => text2.trim()).filter((text2) => text2 !== "");
          const cleanRows = allRows.map((row) => {
            const temp = document.createElement("div");
            temp.innerHTML = row;
            const decoded = temp.textContent || temp.innerText || "";
            return decoded.replace(/<[^>]*>/g, "");
          });
          if (cleanRows.length === 0) return;
          let name, rows;
          if (cleanRows.length === 1) {
            name = "Preview";
            rows = [{ text: cleanRows[0] }];
          } else {
            name = cleanRows[0].replace("//", "").trim();
            rows = cleanRows.slice(1).map((text2) => ({ text: text2 }));
          }
          const calcData = {
            name,
            rows
          };
          const previewContainer = document.createElement("div");
          previewContainer.className = "calc-preview my-4 rounded-lg overflow-hidden border border-[var(--border-soft)]";
          previewContainer.setAttribute("data-calc-data", JSON.stringify(calcData));
          previewContainer.setAttribute("x-init", `
                    const calcData = JSON.parse($el.getAttribute('data-calc-data'));
                    // Mark all rows as hidden
                    const rowsWithState = calcData.rows.map(r => ({ ...r, hidden: true }));
                    const tempCalc = window.Calc.createCalcObject({
                        name: calcData.name,
                        rows: rowsWithState
                    });
                    $el.setAttribute('data-calc-id', tempCalc.id);
                    const container = $el.querySelector('.calc-render-container');
                    if (window.Action?.renderCalcInto && container) {
                        window.Action.renderCalcInto(container, tempCalc, {
                            calcViewMode: 'embed'
                        });
                    }
                `);
          const calcContainer = document.createElement("div");
          calcContainer.className = "calc-render-container w-full p-3";
          previewContainer.appendChild(calcContainer);
          const button = document.createElement("button");
          button.className = "w-full px-4 py-2.5 font-semibold text-sm bg-[var(--accent)] text-[var(--bg)] hover:opacity-90 transition-all border-t border-[var(--border-soft)] create-calc-button flex items-center justify-center gap-2";
          button.innerHTML = `
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    <span>Add to My Calcs</span>
                `;
          previewContainer.appendChild(button);
          pre.parentNode.replaceChild(previewContainer, pre);
        });
        text = doc.body.innerHTML;
        return text;
      },
      handleClick(event) {
        if (event.target.matches("button.create-calc-button")) {
          const button = event.target;
          const previewContainer = button.closest(".calc-preview");
          if (!previewContainer) return;
          const calcId = previewContainer.getAttribute("data-calc-id");
          const sourceCalc = window.Calc?.get(calcId);
          if (!sourceCalc) {
            console.error("Could not find preview calc:", calcId);
            return;
          }
          let calc = Action.create({
            name: sourceCalc.name,
            rows: sourceCalc.rows.map((r) => ({ text: r.text }))
          });
          if (window.App?.openCalc) {
            window.App.openCalc(calc.id);
          } else {
            Action.select(calc.id);
          }
          button.disabled = true;
          button.className = "w-full px-4 py-2.5 font-semibold text-sm bg-[var(--bg-alt)] text-[var(--text-dim)] border-t border-[var(--border-soft)] flex items-center justify-center gap-2 cursor-not-allowed opacity-75";
          button.innerHTML = `
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>
                    <span>Added to Library</span>
                `;
        }
      },
      async sendMessage() {
        const message = this.newMessage.trim();
        this.newMessage = "";
        this.error = "";
        this.$nextTick(() => {
          if (this.$refs.chatInput) {
            this.$refs.chatInput.style.height = "auto";
          }
        });
        if (message) {
          this.messages.push({ role: "user", parts: [{ text: message }] });
          let CHAT_URL;
          const params = new URLSearchParams(window.location.search);
          const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
          const hasLocalParam = params.has("local") && params.get("local") !== "false" && params.get("local") !== "0";
          if (isLocalhost && hasLocalParam) {
            const currentPort = parseInt(window.location.port, 10) || 5002;
            const emulatorPort = currentPort - 1;
            const protocol = window.location.protocol;
            const hostname = window.location.hostname;
            CHAT_URL = `${protocol}//${hostname}:${emulatorPort}/firebase-instacalc/us-central1/chatGoogle_v2`;
          } else {
            CHAT_URL = window.getCloudFunctionUrl("chatGoogle_v2");
          }
          const userId = Action.getCurrentUserId();
          this.waiting = 1;
          let response;
          let data;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 1e4);
          try {
            response = await fetch(CHAT_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                message,
                userId,
                messages: this.messages
              }),
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            data = await response.json();
          } catch (error) {
            clearTimeout(timeoutId);
            this.waiting = 0;
            console.error("[Calcy] Chat request failed:", {
              errorName: error.name,
              errorMessage: error.message,
              isTimeout: error.name === "AbortError",
              isOnline: navigator.onLine,
              chatUrl: CHAT_URL,
              isLocalhost
            });
            if (error.name === "AbortError") {
              this.error = "Request timed out after 10 seconds. The server may be overloaded. Please try again.";
            } else if (!navigator.onLine) {
              this.error = "No internet connection. Please check your network and try again.";
            } else if (error.message.includes("Failed to fetch")) {
              this.error = isLocalhost ? "Cannot connect to server. For local testing, add ?local to the URL (e.g., http://localhost:5002/?local) and ensure Firebase emulator is running." : "Cannot connect to server. Please check your internet connection and try again.";
            } else {
              this.error = `Network error: ${error.message}. Please try again.`;
            }
            this.messages.pop();
            return;
          }
          this.waiting = 0;
          if (!response?.ok) {
            console.error("Error sending message:", response.status, data);
            let errorMsg;
            if (response.status === 404) {
              errorMsg = isLocalhost ? "Chat endpoint not found. For local testing, add ?local to the URL (e.g., http://localhost:5002/?local) and ensure Firebase emulator is running." : "Chat service not available. The endpoint may not be deployed yet.";
            } else if (response.status === 500) {
              errorMsg = data?.error || data?.message || "Server error. The AI service encountered an error. Please try again.";
            } else if (response.status === 403) {
              errorMsg = "Access forbidden. The chat service may not be configured for this environment.";
            } else {
              errorMsg = data?.error || data?.message || `Server error (${response.status}). Please try again.`;
            }
            this.error = errorMsg;
            this.messages.pop();
            return;
          }
          this.messages.push({ role: "model", parts: [{ text: data.reply }] });
          this.$nextTick(() => {
            this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
          });
        }
      }
    };
  }
  if (typeof window !== "undefined") {
    window.chatInterface = chatInterface;
  }

  // public/src/main.js
  var DebugModule2 = __toESM(require_debug());

  // public/src/PluginRegistry.js
  var DebugModule = __toESM(require_debug());
  var Debug7 = DebugModule.default || DebugModule;
  var Registry = class {
    constructor(name) {
      this.name = name;
      this.items = /* @__PURE__ */ new Map();
    }
    register(item) {
      if (!item.id) {
        console.warn(`[Registry:${this.name}] Item missing 'id'`, item);
        return;
      }
      if (this.items.has(item.id)) {
        Debug7.log("plugins", `[Registry:${this.name}] Overwriting item:`, item.id);
      }
      this.items.set(item.id, item);
      Debug7.log("plugins", `[Registry:${this.name}] Registered:`, item.id);
    }
    get(id) {
      return this.items.get(id);
    }
    getAll() {
      return Array.from(this.items.values());
    }
    getAllSorted(compareFn) {
      const values = this.getAll();
      const sorter = typeof compareFn === "function" ? compareFn : (a, b) => {
        const left = typeof a.order === "number" ? a.order : 99;
        const right = typeof b.order === "number" ? b.order : 99;
        return left - right;
      };
      return values.sort(sorter);
    }
  };
  var PluginRegistry = class {
    constructor() {
      Debug7.log("plugins", "Initializing PluginRegistry");
      this.panels = new Registry("panels");
      this.strategies = new Registry("strategies");
      this.toolbarSlots = new Registry("toolbarSlots");
      this._actionExtensions = {};
    }
    /**
     * Register an action extension (adds method to window.Action)
     * @param {string} name - Action method name
     * @param {Function} fn - Action function
     */
    registerAction(name, fn) {
      if (typeof window !== "undefined" && window.Action) {
        window.Action[name] = fn;
      }
      this._actionExtensions[name] = fn;
      Debug7.log("plugins", `Registered action: ${name}`);
    }
  };
  var plugins = new PluginRegistry();
  if (typeof window !== "undefined") {
    window.__IC_PLUGINS__ = plugins;
  }

  // public/src/plugins/panels/core.js
  var CalcyPanel = {
    id: "calcy",
    label: "Calcy AI",
    icon: "calcy",
    slot: "right-sidebar",
    showInDock: true,
    order: 10,
    templateUrl: "templates/calcy.html",
    innerClass: "h-full"
  };
  var KeypadPanel = {
    id: "keypad",
    label: "Keypad",
    icon: "keypad",
    slot: "right-sidebar",
    showInDock: true,
    order: 20,
    templateUrl: "templates/keypad.html",
    innerClass: "pb-16"
  };
  var HelpPanel = {
    id: "help",
    label: "Help",
    icon: "help",
    slot: "right-sidebar",
    showInDock: true,
    order: 30,
    templateUrl: "templates/help-panel.html",
    innerClass: "h-full text-sm"
  };
  var ThemePanel = {
    id: "theme",
    label: "Theme",
    icon: "theme",
    slot: "right-sidebar",
    showInDock: false,
    order: 40,
    templateUrl: "ui/theme/theme-panel.html"
  };
  var GalleryPanel = {
    id: "gallery",
    label: "Calculator",
    // Dynamic in UI
    icon: "gallery",
    slot: "right-sidebar",
    showInDock: false,
    // Triggered by UI
    order: 50,
    templateUrl: "templates/gallery-preview-panel.html"
  };
  var PreviewPanel = {
    id: "preview",
    label: "Present Mode Preview",
    icon: "presentation",
    slot: "right-sidebar",
    showInDock: false,
    // Triggered by UI
    order: 60,
    templateUrl: "templates/preview-panel.html",
    innerClass: "h-full",
    beta: true,
    betaTooltip: "Presentation mode is in beta. Styling and layout options are still evolving."
  };
  var DebugPanel = {
    id: "debug",
    label: "Debug Settings",
    icon: "bug",
    slot: "right-sidebar",
    showInDock: false,
    order: 99,
    templateUrl: "templates/debug-panel.html",
    alpine: "DebugPanelData",
    innerClass: "h-full"
  };
  var corePanels = [
    CalcyPanel,
    KeypadPanel,
    HelpPanel,
    ThemePanel,
    GalleryPanel,
    PreviewPanel,
    DebugPanel
  ];

  // public/src/main.js
  if (typeof window !== "undefined") {
    window.perfMetrics = window.perfMetrics || perfMetrics;
  }
  if (typeof window !== "undefined") {
    window.offlineStorage = window.offlineStorage || offlineStorage;
    window.FirebaseOptional = window.FirebaseOptional || FirebaseOptional;
  }
  if (typeof window !== "undefined") {
    window.externalDataCache = externalDataCache;
    externalDataCache.init().then(() => externalDataCache.loadTickerList()).then(() => {
      externalDataCache.syncToWorker(true);
    }).catch((err) => {
      console.warn("[ExternalDataCache] Init failed:", err);
    });
  }
  var Debug8 = DebugModule2.default || DebugModule2;
  corePanels.forEach((panel) => plugins.panels.register(panel));
  Debug8.log("plugins", "Registered core panels:", corePanels.map((p) => p.id));
  Promise.resolve().then(() => (init_labs(), labs_exports)).then(() => {
    Debug8.log("plugins", "Labs plugin loaded");
  });
  if (typeof window !== "undefined" && (window.IC_CONFIG?.features?.calcInfo || window.IC_CONFIG?.features?.community)) {
    Promise.resolve().then(() => (init_calc_info(), calc_info_exports)).then(() => {
      Debug8.log("plugins", "Calc Info plugin loaded");
    });
  }
  if (typeof window !== "undefined" && !window.Timestamps) {
    window.Timestamps = timestamps_exports;
  }
  if (typeof window !== "undefined") {
    window.addEventListener("load", () => {
      setTimeout(() => {
        const featureEnabled = window.IC_CONFIG?.features?.perfMonitoring !== false;
        if (!featureEnabled) {
          Debug8.log("perf", "Performance monitoring disabled (feature flag)");
          return;
        }
        const urlParams = new URLSearchParams(window.location.search);
        const forceEnable = urlParams.get("perf_monitoring") === "true";
        const isProduction = window.location.hostname !== "localhost" && !window.location.hostname.includes("127.0.0.1");
        if (forceEnable || isProduction) {
          if (window.PerfAggregator && typeof window.PerfAggregator.init === "function") {
            try {
              window.PerfAggregator.init();
              Debug8.log("perf", "Performance monitoring initialized");
            } catch (err) {
              Debug8.log("perf", "Performance monitoring init failed (non-critical):", err.message);
            }
          } else {
            Debug8.log("perf", "PerfAggregator not loaded, skipping monitoring");
          }
        } else {
          Debug8.log("perf", "Performance monitoring disabled (localhost - use ?perf_monitoring=true to test)");
        }
      }, 2e3);
    });
  }
  Debug8.log("loader", "ES modules loaded");
})();
/*! Bundled license information:

react/cjs/react.production.min.js:
  (**
   * @license React
   * react.production.min.js
   *
   * Copyright (c) Facebook, Inc. and its affiliates.
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE file in the root directory of this source tree.
   *)

scheduler/cjs/scheduler.production.min.js:
  (**
   * @license React
   * scheduler.production.min.js
   *
   * Copyright (c) Facebook, Inc. and its affiliates.
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE file in the root directory of this source tree.
   *)

react-dom/cjs/react-dom.production.min.js:
  (**
   * @license React
   * react-dom.production.min.js
   *
   * Copyright (c) Facebook, Inc. and its affiliates.
   *
   * This source code is licensed under the MIT license found in the
   * LICENSE file in the root directory of this source tree.
   *)
*/
